/**
 * Les libellés de la COQUILLE et des écrans, et rien d'autre.
 *
 * Ce qui n'est PAS ici : le menu, les rôles, les centres. Leurs libellés
 * appartiennent à l'établissement ou au fournisseur (D19) et arrivent déjà
 * traduits de l'API. Les retraduire côté front ferait de chaque renommage une
 * intervention de développeur — exactement ce que D13 cherche à éviter.
 *
 * Pas de bibliothèque : le pilote livre trois langues (D21, D43). La forme retenue —
 * un dictionnaire typé et une fonction `t` — est celle qu'expose n'importe quelle
 * bibliothèque, de sorte que le remplacement, le jour venu, soit mécanique.
 * L'interpolation se limite à `{nom}`.
 *
 * `en` est typé `Record<TranslationKey, string>` : une clé ajoutée en français
 * et oubliée en anglais fait échouer la compilation.
 */

const fr = {
    'app.name': 'Kelassi',
    'signIn.title': 'Connexion',
    'signIn.email': 'Adresse électronique',
    'signIn.password': 'Mot de passe',
    'signIn.submit': 'Se connecter',
    'signIn.pending': 'Connexion…',
    'signIn.refused': 'Identifiants invalides.',
    'signIn.unreachable': "L'API est injoignable.",
    'signIn.forgot': 'Mot de passe oublié ?',
    'signIn.passwordChanged': 'Mot de passe changé. Toutes vos sessions ont été fermées : reconnectez-vous.',
    'signIn.passwordSet': 'Mot de passe enregistré. Vous pouvez vous connecter.',
    'session.loading': 'Chargement…',
    'session.expired': 'Votre session a expiré. Reconnectez-vous.',
    'session.signOut': 'Se déconnecter',
    'session.changePassword': 'Changer mon mot de passe',
    'menu.empty': 'Aucune entrée de menu ne vous est ouverte.',
    'menu.label': 'Navigation principale',
    'language.label': 'Langue',
    'screen.pending': "Cet écran n'est pas encore construit.",
    'screen.pendingHint': 'La route est déclarée et le droit est vérifié : seul le contenu manque.',
    'notFound.title': 'Page introuvable',
    'notFound.body': "Cette adresse ne correspond à aucun écran de l'application.",
    'notFound.back': "Revenir à l'accueil",

    'common.cancel': 'Annuler',
    'common.save': 'Enregistrer',
    'common.saving': 'Enregistrement…',
    'common.confirm': 'Confirmer',
    'common.back': 'Retour',
    'common.never': 'Jamais',
    'common.yes': 'Oui',
    'common.no': 'Non',
    'common.loading': 'Chargement…',
    'common.forbidden': "Vous n'avez pas le droit de faire cela.",
    'common.notFound': 'Introuvable.',
    'common.error': 'Une erreur est survenue. Réessayez.',

    'population.staff': 'Personnel',
    'population.teacher': 'Enseignant',
    'population.student': 'Étudiant',
    'population.guardian': 'Parent ou tuteur',

    'accounts.title': 'Comptes',
    'accounts.new': 'Nouveau compte',
    'accounts.search': 'Rechercher un nom ou une adresse',
    'accounts.filter.population': 'Population',
    'accounts.filter.status': 'État',
    'accounts.filter.all': 'Tous',
    'accounts.status.active': 'Actif',
    'accounts.status.inactive': 'Désactivé',
    'accounts.status.locked': 'Verrouillé',
    'accounts.status.must_change_password': 'Mot de passe à changer',
    'accounts.column.name': 'Nom',
    'accounts.column.population': 'Population',
    'accounts.column.roles': 'Rôles',
    'accounts.column.status': 'État',
    'accounts.column.lastLogin': 'Dernière connexion',
    'accounts.noRole': 'Aucun rôle',
    'accounts.empty': 'Aucun compte ne correspond.',
    'accounts.readOnly': 'Hors de votre portée',

    'account.email': 'Adresse électronique',
    'account.displayName': 'Nom affiché',
    'account.population': 'Population',
    'account.status': 'État',
    'account.lastLogin': 'Dernière connexion',
    'account.password': 'Mot de passe',
    'account.password.none': "Pas encore défini (en attente d'activation)",
    'account.password.provisional': 'Provisoire, à changer à la prochaine connexion',
    'account.password.set': 'Défini le {date}',
    'account.lockedUntil': "Verrouillé jusqu'à {date}",
    'account.createdAt': 'Créé le',
    'account.assignments': 'Rôles et portée',
    'account.assignments.none': "Aucun rôle : ce compte peut se connecter mais n'a accès à rien.",
    'account.assignments.hint': "Les rôles s'attribuent depuis l'écran « Rôles et attributions ».",
    'account.scope.centers': 'Centres',
    'account.scope.cycles': 'Cycles',
    'account.scope.domains': 'Domaines',
    'account.scope.products': 'Produits',
    'account.scope.allCenters': 'Tous les centres',
    'account.scope.allCycles': 'Tous les cycles',
    'account.scope.allDomains': 'Tous les domaines',
    'account.scope.allProducts': 'Tous les produits',
    'account.period': 'Période',
    'account.period.always': 'Sans limite',
    'account.period.from': 'à partir du {date}',
    'account.period.until': "jusqu'au {date}",
    'account.outOfReach': "Ce compte atteint au-delà de votre portée : vous pouvez le consulter, pas le modifier.",

    'account.action.edit': 'Modifier',
    'account.action.deactivate': 'Désactiver',
    'account.action.activate': 'Réactiver',
    'account.action.activateLocked': 'Déverrouiller',
    'account.action.assignPassword': 'Fixer un mot de passe provisoire',
    'account.action.sendLink': 'Envoyer un lien par courriel',
    'account.action.sendLink.confirm': "Envoyer à {email} un lien pour définir son mot de passe ? Il restera valable 72 heures.",
    'account.done.deactivate': 'Compte désactivé, sessions fermées.',
    'account.done.activate': 'Compte réactivé.',
    'account.done.link': 'Lien envoyé à {email}.',
    'account.done.password': 'Mot de passe provisoire enregistré. Transmettez-le au titulaire : il devra le changer à sa prochaine connexion.',
    'account.done.saved': 'Modifications enregistrées.',
    'account.done.created': 'Compte créé.',
    'account.done.createdLink': 'Compte créé. Un lien d’activation a été envoyé à {email}.',
    'account.done.createdPassword': 'Compte créé. Transmettez le mot de passe provisoire au titulaire : il devra le changer à sa première connexion.',

    'app.home': 'Accueil',
    'menu.expand': 'Ouvrir le menu',
    'menu.collapse': 'Réduire le menu',
    'menu.open': 'Afficher le menu',
    'menu.close': 'Fermer le menu',
    'common.moreActions': "Plus d'actions",
    'accounts.count': '{count} compte(s)',
    'accounts.filter.allPopulations': 'Toutes les populations',
    'accounts.filter.allStatuses': 'Tous les états',
    'accounts.column.actions': 'Actions',
    'account.action.deactivate.confirmNamed': 'Désactiver le compte de {name} ? Ses sessions ouvertes seront fermées immédiatement.',
    'account.action.sendLink.short': 'Envoyer le lien',
    'account.action.assignPassword.short': 'Mot de passe provisoire',
    'pagination.rowsPerPage': 'Lignes par page',
    'pagination.range': '{from}–{to} sur {total}',
    'pagination.first': 'Première page',
    'pagination.previous': 'Page précédente',
    'pagination.next': 'Page suivante',
    'pagination.last': 'Dernière page',
    'pagination.page': 'Page {page}',
    'create.title': 'Nouveau compte',
    'create.credential': 'Mot de passe',
    'create.credential.link': 'Envoyer un lien d’activation',
    'create.credential.linkHint': 'Le titulaire choisit son mot de passe ; personne d’autre ne le connaît. Le lien reste valable 72 heures.',
    'create.credential.password': 'Saisir un mot de passe provisoire',
    'create.credential.passwordHint': 'Vous le transmettez vous-même ; le titulaire devra le changer à sa première connexion.',
    'create.credential.required': 'Choisissez comment le titulaire obtiendra son mot de passe.',
    'create.generate': 'Générer',
    'create.submit': 'Créer le compte',
    'create.noRoleYet': 'Le compte naît sans rôle. Attribuez-lui ensuite ses rôles depuis l’écran « Rôles et attributions ».',

    'password.current': 'Mot de passe actuel',
    'password.new': 'Nouveau mot de passe',
    'password.confirm': 'Confirmer le nouveau mot de passe',
    'password.rule': 'Au moins 12 caractères. Une phrase est plus sûre qu’un mot compliqué.',
    'password.mismatch': 'Les deux saisies diffèrent.',
    'password.change.title': 'Changer mon mot de passe',
    'password.change.submit': 'Changer le mot de passe',
    'password.change.hint': 'Toutes vos sessions seront fermées, y compris celle-ci : vous vous reconnecterez avec le nouveau mot de passe.',
    'password.imposed.title': 'Choisissez votre mot de passe',
    'password.imposed.body': 'Le mot de passe que vous avez utilisé est provisoire : il a été fixé par un administrateur. Remplacez-le avant de continuer.',
    'password.provisional': 'Mot de passe provisoire',
    'password.provisional.hint': 'Au moins 12 caractères. Le titulaire devra le changer à sa prochaine connexion ; ses sessions ouvertes seront fermées.',

    'forgot.title': 'Mot de passe oublié',
    'forgot.body': 'Indiquez votre adresse : si un compte lui correspond, vous recevrez un lien pour choisir un nouveau mot de passe.',
    'forgot.submit': 'Envoyer le lien',
    'forgot.sent': 'Si un compte existe pour cette adresse, un lien vient d’y être envoyé. Il reste valable une heure.',
    'forgot.tooMany': 'Trop de demandes depuis cet appareil. Réessayez dans quelques minutes.',
    'forgot.back': 'Retour à la connexion',

    'reset.title': 'Choisir un mot de passe',
    'reset.submit': 'Enregistrer le mot de passe',
    'reset.missing': 'Ce lien est incomplet. Ouvrez-le directement depuis le courriel reçu.',
    'reset.invalid': 'Ce lien n’est plus valable : il a déjà servi, a expiré, ou un lien plus récent a été envoyé.',
    'reset.askAgain': 'Demander un nouveau lien',

    'violation.blank': 'Ce champ est obligatoire.',
    'violation.email': 'Adresse électronique invalide.',
    'violation.tooShort': 'Trop court.',
    'violation.tooLong': 'Trop long.',
    'violation.sameAsCurrent': 'Le nouveau mot de passe doit différer de l’actuel.',
    'violation.emailTaken': 'Un compte utilise déjà cette adresse.',
    'violation.currentPasswordInvalid': 'Le mot de passe actuel est incorrect.',
    'violation.tokenInvalid': 'Ce lien n’est plus valable.',
    'violation.choice': 'Valeur non reconnue.',
    'common.close': 'Fermer',
    'menu.trail': "Fil d'Ariane",
    'photo.title': 'Photo de profil',
    'photo.change': 'Changer la photo',
    'photo.drop': 'Glissez une photo ici',
    'photo.dropAnother': 'Glissez une autre photo',
    'photo.or': 'ou',
    'photo.choose': 'Choisir un fichier',
    'photo.rules': 'JPG, PNG ou WebP · 2 Mo au plus · au moins 200 × 200 px',
    'photo.crop': 'Recadrage',
    'photo.cropHint': 'Faites glisser la photo, ou utilisez les flèches du clavier, pour la placer dans le cercle.',
    'photo.zoom': 'Zoom',
    'photo.preview': 'Aperçu',
    'photo.remove': 'Retirer la photo',
    'photo.error.type': 'Ce fichier n’est pas une image JPG, PNG ou WebP lisible.',
    'photo.error.weight': 'Cette image dépasse 2 Mo.',
    'photo.error.small': 'Cette image est trop petite : au moins 200 × 200 px.',
    'photo.done.saved': 'Photo enregistrée.',
    'photo.done.removed': 'Photo retirée.',
    'profile.title': 'Mon profil',
    'profile.open': 'Mon profil',
    'profile.card': 'Profil',
    'profile.information': 'Informations',
    'profile.security': 'Sécurité',
    'profile.roles': 'Rôles',
    'profile.rolesNone': 'Aucun',
    'profile.institution': 'Établissement',
    'profile.lock': 'Verrouillage',
    'profile.lockNone': 'Aucun',
    'profile.editProfile': 'Modifier le profil',
    'profile.noRolesTitle': 'Aucun rôle attribué',
    'account.action.sendLink.tiny': 'Lien',
    'account.action.assignPassword.tiny': 'Provisoire',
    'account.action.deactivate.long': 'Désactiver le compte',
    'roles.title': "Rôles et attributions",
    'roles.subtitle': "Qui détient quel rôle, et sur quelle partie de l'école.",
    'roles.views': "Vues",
    'roles.tab.assignments': "Attributions",
    'roles.tab.roles': "Rôles",
    'roles.catalogue.note': "Les rôles sont livrés avec Kelassi et identiques dans tous les établissements : leur contenu ne se modifie pas ici. L'école choisit qui les détient et sur quelle partie de l'école, depuis l'onglet « Attributions ».",
    'roles.provided': "Fourni par Kelassi",
    'roles.holders': "{count} titulaire(s) · voir les attributions",
    'roles.grantThis': "Attribuer ce rôle",
    'assignment.new': "Attribuer un rôle",
    'assignment.edit': "Modifier l'attribution",
    'assignment.submit': "Attribuer le rôle",
    'assignment.search': "Rechercher un compte",
    'assignment.filter.role': "Rôle",
    'assignment.filter.allRoles': "Tous les rôles",
    'assignment.filter.center': "Centre",
    'assignment.filter.state': "État",
    'assignment.filter.live': "En vigueur et à venir",
    'assignment.filter.current': "En vigueur",
    'assignment.filter.upcoming': "À venir",
    'assignment.filter.expired': "Échues",
    'assignment.filter.revoked': "Retirées",
    'assignment.filter.all': "Toutes, historique compris",
    'assignment.filter.oneAccount': "Seules les attributions d'un compte sont affichées.",
    'assignment.filter.clear': "Tout afficher",
    'assignment.empty': "Aucune attribution ne correspond.",
    'assignment.column.account': "Compte",
    'assignment.column.role': "Rôle",
    'assignment.column.scope': "Portée",
    'assignment.column.grantedBy': "Attribué par",
    'assignment.state.current': "En vigueur",
    'assignment.state.upcoming': "À venir",
    'assignment.state.expired': "Échue",
    'assignment.state.revoked': "Retirée",
    'assignment.grantedBy.system': "Installation",
    'assignment.revoke': "Retirer le rôle",
    'assignment.revoke.title': "Retirer le rôle ?",
    'assignment.revoke.confirm': "{name} perd le rôle {role} immédiatement, y compris dans ses sessions ouvertes. L'attribution reste dans l'historique et pourra être réattribuée.",
    'assignment.regrant': "Réattribuer",
    'assignment.done.revoked': "Rôle {role} retiré à {name}.",
    'assignment.done.updated': "Attribution de {name} modifiée.",
    'assignment.done.granted': "Rôle {role} attribué à {name}.",
    'assignment.scope.everything': "Toute l'école",
    'assignment.self': "Votre propre attribution : un autre administrateur doit la modifier.",
    'assignment.you': "Vous",
    'assignment.period.revokedOn': "Retiré le {date}",
    'assignment.period.between': "Du {from} au {until}",
    'assignment.period.from': "À partir du {date}",
    'assignment.period.until': "Jusqu'au {date}",
    'permission.subject.user_account': "Comptes",
    'permission.subject.role': "Rôles",
    'permission.subject.role_assignment': "Attributions",
    'permission.subject.menu': "Menu",
    'permission.subject.identity_provider': "Annuaires",
    'permission.subject.login_policy': "Politique de connexion",
    'permission.subject.authentication_log': "Journal",
    'assignment.step.account': "Compte",
    'assignment.step.role': "Rôle",
    'assignment.step.roleHint': "Les rôles sont fournis par Kelassi ; leur contenu ne se modifie pas.",
    'assignment.step.scope': "Portée",
    'assignment.step.scopeHint': "Sur quelle partie de l'école le rôle s'exerce.",
    'assignment.step.period': "Période",
    'assignment.step.periodHint': "Facultative.",
    'assignment.account.change': "Changer",
    'assignment.account.hint': "Votre propre compte n'est pas proposé : un autre administrateur doit vous attribuer un rôle.",
    'assignment.account.search': "Rechercher par nom ou adresse",
    'assignment.account.none': "Aucun compte actif ne correspond.",
    'assignment.role.permissions': "{count} permission(s)",
    'assignment.role.unreachable': "Vous ne pouvez pas attribuer ce rôle : il porterait sur une partie de l'école que vous n'atteignez pas.",
    'assignment.scope.rule': "Pour chaque dimension : tout, ou seulement une partie. « Tous » vaut aussi pour ce qui sera créé plus tard. « Seulement certains » sans rien cocher n'est pas permis.",
    'assignment.scope.some': "Seulement certains",
    'assignment.scope.emptySome': "Cochez au moins un élément, ou choisissez « Tous ».",
    'assignment.scope.outOfReach': "hors de votre portée",
    'assignment.scope.inactive': "fermé",
    'assignment.scope.noneDefined': "Rien de défini pour l’instant ({dimension}) : le rôle vaudra pour tout ce qui sera créé.",
    'assignment.scope.ownLimit': "Votre propre portée ne va pas au-delà : vous ne pouvez pas accorder davantage.",
    'assignment.period.start': "Début",
    'assignment.period.startHint': "Vide : le rôle vaut dès l’enregistrement.",
    'assignment.period.end': "Fin",
    'assignment.period.endHint': "Une date : le rôle s’éteint seul à la fin de ce jour.",
    'assignment.summary.title': "En clair",
    'assignment.summary.incomplete': "Choisissez un compte et un rôle.",
    'assignment.summary.gives': "Ce que le rôle {role} donne",
    'assignment.summary.everythingWarning': "Portée la plus large : toute l'école, y compris les centres, cycles, domaines et produits créés plus tard.",
    'assignment.summary.manyPermissions': "exercer les {count} permissions du rôle {role}",
    'assignment.summary.everywhere': "sur toute l'école",
    'assignment.summary.on': "sur {parts}",
    'assignment.summary.always': "dès l'enregistrement et sans limite de durée",
    'assignment.summary.between': "du {from} au {until}",
    'assignment.summary.from': "à partir du {date}, sans limite de durée",
    'assignment.summary.until': "dès l'enregistrement et jusqu'au {date}",
    'assignment.summary.sentence': "{name} pourra {permissions}, {where}, {when}.",
    'violation.periodInverted': "La fin précède le début.",
    'violation.assignmentSelf': "On ne s'attribue pas un rôle à soi-même.",
    'violation.assignmentExists': "Ce compte détient déjà ce rôle : modifiez son attribution depuis la liste.",
    'common.and': "et",
    'assignment.role.alreadyHeld': "Déjà détenu par ce compte.",
    'assignment.role.editIt': "Modifier cette attribution",
    'assignment.role.revokedBefore': "Retiré auparavant : il sera réattribué.",
    'rolesMenus.title': "Rôles et menus",
    'rolesMenus.subtitle': "Chaque rôle, et le menu qu'il voit. Glissez une entrée pour la déplacer ; déposez-la sur un rôle pour la lui copier.",
    'rolesMenus.saved': "Modifications enregistrées. Elles valent dès la prochaine action de chaque compte concerné.",
    'rolesMenus.copiedTo': "« {item} » copiée vers {role} (à enregistrer).",
    'rolesMenus.collapse': "Replier",
    'rolesMenus.expand': "Déplier",
    'rolesMenus.hidden': "masquée",
    'rolesMenus.local': "locale",
    'rolesMenus.action.up': "Monter",
    'rolesMenus.action.down': "Descendre",
    'rolesMenus.action.move': "Déplacer vers…",
    'rolesMenus.action.copyTo': "Copier vers un autre rôle…",
    'rolesMenus.action.edit': "Modifier",
    'rolesMenus.action.withdraw': "Retirer de ce rôle",
    'rolesMenus.action.delete': "Supprimer",
    'rolesMenus.action.copyRole': "Copier ce rôle",
    'rolesMenus.action.editRole': "Modifier le rôle",
    'rolesMenus.action.deleteRole': "Supprimer le rôle",
    'rolesMenus.filter': "Filtrer les entrées",
    'rolesMenus.newSection': "Nouvelle section",
    'rolesMenus.newRole': "Nouveau rôle",
    'rolesMenus.tree': "Rôles et leurs menus",
    'rolesMenus.allEntries': "Menu complet",
    'rolesMenus.reserved': "Réservé",
    'rolesMenus.copyOf': "Copie de {role}",
    'rolesMenus.entries': "{count} entrée(s)",
    'rolesMenus.roleMeta': "{permissions} permission(s) · {entries} entrée(s)",
    'rolesMenus.pendingRegion': "Modifications en attente",
    'rolesMenus.pending': "{count} modification(s) non enregistrée(s)",
    'rolesMenus.pendingHint': "Elles s’appliqueront à tous les comptes qui détiennent ces rôles.",
    'rolesMenus.discard': "Tout annuler",
    'rolesMenus.labelIn': "Libellé · {language}",
    'rolesMenus.kind.section': "Section du menu",
    'rolesMenus.kind.entry': "Entrée du menu",
    'rolesMenus.icon': "Icône",
    'rolesMenus.route': "Écran (route)",
    'rolesMenus.routeHint': "Défini par le code du produit : un écran qui n'existe pas ne peut pas être choisi.",
    'rolesMenus.permission': "Permission requise",
    'rolesMenus.permission.section': "Aucune : visible dès qu’une entrée dessous l’est",
    'rolesMenus.permission.none': "Aucune : visible de tout compte connecté",
    'rolesMenus.permissionNoneHint': "Sans permission, l’entrée ne dépend d’aucun rôle.",
    'rolesMenus.permissionHint': "Un rôle voit cette entrée s’il détient « {label} ».",
    'rolesMenus.visible': "Visible dans le menu",
    'rolesMenus.viewers': "Rôles qui voient cette entrée",
    'rolesMenus.viewersNone': "Aucun rôle pour l’instant.",
    'rolesMenus.provided': "livré par Kelassi",
    'rolesMenus.localRole': "créé ici",
    'rolesMenus.holders': "{count} titulaire(s)",
    'rolesMenus.reservedHint': "Le rôle Super Administrateur ne se modifie pas ici : il s’attribue en ligne de commande, et lui seul ouvre cet écran.",
    'rolesMenus.description': "Description",
    'rolesMenus.permissions': "Permissions ({count})",
    'rolesMenus.permissionsHint': "Une permission ouvre l’écran ET les actions correspondantes, pas seulement l’entrée du menu.",
    'rolesMenus.copyRoleTitle': "Copier le rôle « {role} »",
    'rolesMenus.copyRoleSubtitle': "Le nouveau rôle part de ce contenu ; vous le modifiez ensuite.",
    'rolesMenus.createRole': "Créer le rôle",
    'rolesMenus.code': "Code technique",
    'rolesMenus.codeHint': "Unique et définitif : c’est lui que retient l’historique.",
    'rolesMenus.codeInvalid': "Minuscules, chiffres et _ ; 3 à 60 caractères, en commençant par une lettre.",
    'rolesMenus.codeTaken': "Ce code est déjà pris.",
    'rolesMenus.copyRoleNote': "Le nouveau rôle reçoit les {count} permission(s) du rôle d’origine, donc les mêmes entrées de menu. Aucun compte ne le détient : il s’attribue ensuite depuis « Rôles et attributions ».",
    'rolesMenus.copyItemTitle': "Copier « {item} » vers…",
    'rolesMenus.copyItemSubmit': "Copier vers {count} rôle(s)",
    'rolesMenus.alreadyHas': "a déjà cette entrée",
    'rolesMenus.copyItemWarning': "Ces rôles reçoivent la permission {permission}. Tous les comptes qui les détiennent verront l’entrée, et pourront faire ce que la permission permet, sur la portée de leur attribution.",
    'rolesMenus.deleteItemTitle': "Supprimer « {item} » ?",
    'rolesMenus.seenFrom': "Vu depuis le rôle {role}",
    'rolesMenus.withdrawTitle': "Retirer de ce rôle seulement",
    'rolesMenus.withdrawText': "{role} perd la permission {permission}, et avec elle tout ce qu’elle ouvre. Les autres rôles la gardent.",
    'rolesMenus.deleteTitle': "Supprimer l’entrée du menu",
    'rolesMenus.deleteText': "Elle disparaît du menu de tous les rôles. L’écran et sa permission restent ; la mise à jour suivante ne la recrée pas.",
    'rolesMenus.deleteLocalText': "La section disparaît ; ce qu’elle contenait remonte d’un niveau.",
    'rolesMenus.notYet': "Rien n’est appliqué avant « Enregistrer ».",
    'rolesMenus.moveTitle': "Déplacer « {item} »",
    'rolesMenus.moveTarget': "Vers",
    'rolesMenus.root': "Le premier niveau du menu",
    'rolesMenus.moveHint': "L’entrée se range à la fin de la section choisie ; « Monter » et « Descendre » l’ajustent ensuite.",
    'rolesMenus.newSectionSubtitle': "Une section regroupe des entrées ; vide, elle n’apparaît dans le menu de personne.",
    'rolesMenus.createSection': "Créer la section",
    'rolesMenus.deleteRoleTitle': "Supprimer le rôle « {role} » ?",
    'rolesMenus.deleteRoleBlocked': "Ce rôle a {count} titulaire(s) : retirez-le d’abord depuis « Rôles et attributions ».",
    'rolesMenus.deleteRoleText': "Le rôle disparaît. S’il a déjà été attribué par le passé, le serveur refusera : l’historique le désigne encore.",
    'journal.title': "Journal de sécurité",
    'journal.subtitle': "Qui s’est connecté, d’où, et qui a changé quoi. Rien ici ne se modifie ni ne s’efface.",
    'journal.views': "Vues du journal",
    'journal.tab.sign_ins': "Connexions",
    'journal.tab.passwords': "Mots de passe",
    'journal.tab.actions': "Actions d’administration",
    'journal.tab.actionsShort': "Actions",
    'journal.export': "Exporter (CSV)",
    'journal.export.pending': "Export…",
    'journal.export.truncated': "{total} lignes correspondent : le fichier contient les 10 000 plus récentes. Resserrez la période pour le reste.",
    'journal.search': "Compte, adresse saisie ou adresse IP",
    'journal.search.actions': "Auteur ou compte visé",
    'journal.period': "Période",
    'journal.period.day': "Dernières 24 heures",
    'journal.period.days': "{days} derniers jours",
    'journal.period.all': "Tout l’historique conservé",
    'journal.filter.outcome': "Résultat",
    'journal.filter.allOutcomes': "Tous les résultats",
    'journal.filter.success': "Réussis",
    'journal.filter.failure': "Échecs",
    'journal.filter.renewals': "Afficher les renouvellements de session",
    'journal.filter.action': "Action",
    'journal.filter.allActions': "Toutes les actions",
    'journal.filter.group.account': "Comptes (toutes)",
    'journal.filter.group.account.photo': "Photos",
    'journal.filter.group.role': "Attributions (toutes)",
    'journal.filter.actor': "Auteur",
    'journal.filter.allActors': "Tous les auteurs",
    'journal.filter.oneAccount': "Les événements d’un seul compte.",
    'journal.filter.oneTarget': "Les actions qui visent un seul compte.",
    'journal.filter.oneActor': "Les actions d’un seul auteur.",
    'journal.filter.oneIp': "Les événements de l’adresse {ip}.",
    'journal.empty': "Aucun événement pour ces critères.",
    'journal.open': "Voir le détail",
    'journal.column.date': "Date",
    'journal.column.event': "Événement",
    'journal.column.account': "Compte",
    'journal.column.ip': "Adresse IP",
    'journal.column.device': "Appareil",
    'journal.column.actor': "Auteur",
    'journal.column.action': "Action",
    'journal.column.target': "Visé",
    'journal.column.change': "Changement",
    'journal.today': "Aujourd’hui",
    'journal.yesterday': "Hier",
    'journal.noAccount': "Aucun compte",
    'journal.typed': "saisi : {email}",
    'journal.vendor': "Fournisseur",
    'journal.vendor.hidden': "Intervention du fournisseur : son identité et son adresse IP ne sont pas communiquées à l’établissement.",
    'journal.by': "par {name}",
    'journal.changes': "{count} changement(s)",
    'journal.tile.successes': "Connexions réussies · 24 h",
    'journal.tile.accounts': "{count} compte(s) différent(s)",
    'journal.tile.failures': "Échecs · 24 h",
    'journal.tile.unknown': "dont {count} sur une adresse inconnue",
    'journal.tile.locked': "Comptes verrouillés",
    'journal.tile.lockedNone': "Aucun en ce moment",
    'journal.tile.lockedUntil': "{name}, jusqu’à {time}",
    'journal.tile.links': "Liens de mot de passe · 7 j",
    'journal.tile.linksUsed': "{count} utilisé(s)",
    'journal.event.signIn': "Connexion réussie",
    'journal.event.renewed': "Session renouvelée",
    'journal.event.locked': "Compte verrouillé",
    'journal.event.reused': "Jeton rejoué : sessions coupées",
    'journal.event.failed': "Échec : {reason}",
    'journal.event.methodFailed': "{method} : {reason}",
    'journal.event.reset.requested': "Lien de mot de passe demandé",
    'journal.event.reset.sent': "Lien de mot de passe envoyé",
    'journal.event.reset.completed': "Mot de passe défini par lien",
    'journal.event.assigned': "Mot de passe provisoire fixé",
    'journal.event.changed': "Mot de passe changé",
    'journal.event.invitation': "Invitation acceptée",
    'journal.method.password': "Connexion",
    'journal.method.totp': "Code à usage unique",
    'journal.method.google': "Connexion Google",
    'journal.method.microsoft': "Connexion Microsoft",
    'journal.method.refresh_token': "Renouvellement refusé",
    'journal.method.password_change': "Changement de mot de passe",
    'journal.method.password_assigned': "Mot de passe provisoire",
    'journal.method.password_reset': "Lien de mot de passe",
    'journal.method.invitation': "Invitation",
    'journal.outcome.success': "réussi",
    'journal.outcome.bad_credentials': "mot de passe incorrect",
    'journal.outcome.unknown_account': "aucun compte",
    'journal.outcome.account_disabled': "compte désactivé",
    'journal.outcome.account_locked': "compte verrouillé",
    'journal.outcome.policy_denied': "refusé par la politique de connexion",
    'journal.outcome.provider_rejected': "refusé par le fournisseur d’identité",
    'journal.outcome.token_reused': "jeton réutilisé",
    'journal.outcome.provisioning_refused': "création de compte refusée",
    'journal.outcome.token_invalid': "lien invalide ou expiré",
    'journal.outcome.rate_limited': "trop de tentatives",
    'journal.action.account.created': "Compte créé",
    'journal.action.account.updated': "Compte modifié",
    'journal.action.account.deactivated': "Compte désactivé",
    'journal.action.account.activated': "Compte réactivé",
    'journal.action.account.password_assigned': "Mot de passe provisoire fixé",
    'journal.action.account.link_sent': "Lien envoyé",
    'journal.action.account.photo_changed': "Photo changée",
    'journal.action.account.photo_removed': "Photo retirée",
    'journal.action.role.granted': "Rôle attribué",
    'journal.action.role.assignment_updated': "Attribution modifiée",
    'journal.action.role.revoked': "Rôle retiré",
    'journal.action.roles_menus.changed': "Rôles et menus modifiés",
    'journal.field.displayName': "Nom",
    'journal.field.email': "Adresse",
    'journal.field.population': "Population",
    'journal.field.credential': "Mot de passe",
    'journal.field.sessionsClosed': "Sessions fermées",
    'journal.field.lock': "Verrou",
    'journal.field.role': "Rôle",
    'journal.field.centers': "Centres",
    'journal.field.cycles': "Cycles",
    'journal.field.domains': "Domaines",
    'journal.field.products': "Produits",
    'journal.field.startsAt': "Début",
    'journal.field.endsAt': "Fin",
    'journal.value.all': "Tous",
    'journal.value.open': "sans limite",
    'journal.value.link': "lien d’activation",
    'journal.value.password': "provisoire, saisi",
    'journal.value.lifted': "levé",
    'journal.diff.field': "Champ",
    'journal.diff.before': "Avant",
    'journal.diff.after': "Après",
    'journal.detail.reason': "Raison",
    'journal.detail.lockedUntil': "Verrouillé jusqu’à",
    'journal.detail.lockedReason': "Trop de mots de passe incorrects : le compte est verrouillé temporairement.",
    'journal.detail.by': "Par",
    'journal.detail.sessions': "Sessions fermées",
    'journal.detail.actorIp': "Adresse IP de l’auteur",
    'journal.detail.further': "Aller plus loin",
    'journal.detail.allForAccount': "Tous les événements de ce compte",
    'journal.detail.allForIp': "Tous les événements de cette adresse IP",
    'journal.detail.openAccount': "Ouvrir la fiche du compte",
    'journal.detail.allByActor': "Tout ce qu’a fait {name}",
    'journal.detail.allOnTarget': "Tout ce qui a visé ce compte",
    'journal.detail.id': "Identifiant de l’événement, à citer au support :",
    'directories.title': "Annuaires et connexion",
    'directories.subtitle': "Par où vos comptes se connectent, et les règles qui s’appliquent à chaque population.",
    'directories.section.directories': "Annuaires",
    'directories.section.directoriesHint': "Un annuaire externe permet de se connecter avec le compte Google ou Microsoft de l’établissement.",
    'directories.section.policy': "Politique de connexion",
    'directories.section.policyHint': "Une ligne par population. Elle s’applique à la prochaine connexion ou au prochain renouvellement de session.",
    'directories.passwordRule': "Le mot de passe Kelassi ne peut être retiré à une population que si un annuaire la sert : sans lui, plus personne de cette population ne pourrait se connecter. Les administrateurs de la connexion le gardent dans tous les cas.",
    'directories.type.native': "Mot de passe Kelassi",
    'directories.type.google': "Google Workspace",
    'directories.type.microsoft': "Microsoft 365",
    'directories.state.active': "Actif",
    'directories.state.disabled': "Désactivé",
    'directories.state.unconfigured': "Non configuré",
    'directories.native.text': "Toujours disponible pour tous les comptes. C’est aussi la porte de secours si un annuaire externe ne répond plus : les administrateurs la gardent toujours.",
    'directories.google.text': "Connexion avec le compte Google de l’établissement. Les comptes Kelassi doivent exister : l’annuaire n’en crée pas.",
    'directories.microsoft.text': "Connexion avec le compte Microsoft 365 de l’établissement. Même règle : un compte inconnu de Kelassi est refusé, jamais créé.",
    'directories.external.serves': "Sert : {populations}. Adresses {domains}.",
    'directories.byVendor': "Mise en service par LENSOM CORP, sur demande de l’établissement",
    'directories.details': "Voir le détail",
    'directories.stat.withPassword': "Comptes avec un mot de passe",
    'directories.stat.linked': "Comptes liés",
    'directories.stat.signIns': "Connexions · 7 jours",
    'directories.stat.neverUsed': "Jamais connectés par l’annuaire",
    'directories.detail.tenant': "Locataire (tenant)",
    'directories.detail.issuer': "Émetteur vérifié sur chaque jeton",
    'directories.detail.client': "Application",
    'directories.detail.secret': "Secret de l’application",
    'directories.detail.secretStored': "Enregistré, jamais affiché",
    'directories.detail.secretMissing': "Absent : l’annuaire ne peut pas fonctionner",
    'directories.detail.domains': "Domaines acceptés",
    'directories.detail.populations': "Populations servies",
    'directories.detail.unknown': "Compte inconnu de Kelassi",
    'directories.detail.refused': "Refusé, jamais créé automatiquement",
    'directories.detail.autoProvision': "Créé automatiquement",
    'directories.detail.log': "Connexions dans le journal de sécurité",
    'directories.column.population': "Population",
    'directories.column.accounts': "Comptes",
    'directories.column.password': "Mot de passe",
    'directories.column.directory': "Annuaire",
    'directories.column.totp': "Double auth.",
    'directories.column.idle': "Inactivité",
    'directories.password.allowed': "Autorisé",
    'directories.password.removed': "Retiré",
    'directories.password.short': "Mot de passe",
    'directories.none': "Aucun",
    'directories.idle.minutes': "{count} min",
    'directories.idle.hours': "{count} h",
    'directories.idleShort': "inactivité {idle}",
    'directories.policy.edit': "Modifier la politique : {population}",
    'directories.policy.title': "Politique de connexion · {population}",
    'directories.policy.accounts': "{count} compte(s) concerné(s)",
    'directories.policy.password': "Connexion par mot de passe Kelassi",
    'directories.policy.passwordLocked': "Aucun annuaire ne sert cette population : le mot de passe est son seul moyen de se connecter, il ne peut pas être retiré.",
    'directories.policy.passwordHint': "Servie aussi par {directories}. Sans mot de passe, ces comptes passent uniquement par l’annuaire ; les administrateurs de la connexion le gardent.",
    'directories.policy.totp': "Double authentification (code à usage unique)",
    'directories.policy.idle': "Déconnexion après inactivité",
    'directories.policy.idleHint': "Sans aucune action pendant cette durée, la session se ferme et il faut se reconnecter. Plus court sur les postes partagés.",
    'directories.policy.journal': "Le changement est inscrit au journal de sécurité.",
    'directories.policy.saved': "Politique de connexion enregistrée : {population}.",
    'violation.policyIdle': "Choisissez une durée de la liste.",
    'violation.policyPassword': "Aucun annuaire ne sert cette population : le mot de passe ne peut pas être retiré.",
    'journal.action.login_policy.changed': "Politique de connexion modifiée",
    'journal.field.passwordAllowed': "Mot de passe Kelassi",
    'journal.field.sessionIdleMinutes': "Inactivité",
    'journal.value.allowed': "autorisé",
    'journal.value.removed': "retiré",
    'journal.filter.group.login_policy': "Politique de connexion",
    'directories.totp.none': "Non exigée",
    'directories.totp.email_or_app': "Courriel ou application",
    'directories.totp.app': "Application seule",
    'directories.recommended': "recommandé",
    'directories.policy.mode.none': "Non exigée",
    'directories.policy.mode.none.hint': "Chacun peut l’activer pour lui-même depuis son profil.",
    'directories.policy.mode.email_or_app': "Exigée : courriel ou application",
    'directories.policy.mode.email_or_app.hint': "Un code par courriel à chaque connexion ; ceux qui ont activé une application l’utilisent à la place.",
    'directories.policy.mode.app': "Exigée : application uniquement",
    'directories.policy.mode.app.hint': "Plus sûr. Chaque compte devra activer une application à sa prochaine connexion (après un dernier code par courriel).",
    'twoFactor.progress': "Étapes de la connexion",
    'twoFactor.step.password': "Mot de passe",
    'twoFactor.step.code': "Code",
    'twoFactor.code': "Code à 6 chiffres",
    'twoFactor.email.title': "Vérifiez votre identité",
    'twoFactor.email.body': "Nous avons envoyé un code à 6 chiffres à {email}. Il est valable 10 minutes.",
    'twoFactor.email.notSent': "Recevez un code à 6 chiffres par courriel pour terminer la connexion.",
    'twoFactor.email.send': "Recevoir un code par courriel",
    'twoFactor.app.title': "Code de votre application",
    'twoFactor.app.body': "Ouvrez Google Authenticator ou Microsoft Authenticator et saisissez le code affiché pour Kelassi.",
    'twoFactor.recovery.title': "Code de secours",
    'twoFactor.recovery.body': "Saisissez l’un des codes de secours notés à l’activation de votre application. Chacun ne sert qu’une fois.",
    'twoFactor.recovery.label': "Code de secours",
    'twoFactor.mustEnroll': "Votre établissement exige une application d’authentification : vous l’activerez juste après ce code.",
    'twoFactor.submit': "Valider",
    'twoFactor.resendIn': "Pas reçu ? Vérifiez les indésirables, ou renvoyez dans {time}",
    'twoFactor.resend': "Renvoyer un code",
    'twoFactor.sent': "Nouveau code envoyé à {email}.",
    'twoFactor.useApp': "Utiliser plutôt mon application d’authentification",
    'twoFactor.useEmail': "Recevoir un code par courriel à la place",
    'twoFactor.useRecovery': "Utiliser un code de secours",
    'twoFactor.back': "Revenir à la connexion",
    'twoFactor.invalid': "Code incorrect ou expiré.",
    'twoFactor.invalidLeft': "Code incorrect ou expiré. Il reste {count} essai(s).",
    'twoFactor.expired': "La vérification a expiré ou a échoué trop de fois. Reconnectez-vous.",
    'twoFactor.tooSoon': "Patientez avant de demander un nouveau code.",
    'twoFactor.tooMany': "Trop de codes envoyés : recommencez la connexion.",
    'twoFactor.policy.none': "Votre établissement ne l’exige pas pour votre population : elle protège votre compte si votre mot de passe fuit.",
    'twoFactor.policy.email_or_app': "Exigée par votre établissement : un code par courriel à chaque connexion, ou votre application si vous en activez une.",
    'twoFactor.policy.app': "Exigée par votre établissement : une application d’authentification.",
    'twoFactor.profile.title': "Double authentification",
    'twoFactor.profile.on': "Active",
    'twoFactor.profile.offShort': "Inactive",
    'twoFactor.profile.app': "Application d’authentification activée le {date}.",
    'twoFactor.profile.email': "Un code par courriel à chaque connexion.",
    'twoFactor.profile.mustEnroll': "Activez une application d’authentification.",
    'twoFactor.profile.off': "Le mot de passe seul suffit pour l’instant.",
    'twoFactor.profile.enable': "Activer une application d’authentification",
    'twoFactor.profile.codesLeft': "{count} code(s) de secours restant(s)",
    'twoFactor.profile.renew': "Renouveler les codes de secours",
    'twoFactor.profile.remove': "Retirer l’application",
    'twoFactor.profile.removed': "Application d’authentification retirée.",
    'twoFactor.profile.confirmRemove': "Saisissez un code de votre application pour la retirer.",
    'twoFactor.profile.confirmRenew': "Saisissez un code de votre application : les anciens codes de secours cesseront de fonctionner.",
    'twoFactor.profile.emailToggle': "Recevoir un code par courriel à chaque connexion",
    'twoFactor.profile.emailToggleHint': "Sans application : un code est envoyé à votre adresse après le mot de passe.",
    'twoFactor.setup.install': "Installez l’application",
    'twoFactor.setup.installHint': "Google Authenticator ou Microsoft Authenticator, sur votre téléphone.",
    'twoFactor.setup.scan': "Scannez ce code",
    'twoFactor.setup.qrAlt': "Code QR à scanner avec l’application",
    'twoFactor.setup.key': "Impossible de scanner ? Saisissez cette clé :",
    'twoFactor.setup.copy': "Copier la clé",
    'twoFactor.setup.code': "Saisissez le code affiché",
    'twoFactor.setup.activate': "Activer l’application",
    'twoFactor.codes.title': "Vos codes de secours",
    'twoFactor.codes.body': "Affichés une seule fois. Chacun remplace un code de l’application si vous perdez votre téléphone, et ne sert qu’une fois.",
    'twoFactor.codes.download': "Télécharger (.txt)",
    'twoFactor.codes.copy': "Copier",
    'twoFactor.codes.warning': "Gardez-les hors de votre téléphone. En dernier recours, un administrateur peut réinitialiser votre double authentification.",
    'twoFactor.codes.done': "J’ai noté mes codes",
    'twoFactor.codes.fileHeader': "Kelassi : codes de secours de la double authentification. Chacun ne sert qu’une fois.",
    'twoFactor.forced.title': "Activez votre application d’authentification",
    'twoFactor.forced.body': "Votre établissement l’exige pour votre population. C’est l’affaire de deux minutes, et il ne vous sera plus envoyé de code par courriel.",
    'account.twoFactor': "Double authentification",
    'account.twoFactor.app': "Application d’authentification",
    'account.twoFactor.none': "Pas d’application",
    'account.action.resetTwoFactor': "Réinitialiser la double authentification",
    'account.action.resetTwoFactor.short': "Réinitialiser",
    'account.action.resetTwoFactor.confirm': "L’application d’authentification et les codes de secours de {name} seront retirés. Selon sa politique, il recevra un code par courriel puis devra réactiver une application.",
    'account.done.twoFactorReset': "Double authentification réinitialisée.",
    'journal.method.second_factor': "Code de vérification",
    'journal.event.challenged': "Mot de passe juste, code demandé",
    'journal.event.codeFailed': "Échec : code de vérification incorrect",
    'journal.action.account.two_factor_app_enabled': "Application d’authentification activée",
    'journal.action.account.two_factor_app_disabled': "Application d’authentification retirée",
    'journal.action.account.two_factor_email': "Code par courriel choisi",
    'journal.action.account.recovery_codes_renewed': "Codes de secours renouvelés",
    'journal.action.account.two_factor_reset': "Double authentification réinitialisée",
    'journal.field.twoFactor': "Double authentification",
    'journal.field.email2fa': "Code par courriel",
    'journal.value.on': "activé",
    'journal.value.off': "désactivé",
    'violation.twoFactorInvalid': "Code incorrect ou expiré.",
    'home.title': "Accueil",
    'home.hello': "Bonjour {name}",
    'home.soon': "Votre espace s’enrichira au fil des mises en service : emploi du temps, notes, documents. Pour l’instant, vous pouvez gérer votre profil et la sécurité de votre compte.",
    'home.tile.accounts': "Comptes actifs",
    'home.tile.recent': "+{count} cette semaine",
    'home.todo': "À faire",
    'home.todo.dismiss': "Ne plus afficher",
    'home.todo.locked': "{count} compte(s) verrouillé(s) après des échecs de connexion",
    'home.todo.locked.action': "Voir",
    'home.todo.provisional': "{count} compte(s) n’ont pas encore changé leur mot de passe provisoire",
    'home.todo.provisional.action': "Voir la liste",
    'home.todo.links': "{count} lien(s) d’activation expirent dans moins de 24 h",
    'home.todo.links.action': "Voir les comptes",
    'home.todo.two_factor': "La double authentification n’est exigée pour aucune population",
    'home.todo.two_factor.action': "Régler la politique",
    'home.activity': "Activité récente",
    'home.activity.empty': "Aucune action d’administration ces 30 derniers jours.",
    'home.activity.all': "Tout le journal de sécurité",
    'home.shortcuts': "Raccourcis",
    'home.myAccount': "Mon compte",
    'home.shortcut.createAccount': "Créer un compte",
    'home.shortcut.createAccount.hint': "Lien d’activation ou mot de passe provisoire",
    'home.shortcut.assign': "Attribuer un rôle",
    'home.shortcut.assign.hint': "Sur tout ou partie de l’école",
    'home.shortcut.journal': "Journal de sécurité",
    'home.shortcut.journal.hint': "Connexions et actions",
    'home.shortcut.directories': "Annuaires et connexion",
    'home.shortcut.directories.hint': "Politique par population",
    'home.shortcut.profile': "Mon profil",
    'home.shortcut.profile.hint': "Photo, informations, double authentification",
    'home.shortcut.password': "Changer mon mot de passe",
    'home.shortcut.password.hint': "Toutes vos sessions seront fermées",
    'home.security': "Ma sécurité",
    'home.security.previous': "Connexion précédente",
    'home.security.firstTime': "Première connexion",
    'home.security.passwordSet': "changé le {date}",
    'home.security.twoFactor.app': "Application d’authentification",
    'home.security.twoFactor.email': "Code par courriel",
    'home.security.twoFactor.none': "Non activée · recommandée",
    'home.security.manage': "Gérer ma sécurité",
    'home.security.enable': "Activer la double authentification",
    'signIn.refusedLong': "Adresse ou mot de passe incorrect. Après 10 échecs, le compte se verrouille 15 minutes.",
    'signIn.unreachableLong': "Le service ne répond pas pour l’instant. Réessayez dans un moment ; vos données ne sont pas perdues.",
    'session.expiredLong': "Votre session a expiré après une période d’inactivité. Reconnectez-vous pour continuer.",
    'auth.subtitle': "Votre espace numérique",
    'auth.tagline': "Scolarité, notes et administration\u00a0: toute l’école, au même endroit.",
    'auth.vendor': "Kelassi · une solution LENSOM CORP",
    'auth.never': "Kelassi ne vous demandera jamais votre mot de passe par courriel.",
    'auth.showPassword': "Afficher le mot de passe",
    'auth.hidePassword': "Masquer le mot de passe",
    'auth.rule.length': "Au moins 12 caractères ({count} saisi(s))",
    'auth.rule.sentence': "Une phrase est plus sûre qu’un mot compliqué",
    'forgot.sentTitle': "Vérifiez votre boîte de réception",
    'forgot.sentBody': "Si un compte existe pour {email}, un lien vient d’y être envoyé. Il reste valable une heure et ne sert qu’une fois.",
    'forgot.tip.spam': "Pensez à regarder les courriers indésirables.",
    'forgot.tip.last': "Seul le dernier lien reçu fonctionne.",
    'forgot.tip.sessions': "Choisir un nouveau mot de passe ferme toutes vos sessions.",
    'forgot.again': "Renvoyer un lien",
    'forgot.noAccess': "Vous n’avez plus accès à cette adresse ? Adressez-vous à l’administration de l’école : elle peut vous envoyer un lien ou fixer un mot de passe provisoire.",
    'reset.deadTitle': "Ce lien ne fonctionne plus",
    'reset.body': "Il remplacera votre mot de passe actuel, s’il y en a un.",
    'reset.sessions': "Toutes vos sessions ouvertes seront fermées. Vous vous reconnecterez ensuite avec ce mot de passe.",
    // D45, D46 : Référentiel › Configuration
    'common.actions': "Actions",
    'common.add': "Ajouter",
    'common.delete': "Supprimer",
    'common.edit': "Modifier",
    'ref.nav': "Configuration du référentiel",
    'ref.intro': "Ce qui structure l’offre de l’école, avant les formations elles-mêmes.",
    'ref.rulesNote': "Les règles de calcul (compensation, moyenne de cycle) sont fournies par Kelassi : on les choisit par domaine, on ne les écrit pas. Celles de l’ESCG sont reprises en attendant leur validation.",
    'ref.readOnly': "Lecture seule : la configuration se modifie avec le droit « Configurer le référentiel ».",
    'ref.section.years': "Années académiques",
    'ref.section.domains': "Cycles et domaines",
    'ref.section.structure': "Structure des formations",
    'ref.section.levels': "Niveaux et diplômes",
    'ref.section.mentions': "Mentions et lettres",
    'ref.section.pedagogy': "Pédagogie",
    'ref.saved.created': "« {label} » est créé.",
    'ref.saved.updated': "« {label} » est enregistré.",
    'ref.saved.deleted': "« {label} » est supprimé.",
    'ref.common.code': "Code",
    'ref.common.name': "Nom",
    'ref.common.label': "Libellé",
    'ref.common.none': "Aucun",
    'ref.common.choose': "Choisir…",
    'ref.common.unused': "Utilisé par aucun domaine",
    'ref.common.usedBy': "Utilisé par : {list}",
    'ref.years.sub': "Le calendrier de l’établissement, d’une rentrée à l’autre.",
    'ref.years.prepare': "Préparer une année",
    'ref.years.prepareHint': "Elle s’ouvre aux inscriptions quand vous le décidez ; l’année courante continue d’ici là.",
    'ref.years.edit': "Modifier l’année {label}",
    'ref.years.labelHint': "Forme 2026-2027.",
    'ref.years.empty': "Aucune année académique. Préparez la première.",
    'ref.years.neverClosed': "Aucune année n’a jamais été clôturée. Une année clôturée ne reçoit plus ni inscription ni note ; les délibérations publiées restent consultables.",
    'ref.years.column.year': "Année",
    'ref.years.column.start': "Début",
    'ref.years.column.end': "Fin",
    'ref.years.column.state': "État",
    'ref.years.state.current': "Année courante",
    'ref.years.state.preparing': "En préparation",
    'ref.years.state.open': "Ouverte",
    'ref.years.state.closed': "Clôturée",
    'ref.years.open': "Ouvrir les inscriptions",
    'ref.years.makeCurrent': "Rendre courante",
    'ref.years.close': "Clôturer…",
    'ref.years.readOnly': "Lecture seule",
    'ref.years.closeTitle': "Clôturer l’année {label} ?",
    'ref.years.closeText': "C’est définitif : l’année ne recevra plus ni inscription, ni note, ni délibération. Ce qui a été publié reste consultable. L’action est journalisée.",
    'ref.years.done.open': "L’année {label} est ouverte.",
    'ref.years.done.set_current': "{label} est désormais l’année courante.",
    'ref.years.done.delete': "L’année {label} est supprimée.",
    'ref.years.done.close': "L’année {label} est clôturée.",
    'ref.years.fact.current': "Une seule année courante",
    'ref.years.fact.currentText': "Celle que les écrans proposent par défaut. La suivante peut être préparée sans attendre.",
    'ref.years.fact.centers': "Commune à tous les centres",
    'ref.years.fact.centersText': "Le calendrier propre à un centre viendra avec les sessions.",
    'ref.years.fact.closing': "Clôture définitive",
    'ref.years.fact.closingText': "Réservée à qui détient le droit, journalisée, et précédée d’une confirmation.",
    'ref.domains.sub': "Un cycle a une durée et délivre un diplôme ; un domaine porte les règles d’une famille de formations.",
    'ref.cycles.add': "Ajouter un cycle",
    'ref.cycles.edit': "Modifier le cycle",
    'ref.cycles.empty': "Aucun cycle. Ajoutez-en un pour y ranger des domaines.",
    'ref.cycles.duration': "Durée : {count} an(s)",
    'ref.cycles.durationLabel': "Durée (années)",
    'ref.cycles.diploma': "Diplôme délivré :",
    'ref.cycles.diplomaMissing': "à préciser",
    'ref.cycles.diplomaLabel': "Diplôme délivré",
    'ref.domains.add': "Ajouter un domaine",
    'ref.domains.edit': "Modifier le domaine",
    'ref.domains.editTitle': "Domaine {code}",
    'ref.domains.dialogHint': "Les règles valent pour toutes les formations du domaine ; une formation pourra choisir une autre compensation.",
    'ref.domains.noCycle': "Domaines sans cycle",
    'ref.domains.cycle': "Cycle",
    'ref.domains.notation': "Notation",
    'ref.domains.notationHint': "Les deux : la saisie accepte 14,25 comme B+, le relevé affiche les deux.",
    'ref.domains.letterScale': "Barème de lettres",
    'ref.domains.mentions': "Barème de mentions",
    'ref.domains.noMentions': "Aucun barème",
    'ref.domains.cycleAverage': "Moyenne de cycle",
    'ref.domains.vendorRule': "Règle fournie par Kelassi.",
    'ref.domains.weighting': "Pondération des moyennes",
    'ref.domains.weightingHint': "Par coefficients comme aujourd’hui, ou par crédits.",
    'ref.domains.compensation': "Compensation par défaut",
    'ref.domains.compensationHint': "Chaque formation peut en choisir une autre.",
    'ref.domains.retainedSession': "Session retenue",
    'ref.domains.retainedSessionHint': "Quand un étudiant repasse une épreuve.",
    'ref.domains.creditsEarned': "Crédits acquis",
    'ref.domains.structure': "Structure",
    'ref.domains.noStructure': "Gabarit à définir",
    'ref.domains.marking': "Notation et barèmes",
    'ref.domains.calculation': "Calcul",
    'ref.notation.numeric': "Notes sur 20",
    'ref.notation.letters': "Lettres",
    'ref.notation.both': "Notes sur 20 et lettres",
    'ref.weighting.coefficients': "Par coefficients",
    'ref.weighting.credits': "Par crédits",
    'ref.retainedSession.last': "La dernière note",
    'ref.retainedSession.best': "La meilleure note",
    'ref.creditsEarned.validated_or_compensated': "Si validé ou compensé",
    'ref.creditsEarned.validated_only': "Si validé seulement",
    'ref.structure.sub': "Le gabarit que suivent toutes les formations d’un domaine.",
    'ref.structure.noDomain': "Créez d’abord un domaine dans « Cycles et domaines ».",
    'ref.structure.domains': "Domaines",
    'ref.structure.template': "Gabarit de structure",
    'ref.structure.version': "Version",
    'ref.structure.since': "En vigueur depuis {year}",
    'ref.structure.none': "Pas encore de gabarit",
    'ref.structure.newVersion': "Nouvelle version…",
    'ref.structure.newVersionHint': "La version actuelle est recopiée ; elle vaudra à partir de l’année choisie.",
    'ref.structure.createVersion': "Créer la version",
    'ref.structure.validFrom': "À partir de l’année",
    'ref.structure.create': "Créer le gabarit ({year})",
    'ref.structure.saved': "Le gabarit du domaine {code} est enregistré.",
    'ref.structure.levelName': "Nom du niveau « {kind} »",
    'ref.structure.removeLevel': "Retirer ce niveau",
    'ref.structure.addLevel': "Ajouter un niveau…",
    'ref.structure.vendorNotions': "Les notions sont fournies par Kelassi ; leur nom est à vous.",
    'ref.structure.versioning': "Une formation créée dans ce domaine suit ce gabarit. Le changer crée une nouvelle version, valable à partir d’une année : les formations des années passées gardent la leur, et leurs moyennes ne bougent pas.",
    'ref.structure.flag.classGroups': "Groupes de classe",
    'ref.structure.flag.transcript': "Relevé",
    'ref.structure.flag.taught': "Enseignée",
    'ref.structure.creditsHere': "Crédits ici",
    'ref.structure.creditsEntered': "Crédits saisis ici",
    'ref.structure.creditsSum': "Σ crédits",
    'ref.structure.creditsBelow': "Sans crédits propres",
    'ref.kind.year': "Année",
    'ref.kind.semester': "Semestre",
    'ref.kind.module': "Module",
    'ref.kind.submodule': "Sous-module",
    'ref.kind.subject': "Matière",
    'ref.kind.year.hint': "Porte les inscriptions et les classes",
    'ref.kind.semester.hint': "Apparaît sur le relevé de notes",
    'ref.kind.module.hint': "Se valide ou se compense",
    'ref.kind.submodule.hint': "Facultatif : absent d’une partie des modules",
    'ref.kind.subject.hint': "Reçoit les notes et les heures",
    'ref.levels.sub': "Ce que l’on prépare, à quel niveau, et ce que l’on délivre.",
    'ref.levels.title': "Niveaux",
    'ref.levels.hint': "Une liste libre ; le cycle est facultatif.",
    'ref.levels.empty': "Aucun niveau.",
    'ref.levels.add': "Ajouter un niveau",
    'ref.levels.edit': "Niveau {code}",
    'ref.levels.cycleHint': "Facultatif.",
    'ref.diplomas.title': "Diplômes délivrés",
    'ref.diplomas.hint': "Ce que l’école délivre, et ses spécialités. Les diplômes d’accès décrivent l’étudiant, pas l’offre.",
    'ref.diplomas.empty': "Aucun diplôme délivré.",
    'ref.diplomas.access': "Diplômes d’accès",
    'ref.diplomas.add': "Ajouter un diplôme délivré",
    'ref.diplomas.addAccess': "Ajouter un diplôme d’accès",
    'ref.diplomas.edit': "Diplôme {name}",
    'ref.diplomas.grade': "Niveau",
    'ref.diplomas.specialities': "Spécialités",
    'ref.diplomas.series': "Séries",
    'ref.diplomas.optionsHint': "Une par ligne. Une option qui reste garde son identifiant.",
    'ref.diplomas.noSpeciality': "Aucune spécialité",
    'ref.diplomas.noSeries': "Aucune série",
    'ref.grade.bac': "Bac",
    'ref.grade.bac_plus_2': "Bac + 2",
    'ref.grade.bac_plus_3': "Bac + 3",
    'ref.grade.bac_plus_5': "Bac + 5",
    'ref.grade.doctorate': "Doctorat",
    'ref.mentions.sub': "Les barèmes qui donnent une mention et une lettre à une note ou à une moyenne.",
    'ref.mentions.add': "Nouveau barème de mentions",
    'ref.mentions.edit': "Barème {name}",
    'ref.mentions.empty': "Aucun barème de mentions.",
    'ref.mentions.dialogHint': "Les tranches se touchent sans se chevaucher : le haut de l’une est le bas de la suivante.",
    'ref.mentions.bands': "Tranches",
    'ref.mentions.mention': "Mention",
    'ref.mentions.from': "À partir de",
    'ref.mentions.to': "Jusqu’à",
    'ref.mentions.addBand': "Ajouter une tranche",
    'ref.mentions.range': "{min} ≤ moy. < {max}",
    'ref.mentions.default.pass': "Passable",
    'ref.mentions.default.fair': "Assez bien",
    'ref.mentions.default.good': "Bien",
    'ref.mentions.default.veryGood': "Très bien",
    'ref.letters.add': "Nouveau barème de lettres",
    'ref.letters.edit': "Barème {name}",
    'ref.letters.empty': "Aucun barème de lettres.",
    'ref.letters.title': "Barème de lettres · {name}",
    'ref.letters.americanName': "Américain, GPA sur 4,0",
    'ref.letters.presetHint': "Il est créé avec les lettres américaines (A+ à F, GPA sur 4,0) ; vous ajusterez les plages ensuite.",
    'ref.letters.dialogHint': "Chaque lettre vaut à partir de sa note ; la dernière commence à 0.",
    'ref.letters.letter': "Lettre",
    'ref.letters.range': "Plage sur 20",
    'ref.letters.points': "Points (sur {max})",
    'ref.letters.pointsShort': "Points",
    'ref.letters.result': "Résultat",
    'ref.letters.pass': "Réussite",
    'ref.letters.fail': "Échec",
    'ref.letters.from': "{min} ≤ note < {max}",
    'ref.letters.fromScore': "À partir de",
    'ref.letters.maxPoints': "Points au maximum",
    'ref.letters.grades': "Lettres",
    'ref.letters.addGrade': "Ajouter une lettre",
    'ref.letters.howTitle': "Comment c’est utilisé",
    'ref.letters.howText': "L’enseignant saisit une note sur 20 ou une lettre : les deux sont acceptées. Une lettre saisie vaut le bas de sa plage pour le calcul sur 20, et ses points pour le GPA.",
    'ref.letters.probe': "Essayer une note sur 20",
    'ref.letters.pointsValue': "· {points} points",
    'ref.letters.averages': "Les moyennes se calculent sur 20 et en GPA, pondérées par crédits ou coefficients selon le domaine. Le relevé affiche les deux.",
    'ref.pedagogy.sub': "Les listes que les formations, les notes et l’emploi du temps utilisent.",
    'ref.pedagogy.teaching_type': "Types d’enseignement",
    'ref.pedagogy.teaching_type.hint': "Colonnes des volumes horaires et de l’emploi du temps",
    'ref.pedagogy.assessment_type': "Types d’évaluation",
    'ref.pedagogy.assessment_type.hint': "Ce que l’on note dans une matière",
    'ref.pedagogy.responsible_type': "Types de responsable",
    'ref.pedagogy.responsible_type.hint': "Qui encadre un produit, et ce qu’il peut faire",
    'ref.pedagogy.disciplinary_field': "Champs disciplinaires",
    'ref.pedagogy.disciplinary_field.hint': "Regroupent les matières et les enseignants",
    'ref.pedagogy.department': "Départements",
    'ref.pedagogy.department.hint': "Rattachement administratif",
    'ref.pedagogy.empty': "Liste vide.",
    'ref.pedagogy.addTo': "Ajouter : {list}",
    'ref.pedagogy.editIn': "Modifier : {list}",
    'ref.pedagogy.color': "Couleur",
    'ref.pedagogy.rights': "Ce qu’il peut faire",
    'ref.pedagogy.rightsHint': "Appliqué avec la saisie des notes et les délibérations.",
    'ref.pedagogy.rights.canEnterMarks': "Saisir les notes",
    'ref.pedagogy.rights.canValidateMarks': "Valider les notes",
    'ref.pedagogy.rights.canDeliberate': "Délibérer",
    'ref.pedagogy.rights.enter': "saisit les notes",
    'ref.pedagogy.rights.validate': "valide les notes",
    'ref.pedagogy.rights.deliberate': "délibère",
    'ref.pedagogy.vendor': "Fournis par Kelassi",
    'ref.pedagogy.vendorHint': "Non modifiables ici.",
    'ref.pedagogy.vendor.notation': "Notation : notes sur 20, lettres, les deux",
    'ref.pedagogy.vendor.sessions': "États d’une session : en attente, confirmée, annulée",
    'ref.pedagogy.vendor.compensation': "Règles de compensation : {count}",
    'ref.pedagogy.vendor.cycleAverage': "Règles de moyenne de cycle : {count}",
    'journal.action.referential.created': "Référentiel : ajout",
    'journal.action.referential.updated': "Référentiel : modification",
    'journal.action.referential.deleted': "Référentiel : suppression",
    'journal.action.referential.year_current': "Année courante changée",
    'journal.action.referential.year_closed': "Année académique clôturée",
    'journal.filter.group.referential': "Référentiel",
    'ref.target.academic_year': "Année",
    'ref.target.cycle': "Cycle",
    'ref.target.domain': "Domaine",
    'ref.target.structure': "Gabarit",
    'ref.target.level': "Niveau",
    'ref.target.diploma': "Diplôme",
    'ref.target.mention_scale': "Barème de mentions",
    'ref.target.letter_scale': "Barème de lettres",
    'ref.target.teaching_type': "Type d’enseignement",
    'ref.target.assessment_type': "Type d’évaluation",
    'ref.target.responsible_type': "Type de responsable",
    'ref.target.disciplinary_field': "Champ disciplinaire",
    'ref.target.department': "Département",
    'violation.ref.required': "Ce champ est obligatoire.",
    'violation.ref.too_long': "Trop long.",
    'violation.ref.invalid': "Valeur non reconnue.",
    'violation.ref.out_of_range': "Hors des limites permises.",
    'violation.ref.invalid_date': "Date invalide.",
    'violation.ref.year_closed': "Cette année est clôturée : plus rien ne s’y modifie.",
    'violation.ref.year_not_preparing': "Possible seulement pour une année en préparation.",
    'violation.ref.in_use': "Cet élément est utilisé ailleurs : retirez d’abord ce qui s’y rattache.",
    'violation.ref.year_label': "Forme attendue : 2026-2027.",
    'violation.ref.year_dates': "La fin doit suivre le début.",
    'violation.ref.duplicate': "Déjà utilisé.",
    'violation.ref.letter_scale_required': "Une notation par lettres demande un barème de lettres.",
    'violation.ref.structure_order': "Les niveaux suivent l’ordre Année, Semestre, Module, Sous-module, Matière.",
    'violation.ref.credits_once': "Les crédits se saisissent à un seul niveau.",
    'violation.ref.taught_required': "Un niveau au moins reçoit les notes (enseignée).",
    'violation.ref.last_structure': "Un domaine garde au moins un gabarit.",
    'violation.ref.band_order': "Le haut de la tranche doit dépasser le bas.",
    'violation.ref.band_gap': "Les tranches se touchent sans trou ni recouvrement.",
    'violation.ref.letters_from_zero': "La dernière lettre doit commencer à 0 : toute note a une lettre.",
    'violation.ref.letters_points': "Une lettre ne vaut pas plus de points qu’une lettre au-dessus.",
    'violation.ref.not_found': "Élément introuvable.",
    'violation.ref.assigned': "Des attributions de rôle portent sur cet élément : retirez-les d’abord.",
    'ref.rule.all_years': "Moyenne avec toutes les années du cycle",
    'ref.rule.last_year': "Moyenne avec la dernière année du cycle",
    'ref.rule.cycle_normal_semester_2020': "Validation semestrielle cycle normal, édition 2020",
    'ref.rule.bachelor_2020': "Validation du Bachelor, 2020",
    'ref.rule.cycle_normal_semester_2020_without_submodule': "Validation du semestre, hors sous-module",
    'ref.rule.continuing_education_2021': "Formation continue 2021",
    'ref.rule.cycle_normal_semester_2021_submodules': "Cycle normal avec sous-matières 2021",
    'ref.rule.master_2022': "Master, Master spécialisé 2022",
    'journal.action.catalog.created': "Formations : ajout",
    'journal.action.catalog.updated': "Formations : modification",
    'journal.action.catalog.deleted': "Formations : suppression",
    'journal.action.catalog.renewed': "Formations reconduites",
    'journal.filter.group.catalog': "Formations",
    'cat.target.training': "Formation",
    'cat.target.element': "Élément",
    'cat.target.track': "Parcours",
    'cat.target.equivalence': "Correspondance",
    'cat.journal.elements': "Éléments",
    'cat.journal.source': "Équivalent",
    'cat.journal.linked': "Liens créés",
    'cat.journal.from': "Reconduite depuis",
    'cat.model.metaHours': "{value} h",
    // D47 : le catalogue des formations.
    'cat.title': "Formations",
    'cat.sub': "L’offre de l’école pour une année : chaque formation suit le gabarit de son domaine.",
    'cat.tabs': "Onglets de la formation",
    'cat.backToList': "Formations {year}",
    'cat.year.current': "courante",
    'cat.filter.year': "Année",
    'cat.filter.domain': "Domaine",
    'cat.filter.allDomains': "Tous les domaines",
    'cat.filter.search': "Code ou libellé",
    'cat.summary': "{count} formation(s) · {hours} heures",
    'cat.empty': "Aucune formation pour cette année.",
    'cat.emptyTraining': "Maquette vide",
    'cat.yearClosed': "Cette année est clôturée : ses formations se consultent, elles ne se modifient plus.",
    'cat.readOnly': "Lecture seule : cette formation est hors de votre portée, ou son année est clôturée.",
    'cat.none': "Aucun",
    'cat.notDiploma': "Non diplômante",
    'cat.unlimited': "Sans limite",
    'cat.levelChip': "Niveau {level}",
    'cat.tracksChip': "Parcours : {count}",
    'cat.delivers': "Diplômante · {diploma}",
    'cat.dates': "du {from} au {to}",
    'cat.templateSince': "gabarit en vigueur depuis {year}",
    'cat.noTemplate': "aucun gabarit pour ce domaine",
    'cat.internship': "stage",
    'cat.optional': "facultatif",
    'cat.weighting.credits': "pondération par crédits",
    'cat.weighting.coefficients': "pondération par coefficients",
    'cat.count.semester': "Semestres : {count}",
    'cat.count.module': "Modules : {count}",
    'cat.count.submodule': "Sous-modules : {count}",
    'cat.count.subject': "Matières : {count}",
    'cat.stat.hours': "heures",
    'cat.stat.credits': "crédits",
    'cat.stat.modules': "modules à coefficient",
    'cat.stat.enrolment': "inscriptions",
    'cat.enrolment.open': "Inscriptions ouvertes",
    'cat.enrolment.closed': "Inscriptions fermées",
    'cat.state.confirmed': "Confirmée",
    'cat.state.pending': "En attente",
    'cat.state.cancelled': "Annulée",
    'cat.training.create': "Nouvelle formation",
    'cat.training.createIn': "Nouvelle formation dans ce domaine",
    'cat.training.new': "Nouvelle formation en {year}",
    'cat.training.newHint': "Elle suivra le gabarit de son domaine ; sa maquette se construit ensuite.",
    'cat.training.edit': "Modifier « {name} »",
    'cat.training.deleteTitle': "Supprimer « {name} » ?",
    'cat.training.deleteText': "La formation et ses éléments ({count}) disparaissent, avec leurs heures, responsables et parcours. C’est définitif.",
    'cat.training.deleted': "« {name} » est supprimée.",
    'cat.field.domain': "Domaine",
    'cat.field.level': "Niveau",
    'cat.field.code': "Code",
    'cat.field.codeHint': "Unique dans l’année ; la reconduction le garde.",
    'cat.field.codeElementHint': "Unique dans l’année, comme L2S3M6.",
    'cat.field.name': "Libellé",
    'cat.field.kind': "Notion",
    'cat.field.diploma': "Diplôme délivré",
    'cat.field.diplomaHint': "Laisser vide pour une année qui ne délivre pas de diplôme.",
    'cat.field.startsOn': "Début",
    'cat.field.endsOn': "Fin",
    'cat.field.capacityMax': "Places",
    'cat.field.capacityHint': "Vide : sans limite.",
    'cat.field.state': "État",
    'cat.field.enrollable': "Inscriptions ouvertes",
    'cat.field.enrollableHint': "Les étudiants peuvent y être inscrits.",
    'cat.field.description': "Description",
    'cat.field.objectives': "Objectifs",
    'cat.field.abbreviation': "Abréviation",
    'cat.field.abbreviationHint': "Sur le relevé et les grilles.",
    'cat.field.disciplinaryField': "Champ disciplinaire",
    'cat.field.department': "Département",
    'cat.field.validation': "Seuil de validation",
    'cat.field.resit': "Seuil de rattrapage",
    'cat.field.resitHint': "En dessous : pas de rattrapage.",
    'cat.field.compensation': "Règle de compensation",
    'cat.field.compensationHint': "Fournie par Kelassi ; celle du domaine s’applique par défaut.",
    'cat.field.compensationDefault': "Celle du domaine ({rule})",
    'cat.flag.mandatory': "Obligatoire",
    'cat.flag.mandatoryHint': "Compte dans la moyenne de tous.",
    'cat.flag.bonus': "Bonus",
    'cat.flag.bonusHint': "Ne compte que s’il relève la moyenne.",
    'cat.flag.internship': "Stage",
    'cat.flag.internshipHint': "Un stage plutôt qu’un enseignement.",
    'cat.tab.model': "Maquette",
    'cat.tab.tracks': "Parcours",
    'cat.tab.equivalences': "Correspondances",
    'cat.tab.info': "Informations",
    'cat.tab.elements': "éléments : {count}",
    'cat.col.element': "Élément",
    'cat.col.coef': "Coef.",
    'cat.col.credits': "Crédits",
    'cat.col.responsibles': "Responsables",
    'cat.model.search': "Chercher un élément",
    'cat.model.collapse': "Tout replier",
    'cat.model.expand': "Tout déplier",
    'cat.model.expandOne': "Déplier « {name} »",
    'cat.model.collapseOne': "Replier « {name} »",
    'cat.model.bulk': "Saisie en masse",
    'cat.model.where': "Crédits sur « {credits} » · heures et responsables sur « {taught} »",
    'cat.model.addUnder': "Ajouter : {kind}",
    'cat.model.addIn': "Dans « {name} »",
    'cat.model.up': "Monter",
    'cat.model.down': "Descendre",
    'cat.model.empty': "La maquette est vide : commencez par ajouter un premier niveau.",
    'cat.model.noMatch': "Aucun élément ne correspond.",
    'cat.model.hidden': "{count} replié(s)",
    'cat.model.metaCredits': "{value} crédits",
    'cat.model.noResponsible': "Aucun responsable",
    'cat.model.total': "Total de la formation",
    'cat.model.totalPeople': "Matières : {subjects} · enseignants : {teachers}",
    'cat.model.creditGap': "{code} : {actual} crédits au lieu de {expected}",
    'cat.model.deleteTitle': "Supprimer « {name} » ?",
    'cat.model.deleteText': "L’élément disparaît avec ce qu’il contient ({count} élément(s) en dessous), leurs heures et leurs responsables.",
    'cat.model.deleted': "« {name} » est supprimé.",
    'cat.drawer.taught': "enseignée · reçoit les notes",
    'cat.drawer.creditsHere': "crédits saisis ici",
    'cat.drawer.identity': "Identité",
    'cat.drawer.evaluation': "Évaluation",
    'cat.drawer.hours': "Heures",
    'cat.drawer.responsibles': "Responsables",
    'cat.drawer.creditsHint': "Ils se cumulent au-dessus : semestre, année.",
    'cat.drawer.creditsSum': "Crédits : {value}, somme des éléments en dessous.",
    'cat.drawer.creditsCarried': "Les crédits ne se saisissent pas ici : ils sont portés par « {name} » ({value}).",
    'cat.drawer.coefHint': "Dans l’élément au-dessus.",
    'cat.drawer.hoursTotal': "Total : {value} h. Les colonnes viennent des types d’enseignement.",
    'cat.drawer.nobody': "Aucun responsable pour l’instant.",
    'cat.drawer.remove': "Retirer {name}",
    'cat.drawer.teacher': "Enseignant",
    'cat.drawer.type': "Rôle",
    'cat.drawer.chooseTeacher': "Choisir un enseignant",
    'cat.bulk.title': "Saisie en masse · {name}",
    'cat.bulk.trail': "Saisie en masse · {name}",
    'cat.bulk.sub': "Coefficients, crédits, heures et responsables de toute la formation, comme dans un tableur : Tab passe d’une case à l’autre.",
    'cat.bulk.back': "Retour à la maquette",
    'cat.bulk.note': "Les totaux se recalculent en direct. Un semestre doit totaliser sa part des 60 crédits de l’année : un écart est signalé, sans empêcher d’enregistrer une maquette en construction.",
    'cat.bulk.cell': "{column} de « {name} »",
    'cat.bulk.several': "{count} enseignants, voir le panneau",
    'cat.bulk.changes': "Modifications : {count}",
    'cat.bulk.discard': "Abandonner",
    'cat.bulk.save': "Enregistrer ({count})",
    'cat.tracks.none': "Cette formation n’a pas de parcours.",
    'cat.tracks.why': "Un parcours (« Gestion », « Logistique et transport ») choisit ses éléments ; l’étudiant le choisit à l’inscription.",
    'cat.tracks.add': "Ajouter un parcours",
    'cat.tracks.edit': "Modifier « {name} »",
    'cat.tracks.dialogHint': "Cochez les éléments du parcours ; un élément coché dans tous est le tronc commun.",
    'cat.tracks.option': "Spécialité du diplôme",
    'cat.tracks.comment': "Commentaire",
    'cat.tracks.elements': "Éléments retenus : {count}",
    'cat.tracks.all': "Tout cocher",
    'cat.tracks.clear': "Tout décocher",
    'cat.tracks.mode': "Statut de « {name} »",
    'cat.tracks.mandatory': "obligatoire",
    'cat.tracks.elective': "au choix",
    'cat.tracks.common': "tronc commun",
    'cat.tracks.out': "hors parcours",
    'cat.tracks.own': "Éléments propres : {count}",
    'cat.tracks.total': "Éléments : {count}",
    'cat.tracks.how': "Un étudiant choisit son parcours à l’inscription ; ses moyennes comptent les éléments de son parcours.",
    'cat.tracks.deleteTitle': "Supprimer le parcours « {name} » ?",
    'cat.tracks.deleteText': "Les éléments restent dans la maquette ; seul le parcours disparaît.",
    'cat.eq.from': "Depuis",
    'cat.eq.choose': "Choisir une formation",
    'cat.eq.chooseHint': "Choisissez la formation d’une année passée à relier.",
    'cat.eq.noSource': "Aucune formation d’une année passée.",
    'cat.eq.linksCount': "liens : {count}",
    'cat.eq.linked': "Reliés : {count}",
    'cat.eq.unlinked': "Sans correspondance : {count}",
    'cat.eq.orphans': "Non repris : {count}",
    'cat.eq.byCode': "Relier par code",
    'cat.eq.byCodeDone': "Liens créés par code : {count}.",
    'cat.eq.why': "Un étudiant inscrit « en crédit » (il repasse ce qu’il n’a pas acquis) retrouve, sur chaque élément de cette année, la meilleure de ses notes sur l’élément relié. La reconduction crée ces liens toute seule.",
    'cat.eq.thisYear': "Cette année · {year}",
    'cat.eq.thatYear': "Reprend · {year}",
    'cat.eq.how': "Correspondance",
    'cat.eq.pick': "Équivalent de « {name} »",
    'cat.eq.none': "Aucun équivalent",
    'cat.eq.status.same': "même code",
    'cat.eq.status.renamed': "même code, libellé changé",
    'cat.eq.status.manual': "choisi à la main",
    'cat.eq.status.none': "à décider",
    'cat.eq.orphansTitle': "Éléments de {year} sans équivalent cette année",
    'cat.eq.orphansHint': "Une note obtenue sur ces éléments ne sera pas reprise.",
    'cat.renew.open': "Reconduire sur {year}…",
    'cat.renew.title': "Reconduire sur {year}",
    'cat.renew.sub': "Recopie les formations choisies de {year}. Rien n’y est modifié.",
    'cat.renew.target': "Année cible",
    'cat.renew.list': "Formations de {year}",
    'cat.renew.already': " · déjà présente",
    'cat.renew.elements': "éléments : {count}",
    'cat.renew.copied': "Ce qui est recopié",
    'cat.renew.notCopied': "Ce qui ne l’est pas",
    'cat.renew.copy.model': "La maquette : semestres, modules, matières, codes",
    'cat.renew.copy.evaluation': "Coefficients, crédits, seuils, compensation",
    'cat.renew.copy.hours': "Heures par type d’enseignement",
    'cat.renew.copy.tracks': "Parcours et leurs éléments",
    'cat.renew.copy.dates': "Dates décalées d’autant que les années",
    'cat.renew.copy.responsibles': "Responsables des matières",
    'cat.renew.copy.responsiblesHint': " · à revoir : les vacataires changent souvent",
    'cat.renew.copy.not': "Inscriptions, classes, notes et délibérations",
    'cat.renew.linked': "Chaque élément recopié est relié à son original : un étudiant qui repasse en {year} une matière de {from} y retrouve sa note.",
    'cat.renew.total': "Formations : {count} · éléments créés : {elements}",
    'cat.renew.submit': "Reconduire ({count})",
    'cat.renew.done': "Formations reconduites sur {year} : {count}.",
    'cat.renew.emptyTitle': "{year} n’a encore aucune formation.",
    'cat.renew.emptyText': "« Reconduire » recopie les formations de {year} et relie chaque élément à son équivalent, pour les étudiants qui repassent une matière d’une année sur l’autre.",
    'cat.info.shape': "Maquette",
    'cat.info.session': "Dates et places",
    'cat.info.sessionHint': "Une formation a une session : ses valeurs se règlent avec « Modifier ».",
    'cat.info.none': "Non renseigné.",
    'violation.cat.kind_not_allowed': "Cette notion ne se place pas sous cet élément (gabarit du domaine).",
    'violation.cat.already_renewed': "Cette formation existe déjà dans l’année cible.",
    'violation.cat.source_year': "L’année d’origine doit précéder l’autre.",
    'violation.cat.not_in_training': "Cet élément n’appartient pas à cette formation.",
    'violation.cat.root': "Une formation se modifie depuis la liste des formations.",
    'violation.cat.capacity': "Le maximum doit dépasser le minimum.",
    // D48 : la scolarité.
    'ref.section.numbering': "Numérotation",
    'ref.numbering.sub': "Comment l'établissement numérote ses étudiants : à la main, ou selon un modèle.",
    'ref.numbering.mode': "Mode de numérotation",
    'ref.numbering.mode.manual': "Saisie manuelle",
    'ref.numbering.mode.manual.hint': "Le numéro se saisit sur la fiche. Il reste unique dans l'établissement.",
    'ref.numbering.mode.generated': "Génération automatique",
    'ref.numbering.mode.generated.hint': "Kelassi propose le numéro suivant à la création ; il reste modifiable.",
    'ref.numbering.pattern': "Modèle",
    'ref.numbering.patternHint': "Texte fixe et jetons. Un rang {N:x} est obligatoire.",
    'ref.numbering.token.AAAA': "année sur 4 chiffres",
    'ref.numbering.token.AA': "année sur 2 chiffres",
    'ref.numbering.token.CODE': "code de la formation",
    'ref.numbering.token.N': "rang dans l'année (4 chiffres)",
    'ref.numbering.example': "Exemples :",
    'ref.numbering.manualNote': "Les numéros repris du legacy suivent des formats variés (« B4/F123/25 ») : la saisie manuelle les garde tels quels.",
    'ref.numbering.foot': "Le compteur repart à 1 chaque année ; un numéro déjà pris est sauté.",
    'ref.numbering.saved': "Numérotation enregistrée.",
    'scol.year': "Année",
    'scol.year.current': "courante",
    'scol.year.closed': "clôturée",
    'scol.yearClosed': "Cette année est clôturée : ses inscriptions ne se modifient plus.",
    'scol.noNumber': "sans numéro",
    'scol.notEnrolled': "Non inscrit",
    'scol.graduated': "Lauréat",
    'scol.graduatedIn': "Lauréat {year}",
    'scol.enrolledIn': "Inscrit en {year}",
    'scol.inactiveIn': "Désactivé en {year}",
    'scol.integratedOn': "intégré le {date}",
    'scol.issuedOn': "délivrée le {date}",
    'scol.scholarship': "Boursier",
    'scol.scholarship.short': "B",
    'scol.repeating': "Redoublant",
    'scol.enrol': "Inscrire…",
    'scol.checkAll': "Tout cocher",
    'scol.checkNone': "Tout décocher",
    'scol.sex.F': "Féminin",
    'scol.sex.M': "Masculin",
    'scol.kind.personal': "personnel",
    'scol.kind.work': "professionnel",
    'scol.mode.standard': "Standard",
    'scol.mode.custom': "Personnalisée",
    'scol.mode.credit': "En crédit",
    'scol.mode.standard.hint': "Tous les éléments de la formation",
    'scol.mode.custom.hint': "Une partie des éléments, choisie ici",
    'scol.mode.credit.hint': "Repasser ce qui n'a pas été acquis",
    'scol.regime.external': "Externe",
    'scol.regime.internal': "Interne",
    'scol.enrolment.state.active': "Active",
    'scol.enrolment.state.inactive': "Désactivée",
    'scol.flag.isOfficer': "Officier",
    'scol.flag.isEmployee': "Salarié",
    'scol.flag.hasSpecialNeeds': "Besoins particuliers",
    'scol.flag.hasOutstandingIssue': "Dossier à régulariser",
    'scol.column.student': "Étudiant",
    'scol.column.trainingClass': "Formation {year} · classe",
    'scol.column.training': "Formation",
    'scol.column.mode': "Modalité",
    'scol.column.class': "Classe",
    'scol.column.state': "État",
    'scol.column.particulars': "Particularités",
    'scol.filter.training': "Formation",
    'scol.filter.allTrainings': "Toutes les formations",
    'scol.filter.state': "État",
    'scol.filter.mode': "Modalité",
    'scol.filter.allModes': "Toutes modalités",
    'scol.filter.active': "Actives",
    'scol.filter.inactive': "Désactivées",
    'scol.filter.all': "Toutes",
    'scol.students.title': "Étudiants",
    'scol.students.count': "{count} étudiant(s)",
    'scol.students.sub': "Tous les étudiants de l'école, inscrits ou non dans l'année.",
    'scol.students.new': "Nouvel étudiant",
    'scol.students.search': "Nom, numéro, CIN ou code Massar",
    'scol.students.empty': "Aucun étudiant ne répond à ces critères.",
    'scol.students.extra': "+ {count} autre(s)",
    'scol.students.state.enrolled': "Inscrits en {year}",
    'scol.students.state.inactive': "Désactivés en {year}",
    'scol.students.state.none': "Sans inscription en {year}",
    'scol.students.state.graduated': "Lauréats",
    'scol.students.state.all': "Tous les étudiants",
    'scol.backToStudents': "Étudiants",
    'scol.student.new': "Nouvel étudiant",
    'scol.student.newHint': "Le dossier d'abord ; l'inscription se fait juste après.",
    'scol.student.edit': "Modifier {name}",
    'scol.student.create': "Créer",
    'scol.student.enrolNow': "Inscrire tout de suite",
    'scol.student.enrolNowHint': "Ouvre l'assistant d'inscription après la création.",
    'scol.panel.identity': "Identité",
    'scol.panel.enrolments': "Inscriptions",
    'scol.panel.coordinates': "Coordonnées",
    'scol.panel.parents': "Parents et contacts",
    'scol.panel.diplomas': "Diplômes",
    'scol.panel.enrolment': "Inscription",
    'scol.panel.history': "Historique",
    'scol.file.noEnrolment': "Aucune inscription.",
    'scol.file.noCoordinates': "Aucune coordonnée.",
    'scol.file.noParent': "Aucun parent ni contact.",
    'scol.file.noDiploma': "Aucun diplôme.",
    'scol.field.lastName': "Nom",
    'scol.field.firstNames': "Prénoms",
    'scol.field.sex': "Sexe",
    'scol.field.birthDate': "Date de naissance",
    'scol.field.nationalId': "CIN",
    'scol.field.nationalIdIssuedOn': "CIN délivrée le",
    'scol.field.passport': "Passeport",
    'scol.field.massar': "Code Massar",
    'scol.field.number': "Numéro étudiant",
    'scol.field.integratedOn': "Intégré le",
    'scol.field.integratedOnHint': "Aujourd'hui si vide",
    'scol.field.phone': "Téléphone",
    'scol.field.phones': "Téléphones",
    'scol.field.email': "Courriel",
    'scol.field.emails': "Courriels",
    'scol.field.address': "Adresse",
    'scol.field.addresses': "Adresses",
    'scol.field.street': "Adresse",
    'scol.field.postalCode': "Code postal",
    'scol.field.city': "Ville",
    'scol.field.country': "Pays",
    'scol.field.kind': "Type",
    'scol.field.particulars': "Particularités",
    'scol.field.observations': "Observations",
    'scol.field.relationship': "Lien",
    'scol.field.occupation': "Profession",
    'scol.field.diploma': "Diplôme",
    'scol.field.speciality': "Spécialité",
    'scol.field.diplomaYear': "Année",
    'scol.field.average': "Moyenne",
    'scol.field.mention': "Mention",
    'scol.field.school': "Établissement",
    'scol.field.training': "Formation",
    'scol.field.track': "Parcours",
    'scol.field.class': "Classe",
    'scol.field.mode': "Modalité",
    'scol.field.regime': "Régime",
    'scol.field.enrolledOn': "Inscrit le",
    'scol.field.elements': "Éléments suivis",
    'scol.field.date': "Date",
    'scol.field.reason': "Motif",
    'scol.field.className': "Nom de la classe",
    'scol.number.manualHint': "Saisi à la main, unique",
    'scol.number.generatedHint': "Vide : le suivant selon {pattern}",
    'scol.number.keepHint': "Modifiable ; reste unique",
    'scol.dup.title': "Cette personne existe peut-être déjà",
    'scol.dup.born': "né(e) le {date}",
    'scol.dup.cin': "CIN {cin}",
    'scol.dup.reason.national_id': "même CIN",
    'scol.dup.reason.name_and_birth': "même nom et même date de naissance",
    'scol.dup.reason.similar_name': "nom proche",
    'scol.dup.open': "Ouvrir sa fiche",
    'scol.dup.hint': "Rien n'est bloqué : un homonyme se crée quand même.",
    'scol.add.phone': "Ajouter un téléphone",
    'scol.add.email': "Ajouter un courriel",
    'scol.add.address': "Ajouter une adresse",
    'scol.contact.add': "Ajouter un contact",
    'scol.contact.noType': "Sans lien précisé",
    'scol.contact.remove': "Retirer ce contact",
    'scol.diploma.add': "Ajouter un diplôme",
    'scol.diploma.edit': "Modifier le diplôme",
    'scol.diploma.remove': "Retirer ce diplôme",
    'scol.diploma.average': "moyenne {average}",
    'scol.diploma.mention': "mention {mention}",
    'scol.diploma.kind.access': "accès",
    'scol.diploma.kind.delivered': "délivré",
    'scol.remove.confirm': "Retirer « {label} » ?",
    'scol.saved.identity': "Identité enregistrée.",
    'scol.saved.coordinates': "Coordonnées enregistrées.",
    'scol.saved.contact': "Contact ajouté.",
    'scol.saved.diploma': "Diplôme enregistré.",
    'scol.saved.removed': "« {label} » retiré.",
    'scol.enrolments.title': "Inscriptions",
    'scol.enrolments.count': "{count} inscription(s)",
    'scol.enrolments.sub': "Qui suit quelle formation, dans l'année choisie.",
    'scol.enrolments.new': "Nouvelle inscription",
    'scol.enrolments.search': "Étudiant ou numéro",
    'scol.enrolments.empty': "Aucune inscription ne répond à ces critères.",
    'scol.stats.byTraining': "Par formation",
    'scol.stats.none': "Aucune inscription active.",
    'scol.stats.activeInactive': "{active} actives · {inactive} désactivées",
    'scol.stats.modes': "Modalités",
    'scol.stats.internal': "Internes",
    'scol.stats.scholarship': "Boursiers",
    'scol.enrolment.edit': "Modifier l'inscription",
    'scol.enrolment.delete': "Supprimer l'inscription",
    'scol.enrolment.deleteConfirm': "Supprimer cette inscription saisie par erreur ? Un abandon se déclare, il ne se supprime pas.",
    'scol.elements.all': "{count} (tous)",
    'scol.elements.some': "{count} choisis",
    'scol.event.enrolled': "Inscription",
    'scol.event.abandoned': "Abandon",
    'scol.event.resumed': "Reprise",
    'scol.event.by': "par {name}",
    'scol.abandon.title': "Déclarer un abandon",
    'scol.abandon.hint': "L'inscription est désactivée ; l'abandon s'écrit à l'historique avec sa date et son motif.",
    'scol.abandon.submit': "Déclarer l'abandon",
    'scol.resume.title': "Reprendre",
    'scol.resume.hint': "Une reprise réactive l'inscription et ses éléments. Elle s'écrit à l'historique, avec son motif.",
    'scol.resume.submit': "Reprendre l'inscription",
    'scol.done.abandoned': "Abandon déclaré.",
    'scol.done.resumed': "Inscription reprise.",
    'scol.done.deleted': "Inscription supprimée.",
    'scol.enrol.new': "Nouvelle inscription",
    'scol.enrol.edit': "Modifier l'inscription",
    'scol.enrol.sub': "Choisissez l'étudiant, la formation et les modalités.",
    'scol.enrol.step.student': "Étudiant",
    'scol.enrol.step.training': "Formation et modalités",
    'scol.enrol.step.elements': "Éléments suivis",
    'scol.enrol.pickHint': "Cherchez l'étudiant à inscrire ; créez-le s'il n'existe pas encore.",
    'scol.enrol.pickTraining': "Choisir une formation",
    'scol.enrol.already': "Cet étudiant est déjà inscrit dans cette formation.",
    'scol.enrol.noTrack': "Aucun parcours",
    'scol.enrol.classOption': "{name} ({count} inscrits)",
    'scol.enrol.checked': "{count} élément(s) cochés sur {total}",
    'scol.enrol.creditHint': "Les éléments déjà suivis sont signalés, retrouvés par les correspondances entre années. Les notes arriveront avec le lot 3 : en attendant, cochez à la main ce qui n'a pas été acquis.",
    'scol.enrol.creditNoHistory': "Aucune inscription passée de cet étudiant ne correspond à cette formation : cochez à la main.",
    'scol.enrol.followed': "suivi en {year} ({code})",
    'scol.enrol.recap': "Inscription en cours",
    'scol.enrol.onHint': "Aujourd'hui si vide",
    'scol.enrol.submit': "Inscrire",
    'scol.classes.title': "Classes",
    'scol.classes.titleOf': "Classes · {training}",
    'scol.classes.sub': "Les classes de chaque formation.",
    'scol.classes.subOf': "{count} inscrits · glisser un étudiant d'une classe à l'autre",
    'scol.classes.new': "Nouvelle classe",
    'scol.classes.edit': "Modifier la classe",
    'scol.classes.delete': "Supprimer la classe",
    'scol.classes.deleteConfirm': "Supprimer « {name} » ? Ses {count} membre(s) redeviennent non affectés.",
    'scol.classes.distribute': "Répartir les non affectés…",
    'scol.classes.distributeConfirm': "Répartir {count} étudiant(s) entre {classes} classe(s), par ordre alphabétique, en complétant d'abord les moins remplies ?",
    'scol.classes.distributeGo': "Répartir",
    'scol.classes.distributed': "{count} étudiant(s) répartis.",
    'scol.classes.unassigned': "Non affectés",
    'scol.classes.unassigned.short': "à répartir",
    'scol.classes.unassigned.later': "Plus tard",
    'scol.classes.unassign': "Retirer de la classe",
    'scol.classes.move': "Déplacer",
    'scol.classes.moveTo': "Vers {name}",
    'scol.classes.empty': "Glissez des étudiants ici.",
    'scol.classes.poolEmpty': "Tout le monde a une classe.",
    'scol.classes.delegate': "Délégué",
    'scol.classes.delegate.short': "délégué",
    'scol.classes.countDelegate': "{count} · délégué : {name}",
    'scol.classes.noTraining': "Aucune formation en {year}.",
    'scol.classes.note': "Une classe suit tous les éléments de la formation. Les groupes de TD et de TP (« groupes de cours ») arriveront avec la saisie des notes (lot 3). Un étudiant n'est que dans une classe par formation.",
    'scol.reenrol.open': "Réinscrire une promotion…",
    'scol.reenrol.title': "Réinscrire une promotion",
    'scol.reenrol.sub': "Passer les étudiants d'une formation dans celle d'une année suivante.",
    'scol.reenrol.from': "Depuis",
    'scol.reenrol.to': "Vers",
    'scol.reenrol.pickFrom': "Choisir la formation d'origine",
    'scol.reenrol.pickTo': "Choisir la formation cible",
    'scol.reenrol.pickBoth': "Choisissez la formation d'origine et la formation cible.",
    'scol.reenrol.enrolled': "{count} inscrits",
    'scol.reenrol.filter': "Filtrer",
    'scol.reenrol.empty': "Aucun inscrit actif dans cette formation.",
    'scol.reenrol.already': "déjà inscrit en {training}",
    'scol.reenrol.elsewhere': "inscrit en {training}",
    'scol.reenrol.carried': "Ce qui est repris",
    'scol.reenrol.keepRegime': "Régime (interne, externe)",
    'scol.reenrol.keepScholarship': "Bourse",
    'scol.reenrol.keepScholarshipHint': "à revoir chaque année",
    'scol.reenrol.notCarried': "Le parcours et la classe se choisissent dans la nouvelle formation.",
    'scol.reenrol.note': "Les décisions de jury arriveront avec le lot 3 : elles cocheront d'elles-mêmes les admis. En attendant, on coche à la main ; un étudiant déjà inscrit dans la cible est grisé.",
    'scol.reenrol.submit': "Réinscrire {count} étudiant(s)",
    'scol.reenrol.done': "{count} étudiant(s) réinscrit(s) en {training}.",
    'violation.scol.already_enrolled': "Cet étudiant est déjà inscrit dans cette formation.",
    'violation.scol.no_element': "Cochez au moins un élément.",
    'violation.scol.already_inactive': "Cette inscription est déjà désactivée.",
    'violation.scol.already_active': "Cette inscription est déjà active.",
    'violation.scol.not_member': "Le délégué est un membre de la classe.",
    'violation.scol.not_enrolled': "Cet étudiant n'est pas inscrit dans cette formation.",
    'violation.scol.no_class': "Créez au moins une classe.",
    'violation.scol.target_year': "La formation cible est d'une année suivante.",
    'violation.scol.invalid_pattern': "Modèle invalide : texte et jetons {AAAA}, {AA}, {CODE}, {N:4}, dont un rang {N:x}.",
    'journal.action.schooling.created': "Scolarité : ajout",
    'journal.action.schooling.updated': "Scolarité : modification",
    'journal.action.schooling.deleted': "Scolarité : suppression",
    'journal.action.schooling.abandoned': "Scolarité : abandon",
    'journal.action.schooling.resumed': "Scolarité : reprise",
    'journal.action.schooling.reenrolled': "Scolarité : réinscription",
    'scol.target.student': "Étudiant",
    'scol.target.enrolment': "Inscription",
    'scol.target.class': "Classe",
    'scol.journal.members': "Membres",
    'scol.journal.distributed': "Répartis",
    'scol.journal.count': "Nombre",
    'journal.filter.group.schooling': "Scolarité",
    // D51 : la finance.
    'fin.all': "Tous",
    'fin.active': "Actif",
    'fin.inactive': "Désactivé",
    'fin.default': "par défaut",
    'fin.amount': "Montant",
    'fin.net': "Net",
    'fin.paid': "Payé",
    'fin.due': "Reste dû",
    'fin.total': "Total",
    'fin.quantity': "Quantité",
    'fin.excludingTax': "HT",
    'fin.includingTax': "TTC",
    'fin.noTax': "Aucune taxe",
    'fin.computed': "Calculé.",
    'fin.pdf': "PDF",
    'fin.receipt': "Reçu",
    'fin.deleteConfirm': "Supprimer « {label} » ?",
    'fin.deleted': "« {label} » supprimé.",
    'fin.off.title': "Module Finance non activé",
    'fin.off.text': "Le Super Administrateur active le module et choisit ses réglages dans « Super administration › Modules ».",
    'fin.level.training': "formation entière",
    'fin.level.module': "module",
    'fin.level.subject': "matière",
    'fin.kind.training': "Formation",
    'fin.kind.year': "Année",
    'fin.kind.semester': "Semestre",
    'fin.kind.module': "Module",
    'fin.kind.submodule': "Sous-module",
    'fin.kind.subject': "Matière",
    'fin.method.cash': "Espèces",
    'fin.method.cheque': "Chèque",
    'fin.method.transfer': "Virement",
    'fin.status.late': "En retard",
    'fin.status.current': "À jour",
    'fin.status.paid': "Soldée",
    'fin.status.cancelled': "Annulée",
    'fin.installment.paid': "Payée",
    'fin.installment.partial': "Partielle",
    'fin.installment.late': "En retard",
    'fin.installment.due': "Échue",
    'fin.installment.upcoming': "À venir",
    'fin.invoice.kind.enrolment': "Inscription",
    'fin.invoice.kind.on_demand': "Service à la demande",
    'fin.service.kind.mandatory': "Obligatoire",
    'fin.service.kind.optional': "Au choix",
    'fin.service.kind.on_demand': "À la demande",
    'fin.service.kindHint.mandatory': "Ajouté d'office à la facture de chaque inscription concernée.",
    'fin.service.kindHint.optional': "Ajouté à la facture de l'inscription, à la demande.",
    'fin.service.kindHint.on_demand': "Facturé à part, quand l'étudiant le demande.",
    'fin.service.scope.all': "Tous les étudiants",
    'fin.service.scope.cycles': "Cycles",
    'fin.service.scope.levels': "Niveaux",
    'fin.discount.kind.percent': "Pourcentage",
    'fin.discount.kind.amount': "Montant fixe",
    'fin.discount.kind.free': "Montant saisi à l'octroi",
    'fin.discount.target.invoice': "La facture entière",
    'fin.discount.target.lines': "Des lignes précises",
    'fin.discount.training': "Le tarif de la formation",
    'fin.target.settings': "Réglage",
    'fin.target.tax': "Taxe",
    'fin.target.schedule': "Échéancier",
    'fin.target.service': "Service",
    'fin.target.discount': "Rabais",
    'fin.target.price': "Tarif",
    'fin.target.invoice': "Facture",
    'fin.target.payment': "Paiement",
    'fin.modules.title': "Module Finance",
    'fin.modules.sub': "Super administration › Modules · réservé au fournisseur",
    'fin.modules.list': "Modules de l'établissement",
    'fin.modules.finance': "Finance",
    'fin.modules.active': "Actif",
    'fin.modules.inactive': "Éteint",
    'fin.modules.reserved': "Seul le Super Administrateur voit cet écran. Un module éteint disparaît du menu ; ses données sont conservées.",
    'fin.modules.enabled': "Module Finance activé",
    'fin.modules.enabledHint': "Ouvre la section Finance du menu, le compte dans la fiche étudiant et les rôles « Gestionnaire des finances » et « Caissier ».",
    'fin.modules.enabledOn': "Activé le {date} par {who}. Le désactiver masque les écrans ; aucune facture ni aucun paiement n'est supprimé.",
    'fin.modules.levels': "Niveaux de tarification",
    'fin.modules.priceTraining': "Formation entière",
    'fin.modules.priceTrainingHint': "Un tarif par formation et par année, facturé à l'inscription standard.",
    'fin.modules.priceModule': "Module",
    'fin.modules.priceModuleHint': "Un tarif par module, si l'établissement vend des modules à l'unité.",
    'fin.modules.priceSubject': "Matière",
    'fin.modules.priceSubjectHint': "Un tarif par matière, facturé aux inscriptions personnalisées et en crédit selon les matières choisies.",
    'fin.modules.enrolmentMode': "Type d'inscription",
    'fin.modules.billed': "Montant facturé",
    'fin.modules.billedTraining': "Tarif de la formation",
    'fin.modules.billedAll': "Somme des tarifs de ses éléments",
    'fin.modules.billedChosen': "Somme des tarifs des éléments choisis",
    'fin.modules.billedCredit': "Somme des tarifs des éléments à repasser",
    'fin.modules.missingPrice': "Plus les services obligatoires. Une formation sans tarif ne produit pas de facture : l'inscription apparaît parmi les factures manquantes.",
    'fin.modules.currency': "Monnaie",
    'fin.modules.currencyCode': "Code ISO (MAD, XOF, EUR)",
    'fin.modules.decimals': "Décimales",
    'fin.modules.currencyHint': "La monnaie ne se change plus une fois la première facture émise.",
    'fin.modules.currencyLocked': "Des factures existent : la monnaie est verrouillée.",
    'fin.modules.numbering': "Numérotation des documents",
    'fin.modules.invoicePattern': "Format des factures",
    'fin.modules.receiptPattern': "Format des reçus",
    'fin.modules.next': "Prochain : {number}",
    'fin.modules.insert': "Insérer :",
    'fin.modules.reset': "Le compteur repart à 1",
    'fin.modules.reset.yearly': "À chaque année académique",
    'fin.modules.reset.never': "Jamais",
    'fin.modules.numberingHint': "Le format doit contenir un rang {N:x}. Un numéro attribué n'est jamais réutilisé, même si la facture est annulée.",
    'fin.modules.mentions': "Mentions sur les factures et les reçus",
    'fin.modules.legalName': "Raison sociale",
    'fin.modules.address': "Adresse",
    'fin.modules.businessId': "ICE",
    'fin.modules.taxId': "Identifiant fiscal (IF)",
    'fin.modules.tradeRegister': "Registre de commerce (RC)",
    'fin.modules.footer': "Pied de page",
    'fin.modules.logo': "Le logo vient de l'identité de l'établissement.",
    'fin.modules.saved': "Réglage du module enregistré.",
    'fin.prices.title': "Tarifs des formations",
    'fin.prices.sub': "Niveaux tarifés : {levels}",
    'fin.prices.renew': "Reconduire depuis une autre année",
    'fin.prices.renewSub': "Recopie les tarifs élément par élément, par leur code, sur l'année affichée.",
    'fin.prices.renewFrom': "Depuis",
    'fin.prices.renewGo': "Reconduire",
    'fin.prices.renewed': "{count} tarifs reconduits.",
    'fin.prices.bulk': "Appliquer un tarif en masse",
    'fin.prices.bulkSub': "Un même tarif pour tous les éléments d'un niveau de cette formation.",
    'fin.prices.overwrite': "Remplacer les tarifs déjà saisis",
    'fin.prices.saved': "{count} tarifs enregistrés.",
    'fin.prices.invalid': "Montant illisible : « {value} ».",
    'fin.prices.empty': "Aucune formation dans l'année.",
    'fin.prices.training': "Formation",
    'fin.prices.amount': "Tarif",
    'fin.prices.schedule': "Échéancier",
    'fin.prices.missing': "À compléter",
    'fin.prices.complete': "Complet",
    'fin.prices.missingCount': "{count} sans tarif",
    'fin.prices.defaultSchedule': "Échéancier par défaut",
    'fin.prices.element': "Élément du catalogue",
    'fin.prices.level': "Niveau",
    'fin.prices.credits': "Crédits",
    'fin.prices.notPriced': "niveau non tarifé",
    'fin.prices.toEnter': "à saisir",
    'fin.prices.howTitle': "Ce que paie un étudiant",
    'fin.prices.how': "Inscription standard : le tarif de la formation. Personnalisée ou en crédit : la somme des tarifs des éléments suivis. Plus les services obligatoires et ceux qu'on ajoute.",
    'fin.prices.frozenTitle': "Tarifs et factures émises",
    'fin.prices.frozen': "Une facture recopie le tarif à son émission : changer un tarif ne touche que les factures émises ensuite.",
    'fin.services.title': "Services complémentaires",
    'fin.services.sub': "Frais, assurances, options et services à la demande, avec leur taxe.",
    'fin.services.new': "Nouveau service",
    'fin.services.empty': "Aucun service.",
    'fin.services.label': "Service",
    'fin.services.scope': "Portée",
    'fin.services.kind': "Caractère",
    'fin.services.tax': "Taxe",
    'fin.services.students': "Étudiants",
    'fin.services.thisYear': "cette année seulement",
    'fin.services.validity': "Validité",
    'fin.services.validityHint': "Toutes les années : le service se reconduit sans recopie.",
    'fin.services.allYears': "Toutes les années",
    'fin.services.onlyYear': "{year} seulement",
    'fin.services.saved': "Service enregistré.",
    'fin.services.delete': "Supprimer le service",
    'fin.services.deleteConfirm': "Supprimer « {label} » ? Un service déjà facturé ne se supprime pas : désactivez-le.",
    'fin.services.deleted': "« {label} » supprimé.",
    'fin.services.mandatoryHint': "Un service obligatoire ajouté en cours d'année ne modifie pas les factures déjà émises : ajoutez-le depuis le compte des étudiants concernés.",
    'fin.discounts.title': "Rabais",
    'fin.discounts.sub': "Rabais spéciaux, accordés depuis le compte de l'étudiant.",
    'fin.discounts.new': "Nouveau rabais",
    'fin.discounts.empty': "Aucun rabais.",
    'fin.discounts.label': "Rabais",
    'fin.discounts.value': "Valeur",
    'fin.discounts.target': "S'applique à",
    'fin.discounts.rules': "Règles",
    'fin.discounts.granted': "Accordés cette année",
    'fin.discounts.kind': "Type",
    'fin.discounts.percent': "Pourcentage",
    'fin.discounts.amount': "Montant",
    'fin.discounts.freeHint': "Le montant se saisit à l'octroi, avec un motif.",
    'fin.discounts.invoiceHint': "Déduit du total, services compris.",
    'fin.discounts.restricted': "Droit dédié",
    'fin.discounts.notStackable': "Non cumulable",
    'fin.discounts.reason': "Motif obligatoire",
    'fin.discounts.flag.stackable': "Cumulable avec les autres rabais",
    'fin.discounts.flagHint.stackable': "Sinon, il ne s'accorde pas sur une facture qui porte déjà un rabais.",
    'fin.discounts.flag.restricted': "Réservé au droit « réduction exceptionnelle »",
    'fin.discounts.flagHint.restricted': "Un caissier encaisse sans pouvoir l'accorder.",
    'fin.discounts.flag.requiresReason': "Motif obligatoire",
    'fin.discounts.flagHint.requiresReason': "Le motif figure sur la facture et au journal.",
    'fin.discounts.flag.active': "Actif",
    'fin.discounts.flagHint.active': "",
    'fin.discounts.journal': "Accorder un rabais est journalisé : qui, quand, sur quelle facture, avec quel motif. Il apparaît en ligne négative sur la facture et son PDF.",
    'fin.discounts.saved': "Rabais enregistré.",
    'fin.discounts.delete': "Supprimer le rabais",
    'fin.discounts.deleteConfirm': "Supprimer « {label} » ? Un rabais déjà accordé ne se supprime pas : désactivez-le.",
    'fin.discounts.deleted': "« {label} » supprimé.",
    'fin.taxes.title': "Taxes",
    'fin.taxes.new': "Nouvelle taxe",
    'fin.taxes.empty': "Aucune taxe : les services sont facturés sans taxe.",
    'fin.taxes.services': "{count} services",
    'fin.taxes.hint': "Une taxe déjà facturée ne change pas de taux : créez-en une nouvelle.",
    'fin.taxes.name': "Nom",
    'fin.taxes.rate': "Taux (%)",
    'fin.taxes.isDefault': "Taxe proposée par défaut pour un nouveau service",
    'fin.taxes.saved': "Taxe enregistrée.",
    'fin.taxes.delete': "Supprimer la taxe",
    'fin.schedules.title': "Taxes et échéanciers",
    'fin.schedules.sub': "Paramètres du gestionnaire des finances.",
    'fin.schedules.list': "Échéanciers",
    'fin.schedules.new': "Nouvel échéancier",
    'fin.schedules.empty': "Aucun échéancier : chaque facture se paie en une fois.",
    'fin.schedules.pick': "Choisissez un échéancier.",
    'fin.schedules.name': "Nom",
    'fin.schedules.grace': "Délai de grâce (jours)",
    'fin.schedules.graceHint': "Une échéance impayée passe « en retard » après ce délai.",
    'fin.schedules.due': "Échéance",
    'fin.schedules.share': "Part",
    'fin.schedules.day': "Jour",
    'fin.schedules.month': "Mois",
    'fin.schedules.addLine': "Ajouter un versement",
    'fin.schedules.dateHint': "Une date fixe tombe dans l'année académique : en mars, c'est le mars qui suit la rentrée. Une échéance déjà passée le jour de la facture tombe ce jour-là.",
    'fin.schedules.isDefault': "Échéancier par défaut de l'établissement",
    'fin.schedules.isDefaultHint': "Utilisé par toute formation qui n'en désigne pas un autre.",
    'fin.schedules.saved': "Échéancier enregistré.",
    'fin.schedules.delete': "Supprimer l'échéancier",
    'fin.schedules.ruleTitle': "Comment un paiement est imputé",
    'fin.schedules.rule': "Un paiement solde d'abord l'échéance impayée la plus ancienne, puis la suivante. Payer selon l'échéancier ou par acomptes libres donne le même résultat.",
    'fin.schedules.example': "Exemple",
    'fin.schedules.example1': "12 sept. · 9 840",
    'fin.schedules.example2': "5 janv. · 3 000",
    'fin.schedules.example3': "20 janv. · 6 000",
    'fin.schedules.surplus': "Un paiement qui dépasse le reste dû laisse un avoir au compte de l'étudiant, jamais perdu.",
    'fin.schedule.onIssue': "À la facture",
    'fin.schedule.onDate': "Date fixe",
    'fin.invoices.title': "Factures et encaissements",
    'fin.invoices.sub': "Une facture par inscription, émise d'office à l'inscription.",
    'fin.invoices.tab': "Factures",
    'fin.invoices.search': "N°, étudiant, numéro d'étudiant",
    'fin.invoices.filter': "État",
    'fin.invoices.discounted': "Avec rabais",
    'fin.invoices.empty': "Aucune facture ne correspond.",
    'fin.invoices.number': "N°",
    'fin.invoices.student': "Étudiant",
    'fin.invoices.training': "Formation",
    'fin.invoices.next': "Prochaine échéance",
    'fin.invoices.status': "État",
    'fin.invoices.missing': "{count} inscriptions actives n'ont pas de facture : leur formation n'avait pas de tarif à l'inscription.",
    'fin.invoices.issueMissing': "Émettre les {count} factures manquantes",
    'fin.invoices.issueTitle': "Émettre les factures manquantes",
    'fin.invoices.issueConfirm': "Émettre une facture pour chacune des {count} inscriptions qui n'en ont pas ? Celles dont la formation n'a toujours pas de tarif resteront sans facture.",
    'fin.invoices.issueGo': "Émettre",
    'fin.invoices.issued': "{count} factures émises.",
    'fin.payments.tab': "Encaissements",
    'fin.payments.method': "Mode",
    'fin.payments.allMethods': "Tous les modes",
    'fin.payments.from': "Du",
    'fin.payments.to': "Au",
    'fin.payments.empty': "Aucun encaissement.",
    'fin.payments.sum': "{count} encaissements, total :",
    'fin.payments.date': "Date",
    'fin.payments.receipt': "Reçu",
    'fin.payments.by': "Saisi par",
    'fin.dashboard.title': "Tableau de bord financier",
    'fin.dashboard.sub': "Année {year} · au {date}",
    'fin.dashboard.billed': "Facturé net",
    'fin.dashboard.invoices': "{count} factures",
    'fin.dashboard.paid': "Encaissé",
    'fin.dashboard.rate': "{rate} du facturé",
    'fin.dashboard.due': "Reste dû",
    'fin.dashboard.students': "{count} étudiants",
    'fin.dashboard.late': "En retard",
    'fin.dashboard.discounts': "Rabais accordés",
    'fin.dashboard.discountCount': "{count} rabais",
    'fin.dashboard.expected': "Attendu à ce jour",
    'fin.dashboard.expectedNote': "{paid} sur {expected} attendus",
    'fin.dashboard.months': "Encaissements par mois",
    'fin.dashboard.methods': "Par mode de paiement",
    'fin.dashboard.cash': "Caisse du jour · {date}",
    'fin.dashboard.noPayment': "Aucun encaissement cette année.",
    'fin.dashboard.noCash': "Aucun encaissement aujourd'hui.",
    'fin.dashboard.byTraining': "Par formation",
    'fin.dashboard.invoiceCount': "Factures",
    'fin.dashboard.collection': "Recouvrement",
    'fin.dashboard.noInvoice': "Aucune facture cette année.",
    'fin.dashboard.oldestLate': "Retards les plus anciens",
    'fin.dashboard.noLate': "Aucun retard.",
    'fin.dashboard.allLate': "Voir toutes les factures en retard",
    'fin.archive.title': "Historique de l'ancien Kelassi",
    'fin.archive.summary': "{count} factures · {from} à {to}",
    'fin.archive.show': "Afficher l'historique",
    'fin.archive.hide': "Masquer l'historique",
    'fin.archive.bannerTitle': "Repris de l'ancien Kelassi, en lecture seule",
    'fin.archive.bannerText': "Factures, paiements et avoirs tels qu'ils ont été saisis. Aucun reste dû n'est repris : les soldes d'ouverture viennent de l'établissement. Rien ici n'entre dans le compte de l'année.",
    'fin.archive.billed': "Facturé",
    'fin.archive.billedNote': "{count} factures, hors annulées",
    'fin.archive.reductions': "Réductions",
    'fin.archive.special': "réductions spéciales",
    'fin.archive.creditNotes': "avoirs",
    'fin.archive.paid': "Encaissé",
    'fin.archive.paidNote': "Annulations déduites",
    'fin.archive.expand': "Déplier la facture",
    'fin.archive.collapse': "Replier la facture",
    'fin.archive.cancelled': "Annulée",
    'fin.archive.specialLine': "Réduction spéciale",
    'fin.archive.creditLine': "Avoir du {date}",
    'fin.archive.net': "Net facturé",
    'fin.archive.noPayment': "Aucun paiement saisi.",
    'fin.archive.comment': "Commentaire",
    'fin.archive.paymentCancelled': "Annulé",
    'fin.archive.reversal': "Contre-passation",
    'fin.archive.dateCorrected': "Date rétablie",
    'fin.archive.dateCorrectedHint': "« Date rétablie » : la date saisie dans l'ancien Kelassi était impossible (année « 19 », « 20 »…), la reprise l'a rétablie d'après la date de saisie.",
    'fin.method.direct_debit': "Prélèvement",
    'fin.quote.title': "Frais de l'inscription",
    'fin.quote.sub': "La facture émise à l'enregistrement",
    'fin.quote.unpriced': "Aucun tarif pour {training} : l'inscription s'enregistre sans facture. Elle figurera parmi les « factures manquantes » jusqu'à ce que le tarif existe.",
    'fin.quote.price': "Tarif de la formation",
    'fin.quote.mandatory': "Services obligatoires",
    'fin.quote.mandatoryHint': "Ajoutés d'office, selon la portée du service",
    'fin.quote.locked': "obligatoire",
    'fin.quote.optional': "Services au choix",
    'fin.quote.optionalHint': "Cochés ici, ou ajoutés plus tard depuis le compte",
    'fin.quote.total': "À facturer",
    'fin.quote.note': "La facture est émise à l'enregistrement de l'inscription, avec ces lignes. Les rabais s'accordent ensuite depuis le compte de l'étudiant, par la finance.",
    'fin.quote.taxIncluded': "dont {tax}",
    'fin.quote.single': "Paiement unique",
    'fin.quote.schedule': "Échéancier · {name}",
    'fin.quote.atIssue': "À l'inscription",
    'fin.method.internal': "Report",
    'fin.account.discountsCredits': "Rabais et avoirs",
    'fin.credit.open': "Émettre un avoir",
    'fin.credit.sub': "Facture {number} · net {amount}",
    'fin.credit.modeLines': "Sur des lignes",
    'fin.credit.modeLinesHint': "Une partie ou la totalité de lignes choisies",
    'fin.credit.modeInvoice': "Toute la facture",
    'fin.credit.modeInvoiceHint': "Le reste de la facture, d'un coup (abandon, erreur)",
    'fin.credit.line': "Ligne",
    'fin.credit.billed': "Facturé",
    'fin.credit.room': "Créditable",
    'fin.credit.amount': "Avoir",
    'fin.credit.total': "Total de l'avoir",
    'fin.credit.reason': "Motif",
    'fin.credit.reasonHint': "Il figure sur l'avoir et dans le journal.",
    'fin.credit.effect': "Effet sur la facture",
    'fin.credit.before': "Avant",
    'fin.credit.after': "Après l'avoir",
    'fin.credit.net': "Net de la facture",
    'fin.credit.due': "Reste dû",
    'fin.credit.overpaid': "Trop-perçu",
    'fin.credit.installments': "Échéances",
    'fin.credit.note': "L'avoir est un document numéroté qui corrige une facture déjà émise, sans la modifier. Les échéances se recalculent : l'avoir réduit d'abord les dernières.",
    'fin.credit.print': "Imprimer l'avoir",
    'fin.credit.go': "Émettre l'avoir",
    'fin.credit.goPrint': "Émettre et imprimer",
    'fin.credit.done': "Avoir émis.",
    'fin.credit.list': "Avoirs",
    'fin.credit.row': "Avoir {number} · {date}",
    'fin.overpaid.title': "{amount} payés au-delà de ce qui est dû",
    'fin.overpaid.text': "Le trop-perçu reste au compte de l'étudiant tant qu'on n'en décide rien. Il n'est jamais perdu.",
    'fin.overpaid.transfer': "Reporter sur une facture",
    'fin.overpaid.refund': "Rembourser…",
    'fin.overpaid.title2': "Rendre le trop-perçu",
    'fin.overpaid.sub': "{number} · {amount} de trop-perçu",
    'fin.refund.tab': "Rembourser",
    'fin.refund.amount': "Montant à rembourser",
    'fin.refund.amountHint': "Au plus {amount}",
    'fin.refund.date': "Date",
    'fin.refund.beneficiary': "Bénéficiaire",
    'fin.refund.beneficiaryHint': "L'étudiant ou un contact de sa fiche",
    'fin.refund.reason': "Motif",
    'fin.refund.note': "Le remboursement est une sortie de caisse : numérotée, journalisée, immuable. Il se corrige par une annulation, comme un paiement.",
    'fin.refund.print': "Imprimer le bon de remboursement",
    'fin.refund.go': "Rembourser",
    'fin.refund.done': "Remboursement enregistré.",
    'fin.transfer.tab': "Reporter sur une facture",
    'fin.transfer.none': "Aucune autre facture de l'étudiant n'a de reste dû.",
    'fin.transfer.note': "Le report est un paiement interne : il solde l'autre facture sans mouvement de caisse, et réduit d'autant le trop-perçu.",
    'fin.transfer.amount': "Montant à reporter",
    'fin.transfer.object': "Objet",
    'fin.transfer.go': "Reporter",
    'fin.transfer.done': "Trop-perçu reporté.",
    'fin.payment.refund': "Remboursement",
    'fin.payment.transferOut': "Report vers une autre facture",
    'fin.payment.transferIn': "Report depuis {number}",
    'fin.modules.creditNotePattern': "Format des avoirs",
    'fin.modules.refundPattern': "Format des remboursements",
    'journal.action.finance.credit_note_issued': "Avoir émis",
    'journal.action.finance.refund_recorded': "Trop-perçu remboursé",
    'journal.action.finance.payment_transferred': "Trop-perçu reporté",
    'violation.fin.credit_too_high': "L'avoir dépasse ce qui reste à créditer.",
    'violation.fin.nothing_to_credit': "Cette facture est déjà entièrement créditée.",
    'violation.fin.no_line': "Choisissez au moins une ligne.",
    'violation.fin.below_credited': "La facture passerait sous ce que ses avoirs ont déjà crédité.",
    'violation.fin.invoice_has_credit_note': "Cette facture porte un avoir : elle ne s'annule plus.",
    'violation.fin.refund_too_high': "Le montant dépasse le trop-perçu de la facture.",
    'violation.fin.transfer_too_high': "Le report dépasse le reste dû de la facture visée.",
    'violation.fin.transfer_target': "Le report se fait sur une autre facture du même étudiant.",
    'violation.fin.service_not_offered': "Ce service n'est pas proposé pour cette formation.",
    'account.person': "Fiche d’enseignant liée",
    'account.person.hint': "L’enseignant que ce compte représente : il saisit les notes de ses matières.",
    'account.person.none': "Aucune",
    'account.person.taken': "déjà liée à un autre compte",
    'ref.pedagogy.absence_type': "Types d’absence aux évaluations",
    'ref.pedagogy.absence_type.hint': "Ce que vaut chaque absence : une note (0) ou rien",
    'ref.target.absence_type': "Type d’absence",
    'ref.pedagogy.absence.effect': "Effet sur la note",
    'ref.pedagogy.absence.countsLabel': "L’absence compte comme une note",
    'ref.pedagogy.absence.value': "Note attribuée",
    'ref.pedagogy.absence.hint': "Décochée, l’étudiant absent est retiré du calcul (une absence justifiée, par exemple). Le code se tape aussi dans la case de la note.",
    'ref.pedagogy.absence.counts': "compte {value}",
    'ref.pedagogy.absence.notCounted': "ne compte pas",
    'journal.action.assessment.created': "Évaluation créée",
    'journal.action.assessment.updated': "Évaluation modifiée",
    'journal.action.assessment.deleted': "Évaluation supprimée",
    'journal.action.assessment.validated': "Notes validées",
    'journal.action.assessment.reopened': "Saisie rouverte",
    'journal.action.assessment.marks_imported': "Notes importées",
    'journal.action.assessment.mark_corrected': "Note corrigée",
    'asm.target.assessment': "Évaluation",
    'asm.target.mark': "Note",
    'asm.journal.time': "Horaire",
    'asm.journal.candidates': "Convoqués",
    'asm.journal.candidate': "Convoqué",
    'asm.journal.validated': "Validation",
    'asm.journal.mark': "Note",
    'asm.journal.marks': "Notes",
    'asm.absence.counts': "compte {value}",
    'asm.absence.notCounted': "ne compte pas",
    'asm.absence.label': "Absence de {name}",
    'asm.banner.validated': "Validée le {date} par {name}.",
    'asm.banner.locked': "Les notes sont verrouillées. Une correction demande un motif, et l’ancienne valeur est gardée.",
    'asm.candidates.add': "Convoquer des étudiants",
    'asm.candidates.addSub': "{count} inscrits ne sont pas encore convoqués",
    'asm.candidates.added': "{count} étudiants convoqués.",
    'asm.candidates.allIn': "Tous les inscrits sont convoqués.",
    'asm.candidates.classes': "Classes",
    'asm.candidates.go': "Convoquer",
    'asm.candidates.none': "Aucun étudiant convoqué.",
    'asm.candidates.remove': "Retirer de l’évaluation",
    'asm.candidates.removed': "{name} n’est plus convoqué.",
    'asm.col.absence': "Absence",
    'asm.col.assessment': "Évaluation",
    'asm.col.candidates': "Convoqués",
    'asm.col.class': "Classe",
    'asm.col.entry': "Saisie",
    'asm.col.mark': "Note /{outOf}",
    'asm.col.number': "N°",
    'asm.col.student': "Étudiant",
    'asm.correct': "Corriger la note",
    'asm.correct.current': "Note actuelle",
    'asm.correct.note': "La correction est journalisée. Elle ne déverrouille pas les autres notes.",
    'asm.correct.reason': "Motif",
    'asm.correct.value': "Nouvelle note /{outOf}",
    'asm.corrected': "corrigée",
    'asm.corrected.done': "Note de {name} corrigée.",
    'asm.corrections.after': "Après",
    'asm.corrections.before': "Avant",
    'asm.corrections.by': "Par",
    'asm.corrections.date': "Date",
    'asm.corrections.reason': "Motif",
    'asm.corrections.title': "Corrections après validation",
    'asm.delete': "Supprimer l’évaluation",
    'asm.delete.confirm': "Supprimer « {label} » et ses convocations ? Aucune note n’est encore saisie.",
    'asm.dialog.addResponsible': "+ ajouter un responsable",
    'asm.dialog.addStudent': "Convoquer un étudiant un par un",
    'asm.dialog.addStudentHint': "Pour un rattrapage, ou un étudiant hors des classes cochées.",
    'asm.dialog.chooseSubject': "Choisir la matière",
    'asm.dialog.convoke': "Convoquer",
    'asm.dialog.convokeHint': "Les membres des classes cochées, ce jour-là. La liste se modifie ensuite depuis l’évaluation (ajouter un étudiant, en retirer un).",
    'asm.dialog.create': "Créer et saisir les notes",
    'asm.dialog.members': "{count} étudiants",
    'asm.dialog.noClass': "Aucune classe dans cette formation : convoquez les étudiants un par un.",
    'asm.dialog.noResponsible': "Aucun responsable",
    'asm.dialog.responsiblesHint': "Repris des responsables de la matière (catalogue). Un responsable saisit les notes depuis « Mes évaluations » si son compte est lié à sa fiche.",
    'asm.dialog.searchStudent': "Nom ou numéro",
    'asm.dialog.sub': "{training} · {year} · {count} étudiants convoqués",
    'asm.dialog.subjectTeacher': "enseignant de la matière",
    'asm.edit': "Modifier l’évaluation",
    'asm.field.coefficient': "Coefficient",
    'asm.field.date': "Date",
    'asm.field.element': "Élément",
    'asm.field.end': "Fin",
    'asm.field.label': "Intitulé",
    'asm.field.labelHint': "Visible dans la liste et sur les relevés",
    'asm.field.labelPlaceholder': "CC 1, Examen final…",
    'asm.field.nature': "Nature",
    'asm.field.outOf': "Noté sur",
    'asm.field.responsibles': "Responsables",
    'asm.field.retake': "Session de rattrapage",
    'asm.field.start': "Début",
    'asm.field.state': "État",
    'asm.field.subject': "Matière",
    'asm.field.training': "Formation",
    'asm.field.type': "Type",
    'asm.filter.all': "Tous les convoqués",
    'asm.filter.allElements': "Tous les éléments",
    'asm.filter.allStates': "Tous les états",
    'asm.filter.allTypes': "Tous les types",
    'asm.filter.label': "Filtrer les convoqués",
    'asm.filter.missing': "Sans note",
    'asm.head.coefficient': "coefficient {value}",
    'asm.head.outOf': "noté sur {value}",
    'asm.head.responsibles': "responsable : {names}",
    'asm.input.label': "Note de {name}",
    'asm.keys': "Clavier : Entrée ou ↓ passe à l’étudiant suivant, ↑ revient, Échap annule la case. Chaque note s’enregistre en quittant la case. Tapez le code d’une absence ({codes}) pour la poser.",
    'asm.kpi.complete': "à valider",
    'asm.kpi.partial': "saisie commencée",
    'asm.kpi.toEnter': "date passée, aucune note",
    'asm.kpi.total': "Évaluations",
    'asm.kpi.validated': "notes verrouillées",
    'asm.list.empty': "Aucune évaluation pour ces critères.",
    'asm.list.noTraining': "Aucune formation de cette année dans votre portée.",
    'asm.list.note': "Sans moyennes : elles arrivent avec le calcul. Ici, les évaluations et leurs notes.",
    'asm.list.sub': "Les contrôles et les examens d’une année, et l’avancement de la saisie des notes",
    'asm.list.title': "Évaluations",
    'asm.mine.empty': "Aucune évaluation dans vos matières cette année.",
    'asm.mine.sub': "Les évaluations des matières dont vous êtes responsable : saisissez les notes tant qu’elles ne sont pas validées",
    'asm.mine.title': "Mes évaluations",
    'asm.mine.unlinked': "Votre compte n’est lié à aucune fiche d’enseignant : demandez à l’administrateur de le lier, depuis la fiche de votre compte.",
    'asm.nature.written': "Écrit",
    'asm.nature.oral': "Oral",
    'asm.nature.practical': "Pratique",
    'asm.nature.project': "Projet",
    'asm.new': "Nouvelle évaluation",
    'asm.noClass': "Sans classe",
    'asm.reopen': "Rouvrir la saisie",
    'asm.reopen.confirm': "Rouvrir la saisie ? Les notes redeviennent modifiables sans motif jusqu’à la prochaine validation.",
    'asm.reopened': "Saisie rouverte.",
    'asm.retake': "Rattrapage",
    'asm.saved': "Évaluation enregistrée.",
    'asm.sheet.download': "Télécharger la feuille Excel",
    'asm.sheet.import': "Importer une feuille Excel",
    'asm.sheet.imported': "{count} notes importées ({skipped} lignes vides laissées telles quelles).",
    'asm.sheet.importing': "Import en cours…",
    'asm.sheet.refused': "Feuille refusée",
    'asm.sheet.refusedSub': "{count} erreurs : aucune note n’a été enregistrée. Corrigez la feuille et importez-la de nouveau.",
    'asm.sheet.row': "Ligne {row} :",
    'asm.state.planned': "À venir",
    'asm.state.to_enter': "À saisir",
    'asm.state.partial': "En cours",
    'asm.state.complete': "Saisie complète",
    'asm.state.validated': "Validée",
    'asm.stats.between': "{from} à {to}",
    'asm.stats.counting': "Absences qui comptent",
    'asm.stats.distribution': "Répartition des notes",
    'asm.stats.excused': "Absences qui ne comptent pas",
    'asm.stats.marks': "Notes saisies",
    'asm.stats.mean': "Moyenne de l’évaluation {value}",
    'asm.stats.missing': "Sans note",
    'asm.stats.noMark': "Aucune note saisie.",
    'asm.stats.progress': "Avancement",
    'asm.stats.range': "min {min} · max {max}",
    'asm.status.empty': "sans note",
    'asm.status.saved': "enregistrée",
    'asm.status.saving': "enregistrement…",
    'asm.student.absent': "Absences",
    'asm.student.convoked': "Convocations",
    'asm.student.empty': "Aucune évaluation pour cet étudiant.",
    'asm.student.evaluated': "{count} évaluations cette année",
    'asm.student.marked': "Notées",
    'asm.student.note': "Les moyennes et les décisions du jury apparaîtront ici avec le calcul. Les années précédentes viennent de l’ancien Kelassi, avec leurs notes.",
    'asm.student.title': "Notes",
    'asm.student.toCome': "à venir",
    'asm.student.upcoming': "À venir",
    'asm.validate': "Valider les notes",
    'asm.validate.confirm': "Valider les {count} notes ? Elles seront verrouillées : une correction demandera ensuite un motif.",
    'asm.validate.missing': "{count} convoqués sans note ni absence",
    'asm.validated': "Notes validées.",
    'asm.kpi.validatedLabel': "Validées",
    'asm.kpi.toEnterLabel': "À saisir",
    'ref.pedagogy.absence.treatment.mark': "Compte comme une note",
    'ref.pedagogy.absence.treatment.mark.hint': "la note ci-dessous (0 d’ordinaire)",
    'ref.pedagogy.absence.treatment.mark.short': "compte",
    'ref.pedagogy.absence.treatment.neutralized': "Neutralisée",
    'ref.pedagogy.absence.treatment.neutralized.hint': "l’évaluation est retirée du calcul, son poids redistribué",
    'ref.pedagogy.absence.treatment.neutralized.short': "ne compte pas",
    'ref.pedagogy.absence.treatment.blocking': "Bloque le calcul",
    'ref.pedagogy.absence.treatment.blocking.hint': "pas de moyenne tant que la situation n’est pas réglée",
    'ref.pedagogy.absence.treatment.blocking.short': "bloque le calcul",
    'ref.pedagogy.absence.treatment.resit': "Renvoie au rattrapage",
    'ref.pedagogy.absence.treatment.resit.hint': "l’étudiant repasse l’élément en session de rattrapage",
    'ref.pedagogy.absence.treatment.resit.short': "renvoie au rattrapage",
    'reg.title': "Règlement des études",
    'reg.sub': "Les règles de calcul des moyennes, des décisions et du rattrapage, version par version, pour chaque année",
    'reg.and': "et",
    'reg.legacy': "reprise de l'ancien Kelassi",
    'reg.lifecycle': "Cycle de vie d'une version",
    'reg.lifecycle.note': "Une version publiée ne change plus : toute correction crée une nouvelle version, avec une date d'effet et un motif.",
    'reg.filter.allYears': "Toutes les années",
    'reg.list.empty': "Aucune version du règlement pour l'instant.",
    'reg.list.inForce': "en vigueur depuis le {date}",
    'reg.list.frozen': "gelée le {date}",
    'reg.list.archived': "remplacée le {date}",
    'reg.list.blocking': "{count} contrôle(s) bloquant(s)",
    'reg.list.awaiting': "en attente de validation",
    'reg.list.ready': "prête à soumettre",
    'reg.list.note': "À l'ouverture d'une année, copiez la dernière version de l'année précédente et revoyez-la avant publication. La publication passe par une seconde personne (quatre yeux), si la version le demande.",
    'reg.col.version': "Année et version",
    'reg.col.state': "État",
    'reg.col.createdBy': "Saisie par",
    'reg.col.decidedBy': "Validée par",
    'reg.col.status': "Situation",
    'reg.col.scope': "Portée",
    'reg.col.profile': "Profil",
    'reg.col.block': "Bloc",
    'reg.col.value': "Valeur retenue",
    'reg.col.origin': "Portée d'origine",
    'reg.state.draft': "Brouillon",
    'reg.state.review': "En validation",
    'reg.state.published': "Publiée",
    'reg.state.frozen': "Gelée",
    'reg.state.archived': "Archivée",
    'reg.status.V': "Validé",
    'reg.status.VPC': "Validé par compensation",
    'reg.status.VAR': "Validé après rattrapage",
    'reg.status.R': "Rattrapage",
    'reg.status.NV': "Non validé",
    'reg.status.A': "Absent",
    'reg.status.pending': "en attente",
    'reg.status.V.when': "au seuil, enfants acquis",
    'reg.status.VPC.when': "au-dessus du plancher, parent validé",
    'reg.status.VAR.when': "acquis par le rattrapage",
    'reg.status.R.when': "non acquis, rattrapable",
    'reg.status.NV.when': "échoue au rattrapage, ou trop bas",
    'reg.status.A.when': "non acquis, absence qui marque",
    'reg.outcome.admitted': "Admis",
    'reg.outcome.admitted_with_debts': "Admis avec dettes",
    'reg.outcome.retake': "Rattrapage",
    'reg.outcome.not_admitted': "Ajourné",
    'reg.outcome.pending': "En attente",
    'reg.scope.institution': "Établissement",
    'reg.scope.cycle': "Cycle",
    'reg.scope.domain': "Domaine",
    'reg.scope.cycle_domain': "Cycle et domaine",
    'reg.scope.level': "Niveau",
    'reg.scope.track': "Parcours",
    'reg.scope.class': "Classe",
    'reg.scope.element': "Élément de la maquette",
    'reg.block.scale': "Échelle de notation",
    'reg.block.weighting': "Pondération",
    'reg.block.absences': "Absences et situations",
    'reg.block.thresholds': "Seuils et notes éliminatoires",
    'reg.block.compensation': "Compensation et capitalisation",
    'reg.block.retake': "Rattrapage",
    'reg.block.priors': "Reprise de notes",
    'reg.block.rounding': "Arrondis",
    'reg.block.decisions': "Décisions, mentions, rang",
    'reg.criterion.regime': "le régime",
    'reg.criterion.mode': "le mode d'inscription",
    'reg.criterion.repeating': "le redoublement",
    'reg.criterion.scholarship': "la bourse",
    'reg.criterion.employee': "le statut de salarié",
    'reg.criterion.special_needs': "l'aménagement",
    'reg.criterion.entry_year': "l'année d'entrée",
    'reg.op.is': "est",
    'reg.op.is_not': "n'est pas",
    'reg.value.none': "aucun",
    'reg.v.types': "Par type d'évaluation",
    'reg.v.coefficients': "Coefficients",
    'reg.v.mean': "Moyenne simple",
    'reg.v.redistribute': "est retiré, son poids redistribué",
    'reg.v.zero': "compte 0",
    'reg.v.credits': "Crédits",
    'reg.v.equal': "Poids égal",
    'reg.v.elements': "Comme les éléments",
    'reg.v.custom': "Pondération libre",
    'reg.v.catalog': "Seuils de la maquette",
    'reg.v.level': "Seuil par niveau",
    'reg.v.no_compensation': "ne peut pas être compensé",
    'reg.v.fails_parent': "fait aussi échouer son parent",
    'reg.v.best': "La meilleure des deux",
    'reg.v.average': "La moyenne des notes de rattrapage",
    'reg.v.last': "La plus récente",
    'reg.v.merge': "La moyenne des sessions",
    'reg.v.none': "Aucune",
    'reg.v.half_up': "au plus proche",
    'reg.v.half_down': "au plus proche, demi vers le bas",
    'reg.v.half_even': "au plus proche, demi au pair",
    'reg.v.down': "troncature",
    'reg.v.up': "vers le haut",
    'reg.v.kept': "conservée",
    'reg.v.removed': "supprimée",
    'reg.v.numeric': "Chiffres",
    'reg.v.numeric_letter': "Chiffres et lettre",
    'reg.v.letter': "Lettre",
    'reg.v.subject': "Matière",
    'reg.v.submodule': "Sous-module",
    'reg.v.module': "Module",
    'reg.v.semester': "Semestre",
    'reg.v.year': "Année",
    'reg.v.mark': "compte comme une note",
    'reg.v.neutralized': "ne compte pas",
    'reg.v.blocking': "bloque le calcul",
    'reg.v.resit': "renvoie au rattrapage",
    'reg.v.true': "Oui",
    'reg.v.false': "Non",
    'reg.v.defined': "défini",
    'reg.v.external': "externe",
    'reg.v.internal': "interne",
    'reg.v.standard': "standard",
    'reg.v.credit': "en crédit",
    'reg.f.outOf': "Note maximale",
    'reg.f.display': "Décimales affichées",
    'reg.f.assessments': "Évaluations dans une matière",
    'reg.f.typeWeights': "Poids par type",
    'reg.f.missingType': "Type sans note",
    'reg.f.bestOf': "Meilleures notes gardées",
    'reg.f.elements': "Éléments dans leur parent",
    'reg.f.semesters': "Semestres dans l'année",
    'reg.f.semesterWeights': "Poids des semestres",
    'reg.f.bonusCap': "Plafond des points bonus",
    'reg.f.overrides': "Traitement",
    'reg.f.source': "Seuils pris dans",
    'reg.f.validation': "Validé à",
    'reg.f.retake': "Rattrapable dès",
    'reg.f.eliminatory': "Note éliminatoire",
    'reg.f.eliminatoryEffect': "Effet de la note éliminatoire",
    'reg.f.enabled': "Compenser",
    'reg.f.floor': "Compensable dès",
    'reg.f.maxCount': "Au plus",
    'reg.f.capitalisation': "Capitalisation",
    'reg.f.retained': "Note retenue",
    'reg.f.cap': "Plafond après rattrapage",
    'reg.f.wholeGroup': "tout le sous-module au rattrapage",
    'reg.f.sessions': "Plusieurs sessions d'un même type",
    'reg.f.mode': "Mode",
    'reg.f.evaluable': "Arrondi des matières",
    'reg.f.aggregate': "Arrondi des agrégats",
    'reg.f.digits': "Décimales",
    'reg.f.maxDebts': "Admis avec dettes : au plus",
    'reg.f.debtKind': "Niveau des dettes",
    'reg.f.mentions': "Mentions",
    'reg.f.mentionAfterRetake': "Mention après rattrapage",
    'reg.f.rank': "Rang",
    'reg.field.whole': "défini",
    'reg.field.year': "Année",
    'reg.field.from': "Copier la version",
    'reg.field.fromHint': "Les profils, affectations et cas de test sont repris. D'une année à l'autre, classes, parcours et éléments se retrouvent par leurs codes.",
    'reg.field.fromNone': "Aucune : un profil complet par défaut",
    'reg.field.reason': "Motif",
    'reg.field.comment': "Commentaire",
    'reg.field.effectiveFrom': "En vigueur à partir du",
    'reg.field.fourEyes': "Validation à quatre yeux",
    'reg.field.fourEyesHint': "Validation à quatre yeux : qui soumet ne publie pas",
    'reg.field.cohortRule': "Règlement d'un étudiant",
    'reg.field.training': "Formation",
    'reg.field.chooseTraining': "Choisir une formation",
    'reg.field.choose': "Choisir",
    'reg.field.class': "Classe",
    'reg.field.allClasses': "Toutes les classes",
    'reg.field.student': "Étudiant",
    'reg.field.noStudent': "Aucun étudiant en particulier",
    'reg.field.element': "Élément",
    'reg.field.wholeTraining': "La formation entière",
    'reg.field.name': "Nom",
    'reg.cohort.current_year': "celui de l'année en cours",
    'reg.cohort.entry_year': "celui de son année d'entrée",
    'reg.version.label': "{year} · version {number}",
    'reg.version.new': "Nouvelle version",
    'reg.version.newSub': "Un brouillon, à revoir et soumettre à la validation",
    'reg.version.create': "Créer le brouillon",
    'reg.version.blank': "Version créée vierge",
    'reg.version.basedOn': "Copie de {label}",
    'reg.version.created': "saisie par {who} le {when}",
    'reg.version.submitted': "soumise par {who} le {when}",
    'reg.version.published': "publiée par {who} le {when}, en vigueur au {from}",
    'reg.version.settings': "Réglages de la version",
    'reg.version.copy': "Nouvelle version à partir de celle-ci",
    'reg.version.freeze': "Geler la version",
    'reg.version.freezeText': "Les délibérations de l'année sont closes : la version reste lisible et sert à expliquer les moyennes, elle ne se remplace plus.",
    'reg.version.delete': "Supprimer le brouillon",
    'reg.version.deleteText': "Le brouillon, ses profils, ses affectations et ses cas de test seront supprimés.",
    'reg.version.submit': "Demander la validation",
    'reg.version.submitText': "La version ne se modifie plus pendant la validation. Le directeur pédagogique la publie ou la renvoie en brouillon.",
    'reg.version.submitBlocked': "Corrigez d'abord les contrôles bloquants.",
    'reg.version.approve': "Valider et publier",
    'reg.version.reject': "Renvoyer en brouillon",
    'reg.version.fourEyesBlocked': "Vous avez soumis cette version : une autre personne doit la valider (quatre yeux).",
    'reg.version.inReview': "Soumise par {who}, en attente du directeur pédagogique.",
    'reg.version.publishedNotice': "Version publiée : elle s'applique à partir de sa date d'effet.",
    'reg.simulate': "Simuler",
    'reg.scopes.title': "Portées pédagogiques",
    'reg.scopes.sub': "La règle de chaque bloc vient de la portée la plus précise qui la définit ; à défaut, elle remonte jusqu'à l'établissement.",
    'reg.elements.title': "Éléments de la maquette",
    'reg.elements.sub': "Comment une UE ou un module calcule sa propre note.",
    'reg.elements.empty': "Aucun élément n'a de profil à lui. Pour en affecter un, choisissez la portée « Élément de la maquette ».",
    'reg.assign': "Affecter un profil",
    'reg.assign.edit': "Modifier l'affectation",
    'reg.criteria.title': "Seulement pour les étudiants dont",
    'reg.criteria.add': "Ajouter un critère",
    'reg.criteria.hint': "Toutes les conditions doivent tenir. À portée égale, une affectation avec critères est plus précise qu'une affectation sans critère.",
    'reg.locks.title': "Verrouiller pour les portées plus précises",
    'reg.tests.title': "Cas de test",
    'reg.tests.sub': "Relancés à chaque contrôle : un cas qui échoue bloque la publication.",
    'reg.tests.add': "Ajouter un cas",
    'reg.tests.empty': "Aucun cas de test.",
    'reg.tests.case': "Cas",
    'reg.tests.expected': "Attendu",
    'reg.tests.obtained': "Obtenu",
    'reg.tests.passed': "passe",
    'reg.tests.failed': "échoue",
    'reg.tests.notEnrolled': "pas inscrit dans cette formation",
    'reg.tests.expectedValue': "Note attendue",
    'reg.tests.expectedStatus': "Statut attendu",
    'reg.checks.title': "Contrôles de cohérence",
    'reg.checks.none': "Aucun contrôle en défaut.",
    'reg.checks.blocking': "{count} bloquant(s)",
    'reg.checks.warnings': "{count} avertissement(s)",
    'reg.check.institution_missing.title': "Établissement non couvert",
    'reg.check.institution_missing': "Affectez un profil complet à l'établissement.",
    'reg.check.institution_incomplete.title': "Blocs manquants à l'établissement",
    'reg.check.institution_incomplete': "Non définis : {blocks}.",
    'reg.check.duplicate_scope.title': "Portée en double",
    'reg.check.duplicate_scope': "{scope} reçoit plusieurs profils : {profiles}.",
    'reg.check.conflict.title': "Conflit de portée",
    'reg.check.conflict': "{scopes} définissent « {block} » autrement, à précision égale, pour {training}.",
    'reg.check.lock_violation.title': "Bloc verrouillé redéfini",
    'reg.check.lock_violation': "{scope} ({profile}) redéfinit « {block} », verrouillé par une portée plus large.",
    'reg.check.type_weights_total.title': "Poids qui ne font pas 100 %",
    'reg.check.type_weights_total': "{profile} : les types d'évaluation font {total} %.",
    'reg.check.semester_weights_total.title': "Poids des semestres",
    'reg.check.semester_weights_total': "{profile} : les semestres font {total} %.",
    'reg.check.unknown_type.title': "Type d'évaluation inconnu",
    'reg.check.unknown_type': "{profile} : le type {code} n'existe pas dans le référentiel.",
    'reg.check.unknown_absence.title': "Absence inconnue",
    'reg.check.unknown_absence': "{profile} : l'absence {code} n'existe pas dans le référentiel.",
    'reg.check.tests_failed.title': "Cas de test en échec",
    'reg.check.tests_failed': "{failed} cas sur {total} ne passent pas.",
    'reg.check.tests_passed.title': "Cas de test",
    'reg.check.tests_passed': "{total} cas sur {total} passent.",
    'reg.check.redundant.title': "Surcharge identique",
    'reg.check.redundant': "{scope} ({profile}) redéfinit « {block} » avec la valeur héritée.",
    'reg.check.unused_profile.title': "Profil non affecté",
    'reg.check.unused_profile': "{profile} n'est affecté à aucune portée.",
    'reg.check.floor_above_threshold.title': "Plancher au-dessus du seuil",
    'reg.check.floor_above_threshold': "{profile} : le plancher de compensation ({level}) dépasse le seuil de validation.",
    'reg.check.retake_above_threshold.title': "Seuil de rattrapage au-dessus du seuil",
    'reg.check.retake_above_threshold': "{profile} : le seuil de rattrapage ({level}) dépasse le seuil de validation.",
    'reg.profiles.title': "Profils de la version",
    'reg.profiles.new': "Nouveau profil",
    'reg.profiles.newSub': "Un profil partiel : définissez ensuite les blocs qu'il change.",
    'reg.profiles.complete': "complet",
    'reg.profiles.blocks': "{count} bloc(s)",
    'reg.profiles.used': "{count} affectation(s)",
    'reg.profile.blocks': "Blocs du profil",
    'reg.profile.defined': "défini",
    'reg.profile.inherited': "hérite",
    'reg.profile.define': "Définir dans ce profil",
    'reg.profile.definedHere': "Défini dans ce profil : il remplace la règle des portées moins précises.",
    'reg.profile.inheritedHint': "Non défini ici : la règle vient d'une portée moins précise.",
    'reg.profile.defaultIs': "Valeur par défaut de Kelassi : {summary}.",
    'reg.profile.unassigned': "Affecté à aucune portée",
    'reg.profile.assignedTo': "Affecté à : {scopes}",
    'reg.profile.saved': "Profil enregistré. Les contrôles de la version sont relancés.",
    'reg.profile.readOnly': "Version publiée ou en validation : le profil se lit, il ne se modifie plus.",
    'reg.ed.weighting.assessments': "Évaluations dans une matière",
    'reg.ed.weighting.typesHint': "en %",
    'reg.ed.weighting.missing': "Un type sans note",
    'reg.ed.weighting.bestOf': "{code} : ne garder que les N meilleures",
    'reg.ed.weighting.elements': "Éléments dans leur parent (modules dans l'UE, UE dans le semestre…)",
    'reg.ed.weighting.semesters': "Semestres dans l'année",
    'reg.ed.weighting.addSemester': "Ajouter un semestre",
    'reg.ed.weighting.bonusHint': "Seuls les points au-dessus du seuil d'un élément bonus comptent, ajoutés avant la division, comme l'ancien Kelassi.",
    'reg.ed.absences.hint': "Par défaut, chaque absence suit le traitement du référentiel. Changez-le ici pour cette portée seulement.",
    'reg.ed.absences.referential': "référentiel : {value}",
    'reg.ed.absences.asReferential': "Comme le référentiel",
    'reg.ed.thresholds.catalog': "Le seuil de chaque élément de la maquette",
    'reg.ed.thresholds.catalogHint': "10 par défaut, un élément peut avoir le sien",
    'reg.ed.thresholds.level': "Un seuil par niveau",
    'reg.ed.thresholds.levelHint': "le même pour tous les éléments du niveau",
    'reg.ed.thresholds.retakeHint': "Sous le seuil de rattrapage, l'élément est non validé tout de suite et n'a pas droit au rattrapage. Le seuil de rattrapage d'un élément de la maquette prime.",
    'reg.ed.level': "Niveau",
    'reg.ed.noThreshold': "sans seuil",
    'reg.ed.status': "Statut",
    'reg.ed.when': "Quand",
    'reg.ed.compensation.between': "Compenser entre les enfants d'un…",
    'reg.ed.compensation.submodule': "sous-module (ses matières)",
    'reg.ed.compensation.module': "module (ses matières, sous-modules)",
    'reg.ed.compensation.semester': "semestre (ses modules)",
    'reg.ed.compensation.year': "année (ses semestres)",
    'reg.ed.compensation.hint': "Un enfant sous son seuil est acquis par compensation si son parent atteint le sien, s'il est au-dessus du plancher et sans note éliminatoire, les mieux notés d'abord, dans la limite du nombre.",
    'reg.ed.compensation.capitalisation': "Un module ou une UE validé reste acquis si le semestre est échoué (capitalisation)",
    'reg.ed.retake.enabled': "Un élément non acquis part au rattrapage",
    'reg.ed.retake.averageHint': "comme l'ancien Kelassi",
    'reg.ed.retake.wholeGroup': "Si une matière d'un sous-module est en rattrapage, tout le sous-module l'est",
    'reg.ed.priors.best': "Reprendre la note acquise si elle est meilleure (crédits, redoublement)",
    'reg.ed.priors.none': "Ne rien reprendre",
    'reg.ed.rounding.evaluable': "Arrondir la note d'une matière",
    'reg.ed.rounding.aggregate': "Arrondir les modules, semestres, années",
    'reg.ed.rounding.hint': "Le calcul se fait en entiers, au millionième près ; l'arrondi s'applique là où il est demandé, puis à l'affichage.",
    'reg.ed.decisions.mentions': "Attribuer une mention (barème du domaine)",
    'reg.ed.decisions.kept': "Mention conservée après rattrapage",
    'reg.ed.decisions.removed': "Mention supprimée après rattrapage",
    'reg.ed.decisions.rank': "Calculer le rang dans la formation",
    'reg.ed.decisions.hint': "Admis si l'ensemble est validé ; admis avec dettes dans la limite ; rattrapage si un élément y part ; ajourné sinon.",
    'reg.sum.scale': "sur {outOf}, {display}",
    'reg.sum.weighting': "{assessments} ; type sans note : {missing} ; éléments : {elements} ; semestres : {semesters}",
    'reg.sum.absences.referential': "comme le référentiel",
    'reg.sum.thresholds.catalog': "seuils de la maquette",
    'reg.sum.thresholds.noEliminatory': "pas de note éliminatoire",
    'reg.sum.thresholds.eliminatory': "note éliminatoire {value} : {effect}",
    'reg.sum.compensation.none': "aucune compensation",
    'reg.sum.compensation.level': "{level} : plancher {floor}, au plus {max}",
    'reg.sum.retake': "{retained}, plafond {cap}",
    'reg.sum.retake.closed': "pas de rattrapage",
    'reg.sum.rounding': "matières : {evaluable} ; agrégats : {aggregate} ; affichage : {display} décimales",
    'reg.sum.decisions': "dettes : {debts} ; mention : {mentions} ; rang : {rank}",
    'reg.effective.open': "Règle effective",
    'reg.effective.title': "Règle effective",
    'reg.effective.sub': "Ce qui s'applique à une formation, une classe ou un étudiant, bloc par bloc, et d'où cela vient",
    'reg.effective.inForce': "La version en vigueur",
    'reg.effective.noVersion': "Aucune version publiée pour cette année.",
    'reg.effective.chooseTraining': "Choisissez une formation.",
    'reg.effective.student': "{name} : régime {regime}, inscription {mode}, classe(s) {classes}.",
    'reg.effective.locked': "verrouillé",
    'reg.effective.conflict': "En conflit avec {scopes}",
    'reg.effective.overridden': "Écarté par le verrou : {scopes}",
    'reg.effective.default': "valeur par défaut de Kelassi",
    'reg.effective.conditional': "Sous condition : selon l'étudiant",
    'reg.explain.open': "Voir l'explication d'une moyenne",
    'reg.explain.sub': "{training} · {year} · l'explication de la moyenne jusqu'aux notes d'évaluation",
    'reg.explain.noVersion': "Aucune version du règlement ne s'applique à cette année.",
    'reg.explain.notEnrolled': "Pas d'inscription active à expliquer.",
    'reg.explain.noMark': "pas de note",
    'reg.explain.weight': "poids {weight}",
    'reg.explain.threshold': "seuil {threshold}",
    'reg.explain.bonus': "bonus",
    'reg.explain.retake': "rattrapage : {after} remplace {before}",
    'reg.explain.prior': "note acquise reprise : {origin}",
    'reg.explain.rule': "règle : {block} · {scope} · {profile}",
    'reg.explain.coef': "coefficient {coef}",
    'reg.explain.debts': "Dettes : {list}",
    'reg.explain.footer': "{version} · moteur {engine} · calculé le {date}",
    'reg.sim.title': "Simuler la version {number}",
    'reg.sim.sub': "Appliquer la version aux notes réelles et la comparer à une autre, étudiant par étudiant. Rien n'est enregistré.",
    'reg.sim.compare': "Comparer avec",
    'reg.sim.noCompare': "Sans comparaison",
    'reg.sim.with': "Avec {label} ({state})",
    'reg.sim.running': "Calcul en cours…",
    'reg.sim.changes': "Ce qui change entre les deux versions",
    'reg.sim.changesSub': "En langage clair, portée par portée.",
    'reg.sim.chooseTraining': "Choisissez une formation pour voir l'effet sur ses étudiants.",
    'reg.sim.students': "Étudiants simulés",
    'reg.sim.values': "Moyennes qui changent",
    'reg.sim.valuesNote': "au centième, sur au moins un élément",
    'reg.sim.decisions': "Décisions qui changent",
    'reg.sim.decisionsNote': "{better} en mieux, {worse} en moins bien",
    'reg.sim.mentions': "Mentions qui changent",
    'reg.sim.before': "Moyenne comparée",
    'reg.sim.after': "Avec la version {number}",
    'reg.sim.decisionBefore': "Décision comparée",
    'reg.sim.decisionAfter': "Décision",
    'reg.sim.mention': "Mention",
    'reg.sim.rank': "rang {rank}",
    'reg.sim.note': "La simulation n'enregistre rien. Un résultat publié ne change que par un recalcul explicite, après publication de la version, et laisse une trace.",
    'reg.diff.none': "Aucune différence de règles.",
    'reg.diff.added': "nouveau profil « {profile} »",
    'reg.diff.removed': "profil « {profile} » retiré",
    'reg.diff.profile': "« {before} » devient « {after} »",
    'reg.approve.title': "Valider et publier la version {number}",
    'reg.approve.sub': "Règlement {year} · validation à quatre yeux",
    'reg.approve.by': "Demandée par {who}, le {when}",
    'reg.approve.noBlocking': "Aucun contrôle bloquant",
    'reg.approve.tests': "Cas de test : {passed} sur {total}",
    'reg.approve.simulation': "Voir la simulation",
    'reg.approve.note': "Une fois publiée, la version ne change plus. Elle archive la version précédente de l'année.",
    'violation.reg.open_version': "Une version de cette année est déjà en cours de rédaction ou de validation.",
    'violation.reg.not_draft': "Seul un brouillon se modifie ; une correction crée une nouvelle version.",
    'violation.reg.four_eyes': "Validation à quatre yeux : une autre personne que celle qui a soumis doit décider.",
    'violation.reg.blocking_checks': "Des contrôles bloquants restent à corriger.",
    'violation.reg.profile_in_use': "Ce profil est affecté : retirez d'abord ses affectations.",
    'violation.reg.profile_name_taken': "Un profil porte déjà ce nom dans cette version.",
    'violation.reg.invalid': "Valeur non valide.",
    'violation.reg.out_of_range': "Valeur hors des bornes.",
    'violation.reg.precision': "Deux décimales au plus.",
    'violation.reg.unknown_target': "Cible introuvable.",
    'violation.reg.type_weights_empty': "Donnez un poids à au moins un type d'évaluation.",
    'violation.reg.test_expectation': "Une note ou un statut attendu.",
    'violation.reg.year_closed': "Cette année est close.",
    'reg.print': "Imprimer",
    'reg.scope.training': "Formation",
    'reg.v.blocks_compensation': "annule toute la compensation du parent",
    'reg.ed.thresholds.general': "générale",
    'reg.ed.thresholds.generalLabel': "Note éliminatoire générale",
    'reg.ed.thresholds.eliminatoryHint': "Une note éliminatoire par niveau remplace la note générale pour ce niveau. Sous elle, l'élément n'est pas compensé.",
    'reg.ed.thresholds.exempt': "Les matières d'un sous-module échappent à la note éliminatoire",
    'journal.action.regulation.created': "Règlement : ajout",
    'journal.action.regulation.updated': "Règlement : modification",
    'journal.action.regulation.deleted': "Règlement : suppression",
    'journal.action.regulation.submitted': "Règlement soumis à validation",
    'journal.action.regulation.published': "Règlement publié",
    'journal.action.regulation.rejected': "Règlement renvoyé en brouillon",
    'journal.action.regulation.frozen': "Règlement gelé",
    'journal.action.deliberation.computed': "Résultats calculés",
    'journal.action.deliberation.closed': "Délibération close",
    'journal.action.deliberation.reopened': "Délibération rouverte",
    'journal.target.regulation': "Règlement",
    'journal.target.regulation_profile': "Profil du règlement",
    'journal.target.deliberation': "Délibération",
    'delib.title': "Délibérations",
    'delib.sub': "{year} · les résultats calculés et arrêtés, formation par formation, session par session",
    'delib.kpi.trainings': "Formations",
    'delib.kpi.trainingsNote': "à délibérer cette année",
    'delib.kpi.closed': "Closes",
    'delib.kpi.closedNote': "résultats arrêtés",
    'delib.kpi.open': "Ouvertes",
    'delib.kpi.openNote': "à clore",
    'delib.kpi.none': "Pas encore calculées",
    'delib.kpi.noneNote': "aucun calcul enregistré",
    'delib.field.year': "Année",
    'delib.list.empty': "Aucune formation dans votre portée cette année.",
    'delib.list.note': "Chaque calcul est enregistré, numéroté et tracé : la version du règlement, qui l’a lancé et pourquoi. Une délibération close ne se recalcule plus ; la rouvrir demande un motif.",
    'delib.col.training': "Formation et session",
    'delib.col.state': "État",
    'delib.col.lastRun': "Dernier calcul",
    'delib.col.enrolled': "Élèves",
    'delib.col.rank': "Rang",
    'delib.col.number': "N°",
    'delib.col.student': "Élève",
    'delib.col.year': "Moyenne",
    'delib.col.decision': "Décision",
    'delib.col.bonus': "Boni du jury",
    'delib.col.mention': "Mention",
    'delib.outcome.admitted': "Admis",
    'delib.outcome.admitted_with_debts': "Avec dettes",
    'delib.outcome.retake': "Rattrapage",
    'delib.outcome.not_admitted': "Ajournés",
    'delib.state.open': "Ouverte",
    'delib.state.closed': "Close",
    'delib.state.none': "Pas encore calculée",
    'delib.run.none': "aucun",
    'delib.run.number': "calcul n° {number}",
    'delib.run.legacy': "Ancien Kelassi",
    'delib.session.first': "Session normale",
    'delib.session.retake': "Session de rattrapage",
    'delib.session.label': "Session",
    'delib.head.noRun': "{year} · pas encore de calcul enregistré",
    'delib.head.run': "{year} · calcul n° {number} du {at} par {by} · règlement {regulation} · moteur {engine}",
    'delib.action.history': "Historique",
    'delib.action.compute': "Calculer",
    'delib.action.recompute': "Recalculer",
    'delib.action.close': "Clore la délibération",
    'delib.action.reopen': "Rouvrir",
    'delib.action.bonus': "Boni",
    'delib.closed.note': "Délibération close le {at} par {by} : les résultats sont arrêtés.",
    'delib.empty.compute': "Aucun calcul pour cette session. Lancez le premier calcul.",
    'delib.empty.read': "Aucun calcul pour cette session.",
    'delib.todo.title': "À traiter avant de clore",
    'delib.todo.ready': "Prête à être close",
    'delib.todo.unvalidated': "{count} évaluation(s) non validée(s)",
    'delib.todo.pending': "{count} élève(s) sans résultat",
    'delib.todo.near': "{count} élève(s) près du seuil",
    'delib.filter.label': "Filtrer les élèves",
    'delib.filter.all': "Tous",
    'delib.filter.review': "À examiner",
    'delib.filter.changed': "Ont bougé depuis le calcul n° {number}",
    'delib.filter.bonus': "Bonis du jury",
    'delib.tag.near': "près du seuil",
    'delib.tag.changed': "a bougé",
    'delib.bonus.marker': "Boni du jury",
    'delib.legend': "L’icône du jury marque un élève qui a reçu un boni. « À examiner » : moyenne à moins d’un demi-point du seuil, ou résultat manquant.",
    'delib.compute.title': "Calculer les résultats",
    'delib.compute.note': "Le calcul s’ajoute aux précédents, avec la version du règlement en vigueur. Les écarts avec le calcul précédent sont comptés et marqués.",
    'delib.field.reason': "Motif (facultatif)",
    'delib.field.reasonRequired': "Motif",
    'delib.field.comment': "Commentaire au procès-verbal",
    'delib.close.title': "Clore la délibération",
    'delib.close.validated': "Toutes les évaluations sont validées",
    'delib.close.pending': "{count} élève(s) sans résultat : « en attente » au procès-verbal",
    'delib.close.allResults': "Chaque élève a un résultat",
    'delib.close.bonuses': "{count} boni(s) du jury, tous motivés",
    'delib.close.counts': "{admitted} admis · {debts} avec dettes · {retake} au rattrapage · {failed} ajournés · {pending} en attente",
    'delib.close.note': "Une fois close, plus de calcul ni de boni ; le procès-verbal, les relevés et les attestations peuvent être produits. La rouvrir reste possible, avec un motif.",
    'delib.reopen.title': "Rouvrir la délibération",
    'delib.reopen.note': "Le motif est tracé ; chaque calcul ensuite en demandera un aussi. Les documents déjà produits restent dans l’historique.",
    'delib.bonus.title': "Boni du jury",
    'delib.bonus.note': "Le jury agit par des bonis : des points ajoutés à la moyenne d’un élément, dans la limite du règlement. La décision se déduit du nouveau calcul.",
    'delib.bonus.on': "Sur",
    'delib.bonus.points': "Points",
    'delib.bonus.cap': "au plus {cap} (règlement)",
    'delib.bonus.result': "Décision qui en résulte",
    'delib.bonus.element': "Élément",
    'delib.bonus.before': "Calculé",
    'delib.bonus.after': "Avec le boni",
    'delib.bonus.reasonHint': "Il figure au procès-verbal et au journal.",
    'delib.bonus.existing': "Bonis déjà accordés",
    'delib.bonus.remove': "Retirer",
    'delib.bonus.save': "Enregistrer le boni",
    'delib.kind.year': "Année",
    'delib.kind.semester': "Semestre",
    'delib.kind.module': "Module",
    'delib.kind.submodule': "Sous-module",
    'delib.kind.subject': "Matière",
    'delib.history.title': "Historique de la délibération",
    'delib.history.sub': "{training} · {session} · {year} : chaque calcul, chaque boni, chaque clôture, dans l’ordre",
    'delib.history.empty': "Aucune délibération pour cette session.",
    'delib.history.compare': "Ce qui a bougé",
    'delib.history.between': "Ce qui a bougé entre le calcul n° {from} et le calcul n° {to}",
    'delib.history.pair': "n° {from} et n° {to}",
    'delib.history.run': "Calcul n° {number}",
    'delib.history.single': "Un seul calcul : rien à comparer.",
    'delib.history.same': "Rien n’a bougé.",
    'delib.history.summary': "{values} note(s), {statuses} statut(s) et {decisions} décision(s) ont bougé, sur {students} élève(s).",
    'delib.history.note': "Rien ne s’efface : un calcul ne remplace pas le précédent, il s’y ajoute. La trace dit à chaque fois quelle version du règlement a servi, et pourquoi on a recalculé.",
    'journal.action.deliberation.bonus_granted': "Boni du jury accordé",
    'journal.action.deliberation.bonus_changed': "Boni du jury modifié",
    'journal.action.deliberation.bonus_removed': "Boni du jury retiré",
    'reg.f.juryBonusCap': "Boni du jury, au plus",
    'reg.ed.decisions.juryBonusHint': "Le jury agit par des bonis sur la moyenne d’un élément, dans cette limite ; vide : sans plafond.",
    'doc.title': "Documents",
    'doc.sub': "{year} · relevés, procès-verbaux et attestations produits depuis les délibérations closes",
    'doc.batches.title': "Documents produits",
    'doc.batches.empty': "Aucun document produit pour cette année.",
    'doc.col.document': "Document",
    'doc.col.for': "Pour",
    'doc.col.count': "Nombre",
    'doc.col.issued': "Produit",
    'doc.col.reference': "Référence",
    'doc.col.state': "État",
    'doc.col.actions': "Actions",
    'doc.students': "{count} élèves",
    'doc.state.open': "Délibération ouverte",
    'doc.state.superseded': "Remplacé",
    'doc.state.issued': "Produit",
    'doc.state.published': "{count} sur le portail",
    'doc.action.toDeliberation': "Voir la délibération",
    'doc.noneClosed': "Aucune délibération close cette année : les documents se produisent après la clôture.",
    'doc.note': "Chaque document produit est numéroté, tracé au journal (qui, quand, pour qui) et vérifiable par sa référence. Rouvrir une délibération rend ses documents remplacés.",
    'doc.action.preparing': "Préparation…",
    'doc.action.zip': "Archive zip",
    'doc.action.pdf': "PDF",
    'doc.action.excel': "Excel",
    'doc.action.publish': "Publier sur le portail",
    'doc.action.unpublish': "Retirer du portail",
    'doc.action.producing': "Production…",
    'doc.action.produce': "Produire",
    'doc.action.previewMinutes': "Aperçu et options du procès-verbal",
    'doc.action.one': "Produire pour un seul élève, avec aperçu",
    'doc.action.verify': "Vérifier",
    'doc.notice.published': "{count} document(s) publié(s) sur le portail.",
    'doc.notice.unpublished': "{count} document(s) retiré(s) du portail.",
    'doc.notice.issued': "{count} {type} : c'est fait.",
    'doc.notice.one': "Document produit : il est numéroté et tracé.",
    'doc.produce.title': "Produire pour une délibération close",
    'doc.field.deliberation': "Délibération",
    'doc.field.type': "Documents",
    'doc.field.for': "Pour",
    'doc.field.format': "Format",
    'doc.field.publish': "Publier sur le portail : chaque élève télécharge le sien",
    'doc.field.publishOne': "Publier sur le portail de l’élève",
    'doc.field.reference': "Référence",
    'doc.field.student': "Élève",
    'doc.field.lang': "Langue",
    'doc.field.sort': "Tri",
    'doc.for.deliberation': "La délibération",
    'doc.for.admitted': "Les élèves admis ({count})",
    'doc.for.all': "Tous les élèves ({count})",
    'doc.format.minutes': "PDF paysage et Excel",
    'doc.format.zip': "Un PDF par élève, en archive zip",
    'doc.type.transcript': "Relevé de notes",
    'doc.type.minutes': "Procès-verbal",
    'doc.type.certificate': "Attestation de réussite",
    'doc.types.transcript': "Relevés de notes",
    'doc.types.minutes': "Procès-verbal",
    'doc.types.certificate': "Attestations de réussite",
    'doc.verify.title': "Vérifier un document",
    'doc.verify.valid': "Authentique",
    'doc.verify.superseded': "Remplacé",
    'doc.verify.altered': "Non conforme",
    'doc.verify.unknown': "Référence inconnue",
    'doc.verify.unknownBody': "Aucun document ne porte la référence {reference}. Vérifiez la saisie.",
    'doc.verify.body': "{type} de {student}, {training} {year}, {institution} ; produit le {date} par {by} depuis le calcul n° {run}.",
    'doc.verify.supersededBody': "Il a été remplacé le {date} : il n'est plus valable.",
    'doc.verify.alteredBody': "Les résultats qu’il porte ne correspondent plus à ceux arrêtés : signalez-le à l’établissement.",
    'doc.verify.publicTitle': "Vérifier un document",
    'doc.verify.publicSub': "Relevé de notes, procès-verbal ou attestation : saisissez la référence imprimée sous le code QR.",
    'doc.sort.rank': "Par rang",
    'doc.sort.name': "Par nom",
    'doc.flag.transcript.rank': "Afficher le rang",
    'doc.flag.transcript.credits': "Afficher les crédits",
    'doc.flag.transcript.details': "Détailler les matières",
    'doc.flag.transcript.first': "Inclure la session normale",
    'doc.flag.minutes.details': "Détailler les modules (Excel : une feuille par semestre)",
    'doc.flag.minutes.bonuses': "Bonis du jury et motifs",
    'doc.flag.certificate.mention': "Afficher la mention",
    'doc.preview': "Aperçu du document",
    'doc.produceOne.transcript': "Produire le relevé",
    'doc.produceOne.minutes': "Produire le procès-verbal",
    'doc.produceOne.certificate': "Produire l’attestation",
    'doc.produceOne.transcriptNote': "Le relevé se produit depuis une délibération close : il reprend exactement les résultats arrêtés et porte une référence vérifiable.",
    'doc.produceOne.minutesNote': "Le procès-verbal reprend la grille de la délibération close, les bonis motivés et les signataires.",
    'doc.produceOne.certificateNote': "Seulement pour un élève admis d’une délibération close. Chaque attestation produite est numérotée et tracée : qui, quand, pour qui.",
    'journal.action.document.issued': "Documents produits",
    'journal.action.document.published': "Documents publiés sur le portail",
    'journal.action.document.unpublished': "Documents retirés du portail",
    'journal.action.document.settings_changed': "Réglages des documents modifiés",
    'journal.target.document': "Documents",
    'doc.verify.bodyMinutes': "{type}, {training} {year}, {institution} ; produit le {date} par {by} depuis le calcul n° {run}.",
    'scol.tab.file': "Dossier",
    'scol.tab.marks': "Notes",
    'scol.tab.finance': "Finance",
    'scol.tab.history': "Historique",
    'trace.filter.kind': "Type d’événement",
    'trace.filter.all': "Tout",
    'trace.filter.changes': "Modifications",
    'trace.filter.accesses': "Consultations",
    'trace.filter.table': "Partie de la fiche",
    'trace.filter.allTables': "Toute la fiche",
    'trace.filter.actor': "Auteur",
    'trace.filter.allActors': "Tout le monde",
    'trace.empty': "Rien dans l’historique pour ces filtres.",
    'trace.access.student': "a consulté la fiche de l’élève",
    'trace.access.marks': "a consulté les notes",
    'trace.access.results': "a consulté les résultats",
    'trace.access.documents': "a ouvert un document",
    'trace.access.finance': "a consulté le compte",
    'trace.access.exports': "a tiré un export",
    'trace.op.insert': "a créé",
    'trace.op.update': "a modifié",
    'trace.op.delete': "a supprimé",
    'trace.opShort.insert': "Créée",
    'trace.opShort.update': "Modifiée",
    'trace.opShort.delete': "Supprimée",
    'trace.more': "{count} champ(s) de plus",
    'trace.less': "Moins de détails",
    'trace.older': "Plus ancien",
    'trace.request.all': "tout ce que cette action a écrit",
    'trace.request.title': "Ce que cette action a écrit",
    'trace.request.who': "Qui",
    'trace.request.when': "Quand",
    'trace.request.where': "D’où",
    'trace.request.id': "Requête",
    'trace.request.record': "Fiche",
    'trace.request.what': "Quoi",
    'trace.request.operation': "Opération",
    'trace.request.change': "Changement",
    'trace.request.note': "Une action peut écrire plusieurs fiches à la fois : elles partagent la même requête, et se lisent ensemble ici.",
    'trace.aside.title': "Ce que l’historique garde",
    'trace.aside.changes': "Chaque modification, avant et après, même faite hors de l’application : la base l’écrit elle-même.",
    'trace.aside.accesses': "Les consultations de la fiche, des notes, des résultats et des documents, si l’établissement les trace.",
    'trace.aside.retention': "Cinq ans, puis l’historique se purge.",
    'trace.aside.locked': "Personne ne peut modifier ni effacer l’historique, pas même un administrateur de l’application.",
    'trace.actor.console': "Console du serveur",
    'trace.actor.sql': "Hors application (SQL)",
    'trace.actor.unknown': "Inconnu",
    'trace.source.api': "Depuis l’application",
    'trace.source.console': "Depuis la console du serveur",
    'trace.source.sql': "Hors application",
    'trace.value.yes': "oui",
    'trace.value.no': "non",
    'trace.console.title': "Traçabilité",
    'trace.console.sub': "{institution} · ce que la plateforme garde de chaque action",
    'trace.console.saved': "Réglage enregistré.",
    'trace.console.reads': "Consultations",
    'trace.console.readsSub': "Réglable pour cet établissement",
    'trace.console.readsToggle': "Tracer les consultations des données sensibles",
    'trace.console.readsHint': "Qui a lu quoi, et quand. Selon l’établissement, ce n’est pas toujours requis.",
    'trace.console.writes': "Modifications",
    'trace.console.writesBody': "Toujours tracées. Chaque création, modification ou suppression est écrite par la base elle-même, avant et après, y compris en dehors de l’application. Ce n’est pas un réglage.",
    'trace.console.retention': "Conservation",
    'trace.console.duration': "Durée",
    'trace.console.years': "{count} ans",
    'trace.console.durationHint': "minimum imposé par la base",
    'trace.console.oldest': "Plus ancienne entrée",
    'trace.console.oldestHint': "depuis la mise en service",
    'trace.console.purge': "Purge",
    'trace.console.monthly': "Mensuelle",
    'trace.console.purgeHint': "le 1er de chaque mois",
    'trace.console.purgeNote': "La purge retire seulement ce qui a plus de cinq ans. Personne ne peut raccourcir cette durée ni effacer une entrée plus récente.",
    'trace.category.student': "Fiche de l’élève",
    'trace.category.studentHint': "Identité, adresses, contacts, inscriptions",
    'trace.category.marks': "Notes",
    'trace.category.marksHint': "Relevé de l’élève, feuille d’une évaluation",
    'trace.category.results': "Résultats",
    'trace.category.resultsHint': "Délibérations, résultats d’un élève",
    'trace.category.documents': "Documents",
    'trace.category.documentsHint': "Relevés, procès-verbaux, attestations téléchargés",
    'trace.category.finance': "Finance",
    'trace.category.financeHint': "Compte de l’élève, factures, reçus",
    'trace.category.exports': "Exports",
    'trace.category.exportsHint': "Feuilles Excel, archives, journaux exportés",
    'journal.action.trace.settings_changed': "Réglage de la traçabilité modifié",
    'journal.target.trace': "Traçabilité",
    'trace.table.person': "Dossier",
    'trace.table.student': "Fiche élève",
    'trace.table.enrolment': "Inscription",
    'trace.table.enrolment_element': "Élément d’inscription",
    'trace.table.enrolment_event': "Événement d’inscription",
    'trace.table.class_member': "Classe",
    'trace.table.person_address': "Adresse",
    'trace.table.person_phone': "Téléphone",
    'trace.table.person_email': "Courriel",
    'trace.table.contact': "Contact",
    'trace.table.contact_relationship': "Parent ou contact",
    'trace.table.student_diploma': "Diplôme",
    'trace.table.assessment_mark': "Note",
    'trace.table.mark_correction': "Correction de note",
    'trace.table.assessment': "Évaluation",
    'trace.table.jury_bonus': "Boni du jury",
    'trace.table.issued_document': "Document produit",
    'trace.table.invoice': "Facture",
    'trace.table.invoice_line': "Ligne de facture",
    'trace.table.invoice_installment': "Échéance",
    'trace.table.payment': "Paiement",
    'trace.table.credit_note': "Avoir",
    'trace.table.user_account': "Compte",
    'trace.table.role_assignment': "Attribution de rôle",
    'trace.table.deliberation': "Délibération",
    'trace.table.result_run': "Calcul des résultats",
    'trace.table.admin_audit_log': "Journal des actions",
    'trace.field.last_name': "Nom",
    'trace.field.first_names': "Prénoms",
    'trace.field.sex': "Sexe",
    'trace.field.birth_date': "Date de naissance",
    'trace.field.national_id_number': "N° de CIN",
    'trace.field.passport_number': "N° de passeport",
    'trace.field.occupation': "Profession",
    'trace.field.enrolment_number': "N° d’étudiant",
    'trace.field.massar_code': "Code Massar",
    'trace.field.national_code': "Code national",
    'trace.field.street': "Rue",
    'trace.field.city': "Ville",
    'trace.field.postal_code': "Code postal",
    'trace.field.country': "Pays",
    'trace.field.number': "Numéro",
    'trace.field.address': "Adresse",
    'trace.field.kind': "Type",
    'trace.field.training_id': "Formation",
    'trace.field.academic_year_id': "Année",
    'trace.field.class_group_id': "Classe",
    'trace.field.track_id': "Parcours",
    'trace.field.mode': "Mode",
    'trace.field.regime': "Régime",
    'trace.field.state': "État",
    'trace.field.scholarship': "Boursier",
    'trace.field.repeating': "Redoublant",
    'trace.field.enrolled_on': "Inscrit le",
    'trace.field.assigned_on': "Affecté le",
    'trace.field.value': "Note",
    'trace.field.bonus': "Bonus",
    'trace.field.entered_at': "Saisie le",
    'trace.field.absence_type_id': "Absence",
    'trace.field.assessment_id': "Évaluation",
    'trace.field.entered_by_id': "Saisie par",
    'trace.field.value_before': "Ancienne note",
    'trace.field.value_after': "Nouvelle note",
    'trace.field.reason': "Motif",
    'trace.field.points': "Points",
    'trace.field.element_id': "Élément",
    'trace.field.label': "Libellé",
    'trace.field.name': "Nom",
    'trace.field.email': "Courriel",
    'trace.field.display_name': "Nom affiché",
    'trace.field.password_hash': "Mot de passe",
    'trace.field.amount': "Montant",
    'trace.field.reference': "Référence",
    'trace.field.published_at': "Publié le",
    'trace.field.superseded_at': "Remplacé le",
    'trace.field.school': "Établissement",
    'trace.field.year': "Année",
    'trace.field.average': "Moyenne",
    'trace.field.mention': "Mention",
    'trace.field.integrated_on': "Intégré le",
    'trace.field.person_id': "Personne",
    'trace.field.student_id': "Élève",
    'trace.value.personal': "personnel",
    'trace.value.professional': "professionnel",
    'trace.value.home': "domicile",
    'trace.value.active': "active",
    'trace.value.abandoned': "abandonnée",
    'trace.value.M': "masculin",
    'trace.value.F': "féminin",
    'trace.value.***': "(masqué)",
    'asm.field.counted': "Compter dans la moyenne",
    'asm.field.countedHint': "Décochez pour une évaluation de diagnostic, un examen blanc ou une épreuve de cours d’été : ses notes restent sur la feuille et le relevé, marquées « non comptée », mais n’entrent dans aucune moyenne ni décision.",
    'asm.notCounted': "non comptée",
    'asm.notCountedHint': "Ses notes n’entrent dans aucune moyenne ni décision.",
    'doc.settings.title': "Réglages des documents",
    'doc.settings.sub': "{institution} · l’en-tête, le lieu et les signataires de chaque document",
    'doc.settings.saved': "Réglages enregistrés. Ils s’appliquent aux prochains documents produits.",
    'doc.settings.invalid': "Vérifiez les champs signalés : chaque signataire a une qualité.",
    'doc.settings.head': "En-tête et lieu",
    'doc.settings.tagline': "Mention sous le nom",
    'doc.settings.taglineHint': "Imprimée sous le nom de l’établissement",
    'doc.settings.place': "Lieu de signature",
    'doc.settings.placeHint': "« Fait à Tanger, le … »",
    'doc.settings.hint.minutes': "Dans l’ordre où ils signent ; la qualité seule laisse la place d’une signature manuscrite",
    'doc.settings.hint.transcript': "Sans signataire réglé : « Pour la direction » et le nom de qui produit",
    'doc.settings.hint.certificate': "La qualité du premier signataire ouvre la phrase : « Le directeur pédagogique de … atteste que »",
    'doc.settings.default.minutes': "Aucun signataire réglé : le procès-verbal portera quatre qualités par défaut (président du jury, directeur pédagogique, responsable des examens, membre du jury).",
    'doc.settings.default.transcript': "Aucun signataire réglé : « Pour la direction » et le nom de qui produit.",
    'doc.settings.default.certificate': "Aucun signataire réglé : « La direction de l’établissement … atteste que ».",
    'doc.settings.titleField': "Qualité",
    'doc.settings.nameField': "Nom (facultatif)",
    'doc.settings.up': "Monter",
    'doc.settings.down': "Descendre",
    'doc.settings.add': "Ajouter un signataire",
    'doc.settings.preview': "Aperçu du bas de page",
    'doc.settings.signature': "signature",
    'doc.settings.note': "Les réglages sont recopiés dans chaque document à sa production : les changer ne modifie aucun document déjà produit.",
    'trace.group': "{count} × {what}",
    'trace.detail': "voir le détail",
    'trace.drawer.title': "Historique",
    'trace.drawer.assessment': "{label} · ses notes, sa validation, ses corrections",
    'trace.drawer.invoice': "Facture {number} · ses lignes, échéances, paiements et avoirs",
    'trace.drawer.account': "{name} · son compte, ses rôles, ses accès",
    'trace.drawer.open': "Historique",
    'trace.field.validated_at': "Validée le",
    'trace.field.validated_by_id': "Validée par",
    'trace.field.validated_by_name': "Validée par",
    'trace.field.corrected_at': "Corrigée le",
    'trace.field.corrected_by_name': "Corrigée par",
    'trace.field.mark_id': "Note",
    'trace.field.counted': "Comptée dans la moyenne",
    'trace.field.retake': "Rattrapage",
    'trace.field.coefficient': "Coefficient",
    'trace.field.out_of': "Noté sur",
    'trace.field.held_on': "Date",
    'trace.field.subject_id': "Matière",
    'trace.field.type_id': "Type",
    'trace.field.nature': "Nature",
    'trace.field.starts_at': "Début",
    'trace.field.ends_at': "Fin",
    'trace.field.issued_on': "Émise le",
    'trace.field.received_on': "Reçu le",
    'trace.field.method': "Mode",
    'trace.field.due_on': "Échéance",
    'trace.field.active': "Actif",
    'trace.field.role_id': "Rôle",
    'trace.field.locale': "Langue",
    'trace.field.population': "Population",
    'trace.table.assignment_product': "Portée : formation",
    'trace.table.assignment_domain': "Portée : domaine",
    'trace.table.assignment_cycle': "Portée : cycle",
    'trace.table.user_identity': "Identité de connexion",
    'trace.table.two_factor_recovery_code': "Code de secours",
    'trace.table.assessment_class': "Classe convoquée",
    'trace.table.assessment_responsible': "Responsable de l’évaluation",
    'trace.table.absence_justification': "Justificatif d’absence",
    'trace.table.absence_type': "Type d’absence",
    'trace.table.academic_year': "Année universitaire",
    'trace.table.account_invitation': "Invitation de compte",
    'trace.table.assessment_type': "Type d’évaluation",
    'trace.table.assignment_center': "Portée : centre",
    'trace.table.attendance_record': "Présence",
    'trace.table.attendance_waiver': "Dispense d’assiduité",
    'trace.table.calendar_period': "Période du calendrier",
    'trace.table.chat_message': "Message de discussion",
    'trace.table.class_group': "Classe",
    'trace.table.communication_attachment': "Pièce jointe",
    'trace.table.communication_settings': "Réglages de la communication",
    'trace.table.contact_type': "Type de contact",
    'trace.table.conversation': "Conversation",
    'trace.table.conversation_member': "Participant à la conversation",
    'trace.table.department': "Département",
    'trace.table.diploma': "Diplôme",
    'trace.table.diploma_option': "Option de diplôme",
    'trace.table.disciplinary_field': "Champ disciplinaire",
    'trace.table.discount': "Remise",
    'trace.table.discount_service': "Prestation remisée",
    'trace.table.document_settings': "Réglages des documents",
    'trace.table.document_signatory': "Signataire",
    'trace.table.employee': "Employé",
    'trace.table.fee_service': "Prestation",
    'trace.table.fee_service_cycle': "Prestation : cycle",
    'trace.table.fee_service_level': "Prestation : niveau",
    'trace.table.finance_archive_credit': "Avoir archivé",
    'trace.table.finance_archive_invoice': "Facture archivée",
    'trace.table.finance_archive_line': "Ligne de facture archivée",
    'trace.table.finance_archive_payment': "Paiement archivé",
    'trace.table.finance_settings': "Réglages de la facturation",
    'trace.table.identity_provider': "Fournisseur d’identité",
    'trace.table.institution': "Établissement",
    'trace.table.letter_grade': "Lettre de notation",
    'trace.table.letter_scale': "Barème en lettres",
    'trace.table.login_policy': "Politique de connexion",
    'trace.table.mention_band': "Tranche de mention",
    'trace.table.mention_scale': "Barème des mentions",
    'trace.table.menu_item': "Entrée de menu",
    'trace.table.menu_item_translation': "Traduction d’entrée de menu",
    'trace.table.notification': "Notification",
    'trace.table.notification_recipient': "Destinataire de notification",
    'trace.table.notification_reply': "Réponse à une notification",
    'trace.table.notification_type': "Type de notification",
    'trace.table.organisation': "Organisation",
    'trace.table.pay_adjustment': "Ajustement de paie",
    'trace.table.pay_statement': "Bulletin de paie",
    'trace.table.payment_schedule': "Échéancier",
    'trace.table.payment_schedule_line': "Ligne d’échéancier",
    'trace.table.payroll_settings': "Réglages de la paie",
    'trace.table.permission': "Permission",
    'trace.table.permission_translation': "Traduction de permission",
    'trace.table.planning_event': "Séance",
    'trace.table.planning_event_class': "Classe de la séance",
    'trace.table.planning_event_room': "Salle de la séance",
    'trace.table.planning_event_student': "Élève de la séance",
    'trace.table.planning_event_teacher': "Enseignant de la séance",
    'trace.table.product': "Formation ou élément",
    'trace.table.product_equivalence': "Équivalence",
    'trace.table.product_hours': "Volume horaire",
    'trace.table.product_price': "Tarif",
    'trace.table.product_responsible': "Responsable d’élément",
    'trace.table.regulation_assignment': "Affectation du règlement",
    'trace.table.regulation_profile': "Profil de règlement",
    'trace.table.regulation_test_case': "Cas de test du règlement",
    'trace.table.regulation_version': "Version du règlement",
    'trace.table.relations_agreement': "Convention",
    'trace.table.relations_agreement_commitment': "Engagement de convention",
    'trace.table.relations_agreement_training': "Formation de la convention",
    'trace.table.relations_agreement_type': "Type de convention",
    'trace.table.relations_alumni_position': "Situation d’ancien élève",
    'trace.table.relations_contact': "Contact de l’organisation",
    'trace.table.relations_contact_function': "Fonction de contact",
    'trace.table.relations_exchange': "Échange",
    'trace.table.relations_exchange_account': "Participant à l’échange",
    'trace.table.relations_exchange_contact': "Contact de l’échange",
    'trace.table.relations_org_type': "Type d’organisation",
    'trace.table.relations_profile': "Profil de relation",
    'trace.table.relations_prospect': "Prospection",
    'trace.table.relations_prospect_stage': "Étape de prospection",
    'trace.table.relations_prospect_step': "Passage d’étape",
    'trace.table.relations_sector': "Secteur d’activité",
    'trace.table.relations_settings': "Réglages des relations",
    'trace.table.relations_survey_token': "Lien d’enquête",
    'trace.table.responsible_type': "Type de responsable",
    'trace.table.role': "Rôle",
    'trace.table.role_permission': "Permission du rôle",
    'trace.table.role_translation': "Traduction de rôle",
    'trace.table.room': "Salle",
    'trace.table.session_series': "Série de séances",
    'trace.table.session_series_class': "Classe de la série",
    'trace.table.session_series_teacher': "Enseignant de la série",
    'trace.table.structure_level': "Niveau de structure",
    'trace.table.structure_template': "Modèle de structure",
    'trace.table.student_numbering': "Numérotation des élèves",
    'trace.table.tax': "Taxe",
    'trace.table.teacher': "Enseignant",
    'trace.table.teacher_category': "Catégorie d’enseignant",
    'trace.table.teacher_contract': "Contrat d’enseignant",
    'trace.table.teacher_contract_rate': "Taux du contrat",
    'trace.table.teacher_contract_type': "Type de contrat",
    'trace.table.teacher_diploma': "Diplôme de l’enseignant",
    'trace.table.teacher_disciplinary_field': "Champ disciplinaire de l’enseignant",
    'trace.table.teacher_objective': "Objectif horaire",
    'trace.table.teaching_type': "Type d’enseignement",
    'trace.table.trace_settings': "Réglages de la traçabilité",
    'trace.table.training_center': "Centre de formation",
    'trace.table.training_cycle': "Cycle de formation",
    'trace.table.training_domain': "Domaine de formation",
    'trace.table.training_level': "Niveau de formation",
    'trace.table.training_session': "Session de formation",
    'trace.table.training_track': "Parcours",
    'trace.table.training_track_element': "Élément du parcours",
    'trace.table.venue': "Site",
    'trace.field.abbreviation': "Abréviation",
    'trace.field.absence_after': "Nouvelle absence",
    'trace.field.absence_before': "Ancienne absence",
    'trace.field.accepted_at': "Acceptée le",
    'trace.field.account_id': "Compte",
    'trace.field.acronym': "Sigle",
    'trace.field.added_at': "Ajoutée le",
    'trace.field.added_by_id': "Ajoutée par",
    'trace.field.agreement_id': "Convention",
    'trace.field.agreement_window_days': "Préavis d’échéance des conventions (jours)",
    'trace.field.alert_key': "Clé d’alerte",
    'trace.field.alert_target': "Cible de l’alerte",
    'trace.field.allocation': "Ventilation",
    'trace.field.allowed_domains': "Domaines autorisés",
    'trace.field.amount_excluding_tax': "Montant hors taxe",
    'trace.field.amount_gross': "Montant brut",
    'trace.field.amount_net': "Montant net",
    'trace.field.answered_at': "Répondue le",
    'trace.field.archived_at': "Archivé le",
    'trace.field.at': "Date",
    'trace.field.attachments_max_mb': "Taille maximale des pièces jointes (Mo)",
    'trace.field.author_id': "Auteur",
    'trace.field.author_name': "Auteur",
    'trace.field.auto_provision': "Création automatique des comptes",
    'trace.field.avatar_mime': "Format de la photo",
    'trace.field.avatar_version': "Version de la photo",
    'trace.field.bank': "Banque",
    'trace.field.based_on_id': "Basée sur",
    'trace.field.batch_id': "Lot",
    'trace.field.blocking': "Bloquante",
    'trace.field.blocks': "Blocs",
    'trace.field.body': "Contenu",
    'trace.field.business_id': "Identifiant fiscal",
    'trace.field.by_id': "Par",
    'trace.field.by_name': "Par",
    'trace.field.can_deliberate': "Peut délibérer",
    'trace.field.can_enter_marks': "Peut saisir les notes",
    'trace.field.can_validate_marks': "Peut valider les notes",
    'trace.field.cancel_reason': "Motif d’annulation",
    'trace.field.cancelled': "Annulé",
    'trace.field.cancelled_at': "Annulé le",
    'trace.field.cancelled_by_id': "Annulé par",
    'trace.field.capacity': "Capacité",
    'trace.field.capacity_max': "Capacité maximale",
    'trace.field.capacity_min': "Capacité minimale",
    'trace.field.categories': "Catégories",
    'trace.field.category_id': "Catégorie",
    'trace.field.changes': "Changements",
    'trace.field.chat_email_digest': "Résumé des discussions par courriel",
    'trace.field.class_groups': "Classes",
    'trace.field.client_id': "Identifiant client",
    'trace.field.client_secret_encrypted': "Secret client",
    'trace.field.closed_at': "Clôturé le",
    'trace.field.closed_by_id': "Clôturé par",
    'trace.field.closed_by_name': "Clôturé par",
    'trace.field.closed_on': "Clôturée le",
    'trace.field.code': "Code",
    'trace.field.code_hash': "Code",
    'trace.field.cohort_rule': "Règle de cohorte",
    'trace.field.color': "Couleur",
    'trace.field.comment': "Commentaire",
    'trace.field.compensation_rule': "Règle de compensation",
    'trace.field.computed_at': "Calculé le",
    'trace.field.computed_by_id': "Calculé par",
    'trace.field.computed_by_name': "Calculé par",
    'trace.field.contact_id': "Contact",
    'trace.field.contact_type_id': "Type de contact",
    'trace.field.content': "Contenu",
    'trace.field.contract_id': "Contrat",
    'trace.field.conversation_id': "Conversation",
    'trace.field.copied_from': "Copié de",
    'trace.field.corrected_by_id': "Corrigée par",
    'trace.field.counter_reset': "Remise à zéro du compteur",
    'trace.field.counterpart_id': "Contrepartie",
    'trace.field.created_by_id': "Créé par",
    'trace.field.created_by_name': "Créé par",
    'trace.field.credit': "Avoir",
    'trace.field.credit_note_pattern': "Modèle de numéro d’avoir",
    'trace.field.credits': "Crédits",
    'trace.field.credits_earned': "Acquisition des crédits",
    'trace.field.criteria': "Critères",
    'trace.field.currency': "Devise",
    'trace.field.current': "En cours",
    'trace.field.current_run_id': "Calcul en vigueur",
    'trace.field.customized': "Personnalisé",
    'trace.field.cycle_average_rule': "Règle de moyenne du cycle",
    'trace.field.cycle_id': "Cycle",
    'trace.field.date_corrected': "Date corrigée",
    'trace.field.decided_at': "Décidé le",
    'trace.field.decided_by_id': "Décidé par",
    'trace.field.decided_by_name': "Décidé par",
    'trace.field.decimals': "Décimales",
    'trace.field.decision_comment': "Commentaire de la décision",
    'trace.field.default_compensation': "Compensation par défaut",
    'trace.field.default_locale': "Langue par défaut",
    'trace.field.delegate_id': "Délégué",
    'trace.field.deliberation_id': "Délibération",
    'trace.field.department_id': "Département",
    'trace.field.description': "Description",
    'trace.field.digest': "Empreinte",
    'trace.field.digested_at': "Résumé envoyé le",
    'trace.field.diploma_id': "Diplôme",
    'trace.field.diploma_option_id': "Option de diplôme",
    'trace.field.disciplinary_field_id': "Champ disciplinaire",
    'trace.field.discount_amount': "Montant de la remise",
    'trace.field.discount_id': "Remise",
    'trace.field.discount_rate': "Taux de remise",
    'trace.field.discount_scope': "Portée de la remise",
    'trace.field.document_name': "Document",
    'trace.field.document_size': "Taille du document",
    'trace.field.document_type': "Type de document",
    'trace.field.domain_id': "Domaine",
    'trace.field.drawer': "Tireur",
    'trace.field.due_day': "Jour d’échéance",
    'trace.field.due_kind': "Type d’échéance",
    'trace.field.due_month': "Mois d’échéance",
    'trace.field.duration_years': "Durée (années)",
    'trace.field.effective_from': "En vigueur à partir de",
    'trace.field.email_at_provider': "Courriel chez le fournisseur",
    'trace.field.email_canonical': "Courriel",
    'trace.field.email_default': "Courriel par défaut",
    'trace.field.email_failed': "Échec du courriel",
    'trace.field.emailed_at': "Envoyé par courriel le",
    'trace.field.employee_number': "Matricule",
    'trace.field.enabled': "Activé",
    'trace.field.enabled_at': "Activé le",
    'trace.field.enabled_by_id': "Activé par",
    'trace.field.enabled_locales': "Langues activées",
    'trace.field.ends_on': "Fin",
    'trace.field.engine_version': "Version du moteur",
    'trace.field.enrollable': "Ouverte aux inscriptions",
    'trace.field.enrolment_id': "Inscription",
    'trace.field.event_id': "Séance",
    'trace.field.every_weeks': "Toutes les (semaines)",
    'trace.field.exchange_id': "Échange",
    'trace.field.expected_status': "Statut attendu",
    'trace.field.expected_value': "Valeur attendue",
    'trace.field.expires_at': "Expire le",
    'trace.field.extra_cap_hours': "Plafond d’heures supplémentaires",
    'trace.field.extra_minutes': "Minutes supplémentaires",
    'trace.field.extra_rate_gross': "Taux supplémentaire brut",
    'trace.field.extra_rate_net': "Taux supplémentaire net",
    'trace.field.failed_attempts': "Tentatives échouées",
    'trace.field.fax': "Télécopieur",
    'trace.field.fee_service_id': "Prestation",
    'trace.field.first_on': "Première séance",
    'trace.field.footer': "Pied de page",
    'trace.field.four_eyes': "Double validation",
    'trace.field.from_student': "De l’élève",
    'trace.field.frozen_at': "Figée le",
    'trace.field.function_id': "Fonction",
    'trace.field.given_by_id': "Assurée par",
    'trace.field.grace_days': "Délai de grâce (jours)",
    'trace.field.grade': "Grade",
    'trace.field.graduation_year': "Année d’obtention du diplôme",
    'trace.field.granted_at': "Accordé le",
    'trace.field.granted_by_id': "Accordé par",
    'trace.field.granted_by_name': "Accordé par",
    'trace.field.granted_on': "Accordé le",
    'trace.field.gross': "Brut",
    'trace.field.guests': "Invités",
    'trace.field.handled_at': "Traité le",
    'trace.field.happened_at': "Date et heure",
    'trace.field.happened_on': "Date",
    'trace.field.has_graduated': "Diplômé",
    'trace.field.has_outstanding_issue': "Dossier en suspens",
    'trace.field.has_special_needs': "Besoins particuliers",
    'trace.field.hours': "Heures",
    'trace.field.icon': "Icône",
    'trace.field.identity_provider_id': "Fournisseur d’identité",
    'trace.field.included_hours': "Heures incluses",
    'trace.field.internship': "Stage",
    'trace.field.invited_by_id': "Invité par",
    'trace.field.invoice_id': "Facture",
    'trace.field.invoice_pattern': "Modèle de numéro de facture",
    'trace.field.is_default': "Par défaut",
    'trace.field.is_employee': "Salarié",
    'trace.field.is_officer': "Militaire",
    'trace.field.is_private': "Privé",
    'trace.field.issued_at': "Émis le",
    'trace.field.issued_by_id': "Émis par",
    'trace.field.issued_by_name': "Émis par",
    'trace.field.issuer': "Émetteur",
    'trace.field.job_title': "Poste",
    'trace.field.joined_at': "Arrivé le",
    'trace.field.justification_id': "Justificatif",
    'trace.field.kenrol_identifier': "Identifiant Kenrol",
    'trace.field.last_exchange_at': "Dernier échange",
    'trace.field.last_login_at': "Dernière connexion",
    'trace.field.last_message_at': "Dernier message",
    'trace.field.last_on': "Dernière séance",
    'trace.field.last_read_at': "Dernière lecture",
    'trace.field.last_reply_at': "Dernière réponse",
    'trace.field.last_used_at': "Dernière utilisation",
    'trace.field.late_minutes': "Minutes de retard",
    'trace.field.left_at': "Parti le",
    'trace.field.legacy': "Reprise de l’ancien système",
    'trace.field.legal_form': "Forme juridique",
    'trace.field.legal_name': "Raison sociale",
    'trace.field.letter': "Lettre",
    'trace.field.letter_scale_id': "Barème en lettres",
    'trace.field.level_id': "Niveau",
    'trace.field.lines': "Lignes",
    'trace.field.locked_until': "Verrouillé jusqu’au",
    'trace.field.locks': "Verrous",
    'trace.field.lost': "Perdue",
    'trace.field.main': "Principal",
    'trace.field.mandatory': "Obligatoire",
    'trace.field.mark': "Pointage",
    'trace.field.mark_value': "Note attribuée",
    'trace.field.max_hours': "Heures maximales",
    'trace.field.max_points': "Points maximaux",
    'trace.field.max_score': "Note maximale",
    'trace.field.mention_scale_id': "Barème des mentions",
    'trace.field.menu_item_id': "Entrée de menu",
    'trace.field.mime': "Format",
    'trace.field.min_hours': "Heures minimales",
    'trace.field.min_score': "Note minimale",
    'trace.field.minutes': "Minutes",
    'trace.field.module': "Module",
    'trace.field.month': "Mois",
    'trace.field.monthly_gross': "Mensuel brut",
    'trace.field.monthly_net': "Mensuel net",
    'trace.field.moved_from': "Déplacée depuis",
    'trace.field.must_change_password': "Doit changer son mot de passe",
    'trace.field.muted': "En sourdine",
    'trace.field.national_id_issued_on': "CIN délivrée le",
    'trace.field.net': "Net",
    'trace.field.notation': "Notation",
    'trace.field.note': "Note",
    'trace.field.notes': "Notes",
    'trace.field.notice_months': "Préavis (mois)",
    'trace.field.notification_id': "Notification",
    'trace.field.objective': "Objectif",
    'trace.field.objectives': "Objectifs",
    'trace.field.observations': "Observations",
    'trace.field.obsolete': "Obsolète",
    'trace.field.occurred_on': "Survenu le",
    'trace.field.online_allowed': "En ligne autorisé",
    'trace.field.option_id': "Option",
    'trace.field.options': "Options",
    'trace.field.organisation_id': "Organisation",
    'trace.field.organisation_name': "Organisation",
    'trace.field.origin': "Origine",
    'trace.field.other_organisation_name': "Autre organisation",
    'trace.field.override_by_name': "Conflit ignoré par",
    'trace.field.override_reason': "Motif du passage outre",
    'trace.field.owner_id': "Responsable",
    'trace.field.owner_kind': "Rattachée à",
    'trace.field.paid_by_name': "Payé par",
    'trace.field.paid_method': "Mode de paiement",
    'trace.field.paid_on': "Payé le",
    'trace.field.paid_reference': "Référence du paiement",
    'trace.field.parent_id': "Parent",
    'trace.field.partner_since': "Partenaire depuis",
    'trace.field.party': "Partie",
    'trace.field.passing': "Admis",
    'trace.field.passport_issued_on': "Passeport délivré le",
    'trace.field.password_allowed': "Mot de passe autorisé",
    'trace.field.password_changed_at': "Mot de passe changé le",
    'trace.field.pattern': "Modèle de numéro",
    'trace.field.pay_mode': "Mode de rémunération",
    'trace.field.pay_replacements': "Rémunérer les remplacements",
    'trace.field.pay_surveillance': "Rémunérer les surveillances",
    'trace.field.payer_type': "Type de payeur",
    'trace.field.payroll_number': "Matricule de paie",
    'trace.field.permanent': "Permanent",
    'trace.field.permission_id': "Permission",
    'trace.field.photo_path': "Photo",
    'trace.field.place': "Lieu",
    'trace.field.planned': "Prévu",
    'trace.field.populations': "Populations",
    'trace.field.price_module': "Prix au module",
    'trace.field.price_subject': "Prix à la matière",
    'trace.field.price_training': "Prix à la formation",
    'trace.field.private_for_id': "Privé pour",
    'trace.field.product_id': "Formation ou élément",
    'trace.field.profile_id': "Profil",
    'trace.field.prospect_id': "Prospection",
    'trace.field.prospect_idle_days': "Inactivité d’une prospection (jours)",
    'trace.field.prospected_on': "Prospectée le",
    'trace.field.quantity': "Quantité",
    'trace.field.rate': "Taux",
    'trace.field.rate_gross': "Taux brut",
    'trace.field.rate_net': "Taux net",
    'trace.field.read_at': "Lu le",
    'trace.field.reads_enabled': "Journal des consultations activé",
    'trace.field.receipt_pattern': "Modèle de numéro de reçu",
    'trace.field.recipient_id': "Destinataire",
    'trace.field.recipients_count': "Nombre de destinataires",
    'trace.field.recorded_at': "Enregistré le",
    'trace.field.recorded_by_id': "Enregistré par",
    'trace.field.recorded_by_name': "Enregistré par",
    'trace.field.refund_pattern': "Modèle de numéro de remboursement",
    'trace.field.regulation_version_id': "Version du règlement",
    'trace.field.reminder_of_id': "Rappel de",
    'trace.field.renewal': "Reconduction",
    'trace.field.renewed_from_id': "Reconduite depuis",
    'trace.field.reopened': "Rouverte",
    'trace.field.replies': "Réponses",
    'trace.field.replies_allowed': "Réponses autorisées",
    'trace.field.require_rolls': "Appel exigé",
    'trace.field.required_permission': "Permission requise",
    'trace.field.requires_reason': "Motif exigé",
    'trace.field.reserved': "Réservé",
    'trace.field.resit_threshold': "Seuil de rattrapage",
    'trace.field.restricted': "Restreint",
    'trace.field.resulting_account_id': "Compte créé",
    'trace.field.retained_session': "Session retenue",
    'trace.field.retention_years': "Conservation (années)",
    'trace.field.reverses_id': "Annule",
    'trace.field.revoked_at': "Révoqué le",
    'trace.field.revoked_by_id': "Révoqué par",
    'trace.field.role_assignment_id': "Attribution de rôle",
    'trace.field.roll_call': "Appel",
    'trace.field.roll_taken_at': "Appel fait le",
    'trace.field.roll_taken_by_name': "Appel fait par",
    'trace.field.roll_validated_at': "Appel validé le",
    'trace.field.roll_validated_by_name': "Appel validé par",
    'trace.field.room_id': "Salle",
    'trace.field.route': "Route",
    'trace.field.run_id': "Calcul",
    'trace.field.scale_id': "Barème",
    'trace.field.schedule_id': "Échéancier",
    'trace.field.scope': "Portée",
    'trace.field.scope_kind': "Type de portée",
    'trace.field.sector_id': "Secteur d’activité",
    'trace.field.send_email': "Envoi par courriel",
    'trace.field.sender_id': "Expéditeur",
    'trace.field.sender_name': "Expéditeur",
    'trace.field.sender_read_at': "Lu par l’expéditeur le",
    'trace.field.senders': "Expéditeurs",
    'trace.field.sent_at': "Envoyé le",
    'trace.field.series_id': "Série de séances",
    'trace.field.service_basis': "Base du service",
    'trace.field.service_id': "Prestation",
    'trace.field.session': "Session",
    'trace.field.session_idle_minutes': "Inactivité de session (minutes)",
    'trace.field.share': "Part",
    'trace.field.shared': "Partagée",
    'trace.field.signed_on': "Signé le",
    'trace.field.size': "Taille",
    'trace.field.source': "Source",
    'trace.field.source_id': "Source",
    'trace.field.special_discount': "Remise spéciale",
    'trace.field.stackable': "Cumulable",
    'trace.field.staff_chat': "Discussion du personnel",
    'trace.field.stage_id': "Étape",
    'trace.field.stage_label': "Étape",
    'trace.field.starts_on': "Début",
    'trace.field.statement_pattern': "Modèle de numéro de bulletin",
    'trace.field.status': "Statut",
    'trace.field.status_reason': "Motif du statut",
    'trace.field.students': "Élèves",
    'trace.field.subject': "Objet",
    'trace.field.submitted_at': "Soumise le",
    'trace.field.submitted_by_id': "Soumise par",
    'trace.field.submitted_by_name': "Soumise par",
    'trace.field.survey_auto': "Enquête automatique",
    'trace.field.suspended_by_id': "Suspendue par",
    'trace.field.suspends_lessons': "Suspend les cours",
    'trace.field.system': "Système",
    'trace.field.tagline': "Slogan",
    'trace.field.target': "Cible",
    'trace.field.target_id': "Cible",
    'trace.field.targets': "Cibles",
    'trace.field.targets_training': "Porte sur la formation",
    'trace.field.taught': "Enseigné",
    'trace.field.tax': "Taxe",
    'trace.field.tax_amount': "Montant de la taxe",
    'trace.field.tax_id': "Taxe",
    'trace.field.tax_rate': "Taux de taxe",
    'trace.field.teacher_chat': "Discussion des enseignants",
    'trace.field.teacher_id': "Enseignant",
    'trace.field.teacher_notify': "Notifications des enseignants",
    'trace.field.teachers_read': "Lecture par les enseignants",
    'trace.field.teaching_type_id': "Type d’enseignement",
    'trace.field.template_id': "Modèle",
    'trace.field.tenant_id': "Identifiant du locataire",
    'trace.field.termination_reason': "Motif de résiliation",
    'trace.field.title': "Titre",
    'trace.field.token_hash': "Jeton",
    'trace.field.tone': "Couleur",
    'trace.field.totp_confirmed_at': "Application d’authentification confirmée le",
    'trace.field.totp_last_step': "Dernier code d’authentification",
    'trace.field.totp_secret': "Secret d’authentification",
    'trace.field.trade_register': "Registre du commerce",
    'trace.field.training_center_id': "Centre de formation",
    'trace.field.training_cycle_id': "Cycle de formation",
    'trace.field.training_domain_id': "Domaine de formation",
    'trace.field.training_label': "Formation",
    'trace.field.training_level_id': "Niveau de formation",
    'trace.field.transcript': "Relevé de notes",
    'trace.field.treatment': "Traitement",
    'trace.field.two_factor': "Double authentification",
    'trace.field.two_factor_email': "Courriel de double authentification",
    'trace.field.type': "Type",
    'trace.field.unit_amount': "Prix unitaire",
    'trace.field.unpaid_minutes': "Minutes non payées",
    'trace.field.used_at': "Utilisé le",
    'trace.field.user_account_id': "Compte",
    'trace.field.valid_from_id': "Valable à partir de",
    'trace.field.validation_threshold': "Seuil de validation",
    'trace.field.venue_id': "Site",
    'trace.field.version_id': "Version",
    'trace.field.visible': "Visible",
    'trace.field.website': "Site web",
    'trace.field.weekday': "Jour de la semaine",
    'trace.field.weighting': "Pondération",
    'trace.field.won': "Gagnée",
    'trace.field.year_label': "Année",
    'trace.value.draft': "brouillon",
    'trace.value.signed': "signé",
    'trace.value.closed': "clôturé",
    'trace.value.ended': "échu",
    'trace.value.terminated': "résilié",
    'trace.value.hourly': "à l’heure",
    'trace.value.monthly': "mensuel",
    'trace.value.weekly': "hebdomadaire",
    'trace.value.yearly': "annuel",
    'trace.value.none': "aucun",
    'trace.value.teaching': "enseignement",
    'trace.value.surveillance': "surveillance",
    'trace.value.bonus': "prime",
    'trace.value.deduction': "retenue",
    'trace.value.transfer': "virement",
    'trace.value.cheque': "chèque",
    'trace.value.cash': "espèces",
    'trace.value.other': "autre",
    'trace.value.validated': "validé",
    'trace.value.paid': "payé",
    'trace.value.message': "message",
    'trace.value.notification': "notification",
    'trace.value.reply': "réponse",
    'trace.value.direct': "directe",
    'trace.value.group': "groupe",
    'trace.value.sent': "envoyé",
    'trace.value.optional': "facultatif",
    'trace.value.required': "obligatoire",
    'trace.value.staff': "personnel",
    'trace.value.staff_teachers': "personnel et enseignants",
    'trace.value.direction': "direction",
    'trace.value.info': "information",
    'trace.value.warn': "avertissement",
    'trace.value.violet': "violet",
    'trace.value.danger': "danger",
    'trace.value.muted': "discret",
    'trace.value.ok': "succès",
    'trace.value.school': "établissement",
    'trace.value.partner': "partenaire",
    'trace.value.call': "appel",
    'trace.value.email': "courriel",
    'trace.value.meeting': "réunion",
    'trace.value.visit': "visite",
    'trace.value.event': "événement",
    'trace.value.note': "note",
    'trace.value.survey': "enquête",
    'trace.value.legacy': "reprise",
    'trace.value.employed': "en emploi",
    'trace.value.studying': "en études",
    'trace.value.seeking': "en recherche d’emploi",
    'trace.value.unknown': "inconnu",
    'trace.value.prospect': "prospect",
    'trace.value.dormant': "en sommeil",
    'trace.value.renewal': "à renouveler",
    'trace.value.tacit': "tacite",
    'trace.value.explicit': "expresse",
    'trace.value.lesson': "cours",
    'trace.value.assessment': "évaluation",
    'trace.value.seminar': "séminaire",
    'trace.value.conference': "conférence",
    'trace.value.outing': "sortie",
    'trace.value.ceremony': "cérémonie",
    'trace.value.planned': "prévu",
    'trace.value.done': "fait",
    'trace.value.not_done': "non fait",
    'trace.value.cancelled': "annulé",
    'trace.value.holiday': "jour férié",
    'trace.value.vacation': "vacances",
    'trace.value.exams': "examens",
    'trace.value.no_class': "sans cours",
    'trace.value.absent': "absent",
    'trace.value.late': "en retard",
    'trace.value.medical': "médical",
    'trace.value.family': "familial",
    'trace.value.administrative': "administratif",
    'trace.value.transport': "transport",
    'plan.title': "Emploi du temps",
    'plan.sub': "Les séances, évaluations et évènements de la semaine",
    'plan.subManage': "Les séances, évaluations et évènements de la semaine ; cliquer sur un créneau libre pour planifier",
    'plan.mine.title': "Mon emploi du temps",
    'plan.mine.sub': "Vos séances, surveillances et évènements",
    'plan.view': "Vue",
    'plan.view.class': "Classe",
    'plan.view.teacher': "Enseignant",
    'plan.view.room': "Salle",
    'plan.view.student': "Élève",
    'plan.mode': "Affichage",
    'plan.mode.week': "Semaine",
    'plan.mode.month': "Mois",
    'plan.mode.list': "Liste",
    'plan.mode.series': "Série hebdomadaire",
    'plan.mode.lesson': "Une séance",
    'plan.weekOf': "Semaine du {from} au {to} {year}",
    'plan.prevWeek': "Semaine précédente",
    'plan.nextWeek': "Semaine suivante",
    'plan.prevMonth': "Mois précédent",
    'plan.nextMonth': "Mois suivant",
    'plan.prevDay': "Jour précédent",
    'plan.nextDay': "Jour suivant",
    'plan.today': "Aujourd'hui",
    'plan.print': "Imprimer",
    'plan.print.title': "Emploi du temps",
    'plan.print.year': "Année",
    'plan.print.cycle': "Cycle",
    'plan.print.domain': "Domaine de formation",
    'plan.print.training': "Formation",
    'plan.print.class': "Classe",
    'plan.print.venue': "Lieu",
    'plan.chooseTarget': "Choisissez une classe, un enseignant, une salle ou un élève.",
    'plan.empty': "Aucun créneau sur cette période.",
    'plan.allDay': "Sans heure",
    'plan.unplaced': "à placer",
    'plan.more': "+{count} autres",
    'plan.legend.exam': "Évaluation",
    'plan.legend.event': "Évènement spécial",
    'plan.legend.cancelled': "Annulée",
    'plan.col.when': "Quand",
    'plan.col.what': "Quoi",
    'plan.col.who': "Enseignant",
    'plan.col.where': "Salle",
    'plan.col.status': "Statut",
    'plan.status.planned': "Prévue",
    'plan.status.done': "Réalisée",
    'plan.status.not_done': "Non réalisée",
    'plan.status.cancelled': "Annulée",
    'plan.nature.seminar': "Séminaire",
    'plan.nature.conference': "Conférence",
    'plan.nature.outing': "Sortie",
    'plan.nature.meeting': "Réunion",
    'plan.nature.ceremony': "Cérémonie",
    'plan.nature.other': "Autre",
    'plan.period.holiday': "Jour férié",
    'plan.period.vacation': "Vacances",
    'plan.period.exams': "Période d'examens",
    'plan.period.no_class': "Période sans cours",
    'plan.period.other': "Autre",
    'plan.period.blockedShort': "planification bloquée",
    'plan.conflict.title': "Conflits : {count}",
    'plan.conflict.room': "la salle {subject} est occupée ({what})",
    'plan.conflict.teacher': "{subject} donne déjà {what}",
    'plan.conflict.class': "la classe {subject} a déjà {what}",
    'plan.conflict.blocked': "période bloquée : {subject}",
    'plan.conflict.more': "et {count} autres",
    'plan.conflict.skip': "Créer sans les jours en conflit ({count})",
    'plan.conflict.override': "Planifier malgré les conflits, avec un motif",
    'plan.conflict.noOverride': "Passer outre un conflit demande un droit dédié.",
    'plan.conflict.reason': "Motif de la dérogation",
    'plan.field.training': "Formation",
    'plan.field.chooseTraining': "Choisir une formation",
    'plan.field.chooseStudent': "Choisir un élève",
    'plan.field.subject': "Matière",
    'plan.field.chooseSubject': "Choisir une matière",
    'plan.field.type': "Type d'enseignement",
    'plan.field.noType': "Sans type",
    'plan.field.room': "Salle",
    'plan.field.rooms': "Salles",
    'plan.field.noRoom': "Sans salle",
    'plan.field.classes': "Pour",
    'plan.field.noClass': "Aucune classe dans cette formation.",
    'plan.field.teachers': "Enseignants",
    'plan.field.addTeacher': "Ajouter un enseignant",
    'plan.field.addOrganiser': "Ajouter un organisateur",
    'plan.field.addSupervisor': "Ajouter un surveillant",
    'plan.field.every': "Chaque",
    'plan.field.start': "De",
    'plan.field.end': "À",
    'plan.field.rhythm': "Rythme",
    'plan.field.rhythmEvery': "Toutes les {count} semaine(s)",
    'plan.field.firstOn': "Du",
    'plan.field.lastOn': "Au",
    'plan.field.date': "Le",
    'plan.field.nature': "Nature",
    'plan.field.label': "Intitulé",
    'plan.field.participants': "Participants",
    'plan.field.guests': "Intervenants externes",
    'plan.field.guestsHint': "Séparés par des virgules",
    'plan.field.for': "Pour",
    'plan.field.plannedTeacher': "Enseignant prévu",
    'plan.field.supervisors': "Surveillants",
    'plan.field.organisers': "Organisateurs",
    'plan.field.roll': "Appel",
    'plan.field.duration': "Durée",
    'plan.field.status': "Statut",
    'plan.field.givenBy': "Donnée par",
    'plan.field.givenByHint': "Un remplaçant : les heures comptent pour lui, pas pour l'enseignant prévu.",
    'plan.field.substitute': "remplaçant",
    'plan.field.reason': "Motif",
    'plan.series.new': "Nouvelle série",
    'plan.series.sub': "Une séance par semaine sur la période ; les jours fériés et vacances bloquées sont sautés",
    'plan.series.create': "Créer les {count} séances",
    'plan.series.saved': "Séances créées : {count}.",
    'plan.series.editFollowing': "Cette séance et les suivantes",
    'plan.series.followingNote': "Les séances encore prévues à partir du {day} sont refaites ; celles réalisées, annulées ou avec un appel restent.",
    'plan.series.sessions': "Séances créées",
    'plan.series.range': "du {first} au {last}",
    'plan.series.skipped': "Jours sautés",
    'plan.series.hours': "Heures",
    'plan.series.volume': "{planned} planifiées sur {volume} au volume",
    'plan.series.noVolume': "pas de volume prévu pour ce type",
    'plan.lesson.new': "Nouvelle séance",
    'plan.lesson.edit': "Modifier la séance",
    'plan.lesson.saved': "Séance enregistrée.",
    'plan.special.new': "Nouvel évènement",
    'plan.special.edit': "Modifier l'évènement",
    'plan.special.sub': "Séminaire, conférence, sortie, réunion : un créneau qui n'est ni un cours ni une évaluation",
    'plan.special.create': "Planifier l'évènement",
    'plan.special.suspend': "Suspendre les cours des participants sur ce créneau",
    'plan.special.suspendHint': "Leurs séances sont annulées « évènement », sans compter dans l'absentéisme.",
    'plan.special.rollCall': "Faire l'appel",
    'plan.special.rollCallHint': "La présence est obligatoire : les absences s'ajoutent au suivi de l'assiduité.",
    'plan.special.saved': "Évènement enregistré.",
    'plan.special.savedSuspended': "Évènement enregistré ; séances suspendues : {count}.",
    'plan.event.movedFrom': "Reportée du {day}",
    'plan.event.series': "Série : chaque {weekday} du {first} au {last} · séance {index} sur {total}",
    'plan.event.unplacedNote': "Cette évaluation n'a pas encore de salle ni de surveillant.",
    'plan.event.place': "Placer l'évaluation",
    'plan.event.placed': "Évaluation placée.",
    'plan.event.unplace': "Retirer de l'emploi du temps",
    'plan.event.openAssessment': "Ouvrir l'évaluation",
    'plan.event.rollNotTaken': "Pas encore fait",
    'plan.event.rollCounts': "{present} présents · {absent} absents · {late} retards",
    'plan.event.capacity': "Effectif attendu {expected}, au-delà de la capacité de la salle ({capacity}).",
    'plan.event.override': "Planifiée malgré un conflit : {reason} ({by})",
    'plan.event.suspended': "Suspendue par l'évènement « {by} ».",
    'plan.event.saveStatus': "Enregistrer le statut",
    'plan.event.statusSaved': "Statut enregistré.",
    'plan.event.change': "Modifier",
    'plan.event.editOne': "Cette séance",
    'plan.event.editFollowing': "Cette séance et les suivantes",
    'plan.event.move': "Reporter",
    'plan.event.moved': "Séance reportée.",
    'plan.event.delete': "Supprimer",
    'plan.event.deleteText': "Le créneau disparaît de l'emploi du temps. Une séance dont l'appel est fait s'annule plutôt.",
    'plan.event.deleted': "Créneau supprimé.",
    'plan.event.takeRoll': "Faire l'appel",
    'plan.calendar.title': "Calendrier de l'établissement",
    'plan.calendar.sub': "Vacances, jours fériés, périodes particulières ; une période bloquée refuse toute séance",
    'plan.calendar.new': "Nouvelle période",
    'plan.calendar.edit': "Modifier la période",
    'plan.calendar.delete': "Supprimer la période",
    'plan.calendar.empty': "Aucune période pour cette année.",
    'plan.calendar.period': "Période",
    'plan.calendar.kind': "Nature",
    'plan.calendar.scope': "Pour",
    'plan.calendar.dates': "Dates",
    'plan.calendar.planning': "Planification",
    'plan.calendar.blocked': "bloquée",
    'plan.calendar.open': "ouverte",
    'plan.calendar.institution': "Établissement",
    'plan.calendar.cycles': "Cycles",
    'plan.calendar.trainings': "Formations",
    'plan.calendar.from': "Du",
    'plan.calendar.to': "Au",
    'plan.calendar.toHint': "Vide pour un seul jour",
    'plan.calendar.blocking': "Bloquer la planification",
    'plan.calendar.blockingHint': "Aucune séance n'y est planifiée ; une série saute ces jours d'elle-même.",
    'plan.calendar.note': "Une série qui traverse une période bloquée saute ces jours. Les jours fériés religieux se confirment chaque année : leur date reste modifiable.",
    'plan.rooms.title': "Salles et lieux",
    'plan.rooms.sub': "Où ont lieu les séances : la capacité contrôle l'effectif, l'occupation se lit sur la semaine en cours",
    'plan.rooms.new': "Nouvelle salle",
    'plan.rooms.edit': "Modifier la salle",
    'plan.rooms.delete': "Supprimer la salle",
    'plan.rooms.empty': "Aucune salle.",
    'plan.rooms.tab': "Salles · {count}",
    'plan.rooms.room': "Salle",
    'plan.rooms.venue': "Lieu",
    'plan.rooms.kind': "Type",
    'plan.rooms.kindHint': "Salle de cours, amphithéâtre, salle informatique…",
    'plan.rooms.capacity': "Places",
    'plan.rooms.accepts': "Accueil",
    'plan.rooms.shared': "plusieurs cours",
    'plan.rooms.single': "un cours",
    'plan.rooms.occupancy': "Occupation",
    'plan.rooms.timetable': "Emploi du temps",
    'plan.rooms.inactive': "inactive",
    'plan.rooms.sharedLabel': "Accepte plusieurs cours en même temps",
    'plan.rooms.sharedHint': "Une étude, une bibliothèque : le contrôle des conflits l'ignore.",
    'plan.rooms.activeLabel': "Salle active",
    'plan.rooms.note': "Une séance dont l'effectif dépasse la capacité est signalée ; « plusieurs cours » autorise deux séances en même temps dans la salle.",
    'plan.venues.tab': "Lieux · {count}",
    'plan.venues.new': "Nouveau lieu",
    'plan.venues.edit': "Modifier le lieu",
    'plan.venues.delete': "Supprimer le lieu",
    'plan.venues.empty': "Aucun lieu.",
    'plan.venues.address': "Adresse",
    'plan.venues.rooms': "Salles",
    'plan.hours.title': "Heures prévues et réalisées",
    'plan.hours.sub': "Le volume vient de la maquette, le réalisé des séances marquées réalisées",
    'plan.hours.subOf': "{training} · {year} · au {day}",
    'plan.hours.by.subject': "Par matière",
    'plan.hours.by.teacher': "Par enseignant",
    'plan.hours.empty': "Aucune heure à montrer.",
    'plan.hours.volume': "Volume",
    'plan.hours.planned': "Planifié",
    'plan.hours.done': "Réalisé",
    'plan.hours.progress': "Avancement",
    'plan.hours.teacher': "Enseignant",
    'plan.hours.subjects': "Matières",
    'plan.hours.substitute': "Dont remplacements",
    'plan.hours.noTeacher': "Sans enseignant",
    'plan.hours.missing': "manque {hours} à planifier",
    'plan.hours.extra': "{hours} de plus que le volume",
    'plan.hours.note': "Une séance donnée par un remplaçant compte pour lui. Les heures par enseignant préparent les vacations ; leur paie reste hors de ce lot.",
    'att.student': "Élève",
    'att.element': "Élément",
    'att.value': "Mesure",
    'att.threshold': "Seuil",
    'att.validated': "validé",
    'att.toValidate': "à valider",
    'att.notTaken': "non fait",
    'att.justified': "justifiée",
    'att.justifiedAs': "justifiée · {kind}",
    'att.lateBy': "retard {count} min",
    'att.sessions': "{count} séance(s)",
    'att.waived': "levée par le jury",
    'att.mark.present': "Présent",
    'att.mark.absent': "Absent",
    'att.mark.late': "Retard",
    'att.mark.present.short': "P",
    'att.mark.absent.short': "A",
    'att.mark.late.short': "R",
    'att.level.alert': "alerte",
    'att.level.consequence': "conséquence",
    'att.effect.alert': "seuil d'alerte atteint",
    'att.effect.retake': "exclusion de la session normale : passage au rattrapage",
    'att.effect.jury': "renvoi au jury",
    'att.kind.medical': "Certificat médical",
    'att.kind.family': "Raison familiale",
    'att.kind.administrative': "Raison administrative",
    'att.kind.transport': "Transport",
    'att.kind.other': "Autre",
    'att.roll.title': "Appel",
    'att.roll.sub': "Vos séances du jour, et l'appel de chacune",
    'att.roll.today': "Mes séances",
    'att.roll.unlinked': "Aucune fiche d'enseignant n'est liée à votre compte.",
    'att.roll.none': "Aucune séance ce jour.",
    'att.roll.noRoll': "sans appel",
    'att.roll.taken': "appel fait",
    'att.roll.todo': "faire l'appel",
    'att.roll.of': "Appel",
    'att.roll.expected': "{count} attendus",
    'att.roll.allPresent': "Tous présents",
    'att.roll.counts': "{present} présents · {absent} absents · {late} retards",
    'att.roll.noStudent': "Aucun élève attendu à cette séance.",
    'att.roll.lateMinutes': "Minutes de retard",
    'att.roll.minutes': "min",
    'att.roll.save': "Enregistrer l'appel et marquer la séance réalisée",
    'att.roll.saveValidate': "Enregistrer et valider",
    'att.roll.reopen': "Rouvrir l'appel",
    'att.roll.saved': "Appel enregistré.",
    'att.roll.savedValidated': "Appel enregistré et validé.",
    'att.roll.validatedBy': "Appel validé par {by} : il ne se modifie plus.",
    'att.roll.editableUntil': "Modifiable jusqu'à sa validation par la scolarité",
    'att.roll.toTimetable': "Emploi du temps",
    'att.absences.title': "Absences",
    'att.absences.sub': "La scolarité complète, justifie et valide l'appel des enseignants, classe par classe et semaine par semaine",
    'att.absences.weekOf': "Semaine du {day}",
    'att.absences.onlyAbsent': "Absents seulement",
    'att.absences.all': "Toute la classe",
    'att.absences.validate': "Valider les appels ({count})",
    'att.absences.validated': "Appels validés : {count}.",
    'att.absences.noClass': "Aucune classe dans votre portée.",
    'att.absences.noSession': "Aucune séance avec appel cette semaine.",
    'att.absences.week': "Semaine",
    'att.absences.year': "Année",
    'att.absences.count': "{count} abs.",
    'att.absences.roll': "Appel",
    'att.absences.notTaken': "appel non fait",
    'att.absences.clickHint': "Un clic change l'état : absent, retard, présent.",
    'att.justify.title': "Justifier une absence",
    'att.justify.edit': "Modifier la justification",
    'att.justify.sub': "Une pièce justifie toutes les séances de la période ; les évaluations restent traitées par leur note",
    'att.justify.from': "Du",
    'att.justify.to': "Au",
    'att.justify.kind': "Motif",
    'att.justify.reason': "Précision",
    'att.justify.document': "Pièce",
    'att.justify.documentHint': "PDF, PNG ou JPEG, 5 Mo au plus",
    'att.justify.submit': "Justifier",
    'att.justify.saved': "Séances justifiées : {count}.",
    'att.justify.remove': "Retirer la justification",
    'att.justify.removeText': "Les absences qu'elle couvrait redeviennent non justifiées.",
    'att.justify.removed': "Justification retirée.",
    'att.dashboard.title': "Assiduité",
    'att.dashboard.sub': "Cette semaine : les appels à faire et à valider, les élèves au-delà des seuils",
    'att.dashboard.rate': "Assiduité moyenne",
    'att.dashboard.rateNote': "toutes formations, cette semaine",
    'att.dashboard.missing': "Appels non faits",
    'att.dashboard.missingNote': "séances passées sans appel",
    'att.dashboard.toValidate': "Appels à valider",
    'att.dashboard.toValidateNote': "faits par les enseignants",
    'att.dashboard.over': "Élèves au-delà des seuils",
    'att.dashboard.overNote': "dont {count} au seuil de conséquence",
    'att.dashboard.overTitle': "Élèves au-delà des seuils",
    'att.dashboard.overEmpty': "Aucun élève au-delà des seuils du règlement.",
    'att.dashboard.missingTitle': "Séances passées sans appel",
    'att.dashboard.missingEmpty': "Tous les appels sont faits.",
    'att.dashboard.complete': "Compléter",
    'att.student.title': "Assiduité",
    'att.student.empty': "Aucune inscription cette année.",
    'att.student.rate': "Assiduité",
    'att.student.rateNote': "sur {hours} de cours réalisés",
    'att.student.unjustified': "Absences non justifiées",
    'att.student.justified': "Justifiées",
    'att.student.lates': "Retards",
    'att.student.lateMinutes': "{count} min au total",
    'att.student.bySubject': "Par matière",
    'att.student.noRule': "Aucun seuil par matière au règlement",
    'att.student.rule': "Seuils du règlement : alerte {alert} %, conséquence {consequence} %",
    'att.student.realized': "Réalisé",
    'att.student.unjustifiedShort': "Non justifié",
    'att.student.rateCol': "Taux",
    'att.student.records': "Absences et retards",
    'att.student.noRecord': "Aucune absence relevée.",
    'att.student.assessment': "évaluation · {absence}",
    'att.student.justifications': "Justifications",
    'att.student.noJustification': "Aucune justification.",
    'att.student.waivers': "Conséquences levées par le jury",
    'att.student.note': "Seules les séances réalisées comptent. Les absences aux évaluations restent portées par la note.",
    'att.waive.action': "Lever la conséquence",
    'att.waive.title': "Lever la conséquence d'assiduité",
    'att.waive.reason': "Motif du jury",
    'att.waive.saved': "Conséquence levée.",
    'att.waive.remove': "Rétablir la conséquence",
    'att.waive.removed': "Conséquence rétablie.",
    'reg.block.attendance': "Assiduité",
    'reg.ed.attendance.hint': "Chaque règle combine une portée, ce qui compte et sa base ; toutes sont évaluées.",
    'reg.ed.attendance.none': "Aucune règle : les absences n'alertent ni n'entraînent rien.",
    'reg.ed.attendance.rule': "Règle {number}",
    'reg.ed.attendance.scope': "Portée",
    'reg.ed.attendance.measure': "Ce qui compte",
    'reg.ed.attendance.measure.unjustified_hours': "Heures non justifiées",
    'reg.ed.attendance.measure.absence_hours': "Toutes les heures d'absence",
    'reg.ed.attendance.measure.sessions': "Séances non justifiées",
    'reg.ed.attendance.base': "Rapporté à",
    'reg.ed.attendance.base.realized': "Heures réalisées",
    'reg.ed.attendance.base.planned': "Heures prévues au volume",
    'reg.ed.attendance.base.fixed': "Seuil fixe",
    'reg.ed.attendance.effect': "Effet",
    'reg.ed.attendance.effect.retake': "Passage au rattrapage",
    'reg.ed.attendance.effect.jury': "Renvoi au jury",
    'reg.ed.attendance.alert': "Alerte à partir de",
    'reg.ed.attendance.consequence': "Conséquence à partir de",
    'reg.ed.attendance.unit.sessions': "séances",
    'reg.ed.attendance.add': "Ajouter une règle",
    'reg.ed.attendance.lates': "Retards valant une absence",
    'reg.ed.attendance.lateHours': "Durée de cette absence",
    'reg.ed.attendance.waive': "Le jury peut lever la conséquence, avec un motif",
    'reg.ed.attendance.worst': "Plusieurs règles atteintes : la conséquence la plus sévère s'applique. Les séances suspendues par un évènement ne comptent pas.",
    'reg.sum.attendance.none': "Aucune règle d'assiduité",
    'reg.sum.attendance.rule': "{scope} : alerte {alert}, {effect} {consequence}",
    'scol.tab.attendance': "Assiduité",
    'violation.plan.room_name_taken': "Une salle de ce lieu porte déjà ce nom.",
    'violation.plan.room_in_use': "La salle accueille des créneaux : désactivez-la plutôt.",
    'violation.plan.dates_order': "La fin précède le début.",
    'violation.plan.times_order': "La fin doit suivre le début.",
    'violation.plan.invalid_time': "Heure attendue (HH:MM).",
    'violation.plan.outside_year': "Hors de l'année académique.",
    'violation.plan.year_closed': "L'année est close.",
    'violation.plan.no_class': "Choisissez au moins une classe.",
    'violation.plan.subject_expected': "Choisissez une matière de la formation.",
    'violation.plan.other_training': "Hors de la formation de la séance.",
    'violation.plan.series_subject': "La matière d'une série ne change pas : créez une autre série.",
    'violation.plan.series_empty': "Aucune séance à créer sur cette période.",
    'violation.plan.event_has_roll': "L'appel de ce créneau est fait : annulez-le plutôt.",
    'violation.att.not_expected': "Cet élève n'est pas attendu à la séance.",
    'violation.att.already_waived': "La conséquence est déjà levée sur cet élément.",
    'violation.reg.attendance_threshold': "Donnez un seuil d'alerte ou de conséquence.",
    'violation.reg.attendance_order': "La conséquence doit venir après l'alerte.",
    'plan.field.rhythmWeekly': "Chaque semaine",
    'stg.add': "Ajouter",
    'stg.edit': "Modifier",
    'stg.remove': "Supprimer",
    'stg.deactivate': "Désactiver",
    'stg.inactive': "désactivé",
    'stg.change': "Changer",
    'stg.choose': "Choisir…",
    'stg.none': "Aucun",
    'stg.yes': "Oui",
    'stg.no': "Non",
    'stg.see': "Voir",
    'stg.search': "Rechercher",
    'stg.searchPlaceholder': "Tapez au moins deux lettres",
    'stg.export': "Exporter",
    'stg.breadcrumb': "Fil d'Ariane",
    'stg.year': "Année",
    'stg.class': "Classe",
    'stg.student': "Étudiant",
    'stg.supervisor': "Encadrant pédagogique",
    'stg.tutor': "Tuteur dans l'organisation",
    'stg.topic': "Sujet",
    'stg.missions': "Missions prévues",
    'stg.place': "Lieu",
    'stg.startsOn': "Début",
    'stg.endsOn': "Fin",
    'stg.lastName': "Nom",
    'stg.firstNames': "Prénoms",
    'stg.email': "Courriel",
    'stg.phone': "Téléphone",
    'stg.reason': "Motif",
    'stg.tab.count': "{label} ({count})",
    'stg.step.none': "Sans stage",
    'stg.step.proposed': "Proposé",
    'stg.step.validated': "Validé",
    'stg.step.signed': "Convention signée",
    'stg.step.ongoing': "En cours",
    'stg.step.report_due': "Rapport attendu",
    'stg.step.report_late': "Rapport en retard",
    'stg.step.report_received': "Rapport reçu",
    'stg.step.defended': "Soutenu",
    'stg.step.evaluated': "Évalué",
    'stg.step.refused': "Refusé",
    'stg.step.cancelled': "Abandonné",
    'stg.timeline.proposed': "Proposé",
    'stg.timeline.validated': "Validé",
    'stg.timeline.signed': "Convention signée",
    'stg.timeline.ongoing': "En cours",
    'stg.timeline.report': "Rapport reçu",
    'stg.timeline.defended': "Soutenu",
    'stg.timeline.evaluated': "Évalué",
    'stg.nature.internship': "Stage",
    'stg.nature.apprenticeship': "Alternance",
    'stg.nature.job': "Emploi",
    'stg.offerState.draft': "brouillon",
    'stg.offerState.published': "publiée",
    'stg.offerState.closed': "clôturée",
    'stg.applicationState.new': "nouvelle",
    'stg.applicationState.forwarded': "transmise",
    'stg.applicationState.retained': "retenue",
    'stg.applicationState.declined': "non retenue",
    'stg.evaluator.tutor': "Tuteur (fiche d'évaluation)",
    'stg.evaluator.supervisor': "Encadrant pédagogique",
    'stg.evaluator.jury': "Jury de soutenance",
    'stg.jury.president': "Président",
    'stg.jury.supervisor': "Encadrant",
    'stg.jury.member': "Membre",
    'stg.jury.tutor': "Tuteur invité",
    'stg.due.before_start': "avant le début ({date})",
    'stg.due.at_end': "à la fin ({date})",
    'stg.due.after_end': "avant le {date}",
    'stg.type.due.before_start': "Avant le début",
    'stg.type.due.at_end': "À la fin du stage",
    'stg.type.due.after_end': "Après la fin",
    'stg.filter.all': "Tous",
    'stg.filter.without': "Sans stage",
    'stg.filter.to_validate': "À valider",
    'stg.filter.to_sign': "Sans convention",
    'stg.filter.ongoing': "En cours",
    'stg.filter.reports': "Rapports attendus",
    'stg.filter.to_evaluate': "À évaluer",
    'stg.filter.evaluated': "Évalués",
    'stg.filter.allClasses': "Toutes les classes",
    'stg.filter.allSupervisors': "Tous les encadrants",
    'stg.filter.noSupervisor': "Sans encadrant",
    'stg.dashboard.title': "Stages",
    'stg.dashboard.sub': "Les campagnes de l'année, les stagiaires et ce qui reste à faire",
    'stg.dashboard.subYear': "Les campagnes de l'année, les stagiaires et ce qui reste à faire · année {year}",
    'stg.dashboard.campaigns': "Campagnes",
    'stg.dashboard.campaignsSub': "Un type de stage, des formations, une période",
    'stg.dashboard.campaignLine': "{type} · {trainings} · {count} étudiant(s)",
    'stg.dashboard.noCampaign': "Aucune campagne cette année.",
    'stg.dashboard.noCampaignManage': "Aucune campagne cette année : montez la première pour suivre les étudiants attendus en stage.",
    'stg.dashboard.outside': "{count} stage(s) hors campagne cette année",
    'stg.dashboard.hosts': "Organisations d'accueil",
    'stg.dashboard.hostsSub': "Stagiaires de l'année",
    'stg.dashboard.noHosts': "Aucun stagiaire placé cette année.",
    'stg.dashboard.todo': "À faire",
    'stg.dashboard.nothingToDo': "Rien à faire pour l'instant.",
    'stg.todo.proposed': "{count} stage(s) proposé(s) par les étudiants",
    'stg.todo.proposedSub': "à valider ou à refuser",
    'stg.todo.toSign': "{count} convention(s) à faire signer",
    'stg.todo.toSignSub': "Kelassi les prépare en PDF, prêtes à imprimer",
    'stg.todo.noSupervisor': "{count} stagiaire(s) sans encadrant pédagogique",
    'stg.todo.noSupervisorSub': "désignez un enseignant",
    'stg.todo.defenses': "{count} soutenance(s) à planifier",
    'stg.todo.defensesSub': "journées, créneaux et jurys",
    'stg.todo.late': "{count} rapport(s) en retard",
    'stg.todo.lateSub': "une relance part chaque matin à l'étudiant",
    'stg.kpi.expected': "Étudiants attendus en stage",
    'stg.kpi.expectedNote': "{count} campagne(s) cette année",
    'stg.kpi.without': "Sans stage",
    'stg.kpi.withoutNote': "dont {count} à moins de 30 jours du début",
    'stg.kpi.toSign': "Conventions à signer",
    'stg.kpi.toSignNote': "stages validés sans convention signée",
    'stg.kpi.reports': "Rapports à recevoir",
    'stg.kpi.reportsNote': "date limite dépassée pour {count}",
    'stg.column.campaign': "Campagne",
    'stg.column.period': "Période",
    'stg.column.progress': "Avancement",
    'stg.column.without': "Sans stage",
    'stg.column.intern': "Stagiaire",
    'stg.column.internTopic': "Stagiaire et sujet",
    'stg.column.organisationTopic': "Organisation et sujet",
    'stg.column.documents': "Pièces",
    'stg.column.step': "Étape",
    'stg.column.offer': "Offre et organisation",
    'stg.column.audience': "Publics visés",
    'stg.column.closes': "Échéance",
    'stg.column.applications': "Candidatures",
    'stg.column.state': "État",
    'stg.column.candidate': "Candidat",
    'stg.column.received': "Reçue",
    'stg.column.next': "Suite",
    'stg.campaign.new': "Nouvelle campagne",
    'stg.campaign.edit': "Modifier la campagne",
    'stg.campaign.sub': "Un type de stage, des formations, une période ; les inscrits de ces formations sont attendus",
    'stg.campaign.one': "Campagne",
    'stg.campaign.none': "Hors campagne",
    'stg.campaign.type': "Type de stage",
    'stg.campaign.typeHint': "durée minimale {months} mois",
    'stg.campaign.label': "Libellé",
    'stg.campaign.reportDueOn': "Rapport à rendre au plus tard",
    'stg.campaign.reportDueHint': "vide : fin du stage plus le délai du type",
    'stg.campaign.assessmentType': "Type d'évaluation de la note",
    'stg.campaign.assessmentHint': "pour l'évaluation créée dans l'élément du programme",
    'stg.campaign.trainings': "Formations concernées et élément qui reçoit la note",
    'stg.campaign.noTraining': "Aucune formation pour cette année.",
    'stg.campaign.subject': "Élément du programme",
    'stg.campaign.noSubject': "Pas de report de note",
    'stg.campaign.subjectHint': "★ : élément marqué « stage » dans la maquette. La note validée du stage y est reportée.",
    'stg.list.title': "Stages",
    'stg.list.sub': "{type} · {trainings} · {expected} étudiant(s)",
    'stg.list.outside': "Hors campagne",
    'stg.list.outsideSub': "Les stages de l'année repris de l'ancien Kelassi ou enregistrés sans campagne",
    'stg.list.searchPlaceholder': "Étudiant, organisation, sujet",
    'stg.list.empty': "Aucun stagiaire pour ce filtre.",
    'stg.list.noCampaign': "Aucune campagne cette année : montez-en une depuis le tableau de bord.",
    'stg.list.byStudent': "par l'étudiant",
    'stg.list.withoutNote': "Ces étudiants de la campagne n'ont encore aucun stage : écrivez-leur depuis la messagerie, ou publiez une offre qui les vise.",
    'stg.internship.one': "Stage",
    'stg.internship.new': "Nouveau stage",
    'stg.internship.edit': "Modifier le stage",
    'stg.internship.sub': "Un étudiant, une organisation, une période, un sujet",
    'stg.internship.saveProposed': "Enregistrer comme proposé",
    'stg.internship.saveValidate': "Enregistrer et valider",
    'stg.internship.duration': "durée {months} mois",
    'stg.internship.allowShort': "Accepter cette durée sous le minimum du type",
    'stg.internship.note': "L'organisation vient de l'annuaire des Relations extérieures ; une organisation absente se crée ici, avec son tuteur. L'étudiant peut aussi proposer son stage depuis son espace.",
    'stg.organisation.one': "Organisation",
    'stg.organisation.host': "Organisation d'accueil",
    'stg.organisation.new': "Nouvelle organisation",
    'stg.organisation.newHint': "elle rejoint l'annuaire des Relations extérieures",
    'stg.organisation.notFound': "L'organisation n'est pas dans l'annuaire ?",
    'stg.organisation.pick': "Chercher dans l'annuaire",
    'stg.tutor.later': "À préciser",
    'stg.tutor.new': "Nouveau contact…",
    'stg.tutor.none': "Tuteur à préciser",
    'stg.supervisor.later': "À désigner",
    'stg.supervisor.none': "Aucun",
    'stg.supervisor.toAssign': "Encadrant à désigner",
    'stg.supervisor.change': "Encadrant pédagogique",
    'stg.supervisor.hint': "il est prévenu dans sa cloche et voit le stage dans « Mes stagiaires »",
    'stg.file.title': "{name} · {type}",
    'stg.file.months': "{months} mois",
    'stg.file.minimum': "minimum {months}",
    'stg.file.steps': "Étapes du stage",
    'stg.file.before': "avant le {date}",
    'stg.file.refused': "Refusé : {reason}",
    'stg.file.cancelled': "Abandonné : {reason}",
    'stg.file.documents': "Pièces du dossier",
    'stg.file.documentsSub': "Exigées par le type de stage",
    'stg.file.noDocuments': "Aucune pièce exigée.",
    'stg.file.evaluation': "Évaluation",
    'stg.file.markLater': "La note apparaît une fois l'évaluation validée.",
    'stg.file.evaluatedOn': "Évaluation validée le {date} · {who}",
    'stg.file.reportedTo': "Note reportée dans « {subject} »",
    'stg.file.defense': "Soutenance",
    'stg.file.noDefense': "Pas encore planifiée.",
    'stg.file.planDefense': "Planifier",
    'stg.file.defenseOn': "Soutenance le {date} à {time}",
    'stg.file.supervision': "Encadrement",
    'stg.file.since': "depuis le {date}",
    'stg.file.fromOffer': "depuis l'offre « {title} »",
    'stg.file.followUps': "Suivis",
    'stg.file.noFollowUp': "Aucun suivi noté.",
    'stg.action.validate': "Valider",
    'stg.action.refuse': "Refuser",
    'stg.action.convention': "Convention (PDF)",
    'stg.action.sign': "Marquer la convention signée",
    'stg.action.evaluate': "Évaluer",
    'stg.action.reopenEvaluation': "Rouvrir l'évaluation",
    'stg.action.cancel': "Abandonner le stage",
    'stg.action.reopen': "Rouvrir",
    'stg.action.delete': "Supprimer le stage",
    'stg.delete.text': "Le stage de {name} et ses pièces seront supprimés.",
    'stg.reason.refuseSub': "Le motif est envoyé à l'étudiant.",
    'stg.reason.cancelSub': "Le stage reste au dossier, abandonné.",
    'stg.sign.sub': "La convention signée par les trois parties.",
    'stg.sign.on': "Signée le",
    'stg.followUp.new': "Noter un suivi",
    'stg.followUp.one': "Suivi",
    'stg.followUp.type': "Type de suivi",
    'stg.followUp.heldOn': "Date",
    'stg.followUp.present': "Stagiaire présent",
    'stg.followUp.presentUnknown': "Non précisé",
    'stg.followUp.observations': "Ce qui en ressort",
    'stg.followUp.wasPresent': "stagiaire présent",
    'stg.followUp.wasAbsent': "stagiaire absent",
    'stg.followUp.remove': "Supprimer le suivi",
    'stg.document.one': "Pièce",
    'stg.document.expected': "Attendue",
    'stg.document.received': "Reçue",
    'stg.document.awaited': "attendue",
    'stg.document.see': "Voir",
    'stg.document.receive': "Recevoir",
    'stg.document.replace': "Remplacer",
    'stg.document.deposit': "Déposer",
    'stg.document.remove': "Retirer la pièce",
    'stg.document.add': "Pièce libre",
    'stg.document.free': "Pièce libre",
    'stg.document.label': "Libellé de la pièce",
    'stg.document.receivedOn': "Reçue le",
    'stg.document.file': "Fichier PDF",
    'stg.document.fileHint': "PDF, 10 Mo au plus",
    'stg.document.markReceived': "Marquer reçue",
    'stg.document.upload': "Déposer",
    'stg.document.uploadSub': "Votre document en PDF ; il est marqué reçu aujourd'hui.",
    'stg.document.receiveSub': "Reçue sur papier : une date suffit. Ou déposez le PDF.",
    'stg.document.pickFile': "Choisissez le fichier PDF.",
    'stg.evaluation.title': "Évaluation · {name}",
    'stg.evaluation.open': "Noter",
    'stg.evaluation.component': "Composante",
    'stg.evaluation.by': "Évaluée par",
    'stg.evaluation.weight': "Poids",
    'stg.evaluation.mark': "Note / 20",
    'stg.evaluation.total': "Note du stage",
    'stg.evaluation.comment': "Appréciation du jury",
    'stg.evaluation.report': "Reporter la note dans « {subject} » ({training})",
    'stg.evaluation.noTarget': "La campagne ne dit pas quel élément du programme reçoit la note : elle reste dans le stage.",
    'stg.evaluation.note': "La note reportée suit ensuite le circuit habituel des notes (verrouillage, délibération). La grille et les poids se règlent par type de stage.",
    'stg.evaluation.supervisorNote': "Vous notez les composantes de l'encadrant ; l'école valide l'évaluation.",
    'stg.evaluation.noGrid': "Pas de grille pour ce type de stage.",
    'stg.evaluation.validate': "Valider l'évaluation",
    'stg.evaluation.saved': "Notes enregistrées.",
    'stg.evaluation.validated': "Évaluation validée.",
    'stg.evaluation.reported': "Évaluation validée, note reportée dans « {subject} ».",
    'stg.flash.saved': "Enregistré.",
    'stg.flash.validated': "Stage validé : l'étudiant est prévenu.",
    'stg.flash.refused': "Stage refusé : le motif est envoyé à l'étudiant.",
    'stg.flash.cancelled': "Stage abandonné.",
    'stg.flash.signed': "Convention signée.",
    'stg.flash.reopened': "Rouvert.",
    'stg.flash.followUp': "Suivi noté.",
    'stg.flash.document': "Pièce enregistrée.",
    'stg.flash.proposed': "Stage proposé : l'école va l'examiner.",
    'stg.flash.applied': "Candidature envoyée.",
    'stg.flash.withdrawn': "Candidature retirée.",
    'stg.flash.published': "Offre publiée : les étudiants visés sont prévenus.",
    'stg.flash.closed': "Offre clôturée.",
    'stg.flash.placed': "Passage enregistré.",
    'stg.flash.autoplaced': "{count} passage(s) placé(s).",
    'stg.offers.title': "Offres",
    'stg.offers.sub': "Offres de stage, d'alternance et d'emploi publiées aux étudiants et aux lauréats",
    'stg.offers.filter.all': "Toutes",
    'stg.offers.filter.published': "Publiées",
    'stg.offers.filter.draft': "Brouillons",
    'stg.offers.filter.closed': "Clôturées",
    'stg.offers.empty': "Aucune offre.",
    'stg.offers.allTrainings': "Toutes les formations",
    'stg.offers.alumni': "lauréats",
    'stg.offers.fresh': "{count} nouvelle(s)",
    'stg.offers.note': "Une offre publiée apparaît dans l'espace des étudiants visés (et des lauréats pour un emploi), avec une notification. « Retenue » propose de créer le stage, pré-rempli.",
    'stg.offer.new': "Nouvelle offre",
    'stg.offer.edit': "Modifier l'offre",
    'stg.offer.sub': "Une organisation de l'annuaire, des publics visés, une échéance",
    'stg.offer.title': "Intitulé",
    'stg.offer.contact': "Contact",
    'stg.offer.nature': "Nature",
    'stg.offer.startsOn': "Début souhaité",
    'stg.offer.duration': "Durée",
    'stg.offer.durationPlaceholder': "4 à 6 mois",
    'stg.offer.closesOn': "Candidatures jusqu'au",
    'stg.offer.closesLine': "clôture le {date}",
    'stg.offer.description': "Description",
    'stg.offer.audience': "Publics visés",
    'stg.offer.alumni': "Ouvrir aussi aux lauréats",
    'stg.offer.audienceHint': "Sans formation cochée, l'offre vise tous les inscrits de l'année.",
    'stg.offer.publish': "Publier",
    'stg.offer.close': "Clôturer",
    'stg.offer.applications': "Candidatures",
    'stg.offer.applicationsSub': "{count} candidature(s) · le CV est joint par l'étudiant",
    'stg.offer.noApplication': "Aucune candidature pour l'instant.",
    'stg.offer.cv': "CV",
    'stg.offer.internship': "Le stage",
    'stg.offer.createInternship': "Créer le stage",
    'stg.offer.details': "L'offre",
    'stg.offer.publishedAt': "Publiée le",
    'stg.offer.delete': "Supprimer l'offre",
    'stg.offer.deleteText': "L'offre « {title} » sera supprimée.",
    'stg.apply.title': "Postuler · {title}",
    'stg.apply.letter': "Votre lettre",
    'stg.apply.cv': "Votre CV",
    'stg.apply.send': "Envoyer la candidature",
    'stg.apply.button': "Postuler",
    'stg.defense.title': "Soutenances",
    'stg.defense.sub': "Journées, créneaux et jurys",
    'stg.defense.subCampaign': "{campaign} · {days} journée(s), {planned} passage(s)",
    'stg.defense.tab': "{date} ({count})",
    'stg.defense.unplannedTab': "Non planifiés ({count})",
    'stg.defense.unplanned': "Stages qui attendent leur soutenance",
    'stg.defense.noDay': "Aucune journée : créez-en une.",
    'stg.defense.allPlanned': "Tous les stages ont leur passage.",
    'stg.defense.noCampaign': "Aucune campagne cette année.",
    'stg.defense.placeOne': "Placer",
    'stg.defense.placeHere': "Placer ici",
    'stg.defense.dayTitle': "{date} · {place}",
    'stg.defense.newDay': "Nouvelle journée",
    'stg.defense.editDay': "Modifier la journée",
    'stg.defense.deleteDay': "Supprimer la journée",
    'stg.defense.deleteDayText': "La journée et tous ses passages seront supprimés ; les stages retournent dans « non planifiés ».",
    'stg.defense.daySub': "Une date, une salle, l'heure du premier passage, la durée d'un passage",
    'stg.defense.date': "Date",
    'stg.defense.room': "Salle",
    'stg.defense.otherPlace': "Autre lieu…",
    'stg.defense.place': "Placer un passage",
    'stg.defense.edit': "Passage · {name}",
    'stg.defense.firstSlot': "Premier passage",
    'stg.defense.slot': "Durée d'un passage (min)",
    'stg.defense.breakAt': "Pause à",
    'stg.defense.breakMinutes': "Durée de la pause (min)",
    'stg.defense.breakLine': "{at}, {count} min",
    'stg.defense.minutes': "{count} min",
    'stg.defense.time': "Heure",
    'stg.defense.day': "Journée",
    'stg.defense.jury': "Jury",
    'stg.defense.role': "Rôle",
    'stg.defense.member': "Membre du jury",
    'stg.defense.addMember': "Ajouter un membre",
    'stg.defense.removeMember': "Retirer ce membre",
    'stg.defense.inviteTutor': "Inviter le tuteur",
    'stg.defense.tutorInvited': "Tuteur de l'organisation",
    'stg.defense.allowConflict': "Planifier malgré tout",
    'stg.defense.juryNote': "Le jury compte un président et au moins un autre membre ; le tuteur est invité. Un enseignant déjà en cours ou juré à cette heure est signalé.",
    'stg.defense.free': "libre",
    'stg.defense.incomplete': "jury incomplet",
    'stg.defense.remove': "Retirer le passage",
    'stg.defense.rules': "Règles de la journée",
    'stg.defense.autoplan': "Répartir automatiquement",
    'stg.defense.autoplanNote': "Place les stages qui attendent dans les créneaux libres, l'encadrant au jury quand il est libre, le tuteur invité ; le président reste à désigner.",
    'stg.defense.convocations': "Convocations (PDF)",
    'stg.defense.note': "Le stagiaire est prévenu de son passage dans sa cloche ; les convocations s'impriment pour lui et pour le jury.",
    'stg.mine.title': "Mon stage",
    'stg.mine.none': "Pas encore de stage",
    'stg.mine.noneSub': "Proposez le stage que vous avez trouvé, ou postulez à une offre.",
    'stg.mine.supervisor': "encadrant {name}",
    'stg.mine.tutor': "tuteur {name}",
    'stg.mine.toDeposit': "à déposer",
    'stg.mine.received': "reçue",
    'stg.mine.before': "avant le {date}",
    'stg.mine.deposit': "Déposer le PDF",
    'stg.mine.mark': "Note du stage : {mark} / 20",
    'stg.mine.openFile': "Voir la fiche du stage",
    'stg.mine.offers': "Offres pour vous",
    'stg.mine.allOffers': "Toutes les offres",
    'stg.mine.noOffer': "Aucune offre ouverte pour vous.",
    'stg.mine.proposeTitle': "Proposer un stage",
    'stg.mine.proposeText': "Vous avez trouvé un stage vous-même ? Proposez-le : l'école le valide et prépare la convention.",
    'stg.mine.noCampaign': "Aucune campagne ouverte pour votre formation.",
    'stg.mine.propose': "Proposer un stage",
    'stg.mine.proposeNote': "Votre proposition arrive « proposée » : l'école la valide, ou vous dit pourquoi elle la refuse. Une organisation absente de l'annuaire s'ajoute ici.",
    'stg.mine.mobileNote': "Ces écrans serviront aussi l'application mobile : dépôt du rapport, candidatures, notifications à chaque étape.",
    'stg.mine.offersTitle': "Offres",
    'stg.mine.offersSub': "Les offres de stage, d'alternance et d'emploi qui vous visent",
    'stg.mine.description': "Voir le détail",
    'stg.mine.startsOn': "à partir du {date}",
    'stg.mine.applications': "Mes candidatures",
    'stg.mine.noApplication': "Aucune candidature.",
    'stg.mine.withCv': "CV joint",
    'stg.mine.withoutCv': "sans CV",
    'stg.mine.withdraw': "Retirer",
    'stg.supervised.title': "Mes stagiaires",
    'stg.supervised.sub': "{name} · encadrant pédagogique de {count} stagiaire(s)",
    'stg.supervised.interns': "Stagiaires",
    'stg.supervised.idle': "Sans suivi depuis {days} jours",
    'stg.supervised.idleNote': "un rappel vous est envoyé chaque matin",
    'stg.supervised.idleNone': "tous suivis",
    'stg.supervised.toMark': "Rapports à noter",
    'stg.supervised.toMarkNote': "rapport reçu ou soutenance passée",
    'stg.supervised.lastFollowUp': "Dernier suivi",
    'stg.supervised.never': "aucun suivi",
    'stg.supervised.markReport': "Noter le rapport",
    'stg.supervised.open': "Noter un suivi",
    'stg.supervised.empty': "Aucun stagiaire à encadrer.",
    'stg.supervised.note': "Vous voyez seulement vos stagiaires : notez vos suivis (appel, visite, présence du stagiaire), lisez les pièces et notez le rapport. Les heures d'encadrement peuvent compter dans votre service, selon les réglages des stages.",
    'stg.settings.title': "Stages · réglages",
    'stg.settings.sub': "Les types de stage, leurs pièces et leur grille, les suivis et les rappels",
    'stg.settings.types': "Types de stage",
    'stg.settings.followTypes': "Types de suivi",
    'stg.settings.rolesNote': "Les rôles d'encadrement sont fixes : encadrant pédagogique (un enseignant), tuteur dans l'organisation (un contact), membres du jury.",
    'stg.settings.module': "Module",
    'stg.settings.moduleSub': "Réservé au Super Administrateur",
    'stg.settings.enabled': "Module des stages activé",
    'stg.settings.enabledHint': "Allumer les Stages allume aussi les Relations extérieures : l'annuaire des organisations est commun.",
    'stg.settings.enabledOn': "Activé le {date} · {who}",
    'stg.settings.relationsOff': "Les Relations extérieures sont éteintes : elles s'allument avec les Stages.",
    'stg.settings.rules': "Rappels et règles",
    'stg.settings.idle': "Relancer l'encadrant sans suivi depuis (jours)",
    'stg.settings.idleHint': "pour les stages en cours",
    'stg.settings.reminder': "Relancer l'étudiant avant la date limite du rapport (jours)",
    'stg.settings.reminderHint': "et tant que le rapport est en retard",
    'stg.settings.studentsPropose': "Les étudiants peuvent proposer leur stage depuis leur espace",
    'stg.settings.countSupervision': "Compter l'encadrement dans les heures réalisées des enseignants",
    'stg.settings.countSupervisionHint': "Oui : chaque stage signé crédite à son encadrant les heures d'encadrement de son type, à la fin du stage ; elles comptent dans son service et sa paie.",
    'stg.type.new': "Nouveau type de stage",
    'stg.type.edit': "Type de stage · {label}",
    'stg.type.sub': "Sa durée, sa soutenance, ses pièces exigées et sa grille d'évaluation",
    'stg.type.label': "Type de stage",
    'stg.type.minMonths': "Durée minimale (mois)",
    'stg.type.minMonthsHint': "contrôlée à l'enregistrement d'un stage",
    'stg.type.months': "{months} mois",
    'stg.type.reportDays': "Rapport attendu (jours après la fin)",
    'stg.type.reportDaysHint': "sauf date de la campagne",
    'stg.type.supervision': "Heures d'encadrement",
    'stg.type.supervisionHint': "créditées à l'encadrant si le réglage le veut",
    'stg.type.defense': "Soutenance devant un jury",
    'stg.type.defenseShort': "Soutenance",
    'stg.type.active': "Actif",
    'stg.type.documents': "Pièces exigées",
    'stg.type.due': "Attendue",
    'stg.type.isReport': "C'est le rapport",
    'stg.type.addDocument': "Ajouter une pièce",
    'stg.type.grid': "Grille d'évaluation",
    'stg.type.addComponent': "Ajouter une composante",
    'stg.type.total': "Total : {total} % (100 % attendus)",
    'stg.type.defaultConvention': "Convention de stage signée",
    'stg.type.defaultReport': "Rapport de stage",
    'stg.type.deactivateText': "« {label} » sert déjà : il est désactivé, plus proposé pour les nouvelles campagnes.",
    'stg.type.removeText': "« {label} » sera supprimé avec ses pièces et sa grille.",
    'stg.followType.new': "Nouveau type de suivi",
    'stg.followType.edit': "Type de suivi",
    'stg.followType.label': "Libellé",
    'rel.modules.stages': "Stages",
    'rel.modules.stagesHint': "Campagnes, stages, offres, soutenances (avec les Relations extérieures)",
    'rel.modules.relationsOffStages': "Les Stages, qui en dépendent, seront désactivés aussi.",
    'fac.dash.supervision': "dont {hours} d'encadrement de stages",
    'violation.stg.too_short': "Durée sous le minimum de ce type de stage.",
    'violation.stg.duplicate': "Cet étudiant a déjà un stage dans cette campagne.",
    'violation.stg.bad_state': "Action impossible à cette étape du stage.",
    'violation.stg.period': "La fin précède le début.",
    'violation.stg.future_follow_up': "Un suivi se note une fois fait.",
    'violation.stg.not_your_component': "L'encadrant note seulement ses composantes.",
    'violation.stg.mark_missing': "Une composante de la grille n'est pas notée.",
    'violation.stg.evaluated': "Évaluation validée : rouvrez-la pour changer les notes.",
    'violation.stg.no_subject': "La campagne ne dit pas quel élément du programme reçoit la note.",
    'violation.stg.assessment_validated': "Les notes de cet élément sont validées : rouvrez la saisie de l'évaluation.",
    'violation.stg.no_assessment_type': "Aucun type d'évaluation dans l'établissement.",
    'violation.stg.weights': "Les poids de la grille doivent faire 100 %.",
    'violation.stg.two_reports': "Un seul rapport par type.",
    'violation.stg.requirement_used': "Cette pièce a déjà été reçue pour des stages : elle ne se retire pas.",
    'violation.stg.component_used': "Cette composante est déjà notée : elle ne se retire pas.",
    'violation.stg.campaign_closed': "Cette campagne ne vous concerne pas.",
    'violation.stg.bad_training': "Choisissez une formation de l'année de la campagne.",
    'violation.stg.bad_subject': "Choisissez un élément de cette formation.",
    'violation.stg.duplicate_training': "Formation en double.",
    'violation.stg.no_training': "Choisissez au moins une formation.",
    'violation.stg.campaign_used': "Cette campagne a des stages : elle ne se supprime pas.",
    'violation.stg.report_before_end': "Le rapport se rend après la fin du stage.",
    'violation.stg.other_campaign': "Cette journée est celle d'une autre campagne.",
    'violation.stg.slot_taken': "Ce créneau est déjà pris.",
    'violation.stg.juror_busy': "Un juré est déjà pris à cette heure.",
    'violation.stg.juror': "Un enseignant ou un contact.",
    'violation.stg.juror_duplicate': "Membre en double.",
    'violation.stg.two_presidents': "Un seul président.",
    'violation.stg.offer_expired': "L'échéance est passée : changez-la avant de publier.",
    'violation.stg.offer_used': "Cette offre a des candidatures : clôturez-la au lieu de la supprimer.",
    'violation.stg.offer_closed': "Cette offre n'est pas ouverte pour vous.",
    'violation.stg.already_applied': "Vous avez déjà postulé à cette offre.",
    'violation.stg.application_linked': "Cette candidature a déjà son stage.",
    'trace.table.stages_settings': "Réglages des stages",
    'trace.table.stages_type': "Type de stage",
    'trace.table.stages_type_document': "Pièce exigée",
    'trace.table.stages_type_component': "Composante de la grille",
    'trace.table.stages_follow_type': "Type de suivi",
    'trace.table.stages_campaign': "Campagne de stages",
    'trace.table.stages_campaign_training': "Formation d'une campagne",
    'trace.table.stages_offer': "Offre",
    'trace.table.stages_offer_training': "Formation visée par une offre",
    'trace.table.stages_application': "Candidature",
    'trace.table.internship': "Stage",
    'trace.table.internship_document': "Pièce d'un stage",
    'trace.table.internship_follow_up': "Suivi de stage",
    'trace.table.internship_mark': "Note de la grille",
    'trace.table.stages_defense_day': "Journée de soutenances",
    'trace.table.internship_defense': "Soutenance",
    'trace.table.internship_defense_juror': "Membre du jury",
    'trace.field.alumni': "Lauréats",
    'trace.field.assessment_type_id': "Type d'évaluation",
    'trace.field.break_at': "Pause à",
    'trace.field.break_minutes': "Durée de la pause",
    'trace.field.campaign_id': "Campagne",
    'trace.field.closes_on': "Échéance",
    'trace.field.closing_reason': "Motif",
    'trace.field.component_id': "Composante",
    'trace.field.count_supervision': "Encadrement compté",
    'trace.field.cv_name': "CV",
    'trace.field.cv_size': "Taille du CV",
    'trace.field.day_id': "Journée",
    'trace.field.defense_id': "Soutenance",
    'trace.field.defense_required': "Soutenance",
    'trace.field.due': "Attendue",
    'trace.field.duration': "Durée",
    'trace.field.evaluated_at': "Évalué le",
    'trace.field.evaluated_by_id': "Évalué par",
    'trace.field.evaluator': "Évaluée par",
    'trace.field.file_name': "Fichier",
    'trace.field.file_size': "Taille du fichier",
    'trace.field.internship_id': "Stage",
    'trace.field.jury_comment': "Appréciation du jury",
    'trace.field.mark_reported_at': "Note reportée le",
    'trace.field.min_months': "Durée minimale (mois)",
    'trace.field.missions': "Missions",
    'trace.field.offer_id': "Offre",
    'trace.field.proposed_by_student': "Proposé par l'étudiant",
    'trace.field.received_by_id': "Reçue par",
    'trace.field.report': "Rapport",
    'trace.field.report_days': "Délai du rapport",
    'trace.field.report_due_on': "Rapport à rendre le",
    'trace.field.report_reminder_days': "Relance du rapport",
    'trace.field.requirement_id': "Pièce exigée",
    'trace.field.role': "Rôle",
    'trace.field.slot_minutes': "Durée d'un passage",
    'trace.field.student_present': "Stagiaire présent",
    'trace.field.students_propose': "Les étudiants proposent",
    'trace.field.supervision_minutes': "Minutes d'encadrement",
    'trace.field.supervisor_id': "Encadrant pédagogique",
    'trace.field.supervisor_idle_days': "Relance de l'encadrant",
    'trace.field.supervisor_since': "Encadrant depuis",
    'trace.field.topic': "Sujet",
    'trace.field.tutor_id': "Tuteur",
    'trace.field.weight': "Poids",
    'trace.value.proposed': "proposé",
    'trace.value.evaluated': "évalué",
    'trace.value.refused': "refusé",
    'trace.value.kelassi': "Kelassi",
    'trace.value.history': "parcours des lauréats",
    'trace.value.before_start': "avant le début",
    'trace.value.at_end': "à la fin",
    'trace.value.after_end': "après la fin",
    'trace.value.tutor': "tuteur",
    'trace.value.supervisor': "encadrant",
    'trace.value.jury': "jury",
    'trace.value.internship': "stage",
    'trace.value.apprenticeship': "alternance",
    'trace.value.job': "emploi",
    'trace.value.published': "publiée",
    'trace.value.new': "nouvelle",
    'trace.value.forwarded': "transmise",
    'trace.value.retained': "retenue",
    'trace.value.declined': "non retenue",
    'trace.value.president': "président",
    'trace.value.member': "membre",
    'form.required': "Ce champ est obligatoire.",
    'form.requiredMark': "obligatoire",
    'form.legend': "Les champs marqués * sont obligatoires",
    'form.summary': "{count} champ(s) à corriger avant d'enregistrer",
    'form.pasteCut': "Le texte collé a été coupé à {max} caractères.",
    'form.counter': "{count} caractères sur {max}",
    'rel.modules.requests': "Demandes",
    'rel.modules.requestsHint': "Attestations, relevés, justifications d'absence, modifications de notes, autres demandes",
    'req.status.new': "Nouvelle",
    'req.status.ongoing': "En cours",
    'req.status.waiting': "Complément demandé",
    'req.status.done': "Traitée",
    'req.status.rejected': "Rejetée",
    'req.status.cancelled': "Annulée",
    'req.status.late': "En retard",
    'req.when.today': "aujourd'hui {time}",
    'req.when.yesterday': "hier {time}",
    'req.delay.suspended': "suspendu",
    'req.delay.doneIn': "traitée en {count} jour(s)",
    'req.delay.late': "en retard de {count} jour(s)",
    'req.delay.today': "aujourd'hui",
    'req.delay.tomorrow': "demain",
    'req.delay.inDays': "dans {count} jours",
    'req.days': "{value} jour(s)",
    'req.noTeam': "Sans équipe",
    'req.open': "Ouvrir",
    'req.choose': "Choisir…",
    'req.breadcrumb': "Fil d'Ariane",
    'req.files.label': "Pièces jointes",
    'req.files.drop': "Déposez le PDF ou la photo (PNG, JPEG), 10 Mo au plus",
    'req.files.uploading': "Envoi en cours…",
    'req.files.remove': "Retirer",
    'req.files.see': "Voir",
    'req.files.add': "Ajouter une pièce",
    'req.dashboard.title': "Demandes",
    'req.dashboard.sub': "Les demandes des étudiants, des enseignants et du personnel",
    'req.kpi.open': "À traiter",
    'req.kpi.openNote': "{count} nouvelle(s), pas encore prises en main",
    'req.kpi.late': "En retard",
    'req.kpi.lateNote': "délai du type dépassé",
    'req.kpi.waiting': "Complément demandé",
    'req.kpi.waitingNote': "en attente du demandeur",
    'req.kpi.done': "Traitées ce mois-ci",
    'req.kpi.doneNote': "délai moyen {days}",
    'req.kpi.doneNoteEmpty': "aucune encore ce mois-ci",
    'req.dashboard.byType': "Par type",
    'req.dashboard.byTypeSub': "Demandes ouvertes et délai de traitement",
    'req.dashboard.noType': "Aucun type de demande.",
    'req.dashboard.byTeam': "Par équipe",
    'req.dashboard.byTeamSub': "Demandes en cours",
    'req.dashboard.nothingOpen': "Aucune demande en cours.",
    'req.dashboard.mine': "Pour moi et mon équipe",
    'req.dashboard.allMine': "Toute ma file",
    'req.dashboard.nothingMine': "Rien à traiter pour vous.",
    'req.column.type': "Type de demande",
    'req.column.open': "En cours",
    'req.column.late': "En retard",
    'req.column.target': "Délai visé",
    'req.column.average': "Délai moyen",
    'req.column.number': "Numéro",
    'req.column.requester': "Demandeur",
    'req.column.received': "Reçue",
    'req.column.team': "Équipe",
    'req.column.state': "État",
    'req.column.delay': "Délai",
    'req.column.request': "Demande",
    'req.column.filed': "Déposée",
    'req.list.title': "Demandes",
    'req.list.sub': "{open} demande(s) ouverte(s) · {late} en retard",
    'req.view.mine': "Ma file",
    'req.view.team': "Mon équipe",
    'req.view.open': "Toutes ouvertes",
    'req.view.late': "En retard",
    'req.view.waiting': "Complément demandé",
    'req.view.closed': "Fermées",
    'req.view.count': "{label} ({count})",
    'req.list.search': "Rechercher",
    'req.list.searchPlaceholder': "Numéro, demandeur, objet",
    'req.list.allTypes': "Tous les types",
    'req.list.empty': "Aucune demande dans cette vue.",
    'req.list.note': "« Ma file » : les demandes dont l'étape actuelle m'est confiée ou revient à mes équipes ; « Mon équipe » : celles de mes équipes de traitement. Le délai court depuis la réception et s'arrête pendant qu'un complément est attendu.",
    'req.population.student': "Étudiant",
    'req.population.teacher': "Enseignant",
    'req.population.staff': "Personnel",
    'req.kind.student': "Étudiant",
    'req.kind.teacher': "Enseignant",
    'req.audience.student': "Étudiants",
    'req.audience.teacher': "Enseignants",
    'req.audience.staff': "Personnel",
    'req.produces.none': "rien (réponse écrite)",
    'req.produces.enrolment': "Attestation de scolarité (Documents)",
    'req.produces.transcript': "Relevé de notes (Documents)",
    'req.produces.certificate': "Attestation de réussite (Documents)",
    'req.produces.mark_change': "Correction des notes (Notes)",
    'req.produces.absence': "Absence justifiée (Assiduité)",
    'req.produces.internship': "Stage proposé (Stages)",
    'req.produce.none': "Traiter",
    'req.produce.enrolment': "Produire l'attestation et traiter",
    'req.produce.transcript': "Produire le relevé et traiter",
    'req.produce.certificate': "Produire l'attestation et traiter",
    'req.produce.mark_change': "Appliquer les notes et traiter",
    'req.produce.absence': "Justifier l'absence et traiter",
    'req.produce.internship': "Traiter",
    'req.field.year': "Année",
    'req.field.deliberation': "Délibération",
    'req.field.language': "Langue du document",
    'req.field.copies': "Exemplaires",
    'req.field.startsOn': "Du",
    'req.field.endsOn': "Au",
    'req.field.justificationKind': "Motif de l'absence",
    'req.field.establishment': "Établissement visé",
    'req.field.reason': "Motif",
    'req.field.description': "Votre demande",
    'req.field.assessment': "Évaluation",
    'req.field.line': "Note",
    'req.field.other': "Champ",
    'req.lang.fr': "Français",
    'req.lang.en': "Anglais",
    'req.lang.ar': "Arabe",
    'req.rule.hidden': "Caché",
    'req.rule.shown': "Affiché",
    'req.rule.required': "Obligatoire",
    'req.form.delay': "Délai visé : {count} jour(s)",
    'req.form.attachments.hidden': "pièces : aucune",
    'req.form.attachments.shown': "pièces : facultatives",
    'req.form.attachments.required': "pièce obligatoire",
    'req.form.noDeliberation': "Aucune délibération close où vous figurez.",
    'req.ask.sub': "Le délai est celui que l'école s'engage à tenir",
    'req.ask.submit': "Envoyer la demande",
    'req.ask.message': "Message à l'école",
    'req.new.title': "Nouvelle demande",
    'req.new.sub': "Saisie par le personnel, pour un étudiant ou un enseignant",
    'req.new.type': "Type de demande",
    'req.new.for': "Pour",
    'req.new.forHint': "Nom, prénom ou numéro, deux caractères au moins",
    'req.new.change': "Changer",
    'req.new.pickPerson': "Choisissez d'abord la personne.",
    'req.new.pickType': "Choisissez le type de demande.",
    'req.new.handleNow': "Traiter tout de suite (produire et clore)",
    'req.new.handleNowNone': "Traiter tout de suite (clore)",
    'req.file.received': "reçue le {date}",
    'req.file.closed': "fermée le {date}",
    'req.file.suspended': "délai suspendu",
    'req.file.due': "à traiter avant le {date}",
    'req.file.team': "confiée à {team}",
    'req.file.teamAssignee': "confiée à {team} ({name})",
    'req.file.document': "Télécharger le document",
    'req.file.rejected': "Demande rejetée : {reason}",
    'req.file.cancelled': "Demande annulée : {reason}",
    'req.file.request': "La demande",
    'req.file.noFields': "Aucun champ rempli.",
    'req.file.files': "Pièces jointes",
    'req.file.noFiles': "Aucune pièce.",
    'req.file.thread': "Échanges",
    'req.file.threadSub': "Le demandeur voit les messages, pas les notes internes",
    'req.file.noMessages': "Aucun message.",
    'req.file.actions': "Actions",
    'req.file.step': "Étape « {step} »",
    'req.file.waitingNote': "Un complément est attendu du demandeur : le délai est suspendu.",
    'req.file.circuit': "Circuit",
    'req.file.where': "Où en est-elle ?",
    'req.file.history': "Historique",
    'req.circuit.received': "Reçue",
    'req.circuit.current': "en cours",
    'req.circuit.suspended': "en attente du demandeur",
    'req.event.received': "Déposée",
    'req.event.assigned': "Prise en charge",
    'req.event.advanced': "Étape franchie",
    'req.event.waiting': "Complément demandé",
    'req.event.completed': "Complément envoyé",
    'req.event.forwarded': "Transmise",
    'req.event.done': "Traitée",
    'req.event.rejected': "Rejetée",
    'req.event.cancelled': "Annulée",
    'req.event.failed': "Échec de l'exécution",
    'req.outcome.document': "Document produit : {reference}",
    'req.outcome.marks': "{count} note(s) corrigée(s), correction tracée",
    'req.outcome.absence': "Absence justifiée ; {count} absence(s) relevée(s) couverte(s)",
    'req.outcome.done': "Demande traitée.",
    'req.thread.internal': "note interne",
    'req.thread.internalBox': "Note interne",
    'req.thread.write': "Écrire au demandeur",
    'req.thread.writeStaff': "Écrire à l'école",
    'req.thread.send': "Envoyer",
    'req.act.assign': "Prendre en charge",
    'req.act.entrust': "Confier à…",
    'req.assign.team': "Équipe {team} · étape « {step} »",
    'req.assign.any': "Comptes qui traitent les demandes",
    'req.assign.me': "moi",
    'req.assign.current': "responsable",
    'req.assign.hint': "La personne choisie reçoit une notification avec le lien vers la demande.",
    'req.flash.entrusted': "La demande est confiée à {name}.",
    'req.act.advance': "Valider l'étape",
    'req.act.advanceExecutes': "Produire et traiter",
    'req.act.advanceSub': "L'étape est franchie ; à la dernière, la demande est traitée et le demandeur prévenu.",
    'req.act.waiting': "Demander un complément",
    'req.act.waitingSub': "Le demandeur reçoit votre message ; le délai est suspendu jusqu'à sa réponse.",
    'req.act.waitingText': "Ce qu'il manque",
    'req.act.forward': "Transmettre à une autre équipe",
    'req.act.forwardSub': "La demande reste à la même étape, confiée à l'équipe choisie.",
    'req.act.team': "Équipe",
    'req.act.reject': "Rejeter",
    'req.act.rejectSub': "Le demandeur reçoit le motif.",
    'req.act.cancel': "Annuler la demande",
    'req.act.cancelSub': "La demande est fermée sans suite.",
    'req.act.reason': "Motif",
    'req.act.reasonOptional': "Motif (facultatif)",
    'req.act.note': "Note (facultative)",
    'req.flash.advance': "Étape franchie.",
    'req.flash.waiting': "Complément demandé.",
    'req.flash.forward': "Demande transmise.",
    'req.flash.reject': "Demande rejetée.",
    'req.flash.cancel': "Demande annulée.",
    'req.flash.assigned': "La demande vous est attribuée.",
    'req.flash.attached': "Pièce ajoutée.",
    'req.flash.completed': "Complément envoyé : la demande reprend.",
    'req.complete.title': "Compléter la demande",
    'req.complete.sub': "Le délai est suspendu jusqu'à votre réponse",
    'req.complete.message': "Message à l'école",
    'req.complete.submit': "Envoyer le complément",
    'req.mine.title': "Mes demandes",
    'req.mine.ask': "Faire une demande",
    'req.mine.askSub': "Le délai est celui que l'école s'engage à tenir",
    'req.mine.noType': "Aucune demande ouverte pour l'instant.",
    'req.mine.within': "sous {count} jour(s)",
    'req.mine.viaStages': "depuis « Mon stage »",
    'req.mine.askButton': "Demander",
    'req.mine.list': "Mes demandes",
    'req.mine.empty': "Aucune demande pour l'instant.",
    'req.mine.download': "Télécharger le document",
    'req.mine.complete': "Compléter",
    'req.mine.follow': "Suivre",
    'req.mine.note': "Vous êtes prévenu dans la cloche à chaque étape ; un document produit se télécharge ici.",
    'req.marks.button': "Demander une modification",
    'req.marks.title': "Demande de modification de notes",
    'req.marks.sub': "Saisissez les notes à modifier",
    'req.marks.count': "{count} note(s) à modifier",
    'req.marks.pending': "{count} demande(s) de modification déjà en cours sur cette évaluation.",
    'req.marks.submit': "Envoyer la demande",
    'req.marks.assessment': "{label} · sur {outOf} · notes validées et verrouillées",
    'req.marks.student': "Étudiant",
    'req.marks.current': "Note actuelle",
    'req.marks.asked': "Note demandée",
    'req.marks.absence': "Ou absence",
    'req.marks.note': "Précision",
    'req.marks.askedFor': "Note demandée pour {name}",
    'req.marks.absenceFor': "Absence pour {name}",
    'req.marks.noteFor': "Précision pour {name}",
    'req.marks.circuit': "La demande suit le circuit réglé (avis de la direction des études, puis application par les examens) : chaque note modifiée garde l'ancienne valeur et le motif. Une délibération close doit être rouverte avant.",
    'req.marks.sent': "Demande de modification envoyée.",
    'req.settings.title': "Demandes · réglages",
    'req.settings.sub': "Les types de demande, leurs champs, leur circuit, les équipes de traitement",
    'req.settings.module': "Module des demandes",
    'req.settings.moduleSub': "Éteint, le module retire ses droits du menu et des écrans sans rien effacer",
    'req.settings.enabled': "Module allumé",
    'req.settings.enabledHint': "Les étudiants et les enseignants déposent leurs demandes ; le personnel les traite",
    'req.settings.enabledOn': "Allumé le {date} par {who}",
    'req.settings.switchedOn': "Module des demandes allumé.",
    'req.settings.switchedOff': "Module des demandes éteint.",
    'req.settings.types': "Types de demande",
    'req.settings.audience': "Qui peut le demander",
    'req.settings.produces': "Ce que la demande produit",
    'req.settings.inactive': "inactif",
    'req.settings.deactivate': "Désactiver",
    'req.settings.deactivateText': "« {label} » a des demandes : il sera désactivé, plus personne ne pourra le demander.",
    'req.settings.deleteText': "Supprimer le type « {label} » et son circuit ?",
    'req.settings.pick': "Choisissez un type pour le régler.",
    'req.settings.newType': "Nouveau type",
    'req.settings.selected': "Le type sélectionné",
    'req.settings.label': "Libellé",
    'req.settings.actionLocked': "Ce type a des demandes : ce qu'il produit ne change plus.",
    'req.settings.opensOn': "Ouverte du",
    'req.settings.closesOn': "au",
    'req.settings.delay': "Délai visé (jours)",
    'req.settings.delayHint': "Au moins la somme des délais des étapes",
    'req.settings.color': "Couleur",
    'req.settings.guidance': "Ce qu'il faut savoir (affiché au demandeur)",
    'req.settings.active': "Actif",
    'req.settings.fields': "Champs du formulaire",
    'req.settings.fieldsSub': "Caché, affiché ou obligatoire",
    'req.settings.field': "Champ",
    'req.settings.forced': "imposé par ce que la demande produit",
    'req.settings.circuit': "Circuit de traitement",
    'req.settings.circuitSub': "Délai total visé : {days} jour(s)",
    'req.settings.addStep': "Ajouter une étape",
    'req.settings.noSteps': "Aucune étape : ajoutez-en au moins une pour ouvrir ce type.",
    'req.settings.noStepsInternship': "Pas de circuit : la convention de stage se propose depuis « Mon stage ».",
    'req.settings.step': "Étape",
    'req.settings.executes': "Produit",
    'req.settings.executesNote.action': "Une seule étape produit {what} ; les autres valident ou demandent un complément.",
    'req.settings.executesNote.none': "Ce type ne produit rien : chaque étape valide, la dernière clôt la demande avec une réponse écrite.",
    'req.settings.up': "Monter",
    'req.settings.down': "Descendre",
    'req.settings.teams': "Équipes de traitement",
    'req.settings.teamsSub': "Des comptes, ou tous les titulaires d'un rôle",
    'req.settings.roleHolders': "titulaires du rôle {role}",
    'req.settings.members': "{count} membre(s) nommé(s)",
    'req.settings.typeSaved': "Type enregistré.",
    'req.settings.teamSaved': "Équipe enregistrée.",
    'req.settings.teamDeactivated': "L'équipe a des demandes : elle est désactivée.",
    'req.team.new': "Nouvelle équipe",
    'req.team.edit': "Modifier l'équipe",
    'req.team.label': "Nom de l'équipe",
    'req.team.role': "Titulaires d'un rôle",
    'req.team.roleHint': "Tous les comptes qui ont ce rôle sont membres",
    'req.team.noRole': "Aucun rôle",
    'req.team.members': "Comptes membres",
    'req.team.membersHint': "{count} compte(s) choisi(s)",
    'req.team.search': "Filtrer les comptes",
    'violation.req.action_locked': "Ce type a des demandes : ce qu'il produit ne change plus.",
    'violation.req.assessment_open': "Une évaluation encore ouverte se corrige directement.",
    'violation.req.attachment_required': "Joignez la pièce demandée.",
    'violation.req.bad_deliberation': "Choisissez une délibération close où figure la personne.",
    'violation.req.closed': "Cette demande est déjà fermée.",
    'violation.req.closed_type': "Ce type de demande est fermé aujourd'hui.",
    'violation.req.mark_locked': "L'évaluation n'est plus corrigeable (non validée, ou année clôturée).",
    'violation.req.no_audience': "Au moins un public.",
    'violation.req.no_enrolment': "Aucune inscription active cette année-là.",
    'violation.req.no_execution': "Une étape doit produire ce que le type produit.",
    'violation.req.no_lines': "Au moins une note à modifier.",
    'violation.req.no_steps': "Ce type n'a pas de circuit de traitement.",
    'violation.req.not_a_student': "Cette demande concerne un étudiant.",
    'violation.req.not_admitted': "L'attestation de réussite est réservée aux admis.",
    'violation.req.not_for_you': "Ce type de demande n'est pas ouvert à cette personne.",
    'violation.req.not_issued': "Aucun document produit.",
    'violation.req.not_waiting': "Aucun complément n'est attendu.",
    'violation.req.one_execution': "Une seule étape produit ce que la demande produit.",
    'violation.req.too_many_files': "Cinq pièces au plus.",
    'violation.req.use_stages': "Ce type se propose depuis « Mon stage ».",
    'violation.req.waiting': "Un complément est attendu du demandeur.",
    'journal.action.requests.settings_changed': "Réglages des demandes modifiés",
    'journal.action.requests.type_saved': "Type de demande enregistré",
    'journal.action.requests.team_saved': "Équipe de traitement enregistrée",
    'journal.action.requests.request_created': "Demande déposée",
    'journal.action.requests.request_step_changed': "Demande avancée",
    'journal.action.requests.request_closed': "Demande fermée",
    'journal.filter.group.requests': "Demandes",
    'trace.table.requests_settings': "Réglages des demandes",
    'trace.table.requests_team': "Équipe de traitement",
    'trace.table.requests_team_member': "Membre d'une équipe",
    'trace.table.requests_type': "Type de demande",
    'trace.table.requests_type_step': "Étape d'un circuit",
    'trace.table.requests_request': "Demande",
    'trace.table.requests_message': "Message d'une demande",
    'trace.table.requests_file': "Pièce d'une demande",
    'trace.table.requests_event': "Historique d'une demande",
    'doc.type.enrolment': "Attestation de scolarité",
    'doc.types.enrolment': "Attestations de scolarité",
    'doc.verify.bodyEnrolment': "{type} de {student}, {training} {year}, {institution} ; produite le {date} par {by} depuis l'inscription.",
    'doc.settings.hint.enrolment': "Sans signataire réglé : « Pour la direction » et le nom de qui produit",
    'doc.settings.default.enrolment': "Aucun signataire réglé : « Pour la direction » et le nom de qui produit.",
    'violation.doc.enrolment_inactive': "L'attestation de scolarité se délivre pour une inscription active.",
    'violation.doc.not_from_deliberation': "L'attestation de scolarité se produit depuis une inscription.",
    'heb.add': "Ajouter",
    'heb.all': "Tous",
    'heb.allCampuses': "Tous les campus",
    'heb.allCategories': "Toutes les catégories",
    'heb.allGenders': "Tous les genres",
    'heb.allLevels': "Tous les niveaux",
    'heb.allRegimes': "Tous les régimes",
    'heb.breadcrumb': "Fil d'Ariane",
    'heb.change': "Changer",
    'heb.choose': "Choisir",
    'heb.confirm': "Confirmer",
    'heb.disability': "situation de handicap",
    'heb.edit': "Modifier",
    'heb.no': "non",
    'heb.none': "aucun",
    'heb.open': "Ouvrir",
    'heb.optional': "facultative",
    'heb.remove': "Retirer",
    'heb.scholar': "boursier",
    'heb.notScholar': "non boursier",
    'heb.search': "Rechercher",
    'heb.total': "Total",
    'heb.docs.none': "Aucune pièce exigée.",
    'heb.field.campus': "Campus",
    'heb.field.gender': "Genre",
    'heb.field.level': "Niveau",
    'heb.field.regime': "Régime",
    'heb.field.year': "Année",
    'heb.gender.F': "F",
    'heb.gender.M': "M",
    'heb.person.search': "Nom ou matricule, deux lettres au moins",
    'heb.person.student': "Étudiant",
    'heb.upkeep.title': "Visites et réparations",
    'heb.app.draft': "Brouillon",
    'heb.app.submitted': "Déposée",
    'heb.app.incomplete': "Pièce demandée",
    'heb.app.eligible': "Recevable",
    'heb.app.waitlisted': "Liste d'attente",
    'heb.app.rejected': "Rejetée",
    'heb.app.provisional': "Lit proposé",
    'heb.app.accepted': "À payer",
    'heb.app.final': "Allocation finale",
    'heb.app.housed': "Logée",
    'heb.app.withdrawn': "Désistée",
    'heb.app.left': "Sortie",
    'heb.alloc.provisional': "Provisoire",
    'heb.alloc.accepted': "Acceptée, à payer",
    'heb.alloc.final': "Finale",
    'heb.alloc.housed': "Occupant",
    'heb.alloc.released': "Libérée",
    'heb.alloc.ended': "Terminée",
    'heb.campaign.draft': "Programmée",
    'heb.campaign.open': "Dépôt ouvert",
    'heb.campaign.processing': "En traitement",
    'heb.campaign.closed': "Clôturée",
    'heb.repair.reported': "Signalée",
    'heb.repair.planned': "Planifiée",
    'heb.repair.in_progress': "En cours",
    'heb.repair.done': "Réalisée",
    'heb.repair.closed': "Close",
    'heb.priority.urgent': "Urgente",
    'heb.priority.high': "Haute",
    'heb.priority.normal': "Normale",
    'heb.fileStatus.pending': "À vérifier",
    'heb.fileStatus.accepted': "Conforme",
    'heb.fileStatus.rejected': "Refusée",
    'heb.payment.submitted': "À valider",
    'heb.payment.validated': "Validé",
    'heb.payment.refused': "Refusé",
    'heb.method.transfer': "Virement",
    'heb.method.deposit': "Versement",
    'heb.method.cash': "Espèces",
    'heb.method.cheque': "Chèque",
    'heb.roomGender.female': "Filles",
    'heb.roomGender.male': "Garçons",
    'heb.roomGender.mixed': "Mixte",
    'heb.roomGenderShort.female': "F",
    'heb.roomGenderShort.male': "G",
    'heb.roomGenderShort.mixed': "mixte",
    'heb.bedNature.single': "Lit simple",
    'heb.bedNature.double': "Lit double",
    'heb.bedNature.bunk': "Lit superposé",
    'heb.check.ok': "Conforme",
    'heb.check.warn': "À revoir",
    'heb.check.ko': "Non conforme",
    'heb.checklistKind.inspection': "Visite de contrôle",
    'heb.checklistKind.check_in': "État des lieux d'entrée",
    'heb.checklistKind.check_out': "État des lieux de sortie",
    'heb.stay.good': "Bon",
    'heb.stay.worn': "Usé",
    'heb.stay.damaged': "Abîmé",
    'heb.source.student': "signalée par l'occupant",
    'heb.source.inspection': "constat de visite",
    'heb.source.staff': "signalée par un agent",
    'heb.quota.level': "Niveau",
    'heb.quota.scholarship': "Boursiers",
    'heb.quota.foreign': "Étudiants étrangers",
    'heb.quota.disability': "Situation de handicap",
    'heb.quota.newcomer': "Nouveaux inscrits",
    'heb.weight.distance': "Éloignement du domicile",
    'heb.weight.scholarship': "Bourse",
    'heb.weight.results': "Résultats de l'année passée",
    'heb.weight.disability': "Situation de handicap",
    'heb.weight.seniority': "Ancienneté à l'internat",
    'heb.rule.gender': "Respecter le genre des chambres",
    'heb.rule.quotas': "Appliquer les quotas",
    'heb.rule.roommates': "Rapprocher les colocataires qui se sont choisis",
    'heb.rule.reserve': "Garder les lits réservés",
    'heb.rule.otherCampus': "Proposer un autre campus si le souhait est complet",
    'heb.reason.cap': "plus de place",
    'heb.reason.quota': "quota atteint",
    'heb.reason.cabin': "aucune chambre compatible",
    'heb.reason.price': "aucun tarif",
    'heb.column.actions': "Actions",
    'heb.column.agent': "Agent",
    'heb.column.applications': "Demandes",
    'heb.column.bed': "Lit",
    'heb.column.beds': "Lits",
    'heb.column.campaign': "Campagne",
    'heb.column.campus': "Campus",
    'heb.column.category': "Catégorie",
    'heb.column.checkpoint': "Point de contrôle",
    'heb.column.collected': "Encaissé",
    'heb.column.conformity': "Conformité",
    'heb.column.criterion': "Critère",
    'heb.column.distance': "Éloignement",
    'heb.column.due': "À payer",
    'heb.column.endsOn': "Sortie",
    'heb.column.files': "Pièces",
    'heb.column.finding': "Constat",
    'heb.column.findings': "Constats",
    'heb.column.free': "Libres",
    'heb.column.inventory': "Au parc",
    'heb.column.key': "Clé",
    'heb.column.number': "Numéro",
    'heb.column.occupancy': "Occupation",
    'heb.column.occupant': "Occupant",
    'heb.column.occupants': "Occupants",
    'heb.column.occupied': "Occupés",
    'heb.column.open': "Ouverts",
    'heb.column.paid': "Payé",
    'heb.column.period': "Séjour",
    'heb.column.points': "Points",
    'heb.column.priority': "Priorité",
    'heb.column.provisional': "Provisoires",
    'heb.column.room': "Chambre",
    'heb.column.roomType': "Type de chambre",
    'heb.column.rooms': "Chambres",
    'heb.column.startsOn': "Entrée",
    'heb.column.state': "État",
    'heb.column.student': "Étudiant",
    'heb.column.studentBed': "Étudiant et lit",
    'heb.column.total': "Total",
    'heb.column.unpaid': "Impayé",
    'heb.column.value': "Valeur",
    'heb.column.visit': "Visite",
    'heb.column.where': "Où en est-il",
    'heb.column.window': "Dépôt",
    'heb.column.wish': "Souhait",
    'heb.dash.title': "Internat et hébergement",
    'heb.dash.sub': "Année {year} · occupation, paiements, demandes et entretien",
    'heb.dash.noYear': "Aucune année universitaire n'a encore de campagne d'hébergement.",
    'heb.dash.occupancy': "Taux d'occupation",
    'heb.dash.occupancySub': "Par mois, comparé à l'an passé",
    'heb.dash.money': "Frais attendus et encaissés",
    'heb.dash.moneySub': "Par mois, en dirhams",
    'heb.dash.thisYear': "Cette année",
    'heb.dash.lastYear': "L'an passé",
    'heb.dash.expected': "Attendu",
    'heb.dash.collected': "Encaissé",
    'heb.dash.byCampus': "Par campus",
    'heb.dash.repairs': "Réparations ouvertes",
    'heb.dash.repairsSub': "Par catégorie, délai moyen",
    'heb.dash.noRepairs': "Aucune réparation ouverte.",
    'heb.dash.repairLine': "{count} · {days} j",
    'heb.dash.moves': "Le parcours de l'année",
    'heb.moves.submitted': "Demandes déposées",
    'heb.moves.final': "Allocations finales",
    'heb.moves.withdrawn': "Désistements",
    'heb.moves.unpaid_released': "Lits libérés faute de paiement",
    'heb.moves.left_early': "Départs anticipés",
    'heb.moves.moved': "Changements de lit",
    'heb.kpi.occupancy': "Occupation",
    'heb.kpi.occupancyNote': "{occupied} lits sur {beds} · l'an passé {last}",
    'heb.kpi.collected': "Encaissé",
    'heb.kpi.collectedNote': "sur {expected} attendus",
    'heb.kpi.unpaid': "Impayés",
    'heb.kpi.unpaidNote': "{count} allocation(s) en retard",
    'heb.kpi.waitlist': "Liste d'attente",
    'heb.kpi.waitlistNote': "{count} recevable(s) sans lit",
    'heb.kpi.repairs': "Réparations ouvertes",
    'heb.kpi.repairsNote': "délai moyen {days} jours",
    'heb.kpi.conformity': "Conformité des visites",
    'heb.kpi.conformityNote': "chambres conformes à la dernière visite",
    'heb.campaigns.title': "Campagnes d'hébergement",
    'heb.campaigns.sub': "Le dépôt des demandes, les places ouvertes, les règles d'allocation",
    'heb.campaigns.new': "Lancer une campagne",
    'heb.campaigns.current': "En cours",
    'heb.campaigns.scheduled': "Programmées",
    'heb.campaigns.closed': "Clôturées",
    'heb.campaigns.empty': "Aucune campagne ici.",
    'heb.campaigns.moveIn': "entrée le {date}",
    'heb.campaign.edit': "Modifier la campagne",
    'heb.campaign.to.open': "Ouvrir le dépôt",
    'heb.campaign.to.processing': "Clore le dépôt et traiter",
    'heb.campaign.to.closed': "Clôturer la campagne",
    'heb.campaign.toText.open': "Les étudiants des niveaux ouverts pourront déposer leur demande sur le portail.",
    'heb.campaign.toText.processing': "Le dépôt se ferme : vous pourrez allouer les lits et publier les résultats.",
    'heb.campaign.toText.closed': "Une campagne clôturée ne se modifie plus et ne se rouvre pas.",
    'heb.campaign.publish': "Publier les résultats",
    'heb.campaign.publishText': "Chaque étudiant reçoit le lit proposé, ou sa place en liste d'attente, avec le message de la campagne.",
    'heb.campaign.published': "{count} étudiant(s) prévenu(s).",
    'heb.campaign.moved': "La campagne change d'étape.",
    'heb.campaign.saved': "Campagne enregistrée.",
    'heb.campaign.views': "Vues de la campagne",
    'heb.campaign.tab.follow': "Suivi",
    'heb.campaign.tab.applications': "Demandes",
    'heb.campaign.tab.allocation': "Allocation",
    'heb.campaignForm.new': "Lancer une campagne",
    'heb.campaignForm.edit': "Modifier la campagne",
    'heb.campaignForm.step': "Étape {n} sur 6 : {label}",
    'heb.campaignForm.steps.calendar': "Calendrier",
    'heb.campaignForm.steps.publics': "Publics",
    'heb.campaignForm.steps.places': "Places et quotas",
    'heb.campaignForm.steps.documents': "Pièces",
    'heb.campaignForm.steps.rules': "Règles d'allocation",
    'heb.campaignForm.steps.messages': "Messages",
    'heb.campaignForm.label': "Intitulé",
    'heb.campaignForm.description': "Présentation sur le portail",
    'heb.campaignForm.opensOn': "Ouverture du dépôt",
    'heb.campaignForm.closesOn': "Fin du dépôt",
    'heb.campaignForm.processingOn': "Résultats à partir du",
    'heb.campaignForm.moveInOn': "Entrée",
    'heb.campaignForm.stayEndsOn': "Fin du séjour",
    'heb.campaignForm.publicsHint': "Les niveaux qui peuvent déposer. Aucun coché : tous les niveaux.",
    'heb.campaignForm.noLevels': "Aucun niveau au référentiel.",
    'heb.campaignForm.addPlace': "Ouvrir un lieu",
    'heb.campaignForm.noEstate': "Ajoutez d'abord des campus et des chambres au parc.",
    'heb.campaignForm.quotas': "Quotas",
    'heb.campaignForm.quotasHint': "Une part des lits ouverts réservée à un public.",
    'heb.campaignForm.quotaLabel': "Intitulé du quota",
    'heb.campaignForm.criterion': "Critère",
    'heb.campaignForm.percent': "Part",
    'heb.campaignForm.addQuota': "Ajouter un quota",
    'heb.campaignForm.docLabel': "Pièce",
    'heb.campaignForm.condition': "Condition",
    'heb.campaignForm.conditionHint': "Par exemple « si boursier » : la pièce n'est alors pas exigée de tous.",
    'heb.campaignForm.required': "Obligatoire",
    'heb.campaignForm.addDoc': "Ajouter une pièce",
    'heb.campaignForm.defaultDoc': "Copie de la CIN",
    'heb.campaignForm.weights': "Points par critère",
    'heb.campaignForm.rules': "Règles",
    'heb.campaignForm.reserved': "Lits gardés en réserve",
    'heb.campaignForm.reservedHint': "Pour les cas particuliers, alloués à la main.",
    'heb.campaignForm.provisional': "Message de l'allocation provisoire",
    'heb.campaignForm.provisionalHint': "Envoyé avec le lit proposé : délai d'acceptation, paiement.",
    'heb.campaignForm.final': "Message de l'allocation finale",
    'heb.campaignForm.finalHint': "Envoyé une fois les frais payés : convocation à la remise des clés.",
    'heb.follow.submitted': "Demandes déposées",
    'heb.follow.submittedNote': "{count} à traiter",
    'heb.follow.beds': "Lits occupés",
    'heb.follow.bedsNote': "{count} lit(s) libre(s)",
    'heb.follow.payments': "Paiements attendus",
    'heb.follow.paymentsNote': "{count} reçu(s) à valider",
    'heb.follow.waitlisted': "Liste d'attente",
    'heb.follow.waitlistedNote': "servie à chaque lit libéré",
    'heb.follow.flow': "Le parcours des demandes",
    'heb.follow.byCampus': "Occupation par campus",
    'heb.follow.todo': "À faire",
    'heb.follow.todoApplications': "{count} demande(s) à examiner",
    'heb.follow.todoIncomplete': "{count} en attente d'une pièce",
    'heb.follow.todoReceipts': "{count} reçu(s) de paiement à valider",
    'heb.follow.todoLate': "{count} échéance(s) dépassée(s)",
    'heb.follow.todoKeys': "{count} allocation(s) finale(s), clés à remettre",
    'heb.follow.moveIn': "entrée le {date}",
    'heb.follow.setup': "Le réglage de la campagne",
    'heb.follow.stay': "du {from} au {to} · {months} mois",
    'heb.flow.submitted': "Déposées",
    'heb.flow.eligible': "Recevables",
    'heb.flow.incomplete': "Pièce demandée",
    'heb.flow.provisional': "Lit proposé",
    'heb.flow.final': "Finales",
    'heb.flow.waitlisted': "En attente",
    'heb.applications.views': "Vues des demandes",
    'heb.applications.view.todo': "À traiter",
    'heb.applications.view.incomplete': "Pièce demandée",
    'heb.applications.view.eligible': "Recevables",
    'heb.applications.view.allocated': "Allouées",
    'heb.applications.view.waitlisted': "Liste d'attente",
    'heb.applications.view.rejected': "Rejetées",
    'heb.applications.view.all': "Toutes",
    'heb.applications.search': "Nom, numéro de demande, matricule",
    'heb.applications.empty': "Aucune demande ici.",
    'heb.applications.enter': "Saisir une demande",
    'heb.applications.enterSubmit': "Déposer la demande",
    'heb.applications.note': "Les demandes arrivent du portail étudiant ; nom, formation, niveau et régime viennent de l'inscription. Les points se calculent avec les critères de la campagne.",
    'heb.wish.campus': "Campus souhaité",
    'heb.wish.type': "Type de chambre",
    'heb.wish.type2': "Second choix",
    'heb.wish.roommate': "Colocataire souhaité",
    'heb.wish.homeCity': "Ville du domicile familial",
    'heb.wish.distance': "Éloignement en km",
    'heb.wish.distanceHint': "Du domicile familial à l'école.",
    'heb.wish.emergency': "Contact d'urgence",
    'heb.wish.disability': "Situation de handicap",
    'heb.wish.disabilityHint': "Une chambre adaptée est proposée en priorité.",
    'heb.wish.needs': "Besoins particuliers",
    'heb.wish.rules': "J'ai lu le règlement de l'internat et je m'engage à le respecter.",
    'heb.wish.rulesStaff': "L'étudiant a lu et accepté le règlement de l'internat.",
    'heb.application.wishes': "Souhaits",
    'heb.application.typeOr': "{first}, sinon {second}",
    'heb.application.documents': "Pièces",
    'heb.application.history': "Historique",
    'heb.application.treatment': "Traitement",
    'heb.application.points': "Points",
    'heb.application.pointsSub': "Critères de la campagne",
    'heb.application.noPoints': "Les points se calculent quand la demande est recevable.",
    'heb.application.assign': "Allouer un lit",
    'heb.application.release': "Libérer le lit",
    'heb.application.backToQueue': "La demande revient dans la file (sinon, désistement)",
    'heb.application.bed': "Le lit",
    'heb.application.roommates': "Colocataires",
    'heb.application.period': "Séjour",
    'heb.application.acceptBy': "À accepter avant le",
    'heb.application.payBy': "À payer avant le",
    'heb.application.paid': "payé : {amount}",
    'heb.application.invoice': "facture {number}",
    'heb.application.keys': "Clé {key} remise le {at}",
    'heb.application.released': "Lit libéré : {reason}",
    'heb.application.releasedDone': "Le lit est libéré.",
    'heb.application.closed': "Cette demande est close.",
    'heb.application.born': "né(e) le {date}",
    'heb.application.submittedOn': "déposée le {date}",
    'heb.review.eligible': "Déclarer recevable",
    'heb.review.incomplete': "Demander une pièce",
    'heb.review.waitlisted': "Mettre en liste d'attente",
    'heb.review.rejected': "Rejeter",
    'heb.review.eligibleText': "Les points sont calculés ; l'étudiant est prévenu.",
    'heb.review.incompleteText': "Dites quelle pièce manque : l'étudiant la dépose sur le portail.",
    'heb.review.waitlistedText': "La demande attend un lit libéré.",
    'heb.review.rejectedText': "Le motif est envoyé à l'étudiant.",
    'heb.review.note': "Note (facultative)",
    'heb.review.reason': "Motif",
    'heb.review.done': "Décision enregistrée.",
    'heb.event.draft': "Brouillon enregistré",
    'heb.event.submitted': "Demande déposée",
    'heb.event.eligible': "Déclarée recevable",
    'heb.event.incomplete': "Pièce demandée",
    'heb.event.waitlisted': "Mise en liste d'attente",
    'heb.event.rejected': "Rejetée",
    'heb.event.proposed': "Lit proposé",
    'heb.event.accepted': "Lit accepté",
    'heb.event.declined': "Lit refusé",
    'heb.event.receipt': "Reçu de paiement déposé",
    'heb.event.receipt_refused': "Reçu refusé",
    'heb.event.paid': "Paiement validé",
    'heb.event.final': "Allocation finale",
    'heb.event.keys': "Clés remises",
    'heb.event.left': "Sortie",
    'heb.event.moved': "Changement de lit",
    'heb.event.released': "Lit libéré",
    'heb.event.withdrawn': "Désistement",
    'heb.event.file_accepted': "Pièce conforme",
    'heb.event.file_rejected': "Pièce refusée",
    'heb.event.file_pending': "Pièce à revoir",
    'heb.files.see': "Voir",
    'heb.files.uploading': "Envoi en cours…",
    'heb.files.photo': "Prendre ou choisir une photo",
    'heb.files.drop': "Déposer un fichier (PDF, PNG, JPEG, 10 Mo au plus)",
    'heb.files.remove': "Retirer",
    'heb.files.accept': "Conforme",
    'heb.files.refuse': "Refuser la pièce",
    'heb.files.accepted': "Pièce déclarée conforme.",
    'heb.files.refused': "Pièce refusée ; l'étudiant est prévenu.",
    'heb.files.missing': "Pas encore déposée.",
    'heb.files.other': "Autres fichiers",
    'heb.alloc.mode': "Mode d'allocation",
    'heb.alloc.auto': "Automatique",
    'heb.alloc.manual': "Manuelle",
    'heb.alloc.criteria': "Critères et points",
    'heb.alloc.criteriaSub': "Les demandes passent par ordre de points",
    'heb.alloc.rules': "Règles appliquées",
    'heb.alloc.reserved': "{count} lit(s) gardé(s) en réserve",
    'heb.alloc.waiting': "Demandes à placer",
    'heb.alloc.waitingNote': "recevables, sans lit",
    'heb.alloc.placed': "Placées",
    'heb.alloc.placedNote': "dont {roommates} colocation(s) respectée(s)",
    'heb.alloc.unplaced': "Sans lit",
    'heb.alloc.unplacedNote': "places {cap} · quotas {quota} · chambres {cabin}",
    'heb.alloc.seed': "Tirage",
    'heb.alloc.simulatedAt': "simulé le {at}",
    'heb.alloc.preview': "La simulation",
    'heb.alloc.previewSub': "Rien n'est enregistré avant la validation",
    'heb.alloc.reseed': "Nouveau tirage",
    'heb.alloc.validate': "Valider les {count} lits proposés",
    'heb.alloc.validateText': "Les lits deviennent des allocations provisoires ; chaque étudiant les voit sur son portail et doit accepter dans le délai.",
    'heb.alloc.done': "{count} lit(s) proposé(s).",
    'heb.alloc.none': "Aucune demande recevable à placer.",
    'heb.alloc.with': "avec {name}",
    'heb.alloc.autoNote': "À points égaux, le tirage départage ; il est gardé avec la campagne pour refaire la même allocation.",
    'heb.alloc.queue': "La file",
    'heb.alloc.queueSub': "{count} demande(s) sans lit",
    'heb.alloc.queueEmpty': "Toutes les demandes recevables ont un lit.",
    'heb.alloc.pick': "Choisissez une demande",
    'heb.alloc.pickSub': "Les lits compatibles s'affichent ici",
    'heb.alloc.wishes': "Souhaits : {campus} · {type}, sinon {type2}",
    'heb.alloc.noBeds': "Aucun lit libre compatible.",
    'heb.alloc.emptyRoom': "chambre vide",
    'heb.alloc.assign': "Allouer",
    'heb.alloc.assigned': "Lit proposé à {name}.",
    'heb.alloc.openApplication': "Ouvrir la demande",
    'heb.payments.title': "Paiements des frais",
    'heb.payments.sub': "Une allocation acceptée devient finale une fois les frais payés",
    'heb.payments.record': "Enregistrer un paiement au guichet",
    'heb.payments.recordSub': "Espèces, chèque, virement ou versement reçu par l'école",
    'heb.payments.recordSubmit': "Enregistrer le paiement",
    'heb.payments.recorded': "Paiement enregistré.",
    'heb.payments.amount': "Montant (DH)",
    'heb.payments.amountHint': "Par exemple 850 ou 850,50.",
    'heb.payments.method': "Mode de paiement",
    'heb.payments.reference': "Référence",
    'heb.payments.paidOn': "Date du paiement",
    'heb.payments.left': "Reste {amount} sur {total}",
    'heb.payments.receiptsNote': "déposés par les étudiants",
    'heb.payments.lateNote': "au-delà, le lit est libéré",
    'heb.payments.paidNote': "allocations finales",
    'heb.payments.view.receipts': "Reçus à valider",
    'heb.payments.view.due': "À payer",
    'heb.payments.view.late': "Échéance dépassée",
    'heb.payments.view.paid': "Payées",
    'heb.payments.view.all': "Toutes",
    'heb.payments.state.receipt': "Reçu à valider",
    'heb.payments.state.refused': "Reçu refusé",
    'heb.payments.state.paid': "Payé",
    'heb.payments.state.late': "Échéance dépassée",
    'heb.payments.state.part': "Payé en partie",
    'heb.payments.state.due': "À payer",
    'heb.payments.receiptAt': "reçu déposé le {at}",
    'heb.payments.payBy': "avant le {date}",
    'heb.payments.examine': "Examiner",
    'heb.payments.collect': "Encaisser",
    'heb.payments.empty': "Rien ici.",
    'heb.payments.receipt': "Le reçu",
    'heb.payments.noReceipt': "Choisissez un reçu à valider dans la liste.",
    'heb.payments.receiptOf': "Reçu de {name}",
    'heb.payments.declared': "Montant déclaré",
    'heb.payments.leftToPay': "Reste à payer",
    'heb.payments.validate': "Valider le paiement",
    'heb.payments.refuse': "Refuser le reçu",
    'heb.payments.refuseHint': "L'étudiant reçoit le motif et dépose un autre reçu.",
    'heb.payments.refused': "Reçu refusé ; l'étudiant est prévenu.",
    'heb.payments.validated': "Paiement validé.",
    'heb.payments.validatedFinal': "Paiement validé : l'allocation de {name} est finale.",
    'heb.payments.financeNote': "Avec le module Finance, la validation encaisse la facture de l'étudiant.",
    'heb.payments.finalNote': "Tout payé, l'allocation devient finale et l'étudiant est convoqué à la remise des clés.",
    'heb.payments.note': "Pas de paiement en ligne : l'étudiant dépose son reçu (virement, versement) et un agent le valide, ou l'agent enregistre un paiement reçu au guichet. Passé le délai de grâce, le lit revient à la campagne.",
    'heb.price.rent': "Loyer, {months} mois",
    'heb.price.rentTimes': "Loyer, {months} mois × {rent}",
    'heb.price.deposit': "Caution",
    'heb.price.fee': "Frais de dossier",
    'heb.price.toPay': "À payer",
    'heb.stays.title': "Entrées et sorties",
    'heb.stays.sub': "Remise des clés, états des lieux, restitution de la caution",
    'heb.stays.print': "Imprimer les convocations",
    'heb.stays.view.entries': "Entrées",
    'heb.stays.view.occupants': "Clés en circulation",
    'heb.stays.view.exits': "Sorties",
    'heb.stays.empty.entries': "Aucune entrée à venir.",
    'heb.stays.empty.occupants': "Aucun occupant.",
    'heb.stays.empty.exits': "Aucune sortie.",
    'heb.stays.search': "Nom, lit, clé",
    'heb.stays.owing': "Reste {amount} à payer",
    'heb.stays.retained': "retenue {amount}",
    'heb.stays.payments': "Paiements",
    'heb.keys.give': "Remettre les clés",
    'heb.keys.giveShort': "Remettre",
    'heb.keys.return': "Enregistrer la sortie",
    'heb.keys.returnShort': "Sortie",
    'heb.keys.label': "Clé ou badge remis",
    'heb.keys.labelHint': "Le numéro gravé sur la clé, ou celui du badge.",
    'heb.keys.sheet': "État des lieux",
    'heb.keys.noChecklist': "Aucune grille d'état des lieux active : ajoutez-en une dans les réglages.",
    'heb.keys.note': "Observations",
    'heb.keys.photos': "Photos",
    'heb.keys.signed': "L'étudiant a lu et signé l'état des lieux",
    'heb.keys.given': "Clés remises.",
    'heb.keys.returned': "Sortie enregistrée.",
    'heb.keys.deposit': "caution {amount}",
    'heb.keys.retained': "Retenue sur la caution (DH)",
    'heb.keys.retainedHint': "Vide : la caution est rendue en entier. Au plus {amount} DH ; une retenue demande des observations.",
    'heb.visits.title': "Visites de contrôle",
    'heb.visits.sub': "Programmées ou inopinées, chambre par chambre, avec constats et photos",
    'heb.visits.view.planned': "À venir",
    'heb.visits.state.planned': "Programmée",
    'heb.visits.view.followup': "Suites à donner",
    'heb.visits.state.followup': "Suites à donner",
    'heb.visits.view.done': "Faites",
    'heb.visits.state.done': "Faite",
    'heb.visits.empty': "Aucune visite ici.",
    'heb.visits.announced': "annoncée",
    'heb.visits.surprise': "inopinée",
    'heb.visits.plan': "Programmer une visite",
    'heb.visits.planSubmit': "Programmer la visite",
    'heb.visits.where': "Lieu",
    'heb.visits.whereHint': "Un bloc ou un étage du campus.",
    'heb.visits.wholeCampus': "Tout le campus",
    'heb.visits.at': "Date et heure",
    'heb.visits.agent': "Agent",
    'heb.visits.agentHint': "Celui qui fera la visite.",
    'heb.visits.me': "Moi",
    'heb.visits.checklist': "Grille de contrôle",
    'heb.visits.checklistItems': "{label} ({count} points)",
    'heb.visits.announce': "Prévenir les occupants",
    'heb.visits.announceHint': "Décochée : visite inopinée.",
    'heb.visit.title': "Visite · {where}",
    'heb.visit.progress': "{seen} / {total} chambres vues",
    'heb.visit.finish': "Terminer la visite",
    'heb.visit.finishText': "{seen} chambre(s) vue(s) sur {total}. Une visite terminée ne se modifie plus.",
    'heb.visit.finished': "Visite terminée.",
    'heb.visit.rooms': "Chambres",
    'heb.visit.toSee': "à voir",
    'heb.visit.free': "libre",
    'heb.visit.pick': "Choisissez une chambre",
    'heb.visit.pickHint': "La grille de contrôle s'affiche pour la chambre choisie.",
    'heb.visit.room': "Chambre {number}",
    'heb.visit.note': "Observations",
    'heb.visit.present': "Occupants présents",
    'heb.visit.photos': "Photos",
    'heb.visit.addPhoto': "Ajouter une photo",
    'heb.visit.followups': "Suites",
    'heb.visit.repair': "Créer une réparation pour cette chambre",
    'heb.visit.warning': "Avertir les occupants",
    'heb.visit.warningHint': "Ils reçoivent une notification avec les observations.",
    'heb.visit.noFollowup': "Aucune suite.",
    'heb.visit.save': "Enregistrer",
    'heb.visit.next': "Chambre suivante",
    'heb.visit.saved': "Constat enregistré.",
    'heb.visit.back': "Les chambres",
    'heb.repairs.title': "Réparations",
    'heb.repairs.sub': "Signalées par un étudiant, une visite ou un agent, jusqu'à la clôture",
    'heb.repairs.counts': "{open} ouvertes · {urgent} urgentes · {out} lit(s) hors service",
    'heb.repairs.report': "Signaler une réparation",
    'heb.repairs.reportSubmit': "Signaler",
    'heb.repairs.search': "Chambre, objet, intervenant",
    'heb.repairs.col.reported': "Signalées",
    'heb.repairs.col.planned': "Planifiées",
    'heb.repairs.col.in_progress': "En cours",
    'heb.repairs.col.done': "Réalisées, à vérifier",
    'heb.repairs.col.closed': "Closes",
    'heb.repairs.colEmpty': "Aucune.",
    'heb.repairs.since': "depuis le {at}",
    'heb.repairs.out': "hors service",
    'heb.repairs.subject': "Objet",
    'heb.repairs.description': "Description",
    'heb.repairs.photos': "Photos",
    'heb.repairs.access': "Un agent peut entrer en l'absence des occupants",
    'heb.repairs.bedHint': "Laissez vide si toute la chambre est concernée.",
    'heb.repairs.wholeRoom': "Toute la chambre",
    'heb.repair.roomOf': "Chambre {room}",
    'heb.repair.plan': "Planifier",
    'heb.repair.planTitle': "Planifier l'intervention",
    'heb.repair.planSubmit': "Planifier",
    'heb.repair.planSaved': "Intervention planifiée ; le déclarant est prévenu.",
    'heb.repair.assignee': "Agent technique",
    'heb.repair.assigneeHint': "Un agent de l'école, ou un prestataire ci-dessous.",
    'heb.repair.provider': "Prestataire",
    'heb.repair.plannedAt': "Planifiée le",
    'heb.repair.to.in_progress': "Démarrer",
    'heb.repair.to.done': "Marquer réalisée",
    'heb.repair.to.closed': "Vérifier et clore",
    'heb.repair.moved': "Réparation mise à jour.",
    'heb.repair.bedOut': "Mettre le lit hors service",
    'heb.repair.roomOut': "Mettre la chambre hors service",
    'heb.repair.serveAgain': "Remettre en service",
    'heb.repair.putOut': "Mis hors service.",
    'heb.repair.backIn': "Remis en service.",
    'heb.repair.finding': "Le constat",
    'heb.repair.reportedBy': "{name}, le {at} · {source}",
    'heb.repair.noDescription': "Pas de description.",
    'heb.repair.fromVisit': "Constatée lors de la visite du {at}",
    'heb.repair.intervention': "Intervention",
    'heb.repair.who': "Intervenant",
    'heb.repair.accessLabel': "Accès",
    'heb.repair.accessYes': "possible en l'absence des occupants",
    'heb.repair.accessNo': "en présence des occupants",
    'heb.repair.outLabel': "Hors service",
    'heb.repair.wholeRoom': "toute la chambre",
    'heb.repair.costTitle': "Coût et imputation",
    'heb.repair.cost': "Coût (DH)",
    'heb.repair.charge': "Imputation",
    'heb.repair.chargeInstitution': "Pris en charge par l'internat",
    'heb.repair.chargeOccupant': "Dégradation : retenue sur la caution de l'occupant",
    'heb.repair.occupant': "Occupant",
    'heb.repair.costSave': "Enregistrer le coût",
    'heb.repair.costSaved': "Coût enregistré.",
    'heb.repair.follow': "Suivi",
    'heb.estate.title': "Parc et tarifs",
    'heb.estate.sub': "Campus, blocs, étages, plan d'occupation lit par lit",
    'heb.estate.tab': "Campus et chambres",
    'heb.estate.tree': "Le parc",
    'heb.estate.empty': "Aucun campus : ajoutez-en un.",
    'heb.estate.beds': "{count} lits",
    'heb.estate.noBlock': "Sans bloc",
    'heb.estate.noFloor': "Sans étage",
    'heb.estate.allTypes': "Tous les types",
    'heb.estate.occupied': "occupé",
    'heb.estate.free': "libre",
    'heb.estate.provisional': "allocation provisoire",
    'heb.estate.out': "hors service",
    'heb.estate.full': "complète",
    'heb.estate.freeBeds': "{count} lit(s) libre(s)",
    'heb.estate.noRooms': "Aucune chambre ici.",
    'heb.estate.summary': "{where} · {beds} lits · {occupied} occupés · {out} hors service",
    'heb.estate.newCampus': "Ajouter un campus",
    'heb.estate.campusSaved': "Campus enregistré.",
    'heb.room.new': "Ajouter une chambre",
    'heb.room.newHint': "Elle reçoit les lits de son type (B-204-1, B-204-2…).",
    'heb.room.edit': "Modifier la chambre",
    'heb.room.saved': "Chambre enregistrée.",
    'heb.room.title': "Chambre {number}",
    'heb.room.number': "Numéro",
    'heb.room.block': "Bloc",
    'heb.room.floor': "Étage",
    'heb.room.gender': "Genre",
    'heb.room.surface': "Surface (m²)",
    'heb.room.equipment': "Équipements",
    'heb.room.equipmentHint': "Un par ligne, ou séparés par des virgules.",
    'heb.room.note': "Note",
    'heb.room.typeBeds': "{label} ({count} lit(s))",
    'heb.room.beds': "Lits",
    'heb.room.bedsHint': "Un lit qui a déjà servi ne se retire pas : mettez-le hors service.",
    'heb.room.bedsSub': "{occupied} occupé(s), {free} libre(s)",
    'heb.room.nature': "Nature",
    'heb.room.outOfService': "Hors service",
    'heb.room.addBed': "Ajouter un lit",
    'heb.room.putOut': "Mettre hors service",
    'heb.room.history': "Historique",
    'heb.room.noHistory': "Pas encore d'historique.",
    'heb.room.historyStay': "Remise des clés à {name}, lit {bed}",
    'heb.room.historyLeft': "Sortie de {name}, lit {bed}",
    'heb.room.historyVisit': "Visite de contrôle : {label}",
    'heb.room.historyRepair': "Réparation : {label}",
    'heb.room.about': "La chambre",
    'heb.room.capacity': "Capacité",
    'heb.room.place': "Emplacement",
    'heb.room.repairs': "Réparations",
    'heb.room.noRepairs': "Aucune réparation.",
    'heb.room.delete': "Supprimer la chambre",
    'heb.room.deleteText': "Une chambre jamais allouée se supprime ; sinon, mettez-la hors service.",
    'heb.prices.title': "Tarifs",
    'heb.prices.sub': "La règle la plus précise l'emporte : campus, type de chambre, niveau, régime",
    'heb.prices.grid': "La grille",
    'heb.prices.gridSub': "Montants en dirhams · un critère « Tous » vaut pour toutes les valeurs",
    'heb.prices.empty': "Aucun tarif pour cette année.",
    'heb.prices.new': "Ajouter un tarif",
    'heb.prices.edit': "Modifier le tarif",
    'heb.prices.dialogHint': "Un critère laissé à « Tous » vaut pour toutes les valeurs.",
    'heb.prices.rent': "Loyer mensuel",
    'heb.prices.saved': "Tarif enregistré.",
    'heb.prices.delete': "Supprimer le tarif",
    'heb.prices.deleteText': "Les allocations déjà proposées gardent leur montant.",
    'heb.prices.deleted': "Tarif supprimé.",
    'heb.prices.coverage': "Vérification",
    'heb.prices.coverageSub': "Des lits ouverts sans tarif : leurs demandes ne peuvent pas être allouées",
    'heb.prices.covered': "Tous les lits ouverts ont un tarif.",
    'heb.prices.combination': "Combinaison sans tarif",
    'heb.prices.create': "Créer le tarif",
    'heb.prices.simulate': "Simuler un tarif",
    'heb.prices.simulateSub': "Ce que paiera une demande pour un lit",
    'heb.prices.application': "Demande",
    'heb.prices.ruleUsed': "Règle retenue : {label}",
    'heb.prices.noRule': "Aucun tarif ne s'applique.",
    'heb.prices.note': "Le paiement peut être échelonné (réglages). Avec le module Finance, les frais deviennent une facture de l'étudiant.",
    'heb.settings.title': "Réglages de l'internat",
    'heb.settings.sub': "Types de chambre, grilles de contrôle, catégories de réparation, délais et paiements",
    'heb.settings.module': "Le module",
    'heb.settings.on': "Module Internat et hébergement activé",
    'heb.settings.off': "Module Internat et hébergement éteint",
    'heb.settings.since': "Activé le {at} par {by}.",
    'heb.settings.moduleHint': "Éteint, le module disparaît du menu et du portail, sans rien effacer.",
    'heb.settings.enabled': "Module activé.",
    'heb.settings.disabled': "Module éteint.",
    'heb.settings.financeOn': "Finance allumée : les frais sont facturés",
    'heb.settings.financeOff': "Finance éteinte : les paiements restent à l'internat",
    'heb.settings.delays': "Délais et paiements",
    'heb.settings.acceptDays': "Délai pour accepter un lit proposé",
    'heb.settings.payDays': "Délai de paiement après acceptation",
    'heb.settings.graceDays': "Délai de grâce",
    'heb.settings.days': "En jours.",
    'heb.settings.instalments': "Échelonnement",
    'heb.settings.instalmentsHint': "Par exemple : caution et deux mois, puis par trimestre.",
    'heb.settings.autoRelease': "Libérer le lit automatiquement à la fin du délai de grâce",
    'heb.settings.refundDeposit': "Restituer la caution à la sortie, moins les retenues de l'état des lieux",
    'heb.settings.save': "Enregistrer",
    'heb.settings.saved': "Réglages enregistrés.",
    'heb.settings.roomTypes': "Types de chambre",
    'heb.settings.campuses': "Campus",
    'heb.settings.categories': "Catégories de réparation",
    'heb.settings.providers': "Prestataires",
    'heb.settings.providersSub': "Ou un agent technique de l'école",
    'heb.settings.noProviders': "Aucun prestataire.",
    'heb.settings.checklists': "Grilles de contrôle",
    'heb.settings.checklistsSub': "Pour les visites et les états des lieux",
    'heb.settings.points': "{count} points",
    'heb.settings.inactive': "inactif",
    'heb.settings.active': "Actif",
    'heb.settings.label': "Intitulé",
    'heb.settings.beds': "Nombre de lits",
    'heb.settings.name': "Nom",
    'heb.settings.code': "Code",
    'heb.settings.address': "Adresse",
    'heb.settings.trades': "Métiers",
    'heb.settings.phone': "Téléphone",
    'heb.settings.email': "Courriel",
    'heb.settings.checklistKind': "Usage",
    'heb.settings.items': "Points de contrôle",
    'heb.settings.itemsHint': "Un point par ligne, 120 caractères au plus.",
    'heb.settings.roomtype.new': "Ajouter : type de chambre",
    'heb.settings.roomtype.edit': "Modifier : type de chambre",
    'heb.settings.category.new': "Ajouter : catégorie",
    'heb.settings.category.edit': "Modifier : catégorie",
    'heb.settings.provider.new': "Ajouter : prestataire",
    'heb.settings.provider.edit': "Modifier : prestataire",
    'heb.settings.checklist.new': "Ajouter : grille",
    'heb.settings.checklist.edit': "Modifier : grille",
    'heb.settings.campus.new': "Ajouter : campus",
    'heb.settings.campus.edit': "Modifier : campus",
    'heb.settings.rights': "Les droits : housing.read, housing.manage (campagnes, demandes, allocation, parc), housing.pay (paiements), housing.keys (entrées et sorties), housing.inspect (visites), housing.repair (réparations), housing.configure, et housing.own pour l'étudiant.",
    'heb.mine.title': "Mon logement",
    'heb.mine.myApplication': "Ma demande",
    'heb.mine.complete': "Compléter la demande {number}",
    'heb.mine.fromEnrolment': "Vos nom, formation, niveau et situation de boursier viennent de votre inscription : l'école les corrige si besoin.",
    'heb.mine.alreadyFiled': "{label} : {count} fichier(s) déjà déposé(s)",
    'heb.mine.pieces': "Pièces à joindre",
    'heb.mine.saveDraft': "Enregistrer le brouillon",
    'heb.mine.submit': "Déposer ma demande",
    'heb.mine.submitted': "Demande déposée : vous serez prévenu de la suite.",
    'heb.mine.draftSaved': "Brouillon enregistré.",
    'heb.mine.closes': "Dépôt jusqu'au",
    'heb.mine.results': "Résultats à partir du",
    'heb.mine.youWillPay': "Ce que vous paierez",
    'heb.mine.pickToSee': "Choisissez un campus et un type de chambre",
    'heb.mine.pricesHint': "Le montant s'affiche selon votre souhait.",
    'heb.mine.payAfter': "Selon le lit alloué ; payable après l'allocation.",
    'heb.mine.where': "Où en est ma demande",
    'heb.track.submitted': "Déposée",
    'heb.track.eligible': "Recevable",
    'heb.track.provisional': "Lit proposé",
    'heb.track.accepted': "Accepté",
    'heb.track.final': "Paiement validé",
    'heb.track.housed': "Clés remises",
    'heb.mine.accept': "Accepter ce lit",
    'heb.mine.acceptText': "Vous vous engagez à payer les frais dans le délai ; sinon, le lit est libéré.",
    'heb.mine.acceptHint': "À accepter avant le {date}, sinon le lit est proposé à un autre étudiant.",
    'heb.mine.decline': "Refuser ce lit",
    'heb.mine.declineText': "Le lit revient à la campagne.",
    'heb.mine.receipt': "Déposer mon reçu de paiement",
    'heb.mine.receiptFile': "Le reçu (avis de virement ou de versement)",
    'heb.mine.receiptSubmit': "Déposer le reçu",
    'heb.mine.receiptHint': "Payez par virement ou au guichet de l'école, puis déposez le reçu : un agent des finances le valide.",
    'heb.mine.receiptSent': "Reçu déposé : vous serez prévenu de sa validation.",
    'heb.mine.payHint': "À payer avant le {date}, sinon le lit est libéré.",
    'heb.mine.finalHint': "Votre lit est attribué : remise des clés à partir du {date}.",
    'heb.mine.withdraw': "Me désister",
    'heb.mine.withdrawText': "Votre demande se ferme et le lit éventuel revient à la campagne.",
    'heb.mine.reason': "Motif",
    'heb.mine.done.accept': "Lit accepté : déposez votre reçu une fois payé.",
    'heb.mine.done.decline': "Lit refusé.",
    'heb.mine.done.withdraw': "Désistement enregistré.",
    'heb.mine.myRoom': "Ma chambre",
    'heb.mine.key': "Clé {key}",
    'heb.mine.keySince': "remise le {at}",
    'heb.mine.payments': "Mes paiements",
    'heb.mine.upToDate': "À jour",
    'heb.mine.owing': "Reste {amount} à payer",
    'heb.mine.reports': "Mes signalements",
    'heb.mine.problem': "Signaler un problème",
    'heb.mine.problemWhat': "Ce qui ne va pas",
    'heb.mine.problemSubmit': "Envoyer",
    'heb.mine.problemSent': "Signalement envoyé : vous serez prévenu quand ce sera réparé.",
    'heb.mine.access': "Un agent peut entrer en mon absence",
    'heb.mine.nothing': "Aucune campagne ouverte",
    'heb.mine.nothingHint': "Vous serez prévenu à l'ouverture de la prochaine campagne d'hébergement.",
    'heb.mine.note': "Vous recevez une notification à chaque étape : lit proposé, rappel de paiement, convocation à la remise des clés, visite annoncée, réparation terminée.",
    'rel.modules.housing': "Internat et hébergement",
    'rel.modules.housingHint': "Campagnes, demandes, allocation des lits, paiements des frais, clés, visites et réparations",
    'journal.action.housing.settings_changed': "Réglages de l'internat modifiés",
    'journal.action.housing.estate_saved': "Parc ou tarif de l'internat enregistré",
    'journal.action.housing.campaign_saved': "Campagne d'hébergement enregistrée",
    'journal.action.housing.application_decided': "Demande d'hébergement décidée",
    'journal.action.housing.allocation_changed': "Allocation d'un lit modifiée",
    'journal.action.housing.payment_decided': "Paiement de l'internat décidé",
    'journal.action.housing.keys_changed': "Remise ou retour des clés",
    'journal.action.housing.upkeep_changed': "Visite ou réparation de l'internat",
    'journal.action.portal.rib_changed': "RIB d'un enseignant proposé ou vérifié",
    'journal.action.portal.hours_reported': "Écart d'heures signalé ou traité",
    'tch.tabs.rolls': "Appels",
    'tch.kpi.today': "Séances aujourd'hui",
    'tch.kpi.todayNote': "{count} appel(s) à faire",
    'tch.kpi.late': "Appels en retard",
    'tch.kpi.lateNote': "séances passées sans appel",
    'tch.kpi.marks': "Notes à saisir",
    'tch.kpi.marksNote': "évaluations passées non validées",
    'tch.kpi.hours': "Heures réalisées",
    'tch.kpi.hoursNote': "sur {objective} de l'objectif",
    'tch.home.hours': "Mes heures",
    'tch.home.hoursLink': "Heures et contrat",
    'tch.home.ofObjective': "réalisées sur {objective}",
    'tch.home.hoursNote': "Planifiées à venir : {planned} · à planifier : {toPlan} · ce mois : {month}",
    'tch.roll.take': "Faire l'appel",
    'tch.roll.done': "Appel fait",
    'tch.todo.roll_missing': "appel en retard",
    'tch.todo.marks_missing': "notes à saisir ou valider",
    'tch.todo.replacement': "remplacement",
    'tch.todo.cancelled': "séance annulée",
    'tch.todo.requests': "{count} demande(s) en cours",
    'tch.rolls.title': "Appels",
    'tch.rolls.sub': "Mes séances des soixante derniers jours et l'état de leur appel",
    'tch.rolls.todo': "À faire aujourd'hui",
    'tch.rolls.todoNote': "séances du jour",
    'tch.rolls.late': "En retard",
    'tch.rolls.lateNote': "à faire au plus vite",
    'tch.rolls.done': "Faits",
    'tch.rolls.doneNote': "sur soixante jours",
    'tch.rolls.pending': "À faire",
    'tch.rolls.doneTab': "Faits",
    'tch.rolls.all': "Tous",
    'tch.rolls.none': "Aucune séance ici.",
    'tch.rolls.session': "Séance",
    'tch.rolls.course': "Cours",
    'tch.rolls.classes': "Classes",
    'tch.rolls.state': "Appel",
    'tch.rolls.state.todo': "À faire",
    'tch.rolls.state.late': "En retard",
    'tch.rolls.state.done': "Fait",
    'tch.rolls.state.validated': "Validé par la scolarité",
    'tch.rolls.counts': "{absent} absent(s), {late} retard(s)",
    'tch.rolls.fix': "Corriger",
    'tch.rolls.locked': "verrouillé",
    'tch.rolls.hint': "Un appel se corrige jusqu'à sa validation par la scolarité.",
    'tch.classes.title': "Mes classes",
    'tch.classes.sub': "Les classes de mes enseignements de l'année",
    'tch.classes.write': "Écrire aux étudiants",
    'tch.classes.none': "Aucune classe dans vos séances ou évaluations de l'année.",
    'tch.classes.list': "Mes classes",
    'tch.classes.count': "{count} étudiant(s)",
    'tch.classes.empty': "Aucun étudiant dans cette classe.",
    'tch.classes.student': "Étudiant",
    'tch.classes.presence': "Présence",
    'tch.classes.unjustified': "Absences non justifiées",
    'tch.classes.unvalidated': "* évaluation pas encore validée.",
    'tch.classes.hint': "Vous voyez la présence de vos étudiants à vos séances et leurs notes à vos évaluations, jamais leurs coordonnées.",
    'tch.hours.title': "Heures et contrat",
    'tch.hours.report': "Signaler un écart d'heures",
    'tch.hours.reported': "Écart d'heures envoyé à la planification.",
    'tch.hours.objective': "Objectif",
    'tch.hours.objectiveNote': "heures des objectifs du contrat",
    'tch.hours.planned': "Planifié",
    'tch.hours.plannedNote': "{hours} restent à planifier",
    'tch.hours.realized': "Réalisé",
    'tch.hours.realizedNote': "{percent} % de l'objectif",
    'tch.hours.month': "Ce mois",
    'tch.hours.monthNote': "{count} séance(s)",
    'tch.hours.byMonth': "Mois par mois",
    'tch.hours.legendPlanned': "planifié",
    'tch.hours.legendRealized': "réalisé",
    'tch.hours.bySubject': "Par matière",
    'tch.hours.objectiveCol': "Objectif",
    'tch.hours.realizedCol': "Réalisé",
    'tch.hours.contracts': "Mes contrats",
    'tch.hours.noContract': "Aucun contrat.",
    'tch.hours.period': "du {from} au {to}",
    'tch.hours.signed': "signé le {date}",
    'tch.hours.rate': "Taux horaire : {rate}",
    'tch.hours.download': "Télécharger le contrat",
    'tch.hours.reports': "Mes signalements",
    'tch.hours.noReport': "Aucun écart signalé.",
    'tch.hours.hint': "Une heure est réalisée quand l'appel de la séance est fait.",
    'tch.hours.reportSub': "La planification corrige la séance ou l'appel, et vous répond.",
    'tch.hours.send': "Envoyer",
    'tch.hours.reportMonth': "Le mois concerné",
    'tch.hours.reportExpected': "Heures que vous attendez",
    'tch.hours.reportNote': "Ce qui manque",
    'tch.hours.reportHint': "Les heures comptées du mois sont relevées à l'envoi.",
    'tch.report.state.open': "Envoyé",
    'tch.report.state.corrected': "Corrigé",
    'tch.report.state.dismissed': "Écarté",
    'tch.pay.title': "Ma paie",
    'tch.pay.paid': "Payé cette année",
    'tch.pay.paidNote': "{count} mois payé(s), net",
    'tch.pay.hours': "Heures de l'année",
    'tch.pay.hoursNote': "comptées pour la paie",
    'tch.pay.rib': "Virement",
    'tch.pay.ribNote': "RIB vérifié",
    'tch.pay.noRib': "aucun RIB vérifié",
    'tch.pay.byMonth': "Mois par mois",
    'tch.pay.none': "Aucun mois de paie.",
    'tch.pay.month': "Mois",
    'tch.pay.hoursCol': "Heures",
    'tch.pay.gross': "Brut",
    'tch.pay.net': "Net",
    'tch.pay.state': "État",
    'tch.pay.stateOf.paid': "Payé",
    'tch.pay.stateOf.validated': "Validé",
    'tch.pay.stateOf.computed': "En cours",
    'tch.pay.stateOf.blocked': "En attente d'un appel ou d'un contrat",
    'tch.pay.stateOf.nothing': "Rien ce mois",
    'tch.pay.extra': "dont {hours} au-delà du forfait",
    'tch.pay.hint': "Le mois en cours est un calcul ; l'état devient définitif une fois validé par la paie.",
    'tch.profile.name': "Nom",
    'tch.profile.number': "Matricule",
    'tch.profile.department': "Département",
    'tch.profile.fields': "Champs disciplinaires",
    'tch.profile.since': "Enseigne depuis",
    'tch.profile.identitySub': "Une erreur ? Signalez-la aux ressources humaines.",
    'tch.profile.diplomas': "Diplômes",
    'tch.profile.locked': "Elles se modifient aux ressources humaines",
    'tch.profile.rib': "RIB pour la paie",
    'tch.profile.ribSub': "Un nouveau RIB vaut une fois vérifié par les ressources humaines",
    'tch.profile.ribChange': "Changer de RIB",
    'tch.profile.ribCurrent': "RIB en vigueur",
    'tch.profile.ribVerified': "Vérifié",
    'tch.profile.ribPending': "Nouveau RIB {rib} envoyé le {date}, en vérification.",
    'tch.profile.ribRefused': "Votre dernier RIB a été refusé : {reason}",
    'tch.profile.ribSent': "RIB envoyé : les ressources humaines le vérifient.",
    'tch.profile.ribDialogSub': "Il remplace l'ancien après vérification",
    'tch.profile.ribNew': "Nouveau RIB",
    'tch.profile.ribHint': "16 à 34 chiffres ou lettres ; les espaces sont ignorés.",
    'tch.profile.documents': "Mes documents",
    'tch.profile.contract': "Contrat {year}",
    'tch.profile.unsigned': "pas encore signé",
    'tch.bank.sub': "Proposé par l'enseignant depuis son portail",
    'tch.bank.pending': "RIB proposé",
    'tch.bank.verify': "Vérifier",
    'tch.bank.refuse': "Refuser",
    'tch.bank.reason': "Motif",
    'tch.reports.title': "Écarts d'heures signalés ({count})",
    'tch.reports.sub': "Corrigez la séance ou l'appel, puis répondez à l'enseignant",
    'tch.reports.teacher': "Enseignant",
    'tch.reports.counted': "Comptées",
    'tch.reports.expected': "Attendues",
    'tch.reports.note': "Ce qui manque",
    'tch.reports.handle': "Traiter",
    'tch.reports.decision': "Décision",
    'tch.reports.answer': "Réponse à l'enseignant",
    'tch.reports.answerHint': "Obligatoire pour écarter le signalement.",
    'tch.settings.students': "Étudiants",
    'tch.settings.teachers': "Enseignants",
    'tch.settings.rubrics': "Rubriques des enseignants",
    'tch.settings.rubricsSub': "L'emploi du temps, les appels et les évaluations restent toujours visibles",
    'tch.settings.welcome': "Message d'accueil des enseignants",
    'tch.settings.flag.classes': "Mes classes",
    'tch.settings.flag.photos': "Photos des étudiants",
    'tch.settings.flag.hours': "Heures et contrat",
    'tch.settings.flag.pay': "Ma paie",
    'tch.settings.flag.profile': "Coordonnées et RIB modifiables",
    'tch.settings.rule.classes': "présence à SES séances et notes à SES évaluations seulement",
    'tch.settings.rule.photos': "dans « Mes classes »",
    'tch.settings.rule.hours': "objectif, planifié, réalisé, contrats ; écart d'heures signalé",
    'tch.settings.rule.pay': "heures, brut, net, état PDF ; éteinte, la rubrique disparaît",
    'tch.settings.rule.profile': "le RIB est vérifié par les ressources humaines ; journalisé",
    'journal.action.portal.settings_changed': "Réglages du portail étudiant modifiés",
    'journal.action.portal.profile_changed': "Coordonnées modifiées depuis le portail",
    'journal.action.portal.calendar_linked': "Lien d'agenda de l'emploi du temps créé",
    'port.tabs.home': "Accueil",
    'port.tabs.timetable': "Agenda",
    'port.tabs.marks': "Notes",
    'port.tabs.messages': "Messages",
    'port.tabs.more': "Plus",
    'port.home.hello': "Bonjour {name}",
    'port.home.today': "Aujourd'hui",
    'port.home.timetable': "Emploi du temps",
    'port.home.noSession': "Aucune séance aujourd'hui.",
    'port.home.marks': "Dernières notes",
    'port.home.allMarks': "Toutes mes notes",
    'port.home.todo': "À faire",
    'port.home.nothingToDo': "Rien à faire pour le moment.",
    'port.home.messages': "Notifications",
    'port.home.allMessages': "Toutes les notifications",
    'port.home.noMessage': "Aucune notification.",
    'port.home.links': "Liens utiles",
    'port.kpi.average': "Moyenne provisoire",
    'port.kpi.averageNote': "{group} · {count} matière(s) sur {total}",
    'port.kpi.attendance': "Assiduité",
    'port.kpi.attendanceNote': "{count} absence(s) non justifiée(s)",
    'port.kpi.due': "Reste à payer",
    'port.kpi.dueNext': "prochaine échéance le {date}",
    'port.kpi.dueNone': "aucune échéance à venir",
    'port.kpi.credits': "Crédits acquis",
    'port.todo.justify': "{count} absence(s) à justifier",
    'port.todo.between': "du {from} au {to}",
    'port.todo.justifyAction': "Justifier",
    'port.todo.installment': "Échéance du {date} : {amount}",
    'port.todo.late': "En retard depuis le {date} : {amount}",
    'port.todo.see': "Voir",
    'port.todo.offer': "Un lit vous est proposé",
    'port.todo.offerNote': "{bed}, {campus} · à accepter avant le {date}",
    'port.todo.answer': "Répondre",
    'port.todo.housingPay': "Frais d'hébergement : {amount}",
    'port.todo.housingPayNote': "{bed} · à payer avant le {date}",
    'port.todo.deposit': "Déposer le reçu",
    'port.timetable.title': "Emploi du temps",
    'port.timetable.range': "Du {from} au {to}",
    'port.timetable.previous': "Période précédente",
    'port.timetable.next': "Période suivante",
    'port.timetable.today': "Aujourd'hui",
    'port.timetable.addToCalendar': "Ajouter à mon agenda",
    'port.timetable.view': "Affichage",
    'port.timetable.mode.week': "Semaine",
    'port.timetable.mode.day': "Jour",
    'port.timetable.mode.list': "Liste",
    'port.timetable.hint': "Les séances annulées ou reportées restent visibles, avec leur nouvelle date.",
    'port.timetable.allDay': "Toute la journée",
    'port.timetable.cancelled': "Annulée",
    'port.timetable.exam': "Évaluation",
    'port.timetable.status': "État",
    'port.timetable.movedFrom': "Prévue initialement le",
    'port.timetable.rooms': "Salle",
    'port.timetable.teachers': "Enseignant",
    'port.timetable.type': "Type",
    'port.calendar.title': "Ajouter à mon agenda",
    'port.calendar.sub': "Google Agenda, Outlook, Calendrier Apple",
    'port.calendar.explain': "Un lien personnel suit votre emploi du temps : collez-le dans votre agenda (« Ajouter un agenda par URL »). Il se met à jour tout seul, de la semaine passée aux huit semaines à venir. Gardez-le pour vous.",
    'port.calendar.existing': "Un lien a été créé le {date}. En créer un nouveau désactive l'ancien.",
    'port.calendar.create': "Créer mon lien",
    'port.calendar.renew': "Créer un nouveau lien",
    'port.calendar.link': "Lien de l'agenda",
    'port.calendar.copy': "Copier",
    'port.calendar.copied': "Copié",
    'port.calendar.once': "Ce lien ne s'affiche qu'une fois : copiez-le maintenant.",
    'port.marks.title': "Notes et résultats",
    'port.marks.tabMarks': "Mes notes",
    'port.marks.tabResults': "Mes résultats",
    'port.marks.groups': "Semestres",
    'port.marks.year': "Année",
    'port.marks.none': "Aucune note validée pour le moment.",
    'port.marks.subject': "Matière",
    'port.marks.coefficient': "Coef.",
    'port.marks.assessments': "Évaluations",
    'port.marks.average': "Moyenne",
    'port.marks.groupAverage': "Moyenne provisoire · {group}",
    'port.marks.retake': "rattrapage",
    'port.marks.abs': "ABS",
    'port.marks.hint': "Une note paraît ici quand l'enseignant a validé l'évaluation. Une erreur ? Signalez-la à l'enseignant ou faites une demande.",
    'port.marks.hintAverage': "Une note paraît ici quand l'enseignant a validé l'évaluation. La moyenne est provisoire : le résultat qui compte est celui de la délibération. Une erreur ? Signalez-la à l'enseignant ou faites une demande.",
    'port.results.none': "Aucune délibération close pour le moment.",
    'port.results.list': "Mes délibérations",
    'port.results.deliberation': "Délibération",
    'port.results.average': "Moyenne",
    'port.results.rank': "Rang",
    'port.results.credits': "Crédits",
    'port.results.decision': "Décision",
    'port.results.closedOn': "close le {date}",
    'port.results.transcript': "Relevé",
    'port.results.detail': "Le détail par unité d'enseignement",
    'port.results.element': "Unité",
    'port.results.status': "Résultat",
    'port.attendance.title': "Assiduité",
    'port.attendance.none': "Aucun appel enregistré pour cette année.",
    'port.attendance.rate': "Taux de présence",
    'port.attendance.absences': "Absences",
    'port.attendance.absencesNote': "dont {count} à justifier",
    'port.attendance.lates': "Retards",
    'port.attendance.latesNote': "{minutes} minutes au total",
    'port.attendance.justified': "Justifiées",
    'port.attendance.justifiedNote': "absences couvertes par un justificatif",
    'port.attendance.toJustify': "{count} absence(s) attendent un justificatif.",
    'port.attendance.justifyAll': "Justifier ces absences",
    'port.attendance.atDesk': "Remettez votre justificatif à la scolarité.",
    'port.attendance.records': "Absences et retards",
    'port.attendance.noRecord': "Aucune absence ni aucun retard.",
    'port.attendance.date': "Date",
    'port.attendance.session': "Séance",
    'port.attendance.kind': "Type",
    'port.attendance.justification': "Justification",
    'port.attendance.absent': "Absence",
    'port.attendance.late': "Retard {minutes} min",
    'port.attendance.justify': "Justifier",
    'port.attendance.state.none': "-",
    'port.attendance.state.justified': "Justifiée",
    'port.attendance.state.pending': "Demande en cours",
    'port.attendance.state.to_justify': "À justifier",
    'port.attendance.exams': "Absences aux évaluations",
    'port.attendance.bySubject': "Par matière",
    'port.attendance.absentHours': "Heures d'absence",
    'port.attendance.unjustifiedRate': "Non justifié",
    'port.documents.title': "Documents",
    'port.documents.sub': "Les documents que l'école vous a délivrés",
    'port.documents.none': "Aucun document publié pour le moment.",
    'port.documents.published': "publié le {date}",
    'port.documents.download': "Télécharger",
    'port.documents.verify': "Vérifier",
    'port.documents.qr': "Chaque document porte un code QR : la personne à qui vous le remettez le vérifie sur le site de l'école, sans compte.",
    'port.documents.ask': "Demander un document",
    'port.documents.askSub': "Par le module Demandes",
    'port.documents.askButton': "Demander",
    'port.finance.title': "Finances",
    'port.finance.sub': "Mon compte · {year}",
    'port.finance.billed': "Facturé cette année",
    'port.finance.billedNote': "avant réductions et avoirs",
    'port.finance.paid': "Payé",
    'port.finance.paidNote': "{count} reçu(s)",
    'port.finance.due': "Reste à payer",
    'port.finance.dueNote': "toutes années confondues",
    'port.finance.late': "En retard",
    'port.finance.lateNote': "échéances dépassées",
    'port.finance.lateNone': "aucune échéance dépassée",
    'port.finance.schedule': "Échéancier",
    'port.finance.noSchedule': "Aucune échéance.",
    'port.finance.dueOn': "Échéance",
    'port.finance.label': "Libellé",
    'port.finance.amount': "Montant",
    'port.finance.paidColumn': "Payé",
    'port.finance.state': "État",
    'port.finance.installment': "versement {position} sur {count}",
    'port.finance.documents': "Factures et reçus",
    'port.finance.noDocument': "Aucune facture.",
    'port.finance.invoice': "Facture · {label}",
    'port.finance.receipt': "Reçu · {method}",
    'port.finance.howToPay': "Comment payer",
    'port.finance.housing': "Déposer un reçu d'hébergement",
    'port.finance.housingSub': "Après un virement ou un versement en banque ; la caisse le valide.",
    'port.finance.housingDue': "reste {amount} · avant le {date}",
    'port.finance.receiptPending': "Reçu de {amount} envoyé le {date}, en vérification",
    'port.finance.deposit': "Déposer un reçu",
    'port.profile.title': "Mon dossier",
    'port.profile.sub': "Ce que l'école sait de vous",
    'port.profile.saved': "Coordonnées enregistrées.",
    'port.profile.identity': "Identité",
    'port.profile.identitySub': "Une erreur ? Signalez-la à la scolarité.",
    'port.profile.lastName': "Nom",
    'port.profile.firstNames': "Prénoms",
    'port.profile.birthDate': "Date de naissance",
    'port.profile.number': "Matricule",
    'port.profile.nationalId': "CIN",
    'port.profile.massar': "Code Massar",
    'port.profile.training': "Formation",
    'port.profile.since': "Inscrit depuis",
    'port.profile.coordinates': "Mes coordonnées",
    'port.profile.coordinatesSub': "Vous les modifiez vous-même",
    'port.profile.coordinatesLocked': "Elles se modifient à la scolarité",
    'port.profile.edit': "Modifier",
    'port.profile.phones': "Téléphones",
    'port.profile.emails': "Courriels",
    'port.profile.addresses': "Adresses",
    'port.profile.contacts': "Parents et contacts",
    'port.profile.contactsSub': "Tenus par la scolarité",
    'port.profile.account': "Mon compte",
    'port.profile.security': "Mot de passe, double authentification, langue, photo",
    'port.profile.securityHint': "Votre photo de compte se change dans « Mon profil ».",
    'port.settings.title': "Portail étudiant",
    'port.settings.sub': "Ce que voient les étudiants dans « Mon espace »",
    'port.settings.saved': "Réglages enregistrés.",
    'port.settings.off': "Modules éteints, leurs rubriques sont masquées : {modules}.",
    'port.settings.module.finance': "Finance",
    'port.settings.module.requests': "Demandes",
    'port.settings.module.stages': "Stages",
    'port.settings.module.housing': "Internat",
    'port.settings.module.communication': "Communication",
    'port.settings.rubrics': "Rubriques",
    'port.settings.rubricsSub': "Une rubrique éteinte disparaît du menu des étudiants",
    'port.settings.moduleOff': "Module éteint",
    'port.settings.flag.timetable': "Emploi du temps",
    'port.settings.flag.marks': "Notes des évaluations",
    'port.settings.flag.average': "Moyenne provisoire",
    'port.settings.flag.results': "Résultats des délibérations",
    'port.settings.flag.rank': "Rang dans la promotion",
    'port.settings.flag.attendance': "Assiduité",
    'port.settings.flag.documents': "Documents",
    'port.settings.flag.finance': "Finances",
    'port.settings.flag.profile': "Coordonnées modifiables",
    'port.settings.rule.timetable': "les séances de ses classes et groupes ; lien iCal",
    'port.settings.rule.marks': "seules les évaluations validées par l'enseignant",
    'port.settings.rule.average': "calculée sur les notes validées, marquée « provisoire »",
    'port.settings.rule.results': "après la clôture de la délibération",
    'port.settings.rule.rank': "avec les résultats de la délibération close",
    'port.settings.rule.attendance': "appels faits ; justification par le module Demandes",
    'port.settings.rule.documents': "documents publiés par la scolarité",
    'port.settings.rule.finance': "échéancier, factures, reçus",
    'port.settings.rule.profile': "téléphone, courriel, adresse ; chaque modification est journalisée",
    'port.settings.home': "Accueil",
    'port.settings.welcome': "Message d'accueil",
    'port.settings.links': "Liens utiles",
    'port.settings.linkLabel': "Libellé",
    'port.settings.linkUrl': "Adresse",
    'port.settings.addLink': "Ajouter un lien",
    'port.settings.payment': "Comment payer",
    'port.settings.paymentSub': "Affiché dans « Finances » : virement, guichet, horaires",
    'port.settings.paymentInfo': "Modalités de paiement",
    'port.settings.note': "Le portail n'est pas un module à part : il rassemble pour l'étudiant ce que chaque module lui montre déjà, et son menu remplace, pour lui, les entrées dispersées dans chaque module.",
    'journal.filter.group.housing': "Internat",
    'trace.table.housing_settings': "Réglages de l'internat",
    'trace.table.housing_campus': "Campus de l'internat",
    'trace.table.housing_room_type': "Type de chambre",
    'trace.table.housing_room': "Chambre",
    'trace.table.housing_bed': "Lit",
    'trace.table.housing_price': "Tarif de l'internat",
    'trace.table.housing_campaign': "Campagne d'hébergement",
    'trace.table.housing_campaign_place': "Lieu d'une campagne",
    'trace.table.housing_quota': "Quota d'une campagne",
    'trace.table.housing_required_document': "Pièce exigée",
    'trace.table.housing_application': "Demande d'hébergement",
    'trace.table.housing_allocation': "Allocation d'un lit",
    'trace.table.housing_payment': "Paiement de l'internat",
    'trace.table.housing_checklist': "Grille de contrôle",
    'trace.table.housing_inspection': "Visite de contrôle",
    'trace.table.housing_inspection_room': "Chambre d'une visite",
    'trace.table.housing_provider': "Prestataire",
    'trace.table.housing_repair_category': "Catégorie de réparation",
    'trace.table.housing_repair': "Réparation",
    'trace.table.housing_file': "Fichier de l'internat",
    'trace.table.housing_event': "Historique d'une demande d'hébergement",
    'violation.heb.accept_late': "Le délai d'acceptation est passé.",
    'violation.heb.application_locked': "Cette demande ne se modifie plus.",
    'violation.heb.bed_gender': "Cette chambre n'est pas ouverte à ce genre.",
    'violation.heb.bed_out': "Ce lit est hors service.",
    'violation.heb.bed_taken': "Ce lit est déjà alloué.",
    'violation.heb.bed_used': "Ce lit a déjà servi : mettez-le hors service.",
    'violation.heb.campaign_closed': "Une campagne clôturée ne se modifie plus.",
    'violation.heb.campaign_not_open': "Le dépôt de cette campagne est fermé.",
    'violation.heb.campus_locked': "Une chambre ne change pas de campus.",
    'violation.heb.document_required': "Joignez cette pièce.",
    'violation.heb.inspection_locked': "Une visite programmée ne se modifie pas : programmez-en une autre.",
    'violation.heb.no_beds': "Une chambre a au moins un lit.",
    'violation.heb.no_enrolment': "Aucune inscription active pour l'année de la campagne.",
    'violation.heb.no_places': "Ouvrez au moins un lieu (campus et type de chambre) avant le dépôt.",
    'violation.heb.no_price': "Aucun tarif ne s'applique à ce lit pour cette demande.",
    'violation.heb.no_rooms': "Aucune chambre à ce lieu.",
    'violation.heb.not_eligible': "Seule une demande recevable, déposée ou en attente reçoit un lit.",
    'violation.heb.not_final': "Les clés se remettent après l'allocation finale (frais payés).",
    'violation.heb.not_for_level': "Cette campagne n'est pas ouverte à votre niveau.",
    'violation.heb.not_housed': "Seul un occupant sort.",
    'violation.heb.not_provisional': "Ce lit n'attend pas de réponse.",
    'violation.heb.not_releasable': "Cette allocation ne se libère plus.",
    'violation.heb.nothing_to_pay': "Aucun paiement n'est attendu pour ce lit.",
    'violation.heb.payment_decided': "Ce reçu est déjà traité.",
    'violation.heb.price_duplicate': "Une règle porte déjà exactement ces critères.",
    'violation.heb.receipt_required': "Joignez le reçu.",
    'violation.heb.retained_over': "La retenue dépasse la caution.",
    'violation.heb.room_used': "Cette chambre a déjà été allouée : mettez-la hors service.",
    'violation.heb.rules_required': "Acceptez le règlement de l'internat.",
    'violation.heb.too_many_files': "Trop de fichiers.",
    'heb.export.label': "Exporter",
    'heb.export.busy': "Export…",
    'heb.export.simulation': "Exporter la simulation",
    'heb.invoice.download': "Télécharger la facture",
    'heb.selectAll': "Tout sélectionner",
    'heb.selectRow': "Sélectionner {name}",
    'heb.applications.treat': "Traiter la sélection",
    'heb.applications.selected': "{count} demande(s) sélectionnée(s)",
    'heb.applications.decision': "Décision",
    'heb.applications.treatHint': "Les demandes déjà allouées ou closes sont passées.",
    'heb.applications.treated': "{count} demande(s) traitée(s).",
    'heb.applications.treatedSkipped': "{count} demande(s) traitée(s), {skipped} passée(s) (déjà allouées ou closes).",
    'heb.campaignForm.next': "Suivant",
    'heb.campaignForm.previous': "Précédent",
    'heb.offline.waiting': "en attente d'envoi",
    'heb.offline.photo': "sur le téléphone",
    'heb.offline.queued': "Pas de réseau : le constat est gardé sur le téléphone et partira dès le retour de la connexion.",
    'heb.offline.count': "{count} constat(s) en attente d'envoi",
    'heb.offline.retry': "Renvoyer maintenant",
    'heb.offline.sent': "{count} constat(s) en attente envoyé(s).",
    'heb.offline.failed': "{count} constat(s) refusé(s) par le serveur, à refaire : {rooms}.",
    'heb.payments.remind': "Relancer",
    'heb.payments.remindCount': "Relancer ({count})",
    'heb.payments.remindOne': "Relancer",
    'heb.payments.reminded': "{count} étudiant(s) relancé(s).",
    'heb.room.reportBed': "Signaler",
    'heb.repair.roomAt': "Chambre {room} · {campus}",
    'heb.repairs.assignee': "Intervenant",
    'heb.repairs.allAssignees': "Tous les intervenants",
    'heb.repairs.staff': "Agents de l'école",
    'heb.event.reminded': "Rappel de paiement",
    'violation.heb.bad_selection': "Choisissez de 1 à 500 demandes.",
    'rel.modules.skills': "Suivi des compétences",
    'rel.modules.skillsHint': "Référentiels, activités et dépôts de preuves, grille rapide, niveaux finaux, profil des étudiants, bilans",
    'journal.action.skills.settings_changed': "Réglages des compétences modifiés",
    'journal.action.skills.framework_saved': "Référentiel de compétences modifié",
    'journal.action.skills.activity_saved': "Activité de compétences enregistrée",
    'journal.action.skills.ratings_published': "Évaluations de compétences publiées",
    'journal.action.skills.final_published': "Niveau final de compétence validé ou publié",
    'journal.action.skills.deposit_submitted': "Preuve de compétence déposée",
    'violation.skl.scale_size': "Une échelle compte de 2 à 8 niveaux.",
    'violation.skl.conversion_size': "Une note par niveau de l'échelle.",
    'violation.skl.conversion_range': "Une note de 0 à 20.",
    'violation.skl.conversion_order': "Les notes montent avec les niveaux.",
    'violation.skl.scale_locked': "Ce référentiel est déjà évalué : il garde son nombre de niveaux.",
    'violation.skl.marks_off': "Le lien avec les notes est éteint dans les réglages.",
    'violation.skl.teacher_scope': "Un enseignant rattache son référentiel à l'un de ses cours.",
    'violation.skl.unknown_code': "Ce code n'existe pas dans le catalogue de l'année.",
    'violation.skl.empty_framework': "Un référentiel sans compétence ne se publie pas.",
    'violation.skl.framework_used': "Seul un brouillon jamais servi se supprime ; archivez-le.",
    'violation.skl.node_used': "Déjà évalué : cet élément ne se supprime plus.",
    'violation.skl.not_yours': "Cette classe ou cette matière n'est pas l'une des vôtres.",
    'violation.skl.activity_locked': "Déjà évaluée : la classe et la matière ne changent plus.",
    'violation.skl.activities_off': "Seules les évaluations notées se relient ici.",
    'violation.skl.no_skill': "Ciblez au moins une compétence.",
    'violation.skl.framework_no_marks': "Un référentiel ciblé ne convertit pas ses niveaux en notes.",
    'violation.skl.deposits_off': "Les dépôts sont éteints dans les réglages.",
    'violation.skl.activity_used': "Déjà évaluée ou déposée : l'activité ne se supprime plus.",
    'violation.skl.no_assessment': "L'évaluation reliée est introuvable.",
    'violation.skl.assessment_validated': "L'évaluation reliée est validée : ses notes ne changent plus.",
    'violation.skl.file_empty': "Le fichier est vide.",
    'violation.skl.file_too_big': "Le fichier dépasse 5 Mo.",
    'violation.skl.file_format': "Ce format n'est pas accepté pour ce dépôt.",
    'violation.skl.deposit_closed': "La date limite du dépôt est passée.",
    'skl.scope.all': "Tous les programmes",
    'skl.scope.domain': "Filière",
    'skl.scope.training': "Programme",
    'skl.scope.subject': "Cours",
    'skl.state.draft': "Brouillon",
    'skl.state.published': "Publié",
    'skl.state.archived': "Archivé",
    'skl.rule.last': "Dernier niveau obtenu",
    'skl.rule.frequent': "Niveau le plus fréquent",
    'skl.rule.average': "Moyenne arrondie",
    'skl.rule.manual': "Saisie manuelle",
    'skl.ruleHint.last': "le niveau de la plus récente évaluation",
    'skl.ruleHint.frequent': "à égalité, le plus récent",
    'skl.ruleHint.average': "le premier niveau vaut 1, arrondie au plus proche",
    'skl.ruleHint.manual': "l'enseignant fixe le niveau final lui-même",
    'skl.kind.homework': "Devoir",
    'skl.kind.practical': "TP",
    'skl.kind.project': "Projet",
    'skl.kind.assessment': "Évaluation notée",
    'skl.format.pdf': "PDF",
    'skl.format.docx': "Word (.docx)",
    'skl.format.image': "Images (.jpg, .png)",
    'skl.format.xlsx': "Tableurs (.xlsx)",
    'skl.format.pptx': "Présentations (.pptx)",
    'skl.preset.four': "Quatre niveaux",
    'skl.preset.colors': "Trois couleurs",
    'skl.preset.cefr': "CECRL (A1 à C2)",
    'skl.tone.danger': "Rouge",
    'skl.tone.warn': "Orange",
    'skl.tone.info': "Bleu",
    'skl.tone.ok': "Vert",
    'skl.tone.accent': "Indigo",
    'skl.tone.violet': "Violet",
    'skl.tone.teal': "Turquoise",
    'skl.tone.muted': "Gris",
    'skl.node.domain': "Domaine de compétences",
    'skl.node.skill': "Compétence clé",
    'skl.node.indicator': "Indicateur",
    'skl.node.add.domain': "Ajouter un domaine",
    'skl.node.add.skill': "Ajouter une compétence",
    'skl.node.add.indicator': "Ajouter un indicateur",
    'skl.node.label.domain': "Domaine",
    'skl.node.label.skill': "Compétence clé",
    'skl.node.label.indicator': "Indicateur ou critère",
    'skl.node.addConfirm': "Ajouter",
    'skl.node.code': "Code",
    'skl.node.description': "Description",
    'skl.node.up': "Monter",
    'skl.node.down': "Descendre",
    'skl.node.used': "Déjà évalué : il ne se supprime plus",
    'skl.level.none': "Pas encore évalué",
    'skl.frameworks.title': "Référentiels de compétences",
    'skl.frameworks.sub': "Rattachés à un programme, une filière ou un cours",
    'skl.frameworks.new': "Nouveau référentiel",
    'skl.frameworks.kpiPublished': "Référentiels publiés",
    'skl.frameworks.kpiPublishedNote': "{drafts} brouillon(s), {archived} archivé(s)",
    'skl.frameworks.kpiSkills': "Compétences",
    'skl.frameworks.kpiSkillsNote': "dans les référentiels publiés",
    'skl.frameworks.kpiActivities': "Activités liées",
    'skl.frameworks.kpiActivitiesNote': "évaluations et activités",
    'skl.frameworks.kpiTotal': "Référentiels",
    'skl.frameworks.kpiTotalNote': "tous états confondus",
    'skl.frameworks.search': "Chercher un référentiel",
    'skl.frameworks.filter.live': "Publiés et brouillons",
    'skl.frameworks.filter.published': "Publiés",
    'skl.frameworks.filter.draft': "Brouillons",
    'skl.frameworks.filter.archived': "Archivés",
    'skl.frameworks.filter.all': "Tous",
    'skl.frameworks.none': "Aucun référentiel ici.",
    'skl.frameworks.name': "Référentiel",
    'skl.frameworks.attached': "Rattaché à",
    'skl.frameworks.content': "Contenu",
    'skl.frameworks.contentValue': "{domains} domaine(s) · {skills} compétence(s) · {indicators} indicateur(s)",
    'skl.frameworks.scale': "Échelle",
    'skl.frameworks.rule': "Niveau final",
    'skl.frameworks.state': "État",
    'skl.frameworks.author': "Créé par",
    'skl.frameworks.byTeacher': "pour son cours",
    'skl.frameworks.counts': "compte dans les notes",
    'skl.frameworks.note': "La pédagogie crée les référentiels des programmes et des filières. Un enseignant peut en créer un pour son propre cours : la pédagogie le voit et peut le reprendre. Un référentiel déjà évalué ne se supprime pas, il s'archive.",
    'skl.framework.newTitle': "Nouveau référentiel",
    'skl.framework.newSub': "Nommez-le, rattachez-le, choisissez son échelle ; l'arbre se construit ensuite.",
    'skl.framework.publish': "Publier",
    'skl.framework.published': "Référentiel publié : il sert désormais aux activités.",
    'skl.framework.duplicate': "Dupliquer",
    'skl.framework.duplicated': "Copie créée en brouillon.",
    'skl.framework.takeOver': "Reprendre",
    'skl.framework.takenOver': "Référentiel repris par la pédagogie.",
    'skl.framework.archive': "Archiver",
    'skl.framework.archived': "Référentiel archivé : ses niveaux restent.",
    'skl.framework.deleted': "Brouillon supprimé.",
    'skl.framework.tree': "Arborescence",
    'skl.framework.treeSub': "Domaine, compétence clé, indicateurs",
    'skl.framework.emptyTree': "Aucun domaine encore.",
    'skl.framework.skillsCount': "{count} compétence(s)",
    'skl.framework.indicatorsCount': "{count} indicateur(s)",
    'skl.framework.saved': "Référentiel enregistré.",
    'skl.framework.identity': "Le référentiel",
    'skl.framework.name': "Nom",
    'skl.framework.description': "Description",
    'skl.framework.scope': "Rattaché à",
    'skl.framework.domain': "Filière",
    'skl.framework.training': "Programme",
    'skl.framework.subject': "Cours",
    'skl.framework.choose': "Choisir",
    'skl.framework.scale': "Échelle de maîtrise",
    'skl.framework.scaleSub': "De 2 à 8 niveaux, du plus faible au plus fort",
    'skl.framework.scaleLocked': "Déjà évalué : le nombre de niveaux ne change plus, leurs libellés oui",
    'skl.framework.stepCode': "Code du niveau",
    'skl.framework.stepLabel': "Libellé du niveau",
    'skl.framework.stepTone': "Couleur du niveau",
    'skl.framework.newStep': "Nouveau niveau",
    'skl.framework.addStep': "Ajouter un niveau",
    'skl.framework.rule': "Niveau final",
    'skl.framework.ruleNote': "Calculé ou non, l'enseignant valide le niveau final et peut le corriger avant de le publier.",
    'skl.framework.marks': "Lien avec les notes",
    'skl.framework.marksSub': "La note d'une activité est la moyenne de ses compétences converties ; elle suit le circuit habituel des notes",
    'skl.framework.marksOff': "Éteint dans les réglages du module",
    'skl.framework.marksLink': "Les niveaux donnent une note sur 20",
    'skl.framework.markOf': "Note de « {level} »",
    'skl.framework.conversionNote': "Sur 20, ramenée au barème de l'évaluation reliée. Les notes montent avec les niveaux.",
    'skl.framework.create': "Créer le référentiel",
    'skl.activities.title': "Activités de compétences",
    'skl.activities.titleMine': "Compétences",
    'skl.activities.sub': "Activités et évaluations liées à des compétences · {year}",
    'skl.activities.frameworks': "Référentiels",
    'skl.activities.report': "Bilans",
    'skl.activities.new': "Nouvelle activité",
    'skl.activities.kpiOpen': "Activités ouvertes",
    'skl.activities.kpiOpenNote': "pas encore publiées",
    'skl.activities.kpiDeposits': "Dépôts reçus",
    'skl.activities.kpiDepositsNote': "{count} manquent",
    'skl.activities.kpiRated': "Étudiants évalués",
    'skl.activities.kpiRatedNote': "toutes activités confondues",
    'skl.activities.kpiFinals': "Niveaux finaux à valider",
    'skl.activities.kpiFinalsNote': "de mes étudiants",
    'skl.activities.class': "Classe",
    'skl.activities.allClasses': "Toutes les classes",
    'skl.activities.list': "Activités",
    'skl.activities.none': "Aucune activité encore.",
    'skl.activities.finals': "Niveaux finaux à valider",
    'skl.activities.finalsSub': "Évalués dans mes activités, sans niveau final",
    'skl.activities.finalsNone': "Rien à valider.",
    'skl.activities.validate': "Valider",
    'skl.activities.noteMine': "Vous ne voyez que vos activités et vos classes. Un dépôt arrive par une notification ; ouvrir une activité ouvre la grille de la classe.",
    'skl.activities.note': "Les activités sont créées par les enseignants ; la pédagogie les consulte et valide les niveaux finaux au besoin.",
    'skl.activities.activity': "Activité",
    'skl.activities.teacher': "Enseignant",
    'skl.activities.target': "Cible",
    'skl.activities.deposits': "Dépôts",
    'skl.activities.state': "État",
    'skl.activities.counted': "compte dans les notes",
    'skl.activities.skills': "{count} compétence(s)",
    'skl.activities.depositCount': "{count} / {of} déposés",
    'skl.activities.noDeposit': "sans dépôt",
    'skl.activities.published': "Publiée",
    'skl.activities.inProgress': "En cours · {count} / {of}",
    'skl.activities.toRate': "À évaluer",
    'skl.dialog.title': "Nouvelle activité de compétences",
    'skl.dialog.edit': "Modifier l'activité",
    'skl.dialog.create': "Créer",
    'skl.dialog.noClass': "Aucune classe ni matière à votre nom cette année (séances ou évaluations).",
    'skl.dialog.classSubject': "Classe et matière",
    'skl.dialog.kind': "Type",
    'skl.dialog.assessment': "Évaluation notée à relier",
    'skl.dialog.target': "Évaluation qui reçoit la note",
    'skl.dialog.choose': "Choisir",
    'skl.dialog.validated': "validée",
    'skl.dialog.label': "Activité",
    'skl.dialog.labelHint': "Vide : le libellé de l'évaluation",
    'skl.dialog.heldOn': "Date",
    'skl.dialog.skills': "Compétences ciblées",
    'skl.dialog.search': "Chercher dans les référentiels de la classe",
    'skl.dialog.noFramework': "Aucun référentiel publié ne s'applique à cette classe et cette matière.",
    'skl.dialog.deposit': "Exiger un dépôt de fichier",
    'skl.dialog.formats': "Fichiers acceptés",
    'skl.dialog.due': "Date limite",
    'skl.dialog.depositNote': "5 Mo au plus par fichier. Les étudiants reçoivent une notification et voient la demande dans « Mes compétences ».",
    'skl.dialog.counted': "Compter dans les notes",
    'skl.dialog.countedNote': "À la publication, chaque étudiant reçoit la note convertie dans l'évaluation reliée ; elle se valide comme toute note.",
    'skl.dialog.linkNote': "Une évaluation notée (CC, examen) se relie de la même façon : choisissez le type « Évaluation notée ».",
    'skl.grid.ratedOf': "{count} évalué(s) sur {of}",
    'skl.grid.depositsOf': "{count} dépôt(s) sur {of}",
    'skl.grid.skill': "Compétence",
    'skl.grid.edit': "Modifier",
    'skl.grid.publish': "Publier les évaluations",
    'skl.grid.republish': "Republier",
    'skl.grid.published': "Évaluations publiées : les étudiants les voient.",
    'skl.grid.publishedMarks': "Évaluations publiées ; {count} note(s) écrite(s) dans l'évaluation reliée.",
    'skl.grid.draft': "Brouillon : les étudiants ne voient rien avant « Publier les évaluations ».",
    'skl.grid.countedNote': "Compte dans les notes : la note convertie part dans « {assessment} » à la publication.",
    'skl.grid.mode': "Affichage",
    'skl.grid.byIndicator': "Par indicateur",
    'skl.grid.skillOnly': "Compétence seule",
    'skl.grid.live': "Pendant le TP",
    'skl.grid.note': "Un clic suffit par case ; l'enregistrement est immédiat, un second clic retire le niveau. Le dépôt s'ouvre sans quitter la grille.",
    'skl.grid.student': "Étudiant",
    'skl.grid.deposit': "Dépôt",
    'skl.grid.skillLevel': "Compétence",
    'skl.grid.mark': "Note",
    'skl.grid.comment': "Commentaire",
    'skl.grid.noDeposit': "pas de dépôt",
    'skl.grid.addComment': "Ajouter",
    'skl.grid.liveOf': "Indicateur {index} sur {count} · {done} / {of} évalués",
    'skl.grid.previous': "Indicateur précédent",
    'skl.grid.next': "Indicateur suivant",
    'skl.grid.searchStudent': "Chercher un étudiant",
    'skl.grid.liveNote': "Enregistré à chaque toucher. Passez à l'indicateur suivant avec les flèches.",
    'skl.history.progress': "Progression",
    'skl.history.entries': "Historique des évaluations",
    'skl.history.noLevel': "Aucun niveau encore.",
    'skl.history.none': "Aucune évaluation encore.",
    'skl.history.date': "Date",
    'skl.history.activity': "Activité",
    'skl.history.level': "Niveau",
    'skl.history.comment': "Commentaire",
    'skl.history.proof': "Preuve",
    'skl.history.unpublished': "pas encore publiée",
    'skl.history.final': "Niveau final",
    'skl.history.validatedBy': "Validé par {name} le {at}, pas encore publié",
    'skl.history.publishedAt': "Publié le {at}",
    'skl.history.publishedBy': "Publié par {name} le {at}",
    'skl.history.manual': "Saisie manuelle : rien n'est proposé",
    'skl.history.proposed': "Proposé par la règle « {rule} »",
    'skl.history.validated': "Niveau final validé",
    'skl.history.appraisal': "Appréciation finale",
    'skl.history.publish': "Publier à l'étudiant",
    'skl.history.savedNotice': "Niveau final enregistré, pas encore publié.",
    'skl.history.publishedNotice': "Niveau final publié : l'étudiant en est notifié.",
    'skl.history.finalNote': "Publié, il paraît dans « Mes compétences » de l'étudiant.",
    'skl.report.title': "Bilans de compétences",
    'skl.report.sub': "Niveaux finaux d'une classe sur un référentiel",
    'skl.report.excel': "Excel",
    'skl.report.csv': "CSV",
    'skl.report.class': "Classe",
    'skl.report.framework': "Référentiel",
    'skl.report.none': "Aucun référentiel publié ne s'applique à cette classe.",
    'skl.report.note': "L'export reprend une ligne par étudiant et par compétence : niveau, date, enseignant, appréciation. Les compétences ne touchent aux notes que si le lien est allumé, par l'évaluation reliée.",
    'skl.report.finals': "Niveaux finaux",
    'skl.report.student': "Étudiant",
    'skl.report.acquired': "Acquises",
    'skl.report.unpublished': "Validé, pas encore publié",
    'skl.report.distribution': "Répartition des niveaux",
    'skl.settings.title': "Compétences",
    'skl.settings.sub': "Réglages du module",
    'skl.settings.saved': "Réglages enregistrés.",
    'skl.settings.working': "Fonctionnement",
    'skl.settings.enabled': "Module allumé",
    'skl.settings.enabledHint': "éteint, menus et écrans disparaissent ; rien n'est effacé",
    'skl.settings.flag.teachersCreate': "Les enseignants créent des référentiels",
    'skl.settings.flag.activities': "Activités sans note",
    'skl.settings.flag.deposits': "Dépôts de preuves",
    'skl.settings.flag.studentProfile': "Profil visible des étudiants",
    'skl.settings.flag.studentExport': "Bilan téléchargeable par l'étudiant",
    'skl.settings.flag.marksLink': "Lien avec les notes",
    'skl.settings.rule.teachersCreate': "pour leurs propres cours ; la pédagogie les voit et peut les reprendre",
    'skl.settings.rule.activities': "devoir, TP, projet propres au module, en plus des évaluations notées",
    'skl.settings.rule.deposits': "sur demande explicite de l'enseignant seulement",
    'skl.settings.rule.studentProfile': "dans « Mon espace », évaluations publiées seulement",
    'skl.settings.rule.studentExport': "son propre bilan en Excel",
    'skl.settings.rule.marksLink': "éteint : aucune note n'est touchée ; allumé, chaque référentiel et chaque activité choisit de compter ou non",
    'skl.settings.formats': "Fichiers acceptés",
    'skl.settings.formatsSub': "5 Mo au plus par fichier, vérifiés dans le navigateur et sur le serveur",
    'skl.settings.scale': "Échelle par défaut",
    'skl.settings.scaleSub': "Proposée à la création d'un référentiel",
    'skl.settings.note': "Les notifications (demande de dépôt, niveau final publié, dépôt reçu) partent seulement avec le module Communication.",
    'skl.mine.title': "Mes compétences",
    'skl.mine.export': "Mon bilan en Excel",
    'skl.mine.kpiEvaluated': "Compétences évaluées",
    'skl.mine.kpiEvaluatedNote': "sur {total} de mon programme",
    'skl.mine.kpiAcquired': "Acquises ou maîtrisées",
    'skl.mine.kpiAcquiredNote': "dont {count} maîtrisée(s)",
    'skl.mine.kpiProgress': "En cours",
    'skl.mine.kpiProgressNote': "à consolider",
    'skl.mine.kpiDeposits': "Dépôts à faire",
    'skl.mine.kpiDepositsNote': "demandés par mes enseignants",
    'skl.mine.profile': "Mon profil",
    'skl.mine.deposits': "Dépôts demandés",
    'skl.mine.noDeposit': "Aucun dépôt demandé.",
    'skl.mine.due': "avant le {at}",
    'skl.mine.deposited': "déposé : {name} le {at}",
    'skl.mine.closed': "Clos",
    'skl.mine.deposit': "Déposer",
    'skl.mine.replace': "Remplacer",
    'skl.mine.skills': "Mes compétences",
    'skl.mine.skillsSub': "Un clic ouvre l'historique et les commentaires",
    'skl.mine.none': "Aucun référentiel n'est encore publié pour votre programme.",
    'skl.mine.notYet': "pas encore évaluée",
    'skl.mine.evaluations': "{count} évaluation(s)",
    'skl.mine.final': "niveau final publié",
    'skl.mine.note': "Seules les évaluations publiées par vos enseignants paraissent. L'espace de dépôt ne s'ouvre que sur leur demande.",
    'skl.mine.noFinal': "Pas encore de niveau final.",
    'skl.mine.byIndicator': "Par indicateur",
    'skl.mine.byIndicatorSub': "à la dernière évaluation",
    'skl.mine.proofs': "Mes preuves",
    'skl.mine.noProof': "Aucune preuve déposée.",
    'skl.mine.open': "Ouvrir",
    'skl.deposit.title': "Déposer : {label}",
    'skl.deposit.by': "Demandé par",
    'skl.deposit.skills': "Compétences",
    'skl.deposit.due': "Date limite",
    'skl.deposit.noDue': "aucune",
    'skl.deposit.accepted': "Accepté",
    'skl.deposit.acceptedValue': "{formats} · 5 Mo au plus",
    'skl.deposit.drop': "Glisser le fichier ici ou le choisir",
    'skl.deposit.tooBig': "{name} · {size} : le fichier dépasse 5 Mo.",
    'skl.deposit.badFormat': "{name} : format non accepté ({formats}).",
    'skl.deposit.choose': "Choisissez un fichier.",
    'skl.deposit.remove': "Retirer",
    'skl.deposit.current': "Déjà déposé : {name}. Un nouveau fichier le remplace.",
    'skl.deposit.note': "Un mot pour l'enseignant",
    'skl.deposit.replaceNote': "Jusqu'à la date limite, vous pouvez remplacer votre fichier ; l'enseignant en est notifié.",
    'skl.deposit.done': "Preuve déposée : votre enseignant en est notifié.",
    'skl.deposit.replaced': "Preuve remplacée : votre enseignant en est notifié.",
    'export.label': "Excel",
    'export.busy': "Export en cours…",
    'export.pages': "Page {done} sur {total}…",
    'export.hint': "Exporter en Excel toutes les lignes filtrées, avec les colonnes du tableau",
    'violation.export.rows': "L'export est limité à 50 000 lignes : affinez les filtres.",
    'violation.export.columns': "Ce tableau a trop de colonnes pour l'export.",
    'el.acc.allYear': "Toute l'année",
    'el.acc.change': "Changer",
    'el.acc.edit': "Modifier l'aménagement",
    'el.acc.extra': "Tentatives supplémentaires",
    'el.acc.from': "Du",
    'el.acc.grant': "Accorder",
    'el.acc.granted': "Accordé par",
    'el.acc.kind': "Aménagement",
    'el.acc.new': "Nouvel aménagement",
    'el.acc.none': "Aucun aménagement accordé.",
    'el.acc.note': "Accordés par la scolarité, tracés, appliqués d'eux-mêmes à la passation (durée prolongée, fenêtre propre). L'enseignant les voit sur ses copies sans pouvoir les modifier.",
    'el.acc.percent': "Temps en plus (%)",
    'el.acc.quiz': "Évaluation",
    'el.acc.reason': "Motif",
    'el.acc.remove': "Retirer",
    'el.acc.scope': "Portée",
    'el.acc.search': "Nom ou matricule",
    'el.acc.sub': "Tiers-temps, fenêtres décalées, tentatives supplémentaires",
    'el.acc.title': "Aménagements",
    'el.acc.to': "Au",
    'el.acc.kind.extra_time': "Tiers-temps",
    'el.acc.kind.shifted_window': "Fenêtre décalée",
    'el.acc.kind.extra_attempt': "Tentative supplémentaire",
    'el.acc.short.extra_time': "tiers-temps",
    'el.acc.short.shifted_window': "fenêtre décalée",
    'el.acc.short.extra_attempt': "tentative en plus",
    'el.attempt.anonymous': "Copie anonyme",
    'el.attempt.device': "Appareil",
    'el.attempt.log': "Journal de tentative",
    'el.attempt.logSub': "Des indices pour l'enseignant, jamais une sanction automatique",
    'el.attempt.minutes': "Minutes accordées",
    'el.attempt.points': "{got} sur {max} point(s)",
    'el.attempt.points2': "Points",
    'el.attempt.question': "Question {n}",
    'el.attempt.reason': "Motif (tracé)",
    'el.attempt.receipt': "Accusé",
    'el.attempt.reopen': "Rouvrir la tentative",
    'el.attempt.reopenNote': "L'étudiant reprend sa copie avec ses réponses, pour la durée accordée.",
    'el.attempt.score': "Note",
    'el.attempt.summary': "Copie",
    'el.attempt.neutralised.give': "Neutralisée : points rendus",
    'el.attempt.neutralised.remove': "Neutralisée : hors total",
    'el.bank.allChapters': "Tous les chapitres",
    'el.bank.allDifficulties': "Toutes difficultés",
    'el.bank.allOutcomes': "Tous les acquis",
    'el.bank.allTypes': "Tous les types",
    'el.bank.archived': "Archivées",
    'el.bank.chapter': "Chapitre",
    'el.bank.difficulty': "Difficulté",
    'el.bank.discrimination': "Discrimination",
    'el.bank.export': "Exporter",
    'el.bank.import': "Importer (tableur)",
    'el.bank.importErrors': "Lignes écartées : {list}.",
    'el.bank.imported': "{count} question(s) importée(s).",
    'el.bank.live': "En service",
    'el.bank.new': "Nouvelle question",
    'el.bank.none': "Aucune question.",
    'el.bank.note': "Modifier une question déjà utilisée crée une nouvelle version : les copies passées gardent la version répondue. Réussite et indice de discrimination se calculent après usage ; un distracteur jamais choisi est signalé.",
    'el.bank.outcome': "Acquis",
    'el.bank.points': "Points",
    'el.bank.question': "Question",
    'el.bank.search': "Chercher une question",
    'el.bank.sub': "{course} · {count} question(s) · {classes}",
    'el.bank.success': "Réussite",
    'el.bank.title': "Banque de questions",
    'el.bank.type': "Type",
    'el.bank.untitled': "(sans énoncé)",
    'el.bank.unused': "Jamais choisi : {list}",
    'el.bank.version': "Version",
    'el.copies.absent': "Absents",
    'el.copies.absentBadge': "Absent",
    'el.copies.all': "Toutes",
    'el.copies.attempt': "Tentative {n}",
    'el.copies.auto': "soumise d'elle-même",
    'el.copies.autoScore': "{score} auto",
    'el.copies.copies': "Copies",
    'el.copies.filter': "Filtrer les copies",
    'el.copies.focus': "{count} perte(s) de focus",
    'el.copies.grade': "Corriger par question",
    'el.copies.graded': "Corrigées",
    'el.copies.incident': "Incident",
    'el.copies.incidentNote': "Rouvrir une tentative se fait depuis la copie ; prolonger la fenêtre, depuis le paramétrage de l'évaluation. Un motif est demandé et tracé.",
    'el.copies.kept': "retenue {score}",
    'el.copies.lot': "Lot de notes :",
    'el.copies.noCopy': "Aucune copie pour l'instant.",
    'el.copies.notStarted': "Non commencée",
    'el.copies.open': "Copie",
    'el.copies.publish': "Publier les résultats",
    'el.copies.publishedDone': "{count} copie(s) publiée(s) aux étudiants.",
    'el.copies.reconnects': "{count} reconnexion(s)",
    'el.copies.regrade': "Recorriger une question",
    'el.copies.regradeDo': "Recorriger",
    'el.copies.regradeSub': "Pour toutes les copies, avec l'impact avant confirmation",
    'el.copies.results': "Résultats",
    'el.copies.returned': "Lot rendu par la scolarité : {reason}",
    'el.copies.score': "Note",
    'el.copies.signals': "Signaux",
    'el.copies.state': "Statut",
    'el.copies.sub': "{total} étudiant(s) · {graded} corrigée(s) · {toGrade} à corriger · {absent} absent(s)",
    'el.copies.times': "Début · fin",
    'el.copies.title': "{quiz} · copies",
    'el.copies.toGrade': "À corriger",
    'el.copies.validate': "Valider les copies corrigées",
    'el.copies.validated': "Validées",
    'el.copies.validatedDone': "{count} copie(s) validée(s).",
    'el.copies.view': "Vue",
    'el.course.bank': "Banque de questions",
    'el.course.credits': "{credits} crédits",
    'el.course.readOnly': "Lecture seule : ce cours n'est pas dans votre planification ou l'année est close.",
    'el.course.students': "{count} étudiant(s)",
    'el.course.tabs': "Sections du cours",
    'el.dash.allPlans': "Tous les plans de cours",
    'el.dash.beforeOpening': "Avant ouverture",
    'el.dash.byTraining': "Plans de cours par offre",
    'el.dash.lots': "Lots de notes",
    'el.dash.lotsNote': "{control} à contrôler, {transfer} à transférer",
    'el.dash.note': "Prochaine ouverture d'un sujet à valider : {at}.",
    'el.dash.plans': "Plans publiés",
    'el.dash.plansNote': "{rate} % des cours",
    'el.dash.readOnly': "Lecture seule",
    'el.dash.subjects': "Sujets à valider",
    'el.dash.subjectsNote': "examens comptant pour la moyenne",
    'el.dash.title': "Tableau de bord E-Learning",
    'el.dash.toGrade': "Copies à corriger",
    'el.dash.toGradeNote': "dans l'établissement",
    'el.dash.toValidate': "Sujets d'examen à valider",
    'el.dialog.add': "Ajouter",
    'el.dialog.addTitle': "Ajouter une ressource",
    'el.dialog.afterQuiz': "Après réussite du quiz « {title} »",
    'el.dialog.afterViewed': "Après consultation de « {title} »",
    'el.dialog.body': "Contenu de la page",
    'el.dialog.choose': "Choisir…",
    'el.dialog.closesAt': "Fermeture",
    'el.dialog.condition': "Condition",
    'el.dialog.diffusion': "Diffusion",
    'el.dialog.editTitle': "Modifier la ressource",
    'el.dialog.file': "Fichier",
    'el.dialog.fileHint': "{kinds} · {mb} Mo au plus",
    'el.dialog.groups': "Groupes",
    'el.dialog.groupsNote': "Aucun coché : tous les groupes du cours.",
    'el.dialog.kind': "Type de ressource",
    'el.dialog.library': "Bibliothèque commune",
    'el.dialog.min': "Note minimale (sur 20)",
    'el.dialog.noCondition': "Sans condition",
    'el.dialog.notify': "Notifier les étudiants",
    'el.dialog.notifyNote': "dans le résumé quotidien",
    'el.dialog.opensAt': "Ouverture",
    'el.dialog.preview': "Aperçu",
    'el.dialog.readOnly': "Consultation seule",
    'el.dialog.readOnlyNote': "pas de téléchargement",
    'el.dialog.replace': "Remplacer « {name} » (nouvelle version)",
    'el.dialog.section': "Section",
    'el.dialog.shareable': "Partager dans la bibliothèque commune",
    'el.dialog.shareableNote': "les autres cours peuvent la référencer",
    'el.dialog.title': "Titre",
    'el.dialog.url': "Adresse du lien",
    'el.dialog.versions': "Versions précédentes",
    'el.dialog.videoHint': "YouTube, Vimeo ou Microsoft Stream : la vidéo est lue dans la page, jamais hébergée.",
    'el.dialog.videoUrl': "Adresse de la vidéo",
    'el.dialog.visibility': "Visibilité",
    'el.dialog.watermark': "Filigrane nominatif",
    'el.dialog.watermarkNote': "nom, matricule et date sur chaque page du PDF téléchargé par l'étudiant",
    'el.grades.copies': "Copies",
    'el.grades.grade': "Corriger",
    'el.grades.lot': "Lot de notes",
    'el.grades.none': "Aucune copie pour l'instant.",
    'el.grades.published': "Publiées le",
    'el.grades.sub': "Corriger, valider, publier ; le lot part ensuite au contrôle puis au transfert",
    'el.grades.title': "Copies et notes",
    'el.grades.toGrade': "À corriger",
    'el.grading.accept': "Accepter et recorriger les {count} copies",
    'el.grading.anonymous': "anonyme",
    'el.grading.auto': "Correction automatique : {points} sur {max}",
    'el.grading.blank': "Pas de réponse",
    'el.grading.comment': "Commentaire",
    'el.grading.comments': "Commentaires réutilisables",
    'el.grading.copyOf': "copie {n} sur {total}",
    'el.grading.nextCopy': "Copie suivante",
    'el.grading.nextQuestion': "Question suivante",
    'el.grading.noComment': "Les commentaires déjà écrits paraîtront ici.",
    'el.grading.noUnexpected': "Aucune réponse courte non prévue.",
    'el.grading.none': "Aucune copie pour cette question.",
    'el.grading.note': "Les noms restent masqués jusqu'à la validation si l'évaluation est anonyme. La répartition entre plusieurs correcteurs et la double correction viendront avec un lot suivant.",
    'el.grading.pickQuestion': "Choisir la question",
    'el.grading.points': "Points (sur {max})",
    'el.grading.previousCopy': "Copie précédente",
    'el.grading.previousQuestion': "Question précédente",
    'el.grading.question': "Q {n} · {text}",
    'el.grading.rubric': "Grille critériée",
    'el.grading.saveNext': "Valider et suivante",
    'el.grading.statement': "Énoncé",
    'el.grading.sub': "{quiz} · {toGrade} copie(s) à corriger sur {total}",
    'el.grading.title': "Correction par question",
    'el.grading.unexpected': "Réponses courtes non prévues",
    'el.grading.written': "{count} copie(s)",
    'el.mine.afterDate': "après le {at}",
    'el.mine.afterPublication': "après publication",
    'el.mine.attemptsLeft': "{count} tentative(s)",
    'el.mine.copy': "Copie",
    'el.mine.counted': "compte pour la moyenne",
    'el.mine.course': "Cours",
    'el.mine.courses': "Mes cours",
    'el.mine.from': "à partir du {at}",
    'el.mine.new': "Nouveau",
    'el.mine.newCount': "{count} nouvelle(s) ressource(s)",
    'el.mine.newResource': "nouvelle ressource",
    'el.mine.noCourse': "Aucun cours en ligne pour l'instant.",
    'el.mine.noPlan': "plan pas encore publié",
    'el.mine.noResult': "Aucun résultat pour l'instant.",
    'el.mine.notCounted': "sans note",
    'el.mine.note': "Les évaluations ouvertes paraissent aussi dans l'agenda du portail ; un rappel part la veille.",
    'el.mine.nothing': "Rien à faire cette semaine.",
    'el.mine.open': "Ouvrir",
    'el.mine.planUpdated': "plan v{version} mis à jour",
    'el.mine.planVersion': "plan v{version}",
    'el.mine.quiz': "Évaluation",
    'el.mine.results': "Mes résultats en ligne",
    'el.mine.resultsSub': "Les notes paraissent après publication par l'enseignant",
    'el.mine.resume': "Reprendre",
    'el.mine.score': "Note",
    'el.mine.seeCopy': "Voir ma copie",
    'el.mine.seeKey': "Voir le corrigé",
    'el.mine.soon': "Bientôt",
    'el.mine.soonBadge': "Bientôt",
    'el.mine.start': "Commencer",
    'el.mine.sub': "{classes} · cette semaine",
    'el.mine.title': "Mes cours",
    'el.mine.todo': "À faire cette semaine",
    'el.mine.trackedNote': "Votre établissement enregistre l'ouverture et le téléchargement des ressources, pour que l'enseignant sache ce qui a été lu.",
    'el.mine.until': "ouvert jusqu'au {at}",
    'el.plan.assessment': "Évaluation",
    'el.plan.assessments': "Modalités d'évaluation",
    'el.plan.assessmentsSub': "Lues dans le règlement des études",
    'el.plan.changes': "{count} changement(s)",
    'el.plan.coefficient': "Coefficient",
    'el.plan.common': "Plan commun",
    'el.plan.conform': "Conforme au règlement des études : rien à ressaisir.",
    'el.plan.course': "Cours",
    'el.plan.date': "Date",
    'el.plan.duplicate': "Reprendre le plan de l'an passé",
    'el.plan.group': "Plan de",
    'el.plan.groups': "Groupes",
    'el.plan.hours': "Volume",
    'el.plan.level': "Niveau",
    'el.plan.linked': "Liées",
    'el.plan.linkedValue': "{count} ressource(s)",
    'el.plan.missing': "À remplir pour publier : {list}.",
    'el.plan.nextDraft': "Version {version} en préparation",
    'el.plan.noAssessment': "Aucune évaluation au règlement pour ce cours.",
    'el.plan.noSession': "Aucune séance planifiée.",
    'el.plan.noVersion': "Aucune version publiée.",
    'el.plan.objectives': "Objectifs",
    'el.plan.prefilled': "Rubriques préremplies",
    'el.plan.prefilledSub': "Lues dans le catalogue et la planification, en lecture seule",
    'el.plan.publish': "Publier maintenant",
    'el.plan.publishedDone': "Version {version} publiée : les étudiants sont notifiés.",
    'el.plan.readings': "Lectures",
    'el.plan.retake': "rattrapage",
    'el.plan.saveDraft': "Enregistrer le brouillon",
    'el.plan.saved': "Brouillon enregistré.",
    'el.plan.schedule': "Programmer la publication",
    'el.plan.scheduleAt': "Publier le",
    'el.plan.scheduledDone': "Publication programmée.",
    'el.plan.scheduledOn': "Publication programmée le {at}",
    'el.plan.session': "Séance",
    'el.plan.sessions': "Déroulé par séance",
    'el.plan.sessionsSub': "Une ligne par séance planifiée",
    'el.plan.state': "État",
    'el.plan.state.draft': "Brouillon",
    'el.plan.state.none': "Plan à préparer",
    'el.plan.state.published': "Plan publié v{version}",
    'el.plan.state.published_draft': "Plan publié v{version}, modifié",
    'el.plan.state.scheduled': "Programmé",
    'el.plan.teachers': "Enseignants",
    'el.plan.template': "Modèle",
    'el.plan.templateNote': "Rubriques du modèle « {name} » ; celles marquées d'un astérisque sont obligatoires pour publier.",
    'el.plan.theme': "Thème",
    'el.plan.training': "Offre",
    'el.plan.type': "Type",
    'el.plan.unschedule': "Annuler la programmation",
    'el.plan.versionLine': "publiée le {at} par {by}",
    'el.plan.versions': "Versions",
    'el.plan.written': "Rubriques rédigées",
    'el.plans.course': "Cours",
    'el.plans.courses': "Cours",
    'el.plans.drafts': "Brouillons",
    'el.plans.filter': "Filtrer les plans",
    'el.plans.none': "Aucun cours cette année.",
    'el.plans.published': "Plans publiés",
    'el.plans.publishedAt': "Publié le",
    'el.plans.search': "Chercher un cours, un enseignant",
    'el.plans.state': "État",
    'el.plans.title': "Plans de cours",
    'el.plans.toPrepare': "À préparer",
    'el.plans.training': "Offre",
    'el.plans.f.all': "Tous",
    'el.plans.f.published': "Publiés",
    'el.plans.f.draft': "Brouillons",
    'el.plans.f.none': "À préparer",
    'el.play.after': "La note et le corrigé paraîtront après publication par votre enseignant.",
    'el.play.answer': "Votre réponse",
    'el.play.answered': "{count} réponse(s) enregistrée(s) sur {total}",
    'el.play.attemptLine': "Tentative {n} sur {total} · commencée {at}",
    'el.play.autosave': "Sauvegarde continue",
    'el.play.back': "Retour au cours",
    'el.play.confirm': "{answered} question(s) répondue(s) sur {total}. Une fois soumise, la copie ne se modifie plus.",
    'el.play.dontKnow': "Je ne sais pas",
    'el.play.false': "Faux",
    'el.play.flag': "Marquer pour revoir",
    'el.play.from': "à partir du {at}",
    'el.play.goTo': "Aller à la question {n}",
    'el.play.key': "Corrigé :",
    'el.play.legend': "Vert : répondue · ambre : à revoir",
    'el.play.maxWords': "{count} mots au plus",
    'el.play.meta': "{points} point(s) · {type}",
    'el.play.next': "Suivante",
    'el.play.noFile': "Aucun fichier déposé",
    'el.play.noKey': "Le corrigé n'est pas disponible pour cette évaluation, ou pas encore.",
    'el.play.notYet': "après publication",
    'el.play.note': "Chaque réponse part au serveur dès qu'elle est saisie : une coupure réseau ne perd rien, et la tentative reprend où elle était si le navigateur se ferme. À l'échéance, la copie est soumise d'elle-même.",
    'el.play.offline': "Hors ligne : vos réponses partiront dès le retour du réseau.",
    'el.play.pick': "Choisir…",
    'el.play.previous': "Précédente",
    'el.play.questionOf': "Question {n} sur {total}",
    'el.play.questions': "Questions",
    'el.play.receipt': "Accusé n°",
    'el.play.received': "Votre copie est bien reçue",
    'el.play.saved': "Réponse enregistrée à {at}",
    'el.play.score': "Ma note",
    'el.play.submit': "Terminer et soumettre",
    'el.play.submitNow': "Soumettre ma copie",
    'el.play.submittedAt': "soumise {at}",
    'el.play.true': "Vrai",
    'el.play.unflag': "Retirer la marque",
    'el.play.upload': "Déposer un fichier",
    'el.question.abs': "Absolue",
    'el.question.accentsSensitive': "Respecter les accents",
    'el.question.addBlank': "Ajouter un trou",
    'el.question.addChoice': "Ajouter un choix",
    'el.question.addItem': "Ajouter un élément",
    'el.question.addPair': "Ajouter une paire",
    'el.question.answer': "Bonne réponse",
    'el.question.answered': "Cette question a déjà été répondue : l'enregistrer crée une nouvelle version, les copies passées gardent la leur.",
    'el.question.answers': "Réponses acceptées",
    'el.question.answersHint': "Une par ligne",
    'el.question.archive': "Archiver",
    'el.question.blanks': "Trous",
    'el.question.body': "Énoncé",
    'el.question.bodyHint': "Texte riche ; une formule LaTeX s'écrit entre $…$ dans la ligne, ou entre $$…$$ en bloc (aperçu à droite).",
    'el.question.caseSensitive': "Respecter les majuscules",
    'el.question.chapter': "Chapitre",
    'el.question.choice': "Choix {n}",
    'el.question.choices': "Choix et bonnes réponses",
    'el.question.clozeHint': "Placez chaque trou par [[b1]], [[b2]]… dans l'énoncé ; « Ajouter un trou » le fait pour vous.",
    'el.question.correct': "Bonne réponse",
    'el.question.criteria': "Grille critériée",
    'el.question.criteriaHint': "Un critère par ligne : Critère | Niveau: points | Niveau: points",
    'el.question.difficulty': "Difficulté",
    'el.question.dontKnow': "Proposer « Je ne sais pas »",
    'el.question.edit': "Question · version {version}",
    'el.question.feedback': "Commentaire de correction",
    'el.question.item': "Élément {n}",
    'el.question.items': "Éléments dans le bon ordre",
    'el.question.kinds': "Fichiers admis",
    'el.question.left': "À gauche",
    'el.question.loadFailed': "Question introuvable.",
    'el.question.maxWords': "Nombre de mots maximal",
    'el.question.new': "Nouvelle question",
    'el.question.outcome': "Acquis visé",
    'el.question.pairs': "Paires à associer",
    'el.question.panel': "La question",
    'el.question.penalty': "Pénalité d'une mauvaise réponse",
    'el.question.penaltyHint': "Fraction des points retirée (0 à 1)",
    'el.question.points': "Points",
    'el.question.positionMode': "Points par place juste",
    'el.question.positionModeNote': "sinon tout ou rien",
    'el.question.preview': "Aperçu étudiant",
    'el.question.rel': "Relative (%)",
    'el.question.right': "À droite",
    'el.question.scoring': "Barème",
    'el.question.shuffle': "Mélanger l'ordre des choix",
    'el.question.shuffleNote': "à chaque tentative",
    'el.question.stats': "Après usage",
    'el.question.sub': "Banque de {course}",
    'el.question.tags': "Étiquettes",
    'el.question.tagsHint': "Séparées par des virgules",
    'el.question.tolerance': "Tolérance",
    'el.question.toleranceType': "Type de tolérance",
    'el.question.type': "Type",
    'el.question.unarchive': "Remettre en service",
    'el.question.unit': "Unité",
    'el.question.uses': "Réponses",
    'el.question.value': "Valeur attendue",
    'el.quiz.addQuestion': "Ajouter",
    'el.quiz.addRule': "Ajouter un critère",
    'el.quiz.allowed': "Tentatives autorisées",
    'el.quiz.anonymous': "Correction anonyme",
    'el.quiz.anonymousNote': "les noms paraissent à la validation",
    'el.quiz.assessment': "Note transférée à",
    'el.quiz.attach': "Rattachement",
    'el.quiz.attempts': "Tentatives et retour",
    'el.quiz.available': "Disponibles",
    'el.quiz.backToDraft': "Revenir au brouillon",
    'el.quiz.checks': "Avant d'ouvrir",
    'el.quiz.circuit': "Circuit",
    'el.quiz.close': "Fermer maintenant",
    'el.quiz.closedDone': "Évaluation fermée ; les tentatives en cours sont soumises.",
    'el.quiz.composition': "Composition",
    'el.quiz.confirmDelete': "Supprimer ce brouillon ?",
    'el.quiz.copies': "Copies",
    'el.quiz.delay': "Délai entre deux tentatives (min)",
    'el.quiz.delete': "Supprimer le brouillon",
    'el.quiz.draw': "Tirer",
    'el.quiz.duration': "Durée (min)",
    'el.quiz.durationHint': "Vide : sans limite ; soumission automatique à l'échéance",
    'el.quiz.extend': "Prolonger la fenêtre",
    'el.quiz.extendDo': "Prolonger",
    'el.quiz.extendReason': "Motif (tracé)",
    'el.quiz.extendTo': "Nouvelle fermeture",
    'el.quiz.extended': "Fenêtre prolongée.",
    'el.quiz.extensionLine': "Prolongée jusqu'au {to} par {by} : {reason}",
    'el.quiz.feedback': "Retour à l'étudiant",
    'el.quiz.feedbackAt': "À partir du",
    'el.quiz.integrity': "Intégrité",
    'el.quiz.integrityNote': "Tirage et ordre des choix figés par étudiant. Journal de tentative : début, fin, adresse IP, appareil, pertes de focus, reconnexions. Ce sont des indices, jamais un blocage. Aucune restriction d'accès pour l'instant : ni mot de passe, ni plage IP, ni mode verrouillé.",
    'el.quiz.keep': "Note retenue",
    'el.quiz.loadFailed': "Évaluation introuvable.",
    'el.quiz.locked': "Des copies existent : la composition ne change plus. La fenêtre peut encore être prolongée.",
    'el.quiz.maxPoints': "Total actuel : {points} point(s)",
    'el.quiz.missingQuestion': "Question retirée de la banque",
    'el.quiz.navFree': "Libre",
    'el.quiz.navSequential': "Séquentielle (sans retour)",
    'el.quiz.navigation': "Navigation",
    'el.quiz.new': "Nouvelle évaluation en ligne",
    'el.quiz.noAssessment': "Aucune (pas de transfert)",
    'el.quiz.noItem': "Aucune question choisie.",
    'el.quiz.note': "Un examen comptant pour la moyenne passe par la validation du sujet par le coordinateur avant ouverture. Un entraînement n'est jamais transféré.",
    'el.quiz.open': "Rendre ouvrable",
    'el.quiz.opened': "L'évaluation ouvrira à la date prévue.",
    'el.quiz.outOf': "sur {outOf}",
    'el.quiz.pending': "Sujet envoyé au coordinateur le {at}.",
    'el.quiz.pick': "Choisir une question de la banque…",
    'el.quiz.pointsEach': "Points chacune",
    'el.quiz.rejected': "Sujet à reprendre : {reason}",
    'el.quiz.saveFirst': "Enregistrez l'évaluation pour voir les vérifications et l'ouvrir.",
    'el.quiz.saved': "Évaluation enregistrée.",
    'el.quiz.scale': "Barème (sur)",
    'el.quiz.sentToValidation': "Sujet envoyé au coordinateur.",
    'el.quiz.shuffle': "Mélanger les questions et les choix",
    'el.quiz.shuffleNote': "ordre figé par étudiant",
    'el.quiz.submitSubject': "Envoyer le sujet à valider",
    'el.quiz.title': "Titre",
    'el.quiz.total': "{points} point(s), ramenés sur {outOf}",
    'el.quiz.usage': "Usage",
    'el.quiz.validated': "validée",
    'el.quiz.validatedBy': "Sujet validé par {by} le {at}",
    'el.quiz.validatedDone': "Sujet validé.",
    'el.quiz.window': "Fenêtre et durée",
    'el.quiz.fixed': "Questions fixes",
    'el.quiz.random': "Tirage aléatoire",
    'el.quizzes.copies': "Copies",
    'el.quizzes.copiesValue': "{count} copie(s)",
    'el.quizzes.duration': "{minutes} min",
    'el.quizzes.new': "Nouvelle évaluation",
    'el.quizzes.noValidationNote': "Les sujets ne passent pas par le coordinateur (réglage de l'établissement).",
    'el.quizzes.none': "Aucune évaluation en ligne.",
    'el.quizzes.questions': "Questions",
    'el.quizzes.quiz': "Évaluation",
    'el.quizzes.state': "État",
    'el.quizzes.sub': "Entraînements, contrôles continus et examens",
    'el.quizzes.title': "Évaluations en ligne",
    'el.quizzes.to': "vers « {assessment} »",
    'el.quizzes.toGrade': "{count} à corriger",
    'el.quizzes.usage': "Usage",
    'el.quizzes.validationNote': "Un examen relié au règlement passe par la validation du coordinateur avant ouverture.",
    'el.quizzes.window': "Fenêtre",
    'el.regrade.apply': "Recalculer les copies",
    'el.regrade.changed': "Copies modifiées",
    'el.regrade.changedValue': "{count} sur {total}",
    'el.regrade.copies': "répondue par {count} copie(s)",
    'el.regrade.done': "{count} copie(s) recalculée(s).",
    'el.regrade.impact': "Impact avant confirmation",
    'el.regrade.keyNote': "Pour corriger le corrigé d'une question fermée, modifiez-la dans la banque puis recorrigez-la ici.",
    'el.regrade.mean': "Moyenne",
    'el.regrade.mode': "Recorrection",
    'el.regrade.newPoints': "Nouveaux points de la question",
    'el.regrade.none': "aucune",
    'el.regrade.preview': "Voir l'impact",
    'el.regrade.published': "Notes déjà publiées",
    'el.regrade.publishedValue': "{count}, les étudiants seront notifiés",
    'el.regrade.reason': "Motif",
    'el.regrade.title': "Recorriger une question",
    'el.regrade.total': "Total de l'évaluation",
    'el.regrade.give': "Neutraliser : points rendus à tous",
    'el.regrade.giveNote': "chaque copie reçoit les points de la question",
    'el.regrade.remove': "Neutraliser : retirer du total",
    'el.regrade.removeNote': "le total baisse d'autant",
    'el.regrade.points': "Changer le barème",
    'el.regrade.pointsNote': "nouveaux points de la question",
    'el.regrade.restore': "Annuler les recorrections",
    'el.regrade.restoreNote': "revenir à la correction d'origine",
    'el.resources.add': "Ajouter une ressource",
    'el.resources.addHere': "Ajouter",
    'el.resources.addSection': "Ajouter la section",
    'el.resources.broken': "lien injoignable",
    'el.resources.confirmDelete': "Supprimer cette ressource et ses versions ?",
    'el.resources.count': "Ressources",
    'el.resources.countValue': "{count} dans {sections} section(s)",
    'el.resources.diffusion': "Diffusion",
    'el.resources.diffusionNote': "Choisie élément par élément : visible ou masquée, dates d'ouverture et de fermeture, groupes, condition (après consultation d'une ressource ou réussite d'un quiz), téléchargement ou consultation seule, filigrane nominatif.",
    'el.resources.digest': "Résumé quotidien",
    'el.resources.digestValue': "les étudiants sont notifiés une fois par jour",
    'el.resources.down': "Descendre",
    'el.resources.empty': "Aucune séance ni section pour l'instant : ajoutez une section.",
    'el.resources.layout': "Présentation",
    'el.resources.max': "Taille maximale",
    'el.resources.maxValue': "{mb} Mo par fichier",
    'el.resources.newSection': "Nouvelle section (chapitre)",
    'el.resources.noFile': "Aucun fichier déposé",
    'el.resources.none': "Aucune ressource.",
    'el.resources.note': "Un fichier remplacé garde ses versions ; les étudiants voient « mis à jour le … ». Une ressource partagée est référencée, pas copiée : sa mise à jour profite à tous les cours.",
    'el.resources.opened': "{count} / {total} ouvert",
    'el.resources.opensOn': "visible le {at}",
    'el.resources.quota': "Quota du cours",
    'el.resources.quotaValue': "{used} sur {quota}",
    'el.resources.readOnlyFile': "consultation seule",
    'el.resources.renew': "Reconduire depuis l'an passé",
    'el.resources.session': "Séance",
    'el.resources.sharedFrom': "partagée par {course}",
    'el.resources.someGroups': "certains groupes",
    'el.resources.space': "Espace",
    'el.resources.tooBig': "Fichier trop lourd ({mb} Mo au plus).",
    'el.resources.up': "Monter",
    'el.resources.updated': "mis à jour le {at}",
    'el.resources.views': "Consultations",
    'el.resources.watermark': "filigrane nominatif",
    'el.results.allGraded': "toutes les copies sont notées",
    'el.results.below': "Sous la moyenne",
    'el.results.belowNote': "sur {count}",
    'el.results.byQuestion': "Réussite par question",
    'el.results.count': "{count} étudiant(s) noté(s)",
    'el.results.distribution': "Distribution des notes",
    'el.results.distributionSub': "Ramenées sur 20",
    'el.results.mean': "Moyenne",
    'el.results.median': "Médiane",
    'el.results.noOutcome': "Aucune question n'est reliée à un acquis.",
    'el.results.outOf': "sur {outOf}",
    'el.results.outcomes': "Réussite par acquis d'apprentissage",
    'el.results.outcomesSub': "Reliée plus tard au suivi des compétences",
    'el.results.toGrade': "À corriger",
    'el.results.toGradeNote': "copies en attente",
    'el.rich.bold': "Gras",
    'el.rich.bullets': "Liste à puces",
    'el.rich.italic': "Italique",
    'el.rich.link': "Lien",
    'el.rich.linkPrompt': "Adresse du lien",
    'el.rich.numbers': "Liste numérotée",
    'el.rich.toolbar': "Mise en forme",
    'el.settings.absence': "Absence transférée",
    'el.settings.absenceHint': "Le type d'absence posé pour un étudiant qui n'a rendu aucune copie ; sans type, il n'est pas transféré.",
    'el.settings.addRubric': "Ajouter une rubrique",
    'el.settings.addTemplate': "Ajouter un modèle",
    'el.settings.assessments': "Évaluations et notes",
    'el.settings.control': "Contrôle avant transfert",
    'el.settings.controlNote': "le coordinateur contrôle chaque lot",
    'el.settings.courseQuota': "Quota par cours (Mo)",
    'el.settings.courses': "Cours et fichiers",
    'el.settings.feedback': "Retour par défaut",
    'el.settings.fileKinds': "Types de fichiers admis",
    'el.settings.institutionQuota': "Quota de l'établissement (Go)",
    'el.settings.maxFile': "Taille maximale (Mo)",
    'el.settings.newTemplate': "Nouveau modèle",
    'el.settings.noAbsence': "Aucun (ne rien transférer)",
    'el.settings.note': "Le module s'allume dans Modules. Non retenu pour l'instant : classe virtuelle, forum, vidéos hébergées, restrictions d'accès aux examens, surveillance par webcam ; import Moodle et correction assistée viendront plus tard.",
    'el.settings.off': "Le module est éteint : allumez-le dans Modules.",
    'el.settings.parents': "Parents après délibération",
    'el.settings.parentsNote': "notes en ligne visibles des parents seulement après délibération (réglage gardé pour le portail des parents)",
    'el.settings.perGroup': "Un plan par groupe",
    'el.settings.perGroupNote': "sinon un plan commun à tous les groupes du cours",
    'el.settings.removeTemplate': "Retirer le modèle",
    'el.settings.required': "Obligatoire",
    'el.settings.rounding': "Arrondi : deux décimales, comme le moteur de calcul des moyennes.",
    'el.settings.rubric': "Rubrique",
    'el.settings.saved': "Réglages enregistrés.",
    'el.settings.sub': "Réglages de l'établissement",
    'el.settings.templateName': "Nom du modèle",
    'el.settings.templates': "Modèles de plan",
    'el.settings.templatesSub': "Rubriques, ordre, rubriques obligatoires pour publier",
    'el.settings.title': "E-Learning",
    'el.settings.trackViews': "Suivi des consultations",
    'el.settings.trackViewsNote': "qui a ouvert ou téléchargé chaque ressource",
    'el.settings.trainings': "Offres couvertes (codes)",
    'el.settings.trainingsHint': "Vide : modèle par défaut. Par exemple {list}",
    'el.settings.used': "{size} utilisés",
    'el.settings.validation': "Validation des sujets",
    'el.settings.validationNote': "un examen comptant pour la moyenne est validé par le coordinateur",
    'el.student.changed': "Mis à jour le {at} · {count} changement(s) surligné(s)",
    'el.student.download': "Télécharger",
    'el.student.lockedQuiz': "ouverte après {min} / 20 au quiz",
    'el.student.lockedViewed': "ouverte après consultation d'une autre ressource",
    'el.student.noPlan': "Le plan de ce cours n'est pas encore publié.",
    'el.student.noResource': "Aucune ressource diffusée pour l'instant.",
    'el.student.pdf': "Télécharger en PDF",
    'el.student.planLine': "Plan de cours · version {version} du {at}",
    'el.student.quizzes': "Évaluations",
    'el.student.schedule': "Déroulé à venir",
    'el.student.seen': "Vu",
    'el.student.sessions': "Séances",
    'el.student.until': "jusqu'au {at}",
    'el.subjects.examine': "Examiner",
    'el.subjects.line': "{training} · {teacher} · ouverture le {at}",
    'el.subjects.noReady': "Aucun examen validé.",
    'el.subjects.none': "Aucun sujet à valider.",
    'el.subjects.note': "Valider un sujet le rend ouvrable à sa date ; le renvoyer le remet en brouillon, avec le motif, chez l'enseignant.",
    'el.subjects.pending': "À valider",
    'el.subjects.ready': "Examens validés",
    'el.subjects.reason': "Motif du renvoi",
    'el.subjects.reject': "Renvoyer à l'enseignant",
    'el.subjects.sub': "Examens comptant pour la moyenne, avant ouverture",
    'el.subjects.title': "Sujets à valider",
    'el.subjects.validate': "Valider",
    'el.tab.plan': "Plan de cours",
    'el.tab.resources': "Ressources",
    'el.tab.quizzes': "Évaluations en ligne",
    'el.tab.grades': "Copies et notes",
    'el.tab.tracking': "Suivi",
    'el.teach.duplicate': "Dupliquer le plan de l'an passé",
    'el.teach.kpiCourses': "Cours",
    'el.teach.kpiCoursesNote': "dans ma planification",
    'el.teach.kpiEmpty': "Plans à préparer",
    'el.teach.kpiEmptyNote': "aucun plan encore",
    'el.teach.kpiPublished': "Plans publiés",
    'el.teach.kpiPublishedNote': "{drafts} brouillon(s), {empty} à préparer",
    'el.teach.kpiToGrade': "Copies à corriger",
    'el.teach.kpiToGradeNote': "questions ouvertes en attente",
    'el.teach.none': "Aucun cours dans votre planification cette année.",
    'el.teach.note': "Aucun espace ne se crée à la main : chaque espace naît d'un cours planifié (offre, groupe, enseignant affecté). Les inscriptions de la scolarité décident qui voit quoi.",
    'el.teach.nothing': "Rien à faire cette semaine",
    'el.teach.open': "Ouvrir l'espace",
    'el.teach.plan': "Plan de cours",
    'el.teach.quizzes': "Évaluations",
    'el.teach.quizzesNone': "aucune",
    'el.teach.quizzesValue': "{total} · {open} ouverte(s)",
    'el.teach.resources': "Ressources",
    'el.teach.resourcesNew': "{count} · {fresh} nouvelle(s)",
    'el.teach.students': "Étudiants",
    'el.teach.sub': "Un espace par cours de ma planification, {year}",
    'el.teach.title': "Mes cours en ligne",
    'el.teach.todoGrade': "{count} copie(s) à corriger",
    'el.teach.todoPlan': "Plan à publier",
    'el.tracking.downloads': "Téléchargements",
    'el.tracking.off': "Le suivi des consultations est éteint dans les réglages de l'établissement.",
    'el.tracking.opened': "Ouverte par",
    'el.tracking.resource': "Ressource",
    'el.tracking.section': "Section",
    'el.tracking.sub': "{students} étudiant(s) inscrit(s)",
    'el.tracking.title': "Consultations des ressources",
    'el.transfer.absent': "Absents",
    'el.transfer.absentNote': "transférés comme « {absence} »",
    'el.transfer.circuit': "Circuit",
    'el.transfer.control': "Contrôler le lot",
    'el.transfer.copies': "Copies",
    'el.transfer.differences': "Écarts",
    'el.transfer.differencesNote': "avec le module Notes",
    'el.transfer.done': "Lot transféré : {marks} note(s), {absents} absent(s).",
    'el.transfer.lot': "Lot",
    'el.transfer.lots': "Lots publiés par les enseignants",
    'el.transfer.mark': "Dans le module Notes",
    'el.transfer.noAbsence': "aucun type d'absence réglé : non transférés",
    'el.transfer.none': "Aucun lot de notes pour l'instant.",
    'el.transfer.note': "Le transfert est un acte explicite, par lot. Un absent ou une non-soumission part comme tel et suit les règles d'absence du règlement : l'E-Learning ne le change jamais en zéro, et ne calcule ni moyenne, ni lettre, ni compensation. Après transfert, une modification repasse par le circuit du module Notes.",
    'el.transfer.online': "Note en ligne",
    'el.transfer.pending': "En attente",
    'el.transfer.pendingNote': "copies à corriger, valider ou publier",
    'el.transfer.ready': "Prêtes",
    'el.transfer.readyNote': "notes brutes sur {outOf}",
    'el.transfer.reason': "Motif",
    'el.transfer.return': "Rendre à l'enseignant",
    'el.transfer.returnDo': "Rendre le lot",
    'el.transfer.rowAbsent': "Absent",
    'el.transfer.rowNone': "Rien à transférer",
    'el.transfer.rowPending': "En attente",
    'el.transfer.rowReady': "Prête",
    'el.transfer.rowState': "Statut",
    'el.transfer.run': "Transférer le lot",
    'el.transfer.saved': "Lot mis à jour.",
    'el.transfer.state': "État",
    'el.transfer.stepControlled': "Contrôle du coordinateur",
    'el.transfer.stepPublished': "Notes publiées aux étudiants",
    'el.transfer.stepTransferred': "Transfert au module Notes",
    'el.transfer.sub': "Vers « {assessment} » · {training} · {count} étudiant(s)",
    'el.transfer.title': "Transferts des notes",
    'el.transfer.todo': "à faire",
    'el.transfer.validated': "L'évaluation est validée dans le module Notes : rouvrez-y la saisie avant de transférer.",
    'el.views.all': "Tous",
    'el.views.downloaded': "Téléchargé",
    'el.views.downloads': "Téléchargements",
    'el.views.downloadsNote': "au total",
    'el.views.filter': "Filtrer les étudiants",
    'el.views.group': "Groupe",
    'el.views.last': "Dernière ouverture",
    'el.views.never': "Jamais ouvert",
    'el.views.neverBadge': "Jamais",
    'el.views.neverNote': "étudiants inscrits",
    'el.views.neverTab': "Jamais ouvert",
    'el.views.note': "La consultation est tracée seulement si l'établissement l'a choisi ; l'étudiant en est informé dans « Mes cours ».",
    'el.views.opened': "Ouvert",
    'el.views.openedAt': "Ouvert le",
    'el.views.openedTab': "Ouvert",
    'el.views.rate': "{rate} % des inscrits",
    'el.views.search': "Chercher un étudiant",
    'el.views.student': "Étudiant",
    'el.views.sub': "{course} · {section} · consultations",
    'el.views.times': "Fois",
    'el.views.title': "Consultations",
    'el.visibility.closed': "Fermée",
    'el.visibility.conditional': "Conditionnelle",
    'el.visibility.hidden': "Masquée",
    'el.visibility.scheduled': "Visible le {at}",
    'el.visibility.visible': "Visible",
    'el.quizState.draft': "Brouillon",
    'el.quizState.pending': "Sujet à valider",
    'el.quizState.ready': "Ouvrable",
    'el.quizState.closed': "Fermée",
    'el.attemptState.in_progress': "En cours",
    'el.attemptState.submitted': "Soumise",
    'el.attemptState.graded': "Corrigée",
    'el.attemptState.validated': "Validée",
    'el.attemptState.published': "Publiée",
    'el.attemptState.transferred': "Transférée",
    'el.transferState.none': "Aucun",
    'el.transferState.published': "À contrôler",
    'el.transferState.controlled': "Contrôlé",
    'el.transferState.transferred': "Transféré",
    'el.transferState.returned': "Rendu",
    'el.usage.practice': "Entraînement",
    'el.usage.continuous': "Contrôle continu",
    'el.usage.exam': "Examen",
    'el.kind.file': "Fichier",
    'el.kind.video': "Vidéo en lien",
    'el.kind.link': "Lien",
    'el.kind.page': "Page",
    'el.kind.shared': "Partagée",
    'el.layout.session': "Par séance",
    'el.layout.chapter': "Par chapitre",
    'el.layout.free': "Libre",
    'el.sectionKind.session': "séance",
    'el.sectionKind.chapter': "chapitre",
    'el.sectionKind.free': "section",
    'el.fileKind.pdf': "PDF",
    'el.fileKind.office': "Office",
    'el.fileKind.image': "images",
    'el.fileKind.archive': "archives",
    'el.difficulty.easy': "facile",
    'el.difficulty.medium': "moyen",
    'el.difficulty.hard': "difficile",
    'el.qtype.single': "QCM réponse unique",
    'el.qtype.multiple': "QCM réponses multiples",
    'el.qtype.truefalse': "Vrai / Faux",
    'el.qtype.matching': "Appariement",
    'el.qtype.ordering': "Remise en ordre",
    'el.qtype.short': "Réponse courte",
    'el.qtype.numeric': "Numérique",
    'el.qtype.cloze': "Texte à trous",
    'el.qtype.open': "Question ouverte",
    'el.qtype.file': "Dépôt de fichier",
    'el.scoring.proportional': "Proportionnel, plancher à 0",
    'el.scoring.proportional_no_penalty': "Proportionnel, sans pénalité",
    'el.scoring.all_or_nothing': "Tout ou rien",
    'el.keep.best': "La meilleure",
    'el.keep.last': "La dernière",
    'el.keep.mean': "La moyenne",
    'el.feedback.none': "Rien",
    'el.feedback.score': "La note seule",
    'el.feedback.full': "Note et corrigé après publication",
    'el.check.questions': "Questions",
    'el.check.window': "Fenêtre",
    'el.check.duration': "Durée",
    'el.check.assessment': "Note transférée",
    'el.check.accommodations': "Aménagements",
    'el.event.start': "Début",
    'el.event.focus_lost': "Perte de focus de l'onglet",
    'el.event.reconnect': "Reconnexion",
    'el.event.resume': "Reprise",
    'el.event.submit': "Soumission",
    'el.event.auto_submit': "Soumission automatique",
    'el.event.reopen': "Réouverture",
    'rel.modules.learning': "E-Learning",
    'rel.modules.learningHint': "Plans de cours, ressources, banque de questions, évaluations en ligne, correction, transfert des notes, aménagements",
    'journal.action.learning.settings_changed': "Réglages E-Learning modifiés",
    'journal.action.learning.syllabus_changed': "Plan de cours modifié",
    'journal.action.learning.resource_saved': "Ressource de cours enregistrée",
    'journal.action.learning.question_saved': "Question de la banque enregistrée",
    'journal.action.learning.quiz_saved': "Évaluation en ligne enregistrée",
    'journal.action.learning.quiz_validated': "Sujet d'examen validé ou renvoyé",
    'journal.action.learning.grades_changed': "Correction en ligne modifiée",
    'journal.action.learning.grades_transferred': "Lot de notes contrôlé ou transféré",
    'journal.action.learning.accommodation_saved': "Aménagement accordé ou modifié",
    'journal.action.learning.attempt_reopened': "Tentative rouverte",
    'violation.el.assessment_validated': "L'évaluation est validée dans le module Notes : la saisie doit y être rouverte.",
    'violation.el.attempt_closed': "La copie est déjà soumise.",
    'violation.el.attempt_delay': "Attendez le délai avant la tentative suivante.",
    'violation.el.attempt_state': "Cette copie ne peut pas changer dans son état actuel.",
    'violation.el.bad_sheet': "Classeur illisible.",
    'violation.el.bad_url': "Adresse web attendue (https://...).",
    'violation.el.bad_video': "Vidéo YouTube, Vimeo ou Microsoft Stream attendue.",
    'violation.el.cloze_missing': "Un trou manque dans l'énoncé.",
    'violation.el.file_empty': "Fichier vide.",
    'violation.el.file_format': "Type de fichier non accepté.",
    'violation.el.file_too_big': "Fichier trop lourd.",
    'violation.el.lot_pending': "Des copies restent à corriger, valider ou publier.",
    'violation.el.no_assessment': "Aucune évaluation du règlement ne reçoit la note.",
    'violation.el.no_attempt_left': "Plus de tentative disponible.",
    'violation.el.no_previous': "Rien l'année précédente pour ce cours.",
    'violation.el.not_a_file': "Cette ressource n'est pas un fichier.",
    'violation.el.not_shareable': "Choisissez un fichier de la bibliothèque commune.",
    'violation.el.nothing_to_publish': "Aucune copie validée à publier.",
    'violation.el.nothing_to_validate': "Aucune copie corrigée à valider.",
    'violation.el.question_answers': "Une réponse acceptée au moins.",
    'violation.el.question_choices': "Deux choix au moins.",
    'violation.el.question_correct': "Cochez la ou les bonnes réponses (une seule pour un choix unique).",
    'violation.el.quiz_checks': "Une vérification avant ouverture n'est pas passée : voyez la liste.",
    'violation.el.quiz_locked': "Des copies existent : l'évaluation ne change plus.",
    'violation.el.quiz_not_open': "L'évaluation n'est pas ouverte.",
    'violation.el.quiz_pending': "Le sujet est chez le coordinateur.",
    'violation.el.quiz_state': "Action impossible dans l'état actuel de l'évaluation.",
    'violation.el.quota_course': "Quota du cours atteint.",
    'violation.el.quota_institution': "Quota de l'établissement atteint.",
    'violation.el.section_not_empty': "La section contient des ressources.",
    'violation.el.sequential': "Navigation séquentielle : pas de retour en arrière.",
    'violation.el.syllabus_incomplete': "Rubrique obligatoire pour publier.",
    'violation.el.syllabus_unchanged': "Rien de neuf depuis la dernière version.",
    'violation.el.transfer_state': "Aucun lot dans cet état.",
    'violation.el.transferred': "Notes transférées : la correction passe par le module Notes.",
    'violation.el.window_order': "La fermeture suit l'ouverture.",
    'el.transfer.open': "L'évaluation est encore ouverte : les étudiants sans copie ne sont pas encore absents. Le transfert attend sa fermeture.",
    'violation.el.quiz_still_open': "L'évaluation est encore ouverte : fermez-la avant de transférer.",
    'fin.account.title': "Compte",
    'fin.account.billed': "Facturé",
    'fin.account.discounts': "Rabais",
    'fin.account.paid': "Payé",
    'fin.account.due': "Reste dû",
    'fin.account.lateNote': "dont {amount} en retard",
    'fin.account.credit': "Trop-perçu au compte :",
    'fin.account.uninvoiced': "Inscription sans facture : {label}.",
    'fin.account.issue': "Émettre la facture",
    'fin.account.issued': "Facture émise.",
    'fin.account.empty': "Aucune facture.",
    'fin.account.onDemand': "Facturer un service à la demande",
    'fin.account.onDemandSub': "Une facture à part, payable en une fois.",
    'fin.account.onDemandNoYear': "Aucune année de référence : émettez d'abord la facture d'inscription.",
    'fin.account.onDemandDone': "Service facturé.",
    'fin.account.noOnDemand': "Aucun service à la demande.",
    'fin.account.addService': "Ajouter un service",
    'fin.account.noService': "Aucun service à ajouter.",
    'fin.account.serviceAdded': "Service ajouté à la facture.",
    'fin.account.grantDiscount': "Accorder un rabais",
    'fin.account.discountGranted': "Rabais accordé.",
    'fin.account.discountJournal': "Le rabais, son motif et son auteur sont journalisés et figurent sur la facture.",
    'fin.account.reason': "Motif",
    'fin.account.reasonRequired': "Motif",
    'fin.account.cancelInvoice': "Annuler la facture",
    'fin.account.cancelled': "Annulée le {date} : {reason}",
    'fin.account.designation': "Désignation",
    'fin.account.taxIncluded': "dont taxe {amount}",
    'fin.account.removeLine': "Retirer la ligne",
    'fin.account.lineRemoved': "« {label} » retiré de la facture.",
    'fin.account.net': "Total net",
    'fin.account.dueOn': "Échéance",
    'fin.account.rest': "reste {amount}",
    'fin.account.payments': "Paiements",
    'fin.account.allocatedTo': "Imputé sur",
    'fin.account.part': "versement {position}",
    'fin.account.reverses': "annule {number}",
    'fin.account.reversedBy': "annulé par {number}",
    'fin.collect.open': "Encaisser",
    'fin.collect.title': "Encaisser un paiement",
    'fin.collect.sub': "Facture {number} · reste dû {due}",
    'fin.collect.amount': "Montant reçu",
    'fin.collect.date': "Date d'encaissement",
    'fin.collect.chequeNo': "N° du chèque",
    'fin.collect.transferRef': "Référence du virement",
    'fin.collect.bank': "Banque",
    'fin.collect.drawer': "Tireur",
    'fin.collect.drawerHint': "Si différent de l'étudiant.",
    'fin.collect.comment': "Commentaire",
    'fin.collect.commentHint': "Facultatif. Figure sur le reçu.",
    'fin.collect.allocation': "Imputation automatique",
    'fin.collect.left': "Restait",
    'fin.collect.this': "Ce paiement",
    'fin.collect.after': "Après",
    'fin.collect.surplus': "Surplus de {amount} : il reste en avoir au compte.",
    'fin.collect.dueAfter': "Reste dû après ce paiement :",
    'fin.collect.immutable': "Un paiement enregistré ne se modifie plus. Une erreur se corrige par une annulation, qui crée une écriture inverse journalisée et son reçu.",
    'fin.collect.print': "Imprimer le reçu",
    'fin.collect.go': "Encaisser",
    'fin.collect.goPrint': "Encaisser et imprimer le reçu",
    'fin.collect.done': "Paiement encaissé.",
    'fin.cancel.title': "Annuler la facture {number}",
    'fin.cancel.hint': "La facture garde son numéro et reste consultable. Elle ne doit porter aucun paiement.",
    'fin.cancel.go': "Annuler la facture",
    'fin.cancel.done': "Facture annulée.",
    'fin.reverse.open': "Annuler ce paiement",
    'fin.reverse.title': "Annuler le paiement {number}",
    'fin.reverse.hint': "Une écriture inverse, de même montant, est enregistrée avec son propre reçu. Le paiement d'origine reste visible.",
    'fin.reverse.go': "Annuler le paiement",
    'fin.reverse.done': "Paiement annulé.",
    'journal.action.finance.configured': "Finance : module paramétré",
    'journal.action.finance.created': "Finance : ajout",
    'journal.action.finance.updated': "Finance : modification",
    'journal.action.finance.deleted': "Finance : suppression",
    'journal.action.finance.invoice_issued': "Facture émise",
    'journal.action.finance.invoice_changed': "Facture modifiée",
    'journal.action.finance.invoice_cancelled': "Facture annulée",
    'journal.action.finance.discount_granted': "Rabais accordé",
    'journal.action.finance.payment_recorded': "Paiement encaissé",
    'journal.action.finance.payment_cancelled': "Paiement annulé",
    'journal.filter.group.finance': "Finance",
    'violation.fin.no_pricing_level': "Choisissez au moins un niveau de tarification.",
    'violation.fin.invalid_currency': "Code de devise à trois lettres (MAD, XOF, EUR).",
    'violation.fin.currency_locked': "La devise ne change plus une fois la première facture émise.",
    'violation.fin.invalid_pattern': "Le format doit contenir un rang {N:x} et seulement des jetons connus.",
    'violation.fin.tax_billed': "Cette taxe a déjà été facturée : créez-en une nouvelle.",
    'violation.fin.in_use': "Déjà utilisé : désactivez-le plutôt.",
    'violation.fin.invalid_schedule': "De 1 à 24 versements.",
    'violation.fin.shares_incomplete': "Les parts doivent faire 100 %.",
    'violation.fin.invalid_amount': "Montant attendu.",
    'violation.fin.level_not_priced': "Ce niveau n'est pas tarifé par l'établissement.",
    'violation.fin.schedule_on_training': "L'échéancier se règle sur la formation.",
    'violation.fin.already_invoiced': "Cette inscription a déjà sa facture.",
    'violation.fin.nothing_to_bill': "Rien à facturer : la formation n'a pas de tarif.",
    'violation.fin.inactive': "Désactivé.",
    'violation.fin.on_demand_service': "Un service à la demande se facture à part.",
    'violation.fin.already_on_invoice': "Déjà sur la facture.",
    'violation.fin.training_line': "Le tarif de la formation ne se retire pas : annulez la facture.",
    'violation.fin.last_line': "Une facture garde au moins une ligne : annulez-la.",
    'violation.fin.not_stackable': "Ce rabais ne se cumule pas avec un autre.",
    'violation.fin.invoice_has_payments': "Des paiements restent sur cette facture : émettez un avoir.",
    'violation.fin.invoice_cancelled': "Cette facture est annulée.",
    'violation.fin.future_date': "Un paiement ne se date pas dans le futur.",
    'violation.fin.unlikely_date': "Date invraisemblable.",
    'violation.fin.is_reversal': "Une annulation ne s'annule pas : encaissez à nouveau.",
    'violation.fin.already_reversed': "Ce paiement est déjà annulé.",
    'violation.fin.enrolment_has_payments': "Des paiements ont été reçus sur la facture de cette inscription : déclarez un abandon.",
    // D65, D66 : gestion des enseignants (fac.) et paie des enseignants (pay.).
    'fac.none': "Aucun",
    'fac.choose': "Choisir…",
    'fac.open': "Ouvrir",
    'fac.total': "Total",
    'fac.export': "Exporter",
    'fac.period': "{from} → {to}",
    'fac.unit.hours': "En heures, par exemple 4 ou 2,5",
    'fac.unit.hoursPerMonth': "Heures par mois",
    'fac.backToTeachers': "Enseignants",
    'fac.remove.confirm': "Supprimer « {label} » ? Cette action est journalisée.",
    'fac.saved.identity': "Identité enregistrée.",
    'fac.saved.fields': "Champs disciplinaires enregistrés.",
    'fac.saved.diploma': "Diplôme enregistré.",
    'fac.saved.removed': "« {label} » supprimé.",
    'fac.civility.M': "M.",
    'fac.civility.F': "Mme",
    'fac.state.draft': "Brouillon",
    'fac.state.signed': "Signé",
    'fac.state.closed': "Clos",
    'fac.state.none': "Sans contrat",
    'fac.alert.unsigned': "Contrat non signé",
    'fac.alert.no_objective': "Aucun objectif saisi",
    'fac.alert.no_session': "Aucune séance réalisée",
    'fac.alert.no_contract': "Sans contrat cette année",
    'fac.situation.all': "Toutes les situations",
    'fac.situation.contract': "Sous contrat",
    'fac.situation.nocontract': "Sans contrat",
    'fac.situation.todo': "À régulariser",
    'fac.basis.weekly': "Par semaine",
    'fac.basis.monthly': "Par mois",
    'fac.basis.yearly': "Par année",
    'fac.basis.per.weekly': "par semaine",
    'fac.basis.per.monthly': "par mois",
    'fac.basis.per.yearly': "par année",
    'fac.service.range': "{min} à {max}",
    'fac.service.min': "au moins {value}",
    'fac.service.max': "au plus {value}",
    'fac.payMode.hourly': "À l'heure réalisée",
    'fac.payMode.monthly': "Forfait mensuel et heures complémentaires",
    'fac.payMode.none': "Non rémunéré ici",
    'fac.payMode.short.hourly': "À l'heure",
    'fac.payMode.short.monthly': "Forfait mensuel",
    'fac.payMode.short.none': "Non rémunéré",
    'fac.teachers.title': "Enseignants",
    'fac.teachers.sub': "Le contrat de l'année, l'objectif, le réalisé, ce qui reste à régulariser",
    'fac.teachers.count': "{count} enseignant(s) · année {year}",
    'fac.teachers.new': "Nouvel enseignant",
    'fac.teachers.search': "Nom, matricule, matière",
    'fac.teachers.empty': "Aucun enseignant ne correspond à ces critères.",
    'fac.kpi.underContract': "Sous contrat en {year}",
    'fac.kpi.underContractNote': "{permanent} permanent(s), {vacataire} vacataire(s)",
    'fac.kpi.objective': "Heures objectif",
    'fac.kpi.objectiveNote': "Somme des objectifs contractuels",
    'fac.kpi.realized': "Heures réalisées",
    'fac.kpi.realizedNote': "{percent} % de l'objectif",
    'fac.kpi.realizedNoteNone': "Aucun objectif saisi",
    'fac.kpi.toFix': "À régulariser",
    'fac.kpi.toFixNote': "{unsigned} contrat(s) non signé(s), {noObjective} sans objectif",
    'fac.filter.year': "Année",
    'fac.filter.category': "Catégorie",
    'fac.filter.type': "Type de contrat",
    'fac.filter.situation': "Situation",
    'fac.filter.state': "État",
    'fac.filter.allCategories': "Toutes les catégories",
    'fac.filter.allTypes': "Tous les types",
    'fac.filter.allStates': "Tous les états",
    'fac.column.teacher': "Enseignant",
    'fac.column.category': "Catégorie",
    'fac.column.contract': "Contrat",
    'fac.column.state': "État",
    'fac.column.objective': "Objectif",
    'fac.column.realized': "Réalisé",
    'fac.column.planned': "Planifié",
    'fac.column.catalog': "Maquette",
    'fac.column.progress': "Avancement",
    'fac.column.alerts': "Alertes",
    'fac.column.year': "Année",
    'fac.column.type': "Type",
    'fac.column.period': "Période",
    'fac.column.payMode': "Rémunération",
    'fac.column.service': "Service",
    'fac.column.subject': "Matière",
    'fac.column.subjectClass': "Matière et classe",
    'fac.column.teachingType': "Type d'enseignement",
    'fac.column.training': "Formation",
    'fac.column.role': "Rôle",
    'fac.create.title': "Nouvel enseignant",
    'fac.create.sub': "La personne et son matricule ; le département, les diplômes et les contrats se complètent ensuite depuis la fiche",
    'fac.create.submit': "Créer l'enseignant",
    'fac.identity.edit': "Modifier l'identité",
    'fac.field.civility': "Civilité",
    'fac.field.name': "Nom et prénom",
    'fac.field.lastName': "Nom",
    'fac.field.firstName': "Prénom",
    'fac.field.number': "Matricule",
    'fac.field.integratedOn': "Date d'intégration",
    'fac.field.department': "Département",
    'fac.field.email': "Courriel",
    'fac.field.phone': "Téléphone",
    'fac.tab.dossier': "Dossier",
    'fac.tab.contracts': "Contrats",
    'fac.tab.objectives': "Objectifs",
    'fac.tab.dashboard': "Tableau de bord",
    'fac.tab.pay': "Rémunération",
    'fac.tab.history': "Historique",
    'fac.hero.contract.draft': "Contrat {year} en brouillon",
    'fac.hero.contract.signed': "Contrat {year} signé",
    'fac.hero.contract.closed': "Contrat {year} clos",
    'fac.hero.number': "Matricule {number}",
    'fac.hero.integrated': "intégré le {date}",
    'fac.panel.identity': "Identité",
    'fac.panel.diplomas': "Diplômes",
    'fac.panel.account': "Compte Kelassi",
    'fac.panel.fields': "Champs disciplinaires",
    'fac.panel.attributions': "Attributions",
    'fac.panel.attributionsYear': "Attributions {year}",
    'fac.attributions.sub': "Les matières dont il est responsable ou intervenant, au catalogue",
    'fac.attributions.empty': "Aucune attribution au catalogue.",
    'fac.account.none': "Pas de compte Kelassi.",
    'fac.account.active': "Compte actif",
    'fac.account.inactive': "Compte désactivé",
    'fac.account.lastLogin': "dernière connexion {date}",
    'fac.account.neverLogged': "jamais connecté",
    'fac.account.open': "Voir le compte",
    'fac.fields.edit': "Champs disciplinaires",
    'fac.fields.empty': "Aucun champ disciplinaire.",
    'fac.fields.none': "Aucun champ disciplinaire n'est défini pour l'établissement.",
    'fac.diploma.add': "Ajouter un diplôme",
    'fac.diploma.edit': "Modifier le diplôme",
    'fac.diploma.remove': "Supprimer le diplôme",
    'fac.diploma.label': "Diplôme",
    'fac.diploma.school': "Établissement",
    'fac.diploma.city': "Ville",
    'fac.diploma.year': "Année",
    'fac.diploma.empty': "Aucun diplôme saisi.",
    'fac.contracts.title': "Contrats",
    'fac.contracts.sub': "Un contrat par année, ou plusieurs si la situation change en cours d'année (sans chevauchement)",
    'fac.contracts.empty': "Aucun contrat pour cet enseignant.",
    'fac.contract.new': "Nouveau contrat",
    'fac.contract.newTitle': "Nouveau contrat · {name}",
    'fac.contract.editTitle': "Contrat {year}",
    'fac.contract.title': "Contrat {year}",
    'fac.contract.dialogSub': "Le contrat fixe le service attendu et, si la paie est activée, la rémunération",
    'fac.contract.type': "Type de contrat",
    'fac.contract.category': "Catégorie",
    'fac.contract.year': "Année",
    'fac.contract.startsOn': "Début",
    'fac.contract.endsOn': "Fin",
    'fac.contract.basis': "Base du service",
    'fac.contract.minHours': "Service minimum",
    'fac.contract.maxHours': "Service maximum",
    'fac.contract.online': "Cours en ligne autorisés",
    'fac.contract.payMode': "Mode de rémunération",
    'fac.contract.note': "Note",
    'fac.contract.saveDraft': "Enregistrer en brouillon",
    'fac.contract.saveSign': "Enregistrer et marquer signé",
    'fac.contract.saved': "Contrat enregistré.",
    'fac.contract.savedSigned': "Contrat enregistré et signé.",
    'fac.contract.locked': "Un mois de paie de ce contrat est validé : les taux et le mode de rémunération sont verrouillés.",
    'fac.contract.sign': "Marquer signé",
    'fac.contract.signTitle': "Signer le contrat {year}",
    'fac.contract.signSub': "Un contrat signé compte pour la paie ; deux contrats signés d'un même enseignant ne peuvent pas se chevaucher",
    'fac.contract.signDate': "Signé le",
    'fac.contract.signed': "Contrat signé.",
    'fac.contract.signedOn': "Signé le {date}",
    'fac.contract.objectives': "objectifs contractuels : {hours}",
    'fac.contract.close': "Clore le contrat",
    'fac.contract.closed': "Contrat clos.",
    'fac.contract.reopen': "Rouvrir le contrat",
    'fac.contract.reopened': "Contrat rouvert.",
    'fac.contract.deleted': "Contrat supprimé.",
    'fac.contract.renew': "Reconduire l'année suivante",
    'fac.contract.renewed': "Contrat reconduit en brouillon pour l'année suivante.",
    'fac.contract.renewedUnmapped': "Contrat reconduit ; {count} objectif(s) sans matière correspondante l'année suivante n'ont pas été repris.",
    'fac.confirm.contract.close.title': "Clore le contrat",
    'fac.confirm.contract.close.text': "Clore le contrat {year} ? Il reste consultable et peut être rouvert.",
    'fac.confirm.contract.close.ok': "Clore",
    'fac.confirm.contract.reopen.title': "Rouvrir le contrat",
    'fac.confirm.contract.reopen.text': "Rouvrir le contrat {year} ? Il redevient signé.",
    'fac.confirm.contract.reopen.ok': "Rouvrir",
    'fac.confirm.contract.delete.title': "Supprimer le contrat",
    'fac.confirm.contract.delete.text': "Supprimer le contrat {year} et ses objectifs ? Impossible s'il a des heures réalisées ou une paie validée.",
    'fac.confirm.contract.delete.ok': "Supprimer",
    'fac.confirm.contract.renew.title': "Reconduire le contrat",
    'fac.confirm.contract.renew.text': "Copier le contrat {year} sur l'année suivante, en brouillon, avec ses objectifs ?",
    'fac.confirm.contract.renew.ok': "Reconduire",
    'fac.pay.title': "Rémunération",
    'fac.pay.monthlyGross': "Forfait mensuel brut",
    'fac.pay.monthlyNet': "Forfait mensuel net",
    'fac.pay.includedHours': "Heures comprises par mois",
    'fac.pay.extraGross': "Heure complémentaire brute",
    'fac.pay.extraNet': "Heure complémentaire nette",
    'fac.pay.extraCap': "Plafond d'heures complémentaires",
    'fac.pay.perMonth': "{hours} par mois",
    'fac.pay.monthlyNote': "Au-delà des {hours} comprises dans le forfait, chaque heure réalisée est payée au taux complémentaire, dans la limite du plafond. Les heures au-delà du plafond sont signalées, pas payées.",
    'fac.pay.noneNote': "Cet enseignant n'est pas rémunéré par Kelassi.",
    'fac.rate.base': "taux général",
    'fac.rate.extraBase': "taux complémentaire",
    'fac.rate.gross': "Taux horaire brut",
    'fac.rate.net': "Taux horaire net",
    'fac.rate.same': "Un même taux pour tous les types d'enseignement",
    'fac.rate.sameNote': "Un même taux pour tous les types d'enseignement.",
    'fac.rate.teachingType': "Type d'enseignement",
    'fac.rate.surveillance': "Surveillance d'examen",
    'fac.rate.survGross': "Surveillance, taux brut",
    'fac.rate.survNet': "Surveillance, taux net",
    'fac.rate.survHint': "Facultatif : à défaut, le taux général s'applique",
    'fac.rate.note': "Un taux par type d'enseignement est facultatif : à défaut, le taux général s'applique. Les heures payées sont celles des séances réalisées par l'enseignant (lui-même ou en remplacement), lues dans la planification.",
    'fac.document.title': "Pièce signée",
    'fac.document.none': "Aucune pièce déposée.",
    'fac.document.size': "{size} Ko",
    'fac.document.download': "Télécharger",
    'fac.document.upload': "Déposer le contrat signé",
    'fac.document.replace': "Remplacer",
    'fac.document.hint': "PDF, 10 Mo au plus",
    'fac.document.pdfOnly': "Seul un fichier PDF est accepté.",
    'fac.document.tooBig': "Le fichier dépasse 10 Mo.",
    'fac.document.uploaded': "Pièce déposée.",
    'fac.document.deleted': "Pièce supprimée.",
    'fac.objectives.title': "Objectifs contractuels",
    'fac.objectives.titleYear': "Objectifs contractuels · {year}",
    'fac.objectives.sub': "Ce que l'enseignant s'engage à dispenser, par matière, type d'enseignement et classe ou groupe",
    'fac.objectives.contract': "Contrat",
    'fac.objectives.noContract': "Aucun contrat : saisissez d'abord un contrat pour cette année.",
    'fac.objectives.empty': "Aucun objectif saisi.",
    'fac.objectives.service': "Service du contrat : {service}, soit {min} à {max}",
    'fac.objectives.over.weekly': "sur {count} semaines",
    'fac.objectives.over.monthly': "sur {count} mois",
    'fac.objectives.over.yearly': "sur l'année",
    'fac.objectives.inRange': "Objectif de {hours} dans le service du contrat",
    'fac.objectives.below': "Objectif de {hours} sous le service minimum",
    'fac.objectives.above': "Objectif de {hours} au-delà du service maximum",
    'fac.objectives.overCatalog': "au-delà du volume de la maquette",
    'fac.objectives.notPlanned': "non planifié",
    'fac.objectives.fromAttributions': "Reprendre les attributions",
    'fac.objectives.fromAttributionsDone': "Objectifs créés depuis les attributions : {count}.",
    'fac.objectives.add': "Ajouter un objectif",
    'fac.objectives.edit': "Modifier l'objectif",
    'fac.objectives.remove': "Supprimer l'objectif",
    'fac.objectives.saved': "Objectif enregistré.",
    'fac.objectives.deleted': "Objectif supprimé.",
    'fac.objectives.dialogSub': "Une matière, un type d'enseignement, une classe ou toute la formation, un volume",
    'fac.objectives.subject': "Matière",
    'fac.objectives.class': "Classe ou groupe",
    'fac.objectives.classHint': "Facultatif",
    'fac.objectives.allClasses': "Toute la formation",
    'fac.objectives.hours': "Volume",
    'fac.objectives.responsibleNote': "L'enseignant est aussi inscrit comme intervenant de la matière au catalogue, s'il ne l'est pas.",
    'fac.objectives.note': "Ajouter un objectif inscrit aussi l'enseignant comme intervenant de la matière au catalogue, s'il ne l'est pas. La planification propose en priorité les enseignants qui ont un objectif sur la matière et la classe.",
    'fac.dash.realized': "Réalisé",
    'fac.dash.realizedNote': "sur {hours} d'objectif · {percent} %",
    'fac.dash.noObjective': "aucun objectif saisi",
    'fac.dash.plannedRemaining': "Planifié restant",
    'fac.dash.plannedRemainingNote': "séances à venir, jusqu'à la fin du contrat",
    'fac.dash.toPlan': "Reste à planifier",
    'fac.dash.toPlanNote': "objectif non couvert par la planification",
    'fac.dash.month': "Ce mois-ci",
    'fac.dash.monthNote': "séances réalisées : {count}",
    'fac.dash.byMonth': "Heures par mois",
    'fac.dash.byMonthSub': "Séances planifiées et réalisées, toutes matières",
    'fac.dash.noMonth': "Aucune séance cette année.",
    'fac.dash.planned': "planifié",
    'fac.dash.done': "réalisé",
    'fac.dash.bySubject': "Par matière",
    'fac.dash.noSubject': "Aucune matière.",
    'fac.dash.todo': "À suivre",
    'fac.dash.todoEmpty': "Rien à signaler.",
    'fac.dash.group.roll_missing': "{count} appels non faits",
    'fac.dash.group.marks_missing': "{count} évaluations dont les notes sont à saisir",
    'fac.dash.group.replacement': "{count} séances données en remplacement",
    'fac.dash.group.cancelled': "{count} séances annulées",
    'fac.dash.group.out_of_contract': "{count} séances hors contrat",
    'fac.dash.see': "Voir",
    'fac.dash.payDetail': "Détail",
    'fac.dash.paid': "Payé sur l'année",
    'fac.dash.paidNote': "net {net} · mois payés : {count}",
    'fac.dash.current': "{month}",
    'fac.dash.currentNote': "net {net} · {state}",
    'fac.contractsList.title': "Contrats",
    'fac.contractsList.sub': "Les contrats de l'année, tous enseignants confondus",
    'fac.contractsList.count': "{count} contrat(s) · année {year}",
    'fac.contractsList.empty': "Aucun contrat ne correspond à ces critères.",
    'fac.settings.title': "Gestion des enseignants · réglages",
    'fac.settings.sub': "Le module Paie, ses règles, et les listes des contrats",
    'fac.settings.types': "Types de contrat",
    'fac.settings.categories': "Catégories d'enseignant",
    'fac.settings.permanent': "Permanent",
    'fac.settings.permanentHint': "Contrat permanent (CDI, titulaire)",
    'fac.settings.active': "Proposé dans les listes",
    'fac.settings.inactive': "masqué",
    'fac.settings.label': "Libellé",
    'fac.settings.addType': "Ajouter un type de contrat",
    'fac.settings.addCategory': "Ajouter une catégorie",
    'fac.settings.edit': "Modifier « {label} »",
    'fac.settings.removeType': "Supprimer le type de contrat",
    'fac.settings.removeCategory': "Supprimer la catégorie",
    'fac.settings.used': "Utilisé : ne peut pas être supprimé",
    'fac.settings.empty': "Liste vide.",
    'fac.settings.saved': "Réglages enregistrés.",
    'pay.state.computed': "Calculé",
    'pay.state.validated': "Validé",
    'pay.state.paid': "Payé",
    'pay.state.nothing': "Rien à payer",
    'pay.state.blocked': "Bloqué",
    'pay.blocker.rolls_missing': "Appels non faits",
    'pay.blocker.no_contract': "Sans contrat",
    'pay.blocker.draft_contract': "Contrat en brouillon",
    'pay.blocker.out_of_contract': "Séances hors contrat",
    'pay.blocker.over_cap': "Au-delà du plafond",
    'pay.blocker.no_rate': "Taux manquant",
    'pay.filter.all': "Tous",
    'pay.filter.computed': "À valider",
    'pay.filter.validated': "Validés",
    'pay.filter.paid': "Payés",
    'pay.method.transfer': "Virement",
    'pay.method.cheque': "Chèque",
    'pay.method.cash': "Espèces",
    'pay.method.other': "Autre",
    'pay.month.title': "Paie des enseignants",
    'pay.month.sub': "Les heures réalisées du mois, au taux du contrat : validez, puis marquez payé",
    'pay.month.previous': "Mois précédent",
    'pay.month.next': "Mois suivant",
    'pay.month.global': "État global",
    'pay.month.export': "Exporter",
    'pay.month.empty': "Aucun enseignant à payer ce mois-ci.",
    'pay.month.teachers': "Enseignants : {count}",
    'pay.month.note': "Valider fige le mois pour l'enseignant : heures, taux, ajustements. « Marquer payé » note la date, le mode et la référence du virement ; Kelassi ne fait pas le virement. Les mois validés s'exportent pour le logiciel de paie.",
    'pay.kpi.gross': "Brut du mois",
    'pay.kpi.grossNote': "net {net}",
    'pay.kpi.hours': "Heures réalisées",
    'pay.kpi.hoursNote': "dont {hours} complémentaires",
    'pay.kpi.toValidate': "À valider",
    'pay.kpi.toValidateNote': "{validated} validé(s) · {paid} payé(s)",
    'pay.kpi.blocked': "Avant de valider",
    'pay.kpi.blockedNote': "enseignants avec des points bloquants",
    'pay.column.month': "Mois",
    'pay.column.hours': "Heures",
    'pay.column.realized': "Heures réalisées",
    'pay.column.extra': "Compl.",
    'pay.column.grossIn': "Brut ({currency})",
    'pay.column.netIn': "Net ({currency})",
    'pay.column.state': "État",
    'pay.column.line': "Séances réalisées",
    'pay.column.classes': "Classe",
    'pay.column.sessions': "Séances",
    'pay.column.rate': "Taux brut",
    'pay.column.amount': "Montant brut",
    'pay.column.adjustment': "Ajustement",
    'pay.column.reason': "Motif",
    'pay.sessions': "séances : {count}",
    'pay.line.extra': "heures complémentaires",
    'pay.line.included': "dont {hours} comprises dans le forfait",
    'pay.selectAll': "Tout sélectionner",
    'pay.validate': "Valider le mois",
    'pay.validateSelection': "Valider la sélection ({count})",
    'pay.validated': "Mois validé(s) : {count}.",
    'pay.markPaid': "Marquer payé ({count})",
    'pay.paid': "États marqués payés : {count}.",
    'pay.reopen': "Rouvrir",
    'pay.reopen.title': "Rouvrir l'état validé",
    'pay.reopen.sub': "Le mois redevient calculé ; états concernés : {count}",
    'pay.reopened': "États rouverts : {count}.",
    'pay.unpay': "Annuler le paiement",
    'pay.unpay.title': "Annuler le paiement",
    'pay.unpay.sub': "L'état redevient validé, non payé ; états concernés : {count}",
    'pay.unpaid': "Paiements annulés : {count}.",
    'pay.reason.label': "Motif",
    'pay.reason.required': "Le motif est obligatoire.",
    'pay.pay.title': "Marquer payé",
    'pay.pay.sub': "États validés concernés : {count}. Kelassi ne fait pas le virement.",
    'pay.pay.submit': "Marquer payé",
    'pay.pay.date': "Payé le",
    'pay.pay.method': "Mode",
    'pay.pay.reference': "Référence",
    'pay.pay.referenceHint': "Numéro de virement ou de chèque, facultatif",
    'pay.openTeacher': "Ouvrir la fiche",
    'pay.detail': "Détail",
    'pay.statement.pdf': "État de vacation",
    'pay.statement.preview': "Aperçu de l'état",
    'pay.statement.number': "État {number}",
    'pay.statement.validated': "validé le {date} par {who}",
    'pay.statement.paid': "payé le {date} ({method} {reference})",
    'pay.gross': "Brut",
    'pay.net': "Net",
    'pay.extraHours': "dont {hours} complémentaires",
    'pay.unpaidHours': "{hours} non payées",
    'pay.replacementFor': "en remplacement de {name}",
    'pay.outOfContract': "hors contrat",
    'pay.teacher.months': "Mois",
    'pay.teacher.noMonth': "Aucun mois cette année.",
    'pay.teacher.detailTitle': "{month} · détail",
    'pay.teacher.contract': "Contrat {year}",
    'pay.teacher.noContract': "Aucun contrat en vigueur ce mois-ci",
    'pay.teacher.noLine': "Aucune séance payée ce mois-ci.",
    'pay.teacher.sessions': "Séances du mois ({count})",
    'pay.teacher.note': "Le montant se recalcule tant que le mois n'est pas validé : une séance passée réalisée, annulée ou réattribuée le met à jour. Un mois validé est figé ; une correction passe par un ajustement du mois suivant.",
    'pay.adjustment.add': "Ajouter un ajustement",
    'pay.adjustment.edit': "Modifier l'ajustement",
    'pay.adjustment.remove': "Supprimer l'ajustement",
    'pay.adjustment.sub': "Prime ou retenue sur {month}, motivée",
    'pay.adjustment.kind.bonus': "Prime",
    'pay.adjustment.kind.deduction': "Retenue",
    'pay.adjustment.gross': "Montant brut",
    'pay.adjustment.net': "Montant net",
    'pay.adjustment.hint': "Seulement tant que le mois n'est pas validé ; une correction d'un mois validé va sur le mois suivant.",
    'pay.adjustment.saved': "Ajustement enregistré.",
    'pay.adjustment.deleted': "Ajustement supprimé.",
    'pay.settings.module': "Module",
    'pay.settings.reserved': "Réservé au Super Administrateur",
    'pay.settings.enabled': "Paie des enseignants",
    'pay.settings.enabledHint': "Active l'onglet Rémunération des fiches, la paie du mois et les taux dans les contrats. Éteinte : rien n'est effacé, tout est masqué.",
    'pay.settings.enabledOn': "Activée le {date} par {who}",
    'pay.settings.rules': "Règles de paie",
    'pay.settings.payReplacements': "Payer les séances réalisées à l'enseignant qui les a données (le remplaçant, pas l'enseignant prévu)",
    'pay.settings.paySurveillance': "Payer la surveillance des examens, au taux « Surveillance » du contrat",
    'pay.settings.requireRolls': "Ne pas valider un mois tant que l'enseignant a des appels non faits ce mois-là",
    'pay.settings.currency': "Monnaie",
    'pay.settings.currencyHint': "Celle de l'établissement",
    'pay.settings.decimals': "Arrondi des montants",
    'pay.settings.decimals2': "Au centime",
    'pay.settings.decimals0': "À l'unité",
    'pay.settings.pattern': "Numérotation des états",
    'pay.settings.insert': "Insérer",
    'pay.settings.saved': "Réglages de la paie enregistrés.",
    'pay.modules.name': "Paie des enseignants",
    'pay.modules.rules': "Règles de paie et listes des contrats",
    'violation.fac.contract_overlap': "Ce contrat chevauche un autre contrat de l'enseignant.",
    'violation.pay.contract_locked': "Un mois de paie de ce contrat est validé : taux et mode verrouillés.",
    'violation.pay.rolls_missing': "Des appels ne sont pas faits ce mois-ci : complétez-les avant de valider.",
    'violation.com.out_of_scope': "Ce destinataire n'est pas dans votre périmètre : vous ne pouvez notifier que les étudiants que vous enseignez.",
    'violation.com.not_chattable': "Cette personne ne peut pas être ajoutée à une conversation.",
    'violation.com.not_a_group': "Cette action ne vaut que pour une conversation de groupe.",
    'violation.com.type_not_allowed': "Vous ne pouvez pas envoyer ce type de notification.",
    'violation.com.no_year': "Aucune année universitaire en cours.",
    'violation.com.no_recipient': "Aucun destinataire n'a de compte actif : invitez d'abord les étudiants au portail.",
    'violation.com.nothing_to_remind': "Tous les destinataires ont lu la notification.",
    'violation.com.replies_closed': "Cette notification n'accepte pas de réponse.",
    'violation.com.no_reply': "L'étudiant n'a pas encore répondu.",
    'violation.com.too_many_attachments': "Trois pièces jointes au plus.",
    'violation.com.unknown_attachment': "Pièce jointe introuvable : déposez-la à nouveau.",
    'violation.com.system_type': "Le type « Kelassi » est réservé aux alertes de l'application.",
    'violation.com.type_used': "Ce type a déjà servi : désactivez-le plutôt que de le supprimer.",
    'com.allRead': "Tout est marqué comme lu.",
    'com.archive': "Archiver",
    'com.unarchive': "Désarchiver",
    'com.archived': "Notification archivée : elle ne s'affiche plus que dans « Archivées ».",
    'com.unarchived': "Notification désarchivée.",
    'com.backToList': "Retour aux notifications",
    'com.bell.all': "Toutes les notifications",
    'com.bell.empty': "Rien de nouveau.",
    'com.bell.hint': "Les notifications lues n'apparaissent plus ici",
    'com.bell.label': "Notifications non lues : {count}",
    'com.bell.none': "Notifications : aucune non lue",
    'com.bell.title': "Notifications non lues",
    'com.bell.unread': "Non lues · {count}",
    'com.box.label': "Boîte",
    'com.box.received': "Reçues",
    'com.box.sent': "Envoyées",
    'com.box.archived': "Archivées",
    'com.chip.remove': "Retirer {label}",
    'com.column.class': "Classe",
    'com.column.fromSubject': "De et objet",
    'com.column.read': "Lue",
    'com.column.received': "Reçue",
    'com.column.sent': "Envoyée",
    'com.column.reply': "Réponse",
    'com.column.state': "État",
    'com.column.student': "Étudiant",
    'com.column.type': "Type",
    'com.compose.addTarget': "Ajouter une formation, une classe, un groupe ou un élève…",
    'com.compose.body': "Message",
    'com.compose.bodyPlaceholder': "Le message apparaîtra ici.",
    'com.compose.countLine': "Étudiants qui recevront la notification : {students} · avec une adresse électronique : {emails}",
    'com.compose.draftTitle': "Brouillon de notification",
    'com.compose.draftTrail': "Brouillon",
    'com.compose.email': "Envoyer aussi par courriel",
    'com.compose.emailCount': "Envoyer aussi par courriel (adresses : {count})",
    'com.compose.incomplete': "Choisissez un type, un objet, un message et au moins un destinataire.",
    'com.compose.new': "Nouvelle notification",
    'com.compose.noMatch': "Aucune formation, classe ou élève ne correspond.",
    'com.compose.noTarget': "Ajoutez au moins un destinataire.",
    'com.compose.note': "Les étudiants ne peuvent pas écrire les premiers : ils répondent seulement à une notification qui l'autorise. Chaque réponse n'est vue que par l'expéditeur et les gestionnaires de la notification.",
    'com.compose.panel': "Notification",
    'com.compose.preview': "Aperçu chez l'étudiant",
    'com.compose.recipients': "Destinataires",
    'com.compose.replies': "Les étudiants peuvent répondre",
    'com.compose.repliesNone': "Pas de réponse (imposé par le type {type})",
    'com.compose.repliesRequired': "Les étudiants peuvent répondre (imposé par le type {type})",
    'com.compose.saveDraft': "Enregistrer le brouillon",
    'com.compose.saved': "Brouillon enregistré.",
    'com.compose.send': "Envoyer",
    'com.compose.sendTo': "Envoyer aux étudiants ({count})",
    'com.compose.studentsCount': "étudiants : {count}",
    'com.compose.sub': "Aux étudiants : par élève, classe, groupe ou formation",
    'com.compose.subject': "Objet",
    'com.compose.subjectPlaceholder': "Objet de la notification",
    'com.compose.title': "Nouvelle notification",
    'com.compose.trail': "Nouvelle",
    'com.compose.type': "Type de notification",
    'com.draft': "Brouillon",
    'com.file.attach': "Joindre un fichier",
    'com.file.hint': "PDF ou image, 5 Mo au plus, 3 fichiers",
    'com.file.image': "Image",
    'com.file.remove': "Retirer {name}",
    'com.file.tooBig': "{name} dépasse 5 Mo.",
    'com.file.tooMany': "Pièces jointes : {count} au plus.",
    'com.file.uploading': "Envoi du fichier…",
    'com.file.wrongType': "{name} : seuls les PDF, PNG et JPEG sont acceptés.",
    'com.filter.allTypes': "Tous les types",
    'com.filter.period': "Période",
    'com.filter.type': "Type",
    'com.handled': "traité",
    'com.inbox.empty': "Aucune notification.",
    'com.inbox.emptyUnread': "Aucune notification non lue.",
    'com.inbox.search': "Rechercher",
    'com.inbox.sub': "Tout ce que vous avez reçu et envoyé · les lues restent ici, elles ne s'affichent plus dans la cloche",
    'com.inbox.title': "Notifications",
    'com.invite.button': "Inviter au portail",
    'com.invite.done': "Invitations envoyées : {invited} · sans adresse électronique : {noEmail} · compte déjà existant : {existing}",
    'com.invite.selectAll': "Sélectionner les étudiants de la page",
    'com.invite.selection': "Inviter au portail ({count})",
    'com.invite.submit': "Envoyer les invitations",
    'com.invite.textClass': "Les étudiants de la classe {name} qui ont une adresse électronique et pas encore de compte recevront un lien d'activation. Ils pourront lire leurs notifications et y répondre.",
    'com.invite.textSelection': "Étudiants sélectionnés : {count}. Ceux qui ont une adresse électronique et pas encore de compte recevront un lien d'activation. Ils pourront lire leurs notifications et y répondre.",
    'com.invite.textStudent': "{name} recevra un lien d'activation à son adresse électronique, s'il n'a pas encore de compte. Il pourra lire ses notifications et y répondre.",
    'com.invite.textTraining': "Les étudiants inscrits en {name} qui ont une adresse électronique et pas encore de compte recevront un lien d'activation.",
    'com.invite.title': "Inviter au portail",
    'com.kpi.emails': "Courriels",
    'com.kpi.emailsNote': "envoyés · en échec : {count}",
    'com.kpi.read': "Lue",
    'com.kpi.readNote': "{percent} % · non lues : {count}",
    'com.kpi.replies': "Réponses",
    'com.kpi.repliesNote': "à traiter : {count}",
    'com.kpi.sentTo': "Envoyée à",
    'com.kpi.sentToNote': "étudiants · {date}",
    'com.live.on': "Temps réel actif",
    'com.live.onHint': "Serveur de diffusion en ligne · repli : actualisation toutes les 30 s",
    'com.live.polling': "Actualisation toutes les 30 s",
    'com.live.pollingHint': "Le serveur de diffusion ne répond pas ou n'est pas installé : la cloche se met à jour toutes les 30 s.",
    'com.markAllRead': "Tout marquer comme lu",
    'com.message': "Message envoyé",
    'com.messages.archive': "Archiver la conversation",
    'com.messages.back': "Retour aux conversations",
    'com.messages.conversations': "Conversations",
    'com.messages.empty': "Aucune conversation. Commencez-en une avec un collègue.",
    'com.messages.filter.all': "Toutes",
    'com.messages.filter.unread': "Non lues",
    'com.messages.filter.groups': "Groupes",
    'com.messages.groupName': "Nom du groupe",
    'com.messages.groupOf': "groupe · personnes : {count}",
    'com.messages.leave': "Quitter le groupe",
    'com.messages.leaveConfirm': "Vous ne recevrez plus les messages de « {name} ». Les messages déjà échangés restent visibles des autres participants.",
    'com.messages.members': "Participants",
    'com.messages.new': "Nouvelle conversation",
    'com.messages.noMatch': "Aucune conversation ne correspond.",
    'com.messages.noMessage': "Aucun message",
    'com.messages.older': "Messages précédents",
    'com.messages.online': "en ligne : {count}",
    'com.messages.onlineOne': "en ligne",
    'com.messages.pick': "Choisissez une conversation, ou commencez-en une nouvelle.",
    'com.messages.placeholder': "Écrire un message… (Entrée pour envoyer, Maj + Entrée pour aller à la ligne)",
    'com.messages.readBy': "lu par {count}",
    'com.messages.rename': "Renommer le groupe",
    'com.messages.search': "Rechercher une personne, un message",
    'com.messages.start': "Aucun message pour l'instant : écrivez le premier.",
    'com.messages.sub': "Entre membres du personnel et enseignants · en temps réel",
    'com.messages.title': "Messagerie",
    'com.messages.typing': "{names} écrit…",
    'com.messages.unreadCount': "Messages non lus : {count}",
    'com.messages.you': "vous",
    'com.more': "Afficher plus",
    'com.newConv.choose': "Choisir",
    'com.newConv.function': "Fonction",
    'com.newConv.groupName': "Nom du groupe (facultatif, à partir de 3 personnes)",
    'com.newConv.none': "Personne ne correspond.",
    'com.newConv.note': "Les étudiants n'apparaissent pas ici : on leur écrit par une notification. Un enseignant peut écrire aux autres enseignants et à l'administration.",
    'com.newConv.person': "Personne",
    'com.newConv.required': "Choisissez au moins une personne.",
    'com.newConv.search': "Nom, fonction",
    'com.newConv.sub': "Choisissez une ou plusieurs personnes",
    'com.newConv.submit': "Commencer la conversation",
    'com.newConv.title': "Nouvelle conversation",
    'com.period.all': "Toute période",
    'com.period.week': "Cette semaine",
    'com.period.month': "Ce mois-ci",
    'com.period.year': "Cette année",
    'com.read': "lu",
    'com.recipient.emailFailed': "courriel en échec",
    'com.recipient.readAt': "lue {time}",
    'com.recipient.seeReply': "Voir la réponse",
    'com.recipient.unread': "non lue",
    'com.recipients': "Destinataires",
    'com.recipients.all': "Tous ({count})",
    'com.recipients.unread': "Non lues ({count})",
    'com.recipients.replied': "Avec réponse ({count})",
    'com.recipients.empty': "Aucun destinataire dans cette vue.",
    'com.remind': "Relancer les non-lus ({count})",
    'com.remind.done': "Relance envoyée aux étudiants : {count}.",
    'com.remind.submit': "Relancer",
    'com.remind.text': "Les étudiants qui n'ont pas lu la notification ({count}) la recevront à nouveau, par courriel aussi si l'original en avait un.",
    'com.remind.title': "Relancer les non-lus",
    'com.replies.empty': "Aucune réponse pour l'instant.",
    'com.replies.handle': "Marquer traité",
    'com.replies.unhandle': "Rouvrir",
    'com.replies.pick': "Choisissez une réponse dans la liste des destinataires.",
    'com.replies.placeholder': "Répondre à {name}…",
    'com.replies.private': "Réponse privée : seul l'étudiant la voit",
    'com.replies.title': "Réponses · {name}",
    'com.replies.none': "non",
    'com.replies.optional': "au choix",
    'com.replies.required': "imposée",
    'com.replies.noneLong': "Pas de réponse",
    'com.replies.optionalLong': "Au choix de l'expéditeur",
    'com.replies.requiredLong': "Toujours permise",
    'com.repliesAllowed': "réponses permises",
    'com.repliesClosed': "sans réponse",
    'com.reply.action': "Répondre",
    'com.reply.none': "Cette notification n'attend pas de réponse.",
    'com.reply.placeholder': "Répondre…",
    'com.send': "Envoyer",
    'com.sentBy': "Envoyée par {name}",
    'com.senders.staff': "personnel",
    'com.senders.staff_teachers': "personnel, enseignants",
    'com.senders.direction': "direction",
    'com.settings.active': "Type proposé à l'envoi",
    'com.settings.addType': "Ajouter un type",
    'com.settings.attachments': "Pièces jointes",
    'com.settings.attachmentsMb': "PDF et images, {count} Mo",
    'com.settings.digest': "Copie par courriel des messages non lus après 24 h (personnel et enseignants)",
    'com.settings.editType': "Modifier « {label} »",
    'com.settings.emailDefault': "Courriel par défaut",
    'com.settings.emailDefaultHint': "Proposer l'envoi par courriel par défaut",
    'com.settings.inactive': "inactif",
    'com.settings.keep': "Conservation et diffusion",
    'com.settings.label': "Libellé",
    'com.settings.removeConfirm': "Supprimer le type « {label} » ? Les notifications déjà envoyées le gardent.",
    'com.settings.removeType': "Supprimer le type",
    'com.settings.replies': "Réponse",
    'com.settings.retention': "Conservation des messages",
    'com.settings.retention5': "5 ans, comme la traçabilité",
    'com.settings.retentionYears': "Années : {count}",
    'com.settings.rules': "Qui peut écrire à qui",
    'com.settings.saved': "Réglages de la communication enregistrés.",
    'com.settings.senders': "Qui peut l'envoyer",
    'com.settings.staffChat': "Le personnel écrit entre lui et aux enseignants",
    'com.settings.studentsFirst': "Les étudiants peuvent écrire les premiers à l'administration (désactivé : ils répondent seulement)",
    'com.settings.sub': "Les types de notification, qui peut écrire à qui, la conservation",
    'com.settings.system': "Kelassi (alertes)",
    'com.settings.teacherChat': "Les enseignants écrivent entre eux et à l'administration",
    'com.settings.teacherNotify': "Les enseignants notifient les étudiants qu'ils enseignent (leurs classes et groupes de l'année)",
    'com.settings.title': "Communication · réglages",
    'com.settings.tone': "Couleur",
    'com.settings.type': "Type",
    'com.settings.typeDeleted': "Type « {label} » supprimé.",
    'com.settings.typeSaved': "Type de notification enregistré.",
    'com.settings.types': "Types de notification",
    'com.state.label': "Lecture",
    'com.state.all': "Toutes",
    'com.state.unread': "Non lues",
    'com.state.read': "Lues",
    'com.student.noneRead': "Aucune notification lue.",
    'com.student.noneUnread': "Aucune notification non lue.",
    'com.student.read': "Notifications lues",
    'com.student.showRead': "Afficher les notifications lues",
    'com.student.sub': "Les messages de votre établissement · répondez quand c'est permis",
    'com.student.unread': "Non lues : {count}",
    'com.target.student': "Élève",
    'com.target.class': "Classe",
    'com.target.training': "Formation",
    'com.target.studentChip': "Élève · {label}",
    'com.target.classChip': "Classe · {label} ({count})",
    'com.target.trainingChip': "Formation · {label} ({count})",
    'com.time.now': "à l'instant",
    'com.time.today': "Aujourd'hui",
    'com.time.yesterday': "hier",
    'com.tone.info': "Bleu",
    'com.tone.warn': "Ambre",
    'com.tone.violet': "Violet",
    'com.tone.danger': "Rouge",
    'com.tone.muted': "Gris",
    'com.tone.ok': "Vert",
    'com.type.kelassi': "Kelassi",
    'com.type.message': "Message",
    'com.type.reply': "Réponse",
    'com.unread': "non lue",
    'rel.none': "Aucun",
    'rel.open': "Ouvrir",
    'rel.removed': "« {label} » supprimé.",
    'rel.remove.confirm': "Supprimer « {label} » ? Cette action ne peut pas être annulée.",
    'rel.backToOrganisations': "Organisations",
    'rel.backToAgreements': "Conventions",
    'rel.status.prospect': "En prospection",
    'rel.status.active': "Partenaire actif",
    'rel.status.dormant': "Sans échange depuis 12 mois",
    'rel.status.renewal': "Convention à renouveler",
    'rel.filter.allTypes': "Tous les types",
    'rel.filter.allSectors': "Tous les secteurs",
    'rel.filter.allStatuses': "Toutes les relations",
    'rel.filter.allCities': "Toutes les villes",
    'rel.filter.allOwners': "Tous les responsables",
    'rel.column.organisation': "Organisation",
    'rel.column.agreement': "Convention",
    'rel.column.agreementOrg': "Convention et organisation",
    'rel.column.end': "Fin",
    'rel.column.mainContact': "Contact principal",
    'rel.column.agreements': "Conventions",
    'rel.column.interns': "Stagiaires",
    'rel.column.relation': "Relation",
    'rel.column.period': "Période",
    'rel.column.state': "État",
    'rel.column.alumnus': "Lauréat",
    'rel.column.situation': "Situation",
    'rel.column.updated': "Mis à jour",
    'rel.kpi.active': "Partenaires actifs",
    'rel.kpi.activeNote': "sur {count} organisations",
    'rel.kpi.agreements': "Conventions en vigueur",
    'rel.kpi.agreementsNote': "{count} arrivent à échéance d'ici {days} jours",
    'rel.kpi.prospects': "Prospection",
    'rel.kpi.prospectsNote': "{count} rendez-vous ce mois-ci",
    'rel.kpi.insertion': "Lauréats en poste",
    'rel.kpi.insertionNote': "promotion {year}, à 6 mois",
    'rel.kpi.insertionNone': "pas encore d'enquête",
    'rel.dashboard.title': "Relations extérieures",
    'rel.dashboard.sub': "Partenaires, conventions, prospection et insertion des lauréats",
    'rel.dashboard.subYear': "Partenaires, conventions, prospection et insertion des lauréats · année {year}",
    'rel.dashboard.ending': "Conventions à échéance",
    'rel.dashboard.endingSub': "Dans les {days} prochains jours",
    'rel.dashboard.allAgreements': "Toutes",
    'rel.dashboard.noEnding': "Aucune convention n'arrive à échéance.",
    'rel.dashboard.bySector': "Partenaires actifs par secteur",
    'rel.dashboard.noSector': "Aucun partenaire actif pour le moment.",
    'rel.dashboard.lastExchanges': "Derniers échanges",
    'rel.dashboard.directory': "Annuaire",
    'rel.orgs.title': "Organisations",
    'rel.orgs.sub': "Entreprises, universités, associations, administrations",
    'rel.orgs.count': "{count} organisations · entreprises, universités, associations, administrations",
    'rel.orgs.search': "Nom, sigle, contact, ville",
    'rel.orgs.empty': "Aucune organisation ne correspond.",
    'rel.orgs.agreementsCount': "Conventions : {count}",
    'rel.org.new': "Nouvelle organisation",
    'rel.org.newSub': "Une entreprise, une université, une association ou une administration avec laquelle l'école travaille",
    'rel.org.editProfile': "Modifier · {name}",
    'rel.org.name': "Raison sociale",
    'rel.org.legalForm': "Forme juridique",
    'rel.org.acronym': "Sigle",
    'rel.org.type': "Type",
    'rel.org.sector': "Secteur",
    'rel.org.noSector': "Sans secteur",
    'rel.org.status': "Relation",
    'rel.org.statusHint': "Laissez « calculée » : Kelassi la déduit des conventions et des échanges.",
    'rel.org.statusComputed': "Calculée par Kelassi",
    'rel.org.owner': "Responsable à l'école",
    'rel.org.city': "Ville",
    'rel.org.website': "Site web",
    'rel.org.partnerSince': "Partenaire depuis",
    'rel.org.since': "partenaire depuis le {date}",
    'rel.org.saved': "Organisation enregistrée.",
    'rel.org.change': "Changer d'organisation",
    'rel.org.searchPlaceholder': "Tapez au moins deux lettres",
    'rel.org.interns': "Stagiaires accueillis",
    'rel.org.internsNote': "étudiants liés : {count}",
    'rel.org.alumni': "Lauréats en poste",
    'rel.org.alumniNote': "situation actuelle connue",
    'rel.org.studentsSub': "Les étudiants en stage et les anciens étudiants qui y travaillent",
    'rel.org.internsLater': "Les stages seront suivis par le module Stages : le compteur restera à zéro jusque-là.",
    'rel.org.toAlumni': "Voir les lauréats",
    'rel.tab.summary': "Synthèse",
    'rel.tab.contacts': "Contacts",
    'rel.tab.agreements': "Conventions",
    'rel.tab.exchanges': "Échanges",
    'rel.tab.students': "Étudiants et lauréats",
    'rel.tab.history': "Historique",
    'rel.tab.count': "{label} ({count})",
    'rel.contact.new': "Nouveau contact",
    'rel.contact.edit': "Modifier · {name}",
    'rel.contact.lastName': "Nom",
    'rel.contact.firstNames': "Prénoms",
    'rel.contact.email': "Adresse électronique",
    'rel.contact.phone': "Téléphone",
    'rel.contact.function': "Fonction",
    'rel.contact.main': "Contact principal de cette organisation",
    'rel.contact.mainShort': "contact principal",
    'rel.contact.alumnus': "lauréat",
    'rel.contact.none': "Aucun contact.",
    'rel.contact.saved': "Contact enregistré.",
    'rel.contact.remove': "Retirer le contact",
    'rel.exchange.note': "Noter un échange",
    'rel.exchange.new': "Noter un échange",
    'rel.exchange.edit': "Modifier l'échange",
    'rel.exchange.sub': "Un appel, un courriel, un rendez-vous, une visite, un événement ou une note",
    'rel.exchange.kind': "Nature de l'échange",
    'rel.exchange.kind.call': "Appel",
    'rel.exchange.kind.email': "Courriel",
    'rel.exchange.kind.meeting': "Rendez-vous",
    'rel.exchange.kind.visit': "Visite",
    'rel.exchange.kind.event': "Événement",
    'rel.exchange.kind.note': "Note",
    'rel.exchange.on': "Date",
    'rel.exchange.time': "Heure",
    'rel.exchange.planned': "prévu",
    'rel.exchange.plannedHint': "À venir (rendez-vous prévu)",
    'rel.exchange.subject': "Objet",
    'rel.exchange.body': "Compte rendu",
    'rel.exchange.accounts': "Pour l'école",
    'rel.exchange.addAccount': "Ajouter un participant",
    'rel.exchange.contacts': "Pour le partenaire",
    'rel.exchange.addContact': "Ajouter un contact",
    'rel.exchange.agreement': "Convention concernée",
    'rel.exchange.with': "avec {names}",
    'rel.exchange.none': "Aucun échange noté.",
    'rel.exchange.saved': "Échange enregistré.",
    'rel.exchange.remove': "Supprimer l'échange",
    'rel.exchange.journal': "Tout le journal",
    'rel.exchange.journalSub': "Les appels, courriels, rendez-vous et visites, du plus récent au plus ancien",
    'rel.exchange.older': "Plus anciens",
    'rel.agreements.title': "Conventions",
    'rel.agreements.sub': "Les accords en cours, leurs échéances et leurs reconductions",
    'rel.agreements.empty': "Aucune convention ne correspond.",
    'rel.agreements.filter.all': "Toutes",
    'rel.agreements.filter.ending': "À échéance",
    'rel.agreements.filter.draft': "Brouillons",
    'rel.agreements.filter.ended': "Échues",
    'rel.agreement.new': "Nouvelle convention",
    'rel.agreement.newFor': "Nouvelle convention · {name}",
    'rel.agreement.edit': "Modifier · {title}",
    'rel.agreement.sub': "Un accord écrit : son objet, sa durée, les engagements de chacun",
    'rel.agreement.organisation': "Organisation",
    'rel.agreement.type': "Type de convention",
    'rel.agreement.title': "Intitulé",
    'rel.agreement.startsOn': "Début",
    'rel.agreement.endsOn': "Fin",
    'rel.agreement.endsOnHint': "Vide : durée indéterminée",
    'rel.agreement.indefinite': "Durée indéterminée",
    'rel.agreement.renewal': "Reconduction",
    'rel.agreement.notice': "Préavis (mois)",
    'rel.agreement.owner': "Responsable à l'école",
    'rel.agreement.trainings': "Formations concernées",
    'rel.agreement.addTraining': "Ajouter une formation",
    'rel.agreement.commitments': "Engagements",
    'rel.agreement.commitment': "Engagement {n}",
    'rel.agreement.commitmentLabel': "Engagement",
    'rel.agreement.party': "Partie",
    'rel.agreement.quantity': "Quantité",
    'rel.agreement.addCommitment': "Ajouter un engagement",
    'rel.agreement.noCommitment': "Aucun engagement.",
    'rel.agreement.note': "Note",
    'rel.agreement.saveDraft': "Enregistrer en brouillon",
    'rel.agreement.saveSign': "Enregistrer et marquer signée",
    'rel.agreement.saved': "Convention enregistrée.",
    'rel.agreement.savedSigned': "Convention enregistrée et signée.",
    'rel.agreement.reminder': "Kelassi rappelle l'échéance au responsable {days} jours avant la fin (alerte dans la cloche) et propose de renouveler en recopiant la convention.",
    'rel.agreement.none': "Aucune convention.",
    'rel.agreement.state.draft': "brouillon",
    'rel.agreement.state.signed': "en vigueur",
    'rel.agreement.state.ended': "échue",
    'rel.agreement.state.terminated': "résiliée",
    'rel.agreement.endsIn': "échéance dans {days} j",
    'rel.agreement.from': "depuis le {date}",
    'rel.agreement.details': "La convention",
    'rel.agreement.signedOn': "Signée le",
    'rel.agreement.sign': "Marquer signée",
    'rel.agreement.signed': "Convention signée.",
    'rel.agreement.end': "Clore à son terme",
    'rel.agreement.endConfirm': "La convention passe à « échue ». Elle reste consultable et peut être renouvelée.",
    'rel.agreement.ended': "Convention close.",
    'rel.agreement.terminate': "Résilier",
    'rel.agreement.terminated': "Convention résiliée.",
    'rel.agreement.reason': "Motif",
    'rel.agreement.renew': "Renouveler",
    'rel.agreement.renewConfirm': "Kelassi recopie la convention en un nouveau brouillon qui commence le lendemain de sa fin ({date}). Vous le relisez avant de le signer.",
    'rel.agreement.renewed': "Nouveau brouillon créé.",
    'rel.agreement.renewedFrom': "Renouvelle :",
    'rel.agreement.renewedBy': "Renouvelée par :",
    'rel.agreement.delete': "Supprimer le brouillon",
    'rel.agreement.lifecycle': "Cycle de la convention",
    'rel.renewal.none': "Sans reconduction",
    'rel.renewal.tacit': "Tacite",
    'rel.renewal.explicit': "Expresse",
    'rel.renewal.withNotice': "{renewal}, préavis de {months} mois",
    'rel.party.school': "L'école",
    'rel.party.partner': "Le partenaire",
    'rel.document.title': "Convention signée",
    'rel.document.hint': "PDF, 10 Mo au plus",
    'rel.document.none': "Aucune pièce déposée.",
    'rel.document.upload': "Déposer la convention signée",
    'rel.document.replace': "Remplacer la pièce",
    'rel.document.remove': "Retirer la pièce",
    'rel.document.uploaded': "Pièce déposée.",
    'rel.document.removed': "Pièce retirée.",
    'rel.document.tooBig': "Le fichier dépasse 10 Mo.",
    'rel.prospects.title': "Prospection",
    'rel.prospects.sub': "Les organisations que l'école cherche à gagner comme partenaires · glisser une carte pour changer d'étape",
    'rel.prospects.subRead': "Les organisations que l'école cherche à gagner comme partenaires",
    'rel.prospects.all': "Toutes",
    'rel.prospects.mine': "Les miennes",
    'rel.prospects.noStage': "Aucune étape de prospection : ajoutez-en dans les réglages.",
    'rel.prospects.emptyStage': "Aucune piste",
    'rel.prospects.note': "Chaque étape garde la date et l'auteur ; « Partenaire » propose de créer la convention. Une piste sans échange depuis {days} jours est signalée à son responsable.",
    'rel.prospect.new': "Ajouter une piste",
    'rel.prospect.newSub': "Une organisation à gagner comme partenaire, et ce que l'école en attend",
    'rel.prospect.organisation': "Organisation",
    'rel.prospect.mode.existing': "Dans l'annuaire",
    'rel.prospect.mode.new': "Nouvelle organisation",
    'rel.prospect.objective': "Objectif",
    'rel.prospect.objectiveHint': "Par exemple : 4 stages d'audit au printemps",
    'rel.prospect.owner': "Responsable",
    'rel.prospect.ownerMe': "Moi",
    'rel.prospect.stage': "Étape",
    'rel.prospect.saved': "Piste ajoutée.",
    'rel.prospect.addHere': "Ajouter",
    'rel.prospect.followedBy': "suivi : {name}",
    'rel.prospect.idle': "sans échange depuis {days} j",
    'rel.prospect.noExchange': "aucun échange",
    'rel.prospect.lastExchange': "dernier échange : {date}",
    'rel.prospect.moveMenu': "Déplacer vers… · {name}",
    'rel.prospect.moveTo': "Déplacer vers « {stage} »",
    'rel.prospect.moved': "{name} : étape « {stage} ».",
    'rel.prospect.wonTitle': "Un nouveau partenaire",
    'rel.prospect.wonText': "{name} devient partenaire. Créer la convention maintenant ?",
    'rel.prospect.close': "Clore la piste",
    'rel.prospect.closeConfirm': "La piste {name} sort du tableau. Son historique reste dans la fiche de l'organisation.",
    'rel.prospect.closed': "Piste close.",
    'rel.alumni.title': "Lauréats",
    'rel.alumni.sub': "Le parcours professionnel des anciens étudiants et l'insertion par promotion",
    'rel.alumni.kpi.followed': "Lauréats suivis",
    'rel.alumni.kpi.followedNote': "toutes promotions",
    'rel.alumni.kpi.employed': "En poste à 6 mois",
    'rel.alumni.kpi.employedNote': "dernière promotion",
    'rel.alumni.kpi.employedYear': "promotion {year} · répondants : {count}",
    'rel.alumni.kpi.studying': "Poursuite d'études",
    'rel.alumni.kpi.studyingNote': "en France, au Maroc ou ailleurs",
    'rel.alumni.kpi.noNews': "Sans nouvelles",
    'rel.alumni.kpi.noNewsNote': "à relancer par l'enquête",
    'rel.alumni.segment.all': "Tous",
    'rel.alumni.segment.employed': "En poste",
    'rel.alumni.segment.studying': "Études",
    'rel.alumni.segment.unknown': "Sans nouvelles",
    'rel.alumni.status.employed': "en poste",
    'rel.alumni.status.studying': "études",
    'rel.alumni.status.seeking': "en recherche",
    'rel.alumni.status.other': "autre",
    'rel.alumni.status.unknown': "sans nouvelles",
    'rel.alumni.search': "Nom du lauréat",
    'rel.alumni.year': "Promotion",
    'rel.alumni.allYears': "Toutes les promotions",
    'rel.alumni.training': "Formation",
    'rel.alumni.allTrainings': "Toutes les formations",
    'rel.alumni.promotion': "Promotion {year}",
    'rel.alumni.empty': "Aucun lauréat ne correspond.",
    'rel.alumni.select': "Choisir {name}",
    'rel.alumni.selectPage': "Choisir toute la page",
    'rel.alumni.note': "Le lauréat met lui-même sa situation à jour par le lien de l'enquête ; l'école peut aussi la saisir. Les stages ne sont pas des situations : ils iront dans le module Stages.",
    'rel.position.new': "Ajouter une situation",
    'rel.position.edit': "Modifier la situation",
    'rel.position.status': "Situation",
    'rel.position.jobTitle': "Poste",
    'rel.position.programme': "Formation suivie",
    'rel.position.organisation': "Employeur ou établissement",
    'rel.position.linkOrganisation': "Choisir l'organisation dans l'annuaire",
    'rel.position.city': "Ville",
    'rel.position.country': "Pays",
    'rel.position.startsOn': "Depuis le",
    'rel.position.endsOn': "Jusqu'au",
    'rel.position.endsOnHint': "Vide : situation actuelle",
    'rel.position.current': "actuelle",
    'rel.position.today': "aujourd'hui",
    'rel.position.none': "Aucune situation connue.",
    'rel.position.saved': "Situation enregistrée.",
    'rel.position.remove': "Supprimer la situation",
    'rel.position.sourceLine': "Source : {source} · mise à jour le {date}",
    'rel.position.source.survey': "enquête",
    'rel.position.source.school': "saisie de l'école",
    'rel.position.source.legacy': "ancien Kelassi",
    'rel.survey.title': "Enquête d'insertion",
    'rel.survey.sub': "Un lien personnel par courriel, valable 60 jours, sans compte à créer",
    'rel.survey.launch': "Lancer l'enquête d'insertion",
    'rel.survey.launchSelected': "Enquête aux lauréats choisis ({count})",
    'rel.survey.scope': "Destinataires",
    'rel.survey.byYear': "Une promotion",
    'rel.survey.selected': "Les lauréats choisis ({count})",
    'rel.survey.year': "Promotion",
    'rel.survey.send': "Envoyer",
    'rel.survey.hint': "Les lauréats sans adresse électronique sont écartés et comptés. Un lauréat qui a déjà un lien valable en reçoit un nouveau.",
    'rel.survey.sent': "Enquête envoyée : {sent}.",
    'rel.survey.sentSkipped': "Enquête envoyée : {sent} ; sans adresse électronique : {skipped}.",
    'rel.insertion.title': "Enquête d'insertion",
    'rel.insertion.sub': "Dites-nous où vous en êtes depuis votre diplôme",
    'rel.insertion.hello': "Bonjour {name}, dites-nous où vous en êtes depuis votre diplôme",
    'rel.insertion.invalid': "Ce lien n'est pas valable. Vérifiez qu'il est complet, ou demandez-en un nouveau à votre école.",
    'rel.insertion.expired': "Ce lien a expiré. Demandez-en un nouveau à votre école.",
    'rel.insertion.last': "Dernière situation connue : {situation} ({date})",
    'rel.insertion.already': "Vous avez déjà répondu : une nouvelle réponse met votre situation à jour.",
    'rel.insertion.question': "Votre situation aujourd'hui",
    'rel.insertion.status.employed': "J'occupe un emploi",
    'rel.insertion.status.studying': "Je poursuis des études",
    'rel.insertion.status.seeking': "Je cherche un emploi",
    'rel.insertion.status.other': "Autre situation",
    'rel.insertion.employer': "Employeur",
    'rel.insertion.school': "Établissement",
    'rel.insertion.since': "Depuis le",
    'rel.insertion.submit': "Envoyer ma réponse",
    'rel.insertion.thanks': "Merci : votre situation est enregistrée.",
    'rel.insertion.privacy': "Votre réponse n'est lue que par votre école, pour suivre l'insertion de ses lauréats.",
    'rel.list.types': "Types d'organisation",
    'rel.list.sectors': "Secteurs d'activité",
    'rel.list.agreementTypes': "Types de convention",
    'rel.list.functions': "Fonctions des contacts",
    'rel.list.stages': "Étapes de prospection",
    'rel.list.stagesSub': "Dans l'ordre du tableau ; une étape gagnée propose la convention",
    'rel.list.won': "gagnée",
    'rel.list.lost': "abandonnée",
    'rel.list.open': "Étape en cours",
    'rel.list.wonHint': "Étape gagnée (partenaire)",
    'rel.list.lostHint': "Étape abandonnée",
    'rel.settings.title': "Relations extérieures · réglages",
    'rel.settings.sub': "Les listes du module et ses rappels",
    'rel.settings.saved': "Réglages enregistrés.",
    'rel.settings.listSaved': "Liste mise à jour.",
    'rel.settings.empty': "Liste vide.",
    'rel.settings.add': "Ajouter · {list}",
    'rel.settings.edit': "Modifier · {label}",
    'rel.settings.label': "Libellé",
    'rel.settings.position': "Ordre",
    'rel.settings.outcome': "Nature de l'étape",
    'rel.settings.active': "Proposé dans les listes",
    'rel.settings.inactive': "retiré",
    'rel.settings.used': "Utilisé : à retirer plutôt qu'à supprimer",
    'rel.settings.remove': "Supprimer l'entrée",
    'rel.settings.module': "Module Relations extérieures",
    'rel.settings.moduleSub': "Réservé au Super Administrateur",
    'rel.settings.enabled': "Module activé",
    'rel.settings.enabledHint': "Désactivé, le module disparaît du menu sans rien effacer.",
    'rel.settings.enabledOn': "Activé le {date} par {who}",
    'rel.settings.reminders': "Rappels et accès",
    'rel.settings.window': "Rappel avant l'échéance d'une convention (jours)",
    'rel.settings.windowHint': "Alerte au responsable et liste « Conventions à échéance »",
    'rel.settings.idle': "Piste sans échange signalée après (jours)",
    'rel.settings.idleHint': "Alerte au responsable de la piste",
    'rel.settings.surveyAuto': "Envoyer l'enquête d'insertion 6 mois après la remise des diplômes",
    'rel.settings.teachersRead': "Ouvrir l'annuaire des partenaires aux enseignants (lecture)",
    'rel.settings.remindersHint': "Les alertes arrivent dans la cloche quand la Communication est activée.",
    'rel.modules.title': "Modules",
    'rel.modules.sub': "Activer ou désactiver les modules optionnels de l'établissement · réservé au Super Administrateur",
    'rel.modules.finance': "Finance",
    'rel.modules.financeHint': "Facturation, encaissements, comptes des étudiants",
    'rel.modules.payroll': "Paie des enseignants",
    'rel.modules.payrollHint': "Rémunération des enseignants, paie du mois",
    'rel.modules.communication': "Communication",
    'rel.modules.communicationHint': "Messagerie du personnel, notifications aux étudiants, alertes",
    'rel.modules.relations': "Relations extérieures",
    'rel.modules.relationsHint': "Organisations, conventions, prospection, lauréats",
    'rel.modules.enabledOn': "Activé le {date} · {who}",
    'rel.modules.disabled': "Désactivé",
    'rel.modules.settings': "Réglages",
    'rel.modules.switch': "Activer le module {name}",
    'rel.modules.switchedOn': "Module {name} activé.",
    'rel.modules.switchedOff': "Module {name} désactivé.",
    'rel.modules.offTitle': "Désactiver · {name}",
    'rel.modules.offText': "Ses droits sont retirés du menu et des écrans, sans rien effacer ; le réactiver rend tout tel quel.",
    'rel.modules.off': "Désactiver",
    'rel.modules.note': "Désactiver un module retire ses droits du menu et des écrans, sans rien effacer ; le réactiver rend tout tel quel. La Communication devient elle aussi désactivable.",
    'rel.modules.financeTitle': "Finance · réglages",
    'rel.modules.back': "Modules",
    'violation.rel.inactive': "Le module Relations extérieures n'est pas activé.",
    'violation.rel.not_found': "Introuvable.",
    'violation.rel.duplicate': "Ce libellé existe déjà dans la liste.",
    'violation.rel.in_use': "Utilisé : retirez-le plutôt que de le supprimer.",
    'violation.rel.dates_order': "La fin doit suivre le début.",
    'violation.rel.not_draft': "Seul un brouillon peut faire cela.",
    'violation.rel.not_signed': "La convention n'est pas en vigueur.",
    'violation.rel.no_end': "Une convention sans fin ne se renouvelle pas : modifiez-la.",
    'violation.rel.already_renewed': "Cette convention a déjà été renouvelée.",
    'violation.rel.document_invalid': "Le fichier n'est pas un PDF.",
    'violation.rel.document_too_large': "Le fichier dépasse 10 Mo.",
    'violation.rel.organisation_required': "Choisissez une organisation ou nommez-en une nouvelle.",
    'violation.rel.already_prospect': "Cette organisation a déjà une piste ouverte.",
    'violation.rel.prospect_closed': "Cette piste est close.",
    'violation.rel.not_alumnus': "Cet étudiant n'est pas un lauréat.",
    'violation.rel.no_recipient': "Aucun lauréat à interroger.",
    'violation.rel.token_invalid': "Ce lien n'est pas valable.",
    'violation.rel.token_expired': "Ce lien a expiré.",
    'violation.rel.website_invalid': "L'adresse du site n'est pas valable.",
    'violation.rel.reason_required': "Indiquez le motif.",
    'violation.rel.stage_in_use': "Des pistes sont à cette étape : déplacez-les avant.",
    'violation.rel.last_stage': "Il faut au moins une étape de prospection.",
    'violation.rel.out_of_range': "Valeur hors des limites permises.",
    'violation.rel.unknown_list': "Liste inconnue.",
    'violation.rel.unknown_account': "Compte inconnu.",
    'violation.rel.contact_other_organisation': "Ce contact appartient à une autre organisation.",
    'brd.title': "Identité de l'établissement",
    'brd.sub': "Le logo, l'emblème et le nom qui paraissent sur les écrans, les courriels et les documents",
    'brd.history': "Historique",
    'brd.files.title': "Logo et emblème",
    'brd.files.sub': "PNG, JPEG, WebP ou SVG · 2 Mo au plus",
    'brd.logo': "Logo complet",
    'brd.emblem': "Emblème",
    'brd.deployed': "Celui du déploiement : rien n'a encore été déposé ici",
    'brd.meta': "{format} · {width} × {height} px · {weight}",
    'brd.logo.uses': "Menu ouvert, page de connexion, PDF, courriels",
    'brd.emblem.uses': "Menu réduit, onglet du navigateur, application mobile",
    'brd.replace': "Remplacer",
    'brd.download': "Télécharger",
    'brd.names.title': "Nom de l'établissement",
    'brd.names.sub': "Le nom en français est aussi celui des documents produits",
    'brd.names.fr': "Nom officiel (français)",
    'brd.names.en': "Nom officiel (anglais)",
    'brd.names.ar': "Nom officiel (arabe)",
    'brd.names.code': "Sigle",
    'brd.names.codeHint': "Fixé à l'installation : il entre dans la numérotation des factures, des reçus et des états de paie. Il remplace l'emblème s'il n'y en a pas.",
    'brd.names.saved': "Nom de l'établissement enregistré.",
    'brd.preview.title': "Aperçu, partout où il paraît",
    'brd.preview.sub': "Avec les fichiers en vigueur",
    'brd.preview.menu': "Menu ouvert et réduit",
    'brd.preview.login': "Page de connexion",
    'brd.preview.signIn': "Connexion",
    'brd.preview.pdf': "En-tête des PDF",
    'brd.preview.mail': "Courriels",
    'brd.preview.tab': "Onglet du navigateur",
    'brd.preview.tabText': "Accueil · Kelassi",
    'brd.note': "Un changement s'applique partout dès l'enregistrement : écrans, page de connexion, courriels et documents produits ensuite. Les relevés et attestations déjà produits gardent le logo de leur date ; chaque changement est tracé et se rétablit depuis l'historique.",
    'brd.upload.titleLogo': "Remplacer le logo complet",
    'brd.upload.titleEmblem': "Remplacer l'emblème",
    'brd.upload.choose': "Choisir un fichier",
    'brd.upload.formats': "PNG, JPEG, WebP ou SVG, 2 Mo au plus",
    'brd.upload.newFile': "Nouveau fichier",
    'brd.upload.fileLine': "{name} · {width} × {height} px · {weight}",
    'brd.upload.trim': "Rogner les marges vides",
    'brd.upload.rules': "Un SVG qui contient du script, une image ou un lien externe est refusé, comme un fichier de plus de 2 Mo. L'emblème doit être carré.",
    'brd.upload.useLogo': "Utiliser ce logo",
    'brd.upload.useEmblem': "Utiliser cet emblème",
    'brd.upload.doneLogo': "Nouveau logo en vigueur partout.",
    'brd.upload.doneEmblem': "Nouvel emblème en vigueur partout.",
    'brd.check.title': "Vérifications",
    'brd.check.waiting': "Choisissez un fichier : Kelassi le vérifie avant de l'utiliser.",
    'brd.check.running': "Vérification…",
    'brd.check.refused': "Fichier refusé",
    'brd.check.format': "Format accepté",
    'brd.check.size': "Taille",
    'brd.check.sizeText': "{weight}, sous la limite de 2 Mo",
    'brd.check.dimensions': "Dimensions",
    'brd.check.dimensionsText': "{width} × {height} px : net à l'écran comme à l'impression",
    'brd.check.small': "{width} × {height} px : un peu juste, {advised} px au moins sont conseillés",
    'brd.check.vector': "Image vectorielle : nette à toutes les tailles",
    'brd.check.margins': "Marges vides autour du dessin",
    'brd.check.marginsText': "Elles le font paraître petit dans le menu ; cochez « Rogner les marges vides ».",
    'brd.check.trimmed': "Rognées à l'enregistrement : {width} × {height} px",
    'brd.check.noMargins': "Le dessin occupe l'image",
    'brd.check.transparent': "Fond transparent",
    'brd.check.transparentText': "Le logo se pose sur le cadre blanc du menu et sur les documents",
    'brd.check.opaque': "Le fond n'est pas transparent : il se verra s'il n'est pas blanc",
    'brd.change.logo': "Logo complet remplacé",
    'brd.change.emblem': "Emblème remplacé",
    'brd.change.names': "Nom officiel modifié",
    'brd.change.restore': "Version rétablie",
    'brd.hist.title': "Historique de l'identité",
    'brd.hist.sub': "Chaque changement de logo, d'emblème ou de nom, avec son auteur ; rétablir crée un nouveau changement, rien ne s'efface",
    'brd.hist.empty': "Aucun changement : l'identité est celle du déploiement.",
    'brd.hist.date': "Date",
    'brd.hist.by': "Par",
    'brd.hist.change': "Changement",
    'brd.hist.preview': "Aperçu",
    'brd.hist.actions': "Actions",
    'brd.hist.version': "Version {number}",
    'brd.hist.current': "Version en vigueur",
    'brd.hist.restore': "Rétablir",
    'brd.hist.restoredFrom': "Copie de la version {number}",
    'brd.hist.restored': "Version {number} rétablie : elle est en vigueur partout.",
    'brd.hist.confirmTitle': "Rétablir la version {number} ?",
    'brd.hist.confirmText': "Son logo, son emblème et son nom redeviennent ceux des écrans, des courriels et des prochains documents. Les documents déjà produits ne changent pas.",
    'brd.hist.note': "Rétablir une version la remet en vigueur pour les écrans, les courriels et les prochains documents. Les documents déjà produits ne changent pas : leur PDF garde le logo du jour où il a été produit.",
    'journal.action.branding.changed': "Identité de l'établissement modifiée",
    'journal.target.branding': "Établissement",
    'violation.brd.file_empty': "Fichier vide.",
    'violation.brd.file_too_big': "Fichier trop lourd : 2 Mo au plus.",
    'violation.brd.file_format': "Format non accepté : PNG, JPEG, WebP ou SVG.",
    'violation.brd.file_unreadable': "Image illisible.",
    'violation.brd.not_square': "L'emblème doit être carré.",
    'violation.brd.too_small': "Image trop petite.",
    'violation.brd.svg_unsafe': "SVG refusé : il contient du script, une image ou un lien externe.",
    'violation.brd.svg_size': "SVG sans dimensions : ajoutez un viewBox.",
    'violation.brd.text': "Texte attendu.",
    'violation.brd.name_required': "Le nom officiel est obligatoire.",
    'violation.brd.name_too_long': "Nom trop long : 255 caractères au plus.",
    'violation.brd.unchanged': "Rien n'a changé.",
    'violation.brd.already_current': "Cette version est déjà en vigueur.",
    // D78 : la gestion des documents (DG-1).
    'doc.docState.valid': "Valable",
    'doc.docState.superseded': "Remplacé",
    'doc.docState.cancelled': "Annulé",
    'doc.docState.expired': "Expiré",
    'doc.verify.cancelled': "Document annulé",
    'doc.verify.expired': "Document expiré",
    'doc.verify.cancelledBody': "L'établissement l'a annulé le {date} : il ne vaut plus.",
    'doc.verify.expiredBody': "Il était valable jusqu'au {date} : demandez-en un nouveau à l'établissement.",
    'doc.verify.validUntil': "Valable jusqu'au {date}.",
    'dg.dashboard.sub': "Tableau de bord · {year} · relevés, attestations, procès-verbaux",
    'dg.kpi.produced': "Produits cette année",
    'dg.kpi.producedNote': "dont {count} sur le portail",
    'dg.kpi.todo': "À produire",
    'dg.kpi.todoNote': "repérés par Kelassi",
    'dg.kpi.downloads': "Téléchargements",
    'dg.kpi.downloadsNote': "par les étudiants, sur le portail",
    'dg.kpi.verifications': "Vérifications publiques",
    'dg.kpi.verificationsNote': "par le code QR ou la référence",
    'dg.todo.title': "À produire",
    'dg.todo.sub': "Ce que Kelassi a repéré : délibérations closes, inscriptions",
    'dg.todo.empty': "Rien à produire pour le moment.",
    'dg.todo.enrolment': "Inscriptions de l'année",
    'dg.todo.enrolmentNote': "Inscriptions sans attestation de scolarité en cours : {count} ; elles se délivrent par les Demandes.",
    'dg.todo.transcriptsNote': "Close le {date} · relevés manquants : {count}",
    'dg.batches.title': "Derniers lots",
    'dg.batches.all': "Tous les lots",
    'dg.batches.line': "Documents : {count} · {date} · {by}",
    'dg.types.title': "Par type de document",
    'dg.types.empty': "Aucun document produit cette année.",
    'dg.types.cancelled': "annulés : {count}",
    'dg.col.produced': "Produits",
    'dg.col.published': "Sur le portail",
    'dg.col.downloads': "Téléchargés",
    'dg.col.verifications': "Vérifiés",
    'dg.col.issuedOn': "Produit le",
    'dg.col.portal': "Portail",
    'dg.col.verified': "Vérifié",
    'dg.verifications.title': "Vérifications publiques",
    'dg.verifications.sub': "Sans compte, par le code QR ou la référence",
    'dg.verifications.empty': "Aucune vérification récente.",
    'dg.registry.title': "Registre des documents",
    'dg.registry.sub': "Chaque document produit, avec sa référence ; rien ne se supprime : un document se remplace ou s'annule, avec un motif",
    'dg.registry.search': "Référence, nom ou numéro d'étudiant",
    'dg.registry.allTypes': "Tous les documents",
    'dg.registry.training': "Formation",
    'dg.registry.allTrainings': "Toutes les formations",
    'dg.registry.allStates': "Tous les états",
    'dg.registry.empty': "Aucun document ne correspond.",
    'dg.registry.published': "Publié · téléch. : {count}",
    'dg.registry.notPublished': "Non publié",
    'dg.registry.pages': "Pages du registre",
    'dg.registry.count': "Documents : {count} · page {page} sur {pages}",
    'dg.action.open': "Ouvrir la fiche",
    'dg.action.pdf': "Télécharger le PDF",
    'dg.action.duplicate': "Duplicata",
    'dg.action.replace': "Remplacer",
    'dg.action.cancel': "Annuler le document",
    'dg.notice.replaced': "Un nouveau document remplace {reference}.",
    'dg.notice.cancelled': "{reference} est annulé : sa vérification le dit désormais.",
    'dg.item.sub': "{type} de {student}",
    'dg.item.cancelled': "Annulé le {date} par {by}",
    'dg.item.reason': "Motif : {reason}",
    'dg.item.replacedBy': "Remplacé par",
    'dg.item.replaces': "Ce document remplace",
    'dg.item.document': "Le document",
    'dg.item.from': "Depuis",
    'dg.item.issued': "Produit",
    'dg.item.signatories': "Signataires",
    'dg.item.validUntil': "Valable jusqu'au",
    'dg.item.digest': "Empreinte",
    'dg.item.diffusion': "Diffusion",
    'dg.item.notForPortal': "Document interne, hors portail",
    'dg.item.publishedOn': "Publié le {date} · téléchargements par l'étudiant : {count}",
    'dg.item.duplicates': "Duplicatas",
    'dg.item.duplicatesCount': "Imprimés : {count}",
    'dg.item.noVerification': "Pas encore vérifié.",
    'dg.item.verifyPage': "Ouvrir la page de vérification",
    'dg.item.history': "Historique",
    'dg.item.noPreview': "L'aperçu n'est pas disponible : le moteur PDF ne répond pas.",
    'dg.item.note': "Annuler un document demande un motif ; sa vérification publique répond alors « annulé », sans dire pourquoi. Remplacer produit un nouveau document et marque celui-ci « remplacé ».",
    'dg.event.issued': "Produit par {by}",
    'dg.event.downloaded': "Téléchargé par l'étudiant",
    'dg.event.verified': "Vérifié : {result}",
    'dg.event.duplicate': "Duplicata imprimé par {by}",
    'dg.event.published': "Publié sur le portail par {by}",
    'dg.event.unpublished': "Retiré du portail par {by}",
    'dg.event.cancelled': "Annulé par {by} : {detail}",
    'dg.event.replaced': "Remplacé par {detail} ({by})",
    'dg.replace.title': "Remplacer {reference} ?",
    'dg.replace.text': "Un nouveau document est produit, avec une nouvelle référence et les mêmes options ; celui-ci est marqué « remplacé » et sa vérification nomme le nouveau.",
    'dg.cancel.title': "Annuler {reference} ?",
    'dg.cancel.text': "Un document annulé le reste : sa vérification répond « annulé », il quitte le portail et ne se télécharge plus. Le motif reste dans le registre, il n'est jamais montré publiquement.",
    'dg.cancel.reason': "Motif de l'annulation",
    'dg.settings.title': "Diffusion et vérification",
    'dg.settings.sub': "Valables pour les documents produits ensuite",
    'dg.settings.publishDefault': "Publier sur le portail par défaut",
    'dg.settings.publishDefaultHint': "chaque production propose la case cochée",
    'dg.settings.validity': "Validité des attestations de scolarité",
    'dg.settings.validityHint': "Imprimée « Valable jusqu'au » ; au-delà, la vérification répond « expiré ».",
    'dg.settings.noLimit': "Sans limite",
    'dg.settings.months': "{count} mois",
    'dg.settings.oneMonth': "1 mois",
    'dg.settings.shortName': "Nom abrégé sur la page de vérification",
    'dg.settings.shortNameHint': "prénom et initiale du nom",
    'dg.settings.duplicateMark': "Filigrane « DUPLICATA » sur les réimpressions",
    'dg.settings.duplicateMarkHint': "le téléchargement de l'étudiant n'en porte pas",
    'rel.modules.documents': "Documents",
    'rel.modules.documentsHint': "Relevés, attestations, procès-verbaux, registre, vérification publique",
    'rel.modules.documentsOff': "Aucun document ne se produit plus, les Demandes ne délivrent plus de document d'elles-mêmes et « Mes documents » quitte le portail ; la vérification des documents déjà produits continue de répondre.",
    'violation.doc.reason_required': "Indiquez le motif de l'annulation (5 caractères au moins).",
    'violation.doc.already_cancelled': "Ce document est déjà annulé.",
    'violation.doc.not_replaceable': "Seul un document valable ou expiré se remplace.",
    'violation.doc.run_changed': "Les résultats ont changé depuis : produisez le document depuis la délibération.",
    'violation.doc.documents_off': "Le module Documents est éteint : traitez cette demande à la main.",
    'journal.action.document.cancelled': "Document annulé",
    'journal.action.document.replaced': "Document remplacé",
    // D79 : les modèles de documents (DG-2).
    'tpl.title': "Modèles de documents",
    'tpl.sub': "Les modèles que Kelassi remplit : textes, champs, tableaux, signatures, code QR ; une version par publication",
    'tpl.byTraining': "Modèles par formation",
    'tpl.duplicate': "Dupliquer un modèle",
    'tpl.new': "Nouveau modèle",
    'tpl.create': "Créer le modèle",
    'tpl.all': "Tous · {count}",
    'tpl.search': "Chercher un modèle",
    'tpl.showArchived': "Montrer les archivés",
    'tpl.empty': "Aucun modèle ne correspond.",
    'tpl.note': "Un modèle publié ne change plus : le modifier prépare sa version suivante, que l'on publie quand elle est prête. Un document produit garde la version de son jour. Les modèles standard de Kelassi se dupliquent ; les modèles de l'ancien Kelassi sont repris avec leur code.",
    'tpl.allTrainings': "Toutes",
    'tpl.verifiable': "Vérifiable par code QR",
    'tpl.invalid': "Certains blocs sont à corriger.",
    'tpl.col.template': "Modèle",
    'tpl.col.category': "Catégorie",
    'tpl.col.source': "Produit",
    'tpl.col.languages': "Langues",
    'tpl.col.paper': "Papier",
    'tpl.col.qr': "QR",
    'tpl.col.version': "Version",
    'tpl.col.trainings': "Formations",
    'tpl.badge.default': "Par défaut",
    'tpl.badge.system': "Standard",
    'tpl.badge.archived': "Archivé",
    'tpl.version.published': "v{number} publiée",
    'tpl.version.draft': "v{number} brouillon",
    'tpl.field.name': "Nom du modèle",
    'tpl.field.nameCopyHint': "Vide : le nom du modèle copié",
    'tpl.field.from': "Copier le modèle",
    'tpl.field.kind': "Ce qu'il produit",
    'tpl.field.category': "Catégorie",
    'tpl.field.paper': "Papier",
    'tpl.field.languages': "Langues",
    'tpl.field.verifiable': "Code QR et référence de vérification",
    'tpl.field.validity': "Validité",
    'tpl.field.duplicateMark': "Filigrane « DUPLICATA » sur une réimpression",
    'tpl.field.signatureRequired': "Signature requise avant diffusion",
    'tpl.field.signatureRequiredHint': "le lot attendra le signataire dans « À signer »",
    'tpl.field.font': "Police",
    'tpl.field.fontSize': "Taille du texte",
    'tpl.field.margins': "Marges",
    'tpl.font.sans': "Sans empattement",
    'tpl.font.serif': "Avec empattement (Times)",
    'tpl.validity.inherit': "Comme l'établissement",
    'tpl.size.small': "Petite",
    'tpl.size.normal': "Normale",
    'tpl.size.large': "Grande",
    'tpl.margins.narrow': "Étroites",
    'tpl.margins.normal': "Normales",
    'tpl.margins.wide': "Larges",
    'tpl.align.start': "À gauche",
    'tpl.align.center': "Centré",
    'tpl.align.end': "À droite",
    'tpl.align.justify': "Justifié",
    'tpl.align.spread': "Réparties sur la largeur",
    'tpl.layout.list': "Liste continue",
    'tpl.layout.modules': "Un tableau par module",
    'tpl.layout.table': "Tableau",
    'tpl.layout.grid': "Grille de photos",
    'tpl.summary.facts': "En lignes",
    'tpl.summary.boxes': "Dans trois cadres",
    'tpl.summary.none': "Aucun",
    'tpl.kind.transcript': "Relevé de notes",
    'tpl.kind.certificate': "Attestation de réussite",
    'tpl.kind.minutes': "Procès-verbal",
    'tpl.kind.enrolment': "Attestation de scolarité",
    'tpl.kind.card': "Carte étudiant",
    'tpl.kind.list': "Liste d'une classe",
    'tpl.kind.internship': "Document de stage",
    'tpl.category.schooling': "Scolarité",
    'tpl.category.results': "Résultats",
    'tpl.category.lists': "Listes et états",
    'tpl.category.finance': "Finance",
    'tpl.category.stages': "Stages",
    'tpl.category.teachers': "Enseignants",
    'tpl.paper.A4': "A4",
    'tpl.paper.A4-landscape': "A4 paysage",
    'tpl.paper.A5': "A5",
    'tpl.paper.CR80': "Carte CR80",
    'tpl.lang.fr': "Français",
    'tpl.lang.en': "Anglais",
    'tpl.lang.ar': "Arabe",
    'tpl.action.versions': "Versions",
    'tpl.action.pdf': "Aperçu PDF",
    'tpl.action.publish': "Publier la version {number}",
    'tpl.action.duplicate': "Dupliquer pour modifier",
    'tpl.action.makeDefault': "En faire le modèle par défaut",
    'tpl.action.archive': "Archiver ce modèle",
    'tpl.action.unarchive': "Rendre ce modèle",
    'tpl.notice.saved': "Brouillon enregistré.",
    'tpl.notice.published': "Version {number} publiée : les documents produits ensuite la prennent.",
    'tpl.editor.systemSub': "Modèle standard de Kelassi · la page imprimée telle quelle",
    'tpl.editor.systemNote': "Un modèle standard ne se modifie pas. Dupliquez-le : la copie reçoit les blocs équivalents à sa page, que vous adaptez.",
    'tpl.editor.firstDraft': "Version {number}, brouillon · pas encore publiée",
    'tpl.editor.draftSub': "Version {number}, brouillon · la version {current} reste en vigueur tant que celle-ci n'est pas publiée",
    'tpl.editor.currentSub': "Version {number} en vigueur · modifier prépare la suivante",
    'tpl.editor.unsaved': "modifications non enregistrées",
    'tpl.editor.page': "La page",
    'tpl.editor.pageHint': "Glisser pour réordonner, cliquer pour régler",
    'tpl.editor.addBlock': "Ajouter un bloc",
    'tpl.editor.up': "Monter",
    'tpl.editor.down': "Descendre",
    'tpl.editor.copyBlock': "Dupliquer le bloc",
    'tpl.editor.fields': "Champs",
    'tpl.editor.fieldsHint': "Cliquer pour insérer dans le texte en cours",
    'tpl.editor.previewWith': "Aperçu avec",
    'tpl.editor.searchSubject': "Chercher…",
    'tpl.editor.subject': "Sujet de l'aperçu",
    'tpl.editor.noSubject': "Sans données",
    'tpl.editor.selected': "Bloc choisi : {block}",
    'tpl.editor.document': "Le document",
    'tpl.editor.usage': "Utilisation",
    'tpl.editor.code': "Code",
    'tpl.editor.legacy': "Ancien Kelassi",
    'tpl.editor.defaultFor': "Toutes celles sans modèle attribué (par défaut)",
    'tpl.editor.unused': "Aucune pour l'instant",
    'tpl.publish.title': "Publier la version {number} ?",
    'tpl.publish.text': "Les documents produits ensuite prendront cette version. Ceux déjà produits gardent la leur.",
    'tpl.publish.note': "Ce qui change (facultatif)",
    'tpl.versions.title': "Versions du modèle",
    'tpl.versions.number': "Version {number}",
    'tpl.versions.draft': "brouillon",
    'tpl.versions.current': "en vigueur",
    'tpl.versions.created': "commencée le {date} par {by}",
    'tpl.versions.published': "publiée le {date} par {by}",
    'tpl.versions.restore': "Repartir de cette version",
    'tpl.versions.discard': "Abandonner le brouillon",
    'tpl.versions.discardTitle': "Abandonner le brouillon ?",
    'tpl.versions.discardText': "Ses modifications sont perdues ; la version en vigueur ne change pas.",
    'tpl.block.header': "En-tête de l'établissement",
    'tpl.block.title': "Titre",
    'tpl.block.text': "Texte",
    'tpl.block.facts': "Lignes « libellé : valeur »",
    'tpl.block.grades': "Tableau des notes",
    'tpl.block.result': "Résultat",
    'tpl.block.minutes': "Tableau du procès-verbal",
    'tpl.block.students': "Élèves de la classe",
    'tpl.block.table': "Tableau libre",
    'tpl.block.photo': "Photo de l'étudiant",
    'tpl.block.signatures': "Signatures",
    'tpl.block.stamp': "Cachet",
    'tpl.block.qr': "Code QR et référence",
    'tpl.block.footer': "Pied de page",
    'tpl.block.spacer': "Espace",
    'tpl.block.pagebreak': "Saut de page",
    'tpl.block.system': "Page standard",
    'tpl.if.always': "Toujours",
    'tpl.if.normal': "En session normale",
    'tpl.if.retake': "Après rattrapage",
    'tpl.if.credits': "Si l'inscription est en crédits",
    'tpl.if.scholarship': "Si l'étudiant est boursier",
    'tpl.if.female': "Pour une étudiante",
    'tpl.if.male': "Pour un étudiant",
    'tpl.prop.tagline': "Avec la mention sous le nom",
    'tpl.prop.align': "Alignement",
    'tpl.prop.title': "Titre",
    'tpl.prop.sub': "Lignes sous le titre",
    'tpl.prop.size': "Taille",
    'tpl.prop.text': "Texte",
    'tpl.prop.format': "Mise en forme",
    'tpl.prop.validity': "Avec la date de validité",
    'tpl.prop.layout': "Présentation",
    'tpl.prop.summary': "Résultat",
    'tpl.prop.details': "Le détail des matières",
    'tpl.prop.coefficients': "Les coefficients",
    'tpl.prop.credits': "Les crédits",
    'tpl.prop.sessions': "La session de chaque note",
    'tpl.prop.legend': "La légende des statuts",
    'tpl.prop.condensed': "Condensé (petits caractères)",
    'tpl.prop.minutesDetails': "Une colonne par semestre ou module",
    'tpl.prop.counts': "Les effectifs",
    'tpl.prop.bonuses': "Les bonis du jury",
    'tpl.prop.columns': "Colonnes",
    'tpl.prop.blank': "Lignes vides à la fin",
    'tpl.prop.factColumns': "Colonnes",
    'tpl.prop.label': "Libellé",
    'tpl.prop.value': "Valeur (vide : pointillés à remplir)",
    'tpl.prop.addLine': "Ajouter une ligne",
    'tpl.prop.border': "Avec bordures",
    'tpl.prop.column': "Colonne {number}",
    'tpl.prop.addColumn': "Ajouter une colonne",
    'tpl.prop.rows': "Lignes",
    'tpl.prop.cell': "Ligne {row}, colonne {column}",
    'tpl.prop.addRow': "Ajouter une ligne",
    'tpl.prop.made': "Avec « Fait à …, le … »",
    'tpl.prop.parties': "Qui signe",
    'tpl.prop.partiesHint': "Vide : les signataires réglés pour ce document dans les réglages.",
    'tpl.prop.party': "Partie {number}",
    'tpl.prop.addParty': "Ajouter une partie",
    'tpl.prop.qrLine': "Avec la ligne « vérifiable en ligne »",
    'tpl.prop.height': "Hauteur (px)",
    'tpl.prop.pagebreak': "La suite commence sur une nouvelle page.",
    'tpl.prop.if': "Afficher",
    'tpl.format.bold': "Gras",
    'tpl.format.italic': "Italique",
    'tpl.format.list': "Liste",
    'tpl.column.number': "Numéro",
    'tpl.column.name': "Nom et prénom",
    'tpl.column.birth': "Date de naissance",
    'tpl.column.cin': "CIN ou passeport",
    'tpl.column.email': "Courriel",
    'tpl.column.phone': "Téléphone",
    'tpl.column.signature': "Signature",
    'tpl.column.photo': "Photo",
    'tpl.group.etudiant': "Étudiant",
    'tpl.group.inscription': "Inscription",
    'tpl.group.resultats': "Résultats",
    'tpl.group.classe': "Classe",
    'tpl.group.stage': "Stage",
    'tpl.group.document': "Document",
    'tpl.assign.sub': "{year} · quel modèle chaque formation utilise, et les mentions imprimées selon la session",
    'tpl.assign.copy': "Reprendre l'année passée",
    'tpl.assign.copyTitle': "Reprendre les attributions de {year} ?",
    'tpl.assign.copyText': "Chaque formation de {year} qui a le même code qu'une formation de l'année passée, et n'a pas encore de modèle pour un document, reçoit le sien.",
    'tpl.assign.copied': "Attributions reprises : {count}.",
    'tpl.assign.empty': "Aucune formation cette année.",
    'tpl.assign.byDefault': "Par défaut",
    'tpl.assign.standard': "la page standard",
    'tpl.assign.note': "« Par défaut » : le modèle de l'établissement pour ce document. Une formation reconduite garde ses attributions ; « Reprendre l'année passée » les recopie toutes.",
    'tpl.assign.fallback': "Sans modèle attribué, la formation utilise le modèle par défaut : {name}.",
    'tpl.assign.remove': "Retirer ce modèle",
    'tpl.assign.add': "Ajouter un autre modèle",
    'tpl.assign.template': "Modèle",
    'tpl.assign.choose': "Choisir…",
    'tpl.assign.isDefault': "Proposé par défaut à la production",
    'tpl.assign.titleNormal': "Mention en session normale",
    'tpl.assign.titleHint': "Imprimée par le champ {{document.titre}}, par exemple « JUIN 2026 »",
    'tpl.assign.titleRetake': "Mention après rattrapage",
    'tpl.assign.titleCredit': "Mention d'une inscription en crédits",
    'tpl.assign.titleShared': "Titre commun",
    'tpl.assign.titleSharedHint': "Imprimé par {{document.titre_commun}}, par exemple « 4ème Année IFACG »",
    'tpl.assign.saved': "Modèles de {training} enregistrés.",
    'tpl.assign.removed': "Modèle retiré de {training}.",
    'journal.action.document.template_created': "Modèle de document créé",
    'journal.action.document.template_changed': "Modèle de document modifié",
    'journal.action.document.template_published': "Modèle de document publié",
    'journal.action.document.template_assigned': "Modèles d'une formation modifiés",
    'violation.tpl.invalid_block': "Ce bloc est incomplet ou inconnu.",
    'violation.tpl.too_many': "Une page compte 60 blocs au plus.",
    'violation.tpl.block_source': "Ce bloc n'a rien à montrer pour ce document.",
    'violation.tpl.unknown_field': "Un champ n'existe pas pour ce document : utilisez la liste des champs.",
    'violation.tpl.too_long': "Ce texte est trop long.",
    'violation.tpl.validity': "Entre 1 et 24 mois, sans limite, ou comme l'établissement.",
    'violation.tpl.kind': "Choisissez ce que le modèle produit.",
    'violation.tpl.name': "Donnez un nom au modèle.",
    'violation.tpl.languages': "Au moins une langue.",
    'violation.tpl.system': "Un modèle standard ne se modifie pas : dupliquez-le.",
    'violation.tpl.no_draft': "Aucun brouillon.",
    'violation.tpl.only_draft': "Ce modèle n'a que ce brouillon : archivez-le plutôt.",
    'violation.tpl.version': "Version introuvable.",
    'violation.tpl.not_usable': "Un modèle archivé ou jamais publié ne s'utilise pas.",
    'violation.tpl.not_archivable': "Le modèle par défaut, ou un modèle standard, ne s'archive pas.",
    'violation.tpl.wrong_kind': "Ce modèle ne produit pas ce document.",
    'violation.tpl.unpublished': "Ce modèle n'a pas encore de version publiée.",
    'violation.tpl.subject': "Choisissez un sujet d'aperçu.",
    'violation.tpl.no_previous_year': "Aucune année précédente.",
    'tpl.field.etudiant.nom_complet': "Nom complet",
    'tpl.field.etudiant.nom': "Nom",
    'tpl.field.etudiant.prenom': "Prénom",
    'tpl.field.etudiant.civilite': "Civilité (M., Mme)",
    'tpl.field.etudiant.e': "Accord féminin (« e »)",
    'tpl.field.etudiant.numero': "Numéro d'étudiant",
    'tpl.field.etudiant.date_naissance': "Date de naissance",
    'tpl.field.etudiant.cin': "CIN",
    'tpl.field.etudiant.passeport': "Passeport",
    'tpl.field.etudiant.code_massar': "Code Massar ou national",
    'tpl.field.etudiant.email': "Courriel",
    'tpl.field.etudiant.telephone': "Téléphone",
    'tpl.field.inscription.formation': "Formation",
    'tpl.field.inscription.code_formation': "Code de la formation",
    'tpl.field.inscription.parcours': "Parcours",
    'tpl.field.inscription.annee': "Année universitaire",
    'tpl.field.inscription.date': "Date d'inscription",
    'tpl.field.inscription.classe': "Classe",
    'tpl.field.inscription.regime': "Régime",
    'tpl.field.inscription.boursier': "Boursier",
    'tpl.field.resultats.session': "Session",
    'tpl.field.resultats.moyenne': "Moyenne",
    'tpl.field.resultats.decision': "Décision",
    'tpl.field.resultats.mention': "Mention",
    'tpl.field.resultats.credits': "Crédits acquis",
    'tpl.field.resultats.rang': "Rang",
    'tpl.field.resultats.date_jury': "Date du jury",
    'tpl.field.classe.nom': "Classe",
    'tpl.field.classe.formation': "Formation",
    'tpl.field.classe.code_formation': "Code de la formation",
    'tpl.field.classe.annee': "Année universitaire",
    'tpl.field.classe.effectif': "Effectif",
    'tpl.field.classe.delegue': "Délégué",
    'tpl.field.stage.type': "Type de stage",
    'tpl.field.stage.organisme': "Organisme d'accueil",
    'tpl.field.stage.tuteur': "Tuteur",
    'tpl.field.stage.fonction_tuteur': "Fonction du tuteur",
    'tpl.field.stage.encadrant': "Encadrant pédagogique",
    'tpl.field.stage.debut': "Début",
    'tpl.field.stage.fin': "Fin",
    'tpl.field.stage.duree_mois': "Durée en mois",
    'tpl.field.stage.sujet': "Sujet",
    'tpl.field.stage.missions': "Missions",
    'tpl.field.stage.lieu': "Lieu",
    'tpl.field.document.reference': "Référence",
    'tpl.field.document.date': "Date de production",
    'tpl.field.document.lieu': "Lieu de signature",
    'tpl.field.document.etablissement': "Établissement",
    'tpl.field.document.valable_jusqu_au': "Valable jusqu'au",
    'tpl.field.document.titre': "Mention de session (par formation)",
    'tpl.field.document.titre_commun': "Titre commun (par formation)",
    'doc.docState.unsigned': "À signer",
    'doc.type.card': "Carte étudiant",
    'doc.types.card': "Cartes étudiant",
    'doc.type.list': "Liste d'une classe",
    'doc.types.list': "Listes de classe",
    'doc.type.internship': "Document de stage",
    'doc.types.internship': "Documents de stage",
    'doc.verify.unsigned': "Document pas encore signé",
    'doc.verify.unsignedBody': "Il attend la signature de l'établissement : il ne vaut pas encore.",
    'doc.verify.signed': "Signé le {date} par {name}.",
    'dg.event.signed': "Signé par {by}",
    'dg.event.returned': "Renvoyé par {by} : {detail}",
    'dg.registry.batch': "Les documents d'un lot",
    'dg.registry.batchClear': "Tout le registre",
    'journal.action.document.job_started': "Production de documents lancée",
    'journal.action.document.signer_changed': "Signataire réglé",
    'journal.action.document.signed': "Documents signés",
    'journal.action.document.returned': "Lot renvoyé par le signataire",
    'prd.title': "Produire des documents",
    'prd.sub': "Un document, une source, des destinataires : Kelassi vérifie tout avant de produire",
    'prd.jobs': "Production",
    'prd.step.1': "Document",
    'prd.step.2': "Depuis",
    'prd.step.3': "Pour",
    'prd.step.4': "Vérifier et produire",
    'prd.next': "Continuer",
    'prd.back': "Retour",
    'prd.edit': "Modifier",
    'prd.kind.title': "Quel document ?",
    'prd.kind.template': "Modèle",
    'prd.kind.byTraining': "Le modèle de chaque formation",
    'prd.kind.templateHint': "Sans choix, Kelassi prend le modèle attribué à la formation, sinon le modèle par défaut.",
    'prd.from.deliberation': "depuis une délibération close",
    'prd.from.enrolment': "depuis les inscriptions",
    'prd.from.class': "depuis les classes",
    'prd.from.internship': "depuis les stages",
    'prd.source.deliberation': "Quelle délibération close ?",
    'prd.source.training': "Quelle formation ?",
    'prd.source.noDeliberation': "Aucune délibération close cette année dans vos formations.",
    'prd.source.noTraining': "Aucune formation où vous pouvez produire cette année.",
    'prd.source.run': "calcul n° {number}, clos le {date}",
    'prd.for.title': "Pour qui ?",
    'prd.for.count': "{chosen} retenus sur {total}",
    'prd.for.all': "Tous",
    'prd.for.none': "Aucun",
    'prd.for.empty': "Personne à qui produire ce document.",
    'prd.warning.no_photo': "pas de photo",
    'prd.warning.no_number': "numéro manquant",
    'prd.warning.existing': "existe déjà",
    'prd.warningHint.no_photo': "le modèle imprime la photo : produit avec un cadre vide, ou à exclure",
    'prd.warningHint.no_number': "produit sans numéro d'étudiant, ou à compléter d'abord",
    'prd.warningHint.existing': "le même document est rendu, sans nouvelle référence",
    'prd.summary.template': "modèle « {name} », v{version}",
    'prd.summary.for': "{count} destinataires · {excluded} exclus",
    'prd.options': "Options",
    'prd.lang': "Langue",
    'prd.publish': "Publier sur le portail",
    'prd.publishHint': "chacun télécharge le sien dans « Mes documents »",
    'prd.publishAfterSign': "à la signature : rien ne part avant",
    'prd.notify': "Prévenir chacun",
    'prd.notifyHint': "par la cloche et par courriel, avec la Communication",
    'prd.sign': "Faire signer avant de diffuser",
    'prd.signHint': "le lot part dans « À signer » du signataire choisi",
    'prd.signForced': "le modèle l'exige : le lot part dans « À signer »",
    'prd.signer': "Signataire",
    'prd.noSigner': "Aucun signataire n'est réglé : il le fait dans « À signer », en indiquant sa qualité.",
    'prd.checks': "Vérifications",
    'prd.checks.ready': "{count} prêts",
    'prd.checks.readyDelib': "délibération close, calcul figé",
    'prd.checks.readyTraining': "inscriptions actives de la formation",
    'prd.checks.queue': "Le lot passe dans la file",
    'prd.checks.queueHint': "plus de {count} documents : Kelassi produit en arrière-plan et prévient à la fin",
    'prd.start': "Produire {count} · {what}",
    'prd.preview': "Aperçu",
    'prd.previewOf': "{name} · {index} sur {total}",
    'prd.previous': "Précédent",
    'prd.following': "Suivant",
    'prd.previewNote': "Barré « APERÇU » tant que rien n'est produit",
    'prd.kinds.transcript': "Relevés de notes",
    'prd.kinds.certificate': "Attestations de réussite",
    'prd.kinds.minutes': "Procès-verbal",
    'prd.kinds.enrolment': "Attestations de scolarité",
    'prd.kinds.card': "Cartes étudiant",
    'prd.kinds.list': "Listes de classe",
    'prd.kinds.internship': "Documents de stage",
    'prd.state.queued': "Dans la file",
    'prd.state.running': "En cours",
    'prd.state.done': "Terminé",
    'prd.state.partial': "Avec échecs",
    'prd.state.stopped': "Arrêté",
    'prd.state.failed': "Échec",
    'prd.state.to_sign': "À signer",
    'prd.state.signed': "Signé",
    'prd.state.returned': "Renvoyé",
    'prd.jobs.sub': "Les lots, en cours et récents : ils tournent en arrière-plan, même si vous fermez la page",
    'prd.jobs.empty': "Aucun lot pour le moment.",
    'prd.jobs.col.job': "Lot",
    'prd.jobs.col.started': "Lancé",
    'prd.jobs.col.by': "Par",
    'prd.jobs.col.progress': "Avancement",
    'prd.jobs.col.state': "État",
    'prd.jobs.note': "Un lot de plus de {count} documents passe dans la file ; Kelassi vous prévient à la fin, par la cloche et par courriel.",
    'prd.progress.queued': "{total} à produire",
    'prd.progress.running': "{done} sur {total}",
    'prd.progress.done': "{done} produits",
    'prd.progress.failed': "{done} produits · {failed} en échec",
    'prd.progress.toSign': "{done} produits · attendent {name}",
    'prd.progress.signed': "{done} signés par {name}",
    'prd.progress.returned': "renvoyé : {reason}",
    'prd.panel.started': "Lancé {date} par {by}",
    'prd.panel.produced': "Produits",
    'prd.panel.of': "sur {total}",
    'prd.panel.failed': "En échec",
    'prd.panel.toFix': "à corriger",
    'prd.panel.none': "aucun",
    'prd.panel.failures': "Ce qui a échoué",
    'prd.panel.open': "Ouvrir la fiche",
    'prd.panel.toSign': "{count} documents attendent la signature de {title} ({name}) ; rien ne part sur le portail avant.",
    'prd.panel.returned': "Renvoyé par {by} : {reason}. Les documents non signés sont annulés ; corrigez puis produisez un nouveau lot.",
    'prd.panel.retryNote': "Corrigez les fiches puis relancez : seuls les {failed} documents en échec sont produits, les {done} autres ne bougent pas.",
    'prd.panel.background': "Le lot tourne en arrière-plan : vous pouvez fermer ce panneau.",
    'prd.panel.stop': "Arrêter",
    'prd.panel.archive': "Les {count} produits",
    'prd.panel.retry': "Relancer les {count}",
    'sgn.title': "À signer",
    'sgn.sub': "Les lots qui attendent votre signature avant d'être diffusés",
    'sgn.kpi.pending': "Lots en attente",
    'sgn.kpi.pendingNote': "{count} documents",
    'sgn.kpi.signed': "Signés ce mois-ci",
    'sgn.kpi.signedNote': "dont {count} par délégation",
    'sgn.kpi.returned': "Renvoyés",
    'sgn.kpi.returnedNote': "ce mois-ci, avec le motif",
    'sgn.queue.title': "Lots à signer",
    'sgn.queue.sub': "Rien ne part sur le portail avant votre signature",
    'sgn.queue.empty': "Aucun lot ne vous attend.",
    'sgn.queue.line': "{count} documents · produits par {by}, {date}",
    'sgn.queue.delegated': "par délégation de {title}",
    'sgn.queue.browse': "Parcourir",
    'sgn.queue.return': "Renvoyer",
    'sgn.queue.chosen': "Lots choisis : {jobs} · documents : {count}",
    'sgn.action.sign': "Signer {count} documents",
    'sgn.dialog.title': "Signer {count} documents",
    'sgn.dialog.text': "{what}. Votre signature et le cachet y seront posés, avec la date et l'heure.",
    'sgn.dialog.sending': "Envoi du code par courriel…",
    'sgn.dialog.code': "Code reçu par courriel à {email}",
    'sgn.dialog.expires': "Le code vaut jusqu'à {time}.",
    'sgn.dialog.resend': "Renvoyer un code",
    'sgn.dialog.sign': "Signer",
    'sgn.done': "{count} documents signés.",
    'sgn.returned': "Lot renvoyé : son auteur est prévenu.",
    'sgn.return.title': "Renvoyer ce lot ?",
    'sgn.return.text': "« {label} » : ses {count} documents non signés sont annulés, et {by} reçoit votre motif.",
    'sgn.return.reason': "Motif",
    'sgn.return.submit': "Renvoyer le lot",
    'sgn.me.title': "Ma signature",
    'sgn.me.sub': "Image de la signature et du cachet, posées sur chaque document signé",
    'sgn.me.quality': "Qualité",
    'sgn.me.qualityHint': "Le Directeur pédagogique",
    'sgn.me.signature': "Ma signature",
    'sgn.me.stamp': "Mon cachet",
    'sgn.me.noSignature': "Pas encore de signature",
    'sgn.me.noStamp': "Pas de cachet",
    'sgn.me.addSignature': "Ajouter la signature",
    'sgn.me.replaceSignature': "Remplacer la signature",
    'sgn.me.addStamp': "Ajouter le cachet",
    'sgn.me.replaceStamp': "Remplacer le cachet",
    'sgn.me.imageHint': "PNG au fond transparent de préférence, 120 pixels de large au moins, 1 Mo au plus.",
    'sgn.delegation.title': "Délégation",
    'sgn.delegation.who': "Signe à ma place pendant mon absence",
    'sgn.delegation.nobody': "Personne",
    'sgn.delegation.from': "Du",
    'sgn.delegation.to': "Au",
    'sgn.delegation.hint': "Le document dit « Pour {title}, par délégation, {name} ».",
    'sgn.note': "Signature électronique simple : l'image de la signature et du cachet, l'horodatage et l'empreinte du document, confirmés par un code. Une signature qualifiée par un tiers de confiance pourra s'ajouter plus tard.",
    'violation.prd.signer': "Choisissez qui signe.",
    'violation.prd.too_many': "2 000 documents au plus par lot.",
    'violation.prd.training': "Choisissez une formation.",
    'violation.prd.finished': "Ce lot est terminé.",
    'violation.prd.nothing_to_retry': "Rien à relancer.",
    'violation.prd.nobody': "Aucun document à produire.",
    'violation.sgn.title': "Indiquez la qualité qui signe (« Le Directeur pédagogique »).",
    'violation.sgn.no_signer': "Réglez d'abord votre qualité.",
    'violation.sgn.delegate': "Choisissez une autre personne de l'établissement.",
    'violation.sgn.period': "La fin vient après le début.",
    'violation.sgn.too_big': "Une image de 1 Mo au plus.",
    'violation.sgn.format': "Une image PNG, JPEG ou WebP.",
    'violation.sgn.too_small': "Au moins 120 pixels de large.",
    'violation.sgn.too_many_codes': "Trop de codes demandés : réessayez dans quelques minutes.",
    'violation.sgn.code_expired': "Demandez un nouveau code.",
    'violation.sgn.code': "Ce code ne correspond pas.",
    'violation.sgn.not_yours': "Ce lot ne vous attend pas.",
    'violation.sgn.no_job': "Choisissez au moins un lot.",
    'doc.verify.pageTitle': "Vérification d'un document",
    'doc.verify.validBody': "Il est tel que l'établissement l'a produit.",
    'doc.verify.row.type': "Document",
    'doc.verify.row.student': "Au nom de",
    'doc.verify.row.training': "Formation",
    'doc.verify.row.year': "Année",
    'doc.verify.row.issued': "Produit le",
    'doc.verify.row.signed': "Signé par",
    'doc.verify.row.until': "Valable jusqu'au",
    'doc.verify.privacy': "Pour protéger l'étudiant, ni notes ni décision ne s'affichent ici. Comparez avec le document que vous avez reçu.",
    'doc.verify.another': "Vérifier une autre référence",
    'shr.title': "Documents partagés",
    'shr.sub': "Les documents de l'établissement diffusés à un public : règlement, calendrier, chartes, guides ; avec ou sans accusé de lecture",
    'shr.showArchived': "Montrer les archivés",
    'shr.empty': "Aucun document partagé pour le moment.",
    'shr.col.document': "Document",
    'shr.col.for': "Pour",
    'shr.col.reading': "Lecture",
    'shr.col.follow': "Suivi",
    'shr.col.period': "Diffusé",
    'shr.versionOf': "version {number} du {date}",
    'shr.archived': "Archivé",
    'shr.ack': "Accusé de lecture",
    'shr.free': "Lecture libre",
    'shr.follow.pending': "{accepted} sur {total} · {pending} à relancer",
    'shr.follow.all': "{accepted} sur {total}",
    'shr.follow.opened': "Ouvertures : {count}",
    'shr.period.always': "Toute l'année",
    'shr.period.until': "Jusqu'au {date}",
    'shr.period.since': "Depuis le {date}",
    'shr.period.between': "Du {from} au {to}",
    'shr.audience.everyone': "Tous, personnel compris",
    'shr.population.student': "Étudiants",
    'shr.population.teacher': "Enseignants",
    'shr.population.staff': "Personnel",
    'shr.note': "Remplacer le fichier crée une nouvelle version ; si l'accusé de lecture est demandé, chacun doit relire et accepter la nouvelle version. La liste de qui a lu s'exporte en Excel.",
    'shr.new': "Nouveau partage",
    'shr.drop': "Déposer un fichier",
    'shr.dropHint': "PDF, Word, Excel, image · {size} Mo au plus",
    'shr.field.title': "Titre",
    'shr.field.for': "Pour",
    'shr.field.from': "Du",
    'shr.field.to': "Au",
    'shr.field.ack': "Demander un accusé de lecture",
    'shr.field.ackHint': "« J'ai lu et j'accepte » ; la date et l'heure sont gardées",
    'shr.field.notify': "Prévenir par notification",
    'shr.field.notifyHint': "avec la Communication",
    'shr.restrict.none': "Tous les étudiants : restreindre à des formations ou des classes",
    'shr.restrict.some': "Étudiants restreints : {count} choix",
    'shr.restrict.trainings': "Formations",
    'shr.restrict.classes': "Classes",
    'shr.restrict.noTraining': "Aucune formation cette année.",
    'shr.share': "Partager",
    'shr.shared': "Document partagé.",
    'shr.saved': "Partage enregistré.",
    'shr.replaced': "Version {number} partagée.",
    'shr.archivedDone': "Partage archivé : il quitte « Mes documents ».",
    'shr.reminded': "Relance envoyée à {count} personnes.",
    'shr.panel.by': "Partagé par {by}, le {date}",
    'shr.panel.audience': "Public",
    'shr.panel.accepted': "Ont accepté",
    'shr.panel.opened': "Ont ouvert",
    'shr.panel.pending': "{count} à relancer",
    'shr.panel.version': "version {number}",
    'shr.panel.file': "Le fichier et ses versions",
    'shr.panel.versionLine': "v{number}",
    'shr.panel.download': "Télécharger",
    'shr.panel.notifyVersion': "Prévenir le public de la nouvelle version",
    'shr.panel.replace': "Partager la version {number}",
    'shr.panel.settings': "Public, période, accusé",
    'shr.panel.readers': "Qui a lu",
    'shr.panel.archive': "Archiver",
    'shr.panel.remind': "Relancer les {count}",
    'shr.filter.all': "Tout le public",
    'shr.filter.notAccepted': "Pas encore accepté",
    'shr.filter.accepted': "Ont accepté",
    'shr.filter.notOpened': "Pas encore ouvert",
    'shr.filter.opened': "Ont ouvert",
    'shr.readers.name': "Personne",
    'shr.readers.opened': "Ouvert le",
    'shr.readers.accepted': "Accepté le",
    'shr.readers.waiting': "En attente",
    'shr.readers.truncated': "Liste limitée aux 3 000 premières personnes.",
    'shr.mine.toAccept': "À lire et accepter",
    'shr.mine.versionLine': "Version {number} · partagé le {date}",
    'shr.mine.read': "Lire",
    'shr.mine.accept': "J'ai lu et j'accepte",
    'shr.mine.title': "Documents de l'établissement",
    'shr.mine.acceptedOn': "accepté le {date}",
    'shr.mine.sharedOn': "partagé le {date}",
    'shr.mine.until': "jusqu'au {date}",
    'shr.mine.page': "Documents partagés",
    'shr.mine.pageSub': "Ce que l'établissement vous a partagé : à lire, et parfois à accepter",
    'shr.mine.none': "Aucun document partagé avec vous.",
    'journal.action.document.shared': "Document partagé",
    'journal.action.document.share_changed': "Document partagé modifié",
    'journal.action.document.share_archived': "Document partagé archivé",
    'journal.action.document.share_reminded': "Relance de lecture envoyée",
    'violation.shr.audience': "Choisissez à qui partager le document.",
    'violation.shr.audience_students': "Formations et classes restreignent les étudiants : cochez-les.",
    'violation.shr.title': "Donnez un titre (200 caractères au plus).",
    'violation.shr.date': "Date invalide.",
    'violation.shr.period': "La fin vient après le début.",
    'violation.shr.archived': "Ce partage est archivé.",
    'violation.shr.no_acknowledgement': "Ce document ne demande pas d'accusé de lecture.",
    'violation.shr.no_communication': "Les relances passent par la Communication, éteinte.",
    'violation.shr.new_version': "Une nouvelle version est parue : relisez-la.",
    'rel.modules.library': "Bibliothèque",
    'rel.modules.libraryHint': "Catalogue, exemplaires, lecteurs et cartes, prêts, réservations, pénalités, inventaires",
    'rel.modules.libraryOff': "Les prêts en cours restent enregistrés ; aucune relance ne part plus et le catalogue en ligne quitte le portail.",
    'doc.type.library_clearance': "Quitus de bibliothèque",
    'doc.types.library_clearance': "Quitus de bibliothèque",
    'lib.situation.available': "Disponible",
    'lib.situation.loaned': "En prêt",
    'lib.situation.held': "Réservé, à retirer",
    'lib.situation.transit': "En transit",
    'lib.situation.repair': "En réparation",
    'lib.situation.lost': "Perdu",
    'lib.situation.missing': "Manquant",
    'lib.situation.withdrawn': "Retiré du fonds",
    'lib.hold.waiting': "En attente",
    'lib.hold.assigned': "À retirer",
    'lib.hold.collected': "Retirée",
    'lib.hold.cancelled_reader': "Annulée par le lecteur",
    'lib.hold.cancelled_library': "Annulée par la bibliothèque",
    'lib.hold.expired': "Expirée",
    'lib.fee.open': "À régler",
    'lib.fee.paid': "Payée",
    'lib.fee.waived': "Remise",
    'lib.fee.cancelled': "Annulée",
    'lib.late.days': "{count} j de retard",
    'lib.late.due': "Retour le {date}",
    'lib.scan.camera': "Scanner avec la caméra",
    'lib.scan.hint': "Douchette ou saisie, puis Entrée",
    'lib.scan.noCamera': "La caméra n'est pas accessible sur cet appareil.",
    'journal.action.library.settings_changed': "Réglages de la bibliothèque modifiés",
    'journal.action.library.catalog_changed': "Notice ou autorité de la bibliothèque modifiée",
    'journal.action.library.items_changed': "Exemplaires modifiés, déplacés ou étiquetés",
    'journal.action.library.item_withdrawn': "Exemplaire sorti du fonds",
    'journal.action.library.locations_changed': "Emplacements de la bibliothèque modifiés",
    'journal.action.library.reader_changed': "Lecteur, adhésion ou carte modifié",
    'journal.action.library.loan_overridden': "Prêt forcé malgré un blocage",
    'journal.action.library.fee_changed': "Créance de la bibliothèque émise ou réglée",
    'journal.action.library.fee_waived': "Pénalité ou frais remis",
    'journal.action.library.loss_changed': "Perte déclarée, réglée ou exemplaire retrouvé",
    'journal.action.library.inventory_changed': "Inventaire ouvert ou écart traité",
    'journal.action.library.inventory_closed': "Inventaire clôturé",
    'journal.action.library.import_run': "Import de la bibliothèque exécuté ou annulé",
    'journal.action.library.history_viewed': "Historique de prêt d'un lecteur consulté",
    'journal.action.library.clearance_issued': "Quitus de bibliothèque délivré",
    'violation.lib.reason_required': "Un motif est obligatoire.",
    'violation.lib.prefix': "Lettres capitales, chiffres et tirets seulement.",
    'violation.lib.policy_duplicate': "Une politique existe déjà pour ce croisement.",
    'violation.lib.default_policy': "La politique par défaut ne se supprime pas.",
    'violation.lib.isbn': "ISBN non valide : la clé de contrôle ne correspond pas.",
    'violation.lib.year_order': "L'année d'édition précède l'année de parution originale.",
    'violation.lib.dewey': "Indice Dewey attendu (ex. 658.4).",
    'violation.lib.issn': "ISSN attendu (1234-5678).",
    'violation.lib.language': "Code de langue ISO 639 attendu.",
    'violation.lib.contributor_required': "Un responsable ou un éditeur au moins.",
    'violation.lib.edition_used': "Une notice qui a des exemplaires ou des réservations ne se supprime pas.",
    'violation.lib.merge_invalid': "Deux autorités distinctes de même sorte.",
    'violation.lib.location_cycle': "Un nœud ne se range pas sous lui-même.",
    'violation.lib.location_used': "Un emplacement qui contient des nœuds, des exemplaires ou des plages ne se supprime pas.",
    'violation.lib.barcode': "Code-barres non valide.",
    'violation.lib.barcode_taken': "Ce code-barres existe déjà : un code-barres ne se réattribue jamais.",
    'violation.lib.item_withdrawn': "Un exemplaire sorti du fonds ne se modifie plus.",
    'violation.lib.no_category': "Aucune catégorie de lecteur pour cette personne : terminez l'assistant de démarrage.",
    'violation.lib.reader_exists': "Cette personne est déjà inscrite à la bibliothèque.",
    'violation.lib.contact_required': "Un courriel ou un téléphone est obligatoire.",
    'violation.lib.person_exists': "Une personne porte déjà cette pièce : inscrivez-la plutôt.",
    'violation.lib.reader_used': "Ce lecteur a un historique : suspendez-le plutôt que de le supprimer.",
    'violation.lib.no_membership': "Aucune adhésion active.",
    'violation.lib.card_inactive': "Cette carte n'est plus valable (perdue ou remplacée).",
    'violation.lib.fee_invoiced': "Cette créance est facturée : la Finance l'encaisse.",
    'violation.lib.fee_settled': "Cette créance est déjà réglée.",
    'violation.lib.loan_closed': "Ce prêt est clos.",
    'violation.lib.not_lost': "Cet exemplaire n'est ni perdu ni manquant.",
    'violation.lib.not_clear': "Le lecteur n'est pas libre de tout engagement : prêts en cours ou somme due.",
    'violation.lib.unknown_reader': "Carte ou lecteur inconnu.",
    'violation.lib.unknown_item': "Code-barres inconnu.",
    'violation.lib.no_open_loan': "Aucun prêt en cours pour cet exemplaire.",
    'violation.lib.renew_refused': "Ce prêt ne se prolonge pas.",
    'violation.lib.holds_closed': "Les réservations en ligne ne sont pas ouvertes.",
    'violation.lib.confidential': "Document confidentiel : ni prêt ni consultation publique.",
    'violation.lib.hold_exists': "Ce document est déjà réservé par ce lecteur.",
    'violation.lib.hold_loaned': "Le lecteur a déjà ce document en prêt.",
    'violation.lib.no_loanable': "Aucun exemplaire prêtable.",
    'violation.lib.hold_closed': "Cette réservation est close.",
    'violation.lib.hold_item': "Cet exemplaire ne peut pas servir cette réservation.",
    'violation.lib.inventory_open': "Un inventaire complet est déjà ouvert.",
    'violation.lib.inventory_closed': "Cet inventaire est clôturé.",
    'violation.lib.resolve_invalid': "Cette action ne s'applique pas à cet écart.",
    'violation.lib.import_done': "Cet import est déjà exécuté.",
    'violation.lib.import_not_done': "Seul un import exécuté s'annule.",
    'violation.lib.file_size': "Fichier vide ou trop gros (30 Mo au plus).",
    'violation.lib.card_code': "Carte numérique échue ou illisible : rafraîchissez-la.",
    'violation.lib.cover': "Image JPEG, PNG ou WebP de 2 Mo au plus.",
    'violation.lib.not_mine': "Ce prêt n'est pas le vôtre.",
    'lib.dash.title': "Bibliothèque",
    'lib.dash.sub': "Le jour de la bibliothèque : prêts, retours attendus, retards, réservations à préparer",
    'lib.dash.stats': "Statistiques",
    'lib.dash.suggestions': "Suggestions d'achat",
    'lib.dash.desk': "Prêter et rendre",
    'lib.dash.setupNeeded': "La bibliothèque n'est pas encore démarrée : un site, une politique de prêt, un code-barres et une carte suffisent.",
    'lib.dash.setupGo': "Lancer l'assistant",
    'lib.dash.kpiLoans': "Prêts aujourd'hui",
    'lib.dash.kpiLoansNote': "{returns} retours",
    'lib.dash.kpiDue': "Retours attendus",
    'lib.dash.kpiDueNote': "aujourd'hui · {open} prêts en cours",
    'lib.dash.kpiLate': "En retard",
    'lib.dash.kpiLateNote': "{count} lecteurs suspendus",
    'lib.dash.kpiHolds': "Réservations à préparer",
    'lib.dash.kpiHoldsNote': "{collect} en rayon, {pickup} à retirer",
    'lib.dash.kpiStock': "Fonds",
    'lib.dash.kpiStockNote': "exemplaires · {editions} notices · {readers} lecteurs",
    'lib.dash.lateTitle': "Retours attendus et retards",
    'lib.dash.lateSub': "Aujourd'hui d'abord, puis les retards",
    'lib.dash.allLate': "Tous les retards",
    'lib.dash.noLate': "Aucun retour attendu ni retard.",
    'lib.dash.colItem': "Exemplaire",
    'lib.dash.colReader': "Lecteur",
    'lib.dash.colDue': "Échéance",
    'lib.dash.anonymous': "Prêt dépersonnalisé",
    'lib.dash.recentTitle': "Mouvements récents",
    'lib.dash.recentSub': "Prêts, retours, prolongations, pertes",
    'lib.dash.noRecent': "Aucun mouvement pour l'instant.",
    'lib.dash.event.loaned': "Prêt",
    'lib.dash.event.returned': "Retour",
    'lib.dash.event.renewed': "Prolongation",
    'lib.dash.event.lost': "Perte",
    'lib.dash.event.found': "Retrouvé",
    'lib.dash.event.withdrawn': "Retiré",
    'lib.dash.collectTitle': "Réservations à préparer",
    'lib.dash.collectSub': "Liste de collecte : ces exemplaires sont en rayon",
    'lib.dash.collectGo': "Préparer",
    'lib.dash.collectFor': "pour {name} · rang {rank}",
    'lib.dash.noCollect': "Rien à aller chercher en rayon.",
    'lib.dash.watchTitle': "À surveiller",
    'lib.dash.watchSuspended': "{count} lecteurs suspendus",
    'lib.dash.watchDebt': "dette totale des lecteurs : {amount}",
    'lib.dash.watchTransit': "{count} exemplaires en transit",
    'lib.dash.watchTransitNone': "aucun transfert entre sites",
    'lib.dash.watchStock': "{waiting} lecteurs attendent, {copies} exemplaire(s) : en commander ?",
    'lib.desk.title': "Prêter et rendre",
    'lib.desk.sub': "Deux scans pour prêter (carte, puis exemplaire), un seul pour rendre",
    'lib.desk.tabLend': "Prêter",
    'lib.desk.tabReturn': "Rendre",
    'lib.desk.readerScan': "1. Carte du lecteur",
    'lib.desk.readerHint': "Scannez la carte, le QR de la carte numérique ou la carte d'étudiant",
    'lib.desk.itemScan': "2. Exemplaires",
    'lib.desk.itemHint': "Scannez chaque exemplaire ; les contrôles s'affichent aussitôt",
    'lib.desk.unknownReader': "Carte ou lecteur inconnu.",
    'lib.desk.lent': "{count} prêt(s) enregistré(s).",
    'lib.desk.ticket': "Imprimer le ticket",
    'lib.desk.basket': "Ce prêt",
    'lib.desk.basketSub': "La politique du lecteur s'applique à chaque exemplaire",
    'lib.desk.onsite': "Consultation sur place",
    'lib.desk.scanReaderFirst': "Scannez d'abord la carte du lecteur.",
    'lib.desk.basketEmpty': "Scannez les exemplaires à prêter.",
    'lib.desk.colCheck': "Contrôle",
    'lib.desk.colActions': "Actions",
    'lib.desk.accepted': "Accepté",
    'lib.desk.refused': "Refusé",
    'lib.desk.notOverridable': "(ne se force pas)",
    'lib.desk.rentalDay': "Location {amount} par jour",
    'lib.desk.rentalLoan': "Location {amount}",
    'lib.desk.force': "Forcer",
    'lib.desk.remove': "Retirer {barcode} du prêt",
    'lib.desk.receipt': "Reçu de la Finance pour la location",
    'lib.desk.receiptHint': "La location se règle avant la remise de l'ouvrage",
    'lib.desk.lend': "Prêter {count} exemplaire(s)",
    'lib.desk.sideHint': "La fiche du lecteur s'affiche ici après le scan de sa carte.",
    'lib.desk.forceTitle': "Forcer le prêt",
    'lib.desk.forceReason': "Motif du forçage",
    'lib.desk.forceHint': "Le motif est gardé sur le prêt et dans le journal",
    'lib.desk.forceConfirm': "Prêter malgré le blocage",
    'lib.desk.reader': "Le lecteur",
    'lib.desk.openFile': "Ouvrir le dossier",
    'lib.desk.cardNumber': "carte {number}",
    'lib.desk.noMembership': "Aucune adhésion active",
    'lib.desk.membershipUntil': "Adhésion active jusqu'au {date}",
    'lib.desk.membershipPending': "Adhésion en attente de paiement ({amount})",
    'lib.desk.loansCount': "{count} prêt(s) en cours",
    'lib.desk.suspended': "Suspendu : {reason}",
    'lib.desk.penalty': "Pénalité jusqu'au {date}",
    'lib.desk.currentLoans': "Ses prêts en cours",
    'lib.desk.noLoans': "Aucun prêt en cours.",
    'lib.desk.renew': "Prolonger",
    'lib.desk.account': "Son compte",
    'lib.desk.debt': "Dette",
    'lib.desk.late': "Retard",
    'lib.desk.holds': "Réservations",
    'lib.desk.openFees': "Créances ouvertes",
    'lib.desk.none': "aucun",
    'lib.desk.returnScan': "Exemplaire rendu",
    'lib.desk.returnHint': "Un seul scan suffit, sans la carte ; un retour d'un autre site est accepté",
    'lib.desk.returnedOn': "Date réelle de dépôt",
    'lib.desk.returnedOnHint': "Pour la boîte de retour ; vide : aujourd'hui",
    'lib.desk.condition': "L'état de l'exemplaire",
    'lib.desk.conditionSame': "Inchangé",
    'lib.desk.conditionHint': "À choisir avant le scan. « À réparer » envoie l'exemplaire à l'atelier.",
    'lib.desk.cond.new': "Neuf",
    'lib.desk.cond.good': "Bon",
    'lib.desk.cond.worn': "Usé",
    'lib.desk.cond.damaged': "Abîmé",
    'lib.desk.cond.repair': "À réparer",
    'lib.desk.returned': "Le retour",
    'lib.desk.returnedBy': "Rendu par",
    'lib.desk.lateDays': "{count} jour(s) d'ouverture ; les jours de fermeture ne comptent pas",
    'lib.desk.onTime': "à temps",
    'lib.desk.fine': "Pénalité",
    'lib.desk.suspension': "Suspension",
    'lib.desk.suspendedUntil': "prêt suspendu jusqu'au {date}",
    'lib.desk.dest.shelf': "Remettre en rayon",
    'lib.desk.dest.hold': "Mettre de côté pour une réservation",
    'lib.desk.dest.transit': "Envoyer vers son site",
    'lib.desk.dest.repair': "Envoyer à l'atelier",
    'lib.desk.destShelf': "à sa place : {place}",
    'lib.desk.destHold': "pour {name}, prévenu maintenant ; à retirer avant le {date}",
    'lib.desk.destTransit': "l'exemplaire appartient au site {site}",
    'lib.desk.destRepair': "l'exemplaire est marqué en réparation",
    'lib.desk.damageTitle': "Dégradation constatée",
    'lib.desk.damageSub': "Une créance de dégradation part au compte du lecteur",
    'lib.desk.damageAmount': "Montant ({currency})",
    'lib.desk.damageNote': "Constat",
    'lib.desk.damageCharge': "Facturer la dégradation",
    'lib.desk.damageDone': "Créance de dégradation émise.",
    'lib.desk.damageLabel': "Dégradation",
    'lib.holds.title': "Réservations",
    'lib.holds.sub': "La file d'attente de chaque édition, par priorité puis par ordre d'arrivée",
    'lib.holds.tabCollect': "À collecter",
    'lib.holds.tabPickup': "À retirer",
    'lib.holds.tabWaiting': "En file",
    'lib.holds.tabClosed': "Closes",
    'lib.holds.collectTitle': "Liste de collecte",
    'lib.holds.collectSub': "Triée par emplacement : prenez l'exemplaire en rayon, scannez-le, il est mis de côté et le lecteur prévenu",
    'lib.holds.noPlace': "Emplacement non précisé",
    'lib.holds.forReader': "pour {name} · {category}",
    'lib.holds.scanTaken': "Exemplaire pris",
    'lib.holds.scanTakenHint': "Scan puis Entrée",
    'lib.holds.pickupTitle': "À retirer au guichet",
    'lib.holds.pickupSub': "Passé l'échéance, la réservation expire et l'exemplaire va au suivant",
    'lib.holds.waitingTitle': "En attente d'un retour",
    'lib.holds.waitingSub': "Aucun exemplaire en rayon : la réservation sera servie au prochain retour",
    'lib.holds.closedTitle': "Réservations closes",
    'lib.holds.empty': "Aucune réservation.",
    'lib.holds.colTitle': "Document",
    'lib.holds.colUntil': "À retirer avant",
    'lib.holds.colRank': "Rang",
    'lib.holds.colState': "État",
    'lib.holds.colSince': "Depuis",
    'lib.holds.until': "avant le {date}",
    'lib.holds.rank': "rang {rank}",
    'lib.holds.cancel': "Annuler",
    'lib.overdue.title': "Retards et pertes",
    'lib.overdue.sub': "Le traitement de chaque matin détecte les retards et envoie les relances",
    'lib.overdue.exportLoans': "Exporter les prêts",
    'lib.overdue.exportFees': "Exporter les créances",
    'lib.overdue.calendar': "Le calendrier des relances",
    'lib.overdue.calendarSub': "Pénalité de retard : {mode} ; pour un étudiant mineur, la relance va aussi au responsable légal",
    'lib.overdue.mode.money': "financière",
    'lib.overdue.mode.suspension': "en jours de suspension",
    'lib.overdue.mode.both': "financière et suspension",
    'lib.overdue.stepReminder': "Rappel",
    'lib.overdue.stepReminderSub': "avant l'échéance",
    'lib.overdue.stepChase': "Relance",
    'lib.overdue.stepChaseSub': "après l'échéance",
    'lib.overdue.stepNotice': "Mise en demeure",
    'lib.overdue.stepNoticeSub': "suspension ; perte présumée {days} j plus tard",
    'lib.overdue.kpiLate': "En retard",
    'lib.overdue.kpiLateNote': "dont {count} au-delà de 7 jours",
    'lib.overdue.kpiLost': "Pertes",
    'lib.overdue.kpiLostNote': "prêts clos en perte",
    'lib.overdue.kpiMissing': "Manquants et perdus",
    'lib.overdue.kpiMissingNote': "exemplaires à retrouver",
    'lib.overdue.kpiDebt': "Dette des lecteurs",
    'lib.overdue.kpiDebtNote': "{count} créances ouvertes",
    'lib.overdue.tabLate': "Retards",
    'lib.overdue.tabLost': "Pertes",
    'lib.overdue.tabMissing': "Manquants",
    'lib.overdue.tabFees': "Créances",
    'lib.overdue.empty': "Rien à traiter.",
    'lib.overdue.colLate': "Retard",
    'lib.overdue.colLostOn': "Perdu le",
    'lib.overdue.colStage': "Dernière relance",
    'lib.overdue.noReminder': "aucune",
    'lib.overdue.manual': "à la main",
    'lib.overdue.remind': "Relancer",
    'lib.overdue.reminded': "Relance envoyée.",
    'lib.overdue.lose': "Passer en perte",
    'lib.overdue.lost': "Prêt clos en perte : les créances de remplacement sont émises.",
    'lib.overdue.loseTitle': "Passer en perte",
    'lib.overdue.loseText': "« {title} » ({barcode}), prêté à {name}, {days} jours de retard : l'exemplaire passe « perdu », le prêt est clos en perte, la pénalité de retard s'arrête, le remplacement et les frais de traitement sont facturés.",
    'lib.overdue.loseConfirm': "Clore le prêt en perte",
    'lib.overdue.settle': "Remplacement accepté",
    'lib.overdue.settled': "Remplacement accepté : la créance de remplacement est annulée.",
    'lib.overdue.missingSub': "Retrouvé, l'exemplaire réintègre le fonds ; un perdu payé est remboursé selon le réglage",
    'lib.overdue.markFound': "Retrouvé",
    'lib.overdue.found': "Exemplaire réintégré au fonds.",
    'lib.overdue.feesSub': "Une créance facturée s'encaisse à la Finance ; les autres se règlent sur reçu de la Finance",
    'lib.overdue.colFee': "Créance",
    'lib.overdue.colDue': "Reste dû",
    'lib.overdue.colInvoice': "Facture",
    'lib.overdue.kind.membership': "Adhésion",
    'lib.overdue.kind.rental': "Location",
    'lib.overdue.kind.late': "Retard",
    'lib.overdue.kind.damage': "Dégradation",
    'lib.overdue.kind.loss': "Perte",
    'lib.overdue.kind.processing': "Frais de traitement",
    'lib.overdue.kind.duplicate': "Duplicata",
    'lib.overdue.pay': "Payer",
    'lib.overdue.waive': "Remettre",
    'lib.overdue.cancel': "Annuler",
    'lib.overdue.payTitle': "Noter le paiement",
    'lib.overdue.payConfirm': "Enregistrer le reçu",
    'lib.overdue.waiveTitle': "Remettre la créance",
    'lib.overdue.waiveConfirm': "Accorder la remise",
    'lib.overdue.cancelTitle': "Annuler la créance",
    'lib.overdue.cancelConfirm': "Annuler la créance",
    'lib.overdue.receipt': "Numéro du reçu de la Finance",
    'lib.overdue.receiptHint': "La bibliothèque n'encaisse rien elle-même",
    'lib.overdue.amount': "Montant remis ({currency})",
    'lib.overdue.amountHint': "Remise totale par défaut ; un montant plus petit donne une remise partielle",
    'lib.overdue.reason': "Motif",
    'lib.overdue.reasonHint': "Obligatoire, gardé dans le journal",
    'lib.overdue.paid': "Paiement enregistré.",
    'lib.overdue.waived': "Remise accordée.",
    'lib.overdue.cancelled': "Créance annulée.",
    'lib.doctype.book': "Livre",
    'lib.doctype.periodical': "Périodique",
    'lib.doctype.thesis': "Thèse",
    'lib.doctype.dissertation': "Mémoire",
    'lib.doctype.internship_report': "Rapport de stage",
    'lib.doctype.media': "CD ou DVD",
    'lib.doctype.map': "Carte",
    'lib.doctype.ebook': "Livre numérique",
    'lib.doctype.internal': "Document interne",
    'lib.inv.title': "Inventaire",
    'lib.inv.sub': "Confronter les étagères à la base, par campagne, sans fermer la bibliothèque",
    'lib.inv.open': "Ouvrir une campagne",
    'lib.inv.none': "Aucune campagne d'inventaire pour l'instant.",
    'lib.inv.col.campaign': "Campagne",
    'lib.inv.col.kind': "Sorte",
    'lib.inv.col.scope': "Périmètre",
    'lib.inv.col.opened': "Ouverte le",
    'lib.inv.col.progress': "Avancement",
    'lib.inv.col.state': "État",
    'lib.inv.col.zone': "Zone",
    'lib.inv.col.expected': "Attendus",
    'lib.inv.col.seen': "Vus",
    'lib.inv.col.gap': "Écart",
    'lib.inv.col.item': "Exemplaire",
    'lib.inv.col.action': "Action",
    'lib.inv.kind.full': "Inventaire complet",
    'lib.inv.kind.rotating': "Récolement tournant",
    'lib.inv.kind.spot': "Contrôle ponctuel",
    'lib.inv.state.open': "En cours",
    'lib.inv.state.closed': "Clôturée",
    'lib.inv.scopeAll': "Tout le fonds",
    'lib.inv.seenOf': "{seen} vus sur {expected}",
    'lib.inv.openTitle': "Ouvrir une campagne d'inventaire",
    'lib.inv.openSub': "Les exemplaires du périmètre sont figés dans leur état attendu ; la circulation continue.",
    'lib.inv.field.label': "Libellé",
    'lib.inv.field.kind': "Sorte",
    'lib.inv.field.location': "Emplacement",
    'lib.inv.field.locationHint': "Un site, une salle ou une tablette, avec tout ce qu'il contient",
    'lib.inv.field.from': "Cote de début",
    'lib.inv.field.to': "Cote de fin",
    'lib.inv.field.docType': "Type de document",
    'lib.inv.allTypes': "Tous les types",
    'lib.inv.field.team': "Équipe",
    'lib.inv.field.teamHint': "Une personne par ligne, « zone : nom » pour répartir les zones",
    'lib.inv.back': "Inventaires",
    'lib.inv.openedLine': "Ouvert le {date} par {by} · {scope} · la circulation continue pendant le comptage",
    'lib.inv.report': "Procès-verbal",
    'lib.inv.count': "Compter sur tablette",
    'lib.inv.close': "Clore la campagne",
    'lib.inv.closedOn': "Campagne clôturée le {date}.",
    'lib.inv.kpi.expected': "Attendus",
    'lib.inv.kpi.expectedNote': "figés à l'ouverture",
    'lib.inv.kpi.seen': "Vus",
    'lib.inv.kpi.gaps': "Écarts",
    'lib.inv.kpi.gapsNote': "{missing} manquants, {misplaced} mal rangés",
    'lib.inv.kpi.value': "Valeur des manquants",
    'lib.inv.kpi.valueNote': "prix d'achat, sinon forfait par type",
    'lib.inv.byZone': "Par zone",
    'lib.inv.byZoneSub': "Plusieurs agents comptent en parallèle sur des zones distinctes",
    'lib.inv.gaps': "Écarts à traiter",
    'lib.inv.gapsSub': "Les manquants restent recherchés jusqu'à la campagne suivante",
    'lib.inv.allGaps': "Tous",
    'lib.inv.noGap': "Aucun écart.",
    'lib.inv.gap.missing': "Manquant",
    'lib.inv.gap.misplaced': "Mal rangé",
    'lib.inv.gap.unknown': "Inconnu",
    'lib.inv.gap.present_loaned': "Marqué prêté",
    'lib.inv.gap.present_lost': "Perdu retrouvé",
    'lib.inv.gap.present_missing': "Manquant retrouvé",
    'lib.inv.gap.condition': "État à revoir",
    'lib.inv.detail.missing': "attendu {location}",
    'lib.inv.detail.misplaced': "vu {seen}, attendu {location}",
    'lib.inv.detail.unknown': "absent de la base, vu {seen}",
    'lib.inv.unknownTitle': "Code-barres absent de la base",
    'lib.inv.treat': "Traiter",
    'lib.inv.treatTitle': "Traiter l'écart",
    'lib.inv.action': "Action",
    'lib.inv.apply': "Appliquer",
    'lib.inv.do.search': "Recherche ciblée",
    'lib.inv.do.ignore': "Ignorer",
    'lib.inv.do.relocate': "Corriger l'emplacement",
    'lib.inv.do.close_loan': "Clore un prêt oublié",
    'lib.inv.do.reintegrate': "Réintégrer au fonds",
    'lib.inv.do.create': "Créer l'exemplaire",
    'lib.inv.doHint.search': "L'exemplaire reste recherché ; il passera manquant à la clôture",
    'lib.inv.doHint.ignore': "L'écart est noté traité, sans rien changer",
    'lib.inv.doHint.relocate': "L'emplacement de l'exemplaire devient celui où il a été vu",
    'lib.inv.doHint.close_loan': "Le prêt est clos à la date du scan, sans pénalité",
    'lib.inv.doHint.reintegrate': "L'exemplaire redevient disponible",
    'lib.inv.doHint.create': "Un exemplaire de la notice choisie, avec ce code-barres",
    'lib.inv.findEdition': "Rechercher la notice",
    'lib.inv.search': "Rechercher",
    'lib.inv.closeTitle': "Clore la campagne",
    'lib.inv.closeSub': "{count} manquant(s) passeront « manquant » et resteront recherchés",
    'lib.inv.withdraw': "Sortir du fonds les manquants persistants",
    'lib.inv.withdrawHint': "Ceux déjà manquants à la campagne précédente, en lot, avec un motif",
    'lib.inv.reason': "Motif",
    'lib.inv.closeNote': "Clore une campagne est réservé au droit de clôture d'inventaire : les manquants persistants peuvent sortir du fonds en lot, avec un motif, et le procès-verbal donne attendus, trouvés, manquants et valeur des pertes.",
    'lib.scan2.back': "Retour à la campagne",
    'lib.scan2.noShelf': "Scannez d'abord l'étiquette de la tablette",
    'lib.scan2.online': "En ligne · {count} en attente",
    'lib.scan2.offline': "Hors ligne · {count} scans en attente",
    'lib.scan2.closed': "Cette campagne est clôturée : le comptage est fermé.",
    'lib.scan2.scanShelf': "Tablette",
    'lib.scan2.scanItems': "Exemplaires",
    'lib.scan2.hint': "Caméra ou douchette ; un code de tablette change de tablette",
    'lib.scan2.pick': "ou choisissez-la",
    'lib.scan2.choose': "Choisir une tablette",
    'lib.scan2.shelfFirst': "Scannez d'abord la tablette : ce code n'est pas un emplacement.",
    'lib.scan2.expected': "Attendus ici",
    'lib.scan2.seen': "Vus",
    'lib.scan2.left': "{count} restent",
    'lib.scan2.bad': "Écarts",
    'lib.scan2.badNote': "inconnus de la base",
    'lib.scan2.recent': "Derniers scans",
    'lib.scan2.noneYet': "Aucun scan sur cette tablette.",
    'lib.scan2.status.queued': "En attente",
    'lib.scan2.status.seen': "Vu",
    'lib.scan2.status.unknown': "Absent de la base",
    'lib.scan2.status.twice': "Déjà scanné",
    'lib.scan2.changeShelf': "Changer de tablette",
    'lib.scan2.done': "Tablette terminée",
    'lib.mine.title': "Ma bibliothèque",
    'lib.mine.sub': "{name} · {category}",
    'lib.mine.catalog': "Catalogue",
    'lib.mine.welcome': "Vous n'êtes pas encore lecteur",
    'lib.mine.welcomeText': "Passez au guichet de la bibliothèque pour ouvrir votre adhésion et recevoir votre carte. Le catalogue reste ouvert à la consultation.",
    'lib.mine.loans': "Mes prêts ({count})",
    'lib.mine.noLoans': "Aucun prêt en cours.",
    'lib.mine.due': "à rendre le {date}",
    'lib.mine.dueToday': "à rendre aujourd'hui",
    'lib.mine.today': "Aujourd'hui",
    'lib.mine.late': "en retard de {count} j, à rendre le {date}",
    'lib.mine.renew': "Prolonger",
    'lib.mine.renewed': "« {title} » est prolongé.",
    'lib.mine.declareLoss': "Signaler la perte de « {title} »",
    'lib.mine.lossShort': "Signaler une perte",
    'lib.mine.lossTitle': "Signaler une perte",
    'lib.mine.lossText': "Le prêt est clos en perte et les pénalités de retard s'arrêtent. Le remplacement ou son prix, et des frais de traitement, vous seront facturés. Si vous retrouvez l'ouvrage, rapportez-le au guichet.",
    'lib.mine.lossConfirm': "Confirmer la perte",
    'lib.mine.lossDone': "La perte est enregistrée.",
    'lib.mine.holds': "Mes réservations",
    'lib.mine.noHolds': "Aucune réservation.",
    'lib.mine.holdRank': "rang {rank} dans la file · vous serez prévenu",
    'lib.mine.holdReady': "vous attend jusqu'au {date}",
    'lib.mine.cancel': "Annuler",
    'lib.mine.holdCancelled': "Réservation de « {title} » annulée.",
    'lib.mine.account': "Mon compte",
    'lib.mine.debt': "{amount} à régler",
    'lib.mine.noDebt': "aucune dette",
    'lib.mine.until': "adhésion jusqu'au {date}",
    'lib.mine.noMembership': "pas d'adhésion active",
    'lib.mine.pendingMembership': "adhésion en attente de paiement",
    'lib.mine.suspended': "Prêts suspendus : {reason}",
    'lib.mine.penalty': "Pénalité de retard : pas de nouveau prêt avant le {date}.",
    'lib.mine.payDesk': "à régler à la Finance",
    'lib.mine.invoice': "facture {number}",
    'lib.mine.clearance': "Télécharger mon quitus",
    'lib.mine.notClear': "Le quitus s'obtient sans prêt en cours ni somme due ({loans} prêt(s) en cours).",
    'lib.mine.history': "Historique",
    'lib.mine.show': "Afficher ({count})",
    'lib.mine.hide': "Masquer",
    'lib.mine.keepHistory': "Garder mon historique de prêts",
    'lib.mine.keepHistoryHint': "Décoché, vos prêts rendus ne sont plus gardés à votre nom",
    'lib.mine.historyOn': "Votre historique est gardé.",
    'lib.mine.historyOff': "Votre historique ne sera plus gardé.",
    'lib.mine.historyLine': "du {from} au {to}",
    'lib.mine.card': "Ma carte numérique",
    'lib.mine.noCard': "Pas encore de carte",
    'lib.mine.cardTitle': "Carte de bibliothèque",
    'lib.mine.cardValid': "valable jusqu'au {date}",
    'lib.mine.qr': "Code QR de la carte",
    'lib.mine.cardHint': "Présentez ce code au poste de prêt ; il se renouvelle tout seul toutes les dix minutes.",
    'lib.mine.suggest': "Suggérer un achat",
    'lib.mine.suggestText': "Un ouvrage manque au fonds ? La bibliothèque étudie chaque suggestion.",
    'lib.mine.suggestNew': "Nouvelle suggestion",
    'lib.mine.suggestDone': "Suggestion envoyée.",
    'lib.mine.send': "Envoyer",
    'lib.mine.field.title': "Titre",
    'lib.mine.field.authors': "Auteurs",
    'lib.mine.field.isbn': "ISBN",
    'lib.mine.field.note': "Pourquoi cet ouvrage ?",
    'lib.mine.sugState.suggested': "Suggérée",
    'lib.mine.sugState.listed': "Retenue",
    'lib.mine.sugState.ordered': "Commandée",
    'lib.mine.sugState.received': "Reçue",
    'lib.mine.sugState.refused': "Non retenue",
    'lib.opac.title': "Catalogue de la bibliothèque",
    'lib.opac.sub': "Titre, auteur, sujet, cote ou ISBN",
    'lib.opac.mine': "Ma bibliothèque",
    'lib.opac.search': "Rechercher dans le catalogue",
    'lib.opac.placeholder': "Rechercher un ouvrage",
    'lib.opac.go': "Rechercher",
    'lib.opac.filters': "Filtres",
    'lib.opac.all': "Tout",
    'lib.opac.onlyAvailable': "Disponibles",
    'lib.opac.site': "Site",
    'lib.opac.allSites': "Tous les sites",
    'lib.opac.found': "{count} résultat(s)",
    'lib.opac.latest': "Les dernières entrées au fonds",
    'lib.opac.none': "Aucun ouvrage ne correspond.",
    'lib.opac.available': "Disponible ({count})",
    'lib.opac.noCopy': "Aucun exemplaire",
    'lib.opac.loaned': "En prêt",
    'lib.opac.loanedWaiting': "En prêt · {count} réservation(s)",
    'lib.opac.courses': "Bibliographie de mes cours",
    'lib.opac.news': "Nouveautés",
    'lib.opac.noNews': "Pas encore de nouveauté.",
    'lib.opac.pages': "Pages de résultats",
    'lib.opac.prev': "Précédente",
    'lib.opac.next': "Suivante",
    'lib.opac.pageOf': "Page {page} sur {pages}",
    'lib.opac.back': "Catalogue",
    'lib.opac.hold': "Réserver",
    'lib.opac.held': "Réservation enregistrée : vous êtes au rang {rank}.",
    'lib.opac.waiting': "{count} lecteur(s) en attente",
    'lib.opac.where': "Où le trouver",
    'lib.opac.backOn': "retour prévu le {date}",
    'lib.opac.onsite': "Sur place seulement",
    'lib.opac.description': "Description",
    'lib.opac.f.isbn': "ISBN",
    'lib.opac.f.cote': "Cote",
    'lib.opac.f.dewey': "Dewey",
    'lib.opac.f.edition': "Édition",
    'lib.opac.f.pages': "Pages",
    'lib.opac.f.languages': "Langues",
    'lib.opac.f.collection': "Collection",
    'lib.opac.f.subjects': "Sujets",
    'lib.opac.f.note': "Note",
    'lib.opac.digital': "Version numérique",
    'lib.opac.inCourses': "Recommandé dans les cours",
    'lib.opac.volumes': "Tomes",
    'lib.opac.volume': "Tome {n}",
    'lib.verify.clearanceBody': "Le lecteur était libre de tout engagement envers la bibliothèque à la date du {date}.",
    'lib.verify.category': "Catégorie de lecteur",
    'lib.role.author': "auteur",
    'lib.role.coauthor': "coauteur",
    'lib.role.editor': "directeur de publication",
    'lib.role.translator': "traducteur",
    'lib.role.illustrator': "illustrateur",
    'lib.role.prefacer': "préfacier",
    'lib.role.publisher': "éditeur",
    'lib.role.subject': "sujet",
    'lib.loanStatus.loanable': "Prêtable",
    'lib.loanStatus.onsite': "Consultation sur place",
    'lib.loanStatus.excluded': "Exclu du prêt",
    'lib.loanStatus.short': "Prêt court",
    'lib.condition.new': "Neuf",
    'lib.condition.good': "Bon",
    'lib.condition.worn': "Usé",
    'lib.condition.damaged': "Abîmé",
    'lib.condition.repair': "À réparer",
    'lib.kind.author': "Auteurs",
    'lib.kind.publisher': "Éditeurs",
    'lib.kind.collection': "Collections",
    'lib.kind.series': "Séries",
    'lib.kind.subject': "Sujets",
    'lib.lang.fr': "Français",
    'lib.lang.ar': "Arabe",
    'lib.lang.en': "Anglais",
    'lib.lang.es': "Espagnol",
    'lib.lang.de': "Allemand",
    'lib.lang.zgh': "Amazighe",
    'lib.acq.purchase': "Achat",
    'lib.acq.gift': "Don",
    'lib.acq.deposit': "Dépôt",
    'lib.acq.exchange': "Échange",
    'lib.acq.legacy': "Reprise de l'ancien Kelassi",
    'lib.dewey.0': "000 Informatique, information, ouvrages généraux",
    'lib.dewey.1': "100 Philosophie et psychologie",
    'lib.dewey.2': "200 Religion",
    'lib.dewey.3': "300 Sciences sociales",
    'lib.dewey.4': "400 Langues",
    'lib.dewey.5': "500 Sciences de la nature et mathématiques",
    'lib.dewey.6': "600 Technologie, sciences appliquées",
    'lib.dewey.7': "700 Arts, loisirs et sports",
    'lib.dewey.8': "800 Littérature",
    'lib.dewey.9': "900 Histoire et géographie",
    'lib.event.created': "Entré au fonds",
    'lib.event.moved': "Déplacé",
    'lib.event.loaned': "Prêté",
    'lib.event.returned': "Rendu",
    'lib.event.renewed': "Prolongé",
    'lib.event.condition': "État modifié",
    'lib.event.held': "Mis de côté pour une réservation",
    'lib.event.transit': "Parti en transit",
    'lib.event.repair': "Envoyé en réparation",
    'lib.event.lost': "Déclaré perdu",
    'lib.event.found': "Retrouvé",
    'lib.event.missing': "Manquant à l'inventaire",
    'lib.event.seen': "Vu à l'inventaire",
    'lib.event.withdrawn': "Sorti du fonds",
    'lib.event.imported': "Importé",
    'lib.cat.title': "Catalogue",
    'lib.cat.sub': "{count} notices · la recherche ignore accents et casse, et retrouve les formes rejetées des auteurs",
    'lib.cat.authorities': "Autorités",
    'lib.cat.locations': "Emplacements",
    'lib.cat.new': "Cataloguer",
    'lib.cat.search': "Rechercher dans le catalogue",
    'lib.cat.searchHint': "Titre, auteur, ISBN, cote",
    'lib.cat.searchGo': "Rechercher",
    'lib.cat.flags': "Filtres",
    'lib.cat.flag.all': "Tout",
    'lib.cat.flag.incomplete': "À compléter",
    'lib.cat.flag.noitems': "Sans exemplaire",
    'lib.cat.flag.rental': "Soumis à location",
    'lib.cat.flag.new': "Nouveautés",
    'lib.cat.facets': "Affiner",
    'lib.cat.facet.type': "Type",
    'lib.cat.facet.availability': "Disponibilité",
    'lib.cat.facet.language': "Langue",
    'lib.cat.facet.site': "Site",
    'lib.cat.availableOnly': "Disponible en rayon",
    'lib.cat.export': "Exporter le catalogue",
    'lib.cat.exportGo': "Télécharger",
    'lib.cat.format.csv': "Tableur (CSV)",
    'lib.cat.format.marc21': "MARC21",
    'lib.cat.format.unimarc': "UNIMARC",
    'lib.cat.format.ris': "RIS (bibliographie)",
    'lib.cat.format.bibtex': "BibTeX",
    'lib.cat.format.dc': "Dublin Core (XML)",
    'lib.cat.found': "{count} notices",
    'lib.cat.forQuery': "pour « {q} »",
    'lib.cat.none': "Aucune notice ne répond.",
    'lib.cat.noCopy': "Aucun exemplaire",
    'lib.cat.availability': "{available} sur {copies} disponibles",
    'lib.cat.waiting': "{count} réservation(s)",
    'lib.cat.rental': "Location",
    'lib.cat.confidential': "Confidentiel",
    'lib.cat.incomplete': "À compléter",
    'lib.cat.open': "Ouvrir",
    'lib.cat.pages': "Pages de résultats",
    'lib.cat.prev': "Précédent",
    'lib.cat.next': "Suivant",
    'lib.cat.pageOf': "Page {page} sur {pages}",
    'lib.ed.complete': "Notice complète",
    'lib.ed.incomplete': "Notice à compléter",
    'lib.ed.people': "Responsabilités",
    'lib.ed.edition': "Édition",
    'lib.ed.originalYear': "1re parution {year}",
    'lib.ed.collection': "Collection",
    'lib.ed.set': "Série et tomes",
    'lib.ed.volume': "tome {n}",
    'lib.ed.volumes': "{count} tome(s) rattaché(s)",
    'lib.ed.missingVolumes': "Tomes manquants : {list}",
    'lib.ed.ids': "Identifiants",
    'lib.ed.description': "Description",
    'lib.ed.pages': "{count} pages",
    'lib.ed.indexing': "Indexation",
    'lib.ed.cote': "cote {cote}",
    'lib.ed.subjects': "sujets : {list}",
    'lib.ed.languages': "Langues",
    'lib.ed.courses': "Bibliographies de cours",
    'lib.ed.summary': "Résumé",
    'lib.ed.publicNote': "Note publique",
    'lib.ed.internalNote': "Note interne",
    'lib.ed.thesis': "Mémoire ou thèse",
    'lib.ed.director': "dir. {name}",
    'lib.ed.digital': "Version numérique",
    'lib.ed.items': "Exemplaires",
    'lib.ed.itemsSub': "{count} exemplaire(s) · la disponibilité se calcule à partir d'eux, jamais d'un compteur",
    'lib.ed.addItems': "Ajouter des exemplaires",
    'lib.ed.addItemsSub': "Les codes-barres suivent le masque, ou reprennent des étiquettes existantes",
    'lib.ed.addGo': "Ajouter",
    'lib.ed.added': "{count} exemplaire(s) ajouté(s).",
    'lib.ed.labels': "Étiquettes",
    'lib.ed.withdraw': "Proposer au pilon ({count})",
    'lib.ed.noItems': "Aucun exemplaire : ajoutez-en un pour que la notice soit empruntable.",
    'lib.ed.select': "Sélection",
    'lib.ed.selectItem': "Sélectionner {code}",
    'lib.ed.barcode': "Code-barres",
    'lib.ed.coteCol': "Cote",
    'lib.ed.location': "Emplacement",
    'lib.ed.loanStatus': "Statut de prêt",
    'lib.ed.situation': "Situation",
    'lib.ed.condition': "État",
    'lib.ed.until': "jusqu'au {date}",
    'lib.ed.availability': "Disponibilité",
    'lib.ed.availableOf': "{available} disponible(s) sur {total}",
    'lib.ed.holds': "Réservations",
    'lib.ed.noHold': "aucune",
    'lib.ed.reserve': "Réserver pour un lecteur",
    'lib.ed.reserveSub': "Le premier exemplaire disponible lui sera mis de côté",
    'lib.ed.reserveGo': "Réserver",
    'lib.ed.held': "Réservation enregistrée : rang {rank} dans la file.",
    'lib.ed.rank': "rang {rank}",
    'lib.ed.reader': "Lecteur",
    'lib.ed.readerSearch': "Nom ou numéro de carte",
    'lib.ed.readerChange': "Changer",
    'lib.ed.rental': "Location",
    'lib.ed.noRental': "Cette édition n'est pas soumise à des frais de location.",
    'lib.ed.rentalLoan': "{amount} par prêt",
    'lib.ed.rentalDay': "{amount} par jour de prêt",
    'lib.ed.rentalFree': "gratuit pour : {list}",
    'lib.ed.rentalHint': "Se règle dans la notice (tous ses exemplaires) ou sur un exemplaire précis.",
    'lib.ed.record': "La notice",
    'lib.ed.loans': "Prêts",
    'lib.ed.lastLoan': "dernier le {date}",
    'lib.ed.edit': "Modifier la notice",
    'lib.ed.duplicate': "Dupliquer pour une autre édition",
    'lib.ed.duplicateVolume': "Dupliquer pour le tome suivant",
    'lib.ed.delete': "Supprimer la notice",
    'lib.ed.deleteTitle': "Supprimer cette notice ?",
    'lib.ed.deleteText': "La notice n'a ni exemplaire ni réservation ; ses responsabilités et ses liens de cours partent avec elle.",
    'lib.ed.withdrawTitle': "Sortir {count} exemplaire(s) du fonds",
    'lib.ed.withdrawSub': "Une décision motivée : l'exemplaire reste en base avec son historique, et son code-barres ne sera jamais réattribué.",
    'lib.ed.withdrawGo': "Sortir du fonds",
    'lib.ed.withdrawn': "{count} exemplaire(s) sorti(s) du fonds.",
    'lib.ed.reason': "Motif",
    'lib.ed.count': "Nombre d'exemplaires",
    'lib.ed.site': "Site propriétaire",
    'lib.ed.locationHint': "Sans choix, le plan de classement propose le rangement d'après la cote.",
    'lib.ed.locationAuto': "Proposé par le plan de classement",
    'lib.ed.barcodes': "Codes-barres existants (facultatif)",
    'lib.ed.barcodesHint': "Un par ligne, pour reprendre des étiquettes déjà collées ; sinon le masque les génère.",
    'lib.form.new': "Cataloguer une édition",
    'lib.form.edit': "Modifier la notice",
    'lib.form.sub': "Notice par ISBN, puis les groupes de la notice",
    'lib.form.save': "Enregistrer",
    'lib.form.saveAnyway': "Enregistrer quand même",
    'lib.form.duplicate': "Cet ISBN est déjà catalogué.",
    'lib.form.sections': "Groupes de la notice",
    'lib.form.sec.title': "Titre",
    'lib.form.sec.people': "Responsabilités",
    'lib.form.sec.edition': "Édition",
    'lib.form.sec.collection': "Collection",
    'lib.form.sec.set': "Série et tomes",
    'lib.form.sec.ids': "Identifiants",
    'lib.form.sec.description': "Description physique",
    'lib.form.sec.content': "Contenu",
    'lib.form.sec.indexing': "Indexation",
    'lib.form.sec.thesis': "Mémoire ou thèse",
    'lib.form.sec.rental': "Location",
    'lib.form.sec.media': "Médias",
    'lib.form.sec.notes': "Notes",
    'lib.form.byIsbn': "Notice par ISBN",
    'lib.form.isbn': "ISBN",
    'lib.form.isbnHint': "Scannez ou tapez l'ISBN : Kelassi pré-remplit la notice depuis Open Library, la BnF ou Google Books.",
    'lib.form.lookup': "Chercher",
    'lib.form.looking': "Recherche…",
    'lib.form.isbnInvalid': "ISBN non valide : la clé de contrôle ne correspond pas.",
    'lib.form.notFound': "Aucun service n'a trouvé cet ISBN (ou ils ne répondent pas) : la saisie reste manuelle.",
    'lib.form.found': "Trouvé ({source}) : champs pré-remplis, vérifiez puis enregistrez.",
    'lib.form.source.openLibrary': "Open Library",
    'lib.form.source.bnf': "BnF",
    'lib.form.source.google': "Google Books",
    'lib.form.already': "Une notice existe déjà pour cet ISBN :",
    'lib.form.title': "Titre propre",
    'lib.form.subtitle': "Sous-titre",
    'lib.form.parallelTitle': "Titre parallèle (autre langue)",
    'lib.form.originalTitle': "Titre original (traduction)",
    'lib.form.docType': "Type de document",
    'lib.form.peopleHint': "Des autorités partagées, jamais du texte libre ; l'ordre est celui de l'affichage.",
    'lib.form.publishers': "Éditeurs",
    'lib.form.listHint': "Séparés par des virgules ; un nom inconnu crée son autorité.",
    'lib.form.noPeople': "Aucun responsable pour l'instant.",
    'lib.form.newAuthority': "nouvelle autorité",
    'lib.form.existingAuthority': "autorité existante",
    'lib.form.role': "Rôle",
    'lib.form.up': "Monter",
    'lib.form.remove': "Retirer",
    'lib.form.findAuthor': "Chercher un auteur ou une autre forme du nom",
    'lib.form.newAuthor': "Nouvel auteur",
    'lib.form.variants': "formes rejetées : {list}",
    'lib.form.place': "Lieu d'édition",
    'lib.form.editionStatement': "Mention d'édition",
    'lib.form.year': "Année d'édition",
    'lib.form.originalYear': "Année de parution originale",
    'lib.form.detail.editionNumber': "Numéro d'édition",
    'lib.form.detail.legalDeposit': "Dépôt légal",
    'lib.form.detail.reprint': "Réimpression",
    'lib.form.detail.ean': "EAN",
    'lib.form.detail.nationalNumber': "Numéro national",
    'lib.form.detail.internalId': "Identifiant interne",
    'lib.form.detail.illustrations': "Illustrations",
    'lib.form.detail.dimensions': "Dimensions",
    'lib.form.detail.accompanying': "Support d'accompagnement (CD, carte)",
    'lib.form.detail.level': "Niveau",
    'lib.form.collection': "Collection",
    'lib.form.collectionHint': "Une notice d'autorité partagée : un nom inconnu la crée.",
    'lib.form.collectionLinked': "Liée à l'autorité existante ; taper un autre nom la remplace.",
    'lib.form.collectionNumber': "Numéro dans la collection",
    'lib.form.volumeNumber': "Numéro de tome",
    'lib.form.volumeTitle': "Titre du tome",
    'lib.form.volumesTotal': "Nombre total de tomes",
    'lib.form.volumesHint': "Sur la notice de l'ensemble : la fiche affiche les tomes manquants.",
    'lib.form.pages': "Pages ou volumes",
    'lib.form.languages': "Langues",
    'lib.form.languagesHint': "Codes ISO 639 séparés par des virgules (fr, ar, en)",
    'lib.form.audience': "Public visé",
    'lib.form.summary': "Résumé",
    'lib.form.dewey': "Indice Dewey",
    'lib.form.deweyHint': "Ex. 361.3 ; la classe s'affiche ici",
    'lib.form.cote': "Cote",
    'lib.form.coteHint': "La cote de rangement, imprimée sur l'étiquette de dos",
    'lib.form.subjects': "Sujets et mots-clés",
    'lib.form.thesisStudent': "Auteur (étudiant)",
    'lib.form.thesisDirector': "Directeur (enseignant)",
    'lib.form.thesisYear': "Année universitaire",
    'lib.form.thesisDefended': "Soutenu le",
    'lib.form.personSearch': "Nom de la personne",
    'lib.form.confidential': "Confidentiel",
    'lib.form.confidentialHint': "Interdit le prêt et la consultation publique.",
    'lib.form.rental': "Soumettre cette édition à des frais de location",
    'lib.form.rentalHint': "Tous ses exemplaires ; le prêt crée la créance dans la Finance.",
    'lib.form.rentalMode': "Tarif",
    'lib.form.rentalPerLoan': "Par prêt",
    'lib.form.rentalPerDay': "Par jour de prêt",
    'lib.form.rentalAmount': "Montant",
    'lib.form.rentalFree': "Gratuit pour",
    'lib.form.cover': "Couverture",
    'lib.form.coverHint': "JPEG, PNG ou WebP, 2 Mo au plus",
    'lib.form.digitalUrl': "Fichier numérique ou lien",
    'lib.form.publicNote': "Note publique",
    'lib.form.internalNote': "Note interne",
    'lib.form.internalHint': "N'apparaît jamais sur le portail.",
    'lib.item.title': "Exemplaire {code}",
    'lib.item.labels': "Imprimer les étiquettes",
    'lib.item.withdraw': "Proposer au pilon",
    'lib.item.edit': "Modifier",
    'lib.item.card': "La fiche",
    'lib.item.never': "jamais réattribué",
    'lib.item.inventory': "N° d'inventaire",
    'lib.item.suggested': "Rangement proposé",
    'lib.item.suggestedHint': "Plan de classement : {path}",
    'lib.item.sites': "Site",
    'lib.item.sitesValue': "propriétaire {owner} · actuel {current}",
    'lib.item.acquisition': "Acquisition",
    'lib.item.withdrawnAt': "Sorti du fonds le",
    'lib.item.toRepair': "Envoyer en réparation",
    'lib.item.toRepairDone': "Exemplaire envoyé en réparation.",
    'lib.item.repaired': "Réparé, remettre en rayon",
    'lib.item.repairedDone': "Exemplaire remis en rayon.",
    'lib.item.arrived': "Arrivé à son site",
    'lib.item.arrivedDone': "Exemplaire arrivé à son site.",
    'lib.item.found': "Retrouvé",
    'lib.item.foundDone': "Exemplaire retrouvé : il réintègre le fonds.",
    'lib.item.labelsTitle': "Étiquettes",
    'lib.item.history': "Historique",
    'lib.item.noHistory': "Aucun mouvement.",
    'lib.item.by': "par {name}",
    'lib.item.due': "échéance {date}",
    'lib.item.forced': "forcé : {reason}",
    'lib.item.loans': "Prêts de cet exemplaire",
    'lib.item.noLoans': "Jamais prêté.",
    'lib.item.reader': "Lecteur",
    'lib.item.loanedAt': "Prêté le",
    'lib.item.returnedAt': "Rendu",
    'lib.item.anonymous': "historique dépersonnalisé",
    'lib.item.saved': "Exemplaire enregistré.",
    'lib.item.unshelved': "Non rangé",
    'lib.item.rfid': "Étiquette RFID",
    'lib.item.acqMode': "Mode d'acquisition",
    'lib.item.acqOn': "Date d'acquisition",
    'lib.item.supplier': "Fournisseur ou donateur",
    'lib.item.price': "Prix ({currency})",
    'lib.item.invoice': "N° de facture",
    'lib.item.conditionNote': "Constat (dégradation)",
    'lib.item.rental': "Location propre à cet exemplaire",
    'lib.item.rentalHint': "Remplace, pour cet exemplaire, la location de la notice.",
    'lib.auth.title': "Notices d'autorité",
    'lib.auth.sub': "Auteurs, éditeurs, collections, séries et sujets partagés ; les formes rejetées font retrouver les variantes",
    'lib.auth.new': "Nouvelle autorité",
    'lib.auth.kinds': "Sortes d'autorité",
    'lib.auth.search': "Chercher un nom ou une forme rejetée",
    'lib.auth.none': "Aucune autorité.",
    'lib.auth.name': "Nom",
    'lib.auth.variants': "Formes rejetées",
    'lib.auth.variantsHint': "Une par ligne (« Ibn Khaldoun », « ابن خلدون »)",
    'lib.auth.uses': "Notices",
    'lib.auth.usesCount': "{count} notice(s)",
    'lib.auth.actions': "Actions",
    'lib.auth.records': "Ses notices",
    'lib.auth.edit': "Modifier",
    'lib.auth.merge': "Fusionner",
    'lib.auth.mergeTitle': "Fusionner « {name} »",
    'lib.auth.mergeSub': "Le doublon est rattaché à l'autorité gardée ; sa forme devient une forme rejetée.",
    'lib.auth.mergeSearch': "L'autorité à garder",
    'lib.auth.mergeGo': "Fusionner",
    'lib.auth.merged': "Fusion faite : {count} responsabilité(s) rattachée(s).",
    'lib.auth.saved': "Autorité enregistrée.",
    'lib.auth.lastName': "Nom",
    'lib.auth.firstName': "Prénom",
    'lib.auth.dates': "Dates",
    'lib.auth.datesHint': "Ex. 1332-1406",
    'lib.auth.nationality': "Nationalité",
    'lib.auth.note': "Note",
    'lib.loc.title': "Emplacements",
    'lib.loc.sub': "L'arbre des lieux, de profondeur libre, et le plan de classement qui propose le rangement",
    'lib.loc.kinds': "Types de niveau",
    'lib.loc.kindsSub': "Les enchaînements autorisés : une tablette ne contient pas de salle",
    'lib.loc.newSite': "Nouveau site",
    'lib.loc.tree': "Les lieux",
    'lib.loc.empty': "Aucun lieu : créez un site.",
    'lib.loc.expand': "Déplier",
    'lib.loc.collapse': "Replier",
    'lib.loc.status.open': "Ouvert",
    'lib.loc.status.closed': "Fermé",
    'lib.loc.status.works': "En travaux",
    'lib.loc.pick': "Un lieu",
    'lib.loc.pickHint': "Choisissez un lieu dans l'arbre pour le voir, le modifier ou y ranger des exemplaires.",
    'lib.loc.path': "Chemin",
    'lib.loc.code': "Code",
    'lib.loc.codeHint': "Court et unique ; il s'imprime sur l'étiquette de la tablette",
    'lib.loc.capacity': "Capacité indicative",
    'lib.loc.capacityValue': "{capacity} exemplaires · {count} rangés",
    'lib.loc.statusLabel': "Statut",
    'lib.loc.contains': "Contient",
    'lib.loc.containsValue': "{nodes} sous-niveau(x) · {items} exemplaire(s)",
    'lib.loc.bulk': "Déplacer des exemplaires ici",
    'lib.loc.bulkTitle': "Ranger dans « {name} »",
    'lib.loc.bulkScan': "Scannez les exemplaires un à un",
    'lib.loc.bulkEmpty': "Aucun exemplaire scanné.",
    'lib.loc.bulkGo': "Déplacer {count} exemplaire(s)",
    'lib.loc.bulkDone': "{count} exemplaire(s) déplacé(s), chaque déplacement historisé.",
    'lib.loc.bulkUnknown': "Codes-barres inconnus ou retirés : {list}",
    'lib.loc.move': "Déplacer le nœud entier",
    'lib.loc.moveTitle': "Déplacer « {name} »",
    'lib.loc.moveSub': "Avec tout son contenu ; chaque exemplaire garde la trace du déplacement.",
    'lib.loc.newParent': "Nouveau parent",
    'lib.loc.noTarget': "Aucun lieu ne peut recevoir ce type de niveau.",
    'lib.loc.moved': "Nœud déplacé ({count} exemplaire(s)).",
    'lib.loc.addChild': "Ajouter un niveau dessous",
    'lib.loc.addChildUnder': "Ajouter sous « {name} »",
    'lib.loc.edit': "Modifier",
    'lib.loc.delete': "Supprimer",
    'lib.loc.deleteTitle': "Supprimer « {name} » ?",
    'lib.loc.deleteText': "Le lieu est vide : ni sous-niveau, ni exemplaire, ni plage de cotes.",
    'lib.loc.deleted': "Lieu supprimé.",
    'lib.loc.saved': "Lieu enregistré.",
    'lib.loc.kind': "Type de niveau",
    'lib.loc.kindChildren': "Peut contenir",
    'lib.loc.kindSaved': "Types de niveau enregistrés.",
    'lib.loc.newKindCode': "Nouveau type : code",
    'lib.loc.newKindLabel': "Nouveau type : libellé",
    'lib.loc.label': "Libellé",
    'lib.loc.position': "Ordre",
    'lib.loc.ranges': "Plan de classement Dewey",
    'lib.loc.rangesSub': "Les plages de cotes et leur rangement",
    'lib.loc.addRange': "Ajouter une plage",
    'lib.loc.noRange': "Aucune plage : le rangement n'est pas proposé.",
    'lib.loc.range': "Plage de cotes",
    'lib.loc.rangeLabel': "Libellé",
    'lib.loc.shelving': "Rangement",
    'lib.loc.from': "De la cote",
    'lib.loc.to': "À la cote",
    'lib.loc.deleteRange': "Supprimer la plage",
    'lib.loc.rangeDeleted': "Plage supprimée.",
    'lib.loc.rangeSaved': "Plage enregistrée.",
    'lib.loc.unshelved': "{count} exemplaire(s) ne sont rangés nulle part.",
    'lib.sug.title': "Suggestions d'achat",
    'lib.sug.sub': "Celles des lecteurs et des enseignants, et les remplacements proposés après une perte",
    'lib.sug.new': "Nouvelle suggestion",
    'lib.sug.none': "Aucune suggestion.",
    'lib.sug.titleCol': "Titre",
    'lib.sug.authors': "Auteurs",
    'lib.sug.origin': "Origine",
    'lib.sug.date': "Date",
    'lib.sug.quantity': "Quantité",
    'lib.sug.state': "État",
    'lib.sug.note': "Note",
    'lib.sug.saved': "Suggestion mise à jour.",
    'lib.sug.added': "Suggestion enregistrée.",
    'lib.sug.st.suggested': "Suggérée",
    'lib.sug.st.listed': "Sur la liste d'achats",
    'lib.sug.st.ordered': "Commandée",
    'lib.sug.st.received': "Reçue",
    'lib.sug.st.refused': "Refusée",
    'lib.sug.from.reader': "Lecteur",
    'lib.sug.from.teacher': "Enseignant",
    'lib.sug.from.staff': "Bibliothèque",
    'lib.sug.from.loss': "Remplacement d'une perte",
    'lib.population.student': "Étudiant",
    'lib.population.teacher': "Enseignant",
    'lib.population.staff': "Personnel",
    'lib.population.external': "Externe",
    'lib.formula.year': "Année académique",
    'lib.formula.twelve_months': "12 mois glissants",
    'lib.formula.semester': "Semestre",
    'lib.formula.month': "Mois",
    'lib.formula.day': "Journée",
    'lib.feeKind.membership': "Adhésion",
    'lib.feeKind.rental': "Location",
    'lib.feeKind.late': "Retard",
    'lib.feeKind.damage': "Dégradation",
    'lib.feeKind.loss': "Perte",
    'lib.feeKind.processing': "Frais de traitement",
    'lib.feeKind.duplicate': "Duplicata de carte",
    'lib.feature.subscriptions': "Abonnements payants",
    'lib.feature.subscriptions.hint': "formules payantes par catégorie",
    'lib.feature.rental': "Frais de location",
    'lib.feature.rental.hint': "sur les éditions ou exemplaires marqués",
    'lib.feature.fines': "Pénalités financières",
    'lib.feature.fines.hint': "sinon, jours de suspension seulement",
    'lib.feature.own_card': "Carte de bibliothèque spécifique",
    'lib.feature.own_card.hint': "éteint : la carte d'étudiant fait office de carte",
    'lib.feature.opac': "Portail lecteur (OPAC)",
    'lib.feature.opac.hint': "catalogue et espace lecteur sur le portail",
    'lib.feature.online_holds': "Réservations en ligne",
    'lib.feature.online_holds.hint': "depuis le portail et l'application",
    'lib.feature.sms': "Notifications SMS",
    'lib.feature.sms.hint': "en plus du courriel et du portail",
    'lib.penalty.money': "Financières seulement",
    'lib.penalty.suspension': "Jours de suspension seulement",
    'lib.penalty.both': "Financières et jours de suspension",
    'lib.membership.pending': "En attente de paiement",
    'lib.membership.active': "Active",
    'lib.membership.suspended': "Suspendue",
    'lib.membership.expired': "Expirée",
    'lib.membership.cancelled': "Résiliée",
    'lib.cardState.active': "Valide",
    'lib.cardState.lost': "Perdue",
    'lib.cardState.replaced': "Remplacée",
    'lib.cardState.expired': "Expirée",
    'lib.readers.pages': "Pages",
    'lib.readers.prev': "Précédente",
    'lib.readers.next': "Suivante",
    'lib.readers.pageOf': "Page {page} sur {pages}",
    'lib.reader.noFees': "Aucune créance.",
    'lib.reader.fee': "Créance",
    'lib.reader.feeDate': "Émise le",
    'lib.reader.feeDue': "Montant",
    'lib.reader.feeState': "État",
    'lib.reader.actions': "Actions",
    'lib.reader.invoice': "facture {number}",
    'lib.reader.receipt': "reçu {number}",
    'lib.reader.pay': "Noter le reçu",
    'lib.reader.waive': "Remettre",
    'lib.reader.cancelFee': "Annuler",
    'lib.reader.payTitle': "Noter le reçu de la Finance",
    'lib.reader.waiveTitle': "Remettre une créance",
    'lib.reader.cancelFeeTitle': "Annuler une créance émise par erreur",
    'lib.reader.receiptNumber': "Numéro du reçu",
    'lib.reader.receiptHint': "Le reçu remis par la Finance : la bibliothèque n'encaisse rien.",
    'lib.reader.waiveAmount': "Montant ({currency})",
    'lib.reader.reason': "Motif",
    'lib.reader.reasonHint': "Obligatoire, inscrit au journal.",
    'lib.readers.state.all': "Tous",
    'lib.readers.state.late': "En retard",
    'lib.readers.state.debt': "Avec dette",
    'lib.readers.state.suspended': "Suspendus",
    'lib.readers.state.expired': "Sans adhésion",
    'lib.readers.state.external': "Externes",
    'lib.readers.title': "Lecteurs",
    'lib.readers.sub': "Étudiants, enseignants et personnel repris de leur fiche ; lecteurs externes inscrits ici",
    'lib.readers.export': "Exporter",
    'lib.readers.external': "Lecteur externe",
    'lib.readers.enrol': "Inscrire",
    'lib.readers.search': "Nom ou numéro de carte",
    'lib.readers.category': "Catégorie",
    'lib.readers.allCategories': "Toutes les catégories",
    'lib.readers.state': "État",
    'lib.readers.selected': "{count} sélectionné(s)",
    'lib.readers.printCards': "Imprimer les cartes",
    'lib.readers.clearSelection': "Tout désélectionner",
    'lib.readers.count': "{count} lecteur(s)",
    'lib.readers.selectAll': "Tout sélectionner",
    'lib.readers.reader': "Lecteur",
    'lib.readers.card': "Carte",
    'lib.readers.until': "Adhésion jusqu'au",
    'lib.readers.loans': "Prêts",
    'lib.readers.debt': "Dette",
    'lib.readers.status': "Statut",
    'lib.readers.none': "Aucun lecteur ne correspond.",
    'lib.readers.select': "Sélectionner {name}",
    'lib.readers.externalTag': "externe",
    'lib.readers.noMembership': "Sans adhésion",
    'lib.readers.lateCount': "{count} en retard",
    'lib.readers.suspended': "Suspendu",
    'lib.readers.active': "Actif",
    'lib.readers.enrolTitle': "Inscrire une personne",
    'lib.readers.enrolSub': "Étudiant, enseignant ou employé déjà connu : pas de double saisie",
    'lib.readers.person': "Personne",
    'lib.readers.personHint': "Tapez deux lettres du nom au moins.",
    'lib.readers.noPopulation': "Contact",
    'lib.readers.already': "déjà lecteur",
    'lib.readers.categoryAuto': "Par défaut, celle de sa population et de son cycle.",
    'lib.readers.categoryDefault': "Catégorie par défaut",
    'lib.readers.externalTitle': "Nouveau lecteur externe",
    'lib.readers.externalSub': "Lauréat, parent, chercheur, public",
    'lib.readers.create': "Créer",
    'lib.readers.lastName': "Nom",
    'lib.readers.firstNames': "Prénoms",
    'lib.readers.nationalId': "Pièce d'identité (CIN, passeport)",
    'lib.readers.email': "Courriel",
    'lib.readers.phone': "Téléphone",
    'lib.readers.externalHint': "La pièce d'identité et un moyen de contact sont obligatoires. L'adhésion de la catégorie s'ouvre aussitôt ; payante, elle attend son reçu.",
    'lib.reader.title': "Lecteur",
    'lib.reader.back': "Lecteurs",
    'lib.reader.cardNumber': "carte {number}",
    'lib.reader.lend': "Prêter",
    'lib.reader.suspendedBecause': "Suspendu : {reason}. La suspension se lève quand la cause disparaît.",
    'lib.reader.penaltyUntil': "Pénalité de retard : prêt suspendu jusqu'au {date}.",
    'lib.reader.blockedLate': "Un prêt a {days} jours de retard : le prêt est bloqué.",
    'lib.reader.blockedDebt': "Dette de {amount} au-delà du seuil : le prêt est bloqué.",
    'lib.reader.situation': "Situation",
    'lib.reader.changeCategory': "Changer de catégorie",
    'lib.reader.lift': "Lever la suspension",
    'lib.reader.suspend': "Suspendre",
    'lib.reader.category': "Catégorie",
    'lib.reader.email': "Courriel",
    'lib.reader.phone': "Téléphone",
    'lib.reader.debt': "Dette",
    'lib.reader.noDebt': "aucune",
    'lib.reader.late': "Retard",
    'lib.reader.noLate': "aucun",
    'lib.reader.historyOptOut': "Historique désactivé par le lecteur",
    'lib.reader.yes': "oui",
    'lib.reader.no': "non",
    'lib.reader.membership': "Adhésion",
    'lib.reader.membershipSub': "Payée par le module Finance : pas d'encaissement parallèle",
    'lib.reader.newMembership': "Nouvelle adhésion",
    'lib.reader.noMembership': "Aucune adhésion active : le lecteur ne peut pas emprunter.",
    'lib.reader.pending': "Adhésion en attente du paiement de {amount} à la Finance.",
    'lib.reader.active': "Active",
    'lib.reader.membershipLine': "{formula}, du {from} au {to}",
    'lib.reader.automatic': "automatique à l'inscription",
    'lib.reader.formula': "Formule",
    'lib.reader.period': "Période",
    'lib.reader.amount': "Tarif",
    'lib.reader.state': "État",
    'lib.reader.free': "Gratuite",
    'lib.reader.cancelMembership': "Résilier",
    'lib.reader.card': "Carte",
    'lib.reader.cardSub': "Numéro unique, jamais réattribué",
    'lib.reader.issueCard': "Émettre la carte",
    'lib.reader.lostCard': "Déclarer la perte",
    'lib.reader.printCard': "Imprimer",
    'lib.reader.noCard': "Aucune carte active.",
    'lib.reader.studentCard': "Carte d'étudiant",
    'lib.reader.libraryCard': "Carte de bibliothèque",
    'lib.reader.validUntil': "Valable jusqu'au {date}",
    'lib.reader.clearance': "Quitus",
    'lib.reader.clearanceSub': "Libre de tout engagement envers la bibliothèque, vérifiable en ligne",
    'lib.reader.issueClearance': "Délivrer le quitus",
    'lib.reader.clear': "Libre de tout engagement",
    'lib.reader.notClear': "Pas de quitus : {loans} prêt(s) en cours, {debt} dû.",
    'lib.reader.open': "Ouvrir",
    'lib.reader.loans': "Prêts en cours ({count})",
    'lib.reader.noLoans': "Aucun prêt en cours.",
    'lib.reader.item': "Exemplaire",
    'lib.reader.loanedAt': "Prêté le",
    'lib.reader.due': "Échéance",
    'lib.reader.renew': "Prolonger",
    'lib.reader.holds': "Réservations",
    'lib.reader.noHolds': "Aucune réservation.",
    'lib.reader.pickupUntil': "à retirer avant le {date}",
    'lib.reader.rank': "rang {rank}",
    'lib.reader.fees': "Créances",
    'lib.reader.feesSub': "Facturées par la Finance pour un étudiant ; sinon le reçu se note ici",
    'lib.reader.newFee': "Nouvelle créance",
    'lib.reader.reminders': "Relances envoyées",
    'lib.reader.noReminders': "Aucune relance.",
    'lib.reader.manualReminder': "Relance manuelle",
    'lib.reader.guardian': "au responsable légal aussi",
    'lib.reader.history': "Historique de prêt",
    'lib.reader.historyTraced': "Donnée personnelle : sa consultation est tracée (RG-08).",
    'lib.reader.showHistory': "Afficher l'historique",
    'lib.reader.noHistory': "Aucun prêt rendu.",
    'lib.reader.delete': "Supprimer le lecteur",
    'lib.reader.deleteTitle': "Supprimer ce lecteur ?",
    'lib.reader.deleteText': "Seul un lecteur sans prêt, sans créance et sans carte se supprime ; sinon suspendez-le. La personne reste.",
    'lib.reader.lostCardTitle': "Déclarer la carte perdue ?",
    'lib.reader.lostCardText': "L'ancien numéro est invalidé aussitôt ; un duplicata est émis, avec les frais prévus.",
    'lib.reader.lifted': "Suspension levée.",
    'lib.reader.cardIssued': "Carte émise.",
    'lib.reader.renewed': "Prêt prolongé.",
    'lib.reader.duplicated': "Duplicata émis.",
    'lib.reader.saved': "Enregistré.",
    'lib.reader.suspended': "Lecteur suspendu.",
    'lib.reader.membershipCreated': "Adhésion créée.",
    'lib.reader.membershipCancelled': "Adhésion résiliée.",
    'lib.reader.feeCreated': "Créance émise.",
    'lib.reader.clearanceIssued': "Quitus délivré : il est dans la liste.",
    'lib.reader.confirm': "Valider",
    'lib.reader.categoryHint': "La catégorie fixe la politique de prêt et les tarifs.",
    'lib.reader.suspendTitle': "Suspendre le lecteur",
    'lib.reader.until': "Jusqu'au",
    'lib.reader.untilHint': "Vide : jusqu'à levée manuelle.",
    'lib.reader.startsOn': "Début",
    'lib.reader.startsOnHint': "Vide : aujourd'hui, ou à la fin de l'adhésion en cours.",
    'lib.reader.membershipHint': "Gratuite, elle est active aussitôt et la carte suit ; payante, la créance part à la Finance.",
    'lib.reader.feeKind': "Sorte",
    'lib.reader.feeLabel': "Libellé",
    'lib.reader.feeHint': "Pour un étudiant, la Finance émet la facture.",
    'lib.reader.purposeLabel': "Pour",
    'lib.reader.purpose.diploma': "Remise du diplôme",
    'lib.reader.purpose.departure': "Départ",
    'lib.reader.purpose.transfer': "Transfert",
    'lib.reader.purpose.other': "Autre",
    'lib.set.title': "Réglages de la bibliothèque",
    'lib.set.subOn': "Le module est allumé",
    'lib.set.subOff': "Le module est éteint : menus et écrans masqués, données conservées",
    'lib.set.tab.general': "Général",
    'lib.set.tab.policies': "Politiques de prêt",
    'lib.set.tab.categories': "Catégories et tarifs",
    'lib.set.tab.calendar': "Calendrier et délais",
    'lib.set.tab.fees': "Frais",
    'lib.set.saved': "Réglages enregistrés.",
    'lib.set.save': "Enregistrer",
    'lib.set.edit': "Modifier",
    'lib.set.delete': "Supprimer",
    'lib.set.setup.title': "Démarrer la bibliothèque",
    'lib.set.setup.sub': "Le minimum avant le premier prêt : un site, une politique par défaut, un format de code-barres et une carte",
    'lib.set.setup.site': "Le premier site",
    'lib.set.setup.siteSub': "Les autres sites et l'arbre des emplacements se décrivent ensuite",
    'lib.set.setup.siteCode': "Code du site",
    'lib.set.setup.siteCodeHint': "Capitales, chiffres et tirets (RAB)",
    'lib.set.setup.siteLabel': "Nom du site",
    'lib.set.setup.numbering': "Code-barres",
    'lib.set.setup.numberingSub': "Préfixe et séquence ; un code-barres ne se réattribue jamais",
    'lib.set.setup.card': "La carte",
    'lib.set.setup.cardSub': "La carte d'étudiant peut faire office de carte de bibliothèque",
    'lib.set.setup.policy': "La politique par défaut",
    'lib.set.setup.policySub': "Elle vaut pour toute catégorie et tout document, jusqu'à des règles plus précises",
    'lib.set.setup.run': "Démarrer la bibliothèque",
    'lib.set.cardStudent': "La carte d'étudiant sert de carte de bibliothèque",
    'lib.set.cardLibrary': "Une carte de bibliothèque spécifique",
    'lib.set.module': "Le module",
    'lib.set.moduleSub': "Allumé par l'administrateur de l'établissement",
    'lib.set.enabledOn': "Allumé le {date}",
    'lib.set.enabled': "Bibliothèque allumée",
    'lib.set.enabledHint': "Éteindre masque menus et écrans et garde toutes les données",
    'lib.set.features': "Sous-fonctions",
    'lib.set.featuresSub': "Chacune s'allume séparément",
    'lib.set.penalties': "Pénalités et quitus",
    'lib.set.penaltyMode': "Régime des pénalités de retard",
    'lib.set.penaltyModeHint': "Modulable par politique de prêt",
    'lib.set.quitusBlocks': "Bloquer la remise du diplôme sans quitus",
    'lib.set.quitusBlocksHint': "Éteint par défaut ; les autres modules peuvent lire le quitus",
    'lib.set.foundRefund': "Perte retrouvée après paiement",
    'lib.set.refund.full': "Remboursement total",
    'lib.set.refund.half': "Remboursement partiel (50 %)",
    'lib.set.refund.none': "Aucun remboursement",
    'lib.set.numbering': "Codes et numérotation",
    'lib.set.barcodePrefix': "Préfixe des codes-barres",
    'lib.set.barcodeDigits': "Chiffres de la séquence",
    'lib.set.cardPrefix': "Préfixe des cartes",
    'lib.set.barcodeExample': "Prochain format : {example}",
    'lib.set.privacy': "Données personnelles",
    'lib.set.historyYears': "Anonymiser l'historique de prêt après",
    'lib.set.historyYearsHint': "Sauf dette en cours (RG-08)",
    'lib.set.years': "{count} an(s)",
    'lib.set.traceHistory': "Garder l'historique de prêt des lecteurs",
    'lib.set.offNote': "Éteindre le module masque menus et écrans et conserve toutes les données ; un prêt en cours demande une confirmation explicite.",
    'lib.set.offTitle': "Éteindre la bibliothèque ?",
    'lib.set.offText': "Menus et écrans disparaissent ; toutes les données restent et se retrouvent à la réactivation.",
    'lib.set.offForce': "{count} prêt(s) sont en cours : confirmez-vous ?",
    'lib.set.off': "Éteindre",
    'lib.set.offAnyway': "Éteindre malgré les prêts",
    'lib.set.graceDays': "Délai de grâce (jours)",
    'lib.set.pickupDays': "Délai de retrait (jours ouvrés)",
    'lib.set.blockDays': "Blocage après (jours de retard)",
    'lib.set.blockDebt': "Blocage au-delà de ({currency})",
    'lib.set.reminders': "Relances (jours par rapport à l'échéance)",
    'lib.set.remindersHint': "Ex. -2, +1, +7, +30 ; la dernière vaut mise en demeure et suspension",
    'lib.set.lossAfterDays': "Présomption de perte après (jours)",
    'lib.set.lossAfterDaysHint': "Comptés après la mise en demeure",
    'lib.set.autoMembership': "Adhésion automatique à l'inscription",
    'lib.set.autoMembershipHint': "Toute inscription de l'année ouvre l'adhésion, qui expire avec l'année académique",
    'lib.set.cal.open': "Jours d'ouverture",
    'lib.set.cal.openSub': "Les jours fermés ne comptent pas en retard ; une échéance fermée passe au jour ouvert suivant",
    'lib.set.cal.closures': "Fermetures de la bibliothèque",
    'lib.set.cal.closuresSub': "En plus des fériés et vacances de la Planification",
    'lib.set.cal.from': "Du",
    'lib.set.cal.to': "Au",
    'lib.set.cal.label': "Motif",
    'lib.set.cal.remove': "Retirer cette fermeture",
    'lib.set.cal.add': "Ajouter une fermeture",
    'lib.set.cal.delays': "Délais et seuils",
    'lib.set.cal.priority': "Priorité des réservations",
    'lib.set.cal.prioritySub': "À arrivée égale, la première population passe devant",
    'lib.set.cal.up': "Monter",
    'lib.set.cal.down': "Descendre",
    'lib.set.fees.title': "Frais",
    'lib.set.fees.sub': "Il n'y a pas de caution ; certains ouvrages se louent",
    'lib.set.processingFee': "Frais de traitement d'une perte ({currency})",
    'lib.set.duplicateFee': "Duplicata de carte ({currency})",
    'lib.set.fees.rental': "Frais de location",
    'lib.set.rentalPayBefore': "Exiger le paiement avant la remise de l'ouvrage",
    'lib.set.rentalPayBeforeHint': "Sinon, ajoutés au compte du lecteur",
    'lib.set.rentalRefund': "Rembourser au prorata un retour anticipé",
    'lib.set.fees.rentalHint': "Le tarif de location se règle sur la notice ou l'exemplaire, par catégorie de lecteur.",
    'lib.set.fees.replacement': "Prix de remplacement forfaitaires",
    'lib.set.fees.replacementSub': "Quand l'exemplaire perdu n'a pas de prix d'achat",
    'lib.set.pol.title': "Politiques de prêt",
    'lib.set.pol.sub': "Catégorie de lecteur × type de document × statut de prêt de l'exemplaire ; la plus précise s'applique",
    'lib.set.pol.new': "Nouvelle règle",
    'lib.set.pol.editTitle': "Modifier la règle",
    'lib.set.pol.category': "Catégorie",
    'lib.set.pol.docType': "Type de document",
    'lib.set.pol.loanStatus': "Statut",
    'lib.set.pol.maxLoans': "Prêts",
    'lib.set.pol.duration': "Durée",
    'lib.set.pol.renewal': "Prolongation",
    'lib.set.pol.maxHolds': "Réservations",
    'lib.set.pol.penalty': "Pénalité de retard",
    'lib.set.pol.default': "par défaut",
    'lib.set.pol.allCategories': "Toutes catégories",
    'lib.set.pol.allTypes': "Tous types",
    'lib.set.pol.allStatuses': "Tous statuts",
    'lib.set.pol.hours': "{count} h",
    'lib.set.pol.days': "{count} j",
    'lib.set.pol.noRenewal': "aucune",
    'lib.set.pol.renewalText': "{count} × {days} j",
    'lib.set.pol.fineText': "{amount} / j, plafond {cap}",
    'lib.set.pol.suspText': "{days} j de suspension / j",
    'lib.set.pol.none': "aucune",
    'lib.set.pol.allRules': "Pour toutes les règles",
    'lib.set.pol.block': "Seuil de blocage",
    'lib.set.pol.blockText': "retard de plus de {days} jours ou dette de plus de {amount}",
    'lib.set.pol.openDays': "{count} jours ouvrés",
    'lib.set.pol.yearEnd': "Fin d'année",
    'lib.set.pol.yearEndText': "l'échéance ne dépasse pas la fin de l'adhésion",
    'lib.set.pol.calendar': "Calendrier",
    'lib.set.pol.calendarText': "fermetures, fériés et vacances ne comptent pas",
    'lib.set.pol.daysLabel': "Durée (jours)",
    'lib.set.pol.hoursLabel': "ou durée (heures)",
    'lib.set.pol.renewals': "Prolongations",
    'lib.set.pol.renewalDays': "Durée d'une prolongation (jours)",
    'lib.set.pol.finePerDay': "Pénalité par jour ({currency})",
    'lib.set.pol.fineCap': "Plafond ({currency})",
    'lib.set.pol.suspensionPerDay': "Jours de suspension par jour de retard",
    'lib.set.pol.modeHint': "Vide : le régime de l'établissement",
    'lib.set.pol.modeInherit': "Régime de l'établissement",
    'lib.set.pol.maxByType': "Plafonds par type",
    'lib.set.pol.maxByTypeHint': "Ex. media=2, thesis=1",
    'lib.set.pol.defaultHint': "La règle par défaut ne se supprime pas : elle vaut quand aucune autre ne s'applique.",
    'lib.set.cat.title': "Catégories de lecteurs",
    'lib.set.cat.sub': "La catégorie fixe la politique de prêt, le tarif d'adhésion et les tarifs de location",
    'lib.set.cat.new': "Nouvelle catégorie",
    'lib.set.cat.editTitle': "Modifier la catégorie",
    'lib.set.cat.label': "Libellé",
    'lib.set.cat.code': "Code",
    'lib.set.cat.codeHint': "Minuscules et soulignés ; il ne change plus",
    'lib.set.cat.population': "Population",
    'lib.set.cat.cycle': "Cycle",
    'lib.set.cat.cycleHint': "Pour les étudiants : la catégorie de ce cycle",
    'lib.set.cat.anyCycle': "Tous les cycles",
    'lib.set.cat.formula': "Formule",
    'lib.set.cat.fee': "Adhésion",
    'lib.set.cat.feeLabel': "Tarif d'adhésion ({currency})",
    'lib.set.cat.feeHint': "0 : gratuite",
    'lib.set.cat.readers': "Lecteurs",
    'lib.stats.title': "Statistiques",
    'lib.stats.back': "Tableau de bord",
    'lib.stats.sub': "Du {from} au {to}",
    'lib.stats.register': "Registre d'inventaire {year}",
    'lib.stats.exportLoans': "Exporter les prêts",
    'lib.stats.from': "Du",
    'lib.stats.to': "Au",
    'lib.stats.apply': "Afficher",
    'lib.stats.loans': "Prêts",
    'lib.stats.loansNote': "sur la période",
    'lib.stats.activeReaders': "Lecteurs actifs",
    'lib.stats.activeReadersNote': "au moins un prêt",
    'lib.stats.rotation': "Taux de rotation",
    'lib.stats.rotationNote': "prêts par exemplaire ({items} exemplaires)",
    'lib.stats.lateRate': "Taux de retard",
    'lib.stats.lateRateNote': "prêts rendus après l'échéance",
    'lib.stats.neverLoaned': "Jamais prêtés",
    'lib.stats.neverLoanedNote': "exemplaires",
    'lib.stats.value': "Valeur du fonds",
    'lib.stats.valueNote': "prix d'achat, sinon forfait",
    'lib.stats.byMonth': "Prêts par mois",
    'lib.stats.top': "Les plus empruntés",
    'lib.stats.work': "Ouvrage",
    'lib.stats.empty': "Pas encore de prêt sur la période.",
    'lib.stats.byCategory': "Par catégorie de lecteur",
    'lib.stats.byTraining': "Par formation",
    'lib.stats.byType': "Par type de document",
    'lib.stats.byClass': "Fonds par classe Dewey",
    'lib.stats.byClassSub': "Exemplaires présents",
    'lib.stats.noDewey': "Sans indice",
    'lib.imp.title': "Import et export",
    'lib.imp.sub': "Tout import passe par une simulation ligne par ligne avant la moindre écriture",
    'lib.imp.upload': "Importer un fichier",
    'lib.imp.uploadSub': "La simulation montre ce qui sera créé, mis à jour ou rejeté",
    'lib.imp.target': "Objet",
    'lib.imp.format': "Format",
    'lib.imp.file': "Fichier",
    'lib.imp.fileHint': "CSV (point-virgule ou virgule, UTF-8), ISO 2709 ou MARCXML ; 30 Mo au plus",
    'lib.imp.sending': "Envoi…",
    'lib.imp.simulate': "Simuler l'import",
    'lib.imp.template': "Gabarit CSV",
    'lib.imp.note': "Un import entier s'annule tant qu'aucun prêt n'a touché ses exemplaires. Contenu HTML ou script neutralisé, codes-barres uniques, ISBN contrôlés avant toute écriture.",
    'lib.imp.exports': "Exporter",
    'lib.imp.exportsSub': "Dans les droits de celui qui lance l'export",
    'lib.imp.recent': "Imports récents",
    'lib.imp.none': "Aucun import pour l'instant.",
    'lib.imp.when': "Déposé",
    'lib.imp.result': "Résultat",
    'lib.imp.state': "État",
    'lib.imp.countsLine': "{create} créées, {update} mises à jour, {reject} rejetées",
    'lib.imp.back': "Import et export",
    'lib.imp.rows': "{count} ligne(s)",
    'lib.imp.mode': "Mode",
    'lib.imp.key': "Clé de rapprochement",
    'lib.imp.site': "Site par défaut",
    'lib.imp.siteHint': "Le plan de classement affine l'emplacement par cote",
    'lib.imp.siteFirst': "Le premier site",
    'lib.imp.columns': "Correspondance des colonnes",
    'lib.imp.columnsSub': "Mémorisée avec l'import ; une colonne ignorée n'est pas lue",
    'lib.imp.column': "Colonne du fichier",
    'lib.imp.field': "Champ Kelassi",
    'lib.imp.fieldFor': "Champ pour la colonne {column}",
    'lib.imp.ignore': "Ignorée",
    'lib.imp.resimulate': "Relancer la simulation",
    'lib.imp.resimulated': "Simulation refaite.",
    'lib.imp.marc': "Notices MARC",
    'lib.imp.marcSub': "Les zones MARC se lisent d'office",
    'lib.imp.marcText': "Titre (200 ou 245), responsables (700 ou 100), éditeur (210 ou 264), ISBN (010 ou 020), Dewey (676 ou 082), exemplaires (995 ou 952).",
    'lib.imp.created': "Créées",
    'lib.imp.createdNote': "notices nouvelles",
    'lib.imp.updated': "Mises à jour",
    'lib.imp.updatedNote': "rapprochées",
    'lib.imp.rejected': "Rejetées",
    'lib.imp.rejectedNote': "rapport téléchargeable",
    'lib.imp.simulation': "Simulation, ligne par ligne",
    'lib.imp.simulationSub': "Rien n'est encore écrit",
    'lib.imp.report': "Rapport, ligne par ligne",
    'lib.imp.lines': "Lignes",
    'lib.imp.line': "Ligne",
    'lib.imp.record': "Notice",
    'lib.imp.filter.all': "Toutes",
    'lib.imp.filter.create': "Créées",
    'lib.imp.filter.update': "Mises à jour",
    'lib.imp.filter.reject': "Rejetées",
    'lib.imp.downloadReport': "Rapport des lignes",
    'lib.imp.run': "Importer {count} ligne(s)",
    'lib.imp.ran': "Import exécuté.",
    'lib.imp.undo': "Annuler l'import",
    'lib.imp.undone': "Import annulé.",
    'lib.imp.undoTitle': "Annuler cet import ?",
    'lib.imp.undoText': "Ses {count} exemplaire(s) et les notices qu'il a créées sans autre exemplaire sont retirés.",
    'lib.imp.dirty': "Réglages changés : relancez la simulation avant d'importer.",
    'lib.imp.touched': "{count} prêt(s) ont touché ses exemplaires : l'import ne s'annule plus.",
    'lib.imp.target.records': "Notices et exemplaires",
    'lib.imp.target.items': "Exemplaires (par ISBN)",
    'lib.imp.target.authorities': "Autorités",
    'lib.imp.target.readers': "Lecteurs externes",
    'lib.imp.target.locations': "Emplacements",
    'lib.imp.headTitle.records': "Importer des notices",
    'lib.imp.headTitle.items': "Importer des exemplaires",
    'lib.imp.headTitle.authorities': "Importer des autorités",
    'lib.imp.headTitle.readers': "Importer des lecteurs externes",
    'lib.imp.headTitle.locations': "Importer des emplacements",
    'lib.imp.format.csv': "CSV ou Excel (CSV)",
    'lib.imp.format.unimarc': "UNIMARC (ISO 2709)",
    'lib.imp.format.marc21': "MARC21 (ISO 2709)",
    'lib.imp.format.marcxml': "MARCXML",
    'lib.imp.format.isbn': "Liste d'ISBN",
    'lib.imp.st.simulated': "Simulé",
    'lib.imp.st.queued': "En file",
    'lib.imp.st.done': "Importé",
    'lib.imp.st.undone': "Annulé",
    'lib.imp.mode.both': "Créer et mettre à jour",
    'lib.imp.mode.create': "Création seule",
    'lib.imp.mode.update': "Mise à jour seule",
    'lib.imp.key.isbn': "ISBN",
    'lib.imp.key.barcode': "Code-barres",
    'lib.imp.key.external': "Titre et année",
    'lib.imp.action.create': "Création",
    'lib.imp.action.update': "Mise à jour",
    'lib.imp.action.reject': "Rejetée",
    'lib.imp.export.catalog': "Catalogue",
    'lib.imp.export.items': "Exemplaires",
    'lib.imp.export.authorities': "Autorités",
    'lib.imp.export.readers': "Lecteurs",
    'lib.imp.export.locations': "Emplacements",
    'lib.imp.export.loans': "Prêts",
    'lib.imp.export.fees': "Créances",
    'lib.imp.export.holds': "Réservations",
    'lib.imp.exportFormat.csv': "CSV",
    'lib.imp.exportFormat.marc21': "MARC21",
    'lib.imp.exportFormat.unimarc': "UNIMARC",
    'lib.imp.exportFormat.ris': "RIS",
    'lib.imp.exportFormat.bibtex': "BibTeX",
    'lib.imp.exportFormat.dc': "Dublin Core",
    'lib.imp.f.title': "Titre",
    'lib.imp.f.subtitle': "Sous-titre",
    'lib.imp.f.authors': "Auteurs (Nom, Prénom ; …)",
    'lib.imp.f.publisher': "Éditeur",
    'lib.imp.f.place': "Lieu d'édition",
    'lib.imp.f.year': "Année",
    'lib.imp.f.isbn': "ISBN",
    'lib.imp.f.issn': "ISSN",
    'lib.imp.f.cote': "Cote",
    'lib.imp.f.dewey': "Indice Dewey",
    'lib.imp.f.language': "Langue",
    'lib.imp.f.docType': "Type de document",
    'lib.imp.f.pages': "Pages",
    'lib.imp.f.summary': "Résumé",
    'lib.imp.f.collection': "Collection",
    'lib.imp.f.subjects': "Sujets",
    'lib.imp.f.barcode': "Code-barres",
    'lib.imp.f.location': "Emplacement (code)",
    'lib.imp.f.price': "Prix",
    'lib.imp.f.loanStatus': "Statut de prêt",
    'lib.imp.f.acquiredOn': "Date d'achat",
    'lib.imp.f.supplier': "Fournisseur",
    'lib.imp.f.kind': "Sorte d'autorité",
    'lib.imp.f.name': "Nom",
    'lib.imp.f.firstName': "Prénom",
    'lib.imp.f.variants': "Formes rejetées",
    'lib.imp.f.lastName': "Nom de famille",
    'lib.imp.f.firstNames': "Prénoms",
    'lib.imp.f.nationalId': "Pièce d'identité",
    'lib.imp.f.email': "Courriel",
    'lib.imp.f.category': "Catégorie",
    'lib.imp.f.path': "Chemin (Site/Salle/Épi)",
    'lib.imp.f.code': "Code",
    'lib.imp.f.externalId': "Identifiant externe",
    'lib.form.sec.courses': "Bibliographie de cours",
    'lib.form.coursesHint': "Les formations et matières qui recommandent l'ouvrage ; il paraît sur la fiche du cours et dans le portail de leurs étudiants.",
    'lib.form.noCourses': "Aucun cours ne recommande cet ouvrage.",
    'lib.form.courseAdd': "Ajouter une formation ou une matière",
    'lib.form.courseSearch': "Code ou intitulé de la formation ou de la matière",
    'lib.form.trainingSearch': "Code ou intitulé de la formation",
    'lib.form.courseIsTraining': "Formation",
    'lib.form.courseTeacher': "Enseignant qui recommande",
    'lib.form.courseNote': "Note",
    'lib.form.courseNoteHint': "Par exemple : lecture obligatoire, chapitres 2 à 4",
    'lib.form.removeCourse': "Retirer ce cours",
    'lib.form.thesisTraining': "Formation",
    'lib.form.setParent': "Notice de l'ensemble",
    'lib.form.setSearch': "Titre, auteur ou ISBN de l'ensemble",
    'lib.form.setHint': "Pour un tome : la notice de l'ouvrage en plusieurs volumes, qui affichera les tomes présents et manquants.",
    'lib.form.setRemove': "Détacher de l'ensemble",
} as const;

export type TranslationKey = keyof typeof fr;

const en: Record<TranslationKey, string> = {
    'app.name': 'Kelassi',
    'signIn.title': 'Sign in',
    'signIn.email': 'E-mail address',
    'signIn.password': 'Password',
    'signIn.submit': 'Sign in',
    'signIn.pending': 'Signing in…',
    'signIn.refused': 'Invalid credentials.',
    'signIn.unreachable': 'The API is unreachable.',
    'signIn.forgot': 'Forgot your password?',
    'signIn.passwordChanged': 'Password changed. All your sessions were closed: please sign in again.',
    'signIn.passwordSet': 'Password saved. You can now sign in.',
    'session.loading': 'Loading…',
    'session.expired': 'Your session has expired. Please sign in again.',
    'session.signOut': 'Sign out',
    'session.changePassword': 'Change my password',
    'menu.empty': 'No menu entry is open to you.',
    'menu.label': 'Main navigation',
    'language.label': 'Language',
    'screen.pending': 'This screen is not built yet.',
    'screen.pendingHint': 'The route is declared and the permission is checked: only the content is missing.',
    'notFound.title': 'Page not found',
    'notFound.body': 'This address matches no screen of the application.',
    'notFound.back': 'Back to home',

    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.saving': 'Saving…',
    'common.confirm': 'Confirm',
    'common.back': 'Back',
    'common.never': 'Never',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.loading': 'Loading…',
    'common.forbidden': 'You are not allowed to do that.',
    'common.notFound': 'Not found.',
    'common.error': 'Something went wrong. Please try again.',

    'population.staff': 'Staff',
    'population.teacher': 'Teacher',
    'population.student': 'Student',
    'population.guardian': 'Parent or guardian',

    'accounts.title': 'Accounts',
    'accounts.new': 'New account',
    'accounts.search': 'Search a name or an address',
    'accounts.filter.population': 'Population',
    'accounts.filter.status': 'Status',
    'accounts.filter.all': 'All',
    'accounts.status.active': 'Active',
    'accounts.status.inactive': 'Deactivated',
    'accounts.status.locked': 'Locked',
    'accounts.status.must_change_password': 'Password to change',
    'accounts.column.name': 'Name',
    'accounts.column.population': 'Population',
    'accounts.column.roles': 'Roles',
    'accounts.column.status': 'Status',
    'accounts.column.lastLogin': 'Last sign-in',
    'accounts.noRole': 'No role',
    'accounts.empty': 'No account matches.',
    'accounts.readOnly': 'Beyond your reach',

    'account.email': 'E-mail address',
    'account.displayName': 'Display name',
    'account.population': 'Population',
    'account.status': 'Status',
    'account.lastLogin': 'Last sign-in',
    'account.password': 'Password',
    'account.password.none': 'Not set yet (awaiting activation)',
    'account.password.provisional': 'Provisional, to be changed at next sign-in',
    'account.password.set': 'Set on {date}',
    'account.lockedUntil': 'Locked until {date}',
    'account.createdAt': 'Created on',
    'account.assignments': 'Roles and reach',
    'account.assignments.none': 'No role: this account can sign in but has access to nothing.',
    'account.assignments.hint': 'Roles are granted from the “Roles and assignments” screen.',
    'account.scope.centers': 'Centres',
    'account.scope.cycles': 'Cycles',
    'account.scope.domains': 'Domains',
    'account.scope.products': 'Products',
    'account.scope.allCenters': 'All centres',
    'account.scope.allCycles': 'All cycles',
    'account.scope.allDomains': 'All domains',
    'account.scope.allProducts': 'All products',
    'account.period': 'Period',
    'account.period.always': 'No limit',
    'account.period.from': 'from {date}',
    'account.period.until': 'until {date}',
    'account.outOfReach': 'This account reaches beyond your scope: you can view it, not change it.',

    'account.action.edit': 'Edit',
    'account.action.deactivate': 'Deactivate',
    'account.action.activate': 'Reactivate',
    'account.action.activateLocked': 'Unlock',
    'account.action.assignPassword': 'Set a provisional password',
    'account.action.sendLink': 'Send a link by e-mail',
    'account.action.sendLink.confirm': 'Send {email} a link to set their password? It will remain valid for 72 hours.',
    'account.done.deactivate': 'Account deactivated, sessions closed.',
    'account.done.activate': 'Account reactivated.',
    'account.done.link': 'Link sent to {email}.',
    'account.done.password': 'Provisional password saved. Pass it on to the holder: they will have to change it at their next sign-in.',
    'account.done.saved': 'Changes saved.',
    'account.done.created': 'Account created.',
    'account.done.createdLink': 'Account created. An activation link was sent to {email}.',
    'account.done.createdPassword': 'Account created. Pass the provisional password on to the holder: they will have to change it at their first sign-in.',

    'app.home': 'Home',
    'menu.expand': 'Expand the menu',
    'menu.collapse': 'Collapse the menu',
    'menu.open': 'Show the menu',
    'menu.close': 'Close the menu',
    'common.moreActions': 'More actions',
    'accounts.count': '{count} account(s)',
    'accounts.filter.allPopulations': 'All populations',
    'accounts.filter.allStatuses': 'All statuses',
    'accounts.column.actions': 'Actions',
    'account.action.deactivate.confirmNamed': "Deactivate {name}'s account? Its open sessions will be closed immediately.",
    'account.action.sendLink.short': 'Send the link',
    'account.action.assignPassword.short': 'Provisional password',
    'pagination.rowsPerPage': 'Rows per page',
    'pagination.range': '{from}–{to} of {total}',
    'pagination.first': 'First page',
    'pagination.previous': 'Previous page',
    'pagination.next': 'Next page',
    'pagination.last': 'Last page',
    'pagination.page': 'Page {page}',
    'create.title': 'New account',
    'create.credential': 'Password',
    'create.credential.link': 'Send an activation link',
    'create.credential.linkHint': 'The holder chooses their password; nobody else knows it. The link remains valid for 72 hours.',
    'create.credential.password': 'Enter a provisional password',
    'create.credential.passwordHint': 'You pass it on yourself; the holder will have to change it at their first sign-in.',
    'create.credential.required': 'Choose how the holder will get their password.',
    'create.generate': 'Generate',
    'create.submit': 'Create the account',
    'create.noRoleYet': 'The account is created without any role. Grant its roles afterwards from the “Roles and assignments” screen.',

    'password.current': 'Current password',
    'password.new': 'New password',
    'password.confirm': 'Confirm the new password',
    'password.rule': 'At least 12 characters. A sentence is safer than a complicated word.',
    'password.mismatch': 'The two entries differ.',
    'password.change.title': 'Change my password',
    'password.change.submit': 'Change the password',
    'password.change.hint': 'All your sessions will be closed, this one included: you will sign in again with the new password.',
    'password.imposed.title': 'Choose your password',
    'password.imposed.body': 'The password you used is provisional: it was set by an administrator. Replace it before going on.',
    'password.provisional': 'Provisional password',
    'password.provisional.hint': 'At least 12 characters. The holder will have to change it at their next sign-in; their open sessions will be closed.',

    'forgot.title': 'Forgotten password',
    'forgot.body': 'Enter your address: if an account matches it, you will receive a link to choose a new password.',
    'forgot.submit': 'Send the link',
    'forgot.sent': 'If an account exists for this address, a link has just been sent to it. It remains valid for one hour.',
    'forgot.tooMany': 'Too many requests from this device. Try again in a few minutes.',
    'forgot.back': 'Back to sign in',

    'reset.title': 'Choose a password',
    'reset.submit': 'Save the password',
    'reset.missing': 'This link is incomplete. Open it directly from the e-mail you received.',
    'reset.invalid': 'This link is no longer valid: it has already been used, has expired, or a newer link was sent.',
    'reset.askAgain': 'Ask for a new link',

    'violation.blank': 'This field is required.',
    'violation.email': 'Invalid e-mail address.',
    'violation.tooShort': 'Too short.',
    'violation.tooLong': 'Too long.',
    'violation.sameAsCurrent': 'The new password must differ from the current one.',
    'violation.emailTaken': 'An account already uses this address.',
    'violation.currentPasswordInvalid': 'The current password is incorrect.',
    'violation.tokenInvalid': 'This link is no longer valid.',
    'violation.choice': 'Unrecognised value.',
    'common.close': 'Close',
    'menu.trail': 'Breadcrumb',
    'photo.title': 'Profile photo',
    'photo.change': 'Change the photo',
    'photo.drop': 'Drop a photo here',
    'photo.dropAnother': 'Drop another photo',
    'photo.or': 'or',
    'photo.choose': 'Choose a file',
    'photo.rules': 'JPG, PNG or WebP · 2 MB max · at least 200 × 200 px',
    'photo.crop': 'Framing',
    'photo.cropHint': 'Drag the photo, or use the arrow keys, to place it in the circle.',
    'photo.zoom': 'Zoom',
    'photo.preview': 'Preview',
    'photo.remove': 'Remove the photo',
    'photo.error.type': 'This file is not a readable JPG, PNG or WebP image.',
    'photo.error.weight': 'This image is larger than 2 MB.',
    'photo.error.small': 'This image is too small: at least 200 × 200 px.',
    'photo.done.saved': 'Photo saved.',
    'photo.done.removed': 'Photo removed.',
    'profile.title': 'My profile',
    'profile.open': 'My profile',
    'profile.card': 'Profile',
    'profile.information': 'Information',
    'profile.security': 'Security',
    'profile.roles': 'Roles',
    'profile.rolesNone': 'None',
    'profile.institution': 'Institution',
    'profile.lock': 'Lock',
    'profile.lockNone': 'None',
    'profile.editProfile': 'Edit the profile',
    'profile.noRolesTitle': 'No role granted',
    'account.action.sendLink.tiny': 'Link',
    'account.action.assignPassword.tiny': 'Provisional',
    'account.action.deactivate.long': 'Deactivate the account',
    'roles.title': "Roles and assignments",
    'roles.subtitle': "Who holds which role, and over which part of the school.",
    'roles.views': "Views",
    'roles.tab.assignments': "Assignments",
    'roles.tab.roles': "Roles",
    'roles.catalogue.note': "Roles ship with Kelassi and are the same in every institution: their content is not edited here. The school chooses who holds them and over which part of the school, from the “Assignments” tab.",
    'roles.provided': "Provided by Kelassi",
    'roles.holders': "{count} holder(s) · see the assignments",
    'roles.grantThis': "Grant this role",
    'assignment.new': "Grant a role",
    'assignment.edit': "Edit the assignment",
    'assignment.submit': "Grant the role",
    'assignment.search': "Search an account",
    'assignment.filter.role': "Role",
    'assignment.filter.allRoles': "All roles",
    'assignment.filter.center': "Centre",
    'assignment.filter.state': "Status",
    'assignment.filter.live': "Current and upcoming",
    'assignment.filter.current': "Current",
    'assignment.filter.upcoming': "Upcoming",
    'assignment.filter.expired': "Expired",
    'assignment.filter.revoked': "Revoked",
    'assignment.filter.all': "All, history included",
    'assignment.filter.oneAccount': "Only one account's assignments are shown.",
    'assignment.filter.clear': "Show all",
    'assignment.empty': "No assignment matches.",
    'assignment.column.account': "Account",
    'assignment.column.role': "Role",
    'assignment.column.scope': "Reach",
    'assignment.column.grantedBy': "Granted by",
    'assignment.state.current': "Current",
    'assignment.state.upcoming': "Upcoming",
    'assignment.state.expired': "Expired",
    'assignment.state.revoked': "Revoked",
    'assignment.grantedBy.system': "Installation",
    'assignment.revoke': "Revoke the role",
    'assignment.revoke.title': "Revoke the role?",
    'assignment.revoke.confirm': "{name} loses the role {role} at once, open sessions included. The assignment stays in the history and can be granted again.",
    'assignment.regrant': "Grant again",
    'assignment.done.revoked': "Role {role} revoked from {name}.",
    'assignment.done.updated': "{name}'s assignment updated.",
    'assignment.done.granted': "Role {role} granted to {name}.",
    'assignment.scope.everything': "The whole school",
    'assignment.self': "Your own assignment: another administrator must change it.",
    'assignment.you': "You",
    'assignment.period.revokedOn': "Revoked on {date}",
    'assignment.period.between': "From {from} to {until}",
    'assignment.period.from': "From {date}",
    'assignment.period.until': "Until {date}",
    'permission.subject.user_account': "Accounts",
    'permission.subject.role': "Roles",
    'permission.subject.role_assignment': "Assignments",
    'permission.subject.menu': "Menu",
    'permission.subject.identity_provider': "Directories",
    'permission.subject.login_policy': "Sign-in policy",
    'permission.subject.authentication_log': "Log",
    'assignment.step.account': "Account",
    'assignment.step.role': "Role",
    'assignment.step.roleHint': "Roles are provided by Kelassi; their content is not edited.",
    'assignment.step.scope': "Reach",
    'assignment.step.scopeHint': "Over which part of the school the role applies.",
    'assignment.step.period': "Period",
    'assignment.step.periodHint': "Optional.",
    'assignment.account.change': "Change",
    'assignment.account.hint': "Your own account is not offered: another administrator must grant you a role.",
    'assignment.account.search': "Search by name or address",
    'assignment.account.none': "No active account matches.",
    'assignment.role.permissions': "{count} permission(s)",
    'assignment.role.unreachable': "You cannot grant this role: it would cover a part of the school you do not reach.",
    'assignment.scope.rule': "For each dimension: everything, or only a part. “All” also covers what will be created later. “Only some” with nothing ticked is not allowed.",
    'assignment.scope.some': "Only some",
    'assignment.scope.emptySome': "Tick at least one item, or choose “All”.",
    'assignment.scope.outOfReach': "beyond your reach",
    'assignment.scope.inactive': "closed",
    'assignment.scope.noneDefined': "Nothing defined yet ({dimension}): the role will cover whatever is created.",
    'assignment.scope.ownLimit': "Your own reach goes no further: you cannot grant more.",
    'assignment.period.start': "Start",
    'assignment.period.startHint': "Empty: the role applies as soon as it is saved.",
    'assignment.period.end': "End",
    'assignment.period.endHint': "A date: the role ends by itself at the end of that day.",
    'assignment.summary.title': "In plain words",
    'assignment.summary.incomplete': "Choose an account and a role.",
    'assignment.summary.gives': "What the role {role} gives",
    'assignment.summary.everythingWarning': "Widest reach: the whole school, including centres, cycles, domains and products created later.",
    'assignment.summary.manyPermissions': "use the {count} permissions of the role {role}",
    'assignment.summary.everywhere': "over the whole school",
    'assignment.summary.on': "over {parts}",
    'assignment.summary.always': "from now on, with no time limit",
    'assignment.summary.between': "from {from} to {until}",
    'assignment.summary.from': "from {date}, with no time limit",
    'assignment.summary.until': "from now until {date}",
    'assignment.summary.sentence': "{name} will be able to {permissions}, {where}, {when}.",
    'violation.periodInverted': "The end comes before the start.",
    'violation.assignmentSelf': "Nobody grants a role to themselves.",
    'violation.assignmentExists': "This account already holds this role: edit its assignment from the list.",
    'common.and': "and",
    'assignment.role.alreadyHeld': "Already held by this account.",
    'assignment.role.editIt': "Edit that assignment",
    'assignment.role.revokedBefore': "Revoked before: it will be granted again.",
    'rolesMenus.title': "Roles and menus",
    'rolesMenus.subtitle': "Each role, and the menu it sees. Drag an entry to move it; drop it on a role to copy it there.",
    'rolesMenus.saved': "Changes saved. They apply from the next action of every account concerned.",
    'rolesMenus.copiedTo': "“{item}” copied to {role} (to be saved).",
    'rolesMenus.collapse': "Collapse",
    'rolesMenus.expand': "Expand",
    'rolesMenus.hidden': "hidden",
    'rolesMenus.local': "local",
    'rolesMenus.action.up': "Move up",
    'rolesMenus.action.down': "Move down",
    'rolesMenus.action.move': "Move to…",
    'rolesMenus.action.copyTo': "Copy to another role…",
    'rolesMenus.action.edit': "Edit",
    'rolesMenus.action.withdraw': "Remove from this role",
    'rolesMenus.action.delete': "Delete",
    'rolesMenus.action.copyRole': "Copy this role",
    'rolesMenus.action.editRole': "Edit the role",
    'rolesMenus.action.deleteRole': "Delete the role",
    'rolesMenus.filter': "Filter entries",
    'rolesMenus.newSection': "New section",
    'rolesMenus.newRole': "New role",
    'rolesMenus.tree': "Roles and their menus",
    'rolesMenus.allEntries': "Full menu",
    'rolesMenus.reserved': "Reserved",
    'rolesMenus.copyOf': "Copy of {role}",
    'rolesMenus.entries': "{count} entry(ies)",
    'rolesMenus.roleMeta': "{permissions} permission(s) · {entries} entry(ies)",
    'rolesMenus.pendingRegion': "Pending changes",
    'rolesMenus.pending': "{count} unsaved change(s)",
    'rolesMenus.pendingHint': "They will apply to every account holding these roles.",
    'rolesMenus.discard': "Discard all",
    'rolesMenus.labelIn': "Label · {language}",
    'rolesMenus.kind.section': "Menu section",
    'rolesMenus.kind.entry': "Menu entry",
    'rolesMenus.icon': "Icon",
    'rolesMenus.route': "Screen (route)",
    'rolesMenus.routeHint': "Set by the product code: a screen that does not exist cannot be chosen.",
    'rolesMenus.permission': "Required permission",
    'rolesMenus.permission.section': "None: visible as soon as an entry below is",
    'rolesMenus.permission.none': "None: visible to every signed-in account",
    'rolesMenus.permissionNoneHint': "Without a permission, the entry depends on no role.",
    'rolesMenus.permissionHint': "A role sees this entry if it holds “{label}”.",
    'rolesMenus.visible': "Visible in the menu",
    'rolesMenus.viewers': "Roles that see this entry",
    'rolesMenus.viewersNone': "No role yet.",
    'rolesMenus.provided': "provided by Kelassi",
    'rolesMenus.localRole': "created here",
    'rolesMenus.holders': "{count} holder(s)",
    'rolesMenus.reservedHint': "The Super administrator role is not edited here: it is granted from the command line, and only it opens this screen.",
    'rolesMenus.description': "Description",
    'rolesMenus.permissions': "Permissions ({count})",
    'rolesMenus.permissionsHint': "A permission opens the screen AND the matching actions, not only the menu entry.",
    'rolesMenus.copyRoleTitle': "Copy the role “{role}”",
    'rolesMenus.copyRoleSubtitle': "The new role starts from this content; you edit it afterwards.",
    'rolesMenus.createRole': "Create the role",
    'rolesMenus.code': "Technical code",
    'rolesMenus.codeHint': "Unique and final: the history keeps it.",
    'rolesMenus.codeInvalid': "Lowercase letters, digits and _; 3 to 60 characters, starting with a letter.",
    'rolesMenus.codeTaken': "This code is already taken.",
    'rolesMenus.copyRoleNote': "The new role receives the {count} permission(s) of the original, hence the same menu entries. No account holds it: grant it afterwards from “Roles and assignments”.",
    'rolesMenus.copyItemTitle': "Copy “{item}” to…",
    'rolesMenus.copyItemSubmit': "Copy to {count} role(s)",
    'rolesMenus.alreadyHas': "already has this entry",
    'rolesMenus.copyItemWarning': "These roles receive the permission {permission}. Every account holding them will see the entry and be able to do what the permission allows, within the reach of their assignment.",
    'rolesMenus.deleteItemTitle': "Delete “{item}”?",
    'rolesMenus.seenFrom': "Seen from the role {role}",
    'rolesMenus.withdrawTitle': "Remove from this role only",
    'rolesMenus.withdrawText': "{role} loses the permission {permission}, and everything it opens. Other roles keep it.",
    'rolesMenus.deleteTitle': "Delete the menu entry",
    'rolesMenus.deleteText': "It disappears from every role’s menu. The screen and its permission remain; the next update does not recreate it.",
    'rolesMenus.deleteLocalText': "The section disappears; what it held moves up one level.",
    'rolesMenus.notYet': "Nothing applies before “Save”.",
    'rolesMenus.moveTitle': "Move “{item}”",
    'rolesMenus.moveTarget': "To",
    'rolesMenus.root': "The top level of the menu",
    'rolesMenus.moveHint': "The entry goes to the end of the chosen section; “Move up” and “Move down” adjust it afterwards.",
    'rolesMenus.newSectionSubtitle': "A section groups entries; when empty, it appears in nobody’s menu.",
    'rolesMenus.createSection': "Create the section",
    'rolesMenus.deleteRoleTitle': "Delete the role “{role}”?",
    'rolesMenus.deleteRoleBlocked': "This role has {count} holder(s): revoke it first from “Roles and assignments”.",
    'rolesMenus.deleteRoleText': "The role disappears. If it was ever granted, the server will refuse: the history still names it.",
    'journal.title': "Security log",
    'journal.subtitle': "Who signed in, from where, and who changed what. Nothing here can be edited or erased.",
    'journal.views': "Log views",
    'journal.tab.sign_ins': "Sign-ins",
    'journal.tab.passwords': "Passwords",
    'journal.tab.actions': "Administrative actions",
    'journal.tab.actionsShort': "Actions",
    'journal.export': "Export (CSV)",
    'journal.export.pending': "Exporting…",
    'journal.export.truncated': "{total} rows match: the file holds the 10,000 most recent. Narrow the period for the rest.",
    'journal.search': "Account, typed address or IP address",
    'journal.search.actions': "Author or target account",
    'journal.period': "Period",
    'journal.period.day': "Last 24 hours",
    'journal.period.days': "Last {days} days",
    'journal.period.all': "All retained history",
    'journal.filter.outcome': "Result",
    'journal.filter.allOutcomes': "All results",
    'journal.filter.success': "Succeeded",
    'journal.filter.failure': "Failed",
    'journal.filter.renewals': "Show session renewals",
    'journal.filter.action': "Action",
    'journal.filter.allActions': "All actions",
    'journal.filter.group.account': "Accounts (all)",
    'journal.filter.group.account.photo': "Photos",
    'journal.filter.group.role': "Assignments (all)",
    'journal.filter.actor': "Author",
    'journal.filter.allActors': "All authors",
    'journal.filter.oneAccount': "Events of one account.",
    'journal.filter.oneTarget': "Actions on one account.",
    'journal.filter.oneActor': "Actions by one author.",
    'journal.filter.oneIp': "Events from {ip}.",
    'journal.empty': "No event matches these criteria.",
    'journal.open': "See details",
    'journal.column.date': "Date",
    'journal.column.event': "Event",
    'journal.column.account': "Account",
    'journal.column.ip': "IP address",
    'journal.column.device': "Device",
    'journal.column.actor': "Author",
    'journal.column.action': "Action",
    'journal.column.target': "Target",
    'journal.column.change': "Change",
    'journal.today': "Today",
    'journal.yesterday': "Yesterday",
    'journal.noAccount': "No account",
    'journal.typed': "typed: {email}",
    'journal.vendor': "Vendor",
    'journal.vendor.hidden': "Vendor intervention: their identity and IP address are not disclosed to the institution.",
    'journal.by': "by {name}",
    'journal.changes': "{count} change(s)",
    'journal.tile.successes': "Successful sign-ins · 24 h",
    'journal.tile.accounts': "{count} distinct account(s)",
    'journal.tile.failures': "Failures · 24 h",
    'journal.tile.unknown': "{count} on an unknown address",
    'journal.tile.locked': "Locked accounts",
    'journal.tile.lockedNone': "None right now",
    'journal.tile.lockedUntil': "{name}, until {time}",
    'journal.tile.links': "Password links · 7 d",
    'journal.tile.linksUsed': "{count} used",
    'journal.event.signIn': "Signed in",
    'journal.event.renewed': "Session renewed",
    'journal.event.locked': "Account locked",
    'journal.event.reused': "Token replayed: sessions cut",
    'journal.event.failed': "Failed: {reason}",
    'journal.event.methodFailed': "{method}: {reason}",
    'journal.event.reset.requested': "Password link requested",
    'journal.event.reset.sent': "Password link sent",
    'journal.event.reset.completed': "Password set through a link",
    'journal.event.assigned': "Temporary password set",
    'journal.event.changed': "Password changed",
    'journal.event.invitation': "Invitation accepted",
    'journal.method.password': "Sign-in",
    'journal.method.totp': "One-time code",
    'journal.method.google': "Google sign-in",
    'journal.method.microsoft': "Microsoft sign-in",
    'journal.method.refresh_token': "Renewal refused",
    'journal.method.password_change': "Password change",
    'journal.method.password_assigned': "Temporary password",
    'journal.method.password_reset': "Password link",
    'journal.method.invitation': "Invitation",
    'journal.outcome.success': "succeeded",
    'journal.outcome.bad_credentials': "wrong password",
    'journal.outcome.unknown_account': "no account",
    'journal.outcome.account_disabled': "account disabled",
    'journal.outcome.account_locked': "account locked",
    'journal.outcome.policy_denied': "denied by the sign-in policy",
    'journal.outcome.provider_rejected': "rejected by the identity provider",
    'journal.outcome.token_reused': "token reused",
    'journal.outcome.provisioning_refused': "account creation refused",
    'journal.outcome.token_invalid': "invalid or expired link",
    'journal.outcome.rate_limited': "too many attempts",
    'journal.action.account.created': "Account created",
    'journal.action.account.updated': "Account updated",
    'journal.action.account.deactivated': "Account deactivated",
    'journal.action.account.activated': "Account reactivated",
    'journal.action.account.password_assigned': "Temporary password set",
    'journal.action.account.link_sent': "Link sent",
    'journal.action.account.photo_changed': "Photo changed",
    'journal.action.account.photo_removed': "Photo removed",
    'journal.action.role.granted': "Role granted",
    'journal.action.role.assignment_updated': "Assignment updated",
    'journal.action.role.revoked': "Role revoked",
    'journal.action.roles_menus.changed': "Roles and menus changed",
    'journal.field.displayName': "Name",
    'journal.field.email': "Email",
    'journal.field.population': "Population",
    'journal.field.credential': "Password",
    'journal.field.sessionsClosed': "Sessions closed",
    'journal.field.lock': "Lock",
    'journal.field.role': "Role",
    'journal.field.centers': "Centres",
    'journal.field.cycles': "Cycles",
    'journal.field.domains': "Domains",
    'journal.field.products': "Products",
    'journal.field.startsAt': "Start",
    'journal.field.endsAt': "End",
    'journal.value.all': "All",
    'journal.value.open': "no limit",
    'journal.value.link': "activation link",
    'journal.value.password': "temporary, typed",
    'journal.value.lifted': "lifted",
    'journal.diff.field': "Field",
    'journal.diff.before': "Before",
    'journal.diff.after': "After",
    'journal.detail.reason': "Reason",
    'journal.detail.lockedUntil': "Locked until",
    'journal.detail.lockedReason': "Too many wrong passwords: the account is temporarily locked.",
    'journal.detail.by': "By",
    'journal.detail.sessions': "Sessions closed",
    'journal.detail.actorIp': "Author's IP address",
    'journal.detail.further': "Go further",
    'journal.detail.allForAccount': "All events of this account",
    'journal.detail.allForIp': "All events from this IP address",
    'journal.detail.openAccount': "Open the account",
    'journal.detail.allByActor': "Everything {name} did",
    'journal.detail.allOnTarget': "Everything that targeted this account",
    'journal.detail.id': "Event identifier, to quote to support:",
    'directories.title': "Directories and sign-in",
    'directories.subtitle': "How your accounts sign in, and the rules that apply to each population.",
    'directories.section.directories': "Directories",
    'directories.section.directoriesHint': "An external directory lets people sign in with the institution's Google or Microsoft account.",
    'directories.section.policy': "Sign-in policy",
    'directories.section.policyHint': "One row per population. It applies from the next sign-in or session renewal.",
    'directories.passwordRule': "The Kelassi password can only be removed from a population that a directory serves: without it, nobody in that population could sign in. Sign-in administrators always keep it.",
    'directories.type.native': "Kelassi password",
    'directories.type.google': "Google Workspace",
    'directories.type.microsoft': "Microsoft 365",
    'directories.state.active': "Active",
    'directories.state.disabled': "Disabled",
    'directories.state.unconfigured': "Not configured",
    'directories.native.text': "Always available for every account. It is also the way back in if an external directory stops answering: administrators always keep it.",
    'directories.google.text': "Sign-in with the institution's Google account. Kelassi accounts must exist: the directory never creates them.",
    'directories.microsoft.text': "Sign-in with the institution's Microsoft 365 account. Same rule: an account unknown to Kelassi is refused, never created.",
    'directories.external.serves': "Serves: {populations}. Addresses {domains}.",
    'directories.byVendor': "Set up by LENSOM CORP, at the institution's request",
    'directories.details': "See details",
    'directories.stat.withPassword': "Accounts with a password",
    'directories.stat.linked': "Linked accounts",
    'directories.stat.signIns': "Sign-ins · 7 days",
    'directories.stat.neverUsed': "Never signed in through the directory",
    'directories.detail.tenant': "Tenant",
    'directories.detail.issuer': "Issuer checked on every token",
    'directories.detail.client': "Application",
    'directories.detail.secret': "Application secret",
    'directories.detail.secretStored': "Stored, never displayed",
    'directories.detail.secretMissing': "Missing: the directory cannot work",
    'directories.detail.domains': "Accepted domains",
    'directories.detail.populations': "Populations served",
    'directories.detail.unknown': "Account unknown to Kelassi",
    'directories.detail.refused': "Refused, never created automatically",
    'directories.detail.autoProvision': "Created automatically",
    'directories.detail.log': "Sign-ins in the security log",
    'directories.column.population': "Population",
    'directories.column.accounts': "Accounts",
    'directories.column.password': "Password",
    'directories.column.directory': "Directory",
    'directories.column.totp': "Two-factor",
    'directories.column.idle': "Idle timeout",
    'directories.password.allowed': "Allowed",
    'directories.password.removed': "Removed",
    'directories.password.short': "Password",
    'directories.none': "None",
    'directories.idle.minutes': "{count} min",
    'directories.idle.hours': "{count} h",
    'directories.idleShort': "idle {idle}",
    'directories.policy.edit': "Edit the policy: {population}",
    'directories.policy.title': "Sign-in policy · {population}",
    'directories.policy.accounts': "{count} account(s) concerned",
    'directories.policy.password': "Sign-in with the Kelassi password",
    'directories.policy.passwordLocked': "No directory serves this population: the password is its only way to sign in, it cannot be removed.",
    'directories.policy.passwordHint': "Also served by {directories}. Without a password, these accounts go through the directory only; sign-in administrators keep it.",
    'directories.policy.totp': "Two-factor authentication (one-time code)",
    'directories.policy.idle': "Sign out after inactivity",
    'directories.policy.idleHint': "With no action for this long, the session closes and the person must sign in again. Shorter on shared computers.",
    'directories.policy.journal': "The change is written to the security log.",
    'directories.policy.saved': "Sign-in policy saved: {population}.",
    'violation.policyIdle': "Choose a duration from the list.",
    'violation.policyPassword': "No directory serves this population: the password cannot be removed.",
    'journal.action.login_policy.changed': "Sign-in policy changed",
    'journal.field.passwordAllowed': "Kelassi password",
    'journal.field.sessionIdleMinutes': "Idle timeout",
    'journal.value.allowed': "allowed",
    'journal.value.removed': "removed",
    'journal.filter.group.login_policy': "Sign-in policy",
    'directories.totp.none': "Not required",
    'directories.totp.email_or_app': "E-mail or app",
    'directories.totp.app': "App only",
    'directories.recommended': "recommended",
    'directories.policy.mode.none': "Not required",
    'directories.policy.mode.none.hint': "Anyone can turn it on for themselves from their profile.",
    'directories.policy.mode.email_or_app': "Required: e-mail or app",
    'directories.policy.mode.email_or_app.hint': "An e-mailed code at every sign-in; those who enabled an app use it instead.",
    'directories.policy.mode.app': "Required: app only",
    'directories.policy.mode.app.hint': "Safer. Every account will have to enable an app at its next sign-in (after one last e-mailed code).",
    'twoFactor.progress': "Sign-in steps",
    'twoFactor.step.password': "Password",
    'twoFactor.step.code': "Code",
    'twoFactor.code': "6-digit code",
    'twoFactor.email.title': "Verify it is you",
    'twoFactor.email.body': "We sent a 6-digit code to {email}. It is valid for 10 minutes.",
    'twoFactor.email.notSent': "Get a 6-digit code by e-mail to finish signing in.",
    'twoFactor.email.send': "Send me a code by e-mail",
    'twoFactor.app.title': "Code from your app",
    'twoFactor.app.body': "Open Google Authenticator or Microsoft Authenticator and enter the code shown for Kelassi.",
    'twoFactor.recovery.title': "Recovery code",
    'twoFactor.recovery.body': "Enter one of the recovery codes you saved when enabling your app. Each works once.",
    'twoFactor.recovery.label': "Recovery code",
    'twoFactor.mustEnroll': "Your institution requires an authenticator app: you will enable it right after this code.",
    'twoFactor.submit': "Verify",
    'twoFactor.resendIn': "Nothing? Check your spam folder, or resend in {time}",
    'twoFactor.resend': "Resend a code",
    'twoFactor.sent': "New code sent to {email}.",
    'twoFactor.useApp': "Use my authenticator app instead",
    'twoFactor.useEmail': "Get a code by e-mail instead",
    'twoFactor.useRecovery': "Use a recovery code",
    'twoFactor.back': "Back to sign-in",
    'twoFactor.invalid': "Wrong or expired code.",
    'twoFactor.invalidLeft': "Wrong or expired code. {count} attempt(s) left.",
    'twoFactor.expired': "The verification expired or failed too many times. Sign in again.",
    'twoFactor.tooSoon': "Wait a moment before asking for a new code.",
    'twoFactor.tooMany': "Too many codes sent: start signing in again.",
    'twoFactor.policy.none': "Your institution does not require it for your population: it protects your account if your password leaks.",
    'twoFactor.policy.email_or_app': "Required by your institution: an e-mailed code at every sign-in, or your app if you enable one.",
    'twoFactor.policy.app': "Required by your institution: an authenticator app.",
    'twoFactor.profile.title': "Two-factor authentication",
    'twoFactor.profile.on': "On",
    'twoFactor.profile.offShort': "Off",
    'twoFactor.profile.app': "Authenticator app enabled on {date}.",
    'twoFactor.profile.email': "An e-mailed code at every sign-in.",
    'twoFactor.profile.mustEnroll': "Enable an authenticator app.",
    'twoFactor.profile.off': "Your password alone is enough for now.",
    'twoFactor.profile.enable': "Enable an authenticator app",
    'twoFactor.profile.codesLeft': "{count} recovery code(s) left",
    'twoFactor.profile.renew': "Renew recovery codes",
    'twoFactor.profile.remove': "Remove the app",
    'twoFactor.profile.removed': "Authenticator app removed.",
    'twoFactor.profile.confirmRemove': "Enter a code from your app to remove it.",
    'twoFactor.profile.confirmRenew': "Enter a code from your app: the old recovery codes will stop working.",
    'twoFactor.profile.emailToggle': "Get a code by e-mail at every sign-in",
    'twoFactor.profile.emailToggleHint': "Without an app: a code is sent to your address after the password.",
    'twoFactor.setup.install': "Install the app",
    'twoFactor.setup.installHint': "Google Authenticator or Microsoft Authenticator, on your phone.",
    'twoFactor.setup.scan': "Scan this code",
    'twoFactor.setup.qrAlt': "QR code to scan with the app",
    'twoFactor.setup.key': "Cannot scan? Enter this key:",
    'twoFactor.setup.copy': "Copy the key",
    'twoFactor.setup.code': "Enter the code shown",
    'twoFactor.setup.activate': "Enable the app",
    'twoFactor.codes.title': "Your recovery codes",
    'twoFactor.codes.body': "Shown only once. Each one replaces an app code if you lose your phone, and works once.",
    'twoFactor.codes.download': "Download (.txt)",
    'twoFactor.codes.copy': "Copy",
    'twoFactor.codes.warning': "Keep them off your phone. As a last resort, an administrator can reset your two-factor authentication.",
    'twoFactor.codes.done': "I saved my codes",
    'twoFactor.codes.fileHeader': "Kelassi: two-factor recovery codes. Each works once.",
    'twoFactor.forced.title': "Enable your authenticator app",
    'twoFactor.forced.body': "Your institution requires it for your population. It takes two minutes, and no more codes will be e-mailed to you.",
    'account.twoFactor': "Two-factor",
    'account.twoFactor.app': "Authenticator app",
    'account.twoFactor.none': "No app",
    'account.action.resetTwoFactor': "Reset two-factor authentication",
    'account.action.resetTwoFactor.short': "Reset",
    'account.action.resetTwoFactor.confirm': "The authenticator app and recovery codes of {name} will be removed. Depending on the policy, they will get an e-mailed code and then have to enable an app again.",
    'account.done.twoFactorReset': "Two-factor authentication reset.",
    'journal.method.second_factor': "Verification code",
    'journal.event.challenged': "Correct password, code requested",
    'journal.event.codeFailed': "Failed: wrong verification code",
    'journal.action.account.two_factor_app_enabled': "Authenticator app enabled",
    'journal.action.account.two_factor_app_disabled': "Authenticator app removed",
    'journal.action.account.two_factor_email': "E-mail code chosen",
    'journal.action.account.recovery_codes_renewed': "Recovery codes renewed",
    'journal.action.account.two_factor_reset': "Two-factor reset",
    'journal.field.twoFactor': "Two-factor",
    'journal.field.email2fa': "E-mail code",
    'journal.value.on': "on",
    'journal.value.off': "off",
    'violation.twoFactorInvalid': "Wrong or expired code.",
    'home.title': "Home",
    'home.hello': "Hello {name}",
    'home.soon': "Your space will grow as modules go live: timetable, grades, documents. For now, you can manage your profile and your account's security.",
    'home.tile.accounts': "Active accounts",
    'home.tile.recent': "+{count} this week",
    'home.todo': "To do",
    'home.todo.dismiss': "Do not show again",
    'home.todo.locked': "{count} account(s) locked after failed sign-ins",
    'home.todo.locked.action': "See",
    'home.todo.provisional': "{count} account(s) have not changed their temporary password yet",
    'home.todo.provisional.action': "See the list",
    'home.todo.links': "{count} activation link(s) expire within 24 h",
    'home.todo.links.action': "See the accounts",
    'home.todo.two_factor': "Two-factor authentication is required for no population",
    'home.todo.two_factor.action': "Set the policy",
    'home.activity': "Recent activity",
    'home.activity.empty': "No administrative action in the last 30 days.",
    'home.activity.all': "The whole security log",
    'home.shortcuts': "Shortcuts",
    'home.myAccount': "My account",
    'home.shortcut.createAccount': "Create an account",
    'home.shortcut.createAccount.hint': "Activation link or temporary password",
    'home.shortcut.assign': "Grant a role",
    'home.shortcut.assign.hint': "On all or part of the school",
    'home.shortcut.journal': "Security log",
    'home.shortcut.journal.hint': "Sign-ins and actions",
    'home.shortcut.directories': "Directories and sign-in",
    'home.shortcut.directories.hint': "Policy per population",
    'home.shortcut.profile': "My profile",
    'home.shortcut.profile.hint': "Photo, details, two-factor",
    'home.shortcut.password': "Change my password",
    'home.shortcut.password.hint': "All your sessions will be closed",
    'home.security': "My security",
    'home.security.previous': "Previous sign-in",
    'home.security.firstTime': "First sign-in",
    'home.security.passwordSet': "changed on {date}",
    'home.security.twoFactor.app': "Authenticator app",
    'home.security.twoFactor.email': "E-mail code",
    'home.security.twoFactor.none': "Off · recommended",
    'home.security.manage': "Manage my security",
    'home.security.enable': "Turn on two-factor authentication",
    'signIn.refusedLong': "Wrong address or password. After 10 failures, the account locks for 15 minutes.",
    'signIn.unreachableLong': "The service is not answering right now. Try again in a moment; your data is not lost.",
    'session.expiredLong': "Your session expired after a period of inactivity. Sign in again to continue.",
    'auth.subtitle': "Your digital campus",
    'auth.tagline': "Studies, grades and administration: the whole school, in one place.",
    'auth.vendor': "Kelassi · a LENSOM CORP solution",
    'auth.never': "Kelassi will never ask for your password by e-mail.",
    'auth.showPassword': "Show the password",
    'auth.hidePassword': "Hide the password",
    'auth.rule.length': "At least 12 characters ({count} typed)",
    'auth.rule.sentence': "A sentence is safer than a complicated word",
    'forgot.sentTitle': "Check your inbox",
    'forgot.sentBody': "If an account exists for {email}, a link has just been sent to it. It stays valid for one hour and works once.",
    'forgot.tip.spam': "Remember to check your spam folder.",
    'forgot.tip.last': "Only the latest link received works.",
    'forgot.tip.sessions': "Choosing a new password closes all your sessions.",
    'forgot.again': "Send another link",
    'forgot.noAccess': "No longer have access to this address? Ask the school's administration: they can send you a link or set a temporary password.",
    'reset.deadTitle': "This link no longer works",
    'reset.body': "It will replace your current password, if you have one.",
    'reset.sessions': "All your open sessions will be closed. You will then sign in with this password.",
    'common.actions': "Actions",
    'common.add': "Add",
    'common.delete': "Delete",
    'common.edit': "Edit",
    'ref.nav': "Academic framework configuration",
    'ref.intro': "What structures the institution's offering, before the programmes themselves.",
    'ref.rulesNote': "Calculation rules (compensation, cycle average) are provided by Kelassi: you choose them per domain, you don't write them. ESCG's rules are carried over until they are confirmed.",
    'ref.readOnly': "Read only: the configuration is changed with the “Configure the academic framework” right.",
    'ref.section.years': "Academic years",
    'ref.section.domains': "Cycles and domains",
    'ref.section.structure': "Programme structure",
    'ref.section.levels': "Levels and diplomas",
    'ref.section.mentions': "Honours and letters",
    'ref.section.pedagogy': "Teaching",
    'ref.saved.created': "“{label}” created.",
    'ref.saved.updated': "“{label}” saved.",
    'ref.saved.deleted': "“{label}” deleted.",
    'ref.common.code': "Code",
    'ref.common.name': "Name",
    'ref.common.label': "Label",
    'ref.common.none': "None",
    'ref.common.choose': "Choose…",
    'ref.common.unused': "Not used by any domain",
    'ref.common.usedBy': "Used by: {list}",
    'ref.years.sub': "The institution's calendar, from one intake to the next.",
    'ref.years.prepare': "Prepare a year",
    'ref.years.prepareHint': "It opens for enrolment when you decide; the current year carries on until then.",
    'ref.years.edit': "Edit year {label}",
    'ref.years.labelHint': "Format 2026-2027.",
    'ref.years.empty': "No academic year yet. Prepare the first one.",
    'ref.years.neverClosed': "No year has ever been closed. A closed year accepts no more enrolments or marks; published deliberations remain viewable.",
    'ref.years.column.year': "Year",
    'ref.years.column.start': "Start",
    'ref.years.column.end': "End",
    'ref.years.column.state': "Status",
    'ref.years.state.current': "Current year",
    'ref.years.state.preparing': "In preparation",
    'ref.years.state.open': "Open",
    'ref.years.state.closed': "Closed",
    'ref.years.open': "Open enrolment",
    'ref.years.makeCurrent': "Make current",
    'ref.years.close': "Close…",
    'ref.years.readOnly': "Read only",
    'ref.years.closeTitle': "Close year {label}?",
    'ref.years.closeText': "This is final: the year will accept no more enrolments, marks or deliberations. What was published remains viewable. The action is logged.",
    'ref.years.done.open': "Year {label} is open.",
    'ref.years.done.set_current': "{label} is now the current year.",
    'ref.years.done.delete': "Year {label} deleted.",
    'ref.years.done.close': "Year {label} is closed.",
    'ref.years.fact.current': "One current year",
    'ref.years.fact.currentText': "The one screens offer by default. The next one can be prepared ahead.",
    'ref.years.fact.centers': "Shared by all centres",
    'ref.years.fact.centersText': "A centre's own calendar will come with sessions.",
    'ref.years.fact.closing': "Final closing",
    'ref.years.fact.closingText': "Restricted to holders of the right, logged, and confirmed first.",
    'ref.domains.sub': "A cycle has a duration and awards a diploma; a domain carries the rules of a family of programmes.",
    'ref.cycles.add': "Add a cycle",
    'ref.cycles.edit': "Edit the cycle",
    'ref.cycles.empty': "No cycle yet. Add one to hold domains.",
    'ref.cycles.duration': "Duration: {count} year(s)",
    'ref.cycles.durationLabel': "Duration (years)",
    'ref.cycles.diploma': "Awarded diploma:",
    'ref.cycles.diplomaMissing': "to be specified",
    'ref.cycles.diplomaLabel': "Awarded diploma",
    'ref.domains.add': "Add a domain",
    'ref.domains.edit': "Edit the domain",
    'ref.domains.editTitle': "Domain {code}",
    'ref.domains.dialogHint': "The rules apply to every programme in the domain; a programme may choose another compensation.",
    'ref.domains.noCycle': "Domains without a cycle",
    'ref.domains.cycle': "Cycle",
    'ref.domains.notation': "Marking",
    'ref.domains.notationHint': "Both: entry accepts 14.25 as well as B+, the transcript shows both.",
    'ref.domains.letterScale': "Letter scale",
    'ref.domains.mentions': "Honours scale",
    'ref.domains.noMentions': "No scale",
    'ref.domains.cycleAverage': "Cycle average",
    'ref.domains.vendorRule': "Rule provided by Kelassi.",
    'ref.domains.weighting': "Average weighting",
    'ref.domains.weightingHint': "By coefficients as today, or by credits.",
    'ref.domains.compensation': "Default compensation",
    'ref.domains.compensationHint': "Each programme may choose another.",
    'ref.domains.retainedSession': "Mark kept",
    'ref.domains.retainedSessionHint': "When a student retakes an exam.",
    'ref.domains.creditsEarned': "Credits earned",
    'ref.domains.structure': "Structure",
    'ref.domains.noStructure': "Template to define",
    'ref.domains.marking': "Marking and scales",
    'ref.domains.calculation': "Calculation",
    'ref.notation.numeric': "Marks out of 20",
    'ref.notation.letters': "Letters",
    'ref.notation.both': "Marks out of 20 and letters",
    'ref.weighting.coefficients': "By coefficients",
    'ref.weighting.credits': "By credits",
    'ref.retainedSession.last': "The latest mark",
    'ref.retainedSession.best': "The best mark",
    'ref.creditsEarned.validated_or_compensated': "If passed or compensated",
    'ref.creditsEarned.validated_only': "If passed only",
    'ref.structure.sub': "The template every programme of a domain follows.",
    'ref.structure.noDomain': "Create a domain first in “Cycles and domains”.",
    'ref.structure.domains': "Domains",
    'ref.structure.template': "Structure template",
    'ref.structure.version': "Version",
    'ref.structure.since': "In force since {year}",
    'ref.structure.none': "No template yet",
    'ref.structure.newVersion': "New version…",
    'ref.structure.newVersionHint': "The current version is copied; it will apply from the chosen year.",
    'ref.structure.createVersion': "Create the version",
    'ref.structure.validFrom': "From year",
    'ref.structure.create': "Create the template ({year})",
    'ref.structure.saved': "The template of domain {code} is saved.",
    'ref.structure.levelName': "Name of level “{kind}”",
    'ref.structure.removeLevel': "Remove this level",
    'ref.structure.addLevel': "Add a level…",
    'ref.structure.vendorNotions': "The notions are provided by Kelassi; their names are yours.",
    'ref.structure.versioning': "A programme created in this domain follows this template. Changing it creates a new version, valid from a given year: past years' programmes keep theirs, and their averages don't move.",
    'ref.structure.flag.classGroups': "Class groups",
    'ref.structure.flag.transcript': "Transcript",
    'ref.structure.flag.taught': "Taught",
    'ref.structure.creditsHere': "Credits here",
    'ref.structure.creditsEntered': "Credits entered here",
    'ref.structure.creditsSum': "Σ credits",
    'ref.structure.creditsBelow': "No own credits",
    'ref.kind.year': "Year",
    'ref.kind.semester': "Semester",
    'ref.kind.module': "Module",
    'ref.kind.submodule': "Sub-module",
    'ref.kind.subject': "Subject",
    'ref.kind.year.hint': "Holds enrolments and classes",
    'ref.kind.semester.hint': "Shown on the transcript",
    'ref.kind.module.hint': "Passed or compensated",
    'ref.kind.submodule.hint': "Optional: missing from some modules",
    'ref.kind.subject.hint': "Receives marks and hours",
    'ref.levels.sub': "What is studied, at which level, and what is awarded.",
    'ref.levels.title': "Levels",
    'ref.levels.hint': "A free list; the cycle is optional.",
    'ref.levels.empty': "No level.",
    'ref.levels.add': "Add a level",
    'ref.levels.edit': "Level {code}",
    'ref.levels.cycleHint': "Optional.",
    'ref.diplomas.title': "Awarded diplomas",
    'ref.diplomas.hint': "What the institution awards, and its specialities. Entry diplomas describe the student, not the offering.",
    'ref.diplomas.empty': "No awarded diploma.",
    'ref.diplomas.access': "Entry diplomas",
    'ref.diplomas.add': "Add an awarded diploma",
    'ref.diplomas.addAccess': "Add an entry diploma",
    'ref.diplomas.edit': "Diploma {name}",
    'ref.diplomas.grade': "Level",
    'ref.diplomas.specialities': "Specialities",
    'ref.diplomas.series': "Streams",
    'ref.diplomas.optionsHint': "One per line. An option that stays keeps its identifier.",
    'ref.diplomas.noSpeciality': "No speciality",
    'ref.diplomas.noSeries': "No stream",
    'ref.grade.bac': "Baccalaureate",
    'ref.grade.bac_plus_2': "Bac + 2",
    'ref.grade.bac_plus_3': "Bac + 3",
    'ref.grade.bac_plus_5': "Bac + 5",
    'ref.grade.doctorate': "Doctorate",
    'ref.mentions.sub': "The scales that give an honour and a letter to a mark or an average.",
    'ref.mentions.add': "New honours scale",
    'ref.mentions.edit': "Scale {name}",
    'ref.mentions.empty': "No honours scale.",
    'ref.mentions.dialogHint': "Bands touch without overlapping: the top of one is the bottom of the next.",
    'ref.mentions.bands': "Bands",
    'ref.mentions.mention': "Honour",
    'ref.mentions.from': "From",
    'ref.mentions.to': "Up to",
    'ref.mentions.addBand': "Add a band",
    'ref.mentions.range': "{min} ≤ avg. < {max}",
    'ref.mentions.default.pass': "Pass",
    'ref.mentions.default.fair': "Fair",
    'ref.mentions.default.good': "Good",
    'ref.mentions.default.veryGood': "Very good",
    'ref.letters.add': "New letter scale",
    'ref.letters.edit': "Scale {name}",
    'ref.letters.empty': "No letter scale.",
    'ref.letters.title': "Letter scale · {name}",
    'ref.letters.americanName': "American, GPA out of 4.0",
    'ref.letters.presetHint': "It is created with the American letters (A+ to F, GPA out of 4.0); you will adjust the ranges afterwards.",
    'ref.letters.dialogHint': "Each letter applies from its mark; the last one starts at 0.",
    'ref.letters.letter': "Letter",
    'ref.letters.range': "Range out of 20",
    'ref.letters.points': "Points (out of {max})",
    'ref.letters.pointsShort': "Points",
    'ref.letters.result': "Result",
    'ref.letters.pass': "Pass",
    'ref.letters.fail': "Fail",
    'ref.letters.from': "{min} ≤ mark < {max}",
    'ref.letters.fromScore': "From",
    'ref.letters.maxPoints': "Maximum points",
    'ref.letters.grades': "Letters",
    'ref.letters.addGrade': "Add a letter",
    'ref.letters.howTitle': "How it is used",
    'ref.letters.howText': "The teacher enters a mark out of 20 or a letter: both are accepted. An entered letter counts as the bottom of its range for the /20 calculation, and as its points for the GPA.",
    'ref.letters.probe': "Try a mark out of 20",
    'ref.letters.pointsValue': "· {points} points",
    'ref.letters.averages': "Averages are calculated out of 20 and as a GPA, weighted by credits or coefficients depending on the domain. The transcript shows both.",
    'ref.pedagogy.sub': "The lists that programmes, marks and the timetable use.",
    'ref.pedagogy.teaching_type': "Teaching types",
    'ref.pedagogy.teaching_type.hint': "Columns of teaching hours and the timetable",
    'ref.pedagogy.assessment_type': "Assessment types",
    'ref.pedagogy.assessment_type.hint': "What is marked in a subject",
    'ref.pedagogy.responsible_type': "Responsible types",
    'ref.pedagogy.responsible_type.hint': "Who supervises a product, and what they may do",
    'ref.pedagogy.disciplinary_field': "Disciplinary fields",
    'ref.pedagogy.disciplinary_field.hint': "Group subjects and teachers",
    'ref.pedagogy.department': "Departments",
    'ref.pedagogy.department.hint': "Administrative attachment",
    'ref.pedagogy.empty': "Empty list.",
    'ref.pedagogy.addTo': "Add: {list}",
    'ref.pedagogy.editIn': "Edit: {list}",
    'ref.pedagogy.color': "Colour",
    'ref.pedagogy.rights': "What they may do",
    'ref.pedagogy.rightsHint': "Enforced with mark entry and deliberations.",
    'ref.pedagogy.rights.canEnterMarks': "Enter marks",
    'ref.pedagogy.rights.canValidateMarks': "Validate marks",
    'ref.pedagogy.rights.canDeliberate': "Deliberate",
    'ref.pedagogy.rights.enter': "enters marks",
    'ref.pedagogy.rights.validate': "validates marks",
    'ref.pedagogy.rights.deliberate': "deliberates",
    'ref.pedagogy.vendor': "Provided by Kelassi",
    'ref.pedagogy.vendorHint': "Not editable here.",
    'ref.pedagogy.vendor.notation': "Marking: out of 20, letters, both",
    'ref.pedagogy.vendor.sessions': "Session states: pending, confirmed, cancelled",
    'ref.pedagogy.vendor.compensation': "Compensation rules: {count}",
    'ref.pedagogy.vendor.cycleAverage': "Cycle average rules: {count}",
    'journal.action.referential.created': "Framework: added",
    'journal.action.referential.updated': "Framework: changed",
    'journal.action.referential.deleted': "Framework: deleted",
    'journal.action.referential.year_current': "Current year changed",
    'journal.action.referential.year_closed': "Academic year closed",
    'journal.filter.group.referential': "Academic framework",
    'ref.target.academic_year': "Year",
    'ref.target.cycle': "Cycle",
    'ref.target.domain': "Domain",
    'ref.target.structure': "Template",
    'ref.target.level': "Level",
    'ref.target.diploma': "Diploma",
    'ref.target.mention_scale': "Honours scale",
    'ref.target.letter_scale': "Letter scale",
    'ref.target.teaching_type': "Teaching type",
    'ref.target.assessment_type': "Assessment type",
    'ref.target.responsible_type': "Responsible type",
    'ref.target.disciplinary_field': "Disciplinary field",
    'ref.target.department': "Department",
    'violation.ref.required': "This field is required.",
    'violation.ref.too_long': "Too long.",
    'violation.ref.invalid': "Unrecognised value.",
    'violation.ref.out_of_range': "Outside the allowed limits.",
    'violation.ref.invalid_date': "Invalid date.",
    'violation.ref.year_closed': "This year is closed: nothing changes in it any more.",
    'violation.ref.year_not_preparing': "Only possible for a year in preparation.",
    'violation.ref.in_use': "This item is used elsewhere: remove what depends on it first.",
    'violation.ref.year_label': "Expected format: 2026-2027.",
    'violation.ref.year_dates': "The end must follow the start.",
    'violation.ref.duplicate': "Already used.",
    'violation.ref.letter_scale_required': "Letter marking needs a letter scale.",
    'violation.ref.structure_order': "Levels follow the order Year, Semester, Module, Sub-module, Subject.",
    'violation.ref.credits_once': "Credits are entered at one level only.",
    'violation.ref.taught_required': "At least one level receives marks (taught).",
    'violation.ref.last_structure': "A domain keeps at least one template.",
    'violation.ref.band_order': "The top of the band must exceed the bottom.",
    'violation.ref.band_gap': "Bands touch without gaps or overlaps.",
    'violation.ref.letters_from_zero': "The last letter must start at 0: every mark has a letter.",
    'violation.ref.letters_points': "A letter cannot be worth more points than a letter above it.",
    'violation.ref.not_found': "Item not found.",
    'violation.ref.assigned': "Role assignments refer to this item: remove them first.",
    'ref.rule.all_years': "Average over all years of the cycle",
    'ref.rule.last_year': "Average over the last year of the cycle",
    'ref.rule.cycle_normal_semester_2020': "Semester validation, regular cycle, 2020 edition",
    'ref.rule.bachelor_2020': "Bachelor validation, 2020",
    'ref.rule.cycle_normal_semester_2020_without_submodule': "Semester validation, without sub-module",
    'ref.rule.continuing_education_2021': "Continuing education 2021",
    'ref.rule.cycle_normal_semester_2021_submodules': "Regular cycle with sub-subjects 2021",
    'ref.rule.master_2022': "Master, specialised Master 2022",
    'journal.action.catalog.created': "Programmes: added",
    'journal.action.catalog.updated': "Programmes: changed",
    'journal.action.catalog.deleted': "Programmes: deleted",
    'journal.action.catalog.renewed': "Programmes carried over",
    'journal.filter.group.catalog': "Programmes",
    'cat.target.training': "Programme",
    'cat.target.element': "Item",
    'cat.target.track': "Track",
    'cat.target.equivalence': "Equivalence",
    'cat.journal.elements': "Items",
    'cat.journal.source': "Equivalent",
    'cat.journal.linked': "Links created",
    'cat.journal.from': "Carried over from",
    'cat.model.metaHours': "{value} h",
    // D47 : le catalogue des formations.
    'cat.title': "Programmes",
    'cat.sub': "The school’s offer for one year: each programme follows its domain’s template.",
    'cat.tabs': "Programme tabs",
    'cat.backToList': "Programmes {year}",
    'cat.year.current': "current",
    'cat.filter.year': "Year",
    'cat.filter.domain': "Domain",
    'cat.filter.allDomains': "All domains",
    'cat.filter.search': "Code or name",
    'cat.summary': "{count} programme(s) · {hours} hours",
    'cat.empty': "No programme for this year.",
    'cat.emptyTraining': "Empty structure",
    'cat.yearClosed': "This year is closed: its programmes can be viewed, no longer changed.",
    'cat.readOnly': "Read only: this programme is outside your scope, or its year is closed.",
    'cat.none': "None",
    'cat.notDiploma': "Does not award a diploma",
    'cat.unlimited': "Unlimited",
    'cat.levelChip': "Level {level}",
    'cat.tracksChip': "Tracks: {count}",
    'cat.delivers': "Awards · {diploma}",
    'cat.dates': "from {from} to {to}",
    'cat.templateSince': "template in force since {year}",
    'cat.noTemplate': "no template for this domain",
    'cat.internship': "internship",
    'cat.optional': "optional",
    'cat.weighting.credits': "weighted by credits",
    'cat.weighting.coefficients': "weighted by coefficients",
    'cat.count.semester': "Semesters: {count}",
    'cat.count.module': "Modules: {count}",
    'cat.count.submodule': "Sub-modules: {count}",
    'cat.count.subject': "Subjects: {count}",
    'cat.stat.hours': "hours",
    'cat.stat.credits': "credits",
    'cat.stat.modules': "weighted modules",
    'cat.stat.enrolment': "enrolment",
    'cat.enrolment.open': "Enrolment open",
    'cat.enrolment.closed': "Enrolment closed",
    'cat.state.confirmed': "Confirmed",
    'cat.state.pending': "Pending",
    'cat.state.cancelled': "Cancelled",
    'cat.training.create': "New programme",
    'cat.training.createIn': "New programme in this domain",
    'cat.training.new': "New programme in {year}",
    'cat.training.newHint': "It will follow its domain’s template; its structure is built afterwards.",
    'cat.training.edit': "Edit “{name}”",
    'cat.training.deleteTitle': "Delete “{name}”?",
    'cat.training.deleteText': "The programme and its items ({count}) disappear, with their hours, staff and tracks. This is final.",
    'cat.training.deleted': "“{name}” is deleted.",
    'cat.field.domain': "Domain",
    'cat.field.level': "Level",
    'cat.field.code': "Code",
    'cat.field.codeHint': "Unique within the year; carrying over keeps it.",
    'cat.field.codeElementHint': "Unique within the year, like L2S3M6.",
    'cat.field.name': "Name",
    'cat.field.kind': "Kind",
    'cat.field.diploma': "Diploma awarded",
    'cat.field.diplomaHint': "Leave empty for a year that awards no diploma.",
    'cat.field.startsOn': "Start",
    'cat.field.endsOn': "End",
    'cat.field.capacityMax': "Places",
    'cat.field.capacityHint': "Empty: unlimited.",
    'cat.field.state': "Status",
    'cat.field.enrollable': "Enrolment open",
    'cat.field.enrollableHint': "Students can be enrolled.",
    'cat.field.description': "Description",
    'cat.field.objectives': "Objectives",
    'cat.field.abbreviation': "Abbreviation",
    'cat.field.abbreviationHint': "On transcripts and grids.",
    'cat.field.disciplinaryField': "Discipline",
    'cat.field.department': "Department",
    'cat.field.validation': "Pass mark",
    'cat.field.resit': "Resit threshold",
    'cat.field.resitHint': "Below it: no resit.",
    'cat.field.compensation': "Compensation rule",
    'cat.field.compensationHint': "Provided by Kelassi; the domain’s applies by default.",
    'cat.field.compensationDefault': "The domain’s ({rule})",
    'cat.flag.mandatory': "Mandatory",
    'cat.flag.mandatoryHint': "Counts in everyone’s average.",
    'cat.flag.bonus': "Bonus",
    'cat.flag.bonusHint': "Only counts if it raises the average.",
    'cat.flag.internship': "Internship",
    'cat.flag.internshipHint': "An internship rather than a course.",
    'cat.tab.model': "Structure",
    'cat.tab.tracks': "Tracks",
    'cat.tab.equivalences': "Equivalences",
    'cat.tab.info': "Details",
    'cat.tab.elements': "items: {count}",
    'cat.col.element': "Item",
    'cat.col.coef': "Coef.",
    'cat.col.credits': "Credits",
    'cat.col.responsibles': "Staff",
    'cat.model.search': "Find an item",
    'cat.model.collapse': "Collapse all",
    'cat.model.expand': "Expand all",
    'cat.model.expandOne': "Expand “{name}”",
    'cat.model.collapseOne': "Collapse “{name}”",
    'cat.model.bulk': "Bulk entry",
    'cat.model.where': "Credits on “{credits}” · hours and staff on “{taught}”",
    'cat.model.addUnder': "Add: {kind}",
    'cat.model.addIn': "In “{name}”",
    'cat.model.up': "Move up",
    'cat.model.down': "Move down",
    'cat.model.empty': "The structure is empty: start by adding a first level.",
    'cat.model.noMatch': "No item matches.",
    'cat.model.hidden': "{count} hidden",
    'cat.model.metaCredits': "{value} credits",
    'cat.model.noResponsible': "No staff",
    'cat.model.total': "Programme total",
    'cat.model.totalPeople': "Subjects: {subjects} · teachers: {teachers}",
    'cat.model.creditGap': "{code}: {actual} credits instead of {expected}",
    'cat.model.deleteTitle': "Delete “{name}”?",
    'cat.model.deleteText': "The item disappears with what it contains ({count} item(s) below), their hours and staff.",
    'cat.model.deleted': "“{name}” is deleted.",
    'cat.drawer.taught': "taught · receives marks",
    'cat.drawer.creditsHere': "credits entered here",
    'cat.drawer.identity': "Identity",
    'cat.drawer.evaluation': "Assessment",
    'cat.drawer.hours': "Hours",
    'cat.drawer.responsibles': "Staff",
    'cat.drawer.creditsHint': "They add up above: semester, year.",
    'cat.drawer.creditsSum': "Credits: {value}, the sum of the items below.",
    'cat.drawer.creditsCarried': "Credits are not entered here: they are carried by “{name}” ({value}).",
    'cat.drawer.coefHint': "Within the item above.",
    'cat.drawer.hoursTotal': "Total: {value} h. Columns come from the teaching types.",
    'cat.drawer.nobody': "No staff yet.",
    'cat.drawer.remove': "Remove {name}",
    'cat.drawer.teacher': "Teacher",
    'cat.drawer.type': "Role",
    'cat.drawer.chooseTeacher': "Choose a teacher",
    'cat.bulk.title': "Bulk entry · {name}",
    'cat.bulk.trail': "Bulk entry · {name}",
    'cat.bulk.sub': "Coefficients, credits, hours and staff for the whole programme, like a spreadsheet: Tab moves between cells.",
    'cat.bulk.back': "Back to the structure",
    'cat.bulk.note': "Totals update live. A semester should total its share of the year’s 60 credits: a gap is flagged without preventing you from saving a structure in progress.",
    'cat.bulk.cell': "{column} of “{name}”",
    'cat.bulk.several': "{count} teachers, see the panel",
    'cat.bulk.changes': "Changes: {count}",
    'cat.bulk.discard': "Discard",
    'cat.bulk.save': "Save ({count})",
    'cat.tracks.none': "This programme has no tracks.",
    'cat.tracks.why': "A track (“Management”, “Logistics”) picks its items; students choose it at enrolment.",
    'cat.tracks.add': "Add a track",
    'cat.tracks.edit': "Edit “{name}”",
    'cat.tracks.dialogHint': "Tick the track’s items; an item ticked in every track is the common core.",
    'cat.tracks.option': "Diploma specialisation",
    'cat.tracks.comment': "Comment",
    'cat.tracks.elements': "Items selected: {count}",
    'cat.tracks.all': "Select all",
    'cat.tracks.clear': "Clear all",
    'cat.tracks.mode': "Status of “{name}”",
    'cat.tracks.mandatory': "mandatory",
    'cat.tracks.elective': "elective",
    'cat.tracks.common': "common core",
    'cat.tracks.out': "not in track",
    'cat.tracks.own': "Own items: {count}",
    'cat.tracks.total': "Items: {count}",
    'cat.tracks.how': "Students choose their track at enrolment; their averages count their track’s items.",
    'cat.tracks.deleteTitle': "Delete the track “{name}”?",
    'cat.tracks.deleteText': "The items stay in the structure; only the track disappears.",
    'cat.eq.from': "From",
    'cat.eq.choose': "Choose a programme",
    'cat.eq.chooseHint': "Choose a past year’s programme to link.",
    'cat.eq.noSource': "No programme from a past year.",
    'cat.eq.linksCount': "links: {count}",
    'cat.eq.linked': "Linked: {count}",
    'cat.eq.unlinked': "Unlinked: {count}",
    'cat.eq.orphans': "Not carried: {count}",
    'cat.eq.byCode': "Link by code",
    'cat.eq.byCodeDone': "Links created by code: {count}.",
    'cat.eq.why': "A student enrolled “on credit” (retaking what was not passed) gets, on each item of this year, their best mark on the linked item. Carrying over creates these links by itself.",
    'cat.eq.thisYear': "This year · {year}",
    'cat.eq.thatYear': "Takes over · {year}",
    'cat.eq.how': "Match",
    'cat.eq.pick': "Equivalent of “{name}”",
    'cat.eq.none': "No equivalent",
    'cat.eq.status.same': "same code",
    'cat.eq.status.renamed': "same code, renamed",
    'cat.eq.status.manual': "chosen by hand",
    'cat.eq.status.none': "to decide",
    'cat.eq.orphansTitle': "Items of {year} with no equivalent this year",
    'cat.eq.orphansHint': "A mark obtained on these items will not be carried over.",
    'cat.renew.open': "Carry over to {year}…",
    'cat.renew.title': "Carry over to {year}",
    'cat.renew.sub': "Copies the chosen programmes of {year}. Nothing changes there.",
    'cat.renew.target': "Target year",
    'cat.renew.list': "Programmes of {year}",
    'cat.renew.already': " · already there",
    'cat.renew.elements': "items: {count}",
    'cat.renew.copied': "What is copied",
    'cat.renew.notCopied': "What is not",
    'cat.renew.copy.model': "The structure: semesters, modules, subjects, codes",
    'cat.renew.copy.evaluation': "Coefficients, credits, thresholds, compensation",
    'cat.renew.copy.hours': "Hours by teaching type",
    'cat.renew.copy.tracks': "Tracks and their items",
    'cat.renew.copy.dates': "Dates shifted like the years",
    'cat.renew.copy.responsibles': "Subject staff",
    'cat.renew.copy.responsiblesHint': " · to review: visiting staff often change",
    'cat.renew.copy.not': "Enrolments, classes, marks and deliberations",
    'cat.renew.linked': "Each copied item is linked to its original: a student retaking in {year} a subject of {from} finds their mark there.",
    'cat.renew.total': "Programmes: {count} · items created: {elements}",
    'cat.renew.submit': "Carry over ({count})",
    'cat.renew.done': "Programmes carried over to {year}: {count}.",
    'cat.renew.emptyTitle': "{year} has no programme yet.",
    'cat.renew.emptyText': "“Carry over” copies the programmes of {year} and links each item to its equivalent, for students retaking a subject from one year to the next.",
    'cat.info.shape': "Structure",
    'cat.info.session': "Dates and places",
    'cat.info.sessionHint': "A programme has one session: its values are set with “Edit”.",
    'cat.info.none': "Not provided.",
    'violation.cat.kind_not_allowed': "This kind cannot go under this item (domain template).",
    'violation.cat.already_renewed': "This programme already exists in the target year.",
    'violation.cat.source_year': "The source year must come first.",
    'violation.cat.not_in_training': "This item does not belong to this programme.",
    'violation.cat.root': "A programme is managed from the programme list.",
    'violation.cat.capacity': "The maximum must exceed the minimum.",
    // D48 : la scolarité.
    'ref.section.numbering': "Numbering",
    'ref.numbering.sub': "How the institution numbers its students: by hand, or from a pattern.",
    'ref.numbering.mode': "Numbering mode",
    'ref.numbering.mode.manual': "Manual entry",
    'ref.numbering.mode.manual.hint': "The number is typed on the record. It stays unique within the institution.",
    'ref.numbering.mode.generated': "Automatic generation",
    'ref.numbering.mode.generated.hint': "Kelassi suggests the next number on creation; it can still be changed.",
    'ref.numbering.pattern': "Pattern",
    'ref.numbering.patternHint': "Fixed text and tokens. A rank {N:x} is required.",
    'ref.numbering.token.AAAA': "4-digit year",
    'ref.numbering.token.AA': "2-digit year",
    'ref.numbering.token.CODE': "programme code",
    'ref.numbering.token.N': "rank in the year (4 digits)",
    'ref.numbering.example': "Examples:",
    'ref.numbering.manualNote': "Numbers carried over from the legacy follow various formats (\"B4/F123/25\"): manual entry keeps them as they are.",
    'ref.numbering.foot': "The counter restarts at 1 each year; a number already taken is skipped.",
    'ref.numbering.saved': "Numbering saved.",
    'scol.year': "Year",
    'scol.year.current': "current",
    'scol.year.closed': "closed",
    'scol.yearClosed': "This year is closed: its enrolments can no longer be changed.",
    'scol.noNumber': "no number",
    'scol.notEnrolled': "Not enrolled",
    'scol.graduated': "Graduate",
    'scol.graduatedIn': "Graduated {year}",
    'scol.enrolledIn': "Enrolled in {year}",
    'scol.inactiveIn': "Inactive in {year}",
    'scol.integratedOn': "joined on {date}",
    'scol.issuedOn': "issued on {date}",
    'scol.scholarship': "Scholarship",
    'scol.scholarship.short': "S",
    'scol.repeating': "Repeating",
    'scol.enrol': "Enrol…",
    'scol.checkAll': "Check all",
    'scol.checkNone': "Uncheck all",
    'scol.sex.F': "Female",
    'scol.sex.M': "Male",
    'scol.kind.personal': "personal",
    'scol.kind.work': "work",
    'scol.mode.standard': "Standard",
    'scol.mode.custom': "Custom",
    'scol.mode.credit': "Credit",
    'scol.mode.standard.hint': "Every element of the programme",
    'scol.mode.custom.hint': "Some of the elements, chosen here",
    'scol.mode.credit.hint': "Retake what was not passed",
    'scol.regime.external': "Day student",
    'scol.regime.internal': "Boarder",
    'scol.enrolment.state.active': "Active",
    'scol.enrolment.state.inactive': "Inactive",
    'scol.flag.isOfficer': "Officer",
    'scol.flag.isEmployee': "Employed",
    'scol.flag.hasSpecialNeeds': "Special needs",
    'scol.flag.hasOutstandingIssue': "Record to regularise",
    'scol.column.student': "Student",
    'scol.column.trainingClass': "Programme {year} · class",
    'scol.column.training': "Programme",
    'scol.column.mode': "Mode",
    'scol.column.class': "Class",
    'scol.column.state': "Status",
    'scol.column.particulars': "Particulars",
    'scol.filter.training': "Programme",
    'scol.filter.allTrainings': "All programmes",
    'scol.filter.state': "Status",
    'scol.filter.mode': "Mode",
    'scol.filter.allModes': "All modes",
    'scol.filter.active': "Active",
    'scol.filter.inactive': "Inactive",
    'scol.filter.all': "All",
    'scol.students.title': "Students",
    'scol.students.count': "{count} student(s)",
    'scol.students.sub': "Every student of the school, enrolled in the year or not.",
    'scol.students.new': "New student",
    'scol.students.search': "Name, number, ID or Massar code",
    'scol.students.empty': "No student matches these criteria.",
    'scol.students.extra': "+ {count} more",
    'scol.students.state.enrolled': "Enrolled in {year}",
    'scol.students.state.inactive': "Inactive in {year}",
    'scol.students.state.none': "Not enrolled in {year}",
    'scol.students.state.graduated': "Graduates",
    'scol.students.state.all': "All students",
    'scol.backToStudents': "Students",
    'scol.student.new': "New student",
    'scol.student.newHint': "The record first; enrolment comes right after.",
    'scol.student.edit': "Edit {name}",
    'scol.student.create': "Create",
    'scol.student.enrolNow': "Enrol straight away",
    'scol.student.enrolNowHint': "Opens the enrolment assistant after creation.",
    'scol.panel.identity': "Identity",
    'scol.panel.enrolments': "Enrolments",
    'scol.panel.coordinates': "Contact details",
    'scol.panel.parents': "Parents and contacts",
    'scol.panel.diplomas': "Diplomas",
    'scol.panel.enrolment': "Enrolment",
    'scol.panel.history': "History",
    'scol.file.noEnrolment': "No enrolment.",
    'scol.file.noCoordinates': "No contact details.",
    'scol.file.noParent': "No parent or contact.",
    'scol.file.noDiploma': "No diploma.",
    'scol.field.lastName': "Last name",
    'scol.field.firstNames': "First names",
    'scol.field.sex': "Sex",
    'scol.field.birthDate': "Date of birth",
    'scol.field.nationalId': "National ID",
    'scol.field.nationalIdIssuedOn': "ID issued on",
    'scol.field.passport': "Passport",
    'scol.field.massar': "Massar code",
    'scol.field.number': "Student number",
    'scol.field.integratedOn': "Joined on",
    'scol.field.integratedOnHint': "Today if left empty",
    'scol.field.phone': "Phone",
    'scol.field.phones': "Phones",
    'scol.field.email': "Email",
    'scol.field.emails': "Emails",
    'scol.field.address': "Address",
    'scol.field.addresses': "Addresses",
    'scol.field.street': "Street",
    'scol.field.postalCode': "Postal code",
    'scol.field.city': "City",
    'scol.field.country': "Country",
    'scol.field.kind': "Type",
    'scol.field.particulars': "Particulars",
    'scol.field.observations': "Notes",
    'scol.field.relationship': "Relationship",
    'scol.field.occupation': "Occupation",
    'scol.field.diploma': "Diploma",
    'scol.field.speciality': "Speciality",
    'scol.field.diplomaYear': "Year",
    'scol.field.average': "Average",
    'scol.field.mention': "Grade",
    'scol.field.school': "School",
    'scol.field.training': "Programme",
    'scol.field.track': "Track",
    'scol.field.class': "Class",
    'scol.field.mode': "Mode",
    'scol.field.regime': "Boarding",
    'scol.field.enrolledOn': "Enrolled on",
    'scol.field.elements': "Elements followed",
    'scol.field.date': "Date",
    'scol.field.reason': "Reason",
    'scol.field.className': "Class name",
    'scol.number.manualHint': "Typed by hand, unique",
    'scol.number.generatedHint': "Empty: the next one from {pattern}",
    'scol.number.keepHint': "Can be changed; stays unique",
    'scol.dup.title': "This person may already exist",
    'scol.dup.born': "born {date}",
    'scol.dup.cin': "ID {cin}",
    'scol.dup.reason.national_id': "same ID",
    'scol.dup.reason.name_and_birth': "same name and date of birth",
    'scol.dup.reason.similar_name': "similar name",
    'scol.dup.open': "Open record",
    'scol.dup.hint': "Nothing is blocked: a namesake can still be created.",
    'scol.add.phone': "Add a phone",
    'scol.add.email': "Add an email",
    'scol.add.address': "Add an address",
    'scol.contact.add': "Add a contact",
    'scol.contact.noType': "No relationship given",
    'scol.contact.remove': "Remove this contact",
    'scol.diploma.add': "Add a diploma",
    'scol.diploma.edit': "Edit diploma",
    'scol.diploma.remove': "Remove this diploma",
    'scol.diploma.average': "average {average}",
    'scol.diploma.mention': "grade {mention}",
    'scol.diploma.kind.access': "entry",
    'scol.diploma.kind.delivered': "awarded",
    'scol.remove.confirm': "Remove \"{label}\"?",
    'scol.saved.identity': "Identity saved.",
    'scol.saved.coordinates': "Contact details saved.",
    'scol.saved.contact': "Contact added.",
    'scol.saved.diploma': "Diploma saved.",
    'scol.saved.removed': "\"{label}\" removed.",
    'scol.enrolments.title': "Enrolments",
    'scol.enrolments.count': "{count} enrolment(s)",
    'scol.enrolments.sub': "Who follows which programme, in the chosen year.",
    'scol.enrolments.new': "New enrolment",
    'scol.enrolments.search': "Student or number",
    'scol.enrolments.empty': "No enrolment matches these criteria.",
    'scol.stats.byTraining': "By programme",
    'scol.stats.none': "No active enrolment.",
    'scol.stats.activeInactive': "{active} active · {inactive} inactive",
    'scol.stats.modes': "Modes",
    'scol.stats.internal': "Boarders",
    'scol.stats.scholarship': "Scholarship holders",
    'scol.enrolment.edit': "Edit enrolment",
    'scol.enrolment.delete': "Delete enrolment",
    'scol.enrolment.deleteConfirm': "Delete this enrolment entered by mistake? A dropout is declared, not deleted.",
    'scol.elements.all': "{count} (all)",
    'scol.elements.some': "{count} chosen",
    'scol.event.enrolled': "Enrolment",
    'scol.event.abandoned': "Dropout",
    'scol.event.resumed': "Resumption",
    'scol.event.by': "by {name}",
    'scol.abandon.title': "Declare a dropout",
    'scol.abandon.hint': "The enrolment is deactivated; the dropout is kept in the history with its date and reason.",
    'scol.abandon.submit': "Declare dropout",
    'scol.resume.title': "Resume",
    'scol.resume.hint': "A resumption reactivates the enrolment and its elements. It is kept in the history, with its reason.",
    'scol.resume.submit': "Resume enrolment",
    'scol.done.abandoned': "Dropout declared.",
    'scol.done.resumed': "Enrolment resumed.",
    'scol.done.deleted': "Enrolment deleted.",
    'scol.enrol.new': "New enrolment",
    'scol.enrol.edit': "Edit enrolment",
    'scol.enrol.sub': "Choose the student, the programme and the terms.",
    'scol.enrol.step.student': "Student",
    'scol.enrol.step.training': "Programme and terms",
    'scol.enrol.step.elements': "Elements followed",
    'scol.enrol.pickHint': "Search for the student to enrol; create them if they do not exist yet.",
    'scol.enrol.pickTraining': "Choose a programme",
    'scol.enrol.already': "This student is already enrolled in this programme.",
    'scol.enrol.noTrack': "No track",
    'scol.enrol.classOption': "{name} ({count} enrolled)",
    'scol.enrol.checked': "{count} of {total} elements checked",
    'scol.enrol.creditHint': "Elements already followed are flagged, found through the links between years. Marks will come with lot 3: until then, check by hand what was not passed.",
    'scol.enrol.creditNoHistory': "No past enrolment of this student matches this programme: check by hand.",
    'scol.enrol.followed': "followed in {year} ({code})",
    'scol.enrol.recap': "Enrolment in progress",
    'scol.enrol.onHint': "Today if left empty",
    'scol.enrol.submit': "Enrol",
    'scol.classes.title': "Classes",
    'scol.classes.titleOf': "Classes · {training}",
    'scol.classes.sub': "The classes of each programme.",
    'scol.classes.subOf': "{count} enrolled · drag a student from one class to another",
    'scol.classes.new': "New class",
    'scol.classes.edit': "Edit class",
    'scol.classes.delete': "Delete class",
    'scol.classes.deleteConfirm': "Delete \"{name}\"? Its {count} member(s) become unassigned again.",
    'scol.classes.distribute': "Distribute the unassigned…",
    'scol.classes.distributeConfirm': "Distribute {count} student(s) across {classes} class(es), alphabetically, filling the smallest first?",
    'scol.classes.distributeGo': "Distribute",
    'scol.classes.distributed': "{count} student(s) distributed.",
    'scol.classes.unassigned': "Unassigned",
    'scol.classes.unassigned.short': "unassigned",
    'scol.classes.unassigned.later': "Later",
    'scol.classes.unassign': "Remove from class",
    'scol.classes.move': "Move",
    'scol.classes.moveTo': "To {name}",
    'scol.classes.empty': "Drag students here.",
    'scol.classes.poolEmpty': "Everyone has a class.",
    'scol.classes.delegate': "Class representative",
    'scol.classes.delegate.short': "rep",
    'scol.classes.countDelegate': "{count} · rep: {name}",
    'scol.classes.noTraining': "No programme in {year}.",
    'scol.classes.note': "A class follows every element of the programme. Tutorial and lab groups (\"course groups\") will come with mark entry (lot 3). A student is in only one class per programme.",
    'scol.reenrol.open': "Re-enrol a cohort…",
    'scol.reenrol.title': "Re-enrol a cohort",
    'scol.reenrol.sub': "Move the students of a programme into one of a later year.",
    'scol.reenrol.from': "From",
    'scol.reenrol.to': "To",
    'scol.reenrol.pickFrom': "Choose the source programme",
    'scol.reenrol.pickTo': "Choose the target programme",
    'scol.reenrol.pickBoth': "Choose the source and the target programme.",
    'scol.reenrol.enrolled': "{count} enrolled",
    'scol.reenrol.filter': "Filter",
    'scol.reenrol.empty': "No active student in this programme.",
    'scol.reenrol.already': "already in {training}",
    'scol.reenrol.elsewhere': "enrolled in {training}",
    'scol.reenrol.carried': "What is carried over",
    'scol.reenrol.keepRegime': "Boarding (boarder, day student)",
    'scol.reenrol.keepScholarship': "Scholarship",
    'scol.reenrol.keepScholarshipHint': "to review every year",
    'scol.reenrol.notCarried': "Track and class are chosen in the new programme.",
    'scol.reenrol.note': "Jury decisions will come with lot 3: they will check the admitted students on their own. Until then, check by hand; a student already enrolled in the target is greyed out.",
    'scol.reenrol.submit': "Re-enrol {count} student(s)",
    'scol.reenrol.done': "{count} student(s) re-enrolled in {training}.",
    'violation.scol.already_enrolled': "This student is already enrolled in this programme.",
    'violation.scol.no_element': "Check at least one element.",
    'violation.scol.already_inactive': "This enrolment is already inactive.",
    'violation.scol.already_active': "This enrolment is already active.",
    'violation.scol.not_member': "The representative must be a member of the class.",
    'violation.scol.not_enrolled': "This student is not enrolled in this programme.",
    'violation.scol.no_class': "Create at least one class.",
    'violation.scol.target_year': "The target programme must be in a later year.",
    'violation.scol.invalid_pattern': "Invalid pattern: text and tokens {AAAA}, {AA}, {CODE}, {N:4}, including a rank {N:x}.",
    'journal.action.schooling.created': "Student records: added",
    'journal.action.schooling.updated': "Student records: changed",
    'journal.action.schooling.deleted': "Student records: deleted",
    'journal.action.schooling.abandoned': "Student records: dropout",
    'journal.action.schooling.resumed': "Student records: resumption",
    'journal.action.schooling.reenrolled': "Student records: re-enrolment",
    'scol.target.student': "Student",
    'scol.target.enrolment': "Enrolment",
    'scol.target.class': "Class",
    'scol.journal.members': "Members",
    'scol.journal.distributed': "Distributed",
    'scol.journal.count': "Count",
    'journal.filter.group.schooling': "Student records",
    // D51 : la finance.
    'fin.all': "All",
    'fin.active': "Active",
    'fin.inactive': "Inactive",
    'fin.default': "default",
    'fin.amount': "Amount",
    'fin.net': "Net",
    'fin.paid': "Paid",
    'fin.due': "Balance due",
    'fin.total': "Total",
    'fin.quantity': "Quantity",
    'fin.excludingTax': "Excl. tax",
    'fin.includingTax': "Incl. tax",
    'fin.noTax': "No tax",
    'fin.computed': "Computed.",
    'fin.pdf': "PDF",
    'fin.receipt': "Receipt",
    'fin.deleteConfirm': "Delete “{label}”?",
    'fin.deleted': "“{label}” deleted.",
    'fin.off.title': "Finance module not enabled",
    'fin.off.text': "The super administrator enables the module and sets it up under “Super administration › Modules”.",
    'fin.level.training': "whole programme",
    'fin.level.module': "module",
    'fin.level.subject': "subject",
    'fin.kind.training': "Programme",
    'fin.kind.year': "Year",
    'fin.kind.semester': "Semester",
    'fin.kind.module': "Module",
    'fin.kind.submodule': "Sub-module",
    'fin.kind.subject': "Subject",
    'fin.method.cash': "Cash",
    'fin.method.cheque': "Cheque",
    'fin.method.transfer': "Bank transfer",
    'fin.status.late': "Late",
    'fin.status.current': "Up to date",
    'fin.status.paid': "Paid in full",
    'fin.status.cancelled': "Cancelled",
    'fin.installment.paid': "Paid",
    'fin.installment.partial': "Partly paid",
    'fin.installment.late': "Late",
    'fin.installment.due': "Due",
    'fin.installment.upcoming': "Upcoming",
    'fin.invoice.kind.enrolment': "Enrolment",
    'fin.invoice.kind.on_demand': "On-demand service",
    'fin.service.kind.mandatory': "Mandatory",
    'fin.service.kind.optional': "Optional",
    'fin.service.kind.on_demand': "On demand",
    'fin.service.kindHint.mandatory': "Added to the invoice of every enrolment it applies to.",
    'fin.service.kindHint.optional': "Added to the enrolment invoice when requested.",
    'fin.service.kindHint.on_demand': "Invoiced separately, when the student asks for it.",
    'fin.service.scope.all': "All students",
    'fin.service.scope.cycles': "Cycles",
    'fin.service.scope.levels': "Levels",
    'fin.discount.kind.percent': "Percentage",
    'fin.discount.kind.amount': "Fixed amount",
    'fin.discount.kind.free': "Amount entered when granted",
    'fin.discount.target.invoice': "The whole invoice",
    'fin.discount.target.lines': "Specific lines",
    'fin.discount.training': "The programme price",
    'fin.target.settings': "Settings",
    'fin.target.tax': "Tax",
    'fin.target.schedule': "Schedule",
    'fin.target.service': "Service",
    'fin.target.discount': "Discount",
    'fin.target.price': "Price",
    'fin.target.invoice': "Invoice",
    'fin.target.payment': "Payment",
    'fin.modules.title': "Finance module",
    'fin.modules.sub': "Super administration › Modules · vendor only",
    'fin.modules.list': "Institution modules",
    'fin.modules.finance': "Finance",
    'fin.modules.active': "Active",
    'fin.modules.inactive': "Off",
    'fin.modules.reserved': "Only the super administrator sees this screen. A module that is off disappears from the menu; its data is kept.",
    'fin.modules.enabled': "Finance module enabled",
    'fin.modules.enabledHint': "Opens the Finance menu section, the account in the student record, and the “Finance manager” and “Cashier” roles.",
    'fin.modules.enabledOn': "Enabled on {date} by {who}. Turning it off hides the screens; no invoice or payment is deleted.",
    'fin.modules.levels': "Pricing levels",
    'fin.modules.priceTraining': "Whole programme",
    'fin.modules.priceTrainingHint': "One price per programme and year, invoiced on a standard enrolment.",
    'fin.modules.priceModule': "Module",
    'fin.modules.priceModuleHint': "One price per module, if the institution sells modules individually.",
    'fin.modules.priceSubject': "Subject",
    'fin.modules.priceSubjectHint': "One price per subject, invoiced on custom and credit enrolments for the chosen subjects.",
    'fin.modules.enrolmentMode': "Enrolment type",
    'fin.modules.billed': "Amount invoiced",
    'fin.modules.billedTraining': "Programme price",
    'fin.modules.billedAll': "Sum of its elements’ prices",
    'fin.modules.billedChosen': "Sum of the chosen elements’ prices",
    'fin.modules.billedCredit': "Sum of the prices of the elements retaken",
    'fin.modules.missingPrice': "Plus mandatory services. A programme without a price produces no invoice: the enrolment shows among missing invoices.",
    'fin.modules.currency': "Currency",
    'fin.modules.currencyCode': "ISO code (MAD, XOF, EUR)",
    'fin.modules.decimals': "Decimals",
    'fin.modules.currencyHint': "The currency can no longer change once the first invoice is issued.",
    'fin.modules.currencyLocked': "Invoices exist: the currency is locked.",
    'fin.modules.numbering': "Document numbering",
    'fin.modules.invoicePattern': "Invoice format",
    'fin.modules.receiptPattern': "Receipt format",
    'fin.modules.next': "Next: {number}",
    'fin.modules.insert': "Insert:",
    'fin.modules.reset': "The counter restarts at 1",
    'fin.modules.reset.yearly': "Every academic year",
    'fin.modules.reset.never': "Never",
    'fin.modules.numberingHint': "The format must contain a rank {N:x}. A number once given is never reused, even if the invoice is cancelled.",
    'fin.modules.mentions': "Details printed on invoices and receipts",
    'fin.modules.legalName': "Legal name",
    'fin.modules.address': "Address",
    'fin.modules.businessId': "Business ID (ICE)",
    'fin.modules.taxId': "Tax ID",
    'fin.modules.tradeRegister': "Trade register",
    'fin.modules.footer': "Footer",
    'fin.modules.logo': "The logo comes from the institution’s branding.",
    'fin.modules.saved': "Module settings saved.",
    'fin.prices.title': "Programme prices",
    'fin.prices.sub': "Priced levels: {levels}",
    'fin.prices.renew': "Carry over from another year",
    'fin.prices.renewSub': "Copies prices element by element, by code, into the year shown.",
    'fin.prices.renewFrom': "From",
    'fin.prices.renewGo': "Carry over",
    'fin.prices.renewed': "{count} prices carried over.",
    'fin.prices.bulk': "Apply one price to many",
    'fin.prices.bulkSub': "One price for every element of a level in this programme.",
    'fin.prices.overwrite': "Replace prices already entered",
    'fin.prices.saved': "{count} prices saved.",
    'fin.prices.invalid': "Unreadable amount: “{value}”.",
    'fin.prices.empty': "No programme in this year.",
    'fin.prices.training': "Programme",
    'fin.prices.amount': "Price",
    'fin.prices.schedule': "Schedule",
    'fin.prices.missing': "To complete",
    'fin.prices.complete': "Complete",
    'fin.prices.missingCount': "{count} without price",
    'fin.prices.defaultSchedule': "Default schedule",
    'fin.prices.element': "Catalogue element",
    'fin.prices.level': "Level",
    'fin.prices.credits': "Credits",
    'fin.prices.notPriced': "level not priced",
    'fin.prices.toEnter': "to enter",
    'fin.prices.howTitle': "What a student pays",
    'fin.prices.how': "Standard enrolment: the programme price. Custom or credit: the sum of the followed elements’ prices. Plus mandatory services and any added.",
    'fin.prices.frozenTitle': "Prices and issued invoices",
    'fin.prices.frozen': "An invoice copies the price when issued: changing a price only affects invoices issued afterwards.",
    'fin.services.title': "Additional services",
    'fin.services.sub': "Fees, insurance, options and on-demand services, with their tax.",
    'fin.services.new': "New service",
    'fin.services.empty': "No service.",
    'fin.services.label': "Service",
    'fin.services.scope': "Applies to",
    'fin.services.kind': "Type",
    'fin.services.tax': "Tax",
    'fin.services.students': "Students",
    'fin.services.thisYear': "this year only",
    'fin.services.validity': "Validity",
    'fin.services.validityHint': "All years: the service carries over without copying.",
    'fin.services.allYears': "All years",
    'fin.services.onlyYear': "{year} only",
    'fin.services.saved': "Service saved.",
    'fin.services.delete': "Delete the service",
    'fin.services.deleteConfirm': "Delete “{label}”? A service already invoiced cannot be deleted: deactivate it.",
    'fin.services.deleted': "“{label}” deleted.",
    'fin.services.mandatoryHint': "A mandatory service added mid-year does not change invoices already issued: add it from the students’ accounts.",
    'fin.discounts.title': "Discounts",
    'fin.discounts.sub': "Special discounts, granted from the student’s account.",
    'fin.discounts.new': "New discount",
    'fin.discounts.empty': "No discount.",
    'fin.discounts.label': "Discount",
    'fin.discounts.value': "Value",
    'fin.discounts.target': "Applies to",
    'fin.discounts.rules': "Rules",
    'fin.discounts.granted': "Granted this year",
    'fin.discounts.kind': "Type",
    'fin.discounts.percent': "Percentage",
    'fin.discounts.amount': "Amount",
    'fin.discounts.freeHint': "The amount is entered when granted, with a reason.",
    'fin.discounts.invoiceHint': "Taken off the total, services included.",
    'fin.discounts.restricted': "Dedicated right",
    'fin.discounts.notStackable': "Not combinable",
    'fin.discounts.reason': "Reason required",
    'fin.discounts.flag.stackable': "Combinable with other discounts",
    'fin.discounts.flagHint.stackable': "Otherwise it cannot be granted on an invoice that already has a discount.",
    'fin.discounts.flag.restricted': "Reserved to the “exceptional reduction” right",
    'fin.discounts.flagHint.restricted': "A cashier collects payments but cannot grant it.",
    'fin.discounts.flag.requiresReason': "Reason required",
    'fin.discounts.flagHint.requiresReason': "The reason appears on the invoice and in the log.",
    'fin.discounts.flag.active': "Active",
    'fin.discounts.flagHint.active': "",
    'fin.discounts.journal': "Granting a discount is logged: who, when, on which invoice, with what reason. It shows as a negative line on the invoice and its PDF.",
    'fin.discounts.saved': "Discount saved.",
    'fin.discounts.delete': "Delete the discount",
    'fin.discounts.deleteConfirm': "Delete “{label}”? A discount already granted cannot be deleted: deactivate it.",
    'fin.discounts.deleted': "“{label}” deleted.",
    'fin.taxes.title': "Taxes",
    'fin.taxes.new': "New tax",
    'fin.taxes.empty': "No tax: services are invoiced without tax.",
    'fin.taxes.services': "{count} services",
    'fin.taxes.hint': "A tax already invoiced keeps its rate: create a new one.",
    'fin.taxes.name': "Name",
    'fin.taxes.rate': "Rate (%)",
    'fin.taxes.isDefault': "Default tax for a new service",
    'fin.taxes.saved': "Tax saved.",
    'fin.taxes.delete': "Delete the tax",
    'fin.schedules.title': "Taxes and schedules",
    'fin.schedules.sub': "Finance manager settings.",
    'fin.schedules.list': "Schedules",
    'fin.schedules.new': "New schedule",
    'fin.schedules.empty': "No schedule: every invoice is paid at once.",
    'fin.schedules.pick': "Pick a schedule.",
    'fin.schedules.name': "Name",
    'fin.schedules.grace': "Grace period (days)",
    'fin.schedules.graceHint': "An unpaid instalment becomes “late” after this delay.",
    'fin.schedules.due': "Due",
    'fin.schedules.share': "Share",
    'fin.schedules.day': "Day",
    'fin.schedules.month': "Month",
    'fin.schedules.addLine': "Add an instalment",
    'fin.schedules.dateHint': "A fixed date falls within the academic year: March means the March after the start of the year. A date already past on the invoice day falls that day.",
    'fin.schedules.isDefault': "Institution default schedule",
    'fin.schedules.isDefaultHint': "Used by any programme that does not name another one.",
    'fin.schedules.saved': "Schedule saved.",
    'fin.schedules.delete': "Delete the schedule",
    'fin.schedules.ruleTitle': "How a payment is allocated",
    'fin.schedules.rule': "A payment settles the oldest unpaid instalment first, then the next. Paying by the schedule or by free instalments gives the same result.",
    'fin.schedules.example': "Example",
    'fin.schedules.example1': "12 Sept. · 9,840",
    'fin.schedules.example2': "5 Jan. · 3,000",
    'fin.schedules.example3': "20 Jan. · 6,000",
    'fin.schedules.surplus': "A payment above the balance leaves a credit on the student’s account, never lost.",
    'fin.schedule.onIssue': "On invoice",
    'fin.schedule.onDate': "Fixed date",
    'fin.invoices.title': "Invoices and payments",
    'fin.invoices.sub': "One invoice per enrolment, issued automatically on enrolment.",
    'fin.invoices.tab': "Invoices",
    'fin.invoices.search': "No., student, student number",
    'fin.invoices.filter': "Status",
    'fin.invoices.discounted': "With discount",
    'fin.invoices.empty': "No invoice matches.",
    'fin.invoices.number': "No.",
    'fin.invoices.student': "Student",
    'fin.invoices.training': "Programme",
    'fin.invoices.next': "Next due date",
    'fin.invoices.status': "Status",
    'fin.invoices.missing': "{count} active enrolments have no invoice: their programme had no price at enrolment.",
    'fin.invoices.issueMissing': "Issue the {count} missing invoices",
    'fin.invoices.issueTitle': "Issue missing invoices",
    'fin.invoices.issueConfirm': "Issue an invoice for each of the {count} enrolments without one? Those whose programme still has no price will stay without.",
    'fin.invoices.issueGo': "Issue",
    'fin.invoices.issued': "{count} invoices issued.",
    'fin.payments.tab': "Payments",
    'fin.payments.method': "Method",
    'fin.payments.allMethods': "All methods",
    'fin.payments.from': "From",
    'fin.payments.to': "To",
    'fin.payments.empty': "No payment.",
    'fin.payments.sum': "{count} payments, total:",
    'fin.payments.date': "Date",
    'fin.payments.receipt': "Receipt",
    'fin.payments.by': "Recorded by",
    'fin.dashboard.title': "Finance dashboard",
    'fin.dashboard.sub': "Year {year} · as of {date}",
    'fin.dashboard.billed': "Net invoiced",
    'fin.dashboard.invoices': "{count} invoices",
    'fin.dashboard.paid': "Collected",
    'fin.dashboard.rate': "{rate} of invoiced",
    'fin.dashboard.due': "Balance due",
    'fin.dashboard.students': "{count} students",
    'fin.dashboard.late': "Late",
    'fin.dashboard.discounts': "Discounts granted",
    'fin.dashboard.discountCount': "{count} discounts",
    'fin.dashboard.expected': "Expected to date",
    'fin.dashboard.expectedNote': "{paid} of {expected} expected",
    'fin.dashboard.months': "Payments by month",
    'fin.dashboard.methods': "By payment method",
    'fin.dashboard.cash': "Today’s cash · {date}",
    'fin.dashboard.noPayment': "No payment this year.",
    'fin.dashboard.noCash': "No payment today.",
    'fin.dashboard.byTraining': "By programme",
    'fin.dashboard.invoiceCount': "Invoices",
    'fin.dashboard.collection': "Collection",
    'fin.dashboard.noInvoice': "No invoice this year.",
    'fin.dashboard.oldestLate': "Oldest late payments",
    'fin.dashboard.noLate': "No late payment.",
    'fin.dashboard.allLate': "See all late invoices",
    'fin.archive.title': "History from the former Kelassi",
    'fin.archive.summary': "{count} invoices · {from} to {to}",
    'fin.archive.show': "Show history",
    'fin.archive.hide': "Hide history",
    'fin.archive.bannerTitle': "Imported from the former Kelassi, read-only",
    'fin.archive.bannerText': "Invoices, payments and credit notes as they were entered. No balance is carried over: opening balances come from the institution. Nothing here counts in the current year's account.",
    'fin.archive.billed': "Billed",
    'fin.archive.billedNote': "{count} invoices, cancelled excluded",
    'fin.archive.reductions': "Reductions",
    'fin.archive.special': "special reductions",
    'fin.archive.creditNotes': "credit notes",
    'fin.archive.paid': "Collected",
    'fin.archive.paidNote': "Reversals deducted",
    'fin.archive.expand': "Expand the invoice",
    'fin.archive.collapse': "Collapse the invoice",
    'fin.archive.cancelled': "Cancelled",
    'fin.archive.specialLine': "Special reduction",
    'fin.archive.creditLine': "Credit note of {date}",
    'fin.archive.net': "Net billed",
    'fin.archive.noPayment': "No payment recorded.",
    'fin.archive.comment': "Comment",
    'fin.archive.paymentCancelled': "Cancelled",
    'fin.archive.reversal': "Reversal",
    'fin.archive.dateCorrected': "Date restored",
    'fin.archive.dateCorrectedHint': "“Date restored”: the date entered in the former Kelassi was impossible (year “19”, “20”…); the import restored it from the entry date.",
    'fin.method.direct_debit': "Direct debit",
    'fin.quote.title': "Enrolment fees",
    'fin.quote.sub': "The invoice issued on saving",
    'fin.quote.unpriced': "No price for {training}: the enrolment is saved without an invoice. It will appear among the “missing invoices” until a price exists.",
    'fin.quote.price': "Programme price",
    'fin.quote.mandatory': "Mandatory services",
    'fin.quote.mandatoryHint': "Added automatically, according to the service scope",
    'fin.quote.locked': "mandatory",
    'fin.quote.optional': "Optional services",
    'fin.quote.optionalHint': "Ticked here, or added later from the account",
    'fin.quote.total': "To be invoiced",
    'fin.quote.note': "The invoice is issued when the enrolment is saved, with these lines. Discounts are granted afterwards from the student's account, by the finance team.",
    'fin.quote.taxIncluded': "incl. {tax}",
    'fin.quote.single': "Single payment",
    'fin.quote.schedule': "Schedule · {name}",
    'fin.quote.atIssue': "On enrolment",
    'fin.method.internal': "Carried over",
    'fin.account.discountsCredits': "Discounts and credit notes",
    'fin.credit.open': "Issue a credit note",
    'fin.credit.sub': "Invoice {number} · net {amount}",
    'fin.credit.modeLines': "On lines",
    'fin.credit.modeLinesHint': "Part or all of chosen lines",
    'fin.credit.modeInvoice': "The whole invoice",
    'fin.credit.modeInvoiceHint': "The rest of the invoice at once (withdrawal, error)",
    'fin.credit.line': "Line",
    'fin.credit.billed': "Billed",
    'fin.credit.room': "Creditable",
    'fin.credit.amount': "Credit",
    'fin.credit.total': "Credit note total",
    'fin.credit.reason': "Reason",
    'fin.credit.reasonHint': "It appears on the credit note and in the log.",
    'fin.credit.effect': "Effect on the invoice",
    'fin.credit.before': "Before",
    'fin.credit.after': "After the credit note",
    'fin.credit.net': "Invoice net",
    'fin.credit.due': "Balance due",
    'fin.credit.overpaid': "Overpayment",
    'fin.credit.installments': "Instalments",
    'fin.credit.note': "A credit note is a numbered document that corrects an issued invoice without changing it. Instalments are recalculated: the credit reduces the last ones first.",
    'fin.credit.print': "Print the credit note",
    'fin.credit.go': "Issue the credit note",
    'fin.credit.goPrint': "Issue and print",
    'fin.credit.done': "Credit note issued.",
    'fin.credit.list': "Credit notes",
    'fin.credit.row': "Credit note {number} · {date}",
    'fin.overpaid.title': "{amount} paid beyond what is due",
    'fin.overpaid.text': "The overpayment stays on the student's account until something is decided. It is never lost.",
    'fin.overpaid.transfer': "Carry over to an invoice",
    'fin.overpaid.refund': "Refund…",
    'fin.overpaid.title2': "Return the overpayment",
    'fin.overpaid.sub': "{number} · {amount} overpaid",
    'fin.refund.tab': "Refund",
    'fin.refund.amount': "Amount to refund",
    'fin.refund.amountHint': "At most {amount}",
    'fin.refund.date': "Date",
    'fin.refund.beneficiary': "Beneficiary",
    'fin.refund.beneficiaryHint': "The student or a contact from their file",
    'fin.refund.reason': "Reason",
    'fin.refund.note': "A refund is a cash outflow: numbered, logged, immutable. It is corrected by a cancellation, like a payment.",
    'fin.refund.print': "Print the refund voucher",
    'fin.refund.go': "Refund",
    'fin.refund.done': "Refund recorded.",
    'fin.transfer.tab': "Carry over to an invoice",
    'fin.transfer.none': "No other invoice of the student has a balance due.",
    'fin.transfer.note': "A carry-over is an internal payment: it settles the other invoice without any cash movement and reduces the overpayment accordingly.",
    'fin.transfer.amount': "Amount to carry over",
    'fin.transfer.object': "Subject",
    'fin.transfer.go': "Carry over",
    'fin.transfer.done': "Overpayment carried over.",
    'fin.payment.refund': "Refund",
    'fin.payment.transferOut': "Carried over to another invoice",
    'fin.payment.transferIn': "Carried over from {number}",
    'fin.modules.creditNotePattern': "Credit note format",
    'fin.modules.refundPattern': "Refund format",
    'journal.action.finance.credit_note_issued': "Credit note issued",
    'journal.action.finance.refund_recorded': "Overpayment refunded",
    'journal.action.finance.payment_transferred': "Overpayment carried over",
    'violation.fin.credit_too_high': "The credit note exceeds what remains to be credited.",
    'violation.fin.nothing_to_credit': "This invoice is already fully credited.",
    'violation.fin.no_line': "Choose at least one line.",
    'violation.fin.below_credited': "The invoice would fall below what its credit notes already credited.",
    'violation.fin.invoice_has_credit_note': "This invoice carries a credit note: it can no longer be cancelled.",
    'violation.fin.refund_too_high': "The amount exceeds the invoice overpayment.",
    'violation.fin.transfer_too_high': "The carry-over exceeds the balance due of the target invoice.",
    'violation.fin.transfer_target': "A carry-over goes to another invoice of the same student.",
    'violation.fin.service_not_offered': "This service is not offered for this programme.",
    'account.person': "Linked teacher record",
    'account.person.hint': "The teacher this account represents: they enter marks for their subjects.",
    'account.person.none': "None",
    'account.person.taken': "already linked to another account",
    'ref.pedagogy.absence_type': "Assessment absence types",
    'ref.pedagogy.absence_type.hint': "What each absence is worth: a mark (0) or nothing",
    'ref.target.absence_type': "Absence type",
    'ref.pedagogy.absence.effect': "Effect on the mark",
    'ref.pedagogy.absence.countsLabel': "The absence counts as a mark",
    'ref.pedagogy.absence.value': "Mark given",
    'ref.pedagogy.absence.hint': "Unticked, the absent student is left out of the calculation (an excused absence, for instance). The code can also be typed in the mark cell.",
    'ref.pedagogy.absence.counts': "counts {value}",
    'ref.pedagogy.absence.notCounted': "not counted",
    'journal.action.assessment.created': "Assessment created",
    'journal.action.assessment.updated': "Assessment changed",
    'journal.action.assessment.deleted': "Assessment deleted",
    'journal.action.assessment.validated': "Marks validated",
    'journal.action.assessment.reopened': "Entry reopened",
    'journal.action.assessment.marks_imported': "Marks imported",
    'journal.action.assessment.mark_corrected': "Mark corrected",
    'asm.target.assessment': "Assessment",
    'asm.target.mark': "Mark",
    'asm.journal.time': "Time",
    'asm.journal.candidates': "Candidates",
    'asm.journal.candidate': "Candidate",
    'asm.journal.validated': "Validation",
    'asm.journal.mark': "Mark",
    'asm.journal.marks': "Marks",
    'asm.absence.counts': "counts {value}",
    'asm.absence.notCounted': "not counted",
    'asm.absence.label': "Absence of {name}",
    'asm.banner.validated': "Validated on {date} by {name}.",
    'asm.banner.locked': "Marks are locked. A correction needs a reason, and the previous value is kept.",
    'asm.candidates.add': "Summon students",
    'asm.candidates.addSub': "{count} enrolled students are not summoned yet",
    'asm.candidates.added': "{count} students summoned.",
    'asm.candidates.allIn': "Every enrolled student is summoned.",
    'asm.candidates.classes': "Classes",
    'asm.candidates.go': "Summon",
    'asm.candidates.none': "No student summoned.",
    'asm.candidates.remove': "Remove from the assessment",
    'asm.candidates.removed': "{name} is no longer summoned.",
    'asm.col.absence': "Absence",
    'asm.col.assessment': "Assessment",
    'asm.col.candidates': "Summoned",
    'asm.col.class': "Class",
    'asm.col.entry': "Entry",
    'asm.col.mark': "Mark /{outOf}",
    'asm.col.number': "No.",
    'asm.col.student': "Student",
    'asm.correct': "Correct the mark",
    'asm.correct.current': "Current mark",
    'asm.correct.note': "The correction is logged. It does not unlock the other marks.",
    'asm.correct.reason': "Reason",
    'asm.correct.value': "New mark /{outOf}",
    'asm.corrected': "corrected",
    'asm.corrected.done': "Mark of {name} corrected.",
    'asm.corrections.after': "After",
    'asm.corrections.before': "Before",
    'asm.corrections.by': "By",
    'asm.corrections.date': "Date",
    'asm.corrections.reason': "Reason",
    'asm.corrections.title': "Corrections after validation",
    'asm.delete': "Delete the assessment",
    'asm.delete.confirm': "Delete “{label}” and its summons? No mark has been entered yet.",
    'asm.dialog.addResponsible': "+ add a person in charge",
    'asm.dialog.addStudent': "Summon a single student",
    'asm.dialog.addStudentHint': "For a resit, or a student outside the ticked classes.",
    'asm.dialog.chooseSubject': "Choose the subject",
    'asm.dialog.convoke': "Summon",
    'asm.dialog.convokeHint': "The members of the ticked classes, as of today. The list can then be changed from the assessment (add or remove a student).",
    'asm.dialog.create': "Create and enter marks",
    'asm.dialog.members': "{count} students",
    'asm.dialog.noClass': "No class in this programme: summon students one by one.",
    'asm.dialog.noResponsible': "No person in charge",
    'asm.dialog.responsiblesHint': "Taken from the subject’s people in charge (catalogue). A person in charge enters marks from “My assessments” when their account is linked to their record.",
    'asm.dialog.searchStudent': "Name or number",
    'asm.dialog.sub': "{training} · {year} · {count} students summoned",
    'asm.dialog.subjectTeacher': "subject teacher",
    'asm.edit': "Edit the assessment",
    'asm.field.coefficient': "Weight",
    'asm.field.date': "Date",
    'asm.field.element': "Element",
    'asm.field.end': "End",
    'asm.field.label': "Title",
    'asm.field.labelHint': "Shown in the list and on transcripts",
    'asm.field.labelPlaceholder': "Test 1, Final exam…",
    'asm.field.nature': "Format",
    'asm.field.outOf': "Marked out of",
    'asm.field.responsibles': "People in charge",
    'asm.field.retake': "Resit session",
    'asm.field.start': "Start",
    'asm.field.state': "Status",
    'asm.field.subject': "Subject",
    'asm.field.training': "Programme",
    'asm.field.type': "Type",
    'asm.filter.all': "All summoned",
    'asm.filter.allElements': "All elements",
    'asm.filter.allStates': "All statuses",
    'asm.filter.allTypes': "All types",
    'asm.filter.label': "Filter the summoned students",
    'asm.filter.missing': "No mark",
    'asm.head.coefficient': "weight {value}",
    'asm.head.outOf': "out of {value}",
    'asm.head.responsibles': "in charge: {names}",
    'asm.input.label': "Mark of {name}",
    'asm.keys': "Keyboard: Enter or ↓ moves to the next student, ↑ goes back, Esc undoes the cell. Each mark is saved when you leave its cell. Type an absence code ({codes}) to record it.",
    'asm.kpi.complete': "to validate",
    'asm.kpi.partial': "entry started",
    'asm.kpi.toEnter': "date passed, no mark",
    'asm.kpi.total': "Assessments",
    'asm.kpi.validated': "marks locked",
    'asm.list.empty': "No assessment matches these filters.",
    'asm.list.noTraining': "No programme of this year within your scope.",
    'asm.list.note': "No averages yet: they come with the calculation. Here, assessments and their marks.",
    'asm.list.sub': "A year’s tests and exams, and how far mark entry has gone",
    'asm.list.title': "Assessments",
    'asm.mine.empty': "No assessment in your subjects this year.",
    'asm.mine.sub': "Assessments of the subjects you are in charge of: enter marks until they are validated",
    'asm.mine.title': "My assessments",
    'asm.mine.unlinked': "Your account is not linked to a teacher record: ask the administrator to link it from your account page.",
    'asm.nature.written': "Written",
    'asm.nature.oral': "Oral",
    'asm.nature.practical': "Practical",
    'asm.nature.project': "Project",
    'asm.new': "New assessment",
    'asm.noClass': "No class",
    'asm.reopen': "Reopen entry",
    'asm.reopen.confirm': "Reopen entry? Marks can be changed again without a reason until the next validation.",
    'asm.reopened': "Entry reopened.",
    'asm.retake': "Resit",
    'asm.saved': "Assessment saved.",
    'asm.sheet.download': "Download the Excel sheet",
    'asm.sheet.import': "Import an Excel sheet",
    'asm.sheet.imported': "{count} marks imported ({skipped} empty rows left as they were).",
    'asm.sheet.importing': "Importing…",
    'asm.sheet.refused': "Sheet refused",
    'asm.sheet.refusedSub': "{count} errors: no mark was saved. Fix the sheet and import it again.",
    'asm.sheet.row': "Row {row}:",
    'asm.state.planned': "Upcoming",
    'asm.state.to_enter': "To enter",
    'asm.state.partial': "In progress",
    'asm.state.complete': "Entry complete",
    'asm.state.validated': "Validated",
    'asm.stats.between': "{from} to {to}",
    'asm.stats.counting': "Absences that count",
    'asm.stats.distribution': "Mark distribution",
    'asm.stats.excused': "Absences that do not count",
    'asm.stats.marks': "Marks entered",
    'asm.stats.mean': "Assessment mean {value}",
    'asm.stats.missing': "No mark",
    'asm.stats.noMark': "No mark entered.",
    'asm.stats.progress': "Progress",
    'asm.stats.range': "min {min} · max {max}",
    'asm.status.empty': "no mark",
    'asm.status.saved': "saved",
    'asm.status.saving': "saving…",
    'asm.student.absent': "Absences",
    'asm.student.convoked': "Summons",
    'asm.student.empty': "No assessment for this student.",
    'asm.student.evaluated': "{count} assessments this year",
    'asm.student.marked': "Marked",
    'asm.student.note': "Averages and board decisions will appear here with the calculation. Earlier years come from the former Kelassi, with their marks.",
    'asm.student.title': "Marks",
    'asm.student.toCome': "to come",
    'asm.student.upcoming': "Upcoming",
    'asm.validate': "Validate marks",
    'asm.validate.confirm': "Validate the {count} marks? They will be locked: a correction will then need a reason.",
    'asm.validate.missing': "{count} summoned students with no mark or absence",
    'asm.validated': "Marks validated.",
    'asm.kpi.validatedLabel': "Validated",
    'asm.kpi.toEnterLabel': "To enter",
    'ref.pedagogy.absence.treatment.mark': "Counts as a mark",
    'ref.pedagogy.absence.treatment.mark.hint': "the mark below (usually 0)",
    'ref.pedagogy.absence.treatment.mark.short': "counts",
    'ref.pedagogy.absence.treatment.neutralized': "Neutralised",
    'ref.pedagogy.absence.treatment.neutralized.hint': "the assessment is left out, its weight redistributed",
    'ref.pedagogy.absence.treatment.neutralized.short': "not counted",
    'ref.pedagogy.absence.treatment.blocking': "Blocks the calculation",
    'ref.pedagogy.absence.treatment.blocking.hint': "no average until the situation is settled",
    'ref.pedagogy.absence.treatment.blocking.short': "blocks the calculation",
    'ref.pedagogy.absence.treatment.resit': "Sends to the resit",
    'ref.pedagogy.absence.treatment.resit.hint': "the student retakes the element at the resit session",
    'ref.pedagogy.absence.treatment.resit.short': "sends to the resit",
    'reg.title': "Academic regulations",
    'reg.sub': "How averages, decisions and resits are calculated, version by version, for each year",
    'reg.and': "and",
    'reg.legacy': "carried over from the former Kelassi",
    'reg.lifecycle': "Life cycle of a version",
    'reg.lifecycle.note': "A published version never changes: any correction creates a new version, with an effective date and a reason.",
    'reg.filter.allYears': "All years",
    'reg.list.empty': "No version of the regulations yet.",
    'reg.list.inForce': "in force since {date}",
    'reg.list.frozen': "frozen on {date}",
    'reg.list.archived': "replaced on {date}",
    'reg.list.blocking': "{count} blocking check(s)",
    'reg.list.awaiting': "awaiting approval",
    'reg.list.ready': "ready to submit",
    'reg.list.note': "When a year opens, copy the last version of the previous year and review it before publishing. Publishing requires a second person (four eyes), if the version asks for it.",
    'reg.col.version': "Year and version",
    'reg.col.state': "Status",
    'reg.col.createdBy': "Drafted by",
    'reg.col.decidedBy': "Approved by",
    'reg.col.status': "Situation",
    'reg.col.scope': "Scope",
    'reg.col.profile': "Profile",
    'reg.col.block': "Block",
    'reg.col.value': "Value applied",
    'reg.col.origin': "Origin scope",
    'reg.state.draft': "Draft",
    'reg.state.review': "In review",
    'reg.state.published': "Published",
    'reg.state.frozen': "Frozen",
    'reg.state.archived': "Archived",
    'reg.status.V': "Passed",
    'reg.status.VPC': "Passed by compensation",
    'reg.status.VAR': "Passed after resit",
    'reg.status.R': "Resit",
    'reg.status.NV': "Not passed",
    'reg.status.A': "Absent",
    'reg.status.pending': "pending",
    'reg.status.V.when': "at the threshold, all children passed",
    'reg.status.VPC.when': "above the floor, parent passed",
    'reg.status.VAR.when': "passed thanks to the resit",
    'reg.status.R.when': "not passed, may resit",
    'reg.status.NV.when': "failed the resit, or too low",
    'reg.status.A.when': "not passed, with an absence that counts",
    'reg.outcome.admitted': "Passed",
    'reg.outcome.admitted_with_debts': "Passed with debts",
    'reg.outcome.retake': "Resit",
    'reg.outcome.not_admitted': "Failed",
    'reg.outcome.pending': "Pending",
    'reg.scope.institution': "Institution",
    'reg.scope.cycle': "Cycle",
    'reg.scope.domain': "Field",
    'reg.scope.cycle_domain': "Cycle and field",
    'reg.scope.level': "Level",
    'reg.scope.track': "Track",
    'reg.scope.class': "Class",
    'reg.scope.element': "Programme element",
    'reg.block.scale': "Grading scale",
    'reg.block.weighting': "Weighting",
    'reg.block.absences': "Absences",
    'reg.block.thresholds': "Thresholds and eliminatory marks",
    'reg.block.compensation': "Compensation and credit transfer",
    'reg.block.retake': "Resits",
    'reg.block.priors': "Carried-over marks",
    'reg.block.rounding': "Rounding",
    'reg.block.decisions': "Decisions, honours, rank",
    'reg.criterion.regime': "the regime",
    'reg.criterion.mode': "the enrolment mode",
    'reg.criterion.repeating': "repeating",
    'reg.criterion.scholarship': "the scholarship",
    'reg.criterion.employee': "employee status",
    'reg.criterion.special_needs': "special arrangements",
    'reg.criterion.entry_year': "the entry year",
    'reg.op.is': "is",
    'reg.op.is_not': "is not",
    'reg.value.none': "none",
    'reg.v.types': "By assessment type",
    'reg.v.coefficients': "Coefficients",
    'reg.v.mean': "Simple average",
    'reg.v.redistribute': "is removed, its weight redistributed",
    'reg.v.zero': "counts as 0",
    'reg.v.credits': "Credits",
    'reg.v.equal': "Equal weight",
    'reg.v.elements': "As for elements",
    'reg.v.custom': "Custom weighting",
    'reg.v.catalog': "Programme thresholds",
    'reg.v.level': "Threshold per level",
    'reg.v.no_compensation': "cannot be compensated",
    'reg.v.fails_parent': "also fails its parent",
    'reg.v.best': "The better of the two",
    'reg.v.average': "The average of resit marks",
    'reg.v.last': "The most recent",
    'reg.v.merge': "The average of sessions",
    'reg.v.none': "None",
    'reg.v.half_up': "to nearest",
    'reg.v.half_down': "to nearest, half down",
    'reg.v.half_even': "to nearest, half even",
    'reg.v.down': "truncate",
    'reg.v.up': "up",
    'reg.v.kept': "kept",
    'reg.v.removed': "removed",
    'reg.v.numeric': "Numbers",
    'reg.v.numeric_letter': "Numbers and letter",
    'reg.v.letter': "Letter",
    'reg.v.subject': "Subject",
    'reg.v.submodule': "Sub-module",
    'reg.v.module': "Module",
    'reg.v.semester': "Semester",
    'reg.v.year': "Year",
    'reg.v.mark': "counts as a mark",
    'reg.v.neutralized': "does not count",
    'reg.v.blocking': "blocks the calculation",
    'reg.v.resit': "sends to resit",
    'reg.v.true': "Yes",
    'reg.v.false': "No",
    'reg.v.defined': "defined",
    'reg.v.external': "external",
    'reg.v.internal': "internal",
    'reg.v.standard': "standard",
    'reg.v.credit': "credit",
    'reg.f.outOf': "Maximum mark",
    'reg.f.display': "Decimals shown",
    'reg.f.assessments': "Assessments in a subject",
    'reg.f.typeWeights': "Weight by type",
    'reg.f.missingType': "Type without marks",
    'reg.f.bestOf': "Best marks kept",
    'reg.f.elements': "Elements in their parent",
    'reg.f.semesters': "Semesters in the year",
    'reg.f.semesterWeights': "Semester weights",
    'reg.f.bonusCap': "Bonus points cap",
    'reg.f.overrides': "Treatment",
    'reg.f.source': "Thresholds taken from",
    'reg.f.validation': "Passed at",
    'reg.f.retake': "May resit from",
    'reg.f.eliminatory': "Eliminatory mark",
    'reg.f.eliminatoryEffect': "Effect of the eliminatory mark",
    'reg.f.enabled': "Compensate",
    'reg.f.floor': "Compensable from",
    'reg.f.maxCount': "At most",
    'reg.f.capitalisation': "Credit transfer",
    'reg.f.retained': "Mark kept",
    'reg.f.cap': "Cap after resit",
    'reg.f.wholeGroup': "the whole sub-module goes to resit",
    'reg.f.sessions': "Several sessions of one type",
    'reg.f.mode': "Mode",
    'reg.f.evaluable': "Subject rounding",
    'reg.f.aggregate': "Aggregate rounding",
    'reg.f.digits': "Decimals",
    'reg.f.maxDebts': "Passed with debts: at most",
    'reg.f.debtKind': "Debt level",
    'reg.f.mentions': "Honours",
    'reg.f.mentionAfterRetake': "Honours after resit",
    'reg.f.rank': "Rank",
    'reg.field.whole': "defined",
    'reg.field.year': "Year",
    'reg.field.from': "Copy version",
    'reg.field.fromHint': "Profiles, assignments and test cases are copied. Across years, classes, tracks and elements are matched by their codes.",
    'reg.field.fromNone': "None: one complete default profile",
    'reg.field.reason': "Reason",
    'reg.field.comment': "Comment",
    'reg.field.effectiveFrom': "In force from",
    'reg.field.fourEyes': "Four-eyes approval",
    'reg.field.fourEyesHint': "Four-eyes approval: whoever submits does not publish",
    'reg.field.cohortRule': "Regulations for a student",
    'reg.field.training': "Programme",
    'reg.field.chooseTraining': "Choose a programme",
    'reg.field.choose': "Choose",
    'reg.field.class': "Class",
    'reg.field.allClasses': "All classes",
    'reg.field.student': "Student",
    'reg.field.noStudent': "No particular student",
    'reg.field.element': "Element",
    'reg.field.wholeTraining': "The whole programme",
    'reg.field.name': "Name",
    'reg.cohort.current_year': "the current year's",
    'reg.cohort.entry_year': "that of the entry year",
    'reg.version.label': "{year} · version {number}",
    'reg.version.new': "New version",
    'reg.version.newSub': "A draft, to review and submit for approval",
    'reg.version.create': "Create draft",
    'reg.version.blank': "Created blank",
    'reg.version.basedOn': "Copy of {label}",
    'reg.version.created': "drafted by {who} on {when}",
    'reg.version.submitted': "submitted by {who} on {when}",
    'reg.version.published': "published by {who} on {when}, in force from {from}",
    'reg.version.settings': "Version settings",
    'reg.version.copy': "New version from this one",
    'reg.version.freeze': "Freeze version",
    'reg.version.freezeText': "The year's deliberations are closed: the version stays readable to explain averages and is no longer replaced.",
    'reg.version.delete': "Delete draft",
    'reg.version.deleteText': "The draft, its profiles, assignments and test cases will be deleted.",
    'reg.version.submit': "Submit for approval",
    'reg.version.submitText': "The version can no longer be edited during review. The academic director publishes it or sends it back to draft.",
    'reg.version.submitBlocked': "Fix the blocking checks first.",
    'reg.version.approve': "Approve and publish",
    'reg.version.reject': "Send back to draft",
    'reg.version.fourEyesBlocked': "You submitted this version: someone else must approve it (four eyes).",
    'reg.version.inReview': "Submitted by {who}, awaiting the academic director.",
    'reg.version.publishedNotice': "Version published: it applies from its effective date.",
    'reg.simulate': "Simulate",
    'reg.scopes.title': "Academic scopes",
    'reg.scopes.sub': "Each block's rule comes from the most precise scope that defines it; otherwise it goes up to the institution.",
    'reg.elements.title': "Programme elements",
    'reg.elements.sub': "How a unit or module calculates its own mark.",
    'reg.elements.empty': "No element has its own profile. To assign one, choose the scope “Programme element”.",
    'reg.assign': "Assign a profile",
    'reg.assign.edit': "Edit assignment",
    'reg.criteria.title': "Only for students whose",
    'reg.criteria.add': "Add a criterion",
    'reg.criteria.hint': "All conditions must hold. At equal scope, an assignment with criteria is more precise than one without.",
    'reg.locks.title': "Lock for more precise scopes",
    'reg.tests.title': "Test cases",
    'reg.tests.sub': "Run at every check: a failing case blocks publishing.",
    'reg.tests.add': "Add a case",
    'reg.tests.empty': "No test case.",
    'reg.tests.case': "Case",
    'reg.tests.expected': "Expected",
    'reg.tests.obtained': "Obtained",
    'reg.tests.passed': "passes",
    'reg.tests.failed': "fails",
    'reg.tests.notEnrolled': "not enrolled in this programme",
    'reg.tests.expectedValue': "Expected mark",
    'reg.tests.expectedStatus': "Expected status",
    'reg.checks.title': "Consistency checks",
    'reg.checks.none': "No failing check.",
    'reg.checks.blocking': "{count} blocking",
    'reg.checks.warnings': "{count} warning(s)",
    'reg.check.institution_missing.title': "Institution not covered",
    'reg.check.institution_missing': "Assign a complete profile to the institution.",
    'reg.check.institution_incomplete.title': "Blocks missing at institution level",
    'reg.check.institution_incomplete': "Not defined: {blocks}.",
    'reg.check.duplicate_scope.title': "Duplicate scope",
    'reg.check.duplicate_scope': "{scope} receives several profiles: {profiles}.",
    'reg.check.conflict.title': "Scope conflict",
    'reg.check.conflict': "{scopes} define “{block}” differently, at equal precision, for {training}.",
    'reg.check.lock_violation.title': "Locked block redefined",
    'reg.check.lock_violation': "{scope} ({profile}) redefines “{block}”, locked by a wider scope.",
    'reg.check.type_weights_total.title': "Weights not adding up to 100%",
    'reg.check.type_weights_total': "{profile}: assessment types add up to {total}%.",
    'reg.check.semester_weights_total.title': "Semester weights",
    'reg.check.semester_weights_total': "{profile}: semesters add up to {total}%.",
    'reg.check.unknown_type.title': "Unknown assessment type",
    'reg.check.unknown_type': "{profile}: type {code} does not exist in the framework.",
    'reg.check.unknown_absence.title': "Unknown absence",
    'reg.check.unknown_absence': "{profile}: absence {code} does not exist in the framework.",
    'reg.check.tests_failed.title': "Failing test cases",
    'reg.check.tests_failed': "{failed} of {total} cases fail.",
    'reg.check.tests_passed.title': "Test cases",
    'reg.check.tests_passed': "{total} of {total} cases pass.",
    'reg.check.redundant.title': "Identical override",
    'reg.check.redundant': "{scope} ({profile}) redefines “{block}” with the inherited value.",
    'reg.check.unused_profile.title': "Unassigned profile",
    'reg.check.unused_profile': "{profile} is not assigned to any scope.",
    'reg.check.floor_above_threshold.title': "Floor above threshold",
    'reg.check.floor_above_threshold': "{profile}: the compensation floor ({level}) exceeds the pass threshold.",
    'reg.check.retake_above_threshold.title': "Resit threshold above pass threshold",
    'reg.check.retake_above_threshold': "{profile}: the resit threshold ({level}) exceeds the pass threshold.",
    'reg.profiles.title': "Profiles of this version",
    'reg.profiles.new': "New profile",
    'reg.profiles.newSub': "A partial profile: then define the blocks it changes.",
    'reg.profiles.complete': "complete",
    'reg.profiles.blocks': "{count} block(s)",
    'reg.profiles.used': "{count} assignment(s)",
    'reg.profile.blocks': "Profile blocks",
    'reg.profile.defined': "defined",
    'reg.profile.inherited': "inherits",
    'reg.profile.define': "Define in this profile",
    'reg.profile.definedHere': "Defined in this profile: it replaces the rule of less precise scopes.",
    'reg.profile.inheritedHint': "Not defined here: the rule comes from a less precise scope.",
    'reg.profile.defaultIs': "Kelassi default: {summary}.",
    'reg.profile.unassigned': "Not assigned to any scope",
    'reg.profile.assignedTo': "Assigned to: {scopes}",
    'reg.profile.saved': "Profile saved. The version checks run again.",
    'reg.profile.readOnly': "Published or in review: the profile is read-only.",
    'reg.ed.weighting.assessments': "Assessments in a subject",
    'reg.ed.weighting.typesHint': "in %",
    'reg.ed.weighting.missing': "A type without marks",
    'reg.ed.weighting.bestOf': "{code}: keep only the N best",
    'reg.ed.weighting.elements': "Elements in their parent (modules in the unit, units in the semester…)",
    'reg.ed.weighting.semesters': "Semesters in the year",
    'reg.ed.weighting.addSemester': "Add a semester",
    'reg.ed.weighting.bonusHint': "Only points above a bonus element's threshold count, added before division, as in the former Kelassi.",
    'reg.ed.absences.hint': "By default each absence follows the framework treatment. Change it here for this scope only.",
    'reg.ed.absences.referential': "framework: {value}",
    'reg.ed.absences.asReferential': "As in the framework",
    'reg.ed.thresholds.catalog': "Each programme element's threshold",
    'reg.ed.thresholds.catalogHint': "10 by default, an element may have its own",
    'reg.ed.thresholds.level': "One threshold per level",
    'reg.ed.thresholds.levelHint': "the same for every element of the level",
    'reg.ed.thresholds.retakeHint': "Below the resit threshold, the element fails at once and may not resit. A programme element's own resit threshold prevails.",
    'reg.ed.level': "Level",
    'reg.ed.noThreshold': "no threshold",
    'reg.ed.status': "Status",
    'reg.ed.when': "When",
    'reg.ed.compensation.between': "Compensate between the children of a…",
    'reg.ed.compensation.submodule': "sub-module (its subjects)",
    'reg.ed.compensation.module': "module (its subjects, sub-modules)",
    'reg.ed.compensation.semester': "semester (its modules)",
    'reg.ed.compensation.year': "year (its semesters)",
    'reg.ed.compensation.hint': "A child below its threshold passes by compensation if its parent reaches its own, if it is above the floor and has no eliminatory mark, best first, within the limit.",
    'reg.ed.compensation.capitalisation': "A passed module or unit stays acquired if the semester fails (credit transfer)",
    'reg.ed.retake.enabled': "A failed element goes to resit",
    'reg.ed.retake.averageHint': "as in the former Kelassi",
    'reg.ed.retake.wholeGroup': "If a subject of a sub-module goes to resit, the whole sub-module does",
    'reg.ed.priors.best': "Take the acquired mark if better (credits, repeating)",
    'reg.ed.priors.none': "Carry nothing over",
    'reg.ed.rounding.evaluable': "Round a subject mark",
    'reg.ed.rounding.aggregate': "Round modules, semesters, years",
    'reg.ed.rounding.hint': "Calculation uses integers to the millionth; rounding applies where requested, then on display.",
    'reg.ed.decisions.mentions': "Award honours (field scale)",
    'reg.ed.decisions.kept': "Honours kept after resit",
    'reg.ed.decisions.removed': "Honours removed after resit",
    'reg.ed.decisions.rank': "Compute the rank in the programme",
    'reg.ed.decisions.hint': "Passed if the whole is passed; passed with debts within the limit; resit if an element goes to resit; failed otherwise.",
    'reg.sum.scale': "out of {outOf}, {display}",
    'reg.sum.weighting': "{assessments}; type without marks: {missing}; elements: {elements}; semesters: {semesters}",
    'reg.sum.absences.referential': "as in the framework",
    'reg.sum.thresholds.catalog': "programme thresholds",
    'reg.sum.thresholds.noEliminatory': "no eliminatory mark",
    'reg.sum.thresholds.eliminatory': "eliminatory mark {value}: {effect}",
    'reg.sum.compensation.none': "no compensation",
    'reg.sum.compensation.level': "{level}: floor {floor}, at most {max}",
    'reg.sum.retake': "{retained}, cap {cap}",
    'reg.sum.retake.closed': "no resit",
    'reg.sum.rounding': "subjects: {evaluable}; aggregates: {aggregate}; display: {display} decimals",
    'reg.sum.decisions': "debts: {debts}; honours: {mentions}; rank: {rank}",
    'reg.effective.open': "Effective rule",
    'reg.effective.title': "Effective rule",
    'reg.effective.sub': "What applies to a programme, class or student, block by block, and where it comes from",
    'reg.effective.inForce': "The version in force",
    'reg.effective.noVersion': "No published version for this year.",
    'reg.effective.chooseTraining': "Choose a programme.",
    'reg.effective.student': "{name}: regime {regime}, enrolment {mode}, class(es) {classes}.",
    'reg.effective.locked': "locked",
    'reg.effective.conflict': "In conflict with {scopes}",
    'reg.effective.overridden': "Set aside by the lock: {scopes}",
    'reg.effective.default': "Kelassi default",
    'reg.effective.conditional': "Conditional: depending on the student",
    'reg.explain.open': "See how an average is calculated",
    'reg.explain.sub': "{training} · {year} · how the average is built, down to assessment marks",
    'reg.explain.noVersion': "No version of the regulations applies to this year.",
    'reg.explain.notEnrolled': "No active enrolment to explain.",
    'reg.explain.noMark': "no mark",
    'reg.explain.weight': "weight {weight}",
    'reg.explain.threshold': "threshold {threshold}",
    'reg.explain.bonus': "bonus",
    'reg.explain.retake': "resit: {after} replaces {before}",
    'reg.explain.prior': "acquired mark carried over: {origin}",
    'reg.explain.rule': "rule: {block} · {scope} · {profile}",
    'reg.explain.coef': "coefficient {coef}",
    'reg.explain.debts': "Debts: {list}",
    'reg.explain.footer': "{version} · engine {engine} · computed on {date}",
    'reg.sim.title': "Simulate version {number}",
    'reg.sim.sub': "Apply the version to actual marks and compare it with another, student by student. Nothing is saved.",
    'reg.sim.compare': "Compare with",
    'reg.sim.noCompare': "No comparison",
    'reg.sim.with': "With {label} ({state})",
    'reg.sim.running': "Computing…",
    'reg.sim.changes': "What changes between the two versions",
    'reg.sim.changesSub': "In plain words, scope by scope.",
    'reg.sim.chooseTraining': "Choose a programme to see the effect on its students.",
    'reg.sim.students': "Students simulated",
    'reg.sim.values': "Averages that change",
    'reg.sim.valuesNote': "to the hundredth, on at least one element",
    'reg.sim.decisions': "Decisions that change",
    'reg.sim.decisionsNote': "{better} better, {worse} worse",
    'reg.sim.mentions': "Honours that change",
    'reg.sim.before': "Compared average",
    'reg.sim.after': "With version {number}",
    'reg.sim.decisionBefore': "Compared decision",
    'reg.sim.decisionAfter': "Decision",
    'reg.sim.mention': "Honours",
    'reg.sim.rank': "rank {rank}",
    'reg.sim.note': "The simulation saves nothing. A published result changes only through an explicit recalculation, after publishing, and leaves a trace.",
    'reg.diff.none': "No difference in rules.",
    'reg.diff.added': "new profile “{profile}”",
    'reg.diff.removed': "profile “{profile}” removed",
    'reg.diff.profile': "“{before}” becomes “{after}”",
    'reg.approve.title': "Approve and publish version {number}",
    'reg.approve.sub': "Regulations {year} · four-eyes approval",
    'reg.approve.by': "Requested by {who}, on {when}",
    'reg.approve.noBlocking': "No blocking check",
    'reg.approve.tests': "Test cases: {passed} of {total}",
    'reg.approve.simulation': "See the simulation",
    'reg.approve.note': "Once published, the version never changes. It archives the year's previous version.",
    'violation.reg.open_version': "A version of this year is already being drafted or reviewed.",
    'violation.reg.not_draft': "Only a draft can be edited; a correction creates a new version.",
    'violation.reg.four_eyes': "Four-eyes approval: someone other than the submitter must decide.",
    'violation.reg.blocking_checks': "Blocking checks remain to be fixed.",
    'violation.reg.profile_in_use': "This profile is assigned: remove its assignments first.",
    'violation.reg.profile_name_taken': "A profile already has this name in this version.",
    'violation.reg.invalid': "Invalid value.",
    'violation.reg.out_of_range': "Value out of range.",
    'violation.reg.precision': "Two decimals at most.",
    'violation.reg.unknown_target': "Target not found.",
    'violation.reg.type_weights_empty': "Give a weight to at least one assessment type.",
    'violation.reg.test_expectation': "An expected mark or status.",
    'violation.reg.year_closed': "This year is closed.",
    'reg.print': "Print",
    'reg.scope.training': "Programme",
    'reg.v.blocks_compensation': "cancels all compensation in its parent",
    'reg.ed.thresholds.general': "general",
    'reg.ed.thresholds.generalLabel': "General eliminatory mark",
    'reg.ed.thresholds.eliminatoryHint': "An eliminatory mark per level replaces the general one for that level. Below it, the element is not compensated.",
    'reg.ed.thresholds.exempt': "Subjects inside a submodule are exempt from the eliminatory mark",
    'journal.action.regulation.created': "Regulations: added",
    'journal.action.regulation.updated': "Regulations: changed",
    'journal.action.regulation.deleted': "Regulations: deleted",
    'journal.action.regulation.submitted': "Regulations submitted for approval",
    'journal.action.regulation.published': "Regulations published",
    'journal.action.regulation.rejected': "Regulations sent back to draft",
    'journal.action.regulation.frozen': "Regulations frozen",
    'journal.action.deliberation.computed': "Results computed",
    'journal.action.deliberation.closed': "Deliberation closed",
    'journal.action.deliberation.reopened': "Deliberation reopened",
    'journal.target.regulation': "Regulations",
    'journal.target.regulation_profile': "Regulations profile",
    'journal.target.deliberation': "Deliberation",
    'delib.title': "Deliberations",
    'delib.sub': "{year} · results computed and finalised, programme by programme, session by session",
    'delib.kpi.trainings': "Programmes",
    'delib.kpi.trainingsNote': "to deliberate this year",
    'delib.kpi.closed': "Closed",
    'delib.kpi.closedNote': "results finalised",
    'delib.kpi.open': "Open",
    'delib.kpi.openNote': "to close",
    'delib.kpi.none': "Not yet computed",
    'delib.kpi.noneNote': "no recorded computation",
    'delib.field.year': "Year",
    'delib.list.empty': "No programme within your scope this year.",
    'delib.list.note': "Every computation is recorded, numbered and traced: the regulations version, who ran it and why. A closed deliberation is no longer recomputed; reopening it requires a reason.",
    'delib.col.training': "Programme and session",
    'delib.col.state': "Status",
    'delib.col.lastRun': "Last computation",
    'delib.col.enrolled': "Students",
    'delib.col.rank': "Rank",
    'delib.col.number': "No.",
    'delib.col.student': "Student",
    'delib.col.year': "Average",
    'delib.col.decision': "Decision",
    'delib.col.bonus': "Jury bonus",
    'delib.col.mention': "Honours",
    'delib.outcome.admitted': "Admitted",
    'delib.outcome.admitted_with_debts': "With debts",
    'delib.outcome.retake': "Resit",
    'delib.outcome.not_admitted': "Not admitted",
    'delib.state.open': "Open",
    'delib.state.closed': "Closed",
    'delib.state.none': "Not yet computed",
    'delib.run.none': "none",
    'delib.run.number': "computation no. {number}",
    'delib.run.legacy': "Former Kelassi",
    'delib.session.first': "Regular session",
    'delib.session.retake': "Resit session",
    'delib.session.label': "Session",
    'delib.head.noRun': "{year} · no recorded computation yet",
    'delib.head.run': "{year} · computation no. {number} on {at} by {by} · regulations {regulation} · engine {engine}",
    'delib.action.history': "History",
    'delib.action.compute': "Compute",
    'delib.action.recompute': "Recompute",
    'delib.action.close': "Close the deliberation",
    'delib.action.reopen': "Reopen",
    'delib.action.bonus': "Bonus",
    'delib.closed.note': "Deliberation closed on {at} by {by}: results are finalised.",
    'delib.empty.compute': "No computation for this session. Run the first computation.",
    'delib.empty.read': "No computation for this session.",
    'delib.todo.title': "To handle before closing",
    'delib.todo.ready': "Ready to close",
    'delib.todo.unvalidated': "{count} assessment(s) not validated",
    'delib.todo.pending': "{count} student(s) without result",
    'delib.todo.near': "{count} student(s) near the threshold",
    'delib.filter.label': "Filter students",
    'delib.filter.all': "All",
    'delib.filter.review': "To review",
    'delib.filter.changed': "Changed since computation no. {number}",
    'delib.filter.bonus': "Jury bonuses",
    'delib.tag.near': "near the threshold",
    'delib.tag.changed': "changed",
    'delib.bonus.marker': "Jury bonus",
    'delib.legend': "The jury icon marks a student who received a bonus. “To review”: average within half a point of the threshold, or a missing result.",
    'delib.compute.title': "Compute the results",
    'delib.compute.note': "The computation is added to the previous ones, with the regulations version in force. Differences with the previous computation are counted and flagged.",
    'delib.field.reason': "Reason (optional)",
    'delib.field.reasonRequired': "Reason",
    'delib.field.comment': "Comment for the minutes",
    'delib.close.title': "Close the deliberation",
    'delib.close.validated': "All assessments are validated",
    'delib.close.pending': "{count} student(s) without result: “pending” in the minutes",
    'delib.close.allResults': "Every student has a result",
    'delib.close.bonuses': "{count} jury bonus(es), all justified",
    'delib.close.counts': "{admitted} admitted · {debts} with debts · {retake} resit · {failed} not admitted · {pending} pending",
    'delib.close.note': "Once closed, no more computations or bonuses; the minutes, transcripts and certificates can be produced. Reopening remains possible, with a reason.",
    'delib.reopen.title': "Reopen the deliberation",
    'delib.reopen.note': "The reason is traced; every later computation will require one too. Documents already produced remain in the history.",
    'delib.bonus.title': "Jury bonus",
    'delib.bonus.note': "The jury acts through bonuses: points added to an element’s average, within the regulations’ limit. The decision follows from the new computation.",
    'delib.bonus.on': "On",
    'delib.bonus.points': "Points",
    'delib.bonus.cap': "at most {cap} (regulations)",
    'delib.bonus.result': "Resulting decision",
    'delib.bonus.element': "Element",
    'delib.bonus.before': "Computed",
    'delib.bonus.after': "With the bonus",
    'delib.bonus.reasonHint': "It appears in the minutes and in the log.",
    'delib.bonus.existing': "Bonuses already granted",
    'delib.bonus.remove': "Remove",
    'delib.bonus.save': "Save the bonus",
    'delib.kind.year': "Year",
    'delib.kind.semester': "Semester",
    'delib.kind.module': "Module",
    'delib.kind.submodule': "Submodule",
    'delib.kind.subject': "Subject",
    'delib.history.title': "Deliberation history",
    'delib.history.sub': "{training} · {session} · {year}: every computation, bonus and closure, in order",
    'delib.history.empty': "No deliberation for this session.",
    'delib.history.compare': "What changed",
    'delib.history.between': "What changed between computation no. {from} and no. {to}",
    'delib.history.pair': "no. {from} and no. {to}",
    'delib.history.run': "Computation no. {number}",
    'delib.history.single': "A single computation: nothing to compare.",
    'delib.history.same': "Nothing changed.",
    'delib.history.summary': "{values} mark(s), {statuses} status(es) and {decisions} decision(s) changed, for {students} student(s).",
    'delib.history.note': "Nothing is erased: a computation does not replace the previous one, it adds to it. The trace always says which regulations version was used, and why it was recomputed.",
    'journal.action.deliberation.bonus_granted': "Jury bonus granted",
    'journal.action.deliberation.bonus_changed': "Jury bonus changed",
    'journal.action.deliberation.bonus_removed': "Jury bonus removed",
    'reg.f.juryBonusCap': "Jury bonus, at most",
    'reg.ed.decisions.juryBonusHint': "The jury acts through bonuses on an element’s average, within this limit; empty: no cap.",
    'doc.title': "Documents",
    'doc.sub': "{year} · transcripts, minutes and certificates issued from closed deliberations",
    'doc.batches.title': "Issued documents",
    'doc.batches.empty': "No document issued for this year.",
    'doc.col.document': "Document",
    'doc.col.for': "For",
    'doc.col.count': "Count",
    'doc.col.issued': "Issued",
    'doc.col.reference': "Reference",
    'doc.col.state': "Status",
    'doc.col.actions': "Actions",
    'doc.students': "{count} students",
    'doc.state.open': "Deliberation open",
    'doc.state.superseded': "Superseded",
    'doc.state.issued': "Issued",
    'doc.state.published': "{count} on the portal",
    'doc.action.toDeliberation': "Open the deliberation",
    'doc.noneClosed': "No closed deliberation this year: documents are issued after closing.",
    'doc.note': "Every issued document is numbered, logged (who, when, for whom) and verifiable by its reference. Reopening a deliberation supersedes its documents.",
    'doc.action.preparing': "Preparing…",
    'doc.action.zip': "Zip archive",
    'doc.action.pdf': "PDF",
    'doc.action.excel': "Excel",
    'doc.action.publish': "Publish to the portal",
    'doc.action.unpublish': "Withdraw from the portal",
    'doc.action.producing': "Issuing…",
    'doc.action.produce': "Issue",
    'doc.action.previewMinutes': "Minutes preview and options",
    'doc.action.one': "Issue for one student, with preview",
    'doc.action.verify': "Verify",
    'doc.notice.published': "{count} document(s) published to the portal.",
    'doc.notice.unpublished': "{count} document(s) withdrawn from the portal.",
    'doc.notice.issued': "{count} {type}: done.",
    'doc.notice.one': "Document issued: it is numbered and logged.",
    'doc.produce.title': "Issue for a closed deliberation",
    'doc.field.deliberation': "Deliberation",
    'doc.field.type': "Documents",
    'doc.field.for': "For",
    'doc.field.format': "Format",
    'doc.field.publish': "Publish to the portal: each student downloads their own",
    'doc.field.publishOne': "Publish to the student’s portal",
    'doc.field.reference': "Reference",
    'doc.field.student': "Student",
    'doc.field.lang': "Language",
    'doc.field.sort': "Order",
    'doc.for.deliberation': "The deliberation",
    'doc.for.admitted': "Students who passed ({count})",
    'doc.for.all': "All students ({count})",
    'doc.format.minutes': "Landscape PDF and Excel",
    'doc.format.zip': "One PDF per student, in a zip archive",
    'doc.type.transcript': "Transcript",
    'doc.type.minutes': "Minutes",
    'doc.type.certificate': "Certificate of achievement",
    'doc.types.transcript': "Transcripts",
    'doc.types.minutes': "Minutes",
    'doc.types.certificate': "Certificates of achievement",
    'doc.verify.title': "Verify a document",
    'doc.verify.valid': "Authentic",
    'doc.verify.superseded': "Superseded",
    'doc.verify.altered': "Does not match",
    'doc.verify.unknown': "Unknown reference",
    'doc.verify.unknownBody': "No document bears the reference {reference}. Check what you typed.",
    'doc.verify.body': "{type} of {student}, {training} {year}, {institution}; issued on {date} by {by} from run no. {run}.",
    'doc.verify.supersededBody': "It was superseded on {date}: it is no longer valid.",
    'doc.verify.alteredBody': "The results it shows no longer match the finalised ones: report it to the institution.",
    'doc.verify.publicTitle': "Verify a document",
    'doc.verify.publicSub': "Transcript, minutes or certificate: enter the reference printed under the QR code.",
    'doc.sort.rank': "By rank",
    'doc.sort.name': "By name",
    'doc.flag.transcript.rank': "Show the rank",
    'doc.flag.transcript.credits': "Show the credits",
    'doc.flag.transcript.details': "List the subjects",
    'doc.flag.transcript.first': "Include the first session",
    'doc.flag.minutes.details': "Detail the modules (Excel: one sheet per semester)",
    'doc.flag.minutes.bonuses': "Board bonuses and reasons",
    'doc.flag.certificate.mention': "Show the honours",
    'doc.preview': "Document preview",
    'doc.produceOne.transcript': "Issue the transcript",
    'doc.produceOne.minutes': "Issue the minutes",
    'doc.produceOne.certificate': "Issue the certificate",
    'doc.produceOne.transcriptNote': "The transcript is issued from a closed deliberation: it shows exactly the finalised results and carries a verifiable reference.",
    'doc.produceOne.minutesNote': "The minutes show the closed deliberation's grid, the reasoned bonuses and the signatories.",
    'doc.produceOne.certificateNote': "Only for a student who passed, from a closed deliberation. Every certificate is numbered and logged: who, when, for whom.",
    'journal.action.document.issued': "Documents issued",
    'journal.action.document.published': "Documents published to the portal",
    'journal.action.document.unpublished': "Documents withdrawn from the portal",
    'journal.action.document.settings_changed': "Document settings changed",
    'journal.target.document': "Documents",
    'doc.verify.bodyMinutes': "{type}, {training} {year}, {institution}; issued on {date} by {by} from run no. {run}.",
    'scol.tab.file': "File",
    'scol.tab.marks': "Marks",
    'scol.tab.finance': "Finance",
    'scol.tab.history': "History",
    'trace.filter.kind': "Event type",
    'trace.filter.all': "All",
    'trace.filter.changes': "Changes",
    'trace.filter.accesses': "Views",
    'trace.filter.table': "Part of the record",
    'trace.filter.allTables': "The whole record",
    'trace.filter.actor': "Author",
    'trace.filter.allActors': "Everyone",
    'trace.empty': "Nothing in the history for these filters.",
    'trace.access.student': "viewed the student’s record",
    'trace.access.marks': "viewed the marks",
    'trace.access.results': "viewed the results",
    'trace.access.documents': "opened a document",
    'trace.access.finance': "viewed the account",
    'trace.access.exports': "downloaded an export",
    'trace.op.insert': "created",
    'trace.op.update': "changed",
    'trace.op.delete': "deleted",
    'trace.opShort.insert': "Created",
    'trace.opShort.update': "Changed",
    'trace.opShort.delete': "Deleted",
    'trace.more': "{count} more field(s)",
    'trace.less': "Fewer details",
    'trace.older': "Older",
    'trace.request.all': "everything this action wrote",
    'trace.request.title': "What this action wrote",
    'trace.request.who': "Who",
    'trace.request.when': "When",
    'trace.request.where': "From",
    'trace.request.id': "Request",
    'trace.request.record': "Record",
    'trace.request.what': "What",
    'trace.request.operation': "Operation",
    'trace.request.change': "Change",
    'trace.request.note': "One action can write several records at once: they share the same request and are read together here.",
    'trace.aside.title': "What the history keeps",
    'trace.aside.changes': "Every change, before and after, even made outside the application: the database writes it itself.",
    'trace.aside.accesses': "Views of the record, marks, results and documents, if the institution traces them.",
    'trace.aside.retention': "Five years, then the history is purged.",
    'trace.aside.locked': "Nobody can change or erase the history, not even an application administrator.",
    'trace.actor.console': "Server console",
    'trace.actor.sql': "Outside the application (SQL)",
    'trace.actor.unknown': "Unknown",
    'trace.source.api': "From the application",
    'trace.source.console': "From the server console",
    'trace.source.sql': "Outside the application",
    'trace.value.yes': "yes",
    'trace.value.no': "no",
    'trace.console.title': "Traceability",
    'trace.console.sub': "{institution} · what the platform keeps of every action",
    'trace.console.saved': "Settings saved.",
    'trace.console.reads': "Views",
    'trace.console.readsSub': "Configurable for this institution",
    'trace.console.readsToggle': "Trace views of sensitive data",
    'trace.console.readsHint': "Who read what, and when. Depending on the institution, this is not always required.",
    'trace.console.writes': "Changes",
    'trace.console.writesBody': "Always traced. Every creation, change or deletion is written by the database itself, before and after, including outside the application. This is not a setting.",
    'trace.console.retention': "Retention",
    'trace.console.duration': "Duration",
    'trace.console.years': "{count} years",
    'trace.console.durationHint': "minimum enforced by the database",
    'trace.console.oldest': "Oldest entry",
    'trace.console.oldestHint': "since going live",
    'trace.console.purge': "Purge",
    'trace.console.monthly': "Monthly",
    'trace.console.purgeHint': "on the 1st of each month",
    'trace.console.purgeNote': "The purge only removes what is more than five years old. Nobody can shorten this period or erase a more recent entry.",
    'trace.category.student': "Student record",
    'trace.category.studentHint': "Identity, addresses, contacts, enrolments",
    'trace.category.marks': "Marks",
    'trace.category.marksHint': "Student marks, assessment sheet",
    'trace.category.results': "Results",
    'trace.category.resultsHint': "Deliberations, a student’s results",
    'trace.category.documents': "Documents",
    'trace.category.documentsHint': "Downloaded transcripts, minutes, certificates",
    'trace.category.finance': "Finance",
    'trace.category.financeHint': "Student account, invoices, receipts",
    'trace.category.exports': "Exports",
    'trace.category.exportsHint': "Excel sheets, archives, exported logs",
    'journal.action.trace.settings_changed': "Traceability settings changed",
    'journal.target.trace': "Traceability",
    'trace.table.person': "Record",
    'trace.table.student': "Student",
    'trace.table.enrolment': "Enrolment",
    'trace.table.enrolment_element': "Enrolment element",
    'trace.table.enrolment_event': "Enrolment event",
    'trace.table.class_member': "Class",
    'trace.table.person_address': "Address",
    'trace.table.person_phone': "Phone",
    'trace.table.person_email': "Email",
    'trace.table.contact': "Contact",
    'trace.table.contact_relationship': "Parent or contact",
    'trace.table.student_diploma': "Diploma",
    'trace.table.assessment_mark': "Mark",
    'trace.table.mark_correction': "Mark correction",
    'trace.table.assessment': "Assessment",
    'trace.table.jury_bonus': "Board bonus",
    'trace.table.issued_document': "Issued document",
    'trace.table.invoice': "Invoice",
    'trace.table.invoice_line': "Invoice line",
    'trace.table.invoice_installment': "Instalment",
    'trace.table.payment': "Payment",
    'trace.table.credit_note': "Credit note",
    'trace.table.user_account': "Account",
    'trace.table.role_assignment': "Role assignment",
    'trace.table.deliberation': "Deliberation",
    'trace.table.result_run': "Results run",
    'trace.table.admin_audit_log': "Action log",
    'trace.field.last_name': "Last name",
    'trace.field.first_names': "First names",
    'trace.field.sex': "Sex",
    'trace.field.birth_date': "Birth date",
    'trace.field.national_id_number': "National ID",
    'trace.field.passport_number': "Passport",
    'trace.field.occupation': "Occupation",
    'trace.field.enrolment_number': "Student no.",
    'trace.field.massar_code': "Massar code",
    'trace.field.national_code': "National code",
    'trace.field.street': "Street",
    'trace.field.city': "City",
    'trace.field.postal_code': "Postal code",
    'trace.field.country': "Country",
    'trace.field.number': "Number",
    'trace.field.address': "Address",
    'trace.field.kind': "Kind",
    'trace.field.training_id': "Programme",
    'trace.field.academic_year_id': "Year",
    'trace.field.class_group_id': "Class",
    'trace.field.track_id': "Track",
    'trace.field.mode': "Mode",
    'trace.field.regime': "Regime",
    'trace.field.state': "Status",
    'trace.field.scholarship': "Scholarship",
    'trace.field.repeating': "Repeating",
    'trace.field.enrolled_on': "Enrolled on",
    'trace.field.assigned_on': "Assigned on",
    'trace.field.value': "Mark",
    'trace.field.bonus': "Bonus",
    'trace.field.entered_at': "Entered on",
    'trace.field.absence_type_id': "Absence",
    'trace.field.assessment_id': "Assessment",
    'trace.field.entered_by_id': "Entered by",
    'trace.field.value_before': "Previous mark",
    'trace.field.value_after': "New mark",
    'trace.field.reason': "Reason",
    'trace.field.points': "Points",
    'trace.field.element_id': "Element",
    'trace.field.label': "Label",
    'trace.field.name': "Name",
    'trace.field.email': "Email",
    'trace.field.display_name': "Display name",
    'trace.field.password_hash': "Password",
    'trace.field.amount': "Amount",
    'trace.field.reference': "Reference",
    'trace.field.published_at': "Published on",
    'trace.field.superseded_at': "Superseded on",
    'trace.field.school': "School",
    'trace.field.year': "Year",
    'trace.field.average': "Average",
    'trace.field.mention': "Honours",
    'trace.field.integrated_on': "Joined on",
    'trace.field.person_id': "Person",
    'trace.field.student_id': "Student",
    'trace.value.personal': "personal",
    'trace.value.professional': "work",
    'trace.value.home': "home",
    'trace.value.active': "active",
    'trace.value.abandoned': "abandoned",
    'trace.value.M': "male",
    'trace.value.F': "female",
    'trace.value.***': "(hidden)",
    'asm.field.counted': "Count towards the average",
    'asm.field.countedHint': "Untick for a diagnostic test, a mock exam or a summer-course test: its marks stay on the sheet and the transcript, marked “not counted”, but enter no average or decision.",
    'asm.notCounted': "not counted",
    'asm.notCountedHint': "Its marks enter no average or decision.",
    'doc.settings.title': "Document settings",
    'doc.settings.sub': "{institution} · the heading, the place and the signatories of each document",
    'doc.settings.saved': "Settings saved. They apply to the next documents issued.",
    'doc.settings.invalid': "Check the flagged fields: each signatory needs a title.",
    'doc.settings.head': "Heading and place",
    'doc.settings.tagline': "Line under the name",
    'doc.settings.taglineHint': "Printed under the institution’s name",
    'doc.settings.place': "Place of signature",
    'doc.settings.placeHint': "“Tangier, …”",
    'doc.settings.hint.minutes': "In signing order; a title alone leaves room for a handwritten signature",
    'doc.settings.hint.transcript': "With no signatory set: “For the management” and the issuer’s name",
    'doc.settings.hint.certificate': "The first signatory’s title opens the sentence: “The academic director of … certifies that”",
    'doc.settings.default.minutes': "No signatory set: the minutes will carry four default titles.",
    'doc.settings.default.transcript': "No signatory set: “For the management” and the issuer’s name.",
    'doc.settings.default.certificate': "No signatory set: “The management of … certifies that”.",
    'doc.settings.titleField': "Title",
    'doc.settings.nameField': "Name (optional)",
    'doc.settings.up': "Move up",
    'doc.settings.down': "Move down",
    'doc.settings.add': "Add a signatory",
    'doc.settings.preview': "Footer preview",
    'doc.settings.signature': "signature",
    'doc.settings.note': "Settings are copied into each document when it is issued: changing them alters no document already issued.",
    'trace.group': "{count} × {what}",
    'trace.detail': "show details",
    'trace.drawer.title': "History",
    'trace.drawer.assessment': "{label} · its marks, validation and corrections",
    'trace.drawer.invoice': "Invoice {number} · its lines, instalments, payments and credit notes",
    'trace.drawer.account': "{name} · account, roles and access",
    'trace.drawer.open': "History",
    'trace.field.validated_at': "Validated on",
    'trace.field.validated_by_id': "Validated by",
    'trace.field.validated_by_name': "Validated by",
    'trace.field.corrected_at': "Corrected on",
    'trace.field.corrected_by_name': "Corrected by",
    'trace.field.mark_id': "Mark",
    'trace.field.counted': "Counted in the average",
    'trace.field.retake': "Resit",
    'trace.field.coefficient': "Coefficient",
    'trace.field.out_of': "Out of",
    'trace.field.held_on': "Date",
    'trace.field.subject_id': "Subject",
    'trace.field.type_id': "Type",
    'trace.field.nature': "Nature",
    'trace.field.starts_at': "Start",
    'trace.field.ends_at': "End",
    'trace.field.issued_on': "Issued on",
    'trace.field.received_on': "Received on",
    'trace.field.method': "Method",
    'trace.field.due_on': "Due date",
    'trace.field.active': "Active",
    'trace.field.role_id': "Role",
    'trace.field.locale': "Language",
    'trace.field.population': "Population",
    'trace.table.assignment_product': "Scope: programme",
    'trace.table.assignment_domain': "Scope: domain",
    'trace.table.assignment_cycle': "Scope: cycle",
    'trace.table.user_identity': "Sign-in identity",
    'trace.table.two_factor_recovery_code': "Recovery code",
    'trace.table.assessment_class': "Convened class",
    'trace.table.assessment_responsible': "Assessment owner",
    'trace.table.absence_justification': "Absence justification",
    'trace.table.absence_type': "Absence type",
    'trace.table.academic_year': "Academic year",
    'trace.table.account_invitation': "Account invitation",
    'trace.table.assessment_type': "Assessment type",
    'trace.table.assignment_center': "Scope: centre",
    'trace.table.attendance_record': "Attendance record",
    'trace.table.attendance_waiver': "Attendance waiver",
    'trace.table.calendar_period': "Calendar period",
    'trace.table.chat_message': "Chat message",
    'trace.table.class_group': "Class",
    'trace.table.communication_attachment': "Attachment",
    'trace.table.communication_settings': "Communication settings",
    'trace.table.contact_type': "Contact type",
    'trace.table.conversation': "Conversation",
    'trace.table.conversation_member': "Conversation member",
    'trace.table.department': "Department",
    'trace.table.diploma': "Diploma",
    'trace.table.diploma_option': "Diploma option",
    'trace.table.disciplinary_field': "Disciplinary field",
    'trace.table.discount': "Discount",
    'trace.table.discount_service': "Discounted service",
    'trace.table.document_settings': "Document settings",
    'trace.table.document_signatory': "Signatory",
    'trace.table.employee': "Employee",
    'trace.table.fee_service': "Service",
    'trace.table.fee_service_cycle': "Service: cycle",
    'trace.table.fee_service_level': "Service: level",
    'trace.table.finance_archive_credit': "Archived credit note",
    'trace.table.finance_archive_invoice': "Archived invoice",
    'trace.table.finance_archive_line': "Archived invoice line",
    'trace.table.finance_archive_payment': "Archived payment",
    'trace.table.finance_settings': "Billing settings",
    'trace.table.identity_provider': "Identity provider",
    'trace.table.institution': "Institution",
    'trace.table.letter_grade': "Letter grade",
    'trace.table.letter_scale': "Letter scale",
    'trace.table.login_policy': "Sign-in policy",
    'trace.table.mention_band': "Honours band",
    'trace.table.mention_scale': "Honours scale",
    'trace.table.menu_item': "Menu item",
    'trace.table.menu_item_translation': "Menu item translation",
    'trace.table.notification': "Notification",
    'trace.table.notification_recipient': "Notification recipient",
    'trace.table.notification_reply': "Notification reply",
    'trace.table.notification_type': "Notification type",
    'trace.table.organisation': "Organisation",
    'trace.table.pay_adjustment': "Pay adjustment",
    'trace.table.pay_statement': "Pay statement",
    'trace.table.payment_schedule': "Payment schedule",
    'trace.table.payment_schedule_line': "Payment schedule line",
    'trace.table.payroll_settings': "Payroll settings",
    'trace.table.permission': "Permission",
    'trace.table.permission_translation': "Permission translation",
    'trace.table.planning_event': "Timetable event",
    'trace.table.planning_event_class': "Event class",
    'trace.table.planning_event_room': "Event room",
    'trace.table.planning_event_student': "Event student",
    'trace.table.planning_event_teacher': "Event teacher",
    'trace.table.product': "Training or element",
    'trace.table.product_equivalence': "Equivalence",
    'trace.table.product_hours': "Teaching hours",
    'trace.table.product_price': "Price",
    'trace.table.product_responsible': "Element owner",
    'trace.table.regulation_assignment': "Regulation assignment",
    'trace.table.regulation_profile': "Regulation profile",
    'trace.table.regulation_test_case': "Regulation test case",
    'trace.table.regulation_version': "Regulation version",
    'trace.table.relations_agreement': "Agreement",
    'trace.table.relations_agreement_commitment': "Agreement commitment",
    'trace.table.relations_agreement_training': "Agreement training",
    'trace.table.relations_agreement_type': "Agreement type",
    'trace.table.relations_alumni_position': "Alumni position",
    'trace.table.relations_contact': "Organisation contact",
    'trace.table.relations_contact_function': "Contact role",
    'trace.table.relations_exchange': "Interaction",
    'trace.table.relations_exchange_account': "Interaction participant",
    'trace.table.relations_exchange_contact': "Interaction contact",
    'trace.table.relations_org_type': "Organisation type",
    'trace.table.relations_profile': "Relationship profile",
    'trace.table.relations_prospect': "Prospect",
    'trace.table.relations_prospect_stage': "Prospect stage",
    'trace.table.relations_prospect_step': "Stage change",
    'trace.table.relations_sector': "Sector",
    'trace.table.relations_settings': "Relations settings",
    'trace.table.relations_survey_token': "Survey link",
    'trace.table.responsible_type': "Owner type",
    'trace.table.role': "Role",
    'trace.table.role_permission': "Role permission",
    'trace.table.role_translation': "Role translation",
    'trace.table.room': "Room",
    'trace.table.session_series': "Session series",
    'trace.table.session_series_class': "Series class",
    'trace.table.session_series_teacher': "Series teacher",
    'trace.table.structure_level': "Structure level",
    'trace.table.structure_template': "Structure template",
    'trace.table.student_numbering': "Student numbering",
    'trace.table.tax': "Tax",
    'trace.table.teacher': "Teacher",
    'trace.table.teacher_category': "Teacher category",
    'trace.table.teacher_contract': "Teacher contract",
    'trace.table.teacher_contract_rate': "Contract rate",
    'trace.table.teacher_contract_type': "Contract type",
    'trace.table.teacher_diploma': "Teacher diploma",
    'trace.table.teacher_disciplinary_field': "Teacher disciplinary field",
    'trace.table.teacher_objective': "Hours objective",
    'trace.table.teaching_type': "Teaching type",
    'trace.table.trace_settings': "Traceability settings",
    'trace.table.training_center': "Training centre",
    'trace.table.training_cycle': "Training cycle",
    'trace.table.training_domain': "Training domain",
    'trace.table.training_level': "Training level",
    'trace.table.training_session': "Training session",
    'trace.table.training_track': "Track",
    'trace.table.training_track_element': "Track element",
    'trace.table.venue': "Site",
    'trace.field.abbreviation': "Abbreviation",
    'trace.field.absence_after': "New absence",
    'trace.field.absence_before': "Previous absence",
    'trace.field.accepted_at': "Accepted on",
    'trace.field.account_id': "Account",
    'trace.field.acronym': "Acronym",
    'trace.field.added_at': "Added on",
    'trace.field.added_by_id': "Added by",
    'trace.field.agreement_id': "Agreement",
    'trace.field.agreement_window_days': "Agreement expiry notice (days)",
    'trace.field.alert_key': "Alert key",
    'trace.field.alert_target': "Alert target",
    'trace.field.allocation': "Allocation",
    'trace.field.allowed_domains': "Allowed domains",
    'trace.field.amount_excluding_tax': "Amount excluding tax",
    'trace.field.amount_gross': "Gross amount",
    'trace.field.amount_net': "Net amount",
    'trace.field.answered_at': "Answered on",
    'trace.field.archived_at': "Archived on",
    'trace.field.at': "Date",
    'trace.field.attachments_max_mb': "Maximum attachment size (MB)",
    'trace.field.author_id': "Author",
    'trace.field.author_name': "Author",
    'trace.field.auto_provision': "Automatic account creation",
    'trace.field.avatar_mime': "Photo format",
    'trace.field.avatar_version': "Photo version",
    'trace.field.bank': "Bank",
    'trace.field.based_on_id': "Based on",
    'trace.field.batch_id': "Batch",
    'trace.field.blocking': "Blocking",
    'trace.field.blocks': "Blocks",
    'trace.field.body': "Content",
    'trace.field.business_id': "Business ID",
    'trace.field.by_id': "By",
    'trace.field.by_name': "By",
    'trace.field.can_deliberate': "Can deliberate",
    'trace.field.can_enter_marks': "Can enter marks",
    'trace.field.can_validate_marks': "Can validate marks",
    'trace.field.cancel_reason': "Cancellation reason",
    'trace.field.cancelled': "Cancelled",
    'trace.field.cancelled_at': "Cancelled on",
    'trace.field.cancelled_by_id': "Cancelled by",
    'trace.field.capacity': "Capacity",
    'trace.field.capacity_max': "Maximum capacity",
    'trace.field.capacity_min': "Minimum capacity",
    'trace.field.categories': "Categories",
    'trace.field.category_id': "Category",
    'trace.field.changes': "Changes",
    'trace.field.chat_email_digest': "Chat email digest",
    'trace.field.class_groups': "Classes",
    'trace.field.client_id': "Client ID",
    'trace.field.client_secret_encrypted': "Client secret",
    'trace.field.closed_at': "Closed on",
    'trace.field.closed_by_id': "Closed by",
    'trace.field.closed_by_name': "Closed by",
    'trace.field.closed_on': "Closed on",
    'trace.field.code': "Code",
    'trace.field.code_hash': "Code",
    'trace.field.cohort_rule': "Cohort rule",
    'trace.field.color': "Colour",
    'trace.field.comment': "Comment",
    'trace.field.compensation_rule': "Compensation rule",
    'trace.field.computed_at': "Computed on",
    'trace.field.computed_by_id': "Computed by",
    'trace.field.computed_by_name': "Computed by",
    'trace.field.contact_id': "Contact",
    'trace.field.contact_type_id': "Contact type",
    'trace.field.content': "Content",
    'trace.field.contract_id': "Contract",
    'trace.field.conversation_id': "Conversation",
    'trace.field.copied_from': "Copied from",
    'trace.field.corrected_by_id': "Corrected by",
    'trace.field.counter_reset': "Counter reset",
    'trace.field.counterpart_id': "Counterpart",
    'trace.field.created_by_id': "Created by",
    'trace.field.created_by_name': "Created by",
    'trace.field.credit': "Credit",
    'trace.field.credit_note_pattern': "Credit note number pattern",
    'trace.field.credits': "Credits",
    'trace.field.credits_earned': "Credits earned",
    'trace.field.criteria': "Criteria",
    'trace.field.currency': "Currency",
    'trace.field.current': "Current",
    'trace.field.current_run_id': "Current computation",
    'trace.field.customized': "Customised",
    'trace.field.cycle_average_rule': "Cycle average rule",
    'trace.field.cycle_id': "Cycle",
    'trace.field.date_corrected': "Corrected date",
    'trace.field.decided_at': "Decided on",
    'trace.field.decided_by_id': "Decided by",
    'trace.field.decided_by_name': "Decided by",
    'trace.field.decimals': "Decimals",
    'trace.field.decision_comment': "Decision comment",
    'trace.field.default_compensation': "Default compensation",
    'trace.field.default_locale': "Default language",
    'trace.field.delegate_id': "Delegate",
    'trace.field.deliberation_id': "Deliberation",
    'trace.field.department_id': "Department",
    'trace.field.description': "Description",
    'trace.field.digest': "Fingerprint",
    'trace.field.digested_at': "Digest sent on",
    'trace.field.diploma_id': "Diploma",
    'trace.field.diploma_option_id': "Diploma option",
    'trace.field.disciplinary_field_id': "Disciplinary field",
    'trace.field.discount_amount': "Discount amount",
    'trace.field.discount_id': "Discount",
    'trace.field.discount_rate': "Discount rate",
    'trace.field.discount_scope': "Discount scope",
    'trace.field.document_name': "Document",
    'trace.field.document_size': "Document size",
    'trace.field.document_type': "Document type",
    'trace.field.domain_id': "Domain",
    'trace.field.drawer': "Drawer",
    'trace.field.due_day': "Due day",
    'trace.field.due_kind': "Due type",
    'trace.field.due_month': "Due month",
    'trace.field.duration_years': "Duration (years)",
    'trace.field.effective_from': "Effective from",
    'trace.field.email_at_provider': "Email at provider",
    'trace.field.email_canonical': "Email",
    'trace.field.email_default': "Email by default",
    'trace.field.email_failed': "Email failed",
    'trace.field.emailed_at': "Emailed on",
    'trace.field.employee_number': "Employee number",
    'trace.field.enabled': "Enabled",
    'trace.field.enabled_at': "Enabled on",
    'trace.field.enabled_by_id': "Enabled by",
    'trace.field.enabled_locales': "Enabled languages",
    'trace.field.ends_on': "End",
    'trace.field.engine_version': "Engine version",
    'trace.field.enrollable': "Open for enrolment",
    'trace.field.enrolment_id': "Enrolment",
    'trace.field.event_id': "Event",
    'trace.field.every_weeks': "Every (weeks)",
    'trace.field.exchange_id': "Interaction",
    'trace.field.expected_status': "Expected status",
    'trace.field.expected_value': "Expected value",
    'trace.field.expires_at': "Expires on",
    'trace.field.extra_cap_hours': "Overtime cap (hours)",
    'trace.field.extra_minutes': "Extra minutes",
    'trace.field.extra_rate_gross': "Gross overtime rate",
    'trace.field.extra_rate_net': "Net overtime rate",
    'trace.field.failed_attempts': "Failed attempts",
    'trace.field.fax': "Fax",
    'trace.field.fee_service_id': "Service",
    'trace.field.first_on': "First session",
    'trace.field.footer': "Footer",
    'trace.field.four_eyes': "Four-eyes approval",
    'trace.field.from_student': "From the student",
    'trace.field.frozen_at': "Frozen on",
    'trace.field.function_id': "Role",
    'trace.field.given_by_id': "Given by",
    'trace.field.grace_days': "Grace period (days)",
    'trace.field.grade': "Grade",
    'trace.field.graduation_year': "Graduation year",
    'trace.field.granted_at': "Granted on",
    'trace.field.granted_by_id': "Granted by",
    'trace.field.granted_by_name': "Granted by",
    'trace.field.granted_on': "Granted on",
    'trace.field.gross': "Gross",
    'trace.field.guests': "Guests",
    'trace.field.handled_at': "Handled on",
    'trace.field.happened_at': "Date and time",
    'trace.field.happened_on': "Date",
    'trace.field.has_graduated': "Graduated",
    'trace.field.has_outstanding_issue': "Outstanding issue",
    'trace.field.has_special_needs': "Special needs",
    'trace.field.hours': "Hours",
    'trace.field.icon': "Icon",
    'trace.field.identity_provider_id': "Identity provider",
    'trace.field.included_hours': "Included hours",
    'trace.field.internship': "Internship",
    'trace.field.invited_by_id': "Invited by",
    'trace.field.invoice_id': "Invoice",
    'trace.field.invoice_pattern': "Invoice number pattern",
    'trace.field.is_default': "Default",
    'trace.field.is_employee': "Employee",
    'trace.field.is_officer': "Officer",
    'trace.field.is_private': "Private",
    'trace.field.issued_at': "Issued on",
    'trace.field.issued_by_id': "Issued by",
    'trace.field.issued_by_name': "Issued by",
    'trace.field.issuer': "Issuer",
    'trace.field.job_title': "Job title",
    'trace.field.joined_at': "Joined on",
    'trace.field.justification_id': "Justification",
    'trace.field.kenrol_identifier': "Kenrol identifier",
    'trace.field.last_exchange_at': "Last interaction",
    'trace.field.last_login_at': "Last sign-in",
    'trace.field.last_message_at': "Last message",
    'trace.field.last_on': "Last session",
    'trace.field.last_read_at': "Last read",
    'trace.field.last_reply_at': "Last reply",
    'trace.field.last_used_at': "Last used",
    'trace.field.late_minutes': "Minutes late",
    'trace.field.left_at': "Left on",
    'trace.field.legacy': "Legacy",
    'trace.field.legal_form': "Legal form",
    'trace.field.legal_name': "Legal name",
    'trace.field.letter': "Letter",
    'trace.field.letter_scale_id': "Letter scale",
    'trace.field.level_id': "Level",
    'trace.field.lines': "Lines",
    'trace.field.locked_until': "Locked until",
    'trace.field.locks': "Locks",
    'trace.field.lost': "Lost",
    'trace.field.main': "Main",
    'trace.field.mandatory': "Mandatory",
    'trace.field.mark': "Mark",
    'trace.field.mark_value': "Mark given",
    'trace.field.max_hours': "Maximum hours",
    'trace.field.max_points': "Maximum points",
    'trace.field.max_score': "Maximum score",
    'trace.field.mention_scale_id': "Honours scale",
    'trace.field.menu_item_id': "Menu item",
    'trace.field.mime': "Format",
    'trace.field.min_hours': "Minimum hours",
    'trace.field.min_score': "Minimum score",
    'trace.field.minutes': "Minutes",
    'trace.field.module': "Module",
    'trace.field.month': "Month",
    'trace.field.monthly_gross': "Monthly gross",
    'trace.field.monthly_net': "Monthly net",
    'trace.field.moved_from': "Moved from",
    'trace.field.must_change_password': "Must change password",
    'trace.field.muted': "Muted",
    'trace.field.national_id_issued_on': "ID card issued on",
    'trace.field.net': "Net",
    'trace.field.notation': "Grading",
    'trace.field.note': "Note",
    'trace.field.notes': "Notes",
    'trace.field.notice_months': "Notice (months)",
    'trace.field.notification_id': "Notification",
    'trace.field.objective': "Objective",
    'trace.field.objectives': "Objectives",
    'trace.field.observations': "Observations",
    'trace.field.obsolete': "Obsolete",
    'trace.field.occurred_on': "Occurred on",
    'trace.field.online_allowed': "Online allowed",
    'trace.field.option_id': "Option",
    'trace.field.options': "Options",
    'trace.field.organisation_id': "Organisation",
    'trace.field.organisation_name': "Organisation",
    'trace.field.origin': "Origin",
    'trace.field.other_organisation_name': "Other organisation",
    'trace.field.override_by_name': "Conflict overridden by",
    'trace.field.override_reason': "Override reason",
    'trace.field.owner_id': "Owner",
    'trace.field.owner_kind': "Attached to",
    'trace.field.paid_by_name': "Paid by",
    'trace.field.paid_method': "Payment method",
    'trace.field.paid_on': "Paid on",
    'trace.field.paid_reference': "Payment reference",
    'trace.field.parent_id': "Parent",
    'trace.field.partner_since': "Partner since",
    'trace.field.party': "Party",
    'trace.field.passing': "Passing",
    'trace.field.passport_issued_on': "Passport issued on",
    'trace.field.password_allowed': "Password allowed",
    'trace.field.password_changed_at': "Password changed on",
    'trace.field.pattern': "Number pattern",
    'trace.field.pay_mode': "Pay mode",
    'trace.field.pay_replacements': "Pay replacements",
    'trace.field.pay_surveillance': "Pay invigilation",
    'trace.field.payer_type': "Payer type",
    'trace.field.payroll_number': "Payroll number",
    'trace.field.permanent': "Permanent",
    'trace.field.permission_id': "Permission",
    'trace.field.photo_path': "Photo",
    'trace.field.place': "Place",
    'trace.field.planned': "Planned",
    'trace.field.populations': "Populations",
    'trace.field.price_module': "Price per module",
    'trace.field.price_subject': "Price per subject",
    'trace.field.price_training': "Price per training",
    'trace.field.private_for_id': "Private for",
    'trace.field.product_id': "Training or element",
    'trace.field.profile_id': "Profile",
    'trace.field.prospect_id': "Prospect",
    'trace.field.prospect_idle_days': "Prospect idle time (days)",
    'trace.field.prospected_on': "Prospected on",
    'trace.field.quantity': "Quantity",
    'trace.field.rate': "Rate",
    'trace.field.rate_gross': "Gross rate",
    'trace.field.rate_net': "Net rate",
    'trace.field.read_at': "Read on",
    'trace.field.reads_enabled': "Access log enabled",
    'trace.field.receipt_pattern': "Receipt number pattern",
    'trace.field.recipient_id': "Recipient",
    'trace.field.recipients_count': "Number of recipients",
    'trace.field.recorded_at': "Recorded on",
    'trace.field.recorded_by_id': "Recorded by",
    'trace.field.recorded_by_name': "Recorded by",
    'trace.field.refund_pattern': "Refund number pattern",
    'trace.field.regulation_version_id': "Regulation version",
    'trace.field.reminder_of_id': "Reminder of",
    'trace.field.renewal': "Renewal",
    'trace.field.renewed_from_id': "Renewed from",
    'trace.field.reopened': "Reopened",
    'trace.field.replies': "Replies",
    'trace.field.replies_allowed': "Replies allowed",
    'trace.field.require_rolls': "Roll call required",
    'trace.field.required_permission': "Required permission",
    'trace.field.requires_reason': "Reason required",
    'trace.field.reserved': "Reserved",
    'trace.field.resit_threshold': "Resit threshold",
    'trace.field.restricted': "Restricted",
    'trace.field.resulting_account_id': "Resulting account",
    'trace.field.retained_session': "Retained session",
    'trace.field.retention_years': "Retention (years)",
    'trace.field.reverses_id': "Reverses",
    'trace.field.revoked_at': "Revoked on",
    'trace.field.revoked_by_id': "Revoked by",
    'trace.field.role_assignment_id': "Role assignment",
    'trace.field.roll_call': "Roll call",
    'trace.field.roll_taken_at': "Roll taken on",
    'trace.field.roll_taken_by_name': "Roll taken by",
    'trace.field.roll_validated_at': "Roll validated on",
    'trace.field.roll_validated_by_name': "Roll validated by",
    'trace.field.room_id': "Room",
    'trace.field.route': "Route",
    'trace.field.run_id': "Computation",
    'trace.field.scale_id': "Scale",
    'trace.field.schedule_id': "Payment schedule",
    'trace.field.scope': "Scope",
    'trace.field.scope_kind': "Scope type",
    'trace.field.sector_id': "Sector",
    'trace.field.send_email': "Send by email",
    'trace.field.sender_id': "Sender",
    'trace.field.sender_name': "Sender",
    'trace.field.sender_read_at': "Read by sender on",
    'trace.field.senders': "Senders",
    'trace.field.sent_at': "Sent on",
    'trace.field.series_id': "Session series",
    'trace.field.service_basis': "Service basis",
    'trace.field.service_id': "Service",
    'trace.field.session': "Session",
    'trace.field.session_idle_minutes': "Session idle time (minutes)",
    'trace.field.share': "Share",
    'trace.field.shared': "Shared",
    'trace.field.signed_on': "Signed on",
    'trace.field.size': "Size",
    'trace.field.source': "Source",
    'trace.field.source_id': "Source",
    'trace.field.special_discount': "Special discount",
    'trace.field.stackable': "Stackable",
    'trace.field.staff_chat': "Staff chat",
    'trace.field.stage_id': "Stage",
    'trace.field.stage_label': "Stage",
    'trace.field.starts_on': "Start",
    'trace.field.statement_pattern': "Statement number pattern",
    'trace.field.status': "Status",
    'trace.field.status_reason': "Status reason",
    'trace.field.students': "Students",
    'trace.field.subject': "Subject",
    'trace.field.submitted_at': "Submitted on",
    'trace.field.submitted_by_id': "Submitted by",
    'trace.field.submitted_by_name': "Submitted by",
    'trace.field.survey_auto': "Automatic survey",
    'trace.field.suspended_by_id': "Suspended by",
    'trace.field.suspends_lessons': "Suspends lessons",
    'trace.field.system': "System",
    'trace.field.tagline': "Tagline",
    'trace.field.target': "Target",
    'trace.field.target_id': "Target",
    'trace.field.targets': "Targets",
    'trace.field.targets_training': "Applies to training",
    'trace.field.taught': "Taught",
    'trace.field.tax': "Tax",
    'trace.field.tax_amount': "Tax amount",
    'trace.field.tax_id': "Tax",
    'trace.field.tax_rate': "Tax rate",
    'trace.field.teacher_chat': "Teacher chat",
    'trace.field.teacher_id': "Teacher",
    'trace.field.teacher_notify': "Teacher notifications",
    'trace.field.teachers_read': "Teachers can read",
    'trace.field.teaching_type_id': "Teaching type",
    'trace.field.template_id': "Template",
    'trace.field.tenant_id': "Tenant ID",
    'trace.field.termination_reason': "Termination reason",
    'trace.field.title': "Title",
    'trace.field.token_hash': "Token",
    'trace.field.tone': "Tone",
    'trace.field.totp_confirmed_at': "Authenticator app confirmed on",
    'trace.field.totp_last_step': "Last authenticator code",
    'trace.field.totp_secret': "Authenticator secret",
    'trace.field.trade_register': "Trade register",
    'trace.field.training_center_id': "Training centre",
    'trace.field.training_cycle_id': "Training cycle",
    'trace.field.training_domain_id': "Training domain",
    'trace.field.training_label': "Training",
    'trace.field.training_level_id': "Training level",
    'trace.field.transcript': "Transcript",
    'trace.field.treatment': "Treatment",
    'trace.field.two_factor': "Two-factor authentication",
    'trace.field.two_factor_email': "Two-factor email",
    'trace.field.type': "Type",
    'trace.field.unit_amount': "Unit price",
    'trace.field.unpaid_minutes': "Unpaid minutes",
    'trace.field.used_at': "Used on",
    'trace.field.user_account_id': "Account",
    'trace.field.valid_from_id': "Valid from",
    'trace.field.validation_threshold': "Pass threshold",
    'trace.field.venue_id': "Site",
    'trace.field.version_id': "Version",
    'trace.field.visible': "Visible",
    'trace.field.website': "Website",
    'trace.field.weekday': "Weekday",
    'trace.field.weighting': "Weighting",
    'trace.field.won': "Won",
    'trace.field.year_label': "Year",
    'trace.value.draft': "draft",
    'trace.value.signed': "signed",
    'trace.value.closed': "closed",
    'trace.value.ended': "ended",
    'trace.value.terminated': "terminated",
    'trace.value.hourly': "hourly",
    'trace.value.monthly': "monthly",
    'trace.value.weekly': "weekly",
    'trace.value.yearly': "yearly",
    'trace.value.none': "none",
    'trace.value.teaching': "teaching",
    'trace.value.surveillance': "invigilation",
    'trace.value.bonus': "bonus",
    'trace.value.deduction': "deduction",
    'trace.value.transfer': "transfer",
    'trace.value.cheque': "cheque",
    'trace.value.cash': "cash",
    'trace.value.other': "other",
    'trace.value.validated': "validated",
    'trace.value.paid': "paid",
    'trace.value.message': "message",
    'trace.value.notification': "notification",
    'trace.value.reply': "reply",
    'trace.value.direct': "direct",
    'trace.value.group': "group",
    'trace.value.sent': "sent",
    'trace.value.optional': "optional",
    'trace.value.required': "required",
    'trace.value.staff': "staff",
    'trace.value.staff_teachers': "staff and teachers",
    'trace.value.direction': "management",
    'trace.value.info': "information",
    'trace.value.warn': "warning",
    'trace.value.violet': "purple",
    'trace.value.danger': "danger",
    'trace.value.muted': "muted",
    'trace.value.ok': "success",
    'trace.value.school': "school",
    'trace.value.partner': "partner",
    'trace.value.call': "call",
    'trace.value.email': "email",
    'trace.value.meeting': "meeting",
    'trace.value.visit': "visit",
    'trace.value.event': "event",
    'trace.value.note': "note",
    'trace.value.survey': "survey",
    'trace.value.legacy': "legacy",
    'trace.value.employed': "employed",
    'trace.value.studying': "studying",
    'trace.value.seeking': "job seeking",
    'trace.value.unknown': "unknown",
    'trace.value.prospect': "prospect",
    'trace.value.dormant': "dormant",
    'trace.value.renewal': "renewal due",
    'trace.value.tacit': "tacit",
    'trace.value.explicit': "explicit",
    'trace.value.lesson': "lesson",
    'trace.value.assessment': "assessment",
    'trace.value.seminar': "seminar",
    'trace.value.conference': "conference",
    'trace.value.outing': "outing",
    'trace.value.ceremony': "ceremony",
    'trace.value.planned': "planned",
    'trace.value.done': "done",
    'trace.value.not_done': "not done",
    'trace.value.cancelled': "cancelled",
    'trace.value.holiday': "public holiday",
    'trace.value.vacation': "vacation",
    'trace.value.exams': "exams",
    'trace.value.no_class': "no classes",
    'trace.value.absent': "absent",
    'trace.value.late': "late",
    'trace.value.medical': "medical",
    'trace.value.family': "family",
    'trace.value.administrative': "administrative",
    'trace.value.transport': "transport",
    'plan.title': "Timetable",
    'plan.sub': "This week's sessions, assessments and events",
    'plan.subManage': "This week's sessions, assessments and events; click a free slot to schedule",
    'plan.mine.title': "My timetable",
    'plan.mine.sub': "Your sessions, invigilations and events",
    'plan.view': "View",
    'plan.view.class': "Class",
    'plan.view.teacher': "Teacher",
    'plan.view.room': "Room",
    'plan.view.student': "Student",
    'plan.mode': "Display",
    'plan.mode.week': "Week",
    'plan.mode.month': "Month",
    'plan.mode.list': "List",
    'plan.mode.series': "Weekly series",
    'plan.mode.lesson': "One session",
    'plan.weekOf': "Week of {from} to {to} {year}",
    'plan.prevWeek': "Previous week",
    'plan.nextWeek': "Next week",
    'plan.prevMonth': "Previous month",
    'plan.nextMonth': "Next month",
    'plan.prevDay': "Previous day",
    'plan.nextDay': "Next day",
    'plan.today': "Today",
    'plan.print': "Print",
    'plan.print.title': "Timetable",
    'plan.print.year': "Year",
    'plan.print.cycle': "Cycle",
    'plan.print.domain': "Field of study",
    'plan.print.training': "Programme",
    'plan.print.class': "Class",
    'plan.print.venue': "Venue",
    'plan.chooseTarget': "Choose a class, a teacher, a room or a student.",
    'plan.empty': "No slot in this period.",
    'plan.allDay': "No time",
    'plan.unplaced': "to schedule",
    'plan.more': "+{count} more",
    'plan.legend.exam': "Assessment",
    'plan.legend.event': "Special event",
    'plan.legend.cancelled': "Cancelled",
    'plan.col.when': "When",
    'plan.col.what': "What",
    'plan.col.who': "Teacher",
    'plan.col.where': "Room",
    'plan.col.status': "Status",
    'plan.status.planned': "Planned",
    'plan.status.done': "Done",
    'plan.status.not_done': "Not held",
    'plan.status.cancelled': "Cancelled",
    'plan.nature.seminar': "Seminar",
    'plan.nature.conference': "Conference",
    'plan.nature.outing': "Outing",
    'plan.nature.meeting': "Meeting",
    'plan.nature.ceremony': "Ceremony",
    'plan.nature.other': "Other",
    'plan.period.holiday': "Public holiday",
    'plan.period.vacation': "Holidays",
    'plan.period.exams': "Exam period",
    'plan.period.no_class': "No-class period",
    'plan.period.other': "Other",
    'plan.period.blockedShort': "scheduling blocked",
    'plan.conflict.title': "Conflicts: {count}",
    'plan.conflict.room': "room {subject} is taken ({what})",
    'plan.conflict.teacher': "{subject} already teaches {what}",
    'plan.conflict.class': "class {subject} already has {what}",
    'plan.conflict.blocked': "blocked period: {subject}",
    'plan.conflict.more': "and {count} more",
    'plan.conflict.skip': "Create without the conflicting days ({count})",
    'plan.conflict.override': "Schedule despite the conflicts, with a reason",
    'plan.conflict.noOverride': "Overriding a conflict requires a dedicated right.",
    'plan.conflict.reason': "Reason for the override",
    'plan.field.training': "Programme",
    'plan.field.chooseTraining': "Choose a programme",
    'plan.field.chooseStudent': "Choose a student",
    'plan.field.subject': "Subject",
    'plan.field.chooseSubject': "Choose a subject",
    'plan.field.type': "Teaching type",
    'plan.field.noType': "No type",
    'plan.field.room': "Room",
    'plan.field.rooms': "Rooms",
    'plan.field.noRoom': "No room",
    'plan.field.classes': "For",
    'plan.field.noClass': "No class in this programme.",
    'plan.field.teachers': "Teachers",
    'plan.field.addTeacher': "Add a teacher",
    'plan.field.addOrganiser': "Add an organiser",
    'plan.field.addSupervisor': "Add an invigilator",
    'plan.field.every': "Every",
    'plan.field.start': "From",
    'plan.field.end': "To",
    'plan.field.rhythm': "Frequency",
    'plan.field.rhythmEvery': "Every {count} week(s)",
    'plan.field.firstOn': "From",
    'plan.field.lastOn': "To",
    'plan.field.date': "On",
    'plan.field.nature': "Type",
    'plan.field.label': "Title",
    'plan.field.participants': "Participants",
    'plan.field.guests': "External speakers",
    'plan.field.guestsHint': "Separated by commas",
    'plan.field.for': "For",
    'plan.field.plannedTeacher': "Planned teacher",
    'plan.field.supervisors': "Invigilators",
    'plan.field.organisers': "Organisers",
    'plan.field.roll': "Roll call",
    'plan.field.duration': "Duration",
    'plan.field.status': "Status",
    'plan.field.givenBy': "Given by",
    'plan.field.givenByHint': "A substitute: the hours count for them, not for the planned teacher.",
    'plan.field.substitute': "substitute",
    'plan.field.reason': "Reason",
    'plan.series.new': "New series",
    'plan.series.sub': "One session a week over the period; blocked holidays are skipped",
    'plan.series.create': "Create the {count} sessions",
    'plan.series.saved': "Sessions created: {count}.",
    'plan.series.editFollowing': "This session and the following ones",
    'plan.series.followingNote': "Sessions still planned from {day} are recreated; done, cancelled or called ones stay.",
    'plan.series.sessions': "Sessions created",
    'plan.series.range': "from {first} to {last}",
    'plan.series.skipped': "Days skipped",
    'plan.series.hours': "Hours",
    'plan.series.volume': "{planned} planned out of {volume} in the curriculum",
    'plan.series.noVolume': "no planned volume for this type",
    'plan.lesson.new': "New session",
    'plan.lesson.edit': "Edit the session",
    'plan.lesson.saved': "Session saved.",
    'plan.special.new': "New event",
    'plan.special.edit': "Edit the event",
    'plan.special.sub': "Seminar, conference, outing, meeting: a slot that is neither a class nor an assessment",
    'plan.special.create': "Schedule the event",
    'plan.special.suspend': "Suspend the participants' classes during this slot",
    'plan.special.suspendHint': "Their sessions are cancelled for the event, without counting as absences.",
    'plan.special.rollCall': "Take the roll",
    'plan.special.rollCallHint': "Attendance is mandatory: absences add to the attendance record.",
    'plan.special.saved': "Event saved.",
    'plan.special.savedSuspended': "Event saved; sessions suspended: {count}.",
    'plan.event.movedFrom': "Moved from {day}",
    'plan.event.series': "Series: every {weekday} from {first} to {last} · session {index} of {total}",
    'plan.event.unplacedNote': "This assessment has no room or invigilator yet.",
    'plan.event.place': "Schedule the assessment",
    'plan.event.placed': "Assessment scheduled.",
    'plan.event.unplace': "Remove from the timetable",
    'plan.event.openAssessment': "Open the assessment",
    'plan.event.rollNotTaken': "Not taken yet",
    'plan.event.rollCounts': "{present} present · {absent} absent · {late} late",
    'plan.event.capacity': "{expected} expected, above the room capacity ({capacity}).",
    'plan.event.override': "Scheduled despite a conflict: {reason} ({by})",
    'plan.event.suspended': "Suspended by the event \"{by}\".",
    'plan.event.saveStatus': "Save the status",
    'plan.event.statusSaved': "Status saved.",
    'plan.event.change': "Change",
    'plan.event.editOne': "This session",
    'plan.event.editFollowing': "This and the following",
    'plan.event.move': "Reschedule",
    'plan.event.moved': "Session rescheduled.",
    'plan.event.delete': "Delete",
    'plan.event.deleteText': "The slot disappears from the timetable. A session with a roll call should be cancelled instead.",
    'plan.event.deleted': "Slot deleted.",
    'plan.event.takeRoll': "Take the roll",
    'plan.calendar.title': "Institution calendar",
    'plan.calendar.sub': "Holidays, public holidays, special periods; a blocked period refuses any session",
    'plan.calendar.new': "New period",
    'plan.calendar.edit': "Edit the period",
    'plan.calendar.delete': "Delete the period",
    'plan.calendar.empty': "No period for this year.",
    'plan.calendar.period': "Period",
    'plan.calendar.kind': "Type",
    'plan.calendar.scope': "For",
    'plan.calendar.dates': "Dates",
    'plan.calendar.planning': "Scheduling",
    'plan.calendar.blocked': "blocked",
    'plan.calendar.open': "open",
    'plan.calendar.institution': "Institution",
    'plan.calendar.cycles': "Cycles",
    'plan.calendar.trainings': "Programmes",
    'plan.calendar.from': "From",
    'plan.calendar.to': "To",
    'plan.calendar.toHint': "Empty for a single day",
    'plan.calendar.blocking': "Block scheduling",
    'plan.calendar.blockingHint': "No session can be scheduled; a series skips these days by itself.",
    'plan.calendar.note': "A series crossing a blocked period skips those days. Religious holidays are confirmed every year: their date stays editable.",
    'plan.rooms.title': "Rooms and venues",
    'plan.rooms.sub': "Where sessions take place: capacity checks the headcount, occupancy covers the current week",
    'plan.rooms.new': "New room",
    'plan.rooms.edit': "Edit the room",
    'plan.rooms.delete': "Delete the room",
    'plan.rooms.empty': "No room.",
    'plan.rooms.tab': "Rooms · {count}",
    'plan.rooms.room': "Room",
    'plan.rooms.venue': "Venue",
    'plan.rooms.kind': "Type",
    'plan.rooms.kindHint': "Classroom, lecture hall, computer room…",
    'plan.rooms.capacity': "Seats",
    'plan.rooms.accepts': "Use",
    'plan.rooms.shared': "several classes",
    'plan.rooms.single': "one class",
    'plan.rooms.occupancy': "Occupancy",
    'plan.rooms.timetable': "Timetable",
    'plan.rooms.inactive': "inactive",
    'plan.rooms.sharedLabel': "Accepts several classes at once",
    'plan.rooms.sharedHint': "A study room, a library: the conflict check ignores it.",
    'plan.rooms.activeLabel': "Active room",
    'plan.rooms.note': "A session whose headcount exceeds capacity is flagged; \"several classes\" allows two sessions at once in the room.",
    'plan.venues.tab': "Venues · {count}",
    'plan.venues.new': "New venue",
    'plan.venues.edit': "Edit the venue",
    'plan.venues.delete': "Delete the venue",
    'plan.venues.empty': "No venue.",
    'plan.venues.address': "Address",
    'plan.venues.rooms': "Rooms",
    'plan.hours.title': "Planned and delivered hours",
    'plan.hours.sub': "Volume comes from the curriculum, delivered hours from sessions marked done",
    'plan.hours.subOf': "{training} · {year} · as of {day}",
    'plan.hours.by.subject': "By subject",
    'plan.hours.by.teacher': "By teacher",
    'plan.hours.empty': "No hours to show.",
    'plan.hours.volume': "Volume",
    'plan.hours.planned': "Planned",
    'plan.hours.done': "Delivered",
    'plan.hours.progress': "Progress",
    'plan.hours.teacher': "Teacher",
    'plan.hours.subjects': "Subjects",
    'plan.hours.substitute': "Of which substitutions",
    'plan.hours.noTeacher': "No teacher",
    'plan.hours.missing': "{hours} left to schedule",
    'plan.hours.extra': "{hours} above the volume",
    'plan.hours.note': "A session given by a substitute counts for them. Hours by teacher prepare hourly pay; payroll is outside this release.",
    'att.student': "Student",
    'att.element': "Element",
    'att.value': "Value",
    'att.threshold': "Threshold",
    'att.validated': "validated",
    'att.toValidate': "to validate",
    'att.notTaken': "not taken",
    'att.justified': "justified",
    'att.justifiedAs': "justified · {kind}",
    'att.lateBy': "{count} min late",
    'att.sessions': "{count} session(s)",
    'att.waived': "lifted by the jury",
    'att.mark.present': "Present",
    'att.mark.absent': "Absent",
    'att.mark.late': "Late",
    'att.mark.present.short': "P",
    'att.mark.absent.short': "A",
    'att.mark.late.short': "L",
    'att.level.alert': "alert",
    'att.level.consequence': "consequence",
    'att.effect.alert': "alert threshold reached",
    'att.effect.retake': "excluded from the normal session: goes to the resit",
    'att.effect.jury': "referred to the jury",
    'att.kind.medical': "Medical certificate",
    'att.kind.family': "Family reason",
    'att.kind.administrative': "Administrative reason",
    'att.kind.transport': "Transport",
    'att.kind.other': "Other",
    'att.roll.title': "Roll call",
    'att.roll.sub': "Your sessions of the day, and the roll call of each",
    'att.roll.today': "My sessions",
    'att.roll.unlinked': "No teacher record is linked to your account.",
    'att.roll.none': "No session this day.",
    'att.roll.noRoll': "no roll call",
    'att.roll.taken': "roll taken",
    'att.roll.todo': "take the roll",
    'att.roll.of': "Roll call",
    'att.roll.expected': "{count} expected",
    'att.roll.allPresent': "All present",
    'att.roll.counts': "{present} present · {absent} absent · {late} late",
    'att.roll.noStudent': "No student expected at this session.",
    'att.roll.lateMinutes': "Minutes late",
    'att.roll.minutes': "min",
    'att.roll.save': "Save the roll and mark the session done",
    'att.roll.saveValidate': "Save and validate",
    'att.roll.reopen': "Reopen the roll",
    'att.roll.saved': "Roll call saved.",
    'att.roll.savedValidated': "Roll call saved and validated.",
    'att.roll.validatedBy': "Roll call validated by {by}: it can no longer change.",
    'att.roll.editableUntil': "Editable until the registry validates it",
    'att.roll.toTimetable': "Timetable",
    'att.absences.title': "Absences",
    'att.absences.sub': "The registry completes, justifies and validates the teachers' roll calls, class by class and week by week",
    'att.absences.weekOf': "Week of {day}",
    'att.absences.onlyAbsent': "Absent only",
    'att.absences.all': "Whole class",
    'att.absences.validate': "Validate the roll calls ({count})",
    'att.absences.validated': "Roll calls validated: {count}.",
    'att.absences.noClass': "No class within your scope.",
    'att.absences.noSession': "No session with a roll call this week.",
    'att.absences.week': "Week",
    'att.absences.year': "Year",
    'att.absences.count': "{count} abs.",
    'att.absences.roll': "Roll call",
    'att.absences.notTaken': "roll not taken",
    'att.absences.clickHint': "A click changes the state: absent, late, present.",
    'att.justify.title': "Justify an absence",
    'att.justify.edit': "Edit the justification",
    'att.justify.sub': "One document justifies every session in the period; assessments stay handled by their mark",
    'att.justify.from': "From",
    'att.justify.to': "To",
    'att.justify.kind': "Reason",
    'att.justify.reason': "Details",
    'att.justify.document': "Document",
    'att.justify.documentHint': "PDF, PNG or JPEG, 5 MB at most",
    'att.justify.submit': "Justify",
    'att.justify.saved': "Sessions justified: {count}.",
    'att.justify.remove': "Remove the justification",
    'att.justify.removeText': "The absences it covered become unjustified again.",
    'att.justify.removed': "Justification removed.",
    'att.dashboard.title': "Attendance",
    'att.dashboard.sub': "This week: roll calls to take and validate, students beyond the thresholds",
    'att.dashboard.rate': "Average attendance",
    'att.dashboard.rateNote': "all programmes, this week",
    'att.dashboard.missing': "Roll calls not taken",
    'att.dashboard.missingNote': "past sessions without a roll call",
    'att.dashboard.toValidate': "Roll calls to validate",
    'att.dashboard.toValidateNote': "taken by the teachers",
    'att.dashboard.over': "Students beyond thresholds",
    'att.dashboard.overNote': "of which {count} at the consequence threshold",
    'att.dashboard.overTitle': "Students beyond the thresholds",
    'att.dashboard.overEmpty': "No student beyond the regulation thresholds.",
    'att.dashboard.missingTitle': "Past sessions without a roll call",
    'att.dashboard.missingEmpty': "All roll calls are taken.",
    'att.dashboard.complete': "Complete",
    'att.student.title': "Attendance",
    'att.student.empty': "No enrolment this year.",
    'att.student.rate': "Attendance",
    'att.student.rateNote': "over {hours} of classes held",
    'att.student.unjustified': "Unjustified absences",
    'att.student.justified': "Justified",
    'att.student.lates': "Late arrivals",
    'att.student.lateMinutes': "{count} min in total",
    'att.student.bySubject': "By subject",
    'att.student.noRule': "No per-subject threshold in the regulations",
    'att.student.rule': "Regulation thresholds: alert {alert} %, consequence {consequence} %",
    'att.student.realized': "Held",
    'att.student.unjustifiedShort': "Unjustified",
    'att.student.rateCol': "Rate",
    'att.student.records': "Absences and late arrivals",
    'att.student.noRecord': "No absence recorded.",
    'att.student.assessment': "assessment · {absence}",
    'att.student.justifications': "Justifications",
    'att.student.noJustification': "No justification.",
    'att.student.waivers': "Consequences lifted by the jury",
    'att.student.note': "Only sessions held count. Absences from assessments stay carried by the mark.",
    'att.waive.action': "Lift the consequence",
    'att.waive.title': "Lift the attendance consequence",
    'att.waive.reason': "Jury's reason",
    'att.waive.saved': "Consequence lifted.",
    'att.waive.remove': "Restore the consequence",
    'att.waive.removed': "Consequence restored.",
    'reg.block.attendance': "Attendance",
    'reg.ed.attendance.hint': "Each rule combines a scope, what counts and its base; all are evaluated.",
    'reg.ed.attendance.none': "No rule: absences neither alert nor have any effect.",
    'reg.ed.attendance.rule': "Rule {number}",
    'reg.ed.attendance.scope': "Scope",
    'reg.ed.attendance.measure': "What counts",
    'reg.ed.attendance.measure.unjustified_hours': "Unjustified hours",
    'reg.ed.attendance.measure.absence_hours': "All absence hours",
    'reg.ed.attendance.measure.sessions': "Unjustified sessions",
    'reg.ed.attendance.base': "Relative to",
    'reg.ed.attendance.base.realized': "Hours held",
    'reg.ed.attendance.base.planned': "Planned volume",
    'reg.ed.attendance.base.fixed': "Fixed threshold",
    'reg.ed.attendance.effect': "Effect",
    'reg.ed.attendance.effect.retake': "Goes to the resit",
    'reg.ed.attendance.effect.jury': "Referred to the jury",
    'reg.ed.attendance.alert': "Alert from",
    'reg.ed.attendance.consequence': "Consequence from",
    'reg.ed.attendance.unit.sessions': "sessions",
    'reg.ed.attendance.add': "Add a rule",
    'reg.ed.attendance.lates': "Late arrivals worth one absence",
    'reg.ed.attendance.lateHours': "Duration of that absence",
    'reg.ed.attendance.waive': "The jury may lift the consequence, with a reason",
    'reg.ed.attendance.worst': "Several rules reached: the most severe consequence applies. Sessions suspended by an event do not count.",
    'reg.sum.attendance.none': "No attendance rule",
    'reg.sum.attendance.rule': "{scope}: alert {alert}, {effect} {consequence}",
    'scol.tab.attendance': "Attendance",
    'violation.plan.room_name_taken': "A room of this venue already has this name.",
    'violation.plan.room_in_use': "The room hosts slots: deactivate it instead.",
    'violation.plan.dates_order': "The end is before the start.",
    'violation.plan.times_order': "The end must follow the start.",
    'violation.plan.invalid_time': "Time expected (HH:MM).",
    'violation.plan.outside_year': "Outside the academic year.",
    'violation.plan.year_closed': "The year is closed.",
    'violation.plan.no_class': "Choose at least one class.",
    'violation.plan.subject_expected': "Choose a subject of the programme.",
    'violation.plan.other_training': "Outside the session's programme.",
    'violation.plan.series_subject': "A series' subject does not change: create another series.",
    'violation.plan.series_empty': "No session to create in this period.",
    'violation.plan.event_has_roll': "This slot's roll is taken: cancel it instead.",
    'violation.att.not_expected': "This student is not expected at the session.",
    'violation.att.already_waived': "The consequence is already lifted on this element.",
    'violation.reg.attendance_threshold': "Give an alert or consequence threshold.",
    'violation.reg.attendance_order': "The consequence must come after the alert.",
    'plan.field.rhythmWeekly': "Every week",
    'stg.add': "Add",
    'stg.edit': "Edit",
    'stg.remove': "Delete",
    'stg.deactivate': "Deactivate",
    'stg.inactive': "inactive",
    'stg.change': "Change",
    'stg.choose': "Choose…",
    'stg.none': "None",
    'stg.yes': "Yes",
    'stg.no': "No",
    'stg.see': "View",
    'stg.search': "Search",
    'stg.searchPlaceholder': "Type at least two letters",
    'stg.export': "Export",
    'stg.breadcrumb': "Breadcrumb",
    'stg.year': "Year",
    'stg.class': "Class",
    'stg.student': "Student",
    'stg.supervisor': "Academic supervisor",
    'stg.tutor': "Workplace tutor",
    'stg.topic': "Topic",
    'stg.missions': "Planned tasks",
    'stg.place': "Location",
    'stg.startsOn': "Start",
    'stg.endsOn': "End",
    'stg.lastName': "Last name",
    'stg.firstNames': "First names",
    'stg.email': "Email",
    'stg.phone': "Phone",
    'stg.reason': "Reason",
    'stg.tab.count': "{label} ({count})",
    'stg.step.none': "No internship",
    'stg.step.proposed': "Proposed",
    'stg.step.validated': "Approved",
    'stg.step.signed': "Agreement signed",
    'stg.step.ongoing': "Ongoing",
    'stg.step.report_due': "Report due",
    'stg.step.report_late': "Report overdue",
    'stg.step.report_received': "Report received",
    'stg.step.defended': "Defended",
    'stg.step.evaluated': "Evaluated",
    'stg.step.refused': "Refused",
    'stg.step.cancelled': "Cancelled",
    'stg.timeline.proposed': "Proposed",
    'stg.timeline.validated': "Approved",
    'stg.timeline.signed': "Agreement signed",
    'stg.timeline.ongoing': "Ongoing",
    'stg.timeline.report': "Report received",
    'stg.timeline.defended': "Defended",
    'stg.timeline.evaluated': "Evaluated",
    'stg.nature.internship': "Internship",
    'stg.nature.apprenticeship': "Work-study",
    'stg.nature.job': "Job",
    'stg.offerState.draft': "draft",
    'stg.offerState.published': "published",
    'stg.offerState.closed': "closed",
    'stg.applicationState.new': "new",
    'stg.applicationState.forwarded': "forwarded",
    'stg.applicationState.retained': "selected",
    'stg.applicationState.declined': "declined",
    'stg.evaluator.tutor': "Tutor (evaluation form)",
    'stg.evaluator.supervisor': "Academic supervisor",
    'stg.evaluator.jury': "Defence jury",
    'stg.jury.president': "Chair",
    'stg.jury.supervisor': "Supervisor",
    'stg.jury.member': "Member",
    'stg.jury.tutor': "Invited tutor",
    'stg.due.before_start': "before start ({date})",
    'stg.due.at_end': "at the end ({date})",
    'stg.due.after_end': "by {date}",
    'stg.type.due.before_start': "Before start",
    'stg.type.due.at_end': "At the end",
    'stg.type.due.after_end': "After the end",
    'stg.filter.all': "All",
    'stg.filter.without': "No internship",
    'stg.filter.to_validate': "To approve",
    'stg.filter.to_sign': "No agreement",
    'stg.filter.ongoing': "Ongoing",
    'stg.filter.reports': "Reports due",
    'stg.filter.to_evaluate': "To evaluate",
    'stg.filter.evaluated': "Evaluated",
    'stg.filter.allClasses': "All classes",
    'stg.filter.allSupervisors': "All supervisors",
    'stg.filter.noSupervisor': "No supervisor",
    'stg.dashboard.title': "Internships",
    'stg.dashboard.sub': "This year's campaigns, the interns and what is left to do",
    'stg.dashboard.subYear': "This year's campaigns, the interns and what is left to do · year {year}",
    'stg.dashboard.campaigns': "Campaigns",
    'stg.dashboard.campaignsSub': "An internship type, programmes, a period",
    'stg.dashboard.campaignLine': "{type} · {trainings} · {count} student(s)",
    'stg.dashboard.noCampaign': "No campaign this year.",
    'stg.dashboard.noCampaignManage': "No campaign this year: set up the first one to follow the students expected on internship.",
    'stg.dashboard.outside': "{count} internship(s) outside any campaign this year",
    'stg.dashboard.hosts': "Host organisations",
    'stg.dashboard.hostsSub': "This year's interns",
    'stg.dashboard.noHosts': "No intern placed this year.",
    'stg.dashboard.todo': "To do",
    'stg.dashboard.nothingToDo': "Nothing to do for now.",
    'stg.todo.proposed': "{count} internship(s) proposed by students",
    'stg.todo.proposedSub': "to approve or refuse",
    'stg.todo.toSign': "{count} agreement(s) to get signed",
    'stg.todo.toSignSub': "Kelassi prepares them as PDF, ready to print",
    'stg.todo.noSupervisor': "{count} intern(s) without an academic supervisor",
    'stg.todo.noSupervisorSub': "assign a teacher",
    'stg.todo.defenses': "{count} defence(s) to schedule",
    'stg.todo.defensesSub': "days, slots and juries",
    'stg.todo.late': "{count} report(s) overdue",
    'stg.todo.lateSub': "a reminder goes to the student every morning",
    'stg.kpi.expected': "Students expected on internship",
    'stg.kpi.expectedNote': "{count} campaign(s) this year",
    'stg.kpi.without': "No internship",
    'stg.kpi.withoutNote': "including {count} less than 30 days before start",
    'stg.kpi.toSign': "Agreements to sign",
    'stg.kpi.toSignNote': "approved internships without a signed agreement",
    'stg.kpi.reports': "Reports to receive",
    'stg.kpi.reportsNote': "deadline passed for {count}",
    'stg.column.campaign': "Campaign",
    'stg.column.period': "Period",
    'stg.column.progress': "Progress",
    'stg.column.without': "No internship",
    'stg.column.intern': "Intern",
    'stg.column.internTopic': "Intern and topic",
    'stg.column.organisationTopic': "Organisation and topic",
    'stg.column.documents': "Documents",
    'stg.column.step': "Step",
    'stg.column.offer': "Offer and organisation",
    'stg.column.audience': "Audience",
    'stg.column.closes': "Closes",
    'stg.column.applications': "Applications",
    'stg.column.state': "State",
    'stg.column.candidate': "Candidate",
    'stg.column.received': "Received",
    'stg.column.next': "Outcome",
    'stg.campaign.new': "New campaign",
    'stg.campaign.edit': "Edit campaign",
    'stg.campaign.sub': "An internship type, programmes and a period; students enrolled in these programmes are expected",
    'stg.campaign.one': "Campaign",
    'stg.campaign.none': "No campaign",
    'stg.campaign.type': "Internship type",
    'stg.campaign.typeHint': "minimum {months} months",
    'stg.campaign.label': "Label",
    'stg.campaign.reportDueOn': "Report due by",
    'stg.campaign.reportDueHint': "empty: end of internship plus the type's delay",
    'stg.campaign.assessmentType': "Assessment type for the mark",
    'stg.campaign.assessmentHint': "for the assessment created in the programme element",
    'stg.campaign.trainings': "Programmes and the element that receives the mark",
    'stg.campaign.noTraining': "No programme for this year.",
    'stg.campaign.subject': "Programme element",
    'stg.campaign.noSubject': "No mark carried over",
    'stg.campaign.subjectHint': "★: element flagged as internship in the curriculum. The validated mark is carried over to it.",
    'stg.list.title': "Internships",
    'stg.list.sub': "{type} · {trainings} · {expected} student(s)",
    'stg.list.outside': "Outside campaigns",
    'stg.list.outsideSub': "This year's internships imported from the former Kelassi or recorded without a campaign",
    'stg.list.searchPlaceholder': "Student, organisation, topic",
    'stg.list.empty': "No intern for this filter.",
    'stg.list.noCampaign': "No campaign this year: set one up from the dashboard.",
    'stg.list.byStudent': "by the student",
    'stg.list.withoutNote': "These students have no internship yet: write to them from messaging, or publish an offer aimed at them.",
    'stg.internship.one': "Internship",
    'stg.internship.new': "New internship",
    'stg.internship.edit': "Edit internship",
    'stg.internship.sub': "A student, an organisation, a period, a topic",
    'stg.internship.saveProposed': "Save as proposed",
    'stg.internship.saveValidate': "Save and approve",
    'stg.internship.duration': "duration {months} months",
    'stg.internship.allowShort': "Accept this duration below the type's minimum",
    'stg.internship.note': "The organisation comes from the External relations directory; a missing organisation is created here, with its tutor. Students can also propose their internship from their space.",
    'stg.organisation.one': "Organisation",
    'stg.organisation.host': "Host organisation",
    'stg.organisation.new': "New organisation",
    'stg.organisation.newHint': "it joins the External relations directory",
    'stg.organisation.notFound': "Organisation not in the directory?",
    'stg.organisation.pick': "Search the directory",
    'stg.tutor.later': "To be specified",
    'stg.tutor.new': "New contact…",
    'stg.tutor.none': "Tutor to be specified",
    'stg.supervisor.later': "To be assigned",
    'stg.supervisor.none': "None",
    'stg.supervisor.toAssign': "Supervisor to be assigned",
    'stg.supervisor.change': "Academic supervisor",
    'stg.supervisor.hint': "they are notified and see the internship in « My interns »",
    'stg.file.title': "{name} · {type}",
    'stg.file.months': "{months} months",
    'stg.file.minimum': "minimum {months}",
    'stg.file.steps': "Internship steps",
    'stg.file.before': "by {date}",
    'stg.file.refused': "Refused: {reason}",
    'stg.file.cancelled': "Cancelled: {reason}",
    'stg.file.documents': "File documents",
    'stg.file.documentsSub': "Required by the internship type",
    'stg.file.noDocuments': "No document required.",
    'stg.file.evaluation': "Evaluation",
    'stg.file.markLater': "The mark appears once the evaluation is validated.",
    'stg.file.evaluatedOn': "Evaluation validated on {date} · {who}",
    'stg.file.reportedTo': "Mark carried over to « {subject} »",
    'stg.file.defense': "Defence",
    'stg.file.noDefense': "Not scheduled yet.",
    'stg.file.planDefense': "Schedule",
    'stg.file.defenseOn': "Defence on {date} at {time}",
    'stg.file.supervision': "Supervision",
    'stg.file.since': "since {date}",
    'stg.file.fromOffer': "from the offer « {title} »",
    'stg.file.followUps': "Follow-ups",
    'stg.file.noFollowUp': "No follow-up yet.",
    'stg.action.validate': "Approve",
    'stg.action.refuse': "Refuse",
    'stg.action.convention': "Agreement (PDF)",
    'stg.action.sign': "Mark the agreement signed",
    'stg.action.evaluate': "Evaluate",
    'stg.action.reopenEvaluation': "Reopen the evaluation",
    'stg.action.cancel': "Cancel the internship",
    'stg.action.reopen': "Reopen",
    'stg.action.delete': "Delete the internship",
    'stg.delete.text': "{name}'s internship and its documents will be deleted.",
    'stg.reason.refuseSub': "The reason is sent to the student.",
    'stg.reason.cancelSub': "The internship stays on file, cancelled.",
    'stg.sign.sub': "The agreement signed by the three parties.",
    'stg.sign.on': "Signed on",
    'stg.followUp.new': "Log a follow-up",
    'stg.followUp.one': "Follow-up",
    'stg.followUp.type': "Follow-up type",
    'stg.followUp.heldOn': "Date",
    'stg.followUp.present': "Intern present",
    'stg.followUp.presentUnknown': "Not specified",
    'stg.followUp.observations': "Notes",
    'stg.followUp.wasPresent': "intern present",
    'stg.followUp.wasAbsent': "intern absent",
    'stg.followUp.remove': "Delete follow-up",
    'stg.document.one': "Document",
    'stg.document.expected': "Expected",
    'stg.document.received': "Received",
    'stg.document.awaited': "awaited",
    'stg.document.see': "View",
    'stg.document.receive': "Receive",
    'stg.document.replace': "Replace",
    'stg.document.deposit': "Upload",
    'stg.document.remove': "Remove the document",
    'stg.document.add': "Other document",
    'stg.document.free': "Other document",
    'stg.document.label': "Document label",
    'stg.document.receivedOn': "Received on",
    'stg.document.file': "PDF file",
    'stg.document.fileHint': "PDF, 10 MB at most",
    'stg.document.markReceived': "Mark received",
    'stg.document.upload': "Upload",
    'stg.document.uploadSub': "Your document as PDF; it is marked received today.",
    'stg.document.receiveSub': "Received on paper: a date is enough. Or upload the PDF.",
    'stg.document.pickFile': "Choose the PDF file.",
    'stg.evaluation.title': "Evaluation · {name}",
    'stg.evaluation.open': "Mark",
    'stg.evaluation.component': "Component",
    'stg.evaluation.by': "Assessed by",
    'stg.evaluation.weight': "Weight",
    'stg.evaluation.mark': "Mark / 20",
    'stg.evaluation.total': "Internship mark",
    'stg.evaluation.comment': "Jury's comment",
    'stg.evaluation.report': "Carry the mark over to « {subject} » ({training})",
    'stg.evaluation.noTarget': "The campaign does not say which programme element receives the mark: it stays with the internship.",
    'stg.evaluation.note': "The carried-over mark then follows the usual marks workflow (locking, deliberation). The grid and weights are set per internship type.",
    'stg.evaluation.supervisorNote': "You mark the supervisor's components; the school validates the evaluation.",
    'stg.evaluation.noGrid': "No grid for this internship type.",
    'stg.evaluation.validate': "Validate the evaluation",
    'stg.evaluation.saved': "Marks saved.",
    'stg.evaluation.validated': "Evaluation validated.",
    'stg.evaluation.reported': "Evaluation validated, mark carried over to « {subject} ».",
    'stg.flash.saved': "Saved.",
    'stg.flash.validated': "Internship approved: the student is notified.",
    'stg.flash.refused': "Internship refused: the reason is sent to the student.",
    'stg.flash.cancelled': "Internship cancelled.",
    'stg.flash.signed': "Agreement signed.",
    'stg.flash.reopened': "Reopened.",
    'stg.flash.followUp': "Follow-up logged.",
    'stg.flash.document': "Document saved.",
    'stg.flash.proposed': "Internship proposed: the school will review it.",
    'stg.flash.applied': "Application sent.",
    'stg.flash.withdrawn': "Application withdrawn.",
    'stg.flash.published': "Offer published: the targeted students are notified.",
    'stg.flash.closed': "Offer closed.",
    'stg.flash.placed': "Slot saved.",
    'stg.flash.autoplaced': "{count} defence(s) placed.",
    'stg.offers.title': "Offers",
    'stg.offers.sub': "Internship, work-study and job offers published to students and graduates",
    'stg.offers.filter.all': "All",
    'stg.offers.filter.published': "Published",
    'stg.offers.filter.draft': "Drafts",
    'stg.offers.filter.closed': "Closed",
    'stg.offers.empty': "No offer.",
    'stg.offers.allTrainings': "All programmes",
    'stg.offers.alumni': "graduates",
    'stg.offers.fresh': "{count} new",
    'stg.offers.note': "A published offer appears in the targeted students' space (and graduates' for a job), with a notification. « Selected » offers to create the internship, prefilled.",
    'stg.offer.new': "New offer",
    'stg.offer.edit': "Edit offer",
    'stg.offer.sub': "An organisation from the directory, an audience, a deadline",
    'stg.offer.title': "Title",
    'stg.offer.contact': "Contact",
    'stg.offer.nature': "Type",
    'stg.offer.startsOn': "Desired start",
    'stg.offer.duration': "Duration",
    'stg.offer.durationPlaceholder': "4 to 6 months",
    'stg.offer.closesOn': "Applications until",
    'stg.offer.closesLine': "closes on {date}",
    'stg.offer.description': "Description",
    'stg.offer.audience': "Audience",
    'stg.offer.alumni': "Also open to graduates",
    'stg.offer.audienceHint': "With no programme ticked, the offer targets every student enrolled this year.",
    'stg.offer.publish': "Publish",
    'stg.offer.close': "Close",
    'stg.offer.applications': "Applications",
    'stg.offer.applicationsSub': "{count} application(s) · the CV is attached by the student",
    'stg.offer.noApplication': "No application yet.",
    'stg.offer.cv': "CV",
    'stg.offer.internship': "The internship",
    'stg.offer.createInternship': "Create the internship",
    'stg.offer.details': "The offer",
    'stg.offer.publishedAt': "Published on",
    'stg.offer.delete': "Delete offer",
    'stg.offer.deleteText': "The offer « {title} » will be deleted.",
    'stg.apply.title': "Apply · {title}",
    'stg.apply.letter': "Your letter",
    'stg.apply.cv': "Your CV",
    'stg.apply.send': "Send the application",
    'stg.apply.button': "Apply",
    'stg.defense.title': "Defences",
    'stg.defense.sub': "Days, slots and juries",
    'stg.defense.subCampaign': "{campaign} · {days} day(s), {planned} defence(s)",
    'stg.defense.tab': "{date} ({count})",
    'stg.defense.unplannedTab': "Not scheduled ({count})",
    'stg.defense.unplanned': "Internships awaiting their defence",
    'stg.defense.noDay': "No day yet: create one.",
    'stg.defense.allPlanned': "Every internship has its slot.",
    'stg.defense.noCampaign': "No campaign this year.",
    'stg.defense.placeOne': "Place",
    'stg.defense.placeHere': "Place here",
    'stg.defense.dayTitle': "{date} · {place}",
    'stg.defense.newDay': "New day",
    'stg.defense.editDay': "Edit day",
    'stg.defense.deleteDay': "Delete day",
    'stg.defense.deleteDayText': "The day and all its defences will be deleted; internships go back to « not scheduled ».",
    'stg.defense.daySub': "A date, a room, the first slot, a slot's length",
    'stg.defense.date': "Date",
    'stg.defense.room': "Room",
    'stg.defense.otherPlace': "Other place…",
    'stg.defense.place': "Place a defence",
    'stg.defense.edit': "Defence · {name}",
    'stg.defense.firstSlot': "First slot",
    'stg.defense.slot': "Slot length (min)",
    'stg.defense.breakAt': "Break at",
    'stg.defense.breakMinutes': "Break length (min)",
    'stg.defense.breakLine': "{at}, {count} min",
    'stg.defense.minutes': "{count} min",
    'stg.defense.time': "Time",
    'stg.defense.day': "Day",
    'stg.defense.jury': "Jury",
    'stg.defense.role': "Role",
    'stg.defense.member': "Jury member",
    'stg.defense.addMember': "Add a member",
    'stg.defense.removeMember': "Remove this member",
    'stg.defense.inviteTutor': "Invite the tutor",
    'stg.defense.tutorInvited': "Organisation tutor",
    'stg.defense.allowConflict': "Schedule anyway",
    'stg.defense.juryNote': "The jury has a chair and at least one other member; the tutor is invited. A teacher already teaching or on another jury at that time is flagged.",
    'stg.defense.free': "free",
    'stg.defense.incomplete': "incomplete jury",
    'stg.defense.remove': "Remove the defence",
    'stg.defense.rules': "Day settings",
    'stg.defense.autoplan': "Place automatically",
    'stg.defense.autoplanNote': "Places waiting internships in free slots, the supervisor on the jury when free, the tutor invited; the chair remains to be assigned.",
    'stg.defense.convocations': "Notices (PDF)",
    'stg.defense.note': "The intern is notified of their slot; notices are printed for them and the jury.",
    'stg.mine.title': "My internship",
    'stg.mine.none': "No internship yet",
    'stg.mine.noneSub': "Propose the internship you found, or apply to an offer.",
    'stg.mine.supervisor': "supervisor {name}",
    'stg.mine.tutor': "tutor {name}",
    'stg.mine.toDeposit': "to upload",
    'stg.mine.received': "received",
    'stg.mine.before': "by {date}",
    'stg.mine.deposit': "Upload the PDF",
    'stg.mine.mark': "Internship mark: {mark} / 20",
    'stg.mine.openFile': "Open the internship file",
    'stg.mine.offers': "Offers for you",
    'stg.mine.allOffers': "All offers",
    'stg.mine.noOffer': "No open offer for you.",
    'stg.mine.proposeTitle': "Propose an internship",
    'stg.mine.proposeText': "Found an internship yourself? Propose it: the school approves it and prepares the agreement.",
    'stg.mine.noCampaign': "No open campaign for your programme.",
    'stg.mine.propose': "Propose an internship",
    'stg.mine.proposeNote': "Your proposal arrives as « proposed »: the school approves it, or tells you why it refuses it. An organisation missing from the directory is added here.",
    'stg.mine.mobileNote': "These screens will also serve the mobile app: report upload, applications, notifications at each step.",
    'stg.mine.offersTitle': "Offers",
    'stg.mine.offersSub': "Internship, work-study and job offers aimed at you",
    'stg.mine.description': "See details",
    'stg.mine.startsOn': "from {date}",
    'stg.mine.applications': "My applications",
    'stg.mine.noApplication': "No application.",
    'stg.mine.withCv': "CV attached",
    'stg.mine.withoutCv': "no CV",
    'stg.mine.withdraw': "Withdraw",
    'stg.supervised.title': "My interns",
    'stg.supervised.sub': "{name} · academic supervisor of {count} intern(s)",
    'stg.supervised.interns': "Interns",
    'stg.supervised.idle': "No follow-up for {days} days",
    'stg.supervised.idleNote': "a reminder is sent to you every morning",
    'stg.supervised.idleNone': "all followed",
    'stg.supervised.toMark': "Reports to mark",
    'stg.supervised.toMarkNote': "report received or defence held",
    'stg.supervised.lastFollowUp': "Last follow-up",
    'stg.supervised.never': "no follow-up",
    'stg.supervised.markReport': "Mark the report",
    'stg.supervised.open': "Log a follow-up",
    'stg.supervised.empty': "No intern to supervise.",
    'stg.supervised.note': "You only see your interns: log your follow-ups (call, visit, attendance), read the documents and mark the report. Supervision hours can count in your workload, depending on the internship settings.",
    'stg.settings.title': "Internships · settings",
    'stg.settings.sub': "Internship types, their documents and grid, follow-ups and reminders",
    'stg.settings.types': "Internship types",
    'stg.settings.followTypes': "Follow-up types",
    'stg.settings.rolesNote': "Supervision roles are fixed: academic supervisor (a teacher), workplace tutor (a contact), jury members.",
    'stg.settings.module': "Module",
    'stg.settings.moduleSub': "Super Administrator only",
    'stg.settings.enabled': "Internships module enabled",
    'stg.settings.enabledHint': "Enabling Internships also enables External relations: the organisation directory is shared.",
    'stg.settings.enabledOn': "Enabled on {date} · {who}",
    'stg.settings.relationsOff': "External relations are off: they will be enabled with Internships.",
    'stg.settings.rules': "Reminders and rules",
    'stg.settings.idle': "Remind supervisors without follow-up for (days)",
    'stg.settings.idleHint': "for ongoing internships",
    'stg.settings.reminder': "Remind students before the report deadline (days)",
    'stg.settings.reminderHint': "and while the report is overdue",
    'stg.settings.studentsPropose': "Students can propose their internship from their space",
    'stg.settings.countSupervision': "Count supervision in teachers' hours given",
    'stg.settings.countSupervisionHint': "Yes: each signed internship credits its supervisor with the type's supervision hours at its end; they count in workload and pay.",
    'stg.type.new': "New internship type",
    'stg.type.edit': "Internship type · {label}",
    'stg.type.sub': "Its duration, defence, required documents and evaluation grid",
    'stg.type.label': "Internship type",
    'stg.type.minMonths': "Minimum duration (months)",
    'stg.type.minMonthsHint': "checked when an internship is saved",
    'stg.type.months': "{months} months",
    'stg.type.reportDays': "Report due (days after the end)",
    'stg.type.reportDaysHint': "unless the campaign sets a date",
    'stg.type.supervision': "Supervision hours",
    'stg.type.supervisionHint': "credited to the supervisor if the setting says so",
    'stg.type.defense': "Defence before a jury",
    'stg.type.defenseShort': "Defence",
    'stg.type.active': "Active",
    'stg.type.documents': "Required documents",
    'stg.type.due': "Due",
    'stg.type.isReport': "This is the report",
    'stg.type.addDocument': "Add a document",
    'stg.type.grid': "Evaluation grid",
    'stg.type.addComponent': "Add a component",
    'stg.type.total': "Total: {total}% (100% expected)",
    'stg.type.defaultConvention': "Signed internship agreement",
    'stg.type.defaultReport': "Internship report",
    'stg.type.deactivateText': "« {label} » is already used: it is deactivated and no longer offered for new campaigns.",
    'stg.type.removeText': "« {label} » will be deleted with its documents and grid.",
    'stg.followType.new': "New follow-up type",
    'stg.followType.edit': "Follow-up type",
    'stg.followType.label': "Label",
    'rel.modules.stages': "Internships",
    'rel.modules.stagesHint': "Campaigns, internships, offers, defences (with External relations)",
    'rel.modules.relationsOffStages': "Internships, which depend on it, will be disabled too.",
    'fac.dash.supervision': "including {hours} of internship supervision",
    'violation.stg.too_short': "Duration below this internship type's minimum.",
    'violation.stg.duplicate': "This student already has an internship in this campaign.",
    'violation.stg.bad_state': "Not possible at this step of the internship.",
    'violation.stg.period': "The end is before the start.",
    'violation.stg.future_follow_up': "A follow-up is logged once done.",
    'violation.stg.not_your_component': "The supervisor only marks their own components.",
    'violation.stg.mark_missing': "A grid component is not marked.",
    'violation.stg.evaluated': "Evaluation validated: reopen it to change marks.",
    'violation.stg.no_subject': "The campaign does not say which programme element receives the mark.",
    'violation.stg.assessment_validated': "This element's marks are validated: reopen the assessment.",
    'violation.stg.no_assessment_type': "No assessment type in the institution.",
    'violation.stg.weights': "Grid weights must add up to 100%.",
    'violation.stg.two_reports': "Only one report per type.",
    'violation.stg.requirement_used': "This document was already received for internships: it cannot be removed.",
    'violation.stg.component_used': "This component is already marked: it cannot be removed.",
    'violation.stg.campaign_closed': "This campaign does not concern you.",
    'violation.stg.bad_training': "Choose a programme from the campaign's year.",
    'violation.stg.bad_subject': "Choose an element of this programme.",
    'violation.stg.duplicate_training': "Duplicate programme.",
    'violation.stg.no_training': "Choose at least one programme.",
    'violation.stg.campaign_used': "This campaign has internships: it cannot be deleted.",
    'violation.stg.report_before_end': "The report is due after the internship ends.",
    'violation.stg.other_campaign': "This day belongs to another campaign.",
    'violation.stg.slot_taken': "This slot is already taken.",
    'violation.stg.juror_busy': "A jury member is already busy at that time.",
    'violation.stg.juror': "A teacher or a contact.",
    'violation.stg.juror_duplicate': "Duplicate member.",
    'violation.stg.two_presidents': "Only one chair.",
    'violation.stg.offer_expired': "The deadline has passed: change it before publishing.",
    'violation.stg.offer_used': "This offer has applications: close it instead of deleting it.",
    'violation.stg.offer_closed': "This offer is not open to you.",
    'violation.stg.already_applied': "You already applied to this offer.",
    'violation.stg.application_linked': "This application already has its internship.",
    'trace.table.stages_settings': "Internship settings",
    'trace.table.stages_type': "Internship type",
    'trace.table.stages_type_document': "Required document",
    'trace.table.stages_type_component': "Grid component",
    'trace.table.stages_follow_type': "Follow-up type",
    'trace.table.stages_campaign': "Internship campaign",
    'trace.table.stages_campaign_training': "Campaign programme",
    'trace.table.stages_offer': "Offer",
    'trace.table.stages_offer_training': "Offer programme",
    'trace.table.stages_application': "Application",
    'trace.table.internship': "Internship",
    'trace.table.internship_document': "Internship document",
    'trace.table.internship_follow_up': "Internship follow-up",
    'trace.table.internship_mark': "Grid mark",
    'trace.table.stages_defense_day': "Defence day",
    'trace.table.internship_defense': "Defence",
    'trace.table.internship_defense_juror': "Jury member",
    'trace.field.alumni': "Graduates",
    'trace.field.assessment_type_id': "Assessment type",
    'trace.field.break_at': "Break at",
    'trace.field.break_minutes': "Break length",
    'trace.field.campaign_id': "Campaign",
    'trace.field.closes_on': "Closes on",
    'trace.field.closing_reason': "Reason",
    'trace.field.component_id': "Component",
    'trace.field.count_supervision': "Supervision counted",
    'trace.field.cv_name': "CV",
    'trace.field.cv_size': "CV size",
    'trace.field.day_id': "Day",
    'trace.field.defense_id': "Defence",
    'trace.field.defense_required': "Defence required",
    'trace.field.due': "Due",
    'trace.field.duration': "Duration",
    'trace.field.evaluated_at': "Evaluated at",
    'trace.field.evaluated_by_id': "Evaluated by",
    'trace.field.evaluator': "Assessed by",
    'trace.field.file_name': "File",
    'trace.field.file_size': "File size",
    'trace.field.internship_id': "Internship",
    'trace.field.jury_comment': "Jury comment",
    'trace.field.mark_reported_at': "Mark carried over at",
    'trace.field.min_months': "Minimum duration (months)",
    'trace.field.missions': "Tasks",
    'trace.field.offer_id': "Offer",
    'trace.field.proposed_by_student': "Proposed by the student",
    'trace.field.received_by_id': "Received by",
    'trace.field.report': "Report",
    'trace.field.report_days': "Report delay",
    'trace.field.report_due_on': "Report due on",
    'trace.field.report_reminder_days': "Report reminder",
    'trace.field.requirement_id': "Required document",
    'trace.field.role': "Role",
    'trace.field.slot_minutes': "Slot length",
    'trace.field.student_present': "Intern present",
    'trace.field.students_propose': "Students propose",
    'trace.field.supervision_minutes': "Supervision minutes",
    'trace.field.supervisor_id': "Academic supervisor",
    'trace.field.supervisor_idle_days': "Supervisor reminder",
    'trace.field.supervisor_since': "Supervisor since",
    'trace.field.topic': "Topic",
    'trace.field.tutor_id': "Tutor",
    'trace.field.weight': "Weight",
    'trace.value.proposed': "proposed",
    'trace.value.evaluated': "evaluated",
    'trace.value.refused': "refused",
    'trace.value.kelassi': "Kelassi",
    'trace.value.history': "graduate history",
    'trace.value.before_start': "before start",
    'trace.value.at_end': "at the end",
    'trace.value.after_end': "after the end",
    'trace.value.tutor': "tutor",
    'trace.value.supervisor': "supervisor",
    'trace.value.jury': "jury",
    'trace.value.internship': "internship",
    'trace.value.apprenticeship': "work-study",
    'trace.value.job': "job",
    'trace.value.published': "published",
    'trace.value.new': "new",
    'trace.value.forwarded': "forwarded",
    'trace.value.retained': "selected",
    'trace.value.declined': "declined",
    'trace.value.president': "chair",
    'trace.value.member': "member",
    'form.required': "This field is required.",
    'form.requiredMark': "required",
    'form.legend': "Fields marked * are required",
    'form.summary': "{count} field(s) to correct before saving",
    'form.pasteCut': "The pasted text was cut to {max} characters.",
    'form.counter': "{count} of {max} characters",
    'rel.modules.requests': "Requests",
    'rel.modules.requestsHint': "Certificates, transcripts, absence justifications, mark changes, other requests",
    'req.status.new': "New",
    'req.status.ongoing': "In progress",
    'req.status.waiting': "Awaiting information",
    'req.status.done': "Handled",
    'req.status.rejected': "Rejected",
    'req.status.cancelled': "Cancelled",
    'req.status.late': "Late",
    'req.when.today': "today {time}",
    'req.when.yesterday': "yesterday {time}",
    'req.delay.suspended': "on hold",
    'req.delay.doneIn': "handled in {count} day(s)",
    'req.delay.late': "{count} day(s) late",
    'req.delay.today': "today",
    'req.delay.tomorrow': "tomorrow",
    'req.delay.inDays': "in {count} days",
    'req.days': "{value} day(s)",
    'req.noTeam': "No team",
    'req.open': "Open",
    'req.choose': "Choose…",
    'req.breadcrumb': "Breadcrumb",
    'req.files.label': "Attachments",
    'req.files.drop': "Drop the PDF or the photo (PNG, JPEG), 10 MB at most",
    'req.files.uploading': "Uploading…",
    'req.files.remove': "Remove",
    'req.files.see': "View",
    'req.files.add': "Add an attachment",
    'req.dashboard.title': "Requests",
    'req.dashboard.sub': "Requests from students, teachers and staff",
    'req.kpi.open': "To handle",
    'req.kpi.openNote': "{count} new, not yet picked up",
    'req.kpi.late': "Late",
    'req.kpi.lateNote': "type deadline passed",
    'req.kpi.waiting': "Awaiting information",
    'req.kpi.waitingNote': "waiting for the requester",
    'req.kpi.done': "Handled this month",
    'req.kpi.doneNote': "average time {days}",
    'req.kpi.doneNoteEmpty': "none yet this month",
    'req.dashboard.byType': "By type",
    'req.dashboard.byTypeSub': "Open requests and handling time",
    'req.dashboard.noType': "No request type.",
    'req.dashboard.byTeam': "By team",
    'req.dashboard.byTeamSub': "Requests in progress",
    'req.dashboard.nothingOpen': "No request in progress.",
    'req.dashboard.mine': "For me and my team",
    'req.dashboard.allMine': "My whole queue",
    'req.dashboard.nothingMine': "Nothing to handle for you.",
    'req.column.type': "Request type",
    'req.column.open': "In progress",
    'req.column.late': "Late",
    'req.column.target': "Target time",
    'req.column.average': "Average time",
    'req.column.number': "Number",
    'req.column.requester': "Requester",
    'req.column.received': "Received",
    'req.column.team': "Team",
    'req.column.state': "Status",
    'req.column.delay': "Time",
    'req.column.request': "Request",
    'req.column.filed': "Filed",
    'req.list.title': "Requests",
    'req.list.sub': "{open} open request(s) · {late} late",
    'req.view.mine': "My queue",
    'req.view.team': "My team",
    'req.view.open': "All open",
    'req.view.late': "Late",
    'req.view.waiting': "Awaiting information",
    'req.view.closed': "Closed",
    'req.view.count': "{label} ({count})",
    'req.list.search': "Search",
    'req.list.searchPlaceholder': "Number, requester, subject",
    'req.list.allTypes': "All types",
    'req.list.empty': "No request in this view.",
    'req.list.note': "\"My queue\": requests whose current step is assigned to me or to my teams; \"My team\": those of my handling teams. The time runs from receipt and stops while information is awaited.",
    'req.population.student': "Student",
    'req.population.teacher': "Teacher",
    'req.population.staff': "Staff",
    'req.kind.student': "Student",
    'req.kind.teacher': "Teacher",
    'req.audience.student': "Students",
    'req.audience.teacher': "Teachers",
    'req.audience.staff': "Staff",
    'req.produces.none': "nothing (written answer)",
    'req.produces.enrolment': "Certificate of enrolment (Documents)",
    'req.produces.transcript': "Transcript (Documents)",
    'req.produces.certificate': "Certificate of achievement (Documents)",
    'req.produces.mark_change': "Mark correction (Marks)",
    'req.produces.absence': "Justified absence (Attendance)",
    'req.produces.internship': "Proposed internship (Internships)",
    'req.produce.none': "Handle",
    'req.produce.enrolment': "Issue the certificate and handle",
    'req.produce.transcript': "Issue the transcript and handle",
    'req.produce.certificate': "Issue the certificate and handle",
    'req.produce.mark_change': "Apply the marks and handle",
    'req.produce.absence': "Justify the absence and handle",
    'req.produce.internship': "Handle",
    'req.field.year': "Year",
    'req.field.deliberation': "Deliberation",
    'req.field.language': "Document language",
    'req.field.copies': "Copies",
    'req.field.startsOn': "From",
    'req.field.endsOn': "To",
    'req.field.justificationKind': "Reason for absence",
    'req.field.establishment': "Target institution",
    'req.field.reason': "Reason",
    'req.field.description': "Your request",
    'req.field.assessment': "Assessment",
    'req.field.line': "Mark",
    'req.field.other': "Field",
    'req.lang.fr': "French",
    'req.lang.en': "English",
    'req.lang.ar': "Arabic",
    'req.rule.hidden': "Hidden",
    'req.rule.shown': "Shown",
    'req.rule.required': "Required",
    'req.form.delay': "Target time: {count} day(s)",
    'req.form.attachments.hidden': "attachments: none",
    'req.form.attachments.shown': "attachments: optional",
    'req.form.attachments.required': "attachment required",
    'req.form.noDeliberation': "No closed deliberation that includes you.",
    'req.ask.sub': "The time is the one the school commits to",
    'req.ask.submit': "Send the request",
    'req.ask.message': "Message to the school",
    'req.new.title': "New request",
    'req.new.sub': "Filed by staff, for a student or a teacher",
    'req.new.type': "Request type",
    'req.new.for': "For",
    'req.new.forHint': "Name or number, at least two characters",
    'req.new.change': "Change",
    'req.new.pickPerson': "Choose the person first.",
    'req.new.pickType': "Choose the request type.",
    'req.new.handleNow': "Handle at once (issue and close)",
    'req.new.handleNowNone': "Handle at once (close)",
    'req.file.received': "received on {date}",
    'req.file.closed': "closed on {date}",
    'req.file.suspended': "time on hold",
    'req.file.due': "to handle before {date}",
    'req.file.team': "assigned to {team}",
    'req.file.teamAssignee': "assigned to {team} ({name})",
    'req.file.document': "Download the document",
    'req.file.rejected': "Request rejected: {reason}",
    'req.file.cancelled': "Request cancelled: {reason}",
    'req.file.request': "The request",
    'req.file.noFields': "No field filled in.",
    'req.file.files': "Attachments",
    'req.file.noFiles': "No attachment.",
    'req.file.thread': "Messages",
    'req.file.threadSub': "The requester sees the messages, not the internal notes",
    'req.file.noMessages': "No message.",
    'req.file.actions': "Actions",
    'req.file.step': "Step \"{step}\"",
    'req.file.waitingNote': "Information is awaited from the requester: the time is on hold.",
    'req.file.circuit': "Workflow",
    'req.file.where': "Where is it?",
    'req.file.history': "History",
    'req.circuit.received': "Received",
    'req.circuit.current': "in progress",
    'req.circuit.suspended': "waiting for the requester",
    'req.event.received': "Filed",
    'req.event.assigned': "Picked up",
    'req.event.advanced': "Step passed",
    'req.event.waiting': "Information requested",
    'req.event.completed': "Information sent",
    'req.event.forwarded': "Forwarded",
    'req.event.done': "Handled",
    'req.event.rejected': "Rejected",
    'req.event.cancelled': "Cancelled",
    'req.event.failed': "Execution failed",
    'req.outcome.document': "Document issued: {reference}",
    'req.outcome.marks': "{count} mark(s) corrected, correction recorded",
    'req.outcome.absence': "Absence justified; {count} recorded absence(s) covered",
    'req.outcome.done': "Request handled.",
    'req.thread.internal': "internal note",
    'req.thread.internalBox': "Internal note",
    'req.thread.write': "Write to the requester",
    'req.thread.writeStaff': "Write to the school",
    'req.thread.send': "Send",
    'req.act.assign': "Take it",
    'req.act.entrust': "Assign to…",
    'req.assign.team': "{team} team · step “{step}”",
    'req.assign.any': "Accounts that handle requests",
    'req.assign.me': "me",
    'req.assign.current': "in charge",
    'req.assign.hint': "The chosen person receives a notification with a link to the request.",
    'req.flash.entrusted': "The request is assigned to {name}.",
    'req.act.advance': "Complete the step",
    'req.act.advanceExecutes': "Issue and handle",
    'req.act.advanceSub': "The step is passed; at the last one, the request is handled and the requester notified.",
    'req.act.waiting': "Request information",
    'req.act.waitingSub': "The requester receives your message; the time is on hold until the reply.",
    'req.act.waitingText': "What is missing",
    'req.act.forward': "Forward to another team",
    'req.act.forwardSub': "The request stays at the same step, assigned to the chosen team.",
    'req.act.team': "Team",
    'req.act.reject': "Reject",
    'req.act.rejectSub': "The requester receives the reason.",
    'req.act.cancel': "Cancel the request",
    'req.act.cancelSub': "The request is closed without action.",
    'req.act.reason': "Reason",
    'req.act.reasonOptional': "Reason (optional)",
    'req.act.note': "Note (optional)",
    'req.flash.advance': "Step passed.",
    'req.flash.waiting': "Information requested.",
    'req.flash.forward': "Request forwarded.",
    'req.flash.reject': "Request rejected.",
    'req.flash.cancel': "Request cancelled.",
    'req.flash.assigned': "The request is assigned to you.",
    'req.flash.attached': "Attachment added.",
    'req.flash.completed': "Information sent: the request resumes.",
    'req.complete.title': "Complete the request",
    'req.complete.sub': "The time is on hold until your reply",
    'req.complete.message': "Message to the school",
    'req.complete.submit': "Send the information",
    'req.mine.title': "My requests",
    'req.mine.ask': "Make a request",
    'req.mine.askSub': "The time is the one the school commits to",
    'req.mine.noType': "No request open for now.",
    'req.mine.within': "within {count} day(s)",
    'req.mine.viaStages': "from \"My internship\"",
    'req.mine.askButton': "Request",
    'req.mine.list': "My requests",
    'req.mine.empty': "No request yet.",
    'req.mine.download': "Download the document",
    'req.mine.complete': "Complete",
    'req.mine.follow': "Follow",
    'req.mine.note': "You are notified in the bell at each step; an issued document is downloaded here.",
    'req.marks.button': "Request a change",
    'req.marks.title': "Mark change request",
    'req.marks.sub': "Enter the marks to change",
    'req.marks.count': "{count} mark(s) to change",
    'req.marks.pending': "{count} change request(s) already in progress on this assessment.",
    'req.marks.submit': "Send the request",
    'req.marks.assessment': "{label} · out of {outOf} · marks validated and locked",
    'req.marks.student': "Student",
    'req.marks.current': "Current mark",
    'req.marks.asked': "Requested mark",
    'req.marks.absence': "Or absence",
    'req.marks.note': "Detail",
    'req.marks.askedFor': "Requested mark for {name}",
    'req.marks.absenceFor': "Absence for {name}",
    'req.marks.noteFor': "Detail for {name}",
    'req.marks.circuit': "The request follows the configured workflow (academic office opinion, then applied by exams): each changed mark keeps the old value and the reason. A closed deliberation must be reopened first.",
    'req.marks.sent': "Change request sent.",
    'req.settings.title': "Requests · settings",
    'req.settings.sub': "Request types, their fields, their workflow, the handling teams",
    'req.settings.module': "Requests module",
    'req.settings.moduleSub': "When off, the module removes its rights from the menu and screens without deleting anything",
    'req.settings.enabled': "Module on",
    'req.settings.enabledHint': "Students and teachers file their requests; staff handles them",
    'req.settings.enabledOn': "Switched on {date} by {who}",
    'req.settings.switchedOn': "Requests module switched on.",
    'req.settings.switchedOff': "Requests module switched off.",
    'req.settings.types': "Request types",
    'req.settings.audience': "Who can request it",
    'req.settings.produces': "What the request produces",
    'req.settings.inactive': "inactive",
    'req.settings.deactivate': "Deactivate",
    'req.settings.deactivateText': "\"{label}\" has requests: it will be deactivated and no one can request it any more.",
    'req.settings.deleteText': "Delete the type \"{label}\" and its workflow?",
    'req.settings.pick': "Choose a type to configure it.",
    'req.settings.newType': "New type",
    'req.settings.selected': "The selected type",
    'req.settings.label': "Label",
    'req.settings.actionLocked': "This type has requests: what it produces no longer changes.",
    'req.settings.opensOn': "Open from",
    'req.settings.closesOn': "to",
    'req.settings.delay': "Target time (days)",
    'req.settings.delayHint': "At least the sum of the step times",
    'req.settings.color': "Colour",
    'req.settings.guidance': "What to know (shown to the requester)",
    'req.settings.active': "Active",
    'req.settings.fields': "Form fields",
    'req.settings.fieldsSub': "Hidden, shown or required",
    'req.settings.field': "Field",
    'req.settings.forced': "required by what the request produces",
    'req.settings.circuit': "Handling workflow",
    'req.settings.circuitSub': "Total target time: {days} day(s)",
    'req.settings.addStep': "Add a step",
    'req.settings.noSteps': "No step: add at least one to open this type.",
    'req.settings.noStepsInternship': "No workflow: internship agreements are proposed from \"My internship\".",
    'req.settings.step': "Step",
    'req.settings.executes': "Produces",
    'req.settings.executesNote.action': "A single step produces {what}; the others approve or request information.",
    'req.settings.executesNote.none': "This type produces nothing: each step approves, the last closes the request with a written answer.",
    'req.settings.up': "Move up",
    'req.settings.down': "Move down",
    'req.settings.teams': "Handling teams",
    'req.settings.teamsSub': "Accounts, or all holders of a role",
    'req.settings.roleHolders': "holders of the {role} role",
    'req.settings.members': "{count} named member(s)",
    'req.settings.typeSaved': "Type saved.",
    'req.settings.teamSaved': "Team saved.",
    'req.settings.teamDeactivated': "The team has requests: it is deactivated.",
    'req.team.new': "New team",
    'req.team.edit': "Edit the team",
    'req.team.label': "Team name",
    'req.team.role': "Holders of a role",
    'req.team.roleHint': "All accounts with this role are members",
    'req.team.noRole': "No role",
    'req.team.members': "Member accounts",
    'req.team.membersHint': "{count} account(s) chosen",
    'req.team.search': "Filter accounts",
    'violation.req.action_locked': "This type has requests: what it produces no longer changes.",
    'violation.req.assessment_open': "An assessment still open is corrected directly.",
    'violation.req.attachment_required': "Attach the requested document.",
    'violation.req.bad_deliberation': "Choose a closed deliberation that includes the person.",
    'violation.req.closed': "This request is already closed.",
    'violation.req.closed_type': "This request type is closed today.",
    'violation.req.mark_locked': "The assessment can no longer be corrected (not validated, or year closed).",
    'violation.req.no_audience': "At least one audience.",
    'violation.req.no_enrolment': "No active enrolment that year.",
    'violation.req.no_execution': "A step must produce what the type produces.",
    'violation.req.no_lines': "At least one mark to change.",
    'violation.req.no_steps': "This type has no handling workflow.",
    'violation.req.not_a_student': "This request concerns a student.",
    'violation.req.not_admitted': "The certificate of achievement is for admitted students only.",
    'violation.req.not_for_you': "This request type is not open to this person.",
    'violation.req.not_issued': "No document issued.",
    'violation.req.not_waiting': "No information is awaited.",
    'violation.req.one_execution': "A single step produces what the request produces.",
    'violation.req.too_many_files': "Five attachments at most.",
    'violation.req.use_stages': "This type is proposed from \"My internship\".",
    'violation.req.waiting': "Information is awaited from the requester.",
    'journal.action.requests.settings_changed': "Requests settings changed",
    'journal.action.requests.type_saved': "Request type saved",
    'journal.action.requests.team_saved': "Handling team saved",
    'journal.action.requests.request_created': "Request filed",
    'journal.action.requests.request_step_changed': "Request advanced",
    'journal.action.requests.request_closed': "Request closed",
    'journal.filter.group.requests': "Requests",
    'trace.table.requests_settings': "Requests settings",
    'trace.table.requests_team': "Handling team",
    'trace.table.requests_team_member': "Team member",
    'trace.table.requests_type': "Request type",
    'trace.table.requests_type_step': "Workflow step",
    'trace.table.requests_request': "Request",
    'trace.table.requests_message': "Request message",
    'trace.table.requests_file': "Request attachment",
    'trace.table.requests_event': "Request history",
    'doc.type.enrolment': "Certificate of enrolment",
    'doc.types.enrolment': "Enrolment certificates",
    'doc.verify.bodyEnrolment': "{type} of {student}, {training} {year}, {institution}; issued on {date} by {by} from the enrolment record.",
    'doc.settings.hint.enrolment': "With no signatory set: \"For the management\" and the name of the issuer",
    'doc.settings.default.enrolment': "No signatory set: \"For the management\" and the name of the issuer.",
    'violation.doc.enrolment_inactive': "An enrolment certificate is only issued for an active enrolment.",
    'violation.doc.not_from_deliberation': "An enrolment certificate is issued from an enrolment, not a deliberation.",
    'heb.add': "Add",
    'heb.all': "All",
    'heb.allCampuses': "All campuses",
    'heb.allCategories': "All categories",
    'heb.allGenders': "All genders",
    'heb.allLevels': "All levels",
    'heb.allRegimes': "All schemes",
    'heb.breadcrumb': "Breadcrumb",
    'heb.change': "Change",
    'heb.choose': "Choose",
    'heb.confirm': "Confirm",
    'heb.disability': "disability",
    'heb.edit': "Edit",
    'heb.no': "no",
    'heb.none': "none",
    'heb.open': "Open",
    'heb.optional': "optional",
    'heb.remove': "Remove",
    'heb.scholar': "scholarship holder",
    'heb.notScholar': "no scholarship",
    'heb.search': "Search",
    'heb.total': "Total",
    'heb.docs.none': "No document required.",
    'heb.field.campus': "Campus",
    'heb.field.gender': "Gender",
    'heb.field.level': "Level",
    'heb.field.regime': "Scheme",
    'heb.field.year': "Year",
    'heb.gender.F': "F",
    'heb.gender.M': "M",
    'heb.person.search': "Name or student number, at least two letters",
    'heb.person.student': "Student",
    'heb.upkeep.title': "Inspections and repairs",
    'heb.app.draft': "Draft",
    'heb.app.submitted': "Submitted",
    'heb.app.incomplete': "Document requested",
    'heb.app.eligible': "Eligible",
    'heb.app.waitlisted': "Waiting list",
    'heb.app.rejected': "Rejected",
    'heb.app.provisional': "Bed offered",
    'heb.app.accepted': "To pay",
    'heb.app.final': "Final allocation",
    'heb.app.housed': "Housed",
    'heb.app.withdrawn': "Withdrawn",
    'heb.app.left': "Left",
    'heb.alloc.provisional': "Provisional",
    'heb.alloc.accepted': "Accepted, to pay",
    'heb.alloc.final': "Final",
    'heb.alloc.housed': "Occupant",
    'heb.alloc.released': "Released",
    'heb.alloc.ended': "Ended",
    'heb.campaign.draft': "Scheduled",
    'heb.campaign.open': "Open for applications",
    'heb.campaign.processing': "Processing",
    'heb.campaign.closed': "Closed",
    'heb.repair.reported': "Reported",
    'heb.repair.planned': "Planned",
    'heb.repair.in_progress': "In progress",
    'heb.repair.done': "Done",
    'heb.repair.closed': "Closed",
    'heb.priority.urgent': "Urgent",
    'heb.priority.high': "High",
    'heb.priority.normal': "Normal",
    'heb.fileStatus.pending': "To check",
    'heb.fileStatus.accepted': "Accepted",
    'heb.fileStatus.rejected': "Refused",
    'heb.payment.submitted': "To validate",
    'heb.payment.validated': "Validated",
    'heb.payment.refused': "Refused",
    'heb.method.transfer': "Bank transfer",
    'heb.method.deposit': "Bank deposit",
    'heb.method.cash': "Cash",
    'heb.method.cheque': "Cheque",
    'heb.roomGender.female': "Women",
    'heb.roomGender.male': "Men",
    'heb.roomGender.mixed': "Mixed",
    'heb.roomGenderShort.female': "F",
    'heb.roomGenderShort.male': "M",
    'heb.roomGenderShort.mixed': "mixed",
    'heb.bedNature.single': "Single bed",
    'heb.bedNature.double': "Double bed",
    'heb.bedNature.bunk': "Bunk bed",
    'heb.check.ok': "Compliant",
    'heb.check.warn': "To review",
    'heb.check.ko': "Non compliant",
    'heb.checklistKind.inspection': "Inspection",
    'heb.checklistKind.check_in': "Check-in inventory",
    'heb.checklistKind.check_out': "Check-out inventory",
    'heb.stay.good': "Good",
    'heb.stay.worn': "Worn",
    'heb.stay.damaged': "Damaged",
    'heb.source.student': "reported by the occupant",
    'heb.source.inspection': "inspection finding",
    'heb.source.staff': "reported by staff",
    'heb.quota.level': "Level",
    'heb.quota.scholarship': "Scholarship holders",
    'heb.quota.foreign': "International students",
    'heb.quota.disability': "Disability",
    'heb.quota.newcomer': "Newcomers",
    'heb.weight.distance': "Distance from home",
    'heb.weight.scholarship': "Scholarship",
    'heb.weight.results': "Last year's results",
    'heb.weight.disability': "Disability",
    'heb.weight.seniority': "Years in housing",
    'heb.rule.gender': "Respect room gender",
    'heb.rule.quotas': "Apply quotas",
    'heb.rule.roommates': "Pair roommates who chose each other",
    'heb.rule.reserve': "Keep reserved beds",
    'heb.rule.otherCampus': "Offer another campus when the wish is full",
    'heb.reason.cap': "no bed left",
    'heb.reason.quota': "quota reached",
    'heb.reason.cabin': "no compatible room",
    'heb.reason.price': "no price",
    'heb.column.actions': "Actions",
    'heb.column.agent': "Agent",
    'heb.column.applications': "Applications",
    'heb.column.bed': "Bed",
    'heb.column.beds': "Beds",
    'heb.column.campaign': "Campaign",
    'heb.column.campus': "Campus",
    'heb.column.category': "Category",
    'heb.column.checkpoint': "Checkpoint",
    'heb.column.collected': "Collected",
    'heb.column.conformity': "Compliance",
    'heb.column.criterion': "Criterion",
    'heb.column.distance': "Distance",
    'heb.column.due': "Due",
    'heb.column.endsOn': "Check-out",
    'heb.column.files': "Documents",
    'heb.column.finding': "Finding",
    'heb.column.findings': "Findings",
    'heb.column.free': "Free",
    'heb.column.inventory': "In the estate",
    'heb.column.key': "Key",
    'heb.column.number': "Number",
    'heb.column.occupancy': "Occupancy",
    'heb.column.occupant': "Occupant",
    'heb.column.occupants': "Occupants",
    'heb.column.occupied': "Occupied",
    'heb.column.open': "Open",
    'heb.column.paid': "Paid",
    'heb.column.period': "Stay",
    'heb.column.points': "Points",
    'heb.column.priority': "Priority",
    'heb.column.provisional': "Provisional",
    'heb.column.room': "Room",
    'heb.column.roomType': "Room type",
    'heb.column.rooms': "Rooms",
    'heb.column.startsOn': "Check-in",
    'heb.column.state': "Status",
    'heb.column.student': "Student",
    'heb.column.studentBed': "Student and bed",
    'heb.column.total': "Total",
    'heb.column.unpaid': "Unpaid",
    'heb.column.value': "Value",
    'heb.column.visit': "Inspection",
    'heb.column.where': "Where it stands",
    'heb.column.window': "Application window",
    'heb.column.wish': "Wish",
    'heb.dash.title': "Student housing",
    'heb.dash.sub': "Year {year} · occupancy, payments, applications and upkeep",
    'heb.dash.noYear': "No academic year has a housing campaign yet.",
    'heb.dash.occupancy': "Occupancy rate",
    'heb.dash.occupancySub': "By month, compared with last year",
    'heb.dash.money': "Expected and collected fees",
    'heb.dash.moneySub': "By month, in dirhams",
    'heb.dash.thisYear': "This year",
    'heb.dash.lastYear': "Last year",
    'heb.dash.expected': "Expected",
    'heb.dash.collected': "Collected",
    'heb.dash.byCampus': "By campus",
    'heb.dash.repairs': "Open repairs",
    'heb.dash.repairsSub': "By category, average time",
    'heb.dash.noRepairs': "No open repair.",
    'heb.dash.repairLine': "{count} · {days} d",
    'heb.dash.moves': "The year in figures",
    'heb.moves.submitted': "Applications submitted",
    'heb.moves.final': "Final allocations",
    'heb.moves.withdrawn': "Withdrawals",
    'heb.moves.unpaid_released': "Beds released for non-payment",
    'heb.moves.left_early': "Early departures",
    'heb.moves.moved': "Bed changes",
    'heb.kpi.occupancy': "Occupancy",
    'heb.kpi.occupancyNote': "{occupied} of {beds} beds · last year {last}",
    'heb.kpi.collected': "Collected",
    'heb.kpi.collectedNote': "of {expected} expected",
    'heb.kpi.unpaid': "Unpaid",
    'heb.kpi.unpaidNote': "{count} allocation(s) overdue",
    'heb.kpi.waitlist': "Waiting list",
    'heb.kpi.waitlistNote': "{count} eligible without a bed",
    'heb.kpi.repairs': "Open repairs",
    'heb.kpi.repairsNote': "average {days} days",
    'heb.kpi.conformity': "Inspection compliance",
    'heb.kpi.conformityNote': "rooms compliant at the last inspection",
    'heb.campaigns.title': "Housing campaigns",
    'heb.campaigns.sub': "Applications, open places, allocation rules",
    'heb.campaigns.new': "Launch a campaign",
    'heb.campaigns.current': "Current",
    'heb.campaigns.scheduled': "Scheduled",
    'heb.campaigns.closed': "Closed",
    'heb.campaigns.empty': "No campaign here.",
    'heb.campaigns.moveIn': "move-in on {date}",
    'heb.campaign.edit': "Edit the campaign",
    'heb.campaign.to.open': "Open applications",
    'heb.campaign.to.processing': "Close applications and process",
    'heb.campaign.to.closed': "Close the campaign",
    'heb.campaign.toText.open': "Students of the open levels will be able to apply on the portal.",
    'heb.campaign.toText.processing': "Applications close: you can then allocate beds and publish the results.",
    'heb.campaign.toText.closed': "A closed campaign can no longer be changed or reopened.",
    'heb.campaign.publish': "Publish the results",
    'heb.campaign.publishText': "Each student receives the offered bed, or their waiting-list place, with the campaign message.",
    'heb.campaign.published': "{count} student(s) notified.",
    'heb.campaign.moved': "The campaign moved to its next stage.",
    'heb.campaign.saved': "Campaign saved.",
    'heb.campaign.views': "Campaign views",
    'heb.campaign.tab.follow': "Overview",
    'heb.campaign.tab.applications': "Applications",
    'heb.campaign.tab.allocation': "Allocation",
    'heb.campaignForm.new': "Launch a campaign",
    'heb.campaignForm.edit': "Edit the campaign",
    'heb.campaignForm.step': "Step {n} of 6: {label}",
    'heb.campaignForm.steps.calendar': "Calendar",
    'heb.campaignForm.steps.publics': "Audience",
    'heb.campaignForm.steps.places': "Places and quotas",
    'heb.campaignForm.steps.documents': "Documents",
    'heb.campaignForm.steps.rules': "Allocation rules",
    'heb.campaignForm.steps.messages': "Messages",
    'heb.campaignForm.label': "Title",
    'heb.campaignForm.description': "Description on the portal",
    'heb.campaignForm.opensOn': "Applications open",
    'heb.campaignForm.closesOn': "Applications close",
    'heb.campaignForm.processingOn': "Results from",
    'heb.campaignForm.moveInOn': "Move-in",
    'heb.campaignForm.stayEndsOn': "End of stay",
    'heb.campaignForm.publicsHint': "The levels allowed to apply. None ticked: all levels.",
    'heb.campaignForm.noLevels': "No level in the reference data.",
    'heb.campaignForm.addPlace': "Open a place",
    'heb.campaignForm.noEstate': "First add campuses and rooms to the estate.",
    'heb.campaignForm.quotas': "Quotas",
    'heb.campaignForm.quotasHint': "A share of the open beds kept for a group.",
    'heb.campaignForm.quotaLabel': "Quota title",
    'heb.campaignForm.criterion': "Criterion",
    'heb.campaignForm.percent': "Share",
    'heb.campaignForm.addQuota': "Add a quota",
    'heb.campaignForm.docLabel': "Document",
    'heb.campaignForm.condition': "Condition",
    'heb.campaignForm.conditionHint': "For instance \"if scholarship holder\": the document is then not required from everyone.",
    'heb.campaignForm.required': "Required",
    'heb.campaignForm.addDoc': "Add a document",
    'heb.campaignForm.defaultDoc': "Copy of the ID card",
    'heb.campaignForm.weights': "Points per criterion",
    'heb.campaignForm.rules': "Rules",
    'heb.campaignForm.reserved': "Beds kept in reserve",
    'heb.campaignForm.reservedHint': "For special cases, allocated by hand.",
    'heb.campaignForm.provisional': "Provisional allocation message",
    'heb.campaignForm.provisionalHint': "Sent with the offered bed: acceptance deadline, payment.",
    'heb.campaignForm.final': "Final allocation message",
    'heb.campaignForm.finalHint': "Sent once fees are paid: key handover appointment.",
    'heb.follow.submitted': "Applications submitted",
    'heb.follow.submittedNote': "{count} to process",
    'heb.follow.beds': "Beds occupied",
    'heb.follow.bedsNote': "{count} free bed(s)",
    'heb.follow.payments': "Payments due",
    'heb.follow.paymentsNote': "{count} receipt(s) to validate",
    'heb.follow.waitlisted': "Waiting list",
    'heb.follow.waitlistedNote': "served whenever a bed is released",
    'heb.follow.flow': "Application flow",
    'heb.follow.byCampus': "Occupancy by campus",
    'heb.follow.todo': "To do",
    'heb.follow.todoApplications': "{count} application(s) to review",
    'heb.follow.todoIncomplete': "{count} waiting for a document",
    'heb.follow.todoReceipts': "{count} payment receipt(s) to validate",
    'heb.follow.todoLate': "{count} overdue payment(s)",
    'heb.follow.todoKeys': "{count} final allocation(s), keys to hand over",
    'heb.follow.moveIn': "move-in on {date}",
    'heb.follow.setup': "Campaign setup",
    'heb.follow.stay': "from {from} to {to} · {months} months",
    'heb.flow.submitted': "Submitted",
    'heb.flow.eligible': "Eligible",
    'heb.flow.incomplete': "Document requested",
    'heb.flow.provisional': "Bed offered",
    'heb.flow.final': "Final",
    'heb.flow.waitlisted': "Waiting",
    'heb.applications.views': "Application views",
    'heb.applications.view.todo': "To process",
    'heb.applications.view.incomplete': "Document requested",
    'heb.applications.view.eligible': "Eligible",
    'heb.applications.view.allocated': "Allocated",
    'heb.applications.view.waitlisted': "Waiting list",
    'heb.applications.view.rejected': "Rejected",
    'heb.applications.view.all': "All",
    'heb.applications.search': "Name, application number, student number",
    'heb.applications.empty': "No application here.",
    'heb.applications.enter': "Enter an application",
    'heb.applications.enterSubmit': "Submit the application",
    'heb.applications.note': "Applications come from the student portal; name, programme, level and scheme come from enrolment. Points follow the campaign criteria.",
    'heb.wish.campus': "Preferred campus",
    'heb.wish.type': "Room type",
    'heb.wish.type2': "Second choice",
    'heb.wish.roommate': "Preferred roommate",
    'heb.wish.homeCity': "Family home town",
    'heb.wish.distance': "Distance in km",
    'heb.wish.distanceHint': "From the family home to the school.",
    'heb.wish.emergency': "Emergency contact",
    'heb.wish.disability': "Disability",
    'heb.wish.disabilityHint': "An adapted room is offered first.",
    'heb.wish.needs': "Special needs",
    'heb.wish.rules': "I have read the housing rules and agree to follow them.",
    'heb.wish.rulesStaff': "The student has read and accepted the housing rules.",
    'heb.application.wishes': "Wishes",
    'heb.application.typeOr': "{first}, otherwise {second}",
    'heb.application.documents': "Documents",
    'heb.application.history': "History",
    'heb.application.treatment': "Processing",
    'heb.application.points': "Points",
    'heb.application.pointsSub': "Campaign criteria",
    'heb.application.noPoints': "Points are computed once the application is eligible.",
    'heb.application.assign': "Allocate a bed",
    'heb.application.release': "Release the bed",
    'heb.application.backToQueue': "The application goes back to the queue (otherwise, withdrawal)",
    'heb.application.bed': "The bed",
    'heb.application.roommates': "Roommates",
    'heb.application.period': "Stay",
    'heb.application.acceptBy': "Accept by",
    'heb.application.payBy': "Pay by",
    'heb.application.paid': "paid: {amount}",
    'heb.application.invoice': "invoice {number}",
    'heb.application.keys': "Key {key} handed over on {at}",
    'heb.application.released': "Bed released: {reason}",
    'heb.application.releasedDone': "The bed is released.",
    'heb.application.closed': "This application is closed.",
    'heb.application.born': "born on {date}",
    'heb.application.submittedOn': "submitted on {date}",
    'heb.review.eligible': "Declare eligible",
    'heb.review.incomplete': "Request a document",
    'heb.review.waitlisted': "Put on the waiting list",
    'heb.review.rejected': "Reject",
    'heb.review.eligibleText': "Points are computed; the student is notified.",
    'heb.review.incompleteText': "Say which document is missing: the student uploads it on the portal.",
    'heb.review.waitlistedText': "The application waits for a released bed.",
    'heb.review.rejectedText': "The reason is sent to the student.",
    'heb.review.note': "Note (optional)",
    'heb.review.reason': "Reason",
    'heb.review.done': "Decision saved.",
    'heb.event.draft': "Draft saved",
    'heb.event.submitted': "Application submitted",
    'heb.event.eligible': "Declared eligible",
    'heb.event.incomplete': "Document requested",
    'heb.event.waitlisted': "Put on the waiting list",
    'heb.event.rejected': "Rejected",
    'heb.event.proposed': "Bed offered",
    'heb.event.accepted': "Bed accepted",
    'heb.event.declined': "Bed declined",
    'heb.event.receipt': "Payment receipt uploaded",
    'heb.event.receipt_refused': "Receipt refused",
    'heb.event.paid': "Payment validated",
    'heb.event.final': "Final allocation",
    'heb.event.keys': "Keys handed over",
    'heb.event.left': "Checked out",
    'heb.event.moved': "Bed change",
    'heb.event.released': "Bed released",
    'heb.event.withdrawn': "Withdrawal",
    'heb.event.file_accepted': "Document accepted",
    'heb.event.file_rejected': "Document refused",
    'heb.event.file_pending': "Document to review",
    'heb.files.see': "View",
    'heb.files.uploading': "Uploading…",
    'heb.files.photo': "Take or choose a photo",
    'heb.files.drop': "Upload a file (PDF, PNG, JPEG, 10 MB max)",
    'heb.files.remove': "Remove",
    'heb.files.accept': "Accept",
    'heb.files.refuse': "Refuse the document",
    'heb.files.accepted': "Document accepted.",
    'heb.files.refused': "Document refused; the student is notified.",
    'heb.files.missing': "Not uploaded yet.",
    'heb.files.other': "Other files",
    'heb.alloc.mode': "Allocation mode",
    'heb.alloc.auto': "Automatic",
    'heb.alloc.manual': "Manual",
    'heb.alloc.criteria': "Criteria and points",
    'heb.alloc.criteriaSub': "Applications are served by points",
    'heb.alloc.rules': "Rules applied",
    'heb.alloc.reserved': "{count} bed(s) kept in reserve",
    'heb.alloc.waiting': "Applications to place",
    'heb.alloc.waitingNote': "eligible, without a bed",
    'heb.alloc.placed': "Placed",
    'heb.alloc.placedNote': "including {roommates} roommate pair(s) kept",
    'heb.alloc.unplaced': "Without a bed",
    'heb.alloc.unplacedNote': "places {cap} · quotas {quota} · rooms {cabin}",
    'heb.alloc.seed': "Draw",
    'heb.alloc.simulatedAt': "simulated on {at}",
    'heb.alloc.preview': "Simulation",
    'heb.alloc.previewSub': "Nothing is saved before validation",
    'heb.alloc.reseed': "New draw",
    'heb.alloc.validate': "Validate the {count} offered beds",
    'heb.alloc.validateText': "Beds become provisional allocations; each student sees theirs on the portal and must accept in time.",
    'heb.alloc.done': "{count} bed(s) offered.",
    'heb.alloc.none': "No eligible application to place.",
    'heb.alloc.with': "with {name}",
    'heb.alloc.autoNote': "With equal points, the draw decides; it is kept with the campaign to reproduce the same allocation.",
    'heb.alloc.queue': "The queue",
    'heb.alloc.queueSub': "{count} application(s) without a bed",
    'heb.alloc.queueEmpty': "Every eligible application has a bed.",
    'heb.alloc.pick': "Pick an application",
    'heb.alloc.pickSub': "Compatible beds appear here",
    'heb.alloc.wishes': "Wishes: {campus} · {type}, otherwise {type2}",
    'heb.alloc.noBeds': "No compatible free bed.",
    'heb.alloc.emptyRoom': "empty room",
    'heb.alloc.assign': "Allocate",
    'heb.alloc.assigned': "Bed offered to {name}.",
    'heb.alloc.openApplication': "Open the application",
    'heb.payments.title': "Housing fees",
    'heb.payments.sub': "An accepted allocation becomes final once fees are paid",
    'heb.payments.record': "Record a counter payment",
    'heb.payments.recordSub': "Cash, cheque, transfer or deposit received by the school",
    'heb.payments.recordSubmit': "Record the payment",
    'heb.payments.recorded': "Payment recorded.",
    'heb.payments.amount': "Amount (DH)",
    'heb.payments.amountHint': "For instance 850 or 850.50.",
    'heb.payments.method': "Payment method",
    'heb.payments.reference': "Reference",
    'heb.payments.paidOn': "Payment date",
    'heb.payments.left': "{amount} left of {total}",
    'heb.payments.receiptsNote': "uploaded by students",
    'heb.payments.lateNote': "beyond that, the bed is released",
    'heb.payments.paidNote': "final allocations",
    'heb.payments.view.receipts': "Receipts to validate",
    'heb.payments.view.due': "To pay",
    'heb.payments.view.late': "Overdue",
    'heb.payments.view.paid': "Paid",
    'heb.payments.view.all': "All",
    'heb.payments.state.receipt': "Receipt to validate",
    'heb.payments.state.refused': "Receipt refused",
    'heb.payments.state.paid': "Paid",
    'heb.payments.state.late': "Overdue",
    'heb.payments.state.part': "Partly paid",
    'heb.payments.state.due': "To pay",
    'heb.payments.receiptAt': "receipt uploaded on {at}",
    'heb.payments.payBy': "by {date}",
    'heb.payments.examine': "Review",
    'heb.payments.collect': "Collect",
    'heb.payments.empty': "Nothing here.",
    'heb.payments.receipt': "The receipt",
    'heb.payments.noReceipt': "Pick a receipt to validate in the list.",
    'heb.payments.receiptOf': "Receipt from {name}",
    'heb.payments.declared': "Declared amount",
    'heb.payments.leftToPay': "Left to pay",
    'heb.payments.validate': "Validate the payment",
    'heb.payments.refuse': "Refuse the receipt",
    'heb.payments.refuseHint': "The student receives the reason and uploads another receipt.",
    'heb.payments.refused': "Receipt refused; the student is notified.",
    'heb.payments.validated': "Payment validated.",
    'heb.payments.validatedFinal': "Payment validated: {name}'s allocation is final.",
    'heb.payments.financeNote': "With the Finance module, validation records the payment on the student's invoice.",
    'heb.payments.finalNote': "Once fully paid, the allocation becomes final and the student is called for the key handover.",
    'heb.payments.note': "No online payment: the student uploads a receipt (transfer, deposit) and an agent validates it, or the agent records a counter payment. After the grace period, the bed returns to the campaign.",
    'heb.price.rent': "Rent, {months} months",
    'heb.price.rentTimes': "Rent, {months} months × {rent}",
    'heb.price.deposit': "Deposit",
    'heb.price.fee': "Application fee",
    'heb.price.toPay': "To pay",
    'heb.stays.title': "Check-ins and check-outs",
    'heb.stays.sub': "Key handover, inventories, deposit refund",
    'heb.stays.print': "Print the appointments",
    'heb.stays.view.entries': "Check-ins",
    'heb.stays.view.occupants': "Keys out",
    'heb.stays.view.exits': "Check-outs",
    'heb.stays.empty.entries': "No upcoming check-in.",
    'heb.stays.empty.occupants': "No occupant.",
    'heb.stays.empty.exits': "No check-out.",
    'heb.stays.search': "Name, bed, key",
    'heb.stays.owing': "{amount} left to pay",
    'heb.stays.retained': "retained {amount}",
    'heb.stays.payments': "Payments",
    'heb.keys.give': "Hand over the keys",
    'heb.keys.giveShort': "Hand over",
    'heb.keys.return': "Record the check-out",
    'heb.keys.returnShort': "Check-out",
    'heb.keys.label': "Key or badge handed over",
    'heb.keys.labelHint': "The number engraved on the key, or the badge number.",
    'heb.keys.sheet': "Inventory",
    'heb.keys.noChecklist': "No active inventory checklist: add one in the settings.",
    'heb.keys.note': "Notes",
    'heb.keys.photos': "Photos",
    'heb.keys.signed': "The student has read and signed the inventory",
    'heb.keys.given': "Keys handed over.",
    'heb.keys.returned': "Check-out recorded.",
    'heb.keys.deposit': "deposit {amount}",
    'heb.keys.retained': "Deduction from the deposit (DH)",
    'heb.keys.retainedHint': "Empty: the full deposit is refunded. At most {amount} DH; a deduction needs notes.",
    'heb.visits.title': "Inspections",
    'heb.visits.sub': "Scheduled or unannounced, room by room, with findings and photos",
    'heb.visits.view.planned': "Upcoming",
    'heb.visits.state.planned': "Scheduled",
    'heb.visits.view.followup': "Follow-up needed",
    'heb.visits.state.followup': "Follow-up",
    'heb.visits.view.done': "Done",
    'heb.visits.state.done': "Done",
    'heb.visits.empty': "No inspection here.",
    'heb.visits.announced': "announced",
    'heb.visits.surprise': "unannounced",
    'heb.visits.plan': "Schedule an inspection",
    'heb.visits.planSubmit': "Schedule the inspection",
    'heb.visits.where': "Place",
    'heb.visits.whereHint': "A block or floor of the campus.",
    'heb.visits.wholeCampus': "Whole campus",
    'heb.visits.at': "Date and time",
    'heb.visits.agent': "Agent",
    'heb.visits.agentHint': "Who will carry out the inspection.",
    'heb.visits.me': "Me",
    'heb.visits.checklist': "Checklist",
    'heb.visits.checklistItems': "{label} ({count} points)",
    'heb.visits.announce': "Notify the occupants",
    'heb.visits.announceHint': "Unticked: unannounced inspection.",
    'heb.visit.title': "Inspection · {where}",
    'heb.visit.progress': "{seen} / {total} rooms seen",
    'heb.visit.finish': "Finish the inspection",
    'heb.visit.finishText': "{seen} room(s) seen out of {total}. A finished inspection can no longer be changed.",
    'heb.visit.finished': "Inspection finished.",
    'heb.visit.rooms': "Rooms",
    'heb.visit.toSee': "to see",
    'heb.visit.free': "free",
    'heb.visit.pick': "Pick a room",
    'heb.visit.pickHint': "The checklist appears for the chosen room.",
    'heb.visit.room': "Room {number}",
    'heb.visit.note': "Notes",
    'heb.visit.present': "Occupants present",
    'heb.visit.photos': "Photos",
    'heb.visit.addPhoto': "Add a photo",
    'heb.visit.followups': "Follow-up",
    'heb.visit.repair': "Create a repair for this room",
    'heb.visit.warning': "Warn the occupants",
    'heb.visit.warningHint': "They receive a notification with the notes.",
    'heb.visit.noFollowup': "No follow-up.",
    'heb.visit.save': "Save",
    'heb.visit.next': "Next room",
    'heb.visit.saved': "Finding saved.",
    'heb.visit.back': "Rooms",
    'heb.repairs.title': "Repairs",
    'heb.repairs.sub': "Reported by a student, an inspection or staff, until closure",
    'heb.repairs.counts': "{open} open · {urgent} urgent · {out} bed(s) out of service",
    'heb.repairs.report': "Report a repair",
    'heb.repairs.reportSubmit': "Report",
    'heb.repairs.search': "Room, subject, contractor",
    'heb.repairs.col.reported': "Reported",
    'heb.repairs.col.planned': "Planned",
    'heb.repairs.col.in_progress': "In progress",
    'heb.repairs.col.done': "Done, to check",
    'heb.repairs.col.closed': "Closed",
    'heb.repairs.colEmpty': "None.",
    'heb.repairs.since': "since {at}",
    'heb.repairs.out': "out of service",
    'heb.repairs.subject': "Subject",
    'heb.repairs.description': "Description",
    'heb.repairs.photos': "Photos",
    'heb.repairs.access': "Staff may enter when occupants are away",
    'heb.repairs.bedHint': "Leave empty if the whole room is concerned.",
    'heb.repairs.wholeRoom': "Whole room",
    'heb.repair.roomOf': "Room {room}",
    'heb.repair.plan': "Plan",
    'heb.repair.planTitle': "Plan the intervention",
    'heb.repair.planSubmit': "Plan",
    'heb.repair.planSaved': "Intervention planned; the reporter is notified.",
    'heb.repair.assignee': "Technician",
    'heb.repair.assigneeHint': "A school technician, or a contractor below.",
    'heb.repair.provider': "Contractor",
    'heb.repair.plannedAt': "Planned for",
    'heb.repair.to.in_progress': "Start",
    'heb.repair.to.done': "Mark as done",
    'heb.repair.to.closed': "Check and close",
    'heb.repair.moved': "Repair updated.",
    'heb.repair.bedOut': "Put the bed out of service",
    'heb.repair.roomOut': "Put the room out of service",
    'heb.repair.serveAgain': "Back in service",
    'heb.repair.putOut': "Put out of service.",
    'heb.repair.backIn': "Back in service.",
    'heb.repair.finding': "The finding",
    'heb.repair.reportedBy': "{name}, on {at} · {source}",
    'heb.repair.noDescription': "No description.",
    'heb.repair.fromVisit': "Found during the inspection of {at}",
    'heb.repair.intervention': "Intervention",
    'heb.repair.who': "Who",
    'heb.repair.accessLabel': "Access",
    'heb.repair.accessYes': "allowed when occupants are away",
    'heb.repair.accessNo': "with occupants present",
    'heb.repair.outLabel': "Out of service",
    'heb.repair.wholeRoom': "whole room",
    'heb.repair.costTitle': "Cost and charge",
    'heb.repair.cost': "Cost (DH)",
    'heb.repair.charge': "Charged to",
    'heb.repair.chargeInstitution': "Paid by the housing service",
    'heb.repair.chargeOccupant': "Damage: deducted from the occupant's deposit",
    'heb.repair.occupant': "Occupant",
    'heb.repair.costSave': "Save the cost",
    'heb.repair.costSaved': "Cost saved.",
    'heb.repair.follow': "Progress",
    'heb.estate.title': "Estate and prices",
    'heb.estate.sub': "Campuses, blocks, floors, bed-by-bed occupancy",
    'heb.estate.tab': "Campuses and rooms",
    'heb.estate.tree': "The estate",
    'heb.estate.empty': "No campus: add one.",
    'heb.estate.beds': "{count} beds",
    'heb.estate.noBlock': "No block",
    'heb.estate.noFloor': "No floor",
    'heb.estate.allTypes': "All types",
    'heb.estate.occupied': "occupied",
    'heb.estate.free': "free",
    'heb.estate.provisional': "provisional allocation",
    'heb.estate.out': "out of service",
    'heb.estate.full': "full",
    'heb.estate.freeBeds': "{count} free bed(s)",
    'heb.estate.noRooms': "No room here.",
    'heb.estate.summary': "{where} · {beds} beds · {occupied} occupied · {out} out of service",
    'heb.estate.newCampus': "Add a campus",
    'heb.estate.campusSaved': "Campus saved.",
    'heb.room.new': "Add a room",
    'heb.room.newHint': "It gets the beds of its type (B-204-1, B-204-2…).",
    'heb.room.edit': "Edit the room",
    'heb.room.saved': "Room saved.",
    'heb.room.title': "Room {number}",
    'heb.room.number': "Number",
    'heb.room.block': "Block",
    'heb.room.floor': "Floor",
    'heb.room.gender': "Gender",
    'heb.room.surface': "Area (m²)",
    'heb.room.equipment': "Equipment",
    'heb.room.equipmentHint': "One per line, or separated by commas.",
    'heb.room.note': "Note",
    'heb.room.typeBeds': "{label} ({count} bed(s))",
    'heb.room.beds': "Beds",
    'heb.room.bedsHint': "A bed already used cannot be removed: put it out of service.",
    'heb.room.bedsSub': "{occupied} occupied, {free} free",
    'heb.room.nature': "Type",
    'heb.room.outOfService': "Out of service",
    'heb.room.addBed': "Add a bed",
    'heb.room.putOut': "Put out of service",
    'heb.room.history': "History",
    'heb.room.noHistory': "No history yet.",
    'heb.room.historyStay': "Keys handed to {name}, bed {bed}",
    'heb.room.historyLeft': "{name} checked out, bed {bed}",
    'heb.room.historyVisit': "Inspection: {label}",
    'heb.room.historyRepair': "Repair: {label}",
    'heb.room.about': "The room",
    'heb.room.capacity': "Capacity",
    'heb.room.place': "Location",
    'heb.room.repairs': "Repairs",
    'heb.room.noRepairs': "No repair.",
    'heb.room.delete': "Delete the room",
    'heb.room.deleteText': "A room never allocated can be deleted; otherwise, put it out of service.",
    'heb.prices.title': "Prices",
    'heb.prices.sub': "The most specific rule wins: campus, room type, level, scheme",
    'heb.prices.grid': "The grid",
    'heb.prices.gridSub': "Amounts in dirhams · an \"All\" criterion applies to every value",
    'heb.prices.empty': "No price for this year.",
    'heb.prices.new': "Add a price",
    'heb.prices.edit': "Edit the price",
    'heb.prices.dialogHint': "A criterion left at \"All\" applies to every value.",
    'heb.prices.rent': "Monthly rent",
    'heb.prices.saved': "Price saved.",
    'heb.prices.delete': "Delete the price",
    'heb.prices.deleteText': "Allocations already offered keep their amount.",
    'heb.prices.deleted': "Price deleted.",
    'heb.prices.coverage': "Coverage check",
    'heb.prices.coverageSub': "Open beds without a price: their applications cannot be allocated",
    'heb.prices.covered': "Every open bed has a price.",
    'heb.prices.combination': "Combination without a price",
    'heb.prices.create': "Create the price",
    'heb.prices.simulate': "Simulate a price",
    'heb.prices.simulateSub': "What an application will pay for a bed",
    'heb.prices.application': "Application",
    'heb.prices.ruleUsed': "Rule used: {label}",
    'heb.prices.noRule': "No price applies.",
    'heb.prices.note': "Payment can be split into instalments (settings). With the Finance module, fees become a student invoice.",
    'heb.settings.title': "Housing settings",
    'heb.settings.sub': "Room types, checklists, repair categories, deadlines and payments",
    'heb.settings.module': "The module",
    'heb.settings.on': "Student housing module enabled",
    'heb.settings.off': "Student housing module disabled",
    'heb.settings.since': "Enabled on {at} by {by}.",
    'heb.settings.moduleHint': "When disabled, the module leaves the menu and the portal, without deleting anything.",
    'heb.settings.enabled': "Module enabled.",
    'heb.settings.disabled': "Module disabled.",
    'heb.settings.financeOn': "Finance on: fees are invoiced",
    'heb.settings.financeOff': "Finance off: payments stay in housing",
    'heb.settings.delays': "Deadlines and payments",
    'heb.settings.acceptDays': "Time to accept an offered bed",
    'heb.settings.payDays': "Time to pay after acceptance",
    'heb.settings.graceDays': "Grace period",
    'heb.settings.days': "In days.",
    'heb.settings.instalments': "Instalments",
    'heb.settings.instalmentsHint': "For instance: deposit and two months, then quarterly.",
    'heb.settings.autoRelease': "Release the bed automatically after the grace period",
    'heb.settings.refundDeposit': "Refund the deposit at check-out, minus inventory deductions",
    'heb.settings.save': "Save",
    'heb.settings.saved': "Settings saved.",
    'heb.settings.roomTypes': "Room types",
    'heb.settings.campuses': "Campuses",
    'heb.settings.categories': "Repair categories",
    'heb.settings.providers': "Contractors",
    'heb.settings.providersSub': "Or a school technician",
    'heb.settings.noProviders': "No contractor.",
    'heb.settings.checklists': "Checklists",
    'heb.settings.checklistsSub': "For inspections and inventories",
    'heb.settings.points': "{count} points",
    'heb.settings.inactive': "inactive",
    'heb.settings.active': "Active",
    'heb.settings.label': "Label",
    'heb.settings.beds': "Number of beds",
    'heb.settings.name': "Name",
    'heb.settings.code': "Code",
    'heb.settings.address': "Address",
    'heb.settings.trades': "Trades",
    'heb.settings.phone': "Phone",
    'heb.settings.email': "Email",
    'heb.settings.checklistKind': "Use",
    'heb.settings.items': "Checkpoints",
    'heb.settings.itemsHint': "One point per line, 120 characters at most.",
    'heb.settings.roomtype.new': "Add a room type",
    'heb.settings.roomtype.edit': "Edit the room type",
    'heb.settings.category.new': "Add a category",
    'heb.settings.category.edit': "Edit the category",
    'heb.settings.provider.new': "Add a contractor",
    'heb.settings.provider.edit': "Edit the contractor",
    'heb.settings.checklist.new': "Add a checklist",
    'heb.settings.checklist.edit': "Edit the checklist",
    'heb.settings.campus.new': "Add a campus",
    'heb.settings.campus.edit': "Edit the campus",
    'heb.settings.rights': "Permissions: housing.read, housing.manage (campaigns, applications, allocation, estate), housing.pay (payments), housing.keys (check-ins and check-outs), housing.inspect (inspections), housing.repair (repairs), housing.configure, and housing.own for students.",
    'heb.mine.title': "My housing",
    'heb.mine.myApplication': "My application",
    'heb.mine.complete': "Complete application {number}",
    'heb.mine.fromEnrolment': "Your name, programme, level and scholarship status come from your enrolment: the school corrects them if needed.",
    'heb.mine.alreadyFiled': "{label}: {count} file(s) already uploaded",
    'heb.mine.pieces': "Documents to attach",
    'heb.mine.saveDraft': "Save the draft",
    'heb.mine.submit': "Submit my application",
    'heb.mine.submitted': "Application submitted: you will be notified of what follows.",
    'heb.mine.draftSaved': "Draft saved.",
    'heb.mine.closes': "Applications until",
    'heb.mine.results': "Results from",
    'heb.mine.youWillPay': "What you will pay",
    'heb.mine.pickToSee': "Choose a campus and a room type",
    'heb.mine.pricesHint': "The amount appears according to your wish.",
    'heb.mine.payAfter': "Depending on the bed allocated; payable after allocation.",
    'heb.mine.where': "Where my application stands",
    'heb.track.submitted': "Submitted",
    'heb.track.eligible': "Eligible",
    'heb.track.provisional': "Bed offered",
    'heb.track.accepted': "Accepted",
    'heb.track.final': "Payment validated",
    'heb.track.housed': "Keys handed over",
    'heb.mine.accept': "Accept this bed",
    'heb.mine.acceptText': "You commit to paying the fees in time; otherwise, the bed is released.",
    'heb.mine.acceptHint': "Accept by {date}, otherwise the bed goes to another student.",
    'heb.mine.decline': "Decline this bed",
    'heb.mine.declineText': "The bed returns to the campaign.",
    'heb.mine.receipt': "Upload my payment receipt",
    'heb.mine.receiptFile': "The receipt (transfer or deposit slip)",
    'heb.mine.receiptSubmit': "Upload the receipt",
    'heb.mine.receiptHint': "Pay by transfer or at the school counter, then upload the receipt: a finance agent validates it.",
    'heb.mine.receiptSent': "Receipt uploaded: you will be notified once it is validated.",
    'heb.mine.payHint': "Pay by {date}, otherwise the bed is released.",
    'heb.mine.finalHint': "Your bed is allocated: key handover from {date}.",
    'heb.mine.withdraw': "Withdraw",
    'heb.mine.withdrawText': "Your application closes and any bed returns to the campaign.",
    'heb.mine.reason': "Reason",
    'heb.mine.done.accept': "Bed accepted: upload your receipt once paid.",
    'heb.mine.done.decline': "Bed declined.",
    'heb.mine.done.withdraw': "Withdrawal recorded.",
    'heb.mine.myRoom': "My room",
    'heb.mine.key': "Key {key}",
    'heb.mine.keySince': "handed over on {at}",
    'heb.mine.payments': "My payments",
    'heb.mine.upToDate': "Up to date",
    'heb.mine.owing': "{amount} left to pay",
    'heb.mine.reports': "My reports",
    'heb.mine.problem': "Report a problem",
    'heb.mine.problemWhat': "What is wrong",
    'heb.mine.problemSubmit': "Send",
    'heb.mine.problemSent': "Report sent: you will be notified once it is fixed.",
    'heb.mine.access': "Staff may enter when I am away",
    'heb.mine.nothing': "No open campaign",
    'heb.mine.nothingHint': "You will be notified when the next housing campaign opens.",
    'heb.mine.note': "You get a notification at each step: bed offered, payment reminder, key handover appointment, announced inspection, repair done.",
    'rel.modules.housing': "Student housing",
    'rel.modules.housingHint': "Campaigns, applications, bed allocation, fees, keys, inspections and repairs",
    'journal.action.housing.settings_changed': "Housing settings changed",
    'journal.action.housing.estate_saved': "Housing estate or price saved",
    'journal.action.housing.campaign_saved': "Housing campaign saved",
    'journal.action.housing.application_decided': "Housing application decided",
    'journal.action.housing.allocation_changed': "Bed allocation changed",
    'journal.action.housing.payment_decided': "Housing payment decided",
    'journal.action.housing.keys_changed': "Keys handed over or returned",
    'journal.action.housing.upkeep_changed': "Housing inspection or repair",
    'journal.action.portal.rib_changed': "Teacher bank details proposed or verified",
    'journal.action.portal.hours_reported': "Hours discrepancy reported or handled",
    'tch.tabs.rolls': "Roll calls",
    'tch.kpi.today': "Sessions today",
    'tch.kpi.todayNote': "{count} roll call(s) to take",
    'tch.kpi.late': "Late roll calls",
    'tch.kpi.lateNote': "past sessions without roll call",
    'tch.kpi.marks': "Marks to enter",
    'tch.kpi.marksNote': "past assessments not validated",
    'tch.kpi.hours': "Hours taught",
    'tch.kpi.hoursNote': "of the {objective} objective",
    'tch.home.hours': "My hours",
    'tch.home.hoursLink': "Hours and contract",
    'tch.home.ofObjective': "taught of {objective}",
    'tch.home.hoursNote': "Planned ahead: {planned} · to plan: {toPlan} · this month: {month}",
    'tch.roll.take': "Take roll call",
    'tch.roll.done': "Roll call taken",
    'tch.todo.roll_missing': "late roll call",
    'tch.todo.marks_missing': "marks to enter or validate",
    'tch.todo.replacement': "replacement",
    'tch.todo.cancelled': "cancelled session",
    'tch.todo.requests': "{count} request(s) in progress",
    'tch.rolls.title': "Roll calls",
    'tch.rolls.sub': "My sessions of the last sixty days and their roll call",
    'tch.rolls.todo': "To take today",
    'tch.rolls.todoNote': "today's sessions",
    'tch.rolls.late': "Late",
    'tch.rolls.lateNote': "to take as soon as possible",
    'tch.rolls.done': "Taken",
    'tch.rolls.doneNote': "over sixty days",
    'tch.rolls.pending': "To take",
    'tch.rolls.doneTab': "Taken",
    'tch.rolls.all': "All",
    'tch.rolls.none': "No session here.",
    'tch.rolls.session': "Session",
    'tch.rolls.course': "Course",
    'tch.rolls.classes': "Classes",
    'tch.rolls.state': "Roll call",
    'tch.rolls.state.todo': "To take",
    'tch.rolls.state.late': "Late",
    'tch.rolls.state.done': "Taken",
    'tch.rolls.state.validated': "Validated by the registrar",
    'tch.rolls.counts': "{absent} absent, {late} late",
    'tch.rolls.fix': "Correct",
    'tch.rolls.locked': "locked",
    'tch.rolls.hint': "A roll call can be corrected until the registrar validates it.",
    'tch.classes.title': "My classes",
    'tch.classes.sub': "The classes I teach this year",
    'tch.classes.write': "Write to students",
    'tch.classes.none': "No class in your sessions or assessments this year.",
    'tch.classes.list': "My classes",
    'tch.classes.count': "{count} student(s)",
    'tch.classes.empty': "No student in this class.",
    'tch.classes.student': "Student",
    'tch.classes.presence': "Attendance",
    'tch.classes.unjustified': "Unexcused absences",
    'tch.classes.unvalidated': "* assessment not yet validated.",
    'tch.classes.hint': "You see your students' attendance in your sessions and their marks in your assessments, never their contact details.",
    'tch.hours.title': "Hours and contract",
    'tch.hours.report': "Report an hours discrepancy",
    'tch.hours.reported': "Hours discrepancy sent to planning.",
    'tch.hours.objective': "Objective",
    'tch.hours.objectiveNote': "contract objective hours",
    'tch.hours.planned': "Planned",
    'tch.hours.plannedNote': "{hours} left to plan",
    'tch.hours.realized': "Taught",
    'tch.hours.realizedNote': "{percent}% of the objective",
    'tch.hours.month': "This month",
    'tch.hours.monthNote': "{count} session(s)",
    'tch.hours.byMonth': "Month by month",
    'tch.hours.legendPlanned': "planned",
    'tch.hours.legendRealized': "taught",
    'tch.hours.bySubject': "By subject",
    'tch.hours.objectiveCol': "Objective",
    'tch.hours.realizedCol': "Taught",
    'tch.hours.contracts': "My contracts",
    'tch.hours.noContract': "No contract.",
    'tch.hours.period': "from {from} to {to}",
    'tch.hours.signed': "signed on {date}",
    'tch.hours.rate': "Hourly rate: {rate}",
    'tch.hours.download': "Download the contract",
    'tch.hours.reports': "My reports",
    'tch.hours.noReport': "No discrepancy reported.",
    'tch.hours.hint': "An hour counts as taught once the session's roll call is taken.",
    'tch.hours.reportSub': "Planning corrects the session or roll call, and answers you.",
    'tch.hours.send': "Send",
    'tch.hours.reportMonth': "The month",
    'tch.hours.reportExpected': "Hours you expect",
    'tch.hours.reportNote': "What is missing",
    'tch.hours.reportHint': "The month's counted hours are recorded on sending.",
    'tch.report.state.open': "Sent",
    'tch.report.state.corrected': "Corrected",
    'tch.report.state.dismissed': "Dismissed",
    'tch.pay.title': "My pay",
    'tch.pay.paid': "Paid this year",
    'tch.pay.paidNote': "{count} month(s) paid, net",
    'tch.pay.hours': "Hours this year",
    'tch.pay.hoursNote': "counted for pay",
    'tch.pay.rib': "Bank transfer",
    'tch.pay.ribNote': "verified bank details",
    'tch.pay.noRib': "no verified bank details",
    'tch.pay.byMonth': "Month by month",
    'tch.pay.none': "No pay month.",
    'tch.pay.month': "Month",
    'tch.pay.hoursCol': "Hours",
    'tch.pay.gross': "Gross",
    'tch.pay.net': "Net",
    'tch.pay.state': "Status",
    'tch.pay.stateOf.paid': "Paid",
    'tch.pay.stateOf.validated': "Validated",
    'tch.pay.stateOf.computed': "In progress",
    'tch.pay.stateOf.blocked': "Awaiting a roll call or a contract",
    'tch.pay.stateOf.nothing': "Nothing this month",
    'tch.pay.extra': "including {hours} beyond the package",
    'tch.pay.hint': "The current month is an estimate; the statement becomes final once validated by payroll.",
    'tch.profile.name': "Name",
    'tch.profile.number': "Staff number",
    'tch.profile.department': "Department",
    'tch.profile.fields': "Disciplinary fields",
    'tch.profile.since': "Teaching since",
    'tch.profile.identitySub': "A mistake? Tell human resources.",
    'tch.profile.diplomas': "Degrees",
    'tch.profile.locked': "Human resources edit them",
    'tch.profile.rib': "Bank details for pay",
    'tch.profile.ribSub': "New bank details apply once human resources verify them",
    'tch.profile.ribChange': "Change bank details",
    'tch.profile.ribCurrent': "Current bank details",
    'tch.profile.ribVerified': "Verified",
    'tch.profile.ribPending': "New bank details {rib} sent on {date}, being checked.",
    'tch.profile.ribRefused': "Your last bank details were refused: {reason}",
    'tch.profile.ribSent': "Bank details sent: human resources are checking them.",
    'tch.profile.ribDialogSub': "It replaces the old one after verification",
    'tch.profile.ribNew': "New bank details",
    'tch.profile.ribHint': "16 to 34 digits or letters; spaces are ignored.",
    'tch.profile.documents': "My documents",
    'tch.profile.contract': "Contract {year}",
    'tch.profile.unsigned': "not yet signed",
    'tch.bank.sub': "Proposed by the teacher from the portal",
    'tch.bank.pending': "Proposed bank details",
    'tch.bank.verify': "Verify",
    'tch.bank.refuse': "Refuse",
    'tch.bank.reason': "Reason",
    'tch.reports.title': "Reported hours discrepancies ({count})",
    'tch.reports.sub': "Correct the session or roll call, then answer the teacher",
    'tch.reports.teacher': "Teacher",
    'tch.reports.counted': "Counted",
    'tch.reports.expected': "Expected",
    'tch.reports.note': "What is missing",
    'tch.reports.handle': "Handle",
    'tch.reports.decision': "Decision",
    'tch.reports.answer': "Answer to the teacher",
    'tch.reports.answerHint': "Required to dismiss the report.",
    'tch.settings.students': "Students",
    'tch.settings.teachers': "Teachers",
    'tch.settings.rubrics': "Teachers' sections",
    'tch.settings.rubricsSub': "Timetable, roll calls and assessments always stay visible",
    'tch.settings.welcome': "Teachers' welcome message",
    'tch.settings.flag.classes': "My classes",
    'tch.settings.flag.photos': "Students' photos",
    'tch.settings.flag.hours': "Hours and contract",
    'tch.settings.flag.pay': "My pay",
    'tch.settings.flag.profile': "Editable contact and bank details",
    'tch.settings.rule.classes': "attendance in THEIR sessions and marks in THEIR assessments only",
    'tch.settings.rule.photos': "in « My classes »",
    'tch.settings.rule.hours': "objective, planned, taught, contracts; hours discrepancy report",
    'tch.settings.rule.pay': "hours, gross, net, PDF statement; off, the section disappears",
    'tch.settings.rule.profile': "bank details are verified by human resources; logged",
    'journal.action.portal.settings_changed': "Student portal settings changed",
    'journal.action.portal.profile_changed': "Contact details changed from the portal",
    'journal.action.portal.calendar_linked': "Timetable calendar link created",
    'port.tabs.home': "Home",
    'port.tabs.timetable': "Agenda",
    'port.tabs.marks': "Marks",
    'port.tabs.messages': "Messages",
    'port.tabs.more': "More",
    'port.home.hello': "Hello {name}",
    'port.home.today': "Today",
    'port.home.timetable': "Timetable",
    'port.home.noSession': "No session today.",
    'port.home.marks': "Latest marks",
    'port.home.allMarks': "All my marks",
    'port.home.todo': "To do",
    'port.home.nothingToDo': "Nothing to do for now.",
    'port.home.messages': "Notifications",
    'port.home.allMessages': "All notifications",
    'port.home.noMessage': "No notification.",
    'port.home.links': "Useful links",
    'port.kpi.average': "Provisional average",
    'port.kpi.averageNote': "{group} · {count} subject(s) of {total}",
    'port.kpi.attendance': "Attendance",
    'port.kpi.attendanceNote': "{count} unexcused absence(s)",
    'port.kpi.due': "Left to pay",
    'port.kpi.dueNext': "next instalment on {date}",
    'port.kpi.dueNone': "no instalment due",
    'port.kpi.credits': "Credits earned",
    'port.todo.justify': "{count} absence(s) to excuse",
    'port.todo.between': "from {from} to {to}",
    'port.todo.justifyAction': "Excuse",
    'port.todo.installment': "Instalment due {date}: {amount}",
    'port.todo.late': "Overdue since {date}: {amount}",
    'port.todo.see': "View",
    'port.todo.offer': "A bed is offered to you",
    'port.todo.offerNote': "{bed}, {campus} · accept before {date}",
    'port.todo.answer': "Answer",
    'port.todo.housingPay': "Housing fees: {amount}",
    'port.todo.housingPayNote': "{bed} · to pay before {date}",
    'port.todo.deposit': "Upload the receipt",
    'port.timetable.title': "Timetable",
    'port.timetable.range': "From {from} to {to}",
    'port.timetable.previous': "Previous period",
    'port.timetable.next': "Next period",
    'port.timetable.today': "Today",
    'port.timetable.addToCalendar': "Add to my calendar",
    'port.timetable.view': "View",
    'port.timetable.mode.week': "Week",
    'port.timetable.mode.day': "Day",
    'port.timetable.mode.list': "List",
    'port.timetable.hint': "Cancelled or moved sessions stay visible, with their new date.",
    'port.timetable.allDay': "All day",
    'port.timetable.cancelled': "Cancelled",
    'port.timetable.exam': "Assessment",
    'port.timetable.status': "Status",
    'port.timetable.movedFrom': "Originally planned on",
    'port.timetable.rooms': "Room",
    'port.timetable.teachers': "Teacher",
    'port.timetable.type': "Type",
    'port.calendar.title': "Add to my calendar",
    'port.calendar.sub': "Google Calendar, Outlook, Apple Calendar",
    'port.calendar.explain': "A personal link follows your timetable: paste it in your calendar (« Add calendar from URL »). It updates itself, from last week to the next eight weeks. Keep it to yourself.",
    'port.calendar.existing': "A link was created on {date}. Creating a new one disables the old one.",
    'port.calendar.create': "Create my link",
    'port.calendar.renew': "Create a new link",
    'port.calendar.link': "Calendar link",
    'port.calendar.copy': "Copy",
    'port.calendar.copied': "Copied",
    'port.calendar.once': "This link is shown only once: copy it now.",
    'port.marks.title': "Marks and results",
    'port.marks.tabMarks': "My marks",
    'port.marks.tabResults': "My results",
    'port.marks.groups': "Semesters",
    'port.marks.year': "Year",
    'port.marks.none': "No validated mark yet.",
    'port.marks.subject': "Subject",
    'port.marks.coefficient': "Coef.",
    'port.marks.assessments': "Assessments",
    'port.marks.average': "Average",
    'port.marks.groupAverage': "Provisional average · {group}",
    'port.marks.retake': "retake",
    'port.marks.abs': "ABS",
    'port.marks.hint': "A mark shows here once the teacher has validated the assessment. A mistake? Tell the teacher or file a request.",
    'port.marks.hintAverage': "A mark shows here once the teacher has validated the assessment. The average is provisional: what counts is the deliberation result. A mistake? Tell the teacher or file a request.",
    'port.results.none': "No closed deliberation yet.",
    'port.results.list': "My deliberations",
    'port.results.deliberation': "Deliberation",
    'port.results.average': "Average",
    'port.results.rank': "Rank",
    'port.results.credits': "Credits",
    'port.results.decision': "Decision",
    'port.results.closedOn': "closed on {date}",
    'port.results.transcript': "Transcript",
    'port.results.detail': "Detail by teaching unit",
    'port.results.element': "Unit",
    'port.results.status': "Result",
    'port.attendance.title': "Attendance",
    'port.attendance.none': "No roll call recorded for this year.",
    'port.attendance.rate': "Attendance rate",
    'port.attendance.absences': "Absences",
    'port.attendance.absencesNote': "{count} to excuse",
    'port.attendance.lates': "Late arrivals",
    'port.attendance.latesNote': "{minutes} minutes in all",
    'port.attendance.justified': "Excused",
    'port.attendance.justifiedNote': "absences covered by a proof",
    'port.attendance.toJustify': "{count} absence(s) await a proof.",
    'port.attendance.justifyAll': "Excuse these absences",
    'port.attendance.atDesk': "Hand your proof to the registrar.",
    'port.attendance.records': "Absences and late arrivals",
    'port.attendance.noRecord': "No absence and no late arrival.",
    'port.attendance.date': "Date",
    'port.attendance.session': "Session",
    'port.attendance.kind': "Type",
    'port.attendance.justification': "Excuse",
    'port.attendance.absent': "Absence",
    'port.attendance.late': "Late {minutes} min",
    'port.attendance.justify': "Excuse",
    'port.attendance.state.none': "-",
    'port.attendance.state.justified': "Excused",
    'port.attendance.state.pending': "Request in progress",
    'port.attendance.state.to_justify': "To excuse",
    'port.attendance.exams': "Absences from assessments",
    'port.attendance.bySubject': "By subject",
    'port.attendance.absentHours': "Hours absent",
    'port.attendance.unjustifiedRate': "Unexcused",
    'port.documents.title': "Documents",
    'port.documents.sub': "The documents the school issued to you",
    'port.documents.none': "No document published yet.",
    'port.documents.published': "published on {date}",
    'port.documents.download': "Download",
    'port.documents.verify': "Verify",
    'port.documents.qr': "Each document carries a QR code: whoever receives it can check it on the school's site, without an account.",
    'port.documents.ask': "Request a document",
    'port.documents.askSub': "Through Requests",
    'port.documents.askButton': "Request",
    'port.finance.title': "Finances",
    'port.finance.sub': "My account · {year}",
    'port.finance.billed': "Billed this year",
    'port.finance.billedNote': "before discounts and credit notes",
    'port.finance.paid': "Paid",
    'port.finance.paidNote': "{count} receipt(s)",
    'port.finance.due': "Left to pay",
    'port.finance.dueNote': "all years together",
    'port.finance.late': "Overdue",
    'port.finance.lateNote': "past instalments",
    'port.finance.lateNone': "no overdue instalment",
    'port.finance.schedule': "Payment schedule",
    'port.finance.noSchedule': "No instalment.",
    'port.finance.dueOn': "Due on",
    'port.finance.label': "Label",
    'port.finance.amount': "Amount",
    'port.finance.paidColumn': "Paid",
    'port.finance.state': "Status",
    'port.finance.installment': "instalment {position} of {count}",
    'port.finance.documents': "Invoices and receipts",
    'port.finance.noDocument': "No invoice.",
    'port.finance.invoice': "Invoice · {label}",
    'port.finance.receipt': "Receipt · {method}",
    'port.finance.howToPay': "How to pay",
    'port.finance.housing': "Upload a housing receipt",
    'port.finance.housingSub': "After a bank transfer or deposit; the cashier validates it.",
    'port.finance.housingDue': "{amount} left · before {date}",
    'port.finance.receiptPending': "Receipt of {amount} sent on {date}, being checked",
    'port.finance.deposit': "Upload a receipt",
    'port.profile.title': "My record",
    'port.profile.sub': "What the school knows about you",
    'port.profile.saved': "Contact details saved.",
    'port.profile.identity': "Identity",
    'port.profile.identitySub': "A mistake? Tell the registrar.",
    'port.profile.lastName': "Last name",
    'port.profile.firstNames': "First names",
    'port.profile.birthDate': "Date of birth",
    'port.profile.number': "Student number",
    'port.profile.nationalId': "National ID",
    'port.profile.massar': "Massar code",
    'port.profile.training': "Programme",
    'port.profile.since': "Enrolled since",
    'port.profile.coordinates': "My contact details",
    'port.profile.coordinatesSub': "You edit them yourself",
    'port.profile.coordinatesLocked': "The registrar edits them",
    'port.profile.edit': "Edit",
    'port.profile.phones': "Phones",
    'port.profile.emails': "Emails",
    'port.profile.addresses': "Addresses",
    'port.profile.contacts': "Parents and contacts",
    'port.profile.contactsSub': "Kept by the registrar",
    'port.profile.account': "My account",
    'port.profile.security': "Password, two-factor, language, photo",
    'port.profile.securityHint': "Your account photo changes in « My profile ».",
    'port.settings.title': "Student portal",
    'port.settings.sub': "What students see in « My space »",
    'port.settings.saved': "Settings saved.",
    'port.settings.off': "Modules off, their sections are hidden: {modules}.",
    'port.settings.module.finance': "Finance",
    'port.settings.module.requests': "Requests",
    'port.settings.module.stages': "Internships",
    'port.settings.module.housing': "Housing",
    'port.settings.module.communication': "Communication",
    'port.settings.rubrics': "Sections",
    'port.settings.rubricsSub': "A section switched off leaves the students' menu",
    'port.settings.moduleOff': "Module off",
    'port.settings.flag.timetable': "Timetable",
    'port.settings.flag.marks': "Assessment marks",
    'port.settings.flag.average': "Provisional average",
    'port.settings.flag.results': "Deliberation results",
    'port.settings.flag.rank': "Rank in the class",
    'port.settings.flag.attendance': "Attendance",
    'port.settings.flag.documents': "Documents",
    'port.settings.flag.finance': "Finances",
    'port.settings.flag.profile': "Editable contact details",
    'port.settings.rule.timetable': "sessions of their classes and groups; iCal link",
    'port.settings.rule.marks': "only assessments validated by the teacher",
    'port.settings.rule.average': "computed from validated marks, marked « provisional »",
    'port.settings.rule.results': "once the deliberation is closed",
    'port.settings.rule.rank': "with the results of the closed deliberation",
    'port.settings.rule.attendance': "roll calls taken; excuses through Requests",
    'port.settings.rule.documents': "documents published by the registrar",
    'port.settings.rule.finance': "schedule, invoices, receipts",
    'port.settings.rule.profile': "phone, email, address; every change is logged",
    'port.settings.home': "Home",
    'port.settings.welcome': "Welcome message",
    'port.settings.links': "Useful links",
    'port.settings.linkLabel': "Label",
    'port.settings.linkUrl': "Address",
    'port.settings.addLink': "Add a link",
    'port.settings.payment': "How to pay",
    'port.settings.paymentSub': "Shown in « Finances »: transfer, desk, hours",
    'port.settings.paymentInfo': "Payment terms",
    'port.settings.note': "The portal is not a separate module: it gathers for the student what each module already shows them, and its menu replaces, for them, the entries scattered across modules.",
    'journal.filter.group.housing': "Housing",
    'trace.table.housing_settings': "Housing settings",
    'trace.table.housing_campus': "Housing campus",
    'trace.table.housing_room_type': "Room type",
    'trace.table.housing_room': "Room",
    'trace.table.housing_bed': "Bed",
    'trace.table.housing_price': "Housing price",
    'trace.table.housing_campaign': "Housing campaign",
    'trace.table.housing_campaign_place': "Campaign place",
    'trace.table.housing_quota': "Campaign quota",
    'trace.table.housing_required_document': "Required document",
    'trace.table.housing_application': "Housing application",
    'trace.table.housing_allocation': "Bed allocation",
    'trace.table.housing_payment': "Housing payment",
    'trace.table.housing_checklist': "Checklist",
    'trace.table.housing_inspection': "Inspection",
    'trace.table.housing_inspection_room': "Inspected room",
    'trace.table.housing_provider': "Contractor",
    'trace.table.housing_repair_category': "Repair category",
    'trace.table.housing_repair': "Repair",
    'trace.table.housing_file': "Housing file",
    'trace.table.housing_event': "Housing application history",
    'violation.heb.accept_late': "The acceptance deadline has passed.",
    'violation.heb.application_locked': "This application can no longer be changed.",
    'violation.heb.bed_gender': "This room is not open to this gender.",
    'violation.heb.bed_out': "This bed is out of service.",
    'violation.heb.bed_taken': "This bed is already allocated.",
    'violation.heb.bed_used': "This bed has been used: put it out of service.",
    'violation.heb.campaign_closed': "A closed campaign can no longer be changed.",
    'violation.heb.campaign_not_open': "This campaign is not open for applications.",
    'violation.heb.campus_locked': "A room cannot change campus.",
    'violation.heb.document_required': "Attach this document.",
    'violation.heb.inspection_locked': "A scheduled inspection cannot be changed: schedule another one.",
    'violation.heb.no_beds': "A room has at least one bed.",
    'violation.heb.no_enrolment': "No active enrolment for the campaign year.",
    'violation.heb.no_places': "Open at least one place (campus and room type) first.",
    'violation.heb.no_price': "No price applies to this bed for this application.",
    'violation.heb.no_rooms': "No room at this place.",
    'violation.heb.not_eligible': "Only an eligible, submitted or waitlisted application gets a bed.",
    'violation.heb.not_final': "Keys are handed over after the final allocation (fees paid).",
    'violation.heb.not_for_level': "This campaign is not open to your level.",
    'violation.heb.not_housed': "Only an occupant can check out.",
    'violation.heb.not_provisional': "This bed is not awaiting an answer.",
    'violation.heb.not_releasable': "This allocation can no longer be released.",
    'violation.heb.nothing_to_pay': "No payment is due for this bed.",
    'violation.heb.payment_decided': "This receipt has already been processed.",
    'violation.heb.price_duplicate': "A rule already has exactly these criteria.",
    'violation.heb.receipt_required': "Attach the receipt.",
    'violation.heb.retained_over': "The deduction exceeds the deposit.",
    'violation.heb.room_used': "This room has been allocated: put it out of service.",
    'violation.heb.rules_required': "Accept the housing rules.",
    'violation.heb.too_many_files': "Too many files.",
    'heb.export.label': "Export",
    'heb.export.busy': "Exporting…",
    'heb.export.simulation': "Export the simulation",
    'heb.invoice.download': "Download the invoice",
    'heb.selectAll': "Select all",
    'heb.selectRow': "Select {name}",
    'heb.applications.treat': "Process the selection",
    'heb.applications.selected': "{count} application(s) selected",
    'heb.applications.decision': "Decision",
    'heb.applications.treatHint': "Applications already allocated or closed are skipped.",
    'heb.applications.treated': "{count} application(s) processed.",
    'heb.applications.treatedSkipped': "{count} application(s) processed, {skipped} skipped (already allocated or closed).",
    'heb.campaignForm.next': "Next",
    'heb.campaignForm.previous': "Previous",
    'heb.offline.waiting': "waiting to send",
    'heb.offline.photo': "on the phone",
    'heb.offline.queued': "No network: the finding is kept on the phone and will be sent once the connection is back.",
    'heb.offline.count': "{count} finding(s) waiting to be sent",
    'heb.offline.retry': "Send now",
    'heb.offline.sent': "{count} waiting finding(s) sent.",
    'heb.offline.failed': "{count} finding(s) refused by the server, to redo: {rooms}.",
    'heb.payments.remind': "Send a reminder",
    'heb.payments.remindCount': "Remind ({count})",
    'heb.payments.remindOne': "Remind",
    'heb.payments.reminded': "{count} student(s) reminded.",
    'heb.room.reportBed': "Report",
    'heb.repair.roomAt': "Room {room} · {campus}",
    'heb.repairs.assignee': "Assignee",
    'heb.repairs.allAssignees': "All assignees",
    'heb.repairs.staff': "School staff",
    'heb.event.reminded': "Payment reminder",
    'violation.heb.bad_selection': "Choose between 1 and 500 applications.",
    'rel.modules.skills': "Skills tracking",
    'rel.modules.skillsHint': "Frameworks, activities and evidence, quick grid, final levels, student profile, reports",
    'journal.action.skills.settings_changed': "Skills settings changed",
    'journal.action.skills.framework_saved': "Skill framework changed",
    'journal.action.skills.activity_saved': "Skills activity saved",
    'journal.action.skills.ratings_published': "Skill assessments published",
    'journal.action.skills.final_published': "Final skill level validated or published",
    'journal.action.skills.deposit_submitted': "Skill evidence submitted",
    'violation.skl.scale_size': "A scale has 2 to 8 levels.",
    'violation.skl.conversion_size': "One mark per level of the scale.",
    'violation.skl.conversion_range': "A mark from 0 to 20.",
    'violation.skl.conversion_order': "Marks rise with the levels.",
    'violation.skl.scale_locked': "This framework is already in use: it keeps its number of levels.",
    'violation.skl.marks_off': "The link with marks is off in the settings.",
    'violation.skl.teacher_scope': "A teacher attaches a framework to one of their courses.",
    'violation.skl.unknown_code': "This code is not in this year's catalogue.",
    'violation.skl.empty_framework': "A framework without skills cannot be published.",
    'violation.skl.framework_used': "Only an unused draft can be deleted; archive it.",
    'violation.skl.node_used': "Already assessed: this item can no longer be deleted.",
    'violation.skl.not_yours': "This class or subject is not one of yours.",
    'violation.skl.activity_locked': "Already assessed: the class and subject can no longer change.",
    'violation.skl.activities_off': "Only graded assessments can be linked here.",
    'violation.skl.no_skill': "Target at least one skill.",
    'violation.skl.framework_no_marks': "A targeted framework does not convert its levels into marks.",
    'violation.skl.deposits_off': "Submissions are off in the settings.",
    'violation.skl.activity_used': "Already assessed or submitted: the activity can no longer be deleted.",
    'violation.skl.no_assessment': "The linked assessment cannot be found.",
    'violation.skl.assessment_validated': "The linked assessment is validated: its marks can no longer change.",
    'violation.skl.file_empty': "The file is empty.",
    'violation.skl.file_too_big': "The file exceeds 5 MB.",
    'violation.skl.file_format': "This format is not accepted for this submission.",
    'violation.skl.deposit_closed': "The submission deadline has passed.",
    'skl.scope.all': "All programmes",
    'skl.scope.domain': "Field of study",
    'skl.scope.training': "Programme",
    'skl.scope.subject': "Course",
    'skl.state.draft': "Draft",
    'skl.state.published': "Published",
    'skl.state.archived': "Archived",
    'skl.rule.last': "Latest level",
    'skl.rule.frequent': "Most frequent level",
    'skl.rule.average': "Rounded average",
    'skl.rule.manual': "Manual entry",
    'skl.ruleHint.last': "the level of the most recent assessment",
    'skl.ruleHint.frequent': "on a tie, the most recent",
    'skl.ruleHint.average': "the first level counts 1, rounded to the nearest",
    'skl.ruleHint.manual': "the teacher sets the final level",
    'skl.kind.homework': "Homework",
    'skl.kind.practical': "Practical",
    'skl.kind.project': "Project",
    'skl.kind.assessment': "Graded assessment",
    'skl.format.pdf': "PDF",
    'skl.format.docx': "Word (.docx)",
    'skl.format.image': "Images (.jpg, .png)",
    'skl.format.xlsx': "Spreadsheets (.xlsx)",
    'skl.format.pptx': "Presentations (.pptx)",
    'skl.preset.four': "Four levels",
    'skl.preset.colors': "Three colours",
    'skl.preset.cefr': "CEFR (A1 to C2)",
    'skl.tone.danger': "Red",
    'skl.tone.warn': "Orange",
    'skl.tone.info': "Blue",
    'skl.tone.ok': "Green",
    'skl.tone.accent': "Indigo",
    'skl.tone.violet': "Purple",
    'skl.tone.teal': "Teal",
    'skl.tone.muted': "Grey",
    'skl.node.domain': "Skill domain",
    'skl.node.skill': "Key skill",
    'skl.node.indicator': "Indicator",
    'skl.node.add.domain': "Add a domain",
    'skl.node.add.skill': "Add a skill",
    'skl.node.add.indicator': "Add an indicator",
    'skl.node.label.domain': "Domain",
    'skl.node.label.skill': "Key skill",
    'skl.node.label.indicator': "Indicator or criterion",
    'skl.node.addConfirm': "Add",
    'skl.node.code': "Code",
    'skl.node.description': "Description",
    'skl.node.up': "Move up",
    'skl.node.down': "Move down",
    'skl.node.used': "Already assessed: it can no longer be deleted",
    'skl.level.none': "Not assessed yet",
    'skl.frameworks.title': "Skill frameworks",
    'skl.frameworks.sub': "Attached to a programme, a field of study or a course",
    'skl.frameworks.new': "New framework",
    'skl.frameworks.kpiPublished': "Published frameworks",
    'skl.frameworks.kpiPublishedNote': "{drafts} draft(s), {archived} archived",
    'skl.frameworks.kpiSkills': "Skills",
    'skl.frameworks.kpiSkillsNote': "in published frameworks",
    'skl.frameworks.kpiActivities': "Linked activities",
    'skl.frameworks.kpiActivitiesNote': "assessments and activities",
    'skl.frameworks.kpiTotal': "Frameworks",
    'skl.frameworks.kpiTotalNote': "all states",
    'skl.frameworks.search': "Search a framework",
    'skl.frameworks.filter.live': "Published and drafts",
    'skl.frameworks.filter.published': "Published",
    'skl.frameworks.filter.draft': "Drafts",
    'skl.frameworks.filter.archived': "Archived",
    'skl.frameworks.filter.all': "All",
    'skl.frameworks.none': "No framework here.",
    'skl.frameworks.name': "Framework",
    'skl.frameworks.attached': "Attached to",
    'skl.frameworks.content': "Content",
    'skl.frameworks.contentValue': "{domains} domain(s) · {skills} skill(s) · {indicators} indicator(s)",
    'skl.frameworks.scale': "Scale",
    'skl.frameworks.rule': "Final level",
    'skl.frameworks.state': "State",
    'skl.frameworks.author': "Created by",
    'skl.frameworks.byTeacher': "for their course",
    'skl.frameworks.counts': "counts in marks",
    'skl.frameworks.note': "The pedagogy team creates programme and field frameworks. A teacher can create one for their own course: the pedagogy team sees it and can take it over. A framework already in use is not deleted, it is archived.",
    'skl.framework.newTitle': "New framework",
    'skl.framework.newSub': "Name it, attach it, choose its scale; the tree comes next.",
    'skl.framework.publish': "Publish",
    'skl.framework.published': "Framework published: it can now be used by activities.",
    'skl.framework.duplicate': "Duplicate",
    'skl.framework.duplicated': "Copy created as a draft.",
    'skl.framework.takeOver': "Take over",
    'skl.framework.takenOver': "Framework taken over by the pedagogy team.",
    'skl.framework.archive': "Archive",
    'skl.framework.archived': "Framework archived: its levels remain.",
    'skl.framework.deleted': "Draft deleted.",
    'skl.framework.tree': "Tree",
    'skl.framework.treeSub': "Domain, key skill, indicators",
    'skl.framework.emptyTree': "No domain yet.",
    'skl.framework.skillsCount': "{count} skill(s)",
    'skl.framework.indicatorsCount': "{count} indicator(s)",
    'skl.framework.saved': "Framework saved.",
    'skl.framework.identity': "The framework",
    'skl.framework.name': "Name",
    'skl.framework.description': "Description",
    'skl.framework.scope': "Attached to",
    'skl.framework.domain': "Field of study",
    'skl.framework.training': "Programme",
    'skl.framework.subject': "Course",
    'skl.framework.choose': "Choose",
    'skl.framework.scale': "Mastery scale",
    'skl.framework.scaleSub': "From 2 to 8 levels, weakest first",
    'skl.framework.scaleLocked': "Already in use: the number of levels is fixed, their labels are not",
    'skl.framework.stepCode': "Level code",
    'skl.framework.stepLabel': "Level label",
    'skl.framework.stepTone': "Level colour",
    'skl.framework.newStep': "New level",
    'skl.framework.addStep': "Add a level",
    'skl.framework.rule': "Final level",
    'skl.framework.ruleNote': "Computed or not, the teacher validates the final level and can correct it before publishing.",
    'skl.framework.marks': "Link with marks",
    'skl.framework.marksSub': "An activity's mark is the average of its converted skills; it follows the usual marks workflow",
    'skl.framework.marksOff': "Off in the module settings",
    'skl.framework.marksLink': "Levels give a mark out of 20",
    'skl.framework.markOf': "Mark for « {level} »",
    'skl.framework.conversionNote': "Out of 20, scaled to the linked assessment. Marks rise with the levels.",
    'skl.framework.create': "Create the framework",
    'skl.activities.title': "Skills activities",
    'skl.activities.titleMine': "Skills",
    'skl.activities.sub': "Activities and assessments linked to skills · {year}",
    'skl.activities.frameworks': "Frameworks",
    'skl.activities.report': "Reports",
    'skl.activities.new': "New activity",
    'skl.activities.kpiOpen': "Open activities",
    'skl.activities.kpiOpenNote': "not published yet",
    'skl.activities.kpiDeposits': "Submissions received",
    'skl.activities.kpiDepositsNote': "{count} missing",
    'skl.activities.kpiRated': "Students assessed",
    'skl.activities.kpiRatedNote': "across all activities",
    'skl.activities.kpiFinals': "Final levels to validate",
    'skl.activities.kpiFinalsNote': "of my students",
    'skl.activities.class': "Class",
    'skl.activities.allClasses': "All classes",
    'skl.activities.list': "Activities",
    'skl.activities.none': "No activity yet.",
    'skl.activities.finals': "Final levels to validate",
    'skl.activities.finalsSub': "Assessed in my activities, without a final level",
    'skl.activities.finalsNone': "Nothing to validate.",
    'skl.activities.validate': "Validate",
    'skl.activities.noteMine': "You only see your activities and classes. A submission arrives as a notification; opening an activity opens the class grid.",
    'skl.activities.note': "Activities are created by teachers; the pedagogy team views them and validates final levels when needed.",
    'skl.activities.activity': "Activity",
    'skl.activities.teacher': "Teacher",
    'skl.activities.target': "Target",
    'skl.activities.deposits': "Submissions",
    'skl.activities.state': "State",
    'skl.activities.counted': "counts in marks",
    'skl.activities.skills': "{count} skill(s)",
    'skl.activities.depositCount': "{count} / {of} submitted",
    'skl.activities.noDeposit': "no submission",
    'skl.activities.published': "Published",
    'skl.activities.inProgress': "In progress · {count} / {of}",
    'skl.activities.toRate': "To assess",
    'skl.dialog.title': "New skills activity",
    'skl.dialog.edit': "Edit the activity",
    'skl.dialog.create': "Create",
    'skl.dialog.noClass': "No class or subject in your name this year (sessions or assessments).",
    'skl.dialog.classSubject': "Class and subject",
    'skl.dialog.kind': "Type",
    'skl.dialog.assessment': "Graded assessment to link",
    'skl.dialog.target': "Assessment receiving the mark",
    'skl.dialog.choose': "Choose",
    'skl.dialog.validated': "validated",
    'skl.dialog.label': "Activity",
    'skl.dialog.labelHint': "Empty: the assessment's label",
    'skl.dialog.heldOn': "Date",
    'skl.dialog.skills': "Targeted skills",
    'skl.dialog.search': "Search the class frameworks",
    'skl.dialog.noFramework': "No published framework applies to this class and subject.",
    'skl.dialog.deposit': "Require a file submission",
    'skl.dialog.formats': "Accepted files",
    'skl.dialog.due': "Deadline",
    'skl.dialog.depositNote': "5 MB at most per file. Students get a notification and see the request in « My skills ».",
    'skl.dialog.counted': "Count in marks",
    'skl.dialog.countedNote': "On publishing, each student gets the converted mark in the linked assessment; it is validated like any mark.",
    'skl.dialog.linkNote': "A graded assessment (test, exam) is linked the same way: choose the « Graded assessment » type.",
    'skl.grid.ratedOf': "{count} assessed of {of}",
    'skl.grid.depositsOf': "{count} submission(s) of {of}",
    'skl.grid.skill': "Skill",
    'skl.grid.edit': "Edit",
    'skl.grid.publish': "Publish the assessments",
    'skl.grid.republish': "Publish again",
    'skl.grid.published': "Assessments published: students can see them.",
    'skl.grid.publishedMarks': "Assessments published; {count} mark(s) written to the linked assessment.",
    'skl.grid.draft': "Draft: students see nothing before « Publish the assessments ».",
    'skl.grid.countedNote': "Counts in marks: the converted mark goes to « {assessment} » on publishing.",
    'skl.grid.mode': "View",
    'skl.grid.byIndicator': "By indicator",
    'skl.grid.skillOnly': "Skill only",
    'skl.grid.live': "During the practical",
    'skl.grid.note': "One click per cell; it is saved at once, a second click removes the level. The submission opens without leaving the grid.",
    'skl.grid.student': "Student",
    'skl.grid.deposit': "Submission",
    'skl.grid.skillLevel': "Skill",
    'skl.grid.mark': "Mark",
    'skl.grid.comment': "Comment",
    'skl.grid.noDeposit': "no submission",
    'skl.grid.addComment': "Add",
    'skl.grid.liveOf': "Indicator {index} of {count} · {done} / {of} assessed",
    'skl.grid.previous': "Previous indicator",
    'skl.grid.next': "Next indicator",
    'skl.grid.searchStudent': "Search a student",
    'skl.grid.liveNote': "Saved on every tap. Move to the next indicator with the arrows.",
    'skl.history.progress': "Progress",
    'skl.history.entries': "Assessment history",
    'skl.history.noLevel': "No level yet.",
    'skl.history.none': "No assessment yet.",
    'skl.history.date': "Date",
    'skl.history.activity': "Activity",
    'skl.history.level': "Level",
    'skl.history.comment': "Comment",
    'skl.history.proof': "Evidence",
    'skl.history.unpublished': "not published yet",
    'skl.history.final': "Final level",
    'skl.history.validatedBy': "Validated by {name} on {at}, not published yet",
    'skl.history.publishedAt': "Published on {at}",
    'skl.history.publishedBy': "Published by {name} on {at}",
    'skl.history.manual': "Manual entry: nothing is proposed",
    'skl.history.proposed': "Proposed by the « {rule} » rule",
    'skl.history.validated': "Validated final level",
    'skl.history.appraisal': "Final appraisal",
    'skl.history.publish': "Publish to the student",
    'skl.history.savedNotice': "Final level saved, not published yet.",
    'skl.history.publishedNotice': "Final level published: the student is notified.",
    'skl.history.finalNote': "Once published, it appears in the student's « My skills ».",
    'skl.report.title': "Skills reports",
    'skl.report.sub': "Final levels of a class for a framework",
    'skl.report.excel': "Excel",
    'skl.report.csv': "CSV",
    'skl.report.class': "Class",
    'skl.report.framework': "Framework",
    'skl.report.none': "No published framework applies to this class.",
    'skl.report.note': "The export has one line per student and skill: level, date, teacher, appraisal. Skills only affect marks when the link is on, through the linked assessment.",
    'skl.report.finals': "Final levels",
    'skl.report.student': "Student",
    'skl.report.acquired': "Acquired",
    'skl.report.unpublished': "Validated, not published yet",
    'skl.report.distribution': "Level distribution",
    'skl.settings.title': "Skills",
    'skl.settings.sub': "Module settings",
    'skl.settings.saved': "Settings saved.",
    'skl.settings.working': "How it works",
    'skl.settings.enabled': "Module on",
    'skl.settings.enabledHint': "off, menus and screens disappear; nothing is erased",
    'skl.settings.flag.teachersCreate': "Teachers create frameworks",
    'skl.settings.flag.activities': "Activities without marks",
    'skl.settings.flag.deposits': "Evidence submissions",
    'skl.settings.flag.studentProfile': "Profile visible to students",
    'skl.settings.flag.studentExport': "Report downloadable by the student",
    'skl.settings.flag.marksLink': "Link with marks",
    'skl.settings.rule.teachersCreate': "for their own courses; the pedagogy team sees them and can take them over",
    'skl.settings.rule.activities': "homework, practical, project, besides graded assessments",
    'skl.settings.rule.deposits': "only on the teacher's explicit request",
    'skl.settings.rule.studentProfile': "in « My space », published assessments only",
    'skl.settings.rule.studentExport': "their own report in Excel",
    'skl.settings.rule.marksLink': "off: no mark is touched; on, each framework and activity chooses whether to count",
    'skl.settings.formats': "Accepted files",
    'skl.settings.formatsSub': "5 MB at most per file, checked in the browser and on the server",
    'skl.settings.scale': "Default scale",
    'skl.settings.scaleSub': "Offered when creating a framework",
    'skl.settings.note': "Notifications (submission request, final level published, submission received) are only sent with the Communication module.",
    'skl.mine.title': "My skills",
    'skl.mine.export': "My report in Excel",
    'skl.mine.kpiEvaluated': "Skills assessed",
    'skl.mine.kpiEvaluatedNote': "of {total} in my programme",
    'skl.mine.kpiAcquired': "Acquired or mastered",
    'skl.mine.kpiAcquiredNote': "including {count} mastered",
    'skl.mine.kpiProgress': "In progress",
    'skl.mine.kpiProgressNote': "to consolidate",
    'skl.mine.kpiDeposits': "Submissions to make",
    'skl.mine.kpiDepositsNote': "requested by my teachers",
    'skl.mine.profile': "My profile",
    'skl.mine.deposits': "Requested submissions",
    'skl.mine.noDeposit': "No submission requested.",
    'skl.mine.due': "before {at}",
    'skl.mine.deposited': "submitted: {name} on {at}",
    'skl.mine.closed': "Closed",
    'skl.mine.deposit': "Submit",
    'skl.mine.replace': "Replace",
    'skl.mine.skills': "My skills",
    'skl.mine.skillsSub': "A click opens the history and comments",
    'skl.mine.none': "No framework has been published for your programme yet.",
    'skl.mine.notYet': "not assessed yet",
    'skl.mine.evaluations': "{count} assessment(s)",
    'skl.mine.final': "final level published",
    'skl.mine.note': "Only assessments published by your teachers appear. The submission space opens only at their request.",
    'skl.mine.noFinal': "No final level yet.",
    'skl.mine.byIndicator': "By indicator",
    'skl.mine.byIndicatorSub': "at the latest assessment",
    'skl.mine.proofs': "My evidence",
    'skl.mine.noProof': "No evidence submitted.",
    'skl.mine.open': "Open",
    'skl.deposit.title': "Submit: {label}",
    'skl.deposit.by': "Requested by",
    'skl.deposit.skills': "Skills",
    'skl.deposit.due': "Deadline",
    'skl.deposit.noDue': "none",
    'skl.deposit.accepted': "Accepted",
    'skl.deposit.acceptedValue': "{formats} · 5 MB at most",
    'skl.deposit.drop': "Drop the file here or choose it",
    'skl.deposit.tooBig': "{name} · {size}: the file exceeds 5 MB.",
    'skl.deposit.badFormat': "{name}: format not accepted ({formats}).",
    'skl.deposit.choose': "Choose a file.",
    'skl.deposit.remove': "Remove",
    'skl.deposit.current': "Already submitted: {name}. A new file replaces it.",
    'skl.deposit.note': "A note for the teacher",
    'skl.deposit.replaceNote': "Until the deadline you can replace your file; the teacher is notified.",
    'skl.deposit.done': "Evidence submitted: your teacher is notified.",
    'skl.deposit.replaced': "Evidence replaced: your teacher is notified.",
    'export.label': "Excel",
    'export.busy': "Exporting…",
    'export.pages': "Page {done} of {total}…",
    'export.hint': "Export all filtered rows to Excel, with the table's columns",
    'violation.export.rows': "Exports are limited to 50,000 rows: narrow the filters.",
    'violation.export.columns': "This table has too many columns to export.",
    'el.acc.allYear': "Whole year",
    'el.acc.change': "Change",
    'el.acc.edit': "Edit accommodation",
    'el.acc.extra': "Extra attempts",
    'el.acc.from': "From",
    'el.acc.grant': "Grant",
    'el.acc.granted': "Granted by",
    'el.acc.kind': "Accommodation",
    'el.acc.new': "New accommodation",
    'el.acc.none': "No accommodation granted.",
    'el.acc.note': "Granted by the registrar, logged, applied automatically during the test (longer duration, own window). Teachers see them on the copies but cannot change them.",
    'el.acc.percent': "Extra time (%)",
    'el.acc.quiz': "Assessment",
    'el.acc.reason': "Reason",
    'el.acc.remove': "Remove",
    'el.acc.scope': "Scope",
    'el.acc.search': "Name or student number",
    'el.acc.sub': "Extra time, shifted windows, extra attempts",
    'el.acc.title': "Accommodations",
    'el.acc.to': "To",
    'el.acc.kind.extra_time': "Extra time",
    'el.acc.kind.shifted_window': "Shifted window",
    'el.acc.kind.extra_attempt': "Extra attempt",
    'el.acc.short.extra_time': "extra time",
    'el.acc.short.shifted_window': "shifted window",
    'el.acc.short.extra_attempt': "extra attempt",
    'el.attempt.anonymous': "Anonymous copy",
    'el.attempt.device': "Device",
    'el.attempt.log': "Attempt log",
    'el.attempt.logSub': "Hints for the teacher, never an automatic sanction",
    'el.attempt.minutes': "Minutes granted",
    'el.attempt.points': "{got} out of {max} point(s)",
    'el.attempt.points2': "Points",
    'el.attempt.question': "Question {n}",
    'el.attempt.reason': "Reason (logged)",
    'el.attempt.receipt': "Receipt",
    'el.attempt.reopen': "Reopen the attempt",
    'el.attempt.reopenNote': "The student resumes the copy with the saved answers, for the time granted.",
    'el.attempt.score': "Mark",
    'el.attempt.summary': "Copy",
    'el.attempt.neutralised.give': "Neutralised: points given",
    'el.attempt.neutralised.remove': "Neutralised: removed from total",
    'el.bank.allChapters': "All chapters",
    'el.bank.allDifficulties': "All difficulties",
    'el.bank.allOutcomes': "All outcomes",
    'el.bank.allTypes': "All types",
    'el.bank.archived': "Archived",
    'el.bank.chapter': "Chapter",
    'el.bank.difficulty': "Difficulty",
    'el.bank.discrimination': "Discrimination",
    'el.bank.export': "Export",
    'el.bank.import': "Import (spreadsheet)",
    'el.bank.importErrors': "Rows skipped: {list}.",
    'el.bank.imported': "{count} question(s) imported.",
    'el.bank.live': "In use",
    'el.bank.new': "New question",
    'el.bank.none': "No question.",
    'el.bank.note': "Editing a question already used creates a new version: past copies keep the version answered. Success rate and discrimination index are computed after use; a distractor never chosen is flagged.",
    'el.bank.outcome': "Outcome",
    'el.bank.points': "Points",
    'el.bank.question': "Question",
    'el.bank.search': "Search a question",
    'el.bank.sub': "{course} · {count} question(s) · {classes}",
    'el.bank.success': "Success",
    'el.bank.title': "Question bank",
    'el.bank.type': "Type",
    'el.bank.untitled': "(no statement)",
    'el.bank.unused': "Never chosen: {list}",
    'el.bank.version': "Version",
    'el.copies.absent': "Absent",
    'el.copies.absentBadge': "Absent",
    'el.copies.all': "All",
    'el.copies.attempt': "Attempt {n}",
    'el.copies.auto': "submitted automatically",
    'el.copies.autoScore': "{score} auto",
    'el.copies.copies': "Copies",
    'el.copies.filter': "Filter copies",
    'el.copies.focus': "{count} focus loss(es)",
    'el.copies.grade': "Grade by question",
    'el.copies.graded': "Graded",
    'el.copies.incident': "Incident",
    'el.copies.incidentNote': "Reopen an attempt from the copy; extend the window from the assessment settings. A reason is required and logged.",
    'el.copies.kept': "kept {score}",
    'el.copies.lot': "Mark batch:",
    'el.copies.noCopy': "No copy yet.",
    'el.copies.notStarted': "Not started",
    'el.copies.open': "Copy",
    'el.copies.publish': "Publish results",
    'el.copies.publishedDone': "{count} copy(ies) published to students.",
    'el.copies.reconnects': "{count} reconnection(s)",
    'el.copies.regrade': "Regrade a question",
    'el.copies.regradeDo': "Regrade",
    'el.copies.regradeSub': "For all copies, with the impact before confirming",
    'el.copies.results': "Results",
    'el.copies.returned': "Batch returned: {reason}",
    'el.copies.score': "Mark",
    'el.copies.signals': "Signals",
    'el.copies.state': "Status",
    'el.copies.sub': "{total} student(s) · {graded} graded · {toGrade} to grade · {absent} absent",
    'el.copies.times': "Start · end",
    'el.copies.title': "{quiz} · copies",
    'el.copies.toGrade': "To grade",
    'el.copies.validate': "Validate graded copies",
    'el.copies.validated': "Validated",
    'el.copies.validatedDone': "{count} copy(ies) validated.",
    'el.copies.view': "View",
    'el.course.bank': "Question bank",
    'el.course.credits': "{credits} credits",
    'el.course.readOnly': "Read only: this course is not in your timetable or the year is closed.",
    'el.course.students': "{count} student(s)",
    'el.course.tabs': "Course sections",
    'el.dash.allPlans': "All syllabi",
    'el.dash.beforeOpening': "Before opening",
    'el.dash.byTraining': "Syllabi by programme",
    'el.dash.lots': "Mark batches",
    'el.dash.lotsNote': "{control} to check, {transfer} to transfer",
    'el.dash.note': "Next opening of a subject to validate: {at}.",
    'el.dash.plans': "Published syllabi",
    'el.dash.plansNote': "{rate}% of courses",
    'el.dash.readOnly': "Read only",
    'el.dash.subjects': "Subjects to validate",
    'el.dash.subjectsNote': "exams counting toward the average",
    'el.dash.title': "E-Learning dashboard",
    'el.dash.toGrade': "Copies to grade",
    'el.dash.toGradeNote': "in the institution",
    'el.dash.toValidate': "Exam subjects to validate",
    'el.dialog.add': "Add",
    'el.dialog.addTitle': "Add a resource",
    'el.dialog.afterQuiz': "After passing the quiz \"{title}\"",
    'el.dialog.afterViewed': "After viewing \"{title}\"",
    'el.dialog.body': "Page content",
    'el.dialog.choose': "Choose…",
    'el.dialog.closesAt': "Closing",
    'el.dialog.condition': "Condition",
    'el.dialog.diffusion': "Release",
    'el.dialog.editTitle': "Edit the resource",
    'el.dialog.file': "File",
    'el.dialog.fileHint': "{kinds} · {mb} MB max",
    'el.dialog.groups': "Groups",
    'el.dialog.groupsNote': "None ticked: all groups of the course.",
    'el.dialog.kind': "Resource type",
    'el.dialog.library': "Shared library",
    'el.dialog.min': "Minimum mark (out of 20)",
    'el.dialog.noCondition': "No condition",
    'el.dialog.notify': "Notify students",
    'el.dialog.notifyNote': "in the daily digest",
    'el.dialog.opensAt': "Opening",
    'el.dialog.preview': "Preview",
    'el.dialog.readOnly': "View only",
    'el.dialog.readOnlyNote': "no download",
    'el.dialog.replace': "Replace \"{name}\" (new version)",
    'el.dialog.section': "Section",
    'el.dialog.shareable': "Share in the shared library",
    'el.dialog.shareableNote': "other courses can reference it",
    'el.dialog.title': "Title",
    'el.dialog.url': "Link address",
    'el.dialog.versions': "Previous versions",
    'el.dialog.videoHint': "YouTube, Vimeo or Microsoft Stream: the video plays in the page, never hosted.",
    'el.dialog.videoUrl': "Video address",
    'el.dialog.visibility': "Visibility",
    'el.dialog.watermark': "Named watermark",
    'el.dialog.watermarkNote': "name, student number and date on every page of the PDF the student downloads",
    'el.grades.copies': "Copies",
    'el.grades.grade': "Grade",
    'el.grades.lot': "Mark batch",
    'el.grades.none': "No copy yet.",
    'el.grades.published': "Published on",
    'el.grades.sub': "Grade, validate, publish; the batch then goes to checking and transfer",
    'el.grades.title': "Copies and marks",
    'el.grades.toGrade': "To grade",
    'el.grading.accept': "Accept and regrade the {count} copies",
    'el.grading.anonymous': "anonymous",
    'el.grading.auto': "Automatic grading: {points} out of {max}",
    'el.grading.blank': "No answer",
    'el.grading.comment': "Comment",
    'el.grading.comments': "Reusable comments",
    'el.grading.copyOf': "copy {n} of {total}",
    'el.grading.nextCopy': "Next copy",
    'el.grading.nextQuestion': "Next question",
    'el.grading.noComment': "Comments already written will appear here.",
    'el.grading.noUnexpected': "No unexpected short answer.",
    'el.grading.none': "No copy for this question.",
    'el.grading.note': "Names stay hidden until validation when the assessment is anonymous. Splitting between graders and double grading will come in a later batch.",
    'el.grading.pickQuestion': "Pick the question",
    'el.grading.points': "Points (out of {max})",
    'el.grading.previousCopy': "Previous copy",
    'el.grading.previousQuestion': "Previous question",
    'el.grading.question': "Q {n} · {text}",
    'el.grading.rubric': "Rubric",
    'el.grading.saveNext': "Save and next",
    'el.grading.statement': "Statement",
    'el.grading.sub': "{quiz} · {toGrade} copy(ies) to grade out of {total}",
    'el.grading.title': "Grading by question",
    'el.grading.unexpected': "Unexpected short answers",
    'el.grading.written': "{count} copy(ies)",
    'el.mine.afterDate': "after {at}",
    'el.mine.afterPublication': "after publication",
    'el.mine.attemptsLeft': "{count} attempt(s)",
    'el.mine.copy': "Copy",
    'el.mine.counted': "counts toward the average",
    'el.mine.course': "Course",
    'el.mine.courses': "My courses",
    'el.mine.from': "from {at}",
    'el.mine.new': "New",
    'el.mine.newCount': "{count} new resource(s)",
    'el.mine.newResource': "new resource",
    'el.mine.noCourse': "No online course yet.",
    'el.mine.noPlan': "syllabus not yet published",
    'el.mine.noResult': "No result yet.",
    'el.mine.notCounted': "not graded",
    'el.mine.note': "Open assessments also show in the portal calendar; a reminder is sent the day before.",
    'el.mine.nothing': "Nothing to do this week.",
    'el.mine.open': "Open",
    'el.mine.planUpdated': "syllabus v{version} updated",
    'el.mine.planVersion': "syllabus v{version}",
    'el.mine.quiz': "Assessment",
    'el.mine.results': "My online results",
    'el.mine.resultsSub': "Marks appear after the teacher publishes them",
    'el.mine.resume': "Resume",
    'el.mine.score': "Mark",
    'el.mine.seeCopy': "See my copy",
    'el.mine.seeKey': "See the answer key",
    'el.mine.soon': "Soon",
    'el.mine.soonBadge': "Soon",
    'el.mine.start': "Start",
    'el.mine.sub': "{classes} · this week",
    'el.mine.title': "My courses",
    'el.mine.todo': "To do this week",
    'el.mine.trackedNote': "Your institution records when resources are opened and downloaded, so teachers know what has been read.",
    'el.mine.until': "open until {at}",
    'el.plan.assessment': "Assessment",
    'el.plan.assessments': "Assessment methods",
    'el.plan.assessmentsSub': "Read from the academic regulations",
    'el.plan.changes': "{count} change(s)",
    'el.plan.coefficient': "Weight",
    'el.plan.common': "Shared syllabus",
    'el.plan.conform': "Matches the academic regulations: nothing to re-enter.",
    'el.plan.course': "Course",
    'el.plan.date': "Date",
    'el.plan.duplicate': "Reuse last year's syllabus",
    'el.plan.group': "Syllabus of",
    'el.plan.groups': "Groups",
    'el.plan.hours': "Hours",
    'el.plan.level': "Level",
    'el.plan.linked': "Linked",
    'el.plan.linkedValue': "{count} resource(s)",
    'el.plan.missing': "Required to publish: {list}.",
    'el.plan.nextDraft': "Version {version} in progress",
    'el.plan.noAssessment': "No assessment in the regulations for this course.",
    'el.plan.noSession': "No planned session.",
    'el.plan.noVersion': "No published version.",
    'el.plan.objectives': "Objectives",
    'el.plan.prefilled': "Prefilled sections",
    'el.plan.prefilledSub': "Read from the catalogue and the timetable, read only",
    'el.plan.publish': "Publish now",
    'el.plan.publishedDone': "Version {version} published: students are notified.",
    'el.plan.readings': "Readings",
    'el.plan.retake': "resit",
    'el.plan.saveDraft': "Save draft",
    'el.plan.saved': "Draft saved.",
    'el.plan.schedule': "Schedule publication",
    'el.plan.scheduleAt': "Publish on",
    'el.plan.scheduledDone': "Publication scheduled.",
    'el.plan.scheduledOn': "Publication scheduled on {at}",
    'el.plan.session': "Session",
    'el.plan.sessions': "Session by session",
    'el.plan.sessionsSub': "One row per planned session",
    'el.plan.state': "Status",
    'el.plan.state.draft': "Draft",
    'el.plan.state.none': "Syllabus to prepare",
    'el.plan.state.published': "Published v{version}",
    'el.plan.state.published_draft': "Published v{version}, edited",
    'el.plan.state.scheduled': "Scheduled",
    'el.plan.teachers': "Teachers",
    'el.plan.template': "Template",
    'el.plan.templateNote': "Sections of the \"{name}\" template; those with an asterisk are required to publish.",
    'el.plan.theme': "Topic",
    'el.plan.training': "Programme",
    'el.plan.type': "Type",
    'el.plan.unschedule': "Cancel the schedule",
    'el.plan.versionLine': "published on {at} by {by}",
    'el.plan.versions': "Versions",
    'el.plan.written': "Written sections",
    'el.plans.course': "Course",
    'el.plans.courses': "Courses",
    'el.plans.drafts': "Drafts",
    'el.plans.filter': "Filter syllabi",
    'el.plans.none': "No course this year.",
    'el.plans.published': "Published",
    'el.plans.publishedAt': "Published on",
    'el.plans.search': "Search a course, a teacher",
    'el.plans.state': "Status",
    'el.plans.title': "Syllabi",
    'el.plans.toPrepare': "To prepare",
    'el.plans.training': "Programme",
    'el.plans.f.all': "All",
    'el.plans.f.published': "Published",
    'el.plans.f.draft': "Drafts",
    'el.plans.f.none': "To prepare",
    'el.play.after': "Your mark and the answer key will appear once your teacher publishes them.",
    'el.play.answer': "Your answer",
    'el.play.answered': "{count} answer(s) saved out of {total}",
    'el.play.attemptLine': "Attempt {n} of {total} · started {at}",
    'el.play.autosave': "Continuous saving",
    'el.play.back': "Back to the course",
    'el.play.confirm': "{answered} question(s) answered out of {total}. Once submitted, the copy can no longer be changed.",
    'el.play.dontKnow': "I don't know",
    'el.play.false': "False",
    'el.play.flag': "Flag for review",
    'el.play.from': "from {at}",
    'el.play.goTo': "Go to question {n}",
    'el.play.key': "Answer key:",
    'el.play.legend': "Green: answered · amber: to review",
    'el.play.maxWords': "{count} words max",
    'el.play.meta': "{points} point(s) · {type}",
    'el.play.next': "Next",
    'el.play.noFile': "No file uploaded",
    'el.play.noKey': "The answer key is not available for this assessment, or not yet.",
    'el.play.notYet': "after publication",
    'el.play.note': "Each answer goes to the server as soon as it is entered: a network cut loses nothing, and the attempt resumes where it was if the browser closes. At the deadline, the copy is submitted automatically.",
    'el.play.offline': "Offline: your answers will be sent as soon as the network is back.",
    'el.play.pick': "Choose…",
    'el.play.previous': "Previous",
    'el.play.questionOf': "Question {n} of {total}",
    'el.play.questions': "Questions",
    'el.play.receipt': "Receipt no.",
    'el.play.received': "Your copy has been received",
    'el.play.saved': "Answer saved at {at}",
    'el.play.score': "My mark",
    'el.play.submit': "Finish and submit",
    'el.play.submitNow': "Submit my copy",
    'el.play.submittedAt': "submitted {at}",
    'el.play.true': "True",
    'el.play.unflag': "Remove the flag",
    'el.play.upload': "Upload a file",
    'el.question.abs': "Absolute",
    'el.question.accentsSensitive': "Accent-sensitive",
    'el.question.addBlank': "Add a blank",
    'el.question.addChoice': "Add a choice",
    'el.question.addItem': "Add an item",
    'el.question.addPair': "Add a pair",
    'el.question.answer': "Correct answer",
    'el.question.answered': "This question has already been answered: saving creates a new version, past copies keep theirs.",
    'el.question.answers': "Accepted answers",
    'el.question.answersHint': "One per line",
    'el.question.archive': "Archive",
    'el.question.blanks': "Blanks",
    'el.question.body': "Statement",
    'el.question.bodyHint': "Rich text; a LaTeX formula goes between $…$ inline, or between $$…$$ as a block (preview on the right).",
    'el.question.caseSensitive': "Case-sensitive",
    'el.question.chapter': "Chapter",
    'el.question.choice': "Choice {n}",
    'el.question.choices': "Choices and correct answers",
    'el.question.clozeHint': "Place each blank as [[b1]], [[b2]]… in the statement; \"Add a blank\" does it for you.",
    'el.question.correct': "Correct answer",
    'el.question.criteria': "Rubric",
    'el.question.criteriaHint': "One criterion per line: Criterion | Level: points | Level: points",
    'el.question.difficulty': "Difficulty",
    'el.question.dontKnow': "Offer \"I don't know\"",
    'el.question.edit': "Question · version {version}",
    'el.question.feedback': "Grading comment",
    'el.question.item': "Item {n}",
    'el.question.items': "Items in the right order",
    'el.question.kinds': "Accepted files",
    'el.question.left': "Left",
    'el.question.loadFailed': "Question not found.",
    'el.question.maxWords': "Maximum words",
    'el.question.new': "New question",
    'el.question.outcome': "Target outcome",
    'el.question.pairs': "Pairs to match",
    'el.question.panel': "The question",
    'el.question.penalty': "Penalty for a wrong answer",
    'el.question.penaltyHint': "Fraction of points removed (0 to 1)",
    'el.question.points': "Points",
    'el.question.positionMode': "Points per correct place",
    'el.question.positionModeNote': "otherwise all or nothing",
    'el.question.preview': "Student preview",
    'el.question.rel': "Relative (%)",
    'el.question.right': "Right",
    'el.question.scoring': "Scoring",
    'el.question.shuffle': "Shuffle the choices",
    'el.question.shuffleNote': "for each attempt",
    'el.question.stats': "After use",
    'el.question.sub': "{course} bank",
    'el.question.tags': "Tags",
    'el.question.tagsHint': "Comma-separated",
    'el.question.tolerance': "Tolerance",
    'el.question.toleranceType': "Tolerance type",
    'el.question.type': "Type",
    'el.question.unarchive': "Restore",
    'el.question.unit': "Unit",
    'el.question.uses': "Answers",
    'el.question.value': "Expected value",
    'el.quiz.addQuestion': "Add",
    'el.quiz.addRule': "Add a criterion",
    'el.quiz.allowed': "Attempts allowed",
    'el.quiz.anonymous': "Anonymous grading",
    'el.quiz.anonymousNote': "names appear at validation",
    'el.quiz.assessment': "Mark sent to",
    'el.quiz.attach': "Attachment",
    'el.quiz.attempts': "Attempts and feedback",
    'el.quiz.available': "Available",
    'el.quiz.backToDraft': "Back to draft",
    'el.quiz.checks': "Before opening",
    'el.quiz.circuit': "Workflow",
    'el.quiz.close': "Close now",
    'el.quiz.closedDone': "Assessment closed; attempts in progress were submitted.",
    'el.quiz.composition': "Composition",
    'el.quiz.confirmDelete': "Delete this draft?",
    'el.quiz.copies': "Copies",
    'el.quiz.delay': "Delay between attempts (min)",
    'el.quiz.delete': "Delete the draft",
    'el.quiz.draw': "Draw",
    'el.quiz.duration': "Duration (min)",
    'el.quiz.durationHint': "Empty: no limit; automatic submission at the deadline",
    'el.quiz.extend': "Extend the window",
    'el.quiz.extendDo': "Extend",
    'el.quiz.extendReason': "Reason (logged)",
    'el.quiz.extendTo': "New closing",
    'el.quiz.extended': "Window extended.",
    'el.quiz.extensionLine': "Extended until {to} by {by}: {reason}",
    'el.quiz.feedback': "Feedback to the student",
    'el.quiz.feedbackAt': "From",
    'el.quiz.integrity': "Integrity",
    'el.quiz.integrityNote': "Draw and choice order are frozen per student. Attempt log: start, end, IP address, device, focus losses, reconnections. These are hints, never a block. No access restriction for now: no password, IP range or lockdown mode.",
    'el.quiz.keep': "Mark kept",
    'el.quiz.loadFailed': "Assessment not found.",
    'el.quiz.locked': "Copies exist: the composition can no longer change. The window can still be extended.",
    'el.quiz.maxPoints': "Current total: {points} point(s)",
    'el.quiz.missingQuestion': "Question removed from the bank",
    'el.quiz.navFree': "Free",
    'el.quiz.navSequential': "Sequential (no going back)",
    'el.quiz.navigation': "Navigation",
    'el.quiz.new': "New online assessment",
    'el.quiz.noAssessment': "None (no transfer)",
    'el.quiz.noItem': "No question chosen.",
    'el.quiz.note': "An exam counting toward the average goes through subject validation by the coordinator before opening. A practice quiz is never transferred.",
    'el.quiz.open': "Make it available",
    'el.quiz.opened': "The assessment will open on the planned date.",
    'el.quiz.outOf': "out of {outOf}",
    'el.quiz.pending': "Subject sent to the coordinator on {at}.",
    'el.quiz.pick': "Pick a question from the bank…",
    'el.quiz.pointsEach': "Points each",
    'el.quiz.rejected': "Subject to rework: {reason}",
    'el.quiz.saveFirst': "Save the assessment to see the checks and open it.",
    'el.quiz.saved': "Assessment saved.",
    'el.quiz.scale': "Scale (out of)",
    'el.quiz.sentToValidation': "Subject sent to the coordinator.",
    'el.quiz.shuffle': "Shuffle questions and choices",
    'el.quiz.shuffleNote': "order frozen per student",
    'el.quiz.submitSubject': "Send the subject for validation",
    'el.quiz.title': "Title",
    'el.quiz.total': "{points} point(s), scaled to {outOf}",
    'el.quiz.usage': "Use",
    'el.quiz.validated': "validated",
    'el.quiz.validatedBy': "Subject validated by {by} on {at}",
    'el.quiz.validatedDone': "Subject validated.",
    'el.quiz.window': "Window and duration",
    'el.quiz.fixed': "Fixed questions",
    'el.quiz.random': "Random draw",
    'el.quizzes.copies': "Copies",
    'el.quizzes.copiesValue': "{count} copy(ies)",
    'el.quizzes.duration': "{minutes} min",
    'el.quizzes.new': "New assessment",
    'el.quizzes.noValidationNote': "Subjects do not go through the coordinator (institution setting).",
    'el.quizzes.none': "No online assessment.",
    'el.quizzes.questions': "Questions",
    'el.quizzes.quiz': "Assessment",
    'el.quizzes.state': "Status",
    'el.quizzes.sub': "Practice, continuous assessment and exams",
    'el.quizzes.title': "Online assessments",
    'el.quizzes.to': "to \"{assessment}\"",
    'el.quizzes.toGrade': "{count} to grade",
    'el.quizzes.usage': "Use",
    'el.quizzes.validationNote': "An exam linked to the regulations is validated by the coordinator before opening.",
    'el.quizzes.window': "Window",
    'el.regrade.apply': "Recompute the copies",
    'el.regrade.changed': "Copies changed",
    'el.regrade.changedValue': "{count} out of {total}",
    'el.regrade.copies': "answered in {count} copy(ies)",
    'el.regrade.done': "{count} copy(ies) recomputed.",
    'el.regrade.impact': "Impact before confirming",
    'el.regrade.keyNote': "To fix the key of a closed question, edit it in the bank, then regrade it here.",
    'el.regrade.mean': "Average",
    'el.regrade.mode': "Regrading",
    'el.regrade.newPoints': "New points for the question",
    'el.regrade.none': "none",
    'el.regrade.preview': "See the impact",
    'el.regrade.published': "Marks already published",
    'el.regrade.publishedValue': "{count}, students will be notified",
    'el.regrade.reason': "Reason",
    'el.regrade.title': "Regrade a question",
    'el.regrade.total': "Assessment total",
    'el.regrade.give': "Neutralise: points given to all",
    'el.regrade.giveNote': "each copy gets the question's points",
    'el.regrade.remove': "Neutralise: remove from total",
    'el.regrade.removeNote': "the total drops accordingly",
    'el.regrade.points': "Change the points",
    'el.regrade.pointsNote': "new points for the question",
    'el.regrade.restore': "Undo regrading",
    'el.regrade.restoreNote': "back to the original grading",
    'el.resources.add': "Add a resource",
    'el.resources.addHere': "Add",
    'el.resources.addSection': "Add the section",
    'el.resources.broken': "link unreachable",
    'el.resources.confirmDelete': "Delete this resource and its versions?",
    'el.resources.count': "Resources",
    'el.resources.countValue': "{count} in {sections} section(s)",
    'el.resources.diffusion': "Release",
    'el.resources.diffusionNote': "Chosen item by item: visible or hidden, opening and closing dates, groups, condition (after viewing a resource or passing a quiz), download or view only, named watermark.",
    'el.resources.digest': "Daily digest",
    'el.resources.digestValue': "students are notified once a day",
    'el.resources.down': "Move down",
    'el.resources.empty': "No session or section yet: add a section.",
    'el.resources.layout': "Layout",
    'el.resources.max': "Maximum size",
    'el.resources.maxValue': "{mb} MB per file",
    'el.resources.newSection': "New section (chapter)",
    'el.resources.noFile': "No file uploaded",
    'el.resources.none': "No resource.",
    'el.resources.note': "A replaced file keeps its versions; students see \"updated on …\". A shared resource is referenced, not copied: its update benefits every course.",
    'el.resources.opened': "{count} / {total} opened",
    'el.resources.opensOn': "visible on {at}",
    'el.resources.quota': "Course quota",
    'el.resources.quotaValue': "{used} of {quota}",
    'el.resources.readOnlyFile': "view only",
    'el.resources.renew': "Carry over from last year",
    'el.resources.session': "Session",
    'el.resources.sharedFrom': "shared by {course}",
    'el.resources.someGroups': "some groups",
    'el.resources.space': "Space",
    'el.resources.tooBig': "File too large ({mb} MB max).",
    'el.resources.up': "Move up",
    'el.resources.updated': "updated on {at}",
    'el.resources.views': "Views",
    'el.resources.watermark': "named watermark",
    'el.results.allGraded': "all copies are graded",
    'el.results.below': "Below half",
    'el.results.belowNote': "out of {count}",
    'el.results.byQuestion': "Success by question",
    'el.results.count': "{count} student(s) graded",
    'el.results.distribution': "Mark distribution",
    'el.results.distributionSub': "Scaled to 20",
    'el.results.mean': "Average",
    'el.results.median': "Median",
    'el.results.noOutcome': "No question is linked to an outcome.",
    'el.results.outOf': "out of {outOf}",
    'el.results.outcomes': "Success by learning outcome",
    'el.results.outcomesSub': "Linked to skills tracking later",
    'el.results.toGrade': "To grade",
    'el.results.toGradeNote': "copies waiting",
    'el.rich.bold': "Bold",
    'el.rich.bullets': "Bulleted list",
    'el.rich.italic': "Italic",
    'el.rich.link': "Link",
    'el.rich.linkPrompt': "Link address",
    'el.rich.numbers': "Numbered list",
    'el.rich.toolbar': "Formatting",
    'el.settings.absence': "Absence sent",
    'el.settings.absenceHint': "The absence type set for a student with no copy; without a type, nothing is sent for them.",
    'el.settings.addRubric': "Add a section",
    'el.settings.addTemplate': "Add a template",
    'el.settings.assessments': "Assessments and marks",
    'el.settings.control': "Check before transfer",
    'el.settings.controlNote': "the coordinator checks each batch",
    'el.settings.courseQuota': "Quota per course (MB)",
    'el.settings.courses': "Courses and files",
    'el.settings.feedback': "Default feedback",
    'el.settings.fileKinds': "Accepted file types",
    'el.settings.institutionQuota': "Institution quota (GB)",
    'el.settings.maxFile': "Maximum size (MB)",
    'el.settings.newTemplate': "New template",
    'el.settings.noAbsence': "None (send nothing)",
    'el.settings.note': "The module is switched on in Modules. Not included for now: virtual classroom, forum, hosted videos, exam access restrictions, webcam proctoring; Moodle import and assisted grading will come later.",
    'el.settings.off': "The module is off: switch it on in Modules.",
    'el.settings.parents': "Parents after deliberation",
    'el.settings.parentsNote': "online marks visible to parents only after deliberation (setting kept for the parents' portal)",
    'el.settings.perGroup': "One syllabus per group",
    'el.settings.perGroupNote': "otherwise one syllabus shared by all groups",
    'el.settings.removeTemplate': "Remove the template",
    'el.settings.required': "Required",
    'el.settings.rounding': "Rounding: two decimals, like the average engine.",
    'el.settings.rubric': "Section",
    'el.settings.saved': "Settings saved.",
    'el.settings.sub': "Institution settings",
    'el.settings.templateName': "Template name",
    'el.settings.templates': "Syllabus templates",
    'el.settings.templatesSub': "Sections, order, sections required to publish",
    'el.settings.title': "E-Learning",
    'el.settings.trackViews': "View tracking",
    'el.settings.trackViewsNote': "who opened or downloaded each resource",
    'el.settings.trainings': "Programmes covered (codes)",
    'el.settings.trainingsHint': "Empty: default template. For example {list}",
    'el.settings.used': "{size} used",
    'el.settings.validation': "Subject validation",
    'el.settings.validationNote': "an exam counting toward the average is validated by the coordinator",
    'el.student.changed': "Updated on {at} · {count} change(s) highlighted",
    'el.student.download': "Download",
    'el.student.lockedQuiz': "opens after {min} / 20 in the quiz",
    'el.student.lockedViewed': "opens after viewing another resource",
    'el.student.noPlan': "This course's syllabus is not published yet.",
    'el.student.noResource': "No resource released yet.",
    'el.student.pdf': "Download as PDF",
    'el.student.planLine': "Syllabus · version {version} of {at}",
    'el.student.quizzes': "Assessments",
    'el.student.schedule': "Upcoming sessions",
    'el.student.seen': "Seen",
    'el.student.sessions': "Sessions",
    'el.student.until': "until {at}",
    'el.subjects.examine': "Review",
    'el.subjects.line': "{training} · {teacher} · opens on {at}",
    'el.subjects.noReady': "No validated exam.",
    'el.subjects.none': "No subject to validate.",
    'el.subjects.note': "Validating a subject makes it available on its date; sending it back returns it to draft, with the reason, to the teacher.",
    'el.subjects.pending': "To validate",
    'el.subjects.ready': "Validated exams",
    'el.subjects.reason': "Reason for sending back",
    'el.subjects.reject': "Send back to the teacher",
    'el.subjects.sub': "Exams counting toward the average, before opening",
    'el.subjects.title': "Subjects to validate",
    'el.subjects.validate': "Validate",
    'el.tab.plan': "Syllabus",
    'el.tab.resources': "Resources",
    'el.tab.quizzes': "Online assessments",
    'el.tab.grades': "Copies and marks",
    'el.tab.tracking': "Tracking",
    'el.teach.duplicate': "Duplicate last year's syllabus",
    'el.teach.kpiCourses': "Courses",
    'el.teach.kpiCoursesNote': "in my timetable",
    'el.teach.kpiEmpty': "Syllabi to prepare",
    'el.teach.kpiEmptyNote': "no syllabus yet",
    'el.teach.kpiPublished': "Published syllabi",
    'el.teach.kpiPublishedNote': "{drafts} draft(s), {empty} to prepare",
    'el.teach.kpiToGrade': "Copies to grade",
    'el.teach.kpiToGradeNote': "open questions waiting",
    'el.teach.none': "No course in your timetable this year.",
    'el.teach.note': "No space is created by hand: each space comes from a planned course (programme, group, assigned teacher). Registrar enrolments decide who sees what.",
    'el.teach.nothing': "Nothing to do this week",
    'el.teach.open': "Open the space",
    'el.teach.plan': "Syllabus",
    'el.teach.quizzes': "Assessments",
    'el.teach.quizzesNone': "none",
    'el.teach.quizzesValue': "{total} · {open} open",
    'el.teach.resources': "Resources",
    'el.teach.resourcesNew': "{count} · {fresh} new",
    'el.teach.students': "Students",
    'el.teach.sub': "One space per course in my timetable, {year}",
    'el.teach.title': "My online courses",
    'el.teach.todoGrade': "{count} copy(ies) to grade",
    'el.teach.todoPlan': "Syllabus to publish",
    'el.tracking.downloads': "Downloads",
    'el.tracking.off': "View tracking is switched off in the institution settings.",
    'el.tracking.opened': "Opened by",
    'el.tracking.resource': "Resource",
    'el.tracking.section': "Section",
    'el.tracking.sub': "{students} student(s) enrolled",
    'el.tracking.title': "Resource views",
    'el.transfer.absent': "Absent",
    'el.transfer.absentNote': "sent as \"{absence}\"",
    'el.transfer.circuit': "Workflow",
    'el.transfer.control': "Check the batch",
    'el.transfer.copies': "Copies",
    'el.transfer.differences': "Differences",
    'el.transfer.differencesNote': "with the Marks module",
    'el.transfer.done': "Batch transferred: {marks} mark(s), {absents} absent.",
    'el.transfer.lot': "Batch",
    'el.transfer.lots': "Batches published by teachers",
    'el.transfer.mark': "In the Marks module",
    'el.transfer.noAbsence': "no absence type set: not sent",
    'el.transfer.none': "No mark batch yet.",
    'el.transfer.note': "Transfer is an explicit act, per batch. An absence or non-submission is sent as such and follows the regulations' absence rules: E-Learning never turns it into a zero and computes no average, letter or compensation. After transfer, any change goes through the Marks module workflow.",
    'el.transfer.online': "Online mark",
    'el.transfer.pending': "Pending",
    'el.transfer.pendingNote': "copies to grade, validate or publish",
    'el.transfer.ready': "Ready",
    'el.transfer.readyNote': "raw marks out of {outOf}",
    'el.transfer.reason': "Reason",
    'el.transfer.return': "Return to the teacher",
    'el.transfer.returnDo': "Return the batch",
    'el.transfer.rowAbsent': "Absent",
    'el.transfer.rowNone': "Nothing to send",
    'el.transfer.rowPending': "Pending",
    'el.transfer.rowReady': "Ready",
    'el.transfer.rowState': "Status",
    'el.transfer.run': "Transfer the batch",
    'el.transfer.saved': "Batch updated.",
    'el.transfer.state': "Status",
    'el.transfer.stepControlled': "Coordinator check",
    'el.transfer.stepPublished': "Marks published to students",
    'el.transfer.stepTransferred': "Transfer to the Marks module",
    'el.transfer.sub': "To \"{assessment}\" · {training} · {count} student(s)",
    'el.transfer.title': "Mark transfers",
    'el.transfer.todo': "to do",
    'el.transfer.validated': "The assessment is validated in the Marks module: reopen entry there before transferring.",
    'el.views.all': "All",
    'el.views.downloaded': "Downloaded",
    'el.views.downloads': "Downloads",
    'el.views.downloadsNote': "in total",
    'el.views.filter': "Filter students",
    'el.views.group': "Group",
    'el.views.last': "Last opened",
    'el.views.never': "Never opened",
    'el.views.neverBadge': "Never",
    'el.views.neverNote': "students enrolled",
    'el.views.neverTab': "Never opened",
    'el.views.note': "Views are tracked only if the institution chose so; students are told in \"My courses\".",
    'el.views.opened': "Opened",
    'el.views.openedAt': "Opened on",
    'el.views.openedTab': "Opened",
    'el.views.rate': "{rate}% of enrolled",
    'el.views.search': "Search a student",
    'el.views.student': "Student",
    'el.views.sub': "{course} · {section} · views",
    'el.views.times': "Times",
    'el.views.title': "Views",
    'el.visibility.closed': "Closed",
    'el.visibility.conditional': "Conditional",
    'el.visibility.hidden': "Hidden",
    'el.visibility.scheduled': "Visible on {at}",
    'el.visibility.visible': "Visible",
    'el.quizState.draft': "Draft",
    'el.quizState.pending': "Subject to validate",
    'el.quizState.ready': "Available",
    'el.quizState.closed': "Closed",
    'el.attemptState.in_progress': "In progress",
    'el.attemptState.submitted': "Submitted",
    'el.attemptState.graded': "Graded",
    'el.attemptState.validated': "Validated",
    'el.attemptState.published': "Published",
    'el.attemptState.transferred': "Transferred",
    'el.transferState.none': "None",
    'el.transferState.published': "To check",
    'el.transferState.controlled': "Checked",
    'el.transferState.transferred': "Transferred",
    'el.transferState.returned': "Returned",
    'el.usage.practice': "Practice",
    'el.usage.continuous': "Continuous assessment",
    'el.usage.exam': "Exam",
    'el.kind.file': "File",
    'el.kind.video': "Linked video",
    'el.kind.link': "Link",
    'el.kind.page': "Page",
    'el.kind.shared': "Shared",
    'el.layout.session': "By session",
    'el.layout.chapter': "By chapter",
    'el.layout.free': "Free",
    'el.sectionKind.session': "session",
    'el.sectionKind.chapter': "chapter",
    'el.sectionKind.free': "section",
    'el.fileKind.pdf': "PDF",
    'el.fileKind.office': "Office",
    'el.fileKind.image': "images",
    'el.fileKind.archive': "archives",
    'el.difficulty.easy': "easy",
    'el.difficulty.medium': "medium",
    'el.difficulty.hard': "hard",
    'el.qtype.single': "Single choice",
    'el.qtype.multiple': "Multiple choice",
    'el.qtype.truefalse': "True / False",
    'el.qtype.matching': "Matching",
    'el.qtype.ordering': "Ordering",
    'el.qtype.short': "Short answer",
    'el.qtype.numeric': "Numeric",
    'el.qtype.cloze': "Fill in the blanks",
    'el.qtype.open': "Open question",
    'el.qtype.file': "File upload",
    'el.scoring.proportional': "Proportional, floor at 0",
    'el.scoring.proportional_no_penalty': "Proportional, no penalty",
    'el.scoring.all_or_nothing': "All or nothing",
    'el.keep.best': "The best",
    'el.keep.last': "The last",
    'el.keep.mean': "The average",
    'el.feedback.none': "Nothing",
    'el.feedback.score': "Mark only",
    'el.feedback.full': "Mark and answer key after publication",
    'el.check.questions': "Questions",
    'el.check.window': "Window",
    'el.check.duration': "Duration",
    'el.check.assessment': "Mark transferred",
    'el.check.accommodations': "Accommodations",
    'el.event.start': "Start",
    'el.event.focus_lost': "Tab focus lost",
    'el.event.reconnect': "Reconnection",
    'el.event.resume': "Resumed",
    'el.event.submit': "Submission",
    'el.event.auto_submit': "Automatic submission",
    'el.event.reopen': "Reopened",
    'rel.modules.learning': "E-Learning",
    'rel.modules.learningHint': "Syllabi, resources, question bank, online assessments, grading, mark transfer, accommodations",
    'journal.action.learning.settings_changed': "E-Learning settings changed",
    'journal.action.learning.syllabus_changed': "Syllabus changed",
    'journal.action.learning.resource_saved': "Course resource saved",
    'journal.action.learning.question_saved': "Bank question saved",
    'journal.action.learning.quiz_saved': "Online assessment saved",
    'journal.action.learning.quiz_validated': "Exam subject validated or sent back",
    'journal.action.learning.grades_changed': "Online grading changed",
    'journal.action.learning.grades_transferred': "Mark batch checked or transferred",
    'journal.action.learning.accommodation_saved': "Accommodation granted or changed",
    'journal.action.learning.attempt_reopened': "Attempt reopened",
    'violation.el.assessment_validated': "The assessment is validated in the Marks module: entry must be reopened there.",
    'violation.el.attempt_closed': "The copy is already submitted.",
    'violation.el.attempt_delay': "Wait for the delay before the next attempt.",
    'violation.el.attempt_state': "This copy cannot change in its current state.",
    'violation.el.bad_sheet': "Unreadable workbook.",
    'violation.el.bad_url': "Web address expected (https://...).",
    'violation.el.bad_video': "YouTube, Vimeo or Microsoft Stream video expected.",
    'violation.el.cloze_missing': "A blank is missing in the statement.",
    'violation.el.file_empty': "Empty file.",
    'violation.el.file_format': "File type not accepted.",
    'violation.el.file_too_big': "File too large.",
    'violation.el.lot_pending': "Some copies remain to grade, validate or publish.",
    'violation.el.no_assessment': "No assessment of the regulations receives the mark.",
    'violation.el.no_attempt_left': "No attempt left.",
    'violation.el.no_previous': "Nothing last year for this course.",
    'violation.el.not_a_file': "This resource is not a file.",
    'violation.el.not_shareable': "Choose a file from the shared library.",
    'violation.el.nothing_to_publish': "No validated copy to publish.",
    'violation.el.nothing_to_validate': "No graded copy to validate.",
    'violation.el.question_answers': "At least one accepted answer.",
    'violation.el.question_choices': "At least two choices.",
    'violation.el.question_correct': "Tick the correct answer(s) (only one for single choice).",
    'violation.el.quiz_checks': "A check before opening failed: see the list.",
    'violation.el.quiz_locked': "Copies exist: the assessment can no longer change.",
    'violation.el.quiz_not_open': "The assessment is not open.",
    'violation.el.quiz_pending': "The subject is with the coordinator.",
    'violation.el.quiz_state': "Action not possible in the assessment's current state.",
    'violation.el.quota_course': "Course quota reached.",
    'violation.el.quota_institution': "Institution quota reached.",
    'violation.el.section_not_empty': "The section contains resources.",
    'violation.el.sequential': "Sequential navigation: no going back.",
    'violation.el.syllabus_incomplete': "Section required to publish.",
    'violation.el.syllabus_unchanged': "Nothing new since the last version.",
    'violation.el.transfer_state': "No batch in this state.",
    'violation.el.transferred': "Marks transferred: corrections go through the Marks module.",
    'violation.el.window_order': "Closing comes after opening.",
    'el.transfer.open': "The assessment is still open: students without a copy are not yet absent. Transfer waits for closing.",
    'violation.el.quiz_still_open': "The assessment is still open: close it before transferring.",
    'fin.account.title': "Account",
    'fin.account.billed': "Invoiced",
    'fin.account.discounts': "Discounts",
    'fin.account.paid': "Paid",
    'fin.account.due': "Balance due",
    'fin.account.lateNote': "of which {amount} late",
    'fin.account.credit': "Overpayment on the account:",
    'fin.account.uninvoiced': "Enrolment without invoice: {label}.",
    'fin.account.issue': "Issue the invoice",
    'fin.account.issued': "Invoice issued.",
    'fin.account.empty': "No invoice.",
    'fin.account.onDemand': "Invoice an on-demand service",
    'fin.account.onDemandSub': "A separate invoice, payable at once.",
    'fin.account.onDemandNoYear': "No reference year: issue the enrolment invoice first.",
    'fin.account.onDemandDone': "Service invoiced.",
    'fin.account.noOnDemand': "No on-demand service.",
    'fin.account.addService': "Add a service",
    'fin.account.noService': "No service to add.",
    'fin.account.serviceAdded': "Service added to the invoice.",
    'fin.account.grantDiscount': "Grant a discount",
    'fin.account.discountGranted': "Discount granted.",
    'fin.account.discountJournal': "The discount, its reason and who granted it are logged and shown on the invoice.",
    'fin.account.reason': "Reason",
    'fin.account.reasonRequired': "Reason",
    'fin.account.cancelInvoice': "Cancel the invoice",
    'fin.account.cancelled': "Cancelled on {date}: {reason}",
    'fin.account.designation': "Description",
    'fin.account.taxIncluded': "incl. tax {amount}",
    'fin.account.removeLine': "Remove the line",
    'fin.account.lineRemoved': "“{label}” removed from the invoice.",
    'fin.account.net': "Net total",
    'fin.account.dueOn': "Due date",
    'fin.account.rest': "{amount} left",
    'fin.account.payments': "Payments",
    'fin.account.allocatedTo': "Allocated to",
    'fin.account.part': "instalment {position}",
    'fin.account.reverses': "cancels {number}",
    'fin.account.reversedBy': "cancelled by {number}",
    'fin.collect.open': "Collect",
    'fin.collect.title': "Collect a payment",
    'fin.collect.sub': "Invoice {number} · balance {due}",
    'fin.collect.amount': "Amount received",
    'fin.collect.date': "Date received",
    'fin.collect.chequeNo': "Cheque no.",
    'fin.collect.transferRef': "Transfer reference",
    'fin.collect.bank': "Bank",
    'fin.collect.drawer': "Drawer",
    'fin.collect.drawerHint': "If not the student.",
    'fin.collect.comment': "Comment",
    'fin.collect.commentHint': "Optional. Printed on the receipt.",
    'fin.collect.allocation': "Automatic allocation",
    'fin.collect.left': "Was left",
    'fin.collect.this': "This payment",
    'fin.collect.after': "After",
    'fin.collect.surplus': "Surplus of {amount}: it stays as account credit.",
    'fin.collect.dueAfter': "Balance after this payment:",
    'fin.collect.immutable': "A recorded payment cannot be changed. A mistake is corrected by a cancellation, which creates a logged reverse entry and its receipt.",
    'fin.collect.print': "Print the receipt",
    'fin.collect.go': "Collect",
    'fin.collect.goPrint': "Collect and print the receipt",
    'fin.collect.done': "Payment collected.",
    'fin.cancel.title': "Cancel invoice {number}",
    'fin.cancel.hint': "The invoice keeps its number and stays visible. It must carry no payment.",
    'fin.cancel.go': "Cancel the invoice",
    'fin.cancel.done': "Invoice cancelled.",
    'fin.reverse.open': "Cancel this payment",
    'fin.reverse.title': "Cancel payment {number}",
    'fin.reverse.hint': "A reverse entry of the same amount is recorded with its own receipt. The original payment stays visible.",
    'fin.reverse.go': "Cancel the payment",
    'fin.reverse.done': "Payment cancelled.",
    'journal.action.finance.configured': "Finance: module configured",
    'journal.action.finance.created': "Finance: added",
    'journal.action.finance.updated': "Finance: changed",
    'journal.action.finance.deleted': "Finance: deleted",
    'journal.action.finance.invoice_issued': "Invoice issued",
    'journal.action.finance.invoice_changed': "Invoice changed",
    'journal.action.finance.invoice_cancelled': "Invoice cancelled",
    'journal.action.finance.discount_granted': "Discount granted",
    'journal.action.finance.payment_recorded': "Payment received",
    'journal.action.finance.payment_cancelled': "Payment cancelled",
    'journal.filter.group.finance': "Finance",
    'violation.fin.no_pricing_level': "Choose at least one pricing level.",
    'violation.fin.invalid_currency': "Three-letter currency code (MAD, XOF, EUR).",
    'violation.fin.currency_locked': "The currency cannot change once the first invoice is issued.",
    'violation.fin.invalid_pattern': "The format must contain a rank {N:x} and only known tokens.",
    'violation.fin.tax_billed': "This tax has already been invoiced: create a new one.",
    'violation.fin.in_use': "Already used: deactivate it instead.",
    'violation.fin.invalid_schedule': "From 1 to 24 instalments.",
    'violation.fin.shares_incomplete': "Shares must total 100%.",
    'violation.fin.invalid_amount': "Amount expected.",
    'violation.fin.level_not_priced': "This level is not priced by the institution.",
    'violation.fin.schedule_on_training': "The schedule is set on the programme.",
    'violation.fin.already_invoiced': "This enrolment already has its invoice.",
    'violation.fin.nothing_to_bill': "Nothing to invoice: the programme has no price.",
    'violation.fin.inactive': "Deactivated.",
    'violation.fin.on_demand_service': "An on-demand service is invoiced separately.",
    'violation.fin.already_on_invoice': "Already on the invoice.",
    'violation.fin.training_line': "The programme price cannot be removed: cancel the invoice.",
    'violation.fin.last_line': "An invoice keeps at least one line: cancel it.",
    'violation.fin.not_stackable': "This discount cannot be combined with another.",
    'violation.fin.invoice_has_payments': "Payments remain on this invoice: issue a credit note.",
    'violation.fin.invoice_cancelled': "This invoice is cancelled.",
    'violation.fin.future_date': "A payment cannot be dated in the future.",
    'violation.fin.unlikely_date': "Unlikely date.",
    'violation.fin.is_reversal': "A cancellation cannot be cancelled: collect again.",
    'violation.fin.already_reversed': "This payment is already cancelled.",
    'violation.fin.enrolment_has_payments': "Payments were received on this enrolment’s invoice: record a dropout instead.",
    // D65, D66 : gestion des enseignants (fac.) et paie des enseignants (pay.).
    'fac.none': "None",
    'fac.choose': "Choose…",
    'fac.open': "Open",
    'fac.total': "Total",
    'fac.export': "Export",
    'fac.period': "{from} → {to}",
    'fac.unit.hours': "In hours, for example 4 or 2.5",
    'fac.unit.hoursPerMonth': "Hours per month",
    'fac.backToTeachers': "Teachers",
    'fac.remove.confirm': "Delete “{label}”? This action is logged.",
    'fac.saved.identity': "Identity saved.",
    'fac.saved.fields': "Disciplinary fields saved.",
    'fac.saved.diploma': "Diploma saved.",
    'fac.saved.removed': "“{label}” deleted.",
    'fac.civility.M': "Mr",
    'fac.civility.F': "Ms",
    'fac.state.draft': "Draft",
    'fac.state.signed': "Signed",
    'fac.state.closed': "Closed",
    'fac.state.none': "No contract",
    'fac.alert.unsigned': "Contract not signed",
    'fac.alert.no_objective': "No objective entered",
    'fac.alert.no_session': "No session given",
    'fac.alert.no_contract': "No contract this year",
    'fac.situation.all': "All situations",
    'fac.situation.contract': "Under contract",
    'fac.situation.nocontract': "No contract",
    'fac.situation.todo': "To fix",
    'fac.basis.weekly': "Per week",
    'fac.basis.monthly': "Per month",
    'fac.basis.yearly': "Per year",
    'fac.basis.per.weekly': "per week",
    'fac.basis.per.monthly': "per month",
    'fac.basis.per.yearly': "per year",
    'fac.service.range': "{min} to {max}",
    'fac.service.min': "at least {value}",
    'fac.service.max': "at most {value}",
    'fac.payMode.hourly': "Per hour given",
    'fac.payMode.monthly': "Monthly flat fee and extra hours",
    'fac.payMode.none': "Not paid here",
    'fac.payMode.short.hourly': "Hourly",
    'fac.payMode.short.monthly': "Monthly flat fee",
    'fac.payMode.short.none': "Not paid",
    'fac.teachers.title': "Teachers",
    'fac.teachers.sub': "This year's contract, objective, hours given and what remains to fix",
    'fac.teachers.count': "{count} teacher(s) · year {year}",
    'fac.teachers.new': "New teacher",
    'fac.teachers.search': "Name, staff number, subject",
    'fac.teachers.empty': "No teacher matches these criteria.",
    'fac.kpi.underContract': "Under contract in {year}",
    'fac.kpi.underContractNote': "{permanent} permanent, {vacataire} part-time",
    'fac.kpi.objective': "Objective hours",
    'fac.kpi.objectiveNote': "Sum of contractual objectives",
    'fac.kpi.realized': "Hours given",
    'fac.kpi.realizedNote': "{percent} % of the objective",
    'fac.kpi.realizedNoteNone': "No objective entered",
    'fac.kpi.toFix': "To fix",
    'fac.kpi.toFixNote': "{unsigned} unsigned contract(s), {noObjective} without objective",
    'fac.filter.year': "Year",
    'fac.filter.category': "Category",
    'fac.filter.type': "Contract type",
    'fac.filter.situation': "Situation",
    'fac.filter.state': "Status",
    'fac.filter.allCategories': "All categories",
    'fac.filter.allTypes': "All types",
    'fac.filter.allStates': "All statuses",
    'fac.column.teacher': "Teacher",
    'fac.column.category': "Category",
    'fac.column.contract': "Contract",
    'fac.column.state': "Status",
    'fac.column.objective': "Objective",
    'fac.column.realized': "Given",
    'fac.column.planned': "Planned",
    'fac.column.catalog': "Curriculum",
    'fac.column.progress': "Progress",
    'fac.column.alerts': "Alerts",
    'fac.column.year': "Year",
    'fac.column.type': "Type",
    'fac.column.period': "Period",
    'fac.column.payMode': "Pay",
    'fac.column.service': "Workload",
    'fac.column.subject': "Subject",
    'fac.column.subjectClass': "Subject and class",
    'fac.column.teachingType': "Teaching type",
    'fac.column.training': "Programme",
    'fac.column.role': "Role",
    'fac.create.title': "New teacher",
    'fac.create.sub': "The person and staff number; department, diplomas and contracts are completed afterwards from the record",
    'fac.create.submit': "Create the teacher",
    'fac.identity.edit': "Edit identity",
    'fac.field.civility': "Title",
    'fac.field.name': "Full name",
    'fac.field.lastName': "Last name",
    'fac.field.firstName': "First name",
    'fac.field.number': "Staff number",
    'fac.field.integratedOn': "Joined on",
    'fac.field.department': "Department",
    'fac.field.email': "Email",
    'fac.field.phone': "Phone",
    'fac.tab.dossier': "Record",
    'fac.tab.contracts': "Contracts",
    'fac.tab.objectives': "Objectives",
    'fac.tab.dashboard': "Dashboard",
    'fac.tab.pay': "Pay",
    'fac.tab.history': "History",
    'fac.hero.contract.draft': "{year} contract in draft",
    'fac.hero.contract.signed': "{year} contract signed",
    'fac.hero.contract.closed': "{year} contract closed",
    'fac.hero.number': "Staff number {number}",
    'fac.hero.integrated': "joined on {date}",
    'fac.panel.identity': "Identity",
    'fac.panel.diplomas': "Diplomas",
    'fac.panel.account': "Kelassi account",
    'fac.panel.fields': "Disciplinary fields",
    'fac.panel.attributions': "Assignments",
    'fac.panel.attributionsYear': "Assignments {year}",
    'fac.attributions.sub': "Subjects they lead or teach, from the catalogue",
    'fac.attributions.empty': "No assignment in the catalogue.",
    'fac.account.none': "No Kelassi account.",
    'fac.account.active': "Active account",
    'fac.account.inactive': "Disabled account",
    'fac.account.lastLogin': "last sign-in {date}",
    'fac.account.neverLogged': "never signed in",
    'fac.account.open': "View account",
    'fac.fields.edit': "Disciplinary fields",
    'fac.fields.empty': "No disciplinary field.",
    'fac.fields.none': "No disciplinary field is defined for the institution.",
    'fac.diploma.add': "Add a diploma",
    'fac.diploma.edit': "Edit diploma",
    'fac.diploma.remove': "Delete diploma",
    'fac.diploma.label': "Diploma",
    'fac.diploma.school': "Institution",
    'fac.diploma.city': "City",
    'fac.diploma.year': "Year",
    'fac.diploma.empty': "No diploma entered.",
    'fac.contracts.title': "Contracts",
    'fac.contracts.sub': "One contract per year, or several if the situation changes during the year (no overlap)",
    'fac.contracts.empty': "No contract for this teacher.",
    'fac.contract.new': "New contract",
    'fac.contract.newTitle': "New contract · {name}",
    'fac.contract.editTitle': "{year} contract",
    'fac.contract.title': "{year} contract",
    'fac.contract.dialogSub': "The contract sets the expected workload and, if payroll is on, the pay",
    'fac.contract.type': "Contract type",
    'fac.contract.category': "Category",
    'fac.contract.year': "Year",
    'fac.contract.startsOn': "Start",
    'fac.contract.endsOn': "End",
    'fac.contract.basis': "Workload basis",
    'fac.contract.minHours': "Minimum workload",
    'fac.contract.maxHours': "Maximum workload",
    'fac.contract.online': "Online courses allowed",
    'fac.contract.payMode': "Pay mode",
    'fac.contract.note': "Note",
    'fac.contract.saveDraft': "Save as draft",
    'fac.contract.saveSign': "Save and mark signed",
    'fac.contract.saved': "Contract saved.",
    'fac.contract.savedSigned': "Contract saved and signed.",
    'fac.contract.locked': "A pay month of this contract is validated: rates and pay mode are locked.",
    'fac.contract.sign': "Mark signed",
    'fac.contract.signTitle': "Sign the {year} contract",
    'fac.contract.signSub': "A signed contract counts for pay; two signed contracts of one teacher cannot overlap",
    'fac.contract.signDate': "Signed on",
    'fac.contract.signed': "Contract signed.",
    'fac.contract.signedOn': "Signed on {date}",
    'fac.contract.objectives': "contractual objectives: {hours}",
    'fac.contract.close': "Close the contract",
    'fac.contract.closed': "Contract closed.",
    'fac.contract.reopen': "Reopen the contract",
    'fac.contract.reopened': "Contract reopened.",
    'fac.contract.deleted': "Contract deleted.",
    'fac.contract.renew': "Renew for next year",
    'fac.contract.renewed': "Contract renewed as a draft for next year.",
    'fac.contract.renewedUnmapped': "Contract renewed; {count} objective(s) without a matching subject next year were not copied.",
    'fac.confirm.contract.close.title': "Close the contract",
    'fac.confirm.contract.close.text': "Close the {year} contract? It remains viewable and can be reopened.",
    'fac.confirm.contract.close.ok': "Close",
    'fac.confirm.contract.reopen.title': "Reopen the contract",
    'fac.confirm.contract.reopen.text': "Reopen the {year} contract? It becomes signed again.",
    'fac.confirm.contract.reopen.ok': "Reopen",
    'fac.confirm.contract.delete.title': "Delete the contract",
    'fac.confirm.contract.delete.text': "Delete the {year} contract and its objectives? Not possible if it has hours given or validated pay.",
    'fac.confirm.contract.delete.ok': "Delete",
    'fac.confirm.contract.renew.title': "Renew the contract",
    'fac.confirm.contract.renew.text': "Copy the {year} contract to next year, as a draft, with its objectives?",
    'fac.confirm.contract.renew.ok': "Renew",
    'fac.pay.title': "Pay",
    'fac.pay.monthlyGross': "Monthly gross fee",
    'fac.pay.monthlyNet': "Monthly net fee",
    'fac.pay.includedHours': "Hours included per month",
    'fac.pay.extraGross': "Extra hour gross",
    'fac.pay.extraNet': "Extra hour net",
    'fac.pay.extraCap': "Extra hours cap",
    'fac.pay.perMonth': "{hours} per month",
    'fac.pay.monthlyNote': "Beyond the {hours} included in the fee, each hour given is paid at the extra rate, up to the cap. Hours beyond the cap are flagged, not paid.",
    'fac.pay.noneNote': "This teacher is not paid through Kelassi.",
    'fac.rate.base': "general rate",
    'fac.rate.extraBase': "extra rate",
    'fac.rate.gross': "Gross hourly rate",
    'fac.rate.net': "Net hourly rate",
    'fac.rate.same': "One rate for all teaching types",
    'fac.rate.sameNote': "One rate for all teaching types.",
    'fac.rate.teachingType': "Teaching type",
    'fac.rate.surveillance': "Exam supervision",
    'fac.rate.survGross': "Supervision, gross rate",
    'fac.rate.survNet': "Supervision, net rate",
    'fac.rate.survHint': "Optional: otherwise the general rate applies",
    'fac.rate.note': "A rate per teaching type is optional: otherwise the general rate applies. Paid hours are those of sessions given by the teacher (themselves or as a replacement), read from planning.",
    'fac.document.title': "Signed document",
    'fac.document.none': "No document uploaded.",
    'fac.document.size': "{size} KB",
    'fac.document.download': "Download",
    'fac.document.upload': "Upload the signed contract",
    'fac.document.replace': "Replace",
    'fac.document.hint': "PDF, 10 MB at most",
    'fac.document.pdfOnly': "Only a PDF file is accepted.",
    'fac.document.tooBig': "The file exceeds 10 MB.",
    'fac.document.uploaded': "Document uploaded.",
    'fac.document.deleted': "Document deleted.",
    'fac.objectives.title': "Contractual objectives",
    'fac.objectives.titleYear': "Contractual objectives · {year}",
    'fac.objectives.sub': "What the teacher commits to teach, by subject, teaching type and class or group",
    'fac.objectives.contract': "Contract",
    'fac.objectives.noContract': "No contract: enter a contract for this year first.",
    'fac.objectives.empty': "No objective entered.",
    'fac.objectives.service': "Contract workload: {service}, i.e. {min} to {max}",
    'fac.objectives.over.weekly': "over {count} weeks",
    'fac.objectives.over.monthly': "over {count} months",
    'fac.objectives.over.yearly': "over the year",
    'fac.objectives.inRange': "Objective of {hours} within the contract workload",
    'fac.objectives.below': "Objective of {hours} below the minimum workload",
    'fac.objectives.above': "Objective of {hours} beyond the maximum workload",
    'fac.objectives.overCatalog': "beyond the curriculum volume",
    'fac.objectives.notPlanned': "not planned",
    'fac.objectives.fromAttributions': "Copy assignments",
    'fac.objectives.fromAttributionsDone': "Objectives created from assignments: {count}.",
    'fac.objectives.add': "Add an objective",
    'fac.objectives.edit': "Edit objective",
    'fac.objectives.remove': "Delete objective",
    'fac.objectives.saved': "Objective saved.",
    'fac.objectives.deleted': "Objective deleted.",
    'fac.objectives.dialogSub': "A subject, a teaching type, a class or the whole programme, a volume",
    'fac.objectives.subject': "Subject",
    'fac.objectives.class': "Class or group",
    'fac.objectives.classHint': "Optional",
    'fac.objectives.allClasses': "Whole programme",
    'fac.objectives.hours': "Volume",
    'fac.objectives.responsibleNote': "The teacher is also registered as a teacher of the subject in the catalogue, if not already.",
    'fac.objectives.note': "Adding an objective also registers the teacher as a teacher of the subject in the catalogue, if not already. Planning suggests first the teachers with an objective on the subject and class.",
    'fac.dash.realized': "Given",
    'fac.dash.realizedNote': "of {hours} objective · {percent} %",
    'fac.dash.noObjective': "no objective entered",
    'fac.dash.plannedRemaining': "Planned remaining",
    'fac.dash.plannedRemainingNote': "upcoming sessions, until the end of the contract",
    'fac.dash.toPlan': "Left to plan",
    'fac.dash.toPlanNote': "objective not covered by planning",
    'fac.dash.month': "This month",
    'fac.dash.monthNote': "sessions given: {count}",
    'fac.dash.byMonth': "Hours per month",
    'fac.dash.byMonthSub': "Sessions planned and given, all subjects",
    'fac.dash.noMonth': "No session this year.",
    'fac.dash.planned': "planned",
    'fac.dash.done': "given",
    'fac.dash.bySubject': "By subject",
    'fac.dash.noSubject': "No subject.",
    'fac.dash.todo': "To follow up",
    'fac.dash.todoEmpty': "Nothing to report.",
    'fac.dash.group.roll_missing': "{count} roll calls not taken",
    'fac.dash.group.marks_missing': "{count} assessments awaiting marks",
    'fac.dash.group.replacement': "{count} sessions given as a replacement",
    'fac.dash.group.cancelled': "{count} cancelled sessions",
    'fac.dash.group.out_of_contract': "{count} sessions outside any contract",
    'fac.dash.see': "View",
    'fac.dash.payDetail': "Details",
    'fac.dash.paid': "Paid this year",
    'fac.dash.paidNote': "net {net} · months paid: {count}",
    'fac.dash.current': "{month}",
    'fac.dash.currentNote': "net {net} · {state}",
    'fac.contractsList.title': "Contracts",
    'fac.contractsList.sub': "This year's contracts, all teachers",
    'fac.contractsList.count': "{count} contract(s) · year {year}",
    'fac.contractsList.empty': "No contract matches these criteria.",
    'fac.settings.title': "Teacher management · settings",
    'fac.settings.sub': "The Payroll module, its rules, and contract lists",
    'fac.settings.types': "Contract types",
    'fac.settings.categories': "Teacher categories",
    'fac.settings.permanent': "Permanent",
    'fac.settings.permanentHint': "Permanent contract (open-ended, tenured)",
    'fac.settings.active': "Offered in lists",
    'fac.settings.inactive': "hidden",
    'fac.settings.label': "Label",
    'fac.settings.addType': "Add a contract type",
    'fac.settings.addCategory': "Add a category",
    'fac.settings.edit': "Edit “{label}”",
    'fac.settings.removeType': "Delete contract type",
    'fac.settings.removeCategory': "Delete category",
    'fac.settings.used': "In use: cannot be deleted",
    'fac.settings.empty': "Empty list.",
    'fac.settings.saved': "Settings saved.",
    'pay.state.computed': "Computed",
    'pay.state.validated': "Validated",
    'pay.state.paid': "Paid",
    'pay.state.nothing': "Nothing to pay",
    'pay.state.blocked': "Blocked",
    'pay.blocker.rolls_missing': "Roll calls missing",
    'pay.blocker.no_contract': "No contract",
    'pay.blocker.draft_contract': "Draft contract",
    'pay.blocker.out_of_contract': "Sessions outside contract",
    'pay.blocker.over_cap': "Beyond the cap",
    'pay.blocker.no_rate': "Missing rate",
    'pay.filter.all': "All",
    'pay.filter.computed': "To validate",
    'pay.filter.validated': "Validated",
    'pay.filter.paid': "Paid",
    'pay.method.transfer': "Transfer",
    'pay.method.cheque': "Cheque",
    'pay.method.cash': "Cash",
    'pay.method.other': "Other",
    'pay.month.title': "Teacher payroll",
    'pay.month.sub': "Hours given this month, at the contract rate: validate, then mark paid",
    'pay.month.previous': "Previous month",
    'pay.month.next': "Next month",
    'pay.month.global': "Overall statement",
    'pay.month.export': "Export",
    'pay.month.empty': "No teacher to pay this month.",
    'pay.month.teachers': "Teachers: {count}",
    'pay.month.note': "Validating freezes the month for the teacher: hours, rates, adjustments. “Mark paid” records the date, method and transfer reference; Kelassi does not make the transfer. Validated months are exported for the payroll software.",
    'pay.kpi.gross': "Gross for the month",
    'pay.kpi.grossNote': "net {net}",
    'pay.kpi.hours': "Hours given",
    'pay.kpi.hoursNote': "of which {hours} extra",
    'pay.kpi.toValidate': "To validate",
    'pay.kpi.toValidateNote': "{validated} validated · {paid} paid",
    'pay.kpi.blocked': "Before validating",
    'pay.kpi.blockedNote': "teachers with blocking issues",
    'pay.column.month': "Month",
    'pay.column.hours': "Hours",
    'pay.column.realized': "Hours given",
    'pay.column.extra': "Extra",
    'pay.column.grossIn': "Gross ({currency})",
    'pay.column.netIn': "Net ({currency})",
    'pay.column.state': "Status",
    'pay.column.line': "Sessions given",
    'pay.column.classes': "Class",
    'pay.column.sessions': "Sessions",
    'pay.column.rate': "Gross rate",
    'pay.column.amount': "Gross amount",
    'pay.column.adjustment': "Adjustment",
    'pay.column.reason': "Reason",
    'pay.sessions': "sessions: {count}",
    'pay.line.extra': "extra hours",
    'pay.line.included': "including {hours} covered by the fixed amount",
    'pay.selectAll': "Select all",
    'pay.validate': "Validate the month",
    'pay.validateSelection': "Validate selection ({count})",
    'pay.validated': "Month(s) validated: {count}.",
    'pay.markPaid': "Mark paid ({count})",
    'pay.paid': "Statements marked paid: {count}.",
    'pay.reopen': "Reopen",
    'pay.reopen.title': "Reopen the validated statement",
    'pay.reopen.sub': "The month becomes computed again; statements: {count}",
    'pay.reopened': "Statements reopened: {count}.",
    'pay.unpay': "Cancel payment",
    'pay.unpay.title': "Cancel the payment",
    'pay.unpay.sub': "The statement becomes validated, unpaid; statements: {count}",
    'pay.unpaid': "Payments cancelled: {count}.",
    'pay.reason.label': "Reason",
    'pay.reason.required': "The reason is required.",
    'pay.pay.title': "Mark paid",
    'pay.pay.sub': "Validated statements: {count}. Kelassi does not make the transfer.",
    'pay.pay.submit': "Mark paid",
    'pay.pay.date': "Paid on",
    'pay.pay.method': "Method",
    'pay.pay.reference': "Reference",
    'pay.pay.referenceHint': "Transfer or cheque number, optional",
    'pay.openTeacher': "Open record",
    'pay.detail': "Details",
    'pay.statement.pdf': "Pay statement",
    'pay.statement.preview': "Statement preview",
    'pay.statement.number': "Statement {number}",
    'pay.statement.validated': "validated on {date} by {who}",
    'pay.statement.paid': "paid on {date} ({method} {reference})",
    'pay.gross': "Gross",
    'pay.net': "Net",
    'pay.extraHours': "of which {hours} extra",
    'pay.unpaidHours': "{hours} unpaid",
    'pay.replacementFor': "replacing {name}",
    'pay.outOfContract': "outside contract",
    'pay.teacher.months': "Months",
    'pay.teacher.noMonth': "No month this year.",
    'pay.teacher.detailTitle': "{month} · details",
    'pay.teacher.contract': "{year} contract",
    'pay.teacher.noContract': "No contract in force this month",
    'pay.teacher.noLine': "No paid session this month.",
    'pay.teacher.sessions': "Sessions of the month ({count})",
    'pay.teacher.note': "The amount is recomputed until the month is validated: a past session given, cancelled or reassigned updates it. A validated month is frozen; a correction goes through an adjustment of the next month.",
    'pay.adjustment.add': "Add an adjustment",
    'pay.adjustment.edit': "Edit adjustment",
    'pay.adjustment.remove': "Delete adjustment",
    'pay.adjustment.sub': "Bonus or deduction on {month}, with a reason",
    'pay.adjustment.kind.bonus': "Bonus",
    'pay.adjustment.kind.deduction': "Deduction",
    'pay.adjustment.gross': "Gross amount",
    'pay.adjustment.net': "Net amount",
    'pay.adjustment.hint': "Only while the month is not validated; a correction to a validated month goes into the next month.",
    'pay.adjustment.saved': "Adjustment saved.",
    'pay.adjustment.deleted': "Adjustment deleted.",
    'pay.settings.module': "Module",
    'pay.settings.reserved': "Reserved to the Super Administrator",
    'pay.settings.enabled': "Teacher payroll",
    'pay.settings.enabledHint': "Turns on the Pay tab of records, the monthly payroll and rates in contracts. Off: nothing is deleted, everything is hidden.",
    'pay.settings.enabledOn': "Turned on on {date} by {who}",
    'pay.settings.rules': "Payroll rules",
    'pay.settings.payReplacements': "Pay sessions to the teacher who gave them (the replacement, not the planned teacher)",
    'pay.settings.paySurveillance': "Pay exam supervision, at the contract's “Supervision” rate",
    'pay.settings.requireRolls': "Do not validate a month while the teacher has missing roll calls that month",
    'pay.settings.currency': "Currency",
    'pay.settings.currencyHint': "The institution's",
    'pay.settings.decimals': "Amount rounding",
    'pay.settings.decimals2': "To the cent",
    'pay.settings.decimals0': "To the unit",
    'pay.settings.pattern': "Statement numbering",
    'pay.settings.insert': "Insert",
    'pay.settings.saved': "Payroll settings saved.",
    'pay.modules.name': "Teacher payroll",
    'pay.modules.rules': "Payroll rules and contract lists",
    'violation.fac.contract_overlap': "This contract overlaps another contract of the teacher.",
    'violation.pay.contract_locked': "A pay month of this contract is validated: rates and mode are locked.",
    'violation.pay.rolls_missing': "Some roll calls are missing this month: complete them before validating.",
    'violation.com.out_of_scope': "This recipient is outside your scope: you can only notify the students you teach.",
    'violation.com.not_chattable': "This person cannot be added to a conversation.",
    'violation.com.not_a_group': "This action only applies to a group conversation.",
    'violation.com.type_not_allowed': "You cannot send this type of notification.",
    'violation.com.no_year': "No current academic year.",
    'violation.com.no_recipient': "No recipient has an active account: invite the students to the portal first.",
    'violation.com.nothing_to_remind': "Every recipient has read the notification.",
    'violation.com.replies_closed': "This notification does not accept replies.",
    'violation.com.no_reply': "The student has not replied yet.",
    'violation.com.too_many_attachments': "Three attachments at most.",
    'violation.com.unknown_attachment': "Attachment not found: upload it again.",
    'violation.com.system_type': "The « Kelassi » type is reserved for the application's alerts.",
    'violation.com.type_used': "This type has been used: deactivate it instead of deleting it.",
    'com.allRead': "Everything is marked as read.",
    'com.archive': "Archive",
    'com.unarchive': "Unarchive",
    'com.archived': "Notification archived: it now only shows under “Archived”.",
    'com.unarchived': "Notification unarchived.",
    'com.backToList': "Back to notifications",
    'com.bell.all': "All notifications",
    'com.bell.empty': "Nothing new.",
    'com.bell.hint': "Read notifications no longer show here",
    'com.bell.label': "Unread notifications: {count}",
    'com.bell.none': "Notifications: none unread",
    'com.bell.title': "Unread notifications",
    'com.bell.unread': "Unread · {count}",
    'com.box.label': "Box",
    'com.box.received': "Received",
    'com.box.sent': "Sent",
    'com.box.archived': "Archived",
    'com.chip.remove': "Remove {label}",
    'com.column.class': "Class",
    'com.column.fromSubject': "From and subject",
    'com.column.read': "Read",
    'com.column.received': "Received",
    'com.column.sent': "Sent",
    'com.column.reply': "Reply",
    'com.column.state': "State",
    'com.column.student': "Student",
    'com.column.type': "Type",
    'com.compose.addTarget': "Add a programme, a class, a group or a student…",
    'com.compose.body': "Message",
    'com.compose.bodyPlaceholder': "The message will appear here.",
    'com.compose.countLine': "Students who will receive the notification: {students} · with an e-mail address: {emails}",
    'com.compose.draftTitle': "Notification draft",
    'com.compose.draftTrail': "Draft",
    'com.compose.email': "Also send by e-mail",
    'com.compose.emailCount': "Also send by e-mail (addresses: {count})",
    'com.compose.incomplete': "Choose a type, a subject, a message and at least one recipient.",
    'com.compose.new': "New notification",
    'com.compose.noMatch': "No programme, class or student matches.",
    'com.compose.noTarget': "Add at least one recipient.",
    'com.compose.note': "Students cannot write first: they only reply to a notification that allows it. Each reply is seen only by the sender and the notification's managers.",
    'com.compose.panel': "Notification",
    'com.compose.preview': "Student preview",
    'com.compose.recipients': "Recipients",
    'com.compose.replies': "Students may reply",
    'com.compose.repliesNone': "No reply (set by the type {type})",
    'com.compose.repliesRequired': "Students may reply (set by the type {type})",
    'com.compose.saveDraft': "Save draft",
    'com.compose.saved': "Draft saved.",
    'com.compose.send': "Send",
    'com.compose.sendTo': "Send to students ({count})",
    'com.compose.studentsCount': "students: {count}",
    'com.compose.sub': "To students: by student, class, group or programme",
    'com.compose.subject': "Subject",
    'com.compose.subjectPlaceholder': "Notification subject",
    'com.compose.title': "New notification",
    'com.compose.trail': "New",
    'com.compose.type': "Notification type",
    'com.draft': "Draft",
    'com.file.attach': "Attach a file",
    'com.file.hint': "PDF or image, 5 MB at most, 3 files",
    'com.file.image': "Image",
    'com.file.remove': "Remove {name}",
    'com.file.tooBig': "{name} is larger than 5 MB.",
    'com.file.tooMany': "Attachments: {count} at most.",
    'com.file.uploading': "Uploading…",
    'com.file.wrongType': "{name}: only PDF, PNG and JPEG are accepted.",
    'com.filter.allTypes': "All types",
    'com.filter.period': "Period",
    'com.filter.type': "Type",
    'com.handled': "handled",
    'com.inbox.empty': "No notifications.",
    'com.inbox.emptyUnread': "No unread notifications.",
    'com.inbox.search': "Search",
    'com.inbox.sub': "Everything you received and sent · read items stay here, they no longer show in the bell",
    'com.inbox.title': "Notifications",
    'com.invite.button': "Invite to the portal",
    'com.invite.done': "Invitations sent: {invited} · without e-mail address: {noEmail} · account already existing: {existing}",
    'com.invite.selectAll': "Select the students on this page",
    'com.invite.selection': "Invite to the portal ({count})",
    'com.invite.submit': "Send invitations",
    'com.invite.textClass': "Students of class {name} who have an e-mail address and no account yet will receive an activation link. They will be able to read and reply to their notifications.",
    'com.invite.textSelection': "Selected students: {count}. Those with an e-mail address and no account yet will receive an activation link. They will be able to read and reply to their notifications.",
    'com.invite.textStudent': "{name} will receive an activation link at their e-mail address, if they have no account yet. They will be able to read and reply to their notifications.",
    'com.invite.textTraining': "Students enrolled in {name} who have an e-mail address and no account yet will receive an activation link.",
    'com.invite.title': "Invite to the portal",
    'com.kpi.emails': "E-mails",
    'com.kpi.emailsNote': "sent · failed: {count}",
    'com.kpi.read': "Read",
    'com.kpi.readNote': "{percent} % · unread: {count}",
    'com.kpi.replies': "Replies",
    'com.kpi.repliesNote': "to handle: {count}",
    'com.kpi.sentTo': "Sent to",
    'com.kpi.sentToNote': "students · {date}",
    'com.live.on': "Real time on",
    'com.live.onHint': "Broadcast server online · fallback: refresh every 30 s",
    'com.live.polling': "Refresh every 30 s",
    'com.live.pollingHint': "The broadcast server does not answer or is not installed: the bell updates every 30 s.",
    'com.markAllRead': "Mark all as read",
    'com.message': "Message sent",
    'com.messages.archive': "Archive the conversation",
    'com.messages.back': "Back to conversations",
    'com.messages.conversations': "Conversations",
    'com.messages.empty': "No conversations. Start one with a colleague.",
    'com.messages.filter.all': "All",
    'com.messages.filter.unread': "Unread",
    'com.messages.filter.groups': "Groups",
    'com.messages.groupName': "Group name",
    'com.messages.groupOf': "group · people: {count}",
    'com.messages.leave': "Leave the group",
    'com.messages.leaveConfirm': "You will no longer receive messages from “{name}”. Messages already exchanged remain visible to the other participants.",
    'com.messages.members': "Participants",
    'com.messages.new': "New conversation",
    'com.messages.noMatch': "No conversation matches.",
    'com.messages.noMessage': "No messages",
    'com.messages.older': "Earlier messages",
    'com.messages.online': "online: {count}",
    'com.messages.onlineOne': "online",
    'com.messages.pick': "Pick a conversation, or start a new one.",
    'com.messages.placeholder': "Write a message… (Enter to send, Shift + Enter for a new line)",
    'com.messages.readBy': "read by {count}",
    'com.messages.rename': "Rename the group",
    'com.messages.search': "Search a person, a message",
    'com.messages.start': "No messages yet: write the first one.",
    'com.messages.sub': "Between staff members and teachers · in real time",
    'com.messages.title': "Messages",
    'com.messages.typing': "{names} is typing…",
    'com.messages.unreadCount': "Unread messages: {count}",
    'com.messages.you': "you",
    'com.more': "Show more",
    'com.newConv.choose': "Choose",
    'com.newConv.function': "Role",
    'com.newConv.groupName': "Group name (optional, from 3 people)",
    'com.newConv.none': "Nobody matches.",
    'com.newConv.note': "Students do not appear here: you write to them with a notification. A teacher can write to other teachers and to the administration.",
    'com.newConv.person': "Person",
    'com.newConv.required': "Choose at least one person.",
    'com.newConv.search': "Name, role",
    'com.newConv.sub': "Choose one or more people",
    'com.newConv.submit': "Start the conversation",
    'com.newConv.title': "New conversation",
    'com.period.all': "Any time",
    'com.period.week': "This week",
    'com.period.month': "This month",
    'com.period.year': "This year",
    'com.read': "read",
    'com.recipient.emailFailed': "e-mail failed",
    'com.recipient.readAt': "read {time}",
    'com.recipient.seeReply': "See the reply",
    'com.recipient.unread': "unread",
    'com.recipients': "Recipients",
    'com.recipients.all': "All ({count})",
    'com.recipients.unread': "Unread ({count})",
    'com.recipients.replied': "With a reply ({count})",
    'com.recipients.empty': "No recipients in this view.",
    'com.remind': "Remind unread ({count})",
    'com.remind.done': "Reminder sent to students: {count}.",
    'com.remind.submit': "Remind",
    'com.remind.text': "Students who have not read the notification ({count}) will receive it again, by e-mail too if the original had one.",
    'com.remind.title': "Remind unread recipients",
    'com.replies.empty': "No replies yet.",
    'com.replies.handle': "Mark handled",
    'com.replies.unhandle': "Reopen",
    'com.replies.pick': "Pick a reply in the recipients list.",
    'com.replies.placeholder': "Reply to {name}…",
    'com.replies.private': "Private reply: only the student sees it",
    'com.replies.title': "Replies · {name}",
    'com.replies.none': "no",
    'com.replies.optional': "sender's choice",
    'com.replies.required': "always",
    'com.replies.noneLong': "No reply",
    'com.replies.optionalLong': "Sender's choice",
    'com.replies.requiredLong': "Always allowed",
    'com.repliesAllowed': "replies allowed",
    'com.repliesClosed': "no replies",
    'com.reply.action': "Reply",
    'com.reply.none': "This notification does not expect a reply.",
    'com.reply.placeholder': "Reply…",
    'com.send': "Send",
    'com.sentBy': "Sent by {name}",
    'com.senders.staff': "staff",
    'com.senders.staff_teachers': "staff, teachers",
    'com.senders.direction': "management",
    'com.settings.active': "Type offered when sending",
    'com.settings.addType': "Add a type",
    'com.settings.attachments': "Attachments",
    'com.settings.attachmentsMb': "PDF and images, {count} MB",
    'com.settings.digest': "E-mail copy of messages unread after 24 h (staff and teachers)",
    'com.settings.editType': "Edit “{label}”",
    'com.settings.emailDefault': "E-mail by default",
    'com.settings.emailDefaultHint': "Offer e-mail sending by default",
    'com.settings.inactive': "inactive",
    'com.settings.keep': "Retention and delivery",
    'com.settings.label': "Label",
    'com.settings.removeConfirm': "Delete the type “{label}”? Notifications already sent keep it.",
    'com.settings.removeType': "Delete the type",
    'com.settings.replies': "Reply",
    'com.settings.retention': "Message retention",
    'com.settings.retention5': "5 years, like the audit trail",
    'com.settings.retentionYears': "Years: {count}",
    'com.settings.rules': "Who can write to whom",
    'com.settings.saved': "Communication settings saved.",
    'com.settings.senders': "Who can send it",
    'com.settings.staffChat': "Staff write to each other and to teachers",
    'com.settings.studentsFirst': "Students can write first to the administration (off: they only reply)",
    'com.settings.sub': "Notification types, who can write to whom, retention",
    'com.settings.system': "Kelassi (alerts)",
    'com.settings.teacherChat': "Teachers write to each other and to the administration",
    'com.settings.teacherNotify': "Teachers notify the students they teach (their classes and groups of the year)",
    'com.settings.title': "Communication · settings",
    'com.settings.tone': "Colour",
    'com.settings.type': "Type",
    'com.settings.typeDeleted': "Type “{label}” deleted.",
    'com.settings.typeSaved': "Notification type saved.",
    'com.settings.types': "Notification types",
    'com.state.label': "Reading",
    'com.state.all': "All",
    'com.state.unread': "Unread",
    'com.state.read': "Read",
    'com.student.noneRead': "No read notifications.",
    'com.student.noneUnread': "No unread notifications.",
    'com.student.read': "Read notifications",
    'com.student.showRead': "Show read notifications",
    'com.student.sub': "Messages from your institution · reply when allowed",
    'com.student.unread': "Unread: {count}",
    'com.target.student': "Student",
    'com.target.class': "Class",
    'com.target.training': "Programme",
    'com.target.studentChip': "Student · {label}",
    'com.target.classChip': "Class · {label} ({count})",
    'com.target.trainingChip': "Programme · {label} ({count})",
    'com.time.now': "just now",
    'com.time.today': "Today",
    'com.time.yesterday': "yesterday",
    'com.tone.info': "Blue",
    'com.tone.warn': "Amber",
    'com.tone.violet': "Purple",
    'com.tone.danger': "Red",
    'com.tone.muted': "Grey",
    'com.tone.ok': "Green",
    'com.type.kelassi': "Kelassi",
    'com.type.message': "Message",
    'com.type.reply': "Reply",
    'com.unread': "unread",
    'rel.none': "None",
    'rel.open': "Open",
    'rel.removed': "\"{label}\" deleted.",
    'rel.remove.confirm': "Delete \"{label}\"? This cannot be undone.",
    'rel.backToOrganisations': "Organisations",
    'rel.backToAgreements': "Agreements",
    'rel.status.prospect': "Prospect",
    'rel.status.active': "Active partner",
    'rel.status.dormant': "No contact for 12 months",
    'rel.status.renewal': "Agreement to renew",
    'rel.filter.allTypes': "All types",
    'rel.filter.allSectors': "All sectors",
    'rel.filter.allStatuses': "All relations",
    'rel.filter.allCities': "All cities",
    'rel.filter.allOwners': "All owners",
    'rel.column.organisation': "Organisation",
    'rel.column.agreement': "Agreement",
    'rel.column.agreementOrg': "Agreement and organisation",
    'rel.column.end': "End",
    'rel.column.mainContact': "Main contact",
    'rel.column.agreements': "Agreements",
    'rel.column.interns': "Interns",
    'rel.column.relation': "Relation",
    'rel.column.period': "Period",
    'rel.column.state': "Status",
    'rel.column.alumnus': "Graduate",
    'rel.column.situation': "Situation",
    'rel.column.updated': "Updated",
    'rel.kpi.active': "Active partners",
    'rel.kpi.activeNote': "out of {count} organisations",
    'rel.kpi.agreements': "Agreements in force",
    'rel.kpi.agreementsNote': "{count} ending within {days} days",
    'rel.kpi.prospects': "Prospection",
    'rel.kpi.prospectsNote': "{count} meetings this month",
    'rel.kpi.insertion': "Graduates employed",
    'rel.kpi.insertionNote': "class of {year}, after 6 months",
    'rel.kpi.insertionNone': "no survey yet",
    'rel.dashboard.title': "External relations",
    'rel.dashboard.sub': "Partners, agreements, prospection and graduate employment",
    'rel.dashboard.subYear': "Partners, agreements, prospection and graduate employment · year {year}",
    'rel.dashboard.ending': "Agreements ending",
    'rel.dashboard.endingSub': "Within the next {days} days",
    'rel.dashboard.allAgreements': "All",
    'rel.dashboard.noEnding': "No agreement is ending soon.",
    'rel.dashboard.bySector': "Active partners by sector",
    'rel.dashboard.noSector': "No active partner yet.",
    'rel.dashboard.lastExchanges': "Latest exchanges",
    'rel.dashboard.directory': "Directory",
    'rel.orgs.title': "Organisations",
    'rel.orgs.sub': "Companies, universities, associations, public bodies",
    'rel.orgs.count': "{count} organisations · companies, universities, associations, public bodies",
    'rel.orgs.search': "Name, acronym, contact, city",
    'rel.orgs.empty': "No organisation matches.",
    'rel.orgs.agreementsCount': "Agreements: {count}",
    'rel.org.new': "New organisation",
    'rel.org.newSub': "A company, university, association or public body the school works with",
    'rel.org.editProfile': "Edit · {name}",
    'rel.org.name': "Name",
    'rel.org.legalForm': "Legal form",
    'rel.org.acronym': "Acronym",
    'rel.org.type': "Type",
    'rel.org.sector': "Sector",
    'rel.org.noSector': "No sector",
    'rel.org.status': "Relation",
    'rel.org.statusHint': "Leave \"computed\": Kelassi derives it from agreements and exchanges.",
    'rel.org.statusComputed': "Computed by Kelassi",
    'rel.org.owner': "Owner at the school",
    'rel.org.city': "City",
    'rel.org.website': "Website",
    'rel.org.partnerSince': "Partner since",
    'rel.org.since': "partner since {date}",
    'rel.org.saved': "Organisation saved.",
    'rel.org.change': "Change organisation",
    'rel.org.searchPlaceholder': "Type at least two letters",
    'rel.org.interns': "Interns hosted",
    'rel.org.internsNote': "linked students: {count}",
    'rel.org.alumni': "Graduates employed",
    'rel.org.alumniNote': "current situation known",
    'rel.org.studentsSub': "Students on internship and graduates working there",
    'rel.org.internsLater': "Internships will be tracked by the Internships module: the counter stays at zero until then.",
    'rel.org.toAlumni': "See graduates",
    'rel.tab.summary': "Summary",
    'rel.tab.contacts': "Contacts",
    'rel.tab.agreements': "Agreements",
    'rel.tab.exchanges': "Exchanges",
    'rel.tab.students': "Students and graduates",
    'rel.tab.history': "History",
    'rel.tab.count': "{label} ({count})",
    'rel.contact.new': "New contact",
    'rel.contact.edit': "Edit · {name}",
    'rel.contact.lastName': "Last name",
    'rel.contact.firstNames': "First names",
    'rel.contact.email': "Email",
    'rel.contact.phone': "Phone",
    'rel.contact.function': "Function",
    'rel.contact.main': "Main contact of this organisation",
    'rel.contact.mainShort': "main contact",
    'rel.contact.alumnus': "graduate",
    'rel.contact.none': "No contact.",
    'rel.contact.saved': "Contact saved.",
    'rel.contact.remove': "Remove contact",
    'rel.exchange.note': "Log an exchange",
    'rel.exchange.new': "Log an exchange",
    'rel.exchange.edit': "Edit exchange",
    'rel.exchange.sub': "A call, email, meeting, visit, event or note",
    'rel.exchange.kind': "Kind of exchange",
    'rel.exchange.kind.call': "Call",
    'rel.exchange.kind.email': "Email",
    'rel.exchange.kind.meeting': "Meeting",
    'rel.exchange.kind.visit': "Visit",
    'rel.exchange.kind.event': "Event",
    'rel.exchange.kind.note': "Note",
    'rel.exchange.on': "Date",
    'rel.exchange.time': "Time",
    'rel.exchange.planned': "planned",
    'rel.exchange.plannedHint': "Upcoming (planned meeting)",
    'rel.exchange.subject': "Subject",
    'rel.exchange.body': "Notes",
    'rel.exchange.accounts': "For the school",
    'rel.exchange.addAccount': "Add a participant",
    'rel.exchange.contacts': "For the partner",
    'rel.exchange.addContact': "Add a contact",
    'rel.exchange.agreement': "Related agreement",
    'rel.exchange.with': "with {names}",
    'rel.exchange.none': "No exchange logged.",
    'rel.exchange.saved': "Exchange saved.",
    'rel.exchange.remove': "Delete exchange",
    'rel.exchange.journal': "Full log",
    'rel.exchange.journalSub': "Calls, emails, meetings and visits, newest first",
    'rel.exchange.older': "Older",
    'rel.agreements.title': "Agreements",
    'rel.agreements.sub': "Current agreements, their end dates and renewals",
    'rel.agreements.empty': "No agreement matches.",
    'rel.agreements.filter.all': "All",
    'rel.agreements.filter.ending': "Ending",
    'rel.agreements.filter.draft': "Drafts",
    'rel.agreements.filter.ended': "Ended",
    'rel.agreement.new': "New agreement",
    'rel.agreement.newFor': "New agreement · {name}",
    'rel.agreement.edit': "Edit · {title}",
    'rel.agreement.sub': "A written agreement: its purpose, duration and each party's commitments",
    'rel.agreement.organisation': "Organisation",
    'rel.agreement.type': "Agreement type",
    'rel.agreement.title': "Title",
    'rel.agreement.startsOn': "Start",
    'rel.agreement.endsOn': "End",
    'rel.agreement.endsOnHint': "Empty: no end date",
    'rel.agreement.indefinite': "No end date",
    'rel.agreement.renewal': "Renewal",
    'rel.agreement.notice': "Notice (months)",
    'rel.agreement.owner': "Owner at the school",
    'rel.agreement.trainings': "Programmes concerned",
    'rel.agreement.addTraining': "Add a programme",
    'rel.agreement.commitments': "Commitments",
    'rel.agreement.commitment': "Commitment {n}",
    'rel.agreement.commitmentLabel': "Commitment",
    'rel.agreement.party': "Party",
    'rel.agreement.quantity': "Quantity",
    'rel.agreement.addCommitment': "Add a commitment",
    'rel.agreement.noCommitment': "No commitment.",
    'rel.agreement.note': "Note",
    'rel.agreement.saveDraft': "Save as draft",
    'rel.agreement.saveSign': "Save and mark signed",
    'rel.agreement.saved': "Agreement saved.",
    'rel.agreement.savedSigned': "Agreement saved and signed.",
    'rel.agreement.reminder': "Kelassi reminds the owner {days} days before the end (bell alert) and offers to renew by copying the agreement.",
    'rel.agreement.none': "No agreement.",
    'rel.agreement.state.draft': "draft",
    'rel.agreement.state.signed': "in force",
    'rel.agreement.state.ended': "ended",
    'rel.agreement.state.terminated': "terminated",
    'rel.agreement.endsIn': "ends in {days} d",
    'rel.agreement.from': "since {date}",
    'rel.agreement.details': "The agreement",
    'rel.agreement.signedOn': "Signed on",
    'rel.agreement.sign': "Mark signed",
    'rel.agreement.signed': "Agreement signed.",
    'rel.agreement.end': "Close at term",
    'rel.agreement.endConfirm': "The agreement becomes \"ended\". It remains viewable and can be renewed.",
    'rel.agreement.ended': "Agreement closed.",
    'rel.agreement.terminate': "Terminate",
    'rel.agreement.terminated': "Agreement terminated.",
    'rel.agreement.reason': "Reason",
    'rel.agreement.renew': "Renew",
    'rel.agreement.renewConfirm': "Kelassi copies the agreement into a new draft starting the day after its end ({date}). You review it before signing.",
    'rel.agreement.renewed': "New draft created.",
    'rel.agreement.renewedFrom': "Renews:",
    'rel.agreement.renewedBy': "Renewed by:",
    'rel.agreement.delete': "Delete draft",
    'rel.agreement.lifecycle': "Agreement lifecycle",
    'rel.renewal.none': "No renewal",
    'rel.renewal.tacit': "Tacit",
    'rel.renewal.explicit': "Explicit",
    'rel.renewal.withNotice': "{renewal}, {months} months notice",
    'rel.party.school': "The school",
    'rel.party.partner': "The partner",
    'rel.document.title': "Signed agreement",
    'rel.document.hint': "PDF, 10 MB max",
    'rel.document.none': "No document uploaded.",
    'rel.document.upload': "Upload the signed agreement",
    'rel.document.replace': "Replace the document",
    'rel.document.remove': "Remove the document",
    'rel.document.uploaded': "Document uploaded.",
    'rel.document.removed': "Document removed.",
    'rel.document.tooBig': "The file exceeds 10 MB.",
    'rel.prospects.title': "Prospection",
    'rel.prospects.sub': "Organisations the school wants to win as partners · drag a card to change its stage",
    'rel.prospects.subRead': "Organisations the school wants to win as partners",
    'rel.prospects.all': "All",
    'rel.prospects.mine': "Mine",
    'rel.prospects.noStage': "No prospection stage: add some in the settings.",
    'rel.prospects.emptyStage': "No lead",
    'rel.prospects.note': "Each stage keeps its date and author; \"Partner\" offers to create the agreement. A lead with no exchange for {days} days is flagged to its owner.",
    'rel.prospect.new': "Add a lead",
    'rel.prospect.newSub': "An organisation to win as a partner, and what the school expects",
    'rel.prospect.organisation': "Organisation",
    'rel.prospect.mode.existing': "In the directory",
    'rel.prospect.mode.new': "New organisation",
    'rel.prospect.objective': "Objective",
    'rel.prospect.objectiveHint': "For example: 4 audit internships in spring",
    'rel.prospect.owner': "Owner",
    'rel.prospect.ownerMe': "Me",
    'rel.prospect.stage': "Stage",
    'rel.prospect.saved': "Lead added.",
    'rel.prospect.addHere': "Add",
    'rel.prospect.followedBy': "owner: {name}",
    'rel.prospect.idle': "no exchange for {days} d",
    'rel.prospect.noExchange': "no exchange",
    'rel.prospect.lastExchange': "last exchange: {date}",
    'rel.prospect.moveMenu': "Move to… · {name}",
    'rel.prospect.moveTo': "Move to \"{stage}\"",
    'rel.prospect.moved': "{name}: stage \"{stage}\".",
    'rel.prospect.wonTitle': "A new partner",
    'rel.prospect.wonText': "{name} becomes a partner. Create the agreement now?",
    'rel.prospect.close': "Close the lead",
    'rel.prospect.closeConfirm': "The {name} lead leaves the board. Its history stays in the organisation file.",
    'rel.prospect.closed': "Lead closed.",
    'rel.alumni.title': "Graduates",
    'rel.alumni.sub': "Career paths of former students and employment by class",
    'rel.alumni.kpi.followed': "Graduates followed",
    'rel.alumni.kpi.followedNote': "all classes",
    'rel.alumni.kpi.employed': "Employed at 6 months",
    'rel.alumni.kpi.employedNote': "latest class",
    'rel.alumni.kpi.employedYear': "class of {year} · respondents: {count}",
    'rel.alumni.kpi.studying': "Further studies",
    'rel.alumni.kpi.studyingNote': "at home or abroad",
    'rel.alumni.kpi.noNews': "No news",
    'rel.alumni.kpi.noNewsNote': "to follow up through the survey",
    'rel.alumni.segment.all': "All",
    'rel.alumni.segment.employed': "Employed",
    'rel.alumni.segment.studying': "Studying",
    'rel.alumni.segment.unknown': "No news",
    'rel.alumni.status.employed': "employed",
    'rel.alumni.status.studying': "studying",
    'rel.alumni.status.seeking': "job seeking",
    'rel.alumni.status.other': "other",
    'rel.alumni.status.unknown': "no news",
    'rel.alumni.search': "Graduate name",
    'rel.alumni.year': "Class",
    'rel.alumni.allYears': "All classes",
    'rel.alumni.training': "Programme",
    'rel.alumni.allTrainings': "All programmes",
    'rel.alumni.promotion': "Class of {year}",
    'rel.alumni.empty': "No graduate matches.",
    'rel.alumni.select': "Select {name}",
    'rel.alumni.selectPage': "Select the whole page",
    'rel.alumni.note': "Graduates update their own situation through the survey link; the school can also enter it. Internships are not positions: they will go to the Internships module.",
    'rel.position.new': "Add a position",
    'rel.position.edit': "Edit position",
    'rel.position.status': "Situation",
    'rel.position.jobTitle': "Job title",
    'rel.position.programme': "Programme followed",
    'rel.position.organisation': "Employer or institution",
    'rel.position.linkOrganisation': "Pick the organisation from the directory",
    'rel.position.city': "City",
    'rel.position.country': "Country",
    'rel.position.startsOn': "From",
    'rel.position.endsOn': "Until",
    'rel.position.endsOnHint': "Empty: current position",
    'rel.position.current': "current",
    'rel.position.today': "today",
    'rel.position.none': "No known position.",
    'rel.position.saved': "Position saved.",
    'rel.position.remove': "Delete position",
    'rel.position.sourceLine': "Source: {source} · updated on {date}",
    'rel.position.source.survey': "survey",
    'rel.position.source.school': "entered by the school",
    'rel.position.source.legacy': "former Kelassi",
    'rel.survey.title': "Employment survey",
    'rel.survey.sub': "A personal link by email, valid for 60 days, no account needed",
    'rel.survey.launch': "Launch the employment survey",
    'rel.survey.launchSelected': "Survey the selected graduates ({count})",
    'rel.survey.scope': "Recipients",
    'rel.survey.byYear': "A class",
    'rel.survey.selected': "The selected graduates ({count})",
    'rel.survey.year': "Class",
    'rel.survey.send': "Send",
    'rel.survey.hint': "Graduates without an email address are skipped and counted. A graduate who already has a valid link receives a new one.",
    'rel.survey.sent': "Survey sent: {sent}.",
    'rel.survey.sentSkipped': "Survey sent: {sent}; without email: {skipped}.",
    'rel.insertion.title': "Employment survey",
    'rel.insertion.sub': "Tell us where you are since graduating",
    'rel.insertion.hello': "Hello {name}, tell us where you are since graduating",
    'rel.insertion.invalid': "This link is not valid. Check that it is complete, or ask your school for a new one.",
    'rel.insertion.expired': "This link has expired. Ask your school for a new one.",
    'rel.insertion.last': "Last known situation: {situation} ({date})",
    'rel.insertion.already': "You have already answered: a new answer updates your situation.",
    'rel.insertion.question': "Your situation today",
    'rel.insertion.status.employed': "I have a job",
    'rel.insertion.status.studying': "I am studying",
    'rel.insertion.status.seeking': "I am looking for a job",
    'rel.insertion.status.other': "Other situation",
    'rel.insertion.employer': "Employer",
    'rel.insertion.school': "Institution",
    'rel.insertion.since': "Since",
    'rel.insertion.submit': "Send my answer",
    'rel.insertion.thanks': "Thank you: your situation is recorded.",
    'rel.insertion.privacy': "Your answer is read only by your school, to follow its graduates' employment.",
    'rel.list.types': "Organisation types",
    'rel.list.sectors': "Business sectors",
    'rel.list.agreementTypes': "Agreement types",
    'rel.list.functions': "Contact functions",
    'rel.list.stages': "Prospection stages",
    'rel.list.stagesSub': "In board order; a won stage offers the agreement",
    'rel.list.won': "won",
    'rel.list.lost': "lost",
    'rel.list.open': "Open stage",
    'rel.list.wonHint': "Won stage (partner)",
    'rel.list.lostHint': "Lost stage",
    'rel.settings.title': "External relations · settings",
    'rel.settings.sub': "The module lists and reminders",
    'rel.settings.saved': "Settings saved.",
    'rel.settings.listSaved': "List updated.",
    'rel.settings.empty': "Empty list.",
    'rel.settings.add': "Add · {list}",
    'rel.settings.edit': "Edit · {label}",
    'rel.settings.label': "Label",
    'rel.settings.position': "Order",
    'rel.settings.outcome': "Kind of stage",
    'rel.settings.active': "Offered in the lists",
    'rel.settings.inactive': "retired",
    'rel.settings.used': "In use: retire it rather than delete it",
    'rel.settings.remove': "Delete entry",
    'rel.settings.module': "External relations module",
    'rel.settings.moduleSub': "Super Administrator only",
    'rel.settings.enabled': "Module enabled",
    'rel.settings.enabledHint': "When disabled, the module leaves the menu without deleting anything.",
    'rel.settings.enabledOn': "Enabled on {date} by {who}",
    'rel.settings.reminders': "Reminders and access",
    'rel.settings.window': "Reminder before an agreement ends (days)",
    'rel.settings.windowHint': "Alert to the owner and \"Agreements ending\" list",
    'rel.settings.idle': "Lead without exchange flagged after (days)",
    'rel.settings.idleHint': "Alert to the lead owner",
    'rel.settings.surveyAuto': "Send the employment survey 6 months after graduation",
    'rel.settings.teachersRead': "Open the partner directory to teachers (read only)",
    'rel.settings.remindersHint': "Alerts arrive in the bell when Communication is enabled.",
    'rel.modules.title': "Modules",
    'rel.modules.sub': "Enable or disable the institution's optional modules · Super Administrator only",
    'rel.modules.finance': "Finance",
    'rel.modules.financeHint': "Invoicing, payments, student accounts",
    'rel.modules.payroll': "Teacher payroll",
    'rel.modules.payrollHint': "Teacher pay, monthly payroll",
    'rel.modules.communication': "Communication",
    'rel.modules.communicationHint': "Staff messaging, student notifications, alerts",
    'rel.modules.relations': "External relations",
    'rel.modules.relationsHint': "Organisations, agreements, prospection, graduates",
    'rel.modules.enabledOn': "Enabled on {date} · {who}",
    'rel.modules.disabled': "Disabled",
    'rel.modules.settings': "Settings",
    'rel.modules.switch': "Enable the {name} module",
    'rel.modules.switchedOn': "{name} module enabled.",
    'rel.modules.switchedOff': "{name} module disabled.",
    'rel.modules.offTitle': "Disable · {name}",
    'rel.modules.offText': "Its rights are removed from the menu and screens, without deleting anything; enabling it again restores everything as it was.",
    'rel.modules.off': "Disable",
    'rel.modules.note': "Disabling a module removes its rights from the menu and screens without deleting anything; enabling it again restores everything. Communication can now be disabled too.",
    'rel.modules.financeTitle': "Finance · settings",
    'rel.modules.back': "Modules",
    'violation.rel.inactive': "The external relations module is not enabled.",
    'violation.rel.not_found': "Not found.",
    'violation.rel.duplicate': "This label already exists in the list.",
    'violation.rel.in_use': "In use: retire it rather than delete it.",
    'violation.rel.dates_order': "The end must come after the start.",
    'violation.rel.not_draft': "Only a draft can do this.",
    'violation.rel.not_signed': "The agreement is not in force.",
    'violation.rel.no_end': "An agreement with no end date cannot be renewed: edit it.",
    'violation.rel.already_renewed': "This agreement has already been renewed.",
    'violation.rel.document_invalid': "The file is not a PDF.",
    'violation.rel.document_too_large': "The file exceeds 10 MB.",
    'violation.rel.organisation_required': "Pick an organisation or name a new one.",
    'violation.rel.already_prospect': "This organisation already has an open lead.",
    'violation.rel.prospect_closed': "This lead is closed.",
    'violation.rel.not_alumnus': "This student is not a graduate.",
    'violation.rel.no_recipient': "No graduate to survey.",
    'violation.rel.token_invalid': "This link is not valid.",
    'violation.rel.token_expired': "This link has expired.",
    'violation.rel.website_invalid': "The website address is not valid.",
    'violation.rel.reason_required': "Give the reason.",
    'violation.rel.stage_in_use': "Leads are at this stage: move them first.",
    'violation.rel.last_stage': "At least one prospection stage is required.",
    'violation.rel.out_of_range': "Value out of the allowed range.",
    'violation.rel.unknown_list': "Unknown list.",
    'violation.rel.unknown_account': "Unknown account.",
    'violation.rel.contact_other_organisation': "This contact belongs to another organisation.",
    'brd.title': "Institution identity",
    'brd.sub': "The logo, emblem and name shown on screens, emails and documents",
    'brd.history': "History",
    'brd.files.title': "Logo and emblem",
    'brd.files.sub': "PNG, JPEG, WebP or SVG · 2 MB at most",
    'brd.logo': "Full logo",
    'brd.emblem': "Emblem",
    'brd.deployed': "The deployment's file: nothing has been uploaded here yet",
    'brd.meta': "{format} · {width} × {height} px · {weight}",
    'brd.logo.uses': "Expanded menu, sign-in page, PDFs, emails",
    'brd.emblem.uses': "Collapsed menu, browser tab, mobile app",
    'brd.replace': "Replace",
    'brd.download': "Download",
    'brd.names.title': "Institution name",
    'brd.names.sub': "The French name is also the one on produced documents",
    'brd.names.fr': "Official name (French)",
    'brd.names.en': "Official name (English)",
    'brd.names.ar': "Official name (Arabic)",
    'brd.names.code': "Acronym",
    'brd.names.codeHint': "Set at installation: it is part of invoice, receipt and payroll numbering. It stands in for the emblem when there is none.",
    'brd.names.saved': "Institution name saved.",
    'brd.preview.title': "Preview, wherever it appears",
    'brd.preview.sub': "With the files in force",
    'brd.preview.menu': "Expanded and collapsed menu",
    'brd.preview.login': "Sign-in page",
    'brd.preview.signIn': "Sign in",
    'brd.preview.pdf': "PDF header",
    'brd.preview.mail': "Emails",
    'brd.preview.tab': "Browser tab",
    'brd.preview.tabText': "Home · Kelassi",
    'brd.note': "A change applies everywhere as soon as it is saved: screens, sign-in page, emails and documents produced afterwards. Transcripts and certificates already produced keep the logo of their date; every change is logged and can be restored from the history.",
    'brd.upload.titleLogo': "Replace the full logo",
    'brd.upload.titleEmblem': "Replace the emblem",
    'brd.upload.choose': "Choose a file",
    'brd.upload.formats': "PNG, JPEG, WebP or SVG, 2 MB at most",
    'brd.upload.newFile': "New file",
    'brd.upload.fileLine': "{name} · {width} × {height} px · {weight}",
    'brd.upload.trim': "Trim empty margins",
    'brd.upload.rules': "An SVG containing script, an image or an external link is refused, as is a file over 2 MB. The emblem must be square.",
    'brd.upload.useLogo': "Use this logo",
    'brd.upload.useEmblem': "Use this emblem",
    'brd.upload.doneLogo': "New logo in force everywhere.",
    'brd.upload.doneEmblem': "New emblem in force everywhere.",
    'brd.check.title': "Checks",
    'brd.check.waiting': "Choose a file: Kelassi checks it before using it.",
    'brd.check.running': "Checking…",
    'brd.check.refused': "File refused",
    'brd.check.format': "Accepted format",
    'brd.check.size': "Size",
    'brd.check.sizeText': "{weight}, under the 2 MB limit",
    'brd.check.dimensions': "Dimensions",
    'brd.check.dimensionsText': "{width} × {height} px: sharp on screen and in print",
    'brd.check.small': "{width} × {height} px: a little small, at least {advised} px is advised",
    'brd.check.vector': "Vector image: sharp at any size",
    'brd.check.margins': "Empty margins around the drawing",
    'brd.check.marginsText': "They make it look small in the menu; tick « Trim empty margins ».",
    'brd.check.trimmed': "Trimmed on saving: {width} × {height} px",
    'brd.check.noMargins': "The drawing fills the image",
    'brd.check.transparent': "Transparent background",
    'brd.check.transparentText': "The logo sits on the menu's white frame and on documents",
    'brd.check.opaque': "The background is not transparent: it will show unless it is white",
    'brd.change.logo': "Full logo replaced",
    'brd.change.emblem': "Emblem replaced",
    'brd.change.names': "Official name changed",
    'brd.change.restore': "Version restored",
    'brd.hist.title': "Identity history",
    'brd.hist.sub': "Every change of logo, emblem or name, with its author; restoring creates a new change, nothing is erased",
    'brd.hist.empty': "No change: the identity is the deployment's.",
    'brd.hist.date': "Date",
    'brd.hist.by': "By",
    'brd.hist.change': "Change",
    'brd.hist.preview': "Preview",
    'brd.hist.actions': "Actions",
    'brd.hist.version': "Version {number}",
    'brd.hist.current': "Version in force",
    'brd.hist.restore': "Restore",
    'brd.hist.restoredFrom': "Copy of version {number}",
    'brd.hist.restored': "Version {number} restored: it is in force everywhere.",
    'brd.hist.confirmTitle': "Restore version {number}?",
    'brd.hist.confirmText': "Its logo, emblem and name become those of screens, emails and future documents again. Documents already produced do not change.",
    'brd.hist.note': "Restoring a version puts it back in force for screens, emails and future documents. Documents already produced do not change: their PDF keeps the logo of the day it was produced.",
    'journal.action.branding.changed': "Institution identity changed",
    'journal.target.branding': "Institution",
    'violation.brd.file_empty': "Empty file.",
    'violation.brd.file_too_big': "File too large: 2 MB at most.",
    'violation.brd.file_format': "Format not accepted: PNG, JPEG, WebP or SVG.",
    'violation.brd.file_unreadable': "Unreadable image.",
    'violation.brd.not_square': "The emblem must be square.",
    'violation.brd.too_small': "Image too small.",
    'violation.brd.svg_unsafe': "SVG refused: it contains script, an image or an external link.",
    'violation.brd.svg_size': "SVG without dimensions: add a viewBox.",
    'violation.brd.text': "Text expected.",
    'violation.brd.name_required': "The official name is required.",
    'violation.brd.name_too_long': "Name too long: 255 characters at most.",
    'violation.brd.unchanged': "Nothing has changed.",
    'violation.brd.already_current': "This version is already in force.",
    // D78 : la gestion des documents (DG-1).
    'doc.docState.valid': "Valid",
    'doc.docState.superseded': "Superseded",
    'doc.docState.cancelled': "Cancelled",
    'doc.docState.expired': "Expired",
    'doc.verify.cancelled': "Document cancelled",
    'doc.verify.expired': "Document expired",
    'doc.verify.cancelledBody': "The institution cancelled it on {date}: it is no longer valid.",
    'doc.verify.expiredBody': "It was valid until {date}: ask the institution for a new one.",
    'doc.verify.validUntil': "Valid until {date}.",
    'dg.dashboard.sub': "Dashboard · {year} · transcripts, certificates, minutes",
    'dg.kpi.produced': "Issued this year",
    'dg.kpi.producedNote': "{count} of them on the portal",
    'dg.kpi.todo': "To issue",
    'dg.kpi.todoNote': "spotted by Kelassi",
    'dg.kpi.downloads': "Downloads",
    'dg.kpi.downloadsNote': "by students, on the portal",
    'dg.kpi.verifications': "Public verifications",
    'dg.kpi.verificationsNote': "by QR code or reference",
    'dg.todo.title': "To issue",
    'dg.todo.sub': "What Kelassi spotted: closed deliberations, enrolments",
    'dg.todo.empty': "Nothing to issue for now.",
    'dg.todo.enrolment': "Enrolments of the year",
    'dg.todo.enrolmentNote': "Enrolments without a current enrolment certificate: {count}; they are issued through Requests.",
    'dg.todo.transcriptsNote': "Closed on {date} · missing transcripts: {count}",
    'dg.batches.title': "Latest batches",
    'dg.batches.all': "All batches",
    'dg.batches.line': "Documents: {count} · {date} · {by}",
    'dg.types.title': "By document type",
    'dg.types.empty': "No document issued this year.",
    'dg.types.cancelled': "cancelled: {count}",
    'dg.col.produced': "Issued",
    'dg.col.published': "On the portal",
    'dg.col.downloads': "Downloaded",
    'dg.col.verifications': "Verified",
    'dg.col.issuedOn': "Issued on",
    'dg.col.portal': "Portal",
    'dg.col.verified': "Verified",
    'dg.verifications.title': "Public verifications",
    'dg.verifications.sub': "Without an account, by QR code or reference",
    'dg.verifications.empty': "No recent verification.",
    'dg.registry.title': "Document register",
    'dg.registry.sub': "Every document issued, with its reference; nothing is deleted: a document is replaced or cancelled, with a reason",
    'dg.registry.search': "Reference, name or student number",
    'dg.registry.allTypes': "All documents",
    'dg.registry.training': "Programme",
    'dg.registry.allTrainings': "All programmes",
    'dg.registry.allStates': "All states",
    'dg.registry.empty': "No document matches.",
    'dg.registry.published': "Published · downloads: {count}",
    'dg.registry.notPublished': "Not published",
    'dg.registry.pages': "Register pages",
    'dg.registry.count': "Documents: {count} · page {page} of {pages}",
    'dg.action.open': "Open the record",
    'dg.action.pdf': "Download the PDF",
    'dg.action.duplicate': "Duplicate",
    'dg.action.replace': "Replace",
    'dg.action.cancel': "Cancel the document",
    'dg.notice.replaced': "A new document replaces {reference}.",
    'dg.notice.cancelled': "{reference} is cancelled: its verification now says so.",
    'dg.item.sub': "{type} of {student}",
    'dg.item.cancelled': "Cancelled on {date} by {by}",
    'dg.item.reason': "Reason: {reason}",
    'dg.item.replacedBy': "Replaced by",
    'dg.item.replaces': "This document replaces",
    'dg.item.document': "The document",
    'dg.item.from': "From",
    'dg.item.issued': "Issued",
    'dg.item.signatories': "Signatories",
    'dg.item.validUntil': "Valid until",
    'dg.item.digest': "Fingerprint",
    'dg.item.diffusion': "Distribution",
    'dg.item.notForPortal': "Internal document, not on the portal",
    'dg.item.publishedOn': "Published on {date} · student downloads: {count}",
    'dg.item.duplicates': "Duplicates",
    'dg.item.duplicatesCount': "Printed: {count}",
    'dg.item.noVerification': "Not verified yet.",
    'dg.item.verifyPage': "Open the verification page",
    'dg.item.history': "History",
    'dg.item.noPreview': "The preview is unavailable: the PDF engine does not respond.",
    'dg.item.note': "Cancelling a document requires a reason; its public verification then answers \"cancelled\", without saying why. Replacing issues a new document and marks this one \"superseded\".",
    'dg.event.issued': "Issued by {by}",
    'dg.event.downloaded': "Downloaded by the student",
    'dg.event.verified': "Verified: {result}",
    'dg.event.duplicate': "Duplicate printed by {by}",
    'dg.event.published': "Published to the portal by {by}",
    'dg.event.unpublished': "Withdrawn from the portal by {by}",
    'dg.event.cancelled': "Cancelled by {by}: {detail}",
    'dg.event.replaced': "Replaced by {detail} ({by})",
    'dg.replace.title': "Replace {reference}?",
    'dg.replace.text': "A new document is issued, with a new reference and the same options; this one is marked \"superseded\" and its verification names the new one.",
    'dg.cancel.title': "Cancel {reference}?",
    'dg.cancel.text': "A cancelled document stays cancelled: its verification answers \"cancelled\", it leaves the portal and can no longer be downloaded. The reason stays in the register and is never shown publicly.",
    'dg.cancel.reason': "Reason for cancellation",
    'dg.settings.title': "Distribution and verification",
    'dg.settings.sub': "Apply to documents issued from now on",
    'dg.settings.publishDefault': "Publish to the portal by default",
    'dg.settings.publishDefaultHint': "each issue offers the box ticked",
    'dg.settings.validity': "Validity of enrolment certificates",
    'dg.settings.validityHint': "Printed as \"Valid until\"; after that, the verification answers \"expired\".",
    'dg.settings.noLimit': "No limit",
    'dg.settings.months': "{count} months",
    'dg.settings.oneMonth': "1 month",
    'dg.settings.shortName': "Short name on the verification page",
    'dg.settings.shortNameHint': "first name and initial of the last name",
    'dg.settings.duplicateMark': "\"DUPLICATE\" watermark on reprints",
    'dg.settings.duplicateMarkHint': "the student's download carries none",
    'rel.modules.documents': "Documents",
    'rel.modules.documentsHint': "Transcripts, certificates, minutes, register, public verification",
    'rel.modules.documentsOff': "No document is issued any more, Requests no longer deliver documents on their own and \"My documents\" leaves the portal; verification of documents already issued keeps answering.",
    'violation.doc.reason_required': "Give the reason for the cancellation (at least 5 characters).",
    'violation.doc.already_cancelled': "This document is already cancelled.",
    'violation.doc.not_replaceable': "Only a valid or expired document can be replaced.",
    'violation.doc.run_changed': "The results have changed since: issue the document from the deliberation.",
    'violation.doc.documents_off': "The Documents module is off: handle this request manually.",
    'journal.action.document.cancelled': "Document cancelled",
    'journal.action.document.replaced': "Document replaced",
    // D79 : les modèles de documents (DG-2).
    'tpl.title': "Document templates",
    'tpl.sub': "The templates Kelassi fills in: texts, fields, tables, signatures, QR code; one version per publication",
    'tpl.byTraining': "Templates by programme",
    'tpl.duplicate': "Duplicate a template",
    'tpl.new': "New template",
    'tpl.create': "Create the template",
    'tpl.all': "All · {count}",
    'tpl.search': "Search a template",
    'tpl.showArchived': "Show archived",
    'tpl.empty': "No template matches.",
    'tpl.note': "A published template no longer changes: editing it prepares its next version, published when ready. An issued document keeps the version of its day. Kelassi's standard templates are duplicated; templates from the former Kelassi are carried over with their code.",
    'tpl.allTrainings': "All",
    'tpl.verifiable': "Verifiable by QR code",
    'tpl.invalid': "Some blocks need fixing.",
    'tpl.col.template': "Template",
    'tpl.col.category': "Category",
    'tpl.col.source': "Produces",
    'tpl.col.languages': "Languages",
    'tpl.col.paper': "Paper",
    'tpl.col.qr': "QR",
    'tpl.col.version': "Version",
    'tpl.col.trainings': "Programmes",
    'tpl.badge.default': "Default",
    'tpl.badge.system': "Standard",
    'tpl.badge.archived': "Archived",
    'tpl.version.published': "v{number} published",
    'tpl.version.draft': "v{number} draft",
    'tpl.field.name': "Template name",
    'tpl.field.nameCopyHint': "Empty: the copied template's name",
    'tpl.field.from': "Copy the template",
    'tpl.field.kind': "What it produces",
    'tpl.field.category': "Category",
    'tpl.field.paper': "Paper",
    'tpl.field.languages': "Languages",
    'tpl.field.verifiable': "QR code and verification reference",
    'tpl.field.validity': "Validity",
    'tpl.field.duplicateMark': "\"DUPLICATE\" watermark on a reprint",
    'tpl.field.signatureRequired': "Signature required before release",
    'tpl.field.signatureRequiredHint': "the batch will wait for the signatory in \"To sign\"",
    'tpl.field.font': "Font",
    'tpl.field.fontSize': "Text size",
    'tpl.field.margins': "Margins",
    'tpl.font.sans': "Sans serif",
    'tpl.font.serif': "Serif (Times)",
    'tpl.validity.inherit': "As the institution",
    'tpl.size.small': "Small",
    'tpl.size.normal': "Normal",
    'tpl.size.large': "Large",
    'tpl.margins.narrow': "Narrow",
    'tpl.margins.normal': "Normal",
    'tpl.margins.wide': "Wide",
    'tpl.align.start': "Left",
    'tpl.align.center': "Centred",
    'tpl.align.end': "Right",
    'tpl.align.justify': "Justified",
    'tpl.align.spread': "Spread across the width",
    'tpl.layout.list': "Continuous list",
    'tpl.layout.modules': "One table per module",
    'tpl.layout.table': "Table",
    'tpl.layout.grid': "Photo grid",
    'tpl.summary.facts': "As lines",
    'tpl.summary.boxes': "In three boxes",
    'tpl.summary.none': "None",
    'tpl.kind.transcript': "Transcript",
    'tpl.kind.certificate': "Certificate of achievement",
    'tpl.kind.minutes': "Minutes",
    'tpl.kind.enrolment': "Enrolment certificate",
    'tpl.kind.card': "Student card",
    'tpl.kind.list': "Class list",
    'tpl.kind.internship': "Internship document",
    'tpl.category.schooling': "Schooling",
    'tpl.category.results': "Results",
    'tpl.category.lists': "Lists and reports",
    'tpl.category.finance': "Finance",
    'tpl.category.stages': "Internships",
    'tpl.category.teachers': "Teachers",
    'tpl.paper.A4': "A4",
    'tpl.paper.A4-landscape': "A4 landscape",
    'tpl.paper.A5': "A5",
    'tpl.paper.CR80': "CR80 card",
    'tpl.lang.fr': "French",
    'tpl.lang.en': "English",
    'tpl.lang.ar': "Arabic",
    'tpl.action.versions': "Versions",
    'tpl.action.pdf': "PDF preview",
    'tpl.action.publish': "Publish version {number}",
    'tpl.action.duplicate': "Duplicate to edit",
    'tpl.action.makeDefault': "Make it the default template",
    'tpl.action.archive': "Archive this template",
    'tpl.action.unarchive': "Restore this template",
    'tpl.notice.saved': "Draft saved.",
    'tpl.notice.published': "Version {number} published: documents issued from now on use it.",
    'tpl.editor.systemSub': "Kelassi standard template · the page printed as is",
    'tpl.editor.systemNote': "A standard template cannot be edited. Duplicate it: the copy receives blocks equivalent to its page, which you adapt.",
    'tpl.editor.firstDraft': "Version {number}, draft · not published yet",
    'tpl.editor.draftSub': "Version {number}, draft · version {current} stays in force until this one is published",
    'tpl.editor.currentSub': "Version {number} in force · editing prepares the next one",
    'tpl.editor.unsaved': "unsaved changes",
    'tpl.editor.page': "The page",
    'tpl.editor.pageHint': "Drag to reorder, click to adjust",
    'tpl.editor.addBlock': "Add a block",
    'tpl.editor.up': "Move up",
    'tpl.editor.down': "Move down",
    'tpl.editor.copyBlock': "Duplicate the block",
    'tpl.editor.fields': "Fields",
    'tpl.editor.fieldsHint': "Click to insert into the current text",
    'tpl.editor.previewWith': "Preview with",
    'tpl.editor.searchSubject': "Search…",
    'tpl.editor.subject': "Preview subject",
    'tpl.editor.noSubject': "Without data",
    'tpl.editor.selected': "Selected block: {block}",
    'tpl.editor.document': "The document",
    'tpl.editor.usage': "Usage",
    'tpl.editor.code': "Code",
    'tpl.editor.legacy': "Former Kelassi",
    'tpl.editor.defaultFor': "All those without an assigned template (default)",
    'tpl.editor.unused': "None yet",
    'tpl.publish.title': "Publish version {number}?",
    'tpl.publish.text': "Documents issued from now on will use this version. Those already issued keep theirs.",
    'tpl.publish.note': "What changes (optional)",
    'tpl.versions.title': "Template versions",
    'tpl.versions.number': "Version {number}",
    'tpl.versions.draft': "draft",
    'tpl.versions.current': "in force",
    'tpl.versions.created': "started on {date} by {by}",
    'tpl.versions.published': "published on {date} by {by}",
    'tpl.versions.restore': "Start again from this version",
    'tpl.versions.discard': "Discard the draft",
    'tpl.versions.discardTitle': "Discard the draft?",
    'tpl.versions.discardText': "Its changes are lost; the version in force does not change.",
    'tpl.block.header': "Institution header",
    'tpl.block.title': "Title",
    'tpl.block.text': "Text",
    'tpl.block.facts': "\"Label: value\" lines",
    'tpl.block.grades': "Marks table",
    'tpl.block.result': "Result",
    'tpl.block.minutes': "Minutes table",
    'tpl.block.students': "Class students",
    'tpl.block.table': "Free table",
    'tpl.block.photo': "Student photo",
    'tpl.block.signatures': "Signatures",
    'tpl.block.stamp': "Stamp",
    'tpl.block.qr': "QR code and reference",
    'tpl.block.footer': "Footer",
    'tpl.block.spacer': "Space",
    'tpl.block.pagebreak': "Page break",
    'tpl.block.system': "Standard page",
    'tpl.if.always': "Always",
    'tpl.if.normal': "In the first session",
    'tpl.if.retake': "After resit",
    'tpl.if.credits': "If enrolled in credits",
    'tpl.if.scholarship': "If the student holds a scholarship",
    'tpl.if.female': "For a female student",
    'tpl.if.male': "For a male student",
    'tpl.prop.tagline': "With the line under the name",
    'tpl.prop.align': "Alignment",
    'tpl.prop.title': "Title",
    'tpl.prop.sub': "Lines under the title",
    'tpl.prop.size': "Size",
    'tpl.prop.text': "Text",
    'tpl.prop.format': "Formatting",
    'tpl.prop.validity': "With the validity date",
    'tpl.prop.layout': "Layout",
    'tpl.prop.summary': "Result",
    'tpl.prop.details': "Subject details",
    'tpl.prop.coefficients': "Weights",
    'tpl.prop.credits': "Credits",
    'tpl.prop.sessions': "The session of each mark",
    'tpl.prop.legend': "The status legend",
    'tpl.prop.condensed': "Condensed (small print)",
    'tpl.prop.minutesDetails': "One column per semester or module",
    'tpl.prop.counts': "Head counts",
    'tpl.prop.bonuses': "Board bonuses",
    'tpl.prop.columns': "Columns",
    'tpl.prop.blank': "Blank lines at the end",
    'tpl.prop.factColumns': "Columns",
    'tpl.prop.label': "Label",
    'tpl.prop.value': "Value (empty: dotted line to fill in)",
    'tpl.prop.addLine': "Add a line",
    'tpl.prop.border': "With borders",
    'tpl.prop.column': "Column {number}",
    'tpl.prop.addColumn': "Add a column",
    'tpl.prop.rows': "Rows",
    'tpl.prop.cell': "Row {row}, column {column}",
    'tpl.prop.addRow': "Add a row",
    'tpl.prop.made': "With \"Done at …, on …\"",
    'tpl.prop.parties': "Who signs",
    'tpl.prop.partiesHint': "Empty: the signatories set for this document in the settings.",
    'tpl.prop.party': "Party {number}",
    'tpl.prop.addParty': "Add a party",
    'tpl.prop.qrLine': "With the \"verify online\" line",
    'tpl.prop.height': "Height (px)",
    'tpl.prop.pagebreak': "What follows starts on a new page.",
    'tpl.prop.if': "Show",
    'tpl.format.bold': "Bold",
    'tpl.format.italic': "Italic",
    'tpl.format.list': "List",
    'tpl.column.number': "Number",
    'tpl.column.name': "Full name",
    'tpl.column.birth': "Date of birth",
    'tpl.column.cin': "ID or passport",
    'tpl.column.email': "E-mail",
    'tpl.column.phone': "Phone",
    'tpl.column.signature': "Signature",
    'tpl.column.photo': "Photo",
    'tpl.group.etudiant': "Student",
    'tpl.group.inscription': "Enrolment",
    'tpl.group.resultats': "Results",
    'tpl.group.classe': "Class",
    'tpl.group.stage': "Internship",
    'tpl.group.document': "Document",
    'tpl.assign.sub': "{year} · which template each programme uses, and the session lines printed",
    'tpl.assign.copy': "Copy last year",
    'tpl.assign.copyTitle': "Copy the assignments of {year}?",
    'tpl.assign.copyText': "Each programme of {year} with the same code as last year's, and without a template for a document yet, receives last year's.",
    'tpl.assign.copied': "Assignments copied: {count}.",
    'tpl.assign.empty': "No programme this year.",
    'tpl.assign.byDefault': "Default",
    'tpl.assign.standard': "the standard page",
    'tpl.assign.note': "\"Default\": the institution's template for this document. A renewed programme keeps its assignments; \"Copy last year\" copies them all.",
    'tpl.assign.fallback': "Without an assigned template, the programme uses the default one: {name}.",
    'tpl.assign.remove': "Remove this template",
    'tpl.assign.add': "Add another template",
    'tpl.assign.template': "Template",
    'tpl.assign.choose': "Choose…",
    'tpl.assign.isDefault': "Offered by default when issuing",
    'tpl.assign.titleNormal': "Line in the first session",
    'tpl.assign.titleHint': "Printed by the {{document.titre}} field, e.g. \"JUNE 2026\"",
    'tpl.assign.titleRetake': "Line after resit",
    'tpl.assign.titleCredit': "Line for a credit enrolment",
    'tpl.assign.titleShared': "Shared title",
    'tpl.assign.titleSharedHint': "Printed by {{document.titre_commun}}, e.g. \"4th Year IFACG\"",
    'tpl.assign.saved': "Templates of {training} saved.",
    'tpl.assign.removed': "Template removed from {training}.",
    'journal.action.document.template_created': "Document template created",
    'journal.action.document.template_changed': "Document template changed",
    'journal.action.document.template_published': "Document template published",
    'journal.action.document.template_assigned': "Programme templates changed",
    'violation.tpl.invalid_block': "This block is incomplete or unknown.",
    'violation.tpl.too_many': "A page holds 60 blocks at most.",
    'violation.tpl.block_source': "This block has nothing to show for this document.",
    'violation.tpl.unknown_field': "A field does not exist for this document: use the list of fields.",
    'violation.tpl.too_long': "This text is too long.",
    'violation.tpl.validity': "Between 1 and 24 months, no limit, or as the institution.",
    'violation.tpl.kind': "Choose what the template produces.",
    'violation.tpl.name': "Give the template a name.",
    'violation.tpl.languages': "At least one language.",
    'violation.tpl.system': "A standard template cannot be edited: duplicate it.",
    'violation.tpl.no_draft': "No draft.",
    'violation.tpl.only_draft': "This template only has this draft: archive it instead.",
    'violation.tpl.version': "Version not found.",
    'violation.tpl.not_usable': "An archived or never published template cannot be used.",
    'violation.tpl.not_archivable': "The default template, or a standard one, cannot be archived.",
    'violation.tpl.wrong_kind': "This template does not produce this document.",
    'violation.tpl.unpublished': "This template has no published version yet.",
    'violation.tpl.subject': "Choose a preview subject.",
    'violation.tpl.no_previous_year': "No previous year.",
    'tpl.field.etudiant.nom_complet': "Full name",
    'tpl.field.etudiant.nom': "Last name",
    'tpl.field.etudiant.prenom': "First name",
    'tpl.field.etudiant.civilite': "Title (Mr, Ms)",
    'tpl.field.etudiant.e': "Feminine agreement",
    'tpl.field.etudiant.numero': "Student number",
    'tpl.field.etudiant.date_naissance': "Date of birth",
    'tpl.field.etudiant.cin': "ID number",
    'tpl.field.etudiant.passeport': "Passport",
    'tpl.field.etudiant.code_massar': "Massar or national code",
    'tpl.field.etudiant.email': "E-mail",
    'tpl.field.etudiant.telephone': "Phone",
    'tpl.field.inscription.formation': "Programme",
    'tpl.field.inscription.code_formation': "Programme code",
    'tpl.field.inscription.parcours': "Track",
    'tpl.field.inscription.annee': "Academic year",
    'tpl.field.inscription.date': "Enrolment date",
    'tpl.field.inscription.classe': "Class",
    'tpl.field.inscription.regime': "Regime",
    'tpl.field.inscription.boursier': "Scholarship",
    'tpl.field.resultats.session': "Session",
    'tpl.field.resultats.moyenne': "Average",
    'tpl.field.resultats.decision': "Decision",
    'tpl.field.resultats.mention': "Honours",
    'tpl.field.resultats.credits': "Credits earned",
    'tpl.field.resultats.rang': "Rank",
    'tpl.field.resultats.date_jury': "Board date",
    'tpl.field.classe.nom': "Class",
    'tpl.field.classe.formation': "Programme",
    'tpl.field.classe.code_formation': "Programme code",
    'tpl.field.classe.annee': "Academic year",
    'tpl.field.classe.effectif': "Head count",
    'tpl.field.classe.delegue': "Class representative",
    'tpl.field.stage.type': "Internship type",
    'tpl.field.stage.organisme': "Host organisation",
    'tpl.field.stage.tuteur': "Tutor",
    'tpl.field.stage.fonction_tuteur': "Tutor's position",
    'tpl.field.stage.encadrant': "Academic supervisor",
    'tpl.field.stage.debut': "Start",
    'tpl.field.stage.fin': "End",
    'tpl.field.stage.duree_mois': "Duration in months",
    'tpl.field.stage.sujet': "Topic",
    'tpl.field.stage.missions': "Tasks",
    'tpl.field.stage.lieu': "Place",
    'tpl.field.document.reference': "Reference",
    'tpl.field.document.date': "Issue date",
    'tpl.field.document.lieu': "Place of signature",
    'tpl.field.document.etablissement': "Institution",
    'tpl.field.document.valable_jusqu_au': "Valid until",
    'tpl.field.document.titre': "Session line (per programme)",
    'tpl.field.document.titre_commun': "Shared title (per programme)",
    'doc.docState.unsigned': "To sign",
    'doc.type.card': "Student card",
    'doc.types.card': "Student cards",
    'doc.type.list': "Class list",
    'doc.types.list': "Class lists",
    'doc.type.internship': "Internship document",
    'doc.types.internship': "Internship documents",
    'doc.verify.unsigned': "Document not signed yet",
    'doc.verify.unsignedBody': "It is awaiting the institution's signature: it is not valid yet.",
    'doc.verify.signed': "Signed on {date} by {name}.",
    'dg.event.signed': "Signed by {by}",
    'dg.event.returned': "Sent back by {by}: {detail}",
    'dg.registry.batch': "The documents of a batch",
    'dg.registry.batchClear': "The whole register",
    'journal.action.document.job_started': "Document production started",
    'journal.action.document.signer_changed': "Signatory set",
    'journal.action.document.signed': "Documents signed",
    'journal.action.document.returned': "Batch sent back by the signatory",
    'prd.title': "Issue documents",
    'prd.sub': "A document, a source, recipients: Kelassi checks everything before issuing",
    'prd.jobs': "Production",
    'prd.step.1': "Document",
    'prd.step.2': "From",
    'prd.step.3': "For",
    'prd.step.4': "Check and issue",
    'prd.next': "Continue",
    'prd.back': "Back",
    'prd.edit': "Change",
    'prd.kind.title': "Which document?",
    'prd.kind.template': "Template",
    'prd.kind.byTraining': "Each programme's template",
    'prd.kind.templateHint': "Without a choice, Kelassi takes the template assigned to the programme, otherwise the default one.",
    'prd.from.deliberation': "from a closed deliberation",
    'prd.from.enrolment': "from enrolments",
    'prd.from.class': "from classes",
    'prd.from.internship': "from internships",
    'prd.source.deliberation': "Which closed deliberation?",
    'prd.source.training': "Which programme?",
    'prd.source.noDeliberation': "No closed deliberation this year in your programmes.",
    'prd.source.noTraining': "No programme where you can issue this year.",
    'prd.source.run': "calculation no. {number}, closed on {date}",
    'prd.for.title': "For whom?",
    'prd.for.count': "{chosen} selected out of {total}",
    'prd.for.all': "All",
    'prd.for.none': "None",
    'prd.for.empty': "Nobody to issue this document to.",
    'prd.warning.no_photo': "no photo",
    'prd.warning.no_number': "number missing",
    'prd.warning.existing': "already exists",
    'prd.warningHint.no_photo': "the template prints the photo: issued with an empty frame, or exclude it",
    'prd.warningHint.no_number': "issued without a student number, or complete it first",
    'prd.warningHint.existing': "the same document is returned, without a new reference",
    'prd.summary.template': "template \"{name}\", v{version}",
    'prd.summary.for': "{count} recipients · {excluded} excluded",
    'prd.options': "Options",
    'prd.lang': "Language",
    'prd.publish': "Publish on the portal",
    'prd.publishHint': "everyone downloads theirs in \"My documents\"",
    'prd.publishAfterSign': "on signature: nothing goes out before",
    'prd.notify': "Notify everyone",
    'prd.notifyHint': "by the bell and by e-mail, with Communication",
    'prd.sign': "Have it signed before release",
    'prd.signHint': "the batch goes to the chosen signatory's \"To sign\"",
    'prd.signForced': "the template requires it: the batch goes to \"To sign\"",
    'prd.signer': "Signatory",
    'prd.noSigner': "No signatory is set up: they do it in \"To sign\", stating their position.",
    'prd.checks': "Checks",
    'prd.checks.ready': "{count} ready",
    'prd.checks.readyDelib': "deliberation closed, calculation frozen",
    'prd.checks.readyTraining': "active enrolments of the programme",
    'prd.checks.queue': "The batch goes into the queue",
    'prd.checks.queueHint': "more than {count} documents: Kelassi issues in the background and notifies at the end",
    'prd.start': "Issue {count} · {what}",
    'prd.preview': "Preview",
    'prd.previewOf': "{name} · {index} of {total}",
    'prd.previous': "Previous",
    'prd.following': "Next",
    'prd.previewNote': "Marked \"PREVIEW\" until anything is issued",
    'prd.kinds.transcript': "Transcripts",
    'prd.kinds.certificate': "Certificates of achievement",
    'prd.kinds.minutes': "Minutes",
    'prd.kinds.enrolment': "Enrolment certificates",
    'prd.kinds.card': "Student cards",
    'prd.kinds.list': "Class lists",
    'prd.kinds.internship': "Internship documents",
    'prd.state.queued': "Queued",
    'prd.state.running': "Running",
    'prd.state.done': "Done",
    'prd.state.partial': "With failures",
    'prd.state.stopped': "Stopped",
    'prd.state.failed': "Failed",
    'prd.state.to_sign': "To sign",
    'prd.state.signed': "Signed",
    'prd.state.returned': "Sent back",
    'prd.jobs.sub': "Batches, running and recent: they run in the background, even if you close the page",
    'prd.jobs.empty': "No batch yet.",
    'prd.jobs.col.job': "Batch",
    'prd.jobs.col.started': "Started",
    'prd.jobs.col.by': "By",
    'prd.jobs.col.progress': "Progress",
    'prd.jobs.col.state': "State",
    'prd.jobs.note': "A batch of more than {count} documents goes into the queue; Kelassi notifies you at the end, by the bell and by e-mail.",
    'prd.progress.queued': "{total} to issue",
    'prd.progress.running': "{done} of {total}",
    'prd.progress.done': "{done} issued",
    'prd.progress.failed': "{done} issued · {failed} failed",
    'prd.progress.toSign': "{done} issued · awaiting {name}",
    'prd.progress.signed': "{done} signed by {name}",
    'prd.progress.returned': "sent back: {reason}",
    'prd.panel.started': "Started {date} by {by}",
    'prd.panel.produced': "Issued",
    'prd.panel.of': "of {total}",
    'prd.panel.failed': "Failed",
    'prd.panel.toFix': "to fix",
    'prd.panel.none': "none",
    'prd.panel.failures': "What failed",
    'prd.panel.open': "Open the record",
    'prd.panel.toSign': "{count} documents await the signature of {title} ({name}); nothing goes on the portal before.",
    'prd.panel.returned': "Sent back by {by}: {reason}. Unsigned documents are cancelled; fix then issue a new batch.",
    'prd.panel.retryNote': "Fix the records then retry: only the {failed} failed documents are issued, the other {done} stay as they are.",
    'prd.panel.background': "The batch runs in the background: you can close this panel.",
    'prd.panel.stop': "Stop",
    'prd.panel.archive': "The {count} issued",
    'prd.panel.retry': "Retry the {count}",
    'sgn.title': "To sign",
    'sgn.sub': "Batches awaiting your signature before release",
    'sgn.kpi.pending': "Batches waiting",
    'sgn.kpi.pendingNote': "{count} documents",
    'sgn.kpi.signed': "Signed this month",
    'sgn.kpi.signedNote': "{count} of them by delegation",
    'sgn.kpi.returned': "Sent back",
    'sgn.kpi.returnedNote': "this month, with the reason",
    'sgn.queue.title': "Batches to sign",
    'sgn.queue.sub': "Nothing goes on the portal before your signature",
    'sgn.queue.empty': "No batch is waiting for you.",
    'sgn.queue.line': "{count} documents · issued by {by}, {date}",
    'sgn.queue.delegated': "on behalf of {title}",
    'sgn.queue.browse': "Browse",
    'sgn.queue.return': "Send back",
    'sgn.queue.chosen': "Batches chosen: {jobs} · documents: {count}",
    'sgn.action.sign': "Sign {count} documents",
    'sgn.dialog.title': "Sign {count} documents",
    'sgn.dialog.text': "{what}. Your signature and the stamp will be placed on them, with the date and time.",
    'sgn.dialog.sending': "Sending the code by e-mail…",
    'sgn.dialog.code': "Code received by e-mail at {email}",
    'sgn.dialog.expires': "The code is valid until {time}.",
    'sgn.dialog.resend': "Send a new code",
    'sgn.dialog.sign': "Sign",
    'sgn.done': "{count} documents signed.",
    'sgn.returned': "Batch sent back: its author has been notified.",
    'sgn.return.title': "Send this batch back?",
    'sgn.return.text': "\"{label}\": its {count} unsigned documents are cancelled, and {by} receives your reason.",
    'sgn.return.reason': "Reason",
    'sgn.return.submit': "Send the batch back",
    'sgn.me.title': "My signature",
    'sgn.me.sub': "Image of the signature and stamp, placed on each signed document",
    'sgn.me.quality': "Position",
    'sgn.me.qualityHint': "The Academic Director",
    'sgn.me.signature': "My signature",
    'sgn.me.stamp': "My stamp",
    'sgn.me.noSignature': "No signature yet",
    'sgn.me.noStamp': "No stamp",
    'sgn.me.addSignature': "Add the signature",
    'sgn.me.replaceSignature': "Replace the signature",
    'sgn.me.addStamp': "Add the stamp",
    'sgn.me.replaceStamp': "Replace the stamp",
    'sgn.me.imageHint': "Preferably PNG with a transparent background, at least 120 pixels wide, 1 MB at most.",
    'sgn.delegation.title': "Delegation",
    'sgn.delegation.who': "Signs on my behalf while I am away",
    'sgn.delegation.nobody': "Nobody",
    'sgn.delegation.from': "From",
    'sgn.delegation.to': "To",
    'sgn.delegation.hint': "The document reads \"For {title}, by delegation, {name}\".",
    'sgn.note': "Simple electronic signature: the image of the signature and stamp, the timestamp and the document's fingerprint, confirmed by a code. A qualified signature by a trusted third party may be added later.",
    'violation.prd.signer': "Choose who signs.",
    'violation.prd.too_many': "2,000 documents at most per batch.",
    'violation.prd.training': "Choose a programme.",
    'violation.prd.finished': "This batch is finished.",
    'violation.prd.nothing_to_retry': "Nothing to retry.",
    'violation.prd.nobody': "No document to issue.",
    'violation.sgn.title': "State the signing position (\"The Academic Director\").",
    'violation.sgn.no_signer': "Set your position first.",
    'violation.sgn.delegate': "Choose another person from the institution.",
    'violation.sgn.period': "The end comes after the start.",
    'violation.sgn.too_big': "An image of 1 MB at most.",
    'violation.sgn.format': "A PNG, JPEG or WebP image.",
    'violation.sgn.too_small': "At least 120 pixels wide.",
    'violation.sgn.too_many_codes': "Too many codes requested: try again in a few minutes.",
    'violation.sgn.code_expired': "Request a new code.",
    'violation.sgn.code': "This code does not match.",
    'violation.sgn.not_yours': "This batch is not waiting for you.",
    'violation.sgn.no_job': "Choose at least one batch.",
    'doc.verify.pageTitle': "Document verification",
    'doc.verify.validBody': "It is exactly as the institution issued it.",
    'doc.verify.row.type': "Document",
    'doc.verify.row.student': "In the name of",
    'doc.verify.row.training': "Programme",
    'doc.verify.row.year': "Year",
    'doc.verify.row.issued': "Issued on",
    'doc.verify.row.signed': "Signed by",
    'doc.verify.row.until': "Valid until",
    'doc.verify.privacy': "To protect the student, neither marks nor decisions are shown here. Compare with the document you received.",
    'doc.verify.another': "Verify another reference",
    'shr.title': "Shared documents",
    'shr.sub': "The institution's documents shared with an audience: rules, calendar, charters, guides; with or without read receipt",
    'shr.showArchived': "Show archived",
    'shr.empty': "No shared document yet.",
    'shr.col.document': "Document",
    'shr.col.for': "For",
    'shr.col.reading': "Reading",
    'shr.col.follow': "Follow-up",
    'shr.col.period': "Shared",
    'shr.versionOf': "version {number} of {date}",
    'shr.archived': "Archived",
    'shr.ack': "Read receipt",
    'shr.free': "Free reading",
    'shr.follow.pending': "{accepted} of {total} · {pending} to remind",
    'shr.follow.all': "{accepted} of {total}",
    'shr.follow.opened': "Opened: {count}",
    'shr.period.always': "All year",
    'shr.period.until': "Until {date}",
    'shr.period.since': "Since {date}",
    'shr.period.between': "From {from} to {to}",
    'shr.audience.everyone': "Everyone, staff included",
    'shr.population.student': "Students",
    'shr.population.teacher': "Teachers",
    'shr.population.staff': "Staff",
    'shr.note': "Replacing the file creates a new version; if a read receipt is required, everyone must read and accept the new version again. The list of readers exports to Excel.",
    'shr.new': "New share",
    'shr.drop': "Drop a file",
    'shr.dropHint': "PDF, Word, Excel, image · {size} MB at most",
    'shr.field.title': "Title",
    'shr.field.for': "For",
    'shr.field.from': "From",
    'shr.field.to': "To",
    'shr.field.ack': "Ask for a read receipt",
    'shr.field.ackHint': "\"I have read and accept\"; the date and time are kept",
    'shr.field.notify': "Notify",
    'shr.field.notifyHint': "with Communication",
    'shr.restrict.none': "All students: restrict to programmes or classes",
    'shr.restrict.some': "Restricted students: {count} choices",
    'shr.restrict.trainings': "Programmes",
    'shr.restrict.classes': "Classes",
    'shr.restrict.noTraining': "No programme this year.",
    'shr.share': "Share",
    'shr.shared': "Document shared.",
    'shr.saved': "Share saved.",
    'shr.replaced': "Version {number} shared.",
    'shr.archivedDone': "Share archived: it leaves \"My documents\".",
    'shr.reminded': "Reminder sent to {count} people.",
    'shr.panel.by': "Shared by {by} on {date}",
    'shr.panel.audience': "Audience",
    'shr.panel.accepted': "Accepted",
    'shr.panel.opened': "Opened",
    'shr.panel.pending': "{count} to remind",
    'shr.panel.version': "version {number}",
    'shr.panel.file': "The file and its versions",
    'shr.panel.versionLine': "v{number}",
    'shr.panel.download': "Download",
    'shr.panel.notifyVersion': "Notify the audience of the new version",
    'shr.panel.replace': "Share version {number}",
    'shr.panel.settings': "Audience, period, receipt",
    'shr.panel.readers': "Who read it",
    'shr.panel.archive': "Archive",
    'shr.panel.remind': "Remind the {count}",
    'shr.filter.all': "Whole audience",
    'shr.filter.notAccepted': "Not accepted yet",
    'shr.filter.accepted': "Accepted",
    'shr.filter.notOpened': "Not opened yet",
    'shr.filter.opened': "Opened",
    'shr.readers.name': "Person",
    'shr.readers.opened': "Opened on",
    'shr.readers.accepted': "Accepted on",
    'shr.readers.waiting': "Waiting",
    'shr.readers.truncated': "List limited to the first 3,000 people.",
    'shr.mine.toAccept': "To read and accept",
    'shr.mine.versionLine': "Version {number} · shared on {date}",
    'shr.mine.read': "Read",
    'shr.mine.accept': "I have read and accept",
    'shr.mine.title': "Institution documents",
    'shr.mine.acceptedOn': "accepted on {date}",
    'shr.mine.sharedOn': "shared on {date}",
    'shr.mine.until': "until {date}",
    'shr.mine.page': "Shared documents",
    'shr.mine.pageSub': "What the institution shared with you: to read, sometimes to accept",
    'shr.mine.none': "No document shared with you.",
    'journal.action.document.shared': "Document shared",
    'journal.action.document.share_changed': "Shared document changed",
    'journal.action.document.share_archived': "Shared document archived",
    'journal.action.document.share_reminded': "Reading reminder sent",
    'violation.shr.audience': "Choose who to share the document with.",
    'violation.shr.audience_students': "Programmes and classes restrict students: tick them.",
    'violation.shr.title': "Give a title (200 characters at most).",
    'violation.shr.date': "Invalid date.",
    'violation.shr.period': "The end comes after the start.",
    'violation.shr.archived': "This share is archived.",
    'violation.shr.no_acknowledgement': "This document does not ask for a read receipt.",
    'violation.shr.no_communication': "Reminders go through Communication, which is off.",
    'violation.shr.new_version': "A new version came out: read it again.",
    'rel.modules.library': "Library",
    'rel.modules.libraryHint': "Catalogue, copies, readers and cards, loans, holds, fines, inventories",
    'rel.modules.libraryOff': "Open loans stay recorded; no reminder goes out any more and the online catalogue leaves the portal.",
    'doc.type.library_clearance': "Library clearance",
    'doc.types.library_clearance': "Library clearances",
    'lib.situation.available': "Available",
    'lib.situation.loaned': "On loan",
    'lib.situation.held': "On hold shelf",
    'lib.situation.transit': "In transit",
    'lib.situation.repair': "In repair",
    'lib.situation.lost': "Lost",
    'lib.situation.missing': "Missing",
    'lib.situation.withdrawn': "Withdrawn",
    'lib.hold.waiting': "Waiting",
    'lib.hold.assigned': "Ready for pickup",
    'lib.hold.collected': "Collected",
    'lib.hold.cancelled_reader': "Cancelled by reader",
    'lib.hold.cancelled_library': "Cancelled by library",
    'lib.hold.expired': "Expired",
    'lib.fee.open': "Due",
    'lib.fee.paid': "Paid",
    'lib.fee.waived': "Waived",
    'lib.fee.cancelled': "Cancelled",
    'lib.late.days': "{count} d late",
    'lib.late.due': "Due {date}",
    'lib.scan.camera': "Scan with the camera",
    'lib.scan.hint': "Scanner or typing, then Enter",
    'lib.scan.noCamera': "The camera is not available on this device.",
    'journal.action.library.settings_changed': "Library settings changed",
    'journal.action.library.catalog_changed': "Library record or authority changed",
    'journal.action.library.items_changed': "Copies changed, moved or labelled",
    'journal.action.library.item_withdrawn': "Copy withdrawn from the collection",
    'journal.action.library.locations_changed': "Library locations changed",
    'journal.action.library.reader_changed': "Reader, membership or card changed",
    'journal.action.library.loan_overridden': "Loan forced despite a block",
    'journal.action.library.fee_changed': "Library charge issued or settled",
    'journal.action.library.fee_waived': "Fine or fee waived",
    'journal.action.library.loss_changed': "Loss declared, settled or copy found",
    'journal.action.library.inventory_changed': "Inventory opened or discrepancy handled",
    'journal.action.library.inventory_closed': "Inventory closed",
    'journal.action.library.import_run': "Library import run or undone",
    'journal.action.library.history_viewed': "Reader loan history viewed",
    'journal.action.library.clearance_issued': "Library clearance issued",
    'violation.lib.reason_required': "A reason is required.",
    'violation.lib.prefix': "Capital letters, digits and hyphens only.",
    'violation.lib.policy_duplicate': "A policy already exists for this combination.",
    'violation.lib.default_policy': "The default policy cannot be deleted.",
    'violation.lib.isbn': "Invalid ISBN: the check digit does not match.",
    'violation.lib.year_order': "The edition year is before the original year.",
    'violation.lib.dewey': "Dewey number expected (e.g. 658.4).",
    'violation.lib.issn': "ISSN expected (1234-5678).",
    'violation.lib.language': "ISO 639 language code expected.",
    'violation.lib.contributor_required': "At least one author or publisher.",
    'violation.lib.edition_used': "A record with copies or holds cannot be deleted.",
    'violation.lib.merge_invalid': "Two distinct authorities of the same kind.",
    'violation.lib.location_cycle': "A node cannot go under itself.",
    'violation.lib.location_used': "A location holding nodes, copies or ranges cannot be deleted.",
    'violation.lib.barcode': "Invalid barcode.",
    'violation.lib.barcode_taken': "This barcode already exists: a barcode is never reused.",
    'violation.lib.item_withdrawn': "A withdrawn copy can no longer be changed.",
    'violation.lib.no_category': "No reader category for this person: finish the setup wizard.",
    'violation.lib.reader_exists': "This person is already a library reader.",
    'violation.lib.contact_required': "An email or a phone is required.",
    'violation.lib.person_exists': "Someone already holds this ID: enrol them instead.",
    'violation.lib.reader_used': "This reader has a history: suspend them instead of deleting.",
    'violation.lib.no_membership': "No active membership.",
    'violation.lib.card_inactive': "This card is no longer valid (lost or replaced).",
    'violation.lib.fee_invoiced': "This charge is invoiced: Finance collects it.",
    'violation.lib.fee_settled': "This charge is already settled.",
    'violation.lib.loan_closed': "This loan is closed.",
    'violation.lib.not_lost': "This copy is neither lost nor missing.",
    'violation.lib.not_clear': "The reader is not clear: loans or amounts outstanding.",
    'violation.lib.unknown_reader': "Unknown card or reader.",
    'violation.lib.unknown_item': "Unknown barcode.",
    'violation.lib.no_open_loan': "No open loan for this copy.",
    'violation.lib.renew_refused': "This loan cannot be renewed.",
    'violation.lib.holds_closed': "Online holds are not open.",
    'violation.lib.confidential': "Confidential document: no loan nor public access.",
    'violation.lib.hold_exists': "This reader already holds this document.",
    'violation.lib.hold_loaned': "The reader already has this document on loan.",
    'violation.lib.no_loanable': "No loanable copy.",
    'violation.lib.hold_closed': "This hold is closed.",
    'violation.lib.hold_item': "This copy cannot serve this hold.",
    'violation.lib.inventory_open': "A full inventory is already open.",
    'violation.lib.inventory_closed': "This inventory is closed.",
    'violation.lib.resolve_invalid': "This action does not apply to this discrepancy.",
    'violation.lib.import_done': "This import has already run.",
    'violation.lib.import_not_done': "Only a completed import can be undone.",
    'violation.lib.file_size': "File empty or too large (30 MB max).",
    'violation.lib.card_code': "Digital card expired or unreadable: refresh it.",
    'violation.lib.cover': "JPEG, PNG or WebP image, 2 MB max.",
    'violation.lib.not_mine': "This loan is not yours.",
    'lib.dash.title': "Library",
    'lib.dash.sub': "The library's day: loans, expected returns, overdue, holds to prepare",
    'lib.dash.stats': "Statistics",
    'lib.dash.suggestions': "Purchase suggestions",
    'lib.dash.desk': "Lend and return",
    'lib.dash.setupNeeded': "The library is not set up yet: one site, a loan policy, a barcode and a card are enough.",
    'lib.dash.setupGo': "Start the wizard",
    'lib.dash.kpiLoans': "Loans today",
    'lib.dash.kpiLoansNote': "{returns} returns",
    'lib.dash.kpiDue': "Returns due",
    'lib.dash.kpiDueNote': "today · {open} open loans",
    'lib.dash.kpiLate': "Overdue",
    'lib.dash.kpiLateNote': "{count} readers suspended",
    'lib.dash.kpiHolds': "Holds to prepare",
    'lib.dash.kpiHoldsNote': "{collect} on shelf, {pickup} awaiting pickup",
    'lib.dash.kpiStock': "Collection",
    'lib.dash.kpiStockNote': "copies · {editions} records · {readers} readers",
    'lib.dash.lateTitle': "Returns due and overdue",
    'lib.dash.lateSub': "Today first, then overdue",
    'lib.dash.allLate': "All overdue",
    'lib.dash.noLate': "No return due and nothing overdue.",
    'lib.dash.colItem': "Copy",
    'lib.dash.colReader': "Reader",
    'lib.dash.colDue': "Due",
    'lib.dash.anonymous': "Anonymised loan",
    'lib.dash.recentTitle': "Recent movements",
    'lib.dash.recentSub': "Loans, returns, renewals, losses",
    'lib.dash.noRecent': "No movement yet.",
    'lib.dash.event.loaned': "Loan",
    'lib.dash.event.returned': "Return",
    'lib.dash.event.renewed': "Renewal",
    'lib.dash.event.lost': "Loss",
    'lib.dash.event.found': "Found",
    'lib.dash.event.withdrawn': "Withdrawn",
    'lib.dash.collectTitle': "Holds to prepare",
    'lib.dash.collectSub': "Pick list: these copies are on the shelf",
    'lib.dash.collectGo': "Prepare",
    'lib.dash.collectFor': "for {name} · rank {rank}",
    'lib.dash.noCollect': "Nothing to pick from the shelves.",
    'lib.dash.watchTitle': "To watch",
    'lib.dash.watchSuspended': "{count} readers suspended",
    'lib.dash.watchDebt': "readers' total debt: {amount}",
    'lib.dash.watchTransit': "{count} copies in transit",
    'lib.dash.watchTransitNone': "no transfer between sites",
    'lib.dash.watchStock': "{waiting} readers waiting, {copies} copy(ies): order more?",
    'lib.desk.title': "Lend and return",
    'lib.desk.sub': "Two scans to lend (card, then copy), one to return",
    'lib.desk.tabLend': "Lend",
    'lib.desk.tabReturn': "Return",
    'lib.desk.readerScan': "1. Reader card",
    'lib.desk.readerHint': "Scan the card, the digital card QR or the student card",
    'lib.desk.itemScan': "2. Copies",
    'lib.desk.itemHint': "Scan each copy; checks show at once",
    'lib.desk.unknownReader': "Unknown card or reader.",
    'lib.desk.lent': "{count} loan(s) recorded.",
    'lib.desk.ticket': "Print the slip",
    'lib.desk.basket': "This loan",
    'lib.desk.basketSub': "The reader's policy applies to each copy",
    'lib.desk.onsite': "On-site reading",
    'lib.desk.scanReaderFirst': "Scan the reader card first.",
    'lib.desk.basketEmpty': "Scan the copies to lend.",
    'lib.desk.colCheck': "Check",
    'lib.desk.colActions': "Actions",
    'lib.desk.accepted': "Accepted",
    'lib.desk.refused': "Refused",
    'lib.desk.notOverridable': "(cannot be forced)",
    'lib.desk.rentalDay': "Rental {amount} per day",
    'lib.desk.rentalLoan': "Rental {amount}",
    'lib.desk.force': "Force",
    'lib.desk.remove': "Remove {barcode} from the loan",
    'lib.desk.receipt': "Finance receipt for the rental",
    'lib.desk.receiptHint': "The rental is paid before handing over the book",
    'lib.desk.lend': "Lend {count} copy(ies)",
    'lib.desk.sideHint': "The reader's file shows here once the card is scanned.",
    'lib.desk.forceTitle': "Force the loan",
    'lib.desk.forceReason': "Reason for forcing",
    'lib.desk.forceHint': "The reason is kept on the loan and in the log",
    'lib.desk.forceConfirm': "Lend despite the block",
    'lib.desk.reader': "The reader",
    'lib.desk.openFile': "Open the file",
    'lib.desk.cardNumber': "card {number}",
    'lib.desk.noMembership': "No active membership",
    'lib.desk.membershipUntil': "Membership until {date}",
    'lib.desk.membershipPending': "Membership awaiting payment ({amount})",
    'lib.desk.loansCount': "{count} open loan(s)",
    'lib.desk.suspended': "Suspended: {reason}",
    'lib.desk.penalty': "Penalty until {date}",
    'lib.desk.currentLoans': "Current loans",
    'lib.desk.noLoans': "No current loan.",
    'lib.desk.renew': "Renew",
    'lib.desk.account': "Account",
    'lib.desk.debt': "Debt",
    'lib.desk.late': "Overdue",
    'lib.desk.holds': "Holds",
    'lib.desk.openFees': "Open charges",
    'lib.desk.none': "none",
    'lib.desk.returnScan': "Returned copy",
    'lib.desk.returnHint': "One scan is enough, no card needed; returns from another site are accepted",
    'lib.desk.returnedOn': "Actual drop-off date",
    'lib.desk.returnedOnHint': "For the drop box; empty: today",
    'lib.desk.condition': "Condition of the copy",
    'lib.desk.conditionSame': "Unchanged",
    'lib.desk.conditionHint': "Choose before scanning. \"To repair\" sends the copy to the workshop.",
    'lib.desk.cond.new': "New",
    'lib.desk.cond.good': "Good",
    'lib.desk.cond.worn': "Worn",
    'lib.desk.cond.damaged': "Damaged",
    'lib.desk.cond.repair': "To repair",
    'lib.desk.returned': "The return",
    'lib.desk.returnedBy': "Returned by",
    'lib.desk.lateDays': "{count} opening day(s); closed days do not count",
    'lib.desk.onTime': "on time",
    'lib.desk.fine': "Fine",
    'lib.desk.suspension': "Suspension",
    'lib.desk.suspendedUntil': "borrowing suspended until {date}",
    'lib.desk.dest.shelf': "Back to the shelf",
    'lib.desk.dest.hold': "Set aside for a hold",
    'lib.desk.dest.transit': "Send to its home site",
    'lib.desk.dest.repair': "Send to repair",
    'lib.desk.destShelf': "in its place: {place}",
    'lib.desk.destHold': "for {name}, notified now; to collect by {date}",
    'lib.desk.destTransit': "the copy belongs to the {site} site",
    'lib.desk.destRepair': "the copy is marked in repair",
    'lib.desk.damageTitle': "Damage found",
    'lib.desk.damageSub': "A damage charge goes to the reader's account",
    'lib.desk.damageAmount': "Amount ({currency})",
    'lib.desk.damageNote': "Finding",
    'lib.desk.damageCharge': "Charge the damage",
    'lib.desk.damageDone': "Damage charge issued.",
    'lib.desk.damageLabel': "Damage",
    'lib.holds.title': "Holds",
    'lib.holds.sub': "Each edition's queue, by priority then arrival",
    'lib.holds.tabCollect': "To pick",
    'lib.holds.tabPickup': "Awaiting pickup",
    'lib.holds.tabWaiting': "Queued",
    'lib.holds.tabClosed': "Closed",
    'lib.holds.collectTitle': "Pick list",
    'lib.holds.collectSub': "Sorted by location: take the copy from the shelf, scan it, it is set aside and the reader notified",
    'lib.holds.noPlace': "Location not set",
    'lib.holds.forReader': "for {name} · {category}",
    'lib.holds.scanTaken': "Copy taken",
    'lib.holds.scanTakenHint': "Scan then Enter",
    'lib.holds.pickupTitle': "Awaiting pickup at the desk",
    'lib.holds.pickupSub': "After the deadline the hold expires and the copy goes to the next reader",
    'lib.holds.waitingTitle': "Waiting for a return",
    'lib.holds.waitingSub': "No copy on the shelf: the hold is served at the next return",
    'lib.holds.closedTitle': "Closed holds",
    'lib.holds.empty': "No hold.",
    'lib.holds.colTitle': "Document",
    'lib.holds.colUntil': "Collect by",
    'lib.holds.colRank': "Rank",
    'lib.holds.colState': "State",
    'lib.holds.colSince': "Since",
    'lib.holds.until': "by {date}",
    'lib.holds.rank': "rank {rank}",
    'lib.holds.cancel': "Cancel",
    'lib.overdue.title': "Overdue and losses",
    'lib.overdue.sub': "The morning run detects overdue loans and sends reminders",
    'lib.overdue.exportLoans': "Export loans",
    'lib.overdue.exportFees': "Export charges",
    'lib.overdue.calendar': "Reminder schedule",
    'lib.overdue.calendarSub': "Late penalty: {mode}; for a minor student the reminder also goes to the guardian",
    'lib.overdue.mode.money': "financial",
    'lib.overdue.mode.suspension': "suspension days",
    'lib.overdue.mode.both': "financial and suspension",
    'lib.overdue.stepReminder': "Reminder",
    'lib.overdue.stepReminderSub': "before the due date",
    'lib.overdue.stepChase': "Chase",
    'lib.overdue.stepChaseSub': "after the due date",
    'lib.overdue.stepNotice': "Formal notice",
    'lib.overdue.stepNoticeSub': "suspension; presumed lost {days} d later",
    'lib.overdue.kpiLate': "Overdue",
    'lib.overdue.kpiLateNote': "of which {count} over 7 days",
    'lib.overdue.kpiLost': "Losses",
    'lib.overdue.kpiLostNote': "loans closed as lost",
    'lib.overdue.kpiMissing': "Missing and lost",
    'lib.overdue.kpiMissingNote': "copies to find",
    'lib.overdue.kpiDebt': "Readers' debt",
    'lib.overdue.kpiDebtNote': "{count} open charges",
    'lib.overdue.tabLate': "Overdue",
    'lib.overdue.tabLost': "Losses",
    'lib.overdue.tabMissing': "Missing",
    'lib.overdue.tabFees': "Charges",
    'lib.overdue.empty': "Nothing to handle.",
    'lib.overdue.colLate': "Overdue",
    'lib.overdue.colLostOn': "Lost on",
    'lib.overdue.colStage': "Last reminder",
    'lib.overdue.noReminder': "none",
    'lib.overdue.manual': "manual",
    'lib.overdue.remind': "Remind",
    'lib.overdue.reminded': "Reminder sent.",
    'lib.overdue.lose': "Declare lost",
    'lib.overdue.lost': "Loan closed as lost: replacement charges issued.",
    'lib.overdue.loseTitle': "Declare lost",
    'lib.overdue.loseText': "\"{title}\" ({barcode}), lent to {name}, {days} days overdue: the copy becomes lost, the loan closes as lost, the late fine stops, replacement and processing fees are charged.",
    'lib.overdue.loseConfirm': "Close the loan as lost",
    'lib.overdue.settle': "Replacement accepted",
    'lib.overdue.settled': "Replacement accepted: the replacement charge is cancelled.",
    'lib.overdue.missingSub': "When found, the copy rejoins the collection; a paid loss is refunded per the settings",
    'lib.overdue.markFound': "Found",
    'lib.overdue.found': "Copy back in the collection.",
    'lib.overdue.feesSub': "An invoiced charge is collected by Finance; others are settled on a Finance receipt",
    'lib.overdue.colFee': "Charge",
    'lib.overdue.colDue': "Outstanding",
    'lib.overdue.colInvoice': "Invoice",
    'lib.overdue.kind.membership': "Membership",
    'lib.overdue.kind.rental': "Rental",
    'lib.overdue.kind.late': "Late",
    'lib.overdue.kind.damage': "Damage",
    'lib.overdue.kind.loss': "Loss",
    'lib.overdue.kind.processing': "Processing fee",
    'lib.overdue.kind.duplicate': "Duplicate",
    'lib.overdue.pay': "Pay",
    'lib.overdue.waive': "Waive",
    'lib.overdue.cancel': "Cancel",
    'lib.overdue.payTitle': "Record the payment",
    'lib.overdue.payConfirm': "Save the receipt",
    'lib.overdue.waiveTitle': "Waive the charge",
    'lib.overdue.waiveConfirm': "Grant the waiver",
    'lib.overdue.cancelTitle': "Cancel the charge",
    'lib.overdue.cancelConfirm': "Cancel the charge",
    'lib.overdue.receipt': "Finance receipt number",
    'lib.overdue.receiptHint': "The library collects nothing itself",
    'lib.overdue.amount': "Amount waived ({currency})",
    'lib.overdue.amountHint': "Full waiver by default; a smaller amount gives a partial waiver",
    'lib.overdue.reason': "Reason",
    'lib.overdue.reasonHint': "Required, kept in the log",
    'lib.overdue.paid': "Payment recorded.",
    'lib.overdue.waived': "Waiver granted.",
    'lib.overdue.cancelled': "Charge cancelled.",
    'lib.doctype.book': "Book",
    'lib.doctype.periodical': "Periodical",
    'lib.doctype.thesis': "Thesis",
    'lib.doctype.dissertation': "Dissertation",
    'lib.doctype.internship_report': "Internship report",
    'lib.doctype.media': "CD or DVD",
    'lib.doctype.map': "Map",
    'lib.doctype.ebook': "E-book",
    'lib.doctype.internal': "Internal document",
    'lib.inv.title': "Inventory",
    'lib.inv.sub': "Check the shelves against the records, campaign by campaign, without closing the library",
    'lib.inv.open': "Open a campaign",
    'lib.inv.none': "No inventory campaign yet.",
    'lib.inv.col.campaign': "Campaign",
    'lib.inv.col.kind': "Kind",
    'lib.inv.col.scope': "Scope",
    'lib.inv.col.opened': "Opened",
    'lib.inv.col.progress': "Progress",
    'lib.inv.col.state': "State",
    'lib.inv.col.zone': "Zone",
    'lib.inv.col.expected': "Expected",
    'lib.inv.col.seen': "Seen",
    'lib.inv.col.gap': "Discrepancy",
    'lib.inv.col.item': "Copy",
    'lib.inv.col.action': "Action",
    'lib.inv.kind.full': "Full inventory",
    'lib.inv.kind.rotating': "Rotating check",
    'lib.inv.kind.spot': "Spot check",
    'lib.inv.state.open': "Open",
    'lib.inv.state.closed': "Closed",
    'lib.inv.scopeAll': "Whole collection",
    'lib.inv.seenOf': "{seen} seen of {expected}",
    'lib.inv.openTitle': "Open an inventory campaign",
    'lib.inv.openSub': "Copies in scope are frozen in their expected state; circulation goes on.",
    'lib.inv.field.label': "Label",
    'lib.inv.field.kind': "Kind",
    'lib.inv.field.location': "Location",
    'lib.inv.field.locationHint': "A site, room or shelf, with everything it holds",
    'lib.inv.field.from': "From call number",
    'lib.inv.field.to': "To call number",
    'lib.inv.field.docType': "Document type",
    'lib.inv.allTypes': "All types",
    'lib.inv.field.team': "Team",
    'lib.inv.field.teamHint': "One person per line, \"zone: name\" to split zones",
    'lib.inv.back': "Inventories",
    'lib.inv.openedLine': "Opened on {date} by {by} · {scope} · circulation goes on during counting",
    'lib.inv.report': "Report",
    'lib.inv.count': "Count on tablet",
    'lib.inv.close': "Close the campaign",
    'lib.inv.closedOn': "Campaign closed on {date}.",
    'lib.inv.kpi.expected': "Expected",
    'lib.inv.kpi.expectedNote': "frozen at opening",
    'lib.inv.kpi.seen': "Seen",
    'lib.inv.kpi.gaps': "Discrepancies",
    'lib.inv.kpi.gapsNote': "{missing} missing, {misplaced} misplaced",
    'lib.inv.kpi.value': "Value of missing copies",
    'lib.inv.kpi.valueNote': "purchase price, else flat rate per type",
    'lib.inv.byZone': "By zone",
    'lib.inv.byZoneSub': "Several agents count in parallel on separate zones",
    'lib.inv.gaps': "Discrepancies to handle",
    'lib.inv.gapsSub': "Missing copies stay sought until the next campaign",
    'lib.inv.allGaps': "All",
    'lib.inv.noGap': "No discrepancy.",
    'lib.inv.gap.missing': "Missing",
    'lib.inv.gap.misplaced': "Misplaced",
    'lib.inv.gap.unknown': "Unknown",
    'lib.inv.gap.present_loaned': "Marked on loan",
    'lib.inv.gap.present_lost': "Lost, found",
    'lib.inv.gap.present_missing': "Missing, found",
    'lib.inv.gap.condition': "Condition to check",
    'lib.inv.detail.missing': "expected {location}",
    'lib.inv.detail.misplaced': "seen {seen}, expected {location}",
    'lib.inv.detail.unknown': "not in the records, seen {seen}",
    'lib.inv.unknownTitle': "Barcode not in the records",
    'lib.inv.treat': "Handle",
    'lib.inv.treatTitle': "Handle the discrepancy",
    'lib.inv.action': "Action",
    'lib.inv.apply': "Apply",
    'lib.inv.do.search': "Targeted search",
    'lib.inv.do.ignore': "Ignore",
    'lib.inv.do.relocate': "Fix the location",
    'lib.inv.do.close_loan': "Close a forgotten loan",
    'lib.inv.do.reintegrate': "Return to the collection",
    'lib.inv.do.create': "Create the copy",
    'lib.inv.doHint.search': "The copy stays sought; it becomes missing at closing",
    'lib.inv.doHint.ignore': "The discrepancy is marked handled, nothing changes",
    'lib.inv.doHint.relocate': "The copy's location becomes where it was seen",
    'lib.inv.doHint.close_loan': "The loan is closed at the scan date, without fine",
    'lib.inv.doHint.reintegrate': "The copy becomes available again",
    'lib.inv.doHint.create': "A copy of the chosen record, with this barcode",
    'lib.inv.findEdition': "Find the record",
    'lib.inv.search': "Search",
    'lib.inv.closeTitle': "Close the campaign",
    'lib.inv.closeSub': "{count} missing copies will be marked missing and stay sought",
    'lib.inv.withdraw': "Withdraw persistent missing copies",
    'lib.inv.withdrawHint': "Those already missing at the previous campaign, in bulk, with a reason",
    'lib.inv.reason': "Reason",
    'lib.inv.closeNote': "Closing a campaign needs the inventory closing right: persistent missing copies can be withdrawn in bulk with a reason, and the report gives expected, found, missing and the value of losses.",
    'lib.scan2.back': "Back to the campaign",
    'lib.scan2.noShelf': "Scan the shelf label first",
    'lib.scan2.online': "Online · {count} pending",
    'lib.scan2.offline': "Offline · {count} scans pending",
    'lib.scan2.closed': "This campaign is closed: counting is over.",
    'lib.scan2.scanShelf': "Shelf",
    'lib.scan2.scanItems': "Copies",
    'lib.scan2.hint': "Camera or scanner; a shelf code switches shelf",
    'lib.scan2.pick': "or pick it",
    'lib.scan2.choose': "Pick a shelf",
    'lib.scan2.shelfFirst': "Scan the shelf first: this code is not a location.",
    'lib.scan2.expected': "Expected here",
    'lib.scan2.seen': "Seen",
    'lib.scan2.left': "{count} left",
    'lib.scan2.bad': "Discrepancies",
    'lib.scan2.badNote': "not in the records",
    'lib.scan2.recent': "Latest scans",
    'lib.scan2.noneYet': "No scan on this shelf.",
    'lib.scan2.status.queued': "Pending",
    'lib.scan2.status.seen': "Seen",
    'lib.scan2.status.unknown': "Not in the records",
    'lib.scan2.status.twice': "Already scanned",
    'lib.scan2.changeShelf': "Change shelf",
    'lib.scan2.done': "Shelf done",
    'lib.mine.title': "My library",
    'lib.mine.sub': "{name} · {category}",
    'lib.mine.catalog': "Catalogue",
    'lib.mine.welcome': "You are not a reader yet",
    'lib.mine.welcomeText': "Visit the library desk to open your membership and get your card. The catalogue stays open for browsing.",
    'lib.mine.loans': "My loans ({count})",
    'lib.mine.noLoans': "No current loan.",
    'lib.mine.due': "due {date}",
    'lib.mine.dueToday': "due today",
    'lib.mine.today': "Today",
    'lib.mine.late': "{count} days late, due {date}",
    'lib.mine.renew': "Renew",
    'lib.mine.renewed': "\"{title}\" is renewed.",
    'lib.mine.declareLoss': "Report \"{title}\" as lost",
    'lib.mine.lossShort': "Report a loss",
    'lib.mine.lossTitle': "Report a loss",
    'lib.mine.lossText': "The loan is closed as lost and late fines stop. The replacement or its price, and a processing fee, will be charged. If you find the book, bring it to the desk.",
    'lib.mine.lossConfirm': "Confirm the loss",
    'lib.mine.lossDone': "The loss is recorded.",
    'lib.mine.holds': "My holds",
    'lib.mine.noHolds': "No hold.",
    'lib.mine.holdRank': "position {rank} in the queue · you will be notified",
    'lib.mine.holdReady': "waiting for you until {date}",
    'lib.mine.cancel': "Cancel",
    'lib.mine.holdCancelled': "Hold on \"{title}\" cancelled.",
    'lib.mine.account': "My account",
    'lib.mine.debt': "{amount} due",
    'lib.mine.noDebt': "no debt",
    'lib.mine.until': "membership until {date}",
    'lib.mine.noMembership': "no active membership",
    'lib.mine.pendingMembership': "membership awaiting payment",
    'lib.mine.suspended': "Loans suspended: {reason}",
    'lib.mine.penalty': "Late penalty: no new loan before {date}.",
    'lib.mine.payDesk': "to pay at Finance",
    'lib.mine.invoice': "invoice {number}",
    'lib.mine.clearance': "Download my clearance",
    'lib.mine.notClear': "Clearance needs no current loan nor amount due ({loans} loan(s) open).",
    'lib.mine.history': "History",
    'lib.mine.show': "Show ({count})",
    'lib.mine.hide': "Hide",
    'lib.mine.keepHistory': "Keep my loan history",
    'lib.mine.keepHistoryHint': "Unchecked, your returned loans are no longer kept under your name",
    'lib.mine.historyOn': "Your history is kept.",
    'lib.mine.historyOff': "Your history will no longer be kept.",
    'lib.mine.historyLine': "from {from} to {to}",
    'lib.mine.card': "My digital card",
    'lib.mine.noCard': "No card yet",
    'lib.mine.cardTitle': "Library card",
    'lib.mine.cardValid': "valid until {date}",
    'lib.mine.qr': "Card QR code",
    'lib.mine.cardHint': "Show this code at the loan desk; it renews itself every ten minutes.",
    'lib.mine.suggest': "Suggest a purchase",
    'lib.mine.suggestText': "A book missing from the collection? The library reviews every suggestion.",
    'lib.mine.suggestNew': "New suggestion",
    'lib.mine.suggestDone': "Suggestion sent.",
    'lib.mine.send': "Send",
    'lib.mine.field.title': "Title",
    'lib.mine.field.authors': "Authors",
    'lib.mine.field.isbn': "ISBN",
    'lib.mine.field.note': "Why this book?",
    'lib.mine.sugState.suggested': "Suggested",
    'lib.mine.sugState.listed': "Listed",
    'lib.mine.sugState.ordered': "Ordered",
    'lib.mine.sugState.received': "Received",
    'lib.mine.sugState.refused': "Declined",
    'lib.opac.title': "Library catalogue",
    'lib.opac.sub': "Title, author, subject, call number or ISBN",
    'lib.opac.mine': "My library",
    'lib.opac.search': "Search the catalogue",
    'lib.opac.placeholder': "Search for a book",
    'lib.opac.go': "Search",
    'lib.opac.filters': "Filters",
    'lib.opac.all': "All",
    'lib.opac.onlyAvailable': "Available",
    'lib.opac.site': "Site",
    'lib.opac.allSites': "All sites",
    'lib.opac.found': "{count} result(s)",
    'lib.opac.latest': "Latest additions",
    'lib.opac.none': "No book matches.",
    'lib.opac.available': "Available ({count})",
    'lib.opac.noCopy': "No copy",
    'lib.opac.loaned': "On loan",
    'lib.opac.loanedWaiting': "On loan · {count} hold(s)",
    'lib.opac.courses': "My course reading lists",
    'lib.opac.news': "New arrivals",
    'lib.opac.noNews': "No new arrival yet.",
    'lib.opac.pages': "Result pages",
    'lib.opac.prev': "Previous",
    'lib.opac.next': "Next",
    'lib.opac.pageOf': "Page {page} of {pages}",
    'lib.opac.back': "Catalogue",
    'lib.opac.hold': "Place a hold",
    'lib.opac.held': "Hold placed: you are number {rank}.",
    'lib.opac.waiting': "{count} reader(s) waiting",
    'lib.opac.where': "Where to find it",
    'lib.opac.backOn': "due back {date}",
    'lib.opac.onsite': "On site only",
    'lib.opac.description': "Description",
    'lib.opac.f.isbn': "ISBN",
    'lib.opac.f.cote': "Call number",
    'lib.opac.f.dewey': "Dewey",
    'lib.opac.f.edition': "Edition",
    'lib.opac.f.pages': "Pages",
    'lib.opac.f.languages': "Languages",
    'lib.opac.f.collection': "Series",
    'lib.opac.f.subjects': "Subjects",
    'lib.opac.f.note': "Note",
    'lib.opac.digital': "Digital version",
    'lib.opac.inCourses': "Recommended in courses",
    'lib.opac.volumes': "Volumes",
    'lib.opac.volume': "Volume {n}",
    'lib.verify.clearanceBody': "The reader was clear of any obligation to the library on {date}.",
    'lib.verify.category': "Reader category",
    'lib.role.author': "author",
    'lib.role.coauthor': "co-author",
    'lib.role.editor': "editor",
    'lib.role.translator': "translator",
    'lib.role.illustrator': "illustrator",
    'lib.role.prefacer': "preface writer",
    'lib.role.publisher': "publisher",
    'lib.role.subject': "subject",
    'lib.loanStatus.loanable': "Loanable",
    'lib.loanStatus.onsite': "On-site only",
    'lib.loanStatus.excluded': "Not for loan",
    'lib.loanStatus.short': "Short loan",
    'lib.condition.new': "New",
    'lib.condition.good': "Good",
    'lib.condition.worn': "Worn",
    'lib.condition.damaged': "Damaged",
    'lib.condition.repair': "To repair",
    'lib.kind.author': "Authors",
    'lib.kind.publisher': "Publishers",
    'lib.kind.collection': "Collections",
    'lib.kind.series': "Series",
    'lib.kind.subject': "Subjects",
    'lib.lang.fr': "French",
    'lib.lang.ar': "Arabic",
    'lib.lang.en': "English",
    'lib.lang.es': "Spanish",
    'lib.lang.de': "German",
    'lib.lang.zgh': "Amazigh",
    'lib.acq.purchase': "Purchase",
    'lib.acq.gift': "Gift",
    'lib.acq.deposit': "Deposit",
    'lib.acq.exchange': "Exchange",
    'lib.acq.legacy': "Legacy import",
    'lib.dewey.0': "000 Computer science, information, general works",
    'lib.dewey.1': "100 Philosophy and psychology",
    'lib.dewey.2': "200 Religion",
    'lib.dewey.3': "300 Social sciences",
    'lib.dewey.4': "400 Language",
    'lib.dewey.5': "500 Science",
    'lib.dewey.6': "600 Technology",
    'lib.dewey.7': "700 Arts and recreation",
    'lib.dewey.8': "800 Literature",
    'lib.dewey.9': "900 History and geography",
    'lib.event.created': "Added to the collection",
    'lib.event.moved': "Moved",
    'lib.event.loaned': "Lent",
    'lib.event.returned': "Returned",
    'lib.event.renewed': "Renewed",
    'lib.event.condition': "Condition changed",
    'lib.event.held': "Set aside for a hold",
    'lib.event.transit': "Sent in transit",
    'lib.event.repair': "Sent to repair",
    'lib.event.lost': "Declared lost",
    'lib.event.found': "Found",
    'lib.event.missing': "Missing at inventory",
    'lib.event.seen': "Seen at inventory",
    'lib.event.withdrawn': "Withdrawn",
    'lib.event.imported': "Imported",
    'lib.cat.title': "Catalogue",
    'lib.cat.sub': "{count} records · search ignores accents and case, and finds authors' variant names",
    'lib.cat.authorities': "Authorities",
    'lib.cat.locations': "Locations",
    'lib.cat.new': "Catalogue",
    'lib.cat.search': "Search the catalogue",
    'lib.cat.searchHint': "Title, author, ISBN, call number",
    'lib.cat.searchGo': "Search",
    'lib.cat.flags': "Filters",
    'lib.cat.flag.all': "All",
    'lib.cat.flag.incomplete': "To complete",
    'lib.cat.flag.noitems': "No copy",
    'lib.cat.flag.rental': "Rental",
    'lib.cat.flag.new': "New arrivals",
    'lib.cat.facets': "Refine",
    'lib.cat.facet.type': "Type",
    'lib.cat.facet.availability': "Availability",
    'lib.cat.facet.language': "Language",
    'lib.cat.facet.site': "Site",
    'lib.cat.availableOnly': "On the shelf",
    'lib.cat.export': "Export the catalogue",
    'lib.cat.exportGo': "Download",
    'lib.cat.format.csv': "Spreadsheet (CSV)",
    'lib.cat.format.marc21': "MARC21",
    'lib.cat.format.unimarc': "UNIMARC",
    'lib.cat.format.ris': "RIS (bibliography)",
    'lib.cat.format.bibtex': "BibTeX",
    'lib.cat.format.dc': "Dublin Core (XML)",
    'lib.cat.found': "{count} records",
    'lib.cat.forQuery': "for \"{q}\"",
    'lib.cat.none': "No record matches.",
    'lib.cat.noCopy': "No copy",
    'lib.cat.availability': "{available} of {copies} available",
    'lib.cat.waiting': "{count} hold(s)",
    'lib.cat.rental': "Rental",
    'lib.cat.confidential': "Confidential",
    'lib.cat.incomplete': "To complete",
    'lib.cat.open': "Open",
    'lib.cat.pages': "Result pages",
    'lib.cat.prev': "Previous",
    'lib.cat.next': "Next",
    'lib.cat.pageOf': "Page {page} of {pages}",
    'lib.ed.complete': "Complete record",
    'lib.ed.incomplete': "Record to complete",
    'lib.ed.people': "Responsibilities",
    'lib.ed.edition': "Edition",
    'lib.ed.originalYear': "first published {year}",
    'lib.ed.collection': "Collection",
    'lib.ed.set': "Set and volumes",
    'lib.ed.volume': "volume {n}",
    'lib.ed.volumes': "{count} volume(s) attached",
    'lib.ed.missingVolumes': "Missing volumes: {list}",
    'lib.ed.ids': "Identifiers",
    'lib.ed.description': "Description",
    'lib.ed.pages': "{count} pages",
    'lib.ed.indexing': "Indexing",
    'lib.ed.cote': "call number {cote}",
    'lib.ed.subjects': "subjects: {list}",
    'lib.ed.languages': "Languages",
    'lib.ed.courses': "Course bibliographies",
    'lib.ed.summary': "Summary",
    'lib.ed.publicNote': "Public note",
    'lib.ed.internalNote': "Internal note",
    'lib.ed.thesis': "Dissertation or thesis",
    'lib.ed.director': "supervised by {name}",
    'lib.ed.digital': "Digital version",
    'lib.ed.items': "Copies",
    'lib.ed.itemsSub': "{count} copie(s) · availability is computed from them, never from a counter",
    'lib.ed.addItems': "Add copies",
    'lib.ed.addItemsSub': "Barcodes follow the mask, or reuse existing labels",
    'lib.ed.addGo': "Add",
    'lib.ed.added': "{count} copie(s) added.",
    'lib.ed.labels': "Labels",
    'lib.ed.withdraw': "Withdraw ({count})",
    'lib.ed.noItems': "No copy yet: add one so the record can be borrowed.",
    'lib.ed.select': "Selection",
    'lib.ed.selectItem': "Select {code}",
    'lib.ed.barcode': "Barcode",
    'lib.ed.coteCol': "Call number",
    'lib.ed.location': "Location",
    'lib.ed.loanStatus': "Loan status",
    'lib.ed.situation': "Status",
    'lib.ed.condition': "Condition",
    'lib.ed.until': "until {date}",
    'lib.ed.availability': "Availability",
    'lib.ed.availableOf': "{available} available of {total}",
    'lib.ed.holds': "Holds",
    'lib.ed.noHold': "none",
    'lib.ed.reserve': "Place a hold for a reader",
    'lib.ed.reserveSub': "The first available copy will be set aside",
    'lib.ed.reserveGo': "Place hold",
    'lib.ed.held': "Hold placed: position {rank} in the queue.",
    'lib.ed.rank': "position {rank}",
    'lib.ed.reader': "Reader",
    'lib.ed.readerSearch': "Name or card number",
    'lib.ed.readerChange': "Change",
    'lib.ed.rental': "Rental",
    'lib.ed.noRental': "This edition carries no rental fee.",
    'lib.ed.rentalLoan': "{amount} per loan",
    'lib.ed.rentalDay': "{amount} per day",
    'lib.ed.rentalFree': "free for: {list}",
    'lib.ed.rentalHint': "Set on the record (all copies) or on a single copy.",
    'lib.ed.record': "The record",
    'lib.ed.loans': "Loans",
    'lib.ed.lastLoan': "last on {date}",
    'lib.ed.edit': "Edit the record",
    'lib.ed.duplicate': "Duplicate for another edition",
    'lib.ed.duplicateVolume': "Duplicate for the next volume",
    'lib.ed.delete': "Delete the record",
    'lib.ed.deleteTitle': "Delete this record?",
    'lib.ed.deleteText': "The record has no copy or hold; its responsibilities and course links go with it.",
    'lib.ed.withdrawTitle': "Withdraw {count} copie(s)",
    'lib.ed.withdrawSub': "A reasoned decision: the copy stays on record with its history and its barcode is never reused.",
    'lib.ed.withdrawGo': "Withdraw",
    'lib.ed.withdrawn': "{count} copie(s) withdrawn.",
    'lib.ed.reason': "Reason",
    'lib.ed.count': "Number of copies",
    'lib.ed.site': "Owning site",
    'lib.ed.locationHint': "If empty, the shelving plan suggests a place from the call number.",
    'lib.ed.locationAuto': "Suggested by the shelving plan",
    'lib.ed.barcodes': "Existing barcodes (optional)",
    'lib.ed.barcodesHint': "One per line, to reuse labels already stuck; otherwise the mask generates them.",
    'lib.form.new': "Catalogue an edition",
    'lib.form.edit': "Edit the record",
    'lib.form.sub': "ISBN lookup, then the record's groups",
    'lib.form.save': "Save",
    'lib.form.saveAnyway': "Save anyway",
    'lib.form.duplicate': "This ISBN is already catalogued.",
    'lib.form.sections': "Record groups",
    'lib.form.sec.title': "Title",
    'lib.form.sec.people': "Responsibilities",
    'lib.form.sec.edition': "Edition",
    'lib.form.sec.collection': "Collection",
    'lib.form.sec.set': "Set and volumes",
    'lib.form.sec.ids': "Identifiers",
    'lib.form.sec.description': "Physical description",
    'lib.form.sec.content': "Content",
    'lib.form.sec.indexing': "Indexing",
    'lib.form.sec.thesis': "Dissertation or thesis",
    'lib.form.sec.rental': "Rental",
    'lib.form.sec.media': "Media",
    'lib.form.sec.notes': "Notes",
    'lib.form.byIsbn': "Record by ISBN",
    'lib.form.isbn': "ISBN",
    'lib.form.isbnHint': "Scan or type the ISBN: Kelassi pre-fills the record from Open Library, the BnF or Google Books.",
    'lib.form.lookup': "Look up",
    'lib.form.looking': "Searching…",
    'lib.form.isbnInvalid': "Invalid ISBN: the check digit does not match.",
    'lib.form.notFound': "No service found this ISBN (or none answered): enter the record by hand.",
    'lib.form.found': "Found ({source}): fields pre-filled, check then save.",
    'lib.form.source.openLibrary': "Open Library",
    'lib.form.source.bnf': "BnF",
    'lib.form.source.google': "Google Books",
    'lib.form.already': "A record already exists for this ISBN:",
    'lib.form.title': "Title proper",
    'lib.form.subtitle': "Subtitle",
    'lib.form.parallelTitle': "Parallel title (other language)",
    'lib.form.originalTitle': "Original title (translation)",
    'lib.form.docType': "Document type",
    'lib.form.peopleHint': "Shared authorities, never free text; order is the display order.",
    'lib.form.publishers': "Publishers",
    'lib.form.listHint': "Comma-separated; an unknown name creates its authority.",
    'lib.form.noPeople': "No author yet.",
    'lib.form.newAuthority': "new authority",
    'lib.form.existingAuthority': "existing authority",
    'lib.form.role': "Role",
    'lib.form.up': "Move up",
    'lib.form.remove': "Remove",
    'lib.form.findAuthor': "Find an author or another form of the name",
    'lib.form.newAuthor': "New author",
    'lib.form.variants': "variants: {list}",
    'lib.form.place': "Place of publication",
    'lib.form.editionStatement': "Edition statement",
    'lib.form.year': "Year of publication",
    'lib.form.originalYear': "Original year",
    'lib.form.detail.editionNumber': "Edition number",
    'lib.form.detail.legalDeposit': "Legal deposit",
    'lib.form.detail.reprint': "Reprint",
    'lib.form.detail.ean': "EAN",
    'lib.form.detail.nationalNumber': "National number",
    'lib.form.detail.internalId': "Internal identifier",
    'lib.form.detail.illustrations': "Illustrations",
    'lib.form.detail.dimensions': "Dimensions",
    'lib.form.detail.accompanying': "Accompanying material (CD, map)",
    'lib.form.detail.level': "Level",
    'lib.form.collection': "Collection",
    'lib.form.collectionHint': "A shared authority: an unknown name creates it.",
    'lib.form.collectionLinked': "Linked to the existing authority; typing another name replaces it.",
    'lib.form.collectionNumber': "Number in collection",
    'lib.form.volumeNumber': "Volume number",
    'lib.form.volumeTitle': "Volume title",
    'lib.form.volumesTotal': "Total volumes",
    'lib.form.volumesHint': "On the set record: missing volumes are shown.",
    'lib.form.pages': "Pages or volumes",
    'lib.form.languages': "Languages",
    'lib.form.languagesHint': "ISO 639 codes, comma-separated (fr, ar, en)",
    'lib.form.audience': "Audience",
    'lib.form.summary': "Summary",
    'lib.form.dewey': "Dewey number",
    'lib.form.deweyHint': "E.g. 361.3; the class shows here",
    'lib.form.cote': "Call number",
    'lib.form.coteHint': "Shelf mark, printed on the spine label",
    'lib.form.subjects': "Subjects and keywords",
    'lib.form.thesisStudent': "Author (student)",
    'lib.form.thesisDirector': "Supervisor (teacher)",
    'lib.form.thesisYear': "Academic year",
    'lib.form.thesisDefended': "Defended on",
    'lib.form.personSearch': "Person's name",
    'lib.form.confidential': "Confidential",
    'lib.form.confidentialHint': "Forbids loans and public access.",
    'lib.form.rental': "Charge a rental fee for this edition",
    'lib.form.rentalHint': "All its copies; the loan creates the charge in Finance.",
    'lib.form.rentalMode': "Rate",
    'lib.form.rentalPerLoan': "Per loan",
    'lib.form.rentalPerDay': "Per day",
    'lib.form.rentalAmount': "Amount",
    'lib.form.rentalFree': "Free for",
    'lib.form.cover': "Cover",
    'lib.form.coverHint': "JPEG, PNG or WebP, 2 MB max",
    'lib.form.digitalUrl': "Digital file or link",
    'lib.form.publicNote': "Public note",
    'lib.form.internalNote': "Internal note",
    'lib.form.internalHint': "Never shown on the portal.",
    'lib.item.title': "Copy {code}",
    'lib.item.labels': "Print labels",
    'lib.item.withdraw': "Withdraw",
    'lib.item.edit': "Edit",
    'lib.item.card': "The record",
    'lib.item.never': "never reused",
    'lib.item.inventory': "Inventory no.",
    'lib.item.suggested': "Suggested place",
    'lib.item.suggestedHint': "Shelving plan: {path}",
    'lib.item.sites': "Site",
    'lib.item.sitesValue': "owner {owner} · current {current}",
    'lib.item.acquisition': "Acquisition",
    'lib.item.withdrawnAt': "Withdrawn on",
    'lib.item.toRepair': "Send to repair",
    'lib.item.toRepairDone': "Copy sent to repair.",
    'lib.item.repaired': "Repaired, back to shelf",
    'lib.item.repairedDone': "Copy back on the shelf.",
    'lib.item.arrived': "Arrived at its site",
    'lib.item.arrivedDone': "Copy arrived at its site.",
    'lib.item.found': "Found",
    'lib.item.foundDone': "Copy found: back in the collection.",
    'lib.item.labelsTitle': "Labels",
    'lib.item.history': "History",
    'lib.item.noHistory': "No movement.",
    'lib.item.by': "by {name}",
    'lib.item.due': "due {date}",
    'lib.item.forced': "forced: {reason}",
    'lib.item.loans': "Loans of this copy",
    'lib.item.noLoans': "Never lent.",
    'lib.item.reader': "Reader",
    'lib.item.loanedAt': "Lent on",
    'lib.item.returnedAt': "Returned",
    'lib.item.anonymous': "anonymised history",
    'lib.item.saved': "Copy saved.",
    'lib.item.unshelved': "Not shelved",
    'lib.item.rfid': "RFID tag",
    'lib.item.acqMode': "Acquisition mode",
    'lib.item.acqOn': "Acquisition date",
    'lib.item.supplier': "Supplier or donor",
    'lib.item.price': "Price ({currency})",
    'lib.item.invoice': "Invoice no.",
    'lib.item.conditionNote': "Note (damage)",
    'lib.item.rental': "Rental for this copy only",
    'lib.item.rentalHint': "Overrides the record's rental for this copy.",
    'lib.auth.title': "Authority records",
    'lib.auth.sub': "Shared authors, publishers, collections, series and subjects; variants make other spellings findable",
    'lib.auth.new': "New authority",
    'lib.auth.kinds': "Authority kinds",
    'lib.auth.search': "Find a name or a variant",
    'lib.auth.none': "No authority.",
    'lib.auth.name': "Name",
    'lib.auth.variants': "Variants",
    'lib.auth.variantsHint': "One per line",
    'lib.auth.uses': "Records",
    'lib.auth.usesCount': "{count} record(s)",
    'lib.auth.actions': "Actions",
    'lib.auth.records': "Its records",
    'lib.auth.edit': "Edit",
    'lib.auth.merge': "Merge",
    'lib.auth.mergeTitle': "Merge \"{name}\"",
    'lib.auth.mergeSub': "The duplicate joins the kept authority; its form becomes a variant.",
    'lib.auth.mergeSearch': "The authority to keep",
    'lib.auth.mergeGo': "Merge",
    'lib.auth.merged': "Merged: {count} responsibilit(ies) moved.",
    'lib.auth.saved': "Authority saved.",
    'lib.auth.lastName': "Name",
    'lib.auth.firstName': "First name",
    'lib.auth.dates': "Dates",
    'lib.auth.datesHint': "E.g. 1332-1406",
    'lib.auth.nationality': "Nationality",
    'lib.auth.note': "Note",
    'lib.loc.title': "Locations",
    'lib.loc.sub': "The tree of places, any depth, and the shelving plan that suggests where items go",
    'lib.loc.kinds': "Level types",
    'lib.loc.kindsSub': "Allowed nesting: a shelf holds no room",
    'lib.loc.newSite': "New site",
    'lib.loc.tree': "Places",
    'lib.loc.empty': "No place yet: create a site.",
    'lib.loc.expand': "Expand",
    'lib.loc.collapse': "Collapse",
    'lib.loc.status.open': "Open",
    'lib.loc.status.closed': "Closed",
    'lib.loc.status.works': "Under works",
    'lib.loc.pick': "A place",
    'lib.loc.pickHint': "Pick a place in the tree to view, edit or shelve copies there.",
    'lib.loc.path': "Path",
    'lib.loc.code': "Code",
    'lib.loc.codeHint': "Short and unique; printed on the shelf label",
    'lib.loc.capacity': "Indicative capacity",
    'lib.loc.capacityValue': "{capacity} copies · {count} shelved",
    'lib.loc.statusLabel': "Status",
    'lib.loc.contains': "Contains",
    'lib.loc.containsValue': "{nodes} sub-level(s) · {items} copie(s)",
    'lib.loc.bulk': "Move copies here",
    'lib.loc.bulkTitle': "Shelve in \"{name}\"",
    'lib.loc.bulkScan': "Scan copies one by one",
    'lib.loc.bulkEmpty': "No copy scanned.",
    'lib.loc.bulkGo': "Move {count} copie(s)",
    'lib.loc.bulkDone': "{count} copie(s) moved, each move recorded.",
    'lib.loc.bulkUnknown': "Unknown or withdrawn barcodes: {list}",
    'lib.loc.move': "Move the whole node",
    'lib.loc.moveTitle': "Move \"{name}\"",
    'lib.loc.moveSub': "With all its content; each copy keeps track of the move.",
    'lib.loc.newParent': "New parent",
    'lib.loc.noTarget': "No place can hold this level type.",
    'lib.loc.moved': "Node moved ({count} copie(s)).",
    'lib.loc.addChild': "Add a level below",
    'lib.loc.addChildUnder': "Add under \"{name}\"",
    'lib.loc.edit': "Edit",
    'lib.loc.delete': "Delete",
    'lib.loc.deleteTitle': "Delete \"{name}\"?",
    'lib.loc.deleteText': "The place is empty: no sub-level, copy or range.",
    'lib.loc.deleted': "Place deleted.",
    'lib.loc.saved': "Place saved.",
    'lib.loc.kind': "Level type",
    'lib.loc.kindChildren': "Can contain",
    'lib.loc.kindSaved': "Level types saved.",
    'lib.loc.newKindCode': "New type: code",
    'lib.loc.newKindLabel': "New type: label",
    'lib.loc.label': "Label",
    'lib.loc.position': "Order",
    'lib.loc.ranges': "Dewey shelving plan",
    'lib.loc.rangesSub': "Call number ranges and where they go",
    'lib.loc.addRange': "Add a range",
    'lib.loc.noRange': "No range: no place is suggested.",
    'lib.loc.range': "Range",
    'lib.loc.rangeLabel': "Label",
    'lib.loc.shelving': "Location",
    'lib.loc.from': "From",
    'lib.loc.to': "To",
    'lib.loc.deleteRange': "Delete the range",
    'lib.loc.rangeDeleted': "Range deleted.",
    'lib.loc.rangeSaved': "Range saved.",
    'lib.loc.unshelved': "{count} copie(s) are not shelved anywhere.",
    'lib.sug.title': "Purchase suggestions",
    'lib.sug.sub': "From readers and teachers, and replacements proposed after a loss",
    'lib.sug.new': "New suggestion",
    'lib.sug.none': "No suggestion.",
    'lib.sug.titleCol': "Title",
    'lib.sug.authors': "Authors",
    'lib.sug.origin': "Origin",
    'lib.sug.date': "Date",
    'lib.sug.quantity': "Quantity",
    'lib.sug.state': "Status",
    'lib.sug.note': "Note",
    'lib.sug.saved': "Suggestion updated.",
    'lib.sug.added': "Suggestion saved.",
    'lib.sug.st.suggested': "Suggested",
    'lib.sug.st.listed': "On the purchase list",
    'lib.sug.st.ordered': "Ordered",
    'lib.sug.st.received': "Received",
    'lib.sug.st.refused': "Refused",
    'lib.sug.from.reader': "Reader",
    'lib.sug.from.teacher': "Teacher",
    'lib.sug.from.staff': "Library",
    'lib.sug.from.loss': "Loss replacement",
    'lib.population.student': "Student",
    'lib.population.teacher': "Teacher",
    'lib.population.staff': "Staff",
    'lib.population.external': "External",
    'lib.formula.year': "Academic year",
    'lib.formula.twelve_months': "Rolling 12 months",
    'lib.formula.semester': "Semester",
    'lib.formula.month': "Month",
    'lib.formula.day': "Day",
    'lib.feeKind.membership': "Membership",
    'lib.feeKind.rental': "Rental",
    'lib.feeKind.late': "Late fine",
    'lib.feeKind.damage': "Damage",
    'lib.feeKind.loss': "Loss",
    'lib.feeKind.processing': "Processing fee",
    'lib.feeKind.duplicate': "Card duplicate",
    'lib.feature.subscriptions': "Paid memberships",
    'lib.feature.subscriptions.hint': "paid formulas per category",
    'lib.feature.rental': "Rental fees",
    'lib.feature.rental.hint': "on marked editions or copies",
    'lib.feature.fines': "Money fines",
    'lib.feature.fines.hint': "otherwise suspension days only",
    'lib.feature.own_card': "Separate library card",
    'lib.feature.own_card.hint': "off: the student card is the library card",
    'lib.feature.opac': "Reader portal (OPAC)",
    'lib.feature.opac.hint': "catalogue and reader space on the portal",
    'lib.feature.online_holds': "Online holds",
    'lib.feature.online_holds.hint': "from the portal and the app",
    'lib.feature.sms': "SMS notifications",
    'lib.feature.sms.hint': "on top of email and portal",
    'lib.penalty.money': "Money only",
    'lib.penalty.suspension': "Suspension days only",
    'lib.penalty.both': "Money and suspension days",
    'lib.membership.pending': "Awaiting payment",
    'lib.membership.active': "Active",
    'lib.membership.suspended': "Suspended",
    'lib.membership.expired': "Expired",
    'lib.membership.cancelled': "Cancelled",
    'lib.cardState.active': "Valid",
    'lib.cardState.lost': "Lost",
    'lib.cardState.replaced': "Replaced",
    'lib.cardState.expired': "Expired",
    'lib.readers.pages': "Pages",
    'lib.readers.prev': "Previous",
    'lib.readers.next': "Next",
    'lib.readers.pageOf': "Page {page} of {pages}",
    'lib.reader.noFees': "No charges.",
    'lib.reader.fee': "Charge",
    'lib.reader.feeDate': "Issued",
    'lib.reader.feeDue': "Amount",
    'lib.reader.feeState': "Status",
    'lib.reader.actions': "Actions",
    'lib.reader.invoice': "invoice {number}",
    'lib.reader.receipt': "receipt {number}",
    'lib.reader.pay': "Record receipt",
    'lib.reader.waive': "Waive",
    'lib.reader.cancelFee': "Cancel",
    'lib.reader.payTitle': "Record the Finance receipt",
    'lib.reader.waiveTitle': "Waive a charge",
    'lib.reader.cancelFeeTitle': "Cancel a charge issued in error",
    'lib.reader.receiptNumber': "Receipt number",
    'lib.reader.receiptHint': "The receipt given by Finance: the library collects nothing.",
    'lib.reader.waiveAmount': "Amount ({currency})",
    'lib.reader.reason': "Reason",
    'lib.reader.reasonHint': "Required, written to the log.",
    'lib.readers.state.all': "All",
    'lib.readers.state.late': "Late",
    'lib.readers.state.debt': "With debt",
    'lib.readers.state.suspended': "Suspended",
    'lib.readers.state.expired': "No membership",
    'lib.readers.state.external': "External",
    'lib.readers.title': "Readers",
    'lib.readers.sub': "Students, teachers and staff taken from their records; external readers registered here",
    'lib.readers.export': "Export",
    'lib.readers.external': "External reader",
    'lib.readers.enrol': "Register",
    'lib.readers.search': "Name or card number",
    'lib.readers.category': "Category",
    'lib.readers.allCategories': "All categories",
    'lib.readers.state': "Status",
    'lib.readers.selected': "{count} selected",
    'lib.readers.printCards': "Print cards",
    'lib.readers.clearSelection': "Clear selection",
    'lib.readers.count': "{count} reader(s)",
    'lib.readers.selectAll': "Select all",
    'lib.readers.reader': "Reader",
    'lib.readers.card': "Card",
    'lib.readers.until': "Member until",
    'lib.readers.loans': "Loans",
    'lib.readers.debt': "Debt",
    'lib.readers.status': "Status",
    'lib.readers.none': "No reader matches.",
    'lib.readers.select': "Select {name}",
    'lib.readers.externalTag': "external",
    'lib.readers.noMembership': "No membership",
    'lib.readers.lateCount': "{count} late",
    'lib.readers.suspended': "Suspended",
    'lib.readers.active': "Active",
    'lib.readers.enrolTitle': "Register a person",
    'lib.readers.enrolSub': "A known student, teacher or employee: no double entry",
    'lib.readers.person': "Person",
    'lib.readers.personHint': "Type at least two letters of the name.",
    'lib.readers.noPopulation': "Contact",
    'lib.readers.already': "already a reader",
    'lib.readers.categoryAuto': "By default, that of their population and cycle.",
    'lib.readers.categoryDefault': "Default category",
    'lib.readers.externalTitle': "New external reader",
    'lib.readers.externalSub': "Alumnus, parent, researcher, public",
    'lib.readers.create': "Create",
    'lib.readers.lastName': "Last name",
    'lib.readers.firstNames': "First names",
    'lib.readers.nationalId': "ID document (national ID, passport)",
    'lib.readers.email': "Email",
    'lib.readers.phone': "Phone",
    'lib.readers.externalHint': "The ID and a contact are required. The category membership opens at once; if paid, it awaits its receipt.",
    'lib.reader.title': "Reader",
    'lib.reader.back': "Readers",
    'lib.reader.cardNumber': "card {number}",
    'lib.reader.lend': "Lend",
    'lib.reader.suspendedBecause': "Suspended: {reason}. It lifts once the cause is gone.",
    'lib.reader.penaltyUntil': "Late penalty: lending suspended until {date}.",
    'lib.reader.blockedLate': "A loan is {days} days late: lending is blocked.",
    'lib.reader.blockedDebt': "Debt of {amount} over the threshold: lending is blocked.",
    'lib.reader.situation': "Standing",
    'lib.reader.changeCategory': "Change category",
    'lib.reader.lift': "Lift suspension",
    'lib.reader.suspend': "Suspend",
    'lib.reader.category': "Category",
    'lib.reader.email': "Email",
    'lib.reader.phone': "Phone",
    'lib.reader.debt': "Debt",
    'lib.reader.noDebt': "none",
    'lib.reader.late': "Lateness",
    'lib.reader.noLate': "none",
    'lib.reader.historyOptOut': "History turned off by reader",
    'lib.reader.yes': "yes",
    'lib.reader.no': "no",
    'lib.reader.membership': "Membership",
    'lib.reader.membershipSub': "Paid through Finance: no parallel collection",
    'lib.reader.newMembership': "New membership",
    'lib.reader.noMembership': "No active membership: the reader cannot borrow.",
    'lib.reader.pending': "Membership awaiting payment of {amount} to Finance.",
    'lib.reader.active': "Active",
    'lib.reader.membershipLine': "{formula}, from {from} to {to}",
    'lib.reader.automatic': "automatic at enrolment",
    'lib.reader.formula': "Formula",
    'lib.reader.period': "Period",
    'lib.reader.amount': "Price",
    'lib.reader.state': "Status",
    'lib.reader.free': "Free",
    'lib.reader.cancelMembership': "Cancel membership",
    'lib.reader.card': "Card",
    'lib.reader.cardSub': "Unique number, never reused",
    'lib.reader.issueCard': "Issue card",
    'lib.reader.lostCard': "Report lost",
    'lib.reader.printCard': "Print",
    'lib.reader.noCard': "No active card.",
    'lib.reader.studentCard': "Student card",
    'lib.reader.libraryCard': "Library card",
    'lib.reader.validUntil': "Valid until {date}",
    'lib.reader.clearance': "Clearance",
    'lib.reader.clearanceSub': "Free of any obligation to the library, verifiable online",
    'lib.reader.issueClearance': "Issue clearance",
    'lib.reader.clear': "Clear of obligations",
    'lib.reader.notClear': "No clearance: {loans} open loan(s), {debt} due.",
    'lib.reader.open': "Open",
    'lib.reader.loans': "Open loans ({count})",
    'lib.reader.noLoans': "No open loans.",
    'lib.reader.item': "Copy",
    'lib.reader.loanedAt': "Lent on",
    'lib.reader.due': "Due",
    'lib.reader.renew': "Renew",
    'lib.reader.holds': "Holds",
    'lib.reader.noHolds': "No holds.",
    'lib.reader.pickupUntil': "pick up by {date}",
    'lib.reader.rank': "position {rank}",
    'lib.reader.fees': "Charges",
    'lib.reader.feesSub': "Invoiced by Finance for a student; otherwise the receipt is recorded here",
    'lib.reader.newFee': "New charge",
    'lib.reader.reminders': "Reminders sent",
    'lib.reader.noReminders': "No reminders.",
    'lib.reader.manualReminder': "Manual reminder",
    'lib.reader.guardian': "also to the guardian",
    'lib.reader.history': "Loan history",
    'lib.reader.historyTraced': "Personal data: viewing it is logged (RG-08).",
    'lib.reader.showHistory': "Show history",
    'lib.reader.noHistory': "No returned loans.",
    'lib.reader.delete': "Delete reader",
    'lib.reader.deleteTitle': "Delete this reader?",
    'lib.reader.deleteText': "Only a reader with no loan, charge or card can be deleted; otherwise suspend them. The person stays.",
    'lib.reader.lostCardTitle': "Report the card lost?",
    'lib.reader.lostCardText': "The old number is cancelled at once; a duplicate is issued, with the set fee.",
    'lib.reader.lifted': "Suspension lifted.",
    'lib.reader.cardIssued': "Card issued.",
    'lib.reader.renewed': "Loan renewed.",
    'lib.reader.duplicated': "Duplicate issued.",
    'lib.reader.saved': "Saved.",
    'lib.reader.suspended': "Reader suspended.",
    'lib.reader.membershipCreated': "Membership created.",
    'lib.reader.membershipCancelled': "Membership cancelled.",
    'lib.reader.feeCreated': "Charge issued.",
    'lib.reader.clearanceIssued': "Clearance issued: it is in the list.",
    'lib.reader.confirm': "Confirm",
    'lib.reader.categoryHint': "The category sets the loan policy and prices.",
    'lib.reader.suspendTitle': "Suspend the reader",
    'lib.reader.until': "Until",
    'lib.reader.untilHint': "Empty: until lifted manually.",
    'lib.reader.startsOn': "Start",
    'lib.reader.startsOnHint': "Empty: today, or when the current one ends.",
    'lib.reader.membershipHint': "Free: active at once with the card; paid: the charge goes to Finance.",
    'lib.reader.feeKind': "Kind",
    'lib.reader.feeLabel': "Label",
    'lib.reader.feeHint': "For a student, Finance issues the invoice.",
    'lib.reader.purposeLabel': "For",
    'lib.reader.purpose.diploma': "Diploma",
    'lib.reader.purpose.departure': "Departure",
    'lib.reader.purpose.transfer': "Transfer",
    'lib.reader.purpose.other': "Other",
    'lib.set.title': "Library settings",
    'lib.set.subOn': "The module is on",
    'lib.set.subOff': "The module is off: menus and screens hidden, data kept",
    'lib.set.tab.general': "General",
    'lib.set.tab.policies': "Loan policies",
    'lib.set.tab.categories': "Categories and prices",
    'lib.set.tab.calendar': "Calendar and delays",
    'lib.set.tab.fees': "Fees",
    'lib.set.saved': "Settings saved.",
    'lib.set.save': "Save",
    'lib.set.edit': "Edit",
    'lib.set.delete': "Delete",
    'lib.set.setup.title': "Set up the library",
    'lib.set.setup.sub': "The minimum before the first loan: a site, a default policy, a barcode format and a card",
    'lib.set.setup.site': "The first site",
    'lib.set.setup.siteSub': "Other sites and the location tree come next",
    'lib.set.setup.siteCode': "Site code",
    'lib.set.setup.siteCodeHint': "Capitals, digits and hyphens (RAB)",
    'lib.set.setup.siteLabel': "Site name",
    'lib.set.setup.numbering': "Barcode",
    'lib.set.setup.numberingSub': "Prefix and sequence; a barcode is never reused",
    'lib.set.setup.card': "The card",
    'lib.set.setup.cardSub': "The student card can serve as the library card",
    'lib.set.setup.policy': "The default policy",
    'lib.set.setup.policySub': "It applies to every category and document until finer rules exist",
    'lib.set.setup.run': "Start the library",
    'lib.set.cardStudent': "The student card is the library card",
    'lib.set.cardLibrary': "A separate library card",
    'lib.set.module': "The module",
    'lib.set.moduleSub': "Switched on by the institution administrator",
    'lib.set.enabledOn': "On since {date}",
    'lib.set.enabled': "Library on",
    'lib.set.enabledHint': "Switching off hides menus and screens and keeps all data",
    'lib.set.features': "Sub-features",
    'lib.set.featuresSub': "Each one is switched separately",
    'lib.set.penalties': "Fines and clearance",
    'lib.set.penaltyMode': "Late penalty regime",
    'lib.set.penaltyModeHint': "Can be adjusted per loan policy",
    'lib.set.quitusBlocks': "Block diploma delivery without clearance",
    'lib.set.quitusBlocksHint': "Off by default; other modules can read the clearance",
    'lib.set.foundRefund': "Lost copy found after payment",
    'lib.set.refund.full': "Full refund",
    'lib.set.refund.half': "Partial refund (50 %)",
    'lib.set.refund.none': "No refund",
    'lib.set.numbering': "Codes and numbering",
    'lib.set.barcodePrefix': "Barcode prefix",
    'lib.set.barcodeDigits': "Sequence digits",
    'lib.set.cardPrefix': "Card prefix",
    'lib.set.barcodeExample': "Format: {example}",
    'lib.set.privacy': "Personal data",
    'lib.set.historyYears': "Anonymise loan history after",
    'lib.set.historyYearsHint': "Except outstanding debt (RG-08)",
    'lib.set.years': "{count} year(s)",
    'lib.set.traceHistory': "Keep readers' loan history",
    'lib.set.offNote': "Switching the module off hides menus and screens and keeps all data; an open loan requires explicit confirmation.",
    'lib.set.offTitle': "Switch the library off?",
    'lib.set.offText': "Menus and screens disappear; all data stays and comes back when switched on.",
    'lib.set.offForce': "{count} loan(s) are open: do you confirm?",
    'lib.set.off': "Switch off",
    'lib.set.offAnyway': "Switch off despite loans",
    'lib.set.graceDays': "Grace period (days)",
    'lib.set.pickupDays': "Pickup delay (open days)",
    'lib.set.blockDays': "Block after (days late)",
    'lib.set.blockDebt': "Block above ({currency})",
    'lib.set.reminders': "Reminders (days from due date)",
    'lib.set.remindersHint': "E.g. -2, +1, +7, +30; the last one is a formal notice with suspension",
    'lib.set.lossAfterDays': "Presumed lost after (days)",
    'lib.set.lossAfterDaysHint': "Counted after the formal notice",
    'lib.set.autoMembership': "Automatic membership at enrolment",
    'lib.set.autoMembershipHint': "Each enrolment of the year opens the membership, ending with the academic year",
    'lib.set.cal.open': "Opening days",
    'lib.set.cal.openSub': "Closed days do not count as late; a due date on a closed day moves to the next open day",
    'lib.set.cal.closures': "Library closures",
    'lib.set.cal.closuresSub': "On top of Planning holidays and vacations",
    'lib.set.cal.from': "From",
    'lib.set.cal.to': "To",
    'lib.set.cal.label': "Reason",
    'lib.set.cal.remove': "Remove this closure",
    'lib.set.cal.add': "Add a closure",
    'lib.set.cal.delays': "Delays and thresholds",
    'lib.set.cal.priority': "Hold priority",
    'lib.set.cal.prioritySub': "At equal arrival, the first population goes first",
    'lib.set.cal.up': "Move up",
    'lib.set.cal.down': "Move down",
    'lib.set.fees.title': "Fees",
    'lib.set.fees.sub': "There is no deposit; some items are rented",
    'lib.set.processingFee': "Loss processing fee ({currency})",
    'lib.set.duplicateFee': "Card duplicate ({currency})",
    'lib.set.fees.rental': "Rental fees",
    'lib.set.rentalPayBefore': "Require payment before handing over",
    'lib.set.rentalPayBeforeHint': "Otherwise added to the reader's account",
    'lib.set.rentalRefund': "Refund an early return pro rata",
    'lib.set.fees.rentalHint': "The rental price is set on the record or copy, per reader category.",
    'lib.set.fees.replacement': "Flat replacement prices",
    'lib.set.fees.replacementSub': "When the lost copy has no purchase price",
    'lib.set.pol.title': "Loan policies",
    'lib.set.pol.sub': "Reader category × document type × copy loan status; the most precise applies",
    'lib.set.pol.new': "New rule",
    'lib.set.pol.editTitle': "Edit rule",
    'lib.set.pol.category': "Category",
    'lib.set.pol.docType': "Document type",
    'lib.set.pol.loanStatus': "Status",
    'lib.set.pol.maxLoans': "Loans",
    'lib.set.pol.duration': "Duration",
    'lib.set.pol.renewal': "Renewal",
    'lib.set.pol.maxHolds': "Holds",
    'lib.set.pol.penalty': "Late penalty",
    'lib.set.pol.default': "default",
    'lib.set.pol.allCategories': "All categories",
    'lib.set.pol.allTypes': "All types",
    'lib.set.pol.allStatuses': "All statuses",
    'lib.set.pol.hours': "{count} h",
    'lib.set.pol.days': "{count} d",
    'lib.set.pol.noRenewal': "none",
    'lib.set.pol.renewalText': "{count} × {days} d",
    'lib.set.pol.fineText': "{amount} / day, cap {cap}",
    'lib.set.pol.suspText': "{days} suspension day(s) per day",
    'lib.set.pol.none': "none",
    'lib.set.pol.allRules': "For all rules",
    'lib.set.pol.block': "Block threshold",
    'lib.set.pol.blockText': "more than {days} days late or debt over {amount}",
    'lib.set.pol.openDays': "{count} open days",
    'lib.set.pol.yearEnd': "Year end",
    'lib.set.pol.yearEndText': "the due date never passes the membership end",
    'lib.set.pol.calendar': "Calendar",
    'lib.set.pol.calendarText': "closures, holidays and vacations do not count",
    'lib.set.pol.daysLabel': "Duration (days)",
    'lib.set.pol.hoursLabel': "or duration (hours)",
    'lib.set.pol.renewals': "Renewals",
    'lib.set.pol.renewalDays': "Renewal length (days)",
    'lib.set.pol.finePerDay': "Fine per day ({currency})",
    'lib.set.pol.fineCap': "Cap ({currency})",
    'lib.set.pol.suspensionPerDay': "Suspension days per late day",
    'lib.set.pol.modeHint': "Empty: the institution regime",
    'lib.set.pol.modeInherit': "Institution regime",
    'lib.set.pol.maxByType': "Caps per type",
    'lib.set.pol.maxByTypeHint': "E.g. media=2, thesis=1",
    'lib.set.pol.defaultHint': "The default rule cannot be deleted: it applies when no other does.",
    'lib.set.cat.title': "Reader categories",
    'lib.set.cat.sub': "The category sets the loan policy, membership and rental prices",
    'lib.set.cat.new': "New category",
    'lib.set.cat.editTitle': "Edit category",
    'lib.set.cat.label': "Label",
    'lib.set.cat.code': "Code",
    'lib.set.cat.codeHint': "Lower case and underscores; it never changes",
    'lib.set.cat.population': "Population",
    'lib.set.cat.cycle': "Cycle",
    'lib.set.cat.cycleHint': "For students: this cycle's category",
    'lib.set.cat.anyCycle': "All cycles",
    'lib.set.cat.formula': "Formula",
    'lib.set.cat.fee': "Membership",
    'lib.set.cat.feeLabel': "Membership price ({currency})",
    'lib.set.cat.feeHint': "0: free",
    'lib.set.cat.readers': "Readers",
    'lib.stats.title': "Statistics",
    'lib.stats.back': "Dashboard",
    'lib.stats.sub': "From {from} to {to}",
    'lib.stats.register': "Inventory register {year}",
    'lib.stats.exportLoans': "Export loans",
    'lib.stats.from': "From",
    'lib.stats.to': "To",
    'lib.stats.apply': "Show",
    'lib.stats.loans': "Loans",
    'lib.stats.loansNote': "over the period",
    'lib.stats.activeReaders': "Active readers",
    'lib.stats.activeReadersNote': "at least one loan",
    'lib.stats.rotation': "Turnover rate",
    'lib.stats.rotationNote': "loans per copy ({items} copies)",
    'lib.stats.lateRate': "Late rate",
    'lib.stats.lateRateNote': "loans returned after due date",
    'lib.stats.neverLoaned': "Never lent",
    'lib.stats.neverLoanedNote': "copies",
    'lib.stats.value': "Collection value",
    'lib.stats.valueNote': "purchase price, otherwise flat rate",
    'lib.stats.byMonth': "Loans per month",
    'lib.stats.top': "Most borrowed",
    'lib.stats.work': "Title",
    'lib.stats.empty': "No loans yet over the period.",
    'lib.stats.byCategory': "By reader category",
    'lib.stats.byTraining': "By programme",
    'lib.stats.byType': "By document type",
    'lib.stats.byClass': "Collection by Dewey class",
    'lib.stats.byClassSub': "Copies held",
    'lib.stats.noDewey': "No number",
    'lib.imp.title': "Import and export",
    'lib.imp.sub': "Every import is simulated line by line before anything is written",
    'lib.imp.upload': "Import a file",
    'lib.imp.uploadSub': "The simulation shows what will be created, updated or rejected",
    'lib.imp.target': "Object",
    'lib.imp.format': "Format",
    'lib.imp.file': "File",
    'lib.imp.fileHint': "CSV (semicolon or comma, UTF-8), ISO 2709 or MARCXML; 30 MB max",
    'lib.imp.sending': "Sending…",
    'lib.imp.simulate': "Simulate the import",
    'lib.imp.template': "CSV template",
    'lib.imp.note': "A whole import can be undone until a loan touches its copies. HTML or script neutralised, unique barcodes, ISBNs checked before writing.",
    'lib.imp.exports': "Export",
    'lib.imp.exportsSub': "Within the rights of whoever runs it",
    'lib.imp.recent': "Recent imports",
    'lib.imp.none': "No import yet.",
    'lib.imp.when': "Uploaded",
    'lib.imp.result': "Result",
    'lib.imp.state': "Status",
    'lib.imp.countsLine': "{create} created, {update} updated, {reject} rejected",
    'lib.imp.back': "Import and export",
    'lib.imp.rows': "{count} row(s)",
    'lib.imp.mode': "Mode",
    'lib.imp.key': "Matching key",
    'lib.imp.site': "Default site",
    'lib.imp.siteHint': "The shelving plan refines the location by call number",
    'lib.imp.siteFirst': "The first site",
    'lib.imp.columns': "Column mapping",
    'lib.imp.columnsSub': "Kept with the import; an ignored column is not read",
    'lib.imp.column': "File column",
    'lib.imp.field': "Kelassi field",
    'lib.imp.fieldFor': "Field for column {column}",
    'lib.imp.ignore': "Ignored",
    'lib.imp.resimulate': "Run the simulation again",
    'lib.imp.resimulated': "Simulation redone.",
    'lib.imp.marc': "MARC records",
    'lib.imp.marcSub': "MARC fields are read as is",
    'lib.imp.marcText': "Title (200 or 245), authors (700 or 100), publisher (210 or 264), ISBN (010 or 020), Dewey (676 or 082), copies (995 or 952).",
    'lib.imp.created': "Created",
    'lib.imp.createdNote': "new records",
    'lib.imp.updated': "Updated",
    'lib.imp.updatedNote': "matched",
    'lib.imp.rejected': "Rejected",
    'lib.imp.rejectedNote': "downloadable report",
    'lib.imp.simulation': "Simulation, line by line",
    'lib.imp.simulationSub': "Nothing is written yet",
    'lib.imp.report': "Report, line by line",
    'lib.imp.lines': "Lines",
    'lib.imp.line': "Line",
    'lib.imp.record': "Record",
    'lib.imp.filter.all': "All",
    'lib.imp.filter.create': "Created",
    'lib.imp.filter.update': "Updated",
    'lib.imp.filter.reject': "Rejected",
    'lib.imp.downloadReport': "Line report",
    'lib.imp.run': "Import {count} line(s)",
    'lib.imp.ran': "Import run.",
    'lib.imp.undo': "Undo the import",
    'lib.imp.undone': "Import undone.",
    'lib.imp.undoTitle': "Undo this import?",
    'lib.imp.undoText': "Its {count} copies and the records it created with no other copy are removed.",
    'lib.imp.dirty': "Settings changed: run the simulation again before importing.",
    'lib.imp.touched': "{count} loan(s) touched its copies: the import can no longer be undone.",
    'lib.imp.target.records': "Records and copies",
    'lib.imp.target.items': "Copies (by ISBN)",
    'lib.imp.target.authorities': "Authorities",
    'lib.imp.target.readers': "External readers",
    'lib.imp.target.locations': "Locations",
    'lib.imp.headTitle.records': "Import records",
    'lib.imp.headTitle.items': "Import copies",
    'lib.imp.headTitle.authorities': "Import authorities",
    'lib.imp.headTitle.readers': "Import external readers",
    'lib.imp.headTitle.locations': "Import locations",
    'lib.imp.format.csv': "CSV or Excel (CSV)",
    'lib.imp.format.unimarc': "UNIMARC (ISO 2709)",
    'lib.imp.format.marc21': "MARC21 (ISO 2709)",
    'lib.imp.format.marcxml': "MARCXML",
    'lib.imp.format.isbn': "ISBN list",
    'lib.imp.st.simulated': "Simulated",
    'lib.imp.st.queued': "Queued",
    'lib.imp.st.done': "Imported",
    'lib.imp.st.undone': "Undone",
    'lib.imp.mode.both': "Create and update",
    'lib.imp.mode.create': "Create only",
    'lib.imp.mode.update': "Update only",
    'lib.imp.key.isbn': "ISBN",
    'lib.imp.key.barcode': "Barcode",
    'lib.imp.key.external': "Title and year",
    'lib.imp.action.create': "Creation",
    'lib.imp.action.update': "Update",
    'lib.imp.action.reject': "Rejected",
    'lib.imp.export.catalog': "Catalogue",
    'lib.imp.export.items': "Copies",
    'lib.imp.export.authorities': "Authorities",
    'lib.imp.export.readers': "Readers",
    'lib.imp.export.locations': "Locations",
    'lib.imp.export.loans': "Loans",
    'lib.imp.export.fees': "Charges",
    'lib.imp.export.holds': "Holds",
    'lib.imp.exportFormat.csv': "CSV",
    'lib.imp.exportFormat.marc21': "MARC21",
    'lib.imp.exportFormat.unimarc': "UNIMARC",
    'lib.imp.exportFormat.ris': "RIS",
    'lib.imp.exportFormat.bibtex': "BibTeX",
    'lib.imp.exportFormat.dc': "Dublin Core",
    'lib.imp.f.title': "Title",
    'lib.imp.f.subtitle': "Subtitle",
    'lib.imp.f.authors': "Authors (Last, First; …)",
    'lib.imp.f.publisher': "Publisher",
    'lib.imp.f.place': "Place",
    'lib.imp.f.year': "Year",
    'lib.imp.f.isbn': "ISBN",
    'lib.imp.f.issn': "ISSN",
    'lib.imp.f.cote': "Call number",
    'lib.imp.f.dewey': "Dewey number",
    'lib.imp.f.language': "Language",
    'lib.imp.f.docType': "Document type",
    'lib.imp.f.pages': "Pages",
    'lib.imp.f.summary': "Summary",
    'lib.imp.f.collection': "Series",
    'lib.imp.f.subjects': "Subjects",
    'lib.imp.f.barcode': "Barcode",
    'lib.imp.f.location': "Location (code)",
    'lib.imp.f.price': "Price",
    'lib.imp.f.loanStatus': "Loan status",
    'lib.imp.f.acquiredOn': "Purchase date",
    'lib.imp.f.supplier': "Supplier",
    'lib.imp.f.kind': "Authority kind",
    'lib.imp.f.name': "Name",
    'lib.imp.f.firstName': "First name",
    'lib.imp.f.variants': "Variant forms",
    'lib.imp.f.lastName': "Last name",
    'lib.imp.f.firstNames': "First names",
    'lib.imp.f.nationalId': "ID document",
    'lib.imp.f.email': "Email",
    'lib.imp.f.category': "Category",
    'lib.imp.f.path': "Path (Site/Room/Bay)",
    'lib.imp.f.code': "Code",
    'lib.imp.f.externalId': "External id",
    'lib.form.sec.courses': "Course reading list",
    'lib.form.coursesHint': "The programmes and courses that recommend this work; it shows on the course page and in their students' portal.",
    'lib.form.noCourses': "No course recommends this work.",
    'lib.form.courseAdd': "Add a programme or a course",
    'lib.form.courseSearch': "Programme or course code or title",
    'lib.form.trainingSearch': "Programme code or title",
    'lib.form.courseIsTraining': "Programme",
    'lib.form.courseTeacher': "Recommending teacher",
    'lib.form.courseNote': "Note",
    'lib.form.courseNoteHint': "For example: required reading, chapters 2 to 4",
    'lib.form.removeCourse': "Remove this course",
    'lib.form.thesisTraining': "Programme",
    'lib.form.setParent': "Whole-set record",
    'lib.form.setSearch': "Title, author or ISBN of the set",
    'lib.form.setHint': "For a volume: the multi-volume record, which will show present and missing volumes.",
    'lib.form.setRemove': "Detach from the set",
};

/**
 * D43 : l'arabe (arabe standard, registre administratif marocain), écrit le
 * 18 octobre. Chiffres latins ; pas de « (s) » : les compteurs sont tournés en
 * « libellé : {count} », juste pour 0, 1, 2 ou 100, au lieu des six formes du
 * pluriel arabe. Glossaire : tools/i18n/GLOSSAIRE-AR.md. À faire relire par un
 * arabophone de l'établissement avant l'écran recetté en arabe.
 */
const ar: Record<TranslationKey, string> = {
    'app.name': "Kelassi",
    'signIn.title': "تسجيل الدخول",
    'signIn.email': "البريد الإلكتروني",
    'signIn.password': "كلمة المرور",
    'signIn.submit': "تسجيل الدخول",
    'signIn.pending': "جارٍ تسجيل الدخول…",
    'signIn.refused': "بيانات الدخول غير صحيحة.",
    'signIn.unreachable': "تعذّر الاتصال بالخادم.",
    'signIn.forgot': "نسيت كلمة المرور؟",
    'signIn.passwordChanged': "تم تغيير كلمة المرور. أُغلقت جميع جلساتك: سجّل الدخول من جديد.",
    'signIn.passwordSet': "تم حفظ كلمة المرور. يمكنك الآن تسجيل الدخول.",
    'session.loading': "جارٍ التحميل…",
    'session.expired': "انتهت جلستك. سجّل الدخول من جديد.",
    'session.signOut': "تسجيل الخروج",
    'session.changePassword': "تغيير كلمة المرور",
    'menu.empty': "لا يتاح لك أي عنصر في القائمة.",
    'menu.label': "التنقل الرئيسي",
    'language.label': "اللغة",
    'screen.pending': "هذه الشاشة لم تُنجز بعد.",
    'screen.pendingHint': "المسار مُعرَّف والصلاحية مُتحقَّق منها: لا ينقص سوى المحتوى.",
    'notFound.title': "الصفحة غير موجودة",
    'notFound.body': "هذا العنوان لا يطابق أي شاشة في التطبيق.",
    'notFound.back': "العودة إلى الرئيسية",
    'common.cancel': "إلغاء",
    'common.save': "حفظ",
    'common.saving': "جارٍ الحفظ…",
    'common.confirm': "تأكيد",
    'common.back': "رجوع",
    'common.never': "أبدًا",
    'common.yes': "نعم",
    'common.no': "لا",
    'common.loading': "جارٍ التحميل…",
    'common.forbidden': "ليست لديك صلاحية القيام بذلك.",
    'common.notFound': "غير موجود.",
    'common.error': "حدث خطأ. أعد المحاولة.",
    'population.staff': "موظف",
    'population.teacher': "أستاذ",
    'population.student': "طالب",
    'population.guardian': "وليّ الأمر",
    'accounts.title': "الحسابات",
    'accounts.new': "حساب جديد",
    'accounts.search': "ابحث بالاسم أو البريد الإلكتروني",
    'accounts.filter.population': "الفئة",
    'accounts.filter.status': "الحالة",
    'accounts.filter.all': "الكل",
    'accounts.status.active': "نشط",
    'accounts.status.inactive': "معطّل",
    'accounts.status.locked': "مقفل",
    'accounts.status.must_change_password': "يجب تغيير كلمة المرور",
    'accounts.column.name': "الاسم",
    'accounts.column.population': "الفئة",
    'accounts.column.roles': "الأدوار",
    'accounts.column.status': "الحالة",
    'accounts.column.lastLogin': "آخر تسجيل دخول",
    'accounts.noRole': "بلا دور",
    'accounts.empty': "لا يوجد حساب مطابق.",
    'accounts.readOnly': "خارج نطاق صلاحيتك",
    'account.email': "البريد الإلكتروني",
    'account.displayName': "الاسم المعروض",
    'account.population': "الفئة",
    'account.status': "الحالة",
    'account.lastLogin': "آخر تسجيل دخول",
    'account.password': "كلمة المرور",
    'account.password.none': "لم تُحدَّد بعد (في انتظار التفعيل)",
    'account.password.provisional': "مؤقتة، يجب تغييرها عند تسجيل الدخول القادم",
    'account.password.set': "حُدِّدت في {date}",
    'account.lockedUntil': "مقفل حتى {date}",
    'account.createdAt': "تاريخ الإنشاء",
    'account.assignments': "الأدوار والنطاق",
    'account.assignments.none': "بلا دور: يمكن لهذا الحساب تسجيل الدخول لكنه لا يصل إلى أي شيء.",
    'account.assignments.hint': "تُسنَد الأدوار من شاشة « الأدوار والإسنادات ».",
    'account.scope.centers': "المراكز",
    'account.scope.cycles': "الأسلاك",
    'account.scope.domains': "المجالات",
    'account.scope.products': "المنتجات",
    'account.scope.allCenters': "جميع المراكز",
    'account.scope.allCycles': "جميع الأسلاك",
    'account.scope.allDomains': "جميع المجالات",
    'account.scope.allProducts': "جميع المنتجات",
    'account.period': "المدة",
    'account.period.always': "بلا حد",
    'account.period.from': "ابتداءً من {date}",
    'account.period.until': "حتى {date}",
    'account.outOfReach': "يتجاوز هذا الحساب نطاق صلاحيتك: يمكنك الاطلاع عليه دون تعديله.",
    'account.action.edit': "تعديل",
    'account.action.deactivate': "تعطيل",
    'account.action.activate': "إعادة التفعيل",
    'account.action.activateLocked': "إلغاء القفل",
    'account.action.assignPassword': "تحديد كلمة مرور مؤقتة",
    'account.action.sendLink': "إرسال رابط بالبريد الإلكتروني",
    'account.action.sendLink.confirm': "إرسال رابط إلى {email} لتحديد كلمة المرور؟ يبقى صالحًا مدة 72 ساعة.",
    'account.done.deactivate': "تم تعطيل الحساب وإغلاق الجلسات.",
    'account.done.activate': "تمت إعادة تفعيل الحساب.",
    'account.done.link': "تم إرسال الرابط إلى {email}.",
    'account.done.password': "تم حفظ كلمة المرور المؤقتة. أبلِغها لصاحب الحساب: سيتعيّن عليه تغييرها عند تسجيل الدخول القادم.",
    'account.done.saved': "تم حفظ التعديلات.",
    'account.done.created': "تم إنشاء الحساب.",
    'account.done.createdLink': "تم إنشاء الحساب. أُرسل رابط التفعيل إلى {email}.",
    'account.done.createdPassword': "تم إنشاء الحساب. أبلِغ صاحب الحساب كلمة المرور المؤقتة: سيتعيّن عليه تغييرها عند أول تسجيل دخول.",
    'app.home': "الرئيسية",
    'menu.expand': "توسيع القائمة",
    'menu.collapse': "طيّ القائمة",
    'menu.open': "عرض القائمة",
    'menu.close': "إغلاق القائمة",
    'common.moreActions': "إجراءات أخرى",
    'accounts.count': "الحسابات: {count}",
    'accounts.filter.allPopulations': "جميع الفئات",
    'accounts.filter.allStatuses': "جميع الحالات",
    'accounts.column.actions': "الإجراءات",
    'account.action.deactivate.confirmNamed': "تعطيل حساب {name}؟ ستُغلق جلساته المفتوحة فورًا.",
    'account.action.sendLink.short': "إرسال الرابط",
    'account.action.assignPassword.short': "كلمة مرور مؤقتة",
    'pagination.rowsPerPage': "عدد الأسطر في الصفحة",
    'pagination.range': "{from}–{to} من {total}",
    'pagination.first': "الصفحة الأولى",
    'pagination.previous': "الصفحة السابقة",
    'pagination.next': "الصفحة التالية",
    'pagination.last': "الصفحة الأخيرة",
    'pagination.page': "الصفحة {page}",
    'create.title': "حساب جديد",
    'create.credential': "كلمة المرور",
    'create.credential.link': "إرسال رابط التفعيل",
    'create.credential.linkHint': "يختار صاحب الحساب كلمة مروره بنفسه، ولا يعرفها أحد غيره. يبقى الرابط صالحًا مدة 72 ساعة.",
    'create.credential.password': "إدخال كلمة مرور مؤقتة",
    'create.credential.passwordHint': "تُبلِغها بنفسك، وسيتعيّن على صاحب الحساب تغييرها عند أول تسجيل دخول.",
    'create.credential.required': "اختر الطريقة التي سيحصل بها صاحب الحساب على كلمة المرور.",
    'create.generate': "توليد",
    'create.submit': "إنشاء الحساب",
    'create.noRoleYet': "يُنشأ الحساب بلا دور. أسنِد إليه أدواره بعد ذلك من شاشة « الأدوار والإسنادات ».",
    'password.current': "كلمة المرور الحالية",
    'password.new': "كلمة المرور الجديدة",
    'password.confirm': "تأكيد كلمة المرور الجديدة",
    'password.rule': "12 حرفًا على الأقل. العبارة أكثر أمانًا من كلمة معقدة.",
    'password.mismatch': "الإدخالان غير متطابقين.",
    'password.change.title': "تغيير كلمة المرور",
    'password.change.submit': "تغيير كلمة المرور",
    'password.change.hint': "ستُغلق جميع جلساتك، بما فيها هذه الجلسة: ستسجّل الدخول من جديد بكلمة المرور الجديدة.",
    'password.imposed.title': "اختر كلمة المرور",
    'password.imposed.body': "كلمة المرور التي استخدمتها مؤقتة، حدّدها أحد المسؤولين. استبدلها قبل المتابعة.",
    'password.provisional': "كلمة مرور مؤقتة",
    'password.provisional.hint': "12 حرفًا على الأقل. سيتعيّن على صاحب الحساب تغييرها عند تسجيل الدخول القادم، وستُغلق جلساته المفتوحة.",
    'forgot.title': "نسيت كلمة المرور",
    'forgot.body': "أدخل بريدك الإلكتروني: إذا كان مرتبطًا بحساب، فستتلقى رابطًا لاختيار كلمة مرور جديدة.",
    'forgot.submit': "إرسال الرابط",
    'forgot.sent': "إذا كان هناك حساب بهذا البريد الإلكتروني، فقد أُرسل إليه رابط للتو. يبقى صالحًا مدة ساعة واحدة.",
    'forgot.tooMany': "طلبات كثيرة من هذا الجهاز. أعد المحاولة بعد بضع دقائق.",
    'forgot.back': "العودة إلى تسجيل الدخول",
    'reset.title': "اختيار كلمة المرور",
    'reset.submit': "حفظ كلمة المرور",
    'reset.missing': "هذا الرابط غير مكتمل. افتحه مباشرة من الرسالة التي تلقيتها.",
    'reset.invalid': "هذا الرابط لم يعد صالحًا: سبق استخدامه، أو انتهت صلاحيته، أو أُرسل رابط أحدث منه.",
    'reset.askAgain': "طلب رابط جديد",
    'violation.blank': "هذا الحقل إلزامي.",
    'violation.email': "بريد إلكتروني غير صالح.",
    'violation.tooShort': "قصير جدًا.",
    'violation.tooLong': "طويل جدًا.",
    'violation.sameAsCurrent': "يجب أن تختلف كلمة المرور الجديدة عن الحالية.",
    'violation.emailTaken': "هذا البريد الإلكتروني مستخدم في حساب آخر.",
    'violation.currentPasswordInvalid': "كلمة المرور الحالية غير صحيحة.",
    'violation.tokenInvalid': "هذا الرابط لم يعد صالحًا.",
    'violation.choice': "قيمة غير معروفة.",
    'common.close': "إغلاق",
    'menu.trail': "مسار التنقل",
    'photo.title': "صورة الملف الشخصي",
    'photo.change': "تغيير الصورة",
    'photo.drop': "اسحب صورة إلى هنا",
    'photo.dropAnother': "اسحب صورة أخرى",
    'photo.or': "أو",
    'photo.choose': "اختيار ملف",
    'photo.rules': "JPG أو PNG أو WebP · 2 ميغابايت كحد أقصى · 200 × 200 بكسل على الأقل",
    'photo.crop': "القصّ",
    'photo.cropHint': "اسحب الصورة، أو استخدم أسهم لوحة المفاتيح، لوضعها داخل الدائرة.",
    'photo.zoom': "التكبير",
    'photo.preview': "معاينة",
    'photo.remove': "إزالة الصورة",
    'photo.error.type': "هذا الملف ليس صورة JPG أو PNG أو WebP قابلة للقراءة.",
    'photo.error.weight': "حجم هذه الصورة يتجاوز 2 ميغابايت.",
    'photo.error.small': "هذه الصورة صغيرة جدًا: 200 × 200 بكسل على الأقل.",
    'photo.done.saved': "تم حفظ الصورة.",
    'photo.done.removed': "تمت إزالة الصورة.",
    'profile.title': "ملفي الشخصي",
    'profile.open': "ملفي الشخصي",
    'profile.card': "الملف الشخصي",
    'profile.information': "المعلومات",
    'profile.security': "الأمان",
    'profile.roles': "الأدوار",
    'profile.rolesNone': "لا شيء",
    'profile.institution': "المؤسسة",
    'profile.lock': "القفل",
    'profile.lockNone': "لا يوجد",
    'profile.editProfile': "تعديل الملف الشخصي",
    'profile.noRolesTitle': "لا يوجد دور مُسند",
    'account.action.sendLink.tiny': "رابط",
    'account.action.assignPassword.tiny': "مؤقتة",
    'account.action.deactivate.long': "تعطيل الحساب",
    'roles.title': "الأدوار والإسنادات",
    'roles.subtitle': "من يملك أي دور، وعلى أي جزء من المؤسسة.",
    'roles.views': "طرق العرض",
    'roles.tab.assignments': "الإسنادات",
    'roles.tab.roles': "الأدوار",
    'roles.catalogue.note': "الأدوار مقدَّمة مع Kelassi ومتطابقة في جميع المؤسسات: لا يُعدَّل محتواها هنا. تختار المؤسسة من يملكها وعلى أي جزء منها، من خلال تبويب « الإسنادات ».",
    'roles.provided': "مقدَّم من Kelassi",
    'roles.holders': "أصحاب الدور: {count} · عرض الإسنادات",
    'roles.grantThis': "إسناد هذا الدور",
    'assignment.new': "إسناد دور",
    'assignment.edit': "تعديل الإسناد",
    'assignment.submit': "إسناد الدور",
    'assignment.search': "البحث عن حساب",
    'assignment.filter.role': "الدور",
    'assignment.filter.allRoles': "جميع الأدوار",
    'assignment.filter.center': "المركز",
    'assignment.filter.state': "الحالة",
    'assignment.filter.live': "السارية والقادمة",
    'assignment.filter.current': "سارية",
    'assignment.filter.upcoming': "قادمة",
    'assignment.filter.expired': "منتهية",
    'assignment.filter.revoked': "مسحوبة",
    'assignment.filter.all': "الكل، بما في ذلك السجل التاريخي",
    'assignment.filter.oneAccount': "تُعرض إسنادات حساب واحد فقط.",
    'assignment.filter.clear': "عرض الكل",
    'assignment.empty': "لا يوجد إسناد مطابق.",
    'assignment.column.account': "الحساب",
    'assignment.column.role': "الدور",
    'assignment.column.scope': "النطاق",
    'assignment.column.grantedBy': "أسنده",
    'assignment.state.current': "ساري",
    'assignment.state.upcoming': "قادم",
    'assignment.state.expired': "منتهٍ",
    'assignment.state.revoked': "مسحوب",
    'assignment.grantedBy.system': "التثبيت",
    'assignment.revoke': "سحب الدور",
    'assignment.revoke.title': "سحب الدور؟",
    'assignment.revoke.confirm': "يفقد {name} الدور {role} فورًا، بما في ذلك في الجلسات المفتوحة لهذا الحساب. يبقى الإسناد في السجل التاريخي ويمكن إعادة إسناده.",
    'assignment.regrant': "إعادة الإسناد",
    'assignment.done.revoked': "تم سحب الدور {role} من {name}.",
    'assignment.done.updated': "تم تعديل إسناد {name}.",
    'assignment.done.granted': "تم إسناد الدور {role} إلى {name}.",
    'assignment.scope.everything': "المؤسسة بأكملها",
    'assignment.self': "هذا إسنادك أنت: يجب أن يعدّله مسؤول آخر.",
    'assignment.you': "أنت",
    'assignment.period.revokedOn': "سُحب بتاريخ {date}",
    'assignment.period.between': "من {from} إلى {until}",
    'assignment.period.from': "ابتداءً من {date}",
    'assignment.period.until': "إلى غاية {date}",
    'permission.subject.user_account': "الحسابات",
    'permission.subject.role': "الأدوار",
    'permission.subject.role_assignment': "الإسنادات",
    'permission.subject.menu': "القائمة",
    'permission.subject.identity_provider': "الأدلة",
    'permission.subject.login_policy': "سياسة تسجيل الدخول",
    'permission.subject.authentication_log': "السجل",
    'assignment.step.account': "الحساب",
    'assignment.step.role': "الدور",
    'assignment.step.roleHint': "الأدوار مقدَّمة من Kelassi؛ لا يُعدَّل محتواها.",
    'assignment.step.scope': "النطاق",
    'assignment.step.scopeHint': "على أي جزء من المؤسسة يُمارَس الدور.",
    'assignment.step.period': "المدة",
    'assignment.step.periodHint': "اختيارية.",
    'assignment.account.change': "تغيير",
    'assignment.account.hint': "حسابك لا يظهر هنا: يجب أن يسند إليك مسؤول آخر الدور.",
    'assignment.account.search': "البحث بالاسم أو البريد الإلكتروني",
    'assignment.account.none': "لا يوجد حساب نشط مطابق.",
    'assignment.role.permissions': "الصلاحيات: {count}",
    'assignment.role.unreachable': "لا يمكنك إسناد هذا الدور: فهو يشمل جزءاً من المؤسسة خارج نطاقك.",
    'assignment.scope.rule': "لكل بُعد: الكل، أو جزء فقط. « الكل » يشمل أيضًا ما سيُنشأ لاحقًا. لا يُسمح باختيار « بعضها فقط » دون تحديد أي عنصر.",
    'assignment.scope.some': "بعضها فقط",
    'assignment.scope.emptySome': "حدّد عنصرًا واحدًا على الأقل، أو اختر « الكل ».",
    'assignment.scope.outOfReach': "خارج نطاقك",
    'assignment.scope.inactive': "مغلق",
    'assignment.scope.noneDefined': "لا شيء محدد حاليًا ({dimension}): سيسري الدور على كل ما سيُنشأ.",
    'assignment.scope.ownLimit': "نطاقك أنت لا يتجاوز هذا الحد: لا يمكنك منح المزيد.",
    'assignment.period.start': "البداية",
    'assignment.period.startHint': "فارغ: يسري الدور فور الحفظ.",
    'assignment.period.end': "النهاية",
    'assignment.period.endHint': "عند تحديد تاريخ: ينتهي الدور تلقائيًا في نهاية ذلك اليوم.",
    'assignment.summary.title': "باختصار",
    'assignment.summary.incomplete': "اختر حسابًا ودورًا.",
    'assignment.summary.gives': "ما يمنحه الدور {role}",
    'assignment.summary.everythingWarning': "أوسع نطاق: المؤسسة بأكملها، بما في ذلك المراكز والأسلاك والمجالات والمنتجات التي ستُنشأ لاحقًا.",
    'assignment.summary.manyPermissions': "ممارسة صلاحيات الدور {role}، وعددها {count}",
    'assignment.summary.everywhere': "على المؤسسة بأكملها",
    'assignment.summary.on': "على {parts}",
    'assignment.summary.always': "فور الحفظ ودون حد زمني",
    'assignment.summary.between': "من {from} إلى {until}",
    'assignment.summary.from': "ابتداءً من {date}، دون حد زمني",
    'assignment.summary.until': "فور الحفظ وإلى غاية {date}",
    'assignment.summary.sentence': "سيكون بإمكان {name} {permissions}، {where}، {when}.",
    'violation.periodInverted': "تاريخ النهاية يسبق تاريخ البداية.",
    'violation.assignmentSelf': "لا يمكن إسناد دور إلى نفسك.",
    'violation.assignmentExists': "هذا الحساب يملك هذا الدور بالفعل: عدّل إسناده من القائمة.",
    'common.and': "و",
    'assignment.role.alreadyHeld': "يملكه هذا الحساب بالفعل.",
    'assignment.role.editIt': "تعديل هذا الإسناد",
    'assignment.role.revokedBefore': "سُحب سابقًا: ستتم إعادة إسناده.",
    'rolesMenus.title': "الأدوار والقوائم",
    'rolesMenus.subtitle': "كل دور، والقائمة التي يراها. اسحب عنصرًا لنقله؛ وأفلته على دور لنسخه إليه.",
    'rolesMenus.saved': "تم حفظ التعديلات. تسري ابتداءً من الإجراء التالي لكل حساب معني.",
    'rolesMenus.copiedTo': "تم نسخ « {item} » إلى {role} (في انتظار الحفظ).",
    'rolesMenus.collapse': "طيّ",
    'rolesMenus.expand': "توسيع",
    'rolesMenus.hidden': "مخفي",
    'rolesMenus.local': "محلي",
    'rolesMenus.action.up': "إلى الأعلى",
    'rolesMenus.action.down': "إلى الأسفل",
    'rolesMenus.action.move': "نقل إلى…",
    'rolesMenus.action.copyTo': "نسخ إلى دور آخر…",
    'rolesMenus.action.edit': "تعديل",
    'rolesMenus.action.withdraw': "سحب من هذا الدور",
    'rolesMenus.action.delete': "حذف",
    'rolesMenus.action.copyRole': "نسخ هذا الدور",
    'rolesMenus.action.editRole': "تعديل الدور",
    'rolesMenus.action.deleteRole': "حذف الدور",
    'rolesMenus.filter': "تصفية العناصر",
    'rolesMenus.newSection': "قسم جديد",
    'rolesMenus.newRole': "دور جديد",
    'rolesMenus.tree': "الأدوار وقوائمها",
    'rolesMenus.allEntries': "القائمة الكاملة",
    'rolesMenus.reserved': "محجوز",
    'rolesMenus.copyOf': "نسخة من {role}",
    'rolesMenus.entries': "العناصر: {count}",
    'rolesMenus.roleMeta': "الصلاحيات: {permissions} · العناصر: {entries}",
    'rolesMenus.pendingRegion': "تعديلات في الانتظار",
    'rolesMenus.pending': "تعديلات غير محفوظة: {count}",
    'rolesMenus.pendingHint': "ستُطبَّق على جميع الحسابات التي تملك هذه الأدوار.",
    'rolesMenus.discard': "إلغاء الكل",
    'rolesMenus.labelIn': "التسمية · {language}",
    'rolesMenus.kind.section': "قسم من القائمة",
    'rolesMenus.kind.entry': "عنصر من القائمة",
    'rolesMenus.icon': "الأيقونة",
    'rolesMenus.route': "الشاشة (المسار)",
    'rolesMenus.routeHint': "يحدده كود المنتج: لا يمكن اختيار شاشة غير موجودة.",
    'rolesMenus.permission': "الصلاحية المطلوبة",
    'rolesMenus.permission.section': "لا شيء: يظهر بمجرد ظهور أحد العناصر التي تحته",
    'rolesMenus.permission.none': "لا شيء: يظهر لكل حساب مسجَّل الدخول",
    'rolesMenus.permissionNoneHint': "دون صلاحية، لا يرتبط العنصر بأي دور.",
    'rolesMenus.permissionHint': "يرى الدور هذا العنصر إذا كان يملك « {label} ».",
    'rolesMenus.visible': "ظاهر في القائمة",
    'rolesMenus.viewers': "الأدوار التي ترى هذا العنصر",
    'rolesMenus.viewersNone': "لا يوجد أي دور حاليًا.",
    'rolesMenus.provided': "مقدَّم من Kelassi",
    'rolesMenus.localRole': "أُنشئ هنا",
    'rolesMenus.holders': "أصحاب الدور: {count}",
    'rolesMenus.reservedHint': "لا يُعدَّل دور المشرف العام هنا: يُسند عبر سطر الأوامر، وهو وحده من يفتح هذه الشاشة.",
    'rolesMenus.description': "الوصف",
    'rolesMenus.permissions': "الصلاحيات ({count})",
    'rolesMenus.permissionsHint': "تفتح الصلاحية الشاشة والإجراءات المرتبطة بها معًا، وليس عنصر القائمة فقط.",
    'rolesMenus.copyRoleTitle': "نسخ الدور « {role} »",
    'rolesMenus.copyRoleSubtitle': "ينطلق الدور الجديد من هذا المحتوى؛ ثم تعدّله بعد ذلك.",
    'rolesMenus.createRole': "إنشاء الدور",
    'rolesMenus.code': "الرمز التقني",
    'rolesMenus.codeHint': "فريد ونهائي: هو ما يحتفظ به السجل التاريخي.",
    'rolesMenus.codeInvalid': "حروف لاتينية صغيرة وأرقام و _ ؛ من 3 إلى 60 حرفًا، تبدأ بحرف.",
    'rolesMenus.codeTaken': "هذا الرمز مستعمل بالفعل.",
    'rolesMenus.copyRoleNote': "يتلقى الدور الجديد صلاحيات الدور الأصلي، وعددها {count}، وبالتالي عناصر القائمة نفسها. لا يملكه أي حساب: يُسند بعد ذلك من « الأدوار والإسنادات ».",
    'rolesMenus.copyItemTitle': "نسخ « {item} » إلى…",
    'rolesMenus.copyItemSubmit': "نسخ إلى الأدوار المحددة ({count})",
    'rolesMenus.alreadyHas': "يملك هذا العنصر بالفعل",
    'rolesMenus.copyItemWarning': "تتلقى هذه الأدوار الصلاحية {permission}. ستظهر هذا العنصر لجميع الحسابات التي تملكها، وسيكون بإمكانها القيام بما تسمح به الصلاحية، في حدود نطاق إسنادها.",
    'rolesMenus.deleteItemTitle': "حذف « {item} »؟",
    'rolesMenus.seenFrom': "كما يراه الدور {role}",
    'rolesMenus.withdrawTitle': "السحب من هذا الدور فقط",
    'rolesMenus.withdrawText': "يفقد {role} الصلاحية {permission}، ومعها كل ما تفتحه. تحتفظ بها الأدوار الأخرى.",
    'rolesMenus.deleteTitle': "حذف عنصر القائمة",
    'rolesMenus.deleteText': "يختفي من قائمة جميع الأدوار. تبقى الشاشة وصلاحيتها؛ ولن يعيد التحديث التالي إنشاءه.",
    'rolesMenus.deleteLocalText': "يختفي القسم؛ ويرتفع محتواه مستوى واحدًا.",
    'rolesMenus.notYet': "لا يُطبَّق أي شيء قبل الضغط على « حفظ ».",
    'rolesMenus.moveTitle': "نقل « {item} »",
    'rolesMenus.moveTarget': "إلى",
    'rolesMenus.root': "المستوى الأول من القائمة",
    'rolesMenus.moveHint': "يُوضع العنصر في آخر القسم المختار؛ ثم يمكن ضبط موضعه بـ«إلى الأعلى» و«إلى الأسفل».",
    'rolesMenus.newSectionSubtitle': "يجمع القسم عدة عناصر؛ وإذا كان فارغًا فلن يظهر في قائمة أي مستخدم.",
    'rolesMenus.createSection': "إنشاء القسم",
    'rolesMenus.deleteRoleTitle': "حذف الدور «{role}»؟",
    'rolesMenus.deleteRoleBlocked': "عدد أصحاب هذا الدور: {count}. اسحبه منهم أولًا من «الأدوار والإسنادات».",
    'rolesMenus.deleteRoleText': "سيُحذف الدور. وإذا سبق إسناده في الماضي فسيرفض الخادم ذلك، لأن السجل التاريخي لا يزال يشير إليه.",
    'journal.title': "سجل الأمان",
    'journal.subtitle': "من سجّل الدخول، ومن أين، ومن غيّر ماذا. لا شيء هنا يمكن تعديله أو حذفه.",
    'journal.views': "طرق عرض السجل",
    'journal.tab.sign_ins': "عمليات تسجيل الدخول",
    'journal.tab.passwords': "كلمات المرور",
    'journal.tab.actions': "الإجراءات الإدارية",
    'journal.tab.actionsShort': "الإجراءات",
    'journal.export': "تصدير (CSV)",
    'journal.export.pending': "جارٍ التصدير…",
    'journal.export.truncated': "عدد الأسطر المطابقة: {total}. يتضمن الملف أحدث 10 000 سطر فقط. ضيّق الفترة للحصول على الباقي.",
    'journal.search': "الحساب أو العنوان المُدخل أو عنوان IP",
    'journal.search.actions': "المنفّذ أو الحساب المعني",
    'journal.period': "الفترة",
    'journal.period.day': "آخر 24 ساعة",
    'journal.period.days': "عدد الأيام الأخيرة: {days}",
    'journal.period.all': "كل السجل المحفوظ",
    'journal.filter.outcome': "النتيجة",
    'journal.filter.allOutcomes': "كل النتائج",
    'journal.filter.success': "الناجحة",
    'journal.filter.failure': "الفاشلة",
    'journal.filter.renewals': "عرض عمليات تجديد الجلسة",
    'journal.filter.action': "الإجراء",
    'journal.filter.allActions': "كل الإجراءات",
    'journal.filter.group.account': "الحسابات (الكل)",
    'journal.filter.group.account.photo': "الصور",
    'journal.filter.group.role': "الإسنادات (الكل)",
    'journal.filter.actor': "المنفّذ",
    'journal.filter.allActors': "كل المنفّذين",
    'journal.filter.oneAccount': "أحداث حساب واحد.",
    'journal.filter.oneTarget': "الإجراءات التي تخص حسابًا واحدًا.",
    'journal.filter.oneActor': "إجراءات منفّذ واحد.",
    'journal.filter.oneIp': "أحداث العنوان {ip}.",
    'journal.empty': "لا توجد أحداث تطابق هذه المعايير.",
    'journal.open': "عرض التفاصيل",
    'journal.column.date': "التاريخ",
    'journal.column.event': "الحدث",
    'journal.column.account': "الحساب",
    'journal.column.ip': "عنوان IP",
    'journal.column.device': "الجهاز",
    'journal.column.actor': "المنفّذ",
    'journal.column.action': "الإجراء",
    'journal.column.target': "المعني",
    'journal.column.change': "التغيير",
    'journal.today': "اليوم",
    'journal.yesterday': "أمس",
    'journal.noAccount': "لا يوجد حساب",
    'journal.typed': "المُدخل: {email}",
    'journal.vendor': "المزوّد",
    'journal.vendor.hidden': "تدخّل من المزوّد: لا تُبلَّغ المؤسسة بهويته ولا بعنوان IP الخاص به.",
    'journal.by': "بواسطة {name}",
    'journal.changes': "التغييرات: {count}",
    'journal.tile.successes': "عمليات دخول ناجحة · 24 ساعة",
    'journal.tile.accounts': "حسابات مختلفة: {count}",
    'journal.tile.failures': "محاولات فاشلة · 24 ساعة",
    'journal.tile.unknown': "منها على عنوان غير معروف: {count}",
    'journal.tile.locked': "الحسابات المقفلة",
    'journal.tile.lockedNone': "لا شيء حاليًا",
    'journal.tile.lockedUntil': "{name}، حتى {time}",
    'journal.tile.links': "روابط كلمة المرور · 7 أيام",
    'journal.tile.linksUsed': "المستخدمة: {count}",
    'journal.event.signIn': "تسجيل دخول ناجح",
    'journal.event.renewed': "تجديد الجلسة",
    'journal.event.locked': "قفل الحساب",
    'journal.event.reused': "إعادة استعمال رمز: قُطعت الجلسات",
    'journal.event.failed': "فشل: {reason}",
    'journal.event.methodFailed': "{method}: {reason}",
    'journal.event.reset.requested': "طلب رابط كلمة المرور",
    'journal.event.reset.sent': "إرسال رابط كلمة المرور",
    'journal.event.reset.completed': "تعيين كلمة المرور عبر رابط",
    'journal.event.assigned': "تعيين كلمة مرور مؤقتة",
    'journal.event.changed': "تغيير كلمة المرور",
    'journal.event.invitation': "قبول الدعوة",
    'journal.method.password': "تسجيل الدخول",
    'journal.method.totp': "رمز للاستعمال مرة واحدة",
    'journal.method.google': "تسجيل الدخول عبر Google",
    'journal.method.microsoft': "تسجيل الدخول عبر Microsoft",
    'journal.method.refresh_token': "رفض التجديد",
    'journal.method.password_change': "تغيير كلمة المرور",
    'journal.method.password_assigned': "كلمة مرور مؤقتة",
    'journal.method.password_reset': "رابط كلمة المرور",
    'journal.method.invitation': "دعوة",
    'journal.outcome.success': "ناجح",
    'journal.outcome.bad_credentials': "كلمة مرور غير صحيحة",
    'journal.outcome.unknown_account': "لا يوجد حساب",
    'journal.outcome.account_disabled': "حساب معطّل",
    'journal.outcome.account_locked': "حساب مقفل",
    'journal.outcome.policy_denied': "مرفوض بموجب سياسة تسجيل الدخول",
    'journal.outcome.provider_rejected': "مرفوض من مزوّد الهوية",
    'journal.outcome.token_reused': "رمز أُعيد استعماله",
    'journal.outcome.provisioning_refused': "رفض إنشاء الحساب",
    'journal.outcome.token_invalid': "رابط غير صالح أو منتهي الصلاحية",
    'journal.outcome.rate_limited': "محاولات كثيرة جدًا",
    'journal.action.account.created': "إنشاء حساب",
    'journal.action.account.updated': "تعديل حساب",
    'journal.action.account.deactivated': "تعطيل حساب",
    'journal.action.account.activated': "إعادة تفعيل حساب",
    'journal.action.account.password_assigned': "تعيين كلمة مرور مؤقتة",
    'journal.action.account.link_sent': "إرسال رابط",
    'journal.action.account.photo_changed': "تغيير الصورة",
    'journal.action.account.photo_removed': "حذف الصورة",
    'journal.action.role.granted': "إسناد دور",
    'journal.action.role.assignment_updated': "تعديل إسناد",
    'journal.action.role.revoked': "سحب دور",
    'journal.action.roles_menus.changed': "تعديل الأدوار والقوائم",
    'journal.field.displayName': "الاسم",
    'journal.field.email': "البريد الإلكتروني",
    'journal.field.population': "الفئة",
    'journal.field.credential': "كلمة المرور",
    'journal.field.sessionsClosed': "الجلسات المغلقة",
    'journal.field.lock': "القفل",
    'journal.field.role': "الدور",
    'journal.field.centers': "المراكز",
    'journal.field.cycles': "الأسلاك",
    'journal.field.domains': "المجالات",
    'journal.field.products': "المنتجات",
    'journal.field.startsAt': "البداية",
    'journal.field.endsAt': "النهاية",
    'journal.value.all': "الكل",
    'journal.value.open': "بلا حد",
    'journal.value.link': "رابط التفعيل",
    'journal.value.password': "مؤقتة، أُدخلت يدويًا",
    'journal.value.lifted': "رُفع",
    'journal.diff.field': "الحقل",
    'journal.diff.before': "قبل",
    'journal.diff.after': "بعد",
    'journal.detail.reason': "السبب",
    'journal.detail.lockedUntil': "مقفل حتى",
    'journal.detail.lockedReason': "عدد كبير من كلمات المرور غير الصحيحة: الحساب مقفل مؤقتًا.",
    'journal.detail.by': "بواسطة",
    'journal.detail.sessions': "الجلسات المغلقة",
    'journal.detail.actorIp': "عنوان IP للمنفّذ",
    'journal.detail.further': "للمزيد",
    'journal.detail.allForAccount': "كل أحداث هذا الحساب",
    'journal.detail.allForIp': "كل أحداث عنوان IP هذا",
    'journal.detail.openAccount': "فتح بطاقة الحساب",
    'journal.detail.allByActor': "كل ما قام به {name}",
    'journal.detail.allOnTarget': "كل ما خصّ هذا الحساب",
    'journal.detail.id': "معرّف الحدث، يُذكر عند التواصل مع الدعم:",
    'directories.title': "الأدلة وتسجيل الدخول",
    'directories.subtitle': "من أين تسجّل حساباتكم الدخول، والقواعد المطبّقة على كل فئة.",
    'directories.section.directories': "الأدلة",
    'directories.section.directoriesHint': "يتيح الدليل الخارجي تسجيل الدخول بحساب Google أو Microsoft التابع للمؤسسة.",
    'directories.section.policy': "سياسة تسجيل الدخول",
    'directories.section.policyHint': "سطر واحد لكل فئة. تُطبَّق عند تسجيل الدخول التالي أو عند التجديد التالي للجلسة.",
    'directories.passwordRule': "لا يمكن سحب كلمة مرور Kelassi من فئة إلا إذا كان دليل يخدمها، وإلا لن يتمكن أحد من هذه الفئة من تسجيل الدخول. ويحتفظ بها مسؤولو تسجيل الدخول في جميع الحالات.",
    'directories.type.native': "كلمة مرور Kelassi",
    'directories.type.google': "Google Workspace",
    'directories.type.microsoft': "Microsoft 365",
    'directories.state.active': "نشط",
    'directories.state.disabled': "معطّل",
    'directories.state.unconfigured': "غير مُعَدّ",
    'directories.native.text': "متاح دائمًا لكل الحسابات. وهو أيضًا باب النجدة إذا توقف دليل خارجي عن الاستجابة: يحتفظ به المسؤولون دائمًا.",
    'directories.google.text': "تسجيل الدخول بحساب Google التابع للمؤسسة. يجب أن تكون حسابات Kelassi موجودة مسبقًا: الدليل لا ينشئ حسابات.",
    'directories.microsoft.text': "تسجيل الدخول بحساب Microsoft 365 التابع للمؤسسة. القاعدة نفسها: يُرفض الحساب غير المعروف لدى Kelassi ولا يُنشأ أبدًا.",
    'directories.external.serves': "يخدم: {populations}. العناوين: {domains}.",
    'directories.byVendor': "شغّلته LENSOM CORP بطلب من المؤسسة",
    'directories.details': "عرض التفاصيل",
    'directories.stat.withPassword': "حسابات لها كلمة مرور",
    'directories.stat.linked': "الحسابات المرتبطة",
    'directories.stat.signIns': "عمليات الدخول · 7 أيام",
    'directories.stat.neverUsed': "لم تسجّل الدخول عبر الدليل قط",
    'directories.detail.tenant': "المستأجر (tenant)",
    'directories.detail.issuer': "الجهة المُصدِرة المتحقَّق منها في كل رمز",
    'directories.detail.client': "التطبيق",
    'directories.detail.secret': "سرّ التطبيق",
    'directories.detail.secretStored': "محفوظ، ولا يُعرض أبدًا",
    'directories.detail.secretMissing': "غير موجود: لا يمكن للدليل أن يعمل",
    'directories.detail.domains': "النطاقات المقبولة",
    'directories.detail.populations': "الفئات المخدومة",
    'directories.detail.unknown': "حساب غير معروف لدى Kelassi",
    'directories.detail.refused': "يُرفض، ولا يُنشأ تلقائيًا أبدًا",
    'directories.detail.autoProvision': "يُنشأ تلقائيًا",
    'directories.detail.log': "عمليات الدخول في سجل الأمان",
    'directories.column.population': "الفئة",
    'directories.column.accounts': "الحسابات",
    'directories.column.password': "كلمة المرور",
    'directories.column.directory': "الدليل",
    'directories.column.totp': "المصادقة الثنائية",
    'directories.column.idle': "عدم النشاط",
    'directories.password.allowed': "مسموح",
    'directories.password.removed': "مُلغى",
    'directories.password.short': "كلمة المرور",
    'directories.none': "لا شيء",
    'directories.idle.minutes': "{count} د",
    'directories.idle.hours': "{count} س",
    'directories.idleShort': "عدم النشاط: {idle}",
    'directories.policy.edit': "تعديل السياسة: {population}",
    'directories.policy.title': "سياسة تسجيل الدخول · {population}",
    'directories.policy.accounts': "الحسابات المعنية: {count}",
    'directories.policy.password': "تسجيل الدخول بكلمة مرور Kelassi",
    'directories.policy.passwordLocked': "لا يخدم أي دليل هذه الفئة: كلمة المرور هي وسيلتها الوحيدة لتسجيل الدخول، ولا يمكن سحبها.",
    'directories.policy.passwordHint': "تخدمها أيضًا الأدلة التالية: {directories}. بدون كلمة مرور، لا تسجّل هذه الحسابات الدخول إلا عبر الدليل؛ ويحتفظ بها مسؤولو تسجيل الدخول.",
    'directories.policy.totp': "المصادقة الثنائية (رمز يُستعمل مرة واحدة)",
    'directories.policy.idle': "تسجيل الخروج بعد عدم النشاط",
    'directories.policy.idleHint': "إذا لم يُجرَ أي إجراء خلال هذه المدة، تُغلق الجلسة ويلزم تسجيل الدخول من جديد. اختر مدة أقصر على الأجهزة المشتركة.",
    'directories.policy.journal': "يُقيَّد هذا التغيير في سجل الأمان.",
    'directories.policy.saved': "تم حفظ سياسة تسجيل الدخول: {population}.",
    'violation.policyIdle': "اختر مدة من القائمة.",
    'violation.policyPassword': "لا يخدم أي دليل هذه الفئة: لا يمكن سحب كلمة المرور.",
    'journal.action.login_policy.changed': "تعديل سياسة تسجيل الدخول",
    'journal.field.passwordAllowed': "كلمة مرور Kelassi",
    'journal.field.sessionIdleMinutes': "عدم النشاط",
    'journal.value.allowed': "مسموح",
    'journal.value.removed': "مُلغى",
    'journal.filter.group.login_policy': "سياسة تسجيل الدخول",
    'directories.totp.none': "غير مطلوبة",
    'directories.totp.email_or_app': "البريد الإلكتروني أو التطبيق",
    'directories.totp.app': "التطبيق فقط",
    'directories.recommended': "موصى به",
    'directories.policy.mode.none': "غير مطلوبة",
    'directories.policy.mode.none.hint': "يمكن لكل مستخدم تفعيلها لنفسه من ملفه الشخصي.",
    'directories.policy.mode.email_or_app': "مطلوبة: البريد الإلكتروني أو التطبيق",
    'directories.policy.mode.email_or_app.hint': "رمز عبر البريد الإلكتروني عند كل تسجيل دخول؛ ومن فعّل تطبيقًا يستعمله بدلًا منه.",
    'directories.policy.mode.app': "مطلوبة: التطبيق فقط",
    'directories.policy.mode.app.hint': "أكثر أمانًا. سيتعين على كل حساب تفعيل تطبيق عند تسجيل دخوله التالي (بعد رمز أخير عبر البريد الإلكتروني).",
    'twoFactor.progress': "مراحل تسجيل الدخول",
    'twoFactor.step.password': "كلمة المرور",
    'twoFactor.step.code': "الرمز",
    'twoFactor.code': "رمز من 6 أرقام",
    'twoFactor.email.title': "تحقّق من هويتك",
    'twoFactor.email.body': "أرسلنا رمزًا من 6 أرقام إلى {email}. وهو صالح لمدة 10 دقائق.",
    'twoFactor.email.notSent': "استلم رمزًا من 6 أرقام عبر البريد الإلكتروني لإتمام تسجيل الدخول.",
    'twoFactor.email.send': "استلام رمز عبر البريد الإلكتروني",
    'twoFactor.app.title': "الرمز من تطبيقك",
    'twoFactor.app.body': "افتح Google Authenticator أو Microsoft Authenticator وأدخل الرمز المعروض لـ Kelassi.",
    'twoFactor.recovery.title': "رمز الاسترداد",
    'twoFactor.recovery.body': "أدخل أحد رموز الاسترداد التي دوّنتها عند تفعيل تطبيقك. كل رمز يُستعمل مرة واحدة فقط.",
    'twoFactor.recovery.label': "رمز الاسترداد",
    'twoFactor.mustEnroll': "تشترط مؤسستك تطبيق مصادقة: ستفعّله مباشرة بعد هذا الرمز.",
    'twoFactor.submit': "تأكيد",
    'twoFactor.resendIn': "لم يصلك؟ تحقّق من البريد غير المرغوب فيه، أو أعد الإرسال بعد {time}",
    'twoFactor.resend': "إعادة إرسال رمز",
    'twoFactor.sent': "تم إرسال رمز جديد إلى {email}.",
    'twoFactor.useApp': "استعمال تطبيق المصادقة بدلًا من ذلك",
    'twoFactor.useEmail': "استلام رمز عبر البريد الإلكتروني بدلًا من ذلك",
    'twoFactor.useRecovery': "استعمال رمز الاسترداد",
    'twoFactor.back': "العودة إلى تسجيل الدخول",
    'twoFactor.invalid': "الرمز غير صحيح أو منتهي الصلاحية.",
    'twoFactor.invalidLeft': "الرمز غير صحيح أو منتهي الصلاحية. المحاولات المتبقية: {count}.",
    'twoFactor.expired': "انتهت صلاحية التحقق أو فشل مرات كثيرة. سجّل الدخول من جديد.",
    'twoFactor.tooSoon': "انتظر قليلًا قبل طلب رمز جديد.",
    'twoFactor.tooMany': "أُرسلت رموز كثيرة: أعد تسجيل الدخول من البداية.",
    'twoFactor.policy.none': "لا تشترطها مؤسستك لفئتك: لكنها تحمي حسابك إذا تسرّبت كلمة مرورك.",
    'twoFactor.policy.email_or_app': "تشترطها مؤسستك: رمز عبر البريد الإلكتروني عند كل تسجيل دخول، أو تطبيقك إن فعّلت واحدًا.",
    'twoFactor.policy.app': "تشترطها مؤسستك: تطبيق مصادقة.",
    'twoFactor.profile.title': "المصادقة الثنائية",
    'twoFactor.profile.on': "مفعّلة",
    'twoFactor.profile.offShort': "غير مفعّلة",
    'twoFactor.profile.app': "تم تفعيل تطبيق المصادقة بتاريخ {date}.",
    'twoFactor.profile.email': "رمز عبر البريد الإلكتروني عند كل تسجيل دخول.",
    'twoFactor.profile.mustEnroll': "فعّل تطبيق مصادقة.",
    'twoFactor.profile.off': "كلمة المرور وحدها كافية حاليًا.",
    'twoFactor.profile.enable': "تفعيل تطبيق مصادقة",
    'twoFactor.profile.codesLeft': "رموز الاسترداد المتبقية: {count}",
    'twoFactor.profile.renew': "تجديد رموز الاسترداد",
    'twoFactor.profile.remove': "إزالة التطبيق",
    'twoFactor.profile.removed': "تمت إزالة تطبيق المصادقة.",
    'twoFactor.profile.confirmRemove': "أدخل رمزًا من تطبيقك لإزالته.",
    'twoFactor.profile.confirmRenew': "أدخل رمزًا من تطبيقك: ستتوقف رموز الاسترداد القديمة عن العمل.",
    'twoFactor.profile.emailToggle': "استلام رمز عبر البريد الإلكتروني عند كل تسجيل دخول",
    'twoFactor.profile.emailToggleHint': "بدون تطبيق: يُرسل رمز إلى عنوانك بعد كلمة المرور.",
    'twoFactor.setup.install': "ثبّت التطبيق",
    'twoFactor.setup.installHint': "Google Authenticator أو Microsoft Authenticator، على هاتفك.",
    'twoFactor.setup.scan': "امسح هذا الرمز",
    'twoFactor.setup.qrAlt': "رمز QR يُمسح بالتطبيق",
    'twoFactor.setup.key': "تعذّر المسح؟ أدخل هذا المفتاح:",
    'twoFactor.setup.copy': "نسخ المفتاح",
    'twoFactor.setup.code': "أدخل الرمز المعروض",
    'twoFactor.setup.activate': "تفعيل التطبيق",
    'twoFactor.codes.title': "رموز الاسترداد الخاصة بك",
    'twoFactor.codes.body': "تُعرض مرة واحدة فقط. كل رمز يحل محل رمز التطبيق إذا فقدت هاتفك، ولا يُستعمل إلا مرة واحدة.",
    'twoFactor.codes.download': "تنزيل (.txt)",
    'twoFactor.codes.copy': "نسخ",
    'twoFactor.codes.warning': "احفظها بعيدًا عن هاتفك. وكحل أخير، يمكن للمسؤول إعادة تعيين مصادقتك الثنائية.",
    'twoFactor.codes.done': "دوّنت رموزي",
    'twoFactor.codes.fileHeader': "Kelassi: رموز الاسترداد للمصادقة الثنائية. كل رمز يُستعمل مرة واحدة فقط.",
    'twoFactor.forced.title': "فعّل تطبيق المصادقة",
    'twoFactor.forced.body': "تشترطه مؤسستك لفئتك. لن يستغرق الأمر سوى دقيقتين، ولن تُرسل إليك بعد ذلك رموز عبر البريد الإلكتروني.",
    'account.twoFactor': "المصادقة الثنائية",
    'account.twoFactor.app': "تطبيق المصادقة",
    'account.twoFactor.none': "بدون تطبيق",
    'account.action.resetTwoFactor': "إعادة تعيين المصادقة الثنائية",
    'account.action.resetTwoFactor.short': "إعادة تعيين",
    'account.action.resetTwoFactor.confirm': "سيُزال تطبيق المصادقة ورموز الاسترداد الخاصة بـ {name}. وحسب السياسة المطبقة، سيستلم رمزًا عبر البريد الإلكتروني ثم سيتعين عليه تفعيل تطبيق من جديد.",
    'account.done.twoFactorReset': "تمت إعادة تعيين المصادقة الثنائية.",
    'journal.method.second_factor': "رمز التحقق",
    'journal.event.challenged': "كلمة المرور صحيحة، وطُلب الرمز",
    'journal.event.codeFailed': "فشل: رمز التحقق غير صحيح",
    'journal.action.account.two_factor_app_enabled': "تفعيل تطبيق المصادقة",
    'journal.action.account.two_factor_app_disabled': "إزالة تطبيق المصادقة",
    'journal.action.account.two_factor_email': "اختيار الرمز عبر البريد الإلكتروني",
    'journal.action.account.recovery_codes_renewed': "تجديد رموز الاسترداد",
    'journal.action.account.two_factor_reset': "إعادة تعيين المصادقة الثنائية",
    'journal.field.twoFactor': "المصادقة الثنائية",
    'journal.field.email2fa': "الرمز عبر البريد الإلكتروني",
    'journal.value.on': "مفعّل",
    'journal.value.off': "معطّل",
    'violation.twoFactorInvalid': "الرمز غير صحيح أو منتهي الصلاحية.",
    'home.title': "الرئيسية",
    'home.hello': "مرحبًا {name}",
    'home.soon': "ستغتني مساحتك تدريجيًا مع تشغيل الخدمات: استعمال الزمن، النقط، الوثائق. حاليًا، يمكنك إدارة ملفك الشخصي وأمان حسابك.",
    'home.tile.accounts': "الحسابات النشطة",
    'home.tile.recent': "+{count} هذا الأسبوع",
    'home.todo': "للقيام به",
    'home.todo.dismiss': "عدم الإظهار مجددًا",
    'home.todo.locked': "حسابات مقفلة بعد محاولات تسجيل دخول فاشلة: {count}",
    'home.todo.locked.action': "عرض",
    'home.todo.provisional': "حسابات لم تغيّر كلمة مرورها المؤقتة بعد: {count}",
    'home.todo.provisional.action': "عرض القائمة",
    'home.todo.links': "روابط تفعيل تنتهي صلاحيتها خلال أقل من 24 س: {count}",
    'home.todo.links.action': "عرض الحسابات",
    'home.todo.two_factor': "المصادقة الثنائية غير مطلوبة لأي فئة",
    'home.todo.two_factor.action': "ضبط السياسة",
    'home.activity': "النشاط الأخير",
    'home.activity.empty': "لا توجد إجراءات إدارية خلال آخر 30 يومًا.",
    'home.activity.all': "سجل الأمان كاملًا",
    'home.shortcuts': "اختصارات",
    'home.myAccount': "حسابي",
    'home.shortcut.createAccount': "إنشاء حساب",
    'home.shortcut.createAccount.hint': "رابط تفعيل أو كلمة مرور مؤقتة",
    'home.shortcut.assign': "إسناد دور",
    'home.shortcut.assign.hint': "على المؤسسة كلها أو على جزء منها",
    'home.shortcut.journal': "سجل الأمان",
    'home.shortcut.journal.hint': "عمليات تسجيل الدخول والإجراءات",
    'home.shortcut.directories': "الأدلة وتسجيل الدخول",
    'home.shortcut.directories.hint': "سياسة لكل فئة",
    'home.shortcut.profile': "ملفي الشخصي",
    'home.shortcut.profile.hint': "الصورة، المعلومات، المصادقة الثنائية",
    'home.shortcut.password': "تغيير كلمة المرور",
    'home.shortcut.password.hint': "ستُغلق جميع جلساتك",
    'home.security': "أمان حسابي",
    'home.security.previous': "تسجيل الدخول السابق",
    'home.security.firstTime': "أول تسجيل دخول",
    'home.security.passwordSet': "غُيّرت بتاريخ {date}",
    'home.security.twoFactor.app': "تطبيق المصادقة",
    'home.security.twoFactor.email': "الرمز عبر البريد الإلكتروني",
    'home.security.twoFactor.none': "غير مفعّلة · موصى بها",
    'home.security.manage': "إدارة أمان حسابي",
    'home.security.enable': "تفعيل المصادقة الثنائية",
    'signIn.refusedLong': "البريد الإلكتروني أو كلمة المرور غير صحيحة. بعد 10 محاولات فاشلة، يُقفل الحساب لمدة 15 دقيقة.",
    'signIn.unreachableLong': "الخدمة لا تستجيب حاليًا. أعد المحاولة بعد قليل؛ بياناتك لم تضِع.",
    'session.expiredLong': "انتهت جلستك بعد فترة من عدم النشاط. سجّل الدخول من جديد للمتابعة.",
    'auth.subtitle': "فضاؤك الرقمي",
    'auth.tagline': "التمدرس والنقط والإدارة: المؤسسة كلها في مكان واحد.",
    'auth.vendor': "Kelassi · حل من LENSOM CORP",
    'auth.never': "لن يطلب منك Kelassi أبدًا كلمة مرورك عبر البريد الإلكتروني.",
    'auth.showPassword': "إظهار كلمة المرور",
    'auth.hidePassword': "إخفاء كلمة المرور",
    'auth.rule.length': "12 حرفًا على الأقل (المُدخَل: {count})",
    'auth.rule.sentence': "الجملة أكثر أمانًا من كلمة معقّدة",
    'forgot.sentTitle': "تحقّق من صندوق الوارد",
    'forgot.sentBody': "إذا كان هناك حساب مرتبط بـ {email}، فقد أُرسل إليه رابط للتو. يبقى صالحًا لمدة ساعة ولا يُستعمل إلا مرة واحدة.",
    'forgot.tip.spam': "لا تنسَ التحقق من البريد غير المرغوب فيه.",
    'forgot.tip.last': "آخر رابط مستلَم هو وحده الذي يعمل.",
    'forgot.tip.sessions': "اختيار كلمة مرور جديدة يُغلق جميع جلساتك.",
    'forgot.again': "إعادة إرسال رابط",
    'forgot.noAccess': "لم تعد تستطيع الوصول إلى هذا العنوان؟ توجّه إلى إدارة المؤسسة: يمكنها أن ترسل إليك رابطًا أو أن تحدد كلمة مرور مؤقتة.",
    'reset.deadTitle': "هذا الرابط لم يعد يعمل",
    'reset.body': "ستحل محل كلمة مرورك الحالية، إن وُجدت.",
    'reset.sessions': "ستُغلق جميع جلساتك المفتوحة. ثم ستسجّل الدخول من جديد بكلمة المرور هذه.",
    'common.actions': "إجراءات",
    'common.add': "إضافة",
    'common.delete': "حذف",
    'common.edit': "تعديل",
    'ref.nav': "إعدادات المرجع البيداغوجي",
    'ref.intro': "ما يهيكل عرض المؤسسة، قبل التكوينات نفسها.",
    'ref.rulesNote': "قواعد الحساب (التعويض، معدل السلك) تقدمها Kelassi: تُختار لكل مجال ولا تُكتب. قواعد ESCG مأخوذة كما هي في انتظار المصادقة عليها.",
    'ref.readOnly': "قراءة فقط: تُعدَّل الإعدادات بصلاحية «إعداد المرجع البيداغوجي».",
    'ref.section.years': "السنوات الجامعية",
    'ref.section.domains': "الأسلاك والمجالات",
    'ref.section.structure': "هيكلة التكوينات",
    'ref.section.levels': "المستويات والشهادات",
    'ref.section.mentions': "الميزات والحروف",
    'ref.section.pedagogy': "البيداغوجيا",
    'ref.saved.created': "تم إنشاء «{label}».",
    'ref.saved.updated': "تم حفظ «{label}».",
    'ref.saved.deleted': "تم حذف «{label}».",
    'ref.common.code': "الرمز",
    'ref.common.name': "الاسم",
    'ref.common.label': "التسمية",
    'ref.common.none': "لا شيء",
    'ref.common.choose': "اختر…",
    'ref.common.unused': "لا يستعمله أي مجال",
    'ref.common.usedBy': "يستعمله: {list}",
    'ref.years.sub': "الجدولة الزمنية للمؤسسة، من دخول جامعي إلى آخر.",
    'ref.years.prepare': "إعداد سنة",
    'ref.years.prepareHint': "تُفتح للتسجيل حين تقرر ذلك؛ وتستمر السنة الجارية إلى ذلك الحين.",
    'ref.years.edit': "تعديل السنة {label}",
    'ref.years.labelHint': "الصيغة: 2026-2027.",
    'ref.years.empty': "لا توجد أي سنة جامعية. أعدّ الأولى.",
    'ref.years.neverClosed': "لم تُغلق أي سنة من قبل. السنة المغلقة لا تقبل تسجيلات ولا نقطًا؛ وتبقى المداولات المنشورة قابلة للاطلاع.",
    'ref.years.column.year': "السنة",
    'ref.years.column.start': "البداية",
    'ref.years.column.end': "النهاية",
    'ref.years.column.state': "الحالة",
    'ref.years.state.current': "السنة الجارية",
    'ref.years.state.preparing': "قيد الإعداد",
    'ref.years.state.open': "مفتوحة",
    'ref.years.state.closed': "مغلقة",
    'ref.years.open': "فتح التسجيلات",
    'ref.years.makeCurrent': "جعلها جارية",
    'ref.years.close': "إغلاق…",
    'ref.years.readOnly': "قراءة فقط",
    'ref.years.closeTitle': "إغلاق السنة {label}؟",
    'ref.years.closeText': "هذا نهائي: لن تقبل السنة بعد الآن تسجيلات ولا نقطًا ولا مداولات. يبقى ما نُشر قابلًا للاطلاع. ويُسجَّل الإجراء في السجل.",
    'ref.years.done.open': "السنة {label} مفتوحة.",
    'ref.years.done.set_current': "أصبحت {label} السنة الجارية.",
    'ref.years.done.delete': "تم حذف السنة {label}.",
    'ref.years.done.close': "تم إغلاق السنة {label}.",
    'ref.years.fact.current': "سنة جارية واحدة",
    'ref.years.fact.currentText': "هي التي تقترحها الشاشات افتراضيًا. ويمكن إعداد السنة الموالية مسبقًا.",
    'ref.years.fact.centers': "مشتركة بين كل المراكز",
    'ref.years.fact.centersText': "الجدولة الخاصة بمركز ستأتي مع الدورات.",
    'ref.years.fact.closing': "إغلاق نهائي",
    'ref.years.fact.closingText': "خاص بمن يملك الصلاحية، مسجَّل في السجل، ويسبقه تأكيد.",
    'ref.domains.sub': "للسلك مدة ويمنح شهادة؛ والمجال يحمل قواعد عائلة من التكوينات.",
    'ref.cycles.add': "إضافة سلك",
    'ref.cycles.edit': "تعديل السلك",
    'ref.cycles.empty': "لا يوجد أي سلك. أضف واحدًا لترتيب المجالات فيه.",
    'ref.cycles.duration': "المدة بالسنوات: {count}",
    'ref.cycles.durationLabel': "المدة (بالسنوات)",
    'ref.cycles.diploma': "الشهادة الممنوحة:",
    'ref.cycles.diplomaMissing': "يجب تحديدها",
    'ref.cycles.diplomaLabel': "الشهادة الممنوحة",
    'ref.domains.add': "إضافة مجال",
    'ref.domains.edit': "تعديل المجال",
    'ref.domains.editTitle': "المجال {code}",
    'ref.domains.dialogHint': "تسري القواعد على كل تكوينات المجال؛ ويمكن لتكوين أن يختار تعويضًا آخر.",
    'ref.domains.noCycle': "مجالات بلا سلك",
    'ref.domains.cycle': "السلك",
    'ref.domains.notation': "التنقيط",
    'ref.domains.notationHint': "الاثنان معًا: يقبل الإدخال 14,25 كما يقبل B+، ويعرض كشف النقط الاثنين.",
    'ref.domains.letterScale': "سلّم الحروف",
    'ref.domains.mentions': "سلّم الميزات",
    'ref.domains.noMentions': "بلا سلّم",
    'ref.domains.cycleAverage': "معدل السلك",
    'ref.domains.vendorRule': "قاعدة تقدمها Kelassi.",
    'ref.domains.weighting': "ترجيح المعدلات",
    'ref.domains.weightingHint': "بالمعاملات كما هو الحال اليوم، أو بالأرصدة.",
    'ref.domains.compensation': "التعويض الافتراضي",
    'ref.domains.compensationHint': "يمكن لكل تكوين أن يختار تعويضًا آخر.",
    'ref.domains.retainedSession': "النقطة المعتمدة",
    'ref.domains.retainedSessionHint': "حين يعيد الطالب امتحانًا.",
    'ref.domains.creditsEarned': "اكتساب الأرصدة",
    'ref.domains.structure': "الهيكلة",
    'ref.domains.noStructure': "نموذج يجب تحديده",
    'ref.domains.marking': "التنقيط والسلالم",
    'ref.domains.calculation': "الحساب",
    'ref.notation.numeric': "نقط على 20",
    'ref.notation.letters': "حروف",
    'ref.notation.both': "نقط على 20 وحروف",
    'ref.weighting.coefficients': "بالمعاملات",
    'ref.weighting.credits': "بالأرصدة",
    'ref.retainedSession.last': "آخر نقطة",
    'ref.retainedSession.best': "أفضل نقطة",
    'ref.creditsEarned.validated_or_compensated': "إذا صودق عليه أو عُوِّض",
    'ref.creditsEarned.validated_only': "إذا صودق عليه فقط",
    'ref.structure.sub': "النموذج الذي تتبعه كل تكوينات المجال.",
    'ref.structure.noDomain': "أنشئ مجالًا أولًا في «الأسلاك والمجالات».",
    'ref.structure.domains': "المجالات",
    'ref.structure.template': "نموذج الهيكلة",
    'ref.structure.version': "النسخة",
    'ref.structure.since': "ساري المفعول منذ {year}",
    'ref.structure.none': "لا يوجد نموذج بعد",
    'ref.structure.newVersion': "نسخة جديدة…",
    'ref.structure.newVersionHint': "تُنسخ النسخة الحالية؛ وتسري ابتداءً من السنة المختارة.",
    'ref.structure.createVersion': "إنشاء النسخة",
    'ref.structure.validFrom': "ابتداءً من السنة",
    'ref.structure.create': "إنشاء النموذج ({year})",
    'ref.structure.saved': "تم حفظ نموذج المجال {code}.",
    'ref.structure.levelName': "اسم المستوى «{kind}»",
    'ref.structure.removeLevel': "سحب هذا المستوى",
    'ref.structure.addLevel': "إضافة مستوى…",
    'ref.structure.vendorNotions': "المفاهيم تقدمها Kelassi؛ وأسماؤها لكم.",
    'ref.structure.versioning': "يتبع كل تكوين يُنشأ في هذا المجال هذا النموذج. تغييره ينشئ نسخة جديدة تسري ابتداءً من سنة معينة: تحتفظ تكوينات السنوات الماضية بنسختها، ولا تتغير معدلاتها.",
    'ref.structure.flag.classGroups': "مجموعات القسم",
    'ref.structure.flag.transcript': "كشف النقط",
    'ref.structure.flag.taught': "مُدرَّسة",
    'ref.structure.creditsHere': "الأرصدة هنا",
    'ref.structure.creditsEntered': "تُدخل الأرصدة هنا",
    'ref.structure.creditsSum': "مجموع الأرصدة",
    'ref.structure.creditsBelow': "بلا أرصدة خاصة",
    'ref.kind.year': "السنة",
    'ref.kind.semester': "الفصل",
    'ref.kind.module': "الوحدة",
    'ref.kind.submodule': "الوحدة الفرعية",
    'ref.kind.subject': "المادة",
    'ref.kind.year.hint': "تحمل التسجيلات والأقسام",
    'ref.kind.semester.hint': "يظهر في كشف النقط",
    'ref.kind.module.hint': "يُصادق عليها أو تُعوَّض",
    'ref.kind.submodule.hint': "اختيارية: غائبة عن بعض الوحدات",
    'ref.kind.subject.hint': "تتلقى النقط والساعات",
    'ref.levels.sub': "ما يُحضَّر، وفي أي مستوى، وما يُمنح.",
    'ref.levels.title': "المستويات",
    'ref.levels.hint': "لائحة حرة؛ والسلك اختياري.",
    'ref.levels.empty': "لا يوجد أي مستوى.",
    'ref.levels.add': "إضافة مستوى",
    'ref.levels.edit': "المستوى {code}",
    'ref.levels.cycleHint': "اختياري.",
    'ref.diplomas.title': "الشهادات الممنوحة",
    'ref.diplomas.hint': "ما تمنحه المؤسسة وتخصصاته. شهادات الولوج تصف الطالب لا العرض.",
    'ref.diplomas.empty': "لا توجد أي شهادة ممنوحة.",
    'ref.diplomas.access': "شهادات الولوج",
    'ref.diplomas.add': "إضافة شهادة ممنوحة",
    'ref.diplomas.addAccess': "إضافة شهادة ولوج",
    'ref.diplomas.edit': "الشهادة {name}",
    'ref.diplomas.grade': "المستوى",
    'ref.diplomas.specialities': "التخصصات",
    'ref.diplomas.series': "الشعب",
    'ref.diplomas.optionsHint': "واحد في كل سطر. ما يبقى يحتفظ بمعرّفه.",
    'ref.diplomas.noSpeciality': "لا يوجد تخصص",
    'ref.diplomas.noSeries': "لا توجد شعبة",
    'ref.grade.bac': "البكالوريا",
    'ref.grade.bac_plus_2': "باك + 2",
    'ref.grade.bac_plus_3': "باك + 3",
    'ref.grade.bac_plus_5': "باك + 5",
    'ref.grade.doctorate': "الدكتوراه",
    'ref.mentions.sub': "السلالم التي تمنح ميزة وحرفًا لنقطة أو لمعدل.",
    'ref.mentions.add': "سلّم ميزات جديد",
    'ref.mentions.edit': "السلّم {name}",
    'ref.mentions.empty': "لا يوجد أي سلّم ميزات.",
    'ref.mentions.dialogHint': "الشرائح تتلامس دون تداخل: أعلى شريحة هو أدنى التي تليها.",
    'ref.mentions.bands': "الشرائح",
    'ref.mentions.mention': "الميزة",
    'ref.mentions.from': "ابتداءً من",
    'ref.mentions.to': "إلى",
    'ref.mentions.addBand': "إضافة شريحة",
    'ref.mentions.range': "{min} ≤ المعدل < {max}",
    'ref.mentions.default.pass': "مقبول",
    'ref.mentions.default.fair': "مستحسن",
    'ref.mentions.default.good': "حسن",
    'ref.mentions.default.veryGood': "حسن جدًا",
    'ref.letters.add': "سلّم حروف جديد",
    'ref.letters.edit': "السلّم {name}",
    'ref.letters.empty': "لا يوجد أي سلّم حروف.",
    'ref.letters.title': "سلّم الحروف · {name}",
    'ref.letters.americanName': "أمريكي، GPA على 4,0",
    'ref.letters.presetHint': "يُنشأ بالحروف الأمريكية (من A+ إلى F، GPA على 4,0)؛ وتُعدَّل الشرائح بعد ذلك.",
    'ref.letters.dialogHint': "كل حرف يسري ابتداءً من نقطته؛ والأخير يبدأ من 0.",
    'ref.letters.letter': "الحرف",
    'ref.letters.range': "الشريحة على 20",
    'ref.letters.points': "النقاط (على {max})",
    'ref.letters.pointsShort': "النقاط",
    'ref.letters.result': "النتيجة",
    'ref.letters.pass': "نجاح",
    'ref.letters.fail': "رسوب",
    'ref.letters.from': "{min} ≤ النقطة < {max}",
    'ref.letters.fromScore': "ابتداءً من",
    'ref.letters.maxPoints': "أقصى النقاط",
    'ref.letters.grades': "الحروف",
    'ref.letters.addGrade': "إضافة حرف",
    'ref.letters.howTitle': "كيف يُستعمل",
    'ref.letters.howText': "يُدخل الأستاذ نقطة على 20 أو حرفًا: الاثنان مقبولان. الحرف المُدخل يساوي أدنى شريحته في الحساب على 20، ونقاطه في GPA.",
    'ref.letters.probe': "جرّب نقطة على 20",
    'ref.letters.pointsValue': "· النقاط: {points}",
    'ref.letters.averages': "تُحسب المعدلات على 20 وبنظام GPA، مرجَّحة بالأرصدة أو بالمعاملات حسب المجال. ويعرض كشف النقط الاثنين.",
    'ref.pedagogy.sub': "اللوائح التي تستعملها التكوينات والنقط واستعمال الزمن.",
    'ref.pedagogy.teaching_type': "أنواع التدريس",
    'ref.pedagogy.teaching_type.hint': "أعمدة الحجم الساعي واستعمال الزمن",
    'ref.pedagogy.assessment_type': "أنواع التقييم",
    'ref.pedagogy.assessment_type.hint': "ما يُنقَّط في المادة",
    'ref.pedagogy.responsible_type': "أنواع المسؤولين",
    'ref.pedagogy.responsible_type.hint': "من يؤطر منتوجًا، وما يمكنه فعله",
    'ref.pedagogy.disciplinary_field': "الحقول المعرفية",
    'ref.pedagogy.disciplinary_field.hint': "تجمع المواد والأساتذة",
    'ref.pedagogy.department': "الشعب الإدارية",
    'ref.pedagogy.department.hint': "الانتماء الإداري",
    'ref.pedagogy.empty': "لائحة فارغة.",
    'ref.pedagogy.addTo': "إضافة: {list}",
    'ref.pedagogy.editIn': "تعديل: {list}",
    'ref.pedagogy.color': "اللون",
    'ref.pedagogy.rights': "ما يمكنه فعله",
    'ref.pedagogy.rightsHint': "يُطبَّق مع إدخال النقط والمداولات.",
    'ref.pedagogy.rights.canEnterMarks': "إدخال النقط",
    'ref.pedagogy.rights.canValidateMarks': "المصادقة على النقط",
    'ref.pedagogy.rights.canDeliberate': "التداول",
    'ref.pedagogy.rights.enter': "يُدخل النقط",
    'ref.pedagogy.rights.validate': "يصادق على النقط",
    'ref.pedagogy.rights.deliberate': "يتداول",
    'ref.pedagogy.vendor': "مقدَّمة من Kelassi",
    'ref.pedagogy.vendorHint': "غير قابلة للتعديل هنا.",
    'ref.pedagogy.vendor.notation': "التنقيط: على 20، بالحروف، أو الاثنان",
    'ref.pedagogy.vendor.sessions': "حالات الدورة: في الانتظار، مؤكدة، ملغاة",
    'ref.pedagogy.vendor.compensation': "قواعد التعويض: {count}",
    'ref.pedagogy.vendor.cycleAverage': "قواعد معدل السلك: {count}",
    'journal.action.referential.created': "المرجع: إضافة",
    'journal.action.referential.updated': "المرجع: تعديل",
    'journal.action.referential.deleted': "المرجع: حذف",
    'journal.action.referential.year_current': "تغيير السنة الجارية",
    'journal.action.referential.year_closed': "إغلاق سنة جامعية",
    'journal.filter.group.referential': "المرجع البيداغوجي",
    'ref.target.academic_year': "السنة",
    'ref.target.cycle': "السلك",
    'ref.target.domain': "المجال",
    'ref.target.structure': "النموذج",
    'ref.target.level': "المستوى",
    'ref.target.diploma': "الشهادة",
    'ref.target.mention_scale': "سلّم الميزات",
    'ref.target.letter_scale': "سلّم الحروف",
    'ref.target.teaching_type': "نوع التدريس",
    'ref.target.assessment_type': "نوع التقييم",
    'ref.target.responsible_type': "نوع المسؤول",
    'ref.target.disciplinary_field': "الحقل المعرفي",
    'ref.target.department': "الشعبة الإدارية",
    'violation.ref.required': "هذا الحقل إلزامي.",
    'violation.ref.too_long': "طويل جدًا.",
    'violation.ref.invalid': "قيمة غير معروفة.",
    'violation.ref.out_of_range': "خارج الحدود المسموح بها.",
    'violation.ref.invalid_date': "تاريخ غير صالح.",
    'violation.ref.year_closed': "هذه السنة مغلقة: لم يعد يُعدَّل فيها شيء.",
    'violation.ref.year_not_preparing': "ممكن فقط لسنة قيد الإعداد.",
    'violation.ref.in_use': "هذا العنصر مستعمل في مكان آخر: اسحب أولًا ما يرتبط به.",
    'violation.ref.year_label': "الصيغة المنتظرة: 2026-2027.",
    'violation.ref.year_dates': "يجب أن تلي النهاية البداية.",
    'violation.ref.duplicate': "مستعمل مسبقًا.",
    'violation.ref.letter_scale_required': "التنقيط بالحروف يتطلب سلّم حروف.",
    'violation.ref.structure_order': "المستويات تتبع الترتيب: السنة، الفصل، الوحدة، الوحدة الفرعية، المادة.",
    'violation.ref.credits_once': "تُدخل الأرصدة في مستوى واحد فقط.",
    'violation.ref.taught_required': "يجب أن يتلقى مستوى واحد على الأقل النقط (مُدرَّسة).",
    'violation.ref.last_structure': "يحتفظ المجال بنموذج واحد على الأقل.",
    'violation.ref.band_order': "يجب أن يتجاوز أعلى الشريحة أدناها.",
    'violation.ref.band_gap': "الشرائح تتلامس دون فراغ ولا تداخل.",
    'violation.ref.letters_from_zero': "يجب أن يبدأ الحرف الأخير من 0: لكل نقطة حرف.",
    'violation.ref.letters_points': "لا يساوي حرف نقاطًا أكثر من حرف فوقه.",
    'violation.ref.not_found': "العنصر غير موجود.",
    'violation.ref.assigned': "توجد إسنادات أدوار مرتبطة بهذا العنصر: اسحبها أولًا.",
    'ref.rule.all_years': "المعدل بكل سنوات السلك",
    'ref.rule.last_year': "المعدل بآخر سنة من السلك",
    'ref.rule.cycle_normal_semester_2020': "المصادقة الفصلية للسلك العادي، نسخة 2020",
    'ref.rule.bachelor_2020': "المصادقة على البكالوريوس، 2020",
    'ref.rule.cycle_normal_semester_2020_without_submodule': "المصادقة على الفصل، دون الوحدات الفرعية",
    'ref.rule.continuing_education_2021': "التكوين المستمر 2021",
    'ref.rule.cycle_normal_semester_2021_submodules': "السلك العادي مع المواد الفرعية 2021",
    'ref.rule.master_2022': "الماستر والماستر المتخصص 2022",
    'journal.action.catalog.created': "التكوينات: إضافة",
    'journal.action.catalog.updated': "التكوينات: تعديل",
    'journal.action.catalog.deleted': "التكوينات: حذف",
    'journal.action.catalog.renewed': "التكوينات المجددة",
    'journal.filter.group.catalog': "التكوينات",
    'cat.target.training': "التكوين",
    'cat.target.element': "العنصر",
    'cat.target.track': "المسار",
    'cat.target.equivalence': "المطابقة",
    'cat.journal.elements': "العناصر",
    'cat.journal.source': "المقابل",
    'cat.journal.linked': "الروابط المُنشأة",
    'cat.journal.from': "مجدد من",
    'cat.model.metaHours': "الساعات: {value}",
    // D47 : le catalogue des formations.
    'cat.title': "التكوينات",
    'cat.sub': "عرض المؤسسة لسنة واحدة: يتبع كل تكوين نموذج مجاله.",
    'cat.tabs': "تبويبات التكوين",
    'cat.backToList': "تكوينات {year}",
    'cat.year.current': "الجارية",
    'cat.filter.year': "السنة",
    'cat.filter.domain': "المجال",
    'cat.filter.allDomains': "كل المجالات",
    'cat.filter.search': "الرمز أو التسمية",
    'cat.summary': "عدد التكوينات: {count} · عدد الساعات: {hours}",
    'cat.empty': "لا يوجد أي تكوين لهذه السنة.",
    'cat.emptyTraining': "هيكلة فارغة",
    'cat.yearClosed': "هذه السنة مغلقة: تُعرض تكويناتها ولا تُعدَّل.",
    'cat.readOnly': "للاطلاع فقط: هذا التكوين خارج نطاقك أو سنته مغلقة.",
    'cat.none': "لا شيء",
    'cat.notDiploma': "غير مُفضٍ إلى شهادة",
    'cat.unlimited': "بدون حد",
    'cat.levelChip': "المستوى {level}",
    'cat.tracksChip': "المسارات: {count}",
    'cat.delivers': "مُفضٍ إلى شهادة · {diploma}",
    'cat.dates': "من {from} إلى {to}",
    'cat.templateSince': "النموذج المعمول به منذ {year}",
    'cat.noTemplate': "لا يوجد نموذج لهذا المجال",
    'cat.internship': "تدريب",
    'cat.optional': "اختياري",
    'cat.weighting.credits': "الترجيح بالأرصدة",
    'cat.weighting.coefficients': "الترجيح بالمعاملات",
    'cat.count.semester': "الأسدس: {count}",
    'cat.count.module': "الوحدات: {count}",
    'cat.count.submodule': "الوحدات الفرعية: {count}",
    'cat.count.subject': "المواد: {count}",
    'cat.stat.hours': "الساعات",
    'cat.stat.credits': "الأرصدة",
    'cat.stat.modules': "وحدات بمعامل",
    'cat.stat.enrolment': "التسجيل",
    'cat.enrolment.open': "التسجيل مفتوح",
    'cat.enrolment.closed': "التسجيل مغلق",
    'cat.state.confirmed': "مؤكدة",
    'cat.state.pending': "في الانتظار",
    'cat.state.cancelled': "ملغاة",
    'cat.training.create': "تكوين جديد",
    'cat.training.createIn': "تكوين جديد في هذا المجال",
    'cat.training.new': "تكوين جديد في {year}",
    'cat.training.newHint': "سيتبع نموذج مجاله؛ تُبنى هيكلته بعد ذلك.",
    'cat.training.edit': "تعديل «{name}»",
    'cat.training.deleteTitle': "حذف «{name}»؟",
    'cat.training.deleteText': "يُحذف التكوين وعناصره (العدد: {count}) مع ساعاتها والمسؤولين والمسارات. هذا الإجراء نهائي.",
    'cat.training.deleted': "تم حذف «{name}».",
    'cat.field.domain': "المجال",
    'cat.field.level': "المستوى",
    'cat.field.code': "الرمز",
    'cat.field.codeHint': "فريد داخل السنة؛ يُحتفظ به عند التجديد.",
    'cat.field.codeElementHint': "فريد داخل السنة، مثل L2S3M6.",
    'cat.field.name': "التسمية",
    'cat.field.kind': "النوع",
    'cat.field.diploma': "الشهادة المسلّمة",
    'cat.field.diplomaHint': "اتركه فارغًا لسنة لا تُفضي إلى شهادة.",
    'cat.field.startsOn': "البداية",
    'cat.field.endsOn': "النهاية",
    'cat.field.capacityMax': "المقاعد",
    'cat.field.capacityHint': "فارغ: بدون حد.",
    'cat.field.state': "الحالة",
    'cat.field.enrollable': "التسجيل مفتوح",
    'cat.field.enrollableHint': "يمكن تسجيل الطلبة فيه.",
    'cat.field.description': "الوصف",
    'cat.field.objectives': "الأهداف",
    'cat.field.abbreviation': "الاختصار",
    'cat.field.abbreviationHint': "في كشف النقط والجداول.",
    'cat.field.disciplinaryField': "الحقل المعرفي",
    'cat.field.department': "الشعبة",
    'cat.field.validation': "عتبة الاستيفاء",
    'cat.field.resit': "عتبة الاستدراك",
    'cat.field.resitHint': "دونها: لا استدراك.",
    'cat.field.compensation': "قاعدة التعويض",
    'cat.field.compensationHint': "توفرها Kelassi؛ تُطبَّق قاعدة المجال افتراضيًا.",
    'cat.field.compensationDefault': "قاعدة المجال ({rule})",
    'cat.flag.mandatory': "إلزامي",
    'cat.flag.mandatoryHint': "يُحتسب في معدل الجميع.",
    'cat.flag.bonus': "إضافي",
    'cat.flag.bonusHint': "لا يُحتسب إلا إذا رفع المعدل.",
    'cat.flag.internship': "تدريب",
    'cat.flag.internshipHint': "تدريب بدل درس.",
    'cat.tab.model': "الهيكلة",
    'cat.tab.tracks': "المسارات",
    'cat.tab.equivalences': "المطابقات",
    'cat.tab.info': "معلومات",
    'cat.tab.elements': "العناصر: {count}",
    'cat.col.element': "العنصر",
    'cat.col.coef': "المعامل",
    'cat.col.credits': "الأرصدة",
    'cat.col.responsibles': "المسؤولون",
    'cat.model.search': "البحث عن عنصر",
    'cat.model.collapse': "طي الكل",
    'cat.model.expand': "بسط الكل",
    'cat.model.expandOne': "بسط «{name}»",
    'cat.model.collapseOne': "طي «{name}»",
    'cat.model.bulk': "إدخال جماعي",
    'cat.model.where': "الأرصدة على «{credits}» · الساعات والمسؤولون على «{taught}»",
    'cat.model.addUnder': "إضافة: {kind}",
    'cat.model.addIn': "ضمن «{name}»",
    'cat.model.up': "تقديم",
    'cat.model.down': "تأخير",
    'cat.model.empty': "الهيكلة فارغة: ابدأ بإضافة مستوى أول.",
    'cat.model.noMatch': "لا يوجد عنصر مطابق.",
    'cat.model.hidden': "مطوي: {count}",
    'cat.model.metaCredits': "الأرصدة: {value}",
    'cat.model.noResponsible': "بدون مسؤول",
    'cat.model.total': "مجموع التكوين",
    'cat.model.totalPeople': "المواد: {subjects} · الأساتذة: {teachers}",
    'cat.model.creditGap': "{code}: الأرصدة {actual} بدل {expected}",
    'cat.model.deleteTitle': "حذف «{name}»؟",
    'cat.model.deleteText': "يُحذف العنصر مع محتواه (العناصر التابعة: {count}) وساعاتها ومسؤوليها.",
    'cat.model.deleted': "تم حذف «{name}».",
    'cat.drawer.taught': "مُدرَّسة · تتلقى النقط",
    'cat.drawer.creditsHere': "تُدخل الأرصدة هنا",
    'cat.drawer.identity': "الهوية",
    'cat.drawer.evaluation': "التقييم",
    'cat.drawer.hours': "الساعات",
    'cat.drawer.responsibles': "المسؤولون",
    'cat.drawer.creditsHint': "تُجمع في المستويات الأعلى: الأسدس ثم السنة.",
    'cat.drawer.creditsSum': "الأرصدة: {value}، مجموع العناصر التابعة.",
    'cat.drawer.creditsCarried': "لا تُدخل الأرصدة هنا: يحملها «{name}» ({value}).",
    'cat.drawer.coefHint': "داخل العنصر الأعلى.",
    'cat.drawer.hoursTotal': "المجموع: {value} س. تأتي الأعمدة من أنواع التدريس.",
    'cat.drawer.nobody': "لا يوجد مسؤول حاليًا.",
    'cat.drawer.remove': "إزالة {name}",
    'cat.drawer.teacher': "الأستاذ",
    'cat.drawer.type': "الدور",
    'cat.drawer.chooseTeacher': "اختيار أستاذ",
    'cat.bulk.title': "إدخال جماعي · {name}",
    'cat.bulk.trail': "إدخال جماعي · {name}",
    'cat.bulk.sub': "المعاملات والأرصدة والساعات والمسؤولون لكامل التكوين كما في جدول: تنقل Tab بين الخانات.",
    'cat.bulk.back': "العودة إلى الهيكلة",
    'cat.bulk.note': "تُحسب المجاميع مباشرة. ينبغي أن يبلغ الأسدس حصته من 60 رصيدًا للسنة: يُشار إلى أي فرق دون منع حفظ هيكلة قيد الإنجاز.",
    'cat.bulk.cell': "{column} لـ «{name}»",
    'cat.bulk.several': "عدد الأساتذة: {count}، انظر اللوحة",
    'cat.bulk.changes': "التعديلات: {count}",
    'cat.bulk.discard': "تجاهل",
    'cat.bulk.save': "حفظ ({count})",
    'cat.tracks.none': "لا يتضمن هذا التكوين أي مسار.",
    'cat.tracks.why': "يختار المسار عناصره، ويختاره الطالب عند التسجيل.",
    'cat.tracks.add': "إضافة مسار",
    'cat.tracks.edit': "تعديل «{name}»",
    'cat.tracks.dialogHint': "حدّد عناصر المسار؛ العنصر المحدد في كل المسارات هو الجذع المشترك.",
    'cat.tracks.option': "تخصص الشهادة",
    'cat.tracks.comment': "ملاحظة",
    'cat.tracks.elements': "العناصر المختارة: {count}",
    'cat.tracks.all': "تحديد الكل",
    'cat.tracks.clear': "إلغاء الكل",
    'cat.tracks.mode': "وضعية «{name}»",
    'cat.tracks.mandatory': "إلزامي",
    'cat.tracks.elective': "اختياري",
    'cat.tracks.common': "جذع مشترك",
    'cat.tracks.out': "خارج المسار",
    'cat.tracks.own': "عناصر خاصة: {count}",
    'cat.tracks.total': "العناصر: {count}",
    'cat.tracks.how': "يختار الطالب مساره عند التسجيل؛ وتحتسب معدلاته عناصر مساره.",
    'cat.tracks.deleteTitle': "حذف المسار «{name}»؟",
    'cat.tracks.deleteText': "تبقى العناصر في الهيكلة؛ يُحذف المسار فقط.",
    'cat.eq.from': "انطلاقًا من",
    'cat.eq.choose': "اختيار تكوين",
    'cat.eq.chooseHint': "اختر تكوين سنة سابقة لربطه.",
    'cat.eq.noSource': "لا يوجد تكوين من سنة سابقة.",
    'cat.eq.linksCount': "الروابط: {count}",
    'cat.eq.linked': "مرتبطة: {count}",
    'cat.eq.unlinked': "بدون مطابقة: {count}",
    'cat.eq.orphans': "غير مأخوذة: {count}",
    'cat.eq.byCode': "الربط بالرمز",
    'cat.eq.byCodeDone': "روابط أُنشئت بالرمز: {count}.",
    'cat.eq.why': "يسترجع الطالب المسجّل «بالأرصدة» (الذي يعيد ما لم يستوفه) أفضل نقطه في العنصر المرتبط على كل عنصر من هذه السنة. يُنشئ التجديد هذه الروابط تلقائيًا.",
    'cat.eq.thisYear': "هذه السنة · {year}",
    'cat.eq.thatYear': "يأخذ من · {year}",
    'cat.eq.how': "المطابقة",
    'cat.eq.pick': "مقابل «{name}»",
    'cat.eq.none': "بدون مقابل",
    'cat.eq.status.same': "نفس الرمز",
    'cat.eq.status.renamed': "نفس الرمز، تسمية معدّلة",
    'cat.eq.status.manual': "مختار يدويًا",
    'cat.eq.status.none': "للبت",
    'cat.eq.orphansTitle': "عناصر {year} بدون مقابل هذه السنة",
    'cat.eq.orphansHint': "لن تُسترجع النقط المحصّلة في هذه العناصر.",
    'cat.renew.open': "التجديد على {year}…",
    'cat.renew.title': "التجديد على {year}",
    'cat.renew.sub': "يَنسخ التكوينات المختارة من {year}. لا يُعدَّل فيها شيء.",
    'cat.renew.target': "السنة المستهدفة",
    'cat.renew.list': "تكوينات {year}",
    'cat.renew.already': " · موجود مسبقًا",
    'cat.renew.elements': "العناصر: {count}",
    'cat.renew.copied': "ما يُنسخ",
    'cat.renew.notCopied': "ما لا يُنسخ",
    'cat.renew.copy.model': "الهيكلة: الأسدس والوحدات والمواد والرموز",
    'cat.renew.copy.evaluation': "المعاملات والأرصدة والعتبات والتعويض",
    'cat.renew.copy.hours': "الساعات حسب نوع التدريس",
    'cat.renew.copy.tracks': "المسارات وعناصرها",
    'cat.renew.copy.dates': "تواريخ مُزاحة بقدر السنوات",
    'cat.renew.copy.responsibles': "مسؤولو المواد",
    'cat.renew.copy.responsiblesHint': " · للمراجعة: يتغير المتعاقدون غالبًا",
    'cat.renew.copy.not': "التسجيلات والأقسام والنقط والمداولات",
    'cat.renew.linked': "يرتبط كل عنصر منسوخ بأصله: يجد الطالب الذي يعيد في {year} مادة من {from} نقطته فيها.",
    'cat.renew.total': "التكوينات: {count} · العناصر المُنشأة: {elements}",
    'cat.renew.submit': "تجديد ({count})",
    'cat.renew.done': "التكوينات المجددة على {year}: {count}.",
    'cat.renew.emptyTitle': "لا يتضمن {year} أي تكوين بعد.",
    'cat.renew.emptyText': "يَنسخ «التجديد» تكوينات {year} ويربط كل عنصر بمقابله، لفائدة الطلبة الذين يعيدون مادة من سنة إلى أخرى.",
    'cat.info.shape': "الهيكلة",
    'cat.info.session': "التواريخ والمقاعد",
    'cat.info.sessionHint': "للتكوين دورة واحدة: تُضبط قيمها عبر «تعديل».",
    'cat.info.none': "غير مُدخل.",
    'violation.cat.kind_not_allowed': "لا يمكن وضع هذا النوع تحت هذا العنصر (نموذج المجال).",
    'violation.cat.already_renewed': "هذا التكوين موجود مسبقًا في السنة المستهدفة.",
    'violation.cat.source_year': "يجب أن تسبق سنة المصدر الأخرى.",
    'violation.cat.not_in_training': "هذا العنصر لا ينتمي إلى هذا التكوين.",
    'violation.cat.root': "يُدبَّر التكوين من لائحة التكوينات.",
    'violation.cat.capacity': "يجب أن يتجاوز الحد الأقصى الحد الأدنى.",
    // D48 : la scolarité.
    'ref.section.numbering': "الترقيم",
    'ref.numbering.sub': "كيف ترقّم المؤسسة طلبتها: يدويًا أو وفق نموذج.",
    'ref.numbering.mode': "طريقة الترقيم",
    'ref.numbering.mode.manual': "إدخال يدوي",
    'ref.numbering.mode.manual.hint': "يُدخل الرقم في الملف ويبقى فريدًا داخل المؤسسة.",
    'ref.numbering.mode.generated': "توليد تلقائي",
    'ref.numbering.mode.generated.hint': "يقترح Kelassi الرقم التالي عند الإنشاء، ويبقى قابلًا للتعديل.",
    'ref.numbering.pattern': "النموذج",
    'ref.numbering.patternHint': "نص ثابت ورموز. الرتبة {N:x} إلزامية.",
    'ref.numbering.token.AAAA': "السنة بأربعة أرقام",
    'ref.numbering.token.AA': "السنة برقمين",
    'ref.numbering.token.CODE': "رمز التكوين",
    'ref.numbering.token.N': "الرتبة في السنة (4 أرقام)",
    'ref.numbering.example': "أمثلة:",
    'ref.numbering.manualNote': "الأرقام المنقولة من النظام القديم بصيغ متنوعة (\"B4/F123/25\")، والإدخال اليدوي يحتفظ بها كما هي.",
    'ref.numbering.foot': "يعود العداد إلى 1 كل سنة، ويُتخطّى الرقم المستعمل.",
    'ref.numbering.saved': "تم حفظ الترقيم.",
    'scol.year': "السنة",
    'scol.year.current': "الجارية",
    'scol.year.closed': "مغلقة",
    'scol.yearClosed': "هذه السنة مغلقة: لم يعد بالإمكان تعديل تسجيلاتها.",
    'scol.noNumber': "بدون رقم",
    'scol.notEnrolled': "غير مسجل",
    'scol.graduated': "متخرج",
    'scol.graduatedIn': "متخرج {year}",
    'scol.enrolledIn': "مسجل في {year}",
    'scol.inactiveIn': "معطل في {year}",
    'scol.integratedOn': "التحق في {date}",
    'scol.issuedOn': "صادرة في {date}",
    'scol.scholarship': "ممنوح",
    'scol.scholarship.short': "م",
    'scol.repeating': "مكرر",
    'scol.enrol': "تسجيل…",
    'scol.checkAll': "تحديد الكل",
    'scol.checkNone': "إلغاء الكل",
    'scol.sex.F': "أنثى",
    'scol.sex.M': "ذكر",
    'scol.kind.personal': "شخصي",
    'scol.kind.work': "مهني",
    'scol.mode.standard': "عادي",
    'scol.mode.custom': "مخصص",
    'scol.mode.credit': "بالأرصدة",
    'scol.mode.standard.hint': "جميع عناصر التكوين",
    'scol.mode.custom.hint': "جزء من العناصر يُختار هنا",
    'scol.mode.credit.hint': "إعادة ما لم يُكتسب",
    'scol.regime.external': "خارجي",
    'scol.regime.internal': "داخلي",
    'scol.enrolment.state.active': "نشط",
    'scol.enrolment.state.inactive': "معطل",
    'scol.flag.isOfficer': "ضابط",
    'scol.flag.isEmployee': "موظف",
    'scol.flag.hasSpecialNeeds': "احتياجات خاصة",
    'scol.flag.hasOutstandingIssue': "ملف يتطلب التسوية",
    'scol.column.student': "الطالب",
    'scol.column.trainingClass': "التكوين {year} · القسم",
    'scol.column.training': "التكوين",
    'scol.column.mode': "الصيغة",
    'scol.column.class': "القسم",
    'scol.column.state': "الحالة",
    'scol.column.particulars': "خصوصيات",
    'scol.filter.training': "التكوين",
    'scol.filter.allTrainings': "جميع التكوينات",
    'scol.filter.state': "الحالة",
    'scol.filter.mode': "الصيغة",
    'scol.filter.allModes': "جميع الصيغ",
    'scol.filter.active': "النشطة",
    'scol.filter.inactive': "المعطلة",
    'scol.filter.all': "الكل",
    'scol.students.title': "الطلبة",
    'scol.students.count': "الطلبة: {count}",
    'scol.students.sub': "جميع طلبة المؤسسة، مسجلين في السنة أم لا.",
    'scol.students.new': "طالب جديد",
    'scol.students.search': "الاسم أو الرقم أو البطاقة أو رمز مسار",
    'scol.students.empty': "لا يوجد طالب يطابق هذه المعايير.",
    'scol.students.extra': "+ {count} أخرى",
    'scol.students.state.enrolled': "المسجلون في {year}",
    'scol.students.state.inactive': "المعطلون في {year}",
    'scol.students.state.none': "بدون تسجيل في {year}",
    'scol.students.state.graduated': "المتخرجون",
    'scol.students.state.all': "جميع الطلبة",
    'scol.backToStudents': "الطلبة",
    'scol.student.new': "طالب جديد",
    'scol.student.newHint': "الملف أولًا، ثم التسجيل مباشرة بعده.",
    'scol.student.edit': "تعديل {name}",
    'scol.student.create': "إنشاء",
    'scol.student.enrolNow': "التسجيل فورًا",
    'scol.student.enrolNowHint': "يفتح مساعد التسجيل بعد الإنشاء.",
    'scol.panel.identity': "الهوية",
    'scol.panel.enrolments': "التسجيلات",
    'scol.panel.coordinates': "معلومات الاتصال",
    'scol.panel.parents': "الآباء وجهات الاتصال",
    'scol.panel.diplomas': "الشهادات",
    'scol.panel.enrolment': "التسجيل",
    'scol.panel.history': "السجل",
    'scol.file.noEnrolment': "لا يوجد تسجيل.",
    'scol.file.noCoordinates': "لا توجد معلومات اتصال.",
    'scol.file.noParent': "لا يوجد ولي أو جهة اتصال.",
    'scol.file.noDiploma': "لا توجد شهادة.",
    'scol.field.lastName': "النسب",
    'scol.field.firstNames': "الاسم",
    'scol.field.sex': "الجنس",
    'scol.field.birthDate': "تاريخ الازدياد",
    'scol.field.nationalId': "البطاقة الوطنية",
    'scol.field.nationalIdIssuedOn': "تاريخ إصدار البطاقة",
    'scol.field.passport': "جواز السفر",
    'scol.field.massar': "رمز مسار",
    'scol.field.number': "رقم الطالب",
    'scol.field.integratedOn': "تاريخ الالتحاق",
    'scol.field.integratedOnHint': "اليوم إن تُرك فارغًا",
    'scol.field.phone': "الهاتف",
    'scol.field.phones': "الهواتف",
    'scol.field.email': "البريد الإلكتروني",
    'scol.field.emails': "عناوين البريد",
    'scol.field.address': "العنوان",
    'scol.field.addresses': "العناوين",
    'scol.field.street': "العنوان",
    'scol.field.postalCode': "الرمز البريدي",
    'scol.field.city': "المدينة",
    'scol.field.country': "البلد",
    'scol.field.kind': "النوع",
    'scol.field.particulars': "خصوصيات",
    'scol.field.observations': "ملاحظات",
    'scol.field.relationship': "الصلة",
    'scol.field.occupation': "المهنة",
    'scol.field.diploma': "الشهادة",
    'scol.field.speciality': "التخصص",
    'scol.field.diplomaYear': "السنة",
    'scol.field.average': "المعدل",
    'scol.field.mention': "الميزة",
    'scol.field.school': "المؤسسة",
    'scol.field.training': "التكوين",
    'scol.field.track': "المسار",
    'scol.field.class': "القسم",
    'scol.field.mode': "الصيغة",
    'scol.field.regime': "النظام",
    'scol.field.enrolledOn': "تاريخ التسجيل",
    'scol.field.elements': "العناصر المتابعة",
    'scol.field.date': "التاريخ",
    'scol.field.reason': "السبب",
    'scol.field.className': "اسم القسم",
    'scol.number.manualHint': "يُدخل يدويًا ويكون فريدًا",
    'scol.number.generatedHint': "فارغ: الرقم التالي وفق {pattern}",
    'scol.number.keepHint': "قابل للتعديل ويبقى فريدًا",
    'scol.dup.title': "قد يكون هذا الشخص موجودًا",
    'scol.dup.born': "مزداد في {date}",
    'scol.dup.cin': "البطاقة {cin}",
    'scol.dup.reason.national_id': "نفس البطاقة",
    'scol.dup.reason.name_and_birth': "نفس الاسم وتاريخ الازدياد",
    'scol.dup.reason.similar_name': "اسم مشابه",
    'scol.dup.open': "فتح الملف",
    'scol.dup.hint': "لا شيء ممنوع: يمكن إنشاء شخص بنفس الاسم.",
    'scol.add.phone': "إضافة هاتف",
    'scol.add.email': "إضافة بريد",
    'scol.add.address': "إضافة عنوان",
    'scol.contact.add': "إضافة جهة اتصال",
    'scol.contact.noType': "بدون صلة محددة",
    'scol.contact.remove': "إزالة جهة الاتصال",
    'scol.diploma.add': "إضافة شهادة",
    'scol.diploma.edit': "تعديل الشهادة",
    'scol.diploma.remove': "إزالة الشهادة",
    'scol.diploma.average': "المعدل {average}",
    'scol.diploma.mention': "ميزة {mention}",
    'scol.diploma.kind.access': "ولوج",
    'scol.diploma.kind.delivered': "مسلمة",
    'scol.remove.confirm': "إزالة \"{label}\"؟",
    'scol.saved.identity': "تم حفظ الهوية.",
    'scol.saved.coordinates': "تم حفظ معلومات الاتصال.",
    'scol.saved.contact': "تمت إضافة جهة الاتصال.",
    'scol.saved.diploma': "تم حفظ الشهادة.",
    'scol.saved.removed': "تمت إزالة \"{label}\".",
    'scol.enrolments.title': "التسجيلات",
    'scol.enrolments.count': "التسجيلات: {count}",
    'scol.enrolments.sub': "من يتابع أي تكوين في السنة المختارة.",
    'scol.enrolments.new': "تسجيل جديد",
    'scol.enrolments.search': "الطالب أو الرقم",
    'scol.enrolments.empty': "لا يوجد تسجيل يطابق هذه المعايير.",
    'scol.stats.byTraining': "حسب التكوين",
    'scol.stats.none': "لا يوجد تسجيل نشط.",
    'scol.stats.activeInactive': "{active} نشطة · {inactive} معطلة",
    'scol.stats.modes': "الصيغ",
    'scol.stats.internal': "الداخليون",
    'scol.stats.scholarship': "الممنوحون",
    'scol.enrolment.edit': "تعديل التسجيل",
    'scol.enrolment.delete': "حذف التسجيل",
    'scol.enrolment.deleteConfirm': "حذف هذا التسجيل المدخل خطأ؟ الانقطاع يُصرَّح به ولا يُحذف.",
    'scol.elements.all': "{count} (الكل)",
    'scol.elements.some': "{count} مختارة",
    'scol.event.enrolled': "تسجيل",
    'scol.event.abandoned': "انقطاع",
    'scol.event.resumed': "استئناف",
    'scol.event.by': "بواسطة {name}",
    'scol.abandon.title': "التصريح بانقطاع",
    'scol.abandon.hint': "يُعطَّل التسجيل ويُسجَّل الانقطاع في السجل بتاريخه وسببه.",
    'scol.abandon.submit': "تأكيد الانقطاع",
    'scol.resume.title': "استئناف",
    'scol.resume.hint': "الاستئناف يعيد تفعيل التسجيل وعناصره ويُسجَّل في السجل مع سببه.",
    'scol.resume.submit': "استئناف التسجيل",
    'scol.done.abandoned': "تم التصريح بالانقطاع.",
    'scol.done.resumed': "تم استئناف التسجيل.",
    'scol.done.deleted': "تم حذف التسجيل.",
    'scol.enrol.new': "تسجيل جديد",
    'scol.enrol.edit': "تعديل التسجيل",
    'scol.enrol.sub': "اختر الطالب والتكوين والصيغ.",
    'scol.enrol.step.student': "الطالب",
    'scol.enrol.step.training': "التكوين والصيغ",
    'scol.enrol.step.elements': "العناصر المتابعة",
    'scol.enrol.pickHint': "ابحث عن الطالب، وأنشئه إن لم يكن موجودًا.",
    'scol.enrol.pickTraining': "اختر تكوينًا",
    'scol.enrol.already': "هذا الطالب مسجل مسبقًا في هذا التكوين.",
    'scol.enrol.noTrack': "لا يوجد مسار",
    'scol.enrol.classOption': "{name} ({count} مسجل)",
    'scol.enrol.checked': "{count} من {total} عنصرًا محددًا",
    'scol.enrol.creditHint': "العناصر المتابعة سابقًا مُشار إليها عبر الروابط بين السنوات. ستأتي النقط مع الدفعة 3، وإلى ذلك الحين حدد يدويًا ما لم يُكتسب.",
    'scol.enrol.creditNoHistory': "لا يوجد تسجيل سابق لهذا الطالب يقابل هذا التكوين: حدد يدويًا.",
    'scol.enrol.followed': "تمت متابعته في {year} ({code})",
    'scol.enrol.recap': "التسجيل الجاري",
    'scol.enrol.onHint': "اليوم إن تُرك فارغًا",
    'scol.enrol.submit': "تسجيل",
    'scol.classes.title': "الأقسام",
    'scol.classes.titleOf': "الأقسام · {training}",
    'scol.classes.sub': "أقسام كل تكوين.",
    'scol.classes.subOf': "{count} مسجل · اسحب طالبًا من قسم إلى آخر",
    'scol.classes.new': "قسم جديد",
    'scol.classes.edit': "تعديل القسم",
    'scol.classes.delete': "حذف القسم",
    'scol.classes.deleteConfirm': "حذف \"{name}\"؟ يعود أعضاؤه ({count}) غير موزعين.",
    'scol.classes.distribute': "توزيع غير الموزعين…",
    'scol.classes.distributeConfirm': "توزيع {count} طالبًا على {classes} قسمًا أبجديًا، بدءًا بالأقل عددًا؟",
    'scol.classes.distributeGo': "توزيع",
    'scol.classes.distributed': "تم توزيع {count} طالبًا.",
    'scol.classes.unassigned': "غير موزعين",
    'scol.classes.unassigned.short': "غير موزع",
    'scol.classes.unassigned.later': "لاحقًا",
    'scol.classes.unassign': "إخراج من القسم",
    'scol.classes.move': "نقل",
    'scol.classes.moveTo': "إلى {name}",
    'scol.classes.empty': "اسحب الطلبة إلى هنا.",
    'scol.classes.poolEmpty': "لكل طالب قسم.",
    'scol.classes.delegate': "المندوب",
    'scol.classes.delegate.short': "مندوب",
    'scol.classes.countDelegate': "{count} · المندوب: {name}",
    'scol.classes.noTraining': "لا يوجد تكوين في {year}.",
    'scol.classes.note': "يتابع القسم جميع عناصر التكوين. ستأتي مجموعات الأعمال الموجهة والتطبيقية مع إدخال النقط (الدفعة 3). الطالب في قسم واحد فقط لكل تكوين.",
    'scol.reenrol.open': "إعادة تسجيل فوج…",
    'scol.reenrol.title': "إعادة تسجيل فوج",
    'scol.reenrol.sub': "نقل طلبة تكوين إلى تكوين في سنة لاحقة.",
    'scol.reenrol.from': "من",
    'scol.reenrol.to': "إلى",
    'scol.reenrol.pickFrom': "اختر التكوين الأصلي",
    'scol.reenrol.pickTo': "اختر التكوين الهدف",
    'scol.reenrol.pickBoth': "اختر التكوين الأصلي والتكوين الهدف.",
    'scol.reenrol.enrolled': "{count} مسجل",
    'scol.reenrol.filter': "تصفية",
    'scol.reenrol.empty': "لا يوجد مسجل نشط في هذا التكوين.",
    'scol.reenrol.already': "مسجل مسبقًا في {training}",
    'scol.reenrol.elsewhere': "مسجل في {training}",
    'scol.reenrol.carried': "ما يُنقل",
    'scol.reenrol.keepRegime': "النظام (داخلي، خارجي)",
    'scol.reenrol.keepScholarship': "المنحة",
    'scol.reenrol.keepScholarshipHint': "تُراجع كل سنة",
    'scol.reenrol.notCarried': "يُختار المسار والقسم في التكوين الجديد.",
    'scol.reenrol.note': "ستأتي قرارات اللجنة مع الدفعة 3 وستحدد الناجحين تلقائيًا. إلى ذلك الحين حدد يدويًا، والطالب المسجل في الهدف يظهر باهتًا.",
    'scol.reenrol.submit': "إعادة تسجيل {count} طالبًا",
    'scol.reenrol.done': "تمت إعادة تسجيل {count} طالبًا في {training}.",
    'violation.scol.already_enrolled': "هذا الطالب مسجل مسبقًا في هذا التكوين.",
    'violation.scol.no_element': "حدد عنصرًا واحدًا على الأقل.",
    'violation.scol.already_inactive': "هذا التسجيل معطل مسبقًا.",
    'violation.scol.already_active': "هذا التسجيل نشط مسبقًا.",
    'violation.scol.not_member': "يجب أن يكون المندوب عضوًا في القسم.",
    'violation.scol.not_enrolled': "هذا الطالب غير مسجل في هذا التكوين.",
    'violation.scol.no_class': "أنشئ قسمًا واحدًا على الأقل.",
    'violation.scol.target_year': "يجب أن يكون التكوين الهدف في سنة لاحقة.",
    'violation.scol.invalid_pattern': "نموذج غير صالح: نص ورموز {AAAA} و{AA} و{CODE} و{N:4}، مع رتبة {N:x}.",
    'journal.action.schooling.created': "الشؤون الدراسية: إضافة",
    'journal.action.schooling.updated': "الشؤون الدراسية: تعديل",
    'journal.action.schooling.deleted': "الشؤون الدراسية: حذف",
    'journal.action.schooling.abandoned': "الشؤون الدراسية: انقطاع",
    'journal.action.schooling.resumed': "الشؤون الدراسية: استئناف",
    'journal.action.schooling.reenrolled': "الشؤون الدراسية: إعادة تسجيل",
    'scol.target.student': "طالب",
    'scol.target.enrolment': "تسجيل",
    'scol.target.class': "قسم",
    'scol.journal.members': "الأعضاء",
    'scol.journal.distributed': "الموزعون",
    'scol.journal.count': "العدد",
    'journal.filter.group.schooling': "الشؤون الدراسية",
    // D51 : la finance.
    'fin.all': "الكل",
    'fin.active': "نشط",
    'fin.inactive': "معطّل",
    'fin.default': "افتراضي",
    'fin.amount': "المبلغ",
    'fin.net': "الصافي",
    'fin.paid': "المؤدى",
    'fin.due': "الباقي",
    'fin.total': "المجموع",
    'fin.quantity': "الكمية",
    'fin.excludingTax': "دون احتساب الضريبة",
    'fin.includingTax': "شامل الضريبة",
    'fin.noTax': "بدون ضريبة",
    'fin.computed': "محسوب.",
    'fin.pdf': "PDF",
    'fin.receipt': "الوصل",
    'fin.deleteConfirm': "حذف «{label}»؟",
    'fin.deleted': "تم حذف «{label}».",
    'fin.off.title': "وحدة المالية غير مفعّلة",
    'fin.off.text': "يفعّل المشرف العام الوحدة ويضبطها من «الإشراف العام › الوحدات».",
    'fin.level.training': "التكوين كاملاً",
    'fin.level.module': "الوحدة",
    'fin.level.subject': "المادة",
    'fin.kind.training': "تكوين",
    'fin.kind.year': "سنة",
    'fin.kind.semester': "فصل",
    'fin.kind.module': "وحدة",
    'fin.kind.submodule': "وحدة فرعية",
    'fin.kind.subject': "مادة",
    'fin.method.cash': "نقداً",
    'fin.method.cheque': "شيك",
    'fin.method.transfer': "تحويل بنكي",
    'fin.status.late': "متأخر",
    'fin.status.current': "في وضعية سليمة",
    'fin.status.paid': "مسددة",
    'fin.status.cancelled': "ملغاة",
    'fin.installment.paid': "مؤداة",
    'fin.installment.partial': "جزئية",
    'fin.installment.late': "متأخرة",
    'fin.installment.due': "مستحقة",
    'fin.installment.upcoming': "قادمة",
    'fin.invoice.kind.enrolment': "تسجيل",
    'fin.invoice.kind.on_demand': "خدمة عند الطلب",
    'fin.service.kind.mandatory': "إجباري",
    'fin.service.kind.optional': "اختياري",
    'fin.service.kind.on_demand': "عند الطلب",
    'fin.service.kindHint.mandatory': "يضاف تلقائياً إلى فاتورة كل تسجيل معني.",
    'fin.service.kindHint.optional': "يضاف إلى فاتورة التسجيل عند الطلب.",
    'fin.service.kindHint.on_demand': "يُفوتَر على حدة عندما يطلبه الطالب.",
    'fin.service.scope.all': "جميع الطلبة",
    'fin.service.scope.cycles': "الأسلاك",
    'fin.service.scope.levels': "المستويات",
    'fin.discount.kind.percent': "نسبة مئوية",
    'fin.discount.kind.amount': "مبلغ ثابت",
    'fin.discount.kind.free': "مبلغ يُحدَّد عند المنح",
    'fin.discount.target.invoice': "الفاتورة كاملة",
    'fin.discount.target.lines': "سطور محددة",
    'fin.discount.training': "سعر التكوين",
    'fin.target.settings': "الإعداد",
    'fin.target.tax': "ضريبة",
    'fin.target.schedule': "جدول الأداء",
    'fin.target.service': "خدمة",
    'fin.target.discount': "تخفيض",
    'fin.target.price': "سعر",
    'fin.target.invoice': "فاتورة",
    'fin.target.payment': "أداء",
    'fin.modules.title': "وحدة المالية",
    'fin.modules.sub': "الإشراف العام › الوحدات · خاص بالمزوّد",
    'fin.modules.list': "وحدات المؤسسة",
    'fin.modules.finance': "المالية",
    'fin.modules.active': "مفعّل",
    'fin.modules.inactive': "متوقف",
    'fin.modules.reserved': "لا يرى هذه الشاشة إلا المشرف العام. تختفي الوحدة المتوقفة من القائمة وتُحفظ بياناتها.",
    'fin.modules.enabled': "وحدة المالية مفعّلة",
    'fin.modules.enabledHint': "يفتح قسم المالية في القائمة والحساب في ملف الطالب ودوري «مسؤول المالية» و«أمين الصندوق».",
    'fin.modules.enabledOn': "فُعّلت في {date} من طرف {who}. إيقافها يخفي الشاشات ولا يحذف أي فاتورة أو أداء.",
    'fin.modules.levels': "مستويات التسعير",
    'fin.modules.priceTraining': "التكوين كاملاً",
    'fin.modules.priceTrainingHint': "سعر واحد لكل تكوين وسنة، يُفوتَر عند التسجيل العادي.",
    'fin.modules.priceModule': "الوحدة",
    'fin.modules.priceModuleHint': "سعر لكل وحدة إذا كانت المؤسسة تبيع الوحدات منفردة.",
    'fin.modules.priceSubject': "المادة",
    'fin.modules.priceSubjectHint': "سعر لكل مادة، يُفوتَر في التسجيلات المخصصة وبالأرصدة حسب المواد المختارة.",
    'fin.modules.enrolmentMode': "نوع التسجيل",
    'fin.modules.billed': "المبلغ المفوتر",
    'fin.modules.billedTraining': "سعر التكوين",
    'fin.modules.billedAll': "مجموع أسعار عناصره",
    'fin.modules.billedChosen': "مجموع أسعار العناصر المختارة",
    'fin.modules.billedCredit': "مجموع أسعار العناصر المعادة",
    'fin.modules.missingPrice': "إضافة إلى الخدمات الإجبارية. التكوين بلا سعر لا يُنتج فاتورة ويظهر التسجيل ضمن الفواتير الناقصة.",
    'fin.modules.currency': "العملة",
    'fin.modules.currencyCode': "رمز ISO (MAD، XOF، EUR)",
    'fin.modules.decimals': "الأرقام العشرية",
    'fin.modules.currencyHint': "لا يمكن تغيير العملة بعد إصدار أول فاتورة.",
    'fin.modules.currencyLocked': "توجد فواتير: العملة مقفلة.",
    'fin.modules.numbering': "ترقيم الوثائق",
    'fin.modules.invoicePattern': "صيغة الفواتير",
    'fin.modules.receiptPattern': "صيغة الوصولات",
    'fin.modules.next': "التالي: {number}",
    'fin.modules.insert': "إدراج:",
    'fin.modules.reset': "يعود العداد إلى 1",
    'fin.modules.reset.yearly': "كل سنة جامعية",
    'fin.modules.reset.never': "أبداً",
    'fin.modules.numberingHint': "يجب أن تتضمن الصيغة رتبة {N:x}. الرقم المسند لا يُعاد استعماله ولو أُلغيت الفاتورة.",
    'fin.modules.mentions': "البيانات المطبوعة على الفواتير والوصولات",
    'fin.modules.legalName': "الاسم القانوني",
    'fin.modules.address': "العنوان",
    'fin.modules.businessId': "المعرف الموحد للمقاولة",
    'fin.modules.taxId': "المعرف الضريبي",
    'fin.modules.tradeRegister': "السجل التجاري",
    'fin.modules.footer': "تذييل الصفحة",
    'fin.modules.logo': "يأتي الشعار من هوية المؤسسة.",
    'fin.modules.saved': "تم حفظ إعداد الوحدة.",
    'fin.prices.title': "أسعار التكوينات",
    'fin.prices.sub': "المستويات المسعّرة: {levels}",
    'fin.prices.renew': "نقل من سنة أخرى",
    'fin.prices.renewSub': "ينسخ الأسعار عنصراً بعنصر حسب الرمز إلى السنة المعروضة.",
    'fin.prices.renewFrom': "من",
    'fin.prices.renewGo': "نقل",
    'fin.prices.renewed': "تم نقل {count} سعراً.",
    'fin.prices.bulk': "تطبيق سعر جماعي",
    'fin.prices.bulkSub': "سعر واحد لكل عناصر مستوى في هذا التكوين.",
    'fin.prices.overwrite': "استبدال الأسعار المدخلة",
    'fin.prices.saved': "تم حفظ {count} سعراً.",
    'fin.prices.invalid': "مبلغ غير مقروء: «{value}».",
    'fin.prices.empty': "لا يوجد تكوين في هذه السنة.",
    'fin.prices.training': "التكوين",
    'fin.prices.amount': "السعر",
    'fin.prices.schedule': "جدول الأداء",
    'fin.prices.missing': "للإتمام",
    'fin.prices.complete': "مكتمل",
    'fin.prices.missingCount': "{count} بدون سعر",
    'fin.prices.defaultSchedule': "الجدول الافتراضي",
    'fin.prices.element': "عنصر الفهرس",
    'fin.prices.level': "المستوى",
    'fin.prices.credits': "الأرصدة",
    'fin.prices.notPriced': "مستوى غير مسعّر",
    'fin.prices.toEnter': "للإدخال",
    'fin.prices.howTitle': "ما يؤديه الطالب",
    'fin.prices.how': "التسجيل العادي: سعر التكوين. المخصص أو بالأرصدة: مجموع أسعار العناصر المتبعة. إضافة إلى الخدمات الإجبارية وما يُضاف.",
    'fin.prices.frozenTitle': "الأسعار والفواتير الصادرة",
    'fin.prices.frozen': "تنسخ الفاتورة السعر عند إصدارها: تغيير السعر لا يمس إلا الفواتير اللاحقة.",
    'fin.services.title': "الخدمات التكميلية",
    'fin.services.sub': "الرسوم والتأمين والخيارات والخدمات عند الطلب مع ضريبتها.",
    'fin.services.new': "خدمة جديدة",
    'fin.services.empty': "لا توجد خدمات.",
    'fin.services.label': "الخدمة",
    'fin.services.scope': "النطاق",
    'fin.services.kind': "الطبيعة",
    'fin.services.tax': "الضريبة",
    'fin.services.students': "الطلبة",
    'fin.services.thisYear': "هذه السنة فقط",
    'fin.services.validity': "الصلاحية",
    'fin.services.validityHint': "كل السنوات: تستمر الخدمة دون نسخ.",
    'fin.services.allYears': "كل السنوات",
    'fin.services.onlyYear': "{year} فقط",
    'fin.services.saved': "تم حفظ الخدمة.",
    'fin.services.delete': "حذف الخدمة",
    'fin.services.deleteConfirm': "حذف «{label}»؟ لا تُحذف خدمة سبقت فوترتها: عطّلها.",
    'fin.services.deleted': "تم حذف «{label}».",
    'fin.services.mandatoryHint': "الخدمة الإجبارية المضافة خلال السنة لا تغيّر الفواتير الصادرة: أضفها من حسابات الطلبة المعنيين.",
    'fin.discounts.title': "التخفيضات",
    'fin.discounts.sub': "تخفيضات خاصة تُمنح من حساب الطالب.",
    'fin.discounts.new': "تخفيض جديد",
    'fin.discounts.empty': "لا توجد تخفيضات.",
    'fin.discounts.label': "التخفيض",
    'fin.discounts.value': "القيمة",
    'fin.discounts.target': "يطبق على",
    'fin.discounts.rules': "القواعد",
    'fin.discounts.granted': "الممنوحة هذه السنة",
    'fin.discounts.kind': "النوع",
    'fin.discounts.percent': "النسبة",
    'fin.discounts.amount': "المبلغ",
    'fin.discounts.freeHint': "يُدخل المبلغ عند المنح مع التعليل.",
    'fin.discounts.invoiceHint': "يخصم من المجموع بما فيه الخدمات.",
    'fin.discounts.restricted': "حق خاص",
    'fin.discounts.notStackable': "غير قابل للجمع",
    'fin.discounts.reason': "التعليل إلزامي",
    'fin.discounts.flag.stackable': "قابل للجمع مع تخفيضات أخرى",
    'fin.discounts.flagHint.stackable': "وإلا فلا يُمنح على فاتورة تحمل تخفيضاً.",
    'fin.discounts.flag.restricted': "خاص بحق «التخفيض الاستثنائي»",
    'fin.discounts.flagHint.restricted': "يحصّل أمين الصندوق دون أن يستطيع منحه.",
    'fin.discounts.flag.requiresReason': "التعليل إلزامي",
    'fin.discounts.flagHint.requiresReason': "يظهر التعليل في الفاتورة والسجل.",
    'fin.discounts.flag.active': "نشط",
    'fin.discounts.flagHint.active': "",
    'fin.discounts.journal': "منح التخفيض يُسجَّل: من ومتى وعلى أي فاتورة وبأي تعليل، ويظهر سطراً سالباً في الفاتورة.",
    'fin.discounts.saved': "تم حفظ التخفيض.",
    'fin.discounts.delete': "حذف التخفيض",
    'fin.discounts.deleteConfirm': "حذف «{label}»؟ لا يُحذف تخفيض سبق منحه: عطّله.",
    'fin.discounts.deleted': "تم حذف «{label}».",
    'fin.taxes.title': "الضرائب",
    'fin.taxes.new': "ضريبة جديدة",
    'fin.taxes.empty': "لا ضرائب: تُفوتَر الخدمات دون ضريبة.",
    'fin.taxes.services': "{count} خدمات",
    'fin.taxes.hint': "الضريبة المفوترة لا يتغير معدلها: أنشئ واحدة جديدة.",
    'fin.taxes.name': "الاسم",
    'fin.taxes.rate': "المعدل (%)",
    'fin.taxes.isDefault': "الضريبة الافتراضية لخدمة جديدة",
    'fin.taxes.saved': "تم حفظ الضريبة.",
    'fin.taxes.delete': "حذف الضريبة",
    'fin.schedules.title': "الضرائب وجداول الأداء",
    'fin.schedules.sub': "إعدادات مسؤول المالية.",
    'fin.schedules.list': "جداول الأداء",
    'fin.schedules.new': "جدول جديد",
    'fin.schedules.empty': "لا جداول: تؤدى كل فاتورة دفعة واحدة.",
    'fin.schedules.pick': "اختر جدولاً.",
    'fin.schedules.name': "الاسم",
    'fin.schedules.grace': "مهلة السماح (أيام)",
    'fin.schedules.graceHint': "تصبح الدفعة غير المؤداة «متأخرة» بعد هذه المهلة.",
    'fin.schedules.due': "الاستحقاق",
    'fin.schedules.share': "الحصة",
    'fin.schedules.day': "اليوم",
    'fin.schedules.month': "الشهر",
    'fin.schedules.addLine': "إضافة دفعة",
    'fin.schedules.dateHint': "يقع التاريخ الثابت داخل السنة الجامعية: مارس يعني مارس الذي يلي الدخول. والتاريخ الفائت يوم الفاتورة يقع في ذلك اليوم.",
    'fin.schedules.isDefault': "الجدول الافتراضي للمؤسسة",
    'fin.schedules.isDefaultHint': "يستعمله كل تكوين لا يحدد جدولاً آخر.",
    'fin.schedules.saved': "تم حفظ الجدول.",
    'fin.schedules.delete': "حذف الجدول",
    'fin.schedules.ruleTitle': "كيف يُخصَّص الأداء",
    'fin.schedules.rule': "يسدد الأداء أولاً أقدم دفعة غير مؤداة ثم التالية. الأداء حسب الجدول أو بأقساط حرة يعطي النتيجة نفسها.",
    'fin.schedules.example': "مثال",
    'fin.schedules.example1': "12 شتنبر · 9840",
    'fin.schedules.example2': "5 يناير · 3000",
    'fin.schedules.example3': "20 يناير · 6000",
    'fin.schedules.surplus': "الأداء الذي يتجاوز الباقي يترك رصيداً دائناً في حساب الطالب لا يضيع.",
    'fin.schedule.onIssue': "عند الفوترة",
    'fin.schedule.onDate': "تاريخ ثابت",
    'fin.invoices.title': "الفواتير والتحصيلات",
    'fin.invoices.sub': "فاتورة لكل تسجيل تصدر تلقائياً عند التسجيل.",
    'fin.invoices.tab': "الفواتير",
    'fin.invoices.search': "الرقم، الطالب، رقم الطالب",
    'fin.invoices.filter': "الحالة",
    'fin.invoices.discounted': "بتخفيض",
    'fin.invoices.empty': "لا توجد فاتورة مطابقة.",
    'fin.invoices.number': "الرقم",
    'fin.invoices.student': "الطالب",
    'fin.invoices.training': "التكوين",
    'fin.invoices.next': "الاستحقاق القادم",
    'fin.invoices.status': "الحالة",
    'fin.invoices.missing': "{count} تسجيلات نشطة بلا فاتورة: لم يكن لتكوينها سعر عند التسجيل.",
    'fin.invoices.issueMissing': "إصدار {count} فواتير ناقصة",
    'fin.invoices.issueTitle': "إصدار الفواتير الناقصة",
    'fin.invoices.issueConfirm': "إصدار فاتورة لكل من {count} تسجيلات بدونها؟ تبقى بلا فاتورة تلك التي ما زال تكوينها بلا سعر.",
    'fin.invoices.issueGo': "إصدار",
    'fin.invoices.issued': "صدرت {count} فواتير.",
    'fin.payments.tab': "التحصيلات",
    'fin.payments.method': "الطريقة",
    'fin.payments.allMethods': "كل الطرق",
    'fin.payments.from': "من",
    'fin.payments.to': "إلى",
    'fin.payments.empty': "لا توجد تحصيلات.",
    'fin.payments.sum': "{count} تحصيلات، المجموع:",
    'fin.payments.date': "التاريخ",
    'fin.payments.receipt': "الوصل",
    'fin.payments.by': "سجله",
    'fin.dashboard.title': "لوحة القيادة المالية",
    'fin.dashboard.sub': "السنة {year} · بتاريخ {date}",
    'fin.dashboard.billed': "الصافي المفوتر",
    'fin.dashboard.invoices': "{count} فواتير",
    'fin.dashboard.paid': "المحصل",
    'fin.dashboard.rate': "{rate} من المفوتر",
    'fin.dashboard.due': "الباقي",
    'fin.dashboard.students': "{count} طلبة",
    'fin.dashboard.late': "المتأخر",
    'fin.dashboard.discounts': "التخفيضات الممنوحة",
    'fin.dashboard.discountCount': "{count} تخفيضات",
    'fin.dashboard.expected': "المنتظر إلى اليوم",
    'fin.dashboard.expectedNote': "{paid} من {expected} منتظرة",
    'fin.dashboard.months': "التحصيلات حسب الشهر",
    'fin.dashboard.methods': "حسب طريقة الأداء",
    'fin.dashboard.cash': "صندوق اليوم · {date}",
    'fin.dashboard.noPayment': "لا تحصيلات هذه السنة.",
    'fin.dashboard.noCash': "لا تحصيلات اليوم.",
    'fin.dashboard.byTraining': "حسب التكوين",
    'fin.dashboard.invoiceCount': "الفواتير",
    'fin.dashboard.collection': "نسبة التحصيل",
    'fin.dashboard.noInvoice': "لا فواتير هذه السنة.",
    'fin.dashboard.oldestLate': "أقدم التأخرات",
    'fin.dashboard.noLate': "لا تأخرات.",
    'fin.dashboard.allLate': "عرض كل الفواتير المتأخرة",
    'fin.archive.title': "السجل من كلاسي السابق",
    'fin.archive.summary': "{count} فواتير · من {from} إلى {to}",
    'fin.archive.show': "عرض السجل",
    'fin.archive.hide': "إخفاء السجل",
    'fin.archive.bannerTitle': "مستورد من كلاسي السابق، للاطلاع فقط",
    'fin.archive.bannerText': "الفواتير والدفعات والأرصدة الدائنة كما أُدخلت. لا يُستورد أي مبلغ متبقٍّ: الأرصدة الافتتاحية تقدمها المؤسسة. لا شيء هنا يدخل في حساب السنة الجارية.",
    'fin.archive.billed': "المفوتر",
    'fin.archive.billedNote': "{count} فواتير، دون الملغاة",
    'fin.archive.reductions': "التخفيضات",
    'fin.archive.special': "تخفيضات خاصة",
    'fin.archive.creditNotes': "أرصدة دائنة",
    'fin.archive.paid': "المحصّل",
    'fin.archive.paidNote': "بعد خصم الإلغاءات",
    'fin.archive.expand': "عرض تفاصيل الفاتورة",
    'fin.archive.collapse': "طيّ الفاتورة",
    'fin.archive.cancelled': "ملغاة",
    'fin.archive.specialLine': "تخفيض خاص",
    'fin.archive.creditLine': "رصيد دائن بتاريخ {date}",
    'fin.archive.net': "الصافي المفوتر",
    'fin.archive.noPayment': "لا توجد دفعات مسجلة.",
    'fin.archive.comment': "تعليق",
    'fin.archive.paymentCancelled': "ملغى",
    'fin.archive.reversal': "قيد عكسي",
    'fin.archive.dateCorrected': "تاريخ مُصحَّح",
    'fin.archive.dateCorrectedHint': "« تاريخ مُصحَّح »: كان التاريخ المُدخل في كلاسي السابق مستحيلاً (سنة « 19 »، « 20 »…)، فصحّحه الاستيراد انطلاقاً من تاريخ الإدخال.",
    'fin.method.direct_debit': "اقتطاع بنكي",
    'fin.quote.title': "رسوم التسجيل",
    'fin.quote.sub': "الفاتورة التي تصدر عند الحفظ",
    'fin.quote.unpriced': "لا توجد تعريفة لـ {training}: يُسجَّل التسجيل دون فاتورة، ويظهر ضمن « الفواتير الناقصة » إلى أن تُحدَّد التعريفة.",
    'fin.quote.price': "تعريفة التكوين",
    'fin.quote.mandatory': "الخدمات الإلزامية",
    'fin.quote.mandatoryHint': "تُضاف تلقائياً حسب نطاق الخدمة",
    'fin.quote.locked': "إلزامي",
    'fin.quote.optional': "خدمات اختيارية",
    'fin.quote.optionalHint': "تُحدَّد هنا أو تُضاف لاحقاً من الحساب",
    'fin.quote.total': "المبلغ المفوتر",
    'fin.quote.note': "تصدر الفاتورة عند حفظ التسجيل بهذه البنود. تُمنح التخفيضات لاحقاً من حساب الطالب من طرف المالية.",
    'fin.quote.taxIncluded': "منها {tax}",
    'fin.quote.single': "دفعة واحدة",
    'fin.quote.schedule': "جدول الدفع · {name}",
    'fin.quote.atIssue': "عند التسجيل",
    'fin.method.internal': "ترحيل",
    'fin.account.discountsCredits': "التخفيضات والأرصدة الدائنة",
    'fin.credit.open': "إصدار رصيد دائن",
    'fin.credit.sub': "الفاتورة {number} · الصافي {amount}",
    'fin.credit.modeLines': "على بنود",
    'fin.credit.modeLinesHint': "جزء من البنود المختارة أو كلها",
    'fin.credit.modeInvoice': "الفاتورة كاملة",
    'fin.credit.modeInvoiceHint': "باقي الفاتورة دفعة واحدة (انقطاع، خطأ)",
    'fin.credit.line': "البند",
    'fin.credit.billed': "المفوتر",
    'fin.credit.room': "القابل للإرجاع",
    'fin.credit.amount': "الرصيد الدائن",
    'fin.credit.total': "مجموع الرصيد الدائن",
    'fin.credit.reason': "السبب",
    'fin.credit.reasonHint': "يظهر في الرصيد الدائن وفي السجل.",
    'fin.credit.effect': "الأثر على الفاتورة",
    'fin.credit.before': "قبل",
    'fin.credit.after': "بعد الرصيد الدائن",
    'fin.credit.net': "صافي الفاتورة",
    'fin.credit.due': "المتبقي",
    'fin.credit.overpaid': "المبلغ الزائد",
    'fin.credit.installments': "الأقساط",
    'fin.credit.note': "الرصيد الدائن وثيقة مرقمة تصحح فاتورة صادرة دون تعديلها. تُعاد حساب الأقساط: يُخفَّض آخرها أولاً.",
    'fin.credit.print': "طباعة الرصيد الدائن",
    'fin.credit.go': "إصدار الرصيد الدائن",
    'fin.credit.goPrint': "إصدار وطباعة",
    'fin.credit.done': "صدر الرصيد الدائن.",
    'fin.credit.list': "الأرصدة الدائنة",
    'fin.credit.row': "رصيد دائن {number} · {date}",
    'fin.overpaid.title': "{amount} مدفوعة زيادة على المستحق",
    'fin.overpaid.text': "يبقى المبلغ الزائد في حساب الطالب إلى أن يُتخذ قرار بشأنه، ولا يضيع أبداً.",
    'fin.overpaid.transfer': "ترحيل إلى فاتورة",
    'fin.overpaid.refund': "استرجاع…",
    'fin.overpaid.title2': "إرجاع المبلغ الزائد",
    'fin.overpaid.sub': "{number} · {amount} زيادة",
    'fin.refund.tab': "استرجاع",
    'fin.refund.amount': "المبلغ المسترجع",
    'fin.refund.amountHint': "على الأكثر {amount}",
    'fin.refund.date': "التاريخ",
    'fin.refund.beneficiary': "المستفيد",
    'fin.refund.beneficiaryHint': "الطالب أو أحد معارفه في ملفه",
    'fin.refund.reason': "السبب",
    'fin.refund.note': "الاسترجاع خروج من الصندوق: مرقم ومسجل وغير قابل للتعديل، ويُصحَّح بالإلغاء مثل الأداء.",
    'fin.refund.print': "طباعة وصل الاسترجاع",
    'fin.refund.go': "استرجاع",
    'fin.refund.done': "سُجل الاسترجاع.",
    'fin.transfer.tab': "ترحيل إلى فاتورة",
    'fin.transfer.none': "لا توجد فاتورة أخرى للطالب بها مبلغ متبقٍّ.",
    'fin.transfer.note': "الترحيل أداء داخلي: يسدد الفاتورة الأخرى دون حركة صندوق، ويُنقص المبلغ الزائد بقدره.",
    'fin.transfer.amount': "المبلغ المرحَّل",
    'fin.transfer.object': "الموضوع",
    'fin.transfer.go': "ترحيل",
    'fin.transfer.done': "رُحِّل المبلغ الزائد.",
    'fin.payment.refund': "استرجاع",
    'fin.payment.transferOut': "ترحيل إلى فاتورة أخرى",
    'fin.payment.transferIn': "ترحيل من {number}",
    'fin.modules.creditNotePattern': "صيغة الأرصدة الدائنة",
    'fin.modules.refundPattern': "صيغة الاسترجاعات",
    'journal.action.finance.credit_note_issued': "إصدار رصيد دائن",
    'journal.action.finance.refund_recorded': "استرجاع مبلغ زائد",
    'journal.action.finance.payment_transferred': "ترحيل مبلغ زائد",
    'violation.fin.credit_too_high': "يتجاوز الرصيد الدائن ما تبقى للإرجاع.",
    'violation.fin.nothing_to_credit': "هذه الفاتورة مُرجعة بالكامل.",
    'violation.fin.no_line': "اختر بنداً واحداً على الأقل.",
    'violation.fin.below_credited': "ستنزل الفاتورة تحت ما أرجعته أرصدتها الدائنة.",
    'violation.fin.invoice_has_credit_note': "تحمل هذه الفاتورة رصيداً دائناً: لم يعد إلغاؤها ممكناً.",
    'violation.fin.refund_too_high': "يتجاوز المبلغ الزيادة في الفاتورة.",
    'violation.fin.transfer_too_high': "يتجاوز الترحيل المتبقي في الفاتورة المستهدفة.",
    'violation.fin.transfer_target': "يكون الترحيل إلى فاتورة أخرى للطالب نفسه.",
    'violation.fin.service_not_offered': "هذه الخدمة غير مقترحة لهذا التكوين.",
    'account.person': "ملف الأستاذ المرتبط",
    'account.person.hint': "الأستاذ الذي يمثله هذا الحساب: يدخل نقط موادّه.",
    'account.person.none': "لا شيء",
    'account.person.taken': "مرتبط بحساب آخر",
    'ref.pedagogy.absence_type': "أنواع الغياب عن التقييمات",
    'ref.pedagogy.absence_type.hint': "قيمة كل غياب: نقطة (0) أو لا شيء",
    'ref.target.absence_type': "نوع الغياب",
    'ref.pedagogy.absence.effect': "الأثر على النقطة",
    'ref.pedagogy.absence.countsLabel': "يحتسب الغياب كنقطة",
    'ref.pedagogy.absence.value': "النقطة الممنوحة",
    'ref.pedagogy.absence.hint': "إذا لم يُحدَّد، يُستبعد الطالب الغائب من الحساب (غياب مبرر مثلاً). يمكن كتابة الرمز في خانة النقطة أيضاً.",
    'ref.pedagogy.absence.counts': "يحتسب {value}",
    'ref.pedagogy.absence.notCounted': "لا يحتسب",
    'journal.action.assessment.created': "إنشاء تقييم",
    'journal.action.assessment.updated': "تعديل تقييم",
    'journal.action.assessment.deleted': "حذف تقييم",
    'journal.action.assessment.validated': "المصادقة على النقط",
    'journal.action.assessment.reopened': "إعادة فتح الإدخال",
    'journal.action.assessment.marks_imported': "استيراد النقط",
    'journal.action.assessment.mark_corrected': "تصحيح نقطة",
    'asm.target.assessment': "تقييم",
    'asm.target.mark': "نقطة",
    'asm.journal.time': "التوقيت",
    'asm.journal.candidates': "المستدعَون",
    'asm.journal.candidate': "مستدعى",
    'asm.journal.validated': "المصادقة",
    'asm.journal.mark': "النقطة",
    'asm.journal.marks': "النقط",
    'asm.absence.counts': "يحتسب {value}",
    'asm.absence.notCounted': "لا يحتسب",
    'asm.absence.label': "غياب {name}",
    'asm.banner.validated': "صودق عليها في {date} من طرف {name}.",
    'asm.banner.locked': "النقط مقفلة. يتطلب التصحيح سبباً، وتُحفظ القيمة السابقة.",
    'asm.candidates.add': "استدعاء طلبة",
    'asm.candidates.addSub': "{count} مسجلين غير مستدعين بعد",
    'asm.candidates.added': "تم استدعاء {count} طلبة.",
    'asm.candidates.allIn': "جميع المسجلين مستدعون.",
    'asm.candidates.classes': "الأقسام",
    'asm.candidates.go': "استدعاء",
    'asm.candidates.none': "لا يوجد طالب مستدعى.",
    'asm.candidates.remove': "سحب من التقييم",
    'asm.candidates.removed': "لم يعد {name} مستدعى.",
    'asm.col.absence': "الغياب",
    'asm.col.assessment': "التقييم",
    'asm.col.candidates': "المستدعَون",
    'asm.col.class': "القسم",
    'asm.col.entry': "الإدخال",
    'asm.col.mark': "النقطة /{outOf}",
    'asm.col.number': "الرقم",
    'asm.col.student': "الطالب",
    'asm.correct': "تصحيح النقطة",
    'asm.correct.current': "النقطة الحالية",
    'asm.correct.note': "يُسجَّل التصحيح في السجل، ولا يفتح باقي النقط.",
    'asm.correct.reason': "السبب",
    'asm.correct.value': "النقطة الجديدة /{outOf}",
    'asm.corrected': "مصححة",
    'asm.corrected.done': "صُححت نقطة {name}.",
    'asm.corrections.after': "بعد",
    'asm.corrections.before': "قبل",
    'asm.corrections.by': "من طرف",
    'asm.corrections.date': "التاريخ",
    'asm.corrections.reason': "السبب",
    'asm.corrections.title': "التصحيحات بعد المصادقة",
    'asm.delete': "حذف التقييم",
    'asm.delete.confirm': "حذف «{label}» واستدعاءاته؟ لم تُدخل أي نقطة بعد.",
    'asm.dialog.addResponsible': "+ إضافة مسؤول",
    'asm.dialog.addStudent': "استدعاء طالب بمفرده",
    'asm.dialog.addStudentHint': "للاستدراك، أو لطالب خارج الأقسام المحددة.",
    'asm.dialog.chooseSubject': "اختيار المادة",
    'asm.dialog.convoke': "الاستدعاء",
    'asm.dialog.convokeHint': "أعضاء الأقسام المحددة في هذا اليوم. يمكن تعديل اللائحة لاحقاً من التقييم (إضافة طالب أو سحبه).",
    'asm.dialog.create': "إنشاء وإدخال النقط",
    'asm.dialog.members': "{count} طلبة",
    'asm.dialog.noClass': "لا يوجد قسم في هذا التكوين: استدعِ الطلبة واحداً واحداً.",
    'asm.dialog.noResponsible': "لا يوجد مسؤول",
    'asm.dialog.responsiblesHint': "مأخوذون من مسؤولي المادة (الكتالوج). يدخل المسؤول النقط من «تقييماتي» إذا كان حسابه مرتبطاً بملفه.",
    'asm.dialog.searchStudent': "الاسم أو الرقم",
    'asm.dialog.sub': "{training} · {year} · {count} طلبة مستدعون",
    'asm.dialog.subjectTeacher': "أستاذ المادة",
    'asm.edit': "تعديل التقييم",
    'asm.field.coefficient': "المعامل",
    'asm.field.date': "التاريخ",
    'asm.field.element': "العنصر",
    'asm.field.end': "النهاية",
    'asm.field.label': "العنوان",
    'asm.field.labelHint': "يظهر في اللائحة وفي كشوف النقط",
    'asm.field.labelPlaceholder': "مراقبة 1، الامتحان النهائي…",
    'asm.field.nature': "الطبيعة",
    'asm.field.outOf': "منقط على",
    'asm.field.responsibles': "المسؤولون",
    'asm.field.retake': "دورة استدراكية",
    'asm.field.start': "البداية",
    'asm.field.state': "الحالة",
    'asm.field.subject': "المادة",
    'asm.field.training': "التكوين",
    'asm.field.type': "النوع",
    'asm.filter.all': "جميع المستدعَين",
    'asm.filter.allElements': "جميع العناصر",
    'asm.filter.allStates': "جميع الحالات",
    'asm.filter.allTypes': "جميع الأنواع",
    'asm.filter.label': "تصفية المستدعَين",
    'asm.filter.missing': "بدون نقطة",
    'asm.head.coefficient': "المعامل {value}",
    'asm.head.outOf': "على {value}",
    'asm.head.responsibles': "المسؤول: {names}",
    'asm.input.label': "نقطة {name}",
    'asm.keys': "لوحة المفاتيح: Enter أو ↓ للطالب التالي، ↑ للرجوع، Esc لإلغاء الخانة. تُحفظ كل نقطة عند مغادرة خانتها. اكتب رمز الغياب ({codes}) لتسجيله.",
    'asm.kpi.complete': "للمصادقة",
    'asm.kpi.partial': "بدأ الإدخال",
    'asm.kpi.toEnter': "انقضى التاريخ، لا نقطة",
    'asm.kpi.total': "التقييمات",
    'asm.kpi.validated': "نقط مقفلة",
    'asm.list.empty': "لا يوجد تقييم بهذه المعايير.",
    'asm.list.noTraining': "لا يوجد تكوين لهذه السنة ضمن نطاقك.",
    'asm.list.note': "بدون معدلات: ستأتي مع الحساب. هنا التقييمات ونقطها.",
    'asm.list.sub': "مراقبات وامتحانات السنة وتقدم إدخال النقط",
    'asm.list.title': "التقييمات",
    'asm.mine.empty': "لا يوجد تقييم في موادك هذه السنة.",
    'asm.mine.sub': "تقييمات المواد التي أنت مسؤول عنها: أدخل النقط ما لم يُصادق عليها",
    'asm.mine.title': "تقييماتي",
    'asm.mine.unlinked': "حسابك غير مرتبط بأي ملف أستاذ: اطلب من المسؤول ربطه من صفحة حسابك.",
    'asm.nature.written': "كتابي",
    'asm.nature.oral': "شفوي",
    'asm.nature.practical': "تطبيقي",
    'asm.nature.project': "مشروع",
    'asm.new': "تقييم جديد",
    'asm.noClass': "بدون قسم",
    'asm.reopen': "إعادة فتح الإدخال",
    'asm.reopen.confirm': "إعادة فتح الإدخال؟ تصبح النقط قابلة للتعديل دون سبب حتى المصادقة التالية.",
    'asm.reopened': "أعيد فتح الإدخال.",
    'asm.retake': "استدراك",
    'asm.saved': "حُفظ التقييم.",
    'asm.sheet.download': "تحميل ورقة Excel",
    'asm.sheet.import': "استيراد ورقة Excel",
    'asm.sheet.imported': "استُوردت {count} نقطة ({skipped} سطر فارغ لم يُغيَّر).",
    'asm.sheet.importing': "جارٍ الاستيراد…",
    'asm.sheet.refused': "رُفضت الورقة",
    'asm.sheet.refusedSub': "{count} أخطاء: لم تُحفظ أي نقطة. صحح الورقة واستوردها من جديد.",
    'asm.sheet.row': "السطر {row}:",
    'asm.state.planned': "قادم",
    'asm.state.to_enter': "للإدخال",
    'asm.state.partial': "قيد الإدخال",
    'asm.state.complete': "إدخال مكتمل",
    'asm.state.validated': "مصادق عليه",
    'asm.stats.between': "{from} إلى {to}",
    'asm.stats.counting': "غيابات محتسبة",
    'asm.stats.distribution': "توزيع النقط",
    'asm.stats.excused': "غيابات غير محتسبة",
    'asm.stats.marks': "النقط المدخلة",
    'asm.stats.mean': "متوسط التقييم {value}",
    'asm.stats.missing': "بدون نقطة",
    'asm.stats.noMark': "لا توجد نقطة مدخلة.",
    'asm.stats.progress': "التقدم",
    'asm.stats.range': "الأدنى {min} · الأعلى {max}",
    'asm.status.empty': "بدون نقطة",
    'asm.status.saved': "محفوظة",
    'asm.status.saving': "جارٍ الحفظ…",
    'asm.student.absent': "الغيابات",
    'asm.student.convoked': "الاستدعاءات",
    'asm.student.empty': "لا يوجد تقييم لهذا الطالب.",
    'asm.student.evaluated': "{count} تقييمات هذه السنة",
    'asm.student.marked': "منقطة",
    'asm.student.note': "ستظهر المعدلات وقرارات اللجنة هنا مع الحساب. السنوات السابقة مأخوذة من Kelassi القديم بنقطها.",
    'asm.student.title': "النقط",
    'asm.student.toCome': "قادم",
    'asm.student.upcoming': "قادمة",
    'asm.validate': "المصادقة على النقط",
    'asm.validate.confirm': "المصادقة على {count} نقطة؟ ستُقفل: يتطلب أي تصحيح بعدها سبباً.",
    'asm.validate.missing': "{count} مستدعين بدون نقطة ولا غياب",
    'asm.validated': "صودق على النقط.",
    'asm.kpi.validatedLabel': "مصادق عليها",
    'asm.kpi.toEnterLabel': "للإدخال",
    'ref.pedagogy.absence.treatment.mark': "يُحتسب كنقطة",
    'ref.pedagogy.absence.treatment.mark.hint': "النقطة أدناه (0 عادة)",
    'ref.pedagogy.absence.treatment.mark.short': "يُحتسب",
    'ref.pedagogy.absence.treatment.neutralized': "مُحيَّد",
    'ref.pedagogy.absence.treatment.neutralized.hint': "يُستبعد التقييم من الحساب ويُوزَّع وزنه",
    'ref.pedagogy.absence.treatment.neutralized.short': "لا يُحتسب",
    'ref.pedagogy.absence.treatment.blocking': "يوقف الحساب",
    'ref.pedagogy.absence.treatment.blocking.hint': "لا معدل حتى تُسوّى الوضعية",
    'ref.pedagogy.absence.treatment.blocking.short': "يوقف الحساب",
    'ref.pedagogy.absence.treatment.resit': "يحيل على الاستدراك",
    'ref.pedagogy.absence.treatment.resit.hint': "يعيد الطالب العنصر في الدورة الاستدراكية",
    'ref.pedagogy.absence.treatment.resit.short': "يحيل على الاستدراك",
    'reg.title': "النظام الدراسي",
    'reg.sub': "قواعد احتساب المعدلات والقرارات والاستدراك، نسخة بنسخة، لكل سنة",
    'reg.and': "و",
    'reg.legacy': "مستوردة من كلاسي السابق",
    'reg.lifecycle': "دورة حياة النسخة",
    'reg.lifecycle.note': "النسخة المنشورة لا تتغير: كل تصحيح ينشئ نسخة جديدة بتاريخ سريان وسبب.",
    'reg.filter.allYears': "كل السنوات",
    'reg.list.empty': "لا توجد أي نسخة من النظام بعد.",
    'reg.list.inForce': "سارية منذ {date}",
    'reg.list.frozen': "مجمدة في {date}",
    'reg.list.archived': "استبدلت في {date}",
    'reg.list.blocking': "{count} مراقبة (مراقبات) مانعة",
    'reg.list.awaiting': "في انتظار المصادقة",
    'reg.list.ready': "جاهزة للإرسال",
    'reg.list.note': "عند افتتاح سنة، انسخوا آخر نسخة من السنة السابقة وراجعوها قبل النشر. يمر النشر عبر شخص ثانٍ (أربع أعين) إذا تطلبت النسخة ذلك.",
    'reg.col.version': "السنة والنسخة",
    'reg.col.state': "الحالة",
    'reg.col.createdBy': "حررها",
    'reg.col.decidedBy': "صادق عليها",
    'reg.col.status': "الوضعية",
    'reg.col.scope': "النطاق",
    'reg.col.profile': "الملف",
    'reg.col.block': "الكتلة",
    'reg.col.value': "القيمة المعتمدة",
    'reg.col.origin': "نطاق المصدر",
    'reg.state.draft': "مسودة",
    'reg.state.review': "قيد المصادقة",
    'reg.state.published': "منشورة",
    'reg.state.frozen': "مجمدة",
    'reg.state.archived': "مؤرشفة",
    'reg.status.V': "مستوفى",
    'reg.status.VPC': "مستوفى بالتعويض",
    'reg.status.VAR': "مستوفى بعد الاستدراك",
    'reg.status.R': "استدراك",
    'reg.status.NV': "غير مستوفى",
    'reg.status.A': "غائب",
    'reg.status.pending': "في الانتظار",
    'reg.status.V.when': "عند العتبة، مع استيفاء العناصر الفرعية",
    'reg.status.VPC.when': "فوق الحد الأدنى، مع استيفاء العنصر الأب",
    'reg.status.VAR.when': "مستوفى بفضل الاستدراك",
    'reg.status.R.when': "غير مستوفى، قابل للاستدراك",
    'reg.status.NV.when': "رسب في الاستدراك أو المعدل منخفض جدا",
    'reg.status.A.when': "غير مستوفى، مع غياب محتسب",
    'reg.outcome.admitted': "ناجح",
    'reg.outcome.admitted_with_debts': "ناجح بديون",
    'reg.outcome.retake': "استدراك",
    'reg.outcome.not_admitted': "راسب",
    'reg.outcome.pending': "في الانتظار",
    'reg.scope.institution': "المؤسسة",
    'reg.scope.cycle': "السلك",
    'reg.scope.domain': "المجال",
    'reg.scope.cycle_domain': "السلك والمجال",
    'reg.scope.level': "المستوى",
    'reg.scope.track': "المسار",
    'reg.scope.class': "القسم",
    'reg.scope.element': "عنصر من البرنامج",
    'reg.block.scale': "سلم التنقيط",
    'reg.block.weighting': "الترجيح",
    'reg.block.absences': "الغيابات والحالات",
    'reg.block.thresholds': "العتبات والنقط الإقصائية",
    'reg.block.compensation': "التعويض والرسملة",
    'reg.block.retake': "الاستدراك",
    'reg.block.priors': "استرجاع النقط",
    'reg.block.rounding': "التقريب",
    'reg.block.decisions': "القرارات والميزات والرتبة",
    'reg.criterion.regime': "النظام",
    'reg.criterion.mode': "نمط التسجيل",
    'reg.criterion.repeating': "التكرار",
    'reg.criterion.scholarship': "المنحة",
    'reg.criterion.employee': "صفة الأجير",
    'reg.criterion.special_needs': "الترتيبات الخاصة",
    'reg.criterion.entry_year': "سنة الولوج",
    'reg.op.is': "هو",
    'reg.op.is_not': "ليس",
    'reg.value.none': "لا شيء",
    'reg.v.types': "حسب نوع التقييم",
    'reg.v.coefficients': "المعاملات",
    'reg.v.mean': "معدل بسيط",
    'reg.v.redistribute': "يُحذف ويُوزّع وزنه",
    'reg.v.zero': "يحتسب 0",
    'reg.v.credits': "الأرصدة",
    'reg.v.equal': "وزن متساوٍ",
    'reg.v.elements': "مثل العناصر",
    'reg.v.custom': "ترجيح حر",
    'reg.v.catalog': "عتبات البرنامج",
    'reg.v.level': "عتبة لكل مستوى",
    'reg.v.no_compensation': "لا يمكن تعويضه",
    'reg.v.fails_parent': "يُسقط العنصر الأب أيضا",
    'reg.v.best': "الأفضل بين الاثنين",
    'reg.v.average': "معدل نقط الاستدراك",
    'reg.v.last': "الأحدث",
    'reg.v.merge': "معدل الدورات",
    'reg.v.none': "لا شيء",
    'reg.v.half_up': "إلى الأقرب",
    'reg.v.half_down': "إلى الأقرب، النصف نحو الأسفل",
    'reg.v.half_even': "إلى الأقرب، النصف نحو الزوجي",
    'reg.v.down': "بتر",
    'reg.v.up': "نحو الأعلى",
    'reg.v.kept': "تُحتفظ بها",
    'reg.v.removed': "تُحذف",
    'reg.v.numeric': "أرقام",
    'reg.v.numeric_letter': "أرقام وحرف",
    'reg.v.letter': "حرف",
    'reg.v.subject': "المادة",
    'reg.v.submodule': "الوحدة الفرعية",
    'reg.v.module': "الوحدة",
    'reg.v.semester': "الأسدس",
    'reg.v.year': "السنة",
    'reg.v.mark': "تحتسب كنقطة",
    'reg.v.neutralized': "لا تحتسب",
    'reg.v.blocking': "توقف الاحتساب",
    'reg.v.resit': "تحيل على الاستدراك",
    'reg.v.true': "نعم",
    'reg.v.false': "لا",
    'reg.v.defined': "محدد",
    'reg.v.external': "خارجي",
    'reg.v.internal': "داخلي",
    'reg.v.standard': "عادي",
    'reg.v.credit': "بالأرصدة",
    'reg.f.outOf': "النقطة القصوى",
    'reg.f.display': "الأرقام العشرية المعروضة",
    'reg.f.assessments': "التقييمات داخل المادة",
    'reg.f.typeWeights': "الوزن حسب النوع",
    'reg.f.missingType': "نوع بدون نقط",
    'reg.f.bestOf': "أفضل النقط المحتفظ بها",
    'reg.f.elements': "العناصر داخل العنصر الأب",
    'reg.f.semesters': "الأسدسان في السنة",
    'reg.f.semesterWeights': "أوزان الأسدسين",
    'reg.f.bonusCap': "سقف نقط المكافأة",
    'reg.f.overrides': "المعالجة",
    'reg.f.source': "العتبات مأخوذة من",
    'reg.f.validation': "يُستوفى عند",
    'reg.f.retake': "قابل للاستدراك ابتداء من",
    'reg.f.eliminatory': "النقطة الإقصائية",
    'reg.f.eliminatoryEffect': "أثر النقطة الإقصائية",
    'reg.f.enabled': "التعويض",
    'reg.f.floor': "قابل للتعويض ابتداء من",
    'reg.f.maxCount': "على الأكثر",
    'reg.f.capitalisation': "الرسملة",
    'reg.f.retained': "النقطة المعتمدة",
    'reg.f.cap': "السقف بعد الاستدراك",
    'reg.f.wholeGroup': "الوحدة الفرعية كاملة في الاستدراك",
    'reg.f.sessions': "عدة دورات من نفس النوع",
    'reg.f.mode': "النمط",
    'reg.f.evaluable': "تقريب المواد",
    'reg.f.aggregate': "تقريب المجاميع",
    'reg.f.digits': "الأرقام العشرية",
    'reg.f.maxDebts': "ناجح بديون: على الأكثر",
    'reg.f.debtKind': "مستوى الديون",
    'reg.f.mentions': "الميزات",
    'reg.f.mentionAfterRetake': "الميزة بعد الاستدراك",
    'reg.f.rank': "الرتبة",
    'reg.field.whole': "محدد",
    'reg.field.year': "السنة",
    'reg.field.from': "نسخ النسخة",
    'reg.field.fromHint': "تُنسخ الملفات والتعيينات وحالات الاختبار. من سنة إلى أخرى، يتم إيجاد الأقسام والمسارات والعناصر برموزها.",
    'reg.field.fromNone': "لا شيء: ملف كامل افتراضي",
    'reg.field.reason': "السبب",
    'reg.field.comment': "تعليق",
    'reg.field.effectiveFrom': "سارية ابتداء من",
    'reg.field.fourEyes': "المصادقة بأربع أعين",
    'reg.field.fourEyesHint': "المصادقة بأربع أعين: من يرسل لا ينشر",
    'reg.field.cohortRule': "نظام الطالب",
    'reg.field.training': "التكوين",
    'reg.field.chooseTraining': "اختيار تكوين",
    'reg.field.choose': "اختيار",
    'reg.field.class': "القسم",
    'reg.field.allClasses': "كل الأقسام",
    'reg.field.student': "الطالب",
    'reg.field.noStudent': "لا طالب بعينه",
    'reg.field.element': "العنصر",
    'reg.field.wholeTraining': "التكوين كاملا",
    'reg.field.name': "الاسم",
    'reg.cohort.current_year': "نظام السنة الجارية",
    'reg.cohort.entry_year': "نظام سنة ولوجه",
    'reg.version.label': "{year} · النسخة {number}",
    'reg.version.new': "نسخة جديدة",
    'reg.version.newSub': "مسودة للمراجعة والإرسال للمصادقة",
    'reg.version.create': "إنشاء المسودة",
    'reg.version.blank': "نسخة أنشئت فارغة",
    'reg.version.basedOn': "نسخة من {label}",
    'reg.version.created': "حررها {who} في {when}",
    'reg.version.submitted': "أرسلها {who} في {when}",
    'reg.version.published': "نشرها {who} في {when}، سارية من {from}",
    'reg.version.settings': "إعدادات النسخة",
    'reg.version.copy': "نسخة جديدة انطلاقا من هذه",
    'reg.version.freeze': "تجميد النسخة",
    'reg.version.freezeText': "مداولات السنة مغلقة: تبقى النسخة مقروءة لتفسير المعدلات ولا تُستبدل.",
    'reg.version.delete': "حذف المسودة",
    'reg.version.deleteText': "ستُحذف المسودة وملفاتها وتعييناتها وحالات اختبارها.",
    'reg.version.submit': "طلب المصادقة",
    'reg.version.submitText': "لا يمكن تعديل النسخة أثناء المصادقة. ينشرها المدير البيداغوجي أو يعيدها إلى المسودة.",
    'reg.version.submitBlocked': "صححوا أولا المراقبات المانعة.",
    'reg.version.approve': "المصادقة والنشر",
    'reg.version.reject': "إعادة إلى المسودة",
    'reg.version.fourEyesBlocked': "أنتم من أرسل هذه النسخة: يجب أن يصادق عليها شخص آخر (أربع أعين).",
    'reg.version.inReview': "أرسلها {who}، في انتظار المدير البيداغوجي.",
    'reg.version.publishedNotice': "نُشرت النسخة: تسري ابتداء من تاريخ سريانها.",
    'reg.simulate': "محاكاة",
    'reg.scopes.title': "النطاقات البيداغوجية",
    'reg.scopes.sub': "تأتي قاعدة كل كتلة من النطاق الأدق الذي يحددها، وإلا تصعد إلى المؤسسة.",
    'reg.elements.title': "عناصر البرنامج",
    'reg.elements.sub': "كيف تحتسب وحدة ما نقطتها.",
    'reg.elements.empty': "لا يتوفر أي عنصر على ملف خاص به. لتعيين ملف، اختاروا نطاق «عنصر من البرنامج».",
    'reg.assign': "تعيين ملف",
    'reg.assign.edit': "تعديل التعيين",
    'reg.criteria.title': "فقط للطلبة الذين",
    'reg.criteria.add': "إضافة معيار",
    'reg.criteria.hint': "يجب أن تتحقق كل الشروط. عند تساوي النطاق، يكون التعيين بمعايير أدق من التعيين بدونها.",
    'reg.locks.title': "قفل بالنسبة للنطاقات الأدق",
    'reg.tests.title': "حالات الاختبار",
    'reg.tests.sub': "تُعاد عند كل مراقبة: حالة فاشلة تمنع النشر.",
    'reg.tests.add': "إضافة حالة",
    'reg.tests.empty': "لا توجد حالة اختبار.",
    'reg.tests.case': "الحالة",
    'reg.tests.expected': "المتوقع",
    'reg.tests.obtained': "المحصل",
    'reg.tests.passed': "ناجحة",
    'reg.tests.failed': "فاشلة",
    'reg.tests.notEnrolled': "غير مسجل في هذا التكوين",
    'reg.tests.expectedValue': "النقطة المتوقعة",
    'reg.tests.expectedStatus': "الوضع المتوقع",
    'reg.checks.title': "مراقبات الانسجام",
    'reg.checks.none': "لا توجد أي مراقبة فاشلة.",
    'reg.checks.blocking': "{count} مانعة",
    'reg.checks.warnings': "{count} تنبيه (تنبيهات)",
    'reg.check.institution_missing.title': "المؤسسة غير مغطاة",
    'reg.check.institution_missing': "عينوا ملفا كاملا للمؤسسة.",
    'reg.check.institution_incomplete.title': "كتل ناقصة على مستوى المؤسسة",
    'reg.check.institution_incomplete': "غير محددة: {blocks}.",
    'reg.check.duplicate_scope.title': "نطاق مكرر",
    'reg.check.duplicate_scope': "{scope} يتلقى عدة ملفات: {profiles}.",
    'reg.check.conflict.title': "تعارض النطاقات",
    'reg.check.conflict': "{scopes} تحدد «{block}» بشكل مختلف وبنفس الدقة بالنسبة لـ {training}.",
    'reg.check.lock_violation.title': "كتلة مقفلة أعيد تحديدها",
    'reg.check.lock_violation': "{scope} ({profile}) يعيد تحديد «{block}» المقفلة من نطاق أوسع.",
    'reg.check.type_weights_total.title': "أوزان لا تساوي 100٪",
    'reg.check.type_weights_total': "{profile}: مجموع أنواع التقييم {total}٪.",
    'reg.check.semester_weights_total.title': "أوزان الأسدسين",
    'reg.check.semester_weights_total': "{profile}: مجموع الأسدسين {total}٪.",
    'reg.check.unknown_type.title': "نوع تقييم غير معروف",
    'reg.check.unknown_type': "{profile}: النوع {code} غير موجود في المرجع.",
    'reg.check.unknown_absence.title': "غياب غير معروف",
    'reg.check.unknown_absence': "{profile}: الغياب {code} غير موجود في المرجع.",
    'reg.check.tests_failed.title': "حالات اختبار فاشلة",
    'reg.check.tests_failed': "{failed} من {total} حالات لا تنجح.",
    'reg.check.tests_passed.title': "حالات الاختبار",
    'reg.check.tests_passed': "{total} من {total} حالات ناجحة.",
    'reg.check.redundant.title': "إعادة تحديد مطابقة",
    'reg.check.redundant': "{scope} ({profile}) يعيد تحديد «{block}» بالقيمة الموروثة.",
    'reg.check.unused_profile.title': "ملف غير معين",
    'reg.check.unused_profile': "{profile} غير معين لأي نطاق.",
    'reg.check.floor_above_threshold.title': "الحد الأدنى فوق العتبة",
    'reg.check.floor_above_threshold': "{profile}: الحد الأدنى للتعويض ({level}) يتجاوز عتبة الاستيفاء.",
    'reg.check.retake_above_threshold.title': "عتبة الاستدراك فوق عتبة الاستيفاء",
    'reg.check.retake_above_threshold': "{profile}: عتبة الاستدراك ({level}) تتجاوز عتبة الاستيفاء.",
    'reg.profiles.title': "ملفات النسخة",
    'reg.profiles.new': "ملف جديد",
    'reg.profiles.newSub': "ملف جزئي: حددوا بعد ذلك الكتل التي يغيرها.",
    'reg.profiles.complete': "كامل",
    'reg.profiles.blocks': "{count} كتلة (كتل)",
    'reg.profiles.used': "{count} تعيين (تعيينات)",
    'reg.profile.blocks': "كتل الملف",
    'reg.profile.defined': "محدد",
    'reg.profile.inherited': "موروث",
    'reg.profile.define': "تحديد في هذا الملف",
    'reg.profile.definedHere': "محدد في هذا الملف: يعوض قاعدة النطاقات الأقل دقة.",
    'reg.profile.inheritedHint': "غير محدد هنا: تأتي القاعدة من نطاق أقل دقة.",
    'reg.profile.defaultIs': "القيمة الافتراضية لكلاسي: {summary}.",
    'reg.profile.unassigned': "غير معين لأي نطاق",
    'reg.profile.assignedTo': "معين لـ: {scopes}",
    'reg.profile.saved': "تم حفظ الملف. أعيدت مراقبات النسخة.",
    'reg.profile.readOnly': "نسخة منشورة أو قيد المصادقة: الملف للقراءة فقط.",
    'reg.ed.weighting.assessments': "التقييمات داخل المادة",
    'reg.ed.weighting.typesHint': "بالنسبة المئوية",
    'reg.ed.weighting.missing': "نوع بدون نقط",
    'reg.ed.weighting.bestOf': "{code}: الاحتفاظ بأفضل N فقط",
    'reg.ed.weighting.elements': "العناصر داخل العنصر الأب (الوحدات داخل الأسدس…)",
    'reg.ed.weighting.semesters': "الأسدسان في السنة",
    'reg.ed.weighting.addSemester': "إضافة أسدس",
    'reg.ed.weighting.bonusHint': "تُحتسب فقط النقط فوق عتبة العنصر الإضافي، وتضاف قبل القسمة كما في كلاسي السابق.",
    'reg.ed.absences.hint': "افتراضيا، يتبع كل غياب معالجة المرجع. غيروها هنا لهذا النطاق فقط.",
    'reg.ed.absences.referential': "المرجع: {value}",
    'reg.ed.absences.asReferential': "كما في المرجع",
    'reg.ed.thresholds.catalog': "عتبة كل عنصر في البرنامج",
    'reg.ed.thresholds.catalogHint': "10 افتراضيا، ويمكن لكل عنصر أن تكون له عتبته",
    'reg.ed.thresholds.level': "عتبة لكل مستوى",
    'reg.ed.thresholds.levelHint': "نفسها لجميع عناصر المستوى",
    'reg.ed.thresholds.retakeHint': "تحت عتبة الاستدراك، يعتبر العنصر غير مستوفى مباشرة ولا يحق له الاستدراك. تسود عتبة الاستدراك الخاصة بالعنصر.",
    'reg.ed.level': "المستوى",
    'reg.ed.noThreshold': "بدون عتبة",
    'reg.ed.status': "الوضع",
    'reg.ed.when': "متى",
    'reg.ed.compensation.between': "التعويض بين عناصر…",
    'reg.ed.compensation.submodule': "الوحدة الفرعية (موادها)",
    'reg.ed.compensation.module': "الوحدة (موادها ووحداتها الفرعية)",
    'reg.ed.compensation.semester': "الأسدس (وحداته)",
    'reg.ed.compensation.year': "السنة (أسدساها)",
    'reg.ed.compensation.hint': "يُستوفى العنصر تحت عتبته بالتعويض إذا بلغ العنصر الأب عتبته، وكان فوق الحد الأدنى وبدون نقطة إقصائية، الأفضل أولا وفي حدود العدد.",
    'reg.ed.compensation.capitalisation': "تبقى الوحدة المستوفاة مكتسبة إذا رسب الأسدس (الرسملة)",
    'reg.ed.retake.enabled': "العنصر غير المستوفى يمر إلى الاستدراك",
    'reg.ed.retake.averageHint': "كما في كلاسي السابق",
    'reg.ed.retake.wholeGroup': "إذا مرت مادة من وحدة فرعية إلى الاستدراك، تمر الوحدة الفرعية كلها",
    'reg.ed.priors.best': "استرجاع النقطة المكتسبة إن كانت أفضل (الأرصدة، التكرار)",
    'reg.ed.priors.none': "عدم استرجاع أي شيء",
    'reg.ed.rounding.evaluable': "تقريب نقطة المادة",
    'reg.ed.rounding.aggregate': "تقريب الوحدات والأسدسات والسنوات",
    'reg.ed.rounding.hint': "يتم الحساب بالأعداد الصحيحة إلى الجزء من المليون؛ ويطبق التقريب حيث يطلب ثم عند العرض.",
    'reg.ed.decisions.mentions': "منح ميزة (سلم المجال)",
    'reg.ed.decisions.kept': "الاحتفاظ بالميزة بعد الاستدراك",
    'reg.ed.decisions.removed': "حذف الميزة بعد الاستدراك",
    'reg.ed.decisions.rank': "احتساب الرتبة داخل التكوين",
    'reg.ed.decisions.hint': "ناجح إذا استوفي المجموع؛ ناجح بديون في الحدود؛ استدراك إذا مر عنصر إليه؛ راسب في غير ذلك.",
    'reg.sum.scale': "على {outOf}، {display}",
    'reg.sum.weighting': "{assessments}؛ نوع بدون نقط: {missing}؛ العناصر: {elements}؛ الأسدسان: {semesters}",
    'reg.sum.absences.referential': "كما في المرجع",
    'reg.sum.thresholds.catalog': "عتبات البرنامج",
    'reg.sum.thresholds.noEliminatory': "بدون نقطة إقصائية",
    'reg.sum.thresholds.eliminatory': "النقطة الإقصائية {value}: {effect}",
    'reg.sum.compensation.none': "بدون تعويض",
    'reg.sum.compensation.level': "{level}: الحد الأدنى {floor}، على الأكثر {max}",
    'reg.sum.retake': "{retained}، السقف {cap}",
    'reg.sum.retake.closed': "بدون استدراك",
    'reg.sum.rounding': "المواد: {evaluable}؛ المجاميع: {aggregate}؛ العرض: {display} أرقام عشرية",
    'reg.sum.decisions': "الديون: {debts}؛ الميزة: {mentions}؛ الرتبة: {rank}",
    'reg.effective.open': "القاعدة الفعلية",
    'reg.effective.title': "القاعدة الفعلية",
    'reg.effective.sub': "ما يطبق على تكوين أو قسم أو طالب، كتلة بكتلة، ومن أين يأتي",
    'reg.effective.inForce': "النسخة السارية",
    'reg.effective.noVersion': "لا توجد نسخة منشورة لهذه السنة.",
    'reg.effective.chooseTraining': "اختاروا تكوينا.",
    'reg.effective.student': "{name}: النظام {regime}، التسجيل {mode}، القسم (الأقسام) {classes}.",
    'reg.effective.locked': "مقفل",
    'reg.effective.conflict': "في تعارض مع {scopes}",
    'reg.effective.overridden': "استبعد بسبب القفل: {scopes}",
    'reg.effective.default': "القيمة الافتراضية لكلاسي",
    'reg.effective.conditional': "بشرط: حسب الطالب",
    'reg.explain.open': "عرض تفسير معدل",
    'reg.explain.sub': "{training} · {year} · تفسير المعدل وصولا إلى نقط التقييم",
    'reg.explain.noVersion': "لا تسري أي نسخة من النظام على هذه السنة.",
    'reg.explain.notEnrolled': "لا يوجد تسجيل نشط لتفسيره.",
    'reg.explain.noMark': "بدون نقطة",
    'reg.explain.weight': "الوزن {weight}",
    'reg.explain.threshold': "العتبة {threshold}",
    'reg.explain.bonus': "مكافأة",
    'reg.explain.retake': "الاستدراك: {after} تعوض {before}",
    'reg.explain.prior': "نقطة مكتسبة مسترجعة: {origin}",
    'reg.explain.rule': "القاعدة: {block} · {scope} · {profile}",
    'reg.explain.coef': "المعامل {coef}",
    'reg.explain.debts': "الديون: {list}",
    'reg.explain.footer': "{version} · المحرك {engine} · احتسب في {date}",
    'reg.sim.title': "محاكاة النسخة {number}",
    'reg.sim.sub': "تطبيق النسخة على النقط الفعلية ومقارنتها بأخرى طالبا بطالب. لا يحفظ أي شيء.",
    'reg.sim.compare': "المقارنة مع",
    'reg.sim.noCompare': "بدون مقارنة",
    'reg.sim.with': "مع {label} ({state})",
    'reg.sim.running': "جار الاحتساب…",
    'reg.sim.changes': "ما يتغير بين النسختين",
    'reg.sim.changesSub': "بلغة واضحة، نطاقا بنطاق.",
    'reg.sim.chooseTraining': "اختاروا تكوينا لرؤية الأثر على طلبته.",
    'reg.sim.students': "الطلبة المحاكون",
    'reg.sim.values': "المعدلات التي تتغير",
    'reg.sim.valuesNote': "إلى الجزء من المائة، في عنصر واحد على الأقل",
    'reg.sim.decisions': "القرارات التي تتغير",
    'reg.sim.decisionsNote': "{better} نحو الأفضل، {worse} نحو الأسوأ",
    'reg.sim.mentions': "الميزات التي تتغير",
    'reg.sim.before': "المعدل المقارن",
    'reg.sim.after': "مع النسخة {number}",
    'reg.sim.decisionBefore': "القرار المقارن",
    'reg.sim.decisionAfter': "القرار",
    'reg.sim.mention': "الميزة",
    'reg.sim.rank': "الرتبة {rank}",
    'reg.sim.note': "لا تحفظ المحاكاة أي شيء. لا تتغير النتيجة المنشورة إلا بإعادة احتساب صريحة بعد النشر، مع ترك أثر.",
    'reg.diff.none': "لا يوجد أي اختلاف في القواعد.",
    'reg.diff.added': "ملف جديد «{profile}»",
    'reg.diff.removed': "حذف الملف «{profile}»",
    'reg.diff.profile': "«{before}» يصبح «{after}»",
    'reg.approve.title': "المصادقة على النسخة {number} ونشرها",
    'reg.approve.sub': "النظام {year} · مصادقة بأربع أعين",
    'reg.approve.by': "طلبها {who} في {when}",
    'reg.approve.noBlocking': "لا توجد مراقبة مانعة",
    'reg.approve.tests': "حالات الاختبار: {passed} من {total}",
    'reg.approve.simulation': "عرض المحاكاة",
    'reg.approve.note': "بعد النشر لا تتغير النسخة، وتؤرشف النسخة السابقة للسنة.",
    'violation.reg.open_version': "توجد نسخة من هذه السنة قيد التحرير أو المصادقة.",
    'violation.reg.not_draft': "لا تعدل إلا المسودة؛ التصحيح ينشئ نسخة جديدة.",
    'violation.reg.four_eyes': "مصادقة بأربع أعين: يجب أن يقرر شخص غير من أرسل.",
    'violation.reg.blocking_checks': "تبقى مراقبات مانعة يجب تصحيحها.",
    'violation.reg.profile_in_use': "هذا الملف معين: احذفوا تعييناته أولا.",
    'violation.reg.profile_name_taken': "يوجد ملف بهذا الاسم في هذه النسخة.",
    'violation.reg.invalid': "قيمة غير صالحة.",
    'violation.reg.out_of_range': "قيمة خارج الحدود.",
    'violation.reg.precision': "رقمان عشريان على الأكثر.",
    'violation.reg.unknown_target': "الهدف غير موجود.",
    'violation.reg.type_weights_empty': "أعطوا وزنا لنوع تقييم واحد على الأقل.",
    'violation.reg.test_expectation': "نقطة أو وضع متوقع.",
    'violation.reg.year_closed': "هذه السنة مغلقة.",
    'reg.print': "طباعة",
    'reg.scope.training': "التكوين",
    'reg.v.blocks_compensation': "يُلغي كل تعويض داخل العنصر الأب",
    'reg.ed.thresholds.general': "العامة",
    'reg.ed.thresholds.generalLabel': "النقطة الإقصائية العامة",
    'reg.ed.thresholds.eliminatoryHint': "النقطة الإقصائية لكل مستوى تحل محل النقطة العامة لذلك المستوى. تحتها لا يُعوَّض العنصر.",
    'reg.ed.thresholds.exempt': "مواد الوحدة الفرعية معفاة من النقطة الإقصائية",
    'journal.action.regulation.created': "النظام الدراسي: إضافة",
    'journal.action.regulation.updated': "النظام الدراسي: تعديل",
    'journal.action.regulation.deleted': "النظام الدراسي: حذف",
    'journal.action.regulation.submitted': "إحالة النظام الدراسي على المصادقة",
    'journal.action.regulation.published': "نشر النظام الدراسي",
    'journal.action.regulation.rejected': "إرجاع النظام الدراسي إلى المسودة",
    'journal.action.regulation.frozen': "تجميد النظام الدراسي",
    'journal.action.deliberation.computed': "احتساب النتائج",
    'journal.action.deliberation.closed': "اختتام المداولة",
    'journal.action.deliberation.reopened': "إعادة فتح المداولة",
    'journal.target.regulation': "النظام الدراسي",
    'journal.target.regulation_profile': "ملف النظام الدراسي",
    'journal.target.deliberation': "مداولة",
    'delib.title': "المداولات",
    'delib.sub': "{year} · النتائج المحتسبة والمعتمدة، تكوينا بتكوين ودورة بدورة",
    'delib.kpi.trainings': "التكوينات",
    'delib.kpi.trainingsNote': "للتداول هذه السنة",
    'delib.kpi.closed': "مختتمة",
    'delib.kpi.closedNote': "نتائج معتمدة",
    'delib.kpi.open': "مفتوحة",
    'delib.kpi.openNote': "في انتظار الاختتام",
    'delib.kpi.none': "لم تُحتسب بعد",
    'delib.kpi.noneNote': "لا احتساب مسجل",
    'delib.field.year': "السنة",
    'delib.list.empty': "لا تكوين ضمن نطاقك هذه السنة.",
    'delib.list.note': "كل احتساب مسجل ومرقم ومتتبع: نسخة النظام ومن أطلقه ولماذا. المداولة المختتمة لا يعاد احتسابها، وإعادة فتحها تتطلب سببا.",
    'delib.col.training': "التكوين والدورة",
    'delib.col.state': "الحالة",
    'delib.col.lastRun': "آخر احتساب",
    'delib.col.enrolled': "الطلبة",
    'delib.col.rank': "الرتبة",
    'delib.col.number': "الرقم",
    'delib.col.student': "الطالب",
    'delib.col.year': "المعدل",
    'delib.col.decision': "القرار",
    'delib.col.bonus': "منحة اللجنة",
    'delib.col.mention': "الميزة",
    'delib.outcome.admitted': "ناجح",
    'delib.outcome.admitted_with_debts': "بديون",
    'delib.outcome.retake': "استدراك",
    'delib.outcome.not_admitted': "مؤجلون",
    'delib.state.open': "مفتوحة",
    'delib.state.closed': "مختتمة",
    'delib.state.none': "لم تُحتسب بعد",
    'delib.run.none': "لا شيء",
    'delib.run.number': "الاحتساب رقم {number}",
    'delib.run.legacy': "كلاسي السابق",
    'delib.session.first': "الدورة العادية",
    'delib.session.retake': "دورة الاستدراك",
    'delib.session.label': "الدورة",
    'delib.head.noRun': "{year} · لا احتساب مسجل بعد",
    'delib.head.run': "{year} · الاحتساب رقم {number} بتاريخ {at} من طرف {by} · النظام {regulation} · المحرك {engine}",
    'delib.action.history': "السجل",
    'delib.action.compute': "احتساب",
    'delib.action.recompute': "إعادة الاحتساب",
    'delib.action.close': "اختتام المداولة",
    'delib.action.reopen': "إعادة الفتح",
    'delib.action.bonus': "منحة",
    'delib.closed.note': "اختُتمت المداولة بتاريخ {at} من طرف {by}: النتائج معتمدة.",
    'delib.empty.compute': "لا احتساب لهذه الدورة. أطلق الاحتساب الأول.",
    'delib.empty.read': "لا احتساب لهذه الدورة.",
    'delib.todo.title': "ما يجب معالجته قبل الاختتام",
    'delib.todo.ready': "جاهزة للاختتام",
    'delib.todo.unvalidated': "{count} تقييم(ات) غير مصادق عليها",
    'delib.todo.pending': "{count} طالب(ة) بدون نتيجة",
    'delib.todo.near': "{count} طالب(ة) قرب العتبة",
    'delib.filter.label': "تصفية الطلبة",
    'delib.filter.all': "الكل",
    'delib.filter.review': "للفحص",
    'delib.filter.changed': "تغيرت منذ الاحتساب رقم {number}",
    'delib.filter.bonus': "منح اللجنة",
    'delib.tag.near': "قرب العتبة",
    'delib.tag.changed': "تغير",
    'delib.bonus.marker': "منحة اللجنة",
    'delib.legend': "رمز اللجنة يشير إلى طالب حصل على منحة. «للفحص»: معدل يقل عن العتبة بأقل من نصف نقطة، أو نتيجة ناقصة.",
    'delib.compute.title': "احتساب النتائج",
    'delib.compute.note': "يُضاف الاحتساب إلى السابقة بنسخة النظام السارية. تُحصى الفروق مع الاحتساب السابق وتُعلَّم.",
    'delib.field.reason': "السبب (اختياري)",
    'delib.field.reasonRequired': "السبب",
    'delib.field.comment': "ملاحظة في المحضر",
    'delib.close.title': "اختتام المداولة",
    'delib.close.validated': "جميع التقييمات مصادق عليها",
    'delib.close.pending': "{count} طالب(ة) بدون نتيجة: «في الانتظار» في المحضر",
    'delib.close.allResults': "لكل طالب نتيجة",
    'delib.close.bonuses': "{count} منحة(منح) للجنة، كلها معللة",
    'delib.close.counts': "{admitted} ناجح · {debts} بديون · {retake} للاستدراك · {failed} مؤجل · {pending} في الانتظار",
    'delib.close.note': "بعد الاختتام لا احتساب ولا منح؛ ويمكن إصدار المحضر وكشوف النقط والشهادات. تبقى إعادة الفتح ممكنة بسبب.",
    'delib.reopen.title': "إعادة فتح المداولة",
    'delib.reopen.note': "يُتتبَّع السبب؛ وكل احتساب لاحق سيتطلب سببا أيضا. تبقى الوثائق الصادرة في السجل.",
    'delib.bonus.title': "منحة اللجنة",
    'delib.bonus.note': "تتصرف اللجنة بالمنح: نقاط تضاف إلى معدل عنصر في حدود النظام. ويُستنتج القرار من الاحتساب الجديد.",
    'delib.bonus.on': "على",
    'delib.bonus.points': "النقاط",
    'delib.bonus.cap': "على الأكثر {cap} (النظام)",
    'delib.bonus.result': "القرار الناتج",
    'delib.bonus.element': "العنصر",
    'delib.bonus.before': "المحتسب",
    'delib.bonus.after': "مع المنحة",
    'delib.bonus.reasonHint': "يظهر في المحضر وفي السجل.",
    'delib.bonus.existing': "منح ممنوحة سابقا",
    'delib.bonus.remove': "سحب",
    'delib.bonus.save': "حفظ المنحة",
    'delib.kind.year': "السنة",
    'delib.kind.semester': "الأسدس",
    'delib.kind.module': "الوحدة",
    'delib.kind.submodule': "الوحدة الفرعية",
    'delib.kind.subject': "المادة",
    'delib.history.title': "سجل المداولة",
    'delib.history.sub': "{training} · {session} · {year}: كل احتساب وكل منحة وكل اختتام بالترتيب",
    'delib.history.empty': "لا مداولة لهذه الدورة.",
    'delib.history.compare': "ما تغير",
    'delib.history.between': "ما تغير بين الاحتساب رقم {from} والاحتساب رقم {to}",
    'delib.history.pair': "رقم {from} ورقم {to}",
    'delib.history.run': "الاحتساب رقم {number}",
    'delib.history.single': "احتساب واحد: لا شيء للمقارنة.",
    'delib.history.same': "لم يتغير شيء.",
    'delib.history.summary': "تغيرت {values} نقطة و{statuses} وضعية و{decisions} قرار، لدى {students} طالب(ة).",
    'delib.history.note': "لا شيء يُمحى: الاحتساب لا يعوض السابق بل يُضاف إليه. ويذكر الأثر كل مرة نسخة النظام المستعملة وسبب إعادة الاحتساب.",
    'journal.action.deliberation.bonus_granted': "منح منحة اللجنة",
    'journal.action.deliberation.bonus_changed': "تعديل منحة اللجنة",
    'journal.action.deliberation.bonus_removed': "سحب منحة اللجنة",
    'reg.f.juryBonusCap': "منحة اللجنة، على الأكثر",
    'reg.ed.decisions.juryBonusHint': "تتصرف اللجنة بمنح على معدل عنصر في هذا الحد؛ فارغ: بلا سقف.",
    'doc.title': "الوثائق",
    'doc.sub': "{year} · كشوف النقط والمحاضر والشهادات الصادرة عن المداولات المختتمة",
    'doc.batches.title': "الوثائق الصادرة",
    'doc.batches.empty': "لا توجد وثائق صادرة لهذه السنة.",
    'doc.col.document': "الوثيقة",
    'doc.col.for': "لفائدة",
    'doc.col.count': "العدد",
    'doc.col.issued': "تاريخ الإصدار",
    'doc.col.reference': "المرجع",
    'doc.col.state': "الحالة",
    'doc.col.actions': "إجراءات",
    'doc.students': "{count} طالب",
    'doc.state.open': "مداولة مفتوحة",
    'doc.state.superseded': "مستبدل",
    'doc.state.issued': "صادر",
    'doc.state.published': "{count} على البوابة",
    'doc.action.toDeliberation': "عرض المداولة",
    'doc.noneClosed': "لا توجد مداولة مختتمة هذه السنة: تصدر الوثائق بعد الاختتام.",
    'doc.note': "كل وثيقة صادرة مرقمة ومسجلة في السجل (من، متى، لمن) ويمكن التحقق منها بمرجعها. إعادة فتح المداولة تجعل وثائقها مستبدلة.",
    'doc.action.preparing': "جارٍ التحضير…",
    'doc.action.zip': "أرشيف مضغوط",
    'doc.action.pdf': "PDF",
    'doc.action.excel': "Excel",
    'doc.action.publish': "النشر على البوابة",
    'doc.action.unpublish': "السحب من البوابة",
    'doc.action.producing': "جارٍ الإصدار…",
    'doc.action.produce': "إصدار",
    'doc.action.previewMinutes': "معاينة المحضر وخياراته",
    'doc.action.one': "إصدار لطالب واحد مع المعاينة",
    'doc.action.verify': "تحقق",
    'doc.notice.published': "تم نشر {count} وثيقة على البوابة.",
    'doc.notice.unpublished': "تم سحب {count} وثيقة من البوابة.",
    'doc.notice.issued': "{count} {type}: تم.",
    'doc.notice.one': "صدرت الوثيقة: مرقمة ومسجلة.",
    'doc.produce.title': "إصدار لمداولة مختتمة",
    'doc.field.deliberation': "المداولة",
    'doc.field.type': "الوثائق",
    'doc.field.for': "لفائدة",
    'doc.field.format': "الصيغة",
    'doc.field.publish': "النشر على البوابة: يحمّل كل طالب وثيقته",
    'doc.field.publishOne': "النشر على بوابة الطالب",
    'doc.field.reference': "المرجع",
    'doc.field.student': "الطالب",
    'doc.field.lang': "اللغة",
    'doc.field.sort': "الترتيب",
    'doc.for.deliberation': "المداولة",
    'doc.for.admitted': "الطلبة الناجحون ({count})",
    'doc.for.all': "جميع الطلبة ({count})",
    'doc.format.minutes': "PDF أفقي و Excel",
    'doc.format.zip': "ملف PDF لكل طالب في أرشيف مضغوط",
    'doc.type.transcript': "كشف النقط",
    'doc.type.minutes': "المحضر",
    'doc.type.certificate': "شهادة النجاح",
    'doc.types.transcript': "كشوف النقط",
    'doc.types.minutes': "المحضر",
    'doc.types.certificate': "شهادات النجاح",
    'doc.verify.title': "التحقق من وثيقة",
    'doc.verify.valid': "أصلية",
    'doc.verify.superseded': "مستبدلة",
    'doc.verify.altered': "غير مطابقة",
    'doc.verify.unknown': "مرجع غير معروف",
    'doc.verify.unknownBody': "لا توجد وثيقة بالمرجع {reference}. تحقق من الإدخال.",
    'doc.verify.body': "{type} الخاص بـ {student}، {training} {year}، {institution}؛ صدر في {date} من طرف {by} انطلاقا من الحساب رقم {run}.",
    'doc.verify.supersededBody': "استبدلت بتاريخ {date}: لم تعد صالحة.",
    'doc.verify.alteredBody': "النتائج التي تحملها لم تعد مطابقة للنتائج المعتمدة: أبلغ المؤسسة.",
    'doc.verify.publicTitle': "التحقق من وثيقة",
    'doc.verify.publicSub': "كشف نقط أو محضر أو شهادة: أدخل المرجع المطبوع تحت رمز QR.",
    'doc.sort.rank': "حسب الرتبة",
    'doc.sort.name': "حسب الاسم",
    'doc.flag.transcript.rank': "إظهار الرتبة",
    'doc.flag.transcript.credits': "إظهار الأرصدة",
    'doc.flag.transcript.details': "تفصيل المواد",
    'doc.flag.transcript.first': "تضمين الدورة العادية",
    'doc.flag.minutes.details': "تفصيل الوحدات (Excel: ورقة لكل فصل)",
    'doc.flag.minutes.bonuses': "منح اللجنة وأسبابها",
    'doc.flag.certificate.mention': "إظهار الميزة",
    'doc.preview': "معاينة الوثيقة",
    'doc.produceOne.transcript': "إصدار كشف النقط",
    'doc.produceOne.minutes': "إصدار المحضر",
    'doc.produceOne.certificate': "إصدار الشهادة",
    'doc.produceOne.transcriptNote': "يصدر كشف النقط عن مداولة مختتمة: يعرض النتائج المعتمدة بالضبط ويحمل مرجعا قابلا للتحقق.",
    'doc.produceOne.minutesNote': "يعرض المحضر شبكة المداولة المختتمة والمنح المعللة والموقعين.",
    'doc.produceOne.certificateNote': "فقط لطالب ناجح من مداولة مختتمة. كل شهادة مرقمة ومسجلة: من، متى، لمن.",
    'journal.action.document.issued': "إصدار وثائق",
    'journal.action.document.published': "نشر وثائق على البوابة",
    'journal.action.document.unpublished': "سحب وثائق من البوابة",
    'journal.action.document.settings_changed': "تعديل إعدادات الوثائق",
    'journal.target.document': "وثائق",
    'doc.verify.bodyMinutes': "{type}، {training} {year}، {institution}؛ صدر في {date} من طرف {by} انطلاقا من الحساب رقم {run}.",
    'scol.tab.file': "الملف",
    'scol.tab.marks': "النقط",
    'scol.tab.finance': "المالية",
    'scol.tab.history': "السجل",
    'trace.filter.kind': "نوع الحدث",
    'trace.filter.all': "الكل",
    'trace.filter.changes': "التعديلات",
    'trace.filter.accesses': "الاطلاعات",
    'trace.filter.table': "جزء الملف",
    'trace.filter.allTables': "الملف كاملا",
    'trace.filter.actor': "المنفذ",
    'trace.filter.allActors': "الجميع",
    'trace.empty': "لا شيء في السجل لهذه المرشحات.",
    'trace.access.student': "اطلع على ملف الطالب",
    'trace.access.marks': "اطلع على النقط",
    'trace.access.results': "اطلع على النتائج",
    'trace.access.documents': "فتح وثيقة",
    'trace.access.finance': "اطلع على الحساب",
    'trace.access.exports': "نزّل تصديرا",
    'trace.op.insert': "أنشأ",
    'trace.op.update': "عدّل",
    'trace.op.delete': "حذف",
    'trace.opShort.insert': "أنشئت",
    'trace.opShort.update': "عدّلت",
    'trace.opShort.delete': "حذفت",
    'trace.more': "{count} حقول إضافية",
    'trace.less': "تفاصيل أقل",
    'trace.older': "الأقدم",
    'trace.request.all': "كل ما كتبه هذا الإجراء",
    'trace.request.title': "ما كتبه هذا الإجراء",
    'trace.request.who': "من",
    'trace.request.when': "متى",
    'trace.request.where': "من أين",
    'trace.request.id': "الطلب",
    'trace.request.record': "الملف",
    'trace.request.what': "ماذا",
    'trace.request.operation': "العملية",
    'trace.request.change': "التغيير",
    'trace.request.note': "قد يكتب إجراء واحد عدة ملفات في آن واحد: تتقاسم الطلب نفسه وتُقرأ معا هنا.",
    'trace.aside.title': "ما يحفظه السجل",
    'trace.aside.changes': "كل تعديل، قبل وبعد، حتى خارج التطبيق: قاعدة البيانات تكتبه بنفسها.",
    'trace.aside.accesses': "الاطلاع على الملف والنقط والنتائج والوثائق، إذا كانت المؤسسة تتتبعها.",
    'trace.aside.retention': "خمس سنوات، ثم يُحذف السجل.",
    'trace.aside.locked': "لا أحد يستطيع تعديل السجل أو محوه، ولا حتى مسؤول التطبيق.",
    'trace.actor.console': "وحدة تحكم الخادم",
    'trace.actor.sql': "خارج التطبيق (SQL)",
    'trace.actor.unknown': "مجهول",
    'trace.source.api': "من التطبيق",
    'trace.source.console': "من وحدة تحكم الخادم",
    'trace.source.sql': "خارج التطبيق",
    'trace.value.yes': "نعم",
    'trace.value.no': "لا",
    'trace.console.title': "التتبع",
    'trace.console.sub': "{institution} · ما تحفظه المنصة من كل إجراء",
    'trace.console.saved': "تم حفظ الإعداد.",
    'trace.console.reads': "الاطلاعات",
    'trace.console.readsSub': "قابل للضبط لهذه المؤسسة",
    'trace.console.readsToggle': "تتبع الاطلاع على البيانات الحساسة",
    'trace.console.readsHint': "من اطلع على ماذا ومتى. حسب المؤسسة، ليس ذلك مطلوبا دائما.",
    'trace.console.writes': "التعديلات",
    'trace.console.writesBody': "تُتتبع دائما. كل إنشاء أو تعديل أو حذف تكتبه قاعدة البيانات بنفسها، قبل وبعد، حتى خارج التطبيق. هذا ليس إعدادا.",
    'trace.console.retention': "مدة الحفظ",
    'trace.console.duration': "المدة",
    'trace.console.years': "{count} سنوات",
    'trace.console.durationHint': "حد أدنى تفرضه قاعدة البيانات",
    'trace.console.oldest': "أقدم إدخال",
    'trace.console.oldestHint': "منذ التشغيل",
    'trace.console.purge': "الحذف",
    'trace.console.monthly': "شهري",
    'trace.console.purgeHint': "في اليوم الأول من كل شهر",
    'trace.console.purgeNote': "لا يحذف التنظيف إلا ما تجاوز خمس سنوات. لا أحد يستطيع تقصير هذه المدة أو محو إدخال أحدث.",
    'trace.category.student': "ملف الطالب",
    'trace.category.studentHint': "الهوية والعناوين والاتصالات والتسجيلات",
    'trace.category.marks': "النقط",
    'trace.category.marksHint': "نقط الطالب، ورقة التقييم",
    'trace.category.results': "النتائج",
    'trace.category.resultsHint': "المداولات، نتائج الطالب",
    'trace.category.documents': "الوثائق",
    'trace.category.documentsHint': "كشوف النقط والمحاضر والشهادات المنزلة",
    'trace.category.finance': "المالية",
    'trace.category.financeHint': "حساب الطالب، الفواتير، الإيصالات",
    'trace.category.exports': "التصديرات",
    'trace.category.exportsHint': "أوراق Excel، الأرشيفات، السجلات المصدرة",
    'journal.action.trace.settings_changed': "تعديل إعداد التتبع",
    'journal.target.trace': "التتبع",
    'trace.table.person': "الملف",
    'trace.table.student': "الطالب",
    'trace.table.enrolment': "التسجيل",
    'trace.table.enrolment_element': "عنصر التسجيل",
    'trace.table.enrolment_event': "حدث التسجيل",
    'trace.table.class_member': "القسم",
    'trace.table.person_address': "العنوان",
    'trace.table.person_phone': "الهاتف",
    'trace.table.person_email': "البريد",
    'trace.table.contact': "جهة الاتصال",
    'trace.table.contact_relationship': "ولي أو جهة اتصال",
    'trace.table.student_diploma': "الشهادة",
    'trace.table.assessment_mark': "النقطة",
    'trace.table.mark_correction': "تصحيح النقطة",
    'trace.table.assessment': "التقييم",
    'trace.table.jury_bonus': "منحة اللجنة",
    'trace.table.issued_document': "وثيقة صادرة",
    'trace.table.invoice': "الفاتورة",
    'trace.table.invoice_line': "سطر الفاتورة",
    'trace.table.invoice_installment': "القسط",
    'trace.table.payment': "الدفعة",
    'trace.table.credit_note': "إشعار دائن",
    'trace.table.user_account': "الحساب",
    'trace.table.role_assignment': "إسناد الدور",
    'trace.table.deliberation': "المداولة",
    'trace.table.result_run': "حساب النتائج",
    'trace.table.admin_audit_log': "سجل الإجراءات",
    'trace.field.last_name': "الاسم العائلي",
    'trace.field.first_names': "الأسماء",
    'trace.field.sex': "الجنس",
    'trace.field.birth_date': "تاريخ الازدياد",
    'trace.field.national_id_number': "رقم البطاقة الوطنية",
    'trace.field.passport_number': "رقم الجواز",
    'trace.field.occupation': "المهنة",
    'trace.field.enrolment_number': "رقم الطالب",
    'trace.field.massar_code': "رمز مسار",
    'trace.field.national_code': "الرمز الوطني",
    'trace.field.street': "الشارع",
    'trace.field.city': "المدينة",
    'trace.field.postal_code': "الرمز البريدي",
    'trace.field.country': "البلد",
    'trace.field.number': "الرقم",
    'trace.field.address': "العنوان",
    'trace.field.kind': "النوع",
    'trace.field.training_id': "التكوين",
    'trace.field.academic_year_id': "السنة",
    'trace.field.class_group_id': "القسم",
    'trace.field.track_id': "المسار",
    'trace.field.mode': "الصيغة",
    'trace.field.regime': "النظام",
    'trace.field.state': "الحالة",
    'trace.field.scholarship': "منحة",
    'trace.field.repeating': "مكرر",
    'trace.field.enrolled_on': "سجل في",
    'trace.field.assigned_on': "عيّن في",
    'trace.field.value': "النقطة",
    'trace.field.bonus': "إضافة",
    'trace.field.entered_at': "أدخلت في",
    'trace.field.absence_type_id': "الغياب",
    'trace.field.assessment_id': "التقييم",
    'trace.field.entered_by_id': "أدخلها",
    'trace.field.value_before': "النقطة السابقة",
    'trace.field.value_after': "النقطة الجديدة",
    'trace.field.reason': "السبب",
    'trace.field.points': "النقط",
    'trace.field.element_id': "العنصر",
    'trace.field.label': "التسمية",
    'trace.field.name': "الاسم",
    'trace.field.email': "البريد",
    'trace.field.display_name': "الاسم المعروض",
    'trace.field.password_hash': "كلمة المرور",
    'trace.field.amount': "المبلغ",
    'trace.field.reference': "المرجع",
    'trace.field.published_at': "نشر في",
    'trace.field.superseded_at': "استبدل في",
    'trace.field.school': "المؤسسة",
    'trace.field.year': "السنة",
    'trace.field.average': "المعدل",
    'trace.field.mention': "الميزة",
    'trace.field.integrated_on': "التحق في",
    'trace.field.person_id': "الشخص",
    'trace.field.student_id': "الطالب",
    'trace.value.personal': "شخصي",
    'trace.value.professional': "مهني",
    'trace.value.home': "المنزل",
    'trace.value.active': "نشط",
    'trace.value.abandoned': "متخلى عنه",
    'trace.value.M': "ذكر",
    'trace.value.F': "أنثى",
    'trace.value.***': "(مخفي)",
    'asm.field.counted': "احتسابها في المعدل",
    'asm.field.countedHint': "ألغِ التحديد لتقييم تشخيصي أو امتحان تجريبي: تبقى النقط في الورقة والكشف بعلامة «غير محتسبة» ولا تدخل في أي معدل أو قرار.",
    'asm.notCounted': "غير محتسبة",
    'asm.notCountedHint': "لا تدخل نقطها في أي معدل أو قرار.",
    'doc.settings.title': "إعدادات الوثائق",
    'doc.settings.sub': "{institution} · الترويسة والمكان والموقعون على كل وثيقة",
    'doc.settings.saved': "تم الحفظ. تنطبق على الوثائق القادمة.",
    'doc.settings.invalid': "تحقق من الحقول: لكل موقع صفة.",
    'doc.settings.head': "الترويسة والمكان",
    'doc.settings.tagline': "عبارة تحت الاسم",
    'doc.settings.taglineHint': "تطبع تحت اسم المؤسسة",
    'doc.settings.place': "مكان التوقيع",
    'doc.settings.placeHint': "«حرر بطنجة في …»",
    'doc.settings.hint.minutes': "بترتيب التوقيع؛ الصفة وحدها تترك مكانا للتوقيع اليدوي",
    'doc.settings.hint.transcript': "دون موقع: «عن الإدارة» واسم من يصدر",
    'doc.settings.hint.certificate': "صفة الموقع الأول تفتتح الجملة",
    'doc.settings.default.minutes': "لا موقع: سيحمل المحضر أربع صفات افتراضية.",
    'doc.settings.default.transcript': "لا موقع: «عن الإدارة» واسم من يصدر.",
    'doc.settings.default.certificate': "لا موقع: «إدارة المؤسسة … تشهد أن».",
    'doc.settings.titleField': "الصفة",
    'doc.settings.nameField': "الاسم (اختياري)",
    'doc.settings.up': "إلى الأعلى",
    'doc.settings.down': "إلى الأسفل",
    'doc.settings.add': "إضافة موقع",
    'doc.settings.preview': "معاينة أسفل الصفحة",
    'doc.settings.signature': "التوقيع",
    'doc.settings.note': "تنسخ الإعدادات في كل وثيقة عند إصدارها: تغييرها لا يعدل أي وثيقة سابقة.",
    'trace.group': "{count} × {what}",
    'trace.detail': "عرض التفاصيل",
    'trace.drawer.title': "السجل",
    'trace.drawer.assessment': "{label} · نقطه والمصادقة والتصحيحات",
    'trace.drawer.invoice': "الفاتورة {number} · أسطرها وأقساطها ومدفوعاتها",
    'trace.drawer.account': "{name} · الحساب والأدوار",
    'trace.drawer.open': "السجل",
    'trace.field.validated_at': "صودق عليها في",
    'trace.field.validated_by_id': "صادق عليها",
    'trace.field.validated_by_name': "صادق عليها",
    'trace.field.corrected_at': "صححت في",
    'trace.field.corrected_by_name': "صححها",
    'trace.field.mark_id': "النقطة",
    'trace.field.counted': "محتسبة في المعدل",
    'trace.field.retake': "استدراكي",
    'trace.field.coefficient': "المعامل",
    'trace.field.out_of': "على",
    'trace.field.held_on': "التاريخ",
    'trace.field.subject_id': "المادة",
    'trace.field.type_id': "النوع",
    'trace.field.nature': "الطبيعة",
    'trace.field.starts_at': "البداية",
    'trace.field.ends_at': "النهاية",
    'trace.field.issued_on': "صدرت في",
    'trace.field.received_on': "استلم في",
    'trace.field.method': "الطريقة",
    'trace.field.due_on': "الاستحقاق",
    'trace.field.active': "نشط",
    'trace.field.role_id': "الدور",
    'trace.field.locale': "اللغة",
    'trace.field.population': "الفئة",
    'trace.table.assignment_product': "النطاق: تكوين",
    'trace.table.assignment_domain': "النطاق: مجال",
    'trace.table.assignment_cycle': "النطاق: سلك",
    'trace.table.user_identity': "هوية الدخول",
    'trace.table.two_factor_recovery_code': "رمز الاسترداد",
    'trace.table.assessment_class': "قسم مستدعى",
    'trace.table.assessment_responsible': "مسؤول التقييم",
    'trace.table.absence_justification': "مبرر الغياب",
    'trace.table.absence_type': "نوع الغياب",
    'trace.table.academic_year': "السنة الجامعية",
    'trace.table.account_invitation': "دعوة إلى الحساب",
    'trace.table.assessment_type': "نوع التقييم",
    'trace.table.assignment_center': "النطاق: المركز",
    'trace.table.attendance_record': "سجل الحضور",
    'trace.table.attendance_waiver': "إعفاء من الحضور",
    'trace.table.calendar_period': "فترة التقويم",
    'trace.table.chat_message': "رسالة محادثة",
    'trace.table.class_group': "القسم",
    'trace.table.communication_attachment': "مرفق",
    'trace.table.communication_settings': "إعدادات التواصل",
    'trace.table.contact_type': "نوع جهة الاتصال",
    'trace.table.conversation': "محادثة",
    'trace.table.conversation_member': "مشارك في المحادثة",
    'trace.table.department': "الشعبة",
    'trace.table.diploma': "الشهادة",
    'trace.table.diploma_option': "تخصص الشهادة",
    'trace.table.disciplinary_field': "المجال التخصصي",
    'trace.table.discount': "التخفيض",
    'trace.table.discount_service': "خدمة مشمولة بالتخفيض",
    'trace.table.document_settings': "إعدادات الوثائق",
    'trace.table.document_signatory': "الموقّع",
    'trace.table.employee': "الموظف",
    'trace.table.fee_service': "الخدمة",
    'trace.table.fee_service_cycle': "الخدمة: السلك",
    'trace.table.fee_service_level': "الخدمة: المستوى",
    'trace.table.finance_archive_credit': "إشعار دائن مؤرشف",
    'trace.table.finance_archive_invoice': "فاتورة مؤرشفة",
    'trace.table.finance_archive_line': "سطر فاتورة مؤرشف",
    'trace.table.finance_archive_payment': "دفعة مؤرشفة",
    'trace.table.finance_settings': "إعدادات الفوترة",
    'trace.table.identity_provider': "مزوّد الهوية",
    'trace.table.institution': "المؤسسة",
    'trace.table.letter_grade': "التقدير بالحرف",
    'trace.table.letter_scale': "سلّم التقديرات بالحروف",
    'trace.table.login_policy': "سياسة تسجيل الدخول",
    'trace.table.mention_band': "شريحة الميزة",
    'trace.table.mention_scale': "سلّم الميزات",
    'trace.table.menu_item': "عنصر القائمة",
    'trace.table.menu_item_translation': "ترجمة عنصر القائمة",
    'trace.table.notification': "إشعار",
    'trace.table.notification_recipient': "مستلم الإشعار",
    'trace.table.notification_reply': "رد على إشعار",
    'trace.table.notification_type': "نوع الإشعار",
    'trace.table.organisation': "الهيئة",
    'trace.table.pay_adjustment': "تعديل الأجر",
    'trace.table.pay_statement': "بيان الأجر",
    'trace.table.payment_schedule': "جدول الأقساط",
    'trace.table.payment_schedule_line': "سطر جدول الأقساط",
    'trace.table.payroll_settings': "إعدادات الأجور",
    'trace.table.permission': "الصلاحية",
    'trace.table.permission_translation': "ترجمة الصلاحية",
    'trace.table.planning_event': "حصة",
    'trace.table.planning_event_class': "قسم الحصة",
    'trace.table.planning_event_room': "قاعة الحصة",
    'trace.table.planning_event_student': "طالب الحصة",
    'trace.table.planning_event_teacher': "أستاذ الحصة",
    'trace.table.product': "التكوين أو العنصر",
    'trace.table.product_equivalence': "المعادلة",
    'trace.table.product_hours': "الحجم الساعي",
    'trace.table.product_price': "السعر",
    'trace.table.product_responsible': "مسؤول العنصر",
    'trace.table.regulation_assignment': "إسناد النظام",
    'trace.table.regulation_profile': "ملف النظام",
    'trace.table.regulation_test_case': "حالة اختبار النظام",
    'trace.table.regulation_version': "إصدار النظام",
    'trace.table.relations_agreement': "الاتفاقية",
    'trace.table.relations_agreement_commitment': "التزام الاتفاقية",
    'trace.table.relations_agreement_training': "تكوين الاتفاقية",
    'trace.table.relations_agreement_type': "نوع الاتفاقية",
    'trace.table.relations_alumni_position': "وضعية الخريج",
    'trace.table.relations_contact': "جهة اتصال الهيئة",
    'trace.table.relations_contact_function': "وظيفة جهة الاتصال",
    'trace.table.relations_exchange': "تواصل",
    'trace.table.relations_exchange_account': "مشارك في التواصل",
    'trace.table.relations_exchange_contact': "جهة اتصال التواصل",
    'trace.table.relations_org_type': "نوع الهيئة",
    'trace.table.relations_profile': "ملف العلاقة",
    'trace.table.relations_prospect': "استقطاب",
    'trace.table.relations_prospect_stage': "مرحلة الاستقطاب",
    'trace.table.relations_prospect_step': "انتقال مرحلة",
    'trace.table.relations_sector': "قطاع النشاط",
    'trace.table.relations_settings': "إعدادات العلاقات",
    'trace.table.relations_survey_token': "رابط الاستبيان",
    'trace.table.responsible_type': "نوع المسؤول",
    'trace.table.role': "الدور",
    'trace.table.role_permission': "صلاحية الدور",
    'trace.table.role_translation': "ترجمة الدور",
    'trace.table.room': "القاعة",
    'trace.table.session_series': "سلسلة الحصص",
    'trace.table.session_series_class': "قسم السلسلة",
    'trace.table.session_series_teacher': "أستاذ السلسلة",
    'trace.table.structure_level': "مستوى البنية",
    'trace.table.structure_template': "نموذج البنية",
    'trace.table.student_numbering': "ترقيم الطلبة",
    'trace.table.tax': "الضريبة",
    'trace.table.teacher': "الأستاذ",
    'trace.table.teacher_category': "فئة الأستاذ",
    'trace.table.teacher_contract': "عقد الأستاذ",
    'trace.table.teacher_contract_rate': "سعر العقد",
    'trace.table.teacher_contract_type': "نوع العقد",
    'trace.table.teacher_diploma': "شهادة الأستاذ",
    'trace.table.teacher_disciplinary_field': "مجال تخصص الأستاذ",
    'trace.table.teacher_objective': "الهدف الساعي",
    'trace.table.teaching_type': "نوع التدريس",
    'trace.table.trace_settings': "إعدادات التتبع",
    'trace.table.training_center': "مركز التكوين",
    'trace.table.training_cycle': "سلك التكوين",
    'trace.table.training_domain': "مجال التكوين",
    'trace.table.training_level': "مستوى التكوين",
    'trace.table.training_session': "دورة التكوين",
    'trace.table.training_track': "المسار",
    'trace.table.training_track_element': "عنصر المسار",
    'trace.table.venue': "الموقع",
    'trace.field.abbreviation': "الاختصار",
    'trace.field.absence_after': "الغياب الجديد",
    'trace.field.absence_before': "الغياب السابق",
    'trace.field.accepted_at': "قُبلت في",
    'trace.field.account_id': "الحساب",
    'trace.field.acronym': "المختصر",
    'trace.field.added_at': "أضيفت في",
    'trace.field.added_by_id': "أضافها",
    'trace.field.agreement_id': "الاتفاقية",
    'trace.field.agreement_window_days': "مهلة التنبيه بانتهاء الاتفاقيات (أيام)",
    'trace.field.alert_key': "مفتاح التنبيه",
    'trace.field.alert_target': "هدف التنبيه",
    'trace.field.allocation': "التوزيع",
    'trace.field.allowed_domains': "النطاقات المسموح بها",
    'trace.field.amount_excluding_tax': "المبلغ دون احتساب الضريبة",
    'trace.field.amount_gross': "المبلغ الإجمالي",
    'trace.field.amount_net': "المبلغ الصافي",
    'trace.field.answered_at': "أجيب في",
    'trace.field.archived_at': "أرشف في",
    'trace.field.at': "التاريخ",
    'trace.field.attachments_max_mb': "الحجم الأقصى للمرفقات (ميغابايت)",
    'trace.field.author_id': "المؤلف",
    'trace.field.author_name': "المؤلف",
    'trace.field.auto_provision': "إنشاء الحسابات تلقائيا",
    'trace.field.avatar_mime': "صيغة الصورة",
    'trace.field.avatar_version': "إصدار الصورة",
    'trace.field.bank': "البنك",
    'trace.field.based_on_id': "مبني على",
    'trace.field.batch_id': "الدفعة",
    'trace.field.blocking': "مانع",
    'trace.field.blocks': "الكتل",
    'trace.field.body': "المحتوى",
    'trace.field.business_id': "المعرّف الجبائي",
    'trace.field.by_id': "بواسطة",
    'trace.field.by_name': "بواسطة",
    'trace.field.can_deliberate': "يمكنه المداولة",
    'trace.field.can_enter_marks': "يمكنه إدخال النقاط",
    'trace.field.can_validate_marks': "يمكنه المصادقة على النقاط",
    'trace.field.cancel_reason': "سبب الإلغاء",
    'trace.field.cancelled': "ملغى",
    'trace.field.cancelled_at': "أُلغي في",
    'trace.field.cancelled_by_id': "ألغاه",
    'trace.field.capacity': "السعة",
    'trace.field.capacity_max': "السعة القصوى",
    'trace.field.capacity_min': "السعة الدنيا",
    'trace.field.categories': "الفئات",
    'trace.field.category_id': "الفئة",
    'trace.field.changes': "التغييرات",
    'trace.field.chat_email_digest': "ملخص المحادثات عبر البريد",
    'trace.field.class_groups': "الأقسام",
    'trace.field.client_id': "معرّف العميل",
    'trace.field.client_secret_encrypted': "السر الخاص بالعميل",
    'trace.field.closed_at': "أُغلق في",
    'trace.field.closed_by_id': "أغلقه",
    'trace.field.closed_by_name': "أغلقه",
    'trace.field.closed_on': "أُغلقت في",
    'trace.field.code': "الرمز",
    'trace.field.code_hash': "الرمز",
    'trace.field.cohort_rule': "قاعدة الفوج",
    'trace.field.color': "اللون",
    'trace.field.comment': "تعليق",
    'trace.field.compensation_rule': "قاعدة التعويض",
    'trace.field.computed_at': "حُسب في",
    'trace.field.computed_by_id': "حسبه",
    'trace.field.computed_by_name': "حسبه",
    'trace.field.contact_id': "جهة الاتصال",
    'trace.field.contact_type_id': "نوع جهة الاتصال",
    'trace.field.content': "المحتوى",
    'trace.field.contract_id': "العقد",
    'trace.field.conversation_id': "المحادثة",
    'trace.field.copied_from': "منسوخ من",
    'trace.field.corrected_by_id': "صححها",
    'trace.field.counter_reset': "إعادة تعيين العداد",
    'trace.field.counterpart_id': "المقابل",
    'trace.field.created_by_id': "أنشأه",
    'trace.field.created_by_name': "أنشأه",
    'trace.field.credit': "رصيد دائن",
    'trace.field.credit_note_pattern': "نمط رقم الإشعار الدائن",
    'trace.field.credits': "الأرصدة",
    'trace.field.credits_earned': "اكتساب الأرصدة",
    'trace.field.criteria': "المعايير",
    'trace.field.currency': "العملة",
    'trace.field.current': "الحالية",
    'trace.field.current_run_id': "الحساب المعتمد",
    'trace.field.customized': "مخصص",
    'trace.field.cycle_average_rule': "قاعدة معدل السلك",
    'trace.field.cycle_id': "السلك",
    'trace.field.date_corrected': "التاريخ المصحح",
    'trace.field.decided_at': "تقرر في",
    'trace.field.decided_by_id': "قرره",
    'trace.field.decided_by_name': "قرره",
    'trace.field.decimals': "المنازل العشرية",
    'trace.field.decision_comment': "تعليق على القرار",
    'trace.field.default_compensation': "التعويض الافتراضي",
    'trace.field.default_locale': "اللغة الافتراضية",
    'trace.field.delegate_id': "المندوب",
    'trace.field.deliberation_id': "المداولة",
    'trace.field.department_id': "الشعبة",
    'trace.field.description': "الوصف",
    'trace.field.digest': "البصمة",
    'trace.field.digested_at': "أرسل الملخص في",
    'trace.field.diploma_id': "الشهادة",
    'trace.field.diploma_option_id': "تخصص الشهادة",
    'trace.field.disciplinary_field_id': "المجال التخصصي",
    'trace.field.discount_amount': "مبلغ التخفيض",
    'trace.field.discount_id': "التخفيض",
    'trace.field.discount_rate': "نسبة التخفيض",
    'trace.field.discount_scope': "نطاق التخفيض",
    'trace.field.document_name': "الوثيقة",
    'trace.field.document_size': "حجم الوثيقة",
    'trace.field.document_type': "نوع الوثيقة",
    'trace.field.domain_id': "المجال",
    'trace.field.drawer': "الساحب",
    'trace.field.due_day': "يوم الاستحقاق",
    'trace.field.due_kind': "نوع الاستحقاق",
    'trace.field.due_month': "شهر الاستحقاق",
    'trace.field.duration_years': "المدة (بالسنوات)",
    'trace.field.effective_from': "ساري المفعول ابتداء من",
    'trace.field.email_at_provider': "البريد لدى المزوّد",
    'trace.field.email_canonical': "البريد الإلكتروني",
    'trace.field.email_default': "البريد افتراضيا",
    'trace.field.email_failed': "فشل البريد",
    'trace.field.emailed_at': "أرسل بالبريد في",
    'trace.field.employee_number': "رقم الموظف",
    'trace.field.enabled': "مفعّل",
    'trace.field.enabled_at': "فُعّل في",
    'trace.field.enabled_by_id': "فعّله",
    'trace.field.enabled_locales': "اللغات المفعّلة",
    'trace.field.ends_on': "النهاية",
    'trace.field.engine_version': "إصدار المحرك",
    'trace.field.enrollable': "مفتوحة للتسجيل",
    'trace.field.enrolment_id': "التسجيل",
    'trace.field.event_id': "الحصة",
    'trace.field.every_weeks': "كل (أسابيع)",
    'trace.field.exchange_id': "التواصل",
    'trace.field.expected_status': "الوضعية المنتظرة",
    'trace.field.expected_value': "القيمة المنتظرة",
    'trace.field.expires_at': "ينتهي في",
    'trace.field.extra_cap_hours': "سقف الساعات الإضافية",
    'trace.field.extra_minutes': "الدقائق الإضافية",
    'trace.field.extra_rate_gross': "السعر الإضافي الإجمالي",
    'trace.field.extra_rate_net': "السعر الإضافي الصافي",
    'trace.field.failed_attempts': "المحاولات الفاشلة",
    'trace.field.fax': "الفاكس",
    'trace.field.fee_service_id': "الخدمة",
    'trace.field.first_on': "الحصة الأولى",
    'trace.field.footer': "تذييل الصفحة",
    'trace.field.four_eyes': "المصادقة المزدوجة",
    'trace.field.from_student': "من الطالب",
    'trace.field.frozen_at': "جُمّدت في",
    'trace.field.function_id': "الوظيفة",
    'trace.field.given_by_id': "قدمها",
    'trace.field.grace_days': "مهلة السماح (أيام)",
    'trace.field.grade': "الدرجة",
    'trace.field.graduation_year': "سنة التخرج",
    'trace.field.granted_at': "مُنح في",
    'trace.field.granted_by_id': "منحه",
    'trace.field.granted_by_name': "منحه",
    'trace.field.granted_on': "مُنح في",
    'trace.field.gross': "الإجمالي",
    'trace.field.guests': "المدعوون",
    'trace.field.handled_at': "عولج في",
    'trace.field.happened_at': "التاريخ والساعة",
    'trace.field.happened_on': "التاريخ",
    'trace.field.has_graduated': "متخرج",
    'trace.field.has_outstanding_issue': "ملف معلق",
    'trace.field.has_special_needs': "احتياجات خاصة",
    'trace.field.hours': "الساعات",
    'trace.field.icon': "الأيقونة",
    'trace.field.identity_provider_id': "مزوّد الهوية",
    'trace.field.included_hours': "الساعات المشمولة",
    'trace.field.internship': "التدريب",
    'trace.field.invited_by_id': "دعاه",
    'trace.field.invoice_id': "الفاتورة",
    'trace.field.invoice_pattern': "نمط رقم الفاتورة",
    'trace.field.is_default': "افتراضي",
    'trace.field.is_employee': "موظف",
    'trace.field.is_officer': "ضابط",
    'trace.field.is_private': "خاص",
    'trace.field.issued_at': "صدر في",
    'trace.field.issued_by_id': "أصدره",
    'trace.field.issued_by_name': "أصدره",
    'trace.field.issuer': "المُصدر",
    'trace.field.job_title': "المنصب",
    'trace.field.joined_at': "انضم في",
    'trace.field.justification_id': "المبرر",
    'trace.field.kenrol_identifier': "معرّف Kenrol",
    'trace.field.last_exchange_at': "آخر تواصل",
    'trace.field.last_login_at': "آخر تسجيل دخول",
    'trace.field.last_message_at': "آخر رسالة",
    'trace.field.last_on': "الحصة الأخيرة",
    'trace.field.last_read_at': "آخر قراءة",
    'trace.field.last_reply_at': "آخر رد",
    'trace.field.last_used_at': "آخر استخدام",
    'trace.field.late_minutes': "دقائق التأخر",
    'trace.field.left_at': "غادر في",
    'trace.field.legacy': "من النظام السابق",
    'trace.field.legal_form': "الشكل القانوني",
    'trace.field.legal_name': "الاسم القانوني",
    'trace.field.letter': "الحرف",
    'trace.field.letter_scale_id': "سلّم التقديرات بالحروف",
    'trace.field.level_id': "المستوى",
    'trace.field.lines': "الأسطر",
    'trace.field.locked_until': "مقفل حتى",
    'trace.field.locks': "الأقفال",
    'trace.field.lost': "خاسرة",
    'trace.field.main': "رئيسي",
    'trace.field.mandatory': "إجباري",
    'trace.field.mark': "التسجيل",
    'trace.field.mark_value': "النقطة الممنوحة",
    'trace.field.max_hours': "الساعات القصوى",
    'trace.field.max_points': "النقاط القصوى",
    'trace.field.max_score': "النقطة القصوى",
    'trace.field.mention_scale_id': "سلّم الميزات",
    'trace.field.menu_item_id': "عنصر القائمة",
    'trace.field.mime': "الصيغة",
    'trace.field.min_hours': "الساعات الدنيا",
    'trace.field.min_score': "النقطة الدنيا",
    'trace.field.minutes': "الدقائق",
    'trace.field.module': "الوحدة",
    'trace.field.month': "الشهر",
    'trace.field.monthly_gross': "الشهري الإجمالي",
    'trace.field.monthly_net': "الشهري الصافي",
    'trace.field.moved_from': "نُقلت من",
    'trace.field.must_change_password': "يجب تغيير كلمة المرور",
    'trace.field.muted': "مكتوم",
    'trace.field.national_id_issued_on': "صدرت البطاقة الوطنية في",
    'trace.field.net': "الصافي",
    'trace.field.notation': "التنقيط",
    'trace.field.note': "ملاحظة",
    'trace.field.notes': "ملاحظات",
    'trace.field.notice_months': "مهلة الإشعار (أشهر)",
    'trace.field.notification_id': "الإشعار",
    'trace.field.objective': "الهدف",
    'trace.field.objectives': "الأهداف",
    'trace.field.observations': "ملاحظات",
    'trace.field.obsolete': "متجاوز",
    'trace.field.occurred_on': "وقع في",
    'trace.field.online_allowed': "مسموح عن بعد",
    'trace.field.option_id': "التخصص",
    'trace.field.options': "الخيارات",
    'trace.field.organisation_id': "الهيئة",
    'trace.field.organisation_name': "الهيئة",
    'trace.field.origin': "المصدر",
    'trace.field.other_organisation_name': "هيئة أخرى",
    'trace.field.override_by_name': "تجاوز التعارض",
    'trace.field.override_reason': "سبب التجاوز",
    'trace.field.owner_id': "المسؤول",
    'trace.field.owner_kind': "مرتبط بـ",
    'trace.field.paid_by_name': "دفعه",
    'trace.field.paid_method': "طريقة الدفع",
    'trace.field.paid_on': "دُفع في",
    'trace.field.paid_reference': "مرجع الدفع",
    'trace.field.parent_id': "الأصل",
    'trace.field.partner_since': "شريك منذ",
    'trace.field.party': "الطرف",
    'trace.field.passing': "ناجح",
    'trace.field.passport_issued_on': "صدر جواز السفر في",
    'trace.field.password_allowed': "كلمة المرور مسموح بها",
    'trace.field.password_changed_at': "غُيّرت كلمة المرور في",
    'trace.field.pattern': "نمط الرقم",
    'trace.field.pay_mode': "طريقة الأجر",
    'trace.field.pay_replacements': "أداء أجر التعويضات",
    'trace.field.pay_surveillance': "أداء أجر الحراسة",
    'trace.field.payer_type': "نوع الدافع",
    'trace.field.payroll_number': "رقم الأجر",
    'trace.field.permanent': "دائم",
    'trace.field.permission_id': "الصلاحية",
    'trace.field.photo_path': "الصورة",
    'trace.field.place': "المكان",
    'trace.field.planned': "مبرمج",
    'trace.field.populations': "الفئات",
    'trace.field.price_module': "السعر حسب الوحدة",
    'trace.field.price_subject': "السعر حسب المادة",
    'trace.field.price_training': "السعر حسب التكوين",
    'trace.field.private_for_id': "خاص بـ",
    'trace.field.product_id': "التكوين أو العنصر",
    'trace.field.profile_id': "الملف",
    'trace.field.prospect_id': "الاستقطاب",
    'trace.field.prospect_idle_days': "مدة خمول الاستقطاب (أيام)",
    'trace.field.prospected_on': "استُقطبت في",
    'trace.field.quantity': "الكمية",
    'trace.field.rate': "النسبة",
    'trace.field.rate_gross': "السعر الإجمالي",
    'trace.field.rate_net': "السعر الصافي",
    'trace.field.read_at': "قُرئ في",
    'trace.field.reads_enabled': "سجل الاطلاع مفعّل",
    'trace.field.receipt_pattern': "نمط رقم الوصل",
    'trace.field.recipient_id': "المستلم",
    'trace.field.recipients_count': "عدد المستلمين",
    'trace.field.recorded_at': "سُجّل في",
    'trace.field.recorded_by_id': "سجّله",
    'trace.field.recorded_by_name': "سجّله",
    'trace.field.refund_pattern': "نمط رقم الاسترداد",
    'trace.field.regulation_version_id': "إصدار النظام",
    'trace.field.reminder_of_id': "تذكير بـ",
    'trace.field.renewal': "التجديد",
    'trace.field.renewed_from_id': "مجددة من",
    'trace.field.reopened': "أعيد فتحها",
    'trace.field.replies': "الردود",
    'trace.field.replies_allowed': "الردود مسموح بها",
    'trace.field.require_rolls': "المناداة إلزامية",
    'trace.field.required_permission': "الصلاحية المطلوبة",
    'trace.field.requires_reason': "السبب إلزامي",
    'trace.field.reserved': "محجوز",
    'trace.field.resit_threshold': "عتبة الاستدراك",
    'trace.field.restricted': "مقيد",
    'trace.field.resulting_account_id': "الحساب الناتج",
    'trace.field.retained_session': "الدورة المعتمدة",
    'trace.field.retention_years': "مدة الحفظ (بالسنوات)",
    'trace.field.reverses_id': "يلغي",
    'trace.field.revoked_at': "أُلغي في",
    'trace.field.revoked_by_id': "ألغاه",
    'trace.field.role_assignment_id': "إسناد الدور",
    'trace.field.roll_call': "المناداة",
    'trace.field.roll_taken_at': "أجريت المناداة في",
    'trace.field.roll_taken_by_name': "أجرى المناداة",
    'trace.field.roll_validated_at': "صودق على المناداة في",
    'trace.field.roll_validated_by_name': "صادق على المناداة",
    'trace.field.room_id': "القاعة",
    'trace.field.route': "المسار",
    'trace.field.run_id': "الحساب",
    'trace.field.scale_id': "السلّم",
    'trace.field.schedule_id': "جدول الأقساط",
    'trace.field.scope': "النطاق",
    'trace.field.scope_kind': "نوع النطاق",
    'trace.field.sector_id': "قطاع النشاط",
    'trace.field.send_email': "الإرسال عبر البريد",
    'trace.field.sender_id': "المرسل",
    'trace.field.sender_name': "المرسل",
    'trace.field.sender_read_at': "قرأه المرسل في",
    'trace.field.senders': "المرسلون",
    'trace.field.sent_at': "أرسل في",
    'trace.field.series_id': "سلسلة الحصص",
    'trace.field.service_basis': "أساس الخدمة",
    'trace.field.service_id': "الخدمة",
    'trace.field.session': "الدورة",
    'trace.field.session_idle_minutes': "مدة خمول الجلسة (دقائق)",
    'trace.field.share': "الحصة",
    'trace.field.shared': "مشتركة",
    'trace.field.signed_on': "وُقّع في",
    'trace.field.size': "الحجم",
    'trace.field.source': "المصدر",
    'trace.field.source_id': "المصدر",
    'trace.field.special_discount': "تخفيض خاص",
    'trace.field.stackable': "قابل للجمع",
    'trace.field.staff_chat': "محادثة الموظفين",
    'trace.field.stage_id': "المرحلة",
    'trace.field.stage_label': "المرحلة",
    'trace.field.starts_on': "البداية",
    'trace.field.statement_pattern': "نمط رقم البيان",
    'trace.field.status': "الوضعية",
    'trace.field.status_reason': "سبب الوضعية",
    'trace.field.students': "الطلبة",
    'trace.field.subject': "الموضوع",
    'trace.field.submitted_at': "قُدّمت في",
    'trace.field.submitted_by_id': "قدّمها",
    'trace.field.submitted_by_name': "قدّمها",
    'trace.field.survey_auto': "استبيان تلقائي",
    'trace.field.suspended_by_id': "علّقها",
    'trace.field.suspends_lessons': "يعلّق الدروس",
    'trace.field.system': "النظام",
    'trace.field.tagline': "الشعار",
    'trace.field.target': "الهدف",
    'trace.field.target_id': "الهدف",
    'trace.field.targets': "الأهداف",
    'trace.field.targets_training': "يخص التكوين",
    'trace.field.taught': "مُدرّس",
    'trace.field.tax': "الضريبة",
    'trace.field.tax_amount': "مبلغ الضريبة",
    'trace.field.tax_id': "الضريبة",
    'trace.field.tax_rate': "نسبة الضريبة",
    'trace.field.teacher_chat': "محادثة الأساتذة",
    'trace.field.teacher_id': "الأستاذ",
    'trace.field.teacher_notify': "إشعارات الأساتذة",
    'trace.field.teachers_read': "اطلاع الأساتذة",
    'trace.field.teaching_type_id': "نوع التدريس",
    'trace.field.template_id': "النموذج",
    'trace.field.tenant_id': "معرّف المستأجر",
    'trace.field.termination_reason': "سبب الفسخ",
    'trace.field.title': "العنوان",
    'trace.field.token_hash': "الرمز المميز",
    'trace.field.tone': "اللون",
    'trace.field.totp_confirmed_at': "أُكّد تطبيق المصادقة في",
    'trace.field.totp_last_step': "آخر رمز مصادقة",
    'trace.field.totp_secret': "سر المصادقة",
    'trace.field.trade_register': "السجل التجاري",
    'trace.field.training_center_id': "مركز التكوين",
    'trace.field.training_cycle_id': "سلك التكوين",
    'trace.field.training_domain_id': "مجال التكوين",
    'trace.field.training_label': "التكوين",
    'trace.field.training_level_id': "مستوى التكوين",
    'trace.field.transcript': "كشف النقاط",
    'trace.field.treatment': "المعالجة",
    'trace.field.two_factor': "المصادقة الثنائية",
    'trace.field.two_factor_email': "بريد المصادقة الثنائية",
    'trace.field.type': "النوع",
    'trace.field.unit_amount': "السعر الوحدوي",
    'trace.field.unpaid_minutes': "الدقائق غير المؤداة",
    'trace.field.used_at': "استُخدم في",
    'trace.field.user_account_id': "الحساب",
    'trace.field.valid_from_id': "صالح ابتداء من",
    'trace.field.validation_threshold': "عتبة الاستيفاء",
    'trace.field.venue_id': "الموقع",
    'trace.field.version_id': "الإصدار",
    'trace.field.visible': "ظاهر",
    'trace.field.website': "الموقع الإلكتروني",
    'trace.field.weekday': "يوم الأسبوع",
    'trace.field.weighting': "الترجيح",
    'trace.field.won': "ناجحة",
    'trace.field.year_label': "السنة",
    'trace.value.draft': "مسودة",
    'trace.value.signed': "موقّع",
    'trace.value.closed': "مغلق",
    'trace.value.ended': "منتهٍ",
    'trace.value.terminated': "مفسوخ",
    'trace.value.hourly': "بالساعة",
    'trace.value.monthly': "شهري",
    'trace.value.weekly': "أسبوعي",
    'trace.value.yearly': "سنوي",
    'trace.value.none': "لا شيء",
    'trace.value.teaching': "تدريس",
    'trace.value.surveillance': "حراسة",
    'trace.value.bonus': "منحة",
    'trace.value.deduction': "اقتطاع",
    'trace.value.transfer': "تحويل",
    'trace.value.cheque': "شيك",
    'trace.value.cash': "نقدا",
    'trace.value.other': "آخر",
    'trace.value.validated': "مصادق عليه",
    'trace.value.paid': "مؤدّى",
    'trace.value.message': "رسالة",
    'trace.value.notification': "إشعار",
    'trace.value.reply': "رد",
    'trace.value.direct': "مباشرة",
    'trace.value.group': "مجموعة",
    'trace.value.sent': "مُرسل",
    'trace.value.optional': "اختياري",
    'trace.value.required': "إلزامي",
    'trace.value.staff': "الموظفون",
    'trace.value.staff_teachers': "الموظفون والأساتذة",
    'trace.value.direction': "الإدارة",
    'trace.value.info': "معلومة",
    'trace.value.warn': "تحذير",
    'trace.value.violet': "بنفسجي",
    'trace.value.danger': "خطر",
    'trace.value.muted': "خافت",
    'trace.value.ok': "نجاح",
    'trace.value.school': "المؤسسة",
    'trace.value.partner': "الشريك",
    'trace.value.call': "مكالمة",
    'trace.value.email': "بريد إلكتروني",
    'trace.value.meeting': "اجتماع",
    'trace.value.visit': "زيارة",
    'trace.value.event': "حدث",
    'trace.value.note': "ملاحظة",
    'trace.value.survey': "استبيان",
    'trace.value.legacy': "من النظام السابق",
    'trace.value.employed': "يعمل",
    'trace.value.studying': "يتابع الدراسة",
    'trace.value.seeking': "يبحث عن عمل",
    'trace.value.unknown': "غير معروف",
    'trace.value.prospect': "مُستقطَب",
    'trace.value.dormant': "خامل",
    'trace.value.renewal': "للتجديد",
    'trace.value.tacit': "ضمني",
    'trace.value.explicit': "صريح",
    'trace.value.lesson': "درس",
    'trace.value.assessment': "تقييم",
    'trace.value.seminar': "ندوة",
    'trace.value.conference': "محاضرة",
    'trace.value.outing': "خرجة",
    'trace.value.ceremony': "حفل",
    'trace.value.planned': "مبرمج",
    'trace.value.done': "منجز",
    'trace.value.not_done': "غير منجز",
    'trace.value.cancelled': "ملغى",
    'trace.value.holiday': "عطلة رسمية",
    'trace.value.vacation': "عطلة",
    'trace.value.exams': "امتحانات",
    'trace.value.no_class': "بدون دروس",
    'trace.value.absent': "غائب",
    'trace.value.late': "متأخر",
    'trace.value.medical': "طبي",
    'trace.value.family': "عائلي",
    'trace.value.administrative': "إداري",
    'trace.value.transport': "نقل",
    'plan.title': "استعمال الزمن",
    'plan.sub': "حصص الأسبوع وتقييماته وأنشطته",
    'plan.subManage': "حصص الأسبوع وتقييماته وأنشطته؛ انقر على خانة فارغة للبرمجة",
    'plan.mine.title': "استعمال زمني",
    'plan.mine.sub': "حصصك ومراقباتك وأنشطتك",
    'plan.view': "العرض",
    'plan.view.class': "القسم",
    'plan.view.teacher': "الأستاذ",
    'plan.view.room': "القاعة",
    'plan.view.student': "الطالب",
    'plan.mode': "طريقة العرض",
    'plan.mode.week': "أسبوع",
    'plan.mode.month': "شهر",
    'plan.mode.list': "قائمة",
    'plan.mode.series': "سلسلة أسبوعية",
    'plan.mode.lesson': "حصة واحدة",
    'plan.weekOf': "أسبوع {from} إلى {to} {year}",
    'plan.prevWeek': "الأسبوع السابق",
    'plan.nextWeek': "الأسبوع التالي",
    'plan.prevMonth': "الشهر السابق",
    'plan.nextMonth': "الشهر التالي",
    'plan.prevDay': "اليوم السابق",
    'plan.nextDay': "اليوم التالي",
    'plan.today': "اليوم",
    'plan.print': "طباعة",
    'plan.print.title': "استعمال الزمن",
    'plan.print.year': "السنة",
    'plan.print.cycle': "السلك",
    'plan.print.domain': "مجال التكوين",
    'plan.print.training': "التكوين",
    'plan.print.class': "القسم",
    'plan.print.venue': "المكان",
    'plan.chooseTarget': "اختر قسما أو أستاذا أو قاعة أو طالبا.",
    'plan.empty': "لا توجد حصة في هذه الفترة.",
    'plan.allDay': "بدون توقيت",
    'plan.unplaced': "للبرمجة",
    'plan.more': "+{count} أخرى",
    'plan.legend.exam': "تقييم",
    'plan.legend.event': "نشاط خاص",
    'plan.legend.cancelled': "ملغاة",
    'plan.col.when': "متى",
    'plan.col.what': "ماذا",
    'plan.col.who': "الأستاذ",
    'plan.col.where': "القاعة",
    'plan.col.status': "الحالة",
    'plan.status.planned': "مبرمجة",
    'plan.status.done': "منجزة",
    'plan.status.not_done': "غير منجزة",
    'plan.status.cancelled': "ملغاة",
    'plan.nature.seminar': "ندوة",
    'plan.nature.conference': "محاضرة",
    'plan.nature.outing': "خرجة",
    'plan.nature.meeting': "اجتماع",
    'plan.nature.ceremony': "حفل",
    'plan.nature.other': "أخرى",
    'plan.period.holiday': "عطلة رسمية",
    'plan.period.vacation': "عطلة",
    'plan.period.exams': "فترة الامتحانات",
    'plan.period.no_class': "فترة بدون دروس",
    'plan.period.other': "أخرى",
    'plan.period.blockedShort': "البرمجة مغلقة",
    'plan.conflict.title': "التعارضات: {count}",
    'plan.conflict.room': "القاعة {subject} محجوزة ({what})",
    'plan.conflict.teacher': "{subject} يدرّس {what}",
    'plan.conflict.class': "للقسم {subject} حصة {what}",
    'plan.conflict.blocked': "فترة مغلقة: {subject}",
    'plan.conflict.more': "و{count} أخرى",
    'plan.conflict.skip': "الإنشاء دون أيام التعارض ({count})",
    'plan.conflict.override': "البرمجة رغم التعارضات مع ذكر السبب",
    'plan.conflict.noOverride': "تجاوز التعارض يتطلب صلاحية خاصة.",
    'plan.conflict.reason': "سبب الاستثناء",
    'plan.field.training': "التكوين",
    'plan.field.chooseTraining': "اختر تكوينا",
    'plan.field.chooseStudent': "اختر طالبا",
    'plan.field.subject': "المادة",
    'plan.field.chooseSubject': "اختر مادة",
    'plan.field.type': "نوع التدريس",
    'plan.field.noType': "بدون نوع",
    'plan.field.room': "القاعة",
    'plan.field.rooms': "القاعات",
    'plan.field.noRoom': "بدون قاعة",
    'plan.field.classes': "لفائدة",
    'plan.field.noClass': "لا يوجد قسم في هذا التكوين.",
    'plan.field.teachers': "الأساتذة",
    'plan.field.addTeacher': "إضافة أستاذ",
    'plan.field.addOrganiser': "إضافة منظم",
    'plan.field.addSupervisor': "إضافة مراقب",
    'plan.field.every': "كل",
    'plan.field.start': "من",
    'plan.field.end': "إلى",
    'plan.field.rhythm': "الوتيرة",
    'plan.field.rhythmEvery': "كل {count} أسبوع",
    'plan.field.firstOn': "من",
    'plan.field.lastOn': "إلى",
    'plan.field.date': "يوم",
    'plan.field.nature': "النوع",
    'plan.field.label': "العنوان",
    'plan.field.participants': "المشاركون",
    'plan.field.guests': "متدخلون خارجيون",
    'plan.field.guestsHint': "مفصولون بفواصل",
    'plan.field.for': "لفائدة",
    'plan.field.plannedTeacher': "الأستاذ المبرمج",
    'plan.field.supervisors': "المراقبون",
    'plan.field.organisers': "المنظمون",
    'plan.field.roll': "المناداة",
    'plan.field.duration': "المدة",
    'plan.field.status': "الحالة",
    'plan.field.givenBy': "قدّمها",
    'plan.field.givenByHint': "في حالة التعويض تحتسب الساعات للمعوض لا للأستاذ المبرمج.",
    'plan.field.substitute': "معوض",
    'plan.field.reason': "السبب",
    'plan.series.new': "سلسلة جديدة",
    'plan.series.sub': "حصة في الأسبوع خلال الفترة؛ تُتجاوز العطل المغلقة",
    'plan.series.create': "إنشاء {count} حصة",
    'plan.series.saved': "الحصص المنشأة: {count}.",
    'plan.series.editFollowing': "هذه الحصة والتي تليها",
    'plan.series.followingNote': "تعاد الحصص المبرمجة ابتداء من {day}؛ وتبقى المنجزة والملغاة وذات المناداة.",
    'plan.series.sessions': "الحصص المنشأة",
    'plan.series.range': "من {first} إلى {last}",
    'plan.series.skipped': "الأيام المتجاوزة",
    'plan.series.hours': "الساعات",
    'plan.series.volume': "{planned} مبرمجة من أصل {volume} في الحجم",
    'plan.series.noVolume': "لا حجم مبرمج لهذا النوع",
    'plan.lesson.new': "حصة جديدة",
    'plan.lesson.edit': "تعديل الحصة",
    'plan.lesson.saved': "تم حفظ الحصة.",
    'plan.special.new': "نشاط جديد",
    'plan.special.edit': "تعديل النشاط",
    'plan.special.sub': "ندوة أو محاضرة أو خرجة أو اجتماع: خانة ليست درسا ولا تقييما",
    'plan.special.create': "برمجة النشاط",
    'plan.special.suspend': "تعليق دروس المشاركين خلال هذه الخانة",
    'plan.special.suspendHint': "تلغى حصصهم بسبب النشاط دون احتسابها في الغياب.",
    'plan.special.rollCall': "إجراء المناداة",
    'plan.special.rollCallHint': "الحضور إلزامي: تضاف الغيابات إلى تتبع المواظبة.",
    'plan.special.saved': "تم حفظ النشاط.",
    'plan.special.savedSuspended': "تم حفظ النشاط؛ الحصص المعلقة: {count}.",
    'plan.event.movedFrom': "مؤجلة من {day}",
    'plan.event.series': "سلسلة: كل {weekday} من {first} إلى {last} · الحصة {index} من {total}",
    'plan.event.unplacedNote': "هذا التقييم بدون قاعة أو مراقب بعد.",
    'plan.event.place': "برمجة التقييم",
    'plan.event.placed': "تمت برمجة التقييم.",
    'plan.event.unplace': "إزالة من استعمال الزمن",
    'plan.event.openAssessment': "فتح التقييم",
    'plan.event.rollNotTaken': "لم تجر بعد",
    'plan.event.rollCounts': "{present} حاضر · {absent} غائب · {late} متأخر",
    'plan.event.capacity': "العدد المنتظر {expected} يتجاوز سعة القاعة ({capacity}).",
    'plan.event.override': "مبرمجة رغم التعارض: {reason} ({by})",
    'plan.event.suspended': "معلقة بسبب النشاط «{by}».",
    'plan.event.saveStatus': "حفظ الحالة",
    'plan.event.statusSaved': "تم حفظ الحالة.",
    'plan.event.change': "تعديل",
    'plan.event.editOne': "هذه الحصة",
    'plan.event.editFollowing': "هذه والتي تليها",
    'plan.event.move': "تأجيل",
    'plan.event.moved': "تم تأجيل الحصة.",
    'plan.event.delete': "حذف",
    'plan.event.deleteText': "تختفي الخانة من استعمال الزمن. الحصة ذات المناداة تُلغى بدل الحذف.",
    'plan.event.deleted': "تم حذف الخانة.",
    'plan.event.takeRoll': "إجراء المناداة",
    'plan.calendar.title': "يومية المؤسسة",
    'plan.calendar.sub': "العطل والأعياد والفترات الخاصة؛ الفترة المغلقة ترفض كل حصة",
    'plan.calendar.new': "فترة جديدة",
    'plan.calendar.edit': "تعديل الفترة",
    'plan.calendar.delete': "حذف الفترة",
    'plan.calendar.empty': "لا توجد فترات لهذه السنة.",
    'plan.calendar.period': "الفترة",
    'plan.calendar.kind': "النوع",
    'plan.calendar.scope': "لفائدة",
    'plan.calendar.dates': "التواريخ",
    'plan.calendar.planning': "البرمجة",
    'plan.calendar.blocked': "مغلقة",
    'plan.calendar.open': "مفتوحة",
    'plan.calendar.institution': "المؤسسة",
    'plan.calendar.cycles': "الأسلاك",
    'plan.calendar.trainings': "التكوينات",
    'plan.calendar.from': "من",
    'plan.calendar.to': "إلى",
    'plan.calendar.toHint': "فارغ ليوم واحد",
    'plan.calendar.blocking': "إغلاق البرمجة",
    'plan.calendar.blockingHint': "لا تبرمج أي حصة؛ وتتجاوز السلسلة هذه الأيام تلقائيا.",
    'plan.calendar.note': "تتجاوز السلسلة أيام الفترة المغلقة. تؤكد الأعياد الدينية كل سنة ويبقى تاريخها قابلا للتعديل.",
    'plan.rooms.title': "القاعات والأماكن",
    'plan.rooms.sub': "أماكن الحصص: السعة تراقب العدد والإشغال يخص الأسبوع الجاري",
    'plan.rooms.new': "قاعة جديدة",
    'plan.rooms.edit': "تعديل القاعة",
    'plan.rooms.delete': "حذف القاعة",
    'plan.rooms.empty': "لا توجد قاعات.",
    'plan.rooms.tab': "القاعات · {count}",
    'plan.rooms.room': "القاعة",
    'plan.rooms.venue': "المكان",
    'plan.rooms.kind': "النوع",
    'plan.rooms.kindHint': "قاعة دروس، مدرج، قاعة إعلاميات…",
    'plan.rooms.capacity': "المقاعد",
    'plan.rooms.accepts': "الاستعمال",
    'plan.rooms.shared': "عدة دروس",
    'plan.rooms.single': "درس واحد",
    'plan.rooms.occupancy': "الإشغال",
    'plan.rooms.timetable': "استعمال الزمن",
    'plan.rooms.inactive': "غير نشطة",
    'plan.rooms.sharedLabel': "تقبل عدة دروس في الوقت نفسه",
    'plan.rooms.sharedHint': "قاعة مطالعة أو مكتبة: لا تخضع لمراقبة التعارض.",
    'plan.rooms.activeLabel': "قاعة نشطة",
    'plan.rooms.note': "يُنبه إلى الحصة التي يتجاوز عددها السعة؛ «عدة دروس» تسمح بحصتين في الوقت نفسه.",
    'plan.venues.tab': "الأماكن · {count}",
    'plan.venues.new': "مكان جديد",
    'plan.venues.edit': "تعديل المكان",
    'plan.venues.delete': "حذف المكان",
    'plan.venues.empty': "لا توجد أماكن.",
    'plan.venues.address': "العنوان",
    'plan.venues.rooms': "القاعات",
    'plan.hours.title': "الساعات المبرمجة والمنجزة",
    'plan.hours.sub': "الحجم من المسار والمنجز من الحصص المنجزة",
    'plan.hours.subOf': "{training} · {year} · إلى غاية {day}",
    'plan.hours.by.subject': "حسب المادة",
    'plan.hours.by.teacher': "حسب الأستاذ",
    'plan.hours.empty': "لا توجد ساعات.",
    'plan.hours.volume': "الحجم",
    'plan.hours.planned': "مبرمج",
    'plan.hours.done': "منجز",
    'plan.hours.progress': "التقدم",
    'plan.hours.teacher': "الأستاذ",
    'plan.hours.subjects': "المواد",
    'plan.hours.substitute': "منها تعويضات",
    'plan.hours.noTeacher': "بدون أستاذ",
    'plan.hours.missing': "ينقص {hours} للبرمجة",
    'plan.hours.extra': "{hours} زائدة عن الحجم",
    'plan.hours.note': "الحصة المقدمة من معوض تحتسب له. ساعات الأساتذة تهيئ للأجور التي تبقى خارج هذا الإصدار.",
    'att.student': "الطالب",
    'att.element': "العنصر",
    'att.value': "القيمة",
    'att.threshold': "العتبة",
    'att.validated': "مصادق",
    'att.toValidate': "للمصادقة",
    'att.notTaken': "غير منجز",
    'att.justified': "مبررة",
    'att.justifiedAs': "مبررة · {kind}",
    'att.lateBy': "تأخر {count} د",
    'att.sessions': "{count} حصة",
    'att.waived': "رفعتها اللجنة",
    'att.mark.present': "حاضر",
    'att.mark.absent': "غائب",
    'att.mark.late': "متأخر",
    'att.mark.present.short': "ح",
    'att.mark.absent.short': "غ",
    'att.mark.late.short': "ت",
    'att.level.alert': "تنبيه",
    'att.level.consequence': "إجراء",
    'att.effect.alert': "بلوغ عتبة التنبيه",
    'att.effect.retake': "إقصاء من الدورة العادية: الانتقال إلى الاستدراكية",
    'att.effect.jury': "إحالة على اللجنة",
    'att.kind.medical': "شهادة طبية",
    'att.kind.family': "سبب عائلي",
    'att.kind.administrative': "سبب إداري",
    'att.kind.transport': "النقل",
    'att.kind.other': "آخر",
    'att.roll.title': "المناداة",
    'att.roll.sub': "حصصك اليوم ومناداة كل منها",
    'att.roll.today': "حصصي",
    'att.roll.unlinked': "لا توجد بطاقة أستاذ مرتبطة بحسابك.",
    'att.roll.none': "لا توجد حصص هذا اليوم.",
    'att.roll.noRoll': "بدون مناداة",
    'att.roll.taken': "تمت المناداة",
    'att.roll.todo': "إجراء المناداة",
    'att.roll.of': "المناداة",
    'att.roll.expected': "{count} منتظر",
    'att.roll.allPresent': "الجميع حاضر",
    'att.roll.counts': "{present} حاضر · {absent} غائب · {late} متأخر",
    'att.roll.noStudent': "لا يوجد طالب منتظر في هذه الحصة.",
    'att.roll.lateMinutes': "دقائق التأخر",
    'att.roll.minutes': "د",
    'att.roll.save': "حفظ المناداة واعتبار الحصة منجزة",
    'att.roll.saveValidate': "حفظ ومصادقة",
    'att.roll.reopen': "إعادة فتح المناداة",
    'att.roll.saved': "تم حفظ المناداة.",
    'att.roll.savedValidated': "تم حفظ المناداة والمصادقة عليها.",
    'att.roll.validatedBy': "صادق على المناداة {by}: لم تعد قابلة للتعديل.",
    'att.roll.editableUntil': "قابلة للتعديل حتى مصادقة الشؤون الدراسية",
    'att.roll.toTimetable': "استعمال الزمن",
    'att.absences.title': "الغيابات",
    'att.absences.sub': "تتمم الشؤون الدراسية مناداة الأساتذة وتبررها وتصادق عليها قسما بقسم وأسبوعا بأسبوع",
    'att.absences.weekOf': "أسبوع {day}",
    'att.absences.onlyAbsent': "الغائبون فقط",
    'att.absences.all': "القسم كله",
    'att.absences.validate': "المصادقة على المناداة ({count})",
    'att.absences.validated': "المناداة المصادق عليها: {count}.",
    'att.absences.noClass': "لا يوجد قسم ضمن نطاقك.",
    'att.absences.noSession': "لا توجد حصص بمناداة هذا الأسبوع.",
    'att.absences.week': "الأسبوع",
    'att.absences.year': "السنة",
    'att.absences.count': "{count} غ.",
    'att.absences.roll': "المناداة",
    'att.absences.notTaken': "مناداة غير منجزة",
    'att.absences.clickHint': "النقر يغير الحالة: غائب، متأخر، حاضر.",
    'att.justify.title': "تبرير غياب",
    'att.justify.edit': "تعديل التبرير",
    'att.justify.sub': "وثيقة واحدة تبرر كل حصص الفترة؛ وتبقى التقييمات معالجة بنقطتها",
    'att.justify.from': "من",
    'att.justify.to': "إلى",
    'att.justify.kind': "السبب",
    'att.justify.reason': "تفاصيل",
    'att.justify.document': "الوثيقة",
    'att.justify.documentHint': "PDF أو PNG أو JPEG بحد أقصى 5 ميغا",
    'att.justify.submit': "تبرير",
    'att.justify.saved': "الحصص المبررة: {count}.",
    'att.justify.remove': "سحب التبرير",
    'att.justify.removeText': "تعود الغيابات التي كان يغطيها غير مبررة.",
    'att.justify.removed': "تم سحب التبرير.",
    'att.dashboard.title': "المواظبة",
    'att.dashboard.sub': "هذا الأسبوع: المناداة المطلوبة والمنتظرة للمصادقة والطلبة المتجاوزون للعتبات",
    'att.dashboard.rate': "متوسط المواظبة",
    'att.dashboard.rateNote': "كل التكوينات هذا الأسبوع",
    'att.dashboard.missing': "مناداة غير منجزة",
    'att.dashboard.missingNote': "حصص ماضية بدون مناداة",
    'att.dashboard.toValidate': "مناداة للمصادقة",
    'att.dashboard.toValidateNote': "أجراها الأساتذة",
    'att.dashboard.over': "طلبة تجاوزوا العتبات",
    'att.dashboard.overNote': "منهم {count} في عتبة الإجراء",
    'att.dashboard.overTitle': "الطلبة المتجاوزون للعتبات",
    'att.dashboard.overEmpty': "لا يوجد طالب تجاوز عتبات النظام.",
    'att.dashboard.missingTitle': "حصص ماضية بدون مناداة",
    'att.dashboard.missingEmpty': "تمت كل المناداة.",
    'att.dashboard.complete': "إتمام",
    'att.student.title': "المواظبة",
    'att.student.empty': "لا يوجد تسجيل هذه السنة.",
    'att.student.rate': "المواظبة",
    'att.student.rateNote': "من أصل {hours} من الدروس المنجزة",
    'att.student.unjustified': "غيابات غير مبررة",
    'att.student.justified': "مبررة",
    'att.student.lates': "التأخرات",
    'att.student.lateMinutes': "{count} د في المجموع",
    'att.student.bySubject': "حسب المادة",
    'att.student.noRule': "لا عتبة حسب المادة في النظام",
    'att.student.rule': "عتبات النظام: تنبيه {alert} %، إجراء {consequence} %",
    'att.student.realized': "المنجز",
    'att.student.unjustifiedShort': "غير مبرر",
    'att.student.rateCol': "النسبة",
    'att.student.records': "الغيابات والتأخرات",
    'att.student.noRecord': "لا يوجد غياب مسجل.",
    'att.student.assessment': "تقييم · {absence}",
    'att.student.justifications': "التبريرات",
    'att.student.noJustification': "لا توجد تبريرات.",
    'att.student.waivers': "إجراءات رفعتها اللجنة",
    'att.student.note': "لا تحتسب إلا الحصص المنجزة. تبقى غيابات التقييمات محمولة على النقطة.",
    'att.waive.action': "رفع الإجراء",
    'att.waive.title': "رفع إجراء المواظبة",
    'att.waive.reason': "سبب اللجنة",
    'att.waive.saved': "تم رفع الإجراء.",
    'att.waive.remove': "إعادة الإجراء",
    'att.waive.removed': "تمت إعادة الإجراء.",
    'reg.block.attendance': "المواظبة",
    'reg.ed.attendance.hint': "تجمع كل قاعدة نطاقا وما يحتسب وأساسه؛ وتقيّم كلها.",
    'reg.ed.attendance.none': "لا قاعدة: لا تنبه الغيابات ولا يترتب عنها شيء.",
    'reg.ed.attendance.rule': "القاعدة {number}",
    'reg.ed.attendance.scope': "النطاق",
    'reg.ed.attendance.measure': "ما يحتسب",
    'reg.ed.attendance.measure.unjustified_hours': "الساعات غير المبررة",
    'reg.ed.attendance.measure.absence_hours': "كل ساعات الغياب",
    'reg.ed.attendance.measure.sessions': "الحصص غير المبررة",
    'reg.ed.attendance.base': "بالنسبة إلى",
    'reg.ed.attendance.base.realized': "الساعات المنجزة",
    'reg.ed.attendance.base.planned': "الحجم المبرمج",
    'reg.ed.attendance.base.fixed': "عتبة ثابتة",
    'reg.ed.attendance.effect': "الأثر",
    'reg.ed.attendance.effect.retake': "الانتقال إلى الاستدراكية",
    'reg.ed.attendance.effect.jury': "إحالة على اللجنة",
    'reg.ed.attendance.alert': "تنبيه ابتداء من",
    'reg.ed.attendance.consequence': "إجراء ابتداء من",
    'reg.ed.attendance.unit.sessions': "حصص",
    'reg.ed.attendance.add': "إضافة قاعدة",
    'reg.ed.attendance.lates': "عدد التأخرات المعادلة لغياب",
    'reg.ed.attendance.lateHours': "مدة هذا الغياب",
    'reg.ed.attendance.waive': "يمكن للجنة رفع الإجراء مع التعليل",
    'reg.ed.attendance.worst': "عند بلوغ عدة قواعد يطبق الإجراء الأشد. لا تحتسب الحصص المعلقة بسبب نشاط.",
    'reg.sum.attendance.none': "لا توجد قاعدة مواظبة",
    'reg.sum.attendance.rule': "{scope}: تنبيه {alert}، {effect} {consequence}",
    'scol.tab.attendance': "المواظبة",
    'violation.plan.room_name_taken': "توجد قاعة بهذا الاسم في هذا المكان.",
    'violation.plan.room_in_use': "القاعة تحتضن حصصا: عطّلها بدل حذفها.",
    'violation.plan.dates_order': "النهاية قبل البداية.",
    'violation.plan.times_order': "يجب أن تلي النهاية البداية.",
    'violation.plan.invalid_time': "الساعة مطلوبة (HH:MM).",
    'violation.plan.outside_year': "خارج السنة الجامعية.",
    'violation.plan.year_closed': "السنة مغلقة.",
    'violation.plan.no_class': "اختر قسما واحدا على الأقل.",
    'violation.plan.subject_expected': "اختر مادة من التكوين.",
    'violation.plan.other_training': "خارج تكوين الحصة.",
    'violation.plan.series_subject': "مادة السلسلة لا تتغير: أنشئ سلسلة أخرى.",
    'violation.plan.series_empty': "لا توجد حصة لإنشائها في هذه الفترة.",
    'violation.plan.event_has_roll': "أجريت مناداة هذه الخانة: ألغها بدل الحذف.",
    'violation.att.not_expected': "هذا الطالب غير منتظر في الحصة.",
    'violation.att.already_waived': "الإجراء مرفوع مسبقا عن هذا العنصر.",
    'violation.reg.attendance_threshold': "حدد عتبة تنبيه أو إجراء.",
    'violation.reg.attendance_order': "يجب أن تأتي عتبة الإجراء بعد التنبيه.",
    'plan.field.rhythmWeekly': "كل أسبوع",
    'stg.add': "إضافة",
    'stg.edit': "تعديل",
    'stg.remove': "حذف",
    'stg.deactivate': "تعطيل",
    'stg.inactive': "معطل",
    'stg.change': "تغيير",
    'stg.choose': "اختيار…",
    'stg.none': "لا شيء",
    'stg.yes': "نعم",
    'stg.no': "لا",
    'stg.see': "عرض",
    'stg.search': "بحث",
    'stg.searchPlaceholder': "اكتب حرفين على الأقل",
    'stg.export': "تصدير",
    'stg.breadcrumb': "مسار التنقل",
    'stg.year': "السنة",
    'stg.class': "القسم",
    'stg.student': "الطالب",
    'stg.supervisor': "المؤطر البيداغوجي",
    'stg.tutor': "المؤطر داخل المؤسسة",
    'stg.topic': "الموضوع",
    'stg.missions': "المهام المقررة",
    'stg.place': "المكان",
    'stg.startsOn': "البداية",
    'stg.endsOn': "النهاية",
    'stg.lastName': "النسب",
    'stg.firstNames': "الاسم",
    'stg.email': "البريد الإلكتروني",
    'stg.phone': "الهاتف",
    'stg.reason': "السبب",
    'stg.tab.count': "{label} ({count})",
    'stg.step.none': "بدون تدريب",
    'stg.step.proposed': "مقترح",
    'stg.step.validated': "مصادق عليه",
    'stg.step.signed': "اتفاقية موقعة",
    'stg.step.ongoing': "جارٍ",
    'stg.step.report_due': "التقرير منتظر",
    'stg.step.report_late': "التقرير متأخر",
    'stg.step.report_received': "التقرير مستلم",
    'stg.step.defended': "تمت المناقشة",
    'stg.step.evaluated': "مقيَّم",
    'stg.step.refused': "مرفوض",
    'stg.step.cancelled': "ملغى",
    'stg.timeline.proposed': "مقترح",
    'stg.timeline.validated': "مصادق عليه",
    'stg.timeline.signed': "اتفاقية موقعة",
    'stg.timeline.ongoing': "جارٍ",
    'stg.timeline.report': "التقرير مستلم",
    'stg.timeline.defended': "تمت المناقشة",
    'stg.timeline.evaluated': "مقيَّم",
    'stg.nature.internship': "تدريب",
    'stg.nature.apprenticeship': "تكوين بالتناوب",
    'stg.nature.job': "وظيفة",
    'stg.offerState.draft': "مسودة",
    'stg.offerState.published': "منشور",
    'stg.offerState.closed': "مغلق",
    'stg.applicationState.new': "جديد",
    'stg.applicationState.forwarded': "محال",
    'stg.applicationState.retained': "مقبول",
    'stg.applicationState.declined': "غير مقبول",
    'stg.evaluator.tutor': "المؤطر (بطاقة التقييم)",
    'stg.evaluator.supervisor': "المؤطر البيداغوجي",
    'stg.evaluator.jury': "لجنة المناقشة",
    'stg.jury.president': "الرئيس",
    'stg.jury.supervisor': "المؤطر",
    'stg.jury.member': "عضو",
    'stg.jury.tutor': "مؤطر مدعو",
    'stg.due.before_start': "قبل البداية ({date})",
    'stg.due.at_end': "في النهاية ({date})",
    'stg.due.after_end': "قبل {date}",
    'stg.type.due.before_start': "قبل البداية",
    'stg.type.due.at_end': "في نهاية التدريب",
    'stg.type.due.after_end': "بعد النهاية",
    'stg.filter.all': "الكل",
    'stg.filter.without': "بدون تدريب",
    'stg.filter.to_validate': "للمصادقة",
    'stg.filter.to_sign': "بدون اتفاقية",
    'stg.filter.ongoing': "جارية",
    'stg.filter.reports': "تقارير منتظرة",
    'stg.filter.to_evaluate': "للتقييم",
    'stg.filter.evaluated': "مقيَّمة",
    'stg.filter.allClasses': "كل الأقسام",
    'stg.filter.allSupervisors': "كل المؤطرين",
    'stg.filter.noSupervisor': "بدون مؤطر",
    'stg.dashboard.title': "التداريب",
    'stg.dashboard.sub': "حملات السنة والمتدربون وما تبقى إنجازه",
    'stg.dashboard.subYear': "حملات السنة والمتدربون وما تبقى إنجازه · السنة {year}",
    'stg.dashboard.campaigns': "الحملات",
    'stg.dashboard.campaignsSub': "نوع تدريب وتكوينات وفترة",
    'stg.dashboard.campaignLine': "{type} · {trainings} · {count} طالب",
    'stg.dashboard.noCampaign': "لا توجد حملة هذه السنة.",
    'stg.dashboard.noCampaignManage': "لا توجد حملة هذه السنة: أنشئ الأولى لتتبع الطلبة المنتظرين في التدريب.",
    'stg.dashboard.outside': "{count} تدريب خارج الحملات هذه السنة",
    'stg.dashboard.hosts': "مؤسسات الاستقبال",
    'stg.dashboard.hostsSub': "متدربو السنة",
    'stg.dashboard.noHosts': "لا يوجد متدرب هذه السنة.",
    'stg.dashboard.todo': "للإنجاز",
    'stg.dashboard.nothingToDo': "لا شيء للإنجاز حالياً.",
    'stg.todo.proposed': "{count} تدريب مقترح من الطلبة",
    'stg.todo.proposedSub': "للمصادقة أو الرفض",
    'stg.todo.toSign': "{count} اتفاقية للتوقيع",
    'stg.todo.toSignSub': "يعدها Kelassi بصيغة PDF جاهزة للطباعة",
    'stg.todo.noSupervisor': "{count} متدرب بدون مؤطر بيداغوجي",
    'stg.todo.noSupervisorSub': "عيّن أستاذاً",
    'stg.todo.defenses': "{count} مناقشة للبرمجة",
    'stg.todo.defensesSub': "الأيام والمواعيد واللجان",
    'stg.todo.late': "{count} تقرير متأخر",
    'stg.todo.lateSub': "يُرسل تذكير للطالب كل صباح",
    'stg.kpi.expected': "الطلبة المنتظرون في التدريب",
    'stg.kpi.expectedNote': "{count} حملة هذه السنة",
    'stg.kpi.without': "بدون تدريب",
    'stg.kpi.withoutNote': "منهم {count} قبل أقل من 30 يوماً من البداية",
    'stg.kpi.toSign': "اتفاقيات للتوقيع",
    'stg.kpi.toSignNote': "تداريب مصادق عليها بدون اتفاقية موقعة",
    'stg.kpi.reports': "تقارير منتظرة",
    'stg.kpi.reportsNote': "تجاوز الأجل لـ {count}",
    'stg.column.campaign': "الحملة",
    'stg.column.period': "الفترة",
    'stg.column.progress': "التقدم",
    'stg.column.without': "بدون تدريب",
    'stg.column.intern': "المتدرب",
    'stg.column.internTopic': "المتدرب والموضوع",
    'stg.column.organisationTopic': "المؤسسة والموضوع",
    'stg.column.documents': "الوثائق",
    'stg.column.step': "المرحلة",
    'stg.column.offer': "العرض والمؤسسة",
    'stg.column.audience': "الفئات المستهدفة",
    'stg.column.closes': "الأجل",
    'stg.column.applications': "الترشيحات",
    'stg.column.state': "الحالة",
    'stg.column.candidate': "المترشح",
    'stg.column.received': "تاريخ الاستلام",
    'stg.column.next': "المآل",
    'stg.campaign.new': "حملة جديدة",
    'stg.campaign.edit': "تعديل الحملة",
    'stg.campaign.sub': "نوع تدريب وتكوينات وفترة؛ الطلبة المسجلون في هذه التكوينات منتظرون",
    'stg.campaign.one': "الحملة",
    'stg.campaign.none': "خارج الحملات",
    'stg.campaign.type': "نوع التدريب",
    'stg.campaign.typeHint': "المدة الدنيا {months} أشهر",
    'stg.campaign.label': "التسمية",
    'stg.campaign.reportDueOn': "آخر أجل لتسليم التقرير",
    'stg.campaign.reportDueHint': "فارغ: نهاية التدريب زائد مهلة النوع",
    'stg.campaign.assessmentType': "نوع تقييم النقطة",
    'stg.campaign.assessmentHint': "للتقييم المنشأ في عنصر البرنامج",
    'stg.campaign.trainings': "التكوينات المعنية والعنصر الذي يتلقى النقطة",
    'stg.campaign.noTraining': "لا يوجد تكوين لهذه السنة.",
    'stg.campaign.subject': "عنصر البرنامج",
    'stg.campaign.noSubject': "بدون نقل النقطة",
    'stg.campaign.subjectHint': "★: عنصر موسوم «تدريب» في البرنامج. تُنقل إليه النقطة المصادق عليها.",
    'stg.list.title': "التداريب",
    'stg.list.sub': "{type} · {trainings} · {expected} طالب",
    'stg.list.outside': "خارج الحملات",
    'stg.list.outsideSub': "تداريب السنة المسترجعة من Kelassi السابق أو المسجلة بدون حملة",
    'stg.list.searchPlaceholder': "الطالب، المؤسسة، الموضوع",
    'stg.list.empty': "لا يوجد متدرب لهذا التصفية.",
    'stg.list.noCampaign': "لا توجد حملة هذه السنة: أنشئ واحدة من لوحة القيادة.",
    'stg.list.byStudent': "من طرف الطالب",
    'stg.list.withoutNote': "هؤلاء الطلبة ليس لديهم تدريب بعد: راسلهم أو انشر عرضاً يستهدفهم.",
    'stg.internship.one': "تدريب",
    'stg.internship.new': "تدريب جديد",
    'stg.internship.edit': "تعديل التدريب",
    'stg.internship.sub': "طالب، مؤسسة، فترة، موضوع",
    'stg.internship.saveProposed': "حفظ كمقترح",
    'stg.internship.saveValidate': "حفظ ومصادقة",
    'stg.internship.duration': "المدة {months} أشهر",
    'stg.internship.allowShort': "قبول هذه المدة رغم أنها أقل من الحد الأدنى",
    'stg.internship.note': "تأتي المؤسسة من دليل العلاقات الخارجية؛ تُنشأ المؤسسة غير الموجودة هنا مع مؤطرها. يمكن للطالب أيضاً اقتراح تدريبه من فضائه.",
    'stg.organisation.one': "المؤسسة",
    'stg.organisation.host': "مؤسسة الاستقبال",
    'stg.organisation.new': "مؤسسة جديدة",
    'stg.organisation.newHint': "تنضم إلى دليل العلاقات الخارجية",
    'stg.organisation.notFound': "المؤسسة غير موجودة في الدليل؟",
    'stg.organisation.pick': "البحث في الدليل",
    'stg.tutor.later': "يُحدد لاحقاً",
    'stg.tutor.new': "جهة اتصال جديدة…",
    'stg.tutor.none': "المؤطر يُحدد لاحقاً",
    'stg.supervisor.later': "يُعيَّن لاحقاً",
    'stg.supervisor.none': "لا أحد",
    'stg.supervisor.toAssign': "المؤطر يُعيَّن لاحقاً",
    'stg.supervisor.change': "المؤطر البيداغوجي",
    'stg.supervisor.hint': "يتلقى إشعاراً ويرى التدريب في «متدربيّ»",
    'stg.file.title': "{name} · {type}",
    'stg.file.months': "{months} أشهر",
    'stg.file.minimum': "الحد الأدنى {months}",
    'stg.file.steps': "مراحل التدريب",
    'stg.file.before': "قبل {date}",
    'stg.file.refused': "مرفوض: {reason}",
    'stg.file.cancelled': "ملغى: {reason}",
    'stg.file.documents': "وثائق الملف",
    'stg.file.documentsSub': "مطلوبة حسب نوع التدريب",
    'stg.file.noDocuments': "لا توجد وثيقة مطلوبة.",
    'stg.file.evaluation': "التقييم",
    'stg.file.markLater': "تظهر النقطة بعد المصادقة على التقييم.",
    'stg.file.evaluatedOn': "صودق على التقييم في {date} · {who}",
    'stg.file.reportedTo': "نُقلت النقطة إلى «{subject}»",
    'stg.file.defense': "المناقشة",
    'stg.file.noDefense': "لم تبرمج بعد.",
    'stg.file.planDefense': "برمجة",
    'stg.file.defenseOn': "المناقشة يوم {date} على الساعة {time}",
    'stg.file.supervision': "التأطير",
    'stg.file.since': "منذ {date}",
    'stg.file.fromOffer': "من العرض «{title}»",
    'stg.file.followUps': "التتبع",
    'stg.file.noFollowUp': "لا يوجد تتبع بعد.",
    'stg.action.validate': "مصادقة",
    'stg.action.refuse': "رفض",
    'stg.action.convention': "الاتفاقية (PDF)",
    'stg.action.sign': "تأشير الاتفاقية كموقعة",
    'stg.action.evaluate': "تقييم",
    'stg.action.reopenEvaluation': "إعادة فتح التقييم",
    'stg.action.cancel': "إلغاء التدريب",
    'stg.action.reopen': "إعادة الفتح",
    'stg.action.delete': "حذف التدريب",
    'stg.delete.text': "سيُحذف تدريب {name} ووثائقه.",
    'stg.reason.refuseSub': "يُرسل السبب إلى الطالب.",
    'stg.reason.cancelSub': "يبقى التدريب في الملف كملغى.",
    'stg.sign.sub': "الاتفاقية الموقعة من الأطراف الثلاثة.",
    'stg.sign.on': "وقعت في",
    'stg.followUp.new': "تسجيل تتبع",
    'stg.followUp.one': "تتبع",
    'stg.followUp.type': "نوع التتبع",
    'stg.followUp.heldOn': "التاريخ",
    'stg.followUp.present': "المتدرب حاضر",
    'stg.followUp.presentUnknown': "غير محدد",
    'stg.followUp.observations': "الملاحظات",
    'stg.followUp.wasPresent': "المتدرب حاضر",
    'stg.followUp.wasAbsent': "المتدرب غائب",
    'stg.followUp.remove': "حذف التتبع",
    'stg.document.one': "الوثيقة",
    'stg.document.expected': "المنتظرة",
    'stg.document.received': "المستلمة",
    'stg.document.awaited': "منتظرة",
    'stg.document.see': "عرض",
    'stg.document.receive': "استلام",
    'stg.document.replace': "تعويض",
    'stg.document.deposit': "إيداع",
    'stg.document.remove': "سحب الوثيقة",
    'stg.document.add': "وثيقة أخرى",
    'stg.document.free': "وثيقة أخرى",
    'stg.document.label': "تسمية الوثيقة",
    'stg.document.receivedOn': "استلمت في",
    'stg.document.file': "ملف PDF",
    'stg.document.fileHint': "PDF، 10 ميغا على الأكثر",
    'stg.document.markReceived': "تأشير كمستلمة",
    'stg.document.upload': "إيداع",
    'stg.document.uploadSub': "وثيقتك بصيغة PDF؛ تؤشر كمستلمة اليوم.",
    'stg.document.receiveSub': "مستلمة ورقياً: يكفي التاريخ. أو أودع ملف PDF.",
    'stg.document.pickFile': "اختر ملف PDF.",
    'stg.evaluation.title': "التقييم · {name}",
    'stg.evaluation.open': "تنقيط",
    'stg.evaluation.component': "المكون",
    'stg.evaluation.by': "يقيّمه",
    'stg.evaluation.weight': "الوزن",
    'stg.evaluation.mark': "النقطة / 20",
    'stg.evaluation.total': "نقطة التدريب",
    'stg.evaluation.comment': "ملاحظة اللجنة",
    'stg.evaluation.report': "نقل النقطة إلى «{subject}» ({training})",
    'stg.evaluation.noTarget': "لا تحدد الحملة عنصر البرنامج الذي يتلقى النقطة: تبقى في التدريب.",
    'stg.evaluation.note': "تتبع النقطة المنقولة بعد ذلك مسار النقط المعتاد (الإقفال، المداولة). تُضبط الشبكة والأوزان حسب نوع التدريب.",
    'stg.evaluation.supervisorNote': "تنقط مكونات المؤطر؛ تصادق المؤسسة على التقييم.",
    'stg.evaluation.noGrid': "لا توجد شبكة لهذا النوع من التدريب.",
    'stg.evaluation.validate': "المصادقة على التقييم",
    'stg.evaluation.saved': "حُفظت النقط.",
    'stg.evaluation.validated': "صودق على التقييم.",
    'stg.evaluation.reported': "صودق على التقييم ونُقلت النقطة إلى «{subject}».",
    'stg.flash.saved': "تم الحفظ.",
    'stg.flash.validated': "صودق على التدريب: تم إشعار الطالب.",
    'stg.flash.refused': "رُفض التدريب: أُرسل السبب إلى الطالب.",
    'stg.flash.cancelled': "أُلغي التدريب.",
    'stg.flash.signed': "وُقعت الاتفاقية.",
    'stg.flash.reopened': "أعيد فتحه.",
    'stg.flash.followUp': "سُجل التتبع.",
    'stg.flash.document': "حُفظت الوثيقة.",
    'stg.flash.proposed': "اقتُرح التدريب: ستدرسه المؤسسة.",
    'stg.flash.applied': "أُرسل الترشيح.",
    'stg.flash.withdrawn': "سُحب الترشيح.",
    'stg.flash.published': "نُشر العرض: تم إشعار الطلبة المستهدفين.",
    'stg.flash.closed': "أُغلق العرض.",
    'stg.flash.placed': "حُفظ الموعد.",
    'stg.flash.autoplaced': "بُرمجت {count} مناقشة.",
    'stg.offers.title': "العروض",
    'stg.offers.sub': "عروض التدريب والتكوين بالتناوب والشغل المنشورة للطلبة والخريجين",
    'stg.offers.filter.all': "الكل",
    'stg.offers.filter.published': "المنشورة",
    'stg.offers.filter.draft': "المسودات",
    'stg.offers.filter.closed': "المغلقة",
    'stg.offers.empty': "لا توجد عروض.",
    'stg.offers.allTrainings': "كل التكوينات",
    'stg.offers.alumni': "الخريجون",
    'stg.offers.fresh': "{count} جديدة",
    'stg.offers.note': "يظهر العرض المنشور في فضاء الطلبة المستهدفين (والخريجين بالنسبة للشغل) مع إشعار. «مقبول» يقترح إنشاء التدريب مملوءاً مسبقاً.",
    'stg.offer.new': "عرض جديد",
    'stg.offer.edit': "تعديل العرض",
    'stg.offer.sub': "مؤسسة من الدليل وفئات مستهدفة وأجل",
    'stg.offer.title': "العنوان",
    'stg.offer.contact': "جهة الاتصال",
    'stg.offer.nature': "النوع",
    'stg.offer.startsOn': "البداية المرغوبة",
    'stg.offer.duration': "المدة",
    'stg.offer.durationPlaceholder': "من 4 إلى 6 أشهر",
    'stg.offer.closesOn': "الترشيحات إلى غاية",
    'stg.offer.closesLine': "يُغلق في {date}",
    'stg.offer.description': "الوصف",
    'stg.offer.audience': "الفئات المستهدفة",
    'stg.offer.alumni': "فتحه أيضاً للخريجين",
    'stg.offer.audienceHint': "بدون تكوين محدد، يستهدف العرض كل المسجلين هذه السنة.",
    'stg.offer.publish': "نشر",
    'stg.offer.close': "إغلاق",
    'stg.offer.applications': "الترشيحات",
    'stg.offer.applicationsSub': "{count} ترشيح · يرفق الطالب سيرته الذاتية",
    'stg.offer.noApplication': "لا توجد ترشيحات بعد.",
    'stg.offer.cv': "السيرة الذاتية",
    'stg.offer.internship': "التدريب",
    'stg.offer.createInternship': "إنشاء التدريب",
    'stg.offer.details': "العرض",
    'stg.offer.publishedAt': "نُشر في",
    'stg.offer.delete': "حذف العرض",
    'stg.offer.deleteText': "سيُحذف العرض «{title}».",
    'stg.apply.title': "الترشح · {title}",
    'stg.apply.letter': "رسالتك",
    'stg.apply.cv': "سيرتك الذاتية",
    'stg.apply.send': "إرسال الترشيح",
    'stg.apply.button': "الترشح",
    'stg.defense.title': "المناقشات",
    'stg.defense.sub': "الأيام والمواعيد واللجان",
    'stg.defense.subCampaign': "{campaign} · {days} يوم، {planned} مناقشة",
    'stg.defense.tab': "{date} ({count})",
    'stg.defense.unplannedTab': "غير مبرمجة ({count})",
    'stg.defense.unplanned': "تداريب تنتظر مناقشتها",
    'stg.defense.noDay': "لا يوجد يوم: أنشئ واحداً.",
    'stg.defense.allPlanned': "كل التداريب لها موعد.",
    'stg.defense.noCampaign': "لا توجد حملة هذه السنة.",
    'stg.defense.placeOne': "برمجة",
    'stg.defense.placeHere': "البرمجة هنا",
    'stg.defense.dayTitle': "{date} · {place}",
    'stg.defense.newDay': "يوم جديد",
    'stg.defense.editDay': "تعديل اليوم",
    'stg.defense.deleteDay': "حذف اليوم",
    'stg.defense.deleteDayText': "سيُحذف اليوم وكل مناقشاته؛ تعود التداريب إلى «غير مبرمجة».",
    'stg.defense.daySub': "تاريخ وقاعة وموعد أول مناقشة ومدة كل مناقشة",
    'stg.defense.date': "التاريخ",
    'stg.defense.room': "القاعة",
    'stg.defense.otherPlace': "مكان آخر…",
    'stg.defense.place': "برمجة مناقشة",
    'stg.defense.edit': "المناقشة · {name}",
    'stg.defense.firstSlot': "أول موعد",
    'stg.defense.slot': "مدة المناقشة (دقيقة)",
    'stg.defense.breakAt': "الاستراحة على الساعة",
    'stg.defense.breakMinutes': "مدة الاستراحة (دقيقة)",
    'stg.defense.breakLine': "{at}، {count} دقيقة",
    'stg.defense.minutes': "{count} دقيقة",
    'stg.defense.time': "الساعة",
    'stg.defense.day': "اليوم",
    'stg.defense.jury': "اللجنة",
    'stg.defense.role': "الدور",
    'stg.defense.member': "عضو اللجنة",
    'stg.defense.addMember': "إضافة عضو",
    'stg.defense.removeMember': "سحب هذا العضو",
    'stg.defense.inviteTutor': "دعوة المؤطر",
    'stg.defense.tutorInvited': "مؤطر المؤسسة",
    'stg.defense.allowConflict': "البرمجة رغم ذلك",
    'stg.defense.juryNote': "تضم اللجنة رئيساً وعضواً آخر على الأقل؛ ويُدعى المؤطر. يُنبَّه إلى الأستاذ الذي لديه حصة أو لجنة أخرى في نفس الوقت.",
    'stg.defense.free': "شاغر",
    'stg.defense.incomplete': "لجنة ناقصة",
    'stg.defense.remove': "سحب المناقشة",
    'stg.defense.rules': "قواعد اليوم",
    'stg.defense.autoplan': "توزيع تلقائي",
    'stg.defense.autoplanNote': "يبرمج التداريب المنتظرة في المواعيد الشاغرة، والمؤطر في اللجنة إن كان متاحاً، ويدعو المؤطر المهني؛ يبقى تعيين الرئيس.",
    'stg.defense.convocations': "الاستدعاءات (PDF)",
    'stg.defense.note': "يُشعَر المتدرب بموعد مناقشته؛ وتُطبع الاستدعاءات له وللجنة.",
    'stg.mine.title': "تدريبي",
    'stg.mine.none': "لا يوجد تدريب بعد",
    'stg.mine.noneSub': "اقترح التدريب الذي وجدته أو ترشح لعرض.",
    'stg.mine.supervisor': "المؤطر {name}",
    'stg.mine.tutor': "المؤطر المهني {name}",
    'stg.mine.toDeposit': "للإيداع",
    'stg.mine.received': "مستلمة",
    'stg.mine.before': "قبل {date}",
    'stg.mine.deposit': "إيداع ملف PDF",
    'stg.mine.mark': "نقطة التدريب: {mark} / 20",
    'stg.mine.openFile': "عرض ملف التدريب",
    'stg.mine.offers': "عروض لك",
    'stg.mine.allOffers': "كل العروض",
    'stg.mine.noOffer': "لا يوجد عرض مفتوح لك.",
    'stg.mine.proposeTitle': "اقتراح تدريب",
    'stg.mine.proposeText': "وجدت تدريباً بنفسك؟ اقترحه: تصادق عليه المؤسسة وتعد الاتفاقية.",
    'stg.mine.noCampaign': "لا توجد حملة مفتوحة لتكوينك.",
    'stg.mine.propose': "اقتراح تدريب",
    'stg.mine.proposeNote': "يصل اقتراحك «مقترحاً»: تصادق عليه المؤسسة أو تخبرك بسبب الرفض. تُضاف هنا المؤسسة غير الموجودة في الدليل.",
    'stg.mine.mobileNote': "ستخدم هذه الشاشات أيضاً التطبيق المحمول: إيداع التقرير والترشيحات والإشعارات في كل مرحلة.",
    'stg.mine.offersTitle': "العروض",
    'stg.mine.offersSub': "عروض التدريب والتكوين بالتناوب والشغل الموجهة لك",
    'stg.mine.description': "عرض التفاصيل",
    'stg.mine.startsOn': "ابتداءً من {date}",
    'stg.mine.applications': "ترشيحاتي",
    'stg.mine.noApplication': "لا توجد ترشيحات.",
    'stg.mine.withCv': "السيرة مرفقة",
    'stg.mine.withoutCv': "بدون سيرة",
    'stg.mine.withdraw': "سحب",
    'stg.supervised.title': "متدربيّ",
    'stg.supervised.sub': "{name} · مؤطر بيداغوجي لـ {count} متدرب",
    'stg.supervised.interns': "المتدربون",
    'stg.supervised.idle': "بدون تتبع منذ {days} يوماً",
    'stg.supervised.idleNote': "يُرسل إليك تذكير كل صباح",
    'stg.supervised.idleNone': "كلهم متتبعون",
    'stg.supervised.toMark': "تقارير للتنقيط",
    'stg.supervised.toMarkNote': "تقرير مستلم أو مناقشة منجزة",
    'stg.supervised.lastFollowUp': "آخر تتبع",
    'stg.supervised.never': "لا يوجد تتبع",
    'stg.supervised.markReport': "تنقيط التقرير",
    'stg.supervised.open': "تسجيل تتبع",
    'stg.supervised.empty': "لا يوجد متدرب للتأطير.",
    'stg.supervised.note': "ترى متدربيك فقط: سجل تتبعك (مكالمة، زيارة، حضور المتدرب)، اطلع على الوثائق ونقط التقرير. قد تُحتسب ساعات التأطير في خدمتك حسب إعدادات التداريب.",
    'stg.settings.title': "التداريب · الإعدادات",
    'stg.settings.sub': "أنواع التداريب ووثائقها وشبكتها والتتبع والتذكيرات",
    'stg.settings.types': "أنواع التداريب",
    'stg.settings.followTypes': "أنواع التتبع",
    'stg.settings.rolesNote': "أدوار التأطير ثابتة: مؤطر بيداغوجي (أستاذ)، مؤطر داخل المؤسسة (جهة اتصال)، أعضاء اللجنة.",
    'stg.settings.module': "الوحدة",
    'stg.settings.moduleSub': "خاص بالمشرف العام",
    'stg.settings.enabled': "وحدة التداريب مفعلة",
    'stg.settings.enabledHint': "تفعيل التداريب يفعّل أيضاً العلاقات الخارجية: دليل المؤسسات مشترك.",
    'stg.settings.enabledOn': "فُعّل في {date} · {who}",
    'stg.settings.relationsOff': "العلاقات الخارجية معطلة: ستُفعّل مع التداريب.",
    'stg.settings.rules': "التذكيرات والقواعد",
    'stg.settings.idle': "تذكير المؤطر بدون تتبع منذ (أيام)",
    'stg.settings.idleHint': "للتداريب الجارية",
    'stg.settings.reminder': "تذكير الطالب قبل أجل التقرير (أيام)",
    'stg.settings.reminderHint': "وما دام التقرير متأخراً",
    'stg.settings.studentsPropose': "يمكن للطلبة اقتراح تدريبهم من فضائهم",
    'stg.settings.countSupervision': "احتساب التأطير في الساعات المنجزة للأساتذة",
    'stg.settings.countSupervisionHint': "نعم: كل تدريب موقع يُحتسب لمؤطره بساعات التأطير الخاصة بنوعه عند نهايته؛ وتحتسب في الخدمة والأجر.",
    'stg.type.new': "نوع تدريب جديد",
    'stg.type.edit': "نوع التدريب · {label}",
    'stg.type.sub': "مدته ومناقشته ووثائقه المطلوبة وشبكة تقييمه",
    'stg.type.label': "نوع التدريب",
    'stg.type.minMonths': "المدة الدنيا (أشهر)",
    'stg.type.minMonthsHint': "يُراقب عند تسجيل التدريب",
    'stg.type.months': "{months} أشهر",
    'stg.type.reportDays': "التقرير منتظر (أيام بعد النهاية)",
    'stg.type.reportDaysHint': "ما لم تحدد الحملة تاريخاً",
    'stg.type.supervision': "ساعات التأطير",
    'stg.type.supervisionHint': "تُحتسب للمؤطر إذا نص الإعداد على ذلك",
    'stg.type.defense': "مناقشة أمام لجنة",
    'stg.type.defenseShort': "المناقشة",
    'stg.type.active': "مفعل",
    'stg.type.documents': "الوثائق المطلوبة",
    'stg.type.due': "الأجل",
    'stg.type.isReport': "هذا هو التقرير",
    'stg.type.addDocument': "إضافة وثيقة",
    'stg.type.grid': "شبكة التقييم",
    'stg.type.addComponent': "إضافة مكون",
    'stg.type.total': "المجموع: {total} % (100 % منتظرة)",
    'stg.type.defaultConvention': "اتفاقية التدريب الموقعة",
    'stg.type.defaultReport': "تقرير التدريب",
    'stg.type.deactivateText': "«{label}» مستعمل: سيُعطّل ولن يُقترح للحملات الجديدة.",
    'stg.type.removeText': "سيُحذف «{label}» مع وثائقه وشبكته.",
    'stg.followType.new': "نوع تتبع جديد",
    'stg.followType.edit': "نوع التتبع",
    'stg.followType.label': "التسمية",
    'rel.modules.stages': "التداريب",
    'rel.modules.stagesHint': "الحملات والتداريب والعروض والمناقشات (مع العلاقات الخارجية)",
    'rel.modules.relationsOffStages': "ستُعطّل التداريب التي تعتمد عليها أيضاً.",
    'fac.dash.supervision': "منها {hours} من تأطير التداريب",
    'violation.stg.too_short': "المدة أقل من الحد الأدنى لهذا النوع.",
    'violation.stg.duplicate': "لهذا الطالب تدريب في هذه الحملة.",
    'violation.stg.bad_state': "إجراء غير ممكن في هذه المرحلة.",
    'violation.stg.period': "النهاية قبل البداية.",
    'violation.stg.future_follow_up': "يُسجل التتبع بعد إنجازه.",
    'violation.stg.not_your_component': "ينقط المؤطر مكوناته فقط.",
    'violation.stg.mark_missing': "مكون من الشبكة غير منقط.",
    'violation.stg.evaluated': "التقييم مصادق عليه: أعد فتحه لتغيير النقط.",
    'violation.stg.no_subject': "لا تحدد الحملة عنصر البرنامج الذي يتلقى النقطة.",
    'violation.stg.assessment_validated': "نقط هذا العنصر مصادق عليها: أعد فتح التقييم.",
    'violation.stg.no_assessment_type': "لا يوجد نوع تقييم في المؤسسة.",
    'violation.stg.weights': "يجب أن يكون مجموع الأوزان 100 %.",
    'violation.stg.two_reports': "تقرير واحد لكل نوع.",
    'violation.stg.requirement_used': "استُلمت هذه الوثيقة لتداريب: لا يمكن سحبها.",
    'violation.stg.component_used': "هذا المكون منقط: لا يمكن سحبه.",
    'violation.stg.campaign_closed': "هذه الحملة لا تخصك.",
    'violation.stg.bad_training': "اختر تكويناً من سنة الحملة.",
    'violation.stg.bad_subject': "اختر عنصراً من هذا التكوين.",
    'violation.stg.duplicate_training': "تكوين مكرر.",
    'violation.stg.no_training': "اختر تكويناً واحداً على الأقل.",
    'violation.stg.campaign_used': "لهذه الحملة تداريب: لا يمكن حذفها.",
    'violation.stg.report_before_end': "يُسلم التقرير بعد نهاية التدريب.",
    'violation.stg.other_campaign': "هذا اليوم لحملة أخرى.",
    'violation.stg.slot_taken': "هذا الموعد محجوز.",
    'violation.stg.juror_busy': "أحد أعضاء اللجنة مشغول في هذا الوقت.",
    'violation.stg.juror': "أستاذ أو جهة اتصال.",
    'violation.stg.juror_duplicate': "عضو مكرر.",
    'violation.stg.two_presidents': "رئيس واحد فقط.",
    'violation.stg.offer_expired': "انتهى الأجل: غيّره قبل النشر.",
    'violation.stg.offer_used': "لهذا العرض ترشيحات: أغلقه بدل حذفه.",
    'violation.stg.offer_closed': "هذا العرض غير مفتوح لك.",
    'violation.stg.already_applied': "سبق أن ترشحت لهذا العرض.",
    'violation.stg.application_linked': "لهذا الترشيح تدريبه.",
    'trace.table.stages_settings': "إعدادات التداريب",
    'trace.table.stages_type': "نوع التدريب",
    'trace.table.stages_type_document': "وثيقة مطلوبة",
    'trace.table.stages_type_component': "مكون الشبكة",
    'trace.table.stages_follow_type': "نوع التتبع",
    'trace.table.stages_campaign': "حملة التداريب",
    'trace.table.stages_campaign_training': "تكوين الحملة",
    'trace.table.stages_offer': "العرض",
    'trace.table.stages_offer_training': "تكوين مستهدف بعرض",
    'trace.table.stages_application': "الترشيح",
    'trace.table.internship': "التدريب",
    'trace.table.internship_document': "وثيقة التدريب",
    'trace.table.internship_follow_up': "تتبع التدريب",
    'trace.table.internship_mark': "نقطة الشبكة",
    'trace.table.stages_defense_day': "يوم المناقشات",
    'trace.table.internship_defense': "المناقشة",
    'trace.table.internship_defense_juror': "عضو اللجنة",
    'trace.field.alumni': "الخريجون",
    'trace.field.assessment_type_id': "نوع التقييم",
    'trace.field.break_at': "الاستراحة على الساعة",
    'trace.field.break_minutes': "مدة الاستراحة",
    'trace.field.campaign_id': "الحملة",
    'trace.field.closes_on': "الأجل",
    'trace.field.closing_reason': "السبب",
    'trace.field.component_id': "المكون",
    'trace.field.count_supervision': "احتساب التأطير",
    'trace.field.cv_name': "السيرة الذاتية",
    'trace.field.cv_size': "حجم السيرة",
    'trace.field.day_id': "اليوم",
    'trace.field.defense_id': "المناقشة",
    'trace.field.defense_required': "المناقشة",
    'trace.field.due': "الأجل",
    'trace.field.duration': "المدة",
    'trace.field.evaluated_at': "قُيّم في",
    'trace.field.evaluated_by_id': "قيّمه",
    'trace.field.evaluator': "يقيّمه",
    'trace.field.file_name': "الملف",
    'trace.field.file_size': "حجم الملف",
    'trace.field.internship_id': "التدريب",
    'trace.field.jury_comment': "ملاحظة اللجنة",
    'trace.field.mark_reported_at': "نُقلت النقطة في",
    'trace.field.min_months': "المدة الدنيا (أشهر)",
    'trace.field.missions': "المهام",
    'trace.field.offer_id': "العرض",
    'trace.field.proposed_by_student': "مقترح من الطالب",
    'trace.field.received_by_id': "استلمها",
    'trace.field.report': "التقرير",
    'trace.field.report_days': "مهلة التقرير",
    'trace.field.report_due_on': "التقرير قبل",
    'trace.field.report_reminder_days': "تذكير التقرير",
    'trace.field.requirement_id': "الوثيقة المطلوبة",
    'trace.field.role': "الدور",
    'trace.field.slot_minutes': "مدة المناقشة",
    'trace.field.student_present': "المتدرب حاضر",
    'trace.field.students_propose': "اقتراح الطلبة",
    'trace.field.supervision_minutes': "دقائق التأطير",
    'trace.field.supervisor_id': "المؤطر البيداغوجي",
    'trace.field.supervisor_idle_days': "تذكير المؤطر",
    'trace.field.supervisor_since': "مؤطر منذ",
    'trace.field.topic': "الموضوع",
    'trace.field.tutor_id': "المؤطر المهني",
    'trace.field.weight': "الوزن",
    'trace.value.proposed': "مقترح",
    'trace.value.evaluated': "مقيّم",
    'trace.value.refused': "مرفوض",
    'trace.value.kelassi': "Kelassi",
    'trace.value.history': "مسار الخريجين",
    'trace.value.before_start': "قبل البداية",
    'trace.value.at_end': "في النهاية",
    'trace.value.after_end': "بعد النهاية",
    'trace.value.tutor': "المؤطر المهني",
    'trace.value.supervisor': "المؤطر",
    'trace.value.jury': "اللجنة",
    'trace.value.internship': "تدريب",
    'trace.value.apprenticeship': "تكوين بالتناوب",
    'trace.value.job': "وظيفة",
    'trace.value.published': "منشور",
    'trace.value.new': "جديد",
    'trace.value.forwarded': "محال",
    'trace.value.retained': "مقبول",
    'trace.value.declined': "غير مقبول",
    'trace.value.president': "الرئيس",
    'trace.value.member': "عضو",
    'form.required': "هذا الحقل إلزامي.",
    'form.requiredMark': "إلزامي",
    'form.legend': "الحقول المشار إليها بـ * إلزامية",
    'form.summary': "{count} حقل (حقول) يجب تصحيحها قبل الحفظ",
    'form.pasteCut': "تم قص النص الملصق إلى {max} حرفًا.",
    'form.counter': "{count} من {max} حرفًا",
    'rel.modules.requests': "الطلبات",
    'rel.modules.requestsHint': "الشهادات وكشوف النقط وتبرير الغياب وتعديل النقط وطلبات أخرى",
    'req.status.new': "جديد",
    'req.status.ongoing': "قيد المعالجة",
    'req.status.waiting': "في انتظار تكملة",
    'req.status.done': "معالج",
    'req.status.rejected': "مرفوض",
    'req.status.cancelled': "ملغى",
    'req.status.late': "متأخر",
    'req.when.today': "اليوم {time}",
    'req.when.yesterday': "أمس {time}",
    'req.delay.suspended': "معلق",
    'req.delay.doneIn': "عولج في {count} يوم",
    'req.delay.late': "متأخر بـ {count} يوم",
    'req.delay.today': "اليوم",
    'req.delay.tomorrow': "غدا",
    'req.delay.inDays': "بعد {count} أيام",
    'req.days': "{value} يوم",
    'req.noTeam': "بدون فريق",
    'req.open': "فتح",
    'req.choose': "اختر…",
    'req.breadcrumb': "مسار التنقل",
    'req.files.label': "المرفقات",
    'req.files.drop': "ضع ملف PDF أو الصورة (PNG، JPEG)، 10 ميغا على الأكثر",
    'req.files.uploading': "جار الإرسال…",
    'req.files.remove': "إزالة",
    'req.files.see': "عرض",
    'req.files.add': "إضافة مرفق",
    'req.dashboard.title': "الطلبات",
    'req.dashboard.sub': "طلبات الطلبة والأساتذة والموظفين",
    'req.kpi.open': "للمعالجة",
    'req.kpi.openNote': "{count} جديد لم يعالج بعد",
    'req.kpi.late': "متأخرة",
    'req.kpi.lateNote': "تجاوز أجل النوع",
    'req.kpi.waiting': "في انتظار تكملة",
    'req.kpi.waitingNote': "في انتظار صاحب الطلب",
    'req.kpi.done': "عولجت هذا الشهر",
    'req.kpi.doneNote': "المدة المتوسطة {days}",
    'req.kpi.doneNoteEmpty': "لا شيء بعد هذا الشهر",
    'req.dashboard.byType': "حسب النوع",
    'req.dashboard.byTypeSub': "الطلبات المفتوحة ومدة المعالجة",
    'req.dashboard.noType': "لا يوجد نوع طلب.",
    'req.dashboard.byTeam': "حسب الفريق",
    'req.dashboard.byTeamSub': "الطلبات قيد المعالجة",
    'req.dashboard.nothingOpen': "لا يوجد طلب قيد المعالجة.",
    'req.dashboard.mine': "لي ولفريقي",
    'req.dashboard.allMine': "كل قائمتي",
    'req.dashboard.nothingMine': "لا شيء للمعالجة.",
    'req.column.type': "نوع الطلب",
    'req.column.open': "قيد المعالجة",
    'req.column.late': "متأخرة",
    'req.column.target': "الأجل المحدد",
    'req.column.average': "المدة المتوسطة",
    'req.column.number': "الرقم",
    'req.column.requester': "صاحب الطلب",
    'req.column.received': "تاريخ الاستلام",
    'req.column.team': "الفريق",
    'req.column.state': "الحالة",
    'req.column.delay': "الأجل",
    'req.column.request': "الطلب",
    'req.column.filed': "تاريخ الإيداع",
    'req.list.title': "الطلبات",
    'req.list.sub': "{open} طلب مفتوح · {late} متأخر",
    'req.view.mine': "قائمتي",
    'req.view.team': "فريقي",
    'req.view.open': "كل المفتوحة",
    'req.view.late': "متأخرة",
    'req.view.waiting': "في انتظار تكملة",
    'req.view.closed': "مغلقة",
    'req.view.count': "{label} ({count})",
    'req.list.search': "بحث",
    'req.list.searchPlaceholder': "الرقم، صاحب الطلب، الموضوع",
    'req.list.allTypes': "كل الأنواع",
    'req.list.empty': "لا يوجد طلب في هذا العرض.",
    'req.list.note': "« قائمتي »: الطلبات التي أسندت إلي مرحلتها الحالية أو إلى فرقي؛ « فريقي »: طلبات فرق المعالجة الخاصة بي. يحتسب الأجل منذ الاستلام ويتوقف أثناء انتظار تكملة.",
    'req.population.student': "طالب",
    'req.population.teacher': "أستاذ",
    'req.population.staff': "موظف",
    'req.kind.student': "طالب",
    'req.kind.teacher': "أستاذ",
    'req.audience.student': "الطلبة",
    'req.audience.teacher': "الأساتذة",
    'req.audience.staff': "الموظفون",
    'req.produces.none': "لا شيء (جواب مكتوب)",
    'req.produces.enrolment': "شهادة مدرسية (الوثائق)",
    'req.produces.transcript': "كشف النقط (الوثائق)",
    'req.produces.certificate': "شهادة النجاح (الوثائق)",
    'req.produces.mark_change': "تصحيح النقط (النقط)",
    'req.produces.absence': "غياب مبرر (المواظبة)",
    'req.produces.internship': "تدريب مقترح (التداريب)",
    'req.produce.none': "معالجة",
    'req.produce.enrolment': "إصدار الشهادة والمعالجة",
    'req.produce.transcript': "إصدار الكشف والمعالجة",
    'req.produce.certificate': "إصدار الشهادة والمعالجة",
    'req.produce.mark_change': "تطبيق النقط والمعالجة",
    'req.produce.absence': "تبرير الغياب والمعالجة",
    'req.produce.internship': "معالجة",
    'req.field.year': "السنة",
    'req.field.deliberation': "المداولة",
    'req.field.language': "لغة الوثيقة",
    'req.field.copies': "عدد النسخ",
    'req.field.startsOn': "من",
    'req.field.endsOn': "إلى",
    'req.field.justificationKind': "سبب الغياب",
    'req.field.establishment': "المؤسسة المعنية",
    'req.field.reason': "السبب",
    'req.field.description': "طلبك",
    'req.field.assessment': "التقييم",
    'req.field.line': "النقطة",
    'req.field.other': "حقل",
    'req.lang.fr': "الفرنسية",
    'req.lang.en': "الإنجليزية",
    'req.lang.ar': "العربية",
    'req.rule.hidden': "مخفي",
    'req.rule.shown': "معروض",
    'req.rule.required': "إجباري",
    'req.form.delay': "الأجل المحدد: {count} يوم",
    'req.form.attachments.hidden': "المرفقات: لا شيء",
    'req.form.attachments.shown': "المرفقات: اختيارية",
    'req.form.attachments.required': "المرفق إجباري",
    'req.form.noDeliberation': "لا توجد مداولة مغلقة تشملك.",
    'req.ask.sub': "الأجل هو الذي تلتزم به المؤسسة",
    'req.ask.submit': "إرسال الطلب",
    'req.ask.message': "رسالة إلى المؤسسة",
    'req.new.title': "طلب جديد",
    'req.new.sub': "يسجله الموظف لفائدة طالب أو أستاذ",
    'req.new.type': "نوع الطلب",
    'req.new.for': "لفائدة",
    'req.new.forHint': "الاسم أو الرقم، حرفان على الأقل",
    'req.new.change': "تغيير",
    'req.new.pickPerson': "اختر الشخص أولا.",
    'req.new.pickType': "اختر نوع الطلب.",
    'req.new.handleNow': "المعالجة فورا (الإصدار والإغلاق)",
    'req.new.handleNowNone': "المعالجة فورا (الإغلاق)",
    'req.file.received': "استلم في {date}",
    'req.file.closed': "أغلق في {date}",
    'req.file.suspended': "الأجل معلق",
    'req.file.due': "يعالج قبل {date}",
    'req.file.team': "مسند إلى {team}",
    'req.file.teamAssignee': "مسند إلى {team} ({name})",
    'req.file.document': "تحميل الوثيقة",
    'req.file.rejected': "رفض الطلب: {reason}",
    'req.file.cancelled': "ألغي الطلب: {reason}",
    'req.file.request': "الطلب",
    'req.file.noFields': "لا يوجد حقل معبأ.",
    'req.file.files': "المرفقات",
    'req.file.noFiles': "لا يوجد مرفق.",
    'req.file.thread': "المراسلات",
    'req.file.threadSub': "يرى صاحب الطلب الرسائل دون الملاحظات الداخلية",
    'req.file.noMessages': "لا توجد رسالة.",
    'req.file.actions': "الإجراءات",
    'req.file.step': "المرحلة « {step} »",
    'req.file.waitingNote': "في انتظار تكملة من صاحب الطلب: الأجل معلق.",
    'req.file.circuit': "المسار",
    'req.file.where': "أين وصل الطلب؟",
    'req.file.history': "السجل",
    'req.circuit.received': "استلم",
    'req.circuit.current': "قيد المعالجة",
    'req.circuit.suspended': "في انتظار صاحب الطلب",
    'req.event.received': "أودع",
    'req.event.assigned': "تكفل",
    'req.event.advanced': "اجتاز المرحلة",
    'req.event.waiting': "طلبت تكملة",
    'req.event.completed': "أرسلت التكملة",
    'req.event.forwarded': "أحيل",
    'req.event.done': "عولج",
    'req.event.rejected': "رفض",
    'req.event.cancelled': "ألغي",
    'req.event.failed': "فشل التنفيذ",
    'req.outcome.document': "الوثيقة الصادرة: {reference}",
    'req.outcome.marks': "تصحيح {count} نقطة مع التتبع",
    'req.outcome.absence': "غياب مبرر؛ تغطية {count} غياب مسجل",
    'req.outcome.done': "عولج الطلب.",
    'req.thread.internal': "ملاحظة داخلية",
    'req.thread.internalBox': "ملاحظة داخلية",
    'req.thread.write': "الكتابة إلى صاحب الطلب",
    'req.thread.writeStaff': "الكتابة إلى المؤسسة",
    'req.thread.send': "إرسال",
    'req.act.assign': "التكفل بها",
    'req.act.entrust': "إسناد إلى…",
    'req.assign.team': "فريق {team} · مرحلة « {step} »",
    'req.assign.any': "الحسابات التي تعالج الطلبات",
    'req.assign.me': "أنا",
    'req.assign.current': "المسؤول",
    'req.assign.hint': "يتلقى الشخص المختار إشعارا برابط الطلب.",
    'req.flash.entrusted': "أسند الطلب إلى {name}.",
    'req.act.advance': "إتمام المرحلة",
    'req.act.advanceExecutes': "الإصدار والمعالجة",
    'req.act.advanceSub': "تجتاز المرحلة؛ وفي الأخيرة يعالج الطلب ويُشعَر صاحبه.",
    'req.act.waiting': "طلب تكملة",
    'req.act.waitingSub': "يتلقى صاحب الطلب رسالتك؛ ويعلق الأجل إلى حين رده.",
    'req.act.waitingText': "ما ينقص",
    'req.act.forward': "الإحالة إلى فريق آخر",
    'req.act.forwardSub': "يبقى الطلب في نفس المرحلة ويسند إلى الفريق المختار.",
    'req.act.team': "الفريق",
    'req.act.reject': "رفض",
    'req.act.rejectSub': "يتلقى صاحب الطلب السبب.",
    'req.act.cancel': "إلغاء الطلب",
    'req.act.cancelSub': "يغلق الطلب دون متابعة.",
    'req.act.reason': "السبب",
    'req.act.reasonOptional': "السبب (اختياري)",
    'req.act.note': "ملاحظة (اختيارية)",
    'req.flash.advance': "اجتيزت المرحلة.",
    'req.flash.waiting': "طلبت التكملة.",
    'req.flash.forward': "أحيل الطلب.",
    'req.flash.reject': "رفض الطلب.",
    'req.flash.cancel': "ألغي الطلب.",
    'req.flash.assigned': "أسند إليك الطلب.",
    'req.flash.attached': "أضيف المرفق.",
    'req.flash.completed': "أرسلت التكملة: يستأنف الطلب.",
    'req.complete.title': "تكملة الطلب",
    'req.complete.sub': "الأجل معلق إلى حين ردك",
    'req.complete.message': "رسالة إلى المؤسسة",
    'req.complete.submit': "إرسال التكملة",
    'req.mine.title': "طلباتي",
    'req.mine.ask': "تقديم طلب",
    'req.mine.askSub': "الأجل هو الذي تلتزم به المؤسسة",
    'req.mine.noType': "لا يوجد طلب مفتوح حاليا.",
    'req.mine.within': "في أجل {count} يوم",
    'req.mine.viaStages': "من « تدريبي »",
    'req.mine.askButton': "طلب",
    'req.mine.list': "طلباتي",
    'req.mine.empty': "لا يوجد طلب بعد.",
    'req.mine.download': "تحميل الوثيقة",
    'req.mine.complete': "تكملة",
    'req.mine.follow': "تتبع",
    'req.mine.note': "تتلقى إشعارا في الجرس عند كل مرحلة؛ وتحمل الوثيقة الصادرة من هنا.",
    'req.marks.button': "طلب تعديل",
    'req.marks.title': "طلب تعديل النقط",
    'req.marks.sub': "أدخل النقط المراد تعديلها",
    'req.marks.count': "{count} نقطة للتعديل",
    'req.marks.pending': "{count} طلب تعديل قيد المعالجة لهذا التقييم.",
    'req.marks.submit': "إرسال الطلب",
    'req.marks.assessment': "{label} · على {outOf} · نقط مصادق عليها ومقفلة",
    'req.marks.student': "الطالب",
    'req.marks.current': "النقطة الحالية",
    'req.marks.asked': "النقطة المطلوبة",
    'req.marks.absence': "أو غياب",
    'req.marks.note': "توضيح",
    'req.marks.askedFor': "النقطة المطلوبة لـ {name}",
    'req.marks.absenceFor': "غياب {name}",
    'req.marks.noteFor': "توضيح لـ {name}",
    'req.marks.circuit': "يتبع الطلب المسار المحدد (رأي إدارة الدراسات ثم التطبيق من طرف الامتحانات): تحتفظ كل نقطة معدلة بالقيمة السابقة والسبب. يجب إعادة فتح المداولة المغلقة أولا.",
    'req.marks.sent': "أرسل طلب التعديل.",
    'req.settings.title': "الطلبات · الإعدادات",
    'req.settings.sub': "أنواع الطلبات وحقولها ومسارها وفرق المعالجة",
    'req.settings.module': "وحدة الطلبات",
    'req.settings.moduleSub': "عند إيقافها تسحب الوحدة صلاحياتها من القائمة والشاشات دون حذف أي شيء",
    'req.settings.enabled': "الوحدة مفعلة",
    'req.settings.enabledHint': "يودع الطلبة والأساتذة طلباتهم ويعالجها الموظفون",
    'req.settings.enabledOn': "فعلت في {date} من طرف {who}",
    'req.settings.switchedOn': "فعلت وحدة الطلبات.",
    'req.settings.switchedOff': "أوقفت وحدة الطلبات.",
    'req.settings.types': "أنواع الطلبات",
    'req.settings.audience': "من يمكنه الطلب",
    'req.settings.produces': "ما ينتجه الطلب",
    'req.settings.inactive': "غير نشط",
    'req.settings.deactivate': "تعطيل",
    'req.settings.deactivateText': "« {label} » له طلبات: سيعطل ولن يتمكن أحد من طلبه.",
    'req.settings.deleteText': "حذف النوع « {label} » ومساره؟",
    'req.settings.pick': "اختر نوعا لضبطه.",
    'req.settings.newType': "نوع جديد",
    'req.settings.selected': "النوع المختار",
    'req.settings.label': "التسمية",
    'req.settings.actionLocked': "لهذا النوع طلبات: لم يعد ما ينتجه قابلا للتغيير.",
    'req.settings.opensOn': "مفتوح من",
    'req.settings.closesOn': "إلى",
    'req.settings.delay': "الأجل المحدد (أيام)",
    'req.settings.delayHint': "على الأقل مجموع آجال المراحل",
    'req.settings.color': "اللون",
    'req.settings.guidance': "ما يجب معرفته (يعرض لصاحب الطلب)",
    'req.settings.active': "نشط",
    'req.settings.fields': "حقول الاستمارة",
    'req.settings.fieldsSub': "مخفي أو معروض أو إجباري",
    'req.settings.field': "الحقل",
    'req.settings.forced': "يفرضه ما ينتجه الطلب",
    'req.settings.circuit': "مسار المعالجة",
    'req.settings.circuitSub': "الأجل الإجمالي: {days} يوم",
    'req.settings.addStep': "إضافة مرحلة",
    'req.settings.noSteps': "لا توجد مرحلة: أضف واحدة على الأقل لفتح هذا النوع.",
    'req.settings.noStepsInternship': "لا يوجد مسار: تقترح اتفاقية التدريب من « تدريبي ».",
    'req.settings.step': "المرحلة",
    'req.settings.executes': "ينتج",
    'req.settings.executesNote.action': "مرحلة واحدة تنتج {what}؛ والأخرى تصادق أو تطلب تكملة.",
    'req.settings.executesNote.none': "لا ينتج هذا النوع شيئا: كل مرحلة تصادق والأخيرة تغلق الطلب بجواب مكتوب.",
    'req.settings.up': "رفع",
    'req.settings.down': "خفض",
    'req.settings.teams': "فرق المعالجة",
    'req.settings.teamsSub': "حسابات أو كل حاملي دور",
    'req.settings.roleHolders': "حاملو دور {role}",
    'req.settings.members': "{count} عضو مسمى",
    'req.settings.typeSaved': "حفظ النوع.",
    'req.settings.teamSaved': "حفظ الفريق.",
    'req.settings.teamDeactivated': "للفريق طلبات: تم تعطيله.",
    'req.team.new': "فريق جديد",
    'req.team.edit': "تعديل الفريق",
    'req.team.label': "اسم الفريق",
    'req.team.role': "حاملو دور",
    'req.team.roleHint': "كل الحسابات الحاملة لهذا الدور أعضاء",
    'req.team.noRole': "بدون دور",
    'req.team.members': "الحسابات الأعضاء",
    'req.team.membersHint': "{count} حساب مختار",
    'req.team.search': "تصفية الحسابات",
    'violation.req.action_locked': "لهذا النوع طلبات: لم يعد ما ينتجه قابلا للتغيير.",
    'violation.req.assessment_open': "يصحح التقييم المفتوح مباشرة.",
    'violation.req.attachment_required': "أرفق الوثيقة المطلوبة.",
    'violation.req.bad_deliberation': "اختر مداولة مغلقة تشمل الشخص.",
    'violation.req.closed': "هذا الطلب مغلق.",
    'violation.req.closed_type': "هذا النوع من الطلبات مغلق اليوم.",
    'violation.req.mark_locked': "لم يعد التقييم قابلا للتصحيح (غير مصادق عليه أو السنة مغلقة).",
    'violation.req.no_audience': "جمهور واحد على الأقل.",
    'violation.req.no_enrolment': "لا يوجد تسجيل نشط في تلك السنة.",
    'violation.req.no_execution': "يجب أن تنتج مرحلة ما ينتجه النوع.",
    'violation.req.no_lines': "نقطة واحدة على الأقل للتعديل.",
    'violation.req.no_steps': "ليس لهذا النوع مسار معالجة.",
    'violation.req.not_a_student': "هذا الطلب يخص طالبا.",
    'violation.req.not_admitted': "شهادة النجاح خاصة بالناجحين.",
    'violation.req.not_for_you': "هذا النوع غير مفتوح لهذا الشخص.",
    'violation.req.not_issued': "لم تصدر أي وثيقة.",
    'violation.req.not_waiting': "لا توجد تكملة منتظرة.",
    'violation.req.one_execution': "مرحلة واحدة فقط تنتج ما ينتجه الطلب.",
    'violation.req.too_many_files': "خمسة مرفقات على الأكثر.",
    'violation.req.use_stages': "يقترح هذا النوع من « تدريبي ».",
    'violation.req.waiting': "في انتظار تكملة من صاحب الطلب.",
    'journal.action.requests.settings_changed': "تعديل إعدادات الطلبات",
    'journal.action.requests.type_saved': "حفظ نوع طلب",
    'journal.action.requests.team_saved': "حفظ فريق معالجة",
    'journal.action.requests.request_created': "إيداع طلب",
    'journal.action.requests.request_step_changed': "تقدم طلب",
    'journal.action.requests.request_closed': "إغلاق طلب",
    'journal.filter.group.requests': "الطلبات",
    'trace.table.requests_settings': "إعدادات الطلبات",
    'trace.table.requests_team': "فريق المعالجة",
    'trace.table.requests_team_member': "عضو فريق",
    'trace.table.requests_type': "نوع الطلب",
    'trace.table.requests_type_step': "مرحلة المسار",
    'trace.table.requests_request': "الطلب",
    'trace.table.requests_message': "رسالة الطلب",
    'trace.table.requests_file': "مرفق الطلب",
    'trace.table.requests_event': "سجل الطلب",
    'doc.type.enrolment': "شهادة مدرسية",
    'doc.types.enrolment': "الشهادات المدرسية",
    'doc.verify.bodyEnrolment': "{type} الخاصة بـ {student}، {training} {year}، {institution}؛ صدرت في {date} من طرف {by} انطلاقا من ملف التسجيل.",
    'doc.settings.hint.enrolment': "دون موقع محدد: « عن الإدارة » واسم من أصدرها",
    'doc.settings.default.enrolment': "لا يوجد موقع محدد: « عن الإدارة » واسم من أصدرها.",
    'violation.doc.enrolment_inactive': "لا تسلم الشهادة المدرسية إلا لتسجيل نشط.",
    'violation.doc.not_from_deliberation': "تصدر الشهادة المدرسية انطلاقا من التسجيل وليس من المداولة.",
    'heb.add': "إضافة",
    'heb.all': "الكل",
    'heb.allCampuses': "كل الأحياء",
    'heb.allCategories': "كل الفئات",
    'heb.allGenders': "كل الأجناس",
    'heb.allLevels': "كل المستويات",
    'heb.allRegimes': "كل الأنظمة",
    'heb.breadcrumb': "مسار التنقل",
    'heb.change': "تغيير",
    'heb.choose': "اختيار",
    'heb.confirm': "تأكيد",
    'heb.disability': "وضعية إعاقة",
    'heb.edit': "تعديل",
    'heb.no': "لا",
    'heb.none': "لا شيء",
    'heb.open': "فتح",
    'heb.optional': "اختيارية",
    'heb.remove': "حذف",
    'heb.scholar': "ممنوح",
    'heb.notScholar': "غير ممنوح",
    'heb.search': "بحث",
    'heb.total': "المجموع",
    'heb.docs.none': "لا وثيقة مطلوبة.",
    'heb.field.campus': "الحي",
    'heb.field.gender': "الجنس",
    'heb.field.level': "المستوى",
    'heb.field.regime': "النظام",
    'heb.field.year': "السنة",
    'heb.gender.F': "إ",
    'heb.gender.M': "ذ",
    'heb.person.search': "الاسم أو رقم التسجيل، حرفان على الأقل",
    'heb.person.student': "الطالب",
    'heb.upkeep.title': "الزيارات والإصلاحات",
    'heb.app.draft': "مسودة",
    'heb.app.submitted': "مودع",
    'heb.app.incomplete': "وثيقة مطلوبة",
    'heb.app.eligible': "مقبول",
    'heb.app.waitlisted': "لائحة الانتظار",
    'heb.app.rejected': "مرفوض",
    'heb.app.provisional': "سرير مقترح",
    'heb.app.accepted': "في انتظار الأداء",
    'heb.app.final': "تخصيص نهائي",
    'heb.app.housed': "مقيم",
    'heb.app.withdrawn': "متنازل عنه",
    'heb.app.left': "غادر",
    'heb.alloc.provisional': "مؤقت",
    'heb.alloc.accepted': "مقبول، في انتظار الأداء",
    'heb.alloc.final': "نهائي",
    'heb.alloc.housed': "مقيم",
    'heb.alloc.released': "محرر",
    'heb.alloc.ended': "منته",
    'heb.campaign.draft': "مبرمجة",
    'heb.campaign.open': "الإيداع مفتوح",
    'heb.campaign.processing': "قيد المعالجة",
    'heb.campaign.closed': "مغلقة",
    'heb.repair.reported': "مبلغ عنه",
    'heb.repair.planned': "مبرمج",
    'heb.repair.in_progress': "قيد الإنجاز",
    'heb.repair.done': "منجز",
    'heb.repair.closed': "مغلق",
    'heb.priority.urgent': "مستعجل",
    'heb.priority.high': "مرتفع",
    'heb.priority.normal': "عادي",
    'heb.fileStatus.pending': "للتحقق",
    'heb.fileStatus.accepted': "مطابقة",
    'heb.fileStatus.rejected': "مرفوضة",
    'heb.payment.submitted': "للمصادقة",
    'heb.payment.validated': "مصادق عليه",
    'heb.payment.refused': "مرفوض",
    'heb.method.transfer': "تحويل بنكي",
    'heb.method.deposit': "إيداع بنكي",
    'heb.method.cash': "نقدا",
    'heb.method.cheque': "شيك",
    'heb.roomGender.female': "إناث",
    'heb.roomGender.male': "ذكور",
    'heb.roomGender.mixed': "مختلط",
    'heb.roomGenderShort.female': "إ",
    'heb.roomGenderShort.male': "ذ",
    'heb.roomGenderShort.mixed': "مختلط",
    'heb.bedNature.single': "سرير فردي",
    'heb.bedNature.double': "سرير مزدوج",
    'heb.bedNature.bunk': "سرير بطابقين",
    'heb.check.ok': "مطابق",
    'heb.check.warn': "للمراجعة",
    'heb.check.ko': "غير مطابق",
    'heb.checklistKind.inspection': "زيارة مراقبة",
    'heb.checklistKind.check_in': "محضر الدخول",
    'heb.checklistKind.check_out': "محضر الخروج",
    'heb.stay.good': "جيد",
    'heb.stay.worn': "مستهلك",
    'heb.stay.damaged': "متضرر",
    'heb.source.student': "بلغ عنه المقيم",
    'heb.source.inspection': "ملاحظة زيارة",
    'heb.source.staff': "بلغ عنه موظف",
    'heb.quota.level': "المستوى",
    'heb.quota.scholarship': "الممنوحون",
    'heb.quota.foreign': "الطلبة الأجانب",
    'heb.quota.disability': "وضعية إعاقة",
    'heb.quota.newcomer': "المسجلون الجدد",
    'heb.weight.distance': "بعد السكن العائلي",
    'heb.weight.scholarship': "المنحة",
    'heb.weight.results': "نتائج السنة الماضية",
    'heb.weight.disability': "وضعية إعاقة",
    'heb.weight.seniority': "الأقدمية في الداخلية",
    'heb.rule.gender': "احترام جنس الغرف",
    'heb.rule.quotas': "تطبيق الحصص",
    'heb.rule.roommates': "جمع الشركاء الذين اختار بعضهم بعضا",
    'heb.rule.reserve': "الاحتفاظ بالأسرة المحجوزة",
    'heb.rule.otherCampus': "اقتراح حي آخر إذا امتلأ الاختيار",
    'heb.reason.cap': "لا مكان شاغر",
    'heb.reason.quota': "الحصة مستوفاة",
    'heb.reason.cabin': "لا غرفة ملائمة",
    'heb.reason.price': "لا تعريفة",
    'heb.column.actions': "إجراءات",
    'heb.column.agent': "العون",
    'heb.column.applications': "الطلبات",
    'heb.column.bed': "السرير",
    'heb.column.beds': "الأسرة",
    'heb.column.campaign': "الحملة",
    'heb.column.campus': "الحي",
    'heb.column.category': "الفئة",
    'heb.column.checkpoint': "نقطة المراقبة",
    'heb.column.collected': "المحصل",
    'heb.column.conformity': "المطابقة",
    'heb.column.criterion': "المعيار",
    'heb.column.distance': "البعد",
    'heb.column.due': "المستحق",
    'heb.column.endsOn': "الخروج",
    'heb.column.files': "الوثائق",
    'heb.column.finding': "الملاحظة",
    'heb.column.findings': "الملاحظات",
    'heb.column.free': "شاغرة",
    'heb.column.inventory': "في المرافق",
    'heb.column.key': "المفتاح",
    'heb.column.number': "الرقم",
    'heb.column.occupancy': "نسبة الإشغال",
    'heb.column.occupant': "المقيم",
    'heb.column.occupants': "المقيمون",
    'heb.column.occupied': "مشغولة",
    'heb.column.open': "مفتوحة",
    'heb.column.paid': "المؤدى",
    'heb.column.period': "الإقامة",
    'heb.column.points': "النقط",
    'heb.column.priority': "الأولوية",
    'heb.column.provisional': "مؤقتة",
    'heb.column.room': "الغرفة",
    'heb.column.roomType': "نوع الغرفة",
    'heb.column.rooms': "الغرف",
    'heb.column.startsOn': "الدخول",
    'heb.column.state': "الحالة",
    'heb.column.student': "الطالب",
    'heb.column.studentBed': "الطالب والسرير",
    'heb.column.total': "المجموع",
    'heb.column.unpaid': "غير المؤدى",
    'heb.column.value': "القيمة",
    'heb.column.visit': "الزيارة",
    'heb.column.where': "أين وصل",
    'heb.column.window': "فترة الإيداع",
    'heb.column.wish': "الاختيار",
    'heb.dash.title': "الداخلية والسكن",
    'heb.dash.sub': "السنة {year} · الإشغال والأداءات والطلبات والصيانة",
    'heb.dash.noYear': "لا توجد بعد أي سنة جامعية بحملة سكن.",
    'heb.dash.occupancy': "نسبة الإشغال",
    'heb.dash.occupancySub': "حسب الشهر، مقارنة بالسنة الماضية",
    'heb.dash.money': "الرسوم المنتظرة والمحصلة",
    'heb.dash.moneySub': "حسب الشهر، بالدرهم",
    'heb.dash.thisYear': "هذه السنة",
    'heb.dash.lastYear': "السنة الماضية",
    'heb.dash.expected': "المنتظر",
    'heb.dash.collected': "المحصل",
    'heb.dash.byCampus': "حسب الحي",
    'heb.dash.repairs': "الإصلاحات المفتوحة",
    'heb.dash.repairsSub': "حسب الفئة، المدة المتوسطة",
    'heb.dash.noRepairs': "لا إصلاح مفتوح.",
    'heb.dash.repairLine': "{count} · {days} ي",
    'heb.dash.moves': "حصيلة السنة",
    'heb.moves.submitted': "الطلبات المودعة",
    'heb.moves.final': "التخصيصات النهائية",
    'heb.moves.withdrawn': "التنازلات",
    'heb.moves.unpaid_released': "أسرة محررة لعدم الأداء",
    'heb.moves.left_early': "مغادرات مبكرة",
    'heb.moves.moved': "تغييرات السرير",
    'heb.kpi.occupancy': "الإشغال",
    'heb.kpi.occupancyNote': "{occupied} سريرا من {beds} · السنة الماضية {last}",
    'heb.kpi.collected': "المحصل",
    'heb.kpi.collectedNote': "من {expected} منتظرة",
    'heb.kpi.unpaid': "غير المؤدى",
    'heb.kpi.unpaidNote': "{count} تخصيص(ات) متأخرة",
    'heb.kpi.waitlist': "لائحة الانتظار",
    'heb.kpi.waitlistNote': "{count} مقبول(ون) بدون سرير",
    'heb.kpi.repairs': "الإصلاحات المفتوحة",
    'heb.kpi.repairsNote': "المدة المتوسطة {days} أيام",
    'heb.kpi.conformity': "مطابقة الزيارات",
    'heb.kpi.conformityNote': "الغرف المطابقة في آخر زيارة",
    'heb.campaigns.title': "حملات السكن",
    'heb.campaigns.sub': "إيداع الطلبات والأماكن المفتوحة وقواعد التخصيص",
    'heb.campaigns.new': "إطلاق حملة",
    'heb.campaigns.current': "جارية",
    'heb.campaigns.scheduled': "مبرمجة",
    'heb.campaigns.closed': "مغلقة",
    'heb.campaigns.empty': "لا حملة هنا.",
    'heb.campaigns.moveIn': "الدخول يوم {date}",
    'heb.campaign.edit': "تعديل الحملة",
    'heb.campaign.to.open': "فتح الإيداع",
    'heb.campaign.to.processing': "إغلاق الإيداع والمعالجة",
    'heb.campaign.to.closed': "إغلاق الحملة",
    'heb.campaign.toText.open': "سيتمكن طلبة المستويات المفتوحة من إيداع طلباتهم عبر البوابة.",
    'heb.campaign.toText.processing': "يغلق الإيداع: يمكنكم بعدها تخصيص الأسرة ونشر النتائج.",
    'heb.campaign.toText.closed': "الحملة المغلقة لا تعدل ولا يعاد فتحها.",
    'heb.campaign.publish': "نشر النتائج",
    'heb.campaign.publishText': "يتوصل كل طالب بالسرير المقترح أو بمرتبته في لائحة الانتظار مع رسالة الحملة.",
    'heb.campaign.published': "تم إشعار {count} طالب(ة).",
    'heb.campaign.moved': "انتقلت الحملة إلى المرحلة الموالية.",
    'heb.campaign.saved': "تم حفظ الحملة.",
    'heb.campaign.views': "عروض الحملة",
    'heb.campaign.tab.follow': "التتبع",
    'heb.campaign.tab.applications': "الطلبات",
    'heb.campaign.tab.allocation': "التخصيص",
    'heb.campaignForm.new': "إطلاق حملة",
    'heb.campaignForm.edit': "تعديل الحملة",
    'heb.campaignForm.step': "المرحلة {n} من 6: {label}",
    'heb.campaignForm.steps.calendar': "الجدول الزمني",
    'heb.campaignForm.steps.publics': "الفئات المعنية",
    'heb.campaignForm.steps.places': "الأماكن والحصص",
    'heb.campaignForm.steps.documents': "الوثائق",
    'heb.campaignForm.steps.rules': "قواعد التخصيص",
    'heb.campaignForm.steps.messages': "الرسائل",
    'heb.campaignForm.label': "العنوان",
    'heb.campaignForm.description': "التقديم في البوابة",
    'heb.campaignForm.opensOn': "فتح الإيداع",
    'heb.campaignForm.closesOn': "نهاية الإيداع",
    'heb.campaignForm.processingOn': "النتائج ابتداء من",
    'heb.campaignForm.moveInOn': "الدخول",
    'heb.campaignForm.stayEndsOn': "نهاية الإقامة",
    'heb.campaignForm.publicsHint': "المستويات التي يمكنها الإيداع. بدون اختيار: كل المستويات.",
    'heb.campaignForm.noLevels': "لا مستوى في المرجع.",
    'heb.campaignForm.addPlace': "فتح مكان",
    'heb.campaignForm.noEstate': "أضيفوا أولا أحياء وغرفا إلى المرافق.",
    'heb.campaignForm.quotas': "الحصص",
    'heb.campaignForm.quotasHint': "نسبة من الأسرة المفتوحة مخصصة لفئة.",
    'heb.campaignForm.quotaLabel': "عنوان الحصة",
    'heb.campaignForm.criterion': "المعيار",
    'heb.campaignForm.percent': "النسبة",
    'heb.campaignForm.addQuota': "إضافة حصة",
    'heb.campaignForm.docLabel': "الوثيقة",
    'heb.campaignForm.condition': "الشرط",
    'heb.campaignForm.conditionHint': "مثلا « إذا كان ممنوحا »: لا تطلب الوثيقة حينها من الجميع.",
    'heb.campaignForm.required': "إلزامية",
    'heb.campaignForm.addDoc': "إضافة وثيقة",
    'heb.campaignForm.defaultDoc': "نسخة من البطاقة الوطنية",
    'heb.campaignForm.weights': "النقط حسب المعيار",
    'heb.campaignForm.rules': "القواعد",
    'heb.campaignForm.reserved': "أسرة محتفظ بها احتياطا",
    'heb.campaignForm.reservedHint': "للحالات الخاصة، تخصص يدويا.",
    'heb.campaignForm.provisional': "رسالة التخصيص المؤقت",
    'heb.campaignForm.provisionalHint': "ترسل مع السرير المقترح: أجل القبول والأداء.",
    'heb.campaignForm.final': "رسالة التخصيص النهائي",
    'heb.campaignForm.finalHint': "ترسل بعد أداء الرسوم: استدعاء لتسلم المفاتيح.",
    'heb.follow.submitted': "الطلبات المودعة",
    'heb.follow.submittedNote': "{count} للمعالجة",
    'heb.follow.beds': "الأسرة المشغولة",
    'heb.follow.bedsNote': "{count} سرير(أسرة) شاغر(ة)",
    'heb.follow.payments': "الأداءات المنتظرة",
    'heb.follow.paymentsNote': "{count} إيصال(ات) للمصادقة",
    'heb.follow.waitlisted': "لائحة الانتظار",
    'heb.follow.waitlistedNote': "تخدم عند تحرير كل سرير",
    'heb.follow.flow': "مسار الطلبات",
    'heb.follow.byCampus': "الإشغال حسب الحي",
    'heb.follow.todo': "للإنجاز",
    'heb.follow.todoApplications': "{count} طلب(ات) للدراسة",
    'heb.follow.todoIncomplete': "{count} في انتظار وثيقة",
    'heb.follow.todoReceipts': "{count} إيصال(ات) أداء للمصادقة",
    'heb.follow.todoLate': "{count} أجل(آجال) متجاوز(ة)",
    'heb.follow.todoKeys': "{count} تخصيص(ات) نهائية، مفاتيح للتسليم",
    'heb.follow.moveIn': "الدخول يوم {date}",
    'heb.follow.setup': "إعدادات الحملة",
    'heb.follow.stay': "من {from} إلى {to} · {months} أشهر",
    'heb.flow.submitted': "مودعة",
    'heb.flow.eligible': "مقبولة",
    'heb.flow.incomplete': "وثيقة مطلوبة",
    'heb.flow.provisional': "سرير مقترح",
    'heb.flow.final': "نهائية",
    'heb.flow.waitlisted': "في الانتظار",
    'heb.applications.views': "عروض الطلبات",
    'heb.applications.view.todo': "للمعالجة",
    'heb.applications.view.incomplete': "وثيقة مطلوبة",
    'heb.applications.view.eligible': "مقبولة",
    'heb.applications.view.allocated': "مخصصة",
    'heb.applications.view.waitlisted': "لائحة الانتظار",
    'heb.applications.view.rejected': "مرفوضة",
    'heb.applications.view.all': "الكل",
    'heb.applications.search': "الاسم، رقم الطلب، رقم التسجيل",
    'heb.applications.empty': "لا طلب هنا.",
    'heb.applications.enter': "إدخال طلب",
    'heb.applications.enterSubmit': "إيداع الطلب",
    'heb.applications.note': "تصل الطلبات من بوابة الطالب؛ والاسم والتكوين والمستوى والنظام مأخوذة من التسجيل. تحسب النقط وفق معايير الحملة.",
    'heb.wish.campus': "الحي المرغوب",
    'heb.wish.type': "نوع الغرفة",
    'heb.wish.type2': "الاختيار الثاني",
    'heb.wish.roommate': "الشريك المرغوب",
    'heb.wish.homeCity': "مدينة السكن العائلي",
    'heb.wish.distance': "البعد بالكيلومتر",
    'heb.wish.distanceHint': "من السكن العائلي إلى المؤسسة.",
    'heb.wish.emergency': "جهة الاتصال في حالة الطوارئ",
    'heb.wish.disability': "وضعية إعاقة",
    'heb.wish.disabilityHint': "تقترح غرفة ملائمة بالأولوية.",
    'heb.wish.needs': "الاحتياجات الخاصة",
    'heb.wish.rules': "اطلعت على النظام الداخلي للداخلية وألتزم باحترامه.",
    'heb.wish.rulesStaff': "اطلع الطالب على النظام الداخلي للداخلية وقبله.",
    'heb.application.wishes': "الاختيارات",
    'heb.application.typeOr': "{first}، وإلا {second}",
    'heb.application.documents': "الوثائق",
    'heb.application.history': "السجل",
    'heb.application.treatment': "المعالجة",
    'heb.application.points': "النقط",
    'heb.application.pointsSub': "معايير الحملة",
    'heb.application.noPoints': "تحسب النقط عند قبول الطلب.",
    'heb.application.assign': "تخصيص سرير",
    'heb.application.release': "تحرير السرير",
    'heb.application.backToQueue': "يعود الطلب إلى اللائحة (وإلا، تنازل)",
    'heb.application.bed': "السرير",
    'heb.application.roommates': "الشركاء",
    'heb.application.period': "الإقامة",
    'heb.application.acceptBy': "القبول قبل",
    'heb.application.payBy': "الأداء قبل",
    'heb.application.paid': "المؤدى: {amount}",
    'heb.application.invoice': "الفاتورة {number}",
    'heb.application.keys': "سلم المفتاح {key} يوم {at}",
    'heb.application.released': "حرر السرير: {reason}",
    'heb.application.releasedDone': "تم تحرير السرير.",
    'heb.application.closed': "هذا الطلب مغلق.",
    'heb.application.born': "مزداد(ة) يوم {date}",
    'heb.application.submittedOn': "أودع يوم {date}",
    'heb.review.eligible': "التصريح بالقبول",
    'heb.review.incomplete': "طلب وثيقة",
    'heb.review.waitlisted': "الوضع في لائحة الانتظار",
    'heb.review.rejected': "رفض",
    'heb.review.eligibleText': "تحسب النقط ويشعر الطالب.",
    'heb.review.incompleteText': "حددوا الوثيقة الناقصة: يودعها الطالب عبر البوابة.",
    'heb.review.waitlistedText': "ينتظر الطلب تحرير سرير.",
    'heb.review.rejectedText': "يرسل السبب إلى الطالب.",
    'heb.review.note': "ملاحظة (اختيارية)",
    'heb.review.reason': "السبب",
    'heb.review.done': "تم حفظ القرار.",
    'heb.event.draft': "حفظت المسودة",
    'heb.event.submitted': "أودع الطلب",
    'heb.event.eligible': "صرح بقبوله",
    'heb.event.incomplete': "طلبت وثيقة",
    'heb.event.waitlisted': "وضع في لائحة الانتظار",
    'heb.event.rejected': "رفض",
    'heb.event.proposed': "اقترح سرير",
    'heb.event.accepted': "قبل السرير",
    'heb.event.declined': "رفض السرير",
    'heb.event.receipt': "أودع إيصال الأداء",
    'heb.event.receipt_refused': "رفض الإيصال",
    'heb.event.paid': "صودق على الأداء",
    'heb.event.final': "تخصيص نهائي",
    'heb.event.keys': "سلمت المفاتيح",
    'heb.event.left': "خروج",
    'heb.event.moved': "تغيير السرير",
    'heb.event.released': "حرر السرير",
    'heb.event.withdrawn': "تنازل",
    'heb.event.file_accepted': "وثيقة مطابقة",
    'heb.event.file_rejected': "وثيقة مرفوضة",
    'heb.event.file_pending': "وثيقة للمراجعة",
    'heb.files.see': "عرض",
    'heb.files.uploading': "جار الإرسال…",
    'heb.files.photo': "التقاط صورة أو اختيارها",
    'heb.files.drop': "إيداع ملف (PDF، PNG، JPEG، 10 ميغا على الأكثر)",
    'heb.files.remove': "حذف",
    'heb.files.accept': "مطابقة",
    'heb.files.refuse': "رفض الوثيقة",
    'heb.files.accepted': "صرح بمطابقة الوثيقة.",
    'heb.files.refused': "رفضت الوثيقة وأشعر الطالب.",
    'heb.files.missing': "لم تودع بعد.",
    'heb.files.other': "ملفات أخرى",
    'heb.alloc.mode': "طريقة التخصيص",
    'heb.alloc.auto': "آلي",
    'heb.alloc.manual': "يدوي",
    'heb.alloc.criteria': "المعايير والنقط",
    'heb.alloc.criteriaSub': "تعالج الطلبات حسب ترتيب النقط",
    'heb.alloc.rules': "القواعد المطبقة",
    'heb.alloc.reserved': "{count} سرير(أسرة) احتياطية",
    'heb.alloc.waiting': "طلبات للتخصيص",
    'heb.alloc.waitingNote': "مقبولة بدون سرير",
    'heb.alloc.placed': "مخصصة",
    'heb.alloc.placedNote': "منها {roommates} شراكة(ات) محترمة",
    'heb.alloc.unplaced': "بدون سرير",
    'heb.alloc.unplacedNote': "الأماكن {cap} · الحصص {quota} · الغرف {cabin}",
    'heb.alloc.seed': "القرعة",
    'heb.alloc.simulatedAt': "محاكاة بتاريخ {at}",
    'heb.alloc.preview': "المحاكاة",
    'heb.alloc.previewSub': "لا يحفظ شيء قبل المصادقة",
    'heb.alloc.reseed': "قرعة جديدة",
    'heb.alloc.validate': "المصادقة على {count} سرير مقترح",
    'heb.alloc.validateText': "تصبح الأسرة تخصيصات مؤقتة؛ يراها كل طالب في بوابته وعليه القبول داخل الأجل.",
    'heb.alloc.done': "اقترح {count} سرير(أسرة).",
    'heb.alloc.none': "لا طلب مقبول للتخصيص.",
    'heb.alloc.with': "مع {name}",
    'heb.alloc.autoNote': "عند تساوي النقط تحسم القرعة؛ ويحتفظ بها مع الحملة لإعادة التخصيص نفسه.",
    'heb.alloc.queue': "اللائحة",
    'heb.alloc.queueSub': "{count} طلب(ات) بدون سرير",
    'heb.alloc.queueEmpty': "كل الطلبات المقبولة لها سرير.",
    'heb.alloc.pick': "اختاروا طلبا",
    'heb.alloc.pickSub': "تظهر هنا الأسرة الملائمة",
    'heb.alloc.wishes': "الاختيارات: {campus} · {type}، وإلا {type2}",
    'heb.alloc.noBeds': "لا سرير شاغر ملائم.",
    'heb.alloc.emptyRoom': "غرفة فارغة",
    'heb.alloc.assign': "تخصيص",
    'heb.alloc.assigned': "اقترح سرير على {name}.",
    'heb.alloc.openApplication': "فتح الطلب",
    'heb.payments.title': "أداء الرسوم",
    'heb.payments.sub': "يصبح التخصيص المقبول نهائيا بعد أداء الرسوم",
    'heb.payments.record': "تسجيل أداء بالشباك",
    'heb.payments.recordSub': "نقدا أو بشيك أو تحويل أو إيداع توصلت به المؤسسة",
    'heb.payments.recordSubmit': "تسجيل الأداء",
    'heb.payments.recorded': "تم تسجيل الأداء.",
    'heb.payments.amount': "المبلغ (درهم)",
    'heb.payments.amountHint': "مثلا 850 أو 850,50.",
    'heb.payments.method': "طريقة الأداء",
    'heb.payments.reference': "المرجع",
    'heb.payments.paidOn': "تاريخ الأداء",
    'heb.payments.left': "الباقي {amount} من {total}",
    'heb.payments.receiptsNote': "أودعها الطلبة",
    'heb.payments.lateNote': "بعدها يحرر السرير",
    'heb.payments.paidNote': "تخصيصات نهائية",
    'heb.payments.view.receipts': "إيصالات للمصادقة",
    'heb.payments.view.due': "للأداء",
    'heb.payments.view.late': "أجل متجاوز",
    'heb.payments.view.paid': "مؤداة",
    'heb.payments.view.all': "الكل",
    'heb.payments.state.receipt': "إيصال للمصادقة",
    'heb.payments.state.refused': "إيصال مرفوض",
    'heb.payments.state.paid': "مؤدى",
    'heb.payments.state.late': "أجل متجاوز",
    'heb.payments.state.part': "مؤدى جزئيا",
    'heb.payments.state.due': "للأداء",
    'heb.payments.receiptAt': "أودع الإيصال يوم {at}",
    'heb.payments.payBy': "قبل {date}",
    'heb.payments.examine': "دراسة",
    'heb.payments.collect': "تحصيل",
    'heb.payments.empty': "لا شيء هنا.",
    'heb.payments.receipt': "الإيصال",
    'heb.payments.noReceipt': "اختاروا إيصالا للمصادقة من اللائحة.",
    'heb.payments.receiptOf': "إيصال {name}",
    'heb.payments.declared': "المبلغ المصرح به",
    'heb.payments.leftToPay': "الباقي للأداء",
    'heb.payments.validate': "المصادقة على الأداء",
    'heb.payments.refuse': "رفض الإيصال",
    'heb.payments.refuseHint': "يتوصل الطالب بالسبب ويودع إيصالا آخر.",
    'heb.payments.refused': "رفض الإيصال وأشعر الطالب.",
    'heb.payments.validated': "تمت المصادقة على الأداء.",
    'heb.payments.validatedFinal': "تمت المصادقة على الأداء: أصبح تخصيص {name} نهائيا.",
    'heb.payments.financeNote': "مع وحدة المالية، تسجل المصادقة الأداء في فاتورة الطالب.",
    'heb.payments.finalNote': "عند أداء المبلغ كاملا يصبح التخصيص نهائيا ويستدعى الطالب لتسلم المفاتيح.",
    'heb.payments.note': "لا أداء عبر الإنترنت: يودع الطالب إيصاله (تحويل، إيداع) ويصادق عليه عون، أو يسجل العون أداء بالشباك. بعد مهلة السماح يعود السرير إلى الحملة.",
    'heb.price.rent': "الكراء، {months} أشهر",
    'heb.price.rentTimes': "الكراء، {months} أشهر × {rent}",
    'heb.price.deposit': "الضمانة",
    'heb.price.fee': "مصاريف الملف",
    'heb.price.toPay': "للأداء",
    'heb.stays.title': "الدخول والخروج",
    'heb.stays.sub': "تسليم المفاتيح ومحاضر الحالة واسترجاع الضمانة",
    'heb.stays.print': "طباعة الاستدعاءات",
    'heb.stays.view.entries': "الدخول",
    'heb.stays.view.occupants': "المفاتيح المسلمة",
    'heb.stays.view.exits': "الخروج",
    'heb.stays.empty.entries': "لا دخول مرتقب.",
    'heb.stays.empty.occupants': "لا مقيم.",
    'heb.stays.empty.exits': "لا خروج.",
    'heb.stays.search': "الاسم، السرير، المفتاح",
    'heb.stays.owing': "الباقي {amount} للأداء",
    'heb.stays.retained': "اقتطاع {amount}",
    'heb.stays.payments': "الأداءات",
    'heb.keys.give': "تسليم المفاتيح",
    'heb.keys.giveShort': "تسليم",
    'heb.keys.return': "تسجيل الخروج",
    'heb.keys.returnShort': "خروج",
    'heb.keys.label': "المفتاح أو الشارة المسلمة",
    'heb.keys.labelHint': "الرقم المنقوش على المفتاح أو رقم الشارة.",
    'heb.keys.sheet': "محضر الحالة",
    'heb.keys.noChecklist': "لا شبكة محضر حالة مفعلة: أضيفوا واحدة في الإعدادات.",
    'heb.keys.note': "ملاحظات",
    'heb.keys.photos': "الصور",
    'heb.keys.signed': "اطلع الطالب على محضر الحالة ووقعه",
    'heb.keys.given': "سلمت المفاتيح.",
    'heb.keys.returned': "سجل الخروج.",
    'heb.keys.deposit': "الضمانة {amount}",
    'heb.keys.retained': "الاقتطاع من الضمانة (درهم)",
    'heb.keys.retainedHint': "فارغ: ترد الضمانة كاملة. على الأكثر {amount} درهم؛ ويستلزم الاقتطاع ملاحظات.",
    'heb.visits.title': "زيارات المراقبة",
    'heb.visits.sub': "مبرمجة أو مفاجئة، غرفة بغرفة، بالملاحظات والصور",
    'heb.visits.view.planned': "مرتقبة",
    'heb.visits.state.planned': "مبرمجة",
    'heb.visits.view.followup': "تتطلب متابعة",
    'heb.visits.state.followup': "تتطلب متابعة",
    'heb.visits.view.done': "منجزة",
    'heb.visits.state.done': "منجزة",
    'heb.visits.empty': "لا زيارة هنا.",
    'heb.visits.announced': "معلنة",
    'heb.visits.surprise': "مفاجئة",
    'heb.visits.plan': "برمجة زيارة",
    'heb.visits.planSubmit': "برمجة الزيارة",
    'heb.visits.where': "المكان",
    'heb.visits.whereHint': "جناح أو طابق من الحي.",
    'heb.visits.wholeCampus': "كل الحي",
    'heb.visits.at': "التاريخ والساعة",
    'heb.visits.agent': "العون",
    'heb.visits.agentHint': "من سيقوم بالزيارة.",
    'heb.visits.me': "أنا",
    'heb.visits.checklist': "شبكة المراقبة",
    'heb.visits.checklistItems': "{label} ({count} نقطة)",
    'heb.visits.announce': "إشعار المقيمين",
    'heb.visits.announceHint': "بدون تأشير: زيارة مفاجئة.",
    'heb.visit.title': "زيارة · {where}",
    'heb.visit.progress': "{seen} / {total} غرفة تمت زيارتها",
    'heb.visit.finish': "إنهاء الزيارة",
    'heb.visit.finishText': "تمت زيارة {seen} غرفة من {total}. لا تعدل الزيارة المنتهية.",
    'heb.visit.finished': "انتهت الزيارة.",
    'heb.visit.rooms': "الغرف",
    'heb.visit.toSee': "للزيارة",
    'heb.visit.free': "شاغرة",
    'heb.visit.pick': "اختاروا غرفة",
    'heb.visit.pickHint': "تظهر شبكة المراقبة للغرفة المختارة.",
    'heb.visit.room': "الغرفة {number}",
    'heb.visit.note': "ملاحظات",
    'heb.visit.present': "المقيمون الحاضرون",
    'heb.visit.photos': "الصور",
    'heb.visit.addPhoto': "إضافة صورة",
    'heb.visit.followups': "المتابعة",
    'heb.visit.repair': "إنشاء إصلاح لهذه الغرفة",
    'heb.visit.warning': "إنذار المقيمين",
    'heb.visit.warningHint': "يتوصلون بإشعار يتضمن الملاحظات.",
    'heb.visit.noFollowup': "لا متابعة.",
    'heb.visit.save': "حفظ",
    'heb.visit.next': "الغرفة الموالية",
    'heb.visit.saved': "حفظت الملاحظة.",
    'heb.visit.back': "الغرف",
    'heb.repairs.title': "الإصلاحات",
    'heb.repairs.sub': "المبلغ عنها من طالب أو زيارة أو عون، إلى غاية الإغلاق",
    'heb.repairs.counts': "{open} مفتوحة · {urgent} مستعجلة · {out} سرير(أسرة) خارج الخدمة",
    'heb.repairs.report': "التبليغ عن إصلاح",
    'heb.repairs.reportSubmit': "تبليغ",
    'heb.repairs.search': "الغرفة، الموضوع، المتدخل",
    'heb.repairs.col.reported': "مبلغ عنها",
    'heb.repairs.col.planned': "مبرمجة",
    'heb.repairs.col.in_progress': "قيد الإنجاز",
    'heb.repairs.col.done': "منجزة، للتحقق",
    'heb.repairs.col.closed': "مغلقة",
    'heb.repairs.colEmpty': "لا شيء.",
    'heb.repairs.since': "منذ {at}",
    'heb.repairs.out': "خارج الخدمة",
    'heb.repairs.subject': "الموضوع",
    'heb.repairs.description': "الوصف",
    'heb.repairs.photos': "الصور",
    'heb.repairs.access': "يمكن لعون الدخول في غياب المقيمين",
    'heb.repairs.bedHint': "اتركوه فارغا إذا تعلق الأمر بالغرفة كلها.",
    'heb.repairs.wholeRoom': "الغرفة كلها",
    'heb.repair.roomOf': "الغرفة {room}",
    'heb.repair.plan': "برمجة",
    'heb.repair.planTitle': "برمجة التدخل",
    'heb.repair.planSubmit': "برمجة",
    'heb.repair.planSaved': "برمج التدخل وأشعر المبلغ.",
    'heb.repair.assignee': "العون التقني",
    'heb.repair.assigneeHint': "عون من المؤسسة، أو مقدم خدمة أدناه.",
    'heb.repair.provider': "مقدم الخدمة",
    'heb.repair.plannedAt': "مبرمج يوم",
    'heb.repair.to.in_progress': "بدء",
    'heb.repair.to.done': "تأشير كمنجز",
    'heb.repair.to.closed': "التحقق والإغلاق",
    'heb.repair.moved': "تم تحيين الإصلاح.",
    'heb.repair.bedOut': "وضع السرير خارج الخدمة",
    'heb.repair.roomOut': "وضع الغرفة خارج الخدمة",
    'heb.repair.serveAgain': "إعادة إلى الخدمة",
    'heb.repair.putOut': "وضع خارج الخدمة.",
    'heb.repair.backIn': "أعيد إلى الخدمة.",
    'heb.repair.finding': "المعاينة",
    'heb.repair.reportedBy': "{name}، يوم {at} · {source}",
    'heb.repair.noDescription': "لا وصف.",
    'heb.repair.fromVisit': "لوحظ خلال زيارة {at}",
    'heb.repair.intervention': "التدخل",
    'heb.repair.who': "المتدخل",
    'heb.repair.accessLabel': "الولوج",
    'heb.repair.accessYes': "ممكن في غياب المقيمين",
    'heb.repair.accessNo': "بحضور المقيمين",
    'heb.repair.outLabel': "خارج الخدمة",
    'heb.repair.wholeRoom': "الغرفة كلها",
    'heb.repair.costTitle': "الكلفة والتحميل",
    'heb.repair.cost': "الكلفة (درهم)",
    'heb.repair.charge': "التحميل",
    'heb.repair.chargeInstitution': "على عاتق الداخلية",
    'heb.repair.chargeOccupant': "إتلاف: يقتطع من ضمانة المقيم",
    'heb.repair.occupant': "المقيم",
    'heb.repair.costSave': "حفظ الكلفة",
    'heb.repair.costSaved': "حفظت الكلفة.",
    'heb.repair.follow': "التتبع",
    'heb.estate.title': "المرافق والتعريفات",
    'heb.estate.sub': "الأحياء والأجنحة والطوابق ومخطط الإشغال سريرا بسرير",
    'heb.estate.tab': "الأحياء والغرف",
    'heb.estate.tree': "المرافق",
    'heb.estate.empty': "لا حي: أضيفوا واحدا.",
    'heb.estate.beds': "{count} سرير",
    'heb.estate.noBlock': "بدون جناح",
    'heb.estate.noFloor': "بدون طابق",
    'heb.estate.allTypes': "كل الأنواع",
    'heb.estate.occupied': "مشغول",
    'heb.estate.free': "شاغر",
    'heb.estate.provisional': "تخصيص مؤقت",
    'heb.estate.out': "خارج الخدمة",
    'heb.estate.full': "ممتلئة",
    'heb.estate.freeBeds': "{count} سرير(أسرة) شاغر(ة)",
    'heb.estate.noRooms': "لا غرفة هنا.",
    'heb.estate.summary': "{where} · {beds} سرير · {occupied} مشغول · {out} خارج الخدمة",
    'heb.estate.newCampus': "إضافة حي",
    'heb.estate.campusSaved': "حفظ الحي.",
    'heb.room.new': "إضافة غرفة",
    'heb.room.newHint': "تتلقى أسرة نوعها (B-204-1، B-204-2…).",
    'heb.room.edit': "تعديل الغرفة",
    'heb.room.saved': "حفظت الغرفة.",
    'heb.room.title': "الغرفة {number}",
    'heb.room.number': "الرقم",
    'heb.room.block': "الجناح",
    'heb.room.floor': "الطابق",
    'heb.room.gender': "الجنس",
    'heb.room.surface': "المساحة (م²)",
    'heb.room.equipment': "التجهيزات",
    'heb.room.equipmentHint': "واحد في كل سطر أو مفصولة بفواصل.",
    'heb.room.note': "ملاحظة",
    'heb.room.typeBeds': "{label} ({count} سرير)",
    'heb.room.beds': "الأسرة",
    'heb.room.bedsHint': "لا يحذف سرير سبق استعماله: ضعوه خارج الخدمة.",
    'heb.room.bedsSub': "{occupied} مشغول، {free} شاغر",
    'heb.room.nature': "النوع",
    'heb.room.outOfService': "خارج الخدمة",
    'heb.room.addBed': "إضافة سرير",
    'heb.room.putOut': "وضع خارج الخدمة",
    'heb.room.history': "السجل",
    'heb.room.noHistory': "لا سجل بعد.",
    'heb.room.historyStay': "تسليم المفاتيح إلى {name}، السرير {bed}",
    'heb.room.historyLeft': "خروج {name}، السرير {bed}",
    'heb.room.historyVisit': "زيارة مراقبة: {label}",
    'heb.room.historyRepair': "إصلاح: {label}",
    'heb.room.about': "الغرفة",
    'heb.room.capacity': "السعة",
    'heb.room.place': "الموقع",
    'heb.room.repairs': "الإصلاحات",
    'heb.room.noRepairs': "لا إصلاح.",
    'heb.room.delete': "حذف الغرفة",
    'heb.room.deleteText': "تحذف الغرفة التي لم تخصص قط؛ وإلا ضعوها خارج الخدمة.",
    'heb.prices.title': "التعريفات",
    'heb.prices.sub': "تعتمد القاعدة الأدق: الحي، نوع الغرفة، المستوى، النظام",
    'heb.prices.grid': "الشبكة",
    'heb.prices.gridSub': "المبالغ بالدرهم · معيار « الكل » يشمل جميع القيم",
    'heb.prices.empty': "لا تعريفة لهذه السنة.",
    'heb.prices.new': "إضافة تعريفة",
    'heb.prices.edit': "تعديل التعريفة",
    'heb.prices.dialogHint': "المعيار المتروك على « الكل » يشمل جميع القيم.",
    'heb.prices.rent': "الكراء الشهري",
    'heb.prices.saved': "حفظت التعريفة.",
    'heb.prices.delete': "حذف التعريفة",
    'heb.prices.deleteText': "تحتفظ التخصيصات المقترحة بمبلغها.",
    'heb.prices.deleted': "حذفت التعريفة.",
    'heb.prices.coverage': "التحقق",
    'heb.prices.coverageSub': "أسرة مفتوحة بدون تعريفة: لا يمكن تخصيص طلباتها",
    'heb.prices.covered': "لكل الأسرة المفتوحة تعريفة.",
    'heb.prices.combination': "تركيبة بدون تعريفة",
    'heb.prices.create': "إنشاء التعريفة",
    'heb.prices.simulate': "محاكاة تعريفة",
    'heb.prices.simulateSub': "ما سيؤديه طلب مقابل سرير",
    'heb.prices.application': "الطلب",
    'heb.prices.ruleUsed': "القاعدة المعتمدة: {label}",
    'heb.prices.noRule': "لا تنطبق أي تعريفة.",
    'heb.prices.note': "يمكن تقسيط الأداء (الإعدادات). مع وحدة المالية تصبح الرسوم فاتورة للطالب.",
    'heb.settings.title': "إعدادات الداخلية",
    'heb.settings.sub': "أنواع الغرف وشبكات المراقبة وفئات الإصلاح والآجال والأداءات",
    'heb.settings.module': "الوحدة",
    'heb.settings.on': "وحدة الداخلية والسكن مفعلة",
    'heb.settings.off': "وحدة الداخلية والسكن معطلة",
    'heb.settings.since': "فعلت يوم {at} من طرف {by}.",
    'heb.settings.moduleHint': "عند تعطيلها تختفي الوحدة من القائمة والبوابة دون حذف أي شيء.",
    'heb.settings.enabled': "فعلت الوحدة.",
    'heb.settings.disabled': "عطلت الوحدة.",
    'heb.settings.financeOn': "المالية مفعلة: تفوتر الرسوم",
    'heb.settings.financeOff': "المالية معطلة: تبقى الأداءات في الداخلية",
    'heb.settings.delays': "الآجال والأداءات",
    'heb.settings.acceptDays': "أجل قبول سرير مقترح",
    'heb.settings.payDays': "أجل الأداء بعد القبول",
    'heb.settings.graceDays': "مهلة السماح",
    'heb.settings.days': "بالأيام.",
    'heb.settings.instalments': "التقسيط",
    'heb.settings.instalmentsHint': "مثلا: الضمانة وشهران ثم كل ثلاثة أشهر.",
    'heb.settings.autoRelease': "تحرير السرير آليا بعد مهلة السماح",
    'heb.settings.refundDeposit': "رد الضمانة عند الخروج بعد خصم اقتطاعات محضر الحالة",
    'heb.settings.save': "حفظ",
    'heb.settings.saved': "حفظت الإعدادات.",
    'heb.settings.roomTypes': "أنواع الغرف",
    'heb.settings.campuses': "الأحياء",
    'heb.settings.categories': "فئات الإصلاح",
    'heb.settings.providers': "مقدمو الخدمات",
    'heb.settings.providersSub': "أو عون تقني من المؤسسة",
    'heb.settings.noProviders': "لا مقدم خدمة.",
    'heb.settings.checklists': "شبكات المراقبة",
    'heb.settings.checklistsSub': "للزيارات ومحاضر الحالة",
    'heb.settings.points': "{count} نقطة",
    'heb.settings.inactive': "غير مفعل",
    'heb.settings.active': "مفعل",
    'heb.settings.label': "العنوان",
    'heb.settings.beds': "عدد الأسرة",
    'heb.settings.name': "الاسم",
    'heb.settings.code': "الرمز",
    'heb.settings.address': "العنوان البريدي",
    'heb.settings.trades': "المهن",
    'heb.settings.phone': "الهاتف",
    'heb.settings.email': "البريد الإلكتروني",
    'heb.settings.checklistKind': "الاستعمال",
    'heb.settings.items': "نقط المراقبة",
    'heb.settings.itemsHint': "نقطة في كل سطر، 120 حرفا على الأكثر.",
    'heb.settings.roomtype.new': "إضافة نوع غرفة",
    'heb.settings.roomtype.edit': "تعديل نوع غرفة",
    'heb.settings.category.new': "إضافة فئة",
    'heb.settings.category.edit': "تعديل فئة",
    'heb.settings.provider.new': "إضافة مقدم خدمة",
    'heb.settings.provider.edit': "تعديل مقدم خدمة",
    'heb.settings.checklist.new': "إضافة شبكة",
    'heb.settings.checklist.edit': "تعديل شبكة",
    'heb.settings.campus.new': "إضافة حي",
    'heb.settings.campus.edit': "تعديل حي",
    'heb.settings.rights': "الصلاحيات: housing.read وhousing.manage (الحملات والطلبات والتخصيص والمرافق) وhousing.pay (الأداءات) وhousing.keys (الدخول والخروج) وhousing.inspect (الزيارات) وhousing.repair (الإصلاحات) وhousing.configure، وhousing.own للطالب.",
    'heb.mine.title': "سكني",
    'heb.mine.myApplication': "طلبي",
    'heb.mine.complete': "إتمام الطلب {number}",
    'heb.mine.fromEnrolment': "اسمكم وتكوينكم ومستواكم ووضعية المنحة مأخوذة من تسجيلكم: تصححها المؤسسة عند الحاجة.",
    'heb.mine.alreadyFiled': "{label}: {count} ملف(ات) مودعة سلفا",
    'heb.mine.pieces': "الوثائق المطلوب إرفاقها",
    'heb.mine.saveDraft': "حفظ المسودة",
    'heb.mine.submit': "إيداع طلبي",
    'heb.mine.submitted': "أودع الطلب: سيتم إشعاركم بالمستجدات.",
    'heb.mine.draftSaved': "حفظت المسودة.",
    'heb.mine.closes': "الإيداع إلى غاية",
    'heb.mine.results': "النتائج ابتداء من",
    'heb.mine.youWillPay': "ما ستؤدونه",
    'heb.mine.pickToSee': "اختاروا حيا ونوع غرفة",
    'heb.mine.pricesHint': "يظهر المبلغ حسب اختياركم.",
    'heb.mine.payAfter': "حسب السرير المخصص؛ يؤدى بعد التخصيص.",
    'heb.mine.where': "أين وصل طلبي",
    'heb.track.submitted': "مودع",
    'heb.track.eligible': "مقبول",
    'heb.track.provisional': "سرير مقترح",
    'heb.track.accepted': "مقبول",
    'heb.track.final': "أداء مصادق عليه",
    'heb.track.housed': "تسلم المفاتيح",
    'heb.mine.accept': "قبول هذا السرير",
    'heb.mine.acceptText': "تلتزمون بأداء الرسوم داخل الأجل؛ وإلا يحرر السرير.",
    'heb.mine.acceptHint': "اقبلوا قبل {date}، وإلا يقترح السرير على طالب آخر.",
    'heb.mine.decline': "رفض هذا السرير",
    'heb.mine.declineText': "يعود السرير إلى الحملة.",
    'heb.mine.receipt': "إيداع إيصال الأداء",
    'heb.mine.receiptFile': "الإيصال (إشعار التحويل أو الإيداع)",
    'heb.mine.receiptSubmit': "إيداع الإيصال",
    'heb.mine.receiptHint': "أدوا بالتحويل أو بشباك المؤسسة ثم أودعوا الإيصال: يصادق عليه عون المالية.",
    'heb.mine.receiptSent': "أودع الإيصال: سيتم إشعاركم بالمصادقة عليه.",
    'heb.mine.payHint': "أدوا قبل {date}، وإلا يحرر السرير.",
    'heb.mine.finalHint': "خصص سريركم: تسليم المفاتيح ابتداء من {date}.",
    'heb.mine.withdraw': "التنازل",
    'heb.mine.withdrawText': "يغلق طلبكم ويعود السرير المحتمل إلى الحملة.",
    'heb.mine.reason': "السبب",
    'heb.mine.done.accept': "قبل السرير: أودعوا إيصالكم بعد الأداء.",
    'heb.mine.done.decline': "رفض السرير.",
    'heb.mine.done.withdraw': "سجل التنازل.",
    'heb.mine.myRoom': "غرفتي",
    'heb.mine.key': "المفتاح {key}",
    'heb.mine.keySince': "سلم يوم {at}",
    'heb.mine.payments': "أداءاتي",
    'heb.mine.upToDate': "محين",
    'heb.mine.owing': "الباقي {amount} للأداء",
    'heb.mine.reports': "تبليغاتي",
    'heb.mine.problem': "التبليغ عن مشكل",
    'heb.mine.problemWhat': "ما المشكل",
    'heb.mine.problemSubmit': "إرسال",
    'heb.mine.problemSent': "أرسل التبليغ: سيتم إشعاركم عند الإصلاح.",
    'heb.mine.access': "يمكن لعون الدخول في غيابي",
    'heb.mine.nothing': "لا حملة مفتوحة",
    'heb.mine.nothingHint': "سيتم إشعاركم عند فتح حملة السكن المقبلة.",
    'heb.mine.note': "تتوصلون بإشعار في كل مرحلة: سرير مقترح، تذكير بالأداء، استدعاء لتسلم المفاتيح، زيارة معلنة، إصلاح منجز.",
    'rel.modules.housing': "الداخلية والسكن",
    'rel.modules.housingHint': "الحملات والطلبات وتخصيص الأسرة وأداء الرسوم والمفاتيح والزيارات والإصلاحات",
    'journal.action.housing.settings_changed': "تعديل إعدادات الداخلية",
    'journal.action.housing.estate_saved': "حفظ مرافق أو تعريفة الداخلية",
    'journal.action.housing.campaign_saved': "حفظ حملة سكن",
    'journal.action.housing.application_decided': "البت في طلب سكن",
    'journal.action.housing.allocation_changed': "تعديل تخصيص سرير",
    'journal.action.housing.payment_decided': "البت في أداء الداخلية",
    'journal.action.housing.keys_changed': "تسليم أو إرجاع المفاتيح",
    'journal.action.housing.upkeep_changed': "زيارة أو إصلاح بالداخلية",
    'journal.action.portal.rib_changed': "اقتراح أو التحقق من رقم حساب أستاذ",
    'journal.action.portal.hours_reported': "التبليغ عن فارق ساعات أو معالجته",
    'tch.tabs.rolls': "المناداة",
    'tch.kpi.today': "حصص اليوم",
    'tch.kpi.todayNote': "{count} مناداة للإنجاز",
    'tch.kpi.late': "مناداة متأخرة",
    'tch.kpi.lateNote': "حصص سابقة دون مناداة",
    'tch.kpi.marks': "نقط للإدخال",
    'tch.kpi.marksNote': "تقييمات سابقة غير مصادق عليها",
    'tch.kpi.hours': "الساعات المنجزة",
    'tch.kpi.hoursNote': "من أصل {objective}",
    'tch.home.hours': "ساعاتي",
    'tch.home.hoursLink': "الساعات والعقد",
    'tch.home.ofObjective': "منجزة من أصل {objective}",
    'tch.home.hoursNote': "مبرمجة: {planned} · للبرمجة: {toPlan} · هذا الشهر: {month}",
    'tch.roll.take': "إجراء المناداة",
    'tch.roll.done': "تمت المناداة",
    'tch.todo.roll_missing': "مناداة متأخرة",
    'tch.todo.marks_missing': "نقط للإدخال أو المصادقة",
    'tch.todo.replacement': "تعويض",
    'tch.todo.cancelled': "حصة ملغاة",
    'tch.todo.requests': "{count} طلب قيد المعالجة",
    'tch.rolls.title': "المناداة",
    'tch.rolls.sub': "حصصي خلال الستين يوما الأخيرة وحالة المناداة",
    'tch.rolls.todo': "للإنجاز اليوم",
    'tch.rolls.todoNote': "حصص اليوم",
    'tch.rolls.late': "متأخرة",
    'tch.rolls.lateNote': "للإنجاز في أقرب وقت",
    'tch.rolls.done': "منجزة",
    'tch.rolls.doneNote': "خلال ستين يوما",
    'tch.rolls.pending': "للإنجاز",
    'tch.rolls.doneTab': "منجزة",
    'tch.rolls.all': "الكل",
    'tch.rolls.none': "لا حصة هنا.",
    'tch.rolls.session': "الحصة",
    'tch.rolls.course': "الدرس",
    'tch.rolls.classes': "الأقسام",
    'tch.rolls.state': "المناداة",
    'tch.rolls.state.todo': "للإنجاز",
    'tch.rolls.state.late': "متأخرة",
    'tch.rolls.state.done': "منجزة",
    'tch.rolls.state.validated': "مصادق عليها",
    'tch.rolls.counts': "{absent} غياب، {late} تأخر",
    'tch.rolls.fix': "تصحيح",
    'tch.rolls.locked': "مقفل",
    'tch.rolls.hint': "تصحح المناداة إلى حين مصادقة الشؤون الدراسية.",
    'tch.classes.title': "أقسامي",
    'tch.classes.sub': "أقسام تدريسي هذه السنة",
    'tch.classes.write': "مراسلة الطلبة",
    'tch.classes.none': "لا قسم في حصصك أو تقييماتك هذه السنة.",
    'tch.classes.list': "أقسامي",
    'tch.classes.count': "{count} طالب",
    'tch.classes.empty': "لا طالب في هذا القسم.",
    'tch.classes.student': "الطالب",
    'tch.classes.presence': "الحضور",
    'tch.classes.unjustified': "غياب غير مبرر",
    'tch.classes.unvalidated': "* تقييم غير مصادق عليه بعد.",
    'tch.classes.hint': "ترى حضور طلبتك في حصصك ونقطهم في تقييماتك، دون معطياتهم الشخصية.",
    'tch.hours.title': "الساعات والعقد",
    'tch.hours.report': "التبليغ عن فارق ساعات",
    'tch.hours.reported': "أرسل الفارق إلى البرمجة.",
    'tch.hours.objective': "الهدف",
    'tch.hours.objectiveNote': "ساعات أهداف العقد",
    'tch.hours.planned': "المبرمج",
    'tch.hours.plannedNote': "{hours} متبقية للبرمجة",
    'tch.hours.realized': "المنجز",
    'tch.hours.realizedNote': "{percent} % من الهدف",
    'tch.hours.month': "هذا الشهر",
    'tch.hours.monthNote': "{count} حصة",
    'tch.hours.byMonth': "شهرا بشهر",
    'tch.hours.legendPlanned': "مبرمج",
    'tch.hours.legendRealized': "منجز",
    'tch.hours.bySubject': "حسب المادة",
    'tch.hours.objectiveCol': "الهدف",
    'tch.hours.realizedCol': "المنجز",
    'tch.hours.contracts': "عقودي",
    'tch.hours.noContract': "لا عقد.",
    'tch.hours.period': "من {from} إلى {to}",
    'tch.hours.signed': "وقع في {date}",
    'tch.hours.rate': "الأجر بالساعة: {rate}",
    'tch.hours.download': "تحميل العقد",
    'tch.hours.reports': "تبليغاتي",
    'tch.hours.noReport': "لا فارق مبلغ عنه.",
    'tch.hours.hint': "تحتسب الساعة منجزة عند إجراء مناداة الحصة.",
    'tch.hours.reportSub': "تصحح البرمجة الحصة أو المناداة وتجيبك.",
    'tch.hours.send': "إرسال",
    'tch.hours.reportMonth': "الشهر المعني",
    'tch.hours.reportExpected': "الساعات المنتظرة",
    'tch.hours.reportNote': "ما ينقص",
    'tch.hours.reportHint': "تسجل الساعات المحتسبة للشهر عند الإرسال.",
    'tch.report.state.open': "مرسل",
    'tch.report.state.corrected': "مصحح",
    'tch.report.state.dismissed': "مرفوض",
    'tch.pay.title': "أجري",
    'tch.pay.paid': "المؤدى هذه السنة",
    'tch.pay.paidNote': "{count} شهر مؤدى، صافي",
    'tch.pay.hours': "ساعات السنة",
    'tch.pay.hoursNote': "المحتسبة للأجر",
    'tch.pay.rib': "تحويل",
    'tch.pay.ribNote': "رقم حساب مؤكد",
    'tch.pay.noRib': "لا رقم حساب مؤكد",
    'tch.pay.byMonth': "شهرا بشهر",
    'tch.pay.none': "لا شهر أجر.",
    'tch.pay.month': "الشهر",
    'tch.pay.hoursCol': "الساعات",
    'tch.pay.gross': "الإجمالي",
    'tch.pay.net': "الصافي",
    'tch.pay.state': "الحالة",
    'tch.pay.stateOf.paid': "مؤدى",
    'tch.pay.stateOf.validated': "مصادق عليه",
    'tch.pay.stateOf.computed': "قيد الحساب",
    'tch.pay.stateOf.blocked': "في انتظار مناداة أو عقد",
    'tch.pay.stateOf.nothing': "لا شيء هذا الشهر",
    'tch.pay.extra': "منها {hours} خارج الجزاف",
    'tch.pay.hint': "الشهر الجاري حساب تقديري؛ يصبح الكشف نهائيا بعد المصادقة.",
    'tch.profile.name': "الاسم",
    'tch.profile.number': "رقم التأجير",
    'tch.profile.department': "الشعبة",
    'tch.profile.fields': "المجالات",
    'tch.profile.since': "يدرس منذ",
    'tch.profile.identitySub': "خطأ؟ أخبر الموارد البشرية.",
    'tch.profile.diplomas': "الشهادات",
    'tch.profile.locked': "تعدل لدى الموارد البشرية",
    'tch.profile.rib': "رقم الحساب للأجر",
    'tch.profile.ribSub': "يعتمد الرقم الجديد بعد تحقق الموارد البشرية",
    'tch.profile.ribChange': "تغيير رقم الحساب",
    'tch.profile.ribCurrent': "الرقم المعتمد",
    'tch.profile.ribVerified': "تم التحقق",
    'tch.profile.ribPending': "رقم جديد {rib} أرسل في {date}، قيد التحقق.",
    'tch.profile.ribRefused': "رفض رقمك الأخير: {reason}",
    'tch.profile.ribSent': "أرسل الرقم: تتحقق منه الموارد البشرية.",
    'tch.profile.ribDialogSub': "يعوض القديم بعد التحقق",
    'tch.profile.ribNew': "الرقم الجديد",
    'tch.profile.ribHint': "من 16 إلى 34 رقما أو حرفا؛ تتجاهل المسافات.",
    'tch.profile.documents': "وثائقي",
    'tch.profile.contract': "عقد {year}",
    'tch.profile.unsigned': "غير موقع بعد",
    'tch.bank.sub': "يقترحه الأستاذ من بوابته",
    'tch.bank.pending': "الرقم المقترح",
    'tch.bank.verify': "تأكيد",
    'tch.bank.refuse': "رفض",
    'tch.bank.reason': "السبب",
    'tch.reports.title': "فوارق الساعات المبلغ عنها ({count})",
    'tch.reports.sub': "صحح الحصة أو المناداة ثم أجب الأستاذ",
    'tch.reports.teacher': "الأستاذ",
    'tch.reports.counted': "المحتسبة",
    'tch.reports.expected': "المنتظرة",
    'tch.reports.note': "ما ينقص",
    'tch.reports.handle': "معالجة",
    'tch.reports.decision': "القرار",
    'tch.reports.answer': "الجواب للأستاذ",
    'tch.reports.answerHint': "إجباري لرفض التبليغ.",
    'tch.settings.students': "الطلبة",
    'tch.settings.teachers': "الأساتذة",
    'tch.settings.rubrics': "أقسام الأساتذة",
    'tch.settings.rubricsSub': "يبقى استعمال الزمن والمناداة والتقييمات ظاهرا دائما",
    'tch.settings.welcome': "رسالة ترحيب الأساتذة",
    'tch.settings.flag.classes': "أقسامي",
    'tch.settings.flag.photos': "صور الطلبة",
    'tch.settings.flag.hours': "الساعات والعقد",
    'tch.settings.flag.pay': "أجري",
    'tch.settings.flag.profile': "معطيات ورقم حساب قابلة للتعديل",
    'tch.settings.rule.classes': "الحضور في حصصه والنقط في تقييماته فقط",
    'tch.settings.rule.photos': "في « أقسامي »",
    'tch.settings.rule.hours': "الهدف والمبرمج والمنجز والعقود؛ التبليغ عن فارق",
    'tch.settings.rule.pay': "الساعات والإجمالي والصافي والكشف؛ عند التعطيل يختفي القسم",
    'tch.settings.rule.profile': "يتحقق من الرقم الموارد البشرية؛ مسجل",
    'journal.action.portal.settings_changed': "تعديل إعدادات بوابة الطالب",
    'journal.action.portal.profile_changed': "تعديل المعطيات من البوابة",
    'journal.action.portal.calendar_linked': "إنشاء رابط اليومية لاستعمال الزمن",
    'port.tabs.home': "الاستقبال",
    'port.tabs.timetable': "اليومية",
    'port.tabs.marks': "النقط",
    'port.tabs.messages': "الرسائل",
    'port.tabs.more': "المزيد",
    'port.home.hello': "مرحبا {name}",
    'port.home.today': "اليوم",
    'port.home.timetable': "استعمال الزمن",
    'port.home.noSession': "لا حصة اليوم.",
    'port.home.marks': "آخر النقط",
    'port.home.allMarks': "كل نقطي",
    'port.home.todo': "للإنجاز",
    'port.home.nothingToDo': "لا شيء للإنجاز حاليا.",
    'port.home.messages': "الإشعارات",
    'port.home.allMessages': "كل الإشعارات",
    'port.home.noMessage': "لا إشعار.",
    'port.home.links': "روابط مفيدة",
    'port.kpi.average': "المعدل المؤقت",
    'port.kpi.averageNote': "{group} · {count} مادة من {total}",
    'port.kpi.attendance': "المواظبة",
    'port.kpi.attendanceNote': "{count} غياب غير مبرر",
    'port.kpi.due': "المتبقي للأداء",
    'port.kpi.dueNext': "القسط المقبل في {date}",
    'port.kpi.dueNone': "لا قسط مستحق",
    'port.kpi.credits': "الأرصدة المكتسبة",
    'port.todo.justify': "{count} غياب للتبرير",
    'port.todo.between': "من {from} إلى {to}",
    'port.todo.justifyAction': "تبرير",
    'port.todo.installment': "قسط {date}: {amount}",
    'port.todo.late': "متأخر منذ {date}: {amount}",
    'port.todo.see': "عرض",
    'port.todo.offer': "سرير مقترح عليك",
    'port.todo.offerNote': "{bed}، {campus} · للقبول قبل {date}",
    'port.todo.answer': "الرد",
    'port.todo.housingPay': "رسوم السكن: {amount}",
    'port.todo.housingPayNote': "{bed} · للأداء قبل {date}",
    'port.todo.deposit': "إيداع الإيصال",
    'port.timetable.title': "استعمال الزمن",
    'port.timetable.range': "من {from} إلى {to}",
    'port.timetable.previous': "الفترة السابقة",
    'port.timetable.next': "الفترة الموالية",
    'port.timetable.today': "اليوم",
    'port.timetable.addToCalendar': "إضافة إلى يوميتي",
    'port.timetable.view': "العرض",
    'port.timetable.mode.week': "الأسبوع",
    'port.timetable.mode.day': "اليوم",
    'port.timetable.mode.list': "اللائحة",
    'port.timetable.hint': "تبقى الحصص الملغاة أو المؤجلة ظاهرة بتاريخها الجديد.",
    'port.timetable.allDay': "طوال اليوم",
    'port.timetable.cancelled': "ملغاة",
    'port.timetable.exam': "تقييم",
    'port.timetable.status': "الحالة",
    'port.timetable.movedFrom': "كانت مقررة في",
    'port.timetable.rooms': "القاعة",
    'port.timetable.teachers': "الأستاذ",
    'port.timetable.type': "النوع",
    'port.calendar.title': "إضافة إلى يوميتي",
    'port.calendar.sub': "يومية Google وOutlook وApple",
    'port.calendar.explain': "رابط شخصي يتبع استعمال زمنك: الصقه في يوميتك (« إضافة يومية عبر رابط »). يتحدث تلقائيا من الأسبوع الماضي إلى الأسابيع الثمانية المقبلة. احتفظ به لنفسك.",
    'port.calendar.existing': "أنشئ رابط في {date}. إنشاء رابط جديد يعطل القديم.",
    'port.calendar.create': "إنشاء رابطي",
    'port.calendar.renew': "إنشاء رابط جديد",
    'port.calendar.link': "رابط اليومية",
    'port.calendar.copy': "نسخ",
    'port.calendar.copied': "تم النسخ",
    'port.calendar.once': "يعرض هذا الرابط مرة واحدة: انسخه الآن.",
    'port.marks.title': "النقط والنتائج",
    'port.marks.tabMarks': "نقطي",
    'port.marks.tabResults': "نتائجي",
    'port.marks.groups': "الأسدس",
    'port.marks.year': "السنة",
    'port.marks.none': "لا نقطة مصادق عليها بعد.",
    'port.marks.subject': "المادة",
    'port.marks.coefficient': "المعامل",
    'port.marks.assessments': "التقييمات",
    'port.marks.average': "المعدل",
    'port.marks.groupAverage': "المعدل المؤقت · {group}",
    'port.marks.retake': "استدراك",
    'port.marks.abs': "غ",
    'port.marks.hint': "تظهر النقطة هنا بعد مصادقة الأستاذ على التقييم. خطأ؟ أخبر الأستاذ أو قدم طلبا.",
    'port.marks.hintAverage': "تظهر النقطة هنا بعد مصادقة الأستاذ على التقييم. المعدل مؤقت: المعتمد هو نتيجة المداولة. خطأ؟ أخبر الأستاذ أو قدم طلبا.",
    'port.results.none': "لا مداولة مغلقة بعد.",
    'port.results.list': "مداولاتي",
    'port.results.deliberation': "المداولة",
    'port.results.average': "المعدل",
    'port.results.rank': "الرتبة",
    'port.results.credits': "الأرصدة",
    'port.results.decision': "القرار",
    'port.results.closedOn': "أغلقت في {date}",
    'port.results.transcript': "الكشف",
    'port.results.detail': "التفصيل حسب الوحدة",
    'port.results.element': "الوحدة",
    'port.results.status': "النتيجة",
    'port.attendance.title': "المواظبة",
    'port.attendance.none': "لا مناداة مسجلة لهذه السنة.",
    'port.attendance.rate': "نسبة الحضور",
    'port.attendance.absences': "الغيابات",
    'port.attendance.absencesNote': "منها {count} للتبرير",
    'port.attendance.lates': "التأخرات",
    'port.attendance.latesNote': "{minutes} دقيقة في المجموع",
    'port.attendance.justified': "المبررة",
    'port.attendance.justifiedNote': "غيابات مغطاة بمبرر",
    'port.attendance.toJustify': "{count} غياب ينتظر مبررا.",
    'port.attendance.justifyAll': "تبرير هذه الغيابات",
    'port.attendance.atDesk': "سلم مبررك إلى الشؤون الدراسية.",
    'port.attendance.records': "الغيابات والتأخرات",
    'port.attendance.noRecord': "لا غياب ولا تأخر.",
    'port.attendance.date': "التاريخ",
    'port.attendance.session': "الحصة",
    'port.attendance.kind': "النوع",
    'port.attendance.justification': "التبرير",
    'port.attendance.absent': "غياب",
    'port.attendance.late': "تأخر {minutes} د",
    'port.attendance.justify': "تبرير",
    'port.attendance.state.none': "-",
    'port.attendance.state.justified': "مبرر",
    'port.attendance.state.pending': "طلب قيد المعالجة",
    'port.attendance.state.to_justify': "للتبرير",
    'port.attendance.exams': "الغياب عن التقييمات",
    'port.attendance.bySubject': "حسب المادة",
    'port.attendance.absentHours': "ساعات الغياب",
    'port.attendance.unjustifiedRate': "غير مبرر",
    'port.documents.title': "الوثائق",
    'port.documents.sub': "الوثائق التي سلمتها لك المؤسسة",
    'port.documents.none': "لا وثيقة منشورة بعد.",
    'port.documents.published': "نشر في {date}",
    'port.documents.download': "تحميل",
    'port.documents.verify': "التحقق",
    'port.documents.qr': "تحمل كل وثيقة رمز QR: يتحقق منه من تسلمه على موقع المؤسسة دون حساب.",
    'port.documents.ask': "طلب وثيقة",
    'port.documents.askSub': "عبر الطلبات",
    'port.documents.askButton': "طلب",
    'port.finance.title': "المالية",
    'port.finance.sub': "حسابي · {year}",
    'port.finance.billed': "المفوتر هذه السنة",
    'port.finance.billedNote': "قبل التخفيضات والأرصدة",
    'port.finance.paid': "المؤدى",
    'port.finance.paidNote': "{count} إيصال",
    'port.finance.due': "المتبقي للأداء",
    'port.finance.dueNote': "كل السنوات",
    'port.finance.late': "متأخر",
    'port.finance.lateNote': "أقساط فات أجلها",
    'port.finance.lateNone': "لا قسط متأخر",
    'port.finance.schedule': "جدول الأقساط",
    'port.finance.noSchedule': "لا قسط.",
    'port.finance.dueOn': "الأجل",
    'port.finance.label': "البيان",
    'port.finance.amount': "المبلغ",
    'port.finance.paidColumn': "المؤدى",
    'port.finance.state': "الحالة",
    'port.finance.installment': "القسط {position} من {count}",
    'port.finance.documents': "الفواتير والإيصالات",
    'port.finance.noDocument': "لا فاتورة.",
    'port.finance.invoice': "فاتورة · {label}",
    'port.finance.receipt': "إيصال · {method}",
    'port.finance.howToPay': "طريقة الأداء",
    'port.finance.housing': "إيداع إيصال السكن",
    'port.finance.housingSub': "بعد تحويل أو إيداع بنكي؛ يصادق عليه الصندوق.",
    'port.finance.housingDue': "المتبقي {amount} · قبل {date}",
    'port.finance.receiptPending': "إيصال {amount} أرسل في {date}، قيد التحقق",
    'port.finance.deposit': "إيداع إيصال",
    'port.profile.title': "ملفي",
    'port.profile.sub': "ما تعرفه المؤسسة عنك",
    'port.profile.saved': "تم حفظ المعطيات.",
    'port.profile.identity': "الهوية",
    'port.profile.identitySub': "خطأ؟ أخبر الشؤون الدراسية.",
    'port.profile.lastName': "الاسم العائلي",
    'port.profile.firstNames': "الاسم الشخصي",
    'port.profile.birthDate': "تاريخ الازدياد",
    'port.profile.number': "رقم التسجيل",
    'port.profile.nationalId': "ب.و.ت",
    'port.profile.massar': "رمز مسار",
    'port.profile.training': "التكوين",
    'port.profile.since': "مسجل منذ",
    'port.profile.coordinates': "معطياتي",
    'port.profile.coordinatesSub': "تعدلها بنفسك",
    'port.profile.coordinatesLocked': "تعدل لدى الشؤون الدراسية",
    'port.profile.edit': "تعديل",
    'port.profile.phones': "الهواتف",
    'port.profile.emails': "البريد الإلكتروني",
    'port.profile.addresses': "العناوين",
    'port.profile.contacts': "الأولياء والمخاطبون",
    'port.profile.contactsSub': "تمسكها الشؤون الدراسية",
    'port.profile.account': "حسابي",
    'port.profile.security': "كلمة المرور والتحقق الثنائي واللغة والصورة",
    'port.profile.securityHint': "تغير صورة حسابك في « ملفي الشخصي ».",
    'port.settings.title': "بوابة الطالب",
    'port.settings.sub': "ما يراه الطلبة في « فضائي »",
    'port.settings.saved': "تم حفظ الإعدادات.",
    'port.settings.off': "وحدات معطلة، أقسامها مخفية: {modules}.",
    'port.settings.module.finance': "المالية",
    'port.settings.module.requests': "الطلبات",
    'port.settings.module.stages': "التداريب",
    'port.settings.module.housing': "الداخلية",
    'port.settings.module.communication': "التواصل",
    'port.settings.rubrics': "الأقسام",
    'port.settings.rubricsSub': "يختفي القسم المعطل من قائمة الطلبة",
    'port.settings.moduleOff': "وحدة معطلة",
    'port.settings.flag.timetable': "استعمال الزمن",
    'port.settings.flag.marks': "نقط التقييمات",
    'port.settings.flag.average': "المعدل المؤقت",
    'port.settings.flag.results': "نتائج المداولات",
    'port.settings.flag.rank': "الرتبة في الفوج",
    'port.settings.flag.attendance': "المواظبة",
    'port.settings.flag.documents': "الوثائق",
    'port.settings.flag.finance': "المالية",
    'port.settings.flag.profile': "معطيات قابلة للتعديل",
    'port.settings.rule.timetable': "حصص أقسامه ومجموعاته؛ رابط iCal",
    'port.settings.rule.marks': "فقط التقييمات المصادق عليها من الأستاذ",
    'port.settings.rule.average': "محسوب من النقط المصادق عليها، موسوم « مؤقت »",
    'port.settings.rule.results': "بعد إغلاق المداولة",
    'port.settings.rule.rank': "مع نتائج المداولة المغلقة",
    'port.settings.rule.attendance': "المناداة المنجزة؛ التبرير عبر الطلبات",
    'port.settings.rule.documents': "الوثائق المنشورة من الشؤون الدراسية",
    'port.settings.rule.finance': "الأقساط والفواتير والإيصالات",
    'port.settings.rule.profile': "الهاتف والبريد والعنوان؛ كل تعديل مسجل",
    'port.settings.home': "الاستقبال",
    'port.settings.welcome': "رسالة الترحيب",
    'port.settings.links': "روابط مفيدة",
    'port.settings.linkLabel': "التسمية",
    'port.settings.linkUrl': "العنوان",
    'port.settings.addLink': "إضافة رابط",
    'port.settings.payment': "طريقة الأداء",
    'port.settings.paymentSub': "يظهر في « المالية »: التحويل والشباك والأوقات",
    'port.settings.paymentInfo': "كيفيات الأداء",
    'port.settings.note': "البوابة ليست وحدة مستقلة: تجمع للطالب ما تعرضه له كل وحدة، وتعوض قائمتها المداخل المتفرقة في الوحدات.",
    'journal.filter.group.housing': "الداخلية",
    'trace.table.housing_settings': "إعدادات الداخلية",
    'trace.table.housing_campus': "حي الداخلية",
    'trace.table.housing_room_type': "نوع الغرفة",
    'trace.table.housing_room': "الغرفة",
    'trace.table.housing_bed': "السرير",
    'trace.table.housing_price': "تعريفة الداخلية",
    'trace.table.housing_campaign': "حملة السكن",
    'trace.table.housing_campaign_place': "مكان حملة",
    'trace.table.housing_quota': "حصة حملة",
    'trace.table.housing_required_document': "وثيقة مطلوبة",
    'trace.table.housing_application': "طلب سكن",
    'trace.table.housing_allocation': "تخصيص سرير",
    'trace.table.housing_payment': "أداء الداخلية",
    'trace.table.housing_checklist': "شبكة المراقبة",
    'trace.table.housing_inspection': "زيارة مراقبة",
    'trace.table.housing_inspection_room': "غرفة زيارة",
    'trace.table.housing_provider': "مقدم خدمة",
    'trace.table.housing_repair_category': "فئة الإصلاح",
    'trace.table.housing_repair': "إصلاح",
    'trace.table.housing_file': "ملف الداخلية",
    'trace.table.housing_event': "سجل طلب سكن",
    'violation.heb.accept_late': "انتهى أجل القبول.",
    'violation.heb.application_locked': "لم يعد هذا الطلب قابلا للتعديل.",
    'violation.heb.bed_gender': "هذه الغرفة غير مفتوحة لهذا الجنس.",
    'violation.heb.bed_out': "هذا السرير خارج الخدمة.",
    'violation.heb.bed_taken': "هذا السرير مخصص سلفا.",
    'violation.heb.bed_used': "سبق استعمال هذا السرير: ضعوه خارج الخدمة.",
    'violation.heb.campaign_closed': "الحملة المغلقة لا تعدل.",
    'violation.heb.campaign_not_open': "الإيداع في هذه الحملة مغلق.",
    'violation.heb.campus_locked': "لا تغير الغرفة حيها.",
    'violation.heb.document_required': "أرفقوا هذه الوثيقة.",
    'violation.heb.inspection_locked': "لا تعدل الزيارة المبرمجة: برمجوا زيارة أخرى.",
    'violation.heb.no_beds': "للغرفة سرير واحد على الأقل.",
    'violation.heb.no_enrolment': "لا تسجيل نشط في سنة الحملة.",
    'violation.heb.no_places': "افتحوا مكانا واحدا على الأقل (حي ونوع غرفة) قبل الإيداع.",
    'violation.heb.no_price': "لا تنطبق أي تعريفة على هذا السرير لهذا الطلب.",
    'violation.heb.no_rooms': "لا غرفة في هذا المكان.",
    'violation.heb.not_eligible': "لا يخصص سرير إلا لطلب مقبول أو مودع أو في الانتظار.",
    'violation.heb.not_final': "تسلم المفاتيح بعد التخصيص النهائي (أداء الرسوم).",
    'violation.heb.not_for_level': "هذه الحملة غير مفتوحة لمستواكم.",
    'violation.heb.not_housed': "لا يخرج إلا المقيم.",
    'violation.heb.not_provisional': "هذا السرير لا ينتظر جوابا.",
    'violation.heb.not_releasable': "لم يعد هذا التخصيص قابلا للتحرير.",
    'violation.heb.nothing_to_pay': "لا أداء منتظر لهذا السرير.",
    'violation.heb.payment_decided': "عولج هذا الإيصال سلفا.",
    'violation.heb.price_duplicate': "توجد قاعدة بالمعايير نفسها.",
    'violation.heb.receipt_required': "أرفقوا الإيصال.",
    'violation.heb.retained_over': "الاقتطاع يتجاوز الضمانة.",
    'violation.heb.room_used': "سبق تخصيص هذه الغرفة: ضعوها خارج الخدمة.",
    'violation.heb.rules_required': "اقبلوا النظام الداخلي للداخلية.",
    'violation.heb.too_many_files': "عدد الملفات كبير.",
    'heb.export.label': "تصدير",
    'heb.export.busy': "جار التصدير…",
    'heb.export.simulation': "تصدير المحاكاة",
    'heb.invoice.download': "تحميل الفاتورة",
    'heb.selectAll': "تحديد الكل",
    'heb.selectRow': "تحديد {name}",
    'heb.applications.treat': "معالجة التحديد",
    'heb.applications.selected': "{count} طلب(ات) محددة",
    'heb.applications.decision': "القرار",
    'heb.applications.treatHint': "تتجاوز الطلبات المخصصة أو المغلقة.",
    'heb.applications.treated': "عولج {count} طلب(ات).",
    'heb.applications.treatedSkipped': "عولج {count} طلب(ات)، وتجووز {skipped} (مخصصة أو مغلقة).",
    'heb.campaignForm.next': "التالي",
    'heb.campaignForm.previous': "السابق",
    'heb.offline.waiting': "في انتظار الإرسال",
    'heb.offline.photo': "في الهاتف",
    'heb.offline.queued': "لا شبكة: تحفظ الملاحظة في الهاتف وترسل عند عودة الاتصال.",
    'heb.offline.count': "{count} ملاحظة(ات) في انتظار الإرسال",
    'heb.offline.retry': "الإرسال الآن",
    'heb.offline.sent': "أرسلت {count} ملاحظة(ات) منتظرة.",
    'heb.offline.failed': "رفض الخادم {count} ملاحظة(ات)، يجب إعادتها: {rooms}.",
    'heb.payments.remind': "تذكير",
    'heb.payments.remindCount': "تذكير ({count})",
    'heb.payments.remindOne': "تذكير",
    'heb.payments.reminded': "تم تذكير {count} طالب(ة).",
    'heb.room.reportBed': "تبليغ",
    'heb.repair.roomAt': "الغرفة {room} · {campus}",
    'heb.repairs.assignee': "المتدخل",
    'heb.repairs.allAssignees': "كل المتدخلين",
    'heb.repairs.staff': "أعوان المؤسسة",
    'heb.event.reminded': "تذكير بالأداء",
    'violation.heb.bad_selection': "اختاروا من 1 إلى 500 طلب.",
    'rel.modules.skills': "تتبع الكفايات",
    'rel.modules.skillsHint': "المرجعيات والأنشطة وإيداع الحجج والشبكة السريعة والمستويات النهائية وملف الطالب والحصائل",
    'journal.action.skills.settings_changed': "تعديل إعدادات الكفايات",
    'journal.action.skills.framework_saved': "تعديل مرجعية كفايات",
    'journal.action.skills.activity_saved': "تسجيل نشاط كفايات",
    'journal.action.skills.ratings_published': "نشر تقييمات الكفايات",
    'journal.action.skills.final_published': "المصادقة على المستوى النهائي لكفاية أو نشره",
    'journal.action.skills.deposit_submitted': "إيداع حجة كفاية",
    'violation.skl.scale_size': "يتضمن السلم من 2 إلى 8 مستويات.",
    'violation.skl.conversion_size': "نقطة لكل مستوى من السلم.",
    'violation.skl.conversion_range': "نقطة من 0 إلى 20.",
    'violation.skl.conversion_order': "ترتفع النقط مع المستويات.",
    'violation.skl.scale_locked': "هذه المرجعية مقيمة مسبقا: تحتفظ بعدد مستوياتها.",
    'violation.skl.marks_off': "الربط بالنقط معطل في الإعدادات.",
    'violation.skl.teacher_scope': "يربط الأستاذ مرجعيته بأحد دروسه.",
    'violation.skl.unknown_code': "هذا الرمز غير موجود في دليل السنة.",
    'violation.skl.empty_framework': "لا تنشر مرجعية بدون كفايات.",
    'violation.skl.framework_used': "لا تحذف إلا مسودة لم تستعمل؛ قم بأرشفتها.",
    'violation.skl.node_used': "مقيم مسبقا: لا يمكن حذف هذا العنصر.",
    'violation.skl.not_yours': "هذا القسم أو هذه المادة ليس من أقسامك.",
    'violation.skl.activity_locked': "مقيم مسبقا: لا يتغير القسم ولا المادة.",
    'violation.skl.activities_off': "لا تربط هنا إلا التقييمات المنقطة.",
    'violation.skl.no_skill': "استهدف كفاية واحدة على الأقل.",
    'violation.skl.framework_no_marks': "إحدى المرجعيات المستهدفة لا تحول مستوياتها إلى نقط.",
    'violation.skl.deposits_off': "الإيداعات معطلة في الإعدادات.",
    'violation.skl.activity_used': "مقيم أو مودع مسبقا: لا يحذف النشاط.",
    'violation.skl.no_assessment': "التقييم المرتبط غير موجود.",
    'violation.skl.assessment_validated': "التقييم المرتبط مصادق عليه: لا تتغير نقطه.",
    'violation.skl.file_empty': "الملف فارغ.",
    'violation.skl.file_too_big': "يتجاوز الملف 5 ميغابايت.",
    'violation.skl.file_format': "هذه الصيغة غير مقبولة لهذا الإيداع.",
    'violation.skl.deposit_closed': "انتهى أجل الإيداع.",
    'skl.scope.all': "كل البرامج",
    'skl.scope.domain': "الشعبة",
    'skl.scope.training': "البرنامج",
    'skl.scope.subject': "الدرس",
    'skl.state.draft': "مسودة",
    'skl.state.published': "منشور",
    'skl.state.archived': "مؤرشف",
    'skl.rule.last': "آخر مستوى",
    'skl.rule.frequent': "المستوى الأكثر تكرارا",
    'skl.rule.average': "المعدل المقرب",
    'skl.rule.manual': "إدخال يدوي",
    'skl.ruleHint.last': "مستوى آخر تقييم",
    'skl.ruleHint.frequent': "عند التساوي، الأحدث",
    'skl.ruleHint.average': "المستوى الأول يساوي 1، مقرب إلى الأقرب",
    'skl.ruleHint.manual': "يحدد الأستاذ المستوى النهائي بنفسه",
    'skl.kind.homework': "واجب",
    'skl.kind.practical': "أشغال تطبيقية",
    'skl.kind.project': "مشروع",
    'skl.kind.assessment': "تقييم منقط",
    'skl.format.pdf': "PDF",
    'skl.format.docx': "Word (.docx)",
    'skl.format.image': "صور (.jpg, .png)",
    'skl.format.xlsx': "جداول (.xlsx)",
    'skl.format.pptx': "عروض (.pptx)",
    'skl.preset.four': "أربعة مستويات",
    'skl.preset.colors': "ثلاثة ألوان",
    'skl.preset.cefr': "الإطار الأوروبي (A1 إلى C2)",
    'skl.tone.danger': "أحمر",
    'skl.tone.warn': "برتقالي",
    'skl.tone.info': "أزرق",
    'skl.tone.ok': "أخضر",
    'skl.tone.accent': "نيلي",
    'skl.tone.violet': "بنفسجي",
    'skl.tone.teal': "فيروزي",
    'skl.tone.muted': "رمادي",
    'skl.node.domain': "مجال كفايات",
    'skl.node.skill': "كفاية أساسية",
    'skl.node.indicator': "مؤشر",
    'skl.node.add.domain': "إضافة مجال",
    'skl.node.add.skill': "إضافة كفاية",
    'skl.node.add.indicator': "إضافة مؤشر",
    'skl.node.label.domain': "المجال",
    'skl.node.label.skill': "الكفاية الأساسية",
    'skl.node.label.indicator': "المؤشر أو المعيار",
    'skl.node.addConfirm': "إضافة",
    'skl.node.code': "الرمز",
    'skl.node.description': "الوصف",
    'skl.node.up': "رفع",
    'skl.node.down': "إنزال",
    'skl.node.used': "مقيم مسبقا: لا يحذف",
    'skl.level.none': "لم يقيم بعد",
    'skl.frameworks.title': "مرجعيات الكفايات",
    'skl.frameworks.sub': "مرتبطة ببرنامج أو شعبة أو درس",
    'skl.frameworks.new': "مرجعية جديدة",
    'skl.frameworks.kpiPublished': "المرجعيات المنشورة",
    'skl.frameworks.kpiPublishedNote': "{drafts} مسودة، {archived} مؤرشفة",
    'skl.frameworks.kpiSkills': "الكفايات",
    'skl.frameworks.kpiSkillsNote': "في المرجعيات المنشورة",
    'skl.frameworks.kpiActivities': "الأنشطة المرتبطة",
    'skl.frameworks.kpiActivitiesNote': "التقييمات والأنشطة",
    'skl.frameworks.kpiTotal': "المرجعيات",
    'skl.frameworks.kpiTotalNote': "كل الحالات",
    'skl.frameworks.search': "البحث عن مرجعية",
    'skl.frameworks.filter.live': "المنشورة والمسودات",
    'skl.frameworks.filter.published': "المنشورة",
    'skl.frameworks.filter.draft': "المسودات",
    'skl.frameworks.filter.archived': "المؤرشفة",
    'skl.frameworks.filter.all': "الكل",
    'skl.frameworks.none': "لا توجد مرجعية هنا.",
    'skl.frameworks.name': "المرجعية",
    'skl.frameworks.attached': "مرتبطة بـ",
    'skl.frameworks.content': "المحتوى",
    'skl.frameworks.contentValue': "{domains} مجال · {skills} كفاية · {indicators} مؤشر",
    'skl.frameworks.scale': "السلم",
    'skl.frameworks.rule': "المستوى النهائي",
    'skl.frameworks.state': "الحالة",
    'skl.frameworks.author': "أنشأها",
    'skl.frameworks.byTeacher': "لدرسه",
    'skl.frameworks.counts': "تحتسب في النقط",
    'skl.frameworks.note': "تنشئ الإدارة البيداغوجية مرجعيات البرامج والشعب. يمكن للأستاذ إنشاء مرجعية لدرسه: تراها الإدارة البيداغوجية ويمكنها استرجاعها. لا تحذف مرجعية مستعملة بل تؤرشف.",
    'skl.framework.newTitle': "مرجعية جديدة",
    'skl.framework.newSub': "سمها واربطها واختر سلمها؛ تبنى الشجرة بعد ذلك.",
    'skl.framework.publish': "نشر",
    'skl.framework.published': "نشرت المرجعية: تستعمل الآن في الأنشطة.",
    'skl.framework.duplicate': "نسخ",
    'skl.framework.duplicated': "أنشئت نسخة كمسودة.",
    'skl.framework.takeOver': "استرجاع",
    'skl.framework.takenOver': "استرجعت الإدارة البيداغوجية المرجعية.",
    'skl.framework.archive': "أرشفة",
    'skl.framework.archived': "أرشفت المرجعية: تبقى مستوياتها.",
    'skl.framework.deleted': "حذفت المسودة.",
    'skl.framework.tree': "الشجرة",
    'skl.framework.treeSub': "المجال، الكفاية الأساسية، المؤشرات",
    'skl.framework.emptyTree': "لا يوجد مجال بعد.",
    'skl.framework.skillsCount': "{count} كفاية",
    'skl.framework.indicatorsCount': "{count} مؤشر",
    'skl.framework.saved': "سجلت المرجعية.",
    'skl.framework.identity': "المرجعية",
    'skl.framework.name': "الاسم",
    'skl.framework.description': "الوصف",
    'skl.framework.scope': "مرتبطة بـ",
    'skl.framework.domain': "الشعبة",
    'skl.framework.training': "البرنامج",
    'skl.framework.subject': "الدرس",
    'skl.framework.choose': "اختر",
    'skl.framework.scale': "سلم التمكن",
    'skl.framework.scaleSub': "من 2 إلى 8 مستويات، من الأضعف إلى الأقوى",
    'skl.framework.scaleLocked': "مستعملة: عدد المستويات ثابت أما تسمياتها فلا",
    'skl.framework.stepCode': "رمز المستوى",
    'skl.framework.stepLabel': "تسمية المستوى",
    'skl.framework.stepTone': "لون المستوى",
    'skl.framework.newStep': "مستوى جديد",
    'skl.framework.addStep': "إضافة مستوى",
    'skl.framework.rule': "المستوى النهائي",
    'skl.framework.ruleNote': "سواء حسب أم لا، يصادق الأستاذ على المستوى النهائي ويمكنه تصحيحه قبل نشره.",
    'skl.framework.marks': "الربط بالنقط",
    'skl.framework.marksSub': "نقطة النشاط هي معدل كفاياته المحولة؛ وتتبع المسار المعتاد للنقط",
    'skl.framework.marksOff': "معطل في إعدادات الوحدة",
    'skl.framework.marksLink': "تعطي المستويات نقطة على 20",
    'skl.framework.markOf': "نقطة « {level} »",
    'skl.framework.conversionNote': "على 20، تحول إلى سلم التقييم المرتبط. ترتفع النقط مع المستويات.",
    'skl.framework.create': "إنشاء المرجعية",
    'skl.activities.title': "أنشطة الكفايات",
    'skl.activities.titleMine': "الكفايات",
    'skl.activities.sub': "الأنشطة والتقييمات المرتبطة بالكفايات · {year}",
    'skl.activities.frameworks': "المرجعيات",
    'skl.activities.report': "الحصائل",
    'skl.activities.new': "نشاط جديد",
    'skl.activities.kpiOpen': "الأنشطة المفتوحة",
    'skl.activities.kpiOpenNote': "غير منشورة بعد",
    'skl.activities.kpiDeposits': "الإيداعات المتوصل بها",
    'skl.activities.kpiDepositsNote': "{count} ناقصة",
    'skl.activities.kpiRated': "الطلبة المقيمون",
    'skl.activities.kpiRatedNote': "في كل الأنشطة",
    'skl.activities.kpiFinals': "مستويات نهائية للمصادقة",
    'skl.activities.kpiFinalsNote': "لطلبتي",
    'skl.activities.class': "القسم",
    'skl.activities.allClasses': "كل الأقسام",
    'skl.activities.list': "الأنشطة",
    'skl.activities.none': "لا يوجد نشاط بعد.",
    'skl.activities.finals': "مستويات نهائية للمصادقة",
    'skl.activities.finalsSub': "مقيمة في أنشطتي دون مستوى نهائي",
    'skl.activities.finalsNone': "لا شيء للمصادقة.",
    'skl.activities.validate': "مصادقة",
    'skl.activities.noteMine': "لا ترى إلا أنشطتك وأقسامك. يصل الإيداع كإشعار؛ فتح نشاط يفتح شبكة القسم.",
    'skl.activities.note': "ينشئ الأساتذة الأنشطة؛ تطلع عليها الإدارة البيداغوجية وتصادق على المستويات النهائية عند الحاجة.",
    'skl.activities.activity': "النشاط",
    'skl.activities.teacher': "الأستاذ",
    'skl.activities.target': "المستهدف",
    'skl.activities.deposits': "الإيداعات",
    'skl.activities.state': "الحالة",
    'skl.activities.counted': "تحتسب في النقط",
    'skl.activities.skills': "{count} كفاية",
    'skl.activities.depositCount': "{count} / {of} مودعة",
    'skl.activities.noDeposit': "بدون إيداع",
    'skl.activities.published': "منشور",
    'skl.activities.inProgress': "جار · {count} / {of}",
    'skl.activities.toRate': "للتقييم",
    'skl.dialog.title': "نشاط كفايات جديد",
    'skl.dialog.edit': "تعديل النشاط",
    'skl.dialog.create': "إنشاء",
    'skl.dialog.noClass': "لا قسم ولا مادة باسمك هذه السنة (حصص أو تقييمات).",
    'skl.dialog.classSubject': "القسم والمادة",
    'skl.dialog.kind': "النوع",
    'skl.dialog.assessment': "التقييم المنقط للربط",
    'skl.dialog.target': "التقييم الذي يتلقى النقطة",
    'skl.dialog.choose': "اختر",
    'skl.dialog.validated': "مصادق عليه",
    'skl.dialog.label': "النشاط",
    'skl.dialog.labelHint': "فارغ: تسمية التقييم",
    'skl.dialog.heldOn': "التاريخ",
    'skl.dialog.skills': "الكفايات المستهدفة",
    'skl.dialog.search': "البحث في مرجعيات القسم",
    'skl.dialog.noFramework': "لا تنطبق أي مرجعية منشورة على هذا القسم وهذه المادة.",
    'skl.dialog.deposit': "طلب إيداع ملف",
    'skl.dialog.formats': "الملفات المقبولة",
    'skl.dialog.due': "آخر أجل",
    'skl.dialog.depositNote': "5 ميغابايت على الأكثر لكل ملف. يتلقى الطلبة إشعارا ويرون الطلب في « كفاياتي ».",
    'skl.dialog.counted': "احتساب في النقط",
    'skl.dialog.countedNote': "عند النشر، يتلقى كل طالب النقطة المحولة في التقييم المرتبط؛ وتصادق كأي نقطة.",
    'skl.dialog.linkNote': "يربط التقييم المنقط (مراقبة، امتحان) بنفس الطريقة: اختر النوع « تقييم منقط ».",
    'skl.grid.ratedOf': "{count} مقيم من {of}",
    'skl.grid.depositsOf': "{count} إيداع من {of}",
    'skl.grid.skill': "الكفاية",
    'skl.grid.edit': "تعديل",
    'skl.grid.publish': "نشر التقييمات",
    'skl.grid.republish': "إعادة النشر",
    'skl.grid.published': "نشرت التقييمات: يراها الطلبة.",
    'skl.grid.publishedMarks': "نشرت التقييمات؛ كتبت {count} نقطة في التقييم المرتبط.",
    'skl.grid.draft': "مسودة: لا يرى الطلبة شيئا قبل « نشر التقييمات ».",
    'skl.grid.countedNote': "تحتسب في النقط: تذهب النقطة المحولة إلى « {assessment} » عند النشر.",
    'skl.grid.mode': "العرض",
    'skl.grid.byIndicator': "حسب المؤشر",
    'skl.grid.skillOnly': "الكفاية فقط",
    'skl.grid.live': "أثناء التطبيق",
    'skl.grid.note': "نقرة واحدة لكل خانة؛ يسجل فورا، ونقرة ثانية تحذف المستوى. يفتح الإيداع دون مغادرة الشبكة.",
    'skl.grid.student': "الطالب",
    'skl.grid.deposit': "الإيداع",
    'skl.grid.skillLevel': "الكفاية",
    'skl.grid.mark': "النقطة",
    'skl.grid.comment': "التعليق",
    'skl.grid.noDeposit': "لا إيداع",
    'skl.grid.addComment': "إضافة",
    'skl.grid.liveOf': "المؤشر {index} من {count} · {done} / {of} مقيمون",
    'skl.grid.previous': "المؤشر السابق",
    'skl.grid.next': "المؤشر التالي",
    'skl.grid.searchStudent': "البحث عن طالب",
    'skl.grid.liveNote': "يسجل عند كل لمسة. انتقل إلى المؤشر التالي بالأسهم.",
    'skl.history.progress': "التقدم",
    'skl.history.entries': "سجل التقييمات",
    'skl.history.noLevel': "لا مستوى بعد.",
    'skl.history.none': "لا تقييم بعد.",
    'skl.history.date': "التاريخ",
    'skl.history.activity': "النشاط",
    'skl.history.level': "المستوى",
    'skl.history.comment': "التعليق",
    'skl.history.proof': "الحجة",
    'skl.history.unpublished': "غير منشور بعد",
    'skl.history.final': "المستوى النهائي",
    'skl.history.validatedBy': "صادق عليه {name} في {at}، غير منشور بعد",
    'skl.history.publishedAt': "نشر في {at}",
    'skl.history.publishedBy': "نشره {name} في {at}",
    'skl.history.manual': "إدخال يدوي: لا يقترح شيء",
    'skl.history.proposed': "مقترح حسب قاعدة « {rule} »",
    'skl.history.validated': "المستوى النهائي المصادق عليه",
    'skl.history.appraisal': "الملاحظة النهائية",
    'skl.history.publish': "نشر للطالب",
    'skl.history.savedNotice': "سجل المستوى النهائي، غير منشور بعد.",
    'skl.history.publishedNotice': "نشر المستوى النهائي: أشعر الطالب.",
    'skl.history.finalNote': "بعد نشره، يظهر في « كفاياتي » لدى الطالب.",
    'skl.report.title': "حصائل الكفايات",
    'skl.report.sub': "المستويات النهائية لقسم حسب مرجعية",
    'skl.report.excel': "Excel",
    'skl.report.csv': "CSV",
    'skl.report.class': "القسم",
    'skl.report.framework': "المرجعية",
    'skl.report.none': "لا تنطبق أي مرجعية منشورة على هذا القسم.",
    'skl.report.note': "يتضمن التصدير سطرا لكل طالب وكفاية: المستوى والتاريخ والأستاذ والملاحظة. لا تؤثر الكفايات في النقط إلا إذا كان الربط مفعلا، عبر التقييم المرتبط.",
    'skl.report.finals': "المستويات النهائية",
    'skl.report.student': "الطالب",
    'skl.report.acquired': "المكتسبة",
    'skl.report.unpublished': "مصادق عليه، غير منشور بعد",
    'skl.report.distribution': "توزيع المستويات",
    'skl.settings.title': "الكفايات",
    'skl.settings.sub': "إعدادات الوحدة",
    'skl.settings.saved': "سجلت الإعدادات.",
    'skl.settings.working': "طريقة العمل",
    'skl.settings.enabled': "الوحدة مفعلة",
    'skl.settings.enabledHint': "عند التعطيل تختفي القوائم والشاشات؛ لا يحذف شيء",
    'skl.settings.flag.teachersCreate': "ينشئ الأساتذة مرجعيات",
    'skl.settings.flag.activities': "أنشطة بدون نقط",
    'skl.settings.flag.deposits': "إيداع الحجج",
    'skl.settings.flag.studentProfile': "ملف ظاهر للطلبة",
    'skl.settings.flag.studentExport': "حصيلة قابلة للتحميل من طرف الطالب",
    'skl.settings.flag.marksLink': "الربط بالنقط",
    'skl.settings.rule.teachersCreate': "لدروسهم؛ تراها الإدارة البيداغوجية ويمكنها استرجاعها",
    'skl.settings.rule.activities': "واجب وتطبيق ومشروع، إضافة إلى التقييمات المنقطة",
    'skl.settings.rule.deposits': "بطلب صريح من الأستاذ فقط",
    'skl.settings.rule.studentProfile': "في « فضائي »، التقييمات المنشورة فقط",
    'skl.settings.rule.studentExport': "حصيلته الخاصة بصيغة Excel",
    'skl.settings.rule.marksLink': "عند التعطيل: لا تمس أي نقطة؛ عند التفعيل تختار كل مرجعية وكل نشاط الاحتساب أم لا",
    'skl.settings.formats': "الملفات المقبولة",
    'skl.settings.formatsSub': "5 ميغابايت على الأكثر لكل ملف، يتحقق منها في المتصفح والخادم",
    'skl.settings.scale': "السلم الافتراضي",
    'skl.settings.scaleSub': "يقترح عند إنشاء مرجعية",
    'skl.settings.note': "لا ترسل الإشعارات (طلب إيداع، نشر مستوى نهائي، توصل بإيداع) إلا مع وحدة التواصل.",
    'skl.mine.title': "كفاياتي",
    'skl.mine.export': "حصيلتي بصيغة Excel",
    'skl.mine.kpiEvaluated': "الكفايات المقيمة",
    'skl.mine.kpiEvaluatedNote': "من أصل {total} في برنامجي",
    'skl.mine.kpiAcquired': "مكتسبة أو متمكن منها",
    'skl.mine.kpiAcquiredNote': "منها {count} متمكن منها",
    'skl.mine.kpiProgress': "في طور الاكتساب",
    'skl.mine.kpiProgressNote': "للتعزيز",
    'skl.mine.kpiDeposits': "إيداعات للإنجاز",
    'skl.mine.kpiDepositsNote': "يطلبها أساتذتي",
    'skl.mine.profile': "ملفي",
    'skl.mine.deposits': "الإيداعات المطلوبة",
    'skl.mine.noDeposit': "لا إيداع مطلوب.",
    'skl.mine.due': "قبل {at}",
    'skl.mine.deposited': "أودع: {name} في {at}",
    'skl.mine.closed': "مغلق",
    'skl.mine.deposit': "إيداع",
    'skl.mine.replace': "استبدال",
    'skl.mine.skills': "كفاياتي",
    'skl.mine.skillsSub': "نقرة تفتح السجل والتعليقات",
    'skl.mine.none': "لم تنشر أي مرجعية لبرنامجك بعد.",
    'skl.mine.notYet': "لم تقيم بعد",
    'skl.mine.evaluations': "{count} تقييم",
    'skl.mine.final': "مستوى نهائي منشور",
    'skl.mine.note': "لا تظهر إلا التقييمات التي ينشرها أساتذتك. لا يفتح فضاء الإيداع إلا بطلب منهم.",
    'skl.mine.noFinal': "لا مستوى نهائي بعد.",
    'skl.mine.byIndicator': "حسب المؤشر",
    'skl.mine.byIndicatorSub': "في آخر تقييم",
    'skl.mine.proofs': "حججي",
    'skl.mine.noProof': "لا حجة مودعة.",
    'skl.mine.open': "فتح",
    'skl.deposit.title': "إيداع: {label}",
    'skl.deposit.by': "طلبه",
    'skl.deposit.skills': "الكفايات",
    'skl.deposit.due': "آخر أجل",
    'skl.deposit.noDue': "لا يوجد",
    'skl.deposit.accepted': "المقبول",
    'skl.deposit.acceptedValue': "{formats} · 5 ميغابايت على الأكثر",
    'skl.deposit.drop': "اسحب الملف هنا أو اختره",
    'skl.deposit.tooBig': "{name} · {size}: يتجاوز الملف 5 ميغابايت.",
    'skl.deposit.badFormat': "{name}: صيغة غير مقبولة ({formats}).",
    'skl.deposit.choose': "اختر ملفا.",
    'skl.deposit.remove': "إزالة",
    'skl.deposit.current': "أودع مسبقا: {name}. يستبدله ملف جديد.",
    'skl.deposit.note': "كلمة للأستاذ",
    'skl.deposit.replaceNote': "حتى آخر أجل يمكنك استبدال ملفك؛ ويشعر الأستاذ بذلك.",
    'skl.deposit.done': "أودعت الحجة: أشعر أستاذك.",
    'skl.deposit.replaced': "استبدلت الحجة: أشعر أستاذك.",
    'export.label': "Excel",
    'export.busy': "جار التصدير…",
    'export.pages': "الصفحة {done} من {total}…",
    'export.hint': "تصدير كل الأسطر المصفاة إلى Excel بأعمدة الجدول",
    'violation.export.rows': "التصدير محدود في 50000 سطر: دقق المرشحات.",
    'violation.export.columns': "يتضمن هذا الجدول أعمدة كثيرة للتصدير.",
    'el.acc.allYear': "طوال السنة",
    'el.acc.change': "تغيير",
    'el.acc.edit': "تعديل الترتيب",
    'el.acc.extra': "محاولات إضافية",
    'el.acc.from': "من",
    'el.acc.grant': "منح",
    'el.acc.granted': "مُنح من",
    'el.acc.kind': "الترتيب",
    'el.acc.new': "ترتيب جديد",
    'el.acc.none': "لا يوجد أي ترتيب ممنوح.",
    'el.acc.note': "تمنحها الشؤون الدراسية وتُسجَّل وتُطبَّق تلقائيًا أثناء الاختبار (مدة أطول، نافذة خاصة). يراها الأستاذ على الأوراق دون أن يتمكن من تعديلها.",
    'el.acc.percent': "وقت إضافي (%)",
    'el.acc.quiz': "التقييم",
    'el.acc.reason': "السبب",
    'el.acc.remove': "سحب",
    'el.acc.scope': "النطاق",
    'el.acc.search': "الاسم أو رقم التسجيل",
    'el.acc.sub': "وقت إضافي، نوافذ مؤجلة، محاولات إضافية",
    'el.acc.title': "الترتيبات",
    'el.acc.to': "إلى",
    'el.acc.kind.extra_time': "وقت إضافي",
    'el.acc.kind.shifted_window': "نافذة مؤجلة",
    'el.acc.kind.extra_attempt': "محاولة إضافية",
    'el.acc.short.extra_time': "وقت إضافي",
    'el.acc.short.shifted_window': "نافذة مؤجلة",
    'el.acc.short.extra_attempt': "محاولة إضافية",
    'el.attempt.anonymous': "ورقة مجهولة",
    'el.attempt.device': "الجهاز",
    'el.attempt.log': "سجل المحاولة",
    'el.attempt.logSub': "مؤشرات للأستاذ، وليست عقوبة تلقائية أبدًا",
    'el.attempt.minutes': "الدقائق الممنوحة",
    'el.attempt.points': "{got} من {max} نقطة",
    'el.attempt.points2': "النقاط",
    'el.attempt.question': "السؤال {n}",
    'el.attempt.reason': "السبب (مسجَّل)",
    'el.attempt.receipt': "الإشعار",
    'el.attempt.reopen': "إعادة فتح المحاولة",
    'el.attempt.reopenNote': "يستأنف الطالب ورقته بإجاباته للمدة الممنوحة.",
    'el.attempt.score': "النقطة",
    'el.attempt.summary': "الورقة",
    'el.attempt.neutralised.give': "مُلغاة: النقاط ممنوحة",
    'el.attempt.neutralised.remove': "مُلغاة: خارج المجموع",
    'el.bank.allChapters': "كل الفصول",
    'el.bank.allDifficulties': "كل المستويات",
    'el.bank.allOutcomes': "كل المكتسبات",
    'el.bank.allTypes': "كل الأنواع",
    'el.bank.archived': "المؤرشفة",
    'el.bank.chapter': "الفصل",
    'el.bank.difficulty': "الصعوبة",
    'el.bank.discrimination': "التمييز",
    'el.bank.export': "تصدير",
    'el.bank.import': "استيراد (جدول)",
    'el.bank.importErrors': "الأسطر المستبعدة: {list}.",
    'el.bank.imported': "تم استيراد {count} سؤال.",
    'el.bank.live': "قيد الاستعمال",
    'el.bank.new': "سؤال جديد",
    'el.bank.none': "لا يوجد أي سؤال.",
    'el.bank.note': "تعديل سؤال مستعمل ينشئ نسخة جديدة: تحتفظ الأوراق السابقة بالنسخة المجاب عنها. تُحسب نسبة النجاح ومؤشر التمييز بعد الاستعمال، ويُشار إلى المشتت الذي لم يُختر أبدًا.",
    'el.bank.outcome': "المكتسب",
    'el.bank.points': "النقاط",
    'el.bank.question': "السؤال",
    'el.bank.search': "البحث عن سؤال",
    'el.bank.sub': "{course} · {count} سؤال · {classes}",
    'el.bank.success': "النجاح",
    'el.bank.title': "بنك الأسئلة",
    'el.bank.type': "النوع",
    'el.bank.untitled': "(بدون نص)",
    'el.bank.unused': "لم يُختر أبدًا: {list}",
    'el.bank.version': "النسخة",
    'el.copies.absent': "الغائبون",
    'el.copies.absentBadge': "غائب",
    'el.copies.all': "الكل",
    'el.copies.attempt': "المحاولة {n}",
    'el.copies.auto': "أُرسلت تلقائيًا",
    'el.copies.autoScore': "{score} تلقائي",
    'el.copies.copies': "الأوراق",
    'el.copies.filter': "تصفية الأوراق",
    'el.copies.focus': "{count} فقدان للتركيز",
    'el.copies.grade': "التصحيح حسب السؤال",
    'el.copies.graded': "المصححة",
    'el.copies.incident': "حادث",
    'el.copies.incidentNote': "تُعاد فتح المحاولة من الورقة، ويُمدَّد الوقت من إعدادات التقييم. يُطلب سبب ويُسجَّل.",
    'el.copies.kept': "المعتمدة {score}",
    'el.copies.lot': "دفعة النقط:",
    'el.copies.noCopy': "لا توجد أوراق بعد.",
    'el.copies.notStarted': "لم تبدأ",
    'el.copies.open': "الورقة",
    'el.copies.publish': "نشر النتائج",
    'el.copies.publishedDone': "تم نشر {count} ورقة للطلبة.",
    'el.copies.reconnects': "{count} إعادة اتصال",
    'el.copies.regrade': "إعادة تصحيح سؤال",
    'el.copies.regradeDo': "إعادة التصحيح",
    'el.copies.regradeSub': "لكل الأوراق، مع الأثر قبل التأكيد",
    'el.copies.results': "النتائج",
    'el.copies.returned': "أُعيدت الدفعة: {reason}",
    'el.copies.score': "النقطة",
    'el.copies.signals': "المؤشرات",
    'el.copies.state': "الحالة",
    'el.copies.sub': "{total} طالب · {graded} مصححة · {toGrade} للتصحيح · {absent} غائب",
    'el.copies.times': "البداية · النهاية",
    'el.copies.title': "{quiz} · الأوراق",
    'el.copies.toGrade': "للتصحيح",
    'el.copies.validate': "اعتماد الأوراق المصححة",
    'el.copies.validated': "المعتمدة",
    'el.copies.validatedDone': "تم اعتماد {count} ورقة.",
    'el.copies.view': "العرض",
    'el.course.bank': "بنك الأسئلة",
    'el.course.credits': "{credits} أرصدة",
    'el.course.readOnly': "قراءة فقط: هذه المادة ليست ضمن برمجتك أو السنة مغلقة.",
    'el.course.students': "{count} طالب",
    'el.course.tabs': "أقسام المادة",
    'el.dash.allPlans': "كل خطط المواد",
    'el.dash.beforeOpening': "قبل الفتح",
    'el.dash.byTraining': "خطط المواد حسب العرض",
    'el.dash.lots': "دفعات النقط",
    'el.dash.lotsNote': "{control} للمراقبة، {transfer} للتحويل",
    'el.dash.note': "أقرب فتح لموضوع ينتظر الاعتماد: {at}.",
    'el.dash.plans': "الخطط المنشورة",
    'el.dash.plansNote': "{rate} % من المواد",
    'el.dash.readOnly': "قراءة فقط",
    'el.dash.subjects': "مواضيع للاعتماد",
    'el.dash.subjectsNote': "امتحانات تحتسب في المعدل",
    'el.dash.title': "لوحة التعلم الإلكتروني",
    'el.dash.toGrade': "أوراق للتصحيح",
    'el.dash.toGradeNote': "في المؤسسة",
    'el.dash.toValidate': "مواضيع امتحان للاعتماد",
    'el.dialog.add': "إضافة",
    'el.dialog.addTitle': "إضافة مورد",
    'el.dialog.afterQuiz': "بعد النجاح في الاختبار «{title}»",
    'el.dialog.afterViewed': "بعد الاطلاع على «{title}»",
    'el.dialog.body': "محتوى الصفحة",
    'el.dialog.choose': "اختر…",
    'el.dialog.closesAt': "الإغلاق",
    'el.dialog.condition': "الشرط",
    'el.dialog.diffusion': "النشر",
    'el.dialog.editTitle': "تعديل المورد",
    'el.dialog.file': "الملف",
    'el.dialog.fileHint': "{kinds} · {mb} م.ب كحد أقصى",
    'el.dialog.groups': "المجموعات",
    'el.dialog.groupsNote': "بدون اختيار: كل مجموعات المادة.",
    'el.dialog.kind': "نوع المورد",
    'el.dialog.library': "المكتبة المشتركة",
    'el.dialog.min': "النقطة الدنيا (على 20)",
    'el.dialog.noCondition': "بدون شرط",
    'el.dialog.notify': "إشعار الطلبة",
    'el.dialog.notifyNote': "في الملخص اليومي",
    'el.dialog.opensAt': "الفتح",
    'el.dialog.preview': "معاينة",
    'el.dialog.readOnly': "اطلاع فقط",
    'el.dialog.readOnlyNote': "بدون تنزيل",
    'el.dialog.replace': "استبدال «{name}» (نسخة جديدة)",
    'el.dialog.section': "القسم",
    'el.dialog.shareable': "مشاركة في المكتبة المشتركة",
    'el.dialog.shareableNote': "يمكن للمواد الأخرى الإحالة إليه",
    'el.dialog.title': "العنوان",
    'el.dialog.url': "عنوان الرابط",
    'el.dialog.versions': "النسخ السابقة",
    'el.dialog.videoHint': "يوتيوب أو فيميو أو مايكروسوفت ستريم: يُشغَّل الفيديو في الصفحة ولا يُستضاف.",
    'el.dialog.videoUrl': "عنوان الفيديو",
    'el.dialog.visibility': "الظهور",
    'el.dialog.watermark': "علامة مائية بالاسم",
    'el.dialog.watermarkNote': "الاسم ورقم التسجيل والتاريخ على كل صفحة من ملف PDF الذي ينزله الطالب",
    'el.grades.copies': "الأوراق",
    'el.grades.grade': "تصحيح",
    'el.grades.lot': "دفعة النقط",
    'el.grades.none': "لا توجد أوراق بعد.",
    'el.grades.published': "نُشرت في",
    'el.grades.sub': "صحح واعتمد وانشر، ثم تنتقل الدفعة إلى المراقبة والتحويل",
    'el.grades.title': "الأوراق والنقط",
    'el.grades.toGrade': "للتصحيح",
    'el.grading.accept': "قبول وإعادة تصحيح {count} ورقة",
    'el.grading.anonymous': "مجهولة",
    'el.grading.auto': "التصحيح التلقائي: {points} من {max}",
    'el.grading.blank': "بدون إجابة",
    'el.grading.comment': "تعليق",
    'el.grading.comments': "تعليقات قابلة لإعادة الاستعمال",
    'el.grading.copyOf': "الورقة {n} من {total}",
    'el.grading.nextCopy': "الورقة التالية",
    'el.grading.nextQuestion': "السؤال التالي",
    'el.grading.noComment': "ستظهر هنا التعليقات المكتوبة سابقًا.",
    'el.grading.noUnexpected': "لا توجد إجابة قصيرة غير متوقعة.",
    'el.grading.none': "لا توجد أوراق لهذا السؤال.",
    'el.grading.note': "تبقى الأسماء مخفية حتى الاعتماد إذا كان التقييم مجهولًا. سيأتي توزيع الأوراق على عدة مصححين والتصحيح المزدوج في دفعة لاحقة.",
    'el.grading.pickQuestion': "اختيار السؤال",
    'el.grading.points': "النقاط (من {max})",
    'el.grading.previousCopy': "الورقة السابقة",
    'el.grading.previousQuestion': "السؤال السابق",
    'el.grading.question': "س {n} · {text}",
    'el.grading.rubric': "شبكة المعايير",
    'el.grading.saveNext': "حفظ والتالية",
    'el.grading.statement': "نص السؤال",
    'el.grading.sub': "{quiz} · {toGrade} ورقة للتصحيح من {total}",
    'el.grading.title': "التصحيح حسب السؤال",
    'el.grading.unexpected': "إجابات قصيرة غير متوقعة",
    'el.grading.written': "{count} ورقة",
    'el.mine.afterDate': "بعد {at}",
    'el.mine.afterPublication': "بعد النشر",
    'el.mine.attemptsLeft': "{count} محاولة",
    'el.mine.copy': "الورقة",
    'el.mine.counted': "تحتسب في المعدل",
    'el.mine.course': "المادة",
    'el.mine.courses': "موادي",
    'el.mine.from': "ابتداء من {at}",
    'el.mine.new': "جديد",
    'el.mine.newCount': "{count} مورد جديد",
    'el.mine.newResource': "مورد جديد",
    'el.mine.noCourse': "لا توجد مواد على الخط بعد.",
    'el.mine.noPlan': "الخطة لم تنشر بعد",
    'el.mine.noResult': "لا توجد نتائج بعد.",
    'el.mine.notCounted': "بدون نقطة",
    'el.mine.note': "تظهر التقييمات المفتوحة أيضًا في أجندة البوابة، ويُرسل تذكير في اليوم السابق.",
    'el.mine.nothing': "لا شيء للقيام به هذا الأسبوع.",
    'el.mine.open': "فتح",
    'el.mine.planUpdated': "الخطة v{version} محدثة",
    'el.mine.planVersion': "الخطة v{version}",
    'el.mine.quiz': "التقييم",
    'el.mine.results': "نتائجي على الخط",
    'el.mine.resultsSub': "تظهر النقط بعد نشرها من طرف الأستاذ",
    'el.mine.resume': "استئناف",
    'el.mine.score': "النقطة",
    'el.mine.seeCopy': "عرض ورقتي",
    'el.mine.seeKey': "عرض التصحيح",
    'el.mine.soon': "قريبًا",
    'el.mine.soonBadge': "قريبًا",
    'el.mine.start': "ابدأ",
    'el.mine.sub': "{classes} · هذا الأسبوع",
    'el.mine.title': "موادي",
    'el.mine.todo': "للقيام به هذا الأسبوع",
    'el.mine.trackedNote': "تسجل مؤسستك فتح الموارد وتنزيلها حتى يعرف الأستاذ ما تمت قراءته.",
    'el.mine.until': "مفتوح حتى {at}",
    'el.plan.assessment': "التقييم",
    'el.plan.assessments': "طرق التقييم",
    'el.plan.assessmentsSub': "مأخوذة من نظام الدراسات",
    'el.plan.changes': "{count} تغيير",
    'el.plan.coefficient': "المعامل",
    'el.plan.common': "خطة مشتركة",
    'el.plan.conform': "مطابق لنظام الدراسات: لا شيء لإعادة إدخاله.",
    'el.plan.course': "المادة",
    'el.plan.date': "التاريخ",
    'el.plan.duplicate': "استئناف خطة السنة الماضية",
    'el.plan.group': "خطة",
    'el.plan.groups': "المجموعات",
    'el.plan.hours': "الحجم الساعي",
    'el.plan.level': "المستوى",
    'el.plan.linked': "المرتبطة",
    'el.plan.linkedValue': "{count} مورد",
    'el.plan.missing': "مطلوب للنشر: {list}.",
    'el.plan.nextDraft': "النسخة {version} قيد الإعداد",
    'el.plan.noAssessment': "لا يوجد تقييم في النظام لهذه المادة.",
    'el.plan.noSession': "لا توجد حصص مبرمجة.",
    'el.plan.noVersion': "لا توجد نسخة منشورة.",
    'el.plan.objectives': "الأهداف",
    'el.plan.prefilled': "الأقسام المعبأة مسبقًا",
    'el.plan.prefilledSub': "مأخوذة من الدليل والبرمجة، للقراءة فقط",
    'el.plan.publish': "نشر الآن",
    'el.plan.publishedDone': "تم نشر النسخة {version} وإشعار الطلبة.",
    'el.plan.readings': "القراءات",
    'el.plan.retake': "استدراك",
    'el.plan.saveDraft': "حفظ المسودة",
    'el.plan.saved': "تم حفظ المسودة.",
    'el.plan.schedule': "برمجة النشر",
    'el.plan.scheduleAt': "النشر في",
    'el.plan.scheduledDone': "تمت برمجة النشر.",
    'el.plan.scheduledOn': "النشر مبرمج في {at}",
    'el.plan.session': "الحصة",
    'el.plan.sessions': "سير الحصص",
    'el.plan.sessionsSub': "سطر لكل حصة مبرمجة",
    'el.plan.state': "الحالة",
    'el.plan.state.draft': "مسودة",
    'el.plan.state.none': "خطة للإعداد",
    'el.plan.state.published': "خطة منشورة v{version}",
    'el.plan.state.published_draft': "خطة منشورة v{version}، معدلة",
    'el.plan.state.scheduled': "مبرمج",
    'el.plan.teachers': "الأساتذة",
    'el.plan.template': "النموذج",
    'el.plan.templateNote': "أقسام النموذج «{name}»، والمعلّمة بنجمة إلزامية للنشر.",
    'el.plan.theme': "الموضوع",
    'el.plan.training': "العرض",
    'el.plan.type': "النوع",
    'el.plan.unschedule': "إلغاء البرمجة",
    'el.plan.versionLine': "نُشرت في {at} من طرف {by}",
    'el.plan.versions': "النسخ",
    'el.plan.written': "الأقسام المحررة",
    'el.plans.course': "المادة",
    'el.plans.courses': "المواد",
    'el.plans.drafts': "المسودات",
    'el.plans.filter': "تصفية الخطط",
    'el.plans.none': "لا توجد مواد هذه السنة.",
    'el.plans.published': "المنشورة",
    'el.plans.publishedAt': "نُشر في",
    'el.plans.search': "البحث عن مادة أو أستاذ",
    'el.plans.state': "الحالة",
    'el.plans.title': "خطط المواد",
    'el.plans.toPrepare': "للإعداد",
    'el.plans.training': "العرض",
    'el.plans.f.all': "الكل",
    'el.plans.f.published': "منشورة",
    'el.plans.f.draft': "مسودات",
    'el.plans.f.none': "للإعداد",
    'el.play.after': "ستظهر النقطة والتصحيح بعد نشرهما من طرف أستاذك.",
    'el.play.answer': "إجابتك",
    'el.play.answered': "{count} إجابة محفوظة من {total}",
    'el.play.attemptLine': "المحاولة {n} من {total} · بدأت {at}",
    'el.play.autosave': "حفظ مستمر",
    'el.play.back': "العودة إلى المادة",
    'el.play.confirm': "تمت الإجابة عن {answered} سؤال من {total}. بعد الإرسال لا يمكن تعديل الورقة.",
    'el.play.dontKnow': "لا أعرف",
    'el.play.false': "خطأ",
    'el.play.flag': "تعليم للمراجعة",
    'el.play.from': "ابتداء من {at}",
    'el.play.goTo': "الانتقال إلى السؤال {n}",
    'el.play.key': "التصحيح:",
    'el.play.legend': "أخضر: مجاب · كهرماني: للمراجعة",
    'el.play.maxWords': "{count} كلمة كحد أقصى",
    'el.play.meta': "{points} نقطة · {type}",
    'el.play.next': "التالي",
    'el.play.noFile': "لم يُودَع أي ملف",
    'el.play.noKey': "التصحيح غير متاح لهذا التقييم أو ليس بعد.",
    'el.play.notYet': "بعد النشر",
    'el.play.note': "تُرسل كل إجابة إلى الخادم فور إدخالها: انقطاع الشبكة لا يُضيع شيئًا، وتُستأنف المحاولة إذا أُغلق المتصفح. عند انتهاء الوقت تُرسل الورقة تلقائيًا.",
    'el.play.offline': "غير متصل: ستُرسل إجاباتك فور عودة الشبكة.",
    'el.play.pick': "اختر…",
    'el.play.previous': "السابق",
    'el.play.questionOf': "السؤال {n} من {total}",
    'el.play.questions': "الأسئلة",
    'el.play.receipt': "إشعار رقم",
    'el.play.received': "تم استلام ورقتك",
    'el.play.saved': "تم حفظ الإجابة على {at}",
    'el.play.score': "نقطتي",
    'el.play.submit': "إنهاء وإرسال",
    'el.play.submitNow': "إرسال ورقتي",
    'el.play.submittedAt': "أُرسلت {at}",
    'el.play.true': "صحيح",
    'el.play.unflag': "إزالة العلامة",
    'el.play.upload': "إيداع ملف",
    'el.question.abs': "مطلقة",
    'el.question.accentsSensitive': "مراعاة الحركات والنبرات",
    'el.question.addBlank': "إضافة فراغ",
    'el.question.addChoice': "إضافة اختيار",
    'el.question.addItem': "إضافة عنصر",
    'el.question.addPair': "إضافة زوج",
    'el.question.answer': "الإجابة الصحيحة",
    'el.question.answered': "تمت الإجابة عن هذا السؤال: حفظه ينشئ نسخة جديدة وتحتفظ الأوراق السابقة بنسختها.",
    'el.question.answers': "الإجابات المقبولة",
    'el.question.answersHint': "إجابة في كل سطر",
    'el.question.archive': "أرشفة",
    'el.question.blanks': "الفراغات",
    'el.question.body': "نص السؤال",
    'el.question.bodyHint': "نص منسق، وتُكتب صيغة LaTeX بين $…$ داخل السطر أو بين $$…$$ في كتلة مستقلة (المعاينة على اليمين).",
    'el.question.caseSensitive': "مراعاة الأحرف الكبيرة",
    'el.question.chapter': "الفصل",
    'el.question.choice': "الاختيار {n}",
    'el.question.choices': "الاختيارات والإجابات الصحيحة",
    'el.question.clozeHint': "ضع كل فراغ بالشكل [[b1]] و[[b2]]… في النص، وزر «إضافة فراغ» يقوم بذلك.",
    'el.question.correct': "إجابة صحيحة",
    'el.question.criteria': "شبكة المعايير",
    'el.question.criteriaHint': "معيار في كل سطر: المعيار | المستوى: النقاط | المستوى: النقاط",
    'el.question.difficulty': "الصعوبة",
    'el.question.dontKnow': "اقتراح «لا أعرف»",
    'el.question.edit': "سؤال · النسخة {version}",
    'el.question.feedback': "تعليق التصحيح",
    'el.question.item': "العنصر {n}",
    'el.question.items': "العناصر بالترتيب الصحيح",
    'el.question.kinds': "الملفات المقبولة",
    'el.question.left': "يسار",
    'el.question.loadFailed': "السؤال غير موجود.",
    'el.question.maxWords': "الحد الأقصى للكلمات",
    'el.question.new': "سؤال جديد",
    'el.question.outcome': "المكتسب المستهدف",
    'el.question.pairs': "الأزواج للربط",
    'el.question.panel': "السؤال",
    'el.question.penalty': "خصم الإجابة الخاطئة",
    'el.question.penaltyHint': "جزء النقاط المخصوم (0 إلى 1)",
    'el.question.points': "النقاط",
    'el.question.positionMode': "نقاط لكل موضع صحيح",
    'el.question.positionModeNote': "وإلا فالكل أو لا شيء",
    'el.question.preview': "معاينة الطالب",
    'el.question.rel': "نسبية (%)",
    'el.question.right': "يمين",
    'el.question.scoring': "سلم التنقيط",
    'el.question.shuffle': "خلط ترتيب الاختيارات",
    'el.question.shuffleNote': "في كل محاولة",
    'el.question.stats': "بعد الاستعمال",
    'el.question.sub': "بنك {course}",
    'el.question.tags': "الوسوم",
    'el.question.tagsHint': "مفصولة بفواصل",
    'el.question.tolerance': "الهامش",
    'el.question.toleranceType': "نوع الهامش",
    'el.question.type': "النوع",
    'el.question.unarchive': "إعادة التفعيل",
    'el.question.unit': "الوحدة",
    'el.question.uses': "الإجابات",
    'el.question.value': "القيمة المنتظرة",
    'el.quiz.addQuestion': "إضافة",
    'el.quiz.addRule': "إضافة معيار",
    'el.quiz.allowed': "المحاولات المسموحة",
    'el.quiz.anonymous': "تصحيح مجهول",
    'el.quiz.anonymousNote': "تظهر الأسماء عند الاعتماد",
    'el.quiz.assessment': "النقطة تحول إلى",
    'el.quiz.attach': "الإلحاق",
    'el.quiz.attempts': "المحاولات والتغذية الراجعة",
    'el.quiz.available': "المتوفرة",
    'el.quiz.backToDraft': "العودة إلى المسودة",
    'el.quiz.checks': "قبل الفتح",
    'el.quiz.circuit': "المسار",
    'el.quiz.close': "إغلاق الآن",
    'el.quiz.closedDone': "أُغلق التقييم وأُرسلت المحاولات الجارية.",
    'el.quiz.composition': "التركيبة",
    'el.quiz.confirmDelete': "حذف هذه المسودة؟",
    'el.quiz.copies': "الأوراق",
    'el.quiz.delay': "المهلة بين محاولتين (دقيقة)",
    'el.quiz.delete': "حذف المسودة",
    'el.quiz.draw': "سحب",
    'el.quiz.duration': "المدة (دقيقة)",
    'el.quiz.durationHint': "فارغ: بدون حد، والإرسال تلقائي عند انتهاء الوقت",
    'el.quiz.extend': "تمديد الفترة",
    'el.quiz.extendDo': "تمديد",
    'el.quiz.extendReason': "السبب (مسجَّل)",
    'el.quiz.extendTo': "الإغلاق الجديد",
    'el.quiz.extended': "تم تمديد الفترة.",
    'el.quiz.extensionLine': "مُددت إلى {to} من طرف {by}: {reason}",
    'el.quiz.feedback': "التغذية الراجعة للطالب",
    'el.quiz.feedbackAt': "ابتداء من",
    'el.quiz.integrity': "النزاهة",
    'el.quiz.integrityNote': "السحب وترتيب الاختيارات ثابتان لكل طالب. سجل المحاولة: البداية والنهاية وعنوان IP والجهاز وفقدان التركيز وإعادة الاتصال. هذه مؤشرات وليست منعًا. لا قيود على الولوج حاليًا: لا كلمة سر ولا نطاق IP ولا وضع مقفل.",
    'el.quiz.keep': "النقطة المعتمدة",
    'el.quiz.loadFailed': "التقييم غير موجود.",
    'el.quiz.locked': "توجد أوراق: لم يعد بالإمكان تغيير التركيبة، ويمكن فقط تمديد الفترة.",
    'el.quiz.maxPoints': "المجموع الحالي: {points} نقطة",
    'el.quiz.missingQuestion': "سؤال محذوف من البنك",
    'el.quiz.navFree': "حرة",
    'el.quiz.navSequential': "متتالية (بدون رجوع)",
    'el.quiz.navigation': "التنقل",
    'el.quiz.new': "تقييم جديد على الخط",
    'el.quiz.noAssessment': "لا شيء (بدون تحويل)",
    'el.quiz.noItem': "لم يتم اختيار أي سؤال.",
    'el.quiz.note': "يمر الامتحان المحتسب في المعدل باعتماد الموضوع من طرف المنسق قبل الفتح. لا يُحوَّل التمرين أبدًا.",
    'el.quiz.open': "جعله متاحًا",
    'el.quiz.opened': "سيفتح التقييم في التاريخ المحدد.",
    'el.quiz.outOf': "على {outOf}",
    'el.quiz.pending': "أُرسل الموضوع إلى المنسق في {at}.",
    'el.quiz.pick': "اختر سؤالًا من البنك…",
    'el.quiz.pointsEach': "النقاط لكل سؤال",
    'el.quiz.rejected': "الموضوع للمراجعة: {reason}",
    'el.quiz.saveFirst': "احفظ التقييم لرؤية التحققات وفتحه.",
    'el.quiz.saved': "تم حفظ التقييم.",
    'el.quiz.scale': "السلم (على)",
    'el.quiz.sentToValidation': "أُرسل الموضوع إلى المنسق.",
    'el.quiz.shuffle': "خلط الأسئلة والاختيارات",
    'el.quiz.shuffleNote': "ترتيب ثابت لكل طالب",
    'el.quiz.submitSubject': "إرسال الموضوع للاعتماد",
    'el.quiz.title': "العنوان",
    'el.quiz.total': "{points} نقطة، محولة على {outOf}",
    'el.quiz.usage': "الاستعمال",
    'el.quiz.validated': "معتمدة",
    'el.quiz.validatedBy': "اعتمد الموضوع {by} في {at}",
    'el.quiz.validatedDone': "تم اعتماد الموضوع.",
    'el.quiz.window': "الفترة والمدة",
    'el.quiz.fixed': "أسئلة ثابتة",
    'el.quiz.random': "سحب عشوائي",
    'el.quizzes.copies': "الأوراق",
    'el.quizzes.copiesValue': "{count} ورقة",
    'el.quizzes.duration': "{minutes} دقيقة",
    'el.quizzes.new': "تقييم جديد",
    'el.quizzes.noValidationNote': "لا تمر المواضيع عبر المنسق (إعداد المؤسسة).",
    'el.quizzes.none': "لا يوجد تقييم على الخط.",
    'el.quizzes.questions': "الأسئلة",
    'el.quizzes.quiz': "التقييم",
    'el.quizzes.state': "الحالة",
    'el.quizzes.sub': "تمارين ومراقبة مستمرة وامتحانات",
    'el.quizzes.title': "التقييمات على الخط",
    'el.quizzes.to': "إلى «{assessment}»",
    'el.quizzes.toGrade': "{count} للتصحيح",
    'el.quizzes.usage': "الاستعمال",
    'el.quizzes.validationNote': "يمر الامتحان المرتبط بالنظام باعتماد المنسق قبل الفتح.",
    'el.quizzes.window': "الفترة",
    'el.regrade.apply': "إعادة حساب الأوراق",
    'el.regrade.changed': "الأوراق المعدلة",
    'el.regrade.changedValue': "{count} من {total}",
    'el.regrade.copies': "أُجيب عنه في {count} ورقة",
    'el.regrade.done': "تمت إعادة حساب {count} ورقة.",
    'el.regrade.impact': "الأثر قبل التأكيد",
    'el.regrade.keyNote': "لتصحيح الإجابة الصحيحة لسؤال مغلق، عدّله في البنك ثم أعد تصحيحه هنا.",
    'el.regrade.mean': "المعدل",
    'el.regrade.mode': "إعادة التصحيح",
    'el.regrade.newPoints': "النقاط الجديدة للسؤال",
    'el.regrade.none': "لا شيء",
    'el.regrade.preview': "عرض الأثر",
    'el.regrade.published': "النقط المنشورة سابقًا",
    'el.regrade.publishedValue': "{count}، سيتم إشعار الطلبة",
    'el.regrade.reason': "السبب",
    'el.regrade.title': "إعادة تصحيح سؤال",
    'el.regrade.total': "مجموع التقييم",
    'el.regrade.give': "إلغاء: منح النقاط للجميع",
    'el.regrade.giveNote': "تحصل كل ورقة على نقاط السؤال",
    'el.regrade.remove': "إلغاء: حذف من المجموع",
    'el.regrade.removeNote': "ينقص المجموع بنفس القدر",
    'el.regrade.points': "تغيير السلم",
    'el.regrade.pointsNote': "نقاط جديدة للسؤال",
    'el.regrade.restore': "إلغاء إعادة التصحيح",
    'el.regrade.restoreNote': "العودة إلى التصحيح الأصلي",
    'el.resources.add': "إضافة مورد",
    'el.resources.addHere': "إضافة",
    'el.resources.addSection': "إضافة القسم",
    'el.resources.broken': "رابط غير متاح",
    'el.resources.confirmDelete': "حذف هذا المورد ونسخه؟",
    'el.resources.count': "الموارد",
    'el.resources.countValue': "{count} في {sections} قسم",
    'el.resources.diffusion': "النشر",
    'el.resources.diffusionNote': "يُختار لكل عنصر: ظاهر أو مخفي، تواريخ الفتح والإغلاق، المجموعات، الشرط (بعد الاطلاع على مورد أو النجاح في اختبار)، التنزيل أو الاطلاع فقط، العلامة المائية بالاسم.",
    'el.resources.digest': "الملخص اليومي",
    'el.resources.digestValue': "يُشعر الطلبة مرة في اليوم",
    'el.resources.down': "إنزال",
    'el.resources.empty': "لا توجد حصص أو أقسام بعد: أضف قسمًا.",
    'el.resources.layout': "طريقة العرض",
    'el.resources.max': "الحجم الأقصى",
    'el.resources.maxValue': "{mb} م.ب لكل ملف",
    'el.resources.newSection': "قسم جديد (فصل)",
    'el.resources.noFile': "لم يُودَع أي ملف",
    'el.resources.none': "لا توجد موارد.",
    'el.resources.note': "يحتفظ الملف المستبدل بنسخه، ويرى الطلبة «حُدّث في …». المورد المشترك يُحال إليه ولا يُنسخ: تحديثه يفيد كل المواد.",
    'el.resources.opened': "{count} / {total} مفتوح",
    'el.resources.opensOn': "ظاهر في {at}",
    'el.resources.quota': "حصة المادة",
    'el.resources.quotaValue': "{used} من {quota}",
    'el.resources.readOnlyFile': "اطلاع فقط",
    'el.resources.renew': "التمديد من السنة الماضية",
    'el.resources.session': "حصة",
    'el.resources.sharedFrom': "مشترك من {course}",
    'el.resources.someGroups': "بعض المجموعات",
    'el.resources.space': "الفضاء",
    'el.resources.tooBig': "الملف كبير جدًا ({mb} م.ب كحد أقصى).",
    'el.resources.up': "رفع",
    'el.resources.updated': "حُدّث في {at}",
    'el.resources.views': "الاطلاعات",
    'el.resources.watermark': "علامة مائية بالاسم",
    'el.results.allGraded': "كل الأوراق منقطة",
    'el.results.below': "تحت المعدل",
    'el.results.belowNote': "من {count}",
    'el.results.byQuestion': "النجاح حسب السؤال",
    'el.results.count': "{count} طالب منقط",
    'el.results.distribution': "توزيع النقط",
    'el.results.distributionSub': "محولة على 20",
    'el.results.mean': "المعدل",
    'el.results.median': "الوسيط",
    'el.results.noOutcome': "لا يوجد سؤال مرتبط بمكتسب.",
    'el.results.outOf': "على {outOf}",
    'el.results.outcomes': "النجاح حسب مكتسبات التعلم",
    'el.results.outcomesSub': "سيُربط لاحقًا بتتبع الكفايات",
    'el.results.toGrade': "للتصحيح",
    'el.results.toGradeNote': "أوراق في الانتظار",
    'el.rich.bold': "عريض",
    'el.rich.bullets': "قائمة نقطية",
    'el.rich.italic': "مائل",
    'el.rich.link': "رابط",
    'el.rich.linkPrompt': "عنوان الرابط",
    'el.rich.numbers': "قائمة مرقمة",
    'el.rich.toolbar': "التنسيق",
    'el.settings.absence': "الغياب المحول",
    'el.settings.absenceHint': "نوع الغياب المسجل للطالب الذي لم يسلم أي ورقة، وبدون نوع لا يُحوّل شيء.",
    'el.settings.addRubric': "إضافة قسم",
    'el.settings.addTemplate': "إضافة نموذج",
    'el.settings.assessments': "التقييمات والنقط",
    'el.settings.control': "المراقبة قبل التحويل",
    'el.settings.controlNote': "يراقب المنسق كل دفعة",
    'el.settings.courseQuota': "الحصة لكل مادة (م.ب)",
    'el.settings.courses': "المواد والملفات",
    'el.settings.feedback': "التغذية الراجعة الافتراضية",
    'el.settings.fileKinds': "أنواع الملفات المقبولة",
    'el.settings.institutionQuota': "حصة المؤسسة (ج.ب)",
    'el.settings.maxFile': "الحجم الأقصى (م.ب)",
    'el.settings.newTemplate': "نموذج جديد",
    'el.settings.noAbsence': "لا شيء (عدم التحويل)",
    'el.settings.note': "يُفعَّل الموديول من صفحة الوحدات. غير مدرج حاليًا: القسم الافتراضي والمنتدى والفيديوهات المستضافة وقيود الولوج إلى الامتحانات والمراقبة بالكاميرا، وسيأتي استيراد Moodle والتصحيح المساعد لاحقًا.",
    'el.settings.off': "الموديول متوقف: فعّله من صفحة الوحدات.",
    'el.settings.parents': "الأولياء بعد المداولة",
    'el.settings.parentsNote': "النقط على الخط تظهر للأولياء بعد المداولة فقط (إعداد محفوظ لبوابة الأولياء)",
    'el.settings.perGroup': "خطة لكل مجموعة",
    'el.settings.perGroupNote': "وإلا خطة مشتركة لكل المجموعات",
    'el.settings.removeTemplate': "حذف النموذج",
    'el.settings.required': "إلزامي",
    'el.settings.rounding': "التقريب: رقمان بعد الفاصلة، مثل محرك حساب المعدلات.",
    'el.settings.rubric': "القسم",
    'el.settings.saved': "تم حفظ الإعدادات.",
    'el.settings.sub': "إعدادات المؤسسة",
    'el.settings.templateName': "اسم النموذج",
    'el.settings.templates': "نماذج الخطط",
    'el.settings.templatesSub': "الأقسام وترتيبها والأقسام الإلزامية للنشر",
    'el.settings.title': "التعلم الإلكتروني",
    'el.settings.trackViews': "تتبع الاطلاعات",
    'el.settings.trackViewsNote': "من فتح أو نزّل كل مورد",
    'el.settings.trainings': "العروض المشمولة (الرموز)",
    'el.settings.trainingsHint': "فارغ: النموذج الافتراضي. مثلًا {list}",
    'el.settings.used': "{size} مستعملة",
    'el.settings.validation': "اعتماد المواضيع",
    'el.settings.validationNote': "يعتمد المنسق الامتحان المحتسب في المعدل",
    'el.student.changed': "حُدّث في {at} · {count} تغيير مُبرز",
    'el.student.download': "تنزيل",
    'el.student.lockedQuiz': "يُفتح بعد {min} / 20 في الاختبار",
    'el.student.lockedViewed': "يُفتح بعد الاطلاع على مورد آخر",
    'el.student.noPlan': "خطة هذه المادة لم تنشر بعد.",
    'el.student.noResource': "لا توجد موارد منشورة بعد.",
    'el.student.pdf': "تنزيل بصيغة PDF",
    'el.student.planLine': "خطة المادة · النسخة {version} بتاريخ {at}",
    'el.student.quizzes': "التقييمات",
    'el.student.schedule': "الحصص القادمة",
    'el.student.seen': "تم الاطلاع",
    'el.student.sessions': "الحصص",
    'el.student.until': "حتى {at}",
    'el.subjects.examine': "فحص",
    'el.subjects.line': "{training} · {teacher} · الفتح في {at}",
    'el.subjects.noReady': "لا يوجد امتحان معتمد.",
    'el.subjects.none': "لا يوجد موضوع للاعتماد.",
    'el.subjects.note': "اعتماد الموضوع يجعله متاحًا في تاريخه، وإرجاعه يعيده مسودة مع السبب إلى الأستاذ.",
    'el.subjects.pending': "للاعتماد",
    'el.subjects.ready': "الامتحانات المعتمدة",
    'el.subjects.reason': "سبب الإرجاع",
    'el.subjects.reject': "إرجاع إلى الأستاذ",
    'el.subjects.sub': "امتحانات تحتسب في المعدل، قبل الفتح",
    'el.subjects.title': "مواضيع للاعتماد",
    'el.subjects.validate': "اعتماد",
    'el.tab.plan': "خطة المادة",
    'el.tab.resources': "الموارد",
    'el.tab.quizzes': "التقييمات على الخط",
    'el.tab.grades': "الأوراق والنقط",
    'el.tab.tracking': "التتبع",
    'el.teach.duplicate': "نسخ خطة السنة الماضية",
    'el.teach.kpiCourses': "المواد",
    'el.teach.kpiCoursesNote': "ضمن برمجتي",
    'el.teach.kpiEmpty': "خطط للإعداد",
    'el.teach.kpiEmptyNote': "بدون خطة بعد",
    'el.teach.kpiPublished': "الخطط المنشورة",
    'el.teach.kpiPublishedNote': "{drafts} مسودة، {empty} للإعداد",
    'el.teach.kpiToGrade': "أوراق للتصحيح",
    'el.teach.kpiToGradeNote': "أسئلة مفتوحة في الانتظار",
    'el.teach.none': "لا توجد مواد في برمجتك هذه السنة.",
    'el.teach.note': "لا يُنشأ أي فضاء يدويًا: يولد كل فضاء من مادة مبرمجة (العرض والمجموعة والأستاذ المعين). وتحدد تسجيلات الشؤون الدراسية من يرى ماذا.",
    'el.teach.nothing': "لا شيء للقيام به هذا الأسبوع",
    'el.teach.open': "فتح الفضاء",
    'el.teach.plan': "خطة المادة",
    'el.teach.quizzes': "التقييمات",
    'el.teach.quizzesNone': "لا شيء",
    'el.teach.quizzesValue': "{total} · {open} مفتوح",
    'el.teach.resources': "الموارد",
    'el.teach.resourcesNew': "{count} · {fresh} جديد",
    'el.teach.students': "الطلبة",
    'el.teach.sub': "فضاء لكل مادة ضمن برمجتي، {year}",
    'el.teach.title': "موادي على الخط",
    'el.teach.todoGrade': "{count} ورقة للتصحيح",
    'el.teach.todoPlan': "خطة للنشر",
    'el.tracking.downloads': "التنزيلات",
    'el.tracking.off': "تتبع الاطلاعات متوقف في إعدادات المؤسسة.",
    'el.tracking.opened': "فُتح من طرف",
    'el.tracking.resource': "المورد",
    'el.tracking.section': "القسم",
    'el.tracking.sub': "{students} طالب مسجل",
    'el.tracking.title': "الاطلاع على الموارد",
    'el.transfer.absent': "الغائبون",
    'el.transfer.absentNote': "تُحوَّل بصفة «{absence}»",
    'el.transfer.circuit': "المسار",
    'el.transfer.control': "مراقبة الدفعة",
    'el.transfer.copies': "الأوراق",
    'el.transfer.differences': "الفروق",
    'el.transfer.differencesNote': "مع موديول النقط",
    'el.transfer.done': "تم تحويل الدفعة: {marks} نقطة، {absents} غائب.",
    'el.transfer.lot': "الدفعة",
    'el.transfer.lots': "الدفعات المنشورة من طرف الأساتذة",
    'el.transfer.mark': "في موديول النقط",
    'el.transfer.noAbsence': "لا يوجد نوع غياب محدد: لا تُحوَّل",
    'el.transfer.none': "لا توجد دفعات نقط بعد.",
    'el.transfer.note': "التحويل فعل صريح لكل دفعة. يُحوَّل الغياب أو عدم التسليم كما هو ويخضع لقواعد الغياب في النظام: لا يحوله التعلم الإلكتروني أبدًا إلى صفر ولا يحسب معدلًا ولا تقديرًا ولا تعويضًا. بعد التحويل يمر أي تعديل عبر مسار موديول النقط.",
    'el.transfer.online': "النقطة على الخط",
    'el.transfer.pending': "في الانتظار",
    'el.transfer.pendingNote': "أوراق للتصحيح أو الاعتماد أو النشر",
    'el.transfer.ready': "جاهزة",
    'el.transfer.readyNote': "نقط خام على {outOf}",
    'el.transfer.reason': "السبب",
    'el.transfer.return': "إرجاع إلى الأستاذ",
    'el.transfer.returnDo': "إرجاع الدفعة",
    'el.transfer.rowAbsent': "غائب",
    'el.transfer.rowNone': "لا شيء للتحويل",
    'el.transfer.rowPending': "في الانتظار",
    'el.transfer.rowReady': "جاهزة",
    'el.transfer.rowState': "الحالة",
    'el.transfer.run': "تحويل الدفعة",
    'el.transfer.saved': "تم تحديث الدفعة.",
    'el.transfer.state': "الحالة",
    'el.transfer.stepControlled': "مراقبة المنسق",
    'el.transfer.stepPublished': "نشر النقط للطلبة",
    'el.transfer.stepTransferred': "التحويل إلى موديول النقط",
    'el.transfer.sub': "إلى «{assessment}» · {training} · {count} طالب",
    'el.transfer.title': "تحويلات النقط",
    'el.transfer.todo': "للقيام به",
    'el.transfer.validated': "التقييم معتمد في موديول النقط: أعد فتح الإدخال هناك قبل التحويل.",
    'el.views.all': "الكل",
    'el.views.downloaded': "تم التنزيل",
    'el.views.downloads': "التنزيلات",
    'el.views.downloadsNote': "في المجموع",
    'el.views.filter': "تصفية الطلبة",
    'el.views.group': "المجموعة",
    'el.views.last': "آخر فتح",
    'el.views.never': "لم يُفتح أبدًا",
    'el.views.neverBadge': "أبدًا",
    'el.views.neverNote': "طلبة مسجلون",
    'el.views.neverTab': "لم يُفتح",
    'el.views.note': "لا يُتتبع الاطلاع إلا إذا اختارت المؤسسة ذلك، ويُخبر الطالب بذلك في «موادي».",
    'el.views.opened': "مفتوح",
    'el.views.openedAt': "فُتح في",
    'el.views.openedTab': "مفتوح",
    'el.views.rate': "{rate} % من المسجلين",
    'el.views.search': "البحث عن طالب",
    'el.views.student': "الطالب",
    'el.views.sub': "{course} · {section} · الاطلاعات",
    'el.views.times': "المرات",
    'el.views.title': "الاطلاعات",
    'el.visibility.closed': "مغلق",
    'el.visibility.conditional': "مشروط",
    'el.visibility.hidden': "مخفي",
    'el.visibility.scheduled': "ظاهر في {at}",
    'el.visibility.visible': "ظاهر",
    'el.quizState.draft': "مسودة",
    'el.quizState.pending': "موضوع للاعتماد",
    'el.quizState.ready': "متاح",
    'el.quizState.closed': "مغلق",
    'el.attemptState.in_progress': "جارية",
    'el.attemptState.submitted': "مُرسلة",
    'el.attemptState.graded': "مصححة",
    'el.attemptState.validated': "معتمدة",
    'el.attemptState.published': "منشورة",
    'el.attemptState.transferred': "محولة",
    'el.transferState.none': "لا شيء",
    'el.transferState.published': "للمراقبة",
    'el.transferState.controlled': "مراقبة",
    'el.transferState.transferred': "محول",
    'el.transferState.returned': "مُرجع",
    'el.usage.practice': "تمرين",
    'el.usage.continuous': "مراقبة مستمرة",
    'el.usage.exam': "امتحان",
    'el.kind.file': "ملف",
    'el.kind.video': "فيديو مرتبط",
    'el.kind.link': "رابط",
    'el.kind.page': "صفحة",
    'el.kind.shared': "مشترك",
    'el.layout.session': "حسب الحصة",
    'el.layout.chapter': "حسب الفصل",
    'el.layout.free': "حر",
    'el.sectionKind.session': "حصة",
    'el.sectionKind.chapter': "فصل",
    'el.sectionKind.free': "قسم",
    'el.fileKind.pdf': "PDF",
    'el.fileKind.office': "أوفيس",
    'el.fileKind.image': "صور",
    'el.fileKind.archive': "أرشيفات",
    'el.difficulty.easy': "سهل",
    'el.difficulty.medium': "متوسط",
    'el.difficulty.hard': "صعب",
    'el.qtype.single': "اختيار من متعدد بإجابة واحدة",
    'el.qtype.multiple': "اختيار من متعدد بإجابات متعددة",
    'el.qtype.truefalse': "صحيح / خطأ",
    'el.qtype.matching': "ربط",
    'el.qtype.ordering': "ترتيب",
    'el.qtype.short': "إجابة قصيرة",
    'el.qtype.numeric': "رقمي",
    'el.qtype.cloze': "ملء الفراغات",
    'el.qtype.open': "سؤال مفتوح",
    'el.qtype.file': "إيداع ملف",
    'el.scoring.proportional': "نسبي، الحد الأدنى 0",
    'el.scoring.proportional_no_penalty': "نسبي، بدون خصم",
    'el.scoring.all_or_nothing': "الكل أو لا شيء",
    'el.keep.best': "الأفضل",
    'el.keep.last': "الأخيرة",
    'el.keep.mean': "المعدل",
    'el.feedback.none': "لا شيء",
    'el.feedback.score': "النقطة فقط",
    'el.feedback.full': "النقطة والتصحيح بعد النشر",
    'el.check.questions': "الأسئلة",
    'el.check.window': "الفترة",
    'el.check.duration': "المدة",
    'el.check.assessment': "النقطة المحولة",
    'el.check.accommodations': "الترتيبات",
    'el.event.start': "البداية",
    'el.event.focus_lost': "فقدان تركيز النافذة",
    'el.event.reconnect': "إعادة الاتصال",
    'el.event.resume': "استئناف",
    'el.event.submit': "الإرسال",
    'el.event.auto_submit': "إرسال تلقائي",
    'el.event.reopen': "إعادة الفتح",
    'rel.modules.learning': "التعلم الإلكتروني",
    'rel.modules.learningHint': "خطط المواد والموارد وبنك الأسئلة والتقييمات على الخط والتصحيح وتحويل النقط والترتيبات",
    'journal.action.learning.settings_changed': "تعديل إعدادات التعلم الإلكتروني",
    'journal.action.learning.syllabus_changed': "تعديل خطة المادة",
    'journal.action.learning.resource_saved': "حفظ مورد المادة",
    'journal.action.learning.question_saved': "حفظ سؤال البنك",
    'journal.action.learning.quiz_saved': "حفظ تقييم على الخط",
    'journal.action.learning.quiz_validated': "اعتماد موضوع امتحان أو إرجاعه",
    'journal.action.learning.grades_changed': "تعديل التصحيح على الخط",
    'journal.action.learning.grades_transferred': "مراقبة دفعة نقط أو تحويلها",
    'journal.action.learning.accommodation_saved': "منح ترتيب أو تعديله",
    'journal.action.learning.attempt_reopened': "إعادة فتح محاولة",
    'violation.el.assessment_validated': "التقييم معتمد في موديول النقط: يجب إعادة فتح الإدخال هناك.",
    'violation.el.attempt_closed': "الورقة مُرسلة سابقًا.",
    'violation.el.attempt_delay': "انتظر المهلة قبل المحاولة التالية.",
    'violation.el.attempt_state': "لا يمكن تغيير هذه الورقة في حالتها الحالية.",
    'violation.el.bad_sheet': "ملف جدول غير مقروء.",
    'violation.el.bad_url': "عنوان ويب مطلوب (https://...).",
    'violation.el.bad_video': "مطلوب فيديو من يوتيوب أو فيميو أو مايكروسوفت ستريم.",
    'violation.el.cloze_missing': "فراغ مفقود في نص السؤال.",
    'violation.el.file_empty': "ملف فارغ.",
    'violation.el.file_format': "نوع الملف غير مقبول.",
    'violation.el.file_too_big': "الملف كبير جدًا.",
    'violation.el.lot_pending': "ما تزال بعض الأوراق للتصحيح أو الاعتماد أو النشر.",
    'violation.el.no_assessment': "لا يوجد تقييم في النظام يستقبل النقطة.",
    'violation.el.no_attempt_left': "لا توجد محاولات متبقية.",
    'violation.el.no_previous': "لا شيء في السنة الماضية لهذه المادة.",
    'violation.el.not_a_file': "هذا المورد ليس ملفًا.",
    'violation.el.not_shareable': "اختر ملفًا من المكتبة المشتركة.",
    'violation.el.nothing_to_publish': "لا توجد ورقة معتمدة للنشر.",
    'violation.el.nothing_to_validate': "لا توجد ورقة مصححة للاعتماد.",
    'violation.el.question_answers': "إجابة مقبولة واحدة على الأقل.",
    'violation.el.question_choices': "اختياران على الأقل.",
    'violation.el.question_correct': "حدد الإجابة أو الإجابات الصحيحة (واحدة فقط للاختيار الوحيد).",
    'violation.el.quiz_checks': "لم ينجح أحد التحققات قبل الفتح: راجع القائمة.",
    'violation.el.quiz_locked': "توجد أوراق: لم يعد التقييم قابلًا للتغيير.",
    'violation.el.quiz_not_open': "التقييم غير مفتوح.",
    'violation.el.quiz_pending': "الموضوع لدى المنسق.",
    'violation.el.quiz_state': "الإجراء غير ممكن في الحالة الحالية للتقييم.",
    'violation.el.quota_course': "تم بلوغ حصة المادة.",
    'violation.el.quota_institution': "تم بلوغ حصة المؤسسة.",
    'violation.el.section_not_empty': "القسم يحتوي على موارد.",
    'violation.el.sequential': "تنقل متتال: لا رجوع إلى الوراء.",
    'violation.el.syllabus_incomplete': "قسم إلزامي للنشر.",
    'violation.el.syllabus_unchanged': "لا جديد منذ آخر نسخة.",
    'violation.el.transfer_state': "لا توجد دفعة في هذه الحالة.",
    'violation.el.transferred': "النقط محولة: يمر التصحيح عبر موديول النقط.",
    'violation.el.window_order': "الإغلاق يأتي بعد الفتح.",
    'el.transfer.open': "التقييم ما يزال مفتوحًا: الطلبة بدون ورقة ليسوا غائبين بعد. ينتظر التحويل إغلاقه.",
    'violation.el.quiz_still_open': "التقييم ما يزال مفتوحًا: أغلقه قبل التحويل.",
    'fin.account.title': "الحساب",
    'fin.account.billed': "المفوتر",
    'fin.account.discounts': "التخفيضات",
    'fin.account.paid': "المؤدى",
    'fin.account.due': "الباقي",
    'fin.account.lateNote': "منها {amount} متأخرة",
    'fin.account.credit': "مبلغ زائد في الحساب:",
    'fin.account.uninvoiced': "تسجيل بلا فاتورة: {label}.",
    'fin.account.issue': "إصدار الفاتورة",
    'fin.account.issued': "صدرت الفاتورة.",
    'fin.account.empty': "لا توجد فاتورة.",
    'fin.account.onDemand': "فوترة خدمة عند الطلب",
    'fin.account.onDemandSub': "فاتورة منفصلة تؤدى دفعة واحدة.",
    'fin.account.onDemandNoYear': "لا سنة مرجعية: أصدر فاتورة التسجيل أولاً.",
    'fin.account.onDemandDone': "تمت فوترة الخدمة.",
    'fin.account.noOnDemand': "لا خدمات عند الطلب.",
    'fin.account.addService': "إضافة خدمة",
    'fin.account.noService': "لا خدمة للإضافة.",
    'fin.account.serviceAdded': "أضيفت الخدمة إلى الفاتورة.",
    'fin.account.grantDiscount': "منح تخفيض",
    'fin.account.discountGranted': "تم منح التخفيض.",
    'fin.account.discountJournal': "يُسجَّل التخفيض وتعليله ومانحه ويظهر في الفاتورة.",
    'fin.account.reason': "التعليل",
    'fin.account.reasonRequired': "التعليل",
    'fin.account.cancelInvoice': "إلغاء الفاتورة",
    'fin.account.cancelled': "أُلغيت في {date}: {reason}",
    'fin.account.designation': "البيان",
    'fin.account.taxIncluded': "منها ضريبة {amount}",
    'fin.account.removeLine': "حذف السطر",
    'fin.account.lineRemoved': "حُذف «{label}» من الفاتورة.",
    'fin.account.net': "الصافي",
    'fin.account.dueOn': "الاستحقاق",
    'fin.account.rest': "الباقي {amount}",
    'fin.account.payments': "الأداءات",
    'fin.account.allocatedTo': "مخصص لـ",
    'fin.account.part': "الدفعة {position}",
    'fin.account.reverses': "يلغي {number}",
    'fin.account.reversedBy': "ألغي بـ {number}",
    'fin.collect.open': "تحصيل",
    'fin.collect.title': "تحصيل أداء",
    'fin.collect.sub': "الفاتورة {number} · الباقي {due}",
    'fin.collect.amount': "المبلغ المتوصل به",
    'fin.collect.date': "تاريخ التحصيل",
    'fin.collect.chequeNo': "رقم الشيك",
    'fin.collect.transferRef': "مرجع التحويل",
    'fin.collect.bank': "البنك",
    'fin.collect.drawer': "الساحب",
    'fin.collect.drawerHint': "إن كان غير الطالب.",
    'fin.collect.comment': "ملاحظة",
    'fin.collect.commentHint': "اختياري. يُطبع في الوصل.",
    'fin.collect.allocation': "التخصيص التلقائي",
    'fin.collect.left': "كان باقياً",
    'fin.collect.this': "هذا الأداء",
    'fin.collect.after': "بعد",
    'fin.collect.surplus': "فائض {amount}: يبقى رصيداً دائناً في الحساب.",
    'fin.collect.dueAfter': "الباقي بعد هذا الأداء:",
    'fin.collect.immutable': "لا يُعدَّل الأداء المسجل. يُصحَّح الخطأ بإلغاء ينشئ قيداً عكسياً مسجلاً ووصله.",
    'fin.collect.print': "طباعة الوصل",
    'fin.collect.go': "تحصيل",
    'fin.collect.goPrint': "تحصيل وطباعة الوصل",
    'fin.collect.done': "تم التحصيل.",
    'fin.cancel.title': "إلغاء الفاتورة {number}",
    'fin.cancel.hint': "تحتفظ الفاتورة برقمها وتبقى قابلة للاطلاع. يجب ألا تحمل أي أداء.",
    'fin.cancel.go': "إلغاء الفاتورة",
    'fin.cancel.done': "أُلغيت الفاتورة.",
    'fin.reverse.open': "إلغاء هذا الأداء",
    'fin.reverse.title': "إلغاء الأداء {number}",
    'fin.reverse.hint': "يُسجَّل قيد عكسي بالمبلغ نفسه مع وصله. ويبقى الأداء الأصلي ظاهراً.",
    'fin.reverse.go': "إلغاء الأداء",
    'fin.reverse.done': "أُلغي الأداء.",
    'journal.action.finance.configured': "المالية: إعداد الوحدة",
    'journal.action.finance.created': "المالية: إضافة",
    'journal.action.finance.updated': "المالية: تعديل",
    'journal.action.finance.deleted': "المالية: حذف",
    'journal.action.finance.invoice_issued': "إصدار فاتورة",
    'journal.action.finance.invoice_changed': "تعديل فاتورة",
    'journal.action.finance.invoice_cancelled': "إلغاء فاتورة",
    'journal.action.finance.discount_granted': "منح تخفيض",
    'journal.action.finance.payment_recorded': "تحصيل أداء",
    'journal.action.finance.payment_cancelled': "إلغاء أداء",
    'journal.filter.group.finance': "المالية",
    'violation.fin.no_pricing_level': "اختر مستوى تسعير واحداً على الأقل.",
    'violation.fin.invalid_currency': "رمز عملة من ثلاثة أحرف (MAD، XOF، EUR).",
    'violation.fin.currency_locked': "لا تتغير العملة بعد إصدار أول فاتورة.",
    'violation.fin.invalid_pattern': "يجب أن تتضمن الصيغة رتبة {N:x} ورموزاً معروفة فقط.",
    'violation.fin.tax_billed': "سبقت فوترة هذه الضريبة: أنشئ واحدة جديدة.",
    'violation.fin.in_use': "مستعمل: عطّله بدل حذفه.",
    'violation.fin.invalid_schedule': "من 1 إلى 24 دفعة.",
    'violation.fin.shares_incomplete': "يجب أن يكون مجموع الحصص 100 %.",
    'violation.fin.invalid_amount': "مبلغ متوقع.",
    'violation.fin.level_not_priced': "هذا المستوى غير مسعّر من طرف المؤسسة.",
    'violation.fin.schedule_on_training': "يُضبط الجدول على مستوى التكوين.",
    'violation.fin.already_invoiced': "لهذا التسجيل فاتورته.",
    'violation.fin.nothing_to_bill': "لا شيء للفوترة: التكوين بلا سعر.",
    'violation.fin.inactive': "معطّل.",
    'violation.fin.on_demand_service': "تُفوتَر الخدمة عند الطلب على حدة.",
    'violation.fin.already_on_invoice': "موجود في الفاتورة.",
    'violation.fin.training_line': "لا يُحذف سعر التكوين: ألغ الفاتورة.",
    'violation.fin.last_line': "تحتفظ الفاتورة بسطر واحد على الأقل: ألغها.",
    'violation.fin.not_stackable': "هذا التخفيض لا يُجمع مع غيره.",
    'violation.fin.invoice_has_payments': "توجد أداءات على هذه الفاتورة: أصدر رصيداً دائناً.",
    'violation.fin.invoice_cancelled': "هذه الفاتورة ملغاة.",
    'violation.fin.future_date': "لا يؤرَّخ الأداء في المستقبل.",
    'violation.fin.unlikely_date': "تاريخ غير معقول.",
    'violation.fin.is_reversal': "لا يُلغى الإلغاء: حصّل من جديد.",
    'violation.fin.already_reversed': "هذا الأداء ملغى.",
    'violation.fin.enrolment_has_payments': "تم التوصل بأداءات على فاتورة هذا التسجيل: صرّح بالانقطاع.",
    // D65, D66 : gestion des enseignants (fac.) et paie des enseignants (pay.).
    'fac.none': "لا شيء",
    'fac.choose': "اختر…",
    'fac.open': "فتح",
    'fac.total': "المجموع",
    'fac.export': "تصدير",
    'fac.period': "{from} ← {to}",
    'fac.unit.hours': "بالساعات، مثلاً 4 أو 2,5",
    'fac.unit.hoursPerMonth': "ساعات في الشهر",
    'fac.backToTeachers': "الأساتذة",
    'fac.remove.confirm': "حذف «{label}»؟ يُسجَّل هذا الإجراء.",
    'fac.saved.identity': "تم حفظ الهوية.",
    'fac.saved.fields': "تم حفظ المجالات التخصصية.",
    'fac.saved.diploma': "تم حفظ الشهادة.",
    'fac.saved.removed': "تم حذف «{label}».",
    'fac.civility.M': "السيد",
    'fac.civility.F': "السيدة",
    'fac.state.draft': "مسودة",
    'fac.state.signed': "موقَّع",
    'fac.state.closed': "مغلق",
    'fac.state.none': "بدون عقد",
    'fac.alert.unsigned': "عقد غير موقَّع",
    'fac.alert.no_objective': "لا أهداف مُدخلة",
    'fac.alert.no_session': "لا حصص منجزة",
    'fac.alert.no_contract': "بدون عقد هذه السنة",
    'fac.situation.all': "كل الوضعيات",
    'fac.situation.contract': "تحت عقد",
    'fac.situation.nocontract': "بدون عقد",
    'fac.situation.todo': "للتسوية",
    'fac.basis.weekly': "في الأسبوع",
    'fac.basis.monthly': "في الشهر",
    'fac.basis.yearly': "في السنة",
    'fac.basis.per.weekly': "في الأسبوع",
    'fac.basis.per.monthly': "في الشهر",
    'fac.basis.per.yearly': "في السنة",
    'fac.service.range': "من {min} إلى {max}",
    'fac.service.min': "على الأقل {value}",
    'fac.service.max': "على الأكثر {value}",
    'fac.payMode.hourly': "بالساعة المنجزة",
    'fac.payMode.monthly': "جزافي شهري وساعات إضافية",
    'fac.payMode.none': "غير مؤدّى عنه هنا",
    'fac.payMode.short.hourly': "بالساعة",
    'fac.payMode.short.monthly': "جزافي شهري",
    'fac.payMode.short.none': "غير مؤدّى عنه",
    'fac.teachers.title': "الأساتذة",
    'fac.teachers.sub': "عقد السنة، الهدف، المنجز، وما تبقى للتسوية",
    'fac.teachers.count': "عدد الأساتذة: {count} · سنة {year}",
    'fac.teachers.new': "أستاذ جديد",
    'fac.teachers.search': "الاسم، رقم التأجير، المادة",
    'fac.teachers.empty': "لا يوجد أستاذ يطابق هذه المعايير.",
    'fac.kpi.underContract': "تحت عقد في {year}",
    'fac.kpi.underContractNote': "دائمون: {permanent}، عرضيون: {vacataire}",
    'fac.kpi.objective': "ساعات الهدف",
    'fac.kpi.objectiveNote': "مجموع الأهداف التعاقدية",
    'fac.kpi.realized': "الساعات المنجزة",
    'fac.kpi.realizedNote': "{percent} % من الهدف",
    'fac.kpi.realizedNoteNone': "لا أهداف مُدخلة",
    'fac.kpi.toFix': "للتسوية",
    'fac.kpi.toFixNote': "عقود غير موقعة: {unsigned}، بدون هدف: {noObjective}",
    'fac.filter.year': "السنة",
    'fac.filter.category': "الفئة",
    'fac.filter.type': "نوع العقد",
    'fac.filter.situation': "الوضعية",
    'fac.filter.state': "الحالة",
    'fac.filter.allCategories': "كل الفئات",
    'fac.filter.allTypes': "كل الأنواع",
    'fac.filter.allStates': "كل الحالات",
    'fac.column.teacher': "الأستاذ",
    'fac.column.category': "الفئة",
    'fac.column.contract': "العقد",
    'fac.column.state': "الحالة",
    'fac.column.objective': "الهدف",
    'fac.column.realized': "المنجز",
    'fac.column.planned': "المبرمج",
    'fac.column.catalog': "المسار",
    'fac.column.progress': "التقدم",
    'fac.column.alerts': "التنبيهات",
    'fac.column.year': "السنة",
    'fac.column.type': "النوع",
    'fac.column.period': "الفترة",
    'fac.column.payMode': "الأجر",
    'fac.column.service': "الخدمة",
    'fac.column.subject': "المادة",
    'fac.column.subjectClass': "المادة والقسم",
    'fac.column.teachingType': "نوع التدريس",
    'fac.column.training': "التكوين",
    'fac.column.role': "الدور",
    'fac.create.title': "أستاذ جديد",
    'fac.create.sub': "الشخص ورقم تأجيره؛ يُستكمل القسم والشهادات والعقود لاحقاً من الملف",
    'fac.create.submit': "إنشاء الأستاذ",
    'fac.identity.edit': "تعديل الهوية",
    'fac.field.civility': "اللقب",
    'fac.field.name': "الاسم الكامل",
    'fac.field.lastName': "النسب",
    'fac.field.firstName': "الاسم",
    'fac.field.number': "رقم التأجير",
    'fac.field.integratedOn': "تاريخ الالتحاق",
    'fac.field.department': "الشعبة",
    'fac.field.email': "البريد الإلكتروني",
    'fac.field.phone': "الهاتف",
    'fac.tab.dossier': "الملف",
    'fac.tab.contracts': "العقود",
    'fac.tab.objectives': "الأهداف",
    'fac.tab.dashboard': "لوحة القيادة",
    'fac.tab.pay': "الأجر",
    'fac.tab.history': "السجل",
    'fac.hero.contract.draft': "عقد {year} مسودة",
    'fac.hero.contract.signed': "عقد {year} موقَّع",
    'fac.hero.contract.closed': "عقد {year} مغلق",
    'fac.hero.number': "رقم التأجير {number}",
    'fac.hero.integrated': "التحق في {date}",
    'fac.panel.identity': "الهوية",
    'fac.panel.diplomas': "الشهادات",
    'fac.panel.account': "حساب Kelassi",
    'fac.panel.fields': "المجالات التخصصية",
    'fac.panel.attributions': "الإسنادات",
    'fac.panel.attributionsYear': "إسنادات {year}",
    'fac.attributions.sub': "المواد التي هو مسؤول عنها أو متدخل فيها، في الكتالوج",
    'fac.attributions.empty': "لا إسناد في الكتالوج.",
    'fac.account.none': "لا يوجد حساب Kelassi.",
    'fac.account.active': "حساب مفعّل",
    'fac.account.inactive': "حساب معطّل",
    'fac.account.lastLogin': "آخر دخول {date}",
    'fac.account.neverLogged': "لم يدخل قط",
    'fac.account.open': "عرض الحساب",
    'fac.fields.edit': "المجالات التخصصية",
    'fac.fields.empty': "لا مجال تخصصي.",
    'fac.fields.none': "لا توجد مجالات تخصصية معرفة للمؤسسة.",
    'fac.diploma.add': "إضافة شهادة",
    'fac.diploma.edit': "تعديل الشهادة",
    'fac.diploma.remove': "حذف الشهادة",
    'fac.diploma.label': "الشهادة",
    'fac.diploma.school': "المؤسسة",
    'fac.diploma.city': "المدينة",
    'fac.diploma.year': "السنة",
    'fac.diploma.empty': "لا شهادات مُدخلة.",
    'fac.contracts.title': "العقود",
    'fac.contracts.sub': "عقد في السنة، أو أكثر إذا تغيرت الوضعية خلالها (دون تداخل)",
    'fac.contracts.empty': "لا عقود لهذا الأستاذ.",
    'fac.contract.new': "عقد جديد",
    'fac.contract.newTitle': "عقد جديد · {name}",
    'fac.contract.editTitle': "عقد {year}",
    'fac.contract.title': "عقد {year}",
    'fac.contract.dialogSub': "يحدد العقد الخدمة المنتظرة، والأجر إذا كانت الأجور مفعّلة",
    'fac.contract.type': "نوع العقد",
    'fac.contract.category': "الفئة",
    'fac.contract.year': "السنة",
    'fac.contract.startsOn': "البداية",
    'fac.contract.endsOn': "النهاية",
    'fac.contract.basis': "أساس الخدمة",
    'fac.contract.minHours': "الحد الأدنى للخدمة",
    'fac.contract.maxHours': "الحد الأقصى للخدمة",
    'fac.contract.online': "الدروس عن بعد مسموح بها",
    'fac.contract.payMode': "طريقة الأداء",
    'fac.contract.note': "ملاحظة",
    'fac.contract.saveDraft': "حفظ كمسودة",
    'fac.contract.saveSign': "حفظ ووضع علامة موقَّع",
    'fac.contract.saved': "تم حفظ العقد.",
    'fac.contract.savedSigned': "تم حفظ العقد وتوقيعه.",
    'fac.contract.locked': "شهر أداء من هذا العقد مصادق عليه: النسب وطريقة الأداء مقفلة.",
    'fac.contract.sign': "وضع علامة موقَّع",
    'fac.contract.signTitle': "توقيع عقد {year}",
    'fac.contract.signSub': "العقد الموقَّع يُحتسب في الأجر؛ لا يمكن أن يتداخل عقدان موقعان لنفس الأستاذ",
    'fac.contract.signDate': "وُقِّع في",
    'fac.contract.signed': "تم توقيع العقد.",
    'fac.contract.signedOn': "وُقِّع في {date}",
    'fac.contract.objectives': "الأهداف التعاقدية: {hours}",
    'fac.contract.close': "إغلاق العقد",
    'fac.contract.closed': "تم إغلاق العقد.",
    'fac.contract.reopen': "إعادة فتح العقد",
    'fac.contract.reopened': "تمت إعادة فتح العقد.",
    'fac.contract.deleted': "تم حذف العقد.",
    'fac.contract.renew': "تجديد للسنة الموالية",
    'fac.contract.renewed': "تم تجديد العقد كمسودة للسنة الموالية.",
    'fac.contract.renewedUnmapped': "تم تجديد العقد؛ أهداف بدون مادة مقابلة في السنة الموالية لم تُنقل: {count}",
    'fac.confirm.contract.close.title': "إغلاق العقد",
    'fac.confirm.contract.close.text': "إغلاق عقد {year}؟ يبقى قابلاً للاطلاع ويمكن إعادة فتحه.",
    'fac.confirm.contract.close.ok': "إغلاق",
    'fac.confirm.contract.reopen.title': "إعادة فتح العقد",
    'fac.confirm.contract.reopen.text': "إعادة فتح عقد {year}؟ يعود موقَّعاً.",
    'fac.confirm.contract.reopen.ok': "إعادة الفتح",
    'fac.confirm.contract.delete.title': "حذف العقد",
    'fac.confirm.contract.delete.text': "حذف عقد {year} وأهدافه؟ غير ممكن إذا كانت له ساعات منجزة أو أجر مصادق عليه.",
    'fac.confirm.contract.delete.ok': "حذف",
    'fac.confirm.contract.renew.title': "تجديد العقد",
    'fac.confirm.contract.renew.text': "نسخ عقد {year} إلى السنة الموالية، كمسودة، مع أهدافه؟",
    'fac.confirm.contract.renew.ok': "تجديد",
    'fac.pay.title': "الأجر",
    'fac.pay.monthlyGross': "الجزافي الشهري الخام",
    'fac.pay.monthlyNet': "الجزافي الشهري الصافي",
    'fac.pay.includedHours': "الساعات المشمولة في الشهر",
    'fac.pay.extraGross': "الساعة الإضافية الخام",
    'fac.pay.extraNet': "الساعة الإضافية الصافية",
    'fac.pay.extraCap': "سقف الساعات الإضافية",
    'fac.pay.perMonth': "{hours} في الشهر",
    'fac.pay.monthlyNote': "ما بعد {hours} المشمولة في الجزافي، تؤدى كل ساعة منجزة بالنسبة الإضافية في حدود السقف. الساعات التي تتجاوز السقف يُشار إليها ولا تؤدى.",
    'fac.pay.noneNote': "لا يؤدى أجر هذا الأستاذ عبر Kelassi.",
    'fac.rate.base': "النسبة العامة",
    'fac.rate.extraBase': "النسبة الإضافية",
    'fac.rate.gross': "النسبة الخام للساعة",
    'fac.rate.net': "النسبة الصافية للساعة",
    'fac.rate.same': "نسبة واحدة لكل أنواع التدريس",
    'fac.rate.sameNote': "نسبة واحدة لكل أنواع التدريس.",
    'fac.rate.teachingType': "نوع التدريس",
    'fac.rate.surveillance': "حراسة الامتحانات",
    'fac.rate.survGross': "الحراسة، النسبة الخام",
    'fac.rate.survNet': "الحراسة، النسبة الصافية",
    'fac.rate.survHint': "اختياري: وإلا تُطبق النسبة العامة",
    'fac.rate.note': "النسبة حسب نوع التدريس اختيارية: وإلا تُطبق النسبة العامة. الساعات المؤداة هي ساعات الحصص المنجزة من طرف الأستاذ (بنفسه أو تعويضاً)، مقروءة من البرمجة.",
    'fac.document.title': "الوثيقة الموقعة",
    'fac.document.none': "لا وثيقة مودعة.",
    'fac.document.size': "{size} ك.ب",
    'fac.document.download': "تحميل",
    'fac.document.upload': "إيداع العقد الموقع",
    'fac.document.replace': "استبدال",
    'fac.document.hint': "PDF، 10 م.ب على الأكثر",
    'fac.document.pdfOnly': "يُقبل ملف PDF فقط.",
    'fac.document.tooBig': "يتجاوز الملف 10 م.ب.",
    'fac.document.uploaded': "تم إيداع الوثيقة.",
    'fac.document.deleted': "تم حذف الوثيقة.",
    'fac.objectives.title': "الأهداف التعاقدية",
    'fac.objectives.titleYear': "الأهداف التعاقدية · {year}",
    'fac.objectives.sub': "ما يلتزم الأستاذ بتدريسه، حسب المادة ونوع التدريس والقسم أو الفوج",
    'fac.objectives.contract': "العقد",
    'fac.objectives.noContract': "لا عقد: أدخل أولاً عقداً لهذه السنة.",
    'fac.objectives.empty': "لا أهداف مُدخلة.",
    'fac.objectives.service': "خدمة العقد: {service}، أي من {min} إلى {max}",
    'fac.objectives.over.weekly': "على مدى {count} أسبوعاً",
    'fac.objectives.over.monthly': "على مدى {count} أشهر",
    'fac.objectives.over.yearly': "على مدى السنة",
    'fac.objectives.inRange': "هدف {hours} داخل خدمة العقد",
    'fac.objectives.below': "هدف {hours} دون الحد الأدنى للخدمة",
    'fac.objectives.above': "هدف {hours} يتجاوز الحد الأقصى للخدمة",
    'fac.objectives.overCatalog': "يتجاوز حجم المسار",
    'fac.objectives.notPlanned': "غير مبرمج",
    'fac.objectives.fromAttributions': "استرجاع الإسنادات",
    'fac.objectives.fromAttributionsDone': "أهداف أُنشئت من الإسنادات: {count}.",
    'fac.objectives.add': "إضافة هدف",
    'fac.objectives.edit': "تعديل الهدف",
    'fac.objectives.remove': "حذف الهدف",
    'fac.objectives.saved': "تم حفظ الهدف.",
    'fac.objectives.deleted': "تم حذف الهدف.",
    'fac.objectives.dialogSub': "مادة، نوع تدريس، قسم أو التكوين كله، وحجم",
    'fac.objectives.subject': "المادة",
    'fac.objectives.class': "القسم أو الفوج",
    'fac.objectives.classHint': "اختياري",
    'fac.objectives.allClasses': "التكوين كله",
    'fac.objectives.hours': "الحجم",
    'fac.objectives.responsibleNote': "يُسجَّل الأستاذ أيضاً كمتدخل في المادة بالكتالوج، إن لم يكن كذلك.",
    'fac.objectives.note': "إضافة هدف تسجل الأستاذ أيضاً كمتدخل في المادة بالكتالوج إن لم يكن كذلك. تقترح البرمجة أولاً الأساتذة الذين لهم هدف في المادة والقسم.",
    'fac.dash.realized': "المنجز",
    'fac.dash.realizedNote': "من {hours} هدفاً · {percent} %",
    'fac.dash.noObjective': "لا أهداف مُدخلة",
    'fac.dash.plannedRemaining': "المبرمج المتبقي",
    'fac.dash.plannedRemainingNote': "الحصص المقبلة، إلى نهاية العقد",
    'fac.dash.toPlan': "المتبقي للبرمجة",
    'fac.dash.toPlanNote': "هدف غير مغطى بالبرمجة",
    'fac.dash.month': "هذا الشهر",
    'fac.dash.monthNote': "الحصص المنجزة: {count}",
    'fac.dash.byMonth': "الساعات في الشهر",
    'fac.dash.byMonthSub': "الحصص المبرمجة والمنجزة، كل المواد",
    'fac.dash.noMonth': "لا حصص هذه السنة.",
    'fac.dash.planned': "مبرمج",
    'fac.dash.done': "منجز",
    'fac.dash.bySubject': "حسب المادة",
    'fac.dash.noSubject': "لا مواد.",
    'fac.dash.todo': "للتتبع",
    'fac.dash.todoEmpty': "لا شيء يُذكر.",
    'fac.dash.group.roll_missing': "{count} مناداة لم تُنجز",
    'fac.dash.group.marks_missing': "{count} تقييمات في انتظار إدخال النقط",
    'fac.dash.group.replacement': "{count} حصص قُدّمت بالنيابة",
    'fac.dash.group.cancelled': "{count} حصص ملغاة",
    'fac.dash.group.out_of_contract': "{count} حصص خارج العقد",
    'fac.dash.see': "عرض",
    'fac.dash.payDetail': "التفاصيل",
    'fac.dash.paid': "المؤدى خلال السنة",
    'fac.dash.paidNote': "الصافي {net} · الأشهر المؤداة: {count}",
    'fac.dash.current': "{month}",
    'fac.dash.currentNote': "الصافي {net} · {state}",
    'fac.contractsList.title': "العقود",
    'fac.contractsList.sub': "عقود السنة، لكل الأساتذة",
    'fac.contractsList.count': "عدد العقود: {count} · سنة {year}",
    'fac.contractsList.empty': "لا يوجد عقد يطابق هذه المعايير.",
    'fac.settings.title': "تدبير الأساتذة · الإعدادات",
    'fac.settings.sub': "وحدة الأجور وقواعدها، ولوائح العقود",
    'fac.settings.types': "أنواع العقود",
    'fac.settings.categories': "فئات الأساتذة",
    'fac.settings.permanent': "دائم",
    'fac.settings.permanentHint': "عقد دائم (غير محدد المدة، رسمي)",
    'fac.settings.active': "مقترح في اللوائح",
    'fac.settings.inactive': "مخفي",
    'fac.settings.label': "التسمية",
    'fac.settings.addType': "إضافة نوع عقد",
    'fac.settings.addCategory': "إضافة فئة",
    'fac.settings.edit': "تعديل «{label}»",
    'fac.settings.removeType': "حذف نوع العقد",
    'fac.settings.removeCategory': "حذف الفئة",
    'fac.settings.used': "مستعمل: لا يمكن حذفه",
    'fac.settings.empty': "لائحة فارغة.",
    'fac.settings.saved': "تم حفظ الإعدادات.",
    'pay.state.computed': "محسوب",
    'pay.state.validated': "مصادق عليه",
    'pay.state.paid': "مؤدى",
    'pay.state.nothing': "لا شيء للأداء",
    'pay.state.blocked': "موقوف",
    'pay.blocker.rolls_missing': "نداءات غير منجزة",
    'pay.blocker.no_contract': "بدون عقد",
    'pay.blocker.draft_contract': "عقد مسودة",
    'pay.blocker.out_of_contract': "حصص خارج العقد",
    'pay.blocker.over_cap': "يتجاوز السقف",
    'pay.blocker.no_rate': "نسبة ناقصة",
    'pay.filter.all': "الكل",
    'pay.filter.computed': "للمصادقة",
    'pay.filter.validated': "مصادق عليها",
    'pay.filter.paid': "مؤداة",
    'pay.method.transfer': "تحويل",
    'pay.method.cheque': "شيك",
    'pay.method.cash': "نقداً",
    'pay.method.other': "آخر",
    'pay.month.title': "أجور الأساتذة",
    'pay.month.sub': "الساعات المنجزة خلال الشهر بنسبة العقد: صادق ثم ضع علامة مؤدى",
    'pay.month.previous': "الشهر السابق",
    'pay.month.next': "الشهر الموالي",
    'pay.month.global': "الكشف العام",
    'pay.month.export': "تصدير",
    'pay.month.empty': "لا أساتذة للأداء هذا الشهر.",
    'pay.month.teachers': "الأساتذة: {count}",
    'pay.month.note': "المصادقة تجمّد الشهر للأستاذ: الساعات والنسب والتسويات. «وضع علامة مؤدى» يسجل التاريخ والطريقة ومرجع التحويل؛ Kelassi لا يقوم بالتحويل. تُصدَّر الأشهر المصادق عليها لبرنامج الأجور.",
    'pay.kpi.gross': "الخام الشهري",
    'pay.kpi.grossNote': "الصافي {net}",
    'pay.kpi.hours': "الساعات المنجزة",
    'pay.kpi.hoursNote': "منها {hours} إضافية",
    'pay.kpi.toValidate': "للمصادقة",
    'pay.kpi.toValidateNote': "مصادق عليها: {validated} · مؤداة: {paid}",
    'pay.kpi.blocked': "قبل المصادقة",
    'pay.kpi.blockedNote': "أساتذة لهم نقط موقِفة",
    'pay.column.month': "الشهر",
    'pay.column.hours': "الساعات",
    'pay.column.realized': "الساعات المنجزة",
    'pay.column.extra': "إضافية",
    'pay.column.grossIn': "الخام ({currency})",
    'pay.column.netIn': "الصافي ({currency})",
    'pay.column.state': "الحالة",
    'pay.column.line': "الحصص المنجزة",
    'pay.column.classes': "القسم",
    'pay.column.sessions': "العدد",
    'pay.column.rate': "النسبة الخام",
    'pay.column.amount': "المبلغ الخام",
    'pay.column.adjustment': "التسوية",
    'pay.column.reason': "السبب",
    'pay.sessions': "الحصص: {count}",
    'pay.line.extra': "ساعات إضافية",
    'pay.line.included': "منها {hours} مشمولة في المبلغ الجزافي",
    'pay.selectAll': "تحديد الكل",
    'pay.validate': "المصادقة على الشهر",
    'pay.validateSelection': "المصادقة على التحديد ({count})",
    'pay.validated': "أشهر مصادق عليها: {count}.",
    'pay.markPaid': "وضع علامة مؤدى ({count})",
    'pay.paid': "كشوف مؤداة: {count}.",
    'pay.reopen': "إعادة الفتح",
    'pay.reopen.title': "إعادة فتح الكشف المصادق عليه",
    'pay.reopen.sub': "يعود الشهر محسوباً؛ الكشوف المعنية: {count}",
    'pay.reopened': "كشوف أعيد فتحها: {count}.",
    'pay.unpay': "إلغاء الأداء",
    'pay.unpay.title': "إلغاء الأداء",
    'pay.unpay.sub': "يعود الكشف مصادقاً عليه غير مؤدى؛ الكشوف المعنية: {count}",
    'pay.unpaid': "أداءات ملغاة: {count}.",
    'pay.reason.label': "السبب",
    'pay.reason.required': "السبب إلزامي.",
    'pay.pay.title': "وضع علامة مؤدى",
    'pay.pay.sub': "الكشوف المصادق عليها: {count}. Kelassi لا يقوم بالتحويل.",
    'pay.pay.submit': "وضع علامة مؤدى",
    'pay.pay.date': "أُدي في",
    'pay.pay.method': "الطريقة",
    'pay.pay.reference': "المرجع",
    'pay.pay.referenceHint': "رقم التحويل أو الشيك، اختياري",
    'pay.openTeacher': "فتح الملف",
    'pay.detail': "التفاصيل",
    'pay.statement.pdf': "كشف الأجر",
    'pay.statement.preview': "معاينة الكشف",
    'pay.statement.number': "الكشف {number}",
    'pay.statement.validated': "صودق عليه في {date} من طرف {who}",
    'pay.statement.paid': "أُدي في {date} ({method} {reference})",
    'pay.gross': "الخام",
    'pay.net': "الصافي",
    'pay.extraHours': "منها {hours} إضافية",
    'pay.unpaidHours': "{hours} غير مؤداة",
    'pay.replacementFor': "تعويضاً لـ {name}",
    'pay.outOfContract': "خارج العقد",
    'pay.teacher.months': "الأشهر",
    'pay.teacher.noMonth': "لا أشهر هذه السنة.",
    'pay.teacher.detailTitle': "{month} · التفاصيل",
    'pay.teacher.contract': "عقد {year}",
    'pay.teacher.noContract': "لا عقد ساري المفعول هذا الشهر",
    'pay.teacher.noLine': "لا حصص مؤداة هذا الشهر.",
    'pay.teacher.sessions': "حصص الشهر ({count})",
    'pay.teacher.note': "يُعاد حساب المبلغ ما دام الشهر غير مصادق عليه: كل حصة منجزة أو ملغاة أو معاد إسنادها تحدّثه. الشهر المصادق عليه مجمَّد؛ التصحيح يتم بتسوية في الشهر الموالي.",
    'pay.adjustment.add': "إضافة تسوية",
    'pay.adjustment.edit': "تعديل التسوية",
    'pay.adjustment.remove': "حذف التسوية",
    'pay.adjustment.sub': "منحة أو اقتطاع على {month}، مع السبب",
    'pay.adjustment.kind.bonus': "منحة",
    'pay.adjustment.kind.deduction': "اقتطاع",
    'pay.adjustment.gross': "المبلغ الخام",
    'pay.adjustment.net': "المبلغ الصافي",
    'pay.adjustment.hint': "فقط ما دام الشهر غير مصادق عليه؛ تصحيح شهر مصادق عليه يذهب إلى الشهر الموالي.",
    'pay.adjustment.saved': "تم حفظ التسوية.",
    'pay.adjustment.deleted': "تم حذف التسوية.",
    'pay.settings.module': "الوحدة",
    'pay.settings.reserved': "مخصص للمدير العام للنظام",
    'pay.settings.enabled': "أجور الأساتذة",
    'pay.settings.enabledHint': "يفعّل تبويب الأجر في الملفات، وأجور الشهر، والنسب في العقود. عند الإيقاف: لا يُحذف شيء، كل شيء يُخفى.",
    'pay.settings.enabledOn': "فُعّلت في {date} من طرف {who}",
    'pay.settings.rules': "قواعد الأجور",
    'pay.settings.payReplacements': "أداء الحصص للأستاذ الذي قدمها (المعوِّض، لا الأستاذ المبرمج)",
    'pay.settings.paySurveillance': "أداء حراسة الامتحانات بنسبة «الحراسة» في العقد",
    'pay.settings.requireRolls': "عدم المصادقة على شهر ما دام للأستاذ نداءات غير منجزة فيه",
    'pay.settings.currency': "العملة",
    'pay.settings.currencyHint': "عملة المؤسسة",
    'pay.settings.decimals': "تقريب المبالغ",
    'pay.settings.decimals2': "إلى السنتيم",
    'pay.settings.decimals0': "إلى الوحدة",
    'pay.settings.pattern': "ترقيم الكشوف",
    'pay.settings.insert': "إدراج",
    'pay.settings.saved': "تم حفظ إعدادات الأجور.",
    'pay.modules.name': "أجور الأساتذة",
    'pay.modules.rules': "قواعد الأجور ولوائح العقود",
    'violation.fac.contract_overlap': "هذا العقد يتداخل مع عقد آخر للأستاذ.",
    'violation.pay.contract_locked': "شهر أداء من هذا العقد مصادق عليه: النسب والطريقة مقفلة.",
    'violation.pay.rolls_missing': "نداءات غير منجزة هذا الشهر: أكملها قبل المصادقة.",
    'violation.com.out_of_scope': "هذا المستلم خارج نطاقك: يمكنك إشعار الطلبة الذين تدرّسهم فقط.",
    'violation.com.not_chattable': "لا يمكن إضافة هذا الشخص إلى محادثة.",
    'violation.com.not_a_group': "هذا الإجراء خاص بمحادثات المجموعات.",
    'violation.com.type_not_allowed': "لا يمكنك إرسال هذا النوع من الإشعارات.",
    'violation.com.no_year': "لا توجد سنة جامعية جارية.",
    'violation.com.no_recipient': "لا يملك أي مستلم حساباً نشطاً: ادعُ الطلبة إلى البوابة أولاً.",
    'violation.com.nothing_to_remind': "قرأ جميع المستلمين الإشعار.",
    'violation.com.replies_closed': "هذا الإشعار لا يقبل الردود.",
    'violation.com.no_reply': "لم يرد الطالب بعد.",
    'violation.com.too_many_attachments': "ثلاثة مرفقات على الأكثر.",
    'violation.com.unknown_attachment': "المرفق غير موجود: أعد تحميله.",
    'violation.com.system_type': "النوع « Kelassi » مخصص لتنبيهات التطبيق.",
    'violation.com.type_used': "سبق استعمال هذا النوع: عطّله بدل حذفه.",
    'com.allRead': "تم تعليم الكل كمقروء.",
    'com.archive': "أرشفة",
    'com.unarchive': "إلغاء الأرشفة",
    'com.archived': "تمت أرشفة الإشعار: لم يعد يظهر إلا في «المؤرشفة».",
    'com.unarchived': "تم إلغاء أرشفة الإشعار.",
    'com.backToList': "العودة إلى الإشعارات",
    'com.bell.all': "كل الإشعارات",
    'com.bell.empty': "لا جديد.",
    'com.bell.hint': "الإشعارات المقروءة لا تظهر هنا",
    'com.bell.label': "الإشعارات غير المقروءة: {count}",
    'com.bell.none': "الإشعارات: لا شيء غير مقروء",
    'com.bell.title': "الإشعارات غير المقروءة",
    'com.bell.unread': "غير مقروءة · {count}",
    'com.box.label': "الصندوق",
    'com.box.received': "المستلمة",
    'com.box.sent': "المرسلة",
    'com.box.archived': "المؤرشفة",
    'com.chip.remove': "إزالة {label}",
    'com.column.class': "القسم",
    'com.column.fromSubject': "المرسل والموضوع",
    'com.column.read': "مقروء",
    'com.column.received': "تاريخ الاستلام",
    'com.column.sent': "تاريخ الإرسال",
    'com.column.reply': "الرد",
    'com.column.state': "الحالة",
    'com.column.student': "الطالب",
    'com.column.type': "النوع",
    'com.compose.addTarget': "أضف تكوينا أو قسما أو فوجا أو طالبا…",
    'com.compose.body': "الرسالة",
    'com.compose.bodyPlaceholder': "ستظهر الرسالة هنا.",
    'com.compose.countLine': "الطلبة الذين سيتلقون الإشعار: {students} · لديهم بريد إلكتروني: {emails}",
    'com.compose.draftTitle': "مسودة إشعار",
    'com.compose.draftTrail': "مسودة",
    'com.compose.email': "الإرسال أيضا عبر البريد الإلكتروني",
    'com.compose.emailCount': "الإرسال أيضا عبر البريد الإلكتروني (العناوين: {count})",
    'com.compose.incomplete': "اختر نوعا وموضوعا ورسالة ومستلما واحدا على الأقل.",
    'com.compose.new': "إشعار جديد",
    'com.compose.noMatch': "لا يوجد تكوين أو قسم أو طالب مطابق.",
    'com.compose.noTarget': "أضف مستلما واحدا على الأقل.",
    'com.compose.note': "لا يمكن للطلبة المبادرة بالكتابة: يردون فقط على إشعار يسمح بذلك. لا يرى كل رد إلا المرسل ومسيرو الإشعار.",
    'com.compose.panel': "الإشعار",
    'com.compose.preview': "معاينة لدى الطالب",
    'com.compose.recipients': "المستلمون",
    'com.compose.replies': "يمكن للطلبة الرد",
    'com.compose.repliesNone': "لا رد (يفرضه النوع {type})",
    'com.compose.repliesRequired': "يمكن للطلبة الرد (يفرضه النوع {type})",
    'com.compose.saveDraft': "حفظ المسودة",
    'com.compose.saved': "تم حفظ المسودة.",
    'com.compose.send': "إرسال",
    'com.compose.sendTo': "إرسال إلى الطلبة ({count})",
    'com.compose.studentsCount': "الطلبة: {count}",
    'com.compose.sub': "إلى الطلبة: حسب الطالب أو القسم أو الفوج أو التكوين",
    'com.compose.subject': "الموضوع",
    'com.compose.subjectPlaceholder': "موضوع الإشعار",
    'com.compose.title': "إشعار جديد",
    'com.compose.trail': "جديد",
    'com.compose.type': "نوع الإشعار",
    'com.draft': "مسودة",
    'com.file.attach': "إرفاق ملف",
    'com.file.hint': "PDF أو صورة، 5 ميغابايت على الأكثر، 3 ملفات",
    'com.file.image': "صورة",
    'com.file.remove': "إزالة {name}",
    'com.file.tooBig': "{name} يتجاوز 5 ميغابايت.",
    'com.file.tooMany': "المرفقات: {count} على الأكثر.",
    'com.file.uploading': "جارٍ رفع الملف…",
    'com.file.wrongType': "{name}: لا تقبل إلا ملفات PDF وPNG وJPEG.",
    'com.filter.allTypes': "كل الأنواع",
    'com.filter.period': "الفترة",
    'com.filter.type': "النوع",
    'com.handled': "تمت المعالجة",
    'com.inbox.empty': "لا توجد إشعارات.",
    'com.inbox.emptyUnread': "لا توجد إشعارات غير مقروءة.",
    'com.inbox.search': "بحث",
    'com.inbox.sub': "كل ما استلمته وأرسلته · المقروءة تبقى هنا ولا تظهر في الجرس",
    'com.inbox.title': "الإشعارات",
    'com.invite.button': "دعوة إلى البوابة",
    'com.invite.done': "الدعوات المرسلة: {invited} · بدون بريد إلكتروني: {noEmail} · لهم حساب مسبقا: {existing}",
    'com.invite.selectAll': "تحديد طلبة هذه الصفحة",
    'com.invite.selection': "دعوة إلى البوابة ({count})",
    'com.invite.submit': "إرسال الدعوات",
    'com.invite.textClass': "سيتلقى طلبة القسم {name} الذين لديهم بريد إلكتروني وليس لهم حساب بعد رابط تفعيل. سيتمكنون من قراءة إشعاراتهم والرد عليها.",
    'com.invite.textSelection': "الطلبة المحددون: {count}. سيتلقى من لديه بريد إلكتروني وليس له حساب بعد رابط تفعيل. سيتمكنون من قراءة إشعاراتهم والرد عليها.",
    'com.invite.textStudent': "سيتلقى {name} رابط تفعيل على بريده الإلكتروني إن لم يكن له حساب بعد. سيتمكن من قراءة إشعاراته والرد عليها.",
    'com.invite.textTraining': "سيتلقى الطلبة المسجلون في {name} الذين لديهم بريد إلكتروني وليس لهم حساب بعد رابط تفعيل.",
    'com.invite.title': "دعوة إلى البوابة",
    'com.kpi.emails': "الرسائل الإلكترونية",
    'com.kpi.emailsNote': "مرسلة · فاشلة: {count}",
    'com.kpi.read': "مقروء",
    'com.kpi.readNote': "{percent} % · غير مقروءة: {count}",
    'com.kpi.replies': "الردود",
    'com.kpi.repliesNote': "للمعالجة: {count}",
    'com.kpi.sentTo': "أُرسل إلى",
    'com.kpi.sentToNote': "طلبة · {date}",
    'com.live.on': "الوقت الفعلي مفعل",
    'com.live.onHint': "خادم البث متصل · البديل: تحديث كل 30 ثانية",
    'com.live.polling': "تحديث كل 30 ثانية",
    'com.live.pollingHint': "خادم البث لا يستجيب أو غير مثبت: يتحدث الجرس كل 30 ثانية.",
    'com.markAllRead': "تعليم الكل كمقروء",
    'com.message': "الرسالة المرسلة",
    'com.messages.archive': "أرشفة المحادثة",
    'com.messages.back': "العودة إلى المحادثات",
    'com.messages.conversations': "المحادثات",
    'com.messages.empty': "لا توجد محادثات. ابدأ واحدة مع زميل.",
    'com.messages.filter.all': "الكل",
    'com.messages.filter.unread': "غير المقروءة",
    'com.messages.filter.groups': "المجموعات",
    'com.messages.groupName': "اسم المجموعة",
    'com.messages.groupOf': "مجموعة · الأشخاص: {count}",
    'com.messages.leave': "مغادرة المجموعة",
    'com.messages.leaveConfirm': "لن تتلقى رسائل «{name}» بعد الآن. تبقى الرسائل المتبادلة مرئية للمشاركين الآخرين.",
    'com.messages.members': "المشاركون",
    'com.messages.new': "محادثة جديدة",
    'com.messages.noMatch': "لا توجد محادثة مطابقة.",
    'com.messages.noMessage': "لا رسائل",
    'com.messages.older': "الرسائل السابقة",
    'com.messages.online': "متصلون: {count}",
    'com.messages.onlineOne': "متصل",
    'com.messages.pick': "اختر محادثة أو ابدأ محادثة جديدة.",
    'com.messages.placeholder': "اكتب رسالة… (Enter للإرسال، Shift + Enter لسطر جديد)",
    'com.messages.readBy': "قرأه: {count}",
    'com.messages.rename': "إعادة تسمية المجموعة",
    'com.messages.search': "ابحث عن شخص أو رسالة",
    'com.messages.start': "لا رسائل بعد: اكتب الأولى.",
    'com.messages.sub': "بين الموظفين والأساتذة · في الوقت الفعلي",
    'com.messages.title': "المراسلة",
    'com.messages.typing': "{names} يكتب…",
    'com.messages.unreadCount': "الرسائل غير المقروءة: {count}",
    'com.messages.you': "أنت",
    'com.more': "عرض المزيد",
    'com.newConv.choose': "اختيار",
    'com.newConv.function': "الوظيفة",
    'com.newConv.groupName': "اسم المجموعة (اختياري، ابتداء من 3 أشخاص)",
    'com.newConv.none': "لا أحد مطابق.",
    'com.newConv.note': "لا يظهر الطلبة هنا: نكتب إليهم عبر إشعار. يمكن للأستاذ مراسلة الأساتذة الآخرين والإدارة.",
    'com.newConv.person': "الشخص",
    'com.newConv.required': "اختر شخصا واحدا على الأقل.",
    'com.newConv.search': "الاسم، الوظيفة",
    'com.newConv.sub': "اختر شخصا أو أكثر",
    'com.newConv.submit': "بدء المحادثة",
    'com.newConv.title': "محادثة جديدة",
    'com.period.all': "كل الفترات",
    'com.period.week': "هذا الأسبوع",
    'com.period.month': "هذا الشهر",
    'com.period.year': "هذه السنة",
    'com.read': "مقروء",
    'com.recipient.emailFailed': "فشل البريد",
    'com.recipient.readAt': "قُرئ {time}",
    'com.recipient.seeReply': "عرض الرد",
    'com.recipient.unread': "غير مقروء",
    'com.recipients': "المستلمون",
    'com.recipients.all': "الكل ({count})",
    'com.recipients.unread': "غير المقروءة ({count})",
    'com.recipients.replied': "بها رد ({count})",
    'com.recipients.empty': "لا مستلمين في هذا العرض.",
    'com.remind': "تذكير غير القارئين ({count})",
    'com.remind.done': "تم إرسال التذكير إلى الطلبة: {count}.",
    'com.remind.submit': "تذكير",
    'com.remind.text': "سيتلقى الطلبة الذين لم يقرؤوا الإشعار ({count}) إياه مجددا، وعبر البريد أيضا إن كان الأصل كذلك.",
    'com.remind.title': "تذكير غير القارئين",
    'com.replies.empty': "لا ردود بعد.",
    'com.replies.handle': "تعليم كمعالج",
    'com.replies.unhandle': "إعادة الفتح",
    'com.replies.pick': "اختر ردا من لائحة المستلمين.",
    'com.replies.placeholder': "الرد على {name}…",
    'com.replies.private': "رد خاص: لا يراه إلا الطالب",
    'com.replies.title': "الردود · {name}",
    'com.replies.none': "لا",
    'com.replies.optional': "حسب الاختيار",
    'com.replies.required': "إلزامي",
    'com.replies.noneLong': "بدون رد",
    'com.replies.optionalLong': "حسب اختيار المرسل",
    'com.replies.requiredLong': "مسموح دائما",
    'com.repliesAllowed': "الردود مسموحة",
    'com.repliesClosed': "بدون ردود",
    'com.reply.action': "رد",
    'com.reply.none': "هذا الإشعار لا ينتظر ردا.",
    'com.reply.placeholder': "رد…",
    'com.send': "إرسال",
    'com.sentBy': "أرسله {name}",
    'com.senders.staff': "الموظفون",
    'com.senders.staff_teachers': "الموظفون، الأساتذة",
    'com.senders.direction': "الإدارة",
    'com.settings.active': "نوع مقترح عند الإرسال",
    'com.settings.addType': "إضافة نوع",
    'com.settings.attachments': "المرفقات",
    'com.settings.attachmentsMb': "PDF وصور، {count} ميغابايت",
    'com.settings.digest': "نسخة بالبريد للرسائل غير المقروءة بعد 24 ساعة (الموظفون والأساتذة)",
    'com.settings.editType': "تعديل «{label}»",
    'com.settings.emailDefault': "البريد افتراضيا",
    'com.settings.emailDefaultHint': "اقتراح الإرسال بالبريد افتراضيا",
    'com.settings.inactive': "غير نشط",
    'com.settings.keep': "الحفظ والبث",
    'com.settings.label': "التسمية",
    'com.settings.removeConfirm': "حذف النوع «{label}»؟ تحتفظ به الإشعارات المرسلة.",
    'com.settings.removeType': "حذف النوع",
    'com.settings.replies': "الرد",
    'com.settings.retention': "مدة حفظ الرسائل",
    'com.settings.retention5': "5 سنوات، مثل التتبع",
    'com.settings.retentionYears': "السنوات: {count}",
    'com.settings.rules': "من يكتب إلى من",
    'com.settings.saved': "تم حفظ إعدادات التواصل.",
    'com.settings.senders': "من يمكنه إرساله",
    'com.settings.staffChat': "يتراسل الموظفون فيما بينهم ومع الأساتذة",
    'com.settings.studentsFirst': "يمكن للطلبة المبادرة بالكتابة إلى الإدارة (معطل: يردون فقط)",
    'com.settings.sub': "أنواع الإشعارات، من يكتب إلى من، الحفظ",
    'com.settings.system': "Kelassi (التنبيهات)",
    'com.settings.teacherChat': "يتراسل الأساتذة فيما بينهم ومع الإدارة",
    'com.settings.teacherNotify': "يرسل الأساتذة إشعارات إلى طلبتهم (أقسامهم وأفواجهم لهذه السنة)",
    'com.settings.title': "التواصل · الإعدادات",
    'com.settings.tone': "اللون",
    'com.settings.type': "النوع",
    'com.settings.typeDeleted': "تم حذف النوع «{label}».",
    'com.settings.typeSaved': "تم حفظ نوع الإشعار.",
    'com.settings.types': "أنواع الإشعارات",
    'com.state.label': "القراءة",
    'com.state.all': "الكل",
    'com.state.unread': "غير المقروءة",
    'com.state.read': "المقروءة",
    'com.student.noneRead': "لا توجد إشعارات مقروءة.",
    'com.student.noneUnread': "لا توجد إشعارات غير مقروءة.",
    'com.student.read': "الإشعارات المقروءة",
    'com.student.showRead': "عرض الإشعارات المقروءة",
    'com.student.sub': "رسائل مؤسستك · رد عندما يكون ذلك مسموحا",
    'com.student.unread': "غير المقروءة: {count}",
    'com.target.student': "طالب",
    'com.target.class': "قسم",
    'com.target.training': "تكوين",
    'com.target.studentChip': "طالب · {label}",
    'com.target.classChip': "قسم · {label} ({count})",
    'com.target.trainingChip': "تكوين · {label} ({count})",
    'com.time.now': "الآن",
    'com.time.today': "اليوم",
    'com.time.yesterday': "أمس",
    'com.tone.info': "أزرق",
    'com.tone.warn': "كهرماني",
    'com.tone.violet': "بنفسجي",
    'com.tone.danger': "أحمر",
    'com.tone.muted': "رمادي",
    'com.tone.ok': "أخضر",
    'com.type.kelassi': "Kelassi",
    'com.type.message': "رسالة",
    'com.type.reply': "رد",
    'com.unread': "غير مقروء",
    'rel.none': "لا شيء",
    'rel.open': "فتح",
    'rel.removed': "تم حذف «{label}».",
    'rel.remove.confirm': "حذف «{label}»؟ لا يمكن التراجع عن هذا الإجراء.",
    'rel.backToOrganisations': "الهيئات",
    'rel.backToAgreements': "الاتفاقيات",
    'rel.status.prospect': "قيد الاستقطاب",
    'rel.status.active': "شريك نشط",
    'rel.status.dormant': "دون تواصل منذ 12 شهرا",
    'rel.status.renewal': "اتفاقية للتجديد",
    'rel.filter.allTypes': "كل الأنواع",
    'rel.filter.allSectors': "كل القطاعات",
    'rel.filter.allStatuses': "كل العلاقات",
    'rel.filter.allCities': "كل المدن",
    'rel.filter.allOwners': "كل المسؤولين",
    'rel.column.organisation': "الهيئة",
    'rel.column.agreement': "الاتفاقية",
    'rel.column.agreementOrg': "الاتفاقية والهيئة",
    'rel.column.end': "النهاية",
    'rel.column.mainContact': "جهة الاتصال الرئيسية",
    'rel.column.agreements': "الاتفاقيات",
    'rel.column.interns': "المتدربون",
    'rel.column.relation': "العلاقة",
    'rel.column.period': "المدة",
    'rel.column.state': "الحالة",
    'rel.column.alumnus': "الخريج",
    'rel.column.situation': "الوضعية",
    'rel.column.updated': "آخر تحديث",
    'rel.kpi.active': "الشركاء النشطون",
    'rel.kpi.activeNote': "من أصل {count} هيئة",
    'rel.kpi.agreements': "الاتفاقيات السارية",
    'rel.kpi.agreementsNote': "تنتهي منها: {count} خلال {days} يوما",
    'rel.kpi.prospects': "الاستقطاب",
    'rel.kpi.prospectsNote': "المواعيد هذا الشهر: {count}",
    'rel.kpi.insertion': "الخريجون العاملون",
    'rel.kpi.insertionNote': "دفعة {year}، بعد 6 أشهر",
    'rel.kpi.insertionNone': "لا يوجد استطلاع بعد",
    'rel.dashboard.title': "العلاقات الخارجية",
    'rel.dashboard.sub': "الشركاء والاتفاقيات والاستقطاب وإدماج الخريجين",
    'rel.dashboard.subYear': "الشركاء والاتفاقيات والاستقطاب وإدماج الخريجين · السنة {year}",
    'rel.dashboard.ending': "اتفاقيات قاربت نهايتها",
    'rel.dashboard.endingSub': "خلال {days} يوما القادمة",
    'rel.dashboard.allAgreements': "الكل",
    'rel.dashboard.noEnding': "لا توجد اتفاقية قاربت نهايتها.",
    'rel.dashboard.bySector': "الشركاء النشطون حسب القطاع",
    'rel.dashboard.noSector': "لا يوجد شريك نشط حاليا.",
    'rel.dashboard.lastExchanges': "آخر التواصلات",
    'rel.dashboard.directory': "الدليل",
    'rel.orgs.title': "الهيئات",
    'rel.orgs.sub': "مقاولات وجامعات وجمعيات وإدارات",
    'rel.orgs.count': "الهيئات: {count} · مقاولات وجامعات وجمعيات وإدارات",
    'rel.orgs.search': "الاسم، الاختصار، جهة الاتصال، المدينة",
    'rel.orgs.empty': "لا توجد هيئة مطابقة.",
    'rel.orgs.agreementsCount': "الاتفاقيات: {count}",
    'rel.org.new': "هيئة جديدة",
    'rel.org.newSub': "مقاولة أو جامعة أو جمعية أو إدارة تتعامل معها المؤسسة",
    'rel.org.editProfile': "تعديل · {name}",
    'rel.org.name': "الاسم",
    'rel.org.legalForm': "الشكل القانوني",
    'rel.org.acronym': "الاختصار",
    'rel.org.type': "النوع",
    'rel.org.sector': "القطاع",
    'rel.org.noSector': "بدون قطاع",
    'rel.org.status': "العلاقة",
    'rel.org.statusHint': "اتركها «محسوبة»: يستنتجها Kelassi من الاتفاقيات والتواصلات.",
    'rel.org.statusComputed': "يحسبها Kelassi",
    'rel.org.owner': "المسؤول بالمؤسسة",
    'rel.org.city': "المدينة",
    'rel.org.website': "الموقع الإلكتروني",
    'rel.org.partnerSince': "شريك منذ",
    'rel.org.since': "شريك منذ {date}",
    'rel.org.saved': "تم حفظ الهيئة.",
    'rel.org.change': "تغيير الهيئة",
    'rel.org.searchPlaceholder': "اكتب حرفين على الأقل",
    'rel.org.interns': "المتدربون المستقبلون",
    'rel.org.internsNote': "الطلبة المرتبطون: {count}",
    'rel.org.alumni': "الخريجون العاملون",
    'rel.org.alumniNote': "الوضعية الحالية معروفة",
    'rel.org.studentsSub': "الطلبة المتدربون والخريجون العاملون بها",
    'rel.org.internsLater': "ستتابع التداريب من خلال وحدة التداريب: يبقى العداد صفرا إلى ذلك الحين.",
    'rel.org.toAlumni': "عرض الخريجين",
    'rel.tab.summary': "الملخص",
    'rel.tab.contacts': "جهات الاتصال",
    'rel.tab.agreements': "الاتفاقيات",
    'rel.tab.exchanges': "التواصلات",
    'rel.tab.students': "الطلبة والخريجون",
    'rel.tab.history': "السجل",
    'rel.tab.count': "{label} ({count})",
    'rel.contact.new': "جهة اتصال جديدة",
    'rel.contact.edit': "تعديل · {name}",
    'rel.contact.lastName': "الاسم العائلي",
    'rel.contact.firstNames': "الاسم الشخصي",
    'rel.contact.email': "البريد الإلكتروني",
    'rel.contact.phone': "الهاتف",
    'rel.contact.function': "الوظيفة",
    'rel.contact.main': "جهة الاتصال الرئيسية لهذه الهيئة",
    'rel.contact.mainShort': "جهة الاتصال الرئيسية",
    'rel.contact.alumnus': "خريج",
    'rel.contact.none': "لا توجد جهة اتصال.",
    'rel.contact.saved': "تم حفظ جهة الاتصال.",
    'rel.contact.remove': "إزالة جهة الاتصال",
    'rel.exchange.note': "تسجيل تواصل",
    'rel.exchange.new': "تسجيل تواصل",
    'rel.exchange.edit': "تعديل التواصل",
    'rel.exchange.sub': "مكالمة أو بريد أو موعد أو زيارة أو حدث أو ملاحظة",
    'rel.exchange.kind': "نوع التواصل",
    'rel.exchange.kind.call': "مكالمة",
    'rel.exchange.kind.email': "بريد",
    'rel.exchange.kind.meeting': "موعد",
    'rel.exchange.kind.visit': "زيارة",
    'rel.exchange.kind.event': "حدث",
    'rel.exchange.kind.note': "ملاحظة",
    'rel.exchange.on': "التاريخ",
    'rel.exchange.time': "الساعة",
    'rel.exchange.planned': "مبرمج",
    'rel.exchange.plannedHint': "قادم (موعد مبرمج)",
    'rel.exchange.subject': "الموضوع",
    'rel.exchange.body': "المحضر",
    'rel.exchange.accounts': "عن المؤسسة",
    'rel.exchange.addAccount': "إضافة مشارك",
    'rel.exchange.contacts': "عن الشريك",
    'rel.exchange.addContact': "إضافة جهة اتصال",
    'rel.exchange.agreement': "الاتفاقية المعنية",
    'rel.exchange.with': "مع {names}",
    'rel.exchange.none': "لا يوجد تواصل مسجل.",
    'rel.exchange.saved': "تم حفظ التواصل.",
    'rel.exchange.remove': "حذف التواصل",
    'rel.exchange.journal': "السجل الكامل",
    'rel.exchange.journalSub': "المكالمات والرسائل والمواعيد والزيارات، من الأحدث إلى الأقدم",
    'rel.exchange.older': "الأقدم",
    'rel.agreements.title': "الاتفاقيات",
    'rel.agreements.sub': "الاتفاقيات الجارية وآجالها وتجديداتها",
    'rel.agreements.empty': "لا توجد اتفاقية مطابقة.",
    'rel.agreements.filter.all': "الكل",
    'rel.agreements.filter.ending': "قاربت نهايتها",
    'rel.agreements.filter.draft': "المسودات",
    'rel.agreements.filter.ended': "المنتهية",
    'rel.agreement.new': "اتفاقية جديدة",
    'rel.agreement.newFor': "اتفاقية جديدة · {name}",
    'rel.agreement.edit': "تعديل · {title}",
    'rel.agreement.sub': "اتفاق مكتوب: موضوعه ومدته والتزامات كل طرف",
    'rel.agreement.organisation': "الهيئة",
    'rel.agreement.type': "نوع الاتفاقية",
    'rel.agreement.title': "العنوان",
    'rel.agreement.startsOn': "البداية",
    'rel.agreement.endsOn': "النهاية",
    'rel.agreement.endsOnHint': "فارغ: مدة غير محددة",
    'rel.agreement.indefinite': "مدة غير محددة",
    'rel.agreement.renewal': "التجديد",
    'rel.agreement.notice': "الإشعار المسبق (بالأشهر)",
    'rel.agreement.owner': "المسؤول بالمؤسسة",
    'rel.agreement.trainings': "التكوينات المعنية",
    'rel.agreement.addTraining': "إضافة تكوين",
    'rel.agreement.commitments': "الالتزامات",
    'rel.agreement.commitment': "الالتزام {n}",
    'rel.agreement.commitmentLabel': "الالتزام",
    'rel.agreement.party': "الطرف",
    'rel.agreement.quantity': "الكمية",
    'rel.agreement.addCommitment': "إضافة التزام",
    'rel.agreement.noCommitment': "لا يوجد التزام.",
    'rel.agreement.note': "ملاحظة",
    'rel.agreement.saveDraft': "حفظ كمسودة",
    'rel.agreement.saveSign': "حفظ ووضع علامة موقعة",
    'rel.agreement.saved': "تم حفظ الاتفاقية.",
    'rel.agreement.savedSigned': "تم حفظ الاتفاقية وتوقيعها.",
    'rel.agreement.reminder': "يذكر Kelassi المسؤول قبل {days} يوما من النهاية (تنبيه في الجرس) ويقترح التجديد بنسخ الاتفاقية.",
    'rel.agreement.none': "لا توجد اتفاقية.",
    'rel.agreement.state.draft': "مسودة",
    'rel.agreement.state.signed': "سارية",
    'rel.agreement.state.ended': "منتهية",
    'rel.agreement.state.terminated': "مفسوخة",
    'rel.agreement.endsIn': "تنتهي بعد {days} يوما",
    'rel.agreement.from': "منذ {date}",
    'rel.agreement.details': "الاتفاقية",
    'rel.agreement.signedOn': "وقعت بتاريخ",
    'rel.agreement.sign': "وضع علامة موقعة",
    'rel.agreement.signed': "تم توقيع الاتفاقية.",
    'rel.agreement.end': "إنهاء عند أجلها",
    'rel.agreement.endConfirm': "تصبح الاتفاقية «منتهية». تبقى قابلة للاطلاع ويمكن تجديدها.",
    'rel.agreement.ended': "تم إنهاء الاتفاقية.",
    'rel.agreement.terminate': "فسخ",
    'rel.agreement.terminated': "تم فسخ الاتفاقية.",
    'rel.agreement.reason': "السبب",
    'rel.agreement.renew': "تجديد",
    'rel.agreement.renewConfirm': "ينسخ Kelassi الاتفاقية في مسودة جديدة تبدأ في اليوم الموالي لنهايتها ({date}). تراجعها قبل توقيعها.",
    'rel.agreement.renewed': "تم إنشاء مسودة جديدة.",
    'rel.agreement.renewedFrom': "تجدد:",
    'rel.agreement.renewedBy': "جددت بواسطة:",
    'rel.agreement.delete': "حذف المسودة",
    'rel.agreement.lifecycle': "دورة الاتفاقية",
    'rel.renewal.none': "دون تجديد",
    'rel.renewal.tacit': "ضمني",
    'rel.renewal.explicit': "صريح",
    'rel.renewal.withNotice': "{renewal}، إشعار مسبق: {months} أشهر",
    'rel.party.school': "المؤسسة",
    'rel.party.partner': "الشريك",
    'rel.document.title': "الاتفاقية الموقعة",
    'rel.document.hint': "PDF، 10 ميغابايت كحد أقصى",
    'rel.document.none': "لم يتم إيداع أي وثيقة.",
    'rel.document.upload': "إيداع الاتفاقية الموقعة",
    'rel.document.replace': "استبدال الوثيقة",
    'rel.document.remove': "إزالة الوثيقة",
    'rel.document.uploaded': "تم إيداع الوثيقة.",
    'rel.document.removed': "تمت إزالة الوثيقة.",
    'rel.document.tooBig': "يتجاوز الملف 10 ميغابايت.",
    'rel.prospects.title': "الاستقطاب",
    'rel.prospects.sub': "الهيئات التي تسعى المؤسسة لكسبها كشركاء · اسحب بطاقة لتغيير مرحلتها",
    'rel.prospects.subRead': "الهيئات التي تسعى المؤسسة لكسبها كشركاء",
    'rel.prospects.all': "الكل",
    'rel.prospects.mine': "الخاصة بي",
    'rel.prospects.noStage': "لا توجد مراحل للاستقطاب: أضفها في الإعدادات.",
    'rel.prospects.emptyStage': "لا يوجد مسار",
    'rel.prospects.note': "تحتفظ كل مرحلة بالتاريخ والكاتب؛ ومرحلة «شريك» تقترح إنشاء الاتفاقية. ينبه المسؤول عن كل مسار دون تواصل منذ {days} يوما.",
    'rel.prospect.new': "إضافة مسار",
    'rel.prospect.newSub': "هيئة لكسبها كشريك، وما تنتظره منها المؤسسة",
    'rel.prospect.organisation': "الهيئة",
    'rel.prospect.mode.existing': "في الدليل",
    'rel.prospect.mode.new': "هيئة جديدة",
    'rel.prospect.objective': "الهدف",
    'rel.prospect.objectiveHint': "مثلا: 4 تداريب في التدقيق في الربيع",
    'rel.prospect.owner': "المسؤول",
    'rel.prospect.ownerMe': "أنا",
    'rel.prospect.stage': "المرحلة",
    'rel.prospect.saved': "تمت إضافة المسار.",
    'rel.prospect.addHere': "إضافة",
    'rel.prospect.followedBy': "المتابعة: {name}",
    'rel.prospect.idle': "دون تواصل منذ {days} يوما",
    'rel.prospect.noExchange': "لا يوجد تواصل",
    'rel.prospect.lastExchange': "آخر تواصل: {date}",
    'rel.prospect.moveMenu': "نقل إلى… · {name}",
    'rel.prospect.moveTo': "نقل إلى «{stage}»",
    'rel.prospect.moved': "{name}: المرحلة «{stage}».",
    'rel.prospect.wonTitle': "شريك جديد",
    'rel.prospect.wonText': "أصبحت {name} شريكا. إنشاء الاتفاقية الآن؟",
    'rel.prospect.close': "إغلاق المسار",
    'rel.prospect.closeConfirm': "يخرج مسار {name} من اللوحة. يبقى سجله في بطاقة الهيئة.",
    'rel.prospect.closed': "تم إغلاق المسار.",
    'rel.alumni.title': "الخريجون",
    'rel.alumni.sub': "المسار المهني للطلبة القدامى والإدماج حسب الدفعة",
    'rel.alumni.kpi.followed': "الخريجون المتابعون",
    'rel.alumni.kpi.followedNote': "كل الدفعات",
    'rel.alumni.kpi.employed': "عاملون بعد 6 أشهر",
    'rel.alumni.kpi.employedNote': "آخر دفعة",
    'rel.alumni.kpi.employedYear': "دفعة {year} · المجيبون: {count}",
    'rel.alumni.kpi.studying': "متابعة الدراسة",
    'rel.alumni.kpi.studyingNote': "داخل البلد أو خارجه",
    'rel.alumni.kpi.noNews': "دون أخبار",
    'rel.alumni.kpi.noNewsNote': "للتذكير عبر الاستطلاع",
    'rel.alumni.segment.all': "الكل",
    'rel.alumni.segment.employed': "عاملون",
    'rel.alumni.segment.studying': "دراسة",
    'rel.alumni.segment.unknown': "دون أخبار",
    'rel.alumni.status.employed': "عامل",
    'rel.alumni.status.studying': "يتابع الدراسة",
    'rel.alumni.status.seeking': "يبحث عن عمل",
    'rel.alumni.status.other': "أخرى",
    'rel.alumni.status.unknown': "دون أخبار",
    'rel.alumni.search': "اسم الخريج",
    'rel.alumni.year': "الدفعة",
    'rel.alumni.allYears': "كل الدفعات",
    'rel.alumni.training': "التكوين",
    'rel.alumni.allTrainings': "كل التكوينات",
    'rel.alumni.promotion': "دفعة {year}",
    'rel.alumni.empty': "لا يوجد خريج مطابق.",
    'rel.alumni.select': "اختيار {name}",
    'rel.alumni.selectPage': "اختيار الصفحة كلها",
    'rel.alumni.note': "يحدث الخريج وضعيته بنفسه عبر رابط الاستطلاع؛ ويمكن للمؤسسة أيضا إدخالها. التداريب ليست وضعيات: ستنقل إلى وحدة التداريب.",
    'rel.position.new': "إضافة وضعية",
    'rel.position.edit': "تعديل الوضعية",
    'rel.position.status': "الوضعية",
    'rel.position.jobTitle': "المنصب",
    'rel.position.programme': "التكوين المتابع",
    'rel.position.organisation': "المشغل أو المؤسسة",
    'rel.position.linkOrganisation': "اختيار الهيئة من الدليل",
    'rel.position.city': "المدينة",
    'rel.position.country': "البلد",
    'rel.position.startsOn': "منذ",
    'rel.position.endsOn': "إلى غاية",
    'rel.position.endsOnHint': "فارغ: الوضعية الحالية",
    'rel.position.current': "الحالية",
    'rel.position.today': "اليوم",
    'rel.position.none': "لا توجد وضعية معروفة.",
    'rel.position.saved': "تم حفظ الوضعية.",
    'rel.position.remove': "حذف الوضعية",
    'rel.position.sourceLine': "المصدر: {source} · حدثت بتاريخ {date}",
    'rel.position.source.survey': "الاستطلاع",
    'rel.position.source.school': "إدخال المؤسسة",
    'rel.position.source.legacy': "Kelassi القديم",
    'rel.survey.title': "استطلاع الإدماج",
    'rel.survey.sub': "رابط شخصي بالبريد صالح لمدة 60 يوما، دون الحاجة إلى حساب",
    'rel.survey.launch': "إطلاق استطلاع الإدماج",
    'rel.survey.launchSelected': "استطلاع الخريجين المختارين ({count})",
    'rel.survey.scope': "المرسل إليهم",
    'rel.survey.byYear': "دفعة",
    'rel.survey.selected': "الخريجون المختارون ({count})",
    'rel.survey.year': "الدفعة",
    'rel.survey.send': "إرسال",
    'rel.survey.hint': "يستبعد الخريجون دون بريد إلكتروني ويحصون. ويتلقى الخريج الذي لديه رابط صالح رابطا جديدا.",
    'rel.survey.sent': "تم إرسال الاستطلاع: {sent}.",
    'rel.survey.sentSkipped': "تم إرسال الاستطلاع: {sent}؛ دون بريد إلكتروني: {skipped}.",
    'rel.insertion.title': "استطلاع الإدماج",
    'rel.insertion.sub': "أخبرنا بوضعيتك منذ تخرجك",
    'rel.insertion.hello': "مرحبا {name}، أخبرنا بوضعيتك منذ تخرجك",
    'rel.insertion.invalid': "هذا الرابط غير صالح. تحقق من أنه كامل أو اطلب رابطا جديدا من مؤسستك.",
    'rel.insertion.expired': "انتهت صلاحية هذا الرابط. اطلب رابطا جديدا من مؤسستك.",
    'rel.insertion.last': "آخر وضعية معروفة: {situation} ({date})",
    'rel.insertion.already': "لقد أجبت من قبل: إجابة جديدة تحدث وضعيتك.",
    'rel.insertion.question': "وضعيتك اليوم",
    'rel.insertion.status.employed': "أشغل منصبا",
    'rel.insertion.status.studying': "أتابع دراستي",
    'rel.insertion.status.seeking': "أبحث عن عمل",
    'rel.insertion.status.other': "وضعية أخرى",
    'rel.insertion.employer': "المشغل",
    'rel.insertion.school': "المؤسسة",
    'rel.insertion.since': "منذ",
    'rel.insertion.submit': "إرسال إجابتي",
    'rel.insertion.thanks': "شكرا: تم تسجيل وضعيتك.",
    'rel.insertion.privacy': "لا تطلع على إجابتك إلا مؤسستك، لتتبع إدماج خريجيها.",
    'rel.list.types': "أنواع الهيئات",
    'rel.list.sectors': "قطاعات النشاط",
    'rel.list.agreementTypes': "أنواع الاتفاقيات",
    'rel.list.functions': "وظائف جهات الاتصال",
    'rel.list.stages': "مراحل الاستقطاب",
    'rel.list.stagesSub': "بترتيب اللوحة؛ المرحلة الناجحة تقترح الاتفاقية",
    'rel.list.won': "ناجحة",
    'rel.list.lost': "متخلى عنها",
    'rel.list.open': "مرحلة جارية",
    'rel.list.wonHint': "مرحلة ناجحة (شريك)",
    'rel.list.lostHint': "مرحلة متخلى عنها",
    'rel.settings.title': "العلاقات الخارجية · الإعدادات",
    'rel.settings.sub': "قوائم الوحدة وتذكيراتها",
    'rel.settings.saved': "تم حفظ الإعدادات.",
    'rel.settings.listSaved': "تم تحديث القائمة.",
    'rel.settings.empty': "قائمة فارغة.",
    'rel.settings.add': "إضافة · {list}",
    'rel.settings.edit': "تعديل · {label}",
    'rel.settings.label': "التسمية",
    'rel.settings.position': "الترتيب",
    'rel.settings.outcome': "نوع المرحلة",
    'rel.settings.active': "مقترح في القوائم",
    'rel.settings.inactive': "مسحوب",
    'rel.settings.used': "مستعمل: يسحب بدل أن يحذف",
    'rel.settings.remove': "حذف العنصر",
    'rel.settings.module': "وحدة العلاقات الخارجية",
    'rel.settings.moduleSub': "خاص بالمدير العام للنظام",
    'rel.settings.enabled': "الوحدة مفعلة",
    'rel.settings.enabledHint': "عند تعطيلها، تختفي الوحدة من القائمة دون حذف أي شيء.",
    'rel.settings.enabledOn': "فعلت بتاريخ {date} من طرف {who}",
    'rel.settings.reminders': "التذكيرات والولوج",
    'rel.settings.window': "التذكير قبل نهاية الاتفاقية (بالأيام)",
    'rel.settings.windowHint': "تنبيه للمسؤول وقائمة «اتفاقيات قاربت نهايتها»",
    'rel.settings.idle': "ينبه على المسار دون تواصل بعد (بالأيام)",
    'rel.settings.idleHint': "تنبيه لمسؤول المسار",
    'rel.settings.surveyAuto': "إرسال استطلاع الإدماج بعد 6 أشهر من التخرج",
    'rel.settings.teachersRead': "فتح دليل الشركاء للأساتذة (قراءة فقط)",
    'rel.settings.remindersHint': "تصل التنبيهات إلى الجرس عندما يكون التواصل مفعلا.",
    'rel.modules.title': "الوحدات",
    'rel.modules.sub': "تفعيل أو تعطيل الوحدات الاختيارية للمؤسسة · خاص بالمدير العام للنظام",
    'rel.modules.finance': "المالية",
    'rel.modules.financeHint': "الفوترة والتحصيل وحسابات الطلبة",
    'rel.modules.payroll': "أجور الأساتذة",
    'rel.modules.payrollHint': "أجور الأساتذة، أجرة الشهر",
    'rel.modules.communication': "التواصل",
    'rel.modules.communicationHint': "مراسلة الموظفين وإشعارات الطلبة والتنبيهات",
    'rel.modules.relations': "العلاقات الخارجية",
    'rel.modules.relationsHint': "الهيئات والاتفاقيات والاستقطاب والخريجون",
    'rel.modules.enabledOn': "فعل بتاريخ {date} · {who}",
    'rel.modules.disabled': "معطل",
    'rel.modules.settings': "الإعدادات",
    'rel.modules.switch': "تفعيل وحدة {name}",
    'rel.modules.switchedOn': "تم تفعيل وحدة {name}.",
    'rel.modules.switchedOff': "تم تعطيل وحدة {name}.",
    'rel.modules.offTitle': "تعطيل · {name}",
    'rel.modules.offText': "تسحب صلاحياتها من القائمة والشاشات دون حذف أي شيء؛ وإعادة تفعيلها ترجع كل شيء كما كان.",
    'rel.modules.off': "تعطيل",
    'rel.modules.note': "تعطيل وحدة يسحب صلاحياتها من القائمة والشاشات دون حذف أي شيء؛ وإعادة تفعيلها ترجع كل شيء. صار التواصل أيضا قابلا للتعطيل.",
    'rel.modules.financeTitle': "المالية · الإعدادات",
    'rel.modules.back': "الوحدات",
    'violation.rel.inactive': "وحدة العلاقات الخارجية غير مفعلة.",
    'violation.rel.not_found': "غير موجود.",
    'violation.rel.duplicate': "هذه التسمية موجودة في القائمة.",
    'violation.rel.in_use': "مستعمل: اسحبه بدل حذفه.",
    'violation.rel.dates_order': "يجب أن تكون النهاية بعد البداية.",
    'violation.rel.not_draft': "لا يمكن القيام بهذا إلا لمسودة.",
    'violation.rel.not_signed': "الاتفاقية غير سارية.",
    'violation.rel.no_end': "لا تجدد اتفاقية دون نهاية: عدلها.",
    'violation.rel.already_renewed': "تم تجديد هذه الاتفاقية من قبل.",
    'violation.rel.document_invalid': "الملف ليس بصيغة PDF.",
    'violation.rel.document_too_large': "يتجاوز الملف 10 ميغابايت.",
    'violation.rel.organisation_required': "اختر هيئة أو سم هيئة جديدة.",
    'violation.rel.already_prospect': "لهذه الهيئة مسار مفتوح بالفعل.",
    'violation.rel.prospect_closed': "هذا المسار مغلق.",
    'violation.rel.not_alumnus': "هذا الطالب ليس خريجا.",
    'violation.rel.no_recipient': "لا يوجد خريج للاستطلاع.",
    'violation.rel.token_invalid': "هذا الرابط غير صالح.",
    'violation.rel.token_expired': "انتهت صلاحية هذا الرابط.",
    'violation.rel.website_invalid': "عنوان الموقع غير صالح.",
    'violation.rel.reason_required': "اذكر السبب.",
    'violation.rel.stage_in_use': "توجد مسارات في هذه المرحلة: انقلها أولا.",
    'violation.rel.last_stage': "يلزم وجود مرحلة استقطاب واحدة على الأقل.",
    'violation.rel.out_of_range': "قيمة خارج الحدود المسموح بها.",
    'violation.rel.unknown_list': "قائمة غير معروفة.",
    'violation.rel.unknown_account': "حساب غير معروف.",
    'violation.rel.contact_other_organisation': "جهة الاتصال هذه تابعة لهيئة أخرى.",
    'brd.title': "هوية المؤسسة",
    'brd.sub': "الشعار والرمز والاسم الظاهرة في الشاشات والرسائل والوثائق",
    'brd.history': "السجل",
    'brd.files.title': "الشعار والرمز",
    'brd.files.sub': "PNG أو JPEG أو WebP أو SVG · 2 ميغابايت على الأكثر",
    'brd.logo': "الشعار الكامل",
    'brd.emblem': "الرمز",
    'brd.deployed': "ملف النشر: لم يودع أي ملف هنا بعد",
    'brd.meta': "{format} · {width} × {height} بكسل · {weight}",
    'brd.logo.uses': "القائمة المفتوحة، صفحة تسجيل الدخول، ملفات PDF، الرسائل",
    'brd.emblem.uses': "القائمة المطوية، تبويب المتصفح، التطبيق المحمول",
    'brd.replace': "استبدال",
    'brd.download': "تحميل",
    'brd.names.title': "اسم المؤسسة",
    'brd.names.sub': "الاسم بالفرنسية هو أيضا اسم الوثائق المنتجة",
    'brd.names.fr': "الاسم الرسمي (بالفرنسية)",
    'brd.names.en': "الاسم الرسمي (بالإنجليزية)",
    'brd.names.ar': "الاسم الرسمي (بالعربية)",
    'brd.names.code': "المختصر",
    'brd.names.codeHint': "محدد عند التثبيت: يدخل في ترقيم الفواتير والإيصالات وكشوف الأجور. يحل محل الرمز إن لم يوجد.",
    'brd.names.saved': "تم حفظ اسم المؤسسة.",
    'brd.preview.title': "معاينة، حيثما يظهر",
    'brd.preview.sub': "بالملفات السارية",
    'brd.preview.menu': "القائمة المفتوحة والمطوية",
    'brd.preview.login': "صفحة تسجيل الدخول",
    'brd.preview.signIn': "تسجيل الدخول",
    'brd.preview.pdf': "رأس ملفات PDF",
    'brd.preview.mail': "الرسائل",
    'brd.preview.tab': "تبويب المتصفح",
    'brd.preview.tabText': "الاستقبال · Kelassi",
    'brd.note': "يسري التغيير في كل مكان فور حفظه: الشاشات وصفحة تسجيل الدخول والرسائل والوثائق المنتجة بعده. تحتفظ كشوف النقط والشهادات المنتجة سابقا بشعار تاريخها؛ كل تغيير مسجل ويمكن استرجاعه من السجل.",
    'brd.upload.titleLogo': "استبدال الشعار الكامل",
    'brd.upload.titleEmblem': "استبدال الرمز",
    'brd.upload.choose': "اختيار ملف",
    'brd.upload.formats': "PNG أو JPEG أو WebP أو SVG، 2 ميغابايت على الأكثر",
    'brd.upload.newFile': "ملف جديد",
    'brd.upload.fileLine': "{name} · {width} × {height} بكسل · {weight}",
    'brd.upload.trim': "قص الهوامش الفارغة",
    'brd.upload.rules': "يرفض ملف SVG يحتوي على برنامج نصي أو صورة أو رابط خارجي، وكذلك ملف يتجاوز 2 ميغابايت. يجب أن يكون الرمز مربعا.",
    'brd.upload.useLogo': "استعمال هذا الشعار",
    'brd.upload.useEmblem': "استعمال هذا الرمز",
    'brd.upload.doneLogo': "الشعار الجديد ساري في كل مكان.",
    'brd.upload.doneEmblem': "الرمز الجديد ساري في كل مكان.",
    'brd.check.title': "التحققات",
    'brd.check.waiting': "اختر ملفا: يتحقق منه Kelassi قبل استعماله.",
    'brd.check.running': "جار التحقق…",
    'brd.check.refused': "ملف مرفوض",
    'brd.check.format': "صيغة مقبولة",
    'brd.check.size': "الحجم",
    'brd.check.sizeText': "{weight}، دون حد 2 ميغابايت",
    'brd.check.dimensions': "الأبعاد",
    'brd.check.dimensionsText': "{width} × {height} بكسل: واضح على الشاشة وعند الطباعة",
    'brd.check.small': "{width} × {height} بكسل: صغير قليلا، ينصح بـ {advised} بكسل على الأقل",
    'brd.check.vector': "صورة متجهة: واضحة بكل الأحجام",
    'brd.check.margins': "هوامش فارغة حول الرسم",
    'brd.check.marginsText': "تجعله يبدو صغيرا في القائمة؛ حدد « قص الهوامش الفارغة ».",
    'brd.check.trimmed': "تقص عند الحفظ: {width} × {height} بكسل",
    'brd.check.noMargins': "الرسم يملأ الصورة",
    'brd.check.transparent': "خلفية شفافة",
    'brd.check.transparentText': "يوضع الشعار على الإطار الأبيض للقائمة وعلى الوثائق",
    'brd.check.opaque': "الخلفية غير شفافة: ستظهر إن لم تكن بيضاء",
    'brd.change.logo': "استبدال الشعار الكامل",
    'brd.change.emblem': "استبدال الرمز",
    'brd.change.names': "تعديل الاسم الرسمي",
    'brd.change.restore': "استرجاع نسخة",
    'brd.hist.title': "سجل الهوية",
    'brd.hist.sub': "كل تغيير للشعار أو الرمز أو الاسم مع صاحبه؛ الاسترجاع يحدث تغييرا جديدا ولا يمحى شيء",
    'brd.hist.empty': "لا تغيير: الهوية هي هوية النشر.",
    'brd.hist.date': "التاريخ",
    'brd.hist.by': "بواسطة",
    'brd.hist.change': "التغيير",
    'brd.hist.preview': "معاينة",
    'brd.hist.actions': "الإجراءات",
    'brd.hist.version': "النسخة {number}",
    'brd.hist.current': "النسخة السارية",
    'brd.hist.restore': "استرجاع",
    'brd.hist.restoredFrom': "نسخة من النسخة {number}",
    'brd.hist.restored': "تم استرجاع النسخة {number}: إنها سارية في كل مكان.",
    'brd.hist.confirmTitle': "استرجاع النسخة {number}؟",
    'brd.hist.confirmText': "يعود شعارها ورمزها واسمها إلى الشاشات والرسائل والوثائق المقبلة. لا تتغير الوثائق المنتجة سابقا.",
    'brd.hist.note': "استرجاع نسخة يعيدها سارية للشاشات والرسائل والوثائق المقبلة. لا تتغير الوثائق المنتجة سابقا: يحتفظ ملف PDF بشعار يوم إنتاجه.",
    'journal.action.branding.changed': "تعديل هوية المؤسسة",
    'journal.target.branding': "المؤسسة",
    'violation.brd.file_empty': "ملف فارغ.",
    'violation.brd.file_too_big': "الملف كبير جدا: 2 ميغابايت على الأكثر.",
    'violation.brd.file_format': "صيغة غير مقبولة: PNG أو JPEG أو WebP أو SVG.",
    'violation.brd.file_unreadable': "صورة غير مقروءة.",
    'violation.brd.not_square': "يجب أن يكون الرمز مربعا.",
    'violation.brd.too_small': "الصورة صغيرة جدا.",
    'violation.brd.svg_unsafe': "ملف SVG مرفوض: يحتوي على برنامج نصي أو صورة أو رابط خارجي.",
    'violation.brd.svg_size': "ملف SVG بلا أبعاد: أضف viewBox.",
    'violation.brd.text': "نص منتظر.",
    'violation.brd.name_required': "الاسم الرسمي إلزامي.",
    'violation.brd.name_too_long': "الاسم طويل جدا: 255 حرفا على الأكثر.",
    'violation.brd.unchanged': "لم يتغير شيء.",
    'violation.brd.already_current': "هذه النسخة سارية بالفعل.",
    // D78 : la gestion des documents (DG-1).
    'doc.docState.valid': "صالحة",
    'doc.docState.superseded': "مستبدلة",
    'doc.docState.cancelled': "ملغاة",
    'doc.docState.expired': "منتهية الصلاحية",
    'doc.verify.cancelled': "وثيقة ملغاة",
    'doc.verify.expired': "وثيقة منتهية الصلاحية",
    'doc.verify.cancelledBody': "ألغتها المؤسسة بتاريخ {date}: لم تعد صالحة.",
    'doc.verify.expiredBody': "كانت صالحة إلى غاية {date}: اطلبوا وثيقة جديدة من المؤسسة.",
    'doc.verify.validUntil': "صالحة إلى غاية {date}.",
    'dg.dashboard.sub': "لوحة القيادة · {year} · كشوف النقط والشهادات والمحاضر",
    'dg.kpi.produced': "صدرت هذه السنة",
    'dg.kpi.producedNote': "منها {count} على البوابة",
    'dg.kpi.todo': "للإصدار",
    'dg.kpi.todoNote': "رصدها Kelassi",
    'dg.kpi.downloads': "التحميلات",
    'dg.kpi.downloadsNote': "من طرف الطلبة على البوابة",
    'dg.kpi.verifications': "التحققات العمومية",
    'dg.kpi.verificationsNote': "بواسطة رمز QR أو المرجع",
    'dg.todo.title': "للإصدار",
    'dg.todo.sub': "ما رصده Kelassi: مداولات مغلقة وتسجيلات",
    'dg.todo.empty': "لا شيء للإصدار حاليا.",
    'dg.todo.enrolment': "تسجيلات السنة",
    'dg.todo.enrolmentNote': "تسجيلات بدون شهادة مدرسية سارية: {count}؛ تسلم عبر الطلبات.",
    'dg.todo.transcriptsNote': "أغلقت بتاريخ {date} · كشوف ناقصة: {count}",
    'dg.batches.title': "آخر الدفعات",
    'dg.batches.all': "كل الدفعات",
    'dg.batches.line': "الوثائق: {count} · {date} · {by}",
    'dg.types.title': "حسب نوع الوثيقة",
    'dg.types.empty': "لم تصدر أي وثيقة هذه السنة.",
    'dg.types.cancelled': "ملغاة: {count}",
    'dg.col.produced': "الصادرة",
    'dg.col.published': "على البوابة",
    'dg.col.downloads': "المحملة",
    'dg.col.verifications': "المتحقق منها",
    'dg.col.issuedOn': "تاريخ الإصدار",
    'dg.col.portal': "البوابة",
    'dg.col.verified': "التحقق",
    'dg.verifications.title': "التحققات العمومية",
    'dg.verifications.sub': "بدون حساب، بواسطة رمز QR أو المرجع",
    'dg.verifications.empty': "لا توجد تحققات حديثة.",
    'dg.registry.title': "سجل الوثائق",
    'dg.registry.sub': "كل وثيقة صادرة بمرجعها؛ لا يحذف شيء: تستبدل الوثيقة أو تلغى مع ذكر السبب",
    'dg.registry.search': "المرجع أو الاسم أو رقم الطالب",
    'dg.registry.allTypes': "كل الوثائق",
    'dg.registry.training': "التكوين",
    'dg.registry.allTrainings': "كل التكوينات",
    'dg.registry.allStates': "كل الحالات",
    'dg.registry.empty': "لا توجد وثيقة مطابقة.",
    'dg.registry.published': "منشورة · التحميلات: {count}",
    'dg.registry.notPublished': "غير منشورة",
    'dg.registry.pages': "صفحات السجل",
    'dg.registry.count': "الوثائق: {count} · الصفحة {page} من {pages}",
    'dg.action.open': "فتح البطاقة",
    'dg.action.pdf': "تحميل PDF",
    'dg.action.duplicate': "نسخة مطابقة",
    'dg.action.replace': "استبدال",
    'dg.action.cancel': "إلغاء الوثيقة",
    'dg.notice.replaced': "وثيقة جديدة تحل محل {reference}.",
    'dg.notice.cancelled': "ألغيت {reference}: التحقق منها يبين ذلك الآن.",
    'dg.item.sub': "{type} الخاصة بـ {student}",
    'dg.item.cancelled': "ألغيت بتاريخ {date} من طرف {by}",
    'dg.item.reason': "السبب: {reason}",
    'dg.item.replacedBy': "استبدلت بـ",
    'dg.item.replaces': "هذه الوثيقة تحل محل",
    'dg.item.document': "الوثيقة",
    'dg.item.from': "انطلاقا من",
    'dg.item.issued': "صدرت",
    'dg.item.signatories': "الموقعون",
    'dg.item.validUntil': "صالحة إلى غاية",
    'dg.item.digest': "البصمة",
    'dg.item.diffusion': "النشر",
    'dg.item.notForPortal': "وثيقة داخلية خارج البوابة",
    'dg.item.publishedOn': "نشرت بتاريخ {date} · تحميلات الطالب: {count}",
    'dg.item.duplicates': "النسخ المطابقة",
    'dg.item.duplicatesCount': "المطبوعة: {count}",
    'dg.item.noVerification': "لم يتم التحقق منها بعد.",
    'dg.item.verifyPage': "فتح صفحة التحقق",
    'dg.item.history': "السجل",
    'dg.item.noPreview': "المعاينة غير متاحة: محرك PDF لا يستجيب.",
    'dg.item.note': "يتطلب إلغاء الوثيقة ذكر السبب؛ ويجيب التحقق العمومي حينها بـ «ملغاة» دون ذكر السبب. أما الاستبدال فيصدر وثيقة جديدة ويضع على هذه علامة «مستبدلة».",
    'dg.event.issued': "أصدرها {by}",
    'dg.event.downloaded': "حملها الطالب",
    'dg.event.verified': "تم التحقق: {result}",
    'dg.event.duplicate': "طبع نسخة مطابقة {by}",
    'dg.event.published': "نشرها على البوابة {by}",
    'dg.event.unpublished': "سحبها من البوابة {by}",
    'dg.event.cancelled': "ألغاها {by}: {detail}",
    'dg.event.replaced': "استبدلت بـ {detail} ({by})",
    'dg.replace.title': "استبدال {reference}؟",
    'dg.replace.text': "تصدر وثيقة جديدة بمرجع جديد وبنفس الخيارات؛ وتوضع على هذه علامة «مستبدلة».",
    'dg.cancel.title': "إلغاء {reference}؟",
    'dg.cancel.text': "تبقى الوثيقة الملغاة ملغاة: يجيب التحقق بـ «ملغاة»، وتسحب من البوابة ولا تحمل بعد ذلك. يبقى السبب في السجل ولا يعرض علنيا أبدا.",
    'dg.cancel.reason': "سبب الإلغاء",
    'dg.settings.title': "النشر والتحقق",
    'dg.settings.sub': "تسري على الوثائق الصادرة لاحقا",
    'dg.settings.publishDefault': "النشر على البوابة افتراضيا",
    'dg.settings.publishDefaultHint': "كل إصدار يقترح الخانة مؤشرا عليها",
    'dg.settings.validity': "صلاحية الشهادات المدرسية",
    'dg.settings.validityHint': "تطبع «صالحة إلى غاية»؛ وبعدها يجيب التحقق بـ «منتهية الصلاحية».",
    'dg.settings.noLimit': "بدون حد",
    'dg.settings.months': "{count} أشهر",
    'dg.settings.oneMonth': "شهر واحد",
    'dg.settings.shortName': "الاسم المختصر في صفحة التحقق",
    'dg.settings.shortNameHint': "الاسم الشخصي والحرف الأول من الاسم العائلي",
    'dg.settings.duplicateMark': "علامة «نسخة مطابقة» على إعادة الطبع",
    'dg.settings.duplicateMarkHint': "لا يحملها تحميل الطالب",
    'rel.modules.documents': "الوثائق",
    'rel.modules.documentsHint': "كشوف النقط والشهادات والمحاضر والسجل والتحقق العمومي",
    'rel.modules.documentsOff': "لا تصدر أي وثيقة بعد ذلك، ولا تسلم الطلبات الوثائق تلقائيا، وتختفي «وثائقي» من البوابة؛ ويستمر التحقق من الوثائق الصادرة سابقا.",
    'violation.doc.reason_required': "اذكروا سبب الإلغاء (5 أحرف على الأقل).",
    'violation.doc.already_cancelled': "هذه الوثيقة ملغاة سلفا.",
    'violation.doc.not_replaceable': "لا تستبدل إلا وثيقة صالحة أو منتهية الصلاحية.",
    'violation.doc.run_changed': "تغيرت النتائج منذ ذلك الحين: أصدروا الوثيقة انطلاقا من المداولة.",
    'violation.doc.documents_off': "وحدة الوثائق معطلة: عالجوا هذا الطلب يدويا.",
    'journal.action.document.cancelled': "إلغاء وثيقة",
    'journal.action.document.replaced': "استبدال وثيقة",
    // D79 : les modèles de documents (DG-2).
    'tpl.title': "نماذج الوثائق",
    'tpl.sub': "النماذج التي يملؤها Kelassi: نصوص وحقول وجداول وتوقيعات ورمز QR؛ نسخة لكل نشر",
    'tpl.byTraining': "النماذج حسب التكوين",
    'tpl.duplicate': "نسخ نموذج",
    'tpl.new': "نموذج جديد",
    'tpl.create': "إنشاء النموذج",
    'tpl.all': "الكل · {count}",
    'tpl.search': "البحث عن نموذج",
    'tpl.showArchived': "إظهار المؤرشفة",
    'tpl.empty': "لا يوجد نموذج مطابق.",
    'tpl.note': "النموذج المنشور لا يتغير: تعديله يحضر نسخته الموالية التي تنشر عند جاهزيتها. وتحتفظ الوثيقة الصادرة بنسخة يومها. تنسخ النماذج القياسية لـ Kelassi، وتسترجع نماذج Kelassi القديم برموزها.",
    'tpl.allTrainings': "الكل",
    'tpl.verifiable': "قابل للتحقق برمز QR",
    'tpl.invalid': "بعض الكتل تحتاج إلى تصحيح.",
    'tpl.col.template': "النموذج",
    'tpl.col.category': "الفئة",
    'tpl.col.source': "ينتج",
    'tpl.col.languages': "اللغات",
    'tpl.col.paper': "الورق",
    'tpl.col.qr': "QR",
    'tpl.col.version': "النسخة",
    'tpl.col.trainings': "التكوينات",
    'tpl.badge.default': "افتراضي",
    'tpl.badge.system': "قياسي",
    'tpl.badge.archived': "مؤرشف",
    'tpl.version.published': "ن{number} منشورة",
    'tpl.version.draft': "ن{number} مسودة",
    'tpl.field.name': "اسم النموذج",
    'tpl.field.nameCopyHint': "فارغ: اسم النموذج المنسوخ",
    'tpl.field.from': "نسخ النموذج",
    'tpl.field.kind': "ما ينتجه",
    'tpl.field.category': "الفئة",
    'tpl.field.paper': "الورق",
    'tpl.field.languages': "اللغات",
    'tpl.field.verifiable': "رمز QR ومرجع التحقق",
    'tpl.field.validity': "الصلاحية",
    'tpl.field.duplicateMark': "علامة «نسخة مطابقة» عند إعادة الطبع",
    'tpl.field.signatureRequired': "التوقيع مطلوب قبل النشر",
    'tpl.field.signatureRequiredHint': "ستنتظر الدفعة الموقع في «للتوقيع»",
    'tpl.field.font': "الخط",
    'tpl.field.fontSize': "حجم النص",
    'tpl.field.margins': "الهوامش",
    'tpl.font.sans': "بدون تذييل",
    'tpl.font.serif': "بتذييل (Times)",
    'tpl.validity.inherit': "كما في المؤسسة",
    'tpl.size.small': "صغير",
    'tpl.size.normal': "عادي",
    'tpl.size.large': "كبير",
    'tpl.margins.narrow': "ضيقة",
    'tpl.margins.normal': "عادية",
    'tpl.margins.wide': "واسعة",
    'tpl.align.start': "إلى البداية",
    'tpl.align.center': "في الوسط",
    'tpl.align.end': "إلى النهاية",
    'tpl.align.justify': "مضبوط",
    'tpl.align.spread': "موزعة على العرض",
    'tpl.layout.list': "لائحة متصلة",
    'tpl.layout.modules': "جدول لكل وحدة",
    'tpl.layout.table': "جدول",
    'tpl.layout.grid': "شبكة صور",
    'tpl.summary.facts': "في أسطر",
    'tpl.summary.boxes': "في ثلاثة إطارات",
    'tpl.summary.none': "بدون",
    'tpl.kind.transcript': "كشف النقط",
    'tpl.kind.certificate': "شهادة النجاح",
    'tpl.kind.minutes': "المحضر",
    'tpl.kind.enrolment': "الشهادة المدرسية",
    'tpl.kind.card': "بطاقة الطالب",
    'tpl.kind.list': "لائحة قسم",
    'tpl.kind.internship': "وثيقة تدريب",
    'tpl.category.schooling': "التمدرس",
    'tpl.category.results': "النتائج",
    'tpl.category.lists': "اللوائح والبيانات",
    'tpl.category.finance': "المالية",
    'tpl.category.stages': "التداريب",
    'tpl.category.teachers': "الأساتذة",
    'tpl.paper.A4': "A4",
    'tpl.paper.A4-landscape': "A4 أفقي",
    'tpl.paper.A5': "A5",
    'tpl.paper.CR80': "بطاقة CR80",
    'tpl.lang.fr': "الفرنسية",
    'tpl.lang.en': "الإنجليزية",
    'tpl.lang.ar': "العربية",
    'tpl.action.versions': "النسخ",
    'tpl.action.pdf': "معاينة PDF",
    'tpl.action.publish': "نشر النسخة {number}",
    'tpl.action.duplicate': "نسخ للتعديل",
    'tpl.action.makeDefault': "جعله النموذج الافتراضي",
    'tpl.action.archive': "أرشفة هذا النموذج",
    'tpl.action.unarchive': "استرجاع هذا النموذج",
    'tpl.notice.saved': "تم حفظ المسودة.",
    'tpl.notice.published': "نشرت النسخة {number}: تعتمدها الوثائق الصادرة لاحقا.",
    'tpl.editor.systemSub': "نموذج Kelassi القياسي · الصفحة كما تطبع",
    'tpl.editor.systemNote': "لا يعدل النموذج القياسي. انسخوه: تتلقى النسخة كتلا مماثلة لصفحته يمكنكم تكييفها.",
    'tpl.editor.firstDraft': "النسخة {number}، مسودة · لم تنشر بعد",
    'tpl.editor.draftSub': "النسخة {number}، مسودة · تبقى النسخة {current} سارية حتى تنشر هذه",
    'tpl.editor.currentSub': "النسخة {number} سارية · التعديل يحضر الموالية",
    'tpl.editor.unsaved': "تعديلات غير محفوظة",
    'tpl.editor.page': "الصفحة",
    'tpl.editor.pageHint': "اسحب لإعادة الترتيب وانقر للضبط",
    'tpl.editor.addBlock': "إضافة كتلة",
    'tpl.editor.up': "إلى الأعلى",
    'tpl.editor.down': "إلى الأسفل",
    'tpl.editor.copyBlock': "نسخ الكتلة",
    'tpl.editor.fields': "الحقول",
    'tpl.editor.fieldsHint': "انقر للإدراج في النص الحالي",
    'tpl.editor.previewWith': "معاينة مع",
    'tpl.editor.searchSubject': "بحث…",
    'tpl.editor.subject': "موضوع المعاينة",
    'tpl.editor.noSubject': "بدون بيانات",
    'tpl.editor.selected': "الكتلة المختارة: {block}",
    'tpl.editor.document': "الوثيقة",
    'tpl.editor.usage': "الاستعمال",
    'tpl.editor.code': "الرمز",
    'tpl.editor.legacy': "Kelassi القديم",
    'tpl.editor.defaultFor': "كل التكوينات بدون نموذج مسند (افتراضي)",
    'tpl.editor.unused': "لا شيء حاليا",
    'tpl.publish.title': "نشر النسخة {number}؟",
    'tpl.publish.text': "ستعتمد الوثائق الصادرة لاحقا هذه النسخة، وتحتفظ الوثائق الصادرة سابقا بنسختها.",
    'tpl.publish.note': "ما الذي يتغير (اختياري)",
    'tpl.versions.title': "نسخ النموذج",
    'tpl.versions.number': "النسخة {number}",
    'tpl.versions.draft': "مسودة",
    'tpl.versions.current': "سارية",
    'tpl.versions.created': "بدأت في {date} من طرف {by}",
    'tpl.versions.published': "نشرت في {date} من طرف {by}",
    'tpl.versions.restore': "الانطلاق من هذه النسخة",
    'tpl.versions.discard': "التخلي عن المسودة",
    'tpl.versions.discardTitle': "التخلي عن المسودة؟",
    'tpl.versions.discardText': "تضيع تعديلاتها؛ ولا تتغير النسخة السارية.",
    'tpl.block.header': "رأسية المؤسسة",
    'tpl.block.title': "العنوان",
    'tpl.block.text': "نص",
    'tpl.block.facts': "أسطر «تسمية: قيمة»",
    'tpl.block.grades': "جدول النقط",
    'tpl.block.result': "النتيجة",
    'tpl.block.minutes': "جدول المحضر",
    'tpl.block.students': "طلبة القسم",
    'tpl.block.table': "جدول حر",
    'tpl.block.photo': "صورة الطالب",
    'tpl.block.signatures': "التوقيعات",
    'tpl.block.stamp': "الخاتم",
    'tpl.block.qr': "رمز QR والمرجع",
    'tpl.block.footer': "تذييل الصفحة",
    'tpl.block.spacer': "فراغ",
    'tpl.block.pagebreak': "فاصل صفحة",
    'tpl.block.system': "الصفحة القياسية",
    'tpl.if.always': "دائما",
    'tpl.if.normal': "في الدورة العادية",
    'tpl.if.retake': "بعد الاستدراك",
    'tpl.if.credits': "إذا كان التسجيل بالأرصدة",
    'tpl.if.scholarship': "إذا كان الطالب ممنوحا",
    'tpl.if.female': "لطالبة",
    'tpl.if.male': "لطالب",
    'tpl.prop.tagline': "مع العبارة تحت الاسم",
    'tpl.prop.align': "المحاذاة",
    'tpl.prop.title': "العنوان",
    'tpl.prop.sub': "أسطر تحت العنوان",
    'tpl.prop.size': "الحجم",
    'tpl.prop.text': "النص",
    'tpl.prop.format': "التنسيق",
    'tpl.prop.validity': "مع تاريخ الصلاحية",
    'tpl.prop.layout': "العرض",
    'tpl.prop.summary': "النتيجة",
    'tpl.prop.details': "تفاصيل المواد",
    'tpl.prop.coefficients': "المعاملات",
    'tpl.prop.credits': "الأرصدة",
    'tpl.prop.sessions': "دورة كل نقطة",
    'tpl.prop.legend': "شرح الحالات",
    'tpl.prop.condensed': "مختصر (خط صغير)",
    'tpl.prop.minutesDetails': "عمود لكل سداسي أو وحدة",
    'tpl.prop.counts': "الأعداد",
    'tpl.prop.bonuses': "إضافات اللجنة",
    'tpl.prop.columns': "الأعمدة",
    'tpl.prop.blank': "أسطر فارغة في النهاية",
    'tpl.prop.factColumns': "الأعمدة",
    'tpl.prop.label': "التسمية",
    'tpl.prop.value': "القيمة (فارغة: نقط للملء)",
    'tpl.prop.addLine': "إضافة سطر",
    'tpl.prop.border': "بحدود",
    'tpl.prop.column': "العمود {number}",
    'tpl.prop.addColumn': "إضافة عمود",
    'tpl.prop.rows': "الأسطر",
    'tpl.prop.cell': "السطر {row}، العمود {column}",
    'tpl.prop.addRow': "إضافة سطر",
    'tpl.prop.made': "مع «حرر بـ …، في …»",
    'tpl.prop.parties': "من يوقع",
    'tpl.prop.partiesHint': "فارغ: الموقعون المحددون لهذه الوثيقة في الإعدادات.",
    'tpl.prop.party': "الطرف {number}",
    'tpl.prop.addParty': "إضافة طرف",
    'tpl.prop.qrLine': "مع عبارة «قابل للتحقق على الخط»",
    'tpl.prop.height': "الارتفاع (px)",
    'tpl.prop.pagebreak': "يبدأ ما يلي في صفحة جديدة.",
    'tpl.prop.if': "الإظهار",
    'tpl.format.bold': "غامق",
    'tpl.format.italic': "مائل",
    'tpl.format.list': "لائحة",
    'tpl.column.number': "الرقم",
    'tpl.column.name': "الاسم الكامل",
    'tpl.column.birth': "تاريخ الازدياد",
    'tpl.column.cin': "البطاقة أو الجواز",
    'tpl.column.email': "البريد الإلكتروني",
    'tpl.column.phone': "الهاتف",
    'tpl.column.signature': "التوقيع",
    'tpl.column.photo': "الصورة",
    'tpl.group.etudiant': "الطالب",
    'tpl.group.inscription': "التسجيل",
    'tpl.group.resultats': "النتائج",
    'tpl.group.classe': "القسم",
    'tpl.group.stage': "التدريب",
    'tpl.group.document': "الوثيقة",
    'tpl.assign.sub': "{year} · النموذج الذي يستعمله كل تكوين والعبارات المطبوعة حسب الدورة",
    'tpl.assign.copy': "استرجاع السنة الماضية",
    'tpl.assign.copyTitle': "استرجاع إسنادات {year}؟",
    'tpl.assign.copyText': "كل تكوين من {year} يحمل نفس رمز تكوين السنة الماضية، وليس له نموذج لوثيقة بعد، يتلقى نموذجها.",
    'tpl.assign.copied': "الإسنادات المسترجعة: {count}.",
    'tpl.assign.empty': "لا يوجد تكوين هذه السنة.",
    'tpl.assign.byDefault': "افتراضي",
    'tpl.assign.standard': "الصفحة القياسية",
    'tpl.assign.note': "«افتراضي»: نموذج المؤسسة لهذه الوثيقة. يحتفظ التكوين المجدد بإسناداته؛ و«استرجاع السنة الماضية» ينسخها كلها.",
    'tpl.assign.fallback': "بدون نموذج مسند، يستعمل التكوين النموذج الافتراضي: {name}.",
    'tpl.assign.remove': "سحب هذا النموذج",
    'tpl.assign.add': "إضافة نموذج آخر",
    'tpl.assign.template': "النموذج",
    'tpl.assign.choose': "اختر…",
    'tpl.assign.isDefault': "مقترح افتراضيا عند الإصدار",
    'tpl.assign.titleNormal': "العبارة في الدورة العادية",
    'tpl.assign.titleHint': "تطبع بالحقل {{document.titre}}، مثلا «يونيو 2026»",
    'tpl.assign.titleRetake': "العبارة بعد الاستدراك",
    'tpl.assign.titleCredit': "عبارة التسجيل بالأرصدة",
    'tpl.assign.titleShared': "العنوان المشترك",
    'tpl.assign.titleSharedHint': "يطبع بـ {{document.titre_commun}}، مثلا «السنة الرابعة IFACG»",
    'tpl.assign.saved': "حفظت نماذج {training}.",
    'tpl.assign.removed': "سحب النموذج من {training}.",
    'journal.action.document.template_created': "إنشاء نموذج وثيقة",
    'journal.action.document.template_changed': "تعديل نموذج وثيقة",
    'journal.action.document.template_published': "نشر نموذج وثيقة",
    'journal.action.document.template_assigned': "تعديل نماذج تكوين",
    'violation.tpl.invalid_block': "هذه الكتلة ناقصة أو غير معروفة.",
    'violation.tpl.too_many': "تضم الصفحة 60 كتلة على الأكثر.",
    'violation.tpl.block_source': "ليس لهذه الكتلة ما تعرضه لهذه الوثيقة.",
    'violation.tpl.unknown_field': "حقل غير موجود لهذه الوثيقة: استعملوا لائحة الحقول.",
    'violation.tpl.too_long': "هذا النص طويل جدا.",
    'violation.tpl.validity': "بين 1 و24 شهرا، أو بدون حد، أو كما في المؤسسة.",
    'violation.tpl.kind': "اختاروا ما ينتجه النموذج.",
    'violation.tpl.name': "أعطوا اسما للنموذج.",
    'violation.tpl.languages': "لغة واحدة على الأقل.",
    'violation.tpl.system': "لا يعدل النموذج القياسي: انسخوه.",
    'violation.tpl.no_draft': "لا توجد مسودة.",
    'violation.tpl.only_draft': "لهذا النموذج هذه المسودة فقط: أرشفوه بدلا من ذلك.",
    'violation.tpl.version': "نسخة غير موجودة.",
    'violation.tpl.not_usable': "لا يستعمل نموذج مؤرشف أو غير منشور.",
    'violation.tpl.not_archivable': "لا يؤرشف النموذج الافتراضي أو القياسي.",
    'violation.tpl.wrong_kind': "هذا النموذج لا ينتج هذه الوثيقة.",
    'violation.tpl.unpublished': "ليست لهذا النموذج نسخة منشورة بعد.",
    'violation.tpl.subject': "اختاروا موضوع المعاينة.",
    'violation.tpl.no_previous_year': "لا توجد سنة سابقة.",
    'tpl.field.etudiant.nom_complet': "الاسم الكامل",
    'tpl.field.etudiant.nom': "الاسم العائلي",
    'tpl.field.etudiant.prenom': "الاسم الشخصي",
    'tpl.field.etudiant.civilite': "اللقب (السيد، السيدة)",
    'tpl.field.etudiant.e': "تاء التأنيث",
    'tpl.field.etudiant.numero': "رقم الطالب",
    'tpl.field.etudiant.date_naissance': "تاريخ الازدياد",
    'tpl.field.etudiant.cin': "رقم البطاقة",
    'tpl.field.etudiant.passeport': "جواز السفر",
    'tpl.field.etudiant.code_massar': "رمز مسار أو الرمز الوطني",
    'tpl.field.etudiant.email': "البريد الإلكتروني",
    'tpl.field.etudiant.telephone': "الهاتف",
    'tpl.field.inscription.formation': "التكوين",
    'tpl.field.inscription.code_formation': "رمز التكوين",
    'tpl.field.inscription.parcours': "المسار",
    'tpl.field.inscription.annee': "السنة الجامعية",
    'tpl.field.inscription.date': "تاريخ التسجيل",
    'tpl.field.inscription.classe': "القسم",
    'tpl.field.inscription.regime': "النظام",
    'tpl.field.inscription.boursier': "ممنوح",
    'tpl.field.resultats.session': "الدورة",
    'tpl.field.resultats.moyenne': "المعدل",
    'tpl.field.resultats.decision': "القرار",
    'tpl.field.resultats.mention': "الميزة",
    'tpl.field.resultats.credits': "الأرصدة المحصلة",
    'tpl.field.resultats.rang': "الرتبة",
    'tpl.field.resultats.date_jury': "تاريخ اللجنة",
    'tpl.field.classe.nom': "القسم",
    'tpl.field.classe.formation': "التكوين",
    'tpl.field.classe.code_formation': "رمز التكوين",
    'tpl.field.classe.annee': "السنة الجامعية",
    'tpl.field.classe.effectif': "العدد",
    'tpl.field.classe.delegue': "المندوب",
    'tpl.field.stage.type': "نوع التدريب",
    'tpl.field.stage.organisme': "الجهة المستقبلة",
    'tpl.field.stage.tuteur': "المؤطر المهني",
    'tpl.field.stage.fonction_tuteur': "وظيفة المؤطر",
    'tpl.field.stage.encadrant': "المؤطر البيداغوجي",
    'tpl.field.stage.debut': "البداية",
    'tpl.field.stage.fin': "النهاية",
    'tpl.field.stage.duree_mois': "المدة بالأشهر",
    'tpl.field.stage.sujet': "الموضوع",
    'tpl.field.stage.missions': "المهام",
    'tpl.field.stage.lieu': "المكان",
    'tpl.field.document.reference': "المرجع",
    'tpl.field.document.date': "تاريخ الإصدار",
    'tpl.field.document.lieu': "مكان التوقيع",
    'tpl.field.document.etablissement': "المؤسسة",
    'tpl.field.document.valable_jusqu_au': "صالحة إلى غاية",
    'tpl.field.document.titre': "عبارة الدورة (حسب التكوين)",
    'tpl.field.document.titre_commun': "العنوان المشترك (حسب التكوين)",
    'doc.docState.unsigned': "في انتظار التوقيع",
    'doc.type.card': "بطاقة الطالب",
    'doc.types.card': "بطاقات الطلبة",
    'doc.type.list': "لائحة قسم",
    'doc.types.list': "لوائح الأقسام",
    'doc.type.internship': "وثيقة تدريب",
    'doc.types.internship': "وثائق التدريب",
    'doc.verify.unsigned': "وثيقة لم توقع بعد",
    'doc.verify.unsignedBody': "تنتظر توقيع المؤسسة: ليست صالحة بعد.",
    'doc.verify.signed': "وقعت بتاريخ {date} من طرف {name}.",
    'dg.event.signed': "وقعها {by}",
    'dg.event.returned': "أرجعها {by}: {detail}",
    'dg.registry.batch': "وثائق دفعة",
    'dg.registry.batchClear': "السجل كاملا",
    'journal.action.document.job_started': "انطلاق إصدار الوثائق",
    'journal.action.document.signer_changed': "ضبط الموقع",
    'journal.action.document.signed': "توقيع الوثائق",
    'journal.action.document.returned': "دفعة أرجعها الموقع",
    'prd.title': "إصدار الوثائق",
    'prd.sub': "وثيقة ومصدر ومستفيدون: يتحقق Kelassi من كل شيء قبل الإصدار",
    'prd.jobs': "الإصدار",
    'prd.step.1': "الوثيقة",
    'prd.step.2': "انطلاقا من",
    'prd.step.3': "لفائدة",
    'prd.step.4': "التحقق والإصدار",
    'prd.next': "متابعة",
    'prd.back': "رجوع",
    'prd.edit': "تعديل",
    'prd.kind.title': "أي وثيقة؟",
    'prd.kind.template': "النموذج",
    'prd.kind.byTraining': "نموذج كل تكوين",
    'prd.kind.templateHint': "بدون اختيار، يأخذ Kelassi النموذج المسند إلى التكوين، وإلا فالنموذج الافتراضي.",
    'prd.from.deliberation': "انطلاقا من مداولة مغلقة",
    'prd.from.enrolment': "انطلاقا من التسجيلات",
    'prd.from.class': "انطلاقا من الأقسام",
    'prd.from.internship': "انطلاقا من التداريب",
    'prd.source.deliberation': "أي مداولة مغلقة؟",
    'prd.source.training': "أي تكوين؟",
    'prd.source.noDeliberation': "لا توجد مداولة مغلقة هذه السنة في تكويناتكم.",
    'prd.source.noTraining': "لا يوجد تكوين يمكنكم الإصدار فيه هذه السنة.",
    'prd.source.run': "الحساب رقم {number}، أغلق بتاريخ {date}",
    'prd.for.title': "لفائدة من؟",
    'prd.for.count': "{chosen} مختارون من أصل {total}",
    'prd.for.all': "الكل",
    'prd.for.none': "لا أحد",
    'prd.for.empty': "لا أحد لإصدار هذه الوثيقة له.",
    'prd.warning.no_photo': "بدون صورة",
    'prd.warning.no_number': "رقم ناقص",
    'prd.warning.existing': "موجودة سلفا",
    'prd.warningHint.no_photo': "النموذج يطبع الصورة: تصدر بإطار فارغ أو تستثنى",
    'prd.warningHint.no_number': "تصدر بدون رقم الطالب أو يستكمل أولا",
    'prd.warningHint.existing': "تعاد الوثيقة نفسها بدون مرجع جديد",
    'prd.summary.template': "النموذج «{name}»، ن{version}",
    'prd.summary.for': "{count} مستفيدون · {excluded} مستثنون",
    'prd.options': "الخيارات",
    'prd.lang': "اللغة",
    'prd.publish': "النشر على البوابة",
    'prd.publishHint': "يحمل كل واحد وثيقته في «وثائقي»",
    'prd.publishAfterSign': "عند التوقيع: لا شيء يخرج قبل ذلك",
    'prd.notify': "إشعار كل واحد",
    'prd.notifyHint': "عبر الجرس والبريد الإلكتروني، مع التواصل",
    'prd.sign': "التوقيع قبل النشر",
    'prd.signHint': "تذهب الدفعة إلى «للتوقيع» لدى الموقع المختار",
    'prd.signForced': "يشترطه النموذج: تذهب الدفعة إلى «للتوقيع»",
    'prd.signer': "الموقع",
    'prd.noSigner': "لم يضبط أي موقع: يقوم بذلك في «للتوقيع» مع بيان صفته.",
    'prd.checks': "التحققات",
    'prd.checks.ready': "{count} جاهزة",
    'prd.checks.readyDelib': "مداولة مغلقة وحساب مثبت",
    'prd.checks.readyTraining': "التسجيلات النشطة في التكوين",
    'prd.checks.queue': "تمر الدفعة إلى قائمة الانتظار",
    'prd.checks.queueHint': "أكثر من {count} وثيقة: يصدر Kelassi في الخلفية وينبه عند الانتهاء",
    'prd.start': "إصدار {count} · {what}",
    'prd.preview': "معاينة",
    'prd.previewOf': "{name} · {index} من {total}",
    'prd.previous': "السابق",
    'prd.following': "التالي",
    'prd.previewNote': "مشطوب «معاينة» ما دام لم يصدر شيء",
    'prd.kinds.transcript': "كشوف النقط",
    'prd.kinds.certificate': "شهادات النجاح",
    'prd.kinds.minutes': "المحضر",
    'prd.kinds.enrolment': "الشهادات المدرسية",
    'prd.kinds.card': "بطاقات الطلبة",
    'prd.kinds.list': "لوائح الأقسام",
    'prd.kinds.internship': "وثائق التدريب",
    'prd.state.queued': "في الانتظار",
    'prd.state.running': "جار",
    'prd.state.done': "منتهية",
    'prd.state.partial': "مع إخفاقات",
    'prd.state.stopped': "متوقفة",
    'prd.state.failed': "فشل",
    'prd.state.to_sign': "للتوقيع",
    'prd.state.signed': "موقعة",
    'prd.state.returned': "مرجعة",
    'prd.jobs.sub': "الدفعات الجارية والحديثة: تشتغل في الخلفية حتى لو أغلقتم الصفحة",
    'prd.jobs.empty': "لا توجد دفعة حاليا.",
    'prd.jobs.col.job': "الدفعة",
    'prd.jobs.col.started': "انطلقت",
    'prd.jobs.col.by': "من طرف",
    'prd.jobs.col.progress': "التقدم",
    'prd.jobs.col.state': "الحالة",
    'prd.jobs.note': "تمر الدفعة التي تتجاوز {count} وثيقة إلى قائمة الانتظار؛ وينبهكم Kelassi عند الانتهاء عبر الجرس والبريد الإلكتروني.",
    'prd.progress.queued': "{total} للإصدار",
    'prd.progress.running': "{done} من {total}",
    'prd.progress.done': "{done} صادرة",
    'prd.progress.failed': "{done} صادرة · {failed} فاشلة",
    'prd.progress.toSign': "{done} صادرة · في انتظار {name}",
    'prd.progress.signed': "{done} وقعها {name}",
    'prd.progress.returned': "مرجعة: {reason}",
    'prd.panel.started': "انطلقت {date} من طرف {by}",
    'prd.panel.produced': "صادرة",
    'prd.panel.of': "من {total}",
    'prd.panel.failed': "فاشلة",
    'prd.panel.toFix': "للتصحيح",
    'prd.panel.none': "لا شيء",
    'prd.panel.failures': "ما فشل",
    'prd.panel.open': "فتح البطاقة",
    'prd.panel.toSign': "{count} وثيقة تنتظر توقيع {title} ({name})؛ لا شيء ينشر على البوابة قبل ذلك.",
    'prd.panel.returned': "أرجعها {by}: {reason}. ألغيت الوثائق غير الموقعة؛ صححوا ثم أصدروا دفعة جديدة.",
    'prd.panel.retryNote': "صححوا البطاقات ثم أعيدوا: تصدر فقط {failed} وثيقة فاشلة، والـ {done} الأخرى لا تتغير.",
    'prd.panel.background': "تشتغل الدفعة في الخلفية: يمكنكم إغلاق هذه اللوحة.",
    'prd.panel.stop': "إيقاف",
    'prd.panel.archive': "الـ {count} الصادرة",
    'prd.panel.retry': "إعادة الـ {count}",
    'sgn.title': "للتوقيع",
    'sgn.sub': "الدفعات التي تنتظر توقيعكم قبل نشرها",
    'sgn.kpi.pending': "دفعات في الانتظار",
    'sgn.kpi.pendingNote': "{count} وثيقة",
    'sgn.kpi.signed': "موقعة هذا الشهر",
    'sgn.kpi.signedNote': "منها {count} بالتفويض",
    'sgn.kpi.returned': "مرجعة",
    'sgn.kpi.returnedNote': "هذا الشهر مع السبب",
    'sgn.queue.title': "دفعات للتوقيع",
    'sgn.queue.sub': "لا شيء ينشر على البوابة قبل توقيعكم",
    'sgn.queue.empty': "لا توجد دفعة في انتظاركم.",
    'sgn.queue.line': "{count} وثيقة · أصدرها {by}، {date}",
    'sgn.queue.delegated': "بالتفويض عن {title}",
    'sgn.queue.browse': "تصفح",
    'sgn.queue.return': "إرجاع",
    'sgn.queue.chosen': "{jobs} دفعات مختارة · {count} وثيقة",
    'sgn.action.sign': "توقيع {count} وثيقة",
    'sgn.dialog.title': "توقيع {count} وثيقة",
    'sgn.dialog.text': "{what}. سيوضع عليها توقيعكم والخاتم مع التاريخ والساعة.",
    'sgn.dialog.sending': "جار إرسال الرمز بالبريد الإلكتروني…",
    'sgn.dialog.code': "الرمز المستلم بالبريد الإلكتروني على {email}",
    'sgn.dialog.expires': "الرمز صالح إلى غاية {time}.",
    'sgn.dialog.resend': "إرسال رمز جديد",
    'sgn.dialog.sign': "توقيع",
    'sgn.done': "تم توقيع {count} وثيقة.",
    'sgn.returned': "أرجعت الدفعة: تم إشعار صاحبها.",
    'sgn.return.title': "إرجاع هذه الدفعة؟",
    'sgn.return.text': "«{label}»: تلغى وثائقها الـ {count} غير الموقعة، ويتوصل {by} بسببكم.",
    'sgn.return.reason': "السبب",
    'sgn.return.submit': "إرجاع الدفعة",
    'sgn.me.title': "توقيعي",
    'sgn.me.sub': "صورة التوقيع والخاتم توضع على كل وثيقة موقعة",
    'sgn.me.quality': "الصفة",
    'sgn.me.qualityHint': "المدير البيداغوجي",
    'sgn.me.signature': "توقيعي",
    'sgn.me.stamp': "خاتمي",
    'sgn.me.noSignature': "لا يوجد توقيع بعد",
    'sgn.me.noStamp': "لا يوجد خاتم",
    'sgn.me.addSignature': "إضافة التوقيع",
    'sgn.me.replaceSignature': "تعويض التوقيع",
    'sgn.me.addStamp': "إضافة الخاتم",
    'sgn.me.replaceStamp': "تعويض الخاتم",
    'sgn.me.imageHint': "يفضل PNG بخلفية شفافة، عرض 120 بكسل على الأقل، 1 ميغابايت على الأكثر.",
    'sgn.delegation.title': "التفويض",
    'sgn.delegation.who': "يوقع مكاني أثناء غيابي",
    'sgn.delegation.nobody': "لا أحد",
    'sgn.delegation.from': "من",
    'sgn.delegation.to': "إلى",
    'sgn.delegation.hint': "تقول الوثيقة «عن {title}، بالتفويض، {name}».",
    'sgn.note': "توقيع إلكتروني بسيط: صورة التوقيع والخاتم، والختم الزمني وبصمة الوثيقة، مؤكدة برمز. ويمكن إضافة توقيع مؤهل من طرف جهة موثوقة لاحقا.",
    'violation.prd.signer': "اختاروا من يوقع.",
    'violation.prd.too_many': "2000 وثيقة على الأكثر في الدفعة.",
    'violation.prd.training': "اختاروا تكوينا.",
    'violation.prd.finished': "انتهت هذه الدفعة.",
    'violation.prd.nothing_to_retry': "لا شيء لإعادته.",
    'violation.prd.nobody': "لا توجد وثيقة للإصدار.",
    'violation.sgn.title': "بينوا صفة الموقع («المدير البيداغوجي»).",
    'violation.sgn.no_signer': "اضبطوا صفتكم أولا.",
    'violation.sgn.delegate': "اختاروا شخصا آخر من المؤسسة.",
    'violation.sgn.period': "النهاية تأتي بعد البداية.",
    'violation.sgn.too_big': "صورة لا تتجاوز 1 ميغابايت.",
    'violation.sgn.format': "صورة PNG أو JPEG أو WebP.",
    'violation.sgn.too_small': "عرض 120 بكسل على الأقل.",
    'violation.sgn.too_many_codes': "طلبتم رموزا كثيرة: أعيدوا المحاولة بعد دقائق.",
    'violation.sgn.code_expired': "اطلبوا رمزا جديدا.",
    'violation.sgn.code': "هذا الرمز غير مطابق.",
    'violation.sgn.not_yours': "هذه الدفعة لا تنتظركم.",
    'violation.sgn.no_job': "اختاروا دفعة واحدة على الأقل.",
    'doc.verify.pageTitle': "التحقق من وثيقة",
    'doc.verify.validBody': "هي كما أصدرتها المؤسسة.",
    'doc.verify.row.type': "الوثيقة",
    'doc.verify.row.student': "باسم",
    'doc.verify.row.training': "التكوين",
    'doc.verify.row.year': "السنة",
    'doc.verify.row.issued': "صدرت بتاريخ",
    'doc.verify.row.signed': "وقعها",
    'doc.verify.row.until': "صالحة إلى غاية",
    'doc.verify.privacy': "حماية للطالب، لا تظهر هنا النقط ولا القرار. قارنوا مع الوثيقة التي توصلتم بها.",
    'doc.verify.another': "التحقق من مرجع آخر",
    'shr.title': "الوثائق المشتركة",
    'shr.sub': "وثائق المؤسسة الموزعة على جمهور: النظام الداخلي، الجدول، المواثيق، الأدلة؛ مع إشعار بالقراءة أو بدونه",
    'shr.showArchived': "إظهار المؤرشفة",
    'shr.empty': "لا توجد وثيقة مشتركة حاليا.",
    'shr.col.document': "الوثيقة",
    'shr.col.for': "لفائدة",
    'shr.col.reading': "القراءة",
    'shr.col.follow': "التتبع",
    'shr.col.period': "النشر",
    'shr.versionOf': "النسخة {number} بتاريخ {date}",
    'shr.archived': "مؤرشف",
    'shr.ack': "إشعار بالقراءة",
    'shr.free': "قراءة حرة",
    'shr.follow.pending': "{accepted} من {total} · {pending} للتذكير",
    'shr.follow.all': "{accepted} من {total}",
    'shr.follow.opened': "مرات الفتح: {count}",
    'shr.period.always': "طوال السنة",
    'shr.period.until': "إلى غاية {date}",
    'shr.period.since': "منذ {date}",
    'shr.period.between': "من {from} إلى {to}",
    'shr.audience.everyone': "الجميع بمن فيهم الأطر",
    'shr.population.student': "الطلبة",
    'shr.population.teacher': "الأساتذة",
    'shr.population.staff': "الأطر الإدارية",
    'shr.note': "تعويض الملف ينشئ نسخة جديدة؛ وإذا طلب إشعار بالقراءة فعلى الجميع قراءة النسخة الجديدة وقبولها. وتصدر لائحة القراء إلى Excel.",
    'shr.new': "مشاركة جديدة",
    'shr.drop': "إيداع ملف",
    'shr.dropHint': "PDF أو Word أو Excel أو صورة · {size} ميغابايت على الأكثر",
    'shr.field.title': "العنوان",
    'shr.field.for': "لفائدة",
    'shr.field.from': "من",
    'shr.field.to': "إلى",
    'shr.field.ack': "طلب إشعار بالقراءة",
    'shr.field.ackHint': "«قرأت وأوافق»؛ ويحتفظ بالتاريخ والساعة",
    'shr.field.notify': "الإشعار",
    'shr.field.notifyHint': "عبر التواصل",
    'shr.restrict.none': "جميع الطلبة: التقييد بتكوينات أو أقسام",
    'shr.restrict.some': "طلبة مقيدون: {count} اختيارات",
    'shr.restrict.trainings': "التكوينات",
    'shr.restrict.classes': "الأقسام",
    'shr.restrict.noTraining': "لا يوجد تكوين هذه السنة.",
    'shr.share': "مشاركة",
    'shr.shared': "تمت مشاركة الوثيقة.",
    'shr.saved': "تم حفظ المشاركة.",
    'shr.replaced': "تمت مشاركة النسخة {number}.",
    'shr.archivedDone': "أرشفت المشاركة: تغادر «وثائقي».",
    'shr.reminded': "أرسل التذكير إلى {count} شخص.",
    'shr.panel.by': "شاركها {by} بتاريخ {date}",
    'shr.panel.audience': "الجمهور",
    'shr.panel.accepted': "وافقوا",
    'shr.panel.opened': "فتحوا",
    'shr.panel.pending': "{count} للتذكير",
    'shr.panel.version': "النسخة {number}",
    'shr.panel.file': "الملف ونسخه",
    'shr.panel.versionLine': "ن{number}",
    'shr.panel.download': "تحميل",
    'shr.panel.notifyVersion': "إشعار الجمهور بالنسخة الجديدة",
    'shr.panel.replace': "مشاركة النسخة {number}",
    'shr.panel.settings': "الجمهور والمدة والإشعار",
    'shr.panel.readers': "من قرأ",
    'shr.panel.archive': "أرشفة",
    'shr.panel.remind': "تذكير الـ {count}",
    'shr.filter.all': "كل الجمهور",
    'shr.filter.notAccepted': "لم يوافق بعد",
    'shr.filter.accepted': "وافقوا",
    'shr.filter.notOpened': "لم يفتح بعد",
    'shr.filter.opened': "فتحوا",
    'shr.readers.name': "الشخص",
    'shr.readers.opened': "فتح بتاريخ",
    'shr.readers.accepted': "وافق بتاريخ",
    'shr.readers.waiting': "في الانتظار",
    'shr.readers.truncated': "اللائحة محدودة في أول 3000 شخص.",
    'shr.mine.toAccept': "للقراءة والموافقة",
    'shr.mine.versionLine': "النسخة {number} · شوركت بتاريخ {date}",
    'shr.mine.read': "قراءة",
    'shr.mine.accept': "قرأت وأوافق",
    'shr.mine.title': "وثائق المؤسسة",
    'shr.mine.acceptedOn': "تمت الموافقة بتاريخ {date}",
    'shr.mine.sharedOn': "شورك بتاريخ {date}",
    'shr.mine.until': "إلى غاية {date}",
    'shr.mine.page': "الوثائق المشتركة",
    'shr.mine.pageSub': "ما شاركته المؤسسة معكم: للقراءة، وأحيانا للموافقة",
    'shr.mine.none': "لا توجد وثيقة مشتركة معكم.",
    'journal.action.document.shared': "مشاركة وثيقة",
    'journal.action.document.share_changed': "تعديل وثيقة مشتركة",
    'journal.action.document.share_archived': "أرشفة وثيقة مشتركة",
    'journal.action.document.share_reminded': "إرسال تذكير بالقراءة",
    'violation.shr.audience': "اختاروا مع من تشاركون الوثيقة.",
    'violation.shr.audience_students': "التكوينات والأقسام تقيد الطلبة: حددوهم.",
    'violation.shr.title': "أعطوا عنوانا (200 حرف على الأكثر).",
    'violation.shr.date': "تاريخ غير صالح.",
    'violation.shr.period': "النهاية تأتي بعد البداية.",
    'violation.shr.archived': "هذه المشاركة مؤرشفة.",
    'violation.shr.no_acknowledgement': "هذه الوثيقة لا تطلب إشعارا بالقراءة.",
    'violation.shr.no_communication': "التذكيرات تمر عبر التواصل، وهو غير مفعل.",
    'violation.shr.new_version': "صدرت نسخة جديدة: أعيدوا قراءتها.",
    'rel.modules.library': "المكتبة",
    'rel.modules.libraryHint': "الفهرس والنسخ والقراء والبطاقات والإعارات والحجوزات والغرامات والجرد",
    'rel.modules.libraryOff': "تبقى الإعارات الجارية مسجلة؛ لا يرسل أي تذكير ويغادر الفهرس الرقمي البوابة.",
    'doc.type.library_clearance': "إبراء ذمة المكتبة",
    'doc.types.library_clearance': "إبراءات ذمة المكتبة",
    'lib.situation.available': "متاح",
    'lib.situation.loaned': "معار",
    'lib.situation.held': "محجوز للسحب",
    'lib.situation.transit': "في الطريق",
    'lib.situation.repair': "في الإصلاح",
    'lib.situation.lost': "مفقود",
    'lib.situation.missing': "ناقص",
    'lib.situation.withdrawn': "مسحوب من الرصيد",
    'lib.hold.waiting': "في الانتظار",
    'lib.hold.assigned': "جاهز للسحب",
    'lib.hold.collected': "تم السحب",
    'lib.hold.cancelled_reader': "ألغاه القارئ",
    'lib.hold.cancelled_library': "ألغته المكتبة",
    'lib.hold.expired': "منتهية",
    'lib.fee.open': "مستحق",
    'lib.fee.paid': "مؤدى",
    'lib.fee.waived': "معفى",
    'lib.fee.cancelled': "ملغى",
    'lib.late.days': "تأخير {count} يوم",
    'lib.late.due': "الإرجاع في {date}",
    'lib.scan.camera': "المسح بالكاميرا",
    'lib.scan.hint': "قارئ الرموز أو الكتابة ثم إدخال",
    'lib.scan.noCamera': "الكاميرا غير متاحة على هذا الجهاز.",
    'journal.action.library.settings_changed': "تعديل إعدادات المكتبة",
    'journal.action.library.catalog_changed': "تعديل بطاقة أو مرجع في المكتبة",
    'journal.action.library.items_changed': "تعديل النسخ أو نقلها أو توسيمها",
    'journal.action.library.item_withdrawn': "إخراج نسخة من الرصيد",
    'journal.action.library.locations_changed': "تعديل أماكن المكتبة",
    'journal.action.library.reader_changed': "تعديل قارئ أو انخراط أو بطاقة",
    'journal.action.library.loan_overridden': "إعارة رغم المنع",
    'journal.action.library.fee_changed': "إصدار أو تسوية مستحق للمكتبة",
    'journal.action.library.fee_waived': "الإعفاء من غرامة أو رسوم",
    'journal.action.library.loss_changed': "تصريح بضياع أو تسويته أو العثور على نسخة",
    'journal.action.library.inventory_changed': "فتح جرد أو معالجة فارق",
    'journal.action.library.inventory_closed': "إغلاق الجرد",
    'journal.action.library.import_run': "تنفيذ أو إلغاء استيراد المكتبة",
    'journal.action.library.history_viewed': "الاطلاع على سجل إعارات قارئ",
    'journal.action.library.clearance_issued': "تسليم إبراء ذمة المكتبة",
    'violation.lib.reason_required': "السبب إلزامي.",
    'violation.lib.prefix': "حروف كبيرة وأرقام وشرطات فقط.",
    'violation.lib.policy_duplicate': "توجد سياسة لهذا التقاطع.",
    'violation.lib.default_policy': "لا تحذف السياسة الافتراضية.",
    'violation.lib.isbn': "رقم ISBN غير صالح: رقم التحقق لا يتطابق.",
    'violation.lib.year_order': "سنة الطبعة قبل سنة الصدور الأصلية.",
    'violation.lib.dewey': "يُنتظر رقم ديوي (مثال 658.4).",
    'violation.lib.issn': "يُنتظر ISSN (1234-5678).",
    'violation.lib.language': "يُنتظر رمز لغة ISO 639.",
    'violation.lib.contributor_required': "مؤلف أو ناشر واحد على الأقل.",
    'violation.lib.edition_used': "لا تحذف بطاقة لها نسخ أو حجوزات.",
    'violation.lib.merge_invalid': "مرجعان مختلفان من النوع نفسه.",
    'violation.lib.location_cycle': "لا يوضع عنصر تحت نفسه.",
    'violation.lib.location_used': "لا يحذف مكان يحتوي على عناصر أو نسخ أو مجالات.",
    'violation.lib.barcode': "رمز شريطي غير صالح.",
    'violation.lib.barcode_taken': "هذا الرمز موجود: لا يعاد إسناد رمز أبدا.",
    'violation.lib.item_withdrawn': "لا تعدل نسخة مسحوبة.",
    'violation.lib.no_category': "لا توجد فئة قارئ لهذا الشخص: أكمل معالج الانطلاق.",
    'violation.lib.reader_exists': "هذا الشخص مسجل بالمكتبة.",
    'violation.lib.contact_required': "البريد أو الهاتف إلزامي.",
    'violation.lib.person_exists': "يوجد شخص بهذه الوثيقة: سجله بدل ذلك.",
    'violation.lib.reader_used': "لهذا القارئ سجل: أوقفه بدل حذفه.",
    'violation.lib.no_membership': "لا يوجد انخراط ساري.",
    'violation.lib.card_inactive': "هذه البطاقة لم تعد صالحة.",
    'violation.lib.fee_invoiced': "هذا المستحق مفوتر: تحصله المالية.",
    'violation.lib.fee_settled': "هذا المستحق مسوى.",
    'violation.lib.loan_closed': "هذه الإعارة مغلقة.",
    'violation.lib.not_lost': "هذه النسخة ليست مفقودة.",
    'violation.lib.not_clear': "القارئ غير مبرأ الذمة.",
    'violation.lib.unknown_reader': "بطاقة أو قارئ غير معروف.",
    'violation.lib.unknown_item': "رمز شريطي غير معروف.",
    'violation.lib.no_open_loan': "لا توجد إعارة جارية لهذه النسخة.",
    'violation.lib.renew_refused': "لا تمدد هذه الإعارة.",
    'violation.lib.holds_closed': "الحجز الرقمي غير مفتوح.",
    'violation.lib.confidential': "وثيقة سرية.",
    'violation.lib.hold_exists': "هذا القارئ حجز هذه الوثيقة.",
    'violation.lib.hold_loaned': "القارئ يستعير هذه الوثيقة.",
    'violation.lib.no_loanable': "لا توجد نسخة قابلة للإعارة.",
    'violation.lib.hold_closed': "هذا الحجز مغلق.",
    'violation.lib.hold_item': "لا تصلح هذه النسخة لهذا الحجز.",
    'violation.lib.inventory_open': "يوجد جرد كامل مفتوح.",
    'violation.lib.inventory_closed': "هذا الجرد مغلق.",
    'violation.lib.resolve_invalid': "لا ينطبق هذا الإجراء على هذا الفارق.",
    'violation.lib.import_done': "تم تنفيذ هذا الاستيراد.",
    'violation.lib.import_not_done': "لا يلغى إلا استيراد منفذ.",
    'violation.lib.file_size': "ملف فارغ أو كبير جدا.",
    'violation.lib.card_code': "البطاقة الرقمية منتهية: حدّثها.",
    'violation.lib.cover': "صورة JPEG أو PNG أو WebP بحجم 2 ميغا كحد أقصى.",
    'violation.lib.not_mine': "هذه الإعارة ليست لك.",
    'lib.dash.title': "المكتبة",
    'lib.dash.sub': "يوم المكتبة: الإعارات والإرجاعات المنتظرة والتأخيرات والحجوزات",
    'lib.dash.stats': "الإحصائيات",
    'lib.dash.suggestions': "اقتراحات الشراء",
    'lib.dash.desk': "الإعارة والإرجاع",
    'lib.dash.setupNeeded': "لم تنطلق المكتبة بعد: يكفي موقع وسياسة إعارة ورمز شريطي وبطاقة.",
    'lib.dash.setupGo': "تشغيل المعالج",
    'lib.dash.kpiLoans': "إعارات اليوم",
    'lib.dash.kpiLoansNote': "{returns} إرجاع",
    'lib.dash.kpiDue': "إرجاعات منتظرة",
    'lib.dash.kpiDueNote': "اليوم · {open} إعارة جارية",
    'lib.dash.kpiLate': "متأخرة",
    'lib.dash.kpiLateNote': "{count} قارئ موقوف",
    'lib.dash.kpiHolds': "حجوزات للتحضير",
    'lib.dash.kpiHoldsNote': "{collect} في الرفوف و{pickup} للسحب",
    'lib.dash.kpiStock': "الرصيد",
    'lib.dash.kpiStockNote': "نسخة · {editions} بطاقة · {readers} قارئ",
    'lib.dash.lateTitle': "الإرجاعات المنتظرة والتأخيرات",
    'lib.dash.lateSub': "اليوم أولا ثم التأخيرات",
    'lib.dash.allLate': "كل التأخيرات",
    'lib.dash.noLate': "لا إرجاع منتظر ولا تأخير.",
    'lib.dash.colItem': "النسخة",
    'lib.dash.colReader': "القارئ",
    'lib.dash.colDue': "الأجل",
    'lib.dash.anonymous': "إعارة مجهولة",
    'lib.dash.recentTitle': "الحركات الأخيرة",
    'lib.dash.recentSub': "إعارات وإرجاعات وتمديدات وضياع",
    'lib.dash.noRecent': "لا حركة حاليا.",
    'lib.dash.event.loaned': "إعارة",
    'lib.dash.event.returned': "إرجاع",
    'lib.dash.event.renewed': "تمديد",
    'lib.dash.event.lost': "ضياع",
    'lib.dash.event.found': "عثر عليه",
    'lib.dash.event.withdrawn': "مسحوب",
    'lib.dash.collectTitle': "حجوزات للتحضير",
    'lib.dash.collectSub': "لائحة الجمع: هذه النسخ في الرفوف",
    'lib.dash.collectGo': "تحضير",
    'lib.dash.collectFor': "لـ {name} · الرتبة {rank}",
    'lib.dash.noCollect': "لا شيء للجمع من الرفوف.",
    'lib.dash.watchTitle': "للمراقبة",
    'lib.dash.watchSuspended': "{count} قارئ موقوف",
    'lib.dash.watchDebt': "مجموع ديون القراء: {amount}",
    'lib.dash.watchTransit': "{count} نسخة في الطريق",
    'lib.dash.watchTransitNone': "لا نقل بين المواقع",
    'lib.dash.watchStock': "{waiting} قارئ ينتظر و{copies} نسخة: طلب المزيد؟",
    'lib.desk.title': "الإعارة والإرجاع",
    'lib.desk.sub': "مسحان للإعارة (البطاقة ثم النسخة) ومسح واحد للإرجاع",
    'lib.desk.tabLend': "إعارة",
    'lib.desk.tabReturn': "إرجاع",
    'lib.desk.readerScan': "1. بطاقة القارئ",
    'lib.desk.readerHint': "امسح البطاقة أو رمز البطاقة الرقمية أو بطاقة الطالب",
    'lib.desk.itemScan': "2. النسخ",
    'lib.desk.itemHint': "امسح كل نسخة وتظهر المراقبات فورا",
    'lib.desk.unknownReader': "بطاقة أو قارئ غير معروف.",
    'lib.desk.lent': "تم تسجيل {count} إعارة.",
    'lib.desk.ticket': "طباعة الوصل",
    'lib.desk.basket': "هذه الإعارة",
    'lib.desk.basketSub': "تطبق سياسة القارئ على كل نسخة",
    'lib.desk.onsite': "اطلاع في عين المكان",
    'lib.desk.scanReaderFirst': "امسح بطاقة القارئ أولا.",
    'lib.desk.basketEmpty': "امسح النسخ المراد إعارتها.",
    'lib.desk.colCheck': "المراقبة",
    'lib.desk.colActions': "الإجراءات",
    'lib.desk.accepted': "مقبول",
    'lib.desk.refused': "مرفوض",
    'lib.desk.notOverridable': "(لا يمكن تجاوزه)",
    'lib.desk.rentalDay': "كراء {amount} لليوم",
    'lib.desk.rentalLoan': "كراء {amount}",
    'lib.desk.force': "تجاوز",
    'lib.desk.remove': "سحب {barcode} من الإعارة",
    'lib.desk.receipt': "وصل المالية للكراء",
    'lib.desk.receiptHint': "يؤدى الكراء قبل تسليم الكتاب",
    'lib.desk.lend': "إعارة {count} نسخة",
    'lib.desk.sideHint': "تظهر بطاقة القارئ هنا بعد مسحها.",
    'lib.desk.forceTitle': "تجاوز المنع",
    'lib.desk.forceReason': "سبب التجاوز",
    'lib.desk.forceHint': "يحفظ السبب في الإعارة والسجل",
    'lib.desk.forceConfirm': "الإعارة رغم المنع",
    'lib.desk.reader': "القارئ",
    'lib.desk.openFile': "فتح الملف",
    'lib.desk.cardNumber': "البطاقة {number}",
    'lib.desk.noMembership': "لا انخراط ساري",
    'lib.desk.membershipUntil': "انخراط ساري إلى {date}",
    'lib.desk.membershipPending': "انخراط في انتظار الأداء ({amount})",
    'lib.desk.loansCount': "{count} إعارة جارية",
    'lib.desk.suspended': "موقوف: {reason}",
    'lib.desk.penalty': "عقوبة إلى {date}",
    'lib.desk.currentLoans': "إعاراته الجارية",
    'lib.desk.noLoans': "لا إعارة جارية.",
    'lib.desk.renew': "تمديد",
    'lib.desk.account': "حسابه",
    'lib.desk.debt': "الدين",
    'lib.desk.late': "التأخير",
    'lib.desk.holds': "الحجوزات",
    'lib.desk.openFees': "المستحقات المفتوحة",
    'lib.desk.none': "لا شيء",
    'lib.desk.returnScan': "النسخة المرجعة",
    'lib.desk.returnHint': "يكفي مسح واحد دون البطاقة؛ يقبل الإرجاع من موقع آخر",
    'lib.desk.returnedOn': "التاريخ الفعلي للإيداع",
    'lib.desk.returnedOnHint': "لصندوق الإرجاع؛ فارغ: اليوم",
    'lib.desk.condition': "حالة النسخة",
    'lib.desk.conditionSame': "دون تغيير",
    'lib.desk.conditionHint': "يختار قبل المسح. «للإصلاح» يرسل النسخة إلى الورشة.",
    'lib.desk.cond.new': "جديد",
    'lib.desk.cond.good': "جيد",
    'lib.desk.cond.worn': "مستعمل",
    'lib.desk.cond.damaged': "تالف",
    'lib.desk.cond.repair': "للإصلاح",
    'lib.desk.returned': "الإرجاع",
    'lib.desk.returnedBy': "أرجعه",
    'lib.desk.lateDays': "{count} يوم عمل؛ أيام الإغلاق لا تحتسب",
    'lib.desk.onTime': "في الوقت",
    'lib.desk.fine': "الغرامة",
    'lib.desk.suspension': "التوقيف",
    'lib.desk.suspendedUntil': "الإعارة موقوفة إلى {date}",
    'lib.desk.dest.shelf': "إعادة إلى الرف",
    'lib.desk.dest.hold': "وضع جانبا لحجز",
    'lib.desk.dest.transit': "إرسال إلى موقعها",
    'lib.desk.dest.repair': "إرسال للإصلاح",
    'lib.desk.destShelf': "في مكانها: {place}",
    'lib.desk.destHold': "لـ {name}، تم إشعاره؛ يسحب قبل {date}",
    'lib.desk.destTransit': "النسخة تابعة لموقع {site}",
    'lib.desk.destRepair': "النسخة مسجلة في الإصلاح",
    'lib.desk.damageTitle': "تلف ملاحظ",
    'lib.desk.damageSub': "يسجل مستحق تلف على حساب القارئ",
    'lib.desk.damageAmount': "المبلغ ({currency})",
    'lib.desk.damageNote': "الملاحظة",
    'lib.desk.damageCharge': "فوترة التلف",
    'lib.desk.damageDone': "تم إصدار مستحق التلف.",
    'lib.desk.damageLabel': "تلف",
    'lib.holds.title': "الحجوزات",
    'lib.holds.sub': "طابور كل طبعة حسب الأولوية ثم الوصول",
    'lib.holds.tabCollect': "للجمع",
    'lib.holds.tabPickup': "للسحب",
    'lib.holds.tabWaiting': "في الطابور",
    'lib.holds.tabClosed': "مغلقة",
    'lib.holds.collectTitle': "لائحة الجمع",
    'lib.holds.collectSub': "مرتبة حسب المكان: خذ النسخة وامسحها فتوضع جانبا ويشعر القارئ",
    'lib.holds.noPlace': "المكان غير محدد",
    'lib.holds.forReader': "لـ {name} · {category}",
    'lib.holds.scanTaken': "النسخة المأخوذة",
    'lib.holds.scanTakenHint': "امسح ثم إدخال",
    'lib.holds.pickupTitle': "للسحب من الشباك",
    'lib.holds.pickupSub': "بعد الأجل ينتهي الحجز وتنتقل النسخة للتالي",
    'lib.holds.waitingTitle': "في انتظار إرجاع",
    'lib.holds.waitingSub': "لا نسخة في الرف: يلبى الحجز عند الإرجاع التالي",
    'lib.holds.closedTitle': "الحجوزات المغلقة",
    'lib.holds.empty': "لا حجز.",
    'lib.holds.colTitle': "الوثيقة",
    'lib.holds.colUntil': "يسحب قبل",
    'lib.holds.colRank': "الرتبة",
    'lib.holds.colState': "الحالة",
    'lib.holds.colSince': "منذ",
    'lib.holds.until': "قبل {date}",
    'lib.holds.rank': "الرتبة {rank}",
    'lib.holds.cancel': "إلغاء",
    'lib.overdue.title': "التأخيرات والضياع",
    'lib.overdue.sub': "يرصد الفحص الصباحي التأخيرات ويرسل التذكيرات",
    'lib.overdue.exportLoans': "تصدير الإعارات",
    'lib.overdue.exportFees': "تصدير المستحقات",
    'lib.overdue.calendar': "جدول التذكيرات",
    'lib.overdue.calendarSub': "غرامة التأخير: {mode}؛ للطالب القاصر يرسل التذكير أيضا لولي الأمر",
    'lib.overdue.mode.money': "مالية",
    'lib.overdue.mode.suspension': "أيام توقيف",
    'lib.overdue.mode.both': "مالية وتوقيف",
    'lib.overdue.stepReminder': "تذكير",
    'lib.overdue.stepReminderSub': "قبل الأجل",
    'lib.overdue.stepChase': "إعادة تذكير",
    'lib.overdue.stepChaseSub': "بعد الأجل",
    'lib.overdue.stepNotice': "إنذار",
    'lib.overdue.stepNoticeSub': "توقيف؛ ضياع مفترض بعد {days} يوم",
    'lib.overdue.kpiLate': "متأخرة",
    'lib.overdue.kpiLateNote': "منها {count} تجاوزت 7 أيام",
    'lib.overdue.kpiLost': "الضياع",
    'lib.overdue.kpiLostNote': "إعارات أغلقت كضياع",
    'lib.overdue.kpiMissing': "ناقصة ومفقودة",
    'lib.overdue.kpiMissingNote': "نسخ للبحث",
    'lib.overdue.kpiDebt': "ديون القراء",
    'lib.overdue.kpiDebtNote': "{count} مستحق مفتوح",
    'lib.overdue.tabLate': "التأخيرات",
    'lib.overdue.tabLost': "الضياع",
    'lib.overdue.tabMissing': "الناقصة",
    'lib.overdue.tabFees': "المستحقات",
    'lib.overdue.empty': "لا شيء للمعالجة.",
    'lib.overdue.colLate': "التأخير",
    'lib.overdue.colLostOn': "ضاع في",
    'lib.overdue.colStage': "آخر تذكير",
    'lib.overdue.noReminder': "لا شيء",
    'lib.overdue.manual': "يدوي",
    'lib.overdue.remind': "تذكير",
    'lib.overdue.reminded': "تم إرسال التذكير.",
    'lib.overdue.lose': "تسجيل الضياع",
    'lib.overdue.lost': "أغلقت الإعارة كضياع وصدرت مستحقات التعويض.",
    'lib.overdue.loseTitle': "تسجيل الضياع",
    'lib.overdue.loseText': "«{title}» ({barcode}) المعار لـ {name} بتأخير {days} يوم: تسجل النسخة مفقودة وتغلق الإعارة وتتوقف الغرامة ويفوتر التعويض ومصاريف المعالجة.",
    'lib.overdue.loseConfirm': "إغلاق الإعارة كضياع",
    'lib.overdue.settle': "قبول التعويض",
    'lib.overdue.settled': "قبل التعويض وألغي المستحق.",
    'lib.overdue.missingSub': "عند العثور عليها تعود النسخة للرصيد ويرد المبلغ حسب الإعداد",
    'lib.overdue.markFound': "عثر عليه",
    'lib.overdue.found': "أعيدت النسخة للرصيد.",
    'lib.overdue.feesSub': "يحصل المستحق المفوتر بالمالية والباقي بوصل المالية",
    'lib.overdue.colFee': "المستحق",
    'lib.overdue.colDue': "الباقي",
    'lib.overdue.colInvoice': "الفاتورة",
    'lib.overdue.kind.membership': "انخراط",
    'lib.overdue.kind.rental': "كراء",
    'lib.overdue.kind.late': "تأخير",
    'lib.overdue.kind.damage': "تلف",
    'lib.overdue.kind.loss': "ضياع",
    'lib.overdue.kind.processing': "مصاريف المعالجة",
    'lib.overdue.kind.duplicate': "نظير",
    'lib.overdue.pay': "أداء",
    'lib.overdue.waive': "إعفاء",
    'lib.overdue.cancel': "إلغاء",
    'lib.overdue.payTitle': "تسجيل الأداء",
    'lib.overdue.payConfirm': "حفظ الوصل",
    'lib.overdue.waiveTitle': "الإعفاء من المستحق",
    'lib.overdue.waiveConfirm': "منح الإعفاء",
    'lib.overdue.cancelTitle': "إلغاء المستحق",
    'lib.overdue.cancelConfirm': "إلغاء المستحق",
    'lib.overdue.receipt': "رقم وصل المالية",
    'lib.overdue.receiptHint': "لا تحصل المكتبة شيئا بنفسها",
    'lib.overdue.amount': "المبلغ المعفى ({currency})",
    'lib.overdue.amountHint': "إعفاء كلي افتراضيا ومبلغ أصغر لإعفاء جزئي",
    'lib.overdue.reason': "السبب",
    'lib.overdue.reasonHint': "إلزامي ويحفظ في السجل",
    'lib.overdue.paid': "تم تسجيل الأداء.",
    'lib.overdue.waived': "تم منح الإعفاء.",
    'lib.overdue.cancelled': "تم إلغاء المستحق.",
    'lib.doctype.book': "كتاب",
    'lib.doctype.periodical': "دورية",
    'lib.doctype.thesis': "أطروحة",
    'lib.doctype.dissertation': "مذكرة",
    'lib.doctype.internship_report': "تقرير تدريب",
    'lib.doctype.media': "قرص مدمج",
    'lib.doctype.map': "خريطة",
    'lib.doctype.ebook': "كتاب رقمي",
    'lib.doctype.internal': "وثيقة داخلية",
    'lib.inv.title': "الجرد",
    'lib.inv.sub': "مطابقة الرفوف مع القاعدة حملة بحملة دون إغلاق المكتبة",
    'lib.inv.open': "فتح حملة",
    'lib.inv.none': "لا توجد حملة جرد بعد.",
    'lib.inv.col.campaign': "الحملة",
    'lib.inv.col.kind': "النوع",
    'lib.inv.col.scope': "النطاق",
    'lib.inv.col.opened': "فتحت في",
    'lib.inv.col.progress': "التقدم",
    'lib.inv.col.state': "الحالة",
    'lib.inv.col.zone': "المنطقة",
    'lib.inv.col.expected': "المنتظر",
    'lib.inv.col.seen': "المرئي",
    'lib.inv.col.gap': "الفارق",
    'lib.inv.col.item': "النسخة",
    'lib.inv.col.action': "الإجراء",
    'lib.inv.kind.full': "جرد كامل",
    'lib.inv.kind.rotating': "مراجعة دورية",
    'lib.inv.kind.spot': "مراقبة ظرفية",
    'lib.inv.state.open': "جارية",
    'lib.inv.state.closed': "مغلقة",
    'lib.inv.scopeAll': "كل الرصيد",
    'lib.inv.seenOf': "{seen} من {expected}",
    'lib.inv.openTitle': "فتح حملة جرد",
    'lib.inv.openSub': "تثبت نسخ النطاق في حالتها المنتظرة وتستمر الإعارة.",
    'lib.inv.field.label': "التسمية",
    'lib.inv.field.kind': "النوع",
    'lib.inv.field.location': "المكان",
    'lib.inv.field.locationHint': "موقع أو قاعة أو رف بكل ما يحتويه",
    'lib.inv.field.from': "من الرمز",
    'lib.inv.field.to': "إلى الرمز",
    'lib.inv.field.docType': "نوع الوثيقة",
    'lib.inv.allTypes': "كل الأنواع",
    'lib.inv.field.team': "الفريق",
    'lib.inv.field.teamHint': "شخص في كل سطر، «المنطقة: الاسم» لتوزيع المناطق",
    'lib.inv.back': "الجرود",
    'lib.inv.openedLine': "فتح في {date} من طرف {by} · {scope} · تستمر الإعارة أثناء العد",
    'lib.inv.report': "المحضر",
    'lib.inv.count': "العد على اللوحة",
    'lib.inv.close': "إغلاق الحملة",
    'lib.inv.closedOn': "أغلقت الحملة في {date}.",
    'lib.inv.kpi.expected': "المنتظر",
    'lib.inv.kpi.expectedNote': "مثبتة عند الفتح",
    'lib.inv.kpi.seen': "المرئي",
    'lib.inv.kpi.gaps': "الفوارق",
    'lib.inv.kpi.gapsNote': "{missing} ناقص، {misplaced} في غير مكانه",
    'lib.inv.kpi.value': "قيمة الناقص",
    'lib.inv.kpi.valueNote': "ثمن الشراء وإلا جزافي حسب النوع",
    'lib.inv.byZone': "حسب المنطقة",
    'lib.inv.byZoneSub': "عدة أعوان يعدون بالتوازي في مناطق مختلفة",
    'lib.inv.gaps': "الفوارق للمعالجة",
    'lib.inv.gapsSub': "يبقى الناقص مبحوثا عنه حتى الحملة الموالية",
    'lib.inv.allGaps': "الكل",
    'lib.inv.noGap': "لا يوجد فارق.",
    'lib.inv.gap.missing': "ناقص",
    'lib.inv.gap.misplaced': "في غير مكانه",
    'lib.inv.gap.unknown': "غير معروف",
    'lib.inv.gap.present_loaned': "مسجل معارا",
    'lib.inv.gap.present_lost': "مفقود عثر عليه",
    'lib.inv.gap.present_missing': "ناقص عثر عليه",
    'lib.inv.gap.condition': "حالة للمراجعة",
    'lib.inv.detail.missing': "منتظر في {location}",
    'lib.inv.detail.misplaced': "رئي في {seen}، منتظر في {location}",
    'lib.inv.detail.unknown': "غير موجود في القاعدة، رئي في {seen}",
    'lib.inv.unknownTitle': "رمز غير موجود في القاعدة",
    'lib.inv.treat': "معالجة",
    'lib.inv.treatTitle': "معالجة الفارق",
    'lib.inv.action': "الإجراء",
    'lib.inv.apply': "تطبيق",
    'lib.inv.do.search': "بحث موجه",
    'lib.inv.do.ignore': "تجاهل",
    'lib.inv.do.relocate': "تصحيح المكان",
    'lib.inv.do.close_loan': "إغلاق إعارة منسية",
    'lib.inv.do.reintegrate': "إعادة إلى الرصيد",
    'lib.inv.do.create': "إنشاء النسخة",
    'lib.inv.doHint.search': "تبقى النسخة مبحوثا عنها وتصبح ناقصة عند الإغلاق",
    'lib.inv.doHint.ignore': "يسجل الفارق معالجا دون تغيير",
    'lib.inv.doHint.relocate': "يصبح مكان النسخة حيث رئيت",
    'lib.inv.doHint.close_loan': "تغلق الإعارة بتاريخ المسح دون غرامة",
    'lib.inv.doHint.reintegrate': "تعود النسخة متاحة",
    'lib.inv.doHint.create': "نسخة من البطاقة المختارة بهذا الرمز",
    'lib.inv.findEdition': "البحث عن البطاقة",
    'lib.inv.search': "بحث",
    'lib.inv.closeTitle': "إغلاق الحملة",
    'lib.inv.closeSub': "{count} نسخة ستسجل ناقصة وتبقى مبحوثا عنها",
    'lib.inv.withdraw': "إخراج الناقص المستمر من الرصيد",
    'lib.inv.withdrawHint': "الناقص منذ الحملة السابقة دفعة واحدة مع سبب",
    'lib.inv.reason': "السبب",
    'lib.inv.closeNote': "إغلاق الحملة مخصص لحق إغلاق الجرد: يمكن إخراج الناقص المستمر دفعة واحدة مع سبب، ويعطي المحضر المنتظر والموجود والناقص وقيمة الخسائر.",
    'lib.scan2.back': "العودة إلى الحملة",
    'lib.scan2.noShelf': "امسح ملصق الرف أولا",
    'lib.scan2.online': "متصل · {count} في الانتظار",
    'lib.scan2.offline': "غير متصل · {count} مسح في الانتظار",
    'lib.scan2.closed': "هذه الحملة مغلقة: انتهى العد.",
    'lib.scan2.scanShelf': "الرف",
    'lib.scan2.scanItems': "النسخ",
    'lib.scan2.hint': "الكاميرا أو القارئ؛ رمز رف يغير الرف",
    'lib.scan2.pick': "أو اختره",
    'lib.scan2.choose': "اختيار رف",
    'lib.scan2.shelfFirst': "امسح الرف أولا: هذا الرمز ليس مكانا.",
    'lib.scan2.expected': "المنتظر هنا",
    'lib.scan2.seen': "المرئي",
    'lib.scan2.left': "يبقى {count}",
    'lib.scan2.bad': "الفوارق",
    'lib.scan2.badNote': "غير موجودة في القاعدة",
    'lib.scan2.recent': "آخر المسوحات",
    'lib.scan2.noneYet': "لا مسح على هذا الرف.",
    'lib.scan2.status.queued': "في الانتظار",
    'lib.scan2.status.seen': "مرئي",
    'lib.scan2.status.unknown': "غير موجود في القاعدة",
    'lib.scan2.status.twice': "ممسوح سابقا",
    'lib.scan2.changeShelf': "تغيير الرف",
    'lib.scan2.done': "انتهى الرف",
    'lib.mine.title': "مكتبتي",
    'lib.mine.sub': "{name} · {category}",
    'lib.mine.catalog': "الفهرس",
    'lib.mine.welcome': "لست قارئا بعد",
    'lib.mine.welcomeText': "توجه إلى شباك المكتبة لفتح انخراطك وتسلم بطاقتك. يبقى الفهرس متاحا للتصفح.",
    'lib.mine.loans': "إعاراتي ({count})",
    'lib.mine.noLoans': "لا توجد إعارة جارية.",
    'lib.mine.due': "للإرجاع في {date}",
    'lib.mine.dueToday': "للإرجاع اليوم",
    'lib.mine.today': "اليوم",
    'lib.mine.late': "متأخر {count} يوم، كان للإرجاع في {date}",
    'lib.mine.renew': "تمديد",
    'lib.mine.renewed': "تم تمديد «{title}».",
    'lib.mine.declareLoss': "التصريح بضياع «{title}»",
    'lib.mine.lossShort': "التصريح بضياع",
    'lib.mine.lossTitle': "التصريح بضياع",
    'lib.mine.lossText': "تغلق الإعارة كضياع وتتوقف غرامات التأخير. يفوتر لك الاستبدال أو ثمنه ورسوم المعالجة. إن عثرت على الكتاب فأرجعه إلى الشباك.",
    'lib.mine.lossConfirm': "تأكيد الضياع",
    'lib.mine.lossDone': "تم تسجيل الضياع.",
    'lib.mine.holds': "حجوزاتي",
    'lib.mine.noHolds': "لا توجد حجوزات.",
    'lib.mine.holdRank': "الرتبة {rank} في الطابور · سيتم إخبارك",
    'lib.mine.holdReady': "في انتظارك حتى {date}",
    'lib.mine.cancel': "إلغاء",
    'lib.mine.holdCancelled': "تم إلغاء حجز «{title}».",
    'lib.mine.account': "حسابي",
    'lib.mine.debt': "{amount} للأداء",
    'lib.mine.noDebt': "لا ديون",
    'lib.mine.until': "الانخراط حتى {date}",
    'lib.mine.noMembership': "لا انخراط ساري",
    'lib.mine.pendingMembership': "انخراط في انتظار الأداء",
    'lib.mine.suspended': "الإعارة موقوفة: {reason}",
    'lib.mine.penalty': "غرامة تأخير: لا إعارة جديدة قبل {date}.",
    'lib.mine.payDesk': "للأداء لدى المالية",
    'lib.mine.invoice': "الفاتورة {number}",
    'lib.mine.clearance': "تحميل إبراء ذمتي",
    'lib.mine.notClear': "يمنح الإبراء دون إعارة جارية أو مبلغ مستحق ({loans} إعارة جارية).",
    'lib.mine.history': "السجل",
    'lib.mine.show': "عرض ({count})",
    'lib.mine.hide': "إخفاء",
    'lib.mine.keepHistory': "الاحتفاظ بسجل إعاراتي",
    'lib.mine.keepHistoryHint': "عند إلغاء التحديد لا تحفظ إعاراتك المرجعة باسمك",
    'lib.mine.historyOn': "سجلك محفوظ.",
    'lib.mine.historyOff': "لن يحفظ سجلك بعد الآن.",
    'lib.mine.historyLine': "من {from} إلى {to}",
    'lib.mine.card': "بطاقتي الرقمية",
    'lib.mine.noCard': "لا بطاقة بعد",
    'lib.mine.cardTitle': "بطاقة المكتبة",
    'lib.mine.cardValid': "صالحة حتى {date}",
    'lib.mine.qr': "رمز البطاقة",
    'lib.mine.cardHint': "قدم هذا الرمز في شباك الإعارة؛ يتجدد تلقائيا كل عشر دقائق.",
    'lib.mine.suggest': "اقتراح اقتناء",
    'lib.mine.suggestText': "كتاب غير موجود؟ تدرس المكتبة كل اقتراح.",
    'lib.mine.suggestNew': "اقتراح جديد",
    'lib.mine.suggestDone': "تم إرسال الاقتراح.",
    'lib.mine.send': "إرسال",
    'lib.mine.field.title': "العنوان",
    'lib.mine.field.authors': "المؤلفون",
    'lib.mine.field.isbn': "ISBN",
    'lib.mine.field.note': "لماذا هذا الكتاب؟",
    'lib.mine.sugState.suggested': "مقترح",
    'lib.mine.sugState.listed': "مقبول",
    'lib.mine.sugState.ordered': "مطلوب",
    'lib.mine.sugState.received': "مستلم",
    'lib.mine.sugState.refused': "مرفوض",
    'lib.opac.title': "فهرس المكتبة",
    'lib.opac.sub': "العنوان أو المؤلف أو الموضوع أو الرمز أو ISBN",
    'lib.opac.mine': "مكتبتي",
    'lib.opac.search': "البحث في الفهرس",
    'lib.opac.placeholder': "البحث عن كتاب",
    'lib.opac.go': "بحث",
    'lib.opac.filters': "المرشحات",
    'lib.opac.all': "الكل",
    'lib.opac.onlyAvailable': "المتاح",
    'lib.opac.site': "الموقع",
    'lib.opac.allSites': "كل المواقع",
    'lib.opac.found': "{count} نتيجة",
    'lib.opac.latest': "آخر الإضافات",
    'lib.opac.none': "لا يوجد كتاب مطابق.",
    'lib.opac.available': "متاح ({count})",
    'lib.opac.noCopy': "لا نسخة",
    'lib.opac.loaned': "معار",
    'lib.opac.loanedWaiting': "معار · {count} حجز",
    'lib.opac.courses': "ببليوغرافيا دروسي",
    'lib.opac.news': "المستجدات",
    'lib.opac.noNews': "لا مستجدات بعد.",
    'lib.opac.pages': "صفحات النتائج",
    'lib.opac.prev': "السابقة",
    'lib.opac.next': "التالية",
    'lib.opac.pageOf': "الصفحة {page} من {pages}",
    'lib.opac.back': "الفهرس",
    'lib.opac.hold': "حجز",
    'lib.opac.held': "تم الحجز: رتبتك {rank}.",
    'lib.opac.waiting': "{count} قارئ في الانتظار",
    'lib.opac.where': "أين تجده",
    'lib.opac.backOn': "الإرجاع المتوقع في {date}",
    'lib.opac.onsite': "في عين المكان فقط",
    'lib.opac.description': "الوصف",
    'lib.opac.f.isbn': "ISBN",
    'lib.opac.f.cote': "الرمز",
    'lib.opac.f.dewey': "ديوي",
    'lib.opac.f.edition': "الطبعة",
    'lib.opac.f.pages': "الصفحات",
    'lib.opac.f.languages': "اللغات",
    'lib.opac.f.collection': "السلسلة",
    'lib.opac.f.subjects': "المواضيع",
    'lib.opac.f.note': "ملاحظة",
    'lib.opac.digital': "النسخة الرقمية",
    'lib.opac.inCourses': "موصى به في الدروس",
    'lib.opac.volumes': "الأجزاء",
    'lib.opac.volume': "الجزء {n}",
    'lib.verify.clearanceBody': "كان القارئ بريء الذمة تجاه المكتبة بتاريخ {date}.",
    'lib.verify.category': "فئة القارئ",
    'lib.role.author': "مؤلف",
    'lib.role.coauthor': "مؤلف مشارك",
    'lib.role.editor': "مدير النشر",
    'lib.role.translator': "مترجم",
    'lib.role.illustrator': "رسام",
    'lib.role.prefacer': "كاتب التقديم",
    'lib.role.publisher': "ناشر",
    'lib.role.subject': "موضوع",
    'lib.loanStatus.loanable': "قابل للإعارة",
    'lib.loanStatus.onsite': "اطلاع في عين المكان",
    'lib.loanStatus.excluded': "مستثنى من الإعارة",
    'lib.loanStatus.short': "إعارة قصيرة",
    'lib.condition.new': "جديد",
    'lib.condition.good': "جيد",
    'lib.condition.worn': "مستعمل",
    'lib.condition.damaged': "متضرر",
    'lib.condition.repair': "للإصلاح",
    'lib.kind.author': "المؤلفون",
    'lib.kind.publisher': "الناشرون",
    'lib.kind.collection': "السلاسل",
    'lib.kind.series': "المجموعات",
    'lib.kind.subject': "المواضيع",
    'lib.lang.fr': "الفرنسية",
    'lib.lang.ar': "العربية",
    'lib.lang.en': "الإنجليزية",
    'lib.lang.es': "الإسبانية",
    'lib.lang.de': "الألمانية",
    'lib.lang.zgh': "الأمازيغية",
    'lib.acq.purchase': "شراء",
    'lib.acq.gift': "هبة",
    'lib.acq.deposit': "إيداع",
    'lib.acq.exchange': "تبادل",
    'lib.acq.legacy': "استرجاع من النظام القديم",
    'lib.dewey.0': "000 المعلوميات والعموميات",
    'lib.dewey.1': "100 الفلسفة وعلم النفس",
    'lib.dewey.2': "200 الدين",
    'lib.dewey.3': "300 العلوم الاجتماعية",
    'lib.dewey.4': "400 اللغات",
    'lib.dewey.5': "500 العلوم والرياضيات",
    'lib.dewey.6': "600 التكنولوجيا",
    'lib.dewey.7': "700 الفنون",
    'lib.dewey.8': "800 الأدب",
    'lib.dewey.9': "900 التاريخ والجغرافيا",
    'lib.event.created': "دخل الرصيد",
    'lib.event.moved': "نقل",
    'lib.event.loaned': "أعير",
    'lib.event.returned': "أرجع",
    'lib.event.renewed': "مدد",
    'lib.event.condition': "تغيرت الحالة",
    'lib.event.held': "وضع جانبا لحجز",
    'lib.event.transit': "أرسل في الطريق",
    'lib.event.repair': "أرسل للإصلاح",
    'lib.event.lost': "صرح بضياعه",
    'lib.event.found': "عثر عليه",
    'lib.event.missing': "ناقص في الجرد",
    'lib.event.seen': "روئي في الجرد",
    'lib.event.withdrawn': "أخرج من الرصيد",
    'lib.event.imported': "استورد",
    'lib.cat.title': "الفهرس",
    'lib.cat.sub': "{count} بطاقة · البحث لا يميز الحركات ويجد الأشكال الأخرى لأسماء المؤلفين",
    'lib.cat.authorities': "المراجع",
    'lib.cat.locations': "الأماكن",
    'lib.cat.new': "فهرسة",
    'lib.cat.search': "البحث في الفهرس",
    'lib.cat.searchHint': "العنوان أو المؤلف أو ISBN أو الرمز",
    'lib.cat.searchGo': "بحث",
    'lib.cat.flags': "مرشحات",
    'lib.cat.flag.all': "الكل",
    'lib.cat.flag.incomplete': "للإتمام",
    'lib.cat.flag.noitems': "بدون نسخة",
    'lib.cat.flag.rental': "خاضع للكراء",
    'lib.cat.flag.new': "المستجدات",
    'lib.cat.facets': "تدقيق",
    'lib.cat.facet.type': "النوع",
    'lib.cat.facet.availability': "التوفر",
    'lib.cat.facet.language': "اللغة",
    'lib.cat.facet.site': "الموقع",
    'lib.cat.availableOnly': "متاح في الرف",
    'lib.cat.export': "تصدير الفهرس",
    'lib.cat.exportGo': "تحميل",
    'lib.cat.format.csv': "جدول (CSV)",
    'lib.cat.format.marc21': "MARC21",
    'lib.cat.format.unimarc': "UNIMARC",
    'lib.cat.format.ris': "RIS",
    'lib.cat.format.bibtex': "BibTeX",
    'lib.cat.format.dc': "Dublin Core",
    'lib.cat.found': "{count} بطاقة",
    'lib.cat.forQuery': "لـ «{q}»",
    'lib.cat.none': "لا توجد بطاقة مطابقة.",
    'lib.cat.noCopy': "لا توجد نسخة",
    'lib.cat.availability': "{available} من {copies} متاحة",
    'lib.cat.waiting': "{count} حجز",
    'lib.cat.rental': "كراء",
    'lib.cat.confidential': "سري",
    'lib.cat.incomplete': "للإتمام",
    'lib.cat.open': "فتح",
    'lib.cat.pages': "صفحات النتائج",
    'lib.cat.prev': "السابق",
    'lib.cat.next': "التالي",
    'lib.cat.pageOf': "الصفحة {page} من {pages}",
    'lib.ed.complete': "بطاقة كاملة",
    'lib.ed.incomplete': "بطاقة للإتمام",
    'lib.ed.people': "المسؤوليات",
    'lib.ed.edition': "الطبعة",
    'lib.ed.originalYear': "الصدور الأول {year}",
    'lib.ed.collection': "السلسلة",
    'lib.ed.set': "المجموعة والأجزاء",
    'lib.ed.volume': "الجزء {n}",
    'lib.ed.volumes': "{count} جزء مرتبط",
    'lib.ed.missingVolumes': "الأجزاء الناقصة: {list}",
    'lib.ed.ids': "المعرفات",
    'lib.ed.description': "الوصف",
    'lib.ed.pages': "{count} صفحة",
    'lib.ed.indexing': "التكشيف",
    'lib.ed.cote': "الرمز {cote}",
    'lib.ed.subjects': "المواضيع: {list}",
    'lib.ed.languages': "اللغات",
    'lib.ed.courses': "بيبليوغرافيا الدروس",
    'lib.ed.summary': "الملخص",
    'lib.ed.publicNote': "ملاحظة عامة",
    'lib.ed.internalNote': "ملاحظة داخلية",
    'lib.ed.thesis': "مذكرة أو أطروحة",
    'lib.ed.director': "تحت إشراف {name}",
    'lib.ed.digital': "النسخة الرقمية",
    'lib.ed.items': "النسخ",
    'lib.ed.itemsSub': "{count} نسخة · يحسب التوفر منها وليس من عداد",
    'lib.ed.addItems': "إضافة نسخ",
    'lib.ed.addItemsSub': "تتبع الرموز القناع أو تستعمل ملصقات موجودة",
    'lib.ed.addGo': "إضافة",
    'lib.ed.added': "أضيفت {count} نسخة.",
    'lib.ed.labels': "الملصقات",
    'lib.ed.withdraw': "إخراج ({count})",
    'lib.ed.noItems': "لا توجد نسخة: أضف واحدة لتصبح البطاقة قابلة للإعارة.",
    'lib.ed.select': "التحديد",
    'lib.ed.selectItem': "تحديد {code}",
    'lib.ed.barcode': "الرمز الشريطي",
    'lib.ed.coteCol': "الرمز",
    'lib.ed.location': "المكان",
    'lib.ed.loanStatus': "وضع الإعارة",
    'lib.ed.situation': "الوضعية",
    'lib.ed.condition': "الحالة",
    'lib.ed.until': "إلى {date}",
    'lib.ed.availability': "التوفر",
    'lib.ed.availableOf': "{available} متاحة من {total}",
    'lib.ed.holds': "الحجوزات",
    'lib.ed.noHold': "لا شيء",
    'lib.ed.reserve': "حجز لقارئ",
    'lib.ed.reserveSub': "توضع له أول نسخة متاحة",
    'lib.ed.reserveGo': "حجز",
    'lib.ed.held': "سجل الحجز: الرتبة {rank}.",
    'lib.ed.rank': "الرتبة {rank}",
    'lib.ed.reader': "القارئ",
    'lib.ed.readerSearch': "الاسم أو رقم البطاقة",
    'lib.ed.readerChange': "تغيير",
    'lib.ed.rental': "الكراء",
    'lib.ed.noRental': "هذه الطبعة غير خاضعة للكراء.",
    'lib.ed.rentalLoan': "{amount} لكل إعارة",
    'lib.ed.rentalDay': "{amount} لكل يوم",
    'lib.ed.rentalFree': "مجاني لـ: {list}",
    'lib.ed.rentalHint': "يضبط في البطاقة أو على نسخة محددة.",
    'lib.ed.record': "البطاقة",
    'lib.ed.loans': "الإعارات",
    'lib.ed.lastLoan': "آخرها في {date}",
    'lib.ed.edit': "تعديل البطاقة",
    'lib.ed.duplicate': "نسخ لطبعة أخرى",
    'lib.ed.duplicateVolume': "نسخ للجزء الموالي",
    'lib.ed.delete': "حذف البطاقة",
    'lib.ed.deleteTitle': "حذف هذه البطاقة؟",
    'lib.ed.deleteText': "لا نسخ ولا حجوزات لهذه البطاقة؛ تحذف معها مسؤولياتها وروابطها.",
    'lib.ed.withdrawTitle': "إخراج {count} نسخة",
    'lib.ed.withdrawSub': "قرار معلل: تبقى النسخة بسجلها ولا يعاد إسناد رمزها.",
    'lib.ed.withdrawGo': "إخراج",
    'lib.ed.withdrawn': "أخرجت {count} نسخة.",
    'lib.ed.reason': "السبب",
    'lib.ed.count': "عدد النسخ",
    'lib.ed.site': "الموقع المالك",
    'lib.ed.locationHint': "إن ترك فارغا يقترح مخطط الترتيب مكانا حسب الرمز.",
    'lib.ed.locationAuto': "يقترحه مخطط الترتيب",
    'lib.ed.barcodes': "رموز موجودة (اختياري)",
    'lib.ed.barcodesHint': "رمز في كل سطر؛ وإلا يولدها القناع.",
    'lib.form.new': "فهرسة طبعة",
    'lib.form.edit': "تعديل البطاقة",
    'lib.form.sub': "البطاقة عبر ISBN ثم مجموعات البطاقة",
    'lib.form.save': "حفظ",
    'lib.form.saveAnyway': "حفظ رغم ذلك",
    'lib.form.duplicate': "هذا الرقم مفهرس.",
    'lib.form.sections': "مجموعات البطاقة",
    'lib.form.sec.title': "العنوان",
    'lib.form.sec.people': "المسؤوليات",
    'lib.form.sec.edition': "الطبعة",
    'lib.form.sec.collection': "السلسلة",
    'lib.form.sec.set': "المجموعة والأجزاء",
    'lib.form.sec.ids': "المعرفات",
    'lib.form.sec.description': "الوصف المادي",
    'lib.form.sec.content': "المحتوى",
    'lib.form.sec.indexing': "التكشيف",
    'lib.form.sec.thesis': "مذكرة أو أطروحة",
    'lib.form.sec.rental': "الكراء",
    'lib.form.sec.media': "الوسائط",
    'lib.form.sec.notes': "الملاحظات",
    'lib.form.byIsbn': "البطاقة عبر ISBN",
    'lib.form.isbn': "ISBN",
    'lib.form.isbnHint': "امسح أو اكتب الرقم: تملأ كيلاسي البطاقة مسبقا.",
    'lib.form.lookup': "بحث",
    'lib.form.looking': "جار البحث…",
    'lib.form.isbnInvalid': "رقم غير صالح.",
    'lib.form.notFound': "لم يجد أي مصدر هذا الرقم: الإدخال يدوي.",
    'lib.form.found': "وجد ({source}): الحقول معبأة، تحقق ثم احفظ.",
    'lib.form.source.openLibrary': "Open Library",
    'lib.form.source.bnf': "BnF",
    'lib.form.source.google': "Google Books",
    'lib.form.already': "توجد بطاقة لهذا الرقم:",
    'lib.form.title': "العنوان",
    'lib.form.subtitle': "العنوان الفرعي",
    'lib.form.parallelTitle': "العنوان الموازي",
    'lib.form.originalTitle': "العنوان الأصلي",
    'lib.form.docType': "نوع الوثيقة",
    'lib.form.peopleHint': "مراجع مشتركة وليس نصا حرا؛ الترتيب هو ترتيب العرض.",
    'lib.form.publishers': "الناشرون",
    'lib.form.listHint': "مفصولة بفواصل؛ الاسم الجديد ينشئ مرجعه.",
    'lib.form.noPeople': "لا مؤلف بعد.",
    'lib.form.newAuthority': "مرجع جديد",
    'lib.form.existingAuthority': "مرجع موجود",
    'lib.form.role': "الدور",
    'lib.form.up': "رفع",
    'lib.form.remove': "إزالة",
    'lib.form.findAuthor': "البحث عن مؤلف أو شكل آخر لاسمه",
    'lib.form.newAuthor': "مؤلف جديد",
    'lib.form.variants': "الأشكال الأخرى: {list}",
    'lib.form.place': "مكان النشر",
    'lib.form.editionStatement': "بيان الطبعة",
    'lib.form.year': "سنة النشر",
    'lib.form.originalYear': "سنة الصدور الأصلية",
    'lib.form.detail.editionNumber': "رقم الطبعة",
    'lib.form.detail.legalDeposit': "الإيداع القانوني",
    'lib.form.detail.reprint': "إعادة الطبع",
    'lib.form.detail.ean': "EAN",
    'lib.form.detail.nationalNumber': "الرقم الوطني",
    'lib.form.detail.internalId': "المعرف الداخلي",
    'lib.form.detail.illustrations': "الرسوم",
    'lib.form.detail.dimensions': "المقاسات",
    'lib.form.detail.accompanying': "مرفقات (قرص، خريطة)",
    'lib.form.detail.level': "المستوى",
    'lib.form.collection': "السلسلة",
    'lib.form.collectionHint': "مرجع مشترك: الاسم الجديد ينشئه.",
    'lib.form.collectionLinked': "مرتبطة بالمرجع؛ كتابة اسم آخر تستبدله.",
    'lib.form.collectionNumber': "الرقم في السلسلة",
    'lib.form.volumeNumber': "رقم الجزء",
    'lib.form.volumeTitle': "عنوان الجزء",
    'lib.form.volumesTotal': "عدد الأجزاء",
    'lib.form.volumesHint': "في بطاقة المجموعة: تظهر الأجزاء الناقصة.",
    'lib.form.pages': "الصفحات",
    'lib.form.languages': "اللغات",
    'lib.form.languagesHint': "رموز ISO 639 مفصولة بفواصل",
    'lib.form.audience': "الجمهور",
    'lib.form.summary': "الملخص",
    'lib.form.dewey': "رقم ديوي",
    'lib.form.deweyHint': "مثال 361.3",
    'lib.form.cote': "الرمز",
    'lib.form.coteHint': "رمز الترتيب المطبوع على الملصق",
    'lib.form.subjects': "المواضيع والكلمات المفتاحية",
    'lib.form.thesisStudent': "المؤلف (طالب)",
    'lib.form.thesisDirector': "المشرف (أستاذ)",
    'lib.form.thesisYear': "السنة الجامعية",
    'lib.form.thesisDefended': "نوقش في",
    'lib.form.personSearch': "اسم الشخص",
    'lib.form.confidential': "سري",
    'lib.form.confidentialHint': "يمنع الإعارة والاطلاع العام.",
    'lib.form.rental': "إخضاع هذه الطبعة للكراء",
    'lib.form.rentalHint': "كل نسخها؛ تنشئ الإعارة المستحق في المالية.",
    'lib.form.rentalMode': "التعريفة",
    'lib.form.rentalPerLoan': "لكل إعارة",
    'lib.form.rentalPerDay': "لكل يوم",
    'lib.form.rentalAmount': "المبلغ",
    'lib.form.rentalFree': "مجاني لـ",
    'lib.form.cover': "الغلاف",
    'lib.form.coverHint': "JPEG أو PNG أو WebP",
    'lib.form.digitalUrl': "ملف رقمي أو رابط",
    'lib.form.publicNote': "ملاحظة عامة",
    'lib.form.internalNote': "ملاحظة داخلية",
    'lib.form.internalHint': "لا تظهر أبدا في البوابة.",
    'lib.item.title': "النسخة {code}",
    'lib.item.labels': "طباعة الملصقات",
    'lib.item.withdraw': "إخراج من الرصيد",
    'lib.item.edit': "تعديل",
    'lib.item.card': "البطاقة",
    'lib.item.never': "لا يعاد إسناده",
    'lib.item.inventory': "رقم الجرد",
    'lib.item.suggested': "المكان المقترح",
    'lib.item.suggestedHint': "مخطط الترتيب: {path}",
    'lib.item.sites': "الموقع",
    'lib.item.sitesValue': "المالك {owner} · الحالي {current}",
    'lib.item.acquisition': "الاقتناء",
    'lib.item.withdrawnAt': "أخرج في",
    'lib.item.toRepair': "إرسال للإصلاح",
    'lib.item.toRepairDone': "أرسلت النسخة للإصلاح.",
    'lib.item.repaired': "أصلحت، إرجاع للرف",
    'lib.item.repairedDone': "أعيدت النسخة للرف.",
    'lib.item.arrived': "وصلت لموقعها",
    'lib.item.arrivedDone': "وصلت النسخة.",
    'lib.item.found': "عثر عليها",
    'lib.item.foundDone': "عثر على النسخة.",
    'lib.item.labelsTitle': "الملصقات",
    'lib.item.history': "السجل",
    'lib.item.noHistory': "لا حركة.",
    'lib.item.by': "من طرف {name}",
    'lib.item.due': "الأجل {date}",
    'lib.item.forced': "بالقوة: {reason}",
    'lib.item.loans': "إعارات هذه النسخة",
    'lib.item.noLoans': "لم تعر قط.",
    'lib.item.reader': "القارئ",
    'lib.item.loanedAt': "أعيرت في",
    'lib.item.returnedAt': "أرجعت",
    'lib.item.anonymous': "سجل مجهول",
    'lib.item.saved': "حفظت النسخة.",
    'lib.item.unshelved': "غير مرتبة",
    'lib.item.rfid': "وسم RFID",
    'lib.item.acqMode': "طريقة الاقتناء",
    'lib.item.acqOn': "تاريخ الاقتناء",
    'lib.item.supplier': "المورد أو الواهب",
    'lib.item.price': "الثمن ({currency})",
    'lib.item.invoice': "رقم الفاتورة",
    'lib.item.conditionNote': "معاينة (ضرر)",
    'lib.item.rental': "كراء خاص بهذه النسخة",
    'lib.item.rentalHint': "يعوض كراء البطاقة لهذه النسخة.",
    'lib.auth.title': "المراجع",
    'lib.auth.sub': "مؤلفون وناشرون وسلاسل ومواضيع مشتركة",
    'lib.auth.new': "مرجع جديد",
    'lib.auth.kinds': "أنواع المراجع",
    'lib.auth.search': "البحث عن اسم",
    'lib.auth.none': "لا مراجع.",
    'lib.auth.name': "الاسم",
    'lib.auth.variants': "الأشكال الأخرى",
    'lib.auth.variantsHint': "شكل في كل سطر",
    'lib.auth.uses': "البطاقات",
    'lib.auth.usesCount': "{count} بطاقة",
    'lib.auth.actions': "إجراءات",
    'lib.auth.records': "بطاقاته",
    'lib.auth.edit': "تعديل",
    'lib.auth.merge': "دمج",
    'lib.auth.mergeTitle': "دمج «{name}»",
    'lib.auth.mergeSub': "يلحق المكرر بالمرجع المحتفظ به.",
    'lib.auth.mergeSearch': "المرجع المحتفظ به",
    'lib.auth.mergeGo': "دمج",
    'lib.auth.merged': "تم الدمج: {count}.",
    'lib.auth.saved': "حفظ المرجع.",
    'lib.auth.lastName': "الاسم",
    'lib.auth.firstName': "الاسم الشخصي",
    'lib.auth.dates': "التواريخ",
    'lib.auth.datesHint': "مثال 1332-1406",
    'lib.auth.nationality': "الجنسية",
    'lib.auth.note': "ملاحظة",
    'lib.loc.title': "الأماكن",
    'lib.loc.sub': "شجرة الأماكن ومخطط الترتيب",
    'lib.loc.kinds': "أنواع المستويات",
    'lib.loc.kindsSub': "التسلسلات المسموحة",
    'lib.loc.newSite': "موقع جديد",
    'lib.loc.tree': "الأماكن",
    'lib.loc.empty': "لا أماكن: أنشئ موقعا.",
    'lib.loc.expand': "فتح",
    'lib.loc.collapse': "طي",
    'lib.loc.status.open': "مفتوح",
    'lib.loc.status.closed': "مغلق",
    'lib.loc.status.works': "في الأشغال",
    'lib.loc.pick': "مكان",
    'lib.loc.pickHint': "اختر مكانا من الشجرة.",
    'lib.loc.path': "المسار",
    'lib.loc.code': "الرمز",
    'lib.loc.codeHint': "قصير وفريد",
    'lib.loc.capacity': "السعة التقريبية",
    'lib.loc.capacityValue': "{capacity} نسخة · {count} مرتبة",
    'lib.loc.statusLabel': "الوضع",
    'lib.loc.contains': "يحتوي",
    'lib.loc.containsValue': "{nodes} مستوى · {items} نسخة",
    'lib.loc.bulk': "نقل نسخ إلى هنا",
    'lib.loc.bulkTitle': "ترتيب في «{name}»",
    'lib.loc.bulkScan': "امسح النسخ واحدة تلو الأخرى",
    'lib.loc.bulkEmpty': "لم تمسح أي نسخة.",
    'lib.loc.bulkGo': "نقل {count} نسخة",
    'lib.loc.bulkDone': "نقلت {count} نسخة.",
    'lib.loc.bulkUnknown': "رموز غير معروفة: {list}",
    'lib.loc.move': "نقل العنصر كاملا",
    'lib.loc.moveTitle': "نقل «{name}»",
    'lib.loc.moveSub': "مع كل محتواه.",
    'lib.loc.newParent': "الأصل الجديد",
    'lib.loc.noTarget': "لا مكان يقبل هذا النوع.",
    'lib.loc.moved': "نقل العنصر ({count}).",
    'lib.loc.addChild': "إضافة مستوى أسفله",
    'lib.loc.addChildUnder': "إضافة تحت «{name}»",
    'lib.loc.edit': "تعديل",
    'lib.loc.delete': "حذف",
    'lib.loc.deleteTitle': "حذف «{name}»؟",
    'lib.loc.deleteText': "المكان فارغ.",
    'lib.loc.deleted': "حذف المكان.",
    'lib.loc.saved': "حفظ المكان.",
    'lib.loc.kind': "نوع المستوى",
    'lib.loc.kindChildren': "يمكن أن يحتوي",
    'lib.loc.kindSaved': "حفظت الأنواع.",
    'lib.loc.newKindCode': "نوع جديد: الرمز",
    'lib.loc.newKindLabel': "نوع جديد: التسمية",
    'lib.loc.label': "التسمية",
    'lib.loc.position': "الترتيب",
    'lib.loc.ranges': "مخطط ترتيب ديوي",
    'lib.loc.rangesSub': "مجالات الرموز وأماكنها",
    'lib.loc.addRange': "إضافة مجال",
    'lib.loc.noRange': "لا مجال.",
    'lib.loc.range': "المجال",
    'lib.loc.rangeLabel': "التسمية",
    'lib.loc.shelving': "المكان",
    'lib.loc.from': "من",
    'lib.loc.to': "إلى",
    'lib.loc.deleteRange': "حذف المجال",
    'lib.loc.rangeDeleted': "حذف المجال.",
    'lib.loc.rangeSaved': "حفظ المجال.",
    'lib.loc.unshelved': "{count} نسخة غير مرتبة.",
    'lib.sug.title': "اقتراحات الشراء",
    'lib.sug.sub': "من القراء والأساتذة والتعويضات بعد الضياع",
    'lib.sug.new': "اقتراح جديد",
    'lib.sug.none': "لا اقتراحات.",
    'lib.sug.titleCol': "العنوان",
    'lib.sug.authors': "المؤلفون",
    'lib.sug.origin': "المصدر",
    'lib.sug.date': "التاريخ",
    'lib.sug.quantity': "الكمية",
    'lib.sug.state': "الحالة",
    'lib.sug.note': "ملاحظة",
    'lib.sug.saved': "حدث الاقتراح.",
    'lib.sug.added': "سجل الاقتراح.",
    'lib.sug.st.suggested': "مقترح",
    'lib.sug.st.listed': "في لائحة الشراء",
    'lib.sug.st.ordered': "مطلوب",
    'lib.sug.st.received': "مستلم",
    'lib.sug.st.refused': "مرفوض",
    'lib.sug.from.reader': "قارئ",
    'lib.sug.from.teacher': "أستاذ",
    'lib.sug.from.staff': "المكتبة",
    'lib.sug.from.loss': "تعويض ضياع",
    'lib.population.student': "طالب",
    'lib.population.teacher': "أستاذ",
    'lib.population.staff': "موظف",
    'lib.population.external': "خارجي",
    'lib.formula.year': "السنة الجامعية",
    'lib.formula.twelve_months': "12 شهرا متتالية",
    'lib.formula.semester': "فصل",
    'lib.formula.month': "شهر",
    'lib.formula.day': "يوم",
    'lib.feeKind.membership': "انخراط",
    'lib.feeKind.rental': "كراء",
    'lib.feeKind.late': "غرامة تأخير",
    'lib.feeKind.damage': "إتلاف",
    'lib.feeKind.loss': "ضياع",
    'lib.feeKind.processing': "رسوم المعالجة",
    'lib.feeKind.duplicate': "نسخة بطاقة",
    'lib.feature.subscriptions': "انخراطات مؤداة",
    'lib.feature.subscriptions.hint': "صيغ مؤداة حسب الفئة",
    'lib.feature.rental': "رسوم الكراء",
    'lib.feature.rental.hint': "على الطبعات أو النسخ المحددة",
    'lib.feature.fines': "غرامات مالية",
    'lib.feature.fines.hint': "وإلا أيام توقيف فقط",
    'lib.feature.own_card': "بطاقة مكتبة خاصة",
    'lib.feature.own_card.hint': "معطل: بطاقة الطالب تقوم مقامها",
    'lib.feature.opac': "بوابة القارئ",
    'lib.feature.opac.hint': "الفهرس وفضاء القارئ في البوابة",
    'lib.feature.online_holds': "الحجز الرقمي",
    'lib.feature.online_holds.hint': "من البوابة والتطبيق",
    'lib.feature.sms': "إشعارات نصية",
    'lib.feature.sms.hint': "إضافة إلى البريد والبوابة",
    'lib.penalty.money': "مالية فقط",
    'lib.penalty.suspension': "أيام توقيف فقط",
    'lib.penalty.both': "مالية وأيام توقيف",
    'lib.membership.pending': "في انتظار الأداء",
    'lib.membership.active': "ساري",
    'lib.membership.suspended': "موقوف",
    'lib.membership.expired': "منته",
    'lib.membership.cancelled': "ملغى",
    'lib.cardState.active': "صالحة",
    'lib.cardState.lost': "ضائعة",
    'lib.cardState.replaced': "معوضة",
    'lib.cardState.expired': "منتهية",
    'lib.readers.pages': "الصفحات",
    'lib.readers.prev': "السابقة",
    'lib.readers.next': "التالية",
    'lib.readers.pageOf': "الصفحة {page} من {pages}",
    'lib.reader.noFees': "لا توجد مستحقات.",
    'lib.reader.fee': "مستحق",
    'lib.reader.feeDate': "أصدر في",
    'lib.reader.feeDue': "المبلغ",
    'lib.reader.feeState': "الحالة",
    'lib.reader.actions': "إجراءات",
    'lib.reader.invoice': "فاتورة {number}",
    'lib.reader.receipt': "وصل {number}",
    'lib.reader.pay': "تسجيل الوصل",
    'lib.reader.waive': "إعفاء",
    'lib.reader.cancelFee': "إلغاء",
    'lib.reader.payTitle': "تسجيل وصل المالية",
    'lib.reader.waiveTitle': "الإعفاء من مستحق",
    'lib.reader.cancelFeeTitle': "إلغاء مستحق صدر خطأ",
    'lib.reader.receiptNumber': "رقم الوصل",
    'lib.reader.receiptHint': "الوصل الذي سلمته المالية: المكتبة لا تحصل شيئا.",
    'lib.reader.waiveAmount': "المبلغ ({currency})",
    'lib.reader.reason': "السبب",
    'lib.reader.reasonHint': "إلزامي، يسجل في السجل.",
    'lib.readers.state.all': "الكل",
    'lib.readers.state.late': "متأخر",
    'lib.readers.state.debt': "عليه دين",
    'lib.readers.state.suspended': "موقوفون",
    'lib.readers.state.expired': "بدون انخراط",
    'lib.readers.state.external': "خارجيون",
    'lib.readers.title': "القراء",
    'lib.readers.sub': "الطلبة والأساتذة والموظفون من بطاقاتهم؛ والقراء الخارجيون يسجلون هنا",
    'lib.readers.export': "تصدير",
    'lib.readers.external': "قارئ خارجي",
    'lib.readers.enrol': "تسجيل",
    'lib.readers.search': "الاسم أو رقم البطاقة",
    'lib.readers.category': "الفئة",
    'lib.readers.allCategories': "كل الفئات",
    'lib.readers.state': "الحالة",
    'lib.readers.selected': "{count} محدد",
    'lib.readers.printCards': "طباعة البطاقات",
    'lib.readers.clearSelection': "إلغاء التحديد",
    'lib.readers.count': "{count} قارئ",
    'lib.readers.selectAll': "تحديد الكل",
    'lib.readers.reader': "القارئ",
    'lib.readers.card': "البطاقة",
    'lib.readers.until': "منخرط إلى",
    'lib.readers.loans': "الإعارات",
    'lib.readers.debt': "الدين",
    'lib.readers.status': "الوضع",
    'lib.readers.none': "لا قارئ مطابق.",
    'lib.readers.select': "تحديد {name}",
    'lib.readers.externalTag': "خارجي",
    'lib.readers.noMembership': "بدون انخراط",
    'lib.readers.lateCount': "{count} متأخرة",
    'lib.readers.suspended': "موقوف",
    'lib.readers.active': "نشط",
    'lib.readers.enrolTitle': "تسجيل شخص",
    'lib.readers.enrolSub': "طالب أو أستاذ أو موظف معروف: دون إدخال مزدوج",
    'lib.readers.person': "الشخص",
    'lib.readers.personHint': "اكتب حرفين على الأقل.",
    'lib.readers.noPopulation': "جهة اتصال",
    'lib.readers.already': "قارئ مسبقا",
    'lib.readers.categoryAuto': "افتراضيا فئة مجموعته وسلكه.",
    'lib.readers.categoryDefault': "الفئة الافتراضية",
    'lib.readers.externalTitle': "قارئ خارجي جديد",
    'lib.readers.externalSub': "خريج أو ولي أو باحث أو عموم",
    'lib.readers.create': "إنشاء",
    'lib.readers.lastName': "النسب",
    'lib.readers.firstNames': "الأسماء",
    'lib.readers.nationalId': "وثيقة الهوية",
    'lib.readers.email': "البريد الإلكتروني",
    'lib.readers.phone': "الهاتف",
    'lib.readers.externalHint': "الوثيقة ووسيلة اتصال إلزاميتان. يفتح الانخراط فورا؛ وإن كان مؤدى ينتظر وصله.",
    'lib.reader.title': "القارئ",
    'lib.reader.back': "القراء",
    'lib.reader.cardNumber': "بطاقة {number}",
    'lib.reader.lend': "إعارة",
    'lib.reader.suspendedBecause': "موقوف: {reason}. يرفع التوقيف بزوال السبب.",
    'lib.reader.penaltyUntil': "غرامة تأخير: الإعارة موقوفة حتى {date}.",
    'lib.reader.blockedLate': "إعارة متأخرة {days} يوما: الإعارة ممنوعة.",
    'lib.reader.blockedDebt': "دين {amount} فوق العتبة: الإعارة ممنوعة.",
    'lib.reader.situation': "الوضعية",
    'lib.reader.changeCategory': "تغيير الفئة",
    'lib.reader.lift': "رفع التوقيف",
    'lib.reader.suspend': "توقيف",
    'lib.reader.category': "الفئة",
    'lib.reader.email': "البريد",
    'lib.reader.phone': "الهاتف",
    'lib.reader.debt': "الدين",
    'lib.reader.noDebt': "لا شيء",
    'lib.reader.late': "التأخير",
    'lib.reader.noLate': "لا شيء",
    'lib.reader.historyOptOut': "السجل معطل من القارئ",
    'lib.reader.yes': "نعم",
    'lib.reader.no': "لا",
    'lib.reader.membership': "الانخراط",
    'lib.reader.membershipSub': "تؤدى عبر المالية: لا تحصيل موازي",
    'lib.reader.newMembership': "انخراط جديد",
    'lib.reader.noMembership': "لا انخراط ساري: لا يمكنه الاستعارة.",
    'lib.reader.pending': "انخراط في انتظار أداء {amount} للمالية.",
    'lib.reader.active': "ساري",
    'lib.reader.membershipLine': "{formula}، من {from} إلى {to}",
    'lib.reader.automatic': "تلقائي عند التسجيل",
    'lib.reader.formula': "الصيغة",
    'lib.reader.period': "المدة",
    'lib.reader.amount': "التعريفة",
    'lib.reader.state': "الحالة",
    'lib.reader.free': "مجاني",
    'lib.reader.cancelMembership': "فسخ",
    'lib.reader.card': "البطاقة",
    'lib.reader.cardSub': "رقم فريد لا يعاد إسناده",
    'lib.reader.issueCard': "إصدار البطاقة",
    'lib.reader.lostCard': "التصريح بالضياع",
    'lib.reader.printCard': "طباعة",
    'lib.reader.noCard': "لا بطاقة نشطة.",
    'lib.reader.studentCard': "بطاقة الطالب",
    'lib.reader.libraryCard': "بطاقة المكتبة",
    'lib.reader.validUntil': "صالحة حتى {date}",
    'lib.reader.clearance': "إبراء الذمة",
    'lib.reader.clearanceSub': "خال من أي التزام تجاه المكتبة، قابل للتحقق",
    'lib.reader.issueClearance': "تسليم الإبراء",
    'lib.reader.clear': "مبرأ الذمة",
    'lib.reader.notClear': "لا إبراء: {loans} إعارة جارية، {debt} مستحقة.",
    'lib.reader.open': "فتح",
    'lib.reader.loans': "الإعارات الجارية ({count})",
    'lib.reader.noLoans': "لا إعارة جارية.",
    'lib.reader.item': "النسخة",
    'lib.reader.loanedAt': "أعير في",
    'lib.reader.due': "الأجل",
    'lib.reader.renew': "تمديد",
    'lib.reader.holds': "الحجوزات",
    'lib.reader.noHolds': "لا حجوزات.",
    'lib.reader.pickupUntil': "للسحب قبل {date}",
    'lib.reader.rank': "الرتبة {rank}",
    'lib.reader.fees': "المستحقات",
    'lib.reader.feesSub': "تفوترها المالية للطالب؛ وإلا يسجل الوصل هنا",
    'lib.reader.newFee': "مستحق جديد",
    'lib.reader.reminders': "التذكيرات المرسلة",
    'lib.reader.noReminders': "لا تذكيرات.",
    'lib.reader.manualReminder': "تذكير يدوي",
    'lib.reader.guardian': "وإلى الولي أيضا",
    'lib.reader.history': "سجل الإعارات",
    'lib.reader.historyTraced': "معطى شخصي: الاطلاع عليه مسجل.",
    'lib.reader.showHistory': "عرض السجل",
    'lib.reader.noHistory': "لا إعارات مرجعة.",
    'lib.reader.delete': "حذف القارئ",
    'lib.reader.deleteTitle': "حذف هذا القارئ؟",
    'lib.reader.deleteText': "لا يحذف إلا قارئ بلا إعارة ولا مستحق ولا بطاقة؛ وإلا أوقفه. يبقى الشخص.",
    'lib.reader.lostCardTitle': "التصريح بضياع البطاقة؟",
    'lib.reader.lostCardText': "يلغى الرقم القديم فورا وتصدر نسخة بالرسوم المقررة.",
    'lib.reader.lifted': "رفع التوقيف.",
    'lib.reader.cardIssued': "أصدرت البطاقة.",
    'lib.reader.renewed': "مددت الإعارة.",
    'lib.reader.duplicated': "أصدرت النسخة.",
    'lib.reader.saved': "حفظ.",
    'lib.reader.suspended': "أوقف القارئ.",
    'lib.reader.membershipCreated': "أنشئ الانخراط.",
    'lib.reader.membershipCancelled': "فسخ الانخراط.",
    'lib.reader.feeCreated': "أصدر المستحق.",
    'lib.reader.clearanceIssued': "سلم الإبراء: يوجد في القائمة.",
    'lib.reader.confirm': "تأكيد",
    'lib.reader.categoryHint': "تحدد الفئة سياسة الإعارة والتعريفات.",
    'lib.reader.suspendTitle': "توقيف القارئ",
    'lib.reader.until': "إلى",
    'lib.reader.untilHint': "فارغ: حتى الرفع اليدوي.",
    'lib.reader.startsOn': "البداية",
    'lib.reader.startsOnHint': "فارغ: اليوم أو عند نهاية الانخراط الحالي.",
    'lib.reader.membershipHint': "مجانيا يسري فورا مع البطاقة؛ مؤدى يذهب المستحق إلى المالية.",
    'lib.reader.feeKind': "النوع",
    'lib.reader.feeLabel': "البيان",
    'lib.reader.feeHint': "للطالب تصدر المالية الفاتورة.",
    'lib.reader.purposeLabel': "من أجل",
    'lib.reader.purpose.diploma': "تسليم الشهادة",
    'lib.reader.purpose.departure': "مغادرة",
    'lib.reader.purpose.transfer': "انتقال",
    'lib.reader.purpose.other': "أخرى",
    'lib.set.title': "إعدادات المكتبة",
    'lib.set.subOn': "الوحدة مفعلة",
    'lib.set.subOff': "الوحدة معطلة: القوائم مخفية والمعطيات محفوظة",
    'lib.set.tab.general': "عام",
    'lib.set.tab.policies': "سياسات الإعارة",
    'lib.set.tab.categories': "الفئات والتعريفات",
    'lib.set.tab.calendar': "التقويم والآجال",
    'lib.set.tab.fees': "الرسوم",
    'lib.set.saved': "حفظت الإعدادات.",
    'lib.set.save': "حفظ",
    'lib.set.edit': "تعديل",
    'lib.set.delete': "حذف",
    'lib.set.setup.title': "انطلاق المكتبة",
    'lib.set.setup.sub': "الحد الأدنى قبل أول إعارة: موقع وسياسة افتراضية وصيغة رمز وبطاقة",
    'lib.set.setup.site': "الموقع الأول",
    'lib.set.setup.siteSub': "المواقع الأخرى والأماكن لاحقا",
    'lib.set.setup.siteCode': "رمز الموقع",
    'lib.set.setup.siteCodeHint': "حروف كبيرة وأرقام وشرطات",
    'lib.set.setup.siteLabel': "اسم الموقع",
    'lib.set.setup.numbering': "الرمز الشريطي",
    'lib.set.setup.numberingSub': "بادئة وتسلسل؛ لا يعاد إسناد رمز",
    'lib.set.setup.card': "البطاقة",
    'lib.set.setup.cardSub': "يمكن لبطاقة الطالب أن تقوم مقام بطاقة المكتبة",
    'lib.set.setup.policy': "السياسة الافتراضية",
    'lib.set.setup.policySub': "تسري على كل فئة ووثيقة إلى أن توضع قواعد أدق",
    'lib.set.setup.run': "انطلاق المكتبة",
    'lib.set.cardStudent': "بطاقة الطالب هي بطاقة المكتبة",
    'lib.set.cardLibrary': "بطاقة مكتبة خاصة",
    'lib.set.module': "الوحدة",
    'lib.set.moduleSub': "يفعله مسؤول المؤسسة",
    'lib.set.enabledOn': "مفعل منذ {date}",
    'lib.set.enabled': "المكتبة مفعلة",
    'lib.set.enabledHint': "التعطيل يخفي القوائم ويحفظ المعطيات",
    'lib.set.features': "الوظائف الفرعية",
    'lib.set.featuresSub': "تفعل كل واحدة على حدة",
    'lib.set.penalties': "الغرامات والإبراء",
    'lib.set.penaltyMode': "نظام غرامات التأخير",
    'lib.set.penaltyModeHint': "قابل للتعديل حسب السياسة",
    'lib.set.quitusBlocks': "منع تسليم الشهادة دون إبراء",
    'lib.set.quitusBlocksHint': "معطل افتراضيا؛ يمكن للوحدات الأخرى قراءة الإبراء",
    'lib.set.foundRefund': "نسخة ضائعة وجدت بعد الأداء",
    'lib.set.refund.full': "استرجاع كلي",
    'lib.set.refund.half': "استرجاع جزئي (50 %)",
    'lib.set.refund.none': "لا استرجاع",
    'lib.set.numbering': "الرموز والترقيم",
    'lib.set.barcodePrefix': "بادئة الرموز",
    'lib.set.barcodeDigits': "أرقام التسلسل",
    'lib.set.cardPrefix': "بادئة البطاقات",
    'lib.set.barcodeExample': "الصيغة: {example}",
    'lib.set.privacy': "المعطيات الشخصية",
    'lib.set.historyYears': "إخفاء هوية السجل بعد",
    'lib.set.historyYearsHint': "إلا في حالة دين جار",
    'lib.set.years': "{count} سنة",
    'lib.set.traceHistory': "الاحتفاظ بسجل إعارات القراء",
    'lib.set.offNote': "تعطيل الوحدة يخفي القوائم ويحفظ المعطيات؛ الإعارة الجارية تتطلب تأكيدا صريحا.",
    'lib.set.offTitle': "تعطيل المكتبة؟",
    'lib.set.offText': "تختفي القوائم وتبقى المعطيات وتعود عند التفعيل.",
    'lib.set.offForce': "{count} إعارة جارية: هل تؤكد؟",
    'lib.set.off': "تعطيل",
    'lib.set.offAnyway': "تعطيل رغم الإعارات",
    'lib.set.graceDays': "مهلة السماح (أيام)",
    'lib.set.pickupDays': "أجل السحب (أيام عمل)",
    'lib.set.blockDays': "المنع بعد (أيام تأخير)",
    'lib.set.blockDebt': "المنع فوق ({currency})",
    'lib.set.reminders': "التذكيرات (أيام بالنسبة للأجل)",
    'lib.set.remindersHint': "مثال -2، +1، +7، +30؛ الأخير إنذار مع توقيف",
    'lib.set.lossAfterDays': "افتراض الضياع بعد (أيام)",
    'lib.set.lossAfterDaysHint': "تحسب بعد الإنذار",
    'lib.set.autoMembership': "انخراط تلقائي عند التسجيل",
    'lib.set.autoMembershipHint': "كل تسجيل يفتح الانخراط الذي ينتهي مع السنة",
    'lib.set.cal.open': "أيام الفتح",
    'lib.set.cal.openSub': "أيام الإغلاق لا تحسب تأخيرا",
    'lib.set.cal.closures': "إغلاقات المكتبة",
    'lib.set.cal.closuresSub': "إضافة إلى العطل في التخطيط",
    'lib.set.cal.from': "من",
    'lib.set.cal.to': "إلى",
    'lib.set.cal.label': "السبب",
    'lib.set.cal.remove': "حذف هذا الإغلاق",
    'lib.set.cal.add': "إضافة إغلاق",
    'lib.set.cal.delays': "الآجال والعتبات",
    'lib.set.cal.priority': "أولوية الحجوزات",
    'lib.set.cal.prioritySub': "عند التساوي تتقدم الفئة الأولى",
    'lib.set.cal.up': "رفع",
    'lib.set.cal.down': "إنزال",
    'lib.set.fees.title': "الرسوم",
    'lib.set.fees.sub': "لا كفالة؛ بعض الكتب تكترى",
    'lib.set.processingFee': "رسوم معالجة الضياع ({currency})",
    'lib.set.duplicateFee': "نسخة البطاقة ({currency})",
    'lib.set.fees.rental': "رسوم الكراء",
    'lib.set.rentalPayBefore': "اشتراط الأداء قبل التسليم",
    'lib.set.rentalPayBeforeHint': "وإلا تضاف لحساب القارئ",
    'lib.set.rentalRefund': "استرجاع نسبي عند الإرجاع المبكر",
    'lib.set.fees.rentalHint': "تحدد تعريفة الكراء في البطاقة أو النسخة حسب الفئة.",
    'lib.set.fees.replacement': "أثمنة التعويض الجزافية",
    'lib.set.fees.replacementSub': "حين لا يكون للنسخة ثمن شراء",
    'lib.set.pol.title': "سياسات الإعارة",
    'lib.set.pol.sub': "الفئة × نوع الوثيقة × وضع الإعارة؛ الأدق تطبق",
    'lib.set.pol.new': "قاعدة جديدة",
    'lib.set.pol.editTitle': "تعديل القاعدة",
    'lib.set.pol.category': "الفئة",
    'lib.set.pol.docType': "نوع الوثيقة",
    'lib.set.pol.loanStatus': "الوضع",
    'lib.set.pol.maxLoans': "الإعارات",
    'lib.set.pol.duration': "المدة",
    'lib.set.pol.renewal': "التمديد",
    'lib.set.pol.maxHolds': "الحجوزات",
    'lib.set.pol.penalty': "غرامة التأخير",
    'lib.set.pol.default': "افتراضية",
    'lib.set.pol.allCategories': "كل الفئات",
    'lib.set.pol.allTypes': "كل الأنواع",
    'lib.set.pol.allStatuses': "كل الأوضاع",
    'lib.set.pol.hours': "{count} س",
    'lib.set.pol.days': "{count} ي",
    'lib.set.pol.noRenewal': "لا شيء",
    'lib.set.pol.renewalText': "{count} × {days} ي",
    'lib.set.pol.fineText': "{amount} لليوم، سقف {cap}",
    'lib.set.pol.suspText': "{days} يوم توقيف لكل يوم",
    'lib.set.pol.none': "لا شيء",
    'lib.set.pol.allRules': "لكل القواعد",
    'lib.set.pol.block': "عتبة المنع",
    'lib.set.pol.blockText': "تأخير أكثر من {days} يوما أو دين يفوق {amount}",
    'lib.set.pol.openDays': "{count} أيام عمل",
    'lib.set.pol.yearEnd': "نهاية السنة",
    'lib.set.pol.yearEndText': "لا يتجاوز الأجل نهاية الانخراط",
    'lib.set.pol.calendar': "التقويم",
    'lib.set.pol.calendarText': "الإغلاقات والعطل لا تحسب",
    'lib.set.pol.daysLabel': "المدة (أيام)",
    'lib.set.pol.hoursLabel': "أو المدة (ساعات)",
    'lib.set.pol.renewals': "التمديدات",
    'lib.set.pol.renewalDays': "مدة التمديد (أيام)",
    'lib.set.pol.finePerDay': "الغرامة لليوم ({currency})",
    'lib.set.pol.fineCap': "السقف ({currency})",
    'lib.set.pol.suspensionPerDay': "أيام التوقيف لكل يوم تأخير",
    'lib.set.pol.modeHint': "فارغ: نظام المؤسسة",
    'lib.set.pol.modeInherit': "نظام المؤسسة",
    'lib.set.pol.maxByType': "سقوف حسب النوع",
    'lib.set.pol.maxByTypeHint': "مثال media=2",
    'lib.set.pol.defaultHint': "القاعدة الافتراضية لا تحذف.",
    'lib.set.cat.title': "فئات القراء",
    'lib.set.cat.sub': "تحدد الفئة السياسة والتعريفات",
    'lib.set.cat.new': "فئة جديدة",
    'lib.set.cat.editTitle': "تعديل الفئة",
    'lib.set.cat.label': "التسمية",
    'lib.set.cat.code': "الرمز",
    'lib.set.cat.codeHint': "حروف صغيرة؛ لا يتغير",
    'lib.set.cat.population': "الفئة السكانية",
    'lib.set.cat.cycle': "السلك",
    'lib.set.cat.cycleHint': "للطلبة: فئة هذا السلك",
    'lib.set.cat.anyCycle': "كل الأسلاك",
    'lib.set.cat.formula': "الصيغة",
    'lib.set.cat.fee': "الانخراط",
    'lib.set.cat.feeLabel': "تعريفة الانخراط ({currency})",
    'lib.set.cat.feeHint': "0: مجاني",
    'lib.set.cat.readers': "القراء",
    'lib.stats.title': "الإحصائيات",
    'lib.stats.back': "لوحة القيادة",
    'lib.stats.sub': "من {from} إلى {to}",
    'lib.stats.register': "سجل الجرد {year}",
    'lib.stats.exportLoans': "تصدير الإعارات",
    'lib.stats.from': "من",
    'lib.stats.to': "إلى",
    'lib.stats.apply': "عرض",
    'lib.stats.loans': "الإعارات",
    'lib.stats.loansNote': "خلال المدة",
    'lib.stats.activeReaders': "القراء النشطون",
    'lib.stats.activeReadersNote': "إعارة واحدة على الأقل",
    'lib.stats.rotation': "معدل الدوران",
    'lib.stats.rotationNote': "إعارات لكل نسخة ({items} نسخة)",
    'lib.stats.lateRate': "معدل التأخير",
    'lib.stats.lateRateNote': "إعارات أرجعت بعد الأجل",
    'lib.stats.neverLoaned': "لم تعر قط",
    'lib.stats.neverLoanedNote': "نسخ",
    'lib.stats.value': "قيمة الرصيد",
    'lib.stats.valueNote': "ثمن الشراء وإلا الجزافي",
    'lib.stats.byMonth': "الإعارات حسب الشهر",
    'lib.stats.top': "الأكثر استعارة",
    'lib.stats.work': "الكتاب",
    'lib.stats.empty': "لا إعارات خلال المدة.",
    'lib.stats.byCategory': "حسب فئة القارئ",
    'lib.stats.byTraining': "حسب التكوين",
    'lib.stats.byType': "حسب نوع الوثيقة",
    'lib.stats.byClass': "الرصيد حسب فئة ديوي",
    'lib.stats.byClassSub': "النسخ الموجودة",
    'lib.stats.noDewey': "بدون رقم",
    'lib.imp.title': "الاستيراد والتصدير",
    'lib.imp.sub': "كل استيراد يحاكى سطرا سطرا قبل أي كتابة",
    'lib.imp.upload': "استيراد ملف",
    'lib.imp.uploadSub': "تبين المحاكاة ما سينشأ أو يحدث أو يرفض",
    'lib.imp.target': "الموضوع",
    'lib.imp.format': "الصيغة",
    'lib.imp.file': "الملف",
    'lib.imp.fileHint': "CSV أو ISO 2709 أو MARCXML؛ 30 ميغا كحد أقصى",
    'lib.imp.sending': "إرسال…",
    'lib.imp.simulate': "محاكاة الاستيراد",
    'lib.imp.template': "نموذج CSV",
    'lib.imp.note': "يلغى الاستيراد ما لم تمس إعارة نسخه. تحييد HTML وتفرد الرموز والتحقق من ISBN قبل الكتابة.",
    'lib.imp.exports': "تصدير",
    'lib.imp.exportsSub': "في حدود صلاحيات من يطلقه",
    'lib.imp.recent': "الاستيرادات الأخيرة",
    'lib.imp.none': "لا استيراد بعد.",
    'lib.imp.when': "أودع",
    'lib.imp.result': "النتيجة",
    'lib.imp.state': "الحالة",
    'lib.imp.countsLine': "{create} منشأة، {update} محدثة، {reject} مرفوضة",
    'lib.imp.back': "الاستيراد والتصدير",
    'lib.imp.rows': "{count} سطر",
    'lib.imp.mode': "النمط",
    'lib.imp.key': "مفتاح المطابقة",
    'lib.imp.site': "الموقع الافتراضي",
    'lib.imp.siteHint': "يحدد مخطط الترتيب المكان حسب الرمز",
    'lib.imp.siteFirst': "الموقع الأول",
    'lib.imp.columns': "مطابقة الأعمدة",
    'lib.imp.columnsSub': "تحفظ مع الاستيراد؛ العمود المتجاهل لا يقرأ",
    'lib.imp.column': "عمود الملف",
    'lib.imp.field': "حقل كلاسي",
    'lib.imp.fieldFor': "حقل للعمود {column}",
    'lib.imp.ignore': "متجاهل",
    'lib.imp.resimulate': "إعادة المحاكاة",
    'lib.imp.resimulated': "أعيدت المحاكاة.",
    'lib.imp.marc': "بطاقات MARC",
    'lib.imp.marcSub': "تقرأ حقول MARC تلقائيا",
    'lib.imp.marcText': "العنوان والمؤلفون والناشر وISBN وديوي والنسخ.",
    'lib.imp.created': "منشأة",
    'lib.imp.createdNote': "بطاقات جديدة",
    'lib.imp.updated': "محدثة",
    'lib.imp.updatedNote': "مطابقة",
    'lib.imp.rejected': "مرفوضة",
    'lib.imp.rejectedNote': "تقرير قابل للتحميل",
    'lib.imp.simulation': "المحاكاة سطرا سطرا",
    'lib.imp.simulationSub': "لم يكتب شيء بعد",
    'lib.imp.report': "التقرير سطرا سطرا",
    'lib.imp.lines': "الأسطر",
    'lib.imp.line': "السطر",
    'lib.imp.record': "البطاقة",
    'lib.imp.filter.all': "الكل",
    'lib.imp.filter.create': "منشأة",
    'lib.imp.filter.update': "محدثة",
    'lib.imp.filter.reject': "مرفوضة",
    'lib.imp.downloadReport': "تقرير الأسطر",
    'lib.imp.run': "استيراد {count} سطر",
    'lib.imp.ran': "نفذ الاستيراد.",
    'lib.imp.undo': "إلغاء الاستيراد",
    'lib.imp.undone': "ألغي الاستيراد.",
    'lib.imp.undoTitle': "إلغاء هذا الاستيراد؟",
    'lib.imp.undoText': "تحذف نسخه {count} والبطاقات التي أنشأها.",
    'lib.imp.dirty': "تغيرت الإعدادات: أعد المحاكاة قبل الاستيراد.",
    'lib.imp.touched': "{count} إعارة مست نسخه: لا يلغى الاستيراد.",
    'lib.imp.target.records': "البطاقات والنسخ",
    'lib.imp.target.items': "النسخ (حسب ISBN)",
    'lib.imp.target.authorities': "المراجع",
    'lib.imp.target.readers': "القراء الخارجيون",
    'lib.imp.target.locations': "الأماكن",
    'lib.imp.headTitle.records': "استيراد البطاقات",
    'lib.imp.headTitle.items': "استيراد النسخ",
    'lib.imp.headTitle.authorities': "استيراد المراجع",
    'lib.imp.headTitle.readers': "استيراد القراء",
    'lib.imp.headTitle.locations': "استيراد الأماكن",
    'lib.imp.format.csv': "CSV",
    'lib.imp.format.unimarc': "UNIMARC",
    'lib.imp.format.marc21': "MARC21",
    'lib.imp.format.marcxml': "MARCXML",
    'lib.imp.format.isbn': "قائمة ISBN",
    'lib.imp.st.simulated': "محاكى",
    'lib.imp.st.queued': "في الطابور",
    'lib.imp.st.done': "مستورد",
    'lib.imp.st.undone': "ملغى",
    'lib.imp.mode.both': "إنشاء وتحديث",
    'lib.imp.mode.create': "إنشاء فقط",
    'lib.imp.mode.update': "تحديث فقط",
    'lib.imp.key.isbn': "ISBN",
    'lib.imp.key.barcode': "الرمز الشريطي",
    'lib.imp.key.external': "العنوان والسنة",
    'lib.imp.action.create': "إنشاء",
    'lib.imp.action.update': "تحديث",
    'lib.imp.action.reject': "مرفوض",
    'lib.imp.export.catalog': "الفهرس",
    'lib.imp.export.items': "النسخ",
    'lib.imp.export.authorities': "المراجع",
    'lib.imp.export.readers': "القراء",
    'lib.imp.export.locations': "الأماكن",
    'lib.imp.export.loans': "الإعارات",
    'lib.imp.export.fees': "المستحقات",
    'lib.imp.export.holds': "الحجوزات",
    'lib.imp.exportFormat.csv': "CSV",
    'lib.imp.exportFormat.marc21': "MARC21",
    'lib.imp.exportFormat.unimarc': "UNIMARC",
    'lib.imp.exportFormat.ris': "RIS",
    'lib.imp.exportFormat.bibtex': "BibTeX",
    'lib.imp.exportFormat.dc': "Dublin Core",
    'lib.imp.f.title': "العنوان",
    'lib.imp.f.subtitle': "العنوان الفرعي",
    'lib.imp.f.authors': "المؤلفون",
    'lib.imp.f.publisher': "الناشر",
    'lib.imp.f.place': "مكان النشر",
    'lib.imp.f.year': "السنة",
    'lib.imp.f.isbn': "ISBN",
    'lib.imp.f.issn': "ISSN",
    'lib.imp.f.cote': "الرمز",
    'lib.imp.f.dewey': "رقم ديوي",
    'lib.imp.f.language': "اللغة",
    'lib.imp.f.docType': "نوع الوثيقة",
    'lib.imp.f.pages': "الصفحات",
    'lib.imp.f.summary': "الملخص",
    'lib.imp.f.collection': "السلسلة",
    'lib.imp.f.subjects': "المواضيع",
    'lib.imp.f.barcode': "الرمز الشريطي",
    'lib.imp.f.location': "المكان",
    'lib.imp.f.price': "الثمن",
    'lib.imp.f.loanStatus': "وضع الإعارة",
    'lib.imp.f.acquiredOn': "تاريخ الشراء",
    'lib.imp.f.supplier': "المورد",
    'lib.imp.f.kind': "نوع المرجع",
    'lib.imp.f.name': "الاسم",
    'lib.imp.f.firstName': "الاسم الشخصي",
    'lib.imp.f.variants': "الصيغ البديلة",
    'lib.imp.f.lastName': "النسب",
    'lib.imp.f.firstNames': "الأسماء",
    'lib.imp.f.nationalId': "الوثيقة",
    'lib.imp.f.email': "البريد",
    'lib.imp.f.category': "الفئة",
    'lib.imp.f.path': "المسار",
    'lib.imp.f.code': "الرمز",
    'lib.imp.f.externalId': "المعرف الخارجي",
    'lib.form.sec.courses': "قائمة مراجع الدروس",
    'lib.form.coursesHint': "التكوينات والمواد التي توصي بهذا الكتاب؛ يظهر في بطاقة الدرس وفي بوابة طلبتها.",
    'lib.form.noCourses': "لا يوصي أي درس بهذا الكتاب.",
    'lib.form.courseAdd': "إضافة تكوين أو مادة",
    'lib.form.courseSearch': "رمز أو عنوان التكوين أو المادة",
    'lib.form.trainingSearch': "رمز أو عنوان التكوين",
    'lib.form.courseIsTraining': "تكوين",
    'lib.form.courseTeacher': "الأستاذ الموصي",
    'lib.form.courseNote': "ملاحظة",
    'lib.form.courseNoteHint': "مثلا: قراءة إلزامية، الفصول 2 إلى 4",
    'lib.form.removeCourse': "إزالة هذا الدرس",
    'lib.form.thesisTraining': "التكوين",
    'lib.form.setParent': "بطاقة المجموعة",
    'lib.form.setSearch': "عنوان أو مؤلف أو ISBN المجموعة",
    'lib.form.setHint': "لجزء: بطاقة العمل متعدد الأجزاء، التي ستعرض الأجزاء الموجودة والناقصة.",
    'lib.form.setRemove': "فصل عن المجموعة",
};

/** Les langues dont le front a un dictionnaire de coquille. */
export const SHIPPED_LOCALES: readonly string[] = ['fr', 'en', 'ar'];

/** Le sens d'écriture d'une langue, avant que /api/me ne le dise. */
export function directionOf(code: string): 'ltr' | 'rtl' {
    return code === 'ar' ? 'rtl' : 'ltr';
}

const DICTIONARIES: Record<string, Record<TranslationKey, string>> = { fr, en, ar };

export type Translate = (key: TranslationKey, params?: Readonly<Record<string, string | number>>) => string;

/**
 * Retombe sur le français quand la langue servie n'a pas encore de dictionnaire
 * de coquille : l'espagnol et le portugais sont modélisés (D15) mais pas livrés
 * (l'arabe l'est depuis D43). Un libellé dans la mauvaise langue est une gêne ;
 * un libellé vide est un rapport de bogue.
 */
export function translator(locale: string): Translate {
    const dictionary = DICTIONARIES[locale] ?? fr;

    return (key, params) => {
        const text = dictionary[key];

        return params === undefined ? text : text.replace(/\{(\w+)}/g, (match, name: string) => String(params[name] ?? match));
    };
}

/**
 * Les codes de violation que le serveur émet, et le libellé qui leur répond.
 *
 * LE CODE EST LE CONTRAT, PAS LE MESSAGE. Le serveur n'a pas de traducteur ; ses
 * messages sont en anglais (contraintes Symfony) ou en français (règles métier).
 * Le front traduit à partir du code — les UUID des contraintes Symfony, stables
 * d'une version à l'autre, et les codes `kelassi.*` des règles métier — et ne
 * montre le message brut qu'en dernier recours.
 */
const VIOLATION_KEYS: Readonly<Record<string, TranslationKey>> = {
    // Les codes que le serveur traduit lui-même (translations/validators.*.yaml,
    // avec la limite : « 255 caractères au plus. ») ne figurent pas ici : leur
    // message arrive déjà dans la langue demandée.
    'aa2e33da-25c8-4d76-8c6c-812f02ea89dd': 'violation.sameAsCurrent', // NotEqualTo
    '8e179f1b-97aa-4560-a02f-2a8b42e49df7': 'violation.choice', // Choice
    'ba785a8c-82cb-4283-967c-3cf342181b40': 'violation.choice', // Type — une valeur d'enum inconnue, selon API Platform
    'kelassi.email_taken': 'violation.emailTaken',
    'kelassi.current_password_invalid': 'violation.currentPasswordInvalid',
    'kelassi.token_invalid': 'violation.tokenInvalid',
    'bef8e338-6ae5-4caf-b8e2-50e7b0579e69': 'assignment.scope.emptySome', // Count (min) : « Seulement certains » sans rien
    'kelassi.assignment.self': 'violation.assignmentSelf',
    'kelassi.assignment.exists': 'violation.assignmentExists',
    'kelassi.assignment.unknown_account': 'violation.choice',
    'kelassi.assignment.unknown_role': 'violation.choice',
    'kelassi.scope.unknown': 'violation.choice',
    'kelassi.period.inverted': 'violation.periodInverted',
    'kelassi.policy.idle': 'violation.policyIdle',
    'kelassi.two_factor.invalid': 'violation.twoFactorInvalid',
    'kelassi.policy.password_required': 'violation.policyPassword',
    'kelassi.planning.room_name_taken': 'violation.plan.room_name_taken',
    'kelassi.planning.room_in_use': 'violation.plan.room_in_use',
    'kelassi.planning.dates_order': 'violation.plan.dates_order',
    'kelassi.planning.times_order': 'violation.plan.times_order',
    'kelassi.planning.invalid_time': 'violation.plan.invalid_time',
    'kelassi.planning.outside_year': 'violation.plan.outside_year',
    'kelassi.planning.year_closed': 'violation.plan.year_closed',
    'kelassi.planning.no_class': 'violation.plan.no_class',
    'kelassi.planning.subject_expected': 'violation.plan.subject_expected',
    'kelassi.planning.other_training': 'violation.plan.other_training',
    'kelassi.planning.series_subject': 'violation.plan.series_subject',
    'kelassi.planning.series_empty': 'violation.plan.series_empty',
    'kelassi.planning.event_has_roll': 'violation.plan.event_has_roll',
    'kelassi.attendance.not_expected': 'violation.att.not_expected',
    'kelassi.attendance.already_waived': 'violation.att.already_waived',
    'kelassi.regulation.attendance_threshold': 'violation.reg.attendance_threshold',
    'kelassi.regulation.attendance_order': 'violation.reg.attendance_order',
    'kelassi.referential.year_closed': 'violation.ref.year_closed',
    'kelassi.referential.year_not_preparing': 'violation.ref.year_not_preparing',
    'kelassi.referential.in_use': 'violation.ref.in_use',
    'kelassi.referential.year_label': 'violation.ref.year_label',
    'kelassi.referential.year_dates': 'violation.ref.year_dates',
    'kelassi.referential.duplicate': 'violation.ref.duplicate',
    'kelassi.referential.letter_scale_required': 'violation.ref.letter_scale_required',
    'kelassi.referential.structure_order': 'violation.ref.structure_order',
    'kelassi.referential.credits_once': 'violation.ref.credits_once',
    'kelassi.referential.taught_required': 'violation.ref.taught_required',
    'kelassi.referential.last_structure': 'violation.ref.last_structure',
    'kelassi.referential.band_order': 'violation.ref.band_order',
    'kelassi.referential.band_gap': 'violation.ref.band_gap',
    'kelassi.referential.letters_from_zero': 'violation.ref.letters_from_zero',
    'kelassi.referential.letters_points': 'violation.ref.letters_points',
    'kelassi.referential.not_found': 'violation.ref.not_found',
    'kelassi.referential.assigned': 'violation.ref.assigned',
    'kelassi.catalog.kind_not_allowed': 'violation.cat.kind_not_allowed',
    'kelassi.catalog.already_renewed': 'violation.cat.already_renewed',
    'kelassi.catalog.source_year': 'violation.cat.source_year',
    'kelassi.catalog.not_in_training': 'violation.cat.not_in_training',
    'kelassi.catalog.root': 'violation.cat.root',
    'kelassi.catalog.capacity': 'violation.cat.capacity',
    'kelassi.schooling.already_active': 'violation.scol.already_active',
    'kelassi.schooling.already_enrolled': 'violation.scol.already_enrolled',
    'kelassi.schooling.already_inactive': 'violation.scol.already_inactive',
    'kelassi.schooling.invalid_pattern': 'violation.scol.invalid_pattern',
    'kelassi.schooling.no_class': 'violation.scol.no_class',
    'kelassi.schooling.no_element': 'violation.scol.no_element',
    'kelassi.schooling.not_enrolled': 'violation.scol.not_enrolled',
    'kelassi.schooling.not_member': 'violation.scol.not_member',
    'kelassi.schooling.target_year': 'violation.scol.target_year',
    'kelassi.finance.no_pricing_level': 'violation.fin.no_pricing_level',
    'kelassi.finance.invalid_currency': 'violation.fin.invalid_currency',
    'kelassi.finance.currency_locked': 'violation.fin.currency_locked',
    'kelassi.finance.invalid_pattern': 'violation.fin.invalid_pattern',
    'kelassi.finance.tax_billed': 'violation.fin.tax_billed',
    'kelassi.finance.in_use': 'violation.fin.in_use',
    'kelassi.finance.invalid_schedule': 'violation.fin.invalid_schedule',
    'kelassi.finance.shares_incomplete': 'violation.fin.shares_incomplete',
    'kelassi.finance.invalid_amount': 'violation.fin.invalid_amount',
    'kelassi.finance.level_not_priced': 'violation.fin.level_not_priced',
    'kelassi.finance.schedule_on_training': 'violation.fin.schedule_on_training',
    'kelassi.finance.already_invoiced': 'violation.fin.already_invoiced',
    'kelassi.finance.nothing_to_bill': 'violation.fin.nothing_to_bill',
    'kelassi.finance.inactive': 'violation.fin.inactive',
    'kelassi.finance.on_demand_service': 'violation.fin.on_demand_service',
    'kelassi.finance.already_on_invoice': 'violation.fin.already_on_invoice',
    'kelassi.finance.training_line': 'violation.fin.training_line',
    'kelassi.finance.last_line': 'violation.fin.last_line',
    'kelassi.finance.not_stackable': 'violation.fin.not_stackable',
    'kelassi.finance.invoice_has_payments': 'violation.fin.invoice_has_payments',
    'kelassi.finance.credit_too_high': 'violation.fin.credit_too_high',
    'kelassi.finance.nothing_to_credit': 'violation.fin.nothing_to_credit',
    'kelassi.finance.no_line': 'violation.fin.no_line',
    'kelassi.finance.below_credited': 'violation.fin.below_credited',
    'kelassi.finance.invoice_has_credit_note': 'violation.fin.invoice_has_credit_note',
    'kelassi.finance.refund_too_high': 'violation.fin.refund_too_high',
    'kelassi.finance.transfer_too_high': 'violation.fin.transfer_too_high',
    'kelassi.finance.transfer_target': 'violation.fin.transfer_target',
    'kelassi.finance.service_not_offered': 'violation.fin.service_not_offered',
    'kelassi.finance.invoice_cancelled': 'violation.fin.invoice_cancelled',
    'kelassi.finance.future_date': 'violation.fin.future_date',
    'kelassi.finance.unlikely_date': 'violation.fin.unlikely_date',
    'kelassi.finance.is_reversal': 'violation.fin.is_reversal',
    'kelassi.finance.already_reversed': 'violation.fin.already_reversed',
    'kelassi.finance.enrolment_has_payments': 'violation.fin.enrolment_has_payments',
    'kelassi.regulation.open_version': 'violation.reg.open_version',
    'kelassi.regulation.not_draft': 'violation.reg.not_draft',
    'kelassi.regulation.four_eyes': 'violation.reg.four_eyes',
    'kelassi.regulation.blocking_checks': 'violation.reg.blocking_checks',
    'kelassi.regulation.profile_in_use': 'violation.reg.profile_in_use',
    'kelassi.regulation.profile_name_taken': 'violation.reg.profile_name_taken',
    'kelassi.regulation.invalid': 'violation.reg.invalid',
    'kelassi.regulation.out_of_range': 'violation.reg.out_of_range',
    'kelassi.regulation.precision': 'violation.reg.precision',
    'kelassi.regulation.unknown_target': 'violation.reg.unknown_target',
    'kelassi.regulation.type_weights_empty': 'violation.reg.type_weights_empty',
    'kelassi.regulation.test_expectation': 'violation.reg.test_expectation',
    'kelassi.regulation.year_closed': 'violation.reg.year_closed',
    'kelassi.faculty.contract_overlap': 'violation.fac.contract_overlap',
    'kelassi.payroll.contract_locked': 'violation.pay.contract_locked',
    'kelassi.payroll.rolls_missing': 'violation.pay.rolls_missing',
    'kelassi.communication.out_of_scope': 'violation.com.out_of_scope',
    'kelassi.communication.not_chattable': 'violation.com.not_chattable',
    'kelassi.communication.not_a_group': 'violation.com.not_a_group',
    'kelassi.communication.type_not_allowed': 'violation.com.type_not_allowed',
    'kelassi.communication.no_year': 'violation.com.no_year',
    'kelassi.communication.no_recipient': 'violation.com.no_recipient',
    'kelassi.communication.nothing_to_remind': 'violation.com.nothing_to_remind',
    'kelassi.communication.replies_closed': 'violation.com.replies_closed',
    'kelassi.communication.no_reply': 'violation.com.no_reply',
    'kelassi.communication.too_many_attachments': 'violation.com.too_many_attachments',
    'kelassi.communication.unknown_attachment': 'violation.com.unknown_attachment',
    'kelassi.communication.system_type': 'violation.com.system_type',
    'kelassi.communication.type_used': 'violation.com.type_used',
    'kelassi.relations.inactive': 'violation.rel.inactive',
    'kelassi.relations.not_found': 'violation.rel.not_found',
    'kelassi.relations.duplicate': 'violation.rel.duplicate',
    'kelassi.relations.label_taken': 'violation.rel.duplicate',
    'kelassi.relations.in_use': 'violation.rel.in_use',
    'kelassi.relations.dates_order': 'violation.rel.dates_order',
    'kelassi.relations.not_draft': 'violation.rel.not_draft',
    'kelassi.relations.not_signed': 'violation.rel.not_signed',
    'kelassi.relations.no_end': 'violation.rel.no_end',
    'kelassi.relations.already_renewed': 'violation.rel.already_renewed',
    'kelassi.relations.document_invalid': 'violation.rel.document_invalid',
    'kelassi.relations.not_pdf': 'violation.rel.document_invalid',
    'kelassi.relations.document_too_large': 'violation.rel.document_too_large',
    'kelassi.relations.too_large': 'violation.rel.document_too_large',
    'kelassi.relations.organisation_required': 'violation.rel.organisation_required',
    'kelassi.relations.already_prospect': 'violation.rel.already_prospect',
    'kelassi.relations.prospect_closed': 'violation.rel.prospect_closed',
    'kelassi.relations.not_alumnus': 'violation.rel.not_alumnus',
    'kelassi.relations.no_recipient': 'violation.rel.no_recipient',
    'kelassi.relations.token_invalid': 'violation.rel.token_invalid',
    'kelassi.relations.token_expired': 'violation.rel.token_expired',
    'kelassi.relations.website_invalid': 'violation.rel.website_invalid',
    'kelassi.relations.reason_required': 'violation.rel.reason_required',
    'kelassi.relations.stage_in_use': 'violation.rel.stage_in_use',
    'kelassi.relations.last_stage': 'violation.rel.last_stage',
    'kelassi.relations.out_of_range': 'violation.rel.out_of_range',
    'kelassi.relations.unknown_list': 'violation.rel.unknown_list',
    'kelassi.relations.unknown_account': 'violation.rel.unknown_account',
    'kelassi.relations.contact_other_organisation': 'violation.rel.contact_other_organisation',
    'kelassi.requests.action_locked': 'violation.req.action_locked',
    'kelassi.requests.assessment_open': 'violation.req.assessment_open',
    'kelassi.requests.attachment_required': 'violation.req.attachment_required',
    'kelassi.requests.bad_deliberation': 'violation.req.bad_deliberation',
    'kelassi.requests.closed': 'violation.req.closed',
    'kelassi.requests.closed_type': 'violation.req.closed_type',
    'kelassi.requests.mark_locked': 'violation.req.mark_locked',
    'kelassi.requests.no_audience': 'violation.req.no_audience',
    'kelassi.requests.no_enrolment': 'violation.req.no_enrolment',
    'kelassi.requests.no_execution': 'violation.req.no_execution',
    'kelassi.requests.no_lines': 'violation.req.no_lines',
    'kelassi.requests.no_steps': 'violation.req.no_steps',
    'kelassi.requests.not_a_student': 'violation.req.not_a_student',
    'kelassi.requests.not_admitted': 'violation.req.not_admitted',
    'kelassi.requests.not_for_you': 'violation.req.not_for_you',
    'kelassi.requests.not_issued': 'violation.req.not_issued',
    'kelassi.requests.not_waiting': 'violation.req.not_waiting',
    'kelassi.requests.one_execution': 'violation.req.one_execution',
    'kelassi.requests.too_many_files': 'violation.req.too_many_files',
    'kelassi.requests.use_stages': 'violation.req.use_stages',
    'kelassi.requests.waiting': 'violation.req.waiting',
    'kelassi.document.reason_required': 'violation.doc.reason_required',
    'kelassi.document.already_cancelled': 'violation.doc.already_cancelled',
    'kelassi.document.not_replaceable': 'violation.doc.not_replaceable',
    'kelassi.document.run_changed': 'violation.doc.run_changed',
    'kelassi.requests.documents_off': 'violation.doc.documents_off',
    'kelassi.template.invalid_block': 'violation.tpl.invalid_block',
    'kelassi.template.too_many': 'violation.tpl.too_many',
    'kelassi.template.block_source': 'violation.tpl.block_source',
    'kelassi.template.unknown_field': 'violation.tpl.unknown_field',
    'kelassi.template.too_long': 'violation.tpl.too_long',
    'kelassi.template.validity': 'violation.tpl.validity',
    'kelassi.template.kind': 'violation.tpl.kind',
    'kelassi.template.name': 'violation.tpl.name',
    'kelassi.template.languages': 'violation.tpl.languages',
    'kelassi.template.system': 'violation.tpl.system',
    'kelassi.template.no_draft': 'violation.tpl.no_draft',
    'kelassi.template.only_draft': 'violation.tpl.only_draft',
    'kelassi.template.version': 'violation.tpl.version',
    'kelassi.template.not_usable': 'violation.tpl.not_usable',
    'kelassi.template.not_archivable': 'violation.tpl.not_archivable',
    'kelassi.template.wrong_kind': 'violation.tpl.wrong_kind',
    'kelassi.template.unpublished': 'violation.tpl.unpublished',
    'kelassi.template.subject': 'violation.tpl.subject',
    'kelassi.template.no_previous_year': 'violation.tpl.no_previous_year',
    'kelassi.production.signer': 'violation.prd.signer',
    'kelassi.production.too_many': 'violation.prd.too_many',
    'kelassi.production.training': 'violation.prd.training',
    'kelassi.production.finished': 'violation.prd.finished',
    'kelassi.production.nothing_to_retry': 'violation.prd.nothing_to_retry',
    'kelassi.document.nobody': 'violation.prd.nobody',
    'kelassi.signature.title': 'violation.sgn.title',
    'kelassi.signature.no_signer': 'violation.sgn.no_signer',
    'kelassi.signature.delegate': 'violation.sgn.delegate',
    'kelassi.signature.period': 'violation.sgn.period',
    'kelassi.signature.too_big': 'violation.sgn.too_big',
    'kelassi.signature.format': 'violation.sgn.format',
    'kelassi.signature.too_small': 'violation.sgn.too_small',
    'kelassi.signature.too_many_codes': 'violation.sgn.too_many_codes',
    'kelassi.signature.code_expired': 'violation.sgn.code_expired',
    'kelassi.signature.code': 'violation.sgn.code',
    'kelassi.signature.not_yours': 'violation.sgn.not_yours',
    'kelassi.signature.no_job': 'violation.sgn.no_job',
    'kelassi.shared.audience': 'violation.shr.audience',
    'kelassi.shared.audience_students': 'violation.shr.audience_students',
    'kelassi.shared.title': 'violation.shr.title',
    'kelassi.shared.date': 'violation.shr.date',
    'kelassi.shared.period': 'violation.shr.period',
    'kelassi.shared.archived': 'violation.shr.archived',
    'kelassi.shared.no_acknowledgement': 'violation.shr.no_acknowledgement',
    'kelassi.shared.no_communication': 'violation.shr.no_communication',
    'kelassi.shared.new_version': 'violation.shr.new_version',
    'kelassi.library.reason_required': 'violation.lib.reason_required',
    'kelassi.library.prefix': 'violation.lib.prefix',
    'kelassi.library.policy_duplicate': 'violation.lib.policy_duplicate',
    'kelassi.library.default_policy': 'violation.lib.default_policy',
    'kelassi.library.isbn': 'violation.lib.isbn',
    'kelassi.library.year_order': 'violation.lib.year_order',
    'kelassi.library.dewey': 'violation.lib.dewey',
    'kelassi.library.issn': 'violation.lib.issn',
    'kelassi.library.language': 'violation.lib.language',
    'kelassi.library.contributor_required': 'violation.lib.contributor_required',
    'kelassi.library.edition_used': 'violation.lib.edition_used',
    'kelassi.library.merge_invalid': 'violation.lib.merge_invalid',
    'kelassi.library.location_cycle': 'violation.lib.location_cycle',
    'kelassi.library.location_used': 'violation.lib.location_used',
    'kelassi.library.barcode': 'violation.lib.barcode',
    'kelassi.library.barcode_taken': 'violation.lib.barcode_taken',
    'kelassi.library.item_withdrawn': 'violation.lib.item_withdrawn',
    'kelassi.library.no_category': 'violation.lib.no_category',
    'kelassi.library.reader_exists': 'violation.lib.reader_exists',
    'kelassi.library.contact_required': 'violation.lib.contact_required',
    'kelassi.library.person_exists': 'violation.lib.person_exists',
    'kelassi.library.reader_used': 'violation.lib.reader_used',
    'kelassi.library.no_membership': 'violation.lib.no_membership',
    'kelassi.library.card_inactive': 'violation.lib.card_inactive',
    'kelassi.library.fee_invoiced': 'violation.lib.fee_invoiced',
    'kelassi.library.fee_settled': 'violation.lib.fee_settled',
    'kelassi.library.loan_closed': 'violation.lib.loan_closed',
    'kelassi.library.not_lost': 'violation.lib.not_lost',
    'kelassi.library.unknown_reader': 'violation.lib.unknown_reader',
    'kelassi.library.unknown_item': 'violation.lib.unknown_item',
    'kelassi.library.no_open_loan': 'violation.lib.no_open_loan',
    'kelassi.library.holds_closed': 'violation.lib.holds_closed',
    'kelassi.library.confidential': 'violation.lib.confidential',
    'kelassi.library.hold_exists': 'violation.lib.hold_exists',
    'kelassi.library.hold_loaned': 'violation.lib.hold_loaned',
    'kelassi.library.no_loanable': 'violation.lib.no_loanable',
    'kelassi.library.hold_closed': 'violation.lib.hold_closed',
    'kelassi.library.hold_item': 'violation.lib.hold_item',
    'kelassi.library.inventory_open': 'violation.lib.inventory_open',
    'kelassi.library.inventory_closed': 'violation.lib.inventory_closed',
    'kelassi.library.resolve_invalid': 'violation.lib.resolve_invalid',
    'kelassi.library.import_done': 'violation.lib.import_done',
    'kelassi.library.import_not_done': 'violation.lib.import_not_done',
    'kelassi.library.file_size': 'violation.lib.file_size',
    'kelassi.library.card_code': 'violation.lib.card_code',
    'kelassi.library.cover': 'violation.lib.cover',
    'kelassi.library.not_mine': 'violation.lib.not_mine',
    'kelassi.document.enrolment_inactive': 'violation.doc.enrolment_inactive',
    'kelassi.document.not_from_deliberation': 'violation.doc.not_from_deliberation',
    'kelassi.housing.accept_late': 'violation.heb.accept_late',
    'kelassi.housing.bad_selection': 'violation.heb.bad_selection',
    'kelassi.housing.application_locked': 'violation.heb.application_locked',
    'kelassi.housing.bed_gender': 'violation.heb.bed_gender',
    'kelassi.housing.bed_out': 'violation.heb.bed_out',
    'kelassi.housing.bed_taken': 'violation.heb.bed_taken',
    'kelassi.housing.bed_used': 'violation.heb.bed_used',
    'kelassi.housing.campaign_closed': 'violation.heb.campaign_closed',
    'kelassi.housing.campaign_not_open': 'violation.heb.campaign_not_open',
    'kelassi.housing.campus_locked': 'violation.heb.campus_locked',
    'kelassi.housing.document_required': 'violation.heb.document_required',
    'kelassi.housing.inspection_locked': 'violation.heb.inspection_locked',
    'kelassi.housing.no_beds': 'violation.heb.no_beds',
    'kelassi.housing.no_enrolment': 'violation.heb.no_enrolment',
    'kelassi.housing.no_places': 'violation.heb.no_places',
    'kelassi.housing.no_price': 'violation.heb.no_price',
    'kelassi.housing.no_rooms': 'violation.heb.no_rooms',
    'kelassi.housing.not_eligible': 'violation.heb.not_eligible',
    'kelassi.housing.not_final': 'violation.heb.not_final',
    'kelassi.housing.not_for_level': 'violation.heb.not_for_level',
    'kelassi.housing.not_housed': 'violation.heb.not_housed',
    'kelassi.housing.not_provisional': 'violation.heb.not_provisional',
    'kelassi.housing.not_releasable': 'violation.heb.not_releasable',
    'kelassi.housing.nothing_to_pay': 'violation.heb.nothing_to_pay',
    'kelassi.housing.payment_decided': 'violation.heb.payment_decided',
    'kelassi.housing.price_duplicate': 'violation.heb.price_duplicate',
    'kelassi.housing.receipt_required': 'violation.heb.receipt_required',
    'kelassi.housing.retained_over': 'violation.heb.retained_over',
    'kelassi.housing.room_used': 'violation.heb.room_used',
    'kelassi.housing.rules_required': 'violation.heb.rules_required',
    'kelassi.housing.too_many_files': 'violation.heb.too_many_files',
    'kelassi.stages.too_short': 'violation.stg.too_short',
    'kelassi.stages.duplicate': 'violation.stg.duplicate',
    'kelassi.stages.bad_state': 'violation.stg.bad_state',
    'kelassi.stages.period': 'violation.stg.period',
    'kelassi.stages.future_follow_up': 'violation.stg.future_follow_up',
    'kelassi.stages.not_your_component': 'violation.stg.not_your_component',
    'kelassi.stages.mark_missing': 'violation.stg.mark_missing',
    'kelassi.stages.evaluated': 'violation.stg.evaluated',
    'kelassi.stages.no_subject': 'violation.stg.no_subject',
    'kelassi.stages.assessment_validated': 'violation.stg.assessment_validated',
    'kelassi.stages.no_assessment_type': 'violation.stg.no_assessment_type',
    'kelassi.stages.weights': 'violation.stg.weights',
    'kelassi.stages.two_reports': 'violation.stg.two_reports',
    'kelassi.stages.requirement_used': 'violation.stg.requirement_used',
    'kelassi.stages.component_used': 'violation.stg.component_used',
    'kelassi.stages.campaign_closed': 'violation.stg.campaign_closed',
    'kelassi.stages.bad_training': 'violation.stg.bad_training',
    'kelassi.stages.bad_subject': 'violation.stg.bad_subject',
    'kelassi.stages.duplicate_training': 'violation.stg.duplicate_training',
    'kelassi.stages.no_training': 'violation.stg.no_training',
    'kelassi.stages.campaign_used': 'violation.stg.campaign_used',
    'kelassi.stages.report_before_end': 'violation.stg.report_before_end',
    'kelassi.stages.other_campaign': 'violation.stg.other_campaign',
    'kelassi.stages.slot_taken': 'violation.stg.slot_taken',
    'kelassi.stages.juror_busy': 'violation.stg.juror_busy',
    'kelassi.stages.juror': 'violation.stg.juror',
    'kelassi.stages.juror_duplicate': 'violation.stg.juror_duplicate',
    'kelassi.stages.two_presidents': 'violation.stg.two_presidents',
    'kelassi.stages.offer_expired': 'violation.stg.offer_expired',
    'kelassi.stages.offer_used': 'violation.stg.offer_used',
    'kelassi.stages.offer_closed': 'violation.stg.offer_closed',
    'kelassi.stages.already_applied': 'violation.stg.already_applied',
    'kelassi.stages.application_linked': 'violation.stg.application_linked',
    'kelassi.learning.assessment_validated': 'violation.el.assessment_validated',
    'kelassi.learning.attempt_closed': 'violation.el.attempt_closed',
    'kelassi.learning.attempt_delay': 'violation.el.attempt_delay',
    'kelassi.learning.attempt_state': 'violation.el.attempt_state',
    'kelassi.learning.bad_sheet': 'violation.el.bad_sheet',
    'kelassi.learning.bad_url': 'violation.el.bad_url',
    'kelassi.learning.bad_video': 'violation.el.bad_video',
    'kelassi.learning.cloze_missing': 'violation.el.cloze_missing',
    'kelassi.learning.file_empty': 'violation.el.file_empty',
    'kelassi.learning.file_format': 'violation.el.file_format',
    'kelassi.learning.file_too_big': 'violation.el.file_too_big',
    'kelassi.branding.file_empty': 'violation.brd.file_empty',
    'kelassi.branding.file_too_big': 'violation.brd.file_too_big',
    'kelassi.branding.file_format': 'violation.brd.file_format',
    'kelassi.branding.file_unreadable': 'violation.brd.file_unreadable',
    'kelassi.branding.not_square': 'violation.brd.not_square',
    'kelassi.branding.too_small': 'violation.brd.too_small',
    'kelassi.branding.svg_unsafe': 'violation.brd.svg_unsafe',
    'kelassi.branding.svg_size': 'violation.brd.svg_size',
    'kelassi.branding.text': 'violation.brd.text',
    'kelassi.branding.name_required': 'violation.brd.name_required',
    'kelassi.branding.name_too_long': 'violation.brd.name_too_long',
    'kelassi.branding.unchanged': 'violation.brd.unchanged',
    'kelassi.branding.already_current': 'violation.brd.already_current',
    'kelassi.learning.lot_pending': 'violation.el.lot_pending',
    'kelassi.learning.no_assessment': 'violation.el.no_assessment',
    'kelassi.learning.no_attempt_left': 'violation.el.no_attempt_left',
    'kelassi.learning.no_previous': 'violation.el.no_previous',
    'kelassi.learning.not_a_file': 'violation.el.not_a_file',
    'kelassi.learning.not_shareable': 'violation.el.not_shareable',
    'kelassi.learning.nothing_to_publish': 'violation.el.nothing_to_publish',
    'kelassi.learning.nothing_to_validate': 'violation.el.nothing_to_validate',
    'kelassi.learning.question_answers': 'violation.el.question_answers',
    'kelassi.learning.question_choices': 'violation.el.question_choices',
    'kelassi.learning.question_correct': 'violation.el.question_correct',
    'kelassi.learning.quiz_checks': 'violation.el.quiz_checks',
    'kelassi.learning.quiz_locked': 'violation.el.quiz_locked',
    'kelassi.learning.quiz_not_open': 'violation.el.quiz_not_open',
    'kelassi.learning.quiz_pending': 'violation.el.quiz_pending',
    'kelassi.learning.quiz_state': 'violation.el.quiz_state',
    'kelassi.learning.quota_course': 'violation.el.quota_course',
    'kelassi.learning.quota_institution': 'violation.el.quota_institution',
    'kelassi.learning.section_not_empty': 'violation.el.section_not_empty',
    'kelassi.learning.sequential': 'violation.el.sequential',
    'kelassi.learning.syllabus_incomplete': 'violation.el.syllabus_incomplete',
    'kelassi.learning.syllabus_unchanged': 'violation.el.syllabus_unchanged',
    'kelassi.learning.transfer_state': 'violation.el.transfer_state',
    'kelassi.learning.transferred': 'violation.el.transferred',
    'kelassi.learning.window_order': 'violation.el.window_order',
    'kelassi.learning.quiz_still_open': 'violation.el.quiz_still_open',
    'kelassi.skills.scale_size': 'violation.skl.scale_size',
    'kelassi.skills.conversion_size': 'violation.skl.conversion_size',
    'kelassi.skills.conversion_range': 'violation.skl.conversion_range',
    'kelassi.skills.conversion_order': 'violation.skl.conversion_order',
    'kelassi.skills.scale_locked': 'violation.skl.scale_locked',
    'kelassi.skills.marks_off': 'violation.skl.marks_off',
    'kelassi.skills.teacher_scope': 'violation.skl.teacher_scope',
    'kelassi.skills.unknown_code': 'violation.skl.unknown_code',
    'kelassi.skills.empty_framework': 'violation.skl.empty_framework',
    'kelassi.skills.framework_used': 'violation.skl.framework_used',
    'kelassi.skills.node_used': 'violation.skl.node_used',
    'kelassi.skills.not_yours': 'violation.skl.not_yours',
    'kelassi.skills.activity_locked': 'violation.skl.activity_locked',
    'kelassi.skills.activities_off': 'violation.skl.activities_off',
    'kelassi.skills.no_skill': 'violation.skl.no_skill',
    'kelassi.skills.framework_no_marks': 'violation.skl.framework_no_marks',
    'kelassi.skills.deposits_off': 'violation.skl.deposits_off',
    'kelassi.skills.activity_used': 'violation.skl.activity_used',
    'kelassi.skills.no_assessment': 'violation.skl.no_assessment',
    'kelassi.skills.assessment_validated': 'violation.skl.assessment_validated',
    'kelassi.skills.file_empty': 'violation.skl.file_empty',
    'kelassi.skills.file_too_big': 'violation.skl.file_too_big',
    'kelassi.skills.file_format': 'violation.skl.file_format',
    'kelassi.skills.deposit_closed': 'violation.skl.deposit_closed',
    'kelassi.export.rows': 'violation.export.rows',
    'kelassi.export.columns': 'violation.export.columns',
};

export function violationText(t: Translate, violation: { readonly code: string | null; readonly message: string }): string {
    const key = violation.code === null ? undefined : VIOLATION_KEYS[violation.code];

    return key === undefined ? violation.message : t(key);
}
